import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  LayoutDashboard, PackagePlus, Warehouse, Factory, MessageSquare, BarChart3,
  Fish, Wheat, CookingPot, Clock, CheckCircle2, Plus, X, Search, ChevronRight,
  ChevronLeft, ChevronDown, Play, User, Truck, Thermometer, Pin, Send, Trash2,
  Circle, AlertTriangle, Upload, FileText, Download, Printer, Calendar,
  ShoppingCart, Utensils, ChefHat, Users, Book, ShieldCheck, Droplets,
  ArrowRight, Package, Tag, Receipt, Settings, Bell, Zap, GitBranch, Copy,
  Scan, Command, TrendingUp, TrendingDown, Hash, Filter, MoreHorizontal,
  ArrowUpRight, Layers, RefreshCw, Sparkles, Flame, Snowflake, Home, Activity,
  CheckSquare, Square, Info, History, Lock, Unlock, Minus, UtensilsCrossed,
  Link2, Edit2, Database,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area,
} from "recharts";
import { processAttachments, MAX_ATTACHMENT_SIZE_MB, MAX_TOTAL_ATTACHMENTS_MB, appendAudit, softDelete, isActive } from "./utils/safety.js";
import { weekdayAdjustedTrend } from "./lib/stats.js";
import { sanitizeCsvCell, validateBackupFile, validateBackupShape } from "./lib/sanitize.js";

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          DESIGN SYSTEM                                     ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
const C = {
  paper: "#F5F0E6", paperDeep: "#EDE6D6", paperSoft: "#FAF5EA",
  ink: "#1C1A17", inkSoft: "#3A3630", inkMid: "#5A5349",
  muted: "#8B8276", mutedSoft: "#B8AF9E", rule: "#D9D0BE", ruleSoft: "#E5DECB",
  teal: "#1F4D48", tealSoft: "#2E6D65", tealWash: "#D6E3E0", tealDeep: "#15322E",
  terra: "#B5472A", terraWash: "#F0DBD0", terraDeep: "#7F2E18",
  amber: "#C8841E", amberWash: "#F3E3C4", amberDeep: "#8A5A0F",
  olive: "#607A2E", oliveWash: "#DCE4C5", oliveDeep: "#3E5119",
  plum: "#6B3E5C", plumWash: "#E8D6DF",
  white: "#FEFBF4",
};

// FONT_STYLE è stato spostato in src/styles.css (importato da src/main.jsx).
// Lasciato commentato qui per riferimento storico — NON ricreare la const,
// modificare gli stili globali direttamente in src/styles.css.
/* eslint-disable */
const _FONT_STYLE_MIGRATED_TO_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600;9..144,700&family=Geist:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
  body { background: ${C.paper}; margin: 0; }
  * { -webkit-font-smoothing: antialiased; box-sizing: border-box; }
  .f-display { font-family: 'Fraunces', Georgia, serif; letter-spacing: -0.02em; }
  .f-body { font-family: 'Geist', system-ui, sans-serif; }
  .f-mono { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
  input, select, textarea, button { font-family: inherit; }
  input:focus, select:focus, textarea:focus { outline: 2px solid ${C.teal}; outline-offset: 1px; }
  .hide-scroll::-webkit-scrollbar { display: none; } .hide-scroll { scrollbar-width: none; }

  @keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slideLeft { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: translateX(0); } }
  @keyframes slideDown { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes scaleIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
  @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }
  @keyframes pulseRing { 0% { box-shadow: 0 0 0 0 rgba(181, 71, 42, 0.5); } 70% { box-shadow: 0 0 0 8px rgba(181, 71, 42, 0); } 100% { box-shadow: 0 0 0 0 rgba(181, 71, 42, 0); } }
  @keyframes shake { 0%,100% { transform: translateX(0); } 10%,30%,50%,70%,90% { transform: translateX(-6px); } 20%,40%,60%,80% { transform: translateX(6px); } }
  .shake { animation: shake 0.4s cubic-bezier(.36,.07,.19,.97) both; }
  @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  @keyframes bellRing { 0%, 100% { transform: rotate(0deg); } 15% { transform: rotate(-10deg); } 30% { transform: rotate(8deg); } 45% { transform: rotate(-6deg); } 60% { transform: rotate(4deg); } }

  .fade-up { animation: fadeUp 0.3s ease-out both; }
  .fade-in { animation: fadeIn 0.25s ease-out both; }
  .slide-left { animation: slideLeft 0.3s ease-out both; }
  .slide-down { animation: slideDown 0.2s ease-out both; }
  .scale-in { animation: scaleIn 0.2s ease-out both; }
  .pulsing { animation: pulse 1.8s infinite; }
  .pulse-ring { animation: pulseRing 2s infinite; }
  .bell-ring { animation: bellRing 2.5s ease-in-out infinite; transform-origin: 50% 0%; }

  .tx { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
  .hover-lift:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
  .hover-border:hover { border-color: ${C.ink} !important; }

  /* Stagger children animations */
  .stagger > * { animation: fadeUp 0.4s ease-out both; }
  .stagger > *:nth-child(1) { animation-delay: 0.02s; }
  .stagger > *:nth-child(2) { animation-delay: 0.06s; }
  .stagger > *:nth-child(3) { animation-delay: 0.10s; }
  .stagger > *:nth-child(4) { animation-delay: 0.14s; }
  .stagger > *:nth-child(5) { animation-delay: 0.18s; }
  .stagger > *:nth-child(6) { animation-delay: 0.22s; }
  .stagger > *:nth-child(7) { animation-delay: 0.26s; }
  .stagger > *:nth-child(8) { animation-delay: 0.30s; }

  @media print {
    body * { visibility: hidden; }
    .printable, .printable * { visibility: visible; }
    .printable { position: absolute; left: 0; top: 0; width: 100%; }
    .no-print { display: none !important; }
  }
`;
/* eslint-enable */
void _FONT_STYLE_MIGRATED_TO_CSS;

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          SEED DATA                                         ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// Data odierna in formato YYYY-MM-DD (timezone locale per evitare bug a mezzanotte UTC)
// Ricalcolata al caricamento dell'app. Per cambio giornaliero senza refresh serve un useEffect,
// ma per il flusso operativo normale (si apre l'app al mattino) basta questo.
const todayLocalISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const TODAY = todayLocalISO();

// ── Stagionalità mensile (FIX #6 audit 2026-06-04) ──────────────────────────
// Indice stagionale MOLTIPLICATIVO per mese (gen..dic), normalizzato a media 1.
// Profilo NEUTRO = tutti 1 → zero impatto sul forecast (backward-compat totale).
const SEASONALITY_NEUTRAL = [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1];
// Preset "ristorante di costa adriatica": coefficienti grezzi (luglio/agosto picco,
// inverno fondo) che VENGONO POI NORMALIZZATI a media 1 prima dell'uso. Sono una
// STIMA di settore (Marche, cucina di mare), NON un dato appreso: Andi li applica
// esplicitamente e li ritocca. Profilo tipico: estate ~2.5-3.5× l'inverno.
//                              gen   feb   mar   apr   mag   giu   lug   ago   set   ott   nov   dic
const SEASONALITY_PRESET_COSTA = [0.55, 0.55, 0.70, 0.95, 1.25, 1.70, 2.30, 2.50, 1.55, 0.95, 0.60, 0.90];
const MONTH_LABELS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

// Normalizza un array di 12 coefficienti a media 1 (così il LIVELLO complessivo
// del forecast non cambia, cambia solo la RIDISTRIBUZIONE tra i mesi).
// Difensivo: input non valido / somma nulla → ritorna profilo neutro.
function normalizeSeasonality(arr) {
  if (!Array.isArray(arr) || arr.length !== 12) return SEASONALITY_NEUTRAL.slice();
  const nums = arr.map(v => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : null; });
  if (nums.some(n => n === null)) return SEASONALITY_NEUTRAL.slice();
  const mean = nums.reduce((s, v) => s + v, 0) / 12;
  if (!(mean > 0)) return SEASONALITY_NEUTRAL.slice();
  return nums.map(v => v / mean);
}

const NOW_HOUR = (() => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
})();
const nowIso = () => new Date().toISOString();
// Estrae la data YYYY-MM-DD LOCALE da un timestamp ISO (che potrebbe essere UTC con Z)
// Questo è fondamentale per confrontare "è stato chiuso oggi?" senza bug di timezone.
const localDateOf = (isoStr) => {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

// CDF normale standard — approssimazione Abramowitz-Stegun 26.2.17 (errore < 7.5e-8).
// Usata per p-value approssimati in test statistici (z-test su variance, drift detection).
// Reference: Abramowitz & Stegun, Handbook of Mathematical Functions (1972).
const normalCDF = (z) => {
  if (!Number.isFinite(z)) return 0.5;
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989423 * Math.exp(-z * z / 2);
  const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  return z > 0 ? 1 - p : p;
};

// Categorie piatti (unificato per menu / sala / piatti / CSV)
const DISH_CATEGORIES = {
  antipasti:      { label: "Antipasti",       color: "#8B5E3C", order: 1 },
  primi:          { label: "Primi",           color: "#C47B3B", order: 2 },
  secondi:        { label: "Secondi",         color: "#B5472A", order: 3 },
  contorni:       { label: "Contorni",        color: "#6C7A52", order: 4 },
  dolci:          { label: "Dolci",           color: "#AA7DA0", order: 5 },
  vini:           { label: "Vini",            color: "#5D2E46", order: 6 },
  bar:            { label: "Bar",             color: "#1F4D48", order: 7 },
  caffetteria:    { label: "Caffetteria",     color: "#3D2B1F", order: 8 },
  menu_speciali:  { label: "Menu speciali",   color: "#2D5F3F", order: 9 },
};
const DISH_CAT_KEYS = Object.keys(DISH_CATEGORIES);
const dishCatLabel = (k) => DISH_CATEGORIES[k]?.label || k;

// Mappa reparto/categoria gestionale Zucchetti TCPOS → categoria sistema interna.
// Usata da: ListinoImporter (~16840), ZucchettiImporter (item._csvReparto), engine lineCat.
// Chiavi UPPERCASE (TCPOS esporta sempre uppercase: ANTIPASTI, PRIMI PESCE, ecc.).
// Mantenere allineata con repartoToCat di ListinoImporter — questa è la SOURCE OF TRUTH.
const REPARTO_TO_CAT = {
  // Antipasti
  "ANTIPASTI": "antipasti", "ANTIPASTO": "antipasti", "STARTERS": "antipasti",
  // Primi
  "PRIMI PESCE": "primi", "PRIMI CARNE": "primi", "PRIM": "primi", "PRIMI": "primi",
  "PRIMO": "primi", "PASTA": "primi", "RISOTTI": "primi", "ZUPPE": "primi",
  // Secondi
  "SECONDI PESCE": "secondi", "SECONDI CARNE": "secondi", "SECONDI": "secondi",
  "SECONDO": "secondi", "PESCE": "secondi", "CARNE": "secondi",
  // Contorni
  "CONTORNI": "contorni", "CONTORNO": "contorni", "SIDES": "contorni",
  // Dolci / dessert
  "DESSERT": "dolci", "DOLCI": "dolci", "DOLCE": "dolci", "DESSERTS": "dolci",
  // Vini (mappa estesa: il TCPOS spesso suddivide per tipo)
  "VINI": "vini", "VINO": "vini", "VINI ROSSI": "vini", "VINI BIANCHI": "vini",
  "VINI ROSATI": "vini", "VINI ROSE": "vini", "VINO ROSSO": "vini", "VINO BIANCO": "vini",
  "VINO ROSE": "vini", "BOLLICINE": "vini", "SPUMANTI": "vini", "CHAMPAGNE": "vini",
  "PROSECCO": "vini", "VINI BOTTIGLIA": "vini", "VINI CALICE": "vini",
  "VINI AL CALICE": "vini", "CARTA VINI": "vini",
  // Bar / bevande / aperitivi / amari
  "BEVANDE": "bar", "BEVANDA": "bar", "BIBITE": "bar", "BIBITE ANALCOLICHE": "bar",
  "ANALCOLICI": "bar", "ALCOLICI": "bar", "APERITIVO": "bar", "APERITIVI": "bar",
  "BAR": "bar", "APE": "bar", "BIRRE": "bar", "BIRRA": "bar",
  "DIGESTIVI": "bar", "AMARI": "bar", "DISTILLATI": "bar", "LIQUORI": "bar",
  "COCKTAIL": "bar", "DRINK": "bar", "ACQUA": "bar", "SUCCHI": "bar",
  // Caffetteria
  "CAFFETTERIA": "caffetteria", "CAFFE": "caffetteria", "CAFFETTERIA E TE": "caffetteria",
  "INFUSI": "caffetteria", "TISANE": "caffetteria", "TE": "caffetteria",
  // Menu speciali / eventi / banqueting
  "EVENTI": "menu_speciali", "MENU": "menu_speciali", "MENU DEGUSTAZIONE": "menu_speciali",
  "MENU CONCORDATO": "menu_speciali", "BANQUETING": "menu_speciali",
  "CATERING": "menu_speciali", "EVENTO": "menu_speciali",
};
// Helper: deriva categoria sistema da reparto CSV (string) — case-insensitive, trim.
// Restituisce null se il reparto non è mappato (NON "altro": permette al chiamante
// di fare fallback su altre fonti tipo dishCatById).
// Naming distinto dalla const locale `repartoToCat` di ListinoImporter (~16840).
const catFromReparto = (rep) => {
  if (!rep) return null;
  const k = String(rep).trim().toUpperCase();
  return REPARTO_TO_CAT[k] || null;
};

// Articoli "regalo" — non sono vendite ma cortesie offerte al cliente.
// Al parsing degli scontrini il loro unitPrice viene forzato a 0:
// l'item appare comunque nello scontrino (per consumo MP e tracciabilità)
// ma non contribuisce all'incasso. Match case-insensitive su sottostringa.
// Per aggiungere un nuovo articolo regalo basta inserirlo in questa lista
// (es. una bottiglia particolare che Andi offre).
const GIFT_ITEMS = new Set([
  "SESCIALA",     // bottiglia di Sesciala regalata
]);
const isGiftItem = (name) => {
  if (!name) return false;
  const n = String(name).toUpperCase();
  for (const g of GIFT_ITEMS) {
    if (n.includes(g)) return true;
  }
  return false;
};

// "Sconto a Valore" / sconti riga del TCPOS: hanno unitPrice < 0 e
// descrizione tipica "Sconto a Valore", "Sconto", "Storno". Match per identificarli
// nelle aggregazioni (es. calcolo sconto% globale: vanno SOTTRATTI dal listino).
const isDiscountLine = (item) => {
  if (!item) return false;
  if (typeof item.unitPrice === "number" && item.unitPrice < 0) return true;
  const n = String(item.name || item._csvName || "").toLowerCase();
  return /sconto\s*a?\s*valore|^sconto\b|^storno/i.test(n);
};

// Identifica una riga "MENU CONCORDATO" — usata sia per fondere scontrini
// frammentati (TCPOS emette N scontrini per N MENU CONCORDATO + 1 con gli articoli)
// sia per il calcolo sconto% (gli articoli a 0 del menu valgono al listino).
const isMenuConcordato = (item) => {
  if (!item) return false;
  const n = String(item.name || item._csvName || "").toUpperCase();
  return n.includes("MENU CONCORDATO");
};

// Fasce orarie servizio
// Fasce di servizio Bottega del Mare (allargate 2026-06-05 su richiesta Andi):
//  - Pranzo 09:00–18:00 (include i banco del mattino presto: caffè/acqua pre-servizio)
//  - Cena   18:00–04:00 (wrap-around: scontrini chiusi tra 00:00 e 03:59 = cena del giorno prima)
//  Fuori turno residuo = 04:00–08:59.
const SERVICE_SHIFTS = {
  pranzo: { label: "Pranzo", from: 9, to: 18, icon: "☀" },
  cena:   { label: "Cena",   from: 18, to: 28, icon: "🌙" },  // to=28 = 04:00 wrap-around
};
// Configurazione orari turno — SOVRASCRIVIBILE da settings.serviceShift (UI: Impostazioni).
//  - pranzoFrom : ora di inizio pranzo (default 9)
//  - cenaFrom   : ORA DI TAGLIO pranzo→cena (default 18) — questo è il valore che Andi
//                 può cambiare per spostare i tavoli a cavallo nel turno giusto.
//  - nightEnd   : ora entro cui il post-mezzanotte conta come cena del giorno PRIMA (default 4).
// I default riproducono ESATTAMENTE il comportamento storico (pranzo 9–18, cena 18–04).
let SHIFT_CONFIG = { pranzoFrom: 9, cenaFrom: 18, nightEnd: 4 };
const setShiftConfig = (cfg) => {
  if (!cfg) return;
  const pf = Number(cfg.pranzoFrom), cf = Number(cfg.cenaFrom), ne = Number(cfg.nightEnd);
  SHIFT_CONFIG = {
    pranzoFrom: Number.isFinite(pf) ? pf : 9,
    cenaFrom:   Number.isFinite(cf) ? cf : 18,
    nightEnd:   Number.isFinite(ne) ? ne : 4,
  };
};
const getShift = (dateIsoStr) => {
  if (!dateIsoStr) return null;
  const h = new Date(dateIsoStr).getHours();
  const { pranzoFrom, cenaFrom, nightEnd } = SHIFT_CONFIG;
  // Cena post-mezzanotte: ore 0..nightEnd → cena del servizio precedente
  if (h >= 0 && h < nightEnd) return "cena";
  if (h >= pranzoFrom && h < cenaFrom) return "pranzo";
  if (h >= cenaFrom) return "cena";
  return null;  // fuori servizio (es. tra nightEnd e pranzoFrom)
};
// Data "di servizio" per uno scontrino: gli scontrini chiusi tra 00:00 e nightEnd
// appartengono al servizio cena del GIORNO PRIMA (per calendario report/analytics).
const getServiceDate = (dateIsoStr) => {
  if (!dateIsoStr) return null;
  const d = new Date(dateIsoStr);
  const h = d.getHours();
  if (h >= 0 && h < SHIFT_CONFIG.nightEnd) {
    d.setDate(d.getDate() - 1);
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
// AUDIT-FIX (integrità incassi, ispettore #3): UNICA verità sul se uno
// scontrino conta nell'incasso. Un ESTRATTO CONTO riconciliato a uno scontrino
// fiscale (SF) è già dentro quel SF → NON va sommato (doppio conteggio). Un EC
// "NERO_DEFINITIVO" è incasso reale non duplicato → conta. Un EC orfano (senza
// _regularizedTo) conta (è incasso in sospeso, non ancora duplicato). Prima
// questa regola viveva solo in todayRevenue e _rgDashData: split pranzo/cena,
// confronto vs ieri e card riconciliazione la ignoravano → numeri che non
// quadravano sulla stessa schermata. Un helper solo, applicato ovunque.
const _receiptCountsForRevenue = (r) => {
  const isEC = !!(r && (r._isPreconto || r._docType === "ESTRATTO CONTO"));
  return !(isEC && r._regularizedTo && r._regularizedTo !== "NERO_DEFINITIVO");
};
// Data offset giorni ISO
const daysAgoISO = (offset) => {
  const d = new Date(TODAY + "T12:00:00");
  d.setDate(d.getDate() - offset);
  return d.toISOString().slice(0, 10);
};

const SEED = {
  meta: { version: 3, lastUpdate: nowIso() },
  lastBackupAt: null,  // ISO timestamp dell'ultimo backup JSON esportato da Andi
  suppliers: [
    { id: "s1", name: "Ittica Marchigiana S.r.l.", category: "pesce", vat: "IT02456781003", phone: "+39 071 123456", email: "ordini@itticamarchigiana.it", address: "Via del Porto 14, Ancona", notes: "Consegna mar/gio/sab mattino", paymentTerms: 30 },
    { id: "s2", name: "Pescheria Adriatica", category: "pesce", vat: "IT03217849005", phone: "+39 071 987654", email: "sara@pescheriadriatica.it", address: "Piazza Mercato 3, Ancona", notes: "Pesce locale giornaliero", paymentTerms: 30 },
    { id: "s3", name: "Molino Rosso Osimo", category: "farine", vat: "IT04312847002", phone: "+39 071 555888", email: "info@molinorosso.it", address: "Str. Jesina 88, Osimo", notes: "Farine bio, min ord. 50kg", paymentTerms: 60 },
    { id: "s4", name: "Az. Agricola Colli", category: "uova", vat: "IT05673421001", phone: "+39 071 222333", email: "colli@azagrcolli.it", address: "Contrada Castelfidardo, Recanati", notes: "Uova bio, pollame", paymentTerms: 30 },
    { id: "s5", name: "Frantoio Grossi", category: "olio", vat: "IT06541209006", phone: "+39 071 444777", email: "frantoiogrossi@pec.it", address: "Via Flaminia 12, Filottrano", notes: "Olio EVO Raggia DOP", paymentTerms: 60 },
    { id: "s6", name: "Verdure dei Colli", category: "verdure", vat: "IT07820154003", phone: "+39 071 889900", email: "ortocolli@gmail.com", address: "Via delle Selve 4, Polverigi", notes: "Stagionali km 0", paymentTerms: 15 },
  ],
  items: [],
  purchaseOrders: [],
  receipts: [],
  invoices: [],
  lots: [],
  lotMovements: [],
  workOrders: [],
  plannedOrders: [],
  dishes: [],
  kitchenOutputs: [],
  kitchenRequests: [],
  // Audit log immutabile (append-only) — civilistico-fiscale art. 2220 c.c., DPR 633/72
  auditLog: [],
  baseProducts: [
    { id: "bp_1", nome: "CANNELLO 7 euro", display: "Cannello", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 7.4, pReale: 8.2222, calo1: 0.1, calo2: 0, caloTot: 0.1, t1: 10.0, t2: 0, tTot: 10.0, desc1: "pulire e stendere", add: "", tGas: 0.0, tForno: 0 },
    { id: "bp_2", nome: "COZZA", display: "Cozza", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["crostacei", "molluschi"], preferredSupplier: "s1", pFatt: 4.1, pReale: 4.334, calo1: 0.054, calo2: 0, caloTot: 0.054, t1: 2.7, t2: 0, tTot: 2.7, desc1: "pulire togliere scarti", add: "", tGas: 0.0, tForno: 0.0 },
    { id: "bp_3", nome: "VONGOLA", display: "Vongola", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["crostacei", "molluschi"], preferredSupplier: "s1", pFatt: 5.0, pReale: 5.102, calo1: 0.02, calo2: 0, caloTot: 0.02, t1: 1.0, t2: 0, tTot: 1.0, desc1: "pulire", add: "50 GR SALE", tGas: 4.0, tForno: 0.0 },
    { id: "bp_4", nome: "CALAMARO Ogni stecchino pesa 69,7 grammi di calamaro   (4,7 ", display: "Calamaro Ogni Stecchino Pesa", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 5.97, pReale: 7.5379, calo1: 0.208, calo2: 0, caloTot: 0.208, t1: 9.6, t2: 0, tTot: 9.6, desc1: "solo pulizia calamaro", add: "", tGas: 0.0, tForno: 0.0 },
    { id: "bp_5", nome: "PESCE PER BRODETTO 8,5   POI L HA MESSO 9,5   (RAZZA 6 POI 5", display: "Pesce Per Brodetto", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 8.5, pReale: 10.3925, calo1: 0.1821, calo2: 0, caloTot: 0.1821, t1: 4.4776, t2: 0, tTot: 4.4776, desc1: "pulito steso", add: "", tGas: 0.0, tForno: 0.0 },
    { id: "bp_6", nome: "PESCE PER FRITTURA SPINE PREX MIN 8,5", display: "Pesce Per Frittura Spine Prex Min", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 8.5, pReale: 9.5078, calo1: 0.106, calo2: 0, caloTot: 0.106, t1: 5.0, t2: 0, tTot: 5.0, desc1: "pulito e steso", add: "", tGas: 0.0, tForno: 0.0 },
    { id: "bp_7", nome: "SARDONCINI PREX PIU BASSO 4,8 EURO    5,5", display: "Sardoncini Prex Piu Basso", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 6.0, pReale: 8.6193, calo1: 0.2472, calo2: 0.0753, caloTot: 0.3039, t1: 3.25, t2: 13.0, tTot: 16.25, desc1: "pulite teste", add: "", tGas: 0.0, tForno: 0.0 },
    { id: "bp_8", nome: "SALMONE 8,5", display: "Salmone", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 8.5, pReale: 14.701, calo1: 0.3102, calo2: 0.1618, caloTot: 0.4218, t1: 2.32, t2: 6.2, tTot: 8.52, desc1: "togliere testa e mettere sale", add: "0.51 KG ZUCCHERO 0.51 SALE", tGas: 0.0, tForno: 0.0 },
    { id: "bp_9", nome: "SEPPIA INDIA TORRY U1 (ART 087121)  PENTOLA", display: "Seppia India Torry U1", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 8.3, pReale: 14.4293, calo1: 0.4175, calo2: 0.0125, caloTot: 0.4248, t1: 0.5, t2: 2.5, tTot: 3.0, desc1: "pulita la seppia bollita", add: "", tGas: 7.5, tForno: 0 },
    { id: "bp_10", nome: "SEPPIA INDIA TORRY U1 (ART 087121)  FORNO", display: "Seppia India Torry U1", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 8.9, pReale: 14.5365, calo1: 0.38, calo2: 0.0125, caloTot: 0.3877, t1: 0.5, t2: 2.5, tTot: 3.0, desc1: "pulita la seppia bollita", add: "", tGas: 0, tForno: 7.5 },
    { id: "bp_11", nome: "POLPO 300 350  PENTOLA", display: "Polpo", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 9.99, pReale: 27.9567, calo1: 0.6318, calo2: 0.0295, caloTot: 0.6427, t1: 2.77, t2: 1.33, tTot: 4.1, desc1: "pulito il polpo", add: "", tGas: 6.6667, tForno: 0 },
    { id: "bp_12", nome: "POLPO 1000  1200  FORNO", display: "Polpo", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 12.9, pReale: 32.6086, calo1: 0.5778, calo2: 0.063, caloTot: 0.6044, t1: 2.77, t2: 1.33, tTot: 4.1, desc1: "pulito il polpo", add: "", tGas: 0, tForno: 6.6667 },
    { id: "bp_13", nome: "GAMBERO 36/40 (12,82 a gambero peso)", display: "Gambero", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["crostacei", "molluschi"], preferredSupplier: "s1", pFatt: 7.37, pReale: 9.4125, calo1: 0.217, calo2: 0.0, caloTot: 0.217, t1: 7.5, t2: 0.0, tTot: 7.5, desc1: "togliere i carapaci", add: "", tGas: 0.0, tForno: 0.0 },
    { id: "bp_14", nome: "ACCIUGHE", display: "Acciughe", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 31.11, pReale: 31.11, calo1: 0.0, calo2: 0.0, caloTot: 0.0, t1: 0.0, t2: 0, tTot: 0.0, desc1: "", add: "ogni filetto pesa 6,71 gr uguale 134 filetti", tGas: 0.0, tForno: 0.0 },
    { id: "bp_15", nome: "RICCIO", display: "Riccio", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["crostacei", "molluschi"], preferredSupplier: "s1", pFatt: 139.0, pReale: 139.0, calo1: 0, calo2: 0, caloTot: 0.0, t1: 0, t2: 0, tTot: 0.0, desc1: "", add: "", tGas: 0.0, tForno: 0.0 },
    { id: "bp_16", nome: "BOTARGA", display: "Botarga", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 79.0, pReale: 79.0, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0.0, desc1: "", add: "", tGas: 0.0, tForno: 0.0 },
    { id: "bp_17", nome: "SCAMPI 31 / 40", display: "Scampi", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["crostacei", "molluschi"], preferredSupplier: "s1", pFatt: 9.9, pReale: 9.9, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0.0, desc1: "", add: "", tGas: 0.0, tForno: 0.0 },
    { id: "bp_18", nome: "GRANCHIO BLU", display: "Granchio Blu", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["crostacei", "molluschi"], preferredSupplier: "s1", pFatt: 5.7, pReale: 5.7, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0.0, desc1: "", add: "ogni porzione di riso o goluppa o scoglio ci va 0,021 kg", tGas: 0.0, tForno: 0.0 },
    { id: "bp_19", nome: "SEPPIA 8 12 ogni seppia di numero pesa  102,56 gr su fattura", display: "Seppia", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 8.7, pReale: 8.7, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0.0, desc1: "", add: "ogni seppia di numero pesa  102,56 gr su fattura", tGas: 0.0, tForno: 0.0 },
    { id: "bp_20", nome: "CODA ROSPO", display: "Coda Rospo", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 11.7, pReale: 12.4046, calo1: 0.0568, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0.0, desc1: "", add: "", tGas: 10.514, tForno: 2.5312 },
    { id: "bp_21", nome: "MAZZANCOLLE", display: "Mazzancolle", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["crostacei", "molluschi"], preferredSupplier: "s1", pFatt: 8.9, pReale: 8.9, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0.0, desc1: "", add: "", tGas: 0.0, tForno: 0.0 },
    { id: "bp_22", nome: "RAGUSA", display: "Ragusa", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["crostacei", "molluschi"], preferredSupplier: "s1", pFatt: 6.0, pReale: 6.1856, calo1: 0.03, calo2: 0, caloTot: 0, t1: 1.2821, t2: 4.6154, tTot: 5.8974, desc1: "PER LAVARE", add: "", tGas: 0, tForno: 0 },
    { id: "bp_23", nome: "RICCIOLA", display: "Ricciola", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["crostacei", "molluschi"], preferredSupplier: "s1", pFatt: 11.5, pReale: 11.5, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0.5587, t2: 0, tTot: 0.5587, desc1: "spezzato e  pulire spine", add: "", tGas: 0, tForno: 0 },
    { id: "bp_24", nome: "CAPASANTA SGUSCIATA 8/12 (35 PEZZI IN UN KG)", display: "Capasanta Sgusciata", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["crostacei", "molluschi"], preferredSupplier: "s1", pFatt: 23.5, pReale: 23.5, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0.0, t2: 0.0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_25", nome: "CAPASANTA SFUSA CON GUSCIO (46 PEZZI 4,750 GR)", display: "Capasanta Sfusa Con Guscio", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["crostacei", "molluschi"], preferredSupplier: "s1", pFatt: 11.7, pReale: 11.7, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_26", nome: "BACCALA 16  POI MESSO 16,50", display: "Baccala", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 16.5, pReale: 16.5, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_27", nome: "CALAMARO PER FRITTURA O SUGO", display: "Calamaro Per Frittura O Sugo", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 5.97, pReale: 7.5379, calo1: 0.208, calo2: 0, caloTot: 0, t1: 7.2, t2: 0, tTot: 7.2, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_28", nome: "POLLO", display: "Pollo", cat: "carne", unit: "kg", minStock: 2, shelfLifeDays: 4, allergens: [], preferredSupplier: "s4", pFatt: 8.9, pReale: 8.9, calo1: 0.0, calo2: 0, caloTot: 0, t1: 4.6, t2: 0, tTot: 4.6, desc1: "preparazione cotoletta", add: "", tGas: 0, tForno: 0 },
    { id: "bp_29", nome: "POMODORINO", display: "Pomodorino", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 3.8, pReale: 3.9167, calo1: 0.0298, calo2: 0, caloTot: 0.0298, t1: 1.166, t2: 2.43, tTot: 3.596, desc1: "togliere verde", add: "", tGas: 0, tForno: 0 },
    { id: "bp_30", nome: "AGLIO", display: "Aglio", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 6.0, pReale: 6.6667, calo1: 0.1, calo2: 0.0, caloTot: 0.1, t1: 20.0, t2: 2.0, tTot: 22.0, desc1: "per pulire", add: "X", tGas: 0, tForno: 0 },
    { id: "bp_31", nome: "CIPOLLA DORATA", display: "Cipolla Dorata", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 1.95, pReale: 2.2842, calo1: 0.1463, calo2: 0, caloTot: 0.1463, t1: 2.0, t2: 1.0, tTot: 3.0, desc1: "per pulire", add: "", tGas: 0, tForno: 0 },
    { id: "bp_32", nome: "PATATE per julienne", display: "Patate Per Julienne", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 1.8, pReale: 2.3256, calo1: 0.226, calo2: 0, caloTot: 0.226, t1: 2.0, t2: 2.0, tTot: 4.0, desc1: "per capare", add: "", tGas: 0, tForno: 0 },
    { id: "bp_33", nome: "PATATE ARROSTO PEX 1,50?", display: "Patate Arrosto Pex", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 1.8, pReale: 2.3256, calo1: 0.226, calo2: 0, caloTot: 0.226, t1: 2.0, t2: 2.0, tTot: 4.0, desc1: "per capare", add: "", tGas: 0, tForno: 0 },
    { id: "bp_34", nome: "CAROTE", display: "Carote", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 1.65, pReale: 1.9231, calo1: 0.142, calo2: 0, caloTot: 0.142, t1: 2.0, t2: 2.0, tTot: 4.0, desc1: "per capare", add: "", tGas: 0, tForno: 0 },
    { id: "bp_35", nome: "ZUCCHINE julienne", display: "Zucchine Julienne", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 2.5, pReale: 2.7115, calo1: 0.078, calo2: 0, caloTot: 0.078, t1: 0.0, t2: 4.0, tTot: 4.0, desc1: "NON SI CAPANO", add: "", tGas: 0, tForno: 0 },
    { id: "bp_36", nome: "ZUCCHINE al graten", display: "Zucchine Al Graten", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 2.5, pReale: 2.7174, calo1: 0.08, calo2: 0, caloTot: 0.08, t1: 9.5, t2: 0, tTot: 9.5, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_37", nome: "ZUCCHINE AL GRIGLIA", display: "Zucchine Al Griglia", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 2.5, pReale: 2.6042, calo1: 0.04, calo2: 0, caloTot: 0.04, t1: 4.0, t2: 0, tTot: 4.0, desc1: "per tagliare con affettatrice", add: "", tGas: 1.0, tForno: 0 },
    { id: "bp_38", nome: "PREZZEMOLO finale un grammo", display: "Prezzemolo Finale Un Grammo", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 3.5, pReale: 5.2505, calo1: 0.3334, calo2: 0, caloTot: 0.3334, t1: 20.0, t2: 0, tTot: 20.0, desc1: "tagliare un kg di prezzemolo", add: "", tGas: 0, tForno: 0 },
    { id: "bp_39", nome: "MELANZANE PER VERDURE GRIGLIATE", display: "Melanzane Per Verdure Grigliate", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 3.5, pReale: 3.8889, calo1: 0.1, calo2: 0, caloTot: 0.1, t1: 4.0, t2: 0, tTot: 4.0, desc1: "per tagliare con affettatrice", add: "", tGas: 1.0, tForno: 0 },
    { id: "bp_40", nome: "MELANZANE PER GRATEN", display: "Melanzane Per Graten", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 3.5, pReale: 5.0, calo1: 0.3, calo2: 0, caloTot: 0.3, t1: 9.5, t2: 0, tTot: 9.5, desc1: "per fare graten", add: "", tGas: 0, tForno: 0 },
    { id: "bp_41", nome: "POMODORO GRATINATO", display: "Pomodoro Gratinato", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 3.3, pReale: 3.5792, calo1: 0.078, calo2: 0, caloTot: 0.078, t1: 9.5, t2: 0, tTot: 9.5, desc1: "per fare graten", add: "", tGas: 0, tForno: 0 },
    { id: "bp_42", nome: "CANNELLINI", display: "Cannellini", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 3.11, pReale: 3.5797, calo1: 0.1312, calo2: 0, caloTot: 0.1312, t1: 0.4167, t2: 0, tTot: 0.4167, desc1: "solo per aprire barattolo e scolare", add: "", tGas: 0, tForno: 0 },
    { id: "bp_43", nome: "CECI", display: "Ceci", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 1.85, pReale: 3.4906, calo1: 0.0, calo2: 0, caloTot: 0.0, t1: 1.639, t2: 0, tTot: 1.639, desc1: "per aprire il barattolo e sgocciolare", add: "", tGas: 0, tForno: 0 },
    { id: "bp_44", nome: "CAPPERI", display: "Capperi", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 8.8, pReale: 15.1203, calo1: 0.418, calo2: 0, caloTot: 0.418, t1: 1.6667, t2: 0, tTot: 1.6667, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_45", nome: "FAGIOLINI VERDI", display: "Fagiolini Verdi", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 3.89, pReale: 4.084, calo1: 0.0475, calo2: 0, caloTot: 0.0475, t1: 0.0, t2: 0, tTot: 0.0, desc1: "non c 'e lavorazione", add: "", tGas: 0, tForno: 0 },
    { id: "bp_46", nome: "LIMONE SUCCO", display: "Limone Succo", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 2.5, pReale: 5.5556, calo1: 0.55, calo2: 0, caloTot: 0.55, t1: 5.0, t2: 0, tTot: 5.0, desc1: "per fare succo", add: "", tGas: 0, tForno: 0 },
    { id: "bp_47", nome: "ARANCIA SUCCO", display: "Arancia Succo", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 2.5, pReale: 5.8824, calo1: 0.575, calo2: 0, caloTot: 0.575, t1: 5.0, t2: 0, tTot: 5.0, desc1: "per fare succo", add: "", tGas: 0, tForno: 0 },
    { id: "bp_48", nome: "LIMONE   AL KG", display: "Limone   Al Kg", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 2.5, pReale: 2.5, calo1: 0.0, calo2: 0, caloTot: 0.0, t1: 0, t2: 0, tTot: 0, desc1: "per fare un kg di fette", add: "", tGas: 0, tForno: 0 },
    { id: "bp_49", nome: "RADICCHIO 1/2 foglia per burrata pesa 10 g", display: "Radicchio", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: ["latte"], preferredSupplier: "s6", pFatt: 3.5, pReale: 3.5, calo1: 0.0, calo2: 0, caloTot: 0.0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_50", nome: "INSALATA julienne    guarnizioni piatti 6 gr", display: "Insalata Julienne    Guarnizioni Piatti", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 2.5, pReale: 3.3784, calo1: 0.26, calo2: 0, caloTot: 0, t1: 3.0, t2: 0.73, tTot: 0, desc1: "per lavare", add: "", tGas: 0, tForno: 0 },
    { id: "bp_51", nome: "INSALATA VERDE", display: "Insalata Verde", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 2.5, pReale: 3.3784, calo1: 0.26, calo2: 0, caloTot: 0, t1: 3.0, t2: 0.73, tTot: 0, desc1: "per lavare", add: "", tGas: 0, tForno: 0 },
    { id: "bp_52", nome: "PISELLI", display: "Piselli", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 4.35, pReale: 4.35, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_53", nome: "CIPOLLA ROSSA", display: "Cipolla Rossa", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 2.5, pReale: 2.7394, calo1: 0.0874, calo2: 0, caloTot: 0, t1: 4.0769, t2: 0, tTot: 0, desc1: "per pulire e tagliare", add: "ogni kg di cipolla aggiungo 231 gr di zucchero  23 ml al kg ", tGas: 0, tForno: 0 },
    { id: "bp_54", nome: "SEMI DI FINOCCHIO", display: "Semi Di Finocchio", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 8.9, pReale: 8.9, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_55", nome: "SALICORNIA", display: "Salicornia", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 14.5, pReale: 14.5, calo1: 0.0, calo2: 0, caloTot: 0, t1: 5.0, t2: 0, tTot: 0, desc1: "frullare", add: "", tGas: 0, tForno: 0 },
    { id: "bp_56", nome: "PACASASSI", display: "Pacasassi", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 20.0, pReale: 20.0, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_57", nome: "CARCIOFI", display: "Carciofi", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 8.8, pReale: 8.8, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_58", nome: "RUCOLA ogni foglia pesa 0,00065 pulita", display: "Rucola Ogni Foglia Pesa", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 12.96, pReale: 18.4091, calo1: 0.296, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_59", nome: "ARANCIA  PER FETTINE", display: "Arancia  Per Fettine", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 2.5, pReale: 2.5, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_60", nome: "OLIVE 3,1 SGOC 1.8   (CONSIDERO SGOCCIOLATA)", display: "Olive", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 6.6056, pReale: 6.6056, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_61", nome: "POMODORINO VALFRUTTA  2500 SGONC  1500", display: "Pomodorino Valfrutta", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 1.996, pReale: 3.3267, calo1: 0.4, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_62", nome: "PELATI  2500 1500  4,85 euro", display: "Pelati", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 1.94, pReale: 3.2333, calo1: 0.4, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_63", nome: "GUANCIALE", display: "Guanciale", cat: "carne", unit: "kg", minStock: 2, shelfLifeDays: 4, allergens: [], preferredSupplier: "s4", pFatt: 12.5, pReale: 13.765, calo1: 0.0919, calo2: 0, caloTot: 0, t1: 10.0, t2: 0, tTot: 0, desc1: "per tagliare guanciale", add: "", tGas: 0, tForno: 0 },
    { id: "bp_64", nome: "PARMIGIANO", display: "Parmigiano", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: ["latte"], preferredSupplier: "s5", pFatt: 15.9, pReale: 15.9, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_65", nome: "OLIO EVO in uscita sono 2,4 gr", display: "Olio Evo In Uscita Sono", cat: "dispensa", unit: "l", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 12.0, pReale: 12.0, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_66", nome: "OLIO MISTO", display: "Olio Misto", cat: "dispensa", unit: "l", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 4.643, pReale: 4.643, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_67", nome: "SALE", display: "Sale", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 0.4075, pReale: 0.4075, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_68", nome: "VINO", display: "Vino", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 1.1, pReale: 1.1, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_69", nome: "BURRO", display: "Burro", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: ["latte"], preferredSupplier: "s5", pFatt: 8.5, pReale: 8.5, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_70", nome: "YOGURT", display: "Yogurt", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: ["latte"], preferredSupplier: "s5", pFatt: 2.38, pReale: 2.38, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_71", nome: "FRUTTI BOSCO", display: "Frutti Bosco", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 5.99, pReale: 5.99, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "zucchero di canna 200 gr    15 ml di limone", tGas: 0, tForno: 0 },
    { id: "bp_72", nome: "FRAGOLE", display: "Fragole", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 6.8, pReale: 6.8, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "zucchero di canna 250 gr    13 ml di limone", tGas: 0, tForno: 0 },
    { id: "bp_73", nome: "PAN GRATTATO  PORZ AD ARROSTO 25, 54GR il prezzo ha un calo ", display: "Pan Grattato  Porz Ad Arrosto", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 6.424, pReale: 6.424, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_74", nome: "ACETO DI VINO BIANCO", display: "Aceto Di Vino Bianco", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 0.92, pReale: 0.92, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_75", nome: "ACETO BALSAMICO PER CIPOLLA", display: "Aceto Balsamico Per Cipolla", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 3.3, pReale: 3.3, calo1: 0.0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_76", nome: "GLASSA DI ACETO PER POLPO", display: "Glassa Di Aceto Per Polpo", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["pesce"], preferredSupplier: "s1", pFatt: 15.6, pReale: 15.6, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_77", nome: "LATTE", display: "Latte", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: ["latte"], preferredSupplier: "s5", pFatt: 1.0, pReale: 1.0, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_78", nome: "UOVA", display: "Uova", cat: "dispensa", unit: "pz", minStock: 5, shelfLifeDays: 180, allergens: ["uova"], preferredSupplier: "s5", pFatt: 0.2333, pReale: 0.2333, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_79", nome: "CIOCCOLATO   FONDENTE", display: "Cioccolato   Fondente", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 12.8, pReale: 12.8, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_80", nome: "MANDORLE", display: "Mandorle", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 12.8, pReale: 12.8, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_81", nome: "FARINA PER DOLCI", display: "Farina Per Dolci", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: ["glutine"], preferredSupplier: "s5", pFatt: 1.196, pReale: 1.196, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_82", nome: "FARINA DI GRANITO", display: "Farina Di Granito", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: ["glutine"], preferredSupplier: "s5", pFatt: 1.7463, pReale: 1.7463, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_83", nome: "ZUCCHERO", display: "Zucchero", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 1.59, pReale: 1.59, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_84", nome: "MASCARPONE", display: "Mascarpone", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: ["latte"], preferredSupplier: "s5", pFatt: 8.044, pReale: 8.044, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_85", nome: "PAVESINI (OGNI PAVESINO PESA 2,27 GR", display: "Pavesini", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 17.25, pReale: 17.25, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_86", nome: "GRANELLA DI NOCCIOLA", display: "Granella Di Nocciola", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 18.7, pReale: 18.7, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_87", nome: "POLENTA", display: "Polenta", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 4.6533, pReale: 4.6533, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_88", nome: "ZAFFERANO (100 PEZZI DA 0,08 GR)", display: "Zafferano", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 7000.0, pReale: 7000.0, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_89", nome: "ZUCCHERO DI CANNA", display: "Zucchero Di Canna", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 2.25, pReale: 2.25, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_90", nome: "BURRATA IN UN KG 8", display: "Burrata In Un Kg", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: ["latte"], preferredSupplier: "s5", pFatt: 6.8, pReale: 6.8, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_91", nome: "VINO", display: "Vino", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 1.1, pReale: 1.1, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_92", nome: "miscela aglio 4 gr per  primo  (100 ml 60 gr aglio)(prod che", display: "Miscela Aglio", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 5.731, pReale: 5.731, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_93", nome: "OLIO GIRASOLE", display: "Olio Girasole", cat: "dispensa", unit: "l", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 1.49, pReale: 1.49, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_94", nome: "CROSTINO", display: "Crostino", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 0.128, pReale: 0.128, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_95", nome: "CITRONETTE 0,9090 OLIO       0,09090 LIMONE", display: "Citronette", cat: "verdura", unit: "l", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 4.7255, pReale: 4.7255, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_96", nome: "FETTA DI LIMONE 25,33 GR", display: "Fetta Di Limone", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 0.0633, pReale: 0.0633, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_97", nome: "SUGO SCAMPI IN UN BARATTOLO CAPPERI", display: "Sugo Scampi In Un Barattolo Capperi", cat: "pesce", unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: ["crostacei", "molluschi"], preferredSupplier: "s1", pFatt: 4.0789, pReale: 4.0789, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_98", nome: "TAGLIOLINI", display: "Tagliolini", cat: "pasta", unit: "kg", minStock: 10, shelfLifeDays: 365, allergens: ["glutine"], preferredSupplier: "s3", pFatt: 5.5, pReale: 5.5, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_99", nome: "SCIALATIELLI", display: "Scialatielli", cat: "pasta", unit: "kg", minStock: 10, shelfLifeDays: 365, allergens: ["glutine"], preferredSupplier: "s3", pFatt: 5.25, pReale: 5.25, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_100", nome: "SPAGO MOLISANA", display: "Spago Molisana", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 1.7877, pReale: 1.7877, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_101", nome: "SPAGO FELICIETTI", display: "Spago Felicietti", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 2.5879, pReale: 2.5879, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_102", nome: "RISO", display: "Riso", cat: "pasta", unit: "kg", minStock: 10, shelfLifeDays: 365, allergens: ["glutine"], preferredSupplier: "s3", pFatt: 6.0, pReale: 6.0, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_103", nome: "PASTA LASAGNE", display: "Pasta Lasagne", cat: "pasta", unit: "kg", minStock: 10, shelfLifeDays: 365, allergens: ["glutine"], preferredSupplier: "s3", pFatt: 2.75, pReale: 2.75, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_104", nome: "costo a cestino in fondo al mar", display: "Costo A Cestino In Fondo Al Mar", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 0.4855, pReale: 0.4855, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_105", nome: "salza saziki", display: "Salza Saziki", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 4.592, pReale: 4.592, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_106", nome: "MAIONESE", display: "Maionese", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 12.0, pReale: 12.0, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_107", nome: "alici del cantabrico una porzione 48 gr", display: "Alici Del Cantabrico Una Porzione", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 62.08, pReale: 62.08, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_108", nome: "maracuja ANNO SCORSO 9,25", display: "Maracuja Anno Scorso", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 9.9836, pReale: 9.9836, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_109", nome: "patatine san carlo su un sacchetto di 1,70  n 3 porzioni", display: "Patatine San Carlo Su Un Sacchetto Di", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 13.4706, pReale: 13.4706, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_110", nome: "cetrioli", display: "Cetrioli", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 4.5, pReale: 5.3514, calo1: 0.1591, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_111", nome: "pinoli", display: "Pinoli", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 89.0, pReale: 89.0, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_112", nome: "OLIO IN BOTTIGLIA DA TAVOLO", display: "Olio In Bottiglia Da Tavolo", cat: "dispensa", unit: "l", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 17.6, pReale: 17.6, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_113", nome: "PEPERONI", display: "Peperoni", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 4.0, pReale: 4.0, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_114", nome: "PANNA", display: "Panna", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 5.0, pReale: 5.0, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_115", nome: "POMODORINO ARANCIONE   5,8    6,8", display: "Pomodorino Arancione", cat: "verdura", unit: "kg", minStock: 3, shelfLifeDays: 7, allergens: [], preferredSupplier: "s6", pFatt: 6.8, pReale: 6.8, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_116", nome: "PISTACCHIO", display: "Pistacchio", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 52.9, pReale: 52.9, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_117", nome: "NEUTRO MARACUJA EURO 5,5 AL KG ANNO SCORSO", display: "Neutro Maracuja Euro", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 5.5, pReale: 5.5, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_118", nome: "pan grattato semplice", display: "Pan Grattato Semplice", cat: "dispensa", unit: "kg", minStock: 5, shelfLifeDays: 180, allergens: [], preferredSupplier: "s5", pFatt: 3.5, pReale: 3.5, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_119", nome: "PASSERINA BOTTIGLIA", display: "Passerina Bottiglia", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 2.5, pReale: 2.5, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_120", nome: "AUTENTICO BOTTIGLIA", display: "Autentico Bottiglia", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 2.62, pReale: 2.62, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_121", nome: "CREATIVO BOTTIGLIA", display: "Creativo Bottiglia", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 3.62, pReale: 3.62, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_122", nome: "TRADIZIONALE BOTTIGLIA", display: "Tradizionale Bottiglia", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 3.0, pReale: 3.0, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_123", nome: "MAGNIFICA BOTTIGLIA", display: "Magnifica Bottiglia", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 6.6, pReale: 6.6, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_124", nome: "LIBERA BOTTIGLIA", display: "Libera Bottiglia", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 7.15, pReale: 7.15, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_125", nome: "ESTROVERSA BOTTIGLIA", display: "Estroversa Bottiglia", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 4.3, pReale: 4.3, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
    { id: "bp_126", nome: "FEMMINILE BOTTIGLIA", display: "Femminile Bottiglia", cat: "altro", unit: "kg", minStock: 2, shelfLifeDays: 30, allergens: [], preferredSupplier: "s5", pFatt: 6.0, pReale: 6.0, calo1: 0, calo2: 0, caloTot: 0, t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0 },
  ],

  dishesReal: [
    { id: "d_1", nome: "INSALATA MARE", cat: "antipasti", prezzo: 9.9, costoRiga: 2.6362, ingredienti: [{ name: "seppia", qty: 0.15, unit: "kg", bpId: "bp_10" }, { name: "pomodorino", qty: 0.014, unit: "kg", bpId: "bp_29" }, { name: "fagiolini", qty: 0.025, unit: "kg", bpId: "bp_45" }, { name: "ceci", qty: 0.014, unit: "kg", bpId: "bp_43" }, { name: "zucchine", qty: 0.017, unit: "kg", bpId: "bp_35" }, { name: "patate", qty: 0.032, unit: "kg", bpId: "bp_32" }, { name: "CAROTE", qty: 0.01, unit: "kg", bpId: "bp_34" }, { name: "citronet", qty: 0.0164, unit: "kg", bpId: "bp_95" }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }] },
    { id: "d_2", nome: "CALAMARATA", cat: "antipasti", prezzo: 9.9, costoRiga: 2.3094, ingredienti: [{ name: "calamari", qty: 0.1818, unit: "kg", bpId: "bp_27" }, { name: "insalata verde", qty: 0.006, unit: "kg", bpId: "bp_51" }, { name: "sale", qty: 0.002, unit: "kg", bpId: "bp_67" }, { name: "pepe", qty: 0.001, unit: "kg", bpId: "bp_113" }, { name: "olio misto", qty: 0.0909, unit: "kg", bpId: "bp_66" }, { name: "succo di limone", qty: 0.045, unit: "kg", bpId: null }, { name: "succo arancia", qty: 0.1091, unit: "kg", bpId: null }, { name: "pepe rosa", qty: 0.001, unit: "kg", bpId: null }, { name: "fettina arancio", qty: 0.0108, unit: "kg", bpId: null }] },
    { id: "d_3", nome: "INSALATA POLPO", cat: "antipasti", prezzo: 9.9, costoRiga: 4.7156, ingredienti: [{ name: "polpo", qty: 0.15, unit: "kg", bpId: "bp_11" }, { name: "pomodorino", qty: 0.014, unit: "kg", bpId: "bp_29" }, { name: "rucola", qty: 0.0007, unit: "kg", bpId: "bp_58" }, { name: "patate", qty: 0.054, unit: "kg", bpId: "bp_32" }, { name: "zafferano", qty: 0.0, unit: "kg", bpId: "bp_88" }, { name: "citronette", qty: 0.0157, unit: "kg", bpId: "bp_95" }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }] },
    { id: "d_4", nome: "SALMONE MARINATO", cat: "antipasti", prezzo: 9.9, costoRiga: 2.7712, ingredienti: [{ name: "salmone", qty: 0.16, unit: "kg", bpId: "bp_8" }, { name: "pomodorino", qty: 0.014, unit: "kg", bpId: "bp_29" }, { name: "insalato", qty: 0.03, unit: "kg", bpId: null }, { name: "citronette", qty: 0.015, unit: "kg", bpId: "bp_95" }] },
    { id: "d_5", nome: "BURRATA E SALMONE", cat: "antipasti", prezzo: 9.9, costoRiga: 2.2393, ingredienti: [{ name: "salmone", qty: 0.08, unit: "kg", bpId: "bp_8" }, { name: "pomodorino", qty: 0.0035, unit: "kg", bpId: "bp_29" }, { name: "radicchio", qty: 0.01, unit: "kg", bpId: "bp_49" }, { name: "burrata", qty: 0.125, unit: "kg", bpId: "bp_90" }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }, { name: "rucola", qty: 0.0013, unit: "kg", bpId: "bp_58" }] },
    { id: "d_6", nome: "BURRATA CON ALICI", cat: "antipasti", prezzo: 9.9, costoRiga: 1.8276, ingredienti: [{ name: "acciughe", qty: 0.0268, unit: "kg", bpId: "bp_14" }, { name: "pomodorino", qty: 0.014, unit: "kg", bpId: "bp_29" }, { name: "radicchio", qty: 0.01, unit: "kg", bpId: "bp_49" }, { name: "rucola", qty: 0.0013, unit: "kg", bpId: "bp_58" }, { name: "burrata", qty: 0.125, unit: "kg", bpId: "bp_90" }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }] },
    { id: "d_7", nome: "ALICE MARINATA", cat: "antipasti", prezzo: 9.9, costoRiga: 1.3084, ingredienti: [{ name: "alici", qty: 0.1077, unit: "kg", bpId: "bp_55" }, { name: "pomodorino", qty: 0.0035, unit: "kg", bpId: "bp_29" }, { name: "n crostini", qty: 1.0, unit: "kg", bpId: null }, { name: "succo di limone", qty: 0.0173, unit: "kg", bpId: null }, { name: "sale", qty: 0.0054, unit: "kg", bpId: "bp_67" }, { name: "aceto", qty: 0.0077, unit: "kg", bpId: "bp_74" }, { name: "aglio", qty: 0.01, unit: "kg", bpId: "bp_30" }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }, { name: "olio misto", qty: 0.0077, unit: "kg", bpId: "bp_66" }, { name: "peporoncino", qty: 0.0015, unit: "kg", bpId: null }, { name: "prezzemolo", qty: 0.0023, unit: "kg", bpId: "bp_38" }] },
    { id: "d_8", nome: "BACCALA MANTECATO", cat: "antipasti", prezzo: 9.9, costoRiga: 1.4936, ingredienti: [{ name: "baccala", qty: 0.0319, unit: "kg", bpId: "bp_26" }, { name: "patate", qty: 0.0777, unit: "kg", bpId: "bp_32" }, { name: "latte", qty: 0.0426, unit: "kg", bpId: "bp_77" }, { name: "pepenero", qty: 0.001, unit: "kg", bpId: null }, { name: "olio evo", qty: 0.0035, unit: "kg", bpId: "bp_65" }, { name: "prezzemolo", qty: 0.0001, unit: "kg", bpId: "bp_38" }, { name: "miscela aglio", qty: 0.0004, unit: "kg", bpId: "bp_92" }, { name: "crostino", qty: 1.0, unit: "kg", bpId: "bp_94" }, { name: "crema di yogurt", qty: 0.0323, unit: "kg", bpId: null }] },
    { id: "d_9", nome: "PACCASASSI E ALICI", cat: "antipasti", prezzo: 9.9, costoRiga: 3.8859, ingredienti: [{ name: "pacassassi", qty: 0.0372, unit: "kg", bpId: null }, { name: "alici cantrabico", qty: 0.048, unit: "kg", bpId: null }, { name: "crostini", qty: 1.0, unit: "kg", bpId: "bp_94" }, { name: "prezzemolo", qty: 0.001, unit: "kg", bpId: "bp_38" }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }, { name: "tovagliolo", qty: 1.0, unit: "kg", bpId: null }] },
    { id: "d_10", nome: "IN FONDO AL MAR", cat: "antipasti", prezzo: 9.9, costoRiga: 1.5915, ingredienti: [{ name: "gamberetti", qty: 0.0641, unit: "kg", bpId: null }, { name: "maionese", qty: 0.034, unit: "kg", bpId: "bp_106" }, { name: "insalata", qty: 0.006, unit: "kg", bpId: "bp_51" }, { name: "paccasassi", qty: 0.0037, unit: "kg", bpId: null }, { name: "cestino", qty: 1.0, unit: "kg", bpId: "bp_104" }] },
    { id: "d_11", nome: "COZZE", cat: "antipasti", prezzo: 9.9, costoRiga: 2.5068, ingredienti: [{ name: "cozze", qty: 0.5, unit: "kg", bpId: "bp_2" }, { name: "miscela olio", qty: 0.01, unit: "kg", bpId: null }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }, { name: "peperoncino", qty: 0.001, unit: "kg", bpId: null }, { name: "prezzemolo", qty: 0.002, unit: "kg", bpId: "bp_38" }, { name: "vino", qty: 0.05, unit: "kg", bpId: "bp_68" }, { name: "2 crostini", qty: 2.0, unit: "kg", bpId: null }, { name: "olio misto", qty: 0.05, unit: "kg", bpId: "bp_66" }] },
    { id: "d_12", nome: "GUAZZETTO", cat: "antipasti", prezzo: 9.9, costoRiga: 4.9834, ingredienti: [{ name: "polpo", qty: 0.1271, unit: "kg", bpId: "bp_11" }, { name: "pomodorini", qty: 0.0208, unit: "kg", bpId: "bp_29" }, { name: "pomodorini valfrutta", qty: 0.1875, unit: "kg", bpId: null }, { name: "olive", qty: 0.0042, unit: "kg", bpId: "bp_60" }, { name: "capperi", qty: 0.0033, unit: "kg", bpId: "bp_44" }, { name: "aglio", qty: 0.0042, unit: "kg", bpId: "bp_30" }, { name: "olio misto", qty: 0.0083, unit: "kg", bpId: "bp_66" }, { name: "olio evo", qty: 0.024, unit: "kg", bpId: "bp_65" }, { name: "peperoncino", qty: 0.006, unit: "kg", bpId: null }, { name: "vino", qty: 0.0042, unit: "kg", bpId: "bp_68" }, { name: "prezzemolo", qty: 0.0004, unit: "kg", bpId: "bp_38" }, { name: "crostino", qty: 1.0, unit: "kg", bpId: "bp_94" }] },
    { id: "d_13", nome: "COZZE E FAGIOLI", cat: "antipasti", prezzo: 9.9, costoRiga: 1.6376, ingredienti: [{ name: "cozze", qty: 0.1, unit: "kg", bpId: "bp_2" }, { name: "fagioli", qty: 0.159, unit: "kg", bpId: "bp_45" }, { name: "pomodirini", qty: 0.05, unit: "kg", bpId: null }, { name: "cipolla", qty: 0.06, unit: "kg", bpId: "bp_53" }, { name: "aglio", qty: 0.006, unit: "kg", bpId: "bp_30" }, { name: "olio evo", qty: 0.01, unit: "kg", bpId: "bp_65" }, { name: "peperoncino", qty: 0.0004, unit: "kg", bpId: null }, { name: "prezzemolo", qty: 0.0006, unit: "kg", bpId: "bp_38" }, { name: "vino", qty: 0.01, unit: "kg", bpId: "bp_68" }, { name: "crostino", qty: 1.0, unit: "kg", bpId: "bp_94" }, { name: "olio misto", qty: 0.01, unit: "kg", bpId: "bp_66" }] },
    { id: "d_14", nome: "SEPPIA E PISELLI", cat: "antipasti", prezzo: 9.9, costoRiga: 2.2187, ingredienti: [{ name: "seppia cong", qty: 0.1455, unit: "kg", bpId: null }, { name: "piselli", qty: 0.125, unit: "kg", bpId: "bp_52" }, { name: "pomodirini", qty: 0.0417, unit: "kg", bpId: null }, { name: "cipolla", qty: 0.0625, unit: "kg", bpId: "bp_53" }, { name: "aglio", qty: 0.005, unit: "kg", bpId: "bp_30" }, { name: "olio misto", qty: 0.015, unit: "kg", bpId: "bp_66" }, { name: "peperoncino", qty: 0.0004, unit: "kg", bpId: null }, { name: "prezzemolo", qty: 0.005, unit: "kg", bpId: "bp_38" }, { name: "vino", qty: 0.0125, unit: "kg", bpId: "bp_68" }, { name: "crostino", qty: 1.0, unit: "kg", bpId: "bp_94" }] },
    { id: "d_15", nome: "COZZE GRATINATE", cat: "antipasti", prezzo: 9.9, costoRiga: 2.6796, ingredienti: [{ name: "cozze", qty: 0.4211, unit: "kg", bpId: "bp_2" }, { name: "pangrattato", qty: 0.1053, unit: "kg", bpId: null }, { name: "aglio", qty: 0.0033, unit: "kg", bpId: "bp_30" }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }, { name: "peperoncino", qty: 0.0002, unit: "kg", bpId: null }, { name: "prezzemolo", qty: 0.0018, unit: "kg", bpId: "bp_38" }, { name: "vino", qty: 0.0137, unit: "kg", bpId: "bp_68" }, { name: "pomodorino", qty: 0.0213, unit: "kg", bpId: "bp_29" }, { name: "insalata", qty: 0.006, unit: "kg", bpId: "bp_51" }, { name: "olio misto", qty: 0.0053, unit: "kg", bpId: "bp_66" }, { name: "pepe nero", qty: 0.0001, unit: "kg", bpId: null }] },
    { id: "d_16", nome: "BECCAFICO", cat: "antipasti", prezzo: 9.9, costoRiga: 1.3743, ingredienti: [{ name: "sardoncini", qty: 0.1111, unit: "kg", bpId: "bp_7" }, { name: "aglio", qty: 0.0004, unit: "kg", bpId: "bp_30" }, { name: "olio misto", qty: 0.004, unit: "kg", bpId: "bp_66" }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }, { name: "pepe", qty: 0.0001, unit: "kg", bpId: "bp_113" }, { name: "prezzemolo", qty: 0.0007, unit: "kg", bpId: "bp_38" }, { name: "insalata", qty: 0.006, unit: "kg", bpId: "bp_51" }, { name: "pomodorino", qty: 0.0316, unit: "kg", bpId: "bp_29" }] },
    { id: "d_17", nome: "CANNELLI", cat: "antipasti", prezzo: 9.9, costoRiga: 1.4889, ingredienti: [{ name: "cannelli", qty: 0.1667, unit: "kg", bpId: "bp_42" }, { name: "pangrattato", qty: 0.01, unit: "kg", bpId: null }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }, { name: "prezzemolo", qty: 0.001, unit: "kg", bpId: "bp_38" }, { name: "insalata", qty: 0.006, unit: "kg", bpId: "bp_51" }] },
    { id: "d_18", nome: "CAPESANTE", cat: "antipasti", prezzo: 9.9, costoRiga: 2.1328, ingredienti: [{ name: "capesante", qty: 0.0857, unit: "kg", bpId: null }, { name: "pangrattato", qty: 0.01, unit: "kg", bpId: null }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }, { name: "prezzemolo", qty: 0.001, unit: "kg", bpId: "bp_38" }, { name: "insalata", qty: 0.006, unit: "kg", bpId: "bp_51" }] },
    { id: "d_19", nome: "RAGUSE", cat: "antipasti", prezzo: 9.9, costoRiga: 1.877, ingredienti: [{ name: "raguse", qty: 0.1625, unit: "kg", bpId: "bp_22" }, { name: "pelati", qty: 0.3125, unit: "kg", bpId: "bp_62" }, { name: "aglio con olio", qty: 0.0044, unit: "kg", bpId: null }, { name: "olio misto", qty: 0.0146, unit: "kg", bpId: "bp_66" }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }, { name: "cipolla", qty: 0.0272, unit: "kg", bpId: "bp_53" }, { name: "prezzemolo", qty: 0.0013, unit: "kg", bpId: "bp_38" }, { name: "vino", qty: 0.0125, unit: "kg", bpId: "bp_68" }, { name: "SALE", qty: 0.0013, unit: "kg", bpId: "bp_67" }, { name: "peperoncino", qty: 0.0001, unit: "kg", bpId: null }, { name: "crostino", qty: 1.0, unit: "kg", bpId: "bp_94" }] },
    { id: "d_20", nome: "SEPPIE E FAGIOLI", cat: "antipasti", prezzo: 9.9, costoRiga: 2.7492, ingredienti: [{ name: "seppia cong", qty: 0.1455, unit: "kg", bpId: null }, { name: "fagioli", qty: 0.265, unit: "kg", bpId: "bp_45" }, { name: "pomodirini", qty: 0.0625, unit: "kg", bpId: null }, { name: "cipolla", qty: 0.0625, unit: "kg", bpId: "bp_53" }, { name: "aglio", qty: 0.005, unit: "kg", bpId: "bp_30" }, { name: "olio evo", qty: 0.0022, unit: "kg", bpId: "bp_65" }, { name: "peperoncino", qty: 0.002, unit: "kg", bpId: null }, { name: "prezzemolo", qty: 0.0008, unit: "kg", bpId: "bp_38" }, { name: "vino", qty: 0.0125, unit: "kg", bpId: "bp_68" }, { name: "crostino", qty: 1.0, unit: "kg", bpId: "bp_94" }, { name: "olio misto", qty: 0.015, unit: "kg", bpId: "bp_66" }] },
    { id: "d_22", nome: "SPAGHETTO LIMONE", cat: "primi", prezzo: 12.9, costoRiga: 1.0474, ingredienti: [{ name: "spaghetto molisana", qty: 0.125, unit: "kg", bpId: null }, { name: "cozze", qty: 0.167, unit: "kg", bpId: "bp_2" }, { name: "pangrattato", qty: 0.001, unit: "kg", bpId: null }, { name: "olio con aglio", qty: 0.004, unit: "kg", bpId: null }, { name: "olio misto", qty: 0.05, unit: "kg", bpId: "bp_66" }, { name: "prezzemo", qty: 0.003, unit: "kg", bpId: "bp_38" }, { name: "peperoncino", qty: 0.001, unit: "kg", bpId: null }, { name: "vino", qty: 0.05, unit: "kg", bpId: "bp_68" }] },
    { id: "d_23", nome: "SPAGHETTO SARDE", cat: "primi", prezzo: 12.9, costoRiga: 1.6149, ingredienti: [{ name: "spaghetto felicietti", qty: 0.125, unit: "kg", bpId: null }, { name: "sarde", qty: 0.066, unit: "kg", bpId: "bp_7" }, { name: "pomodorino", qty: 0.05, unit: "kg", bpId: "bp_29" }, { name: "capperi", qty: 0.01, unit: "kg", bpId: "bp_44" }, { name: "pinoli", qty: 0.002, unit: "kg", bpId: "bp_111" }, { name: "seme finocchio", qty: 0.0001, unit: "kg", bpId: null }, { name: "pangrattato", qty: 0.001, unit: "kg", bpId: null }, { name: "olio misto", qty: 0.058, unit: "kg", bpId: "bp_66" }, { name: "prezzemo", qty: 0.0025, unit: "kg", bpId: "bp_38" }, { name: "peperoncino", qty: 0.0005, unit: "kg", bpId: null }, { name: "vino", qty: 0.03, unit: "kg", bpId: "bp_68" }, { name: "pelati", qty: 0.025, unit: "kg", bpId: "bp_62" }, { name: "olie evo", qty: 0.004, unit: "kg", bpId: null }] },
    { id: "d_24", nome: "TAGLIOLINI NONNA", cat: "primi", prezzo: 12.9, costoRiga: 1.9836, ingredienti: [{ name: "tagliolino", qty: 0.125, unit: "kg", bpId: null }, { name: "cozze", qty: 0.1588, unit: "kg", bpId: "bp_2" }, { name: "seppia congelata", qty: 0.0215, unit: "kg", bpId: null }, { name: "pelati", qty: 0.1894, unit: "kg", bpId: "bp_62" }, { name: "cipolla", qty: 0.0152, unit: "kg", bpId: "bp_53" }, { name: "olio e aglio", qty: 0.0031, unit: "kg", bpId: null }, { name: "olio misto", qty: 0.0106, unit: "kg", bpId: "bp_66" }, { name: "prezzemo", qty: 0.0013, unit: "kg", bpId: "bp_38" }, { name: "peperoncino", qty: 0.0001, unit: "kg", bpId: null }, { name: "vino", qty: 0.0023, unit: "kg", bpId: "bp_68" }, { name: "sale", qty: 0.0003, unit: "kg", bpId: "bp_67" }] },
    { id: "d_25", nome: "CARBONARA MARE", cat: "primi", prezzo: 12.9, costoRiga: 2.4089, ingredienti: [{ name: "spaghetto molisana", qty: 0.125, unit: "kg", bpId: null }, { name: "seppia congelata", qty: 0.0954, unit: "kg", bpId: null }, { name: "guanciale", qty: 0.026, unit: "kg", bpId: "bp_63" }, { name: "riccio", qty: 0.0025, unit: "kg", bpId: "bp_15" }, { name: "parmigiano", qty: 0.01, unit: "kg", bpId: "bp_64" }, { name: "pomodorino arancione", qty: 0.192, unit: "kg", bpId: "bp_115" }, { name: "aglio", qty: 0.004, unit: "kg", bpId: "bp_30" }, { name: "olio misto", qty: 0.02, unit: "kg", bpId: "bp_66" }, { name: "prezzemo", qty: 0.009, unit: "kg", bpId: "bp_38" }, { name: "peperoncino", qty: 0.0002, unit: "kg", bpId: null }, { name: "vino", qty: 0.015, unit: "kg", bpId: "bp_68" }, { name: "pepe", qty: 0.0001, unit: "kg", bpId: "bp_113" }] },
    { id: "d_26", nome: "SPAGO NONNA", cat: "primi", prezzo: 12.9, costoRiga: 1.5195, ingredienti: [{ name: "spaghetto molisana", qty: 0.125, unit: "kg", bpId: null }, { name: "cozze", qty: 0.1588, unit: "kg", bpId: "bp_2" }, { name: "seppia congelata", qty: 0.0215, unit: "kg", bpId: null }, { name: "pelati", qty: 0.1894, unit: "kg", bpId: "bp_62" }, { name: "cipolla", qty: 0.0152, unit: "kg", bpId: "bp_53" }, { name: "olio e aglio", qty: 0.0031, unit: "kg", bpId: null }, { name: "olio misto", qty: 0.0106, unit: "kg", bpId: "bp_66" }, { name: "prezzemo", qty: 0.0013, unit: "kg", bpId: "bp_38" }, { name: "peperoncino", qty: 0.0001, unit: "kg", bpId: null }, { name: "vino", qty: 0.0023, unit: "kg", bpId: "bp_68" }, { name: "sale", qty: 0.0003, unit: "kg", bpId: "bp_67" }] },
    { id: "d_27", nome: "RISOTTO MARE", cat: "primi", prezzo: 12.9, costoRiga: 2.3409, ingredienti: [{ name: "riso", qty: 0.125, unit: "kg", bpId: "bp_102" }, { name: "seppia congelata", qty: 0.0956, unit: "kg", bpId: null }, { name: "cozze", qty: 0.0667, unit: "kg", bpId: "bp_2" }, { name: "gamberetti", qty: 0.0201, unit: "kg", bpId: null }, { name: "granchio blu", qty: 0.0207, unit: "kg", bpId: "bp_18" }, { name: "pomodorino", qty: 0.0283, unit: "kg", bpId: "bp_29" }, { name: "aglio", qty: 0.004, unit: "kg", bpId: "bp_30" }, { name: "olio misto", qty: 0.02, unit: "kg", bpId: "bp_66" }, { name: "prezzemo", qty: 0.009, unit: "kg", bpId: "bp_38" }, { name: "peperoncino", qty: 0.0002, unit: "kg", bpId: null }, { name: "vino", qty: 0.015, unit: "kg", bpId: "bp_68" }] },
    { id: "d_28", nome: "risotto mare rosso", cat: "primi", prezzo: 12.9, costoRiga: 3.0208, ingredienti: [{ name: "riso", qty: 0.125, unit: "kg", bpId: "bp_102" }, { name: "seppia congelata", qty: 0.0956, unit: "kg", bpId: null }, { name: "cozze", qty: 0.0667, unit: "kg", bpId: "bp_2" }, { name: "gamberetti", qty: 0.0201, unit: "kg", bpId: null }, { name: "granchio blu", qty: 0.0207, unit: "kg", bpId: "bp_18" }, { name: "pomodorino", qty: 0.0283, unit: "kg", bpId: "bp_29" }, { name: "aglio", qty: 0.004, unit: "kg", bpId: "bp_30" }, { name: "olio misto", qty: 0.02, unit: "kg", bpId: "bp_66" }, { name: "prezzemo", qty: 0.009, unit: "kg", bpId: "bp_38" }, { name: "peperoncino", qty: 0.0002, unit: "kg", bpId: null }, { name: "vino", qty: 0.015, unit: "kg", bpId: "bp_68" }, { name: "SUGO SCAMPI", qty: 0.1667, unit: "kg", bpId: "bp_97" }] },
    { id: "d_29", nome: "POSITANO", cat: "primi", prezzo: 12.9, costoRiga: 0, ingredienti: [{ name: "spago nero", qty: 0.125, unit: "kg", bpId: null }] },
    { id: "d_30", nome: "LASAGNE", cat: "primi", prezzo: 12.9, costoRiga: 1.8598, ingredienti: [{ name: "pasta lasagne", qty: 0.0667, unit: "kg", bpId: "bp_103" }, { name: "seppia", qty: 0.0478, unit: "kg", bpId: "bp_10" }, { name: "gamberetti", qty: 0.0222, unit: "kg", bpId: null }, { name: "cozze", qty: 0.1111, unit: "kg", bpId: "bp_2" }, { name: "pomodorini arancioni", qty: 0.0333, unit: "kg", bpId: null }, { name: "latte", qty: 0.0667, unit: "kg", bpId: "bp_77" }, { name: "farina", qty: 0.0533, unit: "kg", bpId: "bp_81" }, { name: "burrro", qty: 0.0056, unit: "kg", bpId: null }, { name: "miscela aglio", qty: 0.0001, unit: "kg", bpId: "bp_92" }, { name: "olio misto", qty: 0.0022, unit: "kg", bpId: "bp_66" }, { name: "prezzemo", qty: 0.0011, unit: "kg", bpId: "bp_38" }, { name: "peperoncino", qty: 0.002, unit: "kg", bpId: null }, { name: "vino", qty: 0.0011, unit: "kg", bpId: "bp_68" }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }] },
    { id: "d_31", nome: "PRIMO NIALTRI", cat: "primi", prezzo: 12.9, costoRiga: 2.6376, ingredienti: [{ name: "scialiatelli", qty: 0.1364, unit: "kg", bpId: null }, { name: "cozze", qty: 0.0208, unit: "kg", bpId: "bp_2" }, { name: "fagioli", qty: 0.0883, unit: "kg", bpId: "bp_45" }, { name: "calamari", qty: 0.1183, unit: "kg", bpId: "bp_27" }, { name: "scampi BARATTOLO", qty: 0.125, unit: "kg", bpId: null }, { name: "vino", qty: 0.0667, unit: "kg", bpId: "bp_68" }, { name: "pomodorini", qty: 0.0113, unit: "kg", bpId: "bp_29" }, { name: "aglio", qty: 0.0013, unit: "kg", bpId: "bp_30" }, { name: "olio misto", qty: 0.075, unit: "kg", bpId: "bp_66" }, { name: "prezzemo", qty: 0.001, unit: "kg", bpId: "bp_38" }, { name: "peperoncino", qty: 0.0003, unit: "kg", bpId: null }] },
    { id: "d_32", nome: "MANDRACCHIO", cat: "primi", prezzo: 12.9, costoRiga: 1.9333, ingredienti: [{ name: "spaghetti felicietti", qty: 0.125, unit: "kg", bpId: null }, { name: "vongole", qty: 0.1, unit: "kg", bpId: "bp_3" }, { name: "zucchero canna", qty: 0.0033, unit: "kg", bpId: null }, { name: "miscela di aglio", qty: 0.004, unit: "kg", bpId: null }, { name: "olio misto", qty: 0.0096, unit: "kg", bpId: "bp_66" }, { name: "prezzemo", qty: 0.001, unit: "kg", bpId: "bp_38" }, { name: "peperoncino", qty: 0.001, unit: "kg", bpId: null }, { name: "vino", qty: 0.05, unit: "kg", bpId: "bp_68" }] },
    { id: "d_33", nome: "SPAGO SCOGLIO RO", cat: "primi", prezzo: 12.9, costoRiga: 0.3235, ingredienti: [{ name: "spaghetti felicietti", qty: 0.125, unit: "kg", bpId: null }] },
    { id: "d_34", nome: "ATTRAVERSATA DI", cat: "primi", prezzo: 12.9, costoRiga: 1.5892, ingredienti: [{ name: "spaghetti molisana", qty: 0.125, unit: "kg", bpId: null }, { name: "salicornia", qty: 0.0667, unit: "kg", bpId: "bp_55" }, { name: "botarga di muggine", qty: 0.0045, unit: "kg", bpId: null }, { name: "miscela aglio", qty: 0.0044, unit: "kg", bpId: "bp_92" }, { name: "olio misto", qty: 0.032, unit: "kg", bpId: "bp_66" }, { name: "prezzemo", qty: 0.004, unit: "kg", bpId: "bp_38" }, { name: "peperoncino", qty: 0.001, unit: "kg", bpId: null }] },
    { id: "d_35", nome: "GARUM", cat: "primi", prezzo: 12.9, costoRiga: 0.2235, ingredienti: [{ name: "spaghetti molisana", qty: 0.125, unit: "kg", bpId: null }] },
    { id: "d_36", nome: "SPAGO MARE BIAN", cat: "primi", prezzo: 12.9, costoRiga: 0.3235, ingredienti: [{ name: "spaghetti felicietti", qty: 0.125, unit: "kg", bpId: null }] },
    { id: "d_37", nome: "SPAGO VONGOLE", cat: "primi", prezzo: 12.9, costoRiga: 1.4996, ingredienti: [{ name: "spaghetti felicietti", qty: 0.125, unit: "kg", bpId: null }, { name: "vongole", qty: 0.15, unit: "kg", bpId: "bp_3" }, { name: "miscela aglio", qty: 0.004, unit: "kg", bpId: "bp_92" }, { name: "olio misto", qty: 0.022, unit: "kg", bpId: "bp_66" }, { name: "prezzemo", qty: 0.006, unit: "kg", bpId: "bp_38" }, { name: "peperoncino", qty: 0.001, unit: "kg", bpId: null }, { name: "vino", qty: 0.02, unit: "kg", bpId: "bp_68" }, { name: "prezzemo", qty: 0.007, unit: "kg", bpId: "bp_38" }, { name: "pomodorino", qty: 0.076, unit: "kg", bpId: "bp_29" }] },
    { id: "d_38", nome: "TAGLIOLINI MARE", cat: "primi", prezzo: 12.9, costoRiga: 0.6875, ingredienti: [{ name: "tagliolini mare", qty: 0.125, unit: "kg", bpId: null }] },
    { id: "d_39", nome: "tagliolini ragu", cat: "primi", prezzo: 12.9, costoRiga: 0.6875, ingredienti: [{ name: "tagliolino", qty: 0.125, unit: "kg", bpId: null }] },
    { id: "d_40", nome: "tagliolini pomodoro", cat: "primi", prezzo: 12.9, costoRiga: 0.6875, ingredienti: [{ name: "tagliolino", qty: 0.125, unit: "kg", bpId: null }] },
    { id: "d_41", nome: "spaghetti pomodoro", cat: "primi", prezzo: 12.9, costoRiga: 0.3235, ingredienti: [{ name: "spaghetti felicietti", qty: 0.125, unit: "kg", bpId: null }] },
    { id: "d_42", nome: "SEPPIOLINE GRIG", cat: "secondi", prezzo: 16.9, costoRiga: 2.7786, ingredienti: [{ name: "seppia", qty: 0.2051, unit: "kg", bpId: "bp_10" }, { name: "maionese", qty: 0.0357, unit: "kg", bpId: "bp_106" }, { name: "cipolla rossa", qty: 0.1102, unit: "kg", bpId: "bp_53" }, { name: "zucchero canna", qty: 0.0254, unit: "kg", bpId: null }, { name: "aceto balsamico", qty: 0.0025, unit: "kg", bpId: "bp_75" }, { name: "sale", qty: 0.0001, unit: "kg", bpId: "bp_67" }, { name: "pangrattato MISTO", qty: 0.0255, unit: "kg", bpId: null }, { name: "olio misto", qty: 0.0182, unit: "kg", bpId: "bp_66" }, { name: "prezzemolo", qty: 0.001, unit: "kg", bpId: "bp_38" }, { name: "pepe", qty: 0.001, unit: "kg", bpId: "bp_113" }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }] },
    { id: "d_43", nome: "FRITTURA ADRIAT", cat: "secondi", prezzo: 18.9, prezzoPranzo: 16.0, costoRiga: 2.5512, ingredienti: [{ name: "pesce misto", qty: 0.07, unit: "kg", bpId: null }, { name: "calamari", qty: 0.14, unit: "kg", bpId: "bp_27" }, { name: "farina", qty: 0.0938, unit: "kg", bpId: "bp_81" }, { name: "olio di semi", qty: 0.0641, unit: "kg", bpId: null }, { name: "limone", qty: 1.0, unit: "kg", bpId: "bp_46" }] },
    { id: "d_44", nome: "FRITTURA NO SPINE", cat: "secondi", prezzo: 18.9, prezzoPranzo: 16.0, costoRiga: 2.5734, ingredienti: [{ name: "calamari", qty: 0.15, unit: "kg", bpId: "bp_27" }, { name: "gamberetti", qty: 0.0641, unit: "kg", bpId: null }, { name: "olio di semi", qty: 0.5208, unit: "kg", bpId: null }, { name: "limone", qty: 1.0, unit: "kg", bpId: "bp_46" }] },
    { id: "d_45", nome: "SPIEDINI 3 PEZZI", cat: "secondi", prezzo: 17.9, costoRiga: 2.1795, ingredienti: [{ name: "calamari", qty: 0.2091, unit: "kg", bpId: "bp_27" }, { name: "GAMBERI", qty: 0.0641, unit: "kg", bpId: "bp_13" }] },
    { id: "d_46", nome: "SPIEDINI 4 PEZZI", cat: "secondi", prezzo: 17.9, costoRiga: 3.0669, ingredienti: [{ name: "calamari", qty: 0.2788, unit: "kg", bpId: "bp_27" }, { name: "GAMBERI", qty: 0.1026, unit: "kg", bpId: "bp_13" }] },
    { id: "d_47", nome: "CODA ROSPO", cat: "secondi", prezzo: 19.9, costoRiga: 8.4599, ingredienti: [{ name: "CODA ROSPO", qty: 0.349, unit: "kg", bpId: "bp_20" }, { name: "GUANCIALE", qty: 0.029, unit: "kg", bpId: "bp_63" }, { name: "carciofi", qty: 0.0505, unit: "kg", bpId: "bp_57" }, { name: "cipolla", qty: 0.082, unit: "kg", bpId: "bp_53" }, { name: "olive", qty: 0.18, unit: "kg", bpId: "bp_60" }, { name: "capperi", qty: 0.02, unit: "kg", bpId: "bp_44" }, { name: "pomodorini", qty: 0.101, unit: "kg", bpId: "bp_29" }, { name: "patate", qty: 0.315, unit: "kg", bpId: "bp_32" }, { name: "vino", qty: 0.022, unit: "kg", bpId: "bp_68" }, { name: "aglio", qty: 0.002, unit: "kg", bpId: "bp_30" }, { name: "olio misto", qty: 0.04, unit: "kg", bpId: "bp_66" }, { name: "prezzemolo", qty: 0.031, unit: "kg", bpId: "bp_38" }, { name: "peperoncino", qty: 0.001, unit: "kg", bpId: null }, { name: "olio evo", qty: 0.002, unit: "kg", bpId: "bp_65" }, { name: "crostini", qty: 2.0, unit: "kg", bpId: "bp_94" }] },
    { id: "d_48", nome: "RICCIOLA", cat: "secondi", prezzo: 19.9, costoRiga: 4.7337, ingredienti: [{ name: "ricciola", qty: 0.358, unit: "kg", bpId: "bp_23" }, { name: "patate", qty: 0.1, unit: "kg", bpId: "bp_32" }, { name: "olio misto", qty: 0.04, unit: "kg", bpId: "bp_66" }, { name: "pomodorini", qty: 0.09, unit: "kg", bpId: "bp_29" }, { name: "sale", qty: 0.0016, unit: "kg", bpId: "bp_67" }, { name: "olio evo", qty: 0.0022, unit: "kg", bpId: "bp_65" }, { name: "prezzemolo", qty: 0.001, unit: "kg", bpId: "bp_38" }] },
    { id: "d_49", nome: "MAZZANCOLLE ARR", cat: "secondi", prezzo: 15.0, costoRiga: 2.5429, ingredienti: [{ name: "MAZZANCOLLE", qty: 0.2857, unit: "kg", bpId: "bp_21" }] },
    { id: "d_50", nome: "insalata  mista", cat: "contorni", prezzo: 4.5, prezzoPranzo: 3.9, costoRiga: 0.7904, ingredienti: [{ name: "insalata", qty: 0.07, unit: "kg", bpId: "bp_51" }, { name: "pomodori", qty: 0.051, unit: "kg", bpId: "bp_29" }, { name: "carote", qty: 0.014, unit: "kg", bpId: "bp_34" }, { name: "radicchio", qty: 0.008, unit: "kg", bpId: "bp_49" }, { name: "olio", qty: 0.017, unit: "kg", bpId: "bp_66" }] },
    { id: "d_51", nome: "insala verde", cat: "contorni", prezzo: 4.5, prezzoPranzo: 3.9, costoRiga: 0.6532, ingredienti: [{ name: "insalata", qty: 0.08, unit: "kg", bpId: "bp_51" }, { name: "olio", qty: 0.017, unit: "kg", bpId: "bp_66" }, { name: "sale", qty: 0.001, unit: "kg", bpId: "bp_67" }, { name: "rucola", qty: 0.0046, unit: "kg", bpId: "bp_58" }] },
    { id: "d_52", nome: "patate fritte", cat: "contorni", prezzo: 4.5, prezzoPranzo: 3.9, costoRiga: 0.7942, ingredienti: [{ name: "patate", qty: 0.0567, unit: "kg", bpId: "bp_32" }, { name: "succo limone", qty: 0.0056, unit: "kg", bpId: null }] },
    { id: "d_53", nome: "paccasassi", cat: "contorni", prezzo: 4.5, prezzoPranzo: 3.9, costoRiga: 1.55, ingredienti: [{ name: "paccasassi", qty: 0.0775, unit: "kg", bpId: null }] },
    { id: "d_54", nome: "patate arrosto", cat: "contorni", prezzo: 4.5, prezzoPranzo: 3.9, costoRiga: 0.5981, ingredienti: [{ name: "patate", qty: 0.25, unit: "kg", bpId: "bp_32" }, { name: "aglio", qty: 0.0025, unit: "kg", bpId: "bp_30" }, { name: "olio misto", qty: 0.005, unit: "kg", bpId: "bp_66" }, { name: "sale", qty: 0.002, unit: "kg", bpId: "bp_67" }, { name: "pepe nero", qty: 0.0003, unit: "kg", bpId: null }] },
    { id: "d_55", nome: "verdura grigliata", cat: "contorni", prezzo: 4.5, prezzoPranzo: 3.9, costoRiga: 0.4183, ingredienti: [{ name: "zucchine", qty: 0.0417, unit: "kg", bpId: "bp_35" }, { name: "melanzane", qty: 0.0417, unit: "kg", bpId: "bp_40" }, { name: "pomodorini", qty: 0.0217, unit: "kg", bpId: "bp_29" }, { name: "prezzemolo", qty: 0.001, unit: "kg", bpId: "bp_38" }, { name: "olio evo", qty: 0.0048, unit: "kg", bpId: "bp_65" }] },
    { id: "d_56", nome: "verdura gratinata", cat: "contorni", prezzo: 4.5, prezzoPranzo: 3.9, costoRiga: 1.3611, ingredienti: [{ name: "melanzane", qty: 0.0437, unit: "kg", bpId: "bp_40" }, { name: "zucchine", qty: 0.0417, unit: "kg", bpId: "bp_35" }, { name: "pomodori grandi", qty: 0.0428, unit: "kg", bpId: null }, { name: "peperoni", qty: 0.0437, unit: "kg", bpId: "bp_113" }, { name: "pan grattato", qty: 0.025, unit: "kg", bpId: "bp_118" }, { name: "sale", qty: 0.0005, unit: "kg", bpId: "bp_67" }, { name: "pepe", qty: 0.0003, unit: "kg", bpId: "bp_113" }, { name: "olio misto", qty: 0.0017, unit: "kg", bpId: "bp_66" }, { name: "olio evo", qty: 0.0024, unit: "kg", bpId: "bp_65" }] },
    { id: "d_57", nome: "fagiolini", cat: "contorni", prezzo: 4.5, prezzoPranzo: 3.9, costoRiga: 0.5247, ingredienti: [{ name: "fagiolini", qty: 0.125, unit: "kg", bpId: "bp_45" }, { name: "citronette", qty: 0.003, unit: "kg", bpId: "bp_95" }] },
    { id: "d_58", nome: "COTOLETTA", cat: "secondi", prezzo: 8.0, costoRiga: 1.426, ingredienti: [{ name: "pollo", qty: 0.1364, unit: "kg", bpId: "bp_28" }, { name: "uova", qty: 0.4545, unit: "kg", bpId: "bp_78" }, { name: "farina", qty: 0.0091, unit: "kg", bpId: "bp_81" }, { name: "pangratato", qty: 0.0273, unit: "kg", bpId: null }] },
    { id: "d_59", nome: "CREMOLATA MARAC", cat: "dolci", prezzo: 4.9, prezzoPranzo: 4.5, costoRiga: 0.7054, ingredienti: [{ name: "neutro", qty: 0.0375, unit: "kg", bpId: "bp_117" }, { name: "maracuja", qty: 0.05, unit: "kg", bpId: "bp_108" }] },
  ],

  haccpLogs: {
    temperature: [
      { id: "t1", date: "2026-04-22", time: "08:00", location: "Frigo pesce 1", temp: 2.1, operator: "Sara F.", target: "0/+4" },
      { id: "t2", date: "2026-04-22", time: "08:00", location: "Frigo pesce 2", temp: 5.3, operator: "Sara F.", target: "0/+4" },
      { id: "t3", date: "2026-04-22", time: "08:00", location: "Freezer -18", temp: -19.5, operator: "Sara F.", target: "-20/-18" },
      { id: "t4", date: "2026-04-22", time: "08:00", location: "Cella verdure", temp: 4.2, operator: "Sara F.", target: "+2/+8" },
    ],
    cleaning: [
      { id: "c1", date: "2026-04-22", zone: "Banco pesce", operator: "Marco R.", time: "11:30", notes: "Sanificazione completa con disinfettante alimentare" },
      { id: "c2", date: "2026-04-22", zone: "Pavimenti laboratorio", operator: "Giulia B.", time: "17:00", notes: "Fine turno" },
    ],
    nonConformities: [],
  },
  messages: [
    { id: "m1", author: "Marco R.", role: "Laboratorio", text: "Sistema avviato con i 126 prodotti base e 59 ricette importati dal file Excel.", pinned: true, at: "2026-04-22T09:00" },
    { id: "m2", author: "Sara F.", role: "Magazzino", text: "Pronta a ricevere la prima bolla di ingresso. Ricordo di controllare sempre la temperatura del camion.", pinned: false, at: "2026-04-22T09:15" },
  ],
  // Fix L2 — Reg. CE 852/2004 Allegato II Cap. XII: formazione obbligatoria personale
  // Schema esteso con attestato HACCP + costo orario (uso interno) + mansione.
  // Fase M1 — Mensa aziendale art. 51 c. 2 lett. c) TUIR: contratto + regole mensa.
  // Campi nuovi sono null di default per backward-compat con utenti pre-fix (migration in usePersistedState).
  // Default mensa: mealEntitlementHours=6 (soglia CCNL Pubblici Esercizi), mealsPerDayMax=1, mealValueDaily=5€, mealPreferenceConsume=true.
  // Fase M2a — auto-generati dall'import CSV SEAC paghe (presenze giornaliere → pasti dovuti).
  // Schema record: { id, userId, date(YYYY-MM-DD), hoursWorked, hoursOR, hoursST, hoursON,
  //                  hoursFE, hoursSF, hoursPR, hoursRC, mealsDue, mealsConsumed,
  //                  qualifyingTurn(bool), isRiposo(bool), source('seac_auto'|'manual'), monthYear }
  mealLogs: [],
  users: [
    { id: "u1", name: "Marco R.", role: "Laboratorio", email: "marco@bottega.it", active: true,
      haccpCertNumber: null, haccpCertIssuedAt: null, haccpCertExpiresAt: null, haccpCertLevel: null, haccpCertIssuer: null,
      hourlyGross: null, hourlyFullyLoaded: null, mansione: null,
      // Fase M1 — esempio preconfigurato (currentUser): Pubblici Esercizi, full-time, 40h, 6 giorni, livello IV
      contractType: "fullTime", weeklyHours: 40, workDaysPerWeek: 6,
      ccnl: "Pubblici Esercizi Confcommercio", livello: "IV",
      mealEntitlementHours: 6, mealsPerDayMax: 1, mealPreferenceConsume: true,
      mealDietaryRestrictions: null, mealValueDaily: 5.00,
      // Fase M2a — campi auto-popolati da import CSV SEAC paghe
      codiceFiscale: null, hireDate: null, endDate: null, endTD: null, centroDiCosto: null },
    { id: "u2", name: "Giulia B.", role: "Laboratorio", email: "giulia@bottega.it", active: true,
      haccpCertNumber: null, haccpCertIssuedAt: null, haccpCertExpiresAt: null, haccpCertLevel: null, haccpCertIssuer: null,
      hourlyGross: null, hourlyFullyLoaded: null, mansione: null,
      contractType: null, weeklyHours: null, workDaysPerWeek: null, ccnl: null, livello: null,
      mealEntitlementHours: 6, mealsPerDayMax: 1, mealPreferenceConsume: true,
      mealDietaryRestrictions: null, mealValueDaily: 5.00,
      codiceFiscale: null, hireDate: null, endDate: null, endTD: null, centroDiCosto: null },
    { id: "u3", name: "Elena P.", role: "Laboratorio", email: "elena@bottega.it", active: true,
      haccpCertNumber: null, haccpCertIssuedAt: null, haccpCertExpiresAt: null, haccpCertLevel: null, haccpCertIssuer: null,
      hourlyGross: null, hourlyFullyLoaded: null, mansione: null,
      contractType: null, weeklyHours: null, workDaysPerWeek: null, ccnl: null, livello: null,
      mealEntitlementHours: 6, mealsPerDayMax: 1, mealPreferenceConsume: true,
      mealDietaryRestrictions: null, mealValueDaily: 5.00,
      codiceFiscale: null, hireDate: null, endDate: null, endTD: null, centroDiCosto: null },
    { id: "u4", name: "Sara F.", role: "Magazzino", email: "sara@bottega.it", active: true,
      haccpCertNumber: null, haccpCertIssuedAt: null, haccpCertExpiresAt: null, haccpCertLevel: null, haccpCertIssuer: null,
      hourlyGross: null, hourlyFullyLoaded: null, mansione: null,
      contractType: null, weeklyHours: null, workDaysPerWeek: null, ccnl: null, livello: null,
      mealEntitlementHours: 6, mealsPerDayMax: 1, mealPreferenceConsume: true,
      mealDietaryRestrictions: null, mealValueDaily: 5.00,
      codiceFiscale: null, hireDate: null, endDate: null, endTD: null, centroDiCosto: null },
    { id: "u5", name: "Chef Andrea", role: "Cucina", email: "chef@bottega.it", active: true,
      haccpCertNumber: null, haccpCertIssuedAt: null, haccpCertExpiresAt: null, haccpCertLevel: null, haccpCertIssuer: null,
      hourlyGross: null, hourlyFullyLoaded: null, mansione: null,
      contractType: null, weeklyHours: null, workDaysPerWeek: null, ccnl: null, livello: null,
      mealEntitlementHours: 6, mealsPerDayMax: 1, mealPreferenceConsume: true,
      mealDietaryRestrictions: null, mealValueDaily: 5.00,
      codiceFiscale: null, hireDate: null, endDate: null, endTD: null, centroDiCosto: null },
    { id: "u6", name: "Titolare", role: "Amministrazione", email: "admin@bottega.it", active: true,
      haccpCertNumber: null, haccpCertIssuedAt: null, haccpCertExpiresAt: null, haccpCertLevel: null, haccpCertIssuer: null,
      hourlyGross: null, hourlyFullyLoaded: null, mansione: null,
      contractType: null, weeklyHours: null, workDaysPerWeek: null, ccnl: null, livello: null,
      mealEntitlementHours: 6, mealsPerDayMax: 1, mealPreferenceConsume: true,
      mealDietaryRestrictions: null, mealValueDaily: 5.00,
      codiceFiscale: null, hireDate: null, endDate: null, endTD: null, centroDiCosto: null },
  ],

  // Notifiche create automaticamente dal motore (sovrascritte al refresh)
  notifications: [],
  // Session info
  currentUser: { name: "Marco R.", role: "Laboratorio" },
  tables: [],           // tavoli aperti: {id, number, covers, openedAt, notes, items:[{dishId, qty, unitPrice, note}]}
  tableReceipts: [],    // scontrini chiusi: {id, number, covers, openedAt, closedAt, items, total, notes, operator}
  dishAliases: {},      // mappa normStr(nome_csv) -> dishId, costruita progressivamente dall'import CSV per matching automatico futuro
  // Array di normStr(nome_csv) per articoli che Andi ha esplicitamente marcato come "non è un piatto"
  // (es. "MENU CONCORDATO", "CREATIVO BOT"). Esclusi da topMissing e unmatchedItemsCount.
  dishIgnoredArticles: [],
  // FIX F-13 (audit 2026-05-19): cronologia import per audit e dedup file-hash.
  // Schema record: {id, fileHash, fileName, importer, ts, receiptsImported, receiptsSkipped, articlesUnmatched, totalRevenue}
  importHistory: [],
  // STEP 3 ponte "Venduto → Scarico magazzino": registro ADDITIVO delle proposte/conferme
  // di scarico generate dal venduto. Usato per l'anti-doppio scarico. In questo step NON
  // viene mai scritto (nessun commit dei movimenti). Schema record previsto:
  //   {id, source:"sales_import", fromDate, toDate, receiptIds:[], sourceHash, generatedAt,
  //    confirmedBy, confirmedAt, status:"draft"|"confirmed"|"cancelled",
  //    coverage, estimatedCost, movementsCreated:[], kitchenOutputIds:[], warnings}
  salesDischarges: [],
  // Store storico incassi giornalieri aggregati (dai file "conta 25.xlsx" / "conta 24.xlsx").
  // Non contiene scontrini riga-per-riga: è dati AGGREGATI per giorno / servizio.
  // Schema: {date:"YYYY-MM-DD", revenue, covers, pranzoRevenue, pranzoCovers, cenaRevenue, cenaCovers, source:"import_conta", importedAt}
  // Dedup/merge: re-import dello stesso anno aggiorna i record (non li duplica).
  historicalDaily: [],
  // Store vendite per prodotto aggregate (dai file "prodotti venduti 25.xlsx" / "24.xlsx").
  // Schema per elemento: {year, importedAt, items:[{reparto, nome, qty, valore}]}
  // Dedup: un solo record per anno (re-import sovrascrive).
  historicalProducts: [],
  // Archivio clienti storico Pienissimo — SOLO artefatti PICCOLI in state.
  // L'archivio vero (grosso, ~33k clienti) sta in IndexedDB (VOYA_ARCHIVE_KEY),
  // MAI in state. Qui: meta (conteggi/range) + seasonality (stagionalità distillata).
  voyaArchiveMeta: null,
  voyaSeasonality: null,
  // Modello compatto ATTESI REALI (coperti-presenze mese×dow×servizio), rigenerato
  // agli import storico/archivio sul Mac e SINCRONIZZATO al tablet (dove historicalDaily
  // e voyaSeasonality non arrivano). Max 1 record {id:"current", model, builtAt}.
  demandModel: [],
  settings: {
    priceDisplay: "gross",       // "gross" = prezzi IVA inclusa (come cliente vede), "net" = netto imprenditore
    vatRateSale: 0.10,           // aliquota IVA default sulla vendita (ristorazione)
    vatRatePurchase: 0.10,       // aliquota IVA acquisti default (se serve, non utilizzata attualmente nei costi)
    // FIX #5-B (audit 2026-06-04): oneri di trasformazione forfettari (manodopera + energia
    // di cottura) come % sul prezzo NETTO IVA del piatto. Laboratorio di trasformazione: la
    // seppia 7,5 min forno, il salmone marinato hanno un costo di lavorazione materiale che il
    // contribution margin puro (prezzo - costo MP) ignora. Questo forfait alimenta SOLO il
    // "margine netto" (riga AGGIUNTIVA), MAI il margine di contribuzione esistente.
    // DEFAULT 0 = disattivato → finché Andi non lo imposta NESSUN numero cambia (backward compat).
    oneriTrasformazionePct: 0,   // 0..100 (% sul prezzo netto IVA). 0 = nessun onere applicato.
    // FIX #6 (audit 2026-06-04): indice stagionale mensile MOLTIPLICATIVO (gen..dic).
    // DEFAULT NEUTRO (tutti 1) → ZERO impatto sul forecast finché Andi non lo imposta
    // (backward compat al 100%). Quando Andi inserisce il suo profilo (sa che luglio≈3×
    // gennaio) o applica il preset "costa", il forecast a 14-30gg corregge i mesi di svolta.
    // I valori vengono normalizzati a media 1 al momento dell'uso: cambiano la
    // RIDISTRIBUZIONE tra i mesi, non il livello complessivo.
    seasonalityMonthly: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
    companyName: "Bottega del Mare",
    companyAddress: "Polverigi (AN)",
  },
  // Benchmark KPI personalizzabili (l'utente li sovrascrive da Anagrafiche → Benchmark KPI)
  // Per ogni KPI: { target, ottimo, scarso } — il sistema decide il semaforo in base al valore vs queste soglie
  benchmarksKPI: {
    // PENETRAZIONE per coperto (qty media per coperto)
    dolciPerCop:    { target: 0.50, ottimo: 0.65, scarso: 0.30, label: "Dolci per coperto" },
    contorniPerCop: { target: 0.45, ottimo: 0.60, scarso: 0.25, label: "Contorni per coperto" },
    viniPerCop:     { target: 0.45, ottimo: 0.60, scarso: 0.25, label: "Vini per coperto (bottiglie/calici equivalenti)" },
    caffePerCop:    { target: 0.65, ottimo: 0.85, scarso: 0.40, label: "Caffè per coperto" },
    aperitiviPerCop:{ target: 0.30, ottimo: 0.50, scarso: 0.10, label: "Aperitivi per coperto" },
    acquaPerCop:    { target: 0.50, ottimo: 0.70, scarso: 0.30, label: "Acque/bevande per coperto" },
    antipastiPerCop:{ target: 0.55, ottimo: 0.75, scarso: 0.30, label: "Antipasti per coperto" },
    primiPerCop:    { target: 0.70, ottimo: 0.90, scarso: 0.40, label: "Primi per coperto" },
    secondiPerCop:  { target: 0.55, ottimo: 0.75, scarso: 0.30, label: "Secondi per coperto" },
    // MIX PESO RICAVO (% sul totale incasso)
    mixVini:        { target: 18, ottimo: 25, scarso: 10, label: "Peso vini su ricavo (%)" },
    mixBar:         { target: 8, ottimo: 12, scarso: 4, label: "Peso bar/bevande su ricavo (%)" },
    mixDolci:       { target: 7, ottimo: 10, scarso: 4, label: "Peso dolci su ricavo (%)" },
    mixCaffe:       { target: 3, ottimo: 5, scarso: 1, label: "Peso caffetteria su ricavo (%)" },
    mixFoodBev:     { target: 28, ottimo: 35, scarso: 18, label: "Tasso beverage/food (%)" },
    // SCONTRINO E CROSS-SELL
    ticketCop:      { target: 43, ottimo: 50, scarso: 32, label: "Ticket medio per coperto (€)" },
    ticketCopPranzo:{ target: 38, ottimo: 45, scarso: 28, label: "Ticket pranzo per coperto (€)" },
    ticketCopCena:  { target: 46, ottimo: 55, scarso: 35, label: "Ticket cena per coperto (€)" },
    ticketTavolo:   { target: 100, ottimo: 150, scarso: 60, label: "Ticket medio per tavolo (€)" },
    portatePerCop:  { target: 2.7, ottimo: 3.2, scarso: 2.0, label: "Portate (food) per coperto" },
    articoliPerScn: { target: 5, ottimo: 7, scarso: 3, label: "Articoli totali per scontrino" },
    pctMenuCompleto:{ target: 30, ottimo: 45, scarso: 15, label: "% scontrini con menu completo" },
    // PERFORMANCE FASCIA
    coveriPranzoMedia: { target: 18, ottimo: 28, scarso: 8, label: "Coperti medi pranzo" },
    coveriCenaMedia:   { target: 32, ottimo: 50, scarso: 15, label: "Coperti medi cena" },
    // SOGLIA "menu completo" (quante categorie su 4 = antipasti/primi/secondi/dolci)
    menuCompletoMinCat: 3,  // default: almeno 3 categorie su 4
  },
  // ── CRM Link-Core — Fase 2 (storage additivo) ──────────────────────────────
  // Due store nuovi, vuoti di default → ZERO impatto su utenti esistenti (array
  // guarantee in usePersistedState). Schema gestito da `crmCore` (vedi blocco
  // ⟦CRM-CORE⟧). NB: in Fase 2 NON esistono CustomerReceiptLink/Visit/Reservation.
  //   customers: anagrafica clienti identificabili (ADR §A.5). Record:
  //     { id, tenantId, restaurantId, type:person|family|company, displayName,
  //       contacts:[{kind,value,note}], notes, version, updatedAt, deviceId,
  //       tombstone, anonymized, anonymizedAt, createdBy, createdAt }
  customers: [],
  //   customerConsents: eventi di consenso APPEND-ONLY (ADR §A.9 / P4 / I5). Record:
  //     { id, customerId, tenantId, restaurantId, kind:marketing|profiling|allergies,
  //       channel, action:grant|revoke, seq, at, deviceId, anchorDate, legalHold,
  //       createdBy, subjectAnonymized }
  customerConsents: [],
  //   customerReceiptLinks (CRM Link-Core Fase 3): il FATTO atomico unico (ADR I1) —
  //     cliente ↔ receipt, con ruolo. Vuoto di default → retrocompat. Schema gestito da
  //     `crmLinkCore` (vedi blocco ⟦CRM-LINK⟧). Record:
  //     { id, customerId, role:payer|attendee|reservation_holder, primaryStableReceiptKey,
  //       keyTier, candidateKeySnapshot:[{tier,key,degraded}], keyVersion, reservationId,
  //       tenantId, restaurantId, version, updatedAt, deviceId, tombstone, createdBy, createdAt }
  customerReceiptLinks: [],
  // ── Agenda V1 — store additivo (ADR Reservation as Future Intent, PROPOSED) ─
  //   reservations: il FATTO FUTURO dichiarato — "qualcuno ha detto che verrà".
  //     Non è Customer, non è Receipt, non è Visit; nessun Link nasce da qui
  //     (fulfilment → ADR-CRM-11). Vuoto di default → retrocompat totale.
  //     Schema gestito da `reservationCore` (vedi blocco ⟦RESV-CORE⟧). Record:
  //     { id, tenantId, restaurantId, serviceDate "YYYY-MM-DD", service:pranzo|cena,
  //       time "HH:MM"|null (orario dichiarato, Addendum R3),
  //       rawName, covers, table(testo libero), notes, customerId(rif. opzionale),
  //       source:phone|web|walkin|other (default phone, Addendum R1),
  //       status:booked|seated|cancelled|no_show, version, updatedAt, deviceId,
  //       tombstone, createdBy, createdAt }
  reservations: [],
  // ── Floor Core R2 — anagrafica fisica additiva (ADR Floor Core, PROPOSED) ──
  //   floorAreas: le aree/sale del ristorante. ANAGRAFICA, mai eventi: l'occupazione
  //     è sempre derivata (floorCore.occupancyByArea), mai persistita.
  //     Schema gestito da `floorCore` (blocco ⟦FLOOR-CORE⟧). Record:
  //     { id, name, capacity(int≥0 | null — null = derivata dai tavoli, MAI inventata),
  //       active, sortOrder, tenantId, restaurantId, version, updatedAt, deviceId,
  //       tombstone, createdBy, createdAt }
  //     Seed minimale: le 4 aree note di Da Nialtri con capacity null e ZERO tavoli —
  //     capienze e tavoli veri li inserisce Andi dalla configurazione (ADR §7).
  floorAreas: [
    { id: "fa_sala",    name: "Sala",    capacity: null, active: true, sortOrder: 1, tenantId: "t0", restaurantId: "r0", version: 1, updatedAt: "2026-07-02T00:00:00.000Z", deviceId: "local", tombstone: false, createdBy: "seed", createdAt: "2026-07-02T00:00:00.000Z" },
    { id: "fa_pedana",  name: "Pedana",  capacity: null, active: true, sortOrder: 2, tenantId: "t0", restaurantId: "r0", version: 1, updatedAt: "2026-07-02T00:00:00.000Z", deviceId: "local", tombstone: false, createdBy: "seed", createdAt: "2026-07-02T00:00:00.000Z" },
    { id: "fa_saletta", name: "Saletta", capacity: null, active: true, sortOrder: 3, tenantId: "t0", restaurantId: "r0", version: 1, updatedAt: "2026-07-02T00:00:00.000Z", deviceId: "local", tombstone: false, createdBy: "seed", createdAt: "2026-07-02T00:00:00.000Z" },
    { id: "fa_strada",  name: "Strada",  capacity: null, active: true, sortOrder: 4, tenantId: "t0", restaurantId: "r0", version: 1, updatedAt: "2026-07-02T00:00:00.000Z", deviceId: "local", tombstone: false, createdBy: "seed", createdAt: "2026-07-02T00:00:00.000Z" },
  ],
  //   floorTables: i tavoli — label testo COME LA SCRIVE la segretaria ("12", "431",
  //     "12 bis"), riferimento areaId, posti standard. Record:
  //     { id, label, areaId, seats(int≥0), active, sortOrder, + stessi P3/P5/audit }
  //     reservation.table resta testo libero: il ponte è floorCore.resolveTableTokens
  //     (parsing soft, MAI blocco). Vuoto di default: nessun tavolo inventato.
  floorTables: [],
  // R8-B — lista nera: clienti segnalati con motivo (fatti minimi, sincronizzata)
  blocklist: [],
  // ── R5 Fulfilment — store additivo (ADR-CRM-11, PROPOSED) ──────────────────
  //   reservationReceiptLinks: l'UNICO fatto persistito del fulfilment (F1) —
  //     "questa prenotazione è stata realizzata da questo scontrino". Il receipt
  //     è identificato SOLO da chiavi stabili (F3): receipt.id volatile mai
  //     persistito. fulfilled/no-show/valore/Visit restano derivati (F2/F10).
  //     Schema gestito da `reservationFulfilCore` (blocco ⟦RESV-FULFILL⟧). Record:
  //     { id, reservationId, primaryStableReceiptKey, keyTier,
  //       candidateKeySnapshot:[{tier,key,degraded}] (IMMUTABILE), keyVersion,
  //       origin:manual|suggestion_confirmed, note, tenantId, restaurantId,
  //       version, updatedAt, deviceId, tombstone, createdBy, createdAt }
  reservationReceiptLinks: [],
};

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      UTILITIES & FORMATTERS                                ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
const fmt = {
  kg: (n) => n == null ? "—" : `${Number(n).toFixed(n % 1 === 0 ? 0 : 2)} kg`,
  qty: (n, u) => n == null ? "—" : `${Number(n).toFixed(n % 1 === 0 ? 0 : 2)} ${u || ""}`,
  eur: (n) => n == null ? "—" : `€ ${Number(n).toFixed(2)}`,
  eurShort: (n) => n == null ? "—" : n >= 1000 ? `€ ${(n/1000).toFixed(1)}k` : `€ ${n.toFixed(0)}`,
  date: (s) => s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }) : "—",
  dateFull: (s) => s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—",
  dateShort: (s) => s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }) : "—",
  time: (s) => s ? new Date(s).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—",
  dateTime: (s) => s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—",
  relative: (s) => {
    if (!s) return "—";
    const diff = Math.floor((new Date(s) - new Date()) / 60000);
    const abs = Math.abs(diff);
    if (abs < 60) return diff < 0 ? `${abs}m fa` : `tra ${abs}m`;
    if (abs < 1440) return diff < 0 ? `${Math.floor(abs/60)}h fa` : `tra ${Math.floor(abs/60)}h`;
    return diff < 0 ? `${Math.floor(abs/1440)}g fa` : `tra ${Math.floor(abs/1440)}g`;
  },
  bytes: (n) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`,
  pct: (n) => n == null ? "—" : `${(n * 100).toFixed(0)}%`,
};
// Normalizza qualsiasi input data (stringa "YYYY-MM-DD", ISO completo, Date) a
// mezzogiorno LOCALE. Mezzogiorno evita gli off-by-one da fuso orario / ora legale:
// prima "YYYY-MM-DD" veniva letto come mezzanotte UTC mentre TODAY è locale, sfasando
// di un giorno il conteggio scadenze (lotti, fatture, attestati HACCP) vicino a mezzanotte.
const _toLocalNoon = (v) => {
  if (v == null) return null;
  if (typeof v === "string") {
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
  }
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
};
const daysBetween = (a, b) => {
  const da = _toLocalNoon(a), db = _toLocalNoon(b);
  // Fallback al comportamento storico solo se una delle due date è invalida.
  if (!da || !db) return Math.ceil((new Date(b) - new Date(a)) / 86400000);
  // noon→noon è sempre entro ±1h da multipli di 24h: round dà i giorni di calendario esatti.
  return Math.round((db - da) / 86400000);
};
const uid = (prefix = "") => `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

// Parser numerico robusto per formati italiani/US (mirror di ZucchettiImporter.parsePrice).
// Gestisce migliaia ("1.234,56" → 1234.56), negativi contabili "(12,50)", €/EUR, NBSP/BOM.
// Sostituisce il vecchio parseFloat(replace(",",".")) che rompeva su migliaia e
// trasformava "1.234,56" in 1.234 (errore ×1000 silenzioso sull'incasso).
const parseItNumber = (v) => {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  let s = String(v).trim();
  if (!s) return 0;
  if (s.charCodeAt(0) === 0xFEFF) s = s.slice(1);
  const isNegative = /^\(.*\)$/.test(s);
  s = s.replace(/[()]/g, "").replace(/\s| /g, "").replace(/€|EUR/gi, "");
  if (!s) return 0;
  const lastDot = s.lastIndexOf(".");
  const lastComma = s.lastIndexOf(",");
  if (lastDot > -1 && lastComma > -1) {
    if (lastComma > lastDot) s = s.replace(/\./g, "").replace(",", ".");
    else s = s.replace(/,/g, "");
  } else if (lastComma > -1) {
    s = s.replace(/\./g, "").replace(",", ".");
  }
  const n = parseFloat(s);
  if (!Number.isFinite(n)) return 0;
  return isNegative ? -n : n;
};

// Carica SheetJS (xlsx). Primario: CDN cdnjs (whitelisted in CSP, non gonfia il bundle).
// Fallback: pacchetto bundlato (dipendenza npm) → garantisce l'import Excel anche OFFLINE
// o al primo uso senza rete, quando lo script CDN non è ancora in cache. window.XLSX
// viene riusato se già presente. Promise memoizzata per evitare caricamenti doppi.
let _xlsxPromise = null;
const loadXLSX = () => {
  if (typeof window !== "undefined" && window.XLSX) return Promise.resolve(window.XLSX);
  if (_xlsxPromise) return _xlsxPromise;
  _xlsxPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    s.onload = () => resolve(window.XLSX);
    s.onerror = () => reject(new Error("CDN XLSX irraggiungibile"));
    document.head.appendChild(s);
  })
    .then((x) => x || window.XLSX)
    .catch(async () => {
      // CDN non raggiungibile (offline / cache vuota): usa la copia bundlata.
      const mod = await import("xlsx");
      const XLSX = mod.default || mod;
      if (typeof window !== "undefined") window.XLSX = XLSX;
      return XLSX;
    });
  return _xlsxPromise;
};

const cats = {
  pesce: { label: "Pesce", color: C.teal, icon: Fish },
  farine: { label: "Farine & pasta", color: C.amber, icon: Wheat },
  uova: { label: "Uova", color: C.amber, icon: Circle },
  olio: { label: "Dispensa", color: C.olive, icon: Droplets },
  verdure: { label: "Verdure & frutta", color: C.olive, icon: Wheat },
  carne: { label: "Carne", color: C.terra, icon: Flame },
  latticini: { label: "Latticini", color: C.amber, icon: Droplets },
  altro: { label: "Altro", color: C.muted, icon: Package },
};
const roleColors = { Laboratorio: C.teal, Magazzino: C.amber, Cucina: C.terra, Amministrazione: C.ink };

// ─── HACCP — Categorie critiche per catena del freddo ─────────────────────────
// Reg. CE 853/2004 Allegato III + Reg. CE 852/2004 Allegato II Cap. IX
// Per queste categorie la temperatura di ricezione e' OBBLIGATORIA e bloccante.
const TEMP_CRITICAL_CATS = new Set(["pesce", "carne", "latticini", "uova"]);
const isTempCritical = (cat) => TEMP_CRITICAL_CATS.has(cat);

// Range temperatura ammessi all'ingresso merce per categoria.
// Limiti tarati per tolleranza in trasporto (Reg. CE 853/2004 All. III Sez. VIII per pesce).
const TEMP_RANGES_BY_CAT = {
  pesce:       { min: 0,   max: 4,    label: "Pesce fresco" },        // ghiaccio fondente, tol. trasporto +4
  pesceSurg:   { min: -25, max: -15,  label: "Pesce surgelato" },     // tol. trasporto su -18
  carne:       { min: 0,   max: 7,    label: "Carne fresca" },
  latticini:   { min: 0,   max: 6,    label: "Latticini" },
  uova:        { min: 5,   max: 15,   label: "Uova" },
};
// Risolve il range a partire dalla categoria BP (default = pesce fresco se la categoria
// e' "pesce" senza ulteriori specifiche).
const tempRangeForCat = (cat) => TEMP_RANGES_BY_CAT[cat] || null;

// Reg. UE 1169/2011 art. 24 — TMC ("preferibilmente entro") vs Scadenza ("entro").
// Per pesce/carne/latticini/uova il default e' "consumarsi_entro" (deperibili microbiologicamente);
// per dispensa/oli/farine il default e' "consumarsi_preferibilmente" (TMC).
const defaultExpiryTypeForCat = (cat) => {
  if (TEMP_CRITICAL_CATS.has(cat)) return "consumarsi_entro";
  return "consumarsi_preferibilmente";
};
const statusLabel = {
  disponibile: { t: "Disponibile", c: C.olive, bg: C.oliveWash },
  in_uso: { t: "In uso", c: C.teal, bg: C.tealWash },
  abbattuto: { t: "Abbattuto", c: C.teal, bg: C.tealWash },
  esaurito: { t: "Esaurito", c: C.muted, bg: C.ruleSoft },
  // Stato bloccante per merce critica appena arrivata: NON allocabile finche'
  // l'operatore non conferma temperatura cella + conformita' etichetta.
  in_accettazione: { t: "In accettazione", c: C.amberDeep, bg: C.amberWash },
  quarantena: { t: "Quarantena", c: C.terra, bg: C.terraWash },
  rifiutato: { t: "Rifiutato", c: C.muted, bg: C.ruleSoft },
  in_corso: { t: "In corso", c: C.terra, bg: C.terraWash },
  completato: { t: "Completato", c: C.olive, bg: C.oliveWash },
  completed: { t: "Completato", c: C.olive, bg: C.oliveWash },
  in_progress: { t: "In corso", c: C.terra, bg: C.terraWash },
  pending: { t: "In attesa", c: C.muted, bg: C.ruleSoft },
  bozza: { t: "Bozza", c: C.muted, bg: C.ruleSoft },
  inviato: { t: "Inviato", c: C.amber, bg: C.amberWash },
  parziale: { t: "Parziale", c: C.terra, bg: C.terraWash },
  ricevuto: { t: "Ricevuto", c: C.olive, bg: C.oliveWash },
  da_pagare: { t: "Da pagare", c: C.terra, bg: C.terraWash },
  pagata: { t: "Pagata", c: C.olive, bg: C.oliveWash },
  in_attesa: { t: "In attesa", c: C.amber, bg: C.amberWash },
  completata: { t: "Completata", c: C.olive, bg: C.oliveWash },
  annullata: { t: "Annullata", c: C.muted, bg: C.ruleSoft },
  risolta: { t: "Risolta", c: C.olive, bg: C.oliveWash },
  // FIX P1 (audit 2026-05-19) — Badge per NC HACCP "aperta" / "in_osservazione".
  // Reg. CE 852/2004 art. 5: gli stati delle NC devono essere chiaramente
  // distinguibili in stampa registro mensile.
  aperta: { t: "Aperta", c: C.terra, bg: C.terraWash },
  in_osservazione: { t: "In osservazione", c: C.amber, bg: C.amberWash },
  urgente: { t: "Urgente", c: C.terra, bg: C.terraWash },
  normale: { t: "Normale", c: C.muted, bg: C.ruleSoft },
};

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║               DOMAIN ENGINE — The brain that connects everything          ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
const engine = {
  // ── UNIFIED ITEMS: espone baseProducts come items per retrocompatibilità ───
  // Ogni baseProduct diventa un item (stesso id, field `name` da display)
  getItems: (state) => {
    const bps = (state.baseProducts || []).map(bp => ({
      id: bp.id,
      name: bp.display || bp.nome,
      nomeFull: bp.nome,
      category: bp.cat === "pesce" ? "pesce"
              : bp.cat === "verdura" ? "verdure"
              : bp.cat === "carne" ? "carne"
              : bp.cat === "pasta" ? "farine"
              : bp.cat === "dispensa" ? "olio"
              : "altro",
      unit: bp.unit || "kg",
      minStock: bp.minStock || 2,
      allergens: bp.allergens || [],
      shelfLifeDays: bp.shelfLifeDays || 7,
      preferredSupplier: bp.preferredSupplier || null,
      _bpRef: bp,
    }));
    const legacy = state.items || [];
    return [...legacy, ...bps];
  },

  getItem: (state, itemId) => engine.getItems(state).find(it => it.id === itemId),

  // ── AGGIORNAMENTO PREZZO con cronologia (chiamato dalla bolla o dalla fattura) ─
  // Ritorna nuovo array baseProducts con priceHistory aggiornato.
  // Fix #5 (Patch B2): ora accetta `caloUsed` esplicito per riga DDT — se il pesce
  // arriva gia' sfilettato non va applicato il calo di pulitura globale.
  // updates: [{bpId, newPrice, caloUsed?, source: "ddt"|"fattura"|"manuale", refId, supplierId, date}]
  applyPriceUpdate: (baseProducts, updates) => {
    return baseProducts.map(bp => {
      const u = updates.find(x => x.bpId === bp.id);
      if (!u) return bp;
      const newPFatt = Number(u.newPrice) || 0;
      if (Math.abs(newPFatt - (Number(bp.pFatt) || 0)) < 0.005) return bp; // nessun cambio (tolleranza € 0,005)
      // Se la riga ha un caloOverride esplicito (anche 0 = "gia' lavorato"), usalo;
      // altrimenti fallback al caloTot globale del prodotto base.
      const calo = (typeof u.caloUsed === "number" && Number.isFinite(u.caloUsed))
        ? Math.max(0, Math.min(0.95, u.caloUsed))
        : (Number(bp.caloTot) || 0);
      // Safety cap: oltre 0.95 il calcolo esplode (resa < 5%).
      const safeCalo = calo < 0.95 ? calo : 0.95;
      const newPReale = safeCalo > 0 ? newPFatt / (1 - safeCalo) : newPFatt;
      const history = bp.priceHistory || [];
      history.push({
        at: u.date || TODAY,
        oldPrice: bp.pFatt,
        newPrice: newPFatt,
        deltaPct: bp.pFatt > 0 ? ((newPFatt - bp.pFatt) / bp.pFatt) * 100 : 0,
        source: u.source,
        refId: u.refId,
        supplierId: u.supplierId,
        caloUsed: calo,                 // audit: quale calo e' stato applicato
      });
      return { ...bp, pFatt: newPFatt, pReale: Math.round(newPReale * 10000) / 10000, priceHistory: history.slice(-50), lastPriceUpdate: u.date || TODAY };
    });
  },

  // ── IVA HELPERS ────────────────────────────────────────────────────────────
  // Prezzi listino piatti: memorizzati LORDI (IVA inclusa, come il cliente li vede in menu)
  // Costi materia prima: memorizzati NETTI (da fatture B2B dove l'imponibile è separato dall'IVA)
  // Questi helper convertono in base alla modalità di visualizzazione scelta.

  priceGross: (state) => state.settings?.priceDisplay !== "net",  // true se mostriamo prezzi lordi

  // Un prezzo listino può essere visualizzato lordo o netto (dividi per 1+IVA)
  displaySalePrice: (state, grossPrice, vatRate) => {
    if (grossPrice == null) return 0;
    if (engine.priceGross(state)) return grossPrice;
    const rate = vatRate != null ? vatRate : (state.settings?.vatRateSale || 0.10);
    return grossPrice / (1 + rate);
  },

  // Un costo netto può essere mostrato lordo (aggiungendo l'IVA di acquisto) o netto
  displayCost: (state, netCost, vatRate) => {
    if (netCost == null) return 0;
    if (!engine.priceGross(state)) return netCost;  // modalità netto: mostriamo il costo netto così com'è
    // modalità lordo: aggiungiamo IVA di acquisto per confronto omogeneo col prezzo lordo
    const rate = vatRate != null ? vatRate : (state.settings?.vatRatePurchase || 0.10);
    return netCost * (1 + rate);
  },

  // FIX #5-B — Oneri di trasformazione forfettari (manodopera + energia cottura).
  // Modello scelto: % SUL PREZZO NETTO IVA del piatto (state.settings.oneriTrasformazionePct,
  // 0..100). Restituisce il valore € per unità di piatto. DEFAULT 0 → ritorna 0 (zero impatto).
  // Pura e deterministica: stesso state + stesso prezzo netto → stesso output.
  /**
   * @param {AppState} state
   * @param {number} priceNet — prezzo netto IVA del piatto (€)
   * @returns {number} onere forfettario per unità (€), >= 0
   */
  oneriTrasformazione: (state, priceNet) => {
    const pct = Number(state?.settings?.oneriTrasformazionePct) || 0;
    const pn = Number(priceNet) || 0;
    if (!(pct > 0) || !(pn > 0) || !Number.isFinite(pn)) return 0;
    return pn * (pct / 100);
  },

  // Per simulatore/report: calcola food cost% e margine nei due sistemi
  // grossPrice: prezzo listino lordo del piatto
  // netCost: costo totale netto (somma ingredienti netti)
  // vatSale: IVA del piatto (se override) o default settings
  // vatPurchase: IVA media costi (può essere calcolata come media pesata delle IVA ingredienti)
  // Ritorna anche: oneriUnitari (forfait trasformazione, 0 se disattivato) e marginNet
  // (= margine di contribuzione − oneriUnitari). `margin` (contribuzione) NON è alterato.
  // Quando oneri=0 → marginNet === margin (zero impatto visivo, backward compatible).
  dishEconomics: (state, grossPrice, netCost, vatSale, vatPurchase) => {
    const vs = vatSale != null ? vatSale : (state.settings?.vatRateSale || 0.10);
    const vp = vatPurchase != null ? vatPurchase : (state.settings?.vatRatePurchase || 0.10);
    if (engine.priceGross(state)) {
      // Tutto al lordo: prezzo è già lordo, costo va portato al lordo
      const costLordo = netCost * (1 + vp);
      // Oneri calcolati sul prezzo NETTO (base imponibile), poi riportati al lordo per coerenza
      // con prezzo e costo già in lordo (il forfait è una % del netto).
      const priceNettoForOneri = grossPrice / (1 + vs);
      const oneriNet = engine.oneriTrasformazione(state, priceNettoForOneri);
      const oneriUnitari = oneriNet * (1 + vs);
      const margin = grossPrice - costLordo;
      return { price: grossPrice, cost: costLordo, margin, oneriUnitari, marginNet: margin - oneriUnitari, fcPct: grossPrice > 0 ? (costLordo / grossPrice) * 100 : 0, vatSale: vs, vatPurchase: vp };
    } else {
      // Tutto al netto: prezzo va portato al netto
      const priceNetto = grossPrice / (1 + vs);
      const oneriUnitari = engine.oneriTrasformazione(state, priceNetto);
      const margin = priceNetto - netCost;
      return { price: priceNetto, cost: netCost, margin, oneriUnitari, marginNet: margin - oneriUnitari, fcPct: priceNetto > 0 ? (netCost / priceNetto) * 100 : 0, vatSale: vs, vatPurchase: vp };
    }
  },

  // Per un piatto con ingredienti: calcola IVA media pesata dei costi
  // Ritorna {netCost, avgVatPurchase, hasOverrides}
  computeDishCostWithVat: (state, dish) => {
    let netCost = 0;
    let costWeightedVat = 0;
    let hasOverrides = false;
    const defaultVatP = state.settings?.vatRatePurchase || 0.10;
    (dish.ingredienti || []).forEach(ing => {
      const bp = ing.bpId ? state.baseProducts?.find(b => b.id === ing.bpId) : null;
      let cost = 0;
      let ingVat = defaultVatP;
      if (bp) {
        cost = (bp.pReale || bp.pFatt || 0) * ing.qty;
        if (bp.vatRate != null) { ingVat = bp.vatRate; hasOverrides = true; }
      } else {
        cost = ing.prezzo || 0;
      }
      netCost += cost;
      costWeightedVat += cost * ingVat;
    });
    const avgVatPurchase = netCost > 0 ? costWeightedVat / netCost : defaultVatP;
    return { netCost: Math.round(netCost * 10000) / 10000, avgVatPurchase, hasOverrides };
  },

  // ── DISH ALLERGENS ────────────────────────────────────────────────────────
  // Reg. UE 1169/2011 art. 9 — informazione obbligatoria al consumatore finale
  // sui 14 allergeni dichiarati. Anche nella ristorazione (mensa, asporto, sala).
  // FIX P0-04 (audit 2026-05-19): aggregazione UNION degli allergeni di tutti
  // i baseProducts referenziati dagli ingredienti del piatto. Stringhe lowercase
  // trim per normalizzare casing inconsistente in legacy data. Ritorna Set per
  // permettere boolean .has() in chiamanti diversi.
  // Responsabilita' penale art. 590 c.p. in caso di shock anafilattico cliente.
  dishAllergens: (state, dish) => {
    const set = new Set();
    if (!dish || !Array.isArray(dish.ingredienti)) return set;
    const bps = state.baseProducts || [];
    for (const ing of dish.ingredienti) {
      if (!ing?.bpId) continue;
      const bp = bps.find(b => b.id === ing.bpId);
      if (!bp || !Array.isArray(bp.allergens)) continue;
      for (const a of bp.allergens) {
        if (a == null) continue;
        const norm = String(a).trim().toLowerCase();
        if (norm) set.add(norm);
      }
    }
    return set;
  },

  // ═══ PROIEZIONI STATISTICHE ══════════════════════════════════════════════
  // Metriche giornaliere aggregate dagli scontrini
  dailyMetrics: (state, fromDate = null, toDate = null) => {
    const receipts = state.tableReceipts || [];
    // Pre-index VAT per evitare lookup O(n²) (dish→vatRate) dentro al loop.
    const dishes = state.dishesReal || [];
    const defaultVat = state.settings?.vatRateSale || 0.10;
    const dishVatById = new Map();
    dishes.forEach(d => {
      const v = (d.vatRate != null && Number.isFinite(d.vatRate)) ? d.vatRate : defaultVat;
      dishVatById.set(d.id, v);
    });
    const lineVat = (it) => {
      const snap = it.recipeSnapshot?.vatRate;
      if (snap != null && Number.isFinite(snap)) return snap;
      const cur = dishVatById.get(it.dishId);
      if (cur != null && Number.isFinite(cur)) return cur;
      return defaultVat;
    };

    const byDay = {};
    receipts.forEach(r => {
      // Usa la data LOCALE dello scontrino, non UTC — altrimenti scontrini chiusi tardi la sera
      // vengono contati nel giorno successivo (o viceversa) per errore di timezone.
      const day = getServiceDate(r.closedAt);
      if (!day) return;
      if (fromDate && day < fromDate) return;
      if (toDate && day > toDate) return;
      // FIX F-16 (cross-check 2026-05-19): aggiunto revenueNonFiscal per analytics ESTRATTO CONTO.
      // FIX Batch 3 — riconciliazione EC: EC riconciliato escluso da revenue (già nello SF collegato).
      // revenueNonFiscal = solo EC orfani (non ancora riconciliati, non confermati nero definitivo).
      // revenueNeroDefinitivo = EC confermati nero definitivo (audit interno separato).
      if (!byDay[day]) byDay[day] = { date: day, revenue: 0, revenueNet: 0, revenueNonFiscal: 0, revenueNeroDefinitivo: 0, covers: 0, receipts: 0, tableReceipts: 0, bancoReceipts: 0, bancoRev: 0, dishes: 0, dayOfWeek: new Date(day + "T12:00:00").getDay() };
      const tot = Number(r.total) || 0;
      const cov = Number(r.covers) || 0;
      const isEC = !!(r._isPreconto || r._docType === "ESTRATTO CONTO");
      const isECReconciled = isEC && r._regularizedTo && r._regularizedTo !== "NERO_DEFINITIVO";
      const isECNeroDefinitivo = isEC && r._regularizedTo === "NERO_DEFINITIVO";
      // EC riconciliato: escluso dal totale (il suo importo è già nello SF collegato)
      if (!isECReconciled) byDay[day].revenue += tot;
      if (isEC && !r._regularizedTo) byDay[day].revenueNonFiscal += tot; // solo orfani
      if (isECNeroDefinitivo) byDay[day].revenueNeroDefinitivo += tot;
      // EC riconciliato: coperti e conteggi esclusi (già nella riga dello SF collegato)
      if (!isECReconciled) {
        byDay[day].covers += cov;
        byDay[day].receipts += 1;
        if (cov > 0) byDay[day].tableReceipts += 1;
        else { byDay[day].bancoReceipts += 1; byDay[day].bancoRev += tot; }
      }
      // Scorporo IVA per riga (mai media unica — riga vino 22% va separata da riga cibo 10%)
      // EC riconciliato: items già conteggiati nello SF → skip per evitare doppio conteggio food cost.
      if (!isECReconciled) {
        (r.items || []).forEach(it => {
          const qty = Number(it.qty) || 0;
          const unitPrice = Number(it.unitPrice) || 0;
          const grossLine = qty * unitPrice;
          const vat = lineVat(it);
          byDay[day].dishes += qty;
          byDay[day].revenueNet += vat > -1 ? grossLine / (1 + vat) : grossLine;
        });
      }
    });
    return Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date));
  },

  // ── SERIE GIORNALIERA COMBINATA (storico aggregato + scontrini) ─────────────
  // UNIONE di:
  //   1) state.historicalDaily  → record {date, revenue, covers, ...} importati da
  //      "import conta" (storico 2024-25, mesi aperti ~mar→ott; ristorante stagionale).
  //   2) la serie derivata dagli scontrini (engine.dailyMetrics → {date, revenue, covers}).
  // Output: SOLO {date, revenue, covers, dayOfWeek, source} per giorno — NON ha items,
  // food cost, IVA, banco/tavolo. Serve esclusivamente a trend / stagionalità / grafico.
  // dailyMetrics resta receipt-only e alimenta il food cost: questa è una serie SEPARATA.
  //
  // Dedup per data: storico (2024-25) e scontrini (2026) non si sovrappongono. Se per caso
  // una data esiste in entrambi, VINCE lo scontrino (dato fiscale ricostruito, più ricco).
  // PURA. Backward-compat: se historicalDaily è vuoto → identica alla serie scontrini.
  combinedDailySeries: (state, metrics) => {
    const dm = metrics || engine.dailyMetrics(state);
    const byDay = {};
    // 1) Storico aggregato (priorità BASSA)
    (state.historicalDaily || []).forEach(r => {
      const date = String(r.date || "").slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
      const revenue = Number(r.revenue) || 0;
      const covers = Number(r.covers) || 0;
      byDay[date] = {
        date,
        revenue,
        covers,
        dayOfWeek: new Date(date + "T12:00:00").getDay(),
        source: "historical",
      };
    });
    // 2) Scontrini (priorità ALTA → sovrascrive eventuale collisione di data)
    (dm || []).forEach(d => {
      byDay[d.date] = {
        date: d.date,
        revenue: Number(d.revenue) || 0,
        covers: Number(d.covers) || 0,
        dayOfWeek: d.dayOfWeek != null ? d.dayOfWeek : new Date(d.date + "T12:00:00").getDay(),
        source: "receipts",
      };
    });
    return Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date));
  },

  // ── STORICO AGGREGATI ANNO ─────────────────────────────────────────────────
  // Ritorna statistiche aggregate per un anno dal historicalDaily.
  // { year, incasso, coperti, mediaCoperto, nGiorni, pranzoInc, pranzoCop, cenaInc, cenaCop,
  //   byMonth: [{month:0..11, incasso, coperti, nGiorni}] }
  // Solo giorni con dati; mesi chiusi (stagionale) non compaiono in byMonth.
  historicalYearStats: (state, year) => {
    const rows = (state.historicalDaily || []).filter(r => {
      const d = String(r.date || "").slice(0, 10);
      return d.startsWith(String(year) + "-") && /^\d{4}-\d{2}-\d{2}$/.test(d);
    });
    if (rows.length === 0) return null;
    const byMonth = {};
    let incasso = 0, coperti = 0, pranzoInc = 0, pranzoCop = 0, cenaInc = 0, cenaCop = 0;
    rows.forEach(r => {
      const rev = Number(r.revenue) || 0;
      const cov = Number(r.covers) || 0;
      const mIdx = parseInt(r.date.slice(5, 7), 10) - 1; // 0-based
      incasso += rev;
      coperti += cov;
      pranzoInc += Number(r.pranzoRevenue) || 0;
      pranzoCop += Number(r.pranzoCovers) || 0;
      cenaInc += Number(r.cenaRevenue) || 0;
      cenaCop += Number(r.cenaCovers) || 0;
      if (!byMonth[mIdx]) byMonth[mIdx] = { month: mIdx, incasso: 0, coperti: 0, nGiorni: 0 };
      byMonth[mIdx].incasso += rev;
      byMonth[mIdx].coperti += cov;
      byMonth[mIdx].nGiorni += 1;
    });
    return {
      year,
      incasso,
      coperti,
      mediaCoperto: coperti > 0 ? incasso / coperti : 0,
      nGiorni: rows.length,
      pranzoInc,
      pranzoCop,
      cenaInc,
      cenaCop,
      byMonth: Object.values(byMonth).sort((a, b) => a.month - b.month),
    };
  },

  // ── PRODOTTI STORICI PER ANNO ──────────────────────────────────────────────
  // Ritorna items dell'anno ordinati per valore desc + mappa byCategoria.
  // Ritorna null se l'anno non ha dati in historicalProducts.
  historicalProductsForYear: (state, year) => {
    const rec = (state.historicalProducts || []).find(r => r.year === year);
    if (!rec || !Array.isArray(rec.items) || rec.items.length === 0) return null;
    const items = [...rec.items].sort((a, b) => (Number(b.valore) || 0) - (Number(a.valore) || 0));
    const byCategoria = {};
    rec.items.forEach(it => {
      const cat = it.reparto || "Altro";
      if (!byCategoria[cat]) byCategoria[cat] = { qty: 0, valore: 0 };
      byCategoria[cat].qty += Number(it.qty) || 0;
      byCategoria[cat].valore += Number(it.valore) || 0;
    });
    return { year, items, byCategoria };
  },

  // ── LOOKUP SINGOLO GIORNO STORICO ─────────────────────────────────────────
  // Ritorna il record di historicalDaily per quella data esatta, oppure null.
  historicalForDate: (state, dateISO) => {
    if (!dateISO) return null;
    const d = String(dateISO).slice(0, 10);
    return (state.historicalDaily || []).find(r => String(r.date || "").slice(0, 10) === d) || null;
  },

  // ── CONFRONTO ANNO-SU-ANNO PER DATA ───────────────────────────────────────
  // Dato un giorno (es. "2026-06-10"), trova lo STESSO GIORNO DI CALENDARIO
  // (stesso mese+giorno, anno sostituito) negli anni presenti in historicalDaily.
  // Gestisce 29/02 inesistente → null per quell'anno.
  //
  // Ritorna:
  // {
  //   current: { date, revenue, covers, dayOfWeek } | null,
  //   years: [{ year, date, revenue, covers, dayOfWeek, deltaPctVsCurrent, weekdayDiffers }]
  // }
  // "current" viene popolato prima dai dailyMetrics (scontrini), poi da historicalDaily.
  // La priorità degli scontrini rispecchia combinedDailySeries.
  yoyForDate: (state, dateISO) => {
    if (!dateISO) return { current: null, years: [] };
    const targetDate = String(dateISO).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(targetDate)) return { current: null, years: [] };

    const [targetYear, targetMM, targetDD] = targetDate.split("-");
    const targetMD = `${targetMM}-${targetDD}`; // MM-DD per confronto

    // "current": cerca prima negli scontrini (dailyMetrics)
    const dm = engine.dailyMetrics(state);
    const fromReceipts = (dm || []).find(d => d.date === targetDate);
    let current = null;
    if (fromReceipts) {
      current = {
        date: targetDate,
        revenue: Number(fromReceipts.revenue) || 0,
        covers: Number(fromReceipts.covers) || 0,
        dayOfWeek: fromReceipts.dayOfWeek != null ? fromReceipts.dayOfWeek : new Date(targetDate + "T12:00:00").getDay(),
      };
    } else {
      const fromHist = engine.historicalForDate(state, targetDate);
      if (fromHist) {
        current = {
          date: targetDate,
          revenue: Number(fromHist.revenue) || 0,
          covers: Number(fromHist.covers) || 0,
          dayOfWeek: new Date(targetDate + "T12:00:00").getDay(),
        };
      }
    }

    // Anni presenti nello storico (escludi anno corrente)
    const histYears = [...new Set((state.historicalDaily || [])
      .map(r => String(r.date || "").slice(0, 4))
      .filter(y => /^\d{4}$/.test(y) && y !== targetYear)
    )].sort();

    const targetDow = new Date(targetDate + "T12:00:00").getDay();

    const years = histYears.map(y => {
      // Costruisci data stesso mese+giorno in quell'anno
      const candidate = `${y}-${targetMM}-${targetDD}`;
      // Verifica che la data esista (gestione 29/02)
      const dt = new Date(candidate + "T12:00:00");
      const isValid = !isNaN(dt.getTime()) &&
        dt.getFullYear() === parseInt(y, 10) &&
        (dt.getMonth() + 1) === parseInt(targetMM, 10) &&
        dt.getDate() === parseInt(targetDD, 10);
      if (!isValid) return null; // 29/02 inesistente in anno non bisestile
      const rec = engine.historicalForDate(state, candidate);
      if (!rec) return null; // nessun dato per quel giorno
      const rev = Number(rec.revenue) || 0;
      const cov = Number(rec.covers) || 0;
      const dow = dt.getDay();
      const deltaPctVsCurrent = current && current.revenue > 0
        ? ((rev - current.revenue) / current.revenue) * 100
        : null;
      return {
        year: parseInt(y, 10),
        date: candidate,
        revenue: rev,
        covers: cov,
        dayOfWeek: dow,
        deltaPctVsCurrent,
        weekdayDiffers: dow !== targetDow,
      };
    }).filter(Boolean);

    return { current, years };
  },

  // Media trimmed (rimuove top/bottom 10% come outlier)
  trimmedMean: (values, trim = 0.1) => {
    if (!values || values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const cut = Math.floor(sorted.length * trim);
    const kept = cut > 0 ? sorted.slice(cut, -cut) : sorted;
    if (kept.length === 0) return sorted[Math.floor(sorted.length / 2)];
    return kept.reduce((s, v) => s + v, 0) / kept.length;
  },

  // Deviazione standard
  stdDev: (values, mean = null) => {
    if (!values || values.length < 2) return 0;
    const m = mean != null ? mean : values.reduce((s, v) => s + v, 0) / values.length;
    const variance = values.reduce((s, v) => s + (v - m) ** 2, 0) / (values.length - 1);
    return Math.sqrt(variance);
  },

  // Baseline per giorno della settimana (0=domenica, 1=lunedi, ...)
  dayOfWeekBaseline: (metrics) => {
    const baseline = {};
    for (let dow = 0; dow < 7; dow++) {
      const days = metrics.filter(m => m.dayOfWeek === dow);
      if (days.length === 0) {
        baseline[dow] = { revenue: 0, covers: 0, stdDev: 0, n: 0, confidence: "nessuno" };
      } else {
        const revs = days.map(d => d.revenue);
        const covs = days.map(d => d.covers);
        const meanR = engine.trimmedMean(revs);
        const stdR = engine.stdDev(revs, meanR);
        baseline[dow] = {
          revenue: meanR,
          covers: engine.trimmedMean(covs),
          stdDev: stdR,
          cv: meanR > 0 ? stdR / meanR : 0,
          n: days.length,
          confidence: days.length >= 6 ? "alta" : days.length >= 3 ? "media" : days.length >= 1 ? "bassa" : "nessuno",
        };
      }
    }
    return baseline;
  },

  // Trend settimanale weekday-adjusted (FIX A.3 audit 2026-05-20).
  //
  // PRIMA: confronto naïve sum(last7) vs sum(prev7). Su un ristorante costa adriatica
  // a cavallo maggio→giugno restituiva +30% "trend" che era SOLO stagionalità weekday
  // × stagione turistica (es. ultima settimana ha più sabati pieni). Confondeva
  // segnale strutturale (crescita reale) con segnale stagionale (ciclo annuale).
  //
  // ORA: regressione lineare sul residuo dopo rimozione effetto giorno-della-settimana
  // (vedi src/lib/stats.js → weekdayAdjustedTrend). Richiede ≥28gg (4 settimane piene).
  // Sotto 28gg: reliable=false, pct=0 (nessun trend "inventato" su rumore stagionale).
  //
  // Output backward-compatible:
  //   { pct, reliable, method, rSquared, nUsed }
  weeklyTrend: (metrics) => {
    if (!metrics || metrics.length < 14) return { pct: 0, reliable: false, method: "n/d", rSquared: 0, nUsed: 0 };
    const series = metrics.map(m => ({ date: m.date, revenue: m.revenue }));
    const r = weekdayAdjustedTrend(series);
    if (!r.valid) {
      // n<28 o varianza nulla: niente trend, esposto come non affidabile.
      return { pct: 0, reliable: false, method: "weekday-adjusted (insufficient data)", rSquared: 0, nUsed: r.nUsed };
    }
    return {
      pct: r.slopeWeekly,
      reliable: r.nUsed >= 28,
      method: "weekday-adjusted OLS",
      rSquared: r.rSquared,
      nUsed: r.nUsed,
    };
  },

  // Proiezione per N giorni in avanti.
  //
  // FIX #11 — onestà statistica:
  //   (A) Compound trend (geometrico) invece di lineare:
  //         trendMult(i) = (1 + trend.pct) ^ (i/7)
  //       Un +20%/sett a 30gg dà 2.184× (non 1.857×) — evita underestimate
  //       sistematica del fabbisogno materia prima.
  //   (B) Banda CI 95% che cresce con √h (random walk teorico):
  //         sigma_h = baseline.stdDev × √max(1, i/7)
  //         ciHalf = 1.96 × sigma_h
  //       Banda diversa al gg+1 e al gg+30. Niente "falsa precisione".
  //   (C) Hard stop a 2× la lunghezza dello storico: con 20gg di storia
  //       chiedere 30gg significa pura estrapolazione. Tronchiamo onestamente
  //       a maxUsefulHorizon = min(nDays, dataPoints * 2) e segnaliamo
  //       horizonCapped=true alla UI.
  //   (D) Trend non significativo (n<21gg) → si assume zero trend per non
  //       amplificare rumore. Il valore stimato resta esposto in `trend.pct`
  //       ma non viene applicato all'expected.
  //
  // Banda "naïve normal-symmetric": il low è cappato a 0 (Math.max). Per
  // distribuzioni log-normali (più realistiche sul ticket medio F&B) la
  // banda corretta sarebbe asimmetrica. Roadmap: passare a quantile log-normal
  // se cappedRate > 30% dei punti. Per ora segnaliamo `lowCapped` per
  // diagnostica.

  // ── Indice stagionale mensile (FIX #6 audit 2026-06-04) ────────────────────
  //
  // Restituisce un vettore di 12 moltiplicatori (gen..dic) normalizzati a media 1
  // più i metadati sulla FONTE, per essere ONESTI in UI su cosa poggia il forecast.
  //
  // DUE FONTI, con priorità:
  //   1. APPRESO dai dati — SOLO SE ≥2 cicli annuali per ogni mese coinvolto.
  //      Andi oggi NON ha 2 anni di storico, quindi questa fonte parte solo quando
  //      i dati lo consentono davvero. Decomposizione robusta a indici stagionali:
  //      indice[m] = mediana(revenue giorni del mese m) / mediana(revenue tutti i
  //      giorni), poi normalizzato a media 1. Robusto (mediana) e interpretabile.
  //      REQUISITO: per ogni mese serve ≥2 ANNI distinti di osservazione di quel
  //      mese (≥2 cicli). Sotto soglia → questa fonte NON si attiva (regola d'oro:
  //      mai stagionalità su <2 cicli). Niente Holt-Winters annuale: con <2 anni
  //      produrrebbe spazzatura/fallback. Non falsifichiamo ciò che i dati non
  //      supportano.
  //   2. MANUALE — i 12 coefficienti in state.settings.seasonalityMonthly
  //      (default neutro = tutti 1). È la fonte primaria OGGI. È una STIMA UMANA:
  //      quando applicata, il forecast ALLARGA la banda CI (vedi seasonalCIInflation)
  //      e lo SEGNALA in UI — niente falsa precisione su un'assunzione.
  //
  // Output: { index:[12], source:"learned"|"manual"|"neutral", cyclesByMonth:[12],
  //           minCyclesUsed, isAssumed (true se manuale/non appreso) }
  seasonalIndex: (state, metrics) => {
    // FONTE: serie COMBINATA (storico aggregato 2024-25 + scontrini 2026), NON solo
    // scontrini. Con 2 anni di storico import_conta la soglia ≥2 cicli scatta e la
    // stagionalità diventa APPRESA. `metrics` (se passato) è la serie scontrini, che
    // riuso per non ricalcolare dailyMetrics, ma il segnale stagionale è sull'unione.
    const m = engine.combinedDailySeries(state, metrics);

    // ── Fonte 1: tentativo APPRESO (≥2 cicli per mese) ───────────────────────
    // Conta gli ANNI distinti osservati per ogni mese e raccoglie i revenue.
    const yearsByMonth = Array.from({ length: 12 }, () => new Set());
    const revByMonth = Array.from({ length: 12 }, () => []);
    // anni distinti su TUTTA la serie (per capire quali mesi sono "chiusi": ~0 ricavo
    // in ogni anno osservato). Un mese assente su tutti gli anni = stagione chiusa.
    const allYears = new Set();
    const allRev = [];
    (m || []).forEach(d => {
      const rev = Number(d.revenue);
      const dt = new Date(String(d.date) + "T12:00:00");
      allYears.add(dt.getFullYear());
      if (!Number.isFinite(rev) || rev <= 0) return;
      const mo = dt.getMonth();
      yearsByMonth[mo].add(dt.getFullYear());
      revByMonth[mo].push(rev);
      allRev.push(rev);
    });
    const cyclesByMonth = yearsByMonth.map(s => s.size);
    // Mesi con almeno 1 osservazione (gli unici che la decomposizione può stimare)
    const monthsObserved = cyclesByMonth.map((c, i) => (c > 0 ? i : -1)).filter(i => i >= 0);
    const minCyclesObserved = monthsObserved.length > 0
      ? Math.min(...monthsObserved.map(i => cyclesByMonth[i]))
      : 0;
    // REQUISITO ≥2 cicli: ogni mese OSSERVATO deve avere ≥2 anni distinti, e devono
    // esserci ≥2 mesi distinti (altrimenti non c'è "stagionalità" da apprendere).
    const canLearn = monthsObserved.length >= 2 && minCyclesObserved >= 2 && allRev.length >= 24;

    if (canLearn) {
      // MESI CHIUSI: con ≥2 anni di storia, un mese senza alcun ricavo in NESSUN anno
      // è stagione chiusa (es. nov-feb per ristorante stagionale di costa). Indice 0
      // → forecast ~0 per quei mesi. Richiede almeno 2 anni per non confondere "chiuso
      // strutturale" con "mese non ancora coperto dai dati del primo anno parziale".
      const closedMonths = (allYears.size >= 2)
        ? cyclesByMonth.map((c, i) => (c === 0 ? i : -1)).filter(i => i >= 0)
        : [];

      // MEDIA DI NORMALIZZAZIONE: SOLO sui mesi APERTI (con dati). Così gli indici dei
      // mesi aperti restano centrati ~1 e proiettare DENTRO la stagione aperta non viene
      // schiacciato dagli zeri dei mesi chiusi. I mesi chiusi restano forzati a 0.
      const openRev = [];
      for (const mo of monthsObserved) openRev.push(...revByMonth[mo]);
      const med = (vals) => {
        if (!vals.length) return 0;
        const s = [...vals].sort((a, b) => a - b); const n = s.length;
        return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
      };
      const medOpen = med(openRev);
      // raw: indice grezzo per mese. Mesi chiusi → 0, mesi aperti → mediana/medOpen,
      // mesi non osservati ma NON dichiarati chiusi (storia insufficiente) → 1 (neutro).
      const raw = SEASONALITY_NEUTRAL.slice();
      for (let mo = 0; mo < 12; mo++) {
        if (closedMonths.includes(mo)) { raw[mo] = 0; continue; }
        if (revByMonth[mo].length > 0) {
          raw[mo] = medOpen > 0 ? med(revByMonth[mo]) / medOpen : 1;
        } else {
          raw[mo] = 1; // mese non chiuso ma non osservato → neutro
        }
      }
      // Normalizzazione a media 1 sui SOLI mesi aperti (esclude gli 0 dei chiusi),
      // poi reimposta a 0 i chiusi (la normalizzazione non li deve "riempire").
      const openIdxValues = raw.filter((_, i) => !closedMonths.includes(i));
      const meanOpen = openIdxValues.length
        ? openIdxValues.reduce((s, v) => s + v, 0) / openIdxValues.length
        : 1;
      const index = raw.map((v, i) => {
        if (closedMonths.includes(i)) return 0;
        return meanOpen > 0 ? v / meanOpen : 1;
      });
      return {
        index, source: "learned",
        cyclesByMonth, minCyclesUsed: minCyclesObserved,
        closedMonths,                 // [int] mesi a indice 0 (stagione chiusa)
        yearsObserved: allYears.size, // n° anni distinti nella serie combinata
        isAssumed: false,
      };
    }

    // ── Fonte 2: MANUALE / NEUTRO ────────────────────────────────────────────
    const manual = state.settings?.seasonalityMonthly;
    const index = normalizeSeasonality(manual);
    // È neutro se tutti i coefficienti (post-normalizzazione) sono ≈1.
    const isNeutral = index.every(v => Math.abs(v - 1) < 1e-9);
    return {
      index,
      source: isNeutral ? "neutral" : "manual",
      cyclesByMonth, minCyclesUsed: minCyclesObserved,
      closedMonths: [],           // profilo manuale/neutro: nessun mese forzato a 0
      yearsObserved: 0,
      // isAssumed=true solo quando un profilo NON neutro e NON appreso viene applicato:
      // è una stima umana → allarghiamo la banda CI e lo segnaliamo.
      isAssumed: !isNeutral,
    };
  },

  // ── Modello di FORMA della domanda (stagionalità di dettaglio) ─────────────
  //
  // ADDITIVO / read-only. NON tocca seasonalIndex né forecastNDays: è un modello
  // SEPARATO che combina il LIVELLO reale mensile (preferito quando appreso) con
  // la FORMA RELATIVA giorno×servizio distillata dall'archivio Pienissimo
  // (state.voyaSeasonality). Sarà la base per gli "attesi reali" (fase C futura).
  //
  // VINCOLO DI DOMINIO CRITICO: i coperti Pienissimo sono DOMANDA LORDA (prenotato:
  // include rifiuti/cancellati/no-show), NON presenze reali. La capienza fisica reale
  // è ~260 coperti/servizio. Perciò questo modello usa Pienissimo SOLO per la FORMA
  // RELATIVA (quale giorno/servizio pesa di più), MAI per livelli assoluti di presenze.
  // Etichettare sempre "domanda storica (prenotato), non presenze".
  //
  // CONVENZIONI INDICI:
  //   · cells di voyaSeasonality hanno chiave "<mese 1-12>|<dow 0=lun..6=dom>|<servizio>"
  //     con servizio "pranzo" | "cena" | "na".
  //   · In OUTPUT il mese è chiave 0-11 (m0 = mese-1) per coerenza con seasonalIndex
  //     e con JS Date.getMonth(). Il dow RESTA 0=lunedì..6=domenica (convenzione dei dati).
  //
  // Puro, nessun side-effect. Guardie su ogni divisione: nessun NaN anche con
  // cells mancanti/parziali.
  voyaDemandModel: (state) => {
    const vs = state?.voyaSeasonality;
    if (!vs) return { available: false };

    // ── LIVELLO MENSILE (relativo, media~1) ─────────────────────────────────
    // Preferisci il REALE appreso da engine.seasonalIndex. Se non "learned",
    // ripiega sulla forma mensile di Pienissimo (monthCovers normalizzati).
    const si = engine.seasonalIndex(state);
    let monthLevel, monthLevelSource, closedMonths;
    if (si && si.source === "learned") {
      monthLevel = (si.index || []).slice(0, 12);
      while (monthLevel.length < 12) monthLevel.push(1);
      monthLevelSource = "reale";
      closedMonths = (si.closedMonths || []).slice();
    } else {
      // Normalizza monthCovers (chiavi "1".."12") a media 1 sui mesi con coperti>0.
      const covByM = [];
      for (let m = 1; m <= 12; m++) covByM.push(Number(vs.monthCovers?.[m]) || 0);
      const openVals = covByM.filter(v => v > 0);
      const mean = openVals.length ? openVals.reduce((s, v) => s + v, 0) / openVals.length : 0;
      monthLevel = covByM.map(v => (mean > 0 ? v / mean : 0));
      monthLevelSource = "pienissimo";
      closedMonths = covByM.map((v, i) => (v === 0 ? i : -1)).filter(i => i >= 0);
    }

    // ── FORMA dow×servizio da cells (RELATIVA), accumulata per mese0 ─────────
    const dowCov = Array.from({ length: 12 }, () => new Array(7).fill(0)); // [m0][dow] → coperti
    const svcCov = Array.from({ length: 12 }, () => ({ pranzo: 0, cena: 0 }));
    const cells = vs.cells || {};
    for (const key in cells) {
      const parts = String(key).split("|");
      if (parts.length < 3) continue;
      const mo = parseInt(parts[0], 10);   // 1-12
      const dow = parseInt(parts[1], 10);  // 0=lun..6=dom
      const svc = parts[2];                // "pranzo" | "cena" | "na"
      if (!(mo >= 1 && mo <= 12)) continue;
      const m0 = mo - 1;
      const cov = Number(cells[key] && cells[key].cov) || 0;
      if (cov <= 0) continue;
      if (dow >= 0 && dow <= 6) dowCov[m0][dow] += cov;
      if (svc === "pranzo") svcCov[m0].pranzo += cov;
      else if (svc === "cena") svcCov[m0].cena += cov;
      // "na" contribuisce al profilo giorno ma non allo split pranzo/cena
    }

    // Quote per mese: dow che sommano 1, split servizio che somma 1 (solo mesi con dati).
    const dowShareByMonth = {};
    const serviceSplitByMonth = {};
    const openMonths = [];
    for (let m0 = 0; m0 < 12; m0++) {
      const tot = dowCov[m0].reduce((s, v) => s + v, 0);
      if (tot > 0) {
        dowShareByMonth[m0] = dowCov[m0].map(v => v / tot);
        openMonths.push(m0);
      }
      const sp = svcCov[m0].pranzo, ce = svcCov[m0].cena, st = sp + ce;
      if (st > 0) serviceSplitByMonth[m0] = { pranzoFrac: sp / st, cenaFrac: ce / st };
    }

    // ── Aggregati su TUTTI i mesi aperti (pesati per coperti) ────────────────
    const dowAll = new Array(7).fill(0);
    let pranzoAll = 0, cenaAll = 0;
    for (let m0 = 0; m0 < 12; m0++) {
      for (let d = 0; d < 7; d++) dowAll[d] += dowCov[m0][d];
      pranzoAll += svcCov[m0].pranzo;
      cenaAll += svcCov[m0].cena;
    }
    const dowAllTot = dowAll.reduce((s, v) => s + v, 0);
    const dowShareOverall = dowAllTot > 0 ? dowAll.map(v => v / dowAllTot) : new Array(7).fill(0);
    const svcAllTot = pranzoAll + cenaAll;
    const serviceSplitOverall = svcAllTot > 0
      ? { pranzoFrac: pranzoAll / svcAllTot, cenaFrac: cenaAll / svcAllTot }
      : { pranzoFrac: 0, cenaFrac: 0 };

    // ── Top 3 party-size con % ───────────────────────────────────────────────
    const ps = vs.partySize || {};
    const psTot = Object.values(ps).reduce((s, v) => s + (Number(v) || 0), 0);
    const partyTop = Object.entries(ps)
      .map(([n, c]) => ({ pax: parseInt(n, 10), count: Number(c) || 0 }))
      .filter(x => Number.isFinite(x.pax) && x.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .map(x => ({ pax: x.pax, count: x.count, pct: psTot > 0 ? x.count / psTot : 0 }));

    return {
      available: true,
      monthLevel, monthLevelSource, closedMonths,
      dowShareByMonth, serviceSplitByMonth,
      dowShareOverall, serviceSplitOverall,
      openMonths, // mesi0 con profilo dow disponibile (comodo per la UI)
      // passthrough per la UI
      reservationsUnique: vs.reservationsUnique,
      coversTotal: vs.coversTotal,
      cenaPranzoRatio: vs.cenaPranzoRatio,
      leadtimeMedianDays: vs.leadtimeMedianDays,
      leadtimeWithin24hPct: vs.leadtimeWithin24hPct,
      partyTop,
    };
  },

  // ── MODELLO ATTESI REALI (coperti-presenze per mese×dow×servizio) ──────────
  //
  // ADDITIVO / puro / read-only. Fratello di voyaDemandModel, NON tocca
  // seasonalIndex/forecastNDays/dailyMetrics. Produce un oggetto COMPATTO (poche
  // KB) con 168 celle "<mese 1-12>|<dow 0=lun..6=dom>|<servizio pranzo|cena>".
  //
  // Fonte PRIMARIA: state.historicalDaily (presenze reali già nette, campi
  // pranzoCovers/cenaCovers). Se una cella ha ≥3 giorni reali → mediana +
  // 25°/75° percentile; se 1-2 giorni → media ±20%; from="real".
  // FALLBACK (cella senza giorni reali ma con archivio Pienissimo): stima dalla
  // FORMA per-cella di Pienissimo (cells[m|dow|svc].cov, DOMANDA LORDA) convertita
  // a presenze con calibrazione (pranzo×1.37, cena×0.91) e ancorata al livello
  // reale mensile quando disponibile; range ±25%; from="pienissimo".
  // CAP fisico 260 coperti/servizio SEMPRE. Guardie anti-NaN ovunque.
  buildExpectedCoversModel: (state) => {
    const CAP = 260;
    const CALIB = { pranzo: 1.37, cena: 0.91 };
    const builtAt = new Date().toISOString();

    // ── Raccolta campioni reali per cella + per mese/servizio (ancora) ────────
    // realByCell["<m>|<dow>|<svc>"] = [coperti...] (solo servizi APERTI, cov>0)
    // realMonthSvc[m][svc] = [coperti giornalieri...]  (per l'ancora mensile)
    const realByCell = {};
    const realMonthSvc = {};
    for (let m = 1; m <= 12; m++) realMonthSvc[m] = { pranzo: [], cena: [] };
    const pushCell = (m, dow, svc, val) => {
      if (!(val > 0)) return;
      const k = `${m}|${dow}|${svc}`;
      (realByCell[k] || (realByCell[k] = [])).push(val);
      realMonthSvc[m][svc].push(val);
    };
    const hist = Array.isArray(state?.historicalDaily) ? state.historicalDaily : [];
    for (const r of hist) {
      const date = String(r?.date || "").slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
      const m = parseInt(date.slice(5, 7), 10);
      if (!(m >= 1 && m <= 12)) continue;
      const jsDay = new Date(date + "T12:00:00").getDay(); // 0=dom..6=sab
      const dow = (jsDay + 6) % 7;                          // 0=lun..6=dom
      pushCell(m, dow, "pranzo", Number(r.pranzoCovers) || 0);
      pushCell(m, dow, "cena", Number(r.cenaCovers) || 0);
    }
    const mean = (a) => (a && a.length ? a.reduce((s, v) => s + v, 0) / a.length : null);
    // Percentile lineare (p in [0,1]) su array ordinato asc.
    const pct = (sorted, p) => {
      const n = sorted.length;
      if (n === 0) return NaN;
      if (n === 1) return sorted[0];
      const idx = p * (n - 1);
      const lo = Math.floor(idx), hi = Math.ceil(idx);
      if (lo === hi) return sorted[lo];
      return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
    };

    // ── Materiale per il FALLBACK Pienissimo ─────────────────────────────────
    const vs = state?.voyaSeasonality;
    const pieCells = (vs && vs.cells) ? vs.cells : null;
    // Anni coperti dall'archivio (per stimare le occorrenze di un dow in un mese).
    let yearsSpan = 1;
    const rf = state?.voyaArchiveMeta?.rangeFirst, rl = state?.voyaArchiveMeta?.rangeLast;
    if (rf && rl) {
      const t0 = Date.parse(String(rf).slice(0, 10) + "T00:00:00");
      const t1 = Date.parse(String(rl).slice(0, 10) + "T00:00:00");
      if (Number.isFinite(t0) && Number.isFinite(t1) && t1 > t0) {
        yearsSpan = Math.max(1, (t1 - t0) / (365.25 * 86400000));
      }
    }
    const WEEKS_PER_MONTH = 4.345;
    const occ = yearsSpan * WEEKS_PER_MONTH; // occorrenze stimate di un dato dow in un mese
    // Stima Pienissimo per-cella (presenze): cov gross / occorrenze × calibrazione.
    const pieDirect = (m, dow, svc) => {
      if (!pieCells) return null;
      const c = pieCells[`${m}|${dow}|${svc}`];
      const cov = Number(c && c.cov) || 0;
      if (!(cov > 0) || !(occ > 0)) return null;
      const v = (cov / occ) * (CALIB[svc] || 1);
      return Number.isFinite(v) && v > 0 ? v : null;
    };
    // Rapporto di ancoraggio al livello reale mensile (se il mese ha reale per svc).
    const anchorRatio = (m, svc) => {
      const realMean = mean(realMonthSvc[m][svc]);
      if (realMean == null || !(realMean > 0)) return 1; // niente reale → Pienissimo puro
      // Media delle stime Pienissimo (già calibrate) sui dow con dati, per il mese/svc.
      const pv = [];
      for (let d = 0; d < 7; d++) { const p = pieDirect(m, d, svc); if (p != null) pv.push(p); }
      const pMean = mean(pv);
      if (pMean == null || !(pMean > 0)) return 1;
      let ratio = realMean / pMean;
      if (!Number.isFinite(ratio) || ratio <= 0) return 1;
      return Math.min(2, Math.max(0.5, ratio)); // clamp: evita riscalature selvagge
    };

    const capVal = (v) => Math.max(0, Math.min(CAP, v));
    const cells = {};
    let nReal = 0, nPie = 0;
    for (let m = 1; m <= 12; m++) {
      for (let dow = 0; dow < 7; dow++) {
        for (const svc of ["pranzo", "cena"]) {
          const key = `${m}|${dow}|${svc}`;
          const samples = realByCell[key] || [];
          const n = samples.length;
          let median, low, high, from;
          if (n >= 3) {
            const sorted = [...samples].sort((a, b) => a - b);
            median = pct(sorted, 0.5);
            low = pct(sorted, 0.25);
            high = pct(sorted, 0.75);
            from = "real";
          } else if (n >= 1) {
            median = mean(samples);
            low = median * 0.8;
            high = median * 1.2;
            from = "real";
          } else {
            // FALLBACK Pienissimo
            const base = pieDirect(m, dow, svc);
            if (base == null) continue; // nessun dato → cella assente
            const est = base * anchorRatio(m, svc);
            if (!Number.isFinite(est) || !(est > 0)) continue;
            median = est;
            low = est * 0.75;
            high = est * 1.25;
            from = "pienissimo";
          }
          // Cap + guardie NaN + coerenza low ≤ median ≤ high (interi coperti).
          median = capVal(median); low = capVal(low); high = capVal(high);
          if (![median, low, high].every(Number.isFinite)) continue;
          median = Math.round(median); low = Math.round(low); high = Math.round(high);
          if (low > median) low = median;
          if (high < median) high = median;
          cells[key] = { median, low, high, n, from };
          if (from === "real") nReal++; else nPie++;
        }
      }
    }
    let source;
    if (nReal > 0 && nPie > 0) source = "mixed";
    else if (nReal > 0) source = "historical";
    else if (nPie > 0) source = "pienissimo";
    else source = "none";

    return { builtAt, capacity: CAP, source, cells };
  },

  // Attesi per un giorno/servizio. Legge PRIMA il modello compatto SINCRONIZZATO
  // (state.demandModel[0].model, disponibile anche sul tablet); in mancanza lo
  // calcola live (Mac, dove vivono historicalDaily/voyaSeasonality).
  // Ritorna { median, low, high, capacity, source, from, available }.
  expectedCoversFor: (state, dateStr, service) => {
    const svc = String(service || "");
    const d = String(dateStr || "").slice(0, 10);
    const capacity = 260;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || (svc !== "pranzo" && svc !== "cena")) {
      return { median: null, low: null, high: null, capacity, source: "none", from: null, available: false };
    }
    let model = state?.demandModel?.[0]?.model || null;
    if (!model || typeof model !== "object" || !model.cells) {
      model = engine.buildExpectedCoversModel(state);
    }
    const m = parseInt(d.slice(5, 7), 10);       // 1-12
    const jsDay = new Date(d + "T12:00:00").getDay();
    const dow = (jsDay + 6) % 7;                  // 0=lun..6=dom
    const cell = model && model.cells ? model.cells[`${m}|${dow}|${svc}`] : null;
    const cap = Number(model?.capacity) || capacity;
    const source = model?.source || "none";
    if (!cell) {
      return { median: null, low: null, high: null, capacity: cap, source, from: null, available: false };
    }
    return {
      median: cell.median, low: cell.low, high: cell.high,
      capacity: cap, source, from: cell.from, available: true,
    };
  },

  forecastNDays: (state, nDays = 7) => {
    const metrics = engine.dailyMetrics(state);
    const baseline = engine.dayOfWeekBaseline(metrics);
    const trend = engine.weeklyTrend(metrics);

    // STOP HORIZON: regola "mai estrapolare oltre 2× lo storico"
    const dataPoints = metrics.length;
    const maxUsefulHorizon = Math.max(1, Math.min(nDays, Math.floor(dataPoints * 2)));
    const horizonCapped = nDays > maxUsefulHorizon;

    // Trend applicato solo se statisticamente affidabile (≥21gg storia)
    const trendPctEff = trend.reliable && Number.isFinite(trend.pct) ? trend.pct : 0;

    // Confidence generale del modello
    const confidence = dataPoints >= 56 ? "alta" : dataPoints >= 28 ? "media" : dataPoints >= 14 ? "bassa" : "insufficienti";

    // STAGIONALITÀ MENSILE (FIX #6): indice moltiplicativo per mese + fonte onesta.
    const seasonal = engine.seasonalIndex(state, metrics);
    // Inflazione banda CI quando l'indice è ASSUNTO (profilo manuale, non appreso da
    // ≥2 cicli): NON dare falsa precisione a una stima umana. Allarghiamo σ del 25%
    // per i giorni in cui applichiamo un coefficiente ≠1 da profilo manuale.
    const seasonalCIInflation = seasonal.isAssumed ? 1.25 : 1.0;
    const seasonalApplied = seasonal.source !== "neutral";
    const seasonalIndexByPoint = [];
    const closedMonths = seasonal.closedMonths || [];

    const z = 1.96; // CI 95% (z-score normale)
    const startDate = new Date(TODAY + "T12:00:00");
    const points = [];
    let totalSum = 0;
    let totalCov = 0;
    let totalSigmaSq = 0;
    let cappedCount = 0;

    for (let i = 1; i <= maxUsefulHorizon; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      const dow = d.getDay();
      const base = baseline[dow];
      const targetMonth = d.getMonth();

      // (E) STAGIONALITÀ MENSILE: moltiplicatore del mese del giorno target.
      // Profilo neutro → seasonMult=1 → forecast IDENTICO a prima (backward-compat).
      const seasonMult = seasonal.index[targetMonth];
      seasonalIndexByPoint.push(Math.round(seasonMult * 1000) / 1000);

      // ── MESE CHIUSO: ristorante stagionale chiuso (es. nov-feb). Proietto 0,
      // NON applico il fallback indice=1. Banda nulla, punto marcato "closed".
      if (closedMonths.includes(targetMonth)) {
        points.push({
          date: d.toISOString().slice(0, 10),
          dayOfWeek: dow,
          dayLabel: d.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" }),
          expected: 0, covers: 0, low: 0, high: 0,
          ciHalf: 0, sigmaH: 0, trendMult: 1,
          lowCapped: false,
          closed: true,
          confidence: base.confidence,
          basedOn: base.n,
        });
        continue; // niente somma totale, niente banda: il locale è chiuso
      }

      // (A) COMPOUND: pow geometrico — niente più trend lineare bugiardo
      const trendMult = Math.pow(1 + trendPctEff, i / 7);
      const expectedRev = base.revenue * trendMult * seasonMult;
      const expectedCov = base.covers * trendMult * seasonMult;

      // (B) BANDA √h: sigma cresce con √(orizzonte in settimane). Niente
      // più banda statica. La σ scala con il livello (seasonMult): se luglio è 2×,
      // anche la sua variabilità assoluta cresce. + inflazione se indice ASSUNTO.
      const sigmaH = base.stdDev * Math.sqrt(Math.max(1, i / 7)) * seasonMult * seasonalCIInflation;
      const ciHalf = z * sigmaH;

      const lowRaw = expectedRev - ciHalf;
      const lowCapped = lowRaw < 0;
      if (lowCapped) cappedCount++;
      const lowRev = Math.max(0, lowRaw);
      const highRev = expectedRev + ciHalf;

      points.push({
        date: d.toISOString().slice(0, 10),
        dayOfWeek: dow,
        dayLabel: d.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" }),
        expected: Math.round(expectedRev * 100) / 100,
        covers: Math.round(expectedCov),
        low: Math.round(lowRev * 100) / 100,
        high: Math.round(highRev * 100) / 100,
        ciHalf: Math.round(ciHalf * 100) / 100,
        sigmaH: Math.round(sigmaH * 100) / 100,
        trendMult: Math.round(trendMult * 10000) / 10000,
        lowCapped,
        confidence: base.confidence,
        basedOn: base.n,
      });

      totalSum += expectedRev;
      totalCov += expectedCov;
      // Banda totale: somma in quadratura (assume indipendenza giornaliera).
      // Equivalente: σ_totale = √(Σ σ_h²). Più onesto di sommare low/high.
      totalSigmaSq += sigmaH * sigmaH;
    }

    const totalSigma = Math.sqrt(totalSigmaSq);
    const totalCiHalf = z * totalSigma;
    const totalLowReal = Math.max(0, totalSum - totalCiHalf);
    const totalHighReal = totalSum + totalCiHalf;
    const cappedRate = points.length > 0 ? cappedCount / points.length : 0;

    return {
      points,
      // backward compat: i nomi storici restano (totalExpected/totalLow/totalHigh)
      totalExpected: Math.round(totalSum * 100) / 100,
      totalLow: Math.round(totalLowReal * 100) / 100,
      totalHigh: Math.round(totalHighReal * 100) / 100,
      totalCiHalf: Math.round(totalCiHalf * 100) / 100,
      totalSigma: Math.round(totalSigma * 100) / 100,
      totalCovers: Math.round(totalCov),
      confidence,
      trend,
      trendApplied: trendPctEff,         // 0 se trend non affidabile
      trendReliable: trend.reliable,
      baseline,
      dataPoints,
      horizonRequested: nDays,
      horizonActual: maxUsefulHorizon,
      horizonCapped,
      cappedRate,
      ciNote: "naïve normal-symmetric (roadmap: log-normal quantile)",
      // ── Diagnostica stagionalità (FIX #6) ──────────────────────────────────
      seasonalSource: seasonal.source,           // "learned" | "manual" | "neutral"
      seasonalApplied,                           // true se applicato un profilo ≠ neutro
      seasonalIsAssumed: seasonal.isAssumed,     // true → banda CI allargata (+25%)
      seasonalCIInflation,                       // 1.25 se assunto, 1.0 altrimenti
      seasonalIndex: seasonal.index,             // [12] normalizzato a media 1
      seasonalIndexByPoint,                      // moltiplicatore applicato per ogni punto
      seasonalCyclesByMonth: seasonal.cyclesByMonth,
      seasonalMinCycles: seasonal.minCyclesUsed,
      seasonalClosedMonths: closedMonths,        // [int] mesi a indice 0 (stagione chiusa)
      seasonalYearsObserved: seasonal.yearsObserved || 0,
    };
  },

  // Fabbisogno materia prima proiettato dalla vendita storica piatti
  forecastIngredients: (state, nDays = 7) => {
    const receipts = state.tableReceipts || [];
    if (receipts.length === 0) return { items: [], confidence: "insufficienti" };
    const metrics = engine.dailyMetrics(state);
    const daysData = metrics.length;
    if (daysData < 7) return { items: [], confidence: "insufficienti", daysData };

    // Per ogni piatto: vendite medie giornaliere (per giorno della settimana)
    const dishesByDow = {}; // dishId -> dow -> qta media
    const dishNames = {};

    // Aggrega quantità per dish / giorno della settimana
    const dishDowCounts = {}; // dishId_dow -> [qta_day_1, qta_day_2, ...]
    receipts.forEach(r => {
      const day = getServiceDate(r.closedAt);
      if (!day) return;
      const dow = new Date(day + "T12:00:00").getDay();
      (r.items || []).forEach(it => {
        dishNames[it.dishId] = it.name;
        const key = `${it.dishId}_${dow}_${day}`;
        dishDowCounts[key] = (dishDowCounts[key] || 0) + it.qty;
      });
    });
    // Ora per ogni dishId e dow calcola la media
    const aggregated = {};
    Object.keys(dishDowCounts).forEach(key => {
      const [dishId, dow, day] = key.split("_");
      const k2 = `${dishId}_${dow}`;
      if (!aggregated[k2]) aggregated[k2] = { dishId, dow: parseInt(dow), values: [] };
      aggregated[k2].values.push(dishDowCounts[key]);
    });
    Object.keys(aggregated).forEach(k => {
      const a = aggregated[k];
      a.avg = engine.trimmedMean(a.values);
      if (!dishesByDow[a.dishId]) dishesByDow[a.dishId] = {};
      dishesByDow[a.dishId][a.dow] = a.avg;
    });

    // STAGIONALITÀ MENSILE (FIX #6): il fabbisogno EREDITA lo stesso indice del
    // forecast incassi. Le medie weekday per piatto sono calcolate su tutto lo
    // storico (mescolano i mesi); il moltiplicatore mensile (normalizzato a media 1)
    // ridistribuisce correttamente verso i mesi di picco/fondo. Profilo neutro →
    // seasonMult=1 → fabbisogno IDENTICO a prima (backward-compat).
    const seasonal = engine.seasonalIndex(state, metrics);
    const startDate = new Date(TODAY + "T12:00:00");
    const bpNeed = {}; // bpId -> qtaNet totale
    for (let i = 1; i <= nDays; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      const dow = d.getDay();
      const seasonMult = seasonal.index[d.getMonth()];
      Object.keys(dishesByDow).forEach(dishId => {
        const qta = (dishesByDow[dishId][dow] || 0) * seasonMult;
        if (qta <= 0) return;
        const dish = (state.dishesReal || []).find(x => x.id === dishId);
        if (!dish) return;
        (dish.ingredienti || []).forEach(ing => {
          if (!ing.bpId) return;
          if (!bpNeed[ing.bpId]) bpNeed[ing.bpId] = 0;
          bpNeed[ing.bpId] += ing.qty * qta;
        });
      });
    }

    const items = Object.keys(bpNeed).map(bpId => {
      const bp = (state.baseProducts || []).find(b => b.id === bpId);
      if (!bp) return null;
      const qtyNeeded = Math.round(bpNeed[bpId] * 1000) / 1000;
      const stockNow = engine.itemStock(state, bpId);
      const toOrder = Math.max(0, qtyNeeded + (bp.minStock || 2) - stockNow);
      const cost = qtyNeeded * (bp.pFatt || 0);
      return { bp, qtyNeeded, stockNow, toOrder: Math.round(toOrder * 100) / 100, cost: Math.round(cost * 100) / 100, shortage: stockNow < qtyNeeded };
    }).filter(Boolean).sort((a, b) => b.cost - a.cost);

    const confidence = daysData >= 56 ? "alta" : daysData >= 28 ? "media" : "bassa";
    return {
      items, confidence, daysData,
      // Diagnostica stagionalità ereditata dal forecast (FIX #6).
      seasonalSource: seasonal.source,
      seasonalApplied: seasonal.source !== "neutral",
      seasonalIsAssumed: seasonal.isAssumed,
    };
  },

  // Menu engineering — metodologia Kasavana-Smith canonica (Cornell HRA, 1982)
  //
  // ── Regole canoniche ────────────────────────────────────────────────────────
  // • Soglia margine    = media PONDERATA per quantità venduta (Σ(margin×qty)/Σqty),
  //                       NON la mediana del listino. Pesa per ciò che si vende.
  // • Soglia popolarità = popularityRule × (1/N), default 0.70 (Kasavana-Smith).
  // • Piatti con qty<5 nel periodo: classificazione frequentista instabile,
  //   uso un prior Bayesian Gamma-Poisson (α=2, β=1) → posterior Gamma(α+qty, β+windowDays),
  //   λ atteso = α'/β'. Marcati come `isRare` per UI.
  // • Flag `borderline` per piatti entro ±15% delle soglie (margine o popolarità):
  //   classificazione fragile, una manciata di scontrini può flippare il quadrante.
  // • Wilson CI 95% sulla quota di vendita (per N≥10 totali) — onestà statistica.
  //
  // ── Snapshot-first ──────────────────────────────────────────────────────────
  // Il netCost per Star/Plowhorse/Puzzle/Dog è il costo medio storico effettivo
  // (recipeSnapshot.netCost), NON il costo live oggi. Stabilità storica.
  //
  // ── Parametri ───────────────────────────────────────────────────────────────
  // opts.windowDays    = finestra di osservazione (default 30)
  // opts.popularityRule = moltiplicatore Kasavana-Smith (default 0.70)
  // opts.useBayesian   = abilita prior su piatti rari (default true)
  menuEngineering: (state, opts = {}) => {
    const windowDays = Number(opts.windowDays) > 0 ? Number(opts.windowDays) : 30;
    const popularityRule = Number(opts.popularityRule) > 0 ? Number(opts.popularityRule) : 0.70;
    const useBayesian = opts.useBayesian !== false;

    const receipts = state.tableReceipts || [];
    const dishes = state.dishesReal || [];

    // Pre-index O(1)
    const dishById = new Map();
    dishes.forEach(d => dishById.set(d.id, d));
    const bpById = new Map();
    (state.baseProducts || []).forEach(b => bpById.set(b.id, b));

    // Finestra temporale: [TODAY − windowDays, TODAY]
    const cutoffDate = (() => {
      const d = new Date(TODAY + "T12:00:00");
      d.setDate(d.getDate() - windowDays);
      return d.toISOString().slice(0, 10);
    })();

    // Aggrega vendite per piatto + costo snapshot-first nel periodo
    const agg = {};
    receipts.forEach(r => {
      const date = getServiceDate(r.closedAt) || r.date;
      if (!date || date < cutoffDate || date > TODAY) return;
      (r.items || []).forEach(it => {
        if (!it.dishId) return;
        // skip righe non matchate al catalogo (sono già state filtrate, ma difensivo)
        if (!dishById.has(it.dishId)) return;
        const qty = Number(it.qty) || 0;
        const unitPrice = Number(it.unitPrice) || 0;
        if (qty <= 0) return;
        if (!agg[it.dishId]) agg[it.dishId] = { dishId: it.dishId, name: it.name, qty: 0, revenue: 0, snapshotCost: 0, snapshotQty: 0, liveQty: 0 };
        const row = agg[it.dishId];
        row.qty += qty;
        row.revenue += qty * unitPrice;
        const snapCost = it.recipeSnapshot ? Number(it.recipeSnapshot.netCost) : NaN;
        if (Number.isFinite(snapCost) && snapCost > 0) {
          row.snapshotCost += snapCost * qty;
          row.snapshotQty += qty;
        } else {
          row.liveQty += qty;
        }
      });
    });

    // Fallback: nessun dato nel periodo → output statisticamente onesto, non vuoto
    if (Object.keys(agg).length === 0) {
      return {
        items: [],
        totalQty: 0,
        totalRevenue: 0,
        popThreshold: 0,
        cmThreshold: 0,
        periodDays: windowDays,
        fallback: true,
        popularityRule,
        quadrantCounts: { star: 0, plowhorse: 0, puzzle: 0, dog: 0 },
        borderlineCount: 0,
        rareCount: 0,
      };
    }

    const totalQty = Object.values(agg).reduce((s, a) => s + a.qty, 0);
    const totalRevenue = Object.values(agg).reduce((s, a) => s + a.revenue, 0);
    const defaultVatSale = state.settings?.vatRateSale || 0.10;
    // Pre-index aliquota IVA per piatto (stessa logica di kpiDerived → lineVat).
    // Vino 22%, cibo 10%: una media unica sbaglia priceNet/margin in modo sistematico
    // su qualsiasi piatto fuori dal default. Per snapshot: rispetto il valore storicizzato.
    const dishVatById = new Map();
    dishes.forEach(d => {
      const v = (d.vatRate != null && Number.isFinite(d.vatRate)) ? d.vatRate : defaultVatSale;
      dishVatById.set(d.id, v);
    });
    // Aliquota IVA effettiva per il piatto aggregato: prendo il dish corrente
    // (lo snapshot per-riga è già stato consumato nel computo costo; qui aggreghiamo).
    const dishVatRate = (dishId) => {
      const v = dishVatById.get(dishId);
      return (v != null && Number.isFinite(v)) ? v : defaultVatSale;
    };

    // Calcola per ogni piatto: prezzo netto, costo netto, margine unitario, share
    const items = Object.values(agg).map(a => {
      const dish = dishById.get(a.dishId);
      // Live cost per unit (fallback per qty senza snapshot)
      let liveCostPerUnit = 0;
      if (dish) {
        (dish.ingredienti || []).forEach(ing => {
          const bp = ing.bpId ? bpById.get(ing.bpId) : null;
          const unit = bp ? (Number(bp.pReale) || Number(bp.pFatt) || 0) : (Number(ing.prezzo) || 0);
          liveCostPerUnit += unit * (Number(ing.qty) || 0);
        });
      }
      // Costo medio per unità snapshot-first
      const totalCostHist = a.snapshotCost + liveCostPerUnit * a.liveQty;
      const netCost = a.qty > 0 ? totalCostHist / a.qty : liveCostPerUnit;
      const fromSnapshotPct = a.qty > 0 ? a.snapshotQty / a.qty : 0;

      const unitPrice = a.qty > 0 ? a.revenue / a.qty : 0;
      // Contribution margin netto = prezzo netto - costo netto, IVA per piatto (non media unica).
      const vatRow = dishVatRate(a.dishId);
      const priceNet = unitPrice / (1 + vatRow);
      const margin = priceNet - netCost;
      const marginTotal = margin * a.qty;
      // FIX #5-B — margine netto: contribuzione − oneri forfettari di trasformazione.
      // Riga AGGIUNTIVA: NON usata per la classificazione Kasavana-Smith (che resta su `margin`).
      // Quando oneriTrasformazionePct=0 → oneriUnitari=0 → marginNet===margin (zero impatto).
      const oneriUnitari = engine.oneriTrasformazione(state, priceNet);
      const marginNet = margin - oneriUnitari;
      const shareQty = totalQty > 0 ? a.qty / totalQty : 0;

      return {
        ...a,
        dish,
        cat: dish?.cat || a.cat || "altro",
        unitPrice,
        netCost,
        margin,
        marginTotal,
        oneriUnitari,
        marginNet,
        marginNetTotal: marginNet * a.qty,
        shareQty,
        // popularity index legacy (qty / media aritmetica) — mantenuto per back-compat UI
        popularity: 0,  // calcolato sotto dopo che ho N
        fromSnapshotPct,
        fcPct: priceNet > 0 ? (netCost / priceNet) * 100 : 0,
      };
    });

    const N = items.length;
    // === Soglie Kasavana-Smith ===
    // Popolarità: 0.70 × (1/N), NON 1/N (che produrrebbe "in media è popolare").
    const popThreshold = N > 0 ? popularityRule / N : 0;
    // Margine: media PONDERATA per qty (canone Kasavana-Smith), NON mediana.
    const cmThreshold = totalQty > 0
      ? items.reduce((s, x) => s + x.margin * x.qty, 0) / totalQty
      : 0;

    // Legacy: popularity index per UI esistente (qty / media aritmetica)
    const avgQtyArith = N > 0 ? totalQty / N : 0;
    items.forEach(x => { x.popularity = avgQtyArith > 0 ? x.qty / avgQtyArith : 0; });

    // === Classificazione + Bayesian + Wilson CI + borderline ===
    items.forEach(x => {
      // Bayesian update Gamma-Poisson per piatti rari (<5 vendite nel periodo)
      if (useBayesian && x.qty < 5) {
        const aPost = 2 + x.qty;       // α' = α + Σvendite
        const bPost = 1 + windowDays;  // β' = β + giorni di osservazione
        const lambdaPosterior = aPost / bPost;  // rate atteso/giorno
        x.qtyExpected = lambdaPosterior * windowDays;
        x.shareQtyBayes = totalQty > 0 ? x.qtyExpected / totalQty : 0;
        x.isRare = true;
      } else {
        x.qtyExpected = x.qty;
        x.shareQtyBayes = x.shareQty;
        x.isRare = false;
      }

      // Wilson CI 95% sulla quota di vendita (significativo solo se N totale ≥10)
      if (totalQty >= 10) {
        const w = engine.stats.wilsonCI(Math.round(x.qty), Math.round(totalQty));
        x.shareCI = [w.ciLow, w.ciHigh];
      } else {
        x.shareCI = null;
      }

      // Classificazione (usa stima Bayesiana se rara, altrimenti share osservata)
      const shareForClassify = (useBayesian && x.isRare) ? x.shareQtyBayes : x.shareQty;
      x.isPopular = shareForClassify >= popThreshold;
      x.isHighMargin = x.margin >= cmThreshold;

      // Borderline: entro ±15% dalla soglia su popolarità O su margine
      const popDist = popThreshold > 0 ? Math.abs(shareForClassify - popThreshold) / popThreshold : 1;
      const cmDist = cmThreshold !== 0 ? Math.abs(x.margin - cmThreshold) / Math.abs(cmThreshold) : 1;
      x.borderline = popDist < 0.15 || cmDist < 0.15;

      // Quadrante Kasavana-Smith
      if (x.isPopular && x.isHighMargin) x.quadrant = "star";
      else if (x.isPopular && !x.isHighMargin) x.quadrant = "plowhorse";
      else if (!x.isPopular && x.isHighMargin) x.quadrant = "puzzle";
      else x.quadrant = "dog";
    });

    const quadrantCounts = {
      star: items.filter(x => x.quadrant === "star").length,
      plowhorse: items.filter(x => x.quadrant === "plowhorse").length,
      puzzle: items.filter(x => x.quadrant === "puzzle").length,
      dog: items.filter(x => x.quadrant === "dog").length,
    };

    return {
      items: items.sort((a, b) => b.revenue - a.revenue),
      totalQty,
      totalRevenue,
      popThreshold,
      cmThreshold,
      periodDays: windowDays,
      popularityRule,
      fallback: false,
      quadrantCounts,
      borderlineCount: items.filter(x => x.borderline).length,
      rareCount: items.filter(x => x.isRare).length,
    };
  },

  // ╔═══════════════════════════════════════════════════════════════════════════╗
  // ║   KPI DERIVATI: penetrazione, mix, cross-sell, performance, anomalie      ║
  // ║   Per ogni KPI fornisce: valore, vs precedente, vs benchmark, semaforo.    ║
  // ╚═══════════════════════════════════════════════════════════════════════════╝
  kpiDerived: (state, period = "30d", compareWith = "prev_period") => {
    const allReceipts = state.tableReceipts || [];
    const dishes = state.dishesReal || [];
    const benchmarks = state.benchmarksKPI || {};
    // Pre-index O(n) per evitare lookup O(n²) nei loop sotto.
    const dishCatById = new Map();
    const dishVatById = new Map();
    const defaultVatSale = state.settings?.vatRateSale || 0.10;
    dishes.forEach(d => {
      dishCatById.set(d.id, d.cat);
      // dish.vatRate può essere null (=> usa default). Trasformo in numero finito.
      const v = (d.vatRate != null && Number.isFinite(d.vatRate)) ? d.vatRate : defaultVatSale;
      dishVatById.set(d.id, v);
    });
    // Helper: estrae aliquota IVA di una riga scontrino con fallback chain:
    // recipeSnapshot.vatRate (al momento della vendita) → dish.vatRate corrente → settings default.
    // recipeSnapshot.vatRate può essere null per piatti che usano default — quindi ?? non basta.
    const lineVat = (it) => {
      const snap = it.recipeSnapshot?.vatRate;
      if (snap != null && Number.isFinite(snap)) return snap;
      const cur = dishVatById.get(it.dishId);
      if (cur != null && Number.isFinite(cur)) return cur;
      return defaultVatSale;
    };
    // Categoria di un item scontrino con priorità:
    //  1. recipeSnapshot.cat (snapshot storico, OIC 13 — fonte autorevole)
    //  2. dishCatById (anagrafica piatti corrente)
    //  3. catFromReparto(_csvReparto) ⬅ fallback per _unmatched_*: senza questo i
    //     ~25% di righe con dishId placeholder finivano tutti in "altro" e
    //     sparivano da portatePerCop / mixPctNet / pctMenuCompleto.
    //  4. "altro" (default)
    const lineCat = (it) => it.recipeSnapshot?.cat || dishCatById.get(it.dishId) || catFromReparto(it._csvReparto) || "altro";

    // Determino range periodo corrente e di confronto
    const today = new Date(TODAY + "T12:00:00");
    let nDays;
    if (period === "today") nDays = 1;
    else if (period === "7d") nDays = 7;
    else if (period === "30d") nDays = 30;
    else if (period === "90d") nDays = 90;
    else if (period === "year") nDays = 365;
    else nDays = 30;

    const periodFrom = new Date(today);
    periodFrom.setDate(periodFrom.getDate() - nDays + 1);
    const periodFromStr = periodFrom.toISOString().slice(0, 10);

    // Range precedente
    let prevFrom, prevTo;
    if (compareWith === "prev_period") {
      prevTo = new Date(periodFrom);
      prevTo.setDate(prevTo.getDate() - 1);
      prevFrom = new Date(prevTo);
      prevFrom.setDate(prevFrom.getDate() - nDays + 1);
    } else if (compareWith === "year_ago") {
      prevFrom = new Date(periodFrom);
      prevFrom.setFullYear(prevFrom.getFullYear() - 1);
      prevTo = new Date(today);
      prevTo.setFullYear(prevTo.getFullYear() - 1);
    }
    const prevFromStr = prevFrom ? prevFrom.toISOString().slice(0, 10) : null;
    const prevToStr = prevTo ? prevTo.toISOString().slice(0, 10) : null;

    // Filtra scontrini del periodo corrente e di confronto
    const inRange = (r, from, to) => {
      const d = getServiceDate(r.closedAt);
      return d && d >= from && d <= to;
    };
    const todayStr = TODAY;
    const currentReceipts = allReceipts.filter(r => inRange(r, periodFromStr, todayStr));
    const prevReceipts = (prevFromStr && prevToStr) ? allReceipts.filter(r => inRange(r, prevFromStr, prevToStr)) : [];

    // Funzione helper: calcola tutti i KPI di un set di scontrini.
    // - Separa scontrini-tavolo (covers>0) da scontrini-banco (covers=0): i KPI
    //   per-coperto sono SOLO sui tavolo, altrimenti il ticket medio crolla.
    // - Scorpora IVA per riga (recipeSnapshot.vatRate) per produrre il VERO netto:
    //   uno scontrino con cibo 10% + vino 22% non può usare media unica 10%.
    const computeKpis = (receipts) => {
      if (receipts.length === 0) return null;
      const nReceipts = receipts.length;

      // Segmentazione banco / tavolo
      const tableReceipts = receipts.filter(r => (r.covers || 0) > 0);
      const bancoReceipts = receipts.filter(r => (r.covers || 0) === 0);
      const nTableReceipts = tableReceipts.length;
      const nBancoReceipts = bancoReceipts.length;
      const nCovers = tableReceipts.reduce((s, r) => s + (Number(r.covers) || 0), 0);

      // Revenue totali (gross) — su TUTTI gli scontrini (banco + tavolo)
      const totalRevGross = receipts.reduce((s, r) => s + (Number(r.total) || 0), 0);
      const tableRevGross = tableReceipts.reduce((s, r) => s + (Number(r.total) || 0), 0);
      const bancoRevGross = bancoReceipts.reduce((s, r) => s + (Number(r.total) || 0), 0);

      // Conta quantità + revenue per categoria (gross + net IVA scorporata per riga)
      const qtyByCat = {};
      const qtyByCatTable = {}; // FIX A.2 (audit 2026-05-20): qty solo da scontrini-tavolo
      const revByCat = {};      // GROSS (IVA inclusa) — backward compat
      const revByCatNet = {};   // NETTO IVA (scorporato per riga)
      let totalArticles = 0;
      let totalRevNet = 0;      // Netto IVA totale su TUTTI gli scontrini
      let tableRevNet = 0;      // Netto IVA solo scontrini-tavolo
      let bancoRevNet = 0;      // Netto IVA solo scontrini-banco
      DISH_CAT_KEYS.forEach(c => { qtyByCat[c] = 0; qtyByCatTable[c] = 0; revByCat[c] = 0; revByCatNet[c] = 0; });

      receipts.forEach(r => {
        const isTable = (r.covers || 0) > 0;
        (r.items || []).forEach(it => {
          const qty = Number(it.qty) || 0;
          const unitPrice = Number(it.unitPrice) || 0;
          totalArticles += qty;
          const cat = lineCat(it);
          const grossLine = qty * unitPrice;
          // Scorporo IVA per riga (Reg. UE 1169/2011 — MAI media unica)
          const vat = lineVat(it);
          const netLine = vat > -1 ? grossLine / (1 + vat) : grossLine;
          totalRevNet += netLine;
          if (isTable) tableRevNet += netLine; else bancoRevNet += netLine;
          if (qtyByCat[cat] != null) {
            qtyByCat[cat] += qty;
            if (isTable) qtyByCatTable[cat] += qty; // FIX A.2: separato per penetrazione
            revByCat[cat] += grossLine;
            revByCatNet[cat] += netLine;
          }
        });
      });

      // FIX A.2 (audit 2026-05-20): penetrazione = qty TAVOLO / coperti TAVOLO.
      // Prima si usava `qtyByCat` (banco+tavolo) al numeratore con nCovers (solo tavolo)
      // al denominatore — la penetrazione "vini/caffè per coperto" risultava gonfiata
      // del 15–50% perché il banco vende soprattutto vini/caffè/bevande SENZA coperti.
      // Mantengo `qtyByCat` totale solo per export e mix vendita (che è già su rev totale).
      const perCop = (cat) => nCovers > 0 ? (qtyByCatTable[cat] || 0) / nCovers : 0;
      // Mix ricavo netto (€ netto cat / € netto totale) — più onesto del gross
      const mixPctNet = (cat) => totalRevNet > 0 ? ((revByCatNet[cat] || 0) / totalRevNet) * 100 : 0;
      // Mantengo mixPct gross per backward compat
      const mixPct = (cat) => totalRevGross > 0 ? ((revByCat[cat] || 0) / totalRevGross) * 100 : 0;

      // Tickets pranzo/cena — SOLO scontrini-tavolo (il banco non ha coperti)
      let nCovP = 0, nCovC = 0, revP = 0, revC = 0, nP = 0, nC = 0;
      tableReceipts.forEach(r => {
        const s = getShift(r.closedAt);
        const cov = Number(r.covers) || 0;
        const tot = Number(r.total) || 0;
        if (s === "pranzo") { nCovP += cov; revP += tot; nP++; }
        else if (s === "cena") { nCovC += cov; revC += tot; nC++; }
      });

      // Portate per coperto = solo categorie food (no vini/bar/caffe/menu_speciali)
      const FOOD_CATS = ["antipasti", "primi", "secondi", "contorni", "dolci"];
      // qty FOOD su scontrini-tavolo (ri-aggregato per esattezza)
      let totalPortateTable = 0;
      tableReceipts.forEach(r => {
        (r.items || []).forEach(it => {
          if (FOOD_CATS.includes(lineCat(it))) totalPortateTable += Number(it.qty) || 0;
        });
      });
      const portatePerCop = nCovers > 0 ? totalPortateTable / nCovers : 0;

      // % menu completo: scontrini-TAVOLO con almeno N categorie su {antipasti, primi, secondi, dolci}
      const minCat = benchmarks.menuCompletoMinCat ?? 3;
      const MENU_CATS = ["antipasti", "primi", "secondi", "dolci"];
      const completi = tableReceipts.filter(r => {
        const cats = new Set();
        (r.items || []).forEach(it => {
          const c = lineCat(it);
          if (MENU_CATS.includes(c)) cats.add(c);
        });
        return cats.size >= minCat;
      }).length;
      const pctMenuCompleto = nTableReceipts > 0 ? (completi / nTableReceipts) * 100 : 0;

      // Articoli per scontrino — SOLO scontrini-tavolo
      let totalArticlesTable = 0;
      tableReceipts.forEach(r => {
        (r.items || []).forEach(it => { totalArticlesTable += Number(it.qty) || 0; });
      });

      // Bevande totali (vini + bar + caffè) sul food — su revenue netti (più onesto)
      const bevRevNet = (revByCatNet["vini"] || 0) + (revByCatNet["bar"] || 0) + (revByCatNet["caffetteria"] || 0);
      const foodRevNet = FOOD_CATS.reduce((s, c) => s + (revByCatNet[c] || 0), 0);
      const mixFoodBev = foodRevNet > 0 ? (bevRevNet / foodRevNet) * 100 : 0;

      // Ticket medio per-coperto: SOLO tableRevGross / nCovers
      const ticketCop = nCovers > 0 ? tableRevGross / nCovers : 0;
      // Ticket medio per scontrino-tavolo
      const ticketTavolo = nTableReceipts > 0 ? tableRevGross / nTableReceipts : 0;
      // Banco: scontrino medio e % mix sul totale
      const bancoAvg = nBancoReceipts > 0 ? bancoRevGross / nBancoReceipts : 0;
      const bancoMixPct = totalRevGross > 0 ? (bancoRevGross / totalRevGross) * 100 : 0;

      // Wilson CI 95% sulla penetrazione per categoria.
      // k = qty venduta categoria (numero portate), n = nCovers (coperti totali).
      // Wilson e' robusto su n piccolo e p estremo (vedi engine.stats.wilsonCI).
      // Nota: la "penetrazione" qui e' un tasso (portate/coperto), non strettamente
      // una proporzione 0..1 (un coperto puo' prendere 2 antipasti). Wilson resta
      // un'approssimazione utile per il "tasso di copertura della categoria".
      // FIX A.2 (audit 2026-05-20): k = qty TAVOLO (coerente con numeratore di perCop),
      // non qty totale che include banco senza coperti.
      const ciByCat = {};
      for (const cat of DISH_CAT_KEYS) {
        const k = Math.round(qtyByCatTable[cat] || 0);
        ciByCat[cat] = engine.stats.wilsonCI(k, nCovers);
      }

      return {
        nReceipts, nCovers,
        // Revenue
        totalRev: totalRevGross,       // backward compat (= gross)
        totalRevGross,
        totalRevNet,
        tableRev: tableRevGross,
        tableRevNet,
        bancoRev: bancoRevGross,
        bancoRevNet,
        // Segmentazione
        nTableReceipts, nBancoReceipts,
        bancoAvg, bancoMixPct,
        // Penetrazione coperto
        dolciPerCop: perCop("dolci"),
        contorniPerCop: perCop("contorni"),
        viniPerCop: perCop("vini"),
        caffePerCop: perCop("caffetteria"),
        antipastiPerCop: perCop("antipasti"),
        primiPerCop: perCop("primi"),
        secondiPerCop: perCop("secondi"),
        aperitiviPerCop: 0,  // se vuoi distinguerli serve separare bar
        acquaPerCop: 0,      // idem
        // Mix (gross — backward compat)
        mixVini: mixPct("vini"),
        mixBar: mixPct("bar"),
        mixDolci: mixPct("dolci"),
        mixCaffe: mixPct("caffetteria"),
        // Mix (netto IVA — più onesto economicamente)
        mixViniNet: mixPctNet("vini"),
        mixBarNet: mixPctNet("bar"),
        mixDolciNet: mixPctNet("dolci"),
        mixCaffeNet: mixPctNet("caffetteria"),
        mixFoodBev,
        // Aggregati per UI
        revByCat, revByCatNet, qtyByCat,
        qtyByCatTable,                  // FIX A.2: qty solo tavolo (numeratore penetrazione)
        penetrationSource: "table-only", // FIX A.2: documento esplicito sulla fonte
        // Wilson CI 95% per penetrazione coperto (per categoria)
        // ciByCat[cat] = { p, ciLow, ciHigh, n }
        ciByCat,
        // Ticket — solo scontrini-tavolo (banco escluso)
        ticketCop,
        ticketCopPranzo: nCovP > 0 ? revP / nCovP : 0,
        ticketCopCena: nCovC > 0 ? revC / nCovC : 0,
        ticketTavolo,
        portatePerCop,
        articoliPerScn: nTableReceipts > 0 ? totalArticlesTable / nTableReceipts : 0,
        pctMenuCompleto,
        // Performance fascia
        coveriPranzoMedia: nP > 0 ? nCovP / nP : 0,
        coveriCenaMedia: nC > 0 ? nCovC / nC : 0,
      };
    };

    const current = computeKpis(currentReceipts);
    const prev = computeKpis(prevReceipts);

    // Costruisco gli oggetti KPI risultato con benchmark e semaforo
    const buildKpi = (key, value, opts = {}) => {
      const bench = benchmarks[key] || { target: 0, ottimo: 0, scarso: 0, label: key };
      const prevValue = prev ? prev[key] : null;
      const delta = (prevValue != null && prevValue !== 0) ? ((value - prevValue) / Math.abs(prevValue)) * 100 : null;
      const deltaAbs = prevValue != null ? value - prevValue : null;

      // Semaforo a 5 livelli: ▲▲ molto sopra · ▲ sopra · = in linea · ▼ sotto · ▼▼ molto sotto
      // Calcolato vs target: se valore >= ottimo → ▲▲, >= target → ▲, >= scarso → ▼, < scarso → ▼▼, in [target..target*0.95] = "="
      const reverseBenchmark = opts.reverse === true;  // per KPI dove "meno è meglio" (es. food cost %)
      let level, color, symbol;
      const v = reverseBenchmark ? -value : value;
      const t = reverseBenchmark ? -bench.target : bench.target;
      const o = reverseBenchmark ? -bench.ottimo : bench.ottimo;
      const s = reverseBenchmark ? -bench.scarso : bench.scarso;

      if (v >= o) { level = "molto_sopra"; color = "oliveDeep"; symbol = "▲▲"; }
      else if (v >= t) { level = "sopra"; color = "olive"; symbol = "▲"; }
      else if (v >= s) { level = "sotto"; color = "amber"; symbol = "▼"; }
      else { level = "molto_sotto"; color = "terra"; symbol = "▼▼"; }

      // Linea: se valore è entro ±5% del target lo mostro come "="
      const distFromTarget = bench.target > 0 ? Math.abs(value - bench.target) / bench.target : 0;
      if (distFromTarget < 0.05 && level !== "molto_sopra" && level !== "molto_sotto") {
        level = "in_linea"; color = "muted"; symbol = "=";
      }

      return {
        key, label: bench.label || key, value, prevValue, delta, deltaAbs,
        target: bench.target, ottimo: bench.ottimo, scarso: bench.scarso,
        level, color, symbol,
      };
    };

    if (!current) {
      return { current: null, prev, kpis: [], periodFrom: periodFromStr, periodTo: todayStr, prevFrom: prevFromStr, prevTo: prevToStr, nDays };
    }

    const kpiKeys = [
      "dolciPerCop", "contorniPerCop", "viniPerCop", "caffePerCop", "antipastiPerCop", "primiPerCop", "secondiPerCop",
      "mixVini", "mixBar", "mixDolci", "mixCaffe", "mixFoodBev",
      "ticketCop", "ticketCopPranzo", "ticketCopCena", "ticketTavolo", "portatePerCop", "articoliPerScn", "pctMenuCompleto",
      "coveriPranzoMedia", "coveriCenaMedia",
    ];
    const kpis = kpiKeys.filter(k => benchmarks[k]).map(k => buildKpi(k, current[k]));

    // Trova KPI critici (semaforo rosso o molto_sotto) per gli alert
    const critical = kpis.filter(k => k.level === "molto_sotto");
    const warning = kpis.filter(k => k.level === "sotto");
    const excellent = kpis.filter(k => k.level === "molto_sopra");

    return {
      current, prev, kpis, critical, warning, excellent,
      periodFrom: periodFromStr, periodTo: todayStr, prevFrom: prevFromStr, prevTo: prevToStr, nDays,
      nReceipts: current.nReceipts, nCovers: current.nCovers,
      totalRev: current.totalRev,           // backward compat (gross)
      totalRevGross: current.totalRevGross,
      totalRevNet: current.totalRevNet,
      tableRev: current.tableRev, tableRevNet: current.tableRevNet,
      bancoRev: current.bancoRev, bancoRevNet: current.bancoRevNet,
      nTableReceipts: current.nTableReceipts, nBancoReceipts: current.nBancoReceipts,
      bancoAvg: current.bancoAvg, bancoMixPct: current.bancoMixPct,
      // Wilson CI 95% per penetrazione categoria — esposto a UI KPI
      ciByCat: current.ciByCat || {},
    };
  },

  detectPriceDiffs: (state, lines, ddtRef = null) => {
    const diffs = [];
    lines.forEach(ln => {
      const bp = (state.baseProducts || []).find(b => b.id === ln.itemId);
      if (!bp) return;
      const newPrice = ln.pricePerUnit || ln.priceEst || 0;
      if (newPrice <= 0) return;
      const diff = newPrice - bp.pFatt;
      if (Math.abs(diff) < 0.01) return;
      diffs.push({
        bp, newPrice, oldPrice: bp.pFatt,
        diff, diffPct: bp.pFatt > 0 ? (diff / bp.pFatt) * 100 : 0,
        lineId: ln.id, ddtRef,
      });
    });
    return diffs;
  },

  // ── TOTALE DISPONIBILE per articolo ────────────────────────────────────────
  itemStock: (state, itemId) =>
    state.lots.filter(l => l.itemId === itemId && l.status !== "esaurito")
      .reduce((s, l) => s + l.qtyRemaining, 0),

  // ── COSTO UNITARIO MEDIO PONDERATO (dai prezzi di acquisto recenti) ────────
  // Fix #5 (Patch A): pesatura corretta per QUANTITA' (prima era per numero di righe).
  // Se il pesce arriva 60kg @ €20 + 40kg @ €30 deve dare (60*20+40*30)/100 = €24/kg,
  // NON (20+30)/2 = €25/kg. Le ultime ~10 righe sono un compromesso fra "ultimi prezzi"
  // e stabilita' del CMP rispetto a un'unica consegna outlier.
  itemUnitCost: (state, itemId) => {
    const recs = state.receipts
      .flatMap(r => r.lines.filter(l => l.itemId === itemId).map(l => ({
        qty: Number(l.qty) || 0,
        price: Number(l.pricePerUnit) || 0,
        date: r.date,
      })))
      .filter(r => r.qty > 0 && r.price > 0)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 10);
    if (!recs.length) {
      // Fallback: prezzo fattura del baseProduct
      const bp = (state.baseProducts || []).find(b => b.id === itemId);
      return bp ? (Number(bp.pFatt) || 0) : 0;
    }
    const totalQty = recs.reduce((s, r) => s + r.qty, 0);
    const totalVal = recs.reduce((s, r) => s + r.qty * r.price, 0);
    return totalQty > 0.0001 ? totalVal / totalQty : 0;
  },

  // ── CMP (Costo Medio Ponderato) OIC 13 sui lotti attivi ───────────────────
  // Fix #5 (Patch A2): valorizzazione magazzino corretta basata sui prezzi a cui
  // i lotti sono stati EFFETTIVAMENTE acquistati (snapshot lot.unitCost),
  // non sull'ultimo prezzo fornitore. Esclude lotti scaduti, in accettazione,
  // quarantena, rifiutati, esauriti.
  // Backward compat: lotti pre-fix senza unitCost → fallback a bp.pFatt.
  /**
   * @param {object} state
   * @param {string} itemId
   * @returns {{ cmp: number, totalQty: number, totalValue: number, lotCount: number, fallback: boolean }}
   */
  weightedAvgCost: (state, itemId) => {
    const today = TODAY;
    const activeLots = (state.lots || []).filter(l =>
      l.itemId === itemId &&
      l.status === "disponibile" &&
      (Number(l.qtyRemaining) || 0) > 0 &&
      (!l.expiresAt || l.expiresAt >= today)
    );
    let totalQty = 0;
    let totalValue = 0;
    let lotCount = 0;
    let usedFallback = false;
    const bp = (state.baseProducts || []).find(b => b.id === itemId);
    const fallbackCost = bp ? (Number(bp.pFatt) || Number(bp.pReale) || 0) : 0;
    for (const lot of activeLots) {
      const qty = Number(lot.qtyRemaining) || 0;
      if (qty <= 0) continue;
      // Preferisci lot.unitCost (NETTO IVA snapshot al receipt). Fallback a unitCostReal
      // (post-calo) e poi a bp.pFatt corrente per lotti pre-fix.
      let cost = Number(lot.unitCost);
      if (!Number.isFinite(cost) || cost <= 0) cost = Number(lot.unitCostReal);
      if (!Number.isFinite(cost) || cost <= 0) {
        cost = fallbackCost;
        if (cost > 0) usedFallback = true;
      }
      if (cost > 0) {
        totalQty += qty;
        totalValue += qty * cost;
        lotCount++;
      }
    }
    const cmp = totalQty > 0.0001 ? totalValue / totalQty : 0;
    return {
      cmp: Number.isFinite(cmp) ? cmp : 0,
      totalQty,
      totalValue: Number.isFinite(totalValue) ? totalValue : 0,
      lotCount,
      fallback: usedFallback,
    };
  },

  // ╔═════════════════════════════════════════════════════════════════════════╗
  // ║  STATS — Fix #6: assistente proattivo "quanto e quando ordinare"        ║
  // ║                                                                          ║
  // ║  Regole non negoziabili (Andi non e' statistico, ma noi sì):            ║
  // ║   - MAI un numero senza incertezza esplicita (n, sigma, CI, fallback).  ║
  // ║   - MAI trend su <14gg, MAI stagionalita' su <2 cicli.                  ║
  // ║   - MAI estrapolare oltre 2× lo storico disponibile.                    ║
  // ║   - SEMPRE riportare n (numero di giorni/osservazioni alla base).       ║
  // ║   - SEMPRE offrire `fallback: true` quando i dati sono insufficienti.   ║
  // ║                                                                          ║
  // ║  Pure functions. Stesso input → stesso output. No side effects.         ║
  // ╚═════════════════════════════════════════════════════════════════════════╝
  stats: {
    /**
     * Calcolo sconto% di uno scontrino: confronta il valore di LISTINO (= somma
     * dei prezzi carta di tutti gli articoli) con il TOTALE INCASSATO.
     *
     * Sconto = listinoTotal - realTotal
     * Sconto% = sconto / listinoTotal × 100
     *
     * Casi gestiti:
     *  - Item "regalo" (SESCIALA): listino 0, non contribuisce
     *  - "Sconto a Valore" (unitPrice<0): listino 0, già nel realTotal come negativo
     *  - Articoli inclusi in MENU CONCORDATO (unitPrice=0, dish.prezzo>0):
     *    listino = dish.prezzo (è il prezzo carta dell'articolo "regalato")
     *  - Articolo unmatched a prezzo 0: listino indeterminato → 0 (non distorce)
     *
     * Priorità lettura listino:
     *  1. item._listinoPrice (snapshot fatto al momento dell'import — preciso)
     *  2. fallback dinamico su dishesReal corrente (per scontrini pre-fix)
     *
     * Restituisce { listinoTotal, realTotal, discount, discountPct, hasData }.
     * hasData = false se listinoTotal === 0 (impossibile valutare).
     */
    receiptDiscount(receipt, dishesReal) {
      if (!receipt || !Array.isArray(receipt.items)) {
        return { listinoTotal: 0, realTotal: 0, discount: 0, discountPct: 0, hasData: false };
      }
      const dishMap = new Map((dishesReal || []).map(d => [d.id, d]));
      let listinoTotal = 0;
      for (const it of receipt.items) {
        const qty = Number(it.qty) || 0;
        // I tre casi "forza listino = 0" hanno PRIORITÀ sullo snapshot _listinoPrice
        // (lo snapshot legacy potrebbe avere il bug del prezzo menu concordato
        // contato come listino — vedi storia fix doImport).
        let p;
        if (isMenuConcordato(it)) p = 0;
        else if (it._isGift) p = 0;
        else if (isDiscountLine(it)) p = 0;
        else {
          // Per gli altri item, preferisci lo snapshot listino se valido.
          const snapP = Number(it._listinoPrice);
          if (Number.isFinite(snapP)) p = snapP;
          else if (it.unitPrice === 0) {
            const d = dishMap.get(it.dishId);
            p = d && Number(d.prezzo) > 0 ? Number(d.prezzo) : 0;
          } else {
            p = Number(it.unitPrice) || 0;
          }
        }
        listinoTotal += qty * p;
      }
      const realTotal = Number(receipt.total) || 0;
      const discount = listinoTotal - realTotal;
      const discountPct = listinoTotal > 0 ? discount / listinoTotal : 0;
      return {
        listinoTotal: Math.round(listinoTotal * 100) / 100,
        realTotal: Math.round(realTotal * 100) / 100,
        discount: Math.round(discount * 100) / 100,
        discountPct: Math.round(discountPct * 10000) / 10000,
        hasData: listinoTotal > 0,
      };
    },

    /**
     * Consumo medio giornaliero di un baseProduct dagli scontrini × ricette.
     *
     * Modello: per ogni receipt nell'ultima finestra (windowDays default 28),
     * espandi gli item con dish.ingredienti (oppure recipeSnapshot.ingredienti
     * come fallback), aggrega kg/giorno del bpId richiesto, calcola media e
     * deviazione standard (n-1, Bessel). EWMA α=0.3 (memoria ~7 giorni) per
     * smoothing breve termine. CV = sigma/mean misura la stabilita'.
     *
     * Robustezza: n = numero di GIORNI con almeno una vendita, non scontrini.
     * Se n < 7 → fallback:true (stima preliminare, banda larga richiesta).
     *
     * Output: { daily, sigma, n, ewma, cv, dailyByDow[7], fallback }
     *
     * Complessita': O(receipts × items × ingr) con dishMap pre-indicizzato.
     */
    bpConsumption(state, bpId, windowDays = 28) {
      const today = todayLocalISO();
      const cutoff = (() => {
        const d = new Date(today + "T12:00:00");
        d.setDate(d.getDate() - windowDays);
        return d.toISOString().slice(0, 10);
      })();
      // O(1) lookup ricetta → evita scan lineare per ogni item
      const dishMap = new Map((state.dishesReal || []).map(d => [d.id, d]));
      // Aggregazione daily: Map<dateISO, kg>
      const byDate = new Map();
      const receipts = state.tableReceipts || [];
      for (const r of receipts) {
        const date = getServiceDate(r.closedAt) || r.date;
        if (!date || date < cutoff || date > today) continue;
        for (const it of (r.items || [])) {
          // Preferisci ricetta live, fallback snapshot al momento dell'import
          const dish = dishMap.get(it.dishId);
          const ings = dish?.ingredienti || it.recipeSnapshot?.ingredienti || [];
          for (const ing of ings) {
            if (ing.bpId !== bpId) continue;
            const qtyPerDish = Number(ing.qty) || 0;
            const dishQty = Number(it.qty) || 0;
            if (qtyPerDish <= 0 || dishQty <= 0) continue;
            byDate.set(date, (byDate.get(date) || 0) + qtyPerDish * dishQty);
          }
        }
      }
      const n = byDate.size;
      if (n === 0) {
        return { daily: 0, sigma: 0, n: 0, ewma: 0, cv: 0, dailyByDow: Array(7).fill(0), fallback: true };
      }
      const values = Array.from(byDate.values());
      const mean = values.reduce((a, b) => a + b, 0) / n;
      // Varianza campionaria (n-1) per non sottostimare sigma su piccoli n
      const variance = n > 1 ? values.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1) : 0;
      const sigma = Math.sqrt(variance);
      const cv = mean > 0 ? sigma / mean : 0;
      // EWMA in ordine cronologico (α=0.3 → memoria ~7 step)
      const sortedDates = Array.from(byDate.keys()).sort();
      let ewma = byDate.get(sortedDates[0]) || 0;
      const alpha = 0.3;
      for (let i = 1; i < sortedDates.length; i++) {
        ewma = alpha * byDate.get(sortedDates[i]) + (1 - alpha) * ewma;
      }
      // Profilo settimanale (0=domenica). Utile per "il sabato consuma di piu'".
      const byDow = Array.from({ length: 7 }, () => ({ sum: 0, count: 0 }));
      for (const [date, qty] of byDate) {
        const d = new Date(date + "T12:00:00");
        if (isNaN(d.getTime())) continue;
        const dow = d.getDay();
        byDow[dow].sum += qty;
        byDow[dow].count++;
      }
      const dailyByDow = byDow.map(x => x.count > 0 ? x.sum / x.count : 0);
      return { daily: mean, sigma, n, ewma, cv, dailyByDow, fallback: n < 7 };
    },

    /**
     * Giorni di copertura: stock disponibile / consumo medio giornaliero.
     *
     * Stock = somma qtyRemaining lotti "disponibile", NON scaduti.
     * CI 80% naive: z=0.84 sulla sigma del daily. Banda asimmetrica (piu' consumo
     * → meno giorni). Se sigma=0 (n=1) → CI degenere ma days non-null.
     *
     * riskOutOfStock: days < (leadTimeDays + safetyDays). Default 2+1=3gg.
     *
     * Output: { days, ciLow, ciHigh, exhaustionDate, n, fallback, riskOutOfStock, stock, daily, sigma }
     */
    coverageDays(state, bpId, opts = {}) {
      const { leadTimeDays = 2, safetyDays = 1 } = opts;
      const consumption = engine.stats.bpConsumption(state, bpId);
      if (consumption.n === 0 || consumption.daily <= 0) {
        return { days: Infinity, ciLow: Infinity, ciHigh: Infinity, exhaustionDate: null, n: 0, fallback: true, riskOutOfStock: false, stock: 0, daily: 0, sigma: 0 };
      }
      const today = todayLocalISO();
      const stock = (state.lots || [])
        .filter(l => l.itemId === bpId && l.status === "disponibile" && (Number(l.qtyRemaining) || 0) > 0 && (!l.expiresAt || l.expiresAt >= today))
        .reduce((s, l) => s + (Number(l.qtyRemaining) || 0), 0);
      const days = consumption.daily > 0 ? stock / consumption.daily : Infinity;
      // Banda 80%: piu' consumo (dailyHi) → meno giorni; meno consumo (dailyLo) → piu' giorni
      const dailyHi = consumption.daily + 0.84 * consumption.sigma;
      const dailyLo = Math.max(0.001, consumption.daily - 0.84 * consumption.sigma);
      const ciLow = stock / dailyHi;
      const ciHigh = stock / dailyLo;
      const exhaustionDate = (() => {
        if (!Number.isFinite(days)) return null;
        const d = new Date(today + "T12:00:00");
        d.setDate(d.getDate() + Math.floor(days));
        return d.toISOString().slice(0, 10);
      })();
      const riskOutOfStock = stock > 0 && days < (leadTimeDays + safetyDays);
      return { days, ciLow, ciHigh, exhaustionDate, n: consumption.n, fallback: consumption.fallback, riskOutOfStock, stock, daily: consumption.daily, sigma: consumption.sigma };
    },

    /**
     * Anomalia prezzo: MAD-based robust z-score sul priceHistory.
     *
     * Formula: z = (newPrice - median) / (MAD × 1.4826)
     * Soglia: |z| > 3.5 → anomalia (Iglewicz & Hoaglin, 1993).
     *
     * Robusto a outlier (MAD vs std), funziona anche con distribuzioni asimmetriche
     * tipiche dei prezzi fornitore. Fallback a tolleranza fissa ±10% se n<8.
     *
     * Output: { z, isAnomaly, normalRange:[p25,p75], median, n, fallback, deltaPct? }
     */
    priceAnomaly(state, bpId, newPrice) {
      const bp = (state.baseProducts || []).find(b => b.id === bpId);
      if (!bp) {
        return { z: 0, isAnomaly: false, normalRange: null, median: null, n: 0, fallback: true };
      }
      const priceHistory = Array.isArray(bp.priceHistory) ? bp.priceHistory : [];
      const prices = priceHistory
        .map(h => Number(h.newPrice) ?? Number(h.pFatt))
        .map(p => Number(p))
        .filter(p => Number.isFinite(p) && p > 0);
      const n = prices.length;
      if (n < 8) {
        // Storico troppo corto: fallback a tolleranza relativa rispetto al prezzo corrente
        const currentPrice = Number(bp.pFatt) || (n > 0 ? prices[n - 1] : Number(newPrice) || 0);
        const deltaPct = currentPrice > 0 ? Math.abs(newPrice - currentPrice) / currentPrice : 0;
        return {
          z: 0,
          isAnomaly: deltaPct > 0.10,
          normalRange: currentPrice > 0 ? [currentPrice * 0.9, currentPrice * 1.1] : null,
          median: currentPrice || null,
          n,
          fallback: true,
          deltaPct,
        };
      }
      const sorted = [...prices].sort((a, b) => a - b);
      const median = sorted[Math.floor(n / 2)];
      const absDev = prices.map(p => Math.abs(p - median));
      const sortedDev = [...absDev].sort((a, b) => a - b);
      const mad = sortedDev[Math.floor(n / 2)];
      const sigmaEst = mad * 1.4826;  // costante di consistenza per distribuzione normale
      const z = sigmaEst > 0 ? (newPrice - median) / sigmaEst : 0;
      const p25 = sorted[Math.floor(n * 0.25)];
      const p75 = sorted[Math.floor(n * 0.75)];
      return { z, isAnomaly: Math.abs(z) > 3.5, normalRange: [p25, p75], median, n, fallback: false };
    },

    /**
     * Valore € a rischio scarto nei prossimi horizonDays.
     *
     * Per ogni lotto disponibile in scadenza entro l'orizzonte:
     *   consumoAtteso = consumption.daily × daysToExpiry
     *   eccesso = max(0, lot.qtyRemaining - consumoAtteso)
     *   eur = eccesso × lot.unitCost (fallback unitCostReal, poi bp.pFatt)
     *
     * Aggrega per bpId. Pre-indicizza bpConsumption una volta sola per evitare
     * O(lotti × O(receipts)) — il check di performance dell'iPad e' critico.
     *
     * Output: [{ bpId, name, lots:[{lotId, expiresAt, qty, consumoAtteso, eccesso, eur, daysToExpiry}], totalEur }, ...]
     */
    expiryRiskValue(state, horizonDays = 14) {
      const today = todayLocalISO();
      const horizonDate = (() => {
        const d = new Date(today + "T12:00:00");
        d.setDate(d.getDate() + horizonDays);
        return d.toISOString().slice(0, 10);
      })();
      // Pre-indicizza consumi per bpId UNA VOLTA: evita N chiamate ricorsive a bpConsumption
      const consumptionCache = new Map();
      const getConsumption = (bpId) => {
        if (!consumptionCache.has(bpId)) {
          consumptionCache.set(bpId, engine.stats.bpConsumption(state, bpId));
        }
        return consumptionCache.get(bpId);
      };
      const byBp = new Map();
      const todayMs = new Date(today + "T12:00:00").getTime();
      for (const lot of (state.lots || [])) {
        if (lot.status !== "disponibile") continue;
        const qtyRem = Number(lot.qtyRemaining) || 0;
        if (qtyRem <= 0) continue;
        if (!lot.expiresAt || lot.expiresAt > horizonDate) continue;
        const expMs = new Date(lot.expiresAt + "T12:00:00").getTime();
        if (!Number.isFinite(expMs)) continue;
        const daysToExpiry = Math.max(0, Math.ceil((expMs - todayMs) / 86400000));
        const consumption = getConsumption(lot.itemId);
        const consumoAtteso = consumption.daily * daysToExpiry;
        const eccesso = Math.max(0, qtyRem - consumoAtteso);
        if (eccesso <= 0) continue;
        // unitCost preferito (snapshot fattura), fallback in cascata
        let unitCost = Number(lot.unitCost);
        if (!Number.isFinite(unitCost) || unitCost <= 0) unitCost = Number(lot.unitCostReal);
        if (!Number.isFinite(unitCost) || unitCost <= 0) {
          const bp = (state.baseProducts || []).find(b => b.id === lot.itemId);
          unitCost = bp ? (Number(bp.pFatt) || 0) : 0;
        }
        const eur = eccesso * (unitCost || 0);
        if (!byBp.has(lot.itemId)) {
          const bp = (state.baseProducts || []).find(b => b.id === lot.itemId);
          byBp.set(lot.itemId, { bpId: lot.itemId, name: bp?.display || bp?.nome || "?", lots: [], totalEur: 0 });
        }
        const entry = byBp.get(lot.itemId);
        entry.lots.push({ lotId: lot.id, expiresAt: lot.expiresAt, qty: qtyRem, consumoAtteso, eccesso, eur, daysToExpiry });
        entry.totalEur += eur;
      }
      return Array.from(byBp.values()).sort((a, b) => b.totalEur - a.totalEur);
    },

    /**
     * Pareto/HHI (Herfindahl-Hirschman) sulla spesa fornitori ultimi N giorni.
     *
     * Class A: top fino a 80% cumulato (spesa critica)
     * Class B: 80-95% (spesa media)
     * Class C: residuo (long tail)
     *
     * HHI ∈ [0,1]. Soglie sane:
     *   < 0.15 → spesa distribuita
     *   0.15-0.30 → concentrazione normale
     *   > 0.30 → concentrazione alta (rischio dipendenza fornitore)
     *
     * Single-source: BP che hanno solo UN fornitore tra i receipts attivi.
     *
     * Output: { suppliers:[{id,name,spend,share,cumShare,class}], hhi, totalSpend, singleSourceBpCount, fallback }
     */
    abcSuppliers(state, days = 90) {
      const today = todayLocalISO();
      const cutoff = (() => {
        const d = new Date(today + "T12:00:00");
        d.setDate(d.getDate() - days);
        return d.toISOString().slice(0, 10);
      })();
      const map = new Map();  // supplierId → spend €
      for (const inv of (state.invoices || [])) {
        if (inv.deletedAt) continue;  // soft-deleted
        if (!inv.date || inv.date < cutoff) continue;
        const amount = Number(inv.taxable) || Number(inv.amount) || 0;
        if (amount <= 0) continue;
        map.set(inv.supplierId, (map.get(inv.supplierId) || 0) + amount);
      }
      const totalSpend = Array.from(map.values()).reduce((s, v) => s + v, 0);
      if (totalSpend === 0) {
        return { suppliers: [], hhi: 0, totalSpend: 0, singleSourceBpCount: 0, fallback: true };
      }
      const entries = Array.from(map.entries())
        .map(([id, spend]) => ({
          id,
          name: (state.suppliers || []).find(s => s.id === id)?.name || "?",
          spend,
          share: spend / totalSpend,
        }))
        .sort((a, b) => b.spend - a.spend);
      let cum = 0;
      const suppliers = entries.map(e => {
        cum += e.share;
        let cls = "C";
        if (cum <= 0.80) cls = "A";
        else if (cum <= 0.95) cls = "B";
        return { ...e, cumShare: cum, class: cls };
      });
      // HHI: sum of squared shares. Invariante: ∈ [1/N, 1].
      const hhi = entries.reduce((s, e) => s + e.share ** 2, 0);
      // Single-source: scansiona receipts attivi per identificare BP con un solo fornitore
      const bpSuppliers = new Map();  // bpId → Set<supplierId>
      for (const r of (state.receipts || [])) {
        if (r.deletedAt) continue;
        if (!r.supplierId) continue;
        for (const ln of (r.lines || [])) {
          const id = ln.itemId || ln.bpId;
          if (!id) continue;
          if (!bpSuppliers.has(id)) bpSuppliers.set(id, new Set());
          bpSuppliers.get(id).add(r.supplierId);
        }
      }
      const singleSourceBpCount = Array.from(bpSuppliers.values()).filter(s => s.size === 1).length;
      return { suppliers, hhi, totalSpend, singleSourceBpCount, fallback: entries.length < 3 };
    },

    /**
     * Variance analysis: food cost teorico (da scontrini × recipeSnapshot.netCost)
     * vs food cost reale (da kitchenOutputs × lot.unitCostReal effettivo del magazzino).
     *
     * Differenza tipica F&B = 3-7 punti percentuali. Dove si nasconde:
     *   • sprechi cucina (porzionature larghe, scarti eccessivi)
     *   • errori di scarico magazzino (lotti non consumati registrati)
     *   • furti / consumo personale non registrato
     *   • drift cambio fornitore (prezzo mai aggiornato in recipeSnapshot)
     *
     * Modello:
     *   FC_teorico = Σ item.recipeSnapshot.netCost × item.qty su scontrini del periodo
     *   FC_reale   = Σ consumedLot.qty × lot.unitCostReal (fallback unitCost) negli stessi giorni
     *   variance%  = (FC_reale - FC_teorico) / FC_teorico × 100
     *
     * CI 95% naïve: sigma stimata empiricamente come 20% della |variance| (proxy conservativo
     * sui ristoranti con storico breve), poi banda z=1.96 / √nDays. Un bootstrap completo
     * sui giorni richiederebbe storico daily granulare di entrambe le serie — fattibile in Fase 2.
     * p-value: z-test sulla stessa sigma.
     *
     * Soglie operative:
     *   |variance| ≤ 2%  → fisiologico (rumore di porzionatura)
     *   2-5%             → da monitorare (possibile drift)
     *   > 5%             → indagare (perdita strutturale, €/anno significativa)
     *
     * Fallback: n < 7 piatti venduti con snapshot → preliminare, banda non affidabile.
     *
     * Output: { teorico, reale, variance, varianceCI:[lo,hi], pValue, n, nDays, fallback, perDish:[...] }
     */
    fcVariance(state, windowDays = 14) {
      const today = todayLocalISO();
      const cutoff = (() => {
        const d = new Date(today + "T12:00:00");
        d.setDate(d.getDate() - windowDays);
        return d.toISOString().slice(0, 10);
      })();
      // 1. Teorico dagli scontrini: aggrega recipeSnapshot.netCost × qty per piatto
      let teorico = 0;
      let teoricoQty = 0;
      const perDishTeorico = new Map(); // dishId → { cost, qty }
      for (const r of (state.tableReceipts || [])) {
        const date = getServiceDate(r.closedAt) || r.date;
        if (!date || date < cutoff || date > today) continue;
        for (const it of (r.items || [])) {
          if (!it || (typeof it.dishId === "string" && it.dishId.startsWith("_unmatched_"))) continue;
          const qty = Number(it.qty) || 0;
          const snapNet = Number(it.recipeSnapshot?.netCost);
          if (qty <= 0 || !Number.isFinite(snapNet) || snapNet <= 0) continue;
          teorico += snapNet * qty;
          teoricoQty += qty;
          const e = perDishTeorico.get(it.dishId) || { cost: 0, qty: 0 };
          e.cost += snapNet * qty;
          e.qty += qty;
          perDishTeorico.set(it.dishId, e);
        }
      }
      // 2. Reale dal magazzino: kitchenOutputs.consumedLots × lot.unitCostReal nello stesso periodo
      const lotMap = new Map((state.lots || []).map(l => [l.id, l]));
      let reale = 0;
      for (const ko of (state.kitchenOutputs || [])) {
        const date = localDateOf(ko.date) || ko.date;
        if (!date || date < cutoff || date > today) continue;
        for (const cl of (ko.consumedLots || [])) {
          const lot = lotMap.get(cl.lotId);
          if (!lot) continue;
          const unitCost = Number(lot.unitCostReal) || Number(lot.unitCost) || 0;
          const qty = Number(cl.qty) || 0;
          if (qty <= 0 || unitCost <= 0) continue;
          reale += qty * unitCost;
        }
      }
      // 3. Fallback se dati insufficienti — onestà statistica
      const n = teoricoQty;
      if (teorico <= 0 || n < 7) {
        return {
          teorico, reale,
          variance: 0, varianceCI: [0, 0], pValue: null,
          n, nDays: 0, fallback: true, perDish: [],
        };
      }
      const variance = (reale - teorico) / teorico * 100;
      // Conta giorni unici con scontrini nel periodo (denominatore per CI sulla media giornaliera)
      const days = new Set();
      for (const r of (state.tableReceipts || [])) {
        const date = getServiceDate(r.closedAt) || r.date;
        if (date && date >= cutoff && date <= today) days.add(date);
      }
      const nDays = days.size || 1;
      // Sigma proxy: 20% del valore assoluto della variance (calibrato su benchmark F&B
      // dove la varianza daily reale è tipicamente CV ≈ 0.15-0.25 sul food cost giornaliero).
      // Un'analisi bootstrap daily completa è preferibile quando si hanno ≥30 giorni granulari.
      const sigma = Math.abs(variance) * 0.20;
      const ciHalf = 1.96 * sigma / Math.sqrt(nDays);
      const varianceCI = [variance - ciHalf, variance + ciHalf];
      // p-value z-test: H0 = variance == 0 (food cost reale == teorico)
      const seMean = sigma / Math.sqrt(nDays);
      const z = seMean > 0 ? variance / seMean : 0;
      const pValue = 2 * (1 - normalCDF(Math.abs(z)));
      // Per dish: top 20 per costo teorico (dove la perdita assoluta sarebbe più impattante)
      const perDish = [];
      for (const [dishId, t] of perDishTeorico) {
        const dish = (state.dishesReal || []).find(d => d.id === dishId);
        perDish.push({
          dishId,
          name: dish?.nome || "?",
          teorico: t.cost,
          qty: t.qty,
          avgCostPerUnit: t.qty > 0 ? t.cost / t.qty : 0,
        });
      }
      perDish.sort((a, b) => b.teorico - a.teorico);
      return {
        teorico, reale, variance, varianceCI, pValue,
        n, nDays, fallback: false,
        perDish: perDish.slice(0, 20),
      };
    },

    /**
     * Dashboard qualità import: persiste i KPI di "salute" dei dati importati,
     * che durante la fase importer sono visibili ma poi spariscono. Andi vola
     * cieco sul food cost di ~13% del fatturato (tasso unmatched tipico) per mesi.
     *
     * Metriche (default ultimi 30 giorni):
     *   pctUnmatched   = €(articoli con dishId _unmatched_*) / € fatturato totale
     *   pctNoSnapshot  = scontrini senza alcun recipeSnapshot valido / scontrini totali
     *   pctBanco       = scontrini con covers=0 (banco) / scontrini totali
     *   pctOutOfShift  = scontrini chiusi fuori 09-18 (pranzo) / 18-04 (cena) / scontrini totali
     *   topMissing     = top 10 articoli unmatched per fatturato (mappare → ricetta)
     *
     * Severity (su pctUnmatched, la metrica che impatta direttamente food cost):
     *   ≤ 5%   → VERDE (controllo qualità in linea)
     *   5-15%  → AMBRA (food cost teorico parzialmente cieco)
     *   > 15%  → ROSSO (food cost teorico inattendibile, azione urgente)
     *
     * Output: { pctUnmatched, pctNoSnapshot, pctBanco, pctOutOfShift, topMissing,
     *           totalRev, unmatchedRev, nReceipts, fallback }
     */
    importQuality(state, windowDays = 30) {
      const today = todayLocalISO();
      const cutoff = (() => {
        const d = new Date(today + "T12:00:00");
        d.setDate(d.getDate() - windowDays);
        return d.toISOString().slice(0, 10);
      })();
      const inRange = (state.tableReceipts || []).filter(r => {
        const d = getServiceDate(r.closedAt) || r.date;
        return d && d >= cutoff && d <= today;
      });
      const nReceipts = inRange.length;
      if (nReceipts === 0) {
        return {
          pctUnmatched: 0, pctNoSnapshot: 0, pctBanco: 0, pctOutOfShift: 0,
          topMissing: [], totalRev: 0, unmatchedRev: 0, nReceipts: 0, fallback: true,
        };
      }
      let totalRev = 0;
      let unmatchedRev = 0;
      let noSnapshotCount = 0;
      let bancoCount = 0;
      let outOfShiftCount = 0;
      let reconcileWarningCount = 0;
      let reconcileDiffSum = 0;
      const missingMap = new Map(); // name → { qty, rev }
      // Articoli esplicitamente marcati come "non è un piatto" da Andi (normStr keys)
      const ignoredSet = new Set(state.dishIgnoredArticles || []);
      for (const r of inRange) {
        totalRev += Number(r.total) || 0;
        if ((Number(r.covers) || 0) === 0) bancoCount++;
        // Shift detection coerente con getShift(): pranzo 11-18, cena 18-26 (wrap-around 00-02)
        const inShift = getShift(r.closedAt) !== null;
        if (!inShift) outOfShiftCount++;
        if (r._reconcileWarning) {
          reconcileWarningCount++;
          reconcileDiffSum += Math.abs(Number(r._reconcileDiff) || 0);
        }
        let receiptHasSnapshot = false;
        for (const it of (r.items || [])) {
          const qty = Number(it.qty) || 0;
          const unitPrice = Number(it.unitPrice) || 0;
          const grossLine = qty * unitPrice;
          if (it.recipeSnapshot && Number.isFinite(Number(it.recipeSnapshot.netCost)) && Number(it.recipeSnapshot.netCost) > 0) {
            receiptHasSnapshot = true;
          }
          if (typeof it.dishId === "string" && it.dishId.startsWith("_unmatched_")) {
            const name = it.name || "?";
            // Articoli ignorati: non contano come "food cost cieco" e non appaiono in topMissing
            if (!ignoredSet.has(normStr(name))) {
              unmatchedRev += grossLine;
              const m = missingMap.get(name) || { qty: 0, rev: 0 };
              m.qty += qty;
              m.rev += grossLine;
              missingMap.set(name, m);
            }
          }
        }
        if (!receiptHasSnapshot) noSnapshotCount++;
      }
      const topMissing = Array.from(missingMap.entries())
        .map(([name, v]) => ({ name, qty: v.qty, rev: v.rev }))
        .sort((a, b) => b.rev - a.rev)
        .slice(0, 10);
      return {
        pctUnmatched: totalRev > 0 ? unmatchedRev / totalRev : 0,
        pctNoSnapshot: nReceipts > 0 ? noSnapshotCount / nReceipts : 0,
        pctBanco: nReceipts > 0 ? bancoCount / nReceipts : 0,
        pctOutOfShift: nReceipts > 0 ? outOfShiftCount / nReceipts : 0,
        pctReconcileWarnings: nReceipts > 0 ? reconcileWarningCount / nReceipts : 0,
        reconcileDiffSum,
        topMissing,
        totalRev,
        unmatchedRev,
        nReceipts,
        fallback: false,
      };
    },

    /**
     * Statistiche ticket scontrino con distribuzione log-normale.
     *
     * La distribuzione del ticket F&B e' fortemente asimmetrica a destra
     * (log-normale): il 5-10% di scontrini "tavolata grande / festa" gonfia
     * la media aritmetica. La mediana e' molto piu' rappresentativa.
     *
     * Ritorna: mediana, IQR (P25-P75), P90 (high-roller), media (deenfatizzata),
     * CI 95% bootstrap sulla mediana (B=1000), n.
     *
     * Bootstrap percentile method (Efron & Tibshirani 1993): resampling con
     * reimmissione, calcolo della statistica su ogni resample, percentili
     * 2.5%-97.5% come CI 95%. Funziona indipendentemente dalla forma della
     * distribuzione. PRNG seedabile per riproducibilita' (mulberry32).
     *
     * Esclude scontrini banco (covers=0) di default: senza coperti il
     * "per coperto" e' indefinito e gonfierebbe il ticket.
     *
     * Fallback: n<5 → fallback:true (non sufficiente nemmeno per la mediana).
     *
     * Output: { median, mean, p25, p75, p90, medianCI:[low,high], n, fallback }
     */
    ticketStats(receipts, opts = {}) {
      const { includeBanco = false, perCover = true, bootstrap = 1000, seed = 0xB07AECA } = opts || {};
      const arr = Array.isArray(receipts) ? receipts : [];
      const filtered = arr.filter(r => {
        if (!r) return false;
        const cov = Number(r.covers) || 0;
        if (!includeBanco && cov === 0) return false;
        const total = Number(r.total) || 0;
        return total > 0;
      });
      const values = filtered.map(r => {
        const total = Number(r.total) || 0;
        const cov = Number(r.covers) || 1;
        return perCover ? total / cov : total;
      }).filter(v => Number.isFinite(v) && v > 0).sort((a, b) => a - b);
      const n = values.length;
      if (n < 5) {
        return { median: 0, mean: 0, p25: 0, p75: 0, p90: 0, medianCI: [0, 0], n, fallback: true };
      }
      // Quantile tipo-7 (R default) con linear interp tra indici. Robusto su n piccolo.
      const quant = (sorted, p) => {
        const L = sorted.length;
        if (L === 0) return 0;
        if (L === 1) return sorted[0];
        const h = (L - 1) * p;
        const lo = Math.floor(h), hi = Math.min(L - 1, lo + 1);
        const frac = h - lo;
        return sorted[lo] * (1 - frac) + sorted[hi] * frac;
      };
      const median = quant(values, 0.5);
      const p25 = quant(values, 0.25);
      const p75 = quant(values, 0.75);
      const p90 = quant(values, 0.90);
      const mean = values.reduce((a, b) => a + b, 0) / n;
      // PRNG deterministico (mulberry32) → CI riproducibili a parita' di input.
      let s = (seed ^ (n * 0x9E3779B1)) >>> 0;
      const rand = () => {
        s = (s + 0x6D2B79F5) >>> 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      const B = Math.max(200, Math.min(5000, bootstrap | 0));
      const bootMedians = new Array(B);
      const sample = new Array(n);
      for (let i = 0; i < B; i++) {
        for (let j = 0; j < n; j++) sample[j] = values[Math.floor(rand() * n)];
        sample.sort((a, b) => a - b);
        bootMedians[i] = quant(sample, 0.5);
      }
      bootMedians.sort((a, b) => a - b);
      const loIdx = Math.max(0, Math.floor(B * 0.025));
      const hiIdx = Math.min(B - 1, Math.floor(B * 0.975));
      const medianCI = [bootMedians[loIdx], bootMedians[hiIdx]];
      return { median, mean, p25, p75, p90, medianCI, n, fallback: false };
    },

    /**
     * Wilson score interval per una proporzione (Wilson 1927).
     *
     * Piu' affidabile del Wald (p ± z√(p(1-p)/n)) su n piccolo o p estremo
     * (≈0 o ≈1): Wald produce intervalli che escono da [0,1] e copertura
     * sotto il nominale. Wilson e' raccomandato come default da Agresti &
     * Coull (1998), Brown-Cai-DasGupta (2001).
     *
     * Formula:
     *   center = (p + z²/2n) / (1 + z²/n)
     *   half   = z · √( (p(1-p) + z²/4n) / n ) / (1 + z²/n)
     *
     * z=1.96 → CI 95% (default). Se n<=0 → ritorna zeros (no inferenza).
     *
     * Output: { p, ciLow, ciHigh, n }
     */
    wilsonCI(k, n, z = 1.96) {
      const N = Math.max(0, Number(n) || 0);
      const K = Math.max(0, Math.min(N, Number(k) || 0));
      if (N <= 0) return { p: 0, ciLow: 0, ciHigh: 0, n: 0 };
      const p = K / N;
      const z2 = z * z;
      const denom = 1 + z2 / N;
      const center = (p + z2 / (2 * N)) / denom;
      const half = z * Math.sqrt((p * (1 - p) + z2 / (4 * N)) / N) / denom;
      return {
        p,
        ciLow: Math.max(0, center - half),
        ciHigh: Math.min(1, center + half),
        n: N,
      };
    },

    /**
     * Test chi-quadro (χ²) per tabella 2×2 con correzione di continuità di Yates.
     *
     * Confronta due proporzioni (es. "% scontrini con DOLCI nel gruppo
     * ottimizzato" vs "% nel gruppo non-ottimizzato") e dice se la differenza
     * e' statisticamente significativa o solo rumore campionario.
     *
     * H0: i due gruppi hanno la stessa proporzione (Δ=0).
     * H1: le proporzioni differiscono.
     *
     * Tabella 2×2:
     *              gruppo1  gruppo2
     *   evento SI     a        b
     *   evento NO     c        d
     *
     * Statistica (con correzione Yates per df=1):
     *   χ² = Σ (|O - E| - 0.5)² / E
     * Con E_ij = (row_i × col_j) / n. p-value tramite CDF della χ²(1):
     *   P(X² ≤ x) = erf(√(x/2))   (Abramowitz-Stegun 26.2.7)
     *
     * Pre-condizioni:
     * - tutte le celle ≥ 0 (altrimenti warning, p=1)
     * - n ≥ 20 (sotto e' insignificante e Yates instabile)
     * - tutte le celle ATTESE ≥ 5 (altrimenti suggerire Fisher exact)
     *
     * Output: { chi, df=1, pValue, isSignificant (p<0.05), warning? }
     *
     * Reference: Agresti, Categorical Data Analysis (3rd ed.), § 3.2.
     */
    chiSquare2x2(a, b, c, d) {
      a = Number(a) || 0; b = Number(b) || 0; c = Number(c) || 0; d = Number(d) || 0;
      if (a < 0 || b < 0 || c < 0 || d < 0) {
        return { chi: 0, df: 1, pValue: 1, isSignificant: false, warning: "valori negativi" };
      }
      const n = a + b + c + d;
      if (n < 20) {
        return { chi: 0, df: 1, pValue: 1, isSignificant: false, warning: "n<20 troppo piccolo" };
      }
      const e11 = (a + b) * (a + c) / n;
      const e12 = (a + b) * (b + d) / n;
      const e21 = (c + d) * (a + c) / n;
      const e22 = (c + d) * (b + d) / n;
      if (e11 < 5 || e12 < 5 || e21 < 5 || e22 < 5) {
        return { chi: 0, df: 1, pValue: 1, isSignificant: false, warning: "celle attese <5: usare Fisher exact" };
      }
      // Yates' continuity correction (riduce bias df=1)
      const chi = Math.pow(Math.abs(a - e11) - 0.5, 2) / e11
                + Math.pow(Math.abs(b - e12) - 0.5, 2) / e12
                + Math.pow(Math.abs(c - e21) - 0.5, 2) / e21
                + Math.pow(Math.abs(d - e22) - 0.5, 2) / e22;
      // CDF χ²(1) = erf(√(x/2)). Approssimazione erf via Abramowitz-Stegun 7.1.26.
      const erf = (x) => {
        const sign = x >= 0 ? 1 : -1;
        const ax = Math.abs(x);
        const t = 1 / (1 + 0.3275911 * ax);
        const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t * Math.exp(-ax * ax);
        return sign * y;
      };
      const pValue = Math.max(0, Math.min(1, 1 - erf(Math.sqrt(chi / 2))));
      return { chi, df: 1, pValue, isSignificant: pValue < 0.05 };
    },

    /**
     * CUSUM (cumulative sum) change-point detection.
     *
     * Detect quando una serie temporale ha un "cambio di regime" (es. il food
     * cost giornaliero e' passato da media 28% a 33% intorno al giorno X).
     * Modello: S_t = Σ (x_i - μ̄). Il punto in cui |S_t| e' massimo e' il
     * candidato change-point. Se supera la soglia threshold·σ·√n → significativo.
     *
     * Pre: n ≥ 14 (sotto, una serie non ha abbastanza segnale per separare
     * change-point da fluttuazione casuale).
     *
     * Output:
     *   { changePointAt: idx | null, magnitude, beforeMean, afterMean, found, fallback? }
     *
     * Reference: Page (1954), "Continuous inspection schemes", Biometrika 41.
     */
    cusumChangepoint(series, opts = {}) {
      const { thresholdSigma = 3 } = opts;
      if (!Array.isArray(series) || series.length < 14) {
        return { changePointAt: null, magnitude: 0, beforeMean: 0, afterMean: 0, found: false, fallback: true };
      }
      const n = series.length;
      const vals = series.map(v => Number(v) || 0);
      const mean = vals.reduce((a, b) => a + b, 0) / n;
      let s = 0, maxAbs = 0, idx = -1;
      for (let i = 0; i < n; i++) {
        s += vals[i] - mean;
        if (Math.abs(s) > maxAbs) { maxAbs = Math.abs(s); idx = i; }
      }
      // sigma campionaria (n-1) per non sottostimare
      const sigma = n > 1
        ? Math.sqrt(vals.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1))
        : 0;
      const threshold = thresholdSigma * sigma * Math.sqrt(n);
      const found = sigma > 0 && maxAbs > threshold;
      if (!found || idx < 0) {
        return { changePointAt: null, magnitude: maxAbs, beforeMean: mean, afterMean: mean, found: false };
      }
      const before = vals.slice(0, idx + 1);
      const after = vals.slice(idx + 1);
      const beforeMean = before.length > 0 ? before.reduce((a, b) => a + b, 0) / before.length : 0;
      const afterMean  = after.length  > 0 ? after.reduce((a, b) => a + b, 0)  / after.length  : 0;
      return { changePointAt: idx, magnitude: maxAbs, beforeMean, afterMean, found: true };
    },

    /**
     * Robust z-score basato su MAD (Median Absolute Deviation).
     *
     * Z robusto = (x - mediana) / (1.4826 × MAD). La costante 1.4826 rende il
     * MAD consistente con sigma per distribuzioni normali. Resistente a outlier
     * (la mediana non si sposta con un singolo valore estremo, la media si').
     *
     * Output: numero (puo' essere negativo per valori sotto-mediana).
     *
     * Reference: Huber, Robust Statistics (2nd ed.), § 1.2.
     */
    robustZ(x, series) {
      if (!Array.isArray(series) || series.length === 0) return 0;
      const vals = series.map(v => Number(v) || 0);
      const sorted = [...vals].sort((a, b) => a - b);
      const med = sorted[Math.floor(sorted.length / 2)];
      const absDev = vals.map(v => Math.abs(v - med)).sort((a, b) => a - b);
      const mad = absDev[Math.floor(absDev.length / 2)];
      const sigmaEst = mad * 1.4826;
      return sigmaEst > 0 ? ((Number(x) || 0) - med) / sigmaEst : 0;
    },

    /**
     * Detect scontrini outlier sulla distribuzione del `total` con MAD-z robust.
     *
     * Soglia default |z| > 3.5 (Iglewicz & Hoaglin 1993). Sotto 30 scontrini la
     * stima del MAD e' instabile → ritorna Set vuoto (no flagging).
     *
     * Output: Set<receipt.id> degli scontrini outlier.
     */
    detectOutlierReceipts(receipts, threshold = 3.5) {
      if (!Array.isArray(receipts) || receipts.length < 30) return new Set();
      const valid = receipts.filter(r => (Number(r.total) || 0) > 0);
      if (valid.length < 30) return new Set();
      const totals = valid.map(r => Number(r.total) || 0);
      const sorted = [...totals].sort((a, b) => a - b);
      const med = sorted[Math.floor(sorted.length / 2)];
      const absDev = totals.map(v => Math.abs(v - med)).sort((a, b) => a - b);
      const mad = absDev[Math.floor(absDev.length / 2)];
      const sigmaEst = mad * 1.4826;
      if (sigmaEst <= 0) return new Set();
      const out = new Set();
      for (const r of valid) {
        const z = ((Number(r.total) || 0) - med) / sigmaEst;
        if (Math.abs(z) > threshold) out.add(r.id);
      }
      return out;
    },

    /**
     * Resa lavorazione per tipologia di workOrder ("pesce", "pane", "pasta").
     *
     * Aggrega le rese effettive dai workOrders completati nel periodo, leggendo
     * yieldRatio dal lotto figlio (popolato da engine.createChildLot in Fix L1).
     * Per backward compatibility, su lotti figli pre-fix senza yieldRatio:
     *   - se esistono consumedFromParent e qtyInitial → ricostruisce yieldRatio;
     *   - altrimenti il record viene scartato (no numero inventato).
     *
     * Output:
     *   - n=0..2 → fallback=true, valori a zero (Dashboard mostra "n insuff.")
     *   - n>=3  → median, p25, p75, mean reali + Wilson CI sul tasso di
     *     "scarto >50%" (resa < 0.5), utile per detection di lavorazioni anomale.
     *
     * @param {AppState} state
     * @param {string} type — "pesce" | "pane" | "pasta"
     * @param {number} windowDays — finestra storica (default 90 giorni)
     * @returns {{ median:number, p25:number, p75:number, mean:number, n:number,
     *             wilsonScarto50: object|null, fallback: boolean }}
     */
    labYieldByType(state, type, windowDays = 90) {
      const TODAY = todayLocalISO();
      const cutoff = (() => {
        const d = new Date(TODAY + "T12:00:00");
        d.setDate(d.getDate() - (Number(windowDays) || 90));
        return d.toISOString().slice(0, 10);
      })();

      const lots = state.lots || [];
      const yields = [];
      for (const wo of (state.workOrders || [])) {
        if (wo.type !== type) continue;
        if (wo.status !== "completato" && wo.status !== "completed") continue;
        // Periodo: completedAt (se presente) altrimenti createdAt come fallback.
        const refTs = wo.completedAt || wo.createdAt;
        const completedDate = refTs ? localDateOf(refTs) : null;
        if (!completedDate || completedDate < cutoff || completedDate > TODAY) continue;
        if (!wo.outputLotId) continue;
        const childLot = lots.find(l => l.id === wo.outputLotId);
        if (!childLot) continue;

        // Path A (nuovo): yieldRatio gia' calcolato e salvato.
        let y = Number(childLot.yieldRatio);
        // Path B (legacy): ricostruisci da consumedFromParent + qtyInitial.
        if (!Number.isFinite(y) || y <= 0) {
          const cons = Number(childLot.consumedFromParent) || 0;
          const out = Number(childLot.qtyInitial) || 0;
          if (cons > 0.0001 && out > 0.0001) y = out / cons;
        }
        // Validazione fisica: resa ∈ (0, 1]. Tolleranza 1% sopra 1 per arrotondamenti.
        if (Number.isFinite(y) && y > 0 && y <= 1.01) {
          yields.push(Math.min(1, y));
        }
      }

      const n = yields.length;
      if (n < 3) {
        return { median: 0, p25: 0, p75: 0, mean: 0, n, wilsonScarto50: null, fallback: true };
      }
      const sorted = [...yields].sort((a, b) => a - b);
      const q = (p) => sorted[Math.min(n - 1, Math.floor(n * p))];
      const median = q(0.5);
      const p25 = q(0.25);
      const p75 = q(0.75);
      const mean = yields.reduce((a, b) => a + b, 0) / n;
      // Tasso di "scarto eccessivo" (resa < 50%): se alto, alert al laboratorio.
      const scarto50Count = yields.filter(y => y < 0.5).length;
      const wilsonScarto50 = engine.stats.wilsonCI(scarto50Count, n);
      return { median, p25, p75, mean, n, wilsonScarto50, fallback: false };
    },

    /**
     * Fix L4 — Throughput laboratorio per tipologia di lavorazione (kg/h).
     *
     * Modello: per ogni workOrder completato del `type` nella finestra,
     *   kgIn  = consumedFromParent del lotto figlio (massa entrata effettiva)
     *   ore   = somma delle durate fasi (endedAt - startedAt) / 60
     *   sample = kgIn / ore   (kg materia trasformata per ora di lavorazione)
     * Aggreghiamo i sample puntuali e produciamo mediana + bootstrap CI 95% (B=1000):
     *   per ogni resample con ripetizione di n elementi, prendo la mediana,
     *   poi percentili 2.5 / 97.5 sui 1000 valori (Efron & Tibshirani, 1993).
     *
     * Perche' la mediana e non la media: la distribuzione kg/h e' tipicamente
     * asimmetrica destra (qualche lavorazione molto rapida tira su la media);
     * la mediana e' lo stimatore robusto di tendenza centrale.
     *
     * Soglia di n: < 3 → fallback:true. Bootstrap su n<3 e' instabile.
     *
     * @param {AppState} state
     * @param {string} type — "pesce" | "pane" | "pasta"
     * @param {number} windowDays — finestra storica (default 60)
     * @returns {{ kgPerHour:{median,ci95Low,ci95High}, n, totalKgIn, totalHours, fallback }}
     */
    labThroughput(state, type, windowDays = 60) {
      const TODAY = todayLocalISO();
      const cutoff = (() => {
        const d = new Date(TODAY + "T12:00:00");
        d.setDate(d.getDate() - (Number(windowDays) || 60));
        return d.toISOString().slice(0, 10);
      })();
      // Pre-indicizza lotti per O(1) lookup figlio
      const lotMap = new Map((state.lots || []).map(l => [l.id, l]));
      const samples = [];
      let totalKgIn = 0, totalHours = 0;

      for (const wo of (state.workOrders || [])) {
        if (wo.type !== type) continue;
        if (wo.status !== "completato" && wo.status !== "completed") continue;
        const completedDate = wo.completedAt ? localDateOf(wo.completedAt) : null;
        if (!completedDate || completedDate < cutoff || completedDate > TODAY) continue;

        // qtyIn dal lotto figlio (massa entrata = consumedFromParent)
        let qtyIn = 0;
        if (wo.outputLotId) {
          const childLot = lotMap.get(wo.outputLotId);
          qtyIn = Number(childLot?.consumedFromParent) || 0;
        }
        if (qtyIn <= 0) continue;

        // Durata fasi con start+end validi
        let durationMin = 0;
        for (const ph of (wo.phases || [])) {
          if (!ph.startedAt || !ph.endedAt) continue;
          const d = (new Date(ph.endedAt) - new Date(ph.startedAt)) / 60000;
          if (Number.isFinite(d) && d > 0) durationMin += d;
        }
        if (durationMin <= 0) continue;

        const kgPerHour = qtyIn / (durationMin / 60);
        if (Number.isFinite(kgPerHour) && kgPerHour > 0) {
          samples.push(kgPerHour);
          totalKgIn += qtyIn;
          totalHours += durationMin / 60;
        }
      }

      const n = samples.length;
      if (n < 3) {
        return { kgPerHour: { median: 0, ci95Low: 0, ci95High: 0 }, n, totalKgIn, totalHours, fallback: true };
      }
      const sorted = [...samples].sort((a, b) => a - b);
      const median = sorted[Math.floor(n / 2)];
      // Bootstrap CI 95% (B=1000) — basta per stabilizzare percentili
      const B = 1000;
      const bootMedians = new Array(B);
      for (let b = 0; b < B; b++) {
        const sample = new Array(n);
        for (let j = 0; j < n; j++) sample[j] = samples[Math.floor(Math.random() * n)];
        sample.sort((a, b) => a - b);
        bootMedians[b] = sample[Math.floor(n / 2)];
      }
      bootMedians.sort((a, b) => a - b);
      // Percentili 2.5 e 97.5 sugli 1000 valori
      const ci95Low = bootMedians[Math.floor(B * 0.025)];
      const ci95High = bootMedians[Math.floor(B * 0.975)];
      return {
        kgPerHour: { median, ci95Low, ci95High },
        n, totalKgIn, totalHours, fallback: false,
      };
    },

    /**
     * Fix L4 — Anomaly detection sul tempo per fase di un workOrder.
     *
     * Per ogni fase con start+end registrati, calcola:
     *   observedMinPerKg = durataFase(min) / qtyIn(kg)
     * e la confronta con la distribuzione storica delle stesse fasi (per
     * type × phase.name) usando MAD-z robust (Iglewicz & Hoaglin, 1993):
     *   z = (x - median) / (MAD × 1.4826)
     *   |z| > 3.5 → anomalia
     *
     * Storico < 5 osservazioni → fallback:true sulla singola fase (non chiamiamo
     * una fase "anomala" quando la base di confronto non e' affidabile).
     *
     * Uso tipico: badge inline su PhaseCard quando una fase e' lunga rispetto
     * alla mediana storica → segnala possibile problema (operatore lento,
     * materia prima difficile, attrezzatura guasta).
     *
     * @returns {[{ phaseId, phaseName, z, isAnomaly, observedMinPerKg, medianMinPerKg, n, fallback }]}
     */
    labPhaseAnomaly(workOrder, state) {
      if (!workOrder || !Array.isArray(workOrder.phases)) return [];
      const wo = workOrder;

      // qtyIn dell'ordine corrente (necessario per normalizzare i tempi)
      let qtyIn = 0;
      if (wo.outputLotId) {
        const childLot = (state.lots || []).find(l => l.id === wo.outputLotId);
        qtyIn = Number(childLot?.consumedFromParent) || 0;
      }
      if (qtyIn <= 0) return [];

      // Pre-indicizza i lotti UNA VOLTA — evita O(n²) sul lookup
      const lotMap = new Map((state.lots || []).map(l => [l.id, l]));

      const results = [];
      for (const ph of (wo.phases || [])) {
        if (!ph.startedAt || !ph.endedAt) continue;
        const durationMin = (new Date(ph.endedAt) - new Date(ph.startedAt)) / 60000;
        if (!Number.isFinite(durationMin) || durationMin <= 0) continue;
        const observedMinPerKg = durationMin / qtyIn;

        // Distribuzione storica: stesso type, stessa phase.name, altri workOrder completati
        const historic = [];
        for (const otherWo of (state.workOrders || [])) {
          if (otherWo.id === wo.id) continue;
          if (otherWo.type !== wo.type) continue;
          if (otherWo.status !== "completato" && otherWo.status !== "completed") continue;
          if (!otherWo.outputLotId) continue;
          const otherChild = lotMap.get(otherWo.outputLotId);
          const otherQtyIn = Number(otherChild?.consumedFromParent) || 0;
          if (otherQtyIn <= 0) continue;
          const matchingPhase = (otherWo.phases || []).find(p => p.name === ph.name);
          if (!matchingPhase?.startedAt || !matchingPhase?.endedAt) continue;
          const d = (new Date(matchingPhase.endedAt) - new Date(matchingPhase.startedAt)) / 60000;
          if (Number.isFinite(d) && d > 0) historic.push(d / otherQtyIn);
        }

        if (historic.length < 5) {
          results.push({
            phaseId: ph.id, phaseName: ph.name,
            z: 0, isAnomaly: false,
            observedMinPerKg, medianMinPerKg: null,
            n: historic.length, fallback: true,
          });
          continue;
        }

        const z = engine.stats.robustZ(observedMinPerKg, historic);
        const hSorted = [...historic].sort((a, b) => a - b);
        const medianHist = hSorted[Math.floor(hSorted.length / 2)];
        results.push({
          phaseId: ph.id,
          phaseName: ph.name,
          z,
          isAnomaly: Math.abs(z) > 3.5,
          observedMinPerKg,
          medianMinPerKg: medianHist,
          n: historic.length,
          fallback: false,
        });
      }
      return results;
    },

    /**
     * Fix L4 — Confronto operatori per stesso tipo di lavorazione.
     *
     * Per ogni operatore che ha completato workOrders del `taskType`,
     * aggrega:
     *   - kgPerH.median: throughput mediano (kg materia prima/ora di lavoro)
     *   - yield.{median,p25,p75}: resa mediana e IQR
     *   - scartoOver50.{rate, wilsonCI}: tasso di lavorazioni con resa<50%
     *     con intervallo Wilson 95% (robusto su n piccolo e p estremo)
     *
     * Operatore prevalente = chi ha completato piu' fasi del workOrder
     * (fallback: ph.completedBy → ph.startedBy → wo.operator → "Sconosciuto").
     *
     * Operatori con n<5 lavorazioni → fallback:true e metriche null (no confronto).
     * Output ordinato per kgPerH.median desc.
     *
     * @returns {[{ operator, n, kgPerH, yield, scartoOver50, fallback }]}
     */
    labOperatorCompare(state, taskType, windowDays = 120) {
      const TODAY = todayLocalISO();
      const cutoff = (() => {
        const d = new Date(TODAY + "T12:00:00");
        d.setDate(d.getDate() - (Number(windowDays) || 120));
        return d.toISOString().slice(0, 10);
      })();
      const lotMap = new Map((state.lots || []).map(l => [l.id, l]));
      const byOperator = new Map();

      for (const wo of (state.workOrders || [])) {
        if (wo.type !== taskType) continue;
        if (wo.status !== "completato" && wo.status !== "completed") continue;
        const completedDate = wo.completedAt ? localDateOf(wo.completedAt) : null;
        if (!completedDate || completedDate < cutoff || completedDate > TODAY) continue;

        // Determina operatore prevalente (max fasi completate)
        const opCount = new Map();
        for (const ph of (wo.phases || [])) {
          const op = ph.completedBy || ph.startedBy || wo.operator;
          if (op) opCount.set(op, (opCount.get(op) || 0) + 1);
        }
        const operator = [...opCount.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || wo.operator || "Sconosciuto";

        // qtyIn + durata + yield
        const childLot = wo.outputLotId ? lotMap.get(wo.outputLotId) : null;
        const qtyIn = Number(childLot?.consumedFromParent) || 0;
        let yieldRatio = Number(childLot?.yieldRatio);
        if (!Number.isFinite(yieldRatio) || yieldRatio <= 0) {
          const out = Number(childLot?.qtyInitial) || 0;
          if (qtyIn > 0.0001 && out > 0.0001) yieldRatio = out / qtyIn;
          else yieldRatio = 0;
        }
        if (qtyIn <= 0) continue;

        let durationMin = 0;
        for (const ph of (wo.phases || [])) {
          if (!ph.startedAt || !ph.endedAt) continue;
          const d = (new Date(ph.endedAt) - new Date(ph.startedAt)) / 60000;
          if (Number.isFinite(d) && d > 0) durationMin += d;
        }
        if (durationMin <= 0) continue;

        const kgh = qtyIn / (durationMin / 60);
        if (!byOperator.has(operator)) byOperator.set(operator, { kgh: [], yields: [], n: 0 });
        const e = byOperator.get(operator);
        e.kgh.push(kgh);
        if (yieldRatio > 0 && yieldRatio <= 1.01) e.yields.push(Math.min(1, yieldRatio));
        e.n++;
      }

      const results = [];
      for (const [operator, data] of byOperator) {
        if (data.n < 5) {
          results.push({ operator, n: data.n, kgPerH: null, yield: null, scartoOver50: null, fallback: true });
          continue;
        }
        const kghSorted = [...data.kgh].sort((a, b) => a - b);
        const yieldSorted = [...data.yields].sort((a, b) => a - b);
        const scartoCount = data.yields.filter(y => y < 0.5).length;
        const wilson = data.yields.length > 0 ? engine.stats.wilsonCI(scartoCount, data.yields.length) : null;
        const yieldQ = (p) => yieldSorted.length === 0 ? 0 : yieldSorted[Math.min(yieldSorted.length - 1, Math.floor(yieldSorted.length * p))];

        results.push({
          operator,
          n: data.n,
          kgPerH: { median: kghSorted[Math.floor(kghSorted.length / 2)] },
          yield: yieldSorted.length > 0 ? {
            median: yieldQ(0.5),
            p25: yieldQ(0.25),
            p75: yieldQ(0.75),
          } : null,
          scartoOver50: (data.yields.length > 0 && wilson) ? {
            rate: scartoCount / data.yields.length,
            wilsonCI: [wilson.ciLow, wilson.ciHigh],
          } : null,
          fallback: false,
        });
      }
      results.sort((a, b) => (b.kgPerH?.median || 0) - (a.kgPerH?.median || 0));
      return results;
    },

    /**
     * S1 — Market basket analysis su scontrini (cross-sell).
     *
     * Per i top-N piatti per frequenza, calcola per ogni coppia (A, B) ordinata
     * (A → B):
     *   - support = numero scontrini che contengono sia A sia B
     *   - P(B|A)  = co(A,B) / count(A)             [probabilita' condizionata]
     *   - P(B)    = count(B) / N                   [probabilita' marginale]
     *   - lift    = P(B|A) / P(B)                  [forza dell'associazione]
     *   - chi²    = test 2x2 con Yates             [significativita']
     *
     * Lift > 1.5 = "abbinamento piu' frequente del caso". Lift = 1 indipendenza.
     * Lift < 1 = "evitano l'abbinamento" (raro in F&B).
     *
     * Correzione Bonferroni: con topN × (topN-1) test simultanei, alpha viene
     * diviso per ridurre falsi positivi (necessario quando si scorrono molte
     * coppie). Senza correzione si trovano "associazioni" per puro caso.
     *
     * Pre-condizioni:
     *  - n_scontrini >= 50 (sotto, segnale dominato dal rumore campionario)
     *  - support >= 30 (Agresti, "Categorical Data Analysis": sotto si esce
     *    dal regime CLT e Yates non basta — fallback χ² warning)
     *
     * Performance: O(N × topN²) — con topN=30 e N=10k scontrini ~9M iter, ok.
     *
     * Reference: Agrawal et al. (1993) "Mining association rules between sets
     * of items"; Brin et al. (1997) per lift; Bonferroni per multiple testing.
     *
     * Output: { associations[], nScontrini, nTopDishes, minSupport, minLift,
     *           adjAlpha, fallback, reason? }
     */
    crossSellMatrix(state, opts = {}) {
      const { topN = 30, minSupport = 30, minLift = 1.5, alpha = 0.01, windowDays = 90 } = opts;
      const TODAY = todayLocalISO();
      const cutoff = (() => {
        const d = new Date(TODAY + "T12:00:00");
        d.setDate(d.getDate() - (Number(windowDays) || 90));
        return d.toISOString().slice(0, 10);
      })();

      const receipts = (state.tableReceipts || []).filter(r => {
        const date = getServiceDate(r.closedAt) || r.date;
        return date && date >= cutoff && date <= TODAY;
      });

      if (receipts.length < 50) {
        return {
          associations: [], nScontrini: receipts.length, nTopDishes: 0,
          minSupport, minLift, adjAlpha: alpha, windowDays,
          fallback: true,
          reason: `Servono almeno 50 scontrini per analisi cross-sell (attuali: ${receipts.length}).`,
        };
      }

      // Step 1 — frequenze per piatto unico-per-scontrino (no duplicati interni)
      const dishCount = new Map();
      for (const r of receipts) {
        const dishes = new Set();
        for (const it of (r.items || [])) {
          if (!it.dishId || typeof it.dishId !== "string" || it.dishId.startsWith("_unmatched_")) continue;
          dishes.add(it.dishId);
        }
        for (const d of dishes) {
          dishCount.set(d, (dishCount.get(d) || 0) + 1);
        }
      }

      const topDishes = Array.from(dishCount.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, Math.max(1, topN))
        .map(([id]) => id);
      const topSet = new Set(topDishes);

      // Step 2 — matrice co-occorrenze (ordinata: A|B con A≠B)
      const coCount = new Map();
      for (const r of receipts) {
        const dishes = Array.from(new Set((r.items || [])
          .map(it => it.dishId)
          .filter(d => d && topSet.has(d))
        ));
        for (let i = 0; i < dishes.length; i++) {
          for (let j = 0; j < dishes.length; j++) {
            if (i === j) continue;
            const key = `${dishes[i]}|${dishes[j]}`;
            coCount.set(key, (coCount.get(key) || 0) + 1);
          }
        }
      }

      // Step 3 — metriche per ogni coppia + χ² 2×2 + Bonferroni
      const N = receipts.length;
      const dishMap = new Map((state.dishesReal || []).map(d => [d.id, d]));
      const nTests = Math.max(1, topDishes.length * (topDishes.length - 1));
      const adjAlpha = alpha / nTests;
      const results = [];

      for (const a of topDishes) {
        for (const b of topDishes) {
          if (a === b) continue;
          const cAB = coCount.get(`${a}|${b}`) || 0;
          if (cAB < minSupport) continue;
          const cA = dishCount.get(a) || 0;
          const cB = dishCount.get(b) || 0;
          if (cA <= 0 || cB <= 0) continue;

          const pBA = cAB / cA;
          const pB = cB / N;
          const lift = pB > 0 ? pBA / pB : 0;
          if (lift < minLift) continue;

          // Tabella 2x2 (Yates già dentro chiSquare2x2)
          //                  hasB    noB
          //   hasA           cAB     cA - cAB
          //   noA            cB - cAB    N - cA - cB + cAB
          const t_a = cAB;
          const t_b = cA - cAB;
          const t_c = cB - cAB;
          const t_d = N - cA - cB + cAB;
          const chi = engine.stats.chiSquare2x2(t_a, t_b, t_c, t_d);

          results.push({
            a, aName: dishMap.get(a)?.nome || a,
            b, bName: dishMap.get(b)?.nome || b,
            support: cAB,
            cA, cB,
            pBA, pB, lift,
            pValue: chi.pValue,
            isSignificant: chi.pValue < adjAlpha && !chi.warning,
            chi: chi.chi,
            chiWarning: chi.warning || null,
          });
        }
      }

      results.sort((x, y) => y.lift - x.lift);

      return {
        associations: results,
        nScontrini: N,
        nTopDishes: topDishes.length,
        minSupport, minLift, adjAlpha,
        alpha, windowDays,
        fallback: false,
      };
    },

    /**
     * S2 — Concentrazione menu: HHI (Herfindahl-Hirschman), Gini, Pareto 80.
     *
     * Misura quanto il fatturato dipende da pochi piatti. Utile per risk
     * management: "se va out-of-stock il piatto top, quanto del fatturato
     * scopri?"
     *
     * Formule:
     *  - share_i = revenue_i / Σrevenue
     *  - HHI = Σ share_i²                          ∈ [1/N, 1]
     *  - Gini = (2 Σ i·v_i) / (n Σv_i) − (n+1)/n   con v ordinato crescente
     *  - Pareto 80 = numero piatti che cumulano ≥80% del fatturato (rule)
     *
     * Soglie HHI (interpretazione F&B):
     *   < 0.05  → "Distribuito"          (menu sano, bassa fragilita')
     *   < 0.15  → "Concentrazione normale" (qualche top, ma diversificato)
     *   < 0.25  → "Concentrato — rischio dipendenza"
     *   ≥ 0.25  → "Fragile — over-dependence su pochi piatti"
     *
     * Gini ≈ 0.65+ : menu sbilanciato (regola di Pareto attiva).
     *
     * Pre-condizioni: n_scontrini ≥ 30 (sotto, stima troppo rumorosa).
     *
     * Reference: Herfindahl (1950); Hirschman (1964); Gini (1912);
     * Pareto (1896).
     *
     * Output: { hhi, gini, pareto80, pareto80Pct, topConcentration,
     *           totalRevenue, totalDishes, items[], fallback, reason? }
     */
    menuConcentration(state, opts = {}) {
      const { windowDays = 90 } = opts;
      const TODAY = todayLocalISO();
      const cutoff = (() => {
        const d = new Date(TODAY + "T12:00:00");
        d.setDate(d.getDate() - (Number(windowDays) || 90));
        return d.toISOString().slice(0, 10);
      })();

      const receipts = (state.tableReceipts || []).filter(r => {
        const date = getServiceDate(r.closedAt) || r.date;
        return date && date >= cutoff && date <= TODAY;
      });

      if (receipts.length < 30) {
        return {
          hhi: 0, gini: 0, pareto80: 0, pareto80Pct: 0,
          topConcentration: 0, totalRevenue: 0, totalDishes: 0,
          items: [], fallback: true,
          nScontrini: receipts.length, windowDays,
          reason: `Servono almeno 30 scontrini (attuali: ${receipts.length}).`,
        };
      }

      // Aggrega revenue per piatto (esclusi unmatched)
      const dishRevenue = new Map();
      for (const r of receipts) {
        for (const it of (r.items || [])) {
          if (!it.dishId || typeof it.dishId !== "string" || it.dishId.startsWith("_unmatched_")) continue;
          const qty = Number(it.qty) || 0;
          const unitPrice = Number(it.unitPrice) || 0;
          if (qty <= 0 || unitPrice <= 0) continue;
          const rev = qty * unitPrice;
          dishRevenue.set(it.dishId, (dishRevenue.get(it.dishId) || 0) + rev);
        }
      }

      const total = Array.from(dishRevenue.values()).reduce((s, v) => s + v, 0);
      if (total <= 0 || dishRevenue.size === 0) {
        return {
          hhi: 0, gini: 0, pareto80: 0, pareto80Pct: 0,
          topConcentration: 0, totalRevenue: 0, totalDishes: 0,
          items: [], fallback: true,
          nScontrini: receipts.length, windowDays,
          reason: "Nessun fatturato matchato nei dish nella finestra selezionata.",
        };
      }

      const dishMap = new Map((state.dishesReal || []).map(d => [d.id, d]));
      const sorted = Array.from(dishRevenue.entries())
        .map(([id, rev]) => ({
          id, name: dishMap.get(id)?.nome || id,
          revenue: rev,
          share: rev / total,
        }))
        .sort((a, b) => b.revenue - a.revenue);

      // HHI = Σ(share²)
      const hhi = sorted.reduce((s, x) => s + x.share * x.share, 0);

      // Gini su revenue crescente
      const v = sorted.map(x => x.revenue).slice().reverse();  // ascendente
      const n = v.length;
      const sumV = v.reduce((s, x) => s + x, 0);
      let cum = 0;
      for (let i = 0; i < n; i++) cum += (i + 1) * v[i];
      const gini = (n > 0 && sumV > 0) ? (2 * cum) / (n * sumV) - (n + 1) / n : 0;

      // Pareto 80 (numero piatti per arrivare a 80% del fatturato)
      let cumShare = 0;
      let pareto80 = sorted.length;
      for (let i = 0; i < sorted.length; i++) {
        cumShare += sorted[i].share;
        if (cumShare >= 0.80) { pareto80 = i + 1; break; }
      }

      // Top concentration (quota del piatto #1)
      const topConcentration = sorted[0]?.share || 0;

      // Items per il display (top 20) con cumulato
      let cum2 = 0;
      const items = sorted.slice(0, 20).map((x, i) => {
        cum2 += x.share;
        return { ...x, rank: i + 1, cumShare: cum2 };
      });

      return {
        hhi, gini,
        pareto80,
        pareto80Pct: sorted.length > 0 ? pareto80 / sorted.length : 0,
        topConcentration,
        totalRevenue: total,
        totalDishes: sorted.length,
        items,
        nScontrini: receipts.length,
        windowDays,
        fallback: false,
      };
    },

    /**
     * Fix L4 — Forecast capacita' laboratorio prossimi N giorni.
     *
     * Modello (Little's Law applicata al laboratorio):
     *   oreRichieste = Σ (qtyStimata / throughputMediano_type)
     * con propagazione errore CI95% via somma in quadratura delle sigma
     * (assunzione: type indipendenti — non sono perfettamente vere ma il
     * conservativismo nella banda e' accettabile).
     * Sigma derivata back dalla banda bootstrap: sigma = (CI_high - CI_low) / (2 × 1.96).
     *
     * Capacita' disponibile = operators × hoursPerOperatorWeek (default 3 × 32 = 96h).
     * Deficit = max(0, oreRichieste - oreDisponibili).
     *
     * Edge case: tipi senza storia sufficiente (throughput.fallback=true) entrano
     * con flag fallback ma NON contribuiscono al monte ore (perche' non sappiamo
     * stimare). Vengono comunque mostrati in byType[type].qty per trasparenza.
     *
     * @param {AppState} state
     * @param {{ lookAheadDays?, operatorsCount?, hoursPerOperatorWeek? }} opts
     */
    labCapacityForecast(state, opts = {}) {
      const { lookAheadDays = 7, operatorsCount = 3, hoursPerOperatorWeek = 32 } = opts;
      const TODAY = todayLocalISO();
      const endDate = (() => {
        const d = new Date(TODAY + "T12:00:00");
        d.setDate(d.getDate() + (Number(lookAheadDays) || 7));
        return d.toISOString().slice(0, 10);
      })();

      const planned = (state.plannedOrders || []).filter(p => p.date >= TODAY && p.date <= endDate);
      const hoursAvailable = operatorsCount * hoursPerOperatorWeek;

      if (planned.length === 0) {
        return { byType: {}, hoursRequired: { point: 0, ci95Low: 0, ci95High: 0 }, hoursAvailable, deficit: 0, fallback: true };
      }

      // Cache throughput per type — evita ricalcoli identici
      const thrCache = new Map();
      const byType = {};
      let totalHours = 0;
      let totalVar = 0;
      let anyFallback = false;

      for (const p of planned) {
        // qtyStimata: se assente, 1 kg (assunzione conservativa; meglio sottostimare ore richieste e segnalare con flag)
        const qty = Number(p.estimatedQty) || Number(p.qty) || 1;
        const type = p.type;
        if (!type) continue;

        if (!thrCache.has(type)) thrCache.set(type, engine.stats.labThroughput(state, type, 90));
        const throughput = thrCache.get(type);

        if (!byType[type]) byType[type] = { qty: 0, hours: 0, fallback: throughput.fallback };

        if (throughput.fallback || throughput.kgPerHour.median <= 0) {
          byType[type].qty += qty;
          byType[type].fallback = true;
          anyFallback = true;
          continue;
        }

        const hoursMedian = qty / throughput.kgPerHour.median;
        const hoursLow = qty / throughput.kgPerHour.ci95High;
        const hoursHigh = qty / throughput.kgPerHour.ci95Low;
        const sigma = (hoursHigh - hoursLow) / (2 * 1.96);

        byType[type].qty += qty;
        byType[type].hours += hoursMedian;
        totalHours += hoursMedian;
        totalVar += sigma * sigma;
      }

      const totalSigma = Math.sqrt(totalVar);
      const ci95Half = 1.96 * totalSigma;
      const deficit = Math.max(0, totalHours - hoursAvailable);

      return {
        byType,
        hoursRequired: {
          point: totalHours,
          ci95Low: Math.max(0, totalHours - ci95Half),
          ci95High: totalHours + ci95Half,
        },
        hoursAvailable,
        deficit,
        // fallback: true solo se TUTTI i type pianificati sono in fallback (= zero ore stimabili)
        fallback: totalHours === 0,
        anyTypeFallback: anyFallback,
      };
    },
  },

  // ── COSTO REALE di un piatto usando i lotti effettivamente consumati ───────
  dishRealCost: (state, dishId) => {
    const ko = state.kitchenOutputs.filter(o => o.dishId === dishId);
    if (!ko.length) return null;
    let totalVal = 0, totalServings = 0;
    ko.forEach(o => {
      totalServings += Number(o.servings) || 0;
      (o.consumedLots || []).forEach(cl => {
        const lot = state.lots.find(l => l.id === cl.lotId);
        if (!lot) return;
        // FIX audit 2026-06-06: valorizza al costo snapshot post-calo del lotto
        // (unitCostReal), fallback unitCost, poi pricePerUnit DDT per lotti legacy.
        // Prima usava SOLO pricePerUnit del receipt → 0€ sui lotti figlio
        // (receiptId null) e calo peso ignorato, sottostimando il food cost reale.
        let unit = Number(lot.unitCostReal);
        if (!Number.isFinite(unit) || unit <= 0) unit = Number(lot.unitCost);
        if (!Number.isFinite(unit) || unit <= 0) {
          const rec = state.receipts.find(r => r.id === lot.receiptId);
          const line = rec?.lines.find(ln => ln.itemId === lot.itemId);
          unit = line ? (Number(line.pricePerUnit) || 0) : 0;
        }
        totalVal += (Number(cl.qty) || 0) * unit;
      });
    });
    return totalServings > 0 ? totalVal / totalServings : null;
  },

  // ── COSTO TEORICO del piatto dalla ricetta ─────────────────────────────────
  dishTheoreticalCost: (state, dish) =>
    dish.ingredients.reduce((s, ing) => s + engine.itemUnitCost(state, ing.itemId) * ing.qty, 0),

  // ── FIFO: lotti ordinati per scadenza (il più vicino prima) ────────────────
  // Reg. CE 178/2002 art. 18 + 852/2004 All. II Cap. IX:
  // i lotti in stato "in_accettazione", "quarantena" o "rifiutato" NON sono
  // allocabili ne' FIFO ne' manualmente — la merce deve passare prima il
  // controllo di accettazione (temperatura cella + conformita' etichetta).
  fifoLotsForItem: (state, itemId) => {
    // FIX audit 2026-06-06: (1) ESCLUDI lotti scaduti — Reg. CE 852/2004 All. II
    // Cap. IX: merce oltre scadenza non e' allocabile al consumo. coverageDays e
    // weightedAvgCost gia' filtravano gli scaduti: questa era l'incoerenza che
    // permetteva di proporre lotti scaduti al prelievo.
    // (2) Ordinamento FEFO robusto su stringa ISO (no new Date): un lotto con
    // expiresAt nullo NON deve finire 1970→primo in coda; va in fondo (9999).
    const today = todayLocalISO();
    return state.lots
      .filter(l =>
        l.itemId === itemId &&
        l.status !== "esaurito" &&
        l.status !== "in_accettazione" &&
        l.status !== "quarantena" &&
        l.status !== "rifiutato" &&
        (Number(l.qtyRemaining) || 0) > 0 &&
        (!l.expiresAt || l.expiresAt >= today)
      )
      .sort((a, b) =>
        (a.expiresAt || "9999-12-31").localeCompare(b.expiresAt || "9999-12-31")
      );
  },

  // ── SUGGEST FIFO ALLOCATION ────────────────────────────────────────────────
  suggestFifoAllocation: (state, itemId, qtyNeeded) => {
    const lots = engine.fifoLotsForItem(state, itemId);
    const allocation = [];
    let remaining = qtyNeeded;
    for (const lot of lots) {
      if (remaining <= 0) break;
      const take = Math.min(remaining, lot.qtyRemaining);
      allocation.push({ lotId: lot.id, qty: take, expiresAt: lot.expiresAt });
      remaining -= take;
    }
    return { allocation, shortfall: remaining > 0.001 ? remaining : 0 };
  },

  // ── BOZZA SCARICO DA VENDUTO (STEP 1 — PURA, READ-ONLY) ─────────────────────
  // Ponte "Venduto → Scarico magazzino", primo micro-step. Legge gli scontrini
  // importati in un intervallo di giorni-SERVIZIO, espande le ricette in fabbisogno
  // materia prima per prodotto base (bpId), propone l'allocazione FIFO/FEFO e
  // calcola un costo stimato — SENZA scrivere nulla. Non muta lo state, non crea
  // movimenti/kitchenOutputs/salesDischarges, non chiama setState. Serve a generare
  // una bozza verificabile che (in uno step successivo) Andi potrà confermare.
  // Convenzioni riusate (invariate): getServiceDate(r.closedAt) come il resto
  // dell'engine (dailyMetrics/kpiDerived), banco = covers===0, isMenuConcordato,
  // suggestFifoAllocation (che già esclude lotti scaduti/esauriti via fifoLotsForItem).
  buildScaricoProposalFromSales: (state, fromDate, toDate) => {
    const warnings = [];
    const blockers = [];
    const r4 = (n) => Math.round((Number(n) || 0) * 10000) / 10000;
    const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

    // 0. Validazione intervallo (YYYY-MM-DD, from <= to)
    const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
    const validRange = ISO_RE.test(String(fromDate)) && ISO_RE.test(String(toDate)) && String(fromDate) <= String(toDate);
    if (!validRange) {
      blockers.push({ code: "INVALID_RANGE", message: `Intervallo date non valido: ${fromDate} → ${toDate}` });
    }

    // 1. Selezione scontrini per GIORNO SERVIZIO (no closedAt grezzo)
    const allReceipts = state.tableReceipts || [];
    const inRange = validRange ? allReceipts.filter(r => {
      const sd = getServiceDate(r.closedAt) || r.date;
      return sd && sd >= fromDate && sd <= toDate;
    }) : [];
    if (validRange && inRange.length === 0) {
      blockers.push({ code: "NO_RECEIPTS", message: `Nessuno scontrino tra ${fromDate} e ${toDate}` });
    }

    const receiptIds = inRange.map(r => r.id);
    const receiptCount = inRange.length;
    const covers = inRange.reduce((s, r) => s + (Number(r.covers) || 0), 0);
    const grossRevenue = r2(inRange.reduce((s, r) => s + (Number(r.total) || 0), 0));

    // sourceHash deterministico (djb2-xor, nessuna dipendenza): impronta del CONTENUTO
    // della proposta = intervallo + scontrini ORDINATI per id + (dishId, qty, nome) per
    // ogni riga. Stesso giorno/contenuto → stesso hash, a prescindere dall'ordine array.
    // Serve all'anti-doppio scarico (confronto con i record salesDischarges confermati).
    const stableHash = (str) => { let h = 5381; for (let i = 0; i < str.length; i++) h = ((h * 33) ^ str.charCodeAt(i)) >>> 0; return h.toString(36); };
    const hashParts = [String(fromDate), String(toDate)];
    [...inRange].sort((x, y) => String(x.id).localeCompare(String(y.id))).forEach(rr => {
      hashParts.push("R:" + rr.id);
      (rr.items || []).forEach(it => hashParts.push((it.dishId || "") + "#" + (Number(it.qty) || 0) + "#" + (it._csvName || it.name || "")));
    });
    const sourceHash = stableHash(hashParts.join("|"));

    // Indici O(1) per evitare scansioni ripetute (check performance iPad)
    const bpById = new Map((state.baseProducts || []).map(b => [b.id, b]));
    const lotById = new Map((state.lots || []).map(l => [l.id, l]));

    // 2-4. Lettura righe vendute + match ricetta + espansione ingredienti
    const dishRows = [];          // visibilità riga per riga
    const unmatchedItems = [];    // {receiptId, name, qty, revenue}
    const bpNullIngredients = []; // {dishId, dishName, ingredientName, qty, receiptId}
    const suspiciousRecipes = []; // {dishId, dishName, reason}
    const ignoredItems = [];      // {receiptId, name, key, qty, revenue} — articoli marcati "non è un piatto"
    const reqMap = new Map();     // bpId -> aggregato fabbisogno
    const suspiciousSeen = new Set();
    // Articoli esclusi dallo scarico da Andi (chiave normStr), riuso del meccanismo
    // esistente dishIgnoredArticles (stesso usato dal pannello qualità di Vendite).
    const ignoredSet = new Set(state.dishIgnoredArticles || []);

    let totalSoldRows = 0;
    let coveredRows = 0;
    let ignoredRows = 0;
    let hasBanco = false;
    let nonIntegerQtySeen = false;
    let menuConcordatoSeen = false;

    for (const r of inRange) {
      if ((Number(r.covers) || 0) === 0) hasBanco = true;
      for (const it of (r.items || [])) {
        const qty = Number(it.qty) || 0;
        if (isMenuConcordato(it)) menuConcordatoSeen = true;
        if (qty !== 0 && qty % 1 !== 0) nonIntegerQtySeen = true;
        // Righe di sconto/aggiustamento (qty <= 0) non sono piatti venduti né
        // consumo MP: non le contiamo. Mezze porzioni (0.5) e regali (qty>0) restano.
        if (qty <= 0) continue;
        totalSoldRows++;

        const dishId = it.dishId;
        const isUnmatched = !dishId || (typeof dishId === "string" && dishId.startsWith("_unmatched_"));
        if (isUnmatched) {
          const uname = it._csvName || it.name || String(dishId || "?");
          const ukey = normStr(uname);
          const urev = r2(qty * (Number(it.unitPrice) || 0));
          if (ignoredSet.has(ukey)) {
            // Marcato "non è un piatto" (bevande/coperti/vini…): escluso dallo scarico e
            // dalla Food Coverage, tracciato come escluso, NON genera warning grave.
            ignoredRows++;
            ignoredItems.push({ receiptId: r.id, name: uname, key: ukey, qty, revenue: urev });
            dishRows.push({ receiptId: r.id, dishId: dishId || null, dishName: uname, qty, scaricabile: false, ignored: true });
            continue;
          }
          unmatchedItems.push({ receiptId: r.id, name: uname, key: ukey, qty, revenue: urev });
          dishRows.push({ receiptId: r.id, dishId: dishId || null, dishName: uname, qty, scaricabile: false, unmatched: true });
          continue;
        }

        // Fonte ricetta: prima recipeSnapshot (storica), poi dishesReal corrente
        let ingredienti = null;
        let dishName = it.name || dishId;
        if (it.recipeSnapshot && Array.isArray(it.recipeSnapshot.ingredienti) && it.recipeSnapshot.ingredienti.length > 0) {
          ingredienti = it.recipeSnapshot.ingredienti;
        } else {
          const dish = (state.dishesReal || []).find(d => d.id === dishId);
          if (dish) {
            dishName = dish.nome || dishName;
            if (Array.isArray(dish.ingredienti) && dish.ingredienti.length > 0) ingredienti = dish.ingredienti;
          }
        }

        if (!ingredienti || ingredienti.length === 0) {
          if (!suspiciousSeen.has(dishId)) {
            suspiciousSeen.add(dishId);
            suspiciousRecipes.push({ dishId, dishName, reason: "Ricetta assente o senza ingredienti" });
          }
          dishRows.push({ receiptId: r.id, dishId, dishName, qty, scaricabile: false });
          continue;
        }

        // Espansione: qtyNeeded = ingrediente.qty × item.qty, aggregata per bpId
        let rowHasScaricabile = false;
        for (const ing of ingredienti) {
          const qtyNeeded = (Number(ing.qty) || 0) * qty;
          if (!ing.bpId) {
            bpNullIngredients.push({ dishId, dishName, ingredientName: ing.name || "?", qty: r4(qtyNeeded), receiptId: r.id });
            continue;
          }
          if (qtyNeeded <= 0) continue;
          rowHasScaricabile = true;
          let req = reqMap.get(ing.bpId);
          if (!req) {
            const bp = bpById.get(ing.bpId);
            req = {
              bpId: ing.bpId,
              productName: bp?.display || bp?.nome || ing.bpName || "?",
              totalQty: 0,
              unit: ing.unit || "kg",
              sourceRows: 0,
              dishBreakdown: new Map(),
              affectedReceipts: new Set(),
            };
            reqMap.set(ing.bpId, req);
          }
          req.totalQty += qtyNeeded;
          req.sourceRows++;
          req.dishBreakdown.set(dishId, (req.dishBreakdown.get(dishId) || 0) + qtyNeeded);
          req.affectedReceipts.add(r.id);
        }

        if (rowHasScaricabile) coveredRows++;
        dishRows.push({ receiptId: r.id, dishId, dishName, qty, scaricabile: rowHasScaricabile });
      }
    }

    // 5. Requirements aggregati per bpId
    const requirements = Array.from(reqMap.values()).map(req => ({
      bpId: req.bpId,
      productName: req.productName,
      totalQty: r4(req.totalQty),
      unit: req.unit,
      sourceRows: req.sourceRows,
      dishBreakdown: Array.from(req.dishBreakdown.entries()).map(([dId, q]) => ({ dishId: dId, qty: r4(q) })),
      affectedReceipts: Array.from(req.affectedReceipts),
    }));

    // 6 + 10. Allocazione FIFO/FEFO (riuso suggestFifoAllocation) + costo stimato
    const allocations = [];
    const shortfalls = [];
    let estimatedCost = 0;
    let missingCostSeen = false;
    for (const req of requirements) {
      const { allocation, shortfall } = engine.suggestFifoAllocation(state, req.bpId, req.totalQty);
      let reqCost = 0;
      const allocDetail = allocation.map(a => {
        const lot = lotById.get(a.lotId);
        let unit = lot ? Number(lot.unitCostReal) : NaN;
        if (!Number.isFinite(unit) || unit <= 0) unit = lot ? Number(lot.unitCost) : NaN;
        if (!Number.isFinite(unit) || unit <= 0) { unit = 0; missingCostSeen = true; }
        const lineCost = (Number(a.qty) || 0) * unit;
        reqCost += lineCost;
        return { lotId: a.lotId, qty: r4(a.qty), expiresAt: a.expiresAt, unitCost: r4(unit), lineCost: r4(lineCost) };
      });
      const allocatedQty = allocation.reduce((s, a) => s + (Number(a.qty) || 0), 0);
      estimatedCost += reqCost;
      allocations.push({
        bpId: req.bpId,
        productName: req.productName,
        totalQty: req.totalQty,
        unit: req.unit,
        allocation: allocDetail,
        allocatedQty: r4(allocatedQty),
        shortfall: r4(shortfall),
        estimatedCost: r4(reqCost),
      });
      if (shortfall > 0) {
        shortfalls.push({ bpId: req.bpId, productName: req.productName, needed: req.totalQty, shortfall: r4(shortfall), allocatedQty: r4(allocatedQty), unit: req.unit });
      }
    }
    estimatedCost = r2(estimatedCost);

    // 9. Coverage Totale (come oggi: coperte / TUTTE le righe vendute) e Food Coverage
    // (denominatore = solo righe food-candidate = matched + unmatched NON ignorati; gli
    // articoli marcati "non è un piatto" escono dal denominatore). `coverage` resta alias
    // di coverageTotal per retro-compatibilità con UI/commit esistenti.
    const coverage = totalSoldRows > 0 ? r4(coveredRows / totalSoldRows) : 0;
    const foodRows = Math.max(0, totalSoldRows - ignoredRows);
    const foodCoverage = foodRows > 0 ? r4(coveredRows / foodRows) : 0;
    // Aggregazioni per nome (pannello "Qualità dati venduto"), ordinate per righe poi fatturato.
    const aggBy = (list) => {
      const m = new Map();
      for (const u of list) {
        const k = u.key || normStr(u.name);
        let e = m.get(k); if (!e) { e = { name: u.name, key: k, righe: 0, qta: 0, fatturato: 0 }; m.set(k, e); }
        e.righe++; e.qta += Number(u.qty) || 0; e.fatturato += Number(u.revenue) || 0;
      }
      return [...m.values()].map(e => ({ ...e, qta: r4(e.qta), fatturato: r2(e.fatturato) })).sort((a, b) => b.righe - a.righe || b.fatturato - a.fatturato);
    };
    const unmatchedAgg = aggBy(unmatchedItems);
    const ignoredAgg = aggBy(ignoredItems);

    // 7. Warnings (non bloccanti)
    if (unmatchedItems.length > 0) warnings.push({ code: "UNMATCHED", count: unmatchedItems.length, message: `${unmatchedItems.length} righe non collegate a una ricetta: non verranno scaricate` });
    if (bpNullIngredients.length > 0) warnings.push({ code: "BP_NULL", count: bpNullIngredients.length, message: `${bpNullIngredients.length} ingredienti senza prodotto base (bpId mancante): ignorati nello scarico` });
    if (suspiciousRecipes.length > 0) warnings.push({ code: "SUSPICIOUS_RECIPE", count: suspiciousRecipes.length, message: `${suspiciousRecipes.length} piatti venduti senza ricetta/ingredienti` });
    if (shortfalls.length > 0) warnings.push({ code: "SHORTFALL", count: shortfalls.length, message: `${shortfalls.length} prodotti con scorta insufficiente` });
    // Food coverage = metrica operativa (esclude i non-food ignorati). Il warning si basa su
    // questa, non sulla coverage totale, così le bevande/coperti ignorati non lo fanno scattare.
    if (foodRows > 0 && foodCoverage < 0.95) warnings.push({ code: "LOW_FOOD_COVERAGE", message: `Food coverage ${(foodCoverage * 100).toFixed(1)}% (sotto il 95%): piatti veri non ancora scaricabili` });
    if (ignoredItems.length > 0) warnings.push({ code: "IGNORED", count: ignoredItems.length, message: `${ignoredAgg.length} articoli non-food esclusi dallo scarico (${ignoredItems.length} righe): scelta esplicita, non un problema` });
    if (nonIntegerQtySeen) warnings.push({ code: "NON_INTEGER_QTY", message: "Presenti quantità non intere (mezze porzioni / menu): verificare prima di confermare" });
    if (menuConcordatoSeen) warnings.push({ code: "MENU_CONCORDATO", message: "Rilevate righe MENU CONCORDATO: verificare manualmente l'attribuzione" });
    if (hasBanco) warnings.push({ code: "BANCO", message: "Presenti scontrini al banco (covers=0): i piatti al banco sono inclusi nello scarico" });
    if (missingCostSeen) warnings.push({ code: "MISSING_COST", message: "Alcuni lotti senza costo unitario: costo stimato parziale" });
    // Anti-doppio scarico (SOLO controllo, NESSUNA scrittura): confronta la proposta con
    // i record salesDischarges già CONFERMATI. Sovrapposizione di receiptIds o stesso
    // sourceHash ⇒ blocker (canCommit = false): confermare raddoppierebbe il magazzino.
    if (Array.isArray(state.salesDischarges)) {
      const confirmed = state.salesDischarges.filter(d => d && d.status === "confirmed");
      const idSet = new Set(receiptIds);
      const overlapping = confirmed.filter(d => Array.isArray(d.receiptIds) && d.receiptIds.some(rid => idSet.has(rid)));
      if (overlapping.length > 0) {
        blockers.push({ code: "ALREADY_DISCHARGED", count: overlapping.length, message: `Alcuni scontrini risultano GIÀ scaricati (${overlapping.map(d => d.id).join(", ")}): confermare raddoppierebbe il magazzino`, dischargeIds: overlapping.map(d => d.id) });
      }
      const sameHash = confirmed.filter(d => d.sourceHash && d.sourceHash === sourceHash);
      if (sameHash.length > 0 && overlapping.length === 0) {
        blockers.push({ code: "DUPLICATE_HASH", count: sameHash.length, message: `Una proposta con lo stesso contenuto è già stata scaricata (${sameHash.map(d => d.id).join(", ")})`, dischargeIds: sameHash.map(d => d.id) });
      }
    } else {
      // Stato legacy senza il registro (non dovrebbe accadere dopo la migrazione additiva)
      warnings.push({ code: "NO_DEDUP", message: "Anti-doppio scarico non disponibile (registro salesDischarges assente in questo stato legacy)" });
    }

    // 8. Blockers aggiuntivi: shortfall CRITICO = prodotto totalmente non disponibile
    // (allocatedQty<=0 con fabbisogno>0). Lo shortfall parziale resta solo warning.
    // NB: la guardia Anisakis NON è applicata qui (vive in ScaricoForm, non come
    // helper engine puro: verrà riusata nello step di commit senza spostarla).
    const criticalShort = shortfalls.filter(s => (Number(s.allocatedQty) || 0) <= 0 && (Number(s.needed) || 0) > 0);
    if (criticalShort.length > 0) {
      blockers.push({ code: "CRITICAL_SHORTFALL", count: criticalShort.length, message: `${criticalShort.length} prodotti totalmente non disponibili a magazzino`, bpIds: criticalShort.map(s => s.bpId) });
    }

    // 11. canCommit (il commit NON è implementato in questo step)
    const canCommit = blockers.length === 0;

    return {
      id: uid("sdprop_"),
      source: "sales_import",
      generatedAt: nowIso(),
      sourceHash,
      fromDate,
      toDate,
      receiptIds,
      receiptCount,
      covers,
      grossRevenue,
      itemCount: totalSoldRows,
      dishRows,
      requirements,
      allocations,
      shortfalls,
      unmatchedItems,
      unmatchedAgg,
      ignoredItems,
      ignoredAgg,
      bpNullIngredients,
      suspiciousRecipes,
      warnings,
      blockers,
      coverage,            // = coverageTotal (retro-compat)
      coverageTotal: coverage,
      foodCoverage,
      foodRows,
      ignoredRows,
      estimatedCost,
      canCommit,
    };
  },

  // ── COMMIT SCARICO DA VENDUTO (STEP 4 — SCRITTURE REALI, ATOMICO) ───────────
  // Esegue lo scarico magazzino da una proposta confermabile. NON chiama setState
  // (ritorna {state, ...}): è la UI a persistere. ATOMICO: accumula nextState e committe
  // SOLO se TUTTI i movimenti riescono; al primo errore aborta restituendo lo STATE
  // ORIGINALE (zero scritture). Riusa engine.applyMovement, engine.suggestFifoAllocation
  // e la STESSA guardia Anisakis di ScaricoForm (logica identica). Crea un kitchenOutput
  // per (giorno-servizio, turno, piatto) con dishId reale + un record salesDischarges
  // "confirmed" + una voce di audit. Reg. CE 178/2002 art. 18 / 853/2004 (abbattimento).
  commitScaricoProposalFromSales: (state, proposal, confirmedBy) => {
    const fail = (code, message) => ({ state, error: code, message });

    // 1. Pre-check obbligatori (nessuna scrittura se falliscono)
    if (!proposal) return fail("NO_PROPOSAL", "Nessuna proposta da confermare.");
    if (proposal.canCommit !== true || (proposal.blockers || []).length > 0) return fail("NOT_COMMITTABLE", "La proposta non è confermabile (blocchi presenti).");
    if ((proposal.allocations || []).length === 0) return fail("NOTHING_TO_DISCHARGE", "Nessun ingrediente da scaricare.");
    if ((proposal.shortfalls || []).length > 0) return fail("HAS_SHORTFALL", "Scorte insufficienti per uno o più prodotti: ricevere merce prima di scaricare.");

    // Anti-doppio RICONTROLLATO sullo stato CORRENTE (la proposta può essere vecchia)
    const confirmed = (state.salesDischarges || []).filter(d => d && d.status === "confirmed");
    const idSet = new Set(proposal.receiptIds || []);
    if (confirmed.some(d => Array.isArray(d.receiptIds) && d.receiptIds.some(r => idSet.has(r)))) return fail("ALREADY_DISCHARGED", "Alcuni scontrini risultano già scaricati: operazione annullata.");
    if (confirmed.some(d => d.sourceHash && d.sourceHash === proposal.sourceHash)) return fail("DUPLICATE_HASH", "Una proposta con lo stesso contenuto è già stata scaricata.");

    // 2. Ricostruisci i gruppi (giorno-servizio, turno, piatto) dagli scontrini in ambito,
    //    sullo stato CORRENTE: l'allocazione FIFO viene rifatta qui (più corretta della
    //    preview, che poteva essere generata prima di altri scarichi/ingressi).
    const itemById = new Map(engine.getItems(state).map(it => [it.id, it]));
    const groups = new Map(); // key: serviceDate|shift|dishId
    for (const r of (state.tableReceipts || [])) {
      if (!idSet.has(r.id)) continue;
      const serviceDate = getServiceDate(r.closedAt) || r.date;
      const shift = getShift(r.closedAt) || "fuori_turno";
      for (const it of (r.items || [])) {
        const qty = Number(it.qty) || 0;
        if (qty <= 0) continue;
        const dishId = it.dishId;
        if (!dishId || (typeof dishId === "string" && dishId.startsWith("_unmatched_"))) continue;
        const dishRef = (state.dishesReal || []).find(d => d.id === dishId);
        let ingredienti = null, dishName = it.name || dishId, dishCat = "", reqAbb = false;
        if (it.recipeSnapshot && Array.isArray(it.recipeSnapshot.ingredienti) && it.recipeSnapshot.ingredienti.length > 0) {
          ingredienti = it.recipeSnapshot.ingredienti;
          dishCat = it.recipeSnapshot.cat || dishRef?.cat || "";
        } else if (dishRef && Array.isArray(dishRef.ingredienti) && dishRef.ingredienti.length > 0) {
          ingredienti = dishRef.ingredienti;
          dishCat = dishRef.cat || "";
        }
        if (dishRef) { dishName = dishRef.nome || dishName; if (dishRef.requiresAbbattimento === true) reqAbb = true; }
        if (!ingredienti || ingredienti.length === 0) continue;
        const key = serviceDate + "|" + shift + "|" + dishId;
        let g = groups.get(key);
        if (!g) { g = { serviceDate, shift, dishId, dishName, dishCat, reqAbb, servings: 0, ingredienti }; groups.set(key, g); }
        g.servings += qty;
      }
    }
    const groupList = Array.from(groups.values()).sort((a, b) => (a.serviceDate + a.shift + a.dishId).localeCompare(b.serviceDate + b.shift + b.dishId));
    if (groupList.length === 0) return fail("NOTHING_TO_DISCHARGE", "Nessun piatto scaricabile negli scontrini selezionati.");

    // 3-4. Applica i movimenti ATOMICAMENTE, costruendo i kitchenOutputs per piatto
    const rawFishPatterns = /\b(crud|marinat|sashimi|tartar|carpaccio|ceviche|affumicat)/i;
    const dischargeId = uid("sd_");
    const note = `Scarico da venduto ${proposal.fromDate} → ${proposal.toDate}`;
    let nextState = { ...state };
    const movementsCreated = [];
    const newKos = [];

    for (const g of groupList) {
      // Allocazione FIFO per ingrediente sullo stato corrente (mutato dai gruppi precedenti)
      const dishAllocs = (g.ingredienti || []).filter(ing => ing.bpId).map(ing => {
        const needed = (Number(ing.qty) || 0) * g.servings;
        const { allocation, shortfall } = engine.suggestFifoAllocation(nextState, ing.bpId, needed);
        return { bpId: ing.bpId, item: itemById.get(ing.bpId), needed, allocation, shortfall };
      });
      // Shortfall sopravvenuto (stato cambiato dopo la preview): abort pulito, zero scritture
      const short = dishAllocs.find(a => a.shortfall > 0);
      if (short) return fail("INSUFFICIENT_STOCK", `Scorte insufficienti per ${short.item?.name || short.bpId} (${g.dishName}): nessun movimento applicato.`);

      // Guardia Anisakis — logica IDENTICA a ScaricoForm, valutata su nextState
      const requiresAbbattimento = g.reqAbb || g.dishCat === "antipasti_crudi" || rawFishPatterns.test(g.dishName || "");
      if (requiresAbbattimento) {
        const offendingLots = [];
        for (const a of dishAllocs.filter(x => x.item?.category === "pesce")) {
          for (const al of a.allocation) {
            const lot = nextState.lots.find(l => l.id === al.lotId);
            if (!lot) continue;
            const isAbbattuto = lot.status === "abbattuto" || !!lot.coreTempProtocolUsed;
            if (!isAbbattuto) offendingLots.push({ lotId: al.lotId, itemName: a.item?.name });
          }
        }
        if (offendingLots.length > 0) {
          const lotsStr = offendingLots.slice(0, 3).map(o => `${o.lotId} (${o.itemName})`).join(", ");
          return fail("ABBATTIMENTO", `"${g.dishName}" richiede abbattimento Anisakis. Lotti non conformi: ${lotsStr}. Nessun movimento applicato. D.M. 17/07/2013`);
        }
      }

      // Applica i movimenti del piatto (al primo errore: abort, zero scritture)
      const consumedLots = [];
      for (const a of dishAllocs) {
        for (const al of a.allocation) {
          const res = engine.applyMovement(nextState, { lotId: al.lotId, qty: -al.qty, type: "scarico", ref: { type: "salesDischarge", id: dischargeId }, by: confirmedBy, note });
          if (res.error) return fail(res.error, `Scarico annullato su lotto ${al.lotId}: ${res.error}. Nessun movimento applicato.`);
          movementsCreated.push(res.lotMovements[res.lotMovements.length - 1].id);
          consumedLots.push({ lotId: al.lotId, qty: al.qty });
          nextState = res;
        }
      }
      // Documento giustificativo (shape identica a ScaricoForm + tag origine venduto)
      newKos.push({ id: uid("ko_"), date: g.serviceDate, dishId: g.dishId, servings: g.servings, operator: confirmedBy, shift: g.shift, consumedLots, source: "sales_import", dischargeId });
    }

    // 5 + 4. Commit unico: kitchenOutputs + record salesDischarges "confirmed"
    const dischargeRecord = {
      id: dischargeId,
      source: "sales_import",
      fromDate: proposal.fromDate,
      toDate: proposal.toDate,
      receiptIds: proposal.receiptIds || [],
      sourceHash: proposal.sourceHash,
      generatedAt: proposal.generatedAt,
      confirmedBy: confirmedBy || null,
      confirmedAt: nowIso(),
      status: "confirmed",
      coverage: proposal.coverage,
      estimatedCost: proposal.estimatedCost,
      movementsCreated,
      kitchenOutputIds: newKos.map(k => k.id),
      warnings: (proposal.warnings || []).map(w => w.code),
    };
    nextState.kitchenOutputs = [...(nextState.kitchenOutputs || []), ...newKos];
    nextState.salesDischarges = [...(nextState.salesDischarges || []), dischargeRecord];

    // 6. Audit log
    nextState = {
      ...nextState,
      auditLog: appendAudit(nextState, {
        action: "sales_discharge_confirmed",
        entityType: "salesDischarge",
        entityId: dischargeId,
        reason: `Scarico da venduto ${proposal.fromDate}→${proposal.toDate}: ${movementsCreated.length} movimenti, ${newKos.length} documenti, copertura ${(Number(proposal.coverage) * 100).toFixed(1)}%, ${fmt.eur(proposal.estimatedCost)}`,
        payload: {
          fromDate: proposal.fromDate, toDate: proposal.toDate,
          receiptCount: (proposal.receiptIds || []).length,
          coverage: proposal.coverage, estimatedCost: proposal.estimatedCost,
          movementsCreated: movementsCreated.length, kitchenOutputs: newKos.length,
          warnings: dischargeRecord.warnings, confirmedBy: confirmedBy || null,
        },
      }),
    };

    return { state: nextState, dischargeId, movementCount: movementsCreated.length, koCount: newKos.length };
  },

  // ── APPLY LOT MOVEMENT ─────────────────────────────────────────────────────
  // Reg. CE 178/2002 art. 18: ogni movimento deve essere tracciabile e veridico.
  // Se richiediamo piu' di quanto disponibile NON applichiamo il movimento e
  // restituiamo un errore esplicito: il chiamante deve gestirlo (toast + audit).
  // Il clamp silenzioso a 0 e' stato rimosso perche' nascondeva ammanchi reali.
  applyMovement: (state, { lotId, qty, type, ref, by, note }) => {
    const lot = state.lots.find(l => l.id === lotId);
    if (!lot) return { state, error: "LOT_NOT_FOUND" };
    const newRemaining = lot.qtyRemaining + qty;
    if (newRemaining < -0.001) {
      // Stock insufficiente: movimento NON applicato. Il chiamante registra NC/audit.
      return {
        state,
        error: "INSUFFICIENT_STOCK",
        shortfall: Math.abs(newRemaining),
        lot,
      };
    }
    const clampedRemaining = Math.max(0, newRemaining); // safety contro float drift
    const newStatus = clampedRemaining <= 0.001 ? "esaurito" : (lot.status === "disponibile" && qty < 0 ? "in_uso" : lot.status);
    const movement = { id: uid("mv_"), lotId, qty, type, at: nowIso(), ref, by, note };
    return {
      ...state,
      lots: state.lots.map(l => l.id === lotId ? { ...l, qtyRemaining: clampedRemaining, status: newStatus } : l),
      lotMovements: [...state.lotMovements, movement],
    };
  },

  // ── CREATE CHILD LOT ───────────────────────────────────────────────────────
  // Reg. CE 178/2002 art. 18 + Reg. UE 1169/2011 art. 26:
  // il lotto figlio EREDITA dal padre supplierLotCode, origin (zona FAO, metodo,
  // nome scientifico), expiryType (TMC vs scadenza) e lo status "abbattuto".
  // La scadenza base parte da parent.createdAt (o parent.expiresAt se piu'
  // restrittiva): il pesce non ringiovanisce per essere stato porzionato.
  //
  // FIX L1 (Bug 1 + Bug 2): il padre veniva lasciato a qtyRemaining invariato
  // → inventario falsato (padre + figlio entrambi pieni). Ora:
  //  - consumedFromParent (kg prelevati dal padre) e' parametro esplicito;
  //  - se non passato, fallback grazioso a qtyOut (caso "senza calo" o legacy);
  //  - validazione: consumed <= qtyRemaining + tolleranza (no overdraft);
  //  - CMP corretto: parent.unitCost × consumed (+ laborCost) / qtyOut;
  //  - scarico esplicito del padre con movement di tipo "lavorazione";
  //  - traccia consumedFromParent e yieldRatio sul figlio (analytics resa).
  createChildLot: (state, opts) => {
    const {
      parentLotId,
      itemId,
      qtyOut,
      workOrderId,
      extraShelfLifeDays,
      notes,
      unit,
      consumedFromParent,   // NEW: kg prelevati dal padre (esplicito)
      laborCost = 0,        // NEW: costo manodopera totale (€)
      operatorName = null,  // NEW: operatore che ha eseguito
      coreTempProtocolUsed = null,  // Fix L3: protocollo Anisakis applicato (es. "-20°C × 24h")
    } = opts || {};
    const parent = state.lots.find(l => l.id === parentLotId);
    if (!parent) return { state, newLot: null, error: "PARENT_NOT_FOUND" };

    const qtyOutNum = Number(qtyOut) || 0;
    if (!(qtyOutNum > 0)) return { state, newLot: null, error: "INVALID_QTY_OUT" };

    // Resolve consumed: se passato esplicito → usa quello (rispetta calo peso reale).
    // Altrimenti fallback grazioso a qtyOut (caso pre-fix / lavorazione senza calo).
    const consumed = Number.isFinite(Number(consumedFromParent)) && Number(consumedFromParent) > 0
      ? Number(consumedFromParent)
      : qtyOutNum;

    const parentQtyRemaining = Number(parent.qtyRemaining) || 0;
    // Validazione: consumed non puo' superare giacenza padre (tolleranza 1g).
    if (consumed > parentQtyRemaining + 0.001) {
      return {
        state,
        newLot: null,
        error: "INSUFFICIENT_PARENT_STOCK",
        available: parentQtyRemaining,
        requested: consumed,
      };
    }

    const suffix = String.fromCharCode(70 + state.lots.filter(l => l.parent === parentLotId).length);
    const newLotId = `${parent.id.split("-").slice(0, 3).join("-")}-${suffix}`;
    const item = engine.getItems(state).find(i => i.id === itemId);
    const shelfLife = extraShelfLifeDays != null ? extraShelfLifeDays : (item?.shelfLifeDays || 7);
    // Calcolo expiresAt: base = createdAt del padre (origine reale del prodotto).
    // Se il padre era gia' "abbattuto" la shelfLife estesa parte comunque dal
    // confezionamento del figlio (rework migliorativo). Altrimenti capping con
    // parent.expiresAt per evitare di "regalare" giorni a un prodotto deperibile.
    const parentBase = parent.createdAt ? new Date(parent.createdAt) : new Date();
    const childExpiry = new Date(parentBase.getTime() + shelfLife * 86400000);
    let expiresAt;
    if (parent.status === "abbattuto") {
      // Reg. UE 1169/2011 art. 24 + Reg. CE 178/2002 art. 18:
      // FIX P0-03 (audit 2026-05-19): la shelfLife estesa post-abbattimento parte
      // dalla DATA REALE di abbattimento del padre, non dalla data odierna.
      // Il pesce non ringiovanisce per essere stato porzionato: se il padre era
      // stato abbattuto 40 giorni fa, il figlio ha gia' speso 40 giorni di shelfLife.
      // Fallback ordinato: abbattutoAt (timestamp esplicito) > createdAt > receivedAt > now.
      // Mantenere fallback a Date.now() SOLO se tutti i timestamp padre sono null
      // (situazione anomala, ma evita NaN; il fallback resta append-safe).
      const baseForAbbattuto = parent.abbattutoAt
        ? new Date(parent.abbattutoAt)
        : (parent.createdAt
            ? new Date(parent.createdAt)
            : (parent.receivedAt ? new Date(parent.receivedAt) : new Date()));
      expiresAt = new Date(baseForAbbattuto.getTime() + shelfLife * 86400000).toISOString().slice(0, 10);
    } else if (parent.expiresAt) {
      // Non abbattuto: scadenza figlio MIN(childExpiry, parent.expiresAt).
      const parentExpiry = new Date(parent.expiresAt);
      expiresAt = (childExpiry < parentExpiry ? childExpiry : parentExpiry).toISOString().slice(0, 10);
    } else {
      expiresAt = childExpiry.toISOString().slice(0, 10);
    }
    // Status: se il padre era abbattuto, il figlio resta abbattuto (propagato);
    // se la lavorazione corrente ha protocollo Anisakis applicato (Fix L3), il figlio
    // diventa abbattuto; altrimenti se la shelfLife > 60g implica abbattimento, marca abbattuto.
    const childStatus = (parent.status === "abbattuto" || !!coreTempProtocolUsed)
      ? "abbattuto"
      : (shelfLife > 60 ? "abbattuto" : "disponibile");
    // Fix L1 (Bug 2): CMP figlio = (parent.unitCost × consumed + laborCost) / qtyOut
    // Es: polpo padre 10 kg @ €9.99/kg, consumed=10 kg, qtyOut=3.4 kg
    //     materialCost = 9.99 × 10 = €99.90
    //     childUnitCost = 99.90 / 3.4 = €29.38/kg (corretto, contro €9.99 del bug)
    const parentUnitCost = (Number(parent.unitCost) > 0 ? Number(parent.unitCost) : Number(parent.unitCostReal)) || 0;
    const laborCostNum = Number(laborCost) || 0;
    const materialCost = parentUnitCost * consumed;
    const totalCost = materialCost + laborCostNum;
    const childUnitCost = qtyOutNum > 0.0001 ? totalCost / qtyOutNum : 0;
    // Resa effettiva (kg uscita / kg entrata). Range fisico [0, 1].
    const yieldRatio = consumed > 0.0001 ? qtyOutNum / consumed : 0;
    const newLot = {
      id: newLotId, itemId, receiptId: null,
      qtyInitial: qtyOutNum, qtyRemaining: qtyOutNum,
      unit: unit || parent.unit,
      createdAt: nowIso(),
      expiresAt,
      status: childStatus,
      parent: parentLotId,
      notes: notes || null,
      workOrderId,
      // Eredita catena tracciabilita' fornitore (Reg. CE 178/2002 art. 18)
      supplierLotCode: parent.supplierLotCode || null,
      origin: parent.origin || null,
      expiryType: parent.expiryType || defaultExpiryTypeForCat(item?.category),
      // Radice della catena: punta sempre al primo receipt di filiera.
      rootReceiptId: parent.rootReceiptId || parent.receiptId || null,
      // CMP ereditato (Fix L1): per i figli unitCost coincide gia' con
      // unitCostReal perche' la lavorazione e' avvenuta — il calo e' stato realizzato.
      unitCost: childUnitCost,
      unitCostReal: childUnitCost,
      materialCostTotal: materialCost,    // NEW: tracking del costo MP
      laborCostTotal: laborCostNum,       // NEW: tracking manodopera
      totalCost,                          // NEW: totale per FC reale
      consumedFromParent: consumed,       // NEW: traccia esplicita kg in
      yieldRatio,                         // NEW: resa effettiva (0..1)
      caloApplied: parent.caloApplied != null ? parent.caloApplied : null,
      // Fix L3 — D.M. 17/07/2013: protocollo abbattimento applicato propagato al figlio.
      // Se il padre era gia' abbattuto e non c'e' nuovo protocollo, eredita quello del padre.
      coreTempProtocolUsed: coreTempProtocolUsed || parent.coreTempProtocolUsed || null,
    };

    // Scarico esplicito del padre: qtyRemaining -= consumed, status → "esaurito" se ≈0.
    const newParentRemaining = Math.max(0, parentQtyRemaining - consumed);
    const newParentStatus = newParentRemaining <= 0.001 ? "esaurito" : parent.status;
    const updatedParent = {
      ...parent,
      qtyRemaining: newParentRemaining,
      status: newParentStatus,
    };

    const by = operatorName || state.currentUser?.name || "Sistema";
    // Movimento di SCARICO sul padre (lavorazione) + movimento di INGRESSO sul figlio.
    const parentMovement = {
      id: uid("mv_"),
      lotId: parent.id,
      qty: -consumed,
      type: "lavorazione",
      subtype: "scarico_per_workOrder",
      at: nowIso(),
      ref: { type: "workOrder", id: workOrderId || null },
      by,
      note: `Trasformato in ${newLotId} (resa ${(yieldRatio * 100).toFixed(1)}%)`,
    };
    const childMovement = {
      id: uid("mv_"),
      lotId: newLotId,
      qty: qtyOutNum,
      type: "ingresso",
      subtype: "creazione_da_workOrder",
      at: nowIso(),
      ref: { type: "workOrder", id: workOrderId || null },
      by,
      note: `Generato da ${parent.id} (consumati ${consumed.toFixed(3)} ${parent.unit || "kg"})`,
    };

    return {
      state: {
        ...state,
        lots: state.lots.map(l => l.id === parent.id ? updatedParent : l).concat(newLot),
        lotMovements: [...state.lotMovements, parentMovement, childMovement],
      },
      newLot,
      error: null,
    };
  },

  // ── TRACE FORWARD ──────────────────────────────────────────────────────────
  traceForward: (state, lotId, visited = new Set()) => {
    if (visited.has(lotId)) return { children: [], kitchenUse: [], deeper: [] };
    visited.add(lotId);
    const children = state.lots.filter(l => l.parent === lotId);
    const kitchenUse = state.kitchenOutputs.filter(o => (o.consumedLots || []).some(c => c.lotId === lotId));
    return { children, kitchenUse };
  },

  // ── TRACE BACKWARD: da piatto al pescato ───────────────────────────────────
  traceBackward: (state, kitchenOutputId) => {
    const ko = state.kitchenOutputs.find(o => o.id === kitchenOutputId);
    if (!ko) return null;
    const lots = (ko.consumedLots || []).map(cl => {
      const lot = state.lots.find(l => l.id === cl.lotId);
      if (!lot) return null;
      let current = lot;
      const chain = [lot];
      while (current?.parent) {
        const p = state.lots.find(l => l.id === current.parent);
        if (!p) break;
        chain.unshift(p);
        current = p;
      }
      const rootReceipt = chain[0].receiptId ? state.receipts.find(r => r.id === chain[0].receiptId) : null;
      const rootSupplier = rootReceipt ? state.suppliers.find(s => s.id === rootReceipt.supplierId) : null;
      return { consumedQty: cl.qty, chain, receipt: rootReceipt, supplier: rootSupplier };
    }).filter(Boolean);
    // FIX BLOCKER 2: il piatto e' in dishesReal (state.dishes e' vuoto). La
    // tracciabilita' (Reg. CE 178/2002 art. 18) deve mostrare a quale piatto e'
    // finito il lotto: senza questo fix il nome piatto risultava vuoto nel trace.
    return { ko, dish: (state.dishesReal || []).find(d => d.id === ko.dishId), lots };
  },

  // ── NOTIFICATIONS ──────────────────────────────────────────────────────────
  computeNotifications: (state) => {
    const notifs = [];
    const tempByLoc = {};
    const hl = state.haccpLogs || {};
    const tempLogs = Array.isArray(hl.temperature) ? hl.temperature : [];
    tempLogs.forEach(t => {
      if (!t || !t.location) return;
      const key = t.location;
      if (!tempByLoc[key] || new Date(`${t.date}T${t.time}`) > new Date(`${tempByLoc[key].date}T${tempByLoc[key].time}`))
        tempByLoc[key] = t;
    });
    Object.values(tempByLoc).forEach(t => {
      if (!t.target) return;
      const [minS, maxS] = t.target.split("/");
      const min = parseFloat((minS || "").replace("+", "")), max = parseFloat((maxS || "").replace("+", ""));
      if (t.temp < min || t.temp > max) {
        notifs.push({ id: `nt_tmp_${t.id}`, severity: "critical", type: "haccp",
          title: `Temperatura fuori range: ${t.location}`,
          body: `${t.temp}°C (range ${t.target}°C) rilevata alle ${t.time}`,
          action: { view: "haccp" }, createdAt: t.date + "T" + t.time });
      }
    });
    state.lots.filter(l => l.status !== "esaurito").forEach(l => {
      const days = daysBetween(TODAY, l.expiresAt);
      if (days <= 2) {
        const item = engine.getItems(state).find(i => i.id === l.itemId);
        notifs.push({ id: `nt_exp_${l.id}`, severity: days <= 0 ? "critical" : "warning", type: "expiry",
          title: days <= 0 ? `Lotto SCADUTO: ${item?.name}` : `Scadenza ravvicinata: ${item?.name}`,
          body: `${l.id} · ${fmt.qty(l.qtyRemaining, l.unit)} · ${days <= 0 ? `scaduto da ${-days}g` : `${days}g alla scadenza`}`,
          action: { view: "lot", lotId: l.id }, createdAt: nowIso() });
      }
    });
    // Reg. CE 852/2004 All. II Cap. IX: la merce critica in attesa di accettazione
    // non puo' restare ferma a tempo indeterminato. Alert se passa di 24h.
    state.lots.filter(l => l.status === "in_accettazione" || l.status === "quarantena").forEach(l => {
      const ageMs = l.createdAt ? (Date.now() - new Date(l.createdAt).getTime()) : 0;
      const ageH = ageMs / 3600000;
      if (ageH > 24) {
        const item = engine.getItems(state).find(i => i.id === l.itemId);
        notifs.push({
          id: `nt_acc_${l.id}`,
          severity: ageH > 48 ? "critical" : "warning",
          type: "haccp",
          title: l.status === "quarantena" ? `Lotto in QUARANTENA da ${Math.floor(ageH)}h: ${item?.name}` : `Accettazione pendente da ${Math.floor(ageH)}h: ${item?.name}`,
          body: `${l.id} · ${fmt.qty(l.qtyRemaining, l.unit)} · va confermato controllo etichetta + temperatura cella`,
          action: { view: "lot", lotId: l.id }, createdAt: l.createdAt || nowIso(),
        });
      }
    });
    // NC ricezione aperte da > 48h: ASL/NAS pretende azione correttiva tempestiva.
    (state.haccpLogs?.nonConformities || []).filter(nc => nc.status === "aperta" && (nc.type === "Ricezione" || nc.type === "ricezione" || nc.kind === "ricezione")).forEach(nc => {
      const ageMs = nc.createdAt ? (Date.now() - new Date(nc.createdAt).getTime()) : (nc.date ? (Date.now() - new Date(nc.date).getTime()) : 0);
      const ageH = ageMs / 3600000;
      if (ageH > 48) {
        notifs.push({
          id: `nt_ncrec_${nc.id}`,
          severity: "critical",
          type: "haccp",
          title: `NC ricezione aperta da ${Math.floor(ageH / 24)}g`,
          body: (nc.description || "Non conformita' merce in ingresso") + " — chiudere con azione correttiva",
          action: { view: "haccp" }, createdAt: nc.createdAt || nc.date,
        });
      }
    });
    // Sotto scorta: solo articoli con storico (non tutti i 126 a zero)
    const itemsWithHistory = new Set([
      ...state.lots.map(l => l.itemId),
      ...state.purchaseOrders.flatMap(po => po.lines.map(ln => ln.itemId)),
      ...state.receipts.flatMap(r => r.lines.map(ln => ln.itemId)),
    ]);
    engine.getItems(state).forEach(it => {
      if (!itemsWithHistory.has(it.id)) return;
      const stock = engine.itemStock(state, it.id);
      if (stock < it.minStock) {
        notifs.push({ id: `nt_low_${it.id}`, severity: "warning", type: "lowstock",
          title: `Sotto scorta: ${it.name}`,
          body: `Disponibili ${stock.toFixed(1)} ${it.unit} · minimo ${it.minStock} ${it.unit}`,
          action: { view: "acquisti", itemId: it.id }, createdAt: nowIso() });
      }
    });
    // Soft-deleted invoices (deletedAt) escluse dalle notifiche
    state.invoices.filter(i => i.status === "da_pagare" && !i.deletedAt).forEach(inv => {
      const days = daysBetween(TODAY, inv.dueDate);
      if (days <= 7) {
        const sup = state.suppliers.find(s => s.id === inv.supplierId);
        notifs.push({ id: `nt_inv_${inv.id}`, severity: days < 0 ? "critical" : "info", type: "invoice",
          title: days < 0 ? `Fattura scaduta: ${sup?.name}` : `Fattura in scadenza`,
          body: `${inv.number} · ${fmt.eur(inv.amount)} · ${days < 0 ? `scaduta da ${-days}g` : `${days}g alla scadenza`}`,
          action: { view: "fatture" }, createdAt: nowIso() });
      }
    });
    state.kitchenRequests.filter(kr => kr.status === "in_attesa").forEach(kr => {
      notifs.push({ id: `nt_kr_${kr.id}`, severity: kr.urgency === "urgente" ? "critical" : "info", type: "kitchen",
        title: `Richiesta cucina ${kr.urgency === "urgente" ? "URGENTE" : "in attesa"}`,
        body: `${kr.items.length} articoli richiesti da ${kr.requestedBy}`,
        action: { view: "richieste" }, createdAt: kr.createdAt });
    });
    // Soft-deleted purchaseOrders esclusi dalle notifiche
    state.purchaseOrders.filter(po => po.status === "ricevuto" && !po.receiptId && !po.deletedAt).forEach(po => {
      const sup = state.suppliers.find(s => s.id === po.supplierId);
      notifs.push({ id: `nt_po_${po.id}`, severity: "info", type: "po",
        title: `Ordine ricevuto da registrare`,
        body: `${po.id} · ${sup?.name} · registra la bolla d'ingresso`,
        action: { view: "ingresso", fromPO: po.id }, createdAt: po.expectedAt });
    });

    // Fix L3 — D.M. 17/07/2013: NC critiche CCP Anisakis/Cottura ancora aperte → alert imperdibile.
    // Una NC aperta su CCP = rischio penale art. 590 c.p. fino a che non viene chiusa.
    (state.haccpLogs?.nonConformities || []).filter(nc => nc.status === "aperta" && (nc.kind === "ccp_anisakis_violation" || nc.kind === "ccp_cottura_violation")).forEach(nc => {
      notifs.push({
        id: `nt_ccp_${nc.id}`,
        severity: "critical",
        type: "haccp",
        title: nc.kind === "ccp_anisakis_violation" ? "CCP Anisakis NON conforme" : "CCP Cottura NON conforme",
        body: (nc.description || "Violazione CCP da risolvere immediatamente") + " — chiudere con azione correttiva",
        action: { view: "haccp" },
        createdAt: nc.createdAt || nc.date,
      });
    });

    // Fix L2 — Reg. CE 852/2004 All. II Cap. XII: formazione obbligatoria personale.
    // Alert 60gg prima della scadenza attestato HACCP + critico se gia' scaduto.
    // Sanzione D.Lgs 193/2007 art. 6: €1.000–€6.000 per personale non formato.
    for (const u of (state.users || [])) {
      if (!u || u.active === false || !u.haccpCertExpiresAt) continue;
      const daysToExpiry = daysBetween(TODAY, u.haccpCertExpiresAt);
      if (daysToExpiry < 0) {
        notifs.push({
          id: `nt_haccpcert_expired_${u.id}`,
          severity: "critical",
          type: "haccp_cert_expired",
          title: `Attestato HACCP scaduto: ${u.name}`,
          body: `Scaduto da ${Math.abs(daysToExpiry)} giorni. Reg. CE 852/2004 viola obbligo formazione personale.`,
          action: { view: "anagrafiche", tab: "users" },
          createdAt: nowIso(),
        });
      } else if (daysToExpiry <= 60) {
        notifs.push({
          id: `nt_haccpcert_expiring_${u.id}`,
          severity: "warning",
          type: "haccp_cert_expiring",
          title: `Attestato HACCP in scadenza: ${u.name}`,
          body: `Scade tra ${daysToExpiry} giorni. Programmare rinnovo presso ente accreditato.`,
          action: { view: "anagrafiche", tab: "users" },
          createdAt: nowIso(),
        });
      }
    }

    return notifs.sort((a, b) => {
      const sev = { critical: 0, warning: 1, info: 2 };
      if (sev[a.severity] !== sev[b.severity]) return sev[a.severity] - sev[b.severity];
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  },

  // ── LABOR COST per workOrder ───────────────────────────────────────────────
  // Fix L2 — pure function per uso futuro nel CMP figlio (createChildLot).
  // Calcola costo manodopera reale sommando (durata fase × €/h operatore).
  // Preferisce hourlyFullyLoaded (con ratei/TFR/oneri); fallback hourlyGross.
  // Se nessun operatore tracciato sulla fase, fallback wo.operator (legacy) o "Sconosciuto".
  // Ritorna { totalCost, durationMin, byOperator, fallback }.
  // fallback=true → tempo registrato ma nessun costo orario configurato (UI deve segnalare).
  workOrderLaborCost: (state, workOrderId) => {
    const wo = (state.workOrders || []).find(w => w.id === workOrderId);
    if (!wo) return { totalCost: 0, durationMin: 0, byOperator: {}, fallback: true };

    const users = state.users || [];
    const userMap = new Map(users.map(u => [u.id, u]));
    const userByName = new Map(users.map(u => [u.name, u]));

    let totalCost = 0;
    let totalDurationMin = 0;
    const byOperator = {};

    for (const ph of (wo.phases || [])) {
      if (!ph.startedAt || !ph.endedAt) continue;
      const durationMin = (new Date(ph.endedAt) - new Date(ph.startedAt)) / 60000;
      if (!Number.isFinite(durationMin) || durationMin <= 0) continue;

      const operatorId = ph.completedById || ph.startedById || null;
      const operatorName = ph.completedBy || ph.startedBy || wo.operator || null;
      const user = (operatorId && userMap.get(operatorId))
        || (operatorName && userByName.get(operatorName))
        || null;
      const hourlyRate = Number(user?.hourlyFullyLoaded) || Number(user?.hourlyGross) || 0;
      const cost = (durationMin / 60) * hourlyRate;

      totalCost += cost;
      totalDurationMin += durationMin;
      const key = operatorName || "Sconosciuto";
      if (!byOperator[key]) byOperator[key] = { durationMin: 0, cost: 0, hourlyRate };
      byOperator[key].durationMin += durationMin;
      byOperator[key].cost += cost;
      byOperator[key].hourlyRate = hourlyRate;
    }

    return {
      totalCost: Math.round(totalCost * 100) / 100,
      durationMin: Math.round(totalDurationMin * 10) / 10,
      byOperator,
      // fallback: tempo registrato ma nessun costo (operatori senza €/h configurato)
      fallback: totalCost === 0 && totalDurationMin > 0,
    };
  },

  // ── MENSA AZIENDALE — pasti attesi da contratto (Fase M1) ─────────────────
  // Pure function. Calcola pasti teorici mensili attesi sulla base del contratto
  // del dipendente (giorni/settimana × max pasti/giorno proporzionati al mese).
  // Riferimento normativo: art. 51 c. 2 lett. c) TUIR — mensa aziendale esente
  // se il pasto è consumato in locali aziendali nell'orario di servizio.
  //
  // Usata in M2/M3 per auto-generare i mealLogs e per il delta presenze/pasti.
  //
  // Output deterministico: stesso input ⇒ stesso output.
  // workDaysInMonth è clampato a [0, 31]. Se l'utente non consuma in mensa
  // (mealPreferenceConsume === false), ritorna tutto a zero.
  //
  // @param {Object} user - utente con campi mensa configurati (può essere null)
  // @param {number} workDaysInMonth - giorni lavorativi nel mese (default 22)
  // @returns {{ dailyExpected:number, monthlyExpected:number, withDoubleShift:boolean, workingDays:number }}
  mealsExpectedFromContract(user, workDaysInMonth = 22) {
    if (!user || user.mealPreferenceConsume === false) {
      return { dailyExpected: 0, monthlyExpected: 0, withDoubleShift: false, workingDays: 0 };
    }
    const max = Number(user.mealsPerDayMax) > 0 ? Number(user.mealsPerDayMax) : 1;
    const daysRaw = Number(workDaysInMonth);
    const days = Math.max(0, Math.min(31, Number.isFinite(daysRaw) ? daysRaw : 0));
    const wdpwRaw = Number(user.workDaysPerWeek);
    const workDaysPerWeek = Number.isFinite(wdpwRaw) && wdpwRaw > 0 && wdpwRaw <= 7 ? wdpwRaw : 6;
    // Proporzionamento: se il dipendente lavora 5gg su 7 settimanali, i suoi
    // giorni mensili attesi sono days × (5/7). Arrotondamento al più vicino.
    const proportional = Math.round(days * (workDaysPerWeek / 7));
    return {
      dailyExpected: max,
      monthlyExpected: proportional * max,
      withDoubleShift: max > 1,
      workingDays: proportional,
    };
  },

  // ── MENSA AZIENDALE — aggregazione mensile mealLogs (Fase M3) ─────────────
  // Aggrega state.mealLogs filtrati per yearMonth ("YYYY-MM") in struttura
  // riepilogo per dipendente: giorni lavorati, ore, pasti dovuti/erogati,
  // valore € (mealsConsumed × mealValueDaily). Ordinato per nome.
  //
  // Pure function: nessun side effect, output deterministico.
  //
  // @param {Object} state - app state
  // @param {string} yearMonth - formato "YYYY-MM" (es. "2026-04")
  // @param {{ includeInactive?:boolean }} opts - includi anche i cessati
  // @returns {{ byUser:Array, totals:Object, yearMonth:string, fallback:boolean }}
  mealsAggregateMonth(state, yearMonth, opts = {}) {
    const includeInactive = !!opts.includeInactive;
    const allLogs = Array.isArray(state?.mealLogs) ? state.mealLogs : [];
    const monthLogs = allLogs.filter(ml => ml && typeof ml.date === "string" && ml.date.startsWith(yearMonth));

    if (monthLogs.length === 0) {
      return {
        byUser: [],
        totals: { mealsDue: 0, mealsConsumed: 0, costEur: 0, employeesCount: 0, daysWorked: 0, hoursWorked: 0, rectifiedCount: 0 },
        yearMonth,
        fallback: true,
      };
    }

    const users = Array.isArray(state?.users) ? state.users : [];
    const userMap = new Map(users.map(u => [u.id, u]));
    // Reparto derivato dalla MANSIONE (più affidabile del CDC numerico,
    // che nel CSV SEAC può avere incongruenze del commercialista, es. aprile NIALTRI).
    const repartoFromMansione = (mansione) => {
      const m = String(mansione || "").toUpperCase();
      if (!m) return "—";
      if (m.includes("CHEF") || m.includes("CUOCO") || m.includes("COMMIS CUCINA") ||
          m.includes("LAVAPIATTI") || m.includes("INSERVIENTE")) return "CUCINA";
      if (m.includes("CAMERIER") || m.includes("COMMIS DI SALA") || m.includes("RESPONSABILE S") ||
          m.includes("RESPONSABILE SALA") || m.includes("MAITRE")) return "SALA";
      if (m.includes("BARISTA") || m.includes("BANCONIERE")) return "BAR";
      if (m.includes("SEGRETARIA") || m.includes("AMMINISTRA")) return "AMMINISTRAZIONE";
      return "PERSONALE";
    };

    const byUserMap = new Map();
    for (const ml of monthLogs) {
      const u = userMap.get(ml.userId);
      if (!u) continue;
      const userActive = u.active !== false;
      if (!includeInactive && !userActive) continue;

      if (!byUserMap.has(ml.userId)) {
        byUserMap.set(ml.userId, {
          userId: ml.userId,
          name: u.name || "(senza nome)",
          mansione: u.mansione || u.role || "—",
          cdc: repartoFromMansione(u.mansione || u.role),
          cdcCode: u.centroDiCosto || null,
          active: userActive,
          mealValueDaily: Number(u.mealValueDaily) > 0 ? Number(u.mealValueDaily) : 5,
          mealsPerDayMax: Number(u.mealsPerDayMax) > 0 ? Number(u.mealsPerDayMax) : 1,
          contractType: u.contractType || null,
          livello: u.livello || null,
          ccnl: u.ccnl || null,
          daysWorked: 0,
          daysRiposo: 0,
          hoursWorked: 0,
          mealsDue: 0,
          mealsConsumed: 0,
          rectifiedCount: 0,
          logs: [],
        });
      }

      const e = byUserMap.get(ml.userId);
      e.logs.push(ml);
      if (ml.isRiposo) {
        e.daysRiposo++;
      } else if ((Number(ml.hoursWorked) || 0) > 0) {
        e.daysWorked++;
        e.hoursWorked += Number(ml.hoursWorked) || 0;
      }
      e.mealsDue += Number(ml.mealsDue) || 0;
      e.mealsConsumed += Number(ml.mealsConsumed) || 0;
      if (ml.source === "manual_rectification") e.rectifiedCount++;
    }

    const byUser = Array.from(byUserMap.values()).sort((a, b) => a.name.localeCompare(b.name, "it"));
    for (const u of byUser) {
      u.hoursWorked = Math.round(u.hoursWorked * 100) / 100;
      u.deltaMeals = u.mealsConsumed - u.mealsDue;
      u.totalEur = Math.round(u.mealsConsumed * u.mealValueDaily * 100) / 100;
      u.dueEur = Math.round(u.mealsDue * u.mealValueDaily * 100) / 100;
    }

    const totals = {
      mealsDue: byUser.reduce((s, u) => s + u.mealsDue, 0),
      mealsConsumed: byUser.reduce((s, u) => s + u.mealsConsumed, 0),
      costEur: Math.round(byUser.reduce((s, u) => s + u.totalEur, 0) * 100) / 100,
      employeesCount: byUser.length,
      daysWorked: byUser.reduce((s, u) => s + u.daysWorked, 0),
      hoursWorked: Math.round(byUser.reduce((s, u) => s + u.hoursWorked, 0) * 100) / 100,
      rectifiedCount: byUser.reduce((s, u) => s + u.rectifiedCount, 0),
    };

    return { byUser, totals, yearMonth, fallback: false };
  },

  /**
   * incassoBreakdown — riconcilia incasso reale con stampa di cassa.
   *
   * La piattaforma calcola l'incasso REALE (receipt.total):
   *   - articoli regalo (SESCIALA & co.) → unitPrice forzato 0 nell'item
   *   - mezze porzioni → unitPrice ridotto nell'item
   * La stampa di cassa Zucchetti riporta il LORDO (regali a prezzo pieno,
   * mezze a prezzo intero). Questa funzione calcola il delta per consentire
   * ad Andi di riconciliare il totale cassa con quello della piattaforma.
   *
   * @param {Array} receipts — array di receipt (tableReceipts o sottoinsieme)
   * @returns {{
   *   reale:          number,  // Σ receipt.total (già calcolato dall'importer)
   *   regali:         number,  // valore regali (qty × _csvUnitPrice per _isGift items)
   *   mezze:          number,  // sconto mezze porzioni (Σ _mezzaSconto per item annotati)
   *   aggiustamenti:  number,  // regali + mezze
   *   comeCassa:      number,  // reale + aggiustamenti (≈ stampa cassa)
   * }}
   *
   * Backward compat: scontrini senza _isGift/_mezzaSconto → aggiustamenti 0.
   * Puro: nessun side effect.
   */
  incassoBreakdown(receipts) {
    const r2 = (v) => Math.round((v || 0) * 100) / 100;
    let reale = 0;
    let regali = 0;
    let mezze = 0;
    for (const receipt of (receipts || [])) {
      if (!_receiptCountsForRevenue(receipt)) continue; // AUDIT-FIX: EC riconciliato = già nello SF collegato
      reale += Number(receipt.total) || 0;
      for (const it of (receipt.items || [])) {
        if (it._isGift) {
          regali += (Number(it.qty) || 0) * (Number(it._csvUnitPrice) || 0);
        }
        if (it._mezzaSconto) {
          // _mezzaSconto = sconto unitario; l'item con _mezzaSconto ha sempre qty=1
          // (il parser separa l'item in qty-1 + 1 item con mezza). Moltiplichiamo
          // comunque per qty per robustezza futura.
          mezze += (Number(it.qty) || 1) * (Number(it._mezzaSconto) || 0);
        }
      }
    }
    reale = r2(reale);
    regali = r2(regali);
    mezze = r2(mezze);
    const aggiustamenti = r2(regali + mezze);
    const comeCassa = r2(reale + aggiustamenti);
    return { reale, regali, mezze, aggiustamenti, comeCassa };
  },
};

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                  FASE M2a — PARSER CSV SEAC PAGHE                          ║
// ║                                                                            ║
// ║  Parser nativo per export presenze mensili del consulente del lavoro       ║
// ║  (SEAC paghe — formato NIALTRI SRL, ragione sociale Bottega del Mare).     ║
// ║  Riconosce automaticamente:                                                ║
// ║    - Header SEAC: MESE-ANNO, CODICE FISCALE DIP, COGNOME NOME, VPA, …      ║
// ║    - 28-31 colonne giorno (" 1-Dom" … "31-Mar") con ore HH.MM o "R"        ║
// ║    - VPA: OR (ordinario), ST (straord.), ON (notturno), FE (ferie),        ║
// ║           SF (straord. festivo), PR (permesso retrib.), RC (riposo comp.)  ║
// ║  Aggrega righe multiple per stesso CF in struttura per dipendente.         ║
// ║  Pure functions, niente side effects.                                      ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

// VPA codes che rappresentano LAVORO EFFETTIVO (concorrono a ore turno qualificante per pasto)
const SEAC_VPA_WORK = new Set(["OR", "ST", "ON", "SF"]);
// VPA codes di assenza retribuita (NON concorrono al pasto: ferie/permessi/riposi comp.)
const SEAC_VPA_LEAVE = new Set(["FE", "PR", "RC"]);

// Parser CSV row con quote handling robusto (gestisce escaped quotes "")
function parseCSVRow(line) {
  const cells = [];
  let cur = "";
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuote && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuote = !inQuote;
    } else if (c === ";" && !inQuote) {
      cells.push(cur);
      cur = "";
    } else {
      cur += c;
    }
  }
  cells.push(cur);
  return cells;
}

// Parse formato SEAC TOT HH "HHH,MM" → decimale ore (es. "024,30" = 24h 30min = 24.5).
// Attenzione: NON è un numero decimale italiano! La virgola separa ore da minuti (sessagesimale).
// Cell SEAC giornaliere usano invece formato "HH.MM" (es. "06.30" = 6h 30min).
function parseSeacHoursMinutes(s) {
  if (s == null) return 0;
  const t = String(s).trim().replace(/^"|"$/g, "").trim();
  if (!t) return 0;
  const m = t.match(/^(\d{1,3})[,.](\d{2})$/);
  if (m) {
    const h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (!Number.isFinite(h) || !Number.isFinite(min) || min >= 60) return 0;
    return h + min / 60;
  }
  // Fallback: numero intero
  const mInt = t.match(/^(\d{1,4})$/);
  if (mInt) return parseInt(mInt[1], 10);
  return 0;
}

// Parse numero italiano SEAC ("080,00" → 80.00). Tollerante: gestisce "1.234,56" e "12,5".
function parseSeacNumber(s) {
  if (s == null) return 0;
  const cleaned = String(s).trim().replace(/\s/g, "");
  if (!cleaned) return 0;
  // Gestisce sia "080,00" che "1.234,56" (separatore migliaia)
  const lastDot = cleaned.lastIndexOf(".");
  const lastComma = cleaned.lastIndexOf(",");
  let norm;
  if (lastDot > -1 && lastComma > -1) {
    if (lastComma > lastDot) norm = cleaned.replace(/\./g, "").replace(",", ".");
    else norm = cleaned.replace(/,/g, "");
  } else if (lastComma > -1) {
    norm = cleaned.replace(",", ".");
  } else {
    norm = cleaned;
  }
  const n = parseFloat(norm);
  return Number.isFinite(n) ? n : 0;
}

// Parse data italiana SEAC ("19/03/2026" → "2026-03-19" ISO). null se vuota/invalida.
function parseItDate(s) {
  if (!s) return null;
  const t = String(s).trim();
  if (!t) return null;
  const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const dd = parseInt(m[1], 10), mm = parseInt(m[2], 10), yyyy = parseInt(m[3], 10);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
}

// Parser principale CSV SEAC presenze.
// @returns {{ ok: boolean, employees: Array, errors: Array<string>, meta: Object }}
function parseSEACPresenze(text) {
  if (!text) return { ok: false, employees: [], errors: ["File vuoto"], meta: {} };
  // 1. Strip BOM UTF-8
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

  // 2. Split righe
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length < 2) return { ok: false, employees: [], errors: ["File vuoto o senza righe dati"], meta: {} };

  // 3. Parse header con quote handling
  const header = parseCSVRow(lines[0]).map(h => h.trim().replace(/^"|"$/g, "").trim());
  if (header.length < 20) {
    return { ok: false, employees: [], errors: [`Header con sole ${header.length} colonne — non sembra un CSV SEAC paghe (atteso ≥ 28 colonne).`], meta: {} };
  }

  // 4. Whitelist header essenziali (tolerant: confronta normalizzato senza spazi/trattini)
  const normH = (s) => String(s).toUpperCase().replace(/[-\s_]/g, "");
  const headerNorm = header.map(normH);
  const required = ["MESEANNO", "CODICEFISCALEDIP", "COGNOMENOME", "MANSIONE", "VPA", "TOTHH"];
  const missing = required.filter(r => !headerNorm.some(h => h.includes(r)));
  if (missing.length > 0) {
    return {
      ok: false, employees: [],
      errors: [`Non sembra un CSV SEAC paghe. Header mancanti: ${missing.join(", ")}`],
      meta: {},
    };
  }

  // 5. Indici colonne base
  const colIdx = {};
  headerNorm.forEach((h, i) => {
    if (h === "MESEANNO" && colIdx.monthYear == null) colIdx.monthYear = i;
    else if (h === "RAGIONESOCIALE" && colIdx.ragione == null) colIdx.ragione = i;
    else if (h === "COGNOMENOME" && colIdx.name == null) colIdx.name = i;
    else if (h === "CODICEFISCALEDIP" && colIdx.cf == null) colIdx.cf = i;
    else if (h === "MATRICOLA" && colIdx.matricola == null) colIdx.matricola = i;
    else if (h === "MANSIONE" && colIdx.mansione == null) colIdx.mansione = i;
    else if (h === "DATAASSUNZIONE" && colIdx.hireDate == null) colIdx.hireDate = i;
    else if ((h === "DATACESSAZONE" || h === "DATACESSAZIONE") && colIdx.endDate == null) colIdx.endDate = i;
    else if (h === "DATAFINETD" && colIdx.endTD == null) colIdx.endTD = i;
    else if (h === "VPA" && colIdx.vpa == null) colIdx.vpa = i;
    else if (h === "TOTHH" && colIdx.totHH == null) colIdx.totHH = i;
    else if (h === "TOTGG" && colIdx.totGG == null) colIdx.totGG = i;
    else if (h === "CDC1" && colIdx.cdc1 == null) colIdx.cdc1 = i;
    else if (h === "CDC1DESCRIZIONE" && colIdx.cdc1desc == null) colIdx.cdc1desc = i;
  });

  // 6. Colonne giorno: pattern " 1-Dom", "31-Mar" (numero + giorno settimana abbreviato)
  // Robust: match anche con whitespace variabile.
  const dayColumns = [];
  header.forEach((h, i) => {
    const m = String(h).trim().match(/^(\d{1,2})\s*[-]\s*([A-Za-z]{3})$/);
    if (m) {
      const dayNum = parseInt(m[1], 10);
      if (dayNum >= 1 && dayNum <= 31) dayColumns.push({ idx: i, day: dayNum, dow: m[2] });
    }
  });
  if (dayColumns.length < 28) {
    return {
      ok: false, employees: [],
      errors: [`Trovate solo ${dayColumns.length} colonne giorno (servono 28-31). Verifica il formato del file.`],
      meta: {},
    };
  }

  // 7. Parse righe dati — aggregazione per CF
  const employeesMap = new Map();
  const errors = [];
  let monthYear = null;
  let ragione = null;

  for (let r = 1; r < lines.length; r++) {
    const row = parseCSVRow(lines[r]);
    if (row.length < header.length - 5) {
      errors.push(`Riga ${r + 1}: solo ${row.length} colonne (attese ${header.length}) — saltata`);
      continue;
    }
    const cf = (row[colIdx.cf] || "").trim().replace(/^"|"$/g, "").trim();
    if (!cf || cf.length < 11) continue; // skip righe senza CF valido (es. righe totali)

    const name = (row[colIdx.name] || "").trim().replace(/^"|"$/g, "").trim();
    const vpaRaw = (row[colIdx.vpa] || "").trim().replace(/^"|"$/g, "").trim().toUpperCase();
    if (!SEAC_VPA_WORK.has(vpaRaw) && !SEAC_VPA_LEAVE.has(vpaRaw)) {
      // VPA sconosciuta — non blocca, warn e salta solo questa riga
      errors.push(`Riga ${r + 1} (${name}): VPA sconosciuta "${vpaRaw}" — riga saltata`);
      continue;
    }

    if (!monthYear) monthYear = (row[colIdx.monthYear] || "").trim().replace(/^"|"$/g, "").trim();
    if (!ragione) ragione = (row[colIdx.ragione] || "").trim().replace(/^"|"$/g, "").trim();

    let emp = employeesMap.get(cf);
    if (!emp) {
      const cdc1descRaw = (row[colIdx.cdc1desc] || "").trim().replace(/^"|"$/g, "").trim();
      // "CUCINA - operai" → "cucina" (parte prima del " - ")
      const reparto = cdc1descRaw.toLowerCase().split(/\s+-\s+/)[0].trim();
      emp = {
        cf,
        name,
        mansione: (row[colIdx.mansione] || "").trim().replace(/^"|"$/g, "").trim(),
        matricola: colIdx.matricola != null ? (row[colIdx.matricola] || "").trim().replace(/^"|"$/g, "").trim() : "",
        hireDate: parseItDate((row[colIdx.hireDate] || "").trim().replace(/^"|"$/g, "").trim()),
        endDate: parseItDate((row[colIdx.endDate] || "").trim().replace(/^"|"$/g, "").trim()),
        endTD: parseItDate((row[colIdx.endTD] || "").trim().replace(/^"|"$/g, "").trim()),
        cdc1: (row[colIdx.cdc1] || "").trim().replace(/^"|"$/g, "").trim(),
        cdc1desc: cdc1descRaw,
        reparto, // normalizzato lowercase
        daysHours: new Map(), // day(1-31) → { OR, ST, ON, SF, FE, PR, RC, riposo }
        totHH: { OR: 0, ST: 0, ON: 0, SF: 0, FE: 0, PR: 0, RC: 0 },
        totGG: { OR: 0, ST: 0, ON: 0, SF: 0, FE: 0, PR: 0, RC: 0 },
      };
      employeesMap.set(cf, emp);
    }

    // Tot HH (formato sessagesimale HHH,MM) e Tot GG (intero) per VPA
    // ATTENZIONE: nello stesso file un CF può avere più "CHIAVE SEAC DIP" (cambio mansione
    // intra-mese, es. AGOSTINELLI ERIKA: SEGRETARIA fino al 15/04, APPR.CAMERIERA dal 16/04).
    // In quel caso le righe vanno SOMMATE per VPA, non sovrascritte.
    emp.totHH[vpaRaw] = (emp.totHH[vpaRaw] || 0) + parseSeacHoursMinutes(row[colIdx.totHH]);
    emp.totGG[vpaRaw] = (emp.totGG[vpaRaw] || 0) + (parseInt((row[colIdx.totGG] || "0").trim().replace(/^"|"$/g, ""), 10) || 0);

    // Parse celle giornaliere
    for (const dc of dayColumns) {
      const cellRaw = (row[dc.idx] || "").trim().replace(/^"|"$/g, "").trim();
      if (!cellRaw) continue;
      // "R" (eventualmente con padding) = riposo settimanale
      if (cellRaw === "R") {
        if (!emp.daysHours.has(dc.day)) {
          emp.daysHours.set(dc.day, { OR: 0, ST: 0, ON: 0, SF: 0, FE: 0, PR: 0, RC: 0, riposo: true });
        } else {
          emp.daysHours.get(dc.day).riposo = true;
        }
        continue;
      }
      // Formato HH.MM (es. "07.00", "06.30")
      const m = cellRaw.match(/^(\d{1,2})\.(\d{2})$/);
      if (!m) {
        // Tentativo soft: numero solo intero
        const m2 = cellRaw.match(/^(\d{1,2})$/);
        if (!m2) continue;
        const h = parseInt(m2[1], 10);
        if (!Number.isFinite(h) || h <= 0) continue;
        if (!emp.daysHours.has(dc.day)) emp.daysHours.set(dc.day, { OR: 0, ST: 0, ON: 0, SF: 0, FE: 0, PR: 0, RC: 0, riposo: false });
        emp.daysHours.get(dc.day)[vpaRaw] = (emp.daysHours.get(dc.day)[vpaRaw] || 0) + h;
        continue;
      }
      const hours = parseInt(m[1], 10) + parseInt(m[2], 10) / 60;
      if (!Number.isFinite(hours) || hours <= 0) continue;
      if (!emp.daysHours.has(dc.day)) {
        emp.daysHours.set(dc.day, { OR: 0, ST: 0, ON: 0, SF: 0, FE: 0, PR: 0, RC: 0, riposo: false });
      }
      const dayObj = emp.daysHours.get(dc.day);
      dayObj[vpaRaw] = (dayObj[vpaRaw] || 0) + hours;
    }
  }

  return {
    ok: true,
    employees: Array.from(employeesMap.values()),
    errors,
    meta: {
      monthYear: (monthYear || "").replace(/\s+/g, ""),
      ragione: ragione || "",
      dayCount: dayColumns.length,
    },
  };
}

// Inferenza tipo contratto da campi SEAC (mansione + date).
function inferContractTypeFromSEAC(emp) {
  if (!emp) return null;
  const m = (emp.mansione || "").toUpperCase();
  // Apprendistato: la mansione SEAC contiene "APPR" (es. "APPR. CAMERIER")
  if (/\bAPPR\.?/.test(m)) return "apprendista";
  // Tempo determinato / stagionale: presente DATA FINE TD
  if (emp.endTD) return "stagionale";
  // Default: tempo indeterminato full-time
  return "fullTime";
}

// Suggerimento livello CCNL Pubblici Esercizi Confcommercio (4° liv. medio cameriere; 5° commis; 6° aiuto/apprendista).
function suggestLivelloFromMansione(mansione) {
  if (!mansione) return null;
  const m = mansione.toUpperCase();
  // Apprendistato — livello dedicato (in genere C/D nel CCNL, ma manteniamo "VI" come fallback editabile)
  if (/\bAPPR\.?/.test(m)) return "VI";
  // II = quadri / responsabili di servizio
  if (m.includes("RESPONSABILE") || m.includes("DIRETTORE")) return "II";
  // III = chef di partita / cuoco unico capo cucina
  if (m === "CHEF" || /\bCHEF\b/.test(m)) return "III";
  if (m.includes("CUOCO") && !m.includes("AIUTO") && !m.includes("COMMIS")) return "III";
  // IV = cameriere qualificato / segretaria
  if ((m.includes("CAMERIER") || m.includes("BARMAN")) && !m.includes("AIUTO")) return "IV";
  if (m.includes("SEGRETARIA")) return "IV";
  // V = commis cucina / commis sala / barista / aiuto cuoco
  if (m.includes("COMMIS") || m.includes("BARISTA") || m.includes("AIUTO CUOCO")) return "V";
  // VI = inservienti / lavapiatti / aiuto cameriere
  if (m.includes("INSERVIENTE") || m.includes("LAVAPIATTI") || m.includes("AIUTO CAMERIER")) return "VI";
  return null;
}

// Auto-compila configurazione mensa (soglia ore qualificante + max pasti/giorno)
// in base a mansione SEAC e carico effettivo del mese (totHH lavorate, totGG lavorati).
// Pura: nessun side-effect. Solo per nuovi inserimenti (action="create"), MAI per update —
// un utente esistente potrebbe aver personalizzato i campi a mano e non vogliamo sovrascrivere.
// @param {string} mansione - dal CSV SEAC (es. "CHEF", "APPR. CAMERIER", "INSERVIENTE")
// @param {number} totHHmonth - ore lavorate effettive nel mese (OR+ST+ON, esclude SF/FE/PR/RC)
// @param {number} totGGmonth - giorni di lavoro effettivo nel mese
// @returns {{ mealEntitlementHours: number, mealsPerDayMax: number }}
function inferMealConfigFromSEAC(mansione, totHHmonth, totGGmonth) {
  const m = (mansione || "").toUpperCase();
  const hh = Number(totHHmonth) || 0;
  const gg = Number(totGGmonth) || 0;
  const avgHoursPerDay = gg > 0 ? hh / gg : 0;

  // === Soglia ore qualificante per maturare il pasto ===
  // CCNL Pubblici Esercizi standard: 6h. Ridotta a 5h per supporto/apprendisti
  // (turni più brevi ma comunque a cavallo del servizio).
  let mealEntitlementHours = 6;
  if (m.includes("INSERVIENTE") || m.includes("LAVAPIATTI") || /\bAPPR\.?/.test(m)) {
    mealEntitlementHours = 5;
  }

  // === Max pasti per giorno (1 = single shift, 2 = turno spezzato pranzo+cena) ===
  let mealsPerDayMax = 1;

  // Mansioni che restano single shift sempre → 1 pasto
  const singleShiftRoles = ["BARISTA", "SEGRETARIA"];
  const isSingleShiftRole = singleShiftRoles.some(r => m.includes(r));

  // Mansioni tipicamente con turno spezzato pranzo+cena → potenzialmente 2 pasti
  const dualShiftRoles = ["CHEF", "CUOCO", "COMMIS", "CAMERIER", "RESPONSABILE", "DIRETTORE"];
  const isDualShiftRole = dualShiftRoles.some(r => m.includes(r));
  const isApprendista = /\bAPPR\.?/.test(m);

  if (isSingleShiftRole) {
    mealsPerDayMax = 1;
  } else if (m.includes("AIUTO CUOCO")) {
    // Aiuto cuoco: 2 pasti solo se carico mensile alto (≥100h)
    mealsPerDayMax = hh >= 100 ? 2 : 1;
  } else if (m.includes("LAVAPIATTI") || m.includes("INSERVIENTE")) {
    // Personale di supporto: 2 pasti solo se carico alto (≥80h)
    mealsPerDayMax = hh >= 80 ? 2 : 1;
  } else if (isApprendista) {
    // Apprendista: turno spezzato verosimile se media giornaliera ≥7h
    mealsPerDayMax = avgHoursPerDay >= 7 ? 2 : 1;
  } else if (isDualShiftRole) {
    // Ruoli "core" sala/cucina: spezzato se media giornaliera ≥7h
    mealsPerDayMax = avgHoursPerDay >= 7 ? 2 : 1;
  }

  return { mealEntitlementHours, mealsPerDayMax };
}

// Genera mealLogs giornalieri dalla struttura SEAC + configurazione user.
// Regola pasti (M2a default):
//   - Giorno di lavoro effettivo (OR+ST+ON+SF ≥ user.mealEntitlementHours [default 6h])
//     → 1 pasto dovuto (qualifyingTurn = true)
//   - Se ore totali ≥ 8 E user.mealsPerDayMax ≥ 2 (turno spezzato)
//     → 2 pasti dovuti
//   - Riposo / ferie / permesso retribuito / riposo compensativo → 0 pasti
//   - mealPreferenceConsume === false → 0 pasti per tutti i giorni
// mealsConsumed = mealsDue di default (l'utente rettifica eccezioni in tab "Eroga pasti").
function generateMealLogsFromSEAC(seacEmp, user, monthYear) {
  if (!seacEmp || !user || !monthYear) return [];
  const logs = [];
  const cleaned = monthYear.replace(/\s+/g, "");
  const parts = cleaned.split("-");
  if (parts.length !== 2) return [];
  const monthNum = parseInt(parts[0], 10);
  const yearNum = parseInt(parts[1], 10);
  if (!monthNum || !yearNum || monthNum < 1 || monthNum > 12) return [];

  const soglia = Number(user.mealEntitlementHours) > 0 ? Number(user.mealEntitlementHours) : 6;
  const maxPasti = Number(user.mealsPerDayMax) > 0 ? Number(user.mealsPerDayMax) : 1;
  const preferConsume = user.mealPreferenceConsume !== false;

  for (const [day, dayObj] of seacEmp.daysHours.entries()) {
    const dateISO = `${yearNum}-${String(monthNum).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const hOR = Number(dayObj.OR) || 0;
    const hST = Number(dayObj.ST) || 0;
    const hON = Number(dayObj.ON) || 0;
    const hSF = Number(dayObj.SF) || 0;
    const hFE = Number(dayObj.FE) || 0;
    const hPR = Number(dayObj.PR) || 0;
    const hRC = Number(dayObj.RC) || 0;
    const hoursWork = hOR + hST + hON + hSF;
    const hoursLeave = hFE + hPR + hRC;

    if (dayObj.riposo) {
      logs.push({
        id: uid("ml_"),
        userId: user.id,
        date: dateISO,
        monthYear: cleaned,
        hoursWorked: 0,
        hoursOR: 0, hoursST: 0, hoursON: 0, hoursSF: 0, hoursFE: 0, hoursPR: 0, hoursRC: 0,
        mealsDue: 0, mealsConsumed: 0,
        qualifyingTurn: false, isRiposo: true, isLeave: false,
        source: "seac_auto",
      });
      continue;
    }
    if (hoursWork <= 0 && hoursLeave <= 0) continue; // dipendente non in forza quel giorno

    let mealsDue = 0;
    let qualifyingTurn = false;
    if (preferConsume && hoursWork >= soglia) {
      qualifyingTurn = true;
      mealsDue = 1;
      if (hoursWork >= 8 && maxPasti >= 2) mealsDue = 2;
    }

    logs.push({
      id: uid("ml_"),
      userId: user.id,
      date: dateISO,
      monthYear: cleaned,
      hoursWorked: Math.round(hoursWork * 100) / 100,
      hoursOR: hOR, hoursST: hST, hoursON: hON, hoursSF: hSF,
      hoursFE: hFE, hoursPR: hPR, hoursRC: hRC,
      mealsDue, mealsConsumed: mealsDue,
      qualifyingTurn,
      isRiposo: false,
      isLeave: hoursWork === 0 && hoursLeave > 0,
      source: "seac_auto",
    });
  }
  return logs;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║              HELPER INDEXEDDB — wrapper minimo, native API                 ║
// ║  db "bottega_db", object store "kv" (key-value generico).                  ║
// ║  Nessuna dipendenza esterna. Serializzazione JSON esplicita per             ║
// ║  coerenza con il deep-merge (che fa JSON.parse) e per evitare              ║
// ║  edge-case structured-clone su oggetti ciclici / Date.                     ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
const IDB_DB_NAME = "bottega_db";
const IDB_STORE   = "kv";
const IDB_VERSION = 1;

/** Apre (o riusa) il DB. Restituisce una Promise<IDBDatabase>. */
function _idbOpen() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_DB_NAME, IDB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess  = (e) => resolve(e.target.result);
    req.onerror    = (e) => reject(e.target.error);
    req.onblocked  = ()  => reject(new Error("IndexedDB bloccato da un'altra tab"));
  });
}

/**
 * Legge il valore per `key` dallo store "kv".
 * Restituisce Promise<any> (null se non trovato).
 * Il valore è stato serializzato come stringa JSON → qui viene parse-ato.
 */
async function idbGet(key) {
  const db = await _idbOpen();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(IDB_STORE, "readonly");
    const req = tx.objectStore(IDB_STORE).get(key);
    req.onsuccess = (e) => {
      const raw = e.target.result;
      if (raw == null) { resolve(null); return; }
      try { resolve(JSON.parse(raw)); }
      catch (err) {
        // JSON corrotto: salva una copia grezza prima di trattarlo come assente,
        // altrimenti al primo save il SEED lo sovrascrive e i dati sono persi per sempre.
        try {
          const backupKey = `${key}_corrupted_${Date.now()}`;
          idbSet(backupKey, raw).catch(() => {});
          console.error(`JSON IDB corrotto per "${key}". Copia grezza salvata in "${backupKey}".`, err);
        } catch (_e) {}
        resolve(null);
      }
    };
    req.onerror = (e) => reject(e.target.error);
  });
}

/**
 * Scrive `value` per `key` nello store "kv" (serializzato come JSON).
 * Restituisce Promise<void> che si risolve SOLO dopo il commit della transazione.
 * Il chiamante può fare `await idbSet(...)` per attendere la conferma prima di
 * procedere (es. migrazione sicura localStorage → IDB).
 */
async function idbSet(key, value) {
  const db  = await _idbOpen();
  const str = JSON.stringify(value);
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(IDB_STORE, "readwrite");
    const store = tx.objectStore(IDB_STORE);
    store.put(str, key);
    tx.oncomplete = () => resolve();
    tx.onerror    = (e) => reject(e.target.error);
    tx.onabort    = (e) => reject(e.target.error || new Error("Transazione IDB abortita"));
  });
}

/**
 * Elimina la chiave `key` dallo store "kv".
 * Usata per rimuovere la chiave da localStorage DOPO migrazione confermata
 * (in realtà qui la usiamo solo per localStorage, ma la definiamo per completezza).
 */
async function idbDel(key) {
  const db = await _idbOpen();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror    = (e) => reject(e.target.error);
  });
}

/** True se IndexedDB è disponibile nel browser corrente (Safari private = false). */
function isIdbAvailable() {
  try { return typeof indexedDB !== "undefined" && indexedDB !== null; }
  catch { return false; }
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      PERSISTENCE HOOK                                      ║
// ║                                                                             ║
// ║  Storage primario: IndexedDB ("bottega_db" / store "kv")                   ║
// ║  Fallback automatico: localStorage (Safari private, browser vecchi)        ║
// ║  Migrazione una-tantum: al primo avvio con questa versione, se IDB è       ║
// ║  vuoto ma localStorage ha la chiave, migra in modo sicuro:                 ║
// ║    leggi localStorage → scrivi IDB → ATTENDI conferma → cancella LS       ║
// ║  Se la scrittura IDB fallisce, localStorage NON viene cancellato.          ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function usePersistedState(key, initial) {
  const [state, setState] = useState(initial);
  const [loaded, setLoaded] = useState(false);

  // ── LOAD (asincrono) ──────────────────────────────────────────────────────
  useEffect(() => {
    // Funzione di deep-merge migration: accetta un oggetto già parsato
    // e restituisce lo state merged/sanitizzato pronto per setState.
    // UNICA definizione — usata sia dal path IDB che dal path localStorage.
    function applyMigration(parsed) {
      const merged = { ...initial, ...parsed };
      // Garantisce che gli array essenziali esistano
      ["suppliers", "items", "purchaseOrders", "receipts", "invoices", "lots", "lotMovements",
       "workOrders", "plannedOrders", "dishes", "kitchenOutputs", "kitchenRequests",
       "messages", "users", "baseProducts", "dishesReal", "auditLog",
       // STEP 3 — registro anti-doppio scarico da venduto (additivo, retrocompat)
       "salesDischarges",
       // Fase M2a — mealLogs (presenze giornaliere mensa) auto-generati da import CSV SEAC
       "mealLogs",
       // CRM Link-Core Fase 2 — store additivi (retrocompat: state legacy → array vuoti)
       "customers", "customerConsents",
       // CRM Link-Core Fase 3 — store additivo CustomerReceiptLink (retrocompat)
       "customerReceiptLinks",
       // Agenda V1 — store additivo Reservation (retrocompat: state legacy → array vuoto)
       "reservations",
       // Floor Core — anagrafica fisica additiva (retrocompat: state legacy → seed iniziale)
       "floorAreas", "floorTables",
       // R5 Fulfilment — store additivo ReservationReceiptLink (retrocompat: legacy → [])
       "reservationReceiptLinks"].forEach(k => {
        if (!Array.isArray(merged[k])) merged[k] = initial[k] || [];
      });
      // Merge profondo di haccpLogs
      const defaultHaccp = initial.haccpLogs || { temperature: [], cleaning: [], nonConformities: [] };
      merged.haccpLogs = {
        temperature: Array.isArray(merged.haccpLogs?.temperature) ? merged.haccpLogs.temperature : (defaultHaccp.temperature || []),
        cleaning: Array.isArray(merged.haccpLogs?.cleaning) ? merged.haccpLogs.cleaning : (defaultHaccp.cleaning || []),
        nonConformities: Array.isArray(merged.haccpLogs?.nonConformities) ? merged.haccpLogs.nonConformities : (defaultHaccp.nonConformities || []),
      };
      // Settings (default se mancanti)
      merged.settings = { ...(initial.settings || {}), ...(merged.settings || {}) };
      // FIX #6 migration (audit 2026-06-04): garantisce seasonalityMonthly come array
      // di 12 numeri >0. State legacy / valori corrotti → profilo neutro (tutti 1),
      // così il forecast resta IDENTICO a oggi finché Andi non lo imposta.
      {
        const sm = merged.settings.seasonalityMonthly;
        const valid = Array.isArray(sm) && sm.length === 12 && sm.every(v => Number.isFinite(Number(v)) && Number(v) > 0);
        merged.settings.seasonalityMonthly = valid ? sm.map(Number) : [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1];
      }
      if (!Array.isArray(merged.tables)) merged.tables = [];
      if (!Array.isArray(merged.tableReceipts)) merged.tableReceipts = [];
      if (!merged.dishAliases || typeof merged.dishAliases !== "object") merged.dishAliases = {};
      // Migration: garantisce dishIgnoredArticles su state legacy pre-fix.
      if (!Array.isArray(merged.dishIgnoredArticles)) merged.dishIgnoredArticles = [];
      // FIX F-13 migration (audit 2026-05-19): garantisce importHistory su state legacy pre-fix.
      if (!Array.isArray(merged.importHistory)) merged.importHistory = [];
      // Migration storico incassi: garantisce i due store su state legacy.
      if (!Array.isArray(merged.historicalDaily)) merged.historicalDaily = [];
      if (!Array.isArray(merged.historicalProducts)) merged.historicalProducts = [];
      // Migration archivio storico Pienissimo (artefatti piccoli; l'archivio vero è in IndexedDB).
      if (merged.voyaArchiveMeta === undefined) merged.voyaArchiveMeta = null;
      if (merged.voyaSeasonality === undefined) merged.voyaSeasonality = null;
      // Migration modello attesi reali: garantisce demandModel come array (store sync).
      if (!Array.isArray(merged.demandModel)) merged.demandModel = [];
      // FIX backup promemoria (audit 2026-06-04): garantisce lastBackupAt su state legacy.
      if (merged.lastBackupAt === undefined) merged.lastBackupAt = null;
      if (!merged.currentUser) merged.currentUser = initial.currentUser;
      // Fix L2 — Migration users: garantisce nuovi campi HACCP/economici su utenti pre-fix.
      // Reg. CE 852/2004 All. II Cap. XII — schema attestato obbligatorio.
      // Fase M1 — campi mensa aziendale art. 51 c. 2 lett. c) TUIR (contratto + regole mensa).
      // Pattern: check per chiave con `undefined` per non sovrascrivere valori espliciti (anche null/0/false).
      if (Array.isArray(merged.users)) {
        merged.users = merged.users.map(u => ({
          ...u,
          // L2 — HACCP + costo orario
          haccpCertNumber: u.haccpCertNumber !== undefined ? u.haccpCertNumber : null,
          haccpCertIssuedAt: u.haccpCertIssuedAt !== undefined ? u.haccpCertIssuedAt : null,
          haccpCertExpiresAt: u.haccpCertExpiresAt !== undefined ? u.haccpCertExpiresAt : null,
          haccpCertLevel: u.haccpCertLevel !== undefined ? u.haccpCertLevel : null,
          haccpCertIssuer: u.haccpCertIssuer !== undefined ? u.haccpCertIssuer : null,
          hourlyGross: u.hourlyGross !== undefined ? u.hourlyGross : null,
          hourlyFullyLoaded: u.hourlyFullyLoaded !== undefined ? u.hourlyFullyLoaded : null,
          mansione: u.mansione !== undefined ? u.mansione : null,
          // M1 — Contratto
          contractType: u.contractType !== undefined ? u.contractType : null,
          weeklyHours: u.weeklyHours !== undefined ? u.weeklyHours : null,
          workDaysPerWeek: u.workDaysPerWeek !== undefined ? u.workDaysPerWeek : null,
          ccnl: u.ccnl !== undefined ? u.ccnl : null,
          livello: u.livello !== undefined ? u.livello : null,
          // M1 — Mensa
          mealEntitlementHours: u.mealEntitlementHours !== undefined ? u.mealEntitlementHours : 6,
          mealsPerDayMax: u.mealsPerDayMax !== undefined ? u.mealsPerDayMax : 1,
          mealPreferenceConsume: u.mealPreferenceConsume !== undefined ? u.mealPreferenceConsume : true,
          mealDietaryRestrictions: u.mealDietaryRestrictions !== undefined ? u.mealDietaryRestrictions : null,
          mealValueDaily: u.mealValueDaily !== undefined ? u.mealValueDaily : 5.00,
          // M2a — Anagrafica SEAC paghe (auto-popolata da import CSV)
          codiceFiscale: u.codiceFiscale !== undefined ? u.codiceFiscale : null,
          hireDate: u.hireDate !== undefined ? u.hireDate : null,
          endDate: u.endDate !== undefined ? u.endDate : null,
          endTD: u.endTD !== undefined ? u.endTD : null,
          centroDiCosto: u.centroDiCosto !== undefined ? u.centroDiCosto : null,
        }));
      }
      // FIX #5-A migration (audit 2026-06-04): calo peso a DUE fasi — i dati legacy
      // importati avevano caloTot = calo1 + calo2 (somma LINEARE, errata) invece del
      // compounding corretto 1-(1-calo1)(1-calo2). Questo gonfiava pReale (e quindi ogni
      // food cost teorico/live e ogni quadrante menu engineering) ~9% sul pesce nobile.
      // L'editor BaseProduct calcola GIÀ il compounding corretto; correggiamo solo i dati
      // GIÀ in localStorage che presentano la firma del bug, SENZA toccare valori editati a
      // mano. CRITICO — applichiamo il fix SOLO se TUTTE queste condizioni sono vere:
      //   (a) calo1 > 0 E calo2 > 0  (prodotto a due fasi reali)
      //   (b) |caloTot - (calo1+calo2)| < 1e-4  (caloTot è davvero la somma lineare buggata)
      //   (c) |pReale - pFatt/(1-(calo1+calo2))| < 0.01  (pReale deriva dal caloTot buggato,
      //       cioè NON è stato ricalcolato/editato a mano dopo)
      // Se anche UNA condizione è falsa → prodotto LASCIATO invariato (già corretto o manuale).
      // Idempotente: dopo il fix caloTot≈compound ≠ (calo1+calo2), quindi (b) diventa falsa e
      // il prodotto non viene più ritoccato. Formula IDENTICA all'editor (~riga 13160).
      // NB: NON tocca prodotti con calo2=0 (lì lineare=compound) né caloTot=0 con calo1>0
      // (quirk separato, fuori scope) — entrambi falliscono la condizione (a) o (b).
      if (Array.isArray(merged.baseProducts)) {
        merged.baseProducts = merged.baseProducts.map(bp => {
          if (!bp) return bp;
          const c1 = Number(bp.calo1) || 0;
          const c2 = Number(bp.calo2) || 0;
          const pFatt = Number(bp.pFatt) || 0;
          const caloTot = Number(bp.caloTot) || 0;
          const pReale = Number(bp.pReale) || 0;
          if (!(c1 > 0 && c2 > 0)) return bp;                                  // (a)
          const linearSum = c1 + c2;
          if (Math.abs(caloTot - linearSum) >= 1e-4) return bp;                // (b)
          const pRealeFromBug = linearSum < 1 ? pFatt / (1 - linearSum) : pFatt;
          if (Math.abs(pReale - pRealeFromBug) >= 0.01) return bp;             // (c)
          // Tutte vere → ricalcolo compound (stessa formula dell'editor BaseProduct).
          const calo = c1 + c2 - c1 * c2;                 // = 1-(1-c1)(1-c2)
          const newCaloTot = Math.min(0.99, Math.max(0, calo));
          const newPReale = newCaloTot < 0.99 ? pFatt / (1 - newCaloTot) : pFatt;
          return {
            ...bp,
            caloTot: Math.round(newCaloTot * 10000) / 10000,
            pReale: Math.round(newPReale * 10000) / 10000,
          };
        });
      }
      // Fase M3 — Migration mealLogs: garantisce `id` univoco su record legacy
      // (pre-fix il generator usava già uid, ma pasta vecchi snapshot potrebbero
      // mancare l'id — fondamentale per la rettifica manuale).
      if (Array.isArray(merged.mealLogs)) {
        let needsId = false;
        for (const ml of merged.mealLogs) {
          if (!ml || !ml.id) { needsId = true; break; }
        }
        if (needsId) {
          merged.mealLogs = merged.mealLogs.map(ml => (ml && !ml.id) ? { ...ml, id: uid("ml_") } : ml);
        }
      }
      return merged;
    }

    // Esecuzione del load asincrono.
    // Ordine priorità: IDB → localStorage (migrazione una-tantum) → SEED (initial).
    // loaded diventa true SOLO qui, dopo setState, impedendo al save effect di
    // scrivere prima che i dati reali siano stati caricati.
    (async () => {
      try {
        if (isIdbAvailable()) {
          // --- Prova IndexedDB ---
          let parsed = null;
          try { parsed = await idbGet(key); } catch (e) { console.warn("IDB get fallito:", e); }

          if (parsed != null) {
            // Percorso normale: dati già in IDB (stato stabile dopo prima migrazione)
            setState(applyMigration(parsed));
            setLoaded(true);
            return;
          }

          // IDB vuoto: controlla se c'è il dato vecchio in localStorage
          // (utenti esistenti come Andi — migrazione una-tantum)
          let lsRaw = null;
          try { lsRaw = localStorage.getItem(key); } catch {}

          if (lsRaw) {
            let lsParsed = null;
            try { lsParsed = JSON.parse(lsRaw); } catch (e) {
              console.warn("localStorage parse fallito durante migrazione:", e);
            }
            if (lsParsed != null) {
              // Migrazione sicura: scrivi su IDB e ATTENDI conferma prima di cancellare LS.
              let idbWriteOk = false;
              try {
                await idbSet(key, lsParsed);
                idbWriteOk = true;
              } catch (e) {
                console.error("Migrazione IDB fallita — localStorage preservato:", e);
              }
              if (idbWriteOk) {
                // Scrittura IDB confermata → rimuovi da localStorage per liberare i ~5MB.
                try { localStorage.removeItem(key); } catch {}
                console.info("Migrazione localStorage → IndexedDB completata. Spazio localStorage liberato.");
              }
              setState(applyMigration(lsParsed));
              setLoaded(true);
              return;
            }
          }

          // IDB vuoto e localStorage vuoto → primo avvio, parte dal SEED
          setLoaded(true);
          return;

        } else {
          // --- Fallback: IDB non disponibile (Safari private / browser vecchi) ---
          // Usa localStorage esattamente come prima, senza migrazione.
          try {
            const raw = localStorage.getItem(key);
            if (raw) {
              let parsed = null;
              try { parsed = JSON.parse(raw); }
              catch (perr) {
                // JSON corrotto: salva una copia prima di scivolare al SEED.
                try { localStorage.setItem(`${key}_corrupted_${Date.now()}`, raw); } catch (_e) {}
                console.error(`JSON localStorage corrotto per "${key}". Copia grezza salvata.`, perr);
              }
              if (parsed != null) setState(applyMigration(parsed));
            }
          } catch (e) { console.warn("Storage load (fallback LS) fallito:", e); }
          setLoaded(true);
          return;
        }
      } catch (e) {
        // Errore imprevisto nell'intero blocco load → parte dal SEED comunque
        console.warn("Storage load fallito (outer catch):", e);
        setLoaded(true);
      }
    })();
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── SAVE (debounced, asincrono) ───────────────────────────────────────────
  // loaded=false impedisce qualsiasi scrittura finché il load asincrono
  // non ha completato. Questo previene la race "SEED sovrascrive dati reali".
  useEffect(() => {
    if (!loaded) return;
    // Debounce: non scrive a ogni keystroke intermedio, solo 500ms dopo l'ultimo cambiamento.
    // Trailing edge: l'ultimo state è sempre quello persistito, anche se il componente rimonta.
    const timerId = setTimeout(() => {
      if (isIdbAvailable()) {
        // Salva su IndexedDB (capacità centinaia di MB)
        idbSet(key, state).catch(e => {
          console.error("IDB save fallito:", e);
          window.dispatchEvent(new CustomEvent("bottega:storage-error", {
            detail: {
              type: "write",
              message: `Errore salvataggio IndexedDB: ${e.message || e.name}. Esporta subito un backup JSON.`,
            },
          }));
        });
      } else {
        // Fallback localStorage (browser senza IDB)
        try {
          localStorage.setItem(key, JSON.stringify(state));
        } catch (e) {
          // Rileva QuotaExceededError (code 22 WebKit, code 1014 Firefox, name canonico)
          const isQuota = e.name === "QuotaExceededError" || e.code === 22 || e.code === 1014;
          window.dispatchEvent(new CustomEvent("bottega:storage-error", {
            detail: {
              type: isQuota ? "quota" : "write",
              message: isQuota
                ? "Memoria del browser esaurita — i dati NON vengono salvati. Esporta subito un backup JSON."
                : `Errore scrittura dati: ${e.message || e.name}`,
            },
          }));
          console.error("Storage save (fallback LS) fallito:", e);
        }
      }
    }, 500);
    return () => clearTimeout(timerId);
  }, [state, loaded, key]);

  // AUDIT-FIX (persistenza, ispettore #4 [CRITICA]): il save è debounced 500ms
  // ma non c'era flush alla chiusura. Chiudere il tablet subito dopo un
  // "Arrivato"/walk-in perdeva l'ultima azione. Un ref allo state sempre fresco
  // + flush IMMEDIATO su pagehide/visibilitychange(hidden): questi eventi sono
  // gli unici affidabili su iOS (beforeunload non spara sempre sulla chiusura
  // PWA). Best-effort, non blocca la chiusura.
  const stateFlushRef = useRef(state);
  stateFlushRef.current = state;
  useEffect(() => {
    if (!loaded) return undefined;
    const flush = () => {
      const snap = stateFlushRef.current;
      if (isIdbAvailable()) { idbSet(key, snap).catch(() => { /* best-effort in chiusura */ }); }
      else { try { localStorage.setItem(key, JSON.stringify(snap)); } catch { /* quota/full: nulla da fare in chiusura */ } }
    };
    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onHide);
    return () => { window.removeEventListener("pagehide", flush); document.removeEventListener("visibilitychange", onHide); };
  }, [loaded, key]);

  return [state, setState, loaded];
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║          BANNER ERRORE STORAGE — montato a livello App                    ║
// ║  Ascolta l'evento "bottega:storage-error" e mostra un avviso persistente  ║
// ║  finché il problema non si risolve. NON usa alert().                       ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function StorageErrorBanner() {
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    const handler = (e) => setError(e.detail);
    window.addEventListener("bottega:storage-error", handler);
    return () => window.removeEventListener("bottega:storage-error", handler);
  }, []);
  if (!error) return null;
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
      background: "#B5472A", color: "#F4F0E8",
      padding: "12px 20px",
      display: "flex", alignItems: "center", gap: 12,
      boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
    }}>
      <AlertTriangle size={18} style={{ flexShrink: 0 }} />
      <div className="f-body" style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
        MEMORIA PIENA — i dati NON vengono più salvati.{" "}
        <span style={{ fontWeight: 400 }}>
          Vai in <strong>Backup &amp; dati</strong> ed esporta subito un backup JSON, poi libera spazio cancellando vecchi dati.
        </span>
      </div>
      <button
        onClick={() => setError(null)}
        style={{ background: "transparent", border: "1px solid rgba(244,240,232,0.5)", color: "#F4F0E8", borderRadius: 2, padding: "4px 10px", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}
        aria-label="Chiudi avviso"
      >
        Chiudi
      </button>
    </div>
  );
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      TOAST SYSTEM                                          ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
const ToastContext = React.createContext(null);
function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const push = useCallback((toast) => {
    const id = uid("t_");
    setToasts(ts => [...ts, { ...toast, id }]);
    setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), toast.duration || 3500);
  }, []);
  return <ToastContext.Provider value={push}>
    {children}
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 100, display: "flex", flexDirection: "column", gap: 8, maxWidth: 360 }}>
      {toasts.map(t => <ToastItem key={t.id} toast={t} onClose={() => setToasts(ts => ts.filter(x => x.id !== t.id))} />)}
    </div>
  </ToastContext.Provider>;
}
function ToastItem({ toast, onClose }) {
  const colors = { success: { bg: C.olive, fg: C.white, icon: CheckCircle2 }, error: { bg: C.terra, fg: C.white, icon: AlertTriangle }, info: { bg: C.ink, fg: C.paper, icon: Info } };
  const c = colors[toast.type || "info"];
  const Icon = c.icon;
  return <div className="slide-left" style={{ background: c.bg, color: c.fg, padding: "14px 18px", borderRadius: 2, display: "flex", alignItems: "flex-start", gap: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", minWidth: 280 }}>
    <Icon size={18} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
    <div style={{ flex: 1 }}>
      {toast.title && <div className="f-body" style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{toast.title}</div>}
      <div className="f-body" style={{ fontSize: 12.5, opacity: 0.92, lineHeight: 1.4 }}>{toast.message}</div>
    </div>
    <button onClick={onClose} style={{ border: "none", background: "transparent", color: c.fg, cursor: "pointer", opacity: 0.7, padding: 0 }}><X size={14} /></button>
  </div>;
}
const useToast = () => React.useContext(ToastContext);

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      BASE UI COMPONENTS                                    ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function Badge({ status, children, size = "md" }) {
  const s = statusLabel[status] || { t: status, c: C.muted, bg: C.ruleSoft };
  const sz = size === "sm" ? { padding: "1px 6px", fontSize: 10 } : { padding: "2px 8px", fontSize: 11 };
  return <span className="f-mono" style={{ color: s.c, background: s.bg, borderRadius: 3, ...sz, letterSpacing: "0.06em", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 6 }}>{children || s.t}</span>;
}
function Rule({ style }) { return <div style={{ height: 1, background: C.rule, ...style }} />; }
function Btn({ children, onClick, variant = "primary", size = "md", disabled, icon: Icon, type, style, title }) {
  const sizes = { md: { padding: "10px 16px", fontSize: 13 }, sm: { padding: "6px 10px", fontSize: 12 }, lg: { padding: "14px 22px", fontSize: 14 }, xs: { padding: "4px 8px", fontSize: 11 } };
  const variants = {
    primary: { background: C.ink, color: C.paper, border: `1px solid ${C.ink}` },
    ghost: { background: "transparent", color: C.ink, border: `1px solid ${C.rule}` },
    accent: { background: C.teal, color: C.white, border: `1px solid ${C.teal}` },
    terra: { background: C.terra, color: C.white, border: `1px solid ${C.terra}` },
    olive: { background: C.olive, color: C.white, border: `1px solid ${C.olive}` },
    amber: { background: C.amber, color: C.white, border: `1px solid ${C.amber}` },
    danger: { background: "transparent", color: C.terra, border: `1px solid ${C.terra}` },
    subtle: { background: C.paperDeep, color: C.ink, border: `1px solid ${C.paperDeep}` },
  };
  return <button onClick={onClick} disabled={disabled} type={type || "button"} title={title} className="f-body tx"
    style={{ ...sizes[size], ...variants[variant], fontWeight: 500, borderRadius: 2, cursor: disabled ? "not-allowed" : "pointer", textTransform: "uppercase", letterSpacing: "0.05em", display: "inline-flex", alignItems: "center", gap: 8, opacity: disabled ? 0.4 : 1, ...style }}
    onMouseEnter={e => !disabled && (e.currentTarget.style.opacity = "0.85")}
    onMouseLeave={e => (e.currentTarget.style.opacity = disabled ? "0.4" : "1")}>
    {Icon && <Icon size={size === "sm" || size === "xs" ? 13 : 15} strokeWidth={1.8} />}{children}
  </button>;
}
function IconBtn({ icon: Icon, onClick, title, color, size = 16 }) {
  return <button onClick={onClick} title={title} className="tx" style={{ border: "none", background: "transparent", color: color || C.muted, cursor: "pointer", padding: 6, borderRadius: 2, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
    onMouseEnter={e => { e.currentTarget.style.background = C.paperDeep; e.currentTarget.style.color = C.ink; }}
    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = color || C.muted; }}>
    <Icon size={size} strokeWidth={1.7} />
  </button>;
}
function Field({ label, children, hint, required }) {
  return <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    {label && <span className="f-body" style={{ fontSize: 10, color: C.muted, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase" }}>
      {label}{required && <span style={{ color: C.terra, marginLeft: 3 }}>*</span>}
    </span>}
    {children}
    {hint && <span className="f-body" style={{ fontSize: 11, color: C.muted }}>{hint}</span>}
  </label>;
}

// VatSelector: input IVA con preset 4/10/22 + custom + "usa default"
// value = numero 0-1 (es. 0.10) oppure null/undefined per "usa default"
// defaultValue = valore del default del sistema (per mostrare quale verrà usato)
function VatSelector({ value, onChange, defaultRate, allowDefault = true }) {
  const presets = [0.04, 0.10, 0.22];
  const useDefault = value == null;
  const valuePct = useDefault ? null : Math.round(value * 10000) / 100;
  const isPreset = !useDefault && presets.some(p => Math.abs(p - value) < 0.0001);
  const [customMode, setCustomMode] = useState(!useDefault && !isPreset);

  return <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
    {allowDefault && <button type="button" onClick={() => { onChange(null); setCustomMode(false); }} className="tx"
      style={{ padding: "5px 10px", background: useDefault ? C.ink : C.white, color: useDefault ? C.paper : C.inkSoft, border: `1px solid ${useDefault ? C.ink : C.rule}`, borderRadius: 2, fontSize: 11, cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>
      Default {defaultRate != null ? `(${(defaultRate * 100).toFixed(0)}%)` : ""}
    </button>}
    {presets.map(p => <button key={p} type="button" onClick={() => { onChange(p); setCustomMode(false); }} className="tx"
      style={{ padding: "5px 10px", background: (!useDefault && Math.abs(value - p) < 0.0001) ? C.ink : C.white, color: (!useDefault && Math.abs(value - p) < 0.0001) ? C.paper : C.inkSoft, border: `1px solid ${(!useDefault && Math.abs(value - p) < 0.0001) ? C.ink : C.rule}`, borderRadius: 2, fontSize: 11, cursor: "pointer", fontFamily: "JetBrains Mono, monospace", fontWeight: 500 }}>
      {(p * 100).toFixed(0)}%
    </button>)}
    <button type="button" onClick={() => setCustomMode(true)} className="tx"
      style={{ padding: "5px 10px", background: customMode ? C.ink : C.white, color: customMode ? C.paper : C.inkSoft, border: `1px solid ${customMode ? C.ink : C.rule}`, borderRadius: 2, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
      Custom
    </button>
    {customMode && <input type="number" step="0.1" min="0" max="100" value={valuePct ?? ""} autoFocus
      onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) onChange(v / 100); }}
      placeholder="es. 22" className="f-mono tx"
      style={{ width: 70, padding: "5px 8px", border: `1px solid ${C.teal}`, borderRadius: 2, fontSize: 11, textAlign: "right" }} />}
    {customMode && <span className="f-mono" style={{ fontSize: 11, color: C.muted }}>%</span>}
  </div>;
}
function Input(props) {
  return <input {...props} className="f-body tx" style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, fontSize: 14, color: C.ink, padding: "9px 12px", ...(props.style || {}) }} />;
}
function Select({ children, ...props }) {
  return <select {...props} className="f-body tx" style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, fontSize: 14, color: C.ink, padding: "9px 12px", cursor: "pointer", ...(props.style || {}) }}>{children}</select>;
}
function Textarea(props) {
  return <textarea {...props} className="f-body tx" style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, fontSize: 14, color: C.ink, padding: "9px 12px", minHeight: 80, resize: "vertical", ...(props.style || {}) }} />;
}
function Modal({ children, onClose, maxWidth = 720, title, subtitle, footer }) {
  useEffect(() => { const h = (e) => e.key === "Escape" && onClose && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);

  return <div onClick={onClose} className="fade-in" style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(28,26,23,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 32, overflowY: "auto" }}>
    <div onClick={e => e.stopPropagation()} className="scale-in" style={{ background: C.paper, width: "100%", maxWidth, borderRadius: 2, boxShadow: "0 24px 80px rgba(0,0,0,0.3)" }}>
      {title && <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.rule}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          {subtitle && <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>{subtitle}</div>}
          <h3 className="f-display" style={{ fontSize: 22, color: C.ink, fontWeight: 500, margin: 0, letterSpacing: "-0.02em" }}>{title}</h3>
        </div>
        <IconBtn icon={X} onClick={onClose} title="Chiudi" />
      </div>}
      <div>{children}</div>
      {footer && <div style={{ padding: "16px 24px", borderTop: `1px solid ${C.rule}`, display: "flex", justifyContent: "flex-end", gap: 8, background: C.paperDeep }}>{footer}</div>}
    </div>
  </div>;
}
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║  ConfirmModal — sostituto a11y-safe di window.confirm()                    ║
// ║  Usage: <ConfirmModal open title message onConfirm onCancel [danger]       ║
// ║         [requireType="ARCHIVIA"] [confirmText] [cancelText]>{children}     ║
// ║         </ConfirmModal>                                                    ║
// ║  Caratteristiche:                                                          ║
// ║   - chiusura su click backdrop / Esc -> onCancel (mai onConfirm distratto) ║
// ║   - quando requireType è valorizzato, l'utente deve digitarlo per abilitare║
// ║     il bottone di conferma (anti-cancellazione distratta su importi alti)  ║
// ║   - children: slot per Field aggiuntivi (es. motivazione)                  ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function ConfirmModal({ open, title, message, confirmText = "Conferma", cancelText = "Annulla", danger = false, requireType = null, onConfirm, onCancel, children }) {
  const [typed, setTyped] = useState("");
  useEffect(() => { if (open) setTyped(""); }, [open]);
  if (!open) return null;
  const canConfirm = !requireType || typed === requireType;
  return (
    <Modal onClose={onCancel} maxWidth={520} title={title}>
      <div style={{ padding: "20px 24px" }}>
        <div className="f-body" role="alert" style={{ fontSize: 14, color: C.inkSoft, lineHeight: 1.55, marginBottom: 16, whiteSpace: "pre-wrap" }}>{message}</div>
        {children}
        {requireType && (
          <div style={{ marginTop: 12, marginBottom: 4 }}>
            <Field label={`Per confermare scrivi: ${requireType}`}>
              <Input value={typed} onChange={e => setTyped(e.target.value)} autoFocus aria-label={`Conferma scrivendo ${requireType}`} />
            </Field>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
          <Btn variant="ghost" onClick={onCancel}>{cancelText}</Btn>
          <Btn variant={danger ? "terra" : "primary"} disabled={!canConfirm} onClick={onConfirm}>{confirmText}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function StatCard({ kicker, value, unit, trend, accent, icon: Icon, onClick, sub }) {
  const Component = onClick ? "button" : "div";
  return <Component onClick={onClick} className="tx hover-lift" style={{ background: C.white, border: `1px solid ${C.rule}`, padding: "18px 20px 16px", borderRadius: 2, position: "relative", textAlign: "left", cursor: onClick ? "pointer" : "default", width: "100%", fontFamily: "inherit" }}>
    <div style={{ position: "absolute", top: 0, left: 0, width: 3, height: 24, background: accent }} />
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
      <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.28em", color: C.muted, textTransform: "uppercase" }}>{kicker}</div>
      {Icon && <Icon size={14} color={C.mutedSoft} strokeWidth={1.5} />}
    </div>
    <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
      <div className="f-display" style={{ fontSize: 32, lineHeight: 1, color: C.ink, fontWeight: 500, letterSpacing: "-0.02em" }}>{value}</div>
      <div className="f-body" style={{ fontSize: 11, color: C.muted }}>{unit}</div>
    </div>
    <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, marginTop: 6 }}>{trend}</div>
    {sub && <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 5, paddingTop: 5, borderTop: `1px solid ${C.ruleSoft}` }}>{sub}</div>}
  </Component>;
}
function Metric({ label, value, accent }) {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "3px 0" }}>
    <span className="f-body" style={{ fontSize: 12, color: C.muted }}>{label}</span>
    <span className="f-mono" style={{ fontSize: 14, color: accent || C.ink, fontWeight: 600 }}>{value}</span>
  </div>;
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  TicketStatsCard — sostituisce "ticket medio €X" puntuale.              ║
// ║  Mostra mediana + CI 95% bootstrap come valore principale (la mediana    ║
// ║  e' lo stimatore corretto su distribuzione log-normale F&B), media       ║
// ║  deenfatizzata, IQR (P25-P75) e P90 (high-roller).                      ║
// ║  Variante "compact" replicabile dentro StatCard layout, variante         ║
// ║  "hero" per le grandi card dedicate.                                     ║
// ╚══════════════════════════════════════════════════════════════════════════╝
function TicketStatsCard({ ts, kicker, accent = C.teal, variant = "compact", icon: Icon = User, note }) {
  if (!ts) return null;
  if (ts.fallback || ts.n < 5) {
    return <div className="hover-lift" style={{ background: C.white, border: `1px solid ${C.rule}`, padding: "18px 20px 16px", borderRadius: 2, position: "relative" }}>
      <div style={{ position: "absolute", top: 0, left: 0, width: 3, height: 24, background: accent }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.28em", color: C.muted, textTransform: "uppercase" }}>{kicker}</div>
        {Icon && <Icon size={14} color={C.mutedSoft} strokeWidth={1.5} />}
      </div>
      <div className="f-display" style={{ fontSize: 22, color: C.muted, fontWeight: 500 }}>—</div>
      <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>dati insufficienti (n={ts.n}, servono ≥5)</div>
    </div>;
  }
  if (variant === "hero") {
    return <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderLeft: `4px solid ${accent}`, padding: 18, borderRadius: 2 }}>
      <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", marginBottom: 4 }}>{kicker}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <div className="f-display" style={{ fontSize: 32, fontWeight: 600, color: C.ink, letterSpacing: "-0.01em" }}>{fmt.eur(ts.median)}</div>
        <div className="f-mono" style={{ fontSize: 11, color: C.muted }}>mediana</div>
      </div>
      <div className="f-mono" style={{ fontSize: 10, color: C.inkSoft, marginTop: 4 }}>
        CI 95% <strong>{fmt.eur(ts.medianCI[0])}—{fmt.eur(ts.medianCI[1])}</strong> · n={ts.n}
      </div>
      <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>
        media {fmt.eur(ts.mean)} · P25-P75 {fmt.eur(ts.p25)}-{fmt.eur(ts.p75)} · P90 {fmt.eur(ts.p90)}
      </div>
      {note && <div className="f-body" style={{ fontSize: 10, color: C.muted, marginTop: 6 }}>{note}</div>}
    </div>;
  }
  // compact: dimensioni allineate a StatCard
  return <div className="hover-lift" style={{ background: C.white, border: `1px solid ${C.rule}`, padding: "18px 20px 16px", borderRadius: 2, position: "relative" }}>
    <div style={{ position: "absolute", top: 0, left: 0, width: 3, height: 24, background: accent }} />
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
      <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.28em", color: C.muted, textTransform: "uppercase" }}>{kicker}</div>
      {Icon && <Icon size={14} color={C.mutedSoft} strokeWidth={1.5} />}
    </div>
    <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
      <div className="f-display" style={{ fontSize: 26, lineHeight: 1, color: C.ink, fontWeight: 500, letterSpacing: "-0.02em" }}>{fmt.eur(ts.median)}</div>
      <div className="f-body" style={{ fontSize: 10, color: C.muted }}>mediana</div>
    </div>
    <div className="f-mono" style={{ fontSize: 10, color: C.inkSoft, marginTop: 5 }} title="Intervallo di confidenza 95% sulla mediana (bootstrap, 1000 resample)">
      CI95 {fmt.eur(ts.medianCI[0])}–{fmt.eur(ts.medianCI[1])} · n={ts.n}
    </div>
    <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }} title="Media aritmetica (gonfiata da outlier in distribuzione log-normale). P25-P75 = scarto interquartile. P90 = scontrino 'top 10%'.">
      media {fmt.eur(ts.mean)} · P25-75 €{ts.p25.toFixed(0)}-{ts.p75.toFixed(0)} · P90 €{ts.p90.toFixed(0)}
    </div>
    {note && <div className="f-body" style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{note}</div>}
  </div>;
}
function SectionHeader({ title, subtitle, action }) {
  return <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 16, gap: 16 }}>
    <div>
      <h2 className="f-display" style={{ fontSize: 20, color: C.ink, fontWeight: 500, letterSpacing: "-0.01em", margin: 0 }}>{title}</h2>
      {subtitle && <p className="f-body" style={{ fontSize: 12, color: C.muted, marginTop: 4, margin: "4px 0 0 0" }}>{subtitle}</p>}
    </div>
    {action}
  </div>;
}
function PageHeader({ title, subtitle, kicker, right }) {
  return <div style={{ marginBottom: 28, paddingTop: 8 }}>
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24 }}>
      <div style={{ flex: 1 }}>
        {kicker && <div className="f-mono slide-down" style={{ fontSize: 10, letterSpacing: "0.3em", color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>{kicker}</div>}
        <h1 className="f-display" style={{ fontSize: 42, lineHeight: 1, fontWeight: 400, color: C.ink, letterSpacing: "-0.03em", margin: 0 }}>{title}</h1>
        {subtitle && <p className="f-body" style={{ fontSize: 14, color: C.inkSoft, maxWidth: 640, marginTop: 12, margin: "12px 0 0 0" }}>{subtitle}</p>}
      </div>
      {right}
    </div>
    <Rule style={{ marginTop: 24 }} />
  </div>;
}
function EmptyState({ icon: Icon, title, body, action }) {
  return <div style={{ background: C.white, border: `1px dashed ${C.rule}`, borderRadius: 2, padding: 40, textAlign: "center" }}>
    {Icon && <Icon size={32} color={C.mutedSoft} strokeWidth={1.3} style={{ margin: "0 auto 14px", display: "block" }} />}
    <div className="f-display" style={{ fontSize: 16, color: C.inkSoft, fontWeight: 500, marginBottom: body ? 6 : 0 }}>{title}</div>
    {body && <div className="f-body" style={{ fontSize: 12, color: C.muted, marginBottom: action ? 16 : 0 }}>{body}</div>}
    {action}
  </div>;
}
function Tabs({ tabs, value, onChange }) {
  return <div style={{ display: "flex", gap: 4, marginBottom: 20, flexWrap: "wrap" }}>
    {tabs.map(t => <button key={t.id} onClick={() => onChange(t.id)} className="f-body tx" style={{
      display: "flex", alignItems: "center", gap: 8, padding: "8px 14px",
      background: value === t.id ? C.ink : "transparent",
      color: value === t.id ? C.paper : C.inkSoft,
      border: `1px solid ${value === t.id ? C.ink : C.rule}`,
      borderRadius: 2, fontSize: 13, fontWeight: 500, cursor: "pointer",
    }}>
      {t.icon && <t.icon size={14} />}{t.label}{t.count != null && <span className="f-mono" style={{ opacity: 0.6, fontSize: 11 }}>{t.count}</span>}
    </button>)}
  </div>;
}
function QRMock({ size = 120, data = "" }) {
  const grid = 21;
  const cells = useMemo(() => {
    let seed = 0; for (let c of data) seed = (seed * 31 + c.charCodeAt(0)) >>> 0;
    const out = [];
    for (let i = 0; i < grid * grid; i++) { seed = (seed * 1103515245 + 12345) >>> 0; out.push(seed % 100 < 48); }
    const setF = (r, c) => { for (let i = 0; i < 7; i++) for (let j = 0; j < 7; j++) { const ring = i === 0 || i === 6 || j === 0 || j === 6; const core = i >= 2 && i <= 4 && j >= 2 && j <= 4; out[(r + i) * grid + (c + j)] = ring || core; } };
    setF(0, 0); setF(0, grid - 7); setF(grid - 7, 0);
    return out;
  }, [data]);
  const cs = size / grid;
  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ background: C.white }}>
    {cells.map((on, i) => on && <rect key={i} x={(i % grid) * cs} y={Math.floor(i / grid) * cs} width={cs} height={cs} fill={C.ink} />)}
  </svg>;
}
function Avatar({ name, size = 34, role }) {
  const bg = roleColors[role] || C.tealSoft;
  return <div className="f-body" style={{ width: size, height: size, borderRadius: size / 2, background: bg, color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.36, fontWeight: 600, flexShrink: 0 }}>
    {name.split(" ").map(w => w[0]).join("").slice(0, 2)}
  </div>;
}
function ProgressBar({ value, max = 100, color = C.teal, height = 4 }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return <div style={{ width: "100%", height, background: C.ruleSoft, borderRadius: height / 2, overflow: "hidden" }}>
    <div className="tx" style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: height / 2 }} />
  </div>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      SIDEBAR                                               ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function Sidebar({ view, setView, counts, currentUser, onOpenCommand }) {
  const groups = [
    { title: "Giornata", items: [
      { id: "dashboard", label: "Quadro giornata", icon: LayoutDashboard },
      { id: "bacheca", label: "Bacheca", icon: MessageSquare, count: counts.unread },
    ]},
    { title: "Acquisti & Magazzino", items: [
      { id: "acquisti", label: "Ordini a fornitori", icon: ShoppingCart, count: counts.poOpen },
      { id: "ingresso", label: "Ingresso merce", icon: PackagePlus, count: counts.poToReceive },
      { id: "fatture", label: "Fatture", icon: Receipt, count: counts.invoicesDue },
      { id: "magazzino", label: "Magazzino & lotti", icon: Warehouse, count: counts.lotsActive },
      { id: "haccp", label: "Registro HACCP", icon: ShieldCheck, count: counts.haccpAlerts },
    ]},
    { title: "Produzione", items: [
      { id: "produzione", label: "Ordini lavorazione", icon: Factory, count: counts.ordersActive, accent: true },
      { id: "pianificazione", label: "Pianificazione", icon: Calendar },
    ]},
    { title: "Cucina", items: [
      { id: "richieste", label: "Richieste cucina", icon: ChefHat, count: counts.kitchenRequests, accent: counts.kitchenUrgent > 0 },
      { id: "piatti", label: "Piatti & ricette", icon: Utensils },
      { id: "scarico", label: "Scarico cucina", icon: Flame },
      { id: "foodcost", label: "Costruzione food cost", icon: Activity, count: counts.baseProducts },
    ]},
    { title: "Vendite", items: [
      { id: "agenda", label: "Agenda", icon: Calendar, count: counts.onlineRequests, accent: counts.onlineRequests > 0 },
      { id: "vendite", label: "Vendite & Statistiche", icon: TrendingUp, count: counts.todayReceipts },
    ]},
    { title: "Personale", items: [
      { id: "mensa", label: "Mensa", icon: UtensilsCrossed },
    ]},
    { title: "Tracciabilità & Anagrafiche", items: [
      { id: "tracciabilita", label: "Tracciabilità", icon: GitBranch },
      { id: "anagrafiche", label: "Anagrafiche", icon: Book },
      { id: "clienti", label: "Clienti", icon: Users },
      { id: "sala-tavoli", label: "Sala & Tavoli", icon: Layers },
    ]},
    { title: "Direzione", items: [
      { id: "report", label: "Report & costi", icon: BarChart3 },
      { id: "simulatore", label: "Simulatore menu", icon: TrendingUp },
      { id: "dati", label: "Backup & dati", icon: Download },
    ]},
  ];

  return <aside className="hide-scroll" style={{ width: 260, background: C.ink, color: C.paper, flexShrink: 0, height: "100vh", position: "sticky", top: 0, overflowY: "auto", display: "flex", flexDirection: "column" }}>
    <div style={{ padding: "24px 24px 20px" }}>
      <div className="f-display" style={{ fontSize: 22, lineHeight: 1.05, fontWeight: 500, fontStyle: "italic" }}>Bottega</div>
      <div className="f-display" style={{ fontSize: 22, lineHeight: 1.05, fontWeight: 600 }}>del Mare</div>
      <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.25em", color: C.mutedSoft, marginTop: 12 }}>LAB · MAGAZZINO · CUCINA</div>
    </div>

    <div style={{ padding: "0 16px 12px" }}>
      <button onClick={onOpenCommand} className="f-body tx" style={{ width: "100%", padding: "8px 12px", background: "#2C2824", color: C.mutedSoft, border: "none", borderRadius: 2, fontSize: 12, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", textAlign: "left" }}
        onMouseEnter={e => { e.currentTarget.style.background = "#3A3630"; e.currentTarget.style.color = C.paper; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#2C2824"; e.currentTarget.style.color = C.mutedSoft; }}>
        <Command size={13} /> <span style={{ flex: 1 }}>Cerca & azioni</span>
        <span className="f-mono" style={{ fontSize: 9, opacity: 0.6, background: "#1C1A17", padding: "1px 5px", borderRadius: 2 }}>⌘K</span>
      </button>
    </div>

    <Rule style={{ background: "#2C2824", margin: "0 24px" }} />

    <nav style={{ flex: 1, padding: "12px 12px" }}>
      {groups.map((g, gi) => <div key={gi} style={{ marginBottom: 14 }}>
        <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.mutedSoft, textTransform: "uppercase", padding: "8px 12px 4px" }}>{g.title}</div>
        {g.items.map(it => {
          const active = view === it.id;
          return <button key={it.id} onClick={() => setView(it.id)} className="f-body tx"
            style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", textAlign: "left", width: "100%",
              background: active ? C.paper : "transparent", color: active ? C.ink : C.paper,
              border: "none", borderRadius: 2, fontSize: 12.5, fontWeight: active ? 500 : 400, cursor: "pointer", position: "relative" }}
            onMouseEnter={e => !active && (e.currentTarget.style.background = "#2C2824")}
            onMouseLeave={e => !active && (e.currentTarget.style.background = "transparent")}>
            <it.icon size={15} strokeWidth={1.7} />
            <span style={{ flex: 1 }}>{it.label}</span>
            {it.lock && <Lock size={11} style={{ opacity: 0.55 }} />}
            {it.count != null && it.count > 0 && <span className="f-mono" style={{ fontSize: 10, padding: "1px 6px", borderRadius: 10,
              background: active ? C.ink : (it.accent ? C.terra : "#3A3630"), color: C.paper, fontWeight: 500,
              animation: it.accent ? "pulse 1.8s infinite" : "none" }}>{it.count}</span>}
          </button>;
        })}
      </div>)}
    </nav>

    <Rule style={{ background: "#2C2824", margin: "0 24px" }} />
    <div style={{ padding: 20, display: "flex", alignItems: "center", gap: 12 }}>
      <Avatar name={currentUser.name} role={currentUser.role} />
      <div>
        <div className="f-body" style={{ fontSize: 13, fontWeight: 500 }}>{currentUser.name}</div>
        <div className="f-mono" style={{ fontSize: 10, color: C.mutedSoft, letterSpacing: "0.1em", textTransform: "uppercase" }}>{currentUser.role}</div>
      </div>
    </div>
  </aside>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      TOP BAR (notifications, quick actions)               ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function TopBar({ notifications, onNavigate, state, setState }) {
  const [showNotif, setShowNotif] = useState(false);
  const criticals = notifications.filter(n => n.severity === "critical").length;
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setShowNotif(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const isGross = engine.priceGross(state);
  const toggleIvaMode = () => setState({ ...state, settings: { ...(state.settings || {}), priceDisplay: isGross ? "net" : "gross" } });
  const vatPct = ((state.settings?.vatRateSale || 0.10) * 100).toFixed(0);

  return <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 40px 0 40px", marginBottom: 8 }}>
    <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>
      {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).toUpperCase()}
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ display: "flex", background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden", fontSize: 11 }} title={`IVA vendita ${vatPct}% — cambia modalità di visualizzazione prezzi`}>
        <button onClick={toggleIvaMode} className="tx" style={{ padding: "7px 12px", background: isGross ? C.ink : "transparent", color: isGross ? C.paper : C.inkSoft, border: "none", cursor: "pointer", fontFamily: "inherit", fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}>
          Lordo <span className="f-mono" style={{ fontSize: 9, opacity: 0.65 }}>IVA incl</span>
        </button>
        <button onClick={toggleIvaMode} className="tx" style={{ padding: "7px 12px", background: !isGross ? C.ink : "transparent", color: !isGross ? C.paper : C.inkSoft, border: "none", cursor: "pointer", fontFamily: "inherit", fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}>
          Netto <span className="f-mono" style={{ fontSize: 9, opacity: 0.65 }}>IVA escl</span>
        </button>
      </div>
      <div ref={ref} style={{ position: "relative" }}>
        <button onClick={() => setShowNotif(v => !v)} className="tx" style={{ border: `1px solid ${C.rule}`, background: C.white, padding: "8px 12px", borderRadius: 2, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", position: "relative" }}>
          <Bell size={15} color={criticals > 0 ? C.terra : C.inkSoft} strokeWidth={1.7} className={criticals > 0 ? "bell-ring" : ""} />
          <span className="f-body" style={{ fontSize: 12, color: C.inkSoft, fontWeight: 500 }}>Notifiche</span>
          {notifications.length > 0 && <span className="f-mono" style={{ background: criticals > 0 ? C.terra : C.ink, color: C.paper, fontSize: 10, padding: "1px 6px", borderRadius: 10, fontWeight: 500 }}>{notifications.length}</span>}
        </button>
        {showNotif && <NotificationPanel notifications={notifications} onNavigate={(a) => { setShowNotif(false); onNavigate(a); }} onClose={() => setShowNotif(false)} />}
      </div>
    </div>
  </div>;
}

function NotificationPanel({ notifications, onNavigate, onClose }) {
  const iconFor = (n) => ({
    haccp: Thermometer, expiry: AlertTriangle, lowstock: Package, invoice: Receipt, kitchen: ChefHat, po: Truck,
  })[n.type] || Bell;
  const colorFor = (n) => ({ critical: C.terra, warning: C.amber, info: C.teal }[n.severity] || C.muted);
  const wash = (n) => ({ critical: C.terraWash, warning: C.amberWash, info: C.tealWash }[n.severity] || C.ruleSoft);

  return <div className="slide-down" style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: 440, background: C.paper, border: `1px solid ${C.ink}`, borderRadius: 2, boxShadow: "0 16px 48px rgba(0,0,0,0.2)", zIndex: 50, maxHeight: 560, overflow: "hidden", display: "flex", flexDirection: "column" }}>
    <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.rule}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div>
        <div className="f-display" style={{ fontSize: 16, fontWeight: 500, color: C.ink }}>Centro notifiche</div>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em", marginTop: 2 }}>{notifications.length} ATTIVE</div>
      </div>
      <IconBtn icon={X} onClick={onClose} />
    </div>
    <div style={{ flex: 1, overflowY: "auto" }}>
      {notifications.length === 0 ? <div style={{ padding: 40, textAlign: "center" }}>
        <CheckCircle2 size={28} color={C.olive} style={{ margin: "0 auto 10px", display: "block" }} />
        <div className="f-body" style={{ fontSize: 13, color: C.inkSoft }}>Nessuna notifica attiva</div>
        <div className="f-body" style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>Tutto sotto controllo</div>
      </div> : notifications.map(n => {
        const Icon = iconFor(n);
        return <button key={n.id} onClick={() => onNavigate(n.action)} className="tx" style={{ width: "100%", padding: "12px 18px", borderBottom: `1px solid ${C.ruleSoft}`, background: "transparent", border: "none", borderBottomColor: C.ruleSoft, borderBottomWidth: 1, borderBottomStyle: "solid", display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer", textAlign: "left" }}
          onMouseEnter={e => e.currentTarget.style.background = C.paperDeep}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
          <div style={{ width: 32, height: 32, background: wash(n), borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon size={15} color={colorFor(n)} strokeWidth={1.8} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500, marginBottom: 2 }}>{n.title}</div>
            <div className="f-body" style={{ fontSize: 11.5, color: C.inkSoft, lineHeight: 1.4 }}>{n.body}</div>
          </div>
          <ChevronRight size={14} color={C.muted} style={{ flexShrink: 0, marginTop: 8 }} />
        </button>;
      })}
    </div>
  </div>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      COMMAND PALETTE                                       ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function CommandPalette({ open, onClose, state, onNavigate }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const inputRef = useRef();

  useEffect(() => { if (open) { setTimeout(() => inputRef.current?.focus(), 50); setQuery(""); setSelected(0); } }, [open]);

  const commands = useMemo(() => {
    const cmds = [
      { id: "nav-dashboard", type: "Pagine", label: "Vai a Quadro giornata", icon: LayoutDashboard, action: () => onNavigate({ view: "dashboard" }) },
      { id: "nav-acquisti", type: "Pagine", label: "Vai a Ordini a fornitori", icon: ShoppingCart, action: () => onNavigate({ view: "acquisti" }) },
      { id: "nav-ingresso", type: "Pagine", label: "Vai a Ingresso merce", icon: PackagePlus, action: () => onNavigate({ view: "ingresso" }) },
      { id: "nav-magazzino", type: "Pagine", label: "Vai a Magazzino", icon: Warehouse, action: () => onNavigate({ view: "magazzino" }) },
      { id: "nav-haccp", type: "Pagine", label: "Vai a HACCP", icon: ShieldCheck, action: () => onNavigate({ view: "haccp" }) },
      { id: "nav-produzione", type: "Pagine", label: "Vai a Produzione", icon: Factory, action: () => onNavigate({ view: "produzione" }) },
      { id: "nav-richieste", type: "Pagine", label: "Vai a Richieste cucina", icon: ChefHat, action: () => onNavigate({ view: "richieste" }) },
      { id: "nav-piatti", type: "Pagine", label: "Vai a Piatti & ricette", icon: Utensils, action: () => onNavigate({ view: "piatti" }) },
      { id: "nav-foodcost", type: "Pagine", label: "Vai a Costruzione food cost", icon: Activity, action: () => onNavigate({ view: "foodcost" }) },
      { id: "nav-tracciabilita", type: "Pagine", label: "Vai a Tracciabilità", icon: GitBranch, action: () => onNavigate({ view: "tracciabilita" }) },
      { id: "nav-report", type: "Pagine", label: "Vai a Report", icon: BarChart3, action: () => onNavigate({ view: "report" }) },
      { id: "act-new-receipt", type: "Azioni rapide", label: "Registra nuova bolla ingresso", icon: PackagePlus, action: () => onNavigate({ view: "ingresso", new: true }) },
      { id: "act-new-po", type: "Azioni rapide", label: "Crea ordine a fornitore", icon: ShoppingCart, action: () => onNavigate({ view: "acquisti", new: true }) },
      { id: "act-new-wo", type: "Azioni rapide", label: "Nuovo ordine di lavorazione", icon: Factory, action: () => onNavigate({ view: "produzione", new: true }) },
      { id: "act-new-kr", type: "Azioni rapide", label: "Nuova richiesta cucina", icon: ChefHat, action: () => onNavigate({ view: "richieste", new: true }) },
      { id: "act-new-temp", type: "Azioni rapide", label: "Registra temperatura HACCP", icon: Thermometer, action: () => onNavigate({ view: "haccp", new: "temperature" }) },
      { id: "act-new-clean", type: "Azioni rapide", label: "Registra sanificazione", icon: Droplets, action: () => onNavigate({ view: "haccp", new: "cleaning" }) },
    ];
    state.lots.slice(0, 20).forEach(l => {
      const item = engine.getItems(state).find(i => i.id === l.itemId);
      cmds.push({ id: `lot-${l.id}`, type: "Lotti", label: `${l.id} — ${item?.name}`, hint: `${l.qtyRemaining} ${l.unit} · scade ${fmt.date(l.expiresAt)}`, icon: Tag, action: () => onNavigate({ view: "lot", lotId: l.id }) });
    });
    engine.getItems(state).forEach(it => {
      cmds.push({ id: `item-${it.id}`, type: "Articoli", label: it.name, hint: cats[it.category]?.label, icon: Package, action: () => onNavigate({ view: "magazzino", search: it.name }) });
    });
    state.suppliers.forEach(s => {
      cmds.push({ id: `sup-${s.id}`, type: "Fornitori", label: s.name, hint: s.category, icon: Truck, action: () => onNavigate({ view: "anagrafiche" }) });
    });
    (state.baseProducts || []).forEach(bp => {
      cmds.push({ id: `bp-${bp.id}`, type: "Prodotti base", label: bp.nome, hint: `reale ${fmt.eur(bp.pReale || bp.pFatt)}/kg`, icon: Activity, action: () => onNavigate({ view: "foodcost" }) });
    });
    return cmds;
  }, [state, onNavigate]);

  const filtered = useMemo(() => {
    if (!query) return commands.slice(0, 12);
    const q = query.toLowerCase();
    return commands.filter(c => c.label.toLowerCase().includes(q) || (c.hint || "").toLowerCase().includes(q)).slice(0, 20);
  }, [query, commands]);

  useEffect(() => { setSelected(0); }, [query]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(c => { (g[c.type] = g[c.type] || []).push(c); });
    return g;
  }, [filtered]);

  const keyHandler = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelected(s => Math.min(filtered.length - 1, s + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelected(s => Math.max(0, s - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); filtered[selected] && filtered[selected].action(); onClose(); }
    else if (e.key === "Escape") onClose();
  };

  if (!open) return null;
  return <div onClick={onClose} className="fade-in" style={{ position: "fixed", inset: 0, zIndex: 80, background: "rgba(28,26,23,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "12vh" }}>
    <div onClick={e => e.stopPropagation()} className="scale-in" style={{ background: C.paper, width: "100%", maxWidth: 600, borderRadius: 2, boxShadow: "0 24px 80px rgba(0,0,0,0.35)", overflow: "hidden" }}>
      <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.rule}`, display: "flex", alignItems: "center", gap: 12 }}>
        <Search size={18} color={C.muted} />
        <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={keyHandler} placeholder="Cerca azioni, lotti, articoli, fornitori…"
          className="f-body" style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 15, color: C.ink }} />
        <span className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em", padding: "3px 8px", border: `1px solid ${C.rule}`, borderRadius: 2 }}>ESC</span>
      </div>
      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        {filtered.length === 0 ? <div style={{ padding: 30, textAlign: "center" }}>
          <div className="f-body" style={{ fontSize: 13, color: C.muted }}>Nessun risultato per "{query}"</div>
        </div> : Object.entries(grouped).map(([type, items]) => <div key={type}>
          <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.25em", color: C.muted, textTransform: "uppercase", padding: "10px 18px 4px", background: C.paperDeep }}>{type}</div>
          {items.map(c => {
            const idx = filtered.indexOf(c);
            const isSel = idx === selected;
            return <button key={c.id} onClick={() => { c.action(); onClose(); }} onMouseEnter={() => setSelected(idx)} className="f-body tx" style={{ width: "100%", padding: "10px 18px", background: isSel ? C.ink : "transparent", color: isSel ? C.paper : C.ink, border: "none", borderRadius: 0, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left" }}>
              <c.icon size={15} color={isSel ? C.paper : C.muted} strokeWidth={1.7} />
              <span style={{ flex: 1, fontSize: 13 }}>{c.label}</span>
              {c.hint && <span className="f-mono" style={{ fontSize: 10, color: isSel ? C.mutedSoft : C.muted, letterSpacing: "0.05em" }}>{c.hint}</span>}
              {isSel && <ArrowRight size={14} color={C.paper} />}
            </button>;
          })}
        </div>)}
      </div>
      <div style={{ padding: "10px 18px", borderTop: `1px solid ${C.rule}`, background: C.paperDeep, display: "flex", alignItems: "center", gap: 16 }}>
        <span className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.1em" }}>↑↓ Naviga</span>
        <span className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.1em" }}>↵ Apri</span>
        <span className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.1em", marginLeft: "auto" }}>{filtered.length} RISULTATI</span>
      </div>
    </div>
  </div>;
}


// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      DASHBOARD                                             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// ── LA MIA GIORNATA: task ordinate per ruolo ──────────────────────────────
function MyDayPanel({ state, onNavigate }) {
  const [role, setRole] = useState(state.currentUser?.role || "Laboratorio");
  const [collapsed, setCollapsed] = useState(false);

  // Calcolo tasks raggruppati per ruolo
  const tasks = useMemo(() => {
    const today = TODAY;
    const t = { Laboratorio: [], Magazzino: [], Cucina: [], Amministrazione: [] };

    // Laboratorio: produzioni in corso, ordini da chiudere
    state.workOrders.filter(o => o.status === "in_corso").forEach(o => {
      t.Laboratorio.push({
        icon: Factory, color: C.terra, priority: 1,
        title: `Produzione in corso: ${o.id}`,
        body: o.phases?.filter(p => !p.completedAt).length + " fasi da completare",
        action: () => onNavigate({ view: "order", orderId: o.id }),
      });
    });
    // Laboratorio: richieste urgenti dalla cucina
    state.kitchenRequests.filter(kr => kr.status === "in_attesa" && kr.urgency === "urgente").forEach(kr => {
      t.Laboratorio.push({
        icon: ChefHat, color: C.terra, priority: 0,
        title: `Richiesta URGENTE cucina`,
        body: kr.items?.map(i => i.qty + " " + (engine.getItems(state).find(it => it.id === i.itemId)?.name || "?")).join(", "),
        action: () => onNavigate({ view: "richieste" }),
      });
    });

    // Magazzino: bolle da registrare, temperature da misurare, ordini arrivati
    // Soft-deleted PO esclusi
    state.purchaseOrders.filter(po => po.status === "ricevuto" && !po.receiptId && !po.deletedAt).forEach(po => {
      const sup = state.suppliers.find(s => s.id === po.supplierId);
      t.Magazzino.push({
        icon: Truck, color: C.amber, priority: 1,
        title: `Bolla da registrare: ${po.id}`,
        body: `${sup?.name} · ${po.lines.length} articoli`,
        action: () => onNavigate({ view: "ingresso", fromPO: po.id }),
      });
    });
    // Magazzino: temperature HACCP mancanti oggi
    const tempsToday = new Set((state.haccpLogs?.temperature || []).filter(t => t.date === today).map(t => t.location));
    const requiredLocs = ["Frigo pesce 1", "Frigo pesce 2", "Freezer -18", "Cella verdure"];
    requiredLocs.forEach(loc => {
      if (!tempsToday.has(loc)) {
        t.Magazzino.push({
          icon: Thermometer, color: C.teal, priority: 2,
          title: `Temp. ${loc} da misurare`,
          body: "Rilevazione giornaliera obbligatoria",
          action: () => onNavigate({ view: "haccp", new: "temperature" }),
        });
      }
    });
    // Magazzino: lotti in scadenza oggi/domani
    const criticalLots = state.lots.filter(l => l.status !== "esaurito" && l.expiresAt && daysBetween(today, l.expiresAt) <= 1);
    criticalLots.forEach(l => {
      const item = engine.getItems(state).find(i => i.id === l.itemId);
      const d = daysBetween(today, l.expiresAt);
      t.Magazzino.push({
        icon: AlertTriangle, color: d < 0 ? C.terra : (d === 0 ? C.terra : C.amber), priority: 0,
        title: `${item?.name} scade ${d < 0 ? `da ${-d}g` : d === 0 ? "OGGI" : "DOMANI"}`,
        body: `Lotto ${l.id} · ${l.qtyRemaining.toFixed(1)} ${l.unit}`,
        action: () => onNavigate({ view: "lot", lotId: l.id }),
      });
    });

    // Cucina: richieste pronte da ritirare, piatti con alto food cost da valutare
    state.kitchenRequests.filter(kr => kr.status === "pronta").forEach(kr => {
      t.Cucina.push({
        icon: CheckCircle2, color: C.olive, priority: 1,
        title: `Richiesta pronta da ritirare`,
        body: kr.items?.map(i => i.qty + " " + (engine.getItems(state).find(it => it.id === i.itemId)?.name || "?")).join(", "),
        action: () => onNavigate({ view: "richieste" }),
      });
    });
    // Cucina: piatti con coverage mapping basso (warning: food cost potenzialmente errato)
    const poorMapping = (state.dishesReal || []).filter(d => {
      if (!d.ingredienti?.length) return false;
      const mapped = d.ingredienti.filter(i => i.bpId).length;
      return mapped / d.ingredienti.length < 0.5;
    });
    if (poorMapping.length > 0) {
      t.Cucina.push({
        icon: AlertTriangle, color: C.amber, priority: 3,
        title: `${poorMapping.length} piatti con mapping parziale`,
        body: "Food cost potenzialmente impreciso",
        action: () => onNavigate({ view: "piatti" }),
      });
    }

    // Amministrazione: fatture in scadenza, NC aperte
    // Soft-deleted escluse dalle scadenze
    const upcomingInvoices = state.invoices.filter(i => {
      if (i.deletedAt) return false;
      if (i.status !== "da_pagare") return false;
      const d = daysBetween(today, i.dueDate);
      return d <= 7;
    });
    upcomingInvoices.forEach(inv => {
      const sup = state.suppliers.find(s => s.id === inv.supplierId);
      const d = daysBetween(today, inv.dueDate);
      t.Amministrazione.push({
        icon: Receipt, color: d < 0 ? C.terra : C.amber, priority: d < 0 ? 0 : 1,
        title: `Fattura ${d < 0 ? "SCADUTA" : `scade in ${d}g`}: ${inv.number}`,
        body: `${sup?.name} · ${fmt.eur(inv.amount)}`,
        action: () => onNavigate({ view: "fatture" }),
      });
    });
    const openNcs = (state.haccpLogs?.nonConformities || []).filter(nc => nc.status === "aperta");
    if (openNcs.length > 0) {
      t.Amministrazione.push({
        icon: AlertTriangle, color: C.terra, priority: 0,
        title: `${openNcs.length} Non Conformità HACCP aperte`,
        body: "Da chiudere con azione correttiva",
        action: () => onNavigate({ view: "haccp" }),
      });
    }
    // Aumenti prezzi > 10% negli ultimi 7 giorni
    const recentBigIncreases = (state.baseProducts || []).flatMap(bp =>
      (bp.priceHistory || []).filter(h => {
        const days = (new Date(TODAY) - new Date(h.at)) / 86400000;
        return days <= 7 && h.deltaPct > 10;
      }).map(h => ({ bp, h }))
    );
    if (recentBigIncreases.length > 0) {
      const top = recentBigIncreases.sort((a, b) => b.h.deltaPct - a.h.deltaPct).slice(0, 3);
      t.Amministrazione.push({
        icon: TrendingUp, color: C.terra, priority: 0,
        title: `${recentBigIncreases.length} aumenti prezzi > 10% negli ultimi 7gg`,
        body: top.map(x => `${(x.bp.display || x.bp.nome).slice(0, 22)} +${x.h.deltaPct.toFixed(0)}%`).join(" · "),
        action: () => onNavigate({ view: "foodcost" }),
      });
    }

    // Promemoria import scontrini se mancano quelli di oggi
    const todayReceiptsCount = (state.tableReceipts || []).filter(r => getServiceDate(r.closedAt) === today).length;
    if (todayReceiptsCount === 0) {
      const lastImported = (state.tableReceipts || []).map(r => getServiceDate(r.closedAt)).filter(Boolean).sort();
      const lastDate = lastImported[lastImported.length - 1];
      t.Amministrazione.push({
        icon: Upload, color: C.amber, priority: 1,
        title: `Importa scontrini di oggi`,
        body: lastDate ? `Ultimo import: ${new Date(lastDate + "T12:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })}` : "Nessun import ancora effettuato",
        action: () => onNavigate({ view: "vendite" }),
      });
    }

    // Sort per priorità
    Object.keys(t).forEach(k => t[k].sort((a, b) => a.priority - b.priority));
    return t;
  }, [state, onNavigate]);

  const roleTasks = tasks[role] || [];
  const totalTasks = Object.values(tasks).reduce((s, arr) => s + arr.length, 0);
  const myTaskCount = roleTasks.length;

  const ROLES = [
    { key: "Laboratorio", label: "Laboratorio", color: C.teal },
    { key: "Magazzino", label: "Magazzino", color: C.amber },
    { key: "Cucina", label: "Cucina", color: C.terra },
    { key: "Amministrazione", label: "Amministrazione", color: C.ink },
  ];

  return <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, marginBottom: 24, overflow: "hidden" }}>
    <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: collapsed ? "none" : `1px solid ${C.ruleSoft}`, background: C.paperDeep }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Calendar size={18} color={C.ink} strokeWidth={1.6} />
        <div>
          <div className="f-display" style={{ fontSize: 15, color: C.ink, fontWeight: 500 }}>La giornata di oggi</div>
          <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{myTaskCount === 0 ? "Niente da fare per il tuo ruolo, ottimo!" : `${myTaskCount} task per ${role} · ${totalTasks} in totale su tutti i ruoli`}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        {ROLES.map(r => {
          const count = tasks[r.key].length;
          const urgent = tasks[r.key].filter(t => t.priority <= 0).length;
          const isSel = role === r.key;
          return <button key={r.key} onClick={() => setRole(r.key)} className="tx" style={{ padding: "6px 12px", background: isSel ? r.color : C.white, color: isSel ? C.white : C.inkSoft, border: `1px solid ${isSel ? r.color : C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 500, display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
            {r.label}
            {count > 0 && <span className="f-mono" style={{ fontSize: 9, padding: "1px 5px", background: isSel ? "rgba(255,255,255,0.25)" : (urgent > 0 ? C.terra : C.paperDeep), color: isSel ? C.white : (urgent > 0 ? C.white : C.inkSoft), borderRadius: 8, fontWeight: 600 }}>{count}</span>}
          </button>;
        })}
        <IconBtn icon={collapsed ? ChevronDown : X} size={14} onClick={() => setCollapsed(v => !v)} title={collapsed ? "Espandi" : "Riduci"} />
      </div>
    </div>

    {!collapsed && <div style={{ padding: "14px 20px" }}>
      {roleTasks.length === 0 ? <div style={{ padding: "24px 0", textAlign: "center" }}>
        <CheckCircle2 size={28} color={C.olive} strokeWidth={1.4} style={{ marginBottom: 8 }} />
        <div className="f-body" style={{ fontSize: 13, color: C.inkSoft, fontWeight: 500 }}>Niente da fare oggi per {role}</div>
        <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Nessuna attività in sospeso. Se aspetti nuove richieste verranno qui.</div>
      </div> : <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
        {roleTasks.map((task, i) => <button key={i} onClick={task.action} className="tx" style={{ padding: "10px 14px", background: C.paperDeep, border: `1px solid ${C.ruleSoft}`, borderLeft: `3px solid ${task.color}`, borderRadius: 2, cursor: "pointer", textAlign: "left", display: "flex", gap: 12, alignItems: "flex-start", fontFamily: "inherit" }}
          onMouseEnter={e => e.currentTarget.style.background = C.white}
          onMouseLeave={e => e.currentTarget.style.background = C.paperDeep}>
          <task.icon size={16} color={task.color} strokeWidth={1.8} style={{ marginTop: 1, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500, lineHeight: 1.3 }}>{task.title}</div>
            <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{task.body}</div>
          </div>
          <ChevronRight size={14} color={C.muted} style={{ flexShrink: 0, marginTop: 2 }} />
        </button>)}
      </div>}
    </div>}
  </div>;
}

// REGIA FASE 2 — cruscotto Mac nel linguaggio Regia (spec voya-regia-dash-spec):
// UN numero domina (incasso di oggi) + curva 14 giorni + racconto cronologico.
// Stesso tema della board tablet (voya_regia_theme_v1: chi sceglie lo scuro sul
// tablet lo ritrova qui). Onestà del dato: il cruscotto racconta il giorno DOPO
// l'import degli scontrini (mai numeri "live"), niente food cost giornaliero
// (non lo calcoliamo → non lo mostriamo). Dati da _rgDashData (regole EC
// identiche a VenditeStatistiche, 18/18 test).
function DashboardRegia({ state, setView, onlineCount }) {
  const [rgTheme, setRgThemeState] = useState(_rgThemeGet);
  const toggleTheme = () => setRgThemeState(t => { const n = t === "scuro" ? "chiaro" : "scuro"; _rgThemeSet(n); return n; });
  const [feedAll, setFeedAll] = useState(false);
  const T = RG_THEMES[rgTheme] || RG_THEMES.scuro;
  const today = todayLocalISO();
  const D = useMemo(() => _rgDashData(state.tableReceipts, today), [state.tableReceipts, today]);
  const lastImportMs = useMemo(() => (state.tableReceipts || []).reduce((mx, r) => {
    const ms = r && r.closedAt ? Date.parse(r.closedAt) : NaN;
    return Number.isFinite(ms) && ms > mx ? ms : mx;
  }, 0), [state.tableReceipts]);
  const tonight = useMemo(() => {
    const pending = reservationCore.listReservationsForDay(state.reservations, today)
      .filter(_resvIsActiveStatus).filter(r => r.status !== "seated");
    return { covers: pending.reduce((s, r) => s + (Number(r.covers) || 0), 0), count: pending.length };
  }, [state.reservations, today]);
  const trendPct = D.avg14 > 0 && D.today.revenue > 0 ? Math.round(((D.today.revenue - D.avg14) / D.avg14) * 100) : null;
  const maxDay = Math.max(1, ...D.days.map(d => d.revenue));
  const feed = feedAll ? D.feed : D.feed.slice(0, 8);
  const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
  const card = { background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: "16px 18px" };
  const kick = { fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: T.textMuted, fontWeight: 600 };
  const CURVE_H = 140;
  const avgH = D.avg14 > 0 ? Math.min(CURVE_H, (D.avg14 / maxDay) * CURVE_H) : null;
  return <div className="fade-in" style={{ background: T.appBg, border: `1px solid ${T.line}`, borderRadius: 8, padding: "18px 20px 22px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
      <span className="f-mono" style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: T.accentOnline, fontWeight: 700 }}>◉ Regia · Giornata</span>
      <span className="f-body" style={{ fontSize: 12, color: T.textSecondary }}>{new Date().toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })}</span>
      <div style={{ flex: 1 }} />
      {lastImportMs > 0 && <span className="f-mono" style={{ fontSize: 10, color: T.textMuted }} title="Il cruscotto racconta il giorno dopo l'import degli scontrini: mai numeri in diretta">
        aggiornato all'ultimo scontrino · {new Date(lastImportMs).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
      </span>}
      <button className="tx" onClick={toggleTheme} aria-label="Cambia tema chiaro/scuro" title={rgTheme === "scuro" ? "Passa al tema chiaro" : "Passa al tema scuro"}
        style={{ minWidth: 36, minHeight: 36, borderRadius: 8, cursor: "pointer", fontSize: 15, background: T.surfaceRaised, color: T.textPrimary, border: `1px solid ${T.lineStrong}` }}>
        {rgTheme === "scuro" ? "☀" : "☾"}
      </button>
    </div>
    {/* hero: il numero domina, la curva racconta la tendenza */}
    <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1fr) minmax(300px, 1.1fr)", gap: 22, marginBottom: 16 }}>
      <div style={{ ...card, cursor: "pointer" }} onClick={() => setView("vendite")} title="Apri Vendite & Statistiche">
        <div className="f-mono" style={kick}>Incasso di oggi</div>
        <div className="f-display" style={{ fontSize: 64, fontWeight: 600, letterSpacing: "-0.02em", color: T.textPrimary, lineHeight: 1.05, margin: "6px 0 2px" }}>
          {D.today.revenue > 0 ? fmt.eur(D.today.revenue) : "—"}
        </div>
        {trendPct != null && <div className="f-body" style={{ fontSize: 12.5, fontWeight: 600, color: trendPct >= 0 ? T.stateSeatedBd : T.stateConflictBd }}>
          {trendPct >= 0 ? `▲ +${trendPct}%` : `▼ ${trendPct}%`} vs media 14 giorni
        </div>}
        <div className="f-body" style={{ fontSize: 13, color: T.textSecondary, marginTop: 6 }}>
          {D.today.covers > 0 ? <>{D.today.covers} coperti · ticket {fmt.eur(D.avgTicket)}</> : "nessuno scontrino-tavolo importato oggi"}
          {D.today.bancoCount > 0 && <> · banco {fmt.eur(D.today.bancoRevenue)} ({D.today.bancoCount})</>}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {[["☀ Pranzo", D.today.pranzo], ["🌙 Cena", D.today.cena]].map(([lbl, s]) => <div key={lbl}
            style={{ flex: 1, minWidth: 130, padding: "8px 12px", borderRadius: 8, background: T.surfaceRaised, border: `1px solid ${T.line}` }}>
            <div className="f-mono" style={{ fontSize: 9.5, letterSpacing: "0.14em", textTransform: "uppercase", color: T.textMuted }}>{lbl}</div>
            <div className="f-mono" style={{ fontSize: 20, fontWeight: 700, color: T.textPrimary, marginTop: 2 }}>{s.revenue > 0 ? fmt.eur(s.revenue) : "—"}</div>
            <div className="f-body" style={{ fontSize: 11, color: T.textSecondary }}>{s.covers} coperti · {s.receipts} scontrini</div>
          </div>)}
        </div>
      </div>
      <div style={{ ...card, cursor: "pointer" }} onClick={() => setView("vendite")} title="Apri Vendite & Statistiche">
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <div className="f-mono" style={kick}>Ultimi 14 giorni</div>
          {D.avg14 > 0 && <div className="f-mono" style={{ fontSize: 10, color: T.textMuted }}>media {fmt.eur(D.avg14)}</div>}
        </div>
        <div style={{ position: "relative", height: CURVE_H, marginTop: 12 }}>
          {avgH != null && <div style={{ position: "absolute", left: 0, right: 0, bottom: avgH, borderTop: `1.5px dashed ${T.lineStrong}`, pointerEvents: "none" }} />}
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: "100%" }}>
            {D.days.map(d => {
              const isToday = d.day === today;
              const h = d.revenue > 0 ? Math.max(6, (d.revenue / maxDay) * CURVE_H) : 3;
              return <div key={d.day} title={`${d.day} · ${d.revenue > 0 ? fmt.eur(d.revenue) : "nessun dato"} · ${d.covers} coperti`}
                style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", height: "100%" }}>
                <div style={{ height: h, borderRadius: "3px 3px 0 0", background: isToday ? T.accentOnline : T.stateBookedBd, opacity: isToday ? 1 : d.revenue > 0 ? 0.42 : 0.14 }} />
              </div>;
            })}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          {D.days.map(d => <div key={d.day} className="f-mono" style={{ flex: 1, textAlign: "center", fontSize: 8.5, color: d.day === today ? T.accentOnline : T.textMuted, fontWeight: d.day === today ? 700 : 400 }}>{d.day.slice(8)}</div>)}
        </div>
      </div>
    </div>
    {/* riga 2: il racconto + la colonna di servizio */}
    <div style={{ display: "grid", gridTemplateColumns: "1.55fr 1fr", gap: 16 }}>
      <div style={card}>
        <div className="f-mono" style={{ ...kick, marginBottom: 10 }}>Il racconto di oggi{D.feed.length > 0 ? ` · ${D.feed.length} scontrini` : ""}</div>
        {D.feed.length === 0
          ? <div className="f-body" style={{ fontSize: 12.5, color: T.textMuted, fontStyle: "italic" }}>
              Ancora nessuno scontrino di oggi: il racconto si riempie con l'import da TCPOS.
            </div>
          : <>
            {feed.map((f, i) => <div key={i} title={f.excluded ? "Estratto conto riconciliato: già contato nello scontrino fiscale collegato" : undefined}
              style={{ display: "flex", alignItems: "center", gap: 10, height: 40, borderBottom: i < feed.length - 1 ? `1px solid ${T.line}` : "none", opacity: f.excluded ? 0.45 : 1 }}>
              <span className="f-mono" style={{ fontSize: 13, fontWeight: 700, color: T.accentOnline, width: 44, flexShrink: 0 }}>{fmtTime(f.closedAt)}</span>
              <span className="f-body" style={{ fontSize: 13.5, color: f.table === "Banco" ? T.textMuted : T.textPrimary, flex: 1 }}>
                {f.table === "Banco" ? "Banco" : `Tav ${f.table}`}{f.excluded ? " · EC riconciliato" : ""}
              </span>
              <span className="f-mono" style={{ fontSize: 12, color: T.textSecondary, width: 34, textAlign: "right" }}>{f.covers > 0 ? `${f.covers}p` : "—"}</span>
              <span style={{ width: 22, textAlign: "center", fontSize: 12 }}>{f.shift === "pranzo" ? "☀" : f.shift === "cena" ? "🌙" : ""}</span>
              <span className="f-mono" style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, width: 80, textAlign: "right" }}>{fmt.eur(f.total)}</span>
            </div>)}
            {D.feed.length > 8 && <button className="tx f-mono" onClick={() => setFeedAll(v => !v)}
              style={{ marginTop: 10, padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", background: "transparent", color: T.textSecondary, border: `1px solid ${T.lineStrong}` }}>
              {feedAll ? "Mostra meno" : `Vedi tutti (${D.feed.length})`}
            </button>}
          </>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ ...card, cursor: "pointer", border: `1px solid ${onlineCount > 0 ? T.accentOnline : T.line}` }} onClick={() => setView("agenda")} title="Apri l'Agenda">
          <div className="f-mono" style={kick}>Stasera in arrivo</div>
          <div className="f-display" style={{ fontSize: 30, fontWeight: 600, color: T.textPrimary, margin: "4px 0 0" }}>
            {tonight.covers} <span className="f-body" style={{ fontSize: 13, fontWeight: 400, color: T.textSecondary }}>coperti attesi · {tonight.count} prenotazioni</span>
          </div>
          {onlineCount > 0 && <div className="f-mono" style={{ marginTop: 8, fontSize: 11.5, fontWeight: 700, color: T.textOnAccent, background: T.accentOnline, padding: "6px 10px", borderRadius: 6, display: "inline-block" }}>
            {onlineCount === 1 ? "1 richiesta online in attesa" : `${onlineCount} richieste online in attesa`} → Agenda
          </div>}
        </div>
        {D.today.orphanEC > 0 && <div className="f-body" style={{ fontSize: 11.5, color: T.textMuted, padding: "0 4px" }}>
          {fmt.eur(D.today.orphanEC)} di estratti conto in sospeso, da riconciliare in Vendite &amp; Statistiche.
        </div>}
      </div>
    </div>
    <div style={{ marginTop: 14, textAlign: "right" }}>
      <button className="tx f-mono" onClick={() => setView("vendite")}
        style={{ padding: "9px 16px", borderRadius: 8, cursor: "pointer", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", background: "transparent", color: T.textSecondary, border: `1px solid ${T.lineStrong}` }}>
        Approfondisci in Vendite &amp; Statistiche →
      </button>
    </div>
  </div>;
}

function Dashboard({ state, setView, notifications, onNavigate, onlineCount }) {
  // REGIA FASE 2 — bivio Classico/Regia, scelto e ricordato PER DISPOSITIVO
  // (localStorage, mai sincronizzato): il cruscotto Regia è AFFIANCATO, il
  // quadro classico lab/magazzino resta intatto a un tocco.
  const [dashViz, setDashVizState] = useState(() => {
    try { return localStorage.getItem("voya_dash_viz_v1") === "regia" ? "regia" : "classico"; }
    catch { return "classico"; }
  });
  const setDashViz = (v) => {
    setDashVizState(v);
    try { localStorage.setItem("voya_dash_viz_v1", v); } catch { /* private mode */ }
  };
  const activeOrders = state.workOrders.filter(o => o.status === "in_corso");
  const urgentKRs = state.kitchenRequests.filter(kr => kr.status === "in_attesa" && kr.urgency === "urgente");
  // Fix #6: card statistiche acquisti/magazzino. Memoizzate per evitare ricalcolo
  // ad ogni render del componente (entrambe iterano su tutti i lotti/invoices).
  const expiryRisk = useMemo(() => engine.stats.expiryRiskValue(state, 14), [state.lots, state.tableReceipts, state.dishesReal, state.baseProducts]);
  const abcSuppliers = useMemo(() => engine.stats.abcSuppliers(state, 90), [state.invoices, state.receipts, state.suppliers]);
  const expiryRiskTotal = expiryRisk.reduce((s, r) => s + r.totalEur, 0);
  // Fix #5 (Patch A3): valore lotti calcolato con CMP (snapshot lot.unitCost).
  // Backward compat: lotti pre-fix risolti via fallback al DDT linkato e poi a bp.pFatt.
  const totalLotValue = state.lots.reduce((sum, l) => {
    if (l.status === "esaurito") return sum;
    const qty = Number(l.qtyRemaining) || 0;
    if (qty <= 0) return sum;
    // 1) preferisci unitCost snapshot del lotto
    let cost = Number(l.unitCost);
    if (!Number.isFinite(cost) || cost <= 0) cost = Number(l.unitCostReal);
    // 2) fallback su pricePerUnit del DDT (lotti pre-fix #5)
    if (!Number.isFinite(cost) || cost <= 0) {
      const rec = state.receipts.find(r => r.id === l.receiptId);
      const line = rec?.lines.find(ln => ln.itemId === l.itemId);
      cost = line ? (Number(line.pricePerUnit) || 0) : 0;
    }
    // 3) ultimo fallback: prezzo corrente baseProduct
    if (!Number.isFinite(cost) || cost <= 0) {
      const bp = (state.baseProducts || []).find(b => b.id === l.itemId);
      cost = bp ? (Number(bp.pFatt) || 0) : 0;
    }
    return sum + qty * cost;
  }, 0);
  // Solo articoli che hanno avuto attività (un lotto, un'ordine, un ricevimento) E sono sotto scorta
  const itemIdsWithHistory = new Set([
    ...state.lots.map(l => l.itemId),
    ...state.purchaseOrders.flatMap(po => po.lines.map(ln => ln.itemId)),
    ...state.receipts.flatMap(r => r.lines.map(ln => ln.itemId)),
  ]);
  const lowStockItems = engine.getItems(state).filter(it =>
    itemIdsWithHistory.has(it.id) && engine.itemStock(state, it.id) < it.minStock
  );
  const openPO = state.purchaseOrders.filter(p => !p.deletedAt && (p.status === "inviato" || p.status === "parziale")).length;
  const poToReceive = state.purchaseOrders.filter(p => !p.deletedAt && p.status === "ricevuto" && !p.receiptId).length;
  const criticalNotifs = notifications.filter(n => n.severity === "critical");

  // Fix L1 (Bug 3): yieldData NON puo' essere hardcoded. Calcolo reale per
  // tipologia di workOrder dagli ultimi 90 giorni (vedi engine.stats.labYieldByType).
  // Se n < 3 → fallback=true → label "Dati insufficienti", barra grigia.
  const yieldData = useMemo(() => {
    const types = [
      { key: "pesce", label: "Pesce" },
      { key: "pane", label: "Pane" },
      { key: "pasta", label: "Pasta" },
    ];
    return types.map(t => {
      const stat = engine.stats.labYieldByType(state, t.key, 90);
      return {
        name: t.label,
        resa: stat.fallback ? 0 : Math.round(stat.median * 100),
        n: stat.n,
        fallback: stat.fallback,
      };
    });
  }, [state.workOrders, state.lots]);
  const yieldHasData = yieldData.some(d => !d.fallback);

  return <div className="fade-up">
    <PageHeader
      kicker={new Date().toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
      title="Quadro della giornata"
      subtitle="Sintesi interconnessa di laboratorio, magazzino e cucina. Tutti i blocchi sono cliccabili per approfondire." />

    {/* REGIA FASE 2 — bivio di vista: Regia (incasso+curva+racconto) o Classico */}
    <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
      {[["regia", "◉ Regia"], ["classico", "Classico"]].map(([k, lbl]) =>
        <button key={k} className="tx f-mono" onClick={() => setDashViz(k)}
          style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", padding: "6px 14px", borderRadius: 2, cursor: "pointer",
            background: dashViz === k ? C.ink : C.white, color: dashViz === k ? C.paper : C.inkMid, border: `1px solid ${dashViz === k ? C.ink : C.rule}` }}>
          {lbl}
        </button>)}
    </div>

    {dashViz === "regia" && <DashboardRegia state={state} setView={setView} onlineCount={onlineCount || 0} />}

    {dashViz === "classico" && <>
    <MyDayPanel state={state} onNavigate={onNavigate} />

    {criticalNotifs.length > 0 && <div className="fade-up" style={{ background: C.terra, color: C.white, padding: "14px 20px", borderRadius: 2, marginBottom: 24, display: "flex", alignItems: "center", gap: 16 }}>
      <AlertTriangle size={20} strokeWidth={1.8} className="pulsing" />
      <div style={{ flex: 1 }}>
        <div className="f-body" style={{ fontSize: 13, fontWeight: 600 }}>{criticalNotifs.length} avviso critico richiede attenzione immediata</div>
        <div className="f-body" style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{criticalNotifs[0].title}{criticalNotifs.length > 1 && ` e altri ${criticalNotifs.length - 1}`}</div>
      </div>
      <Btn size="sm" variant="subtle" onClick={() => onNavigate(criticalNotifs[0].action)} style={{ background: C.white, color: C.terra, border: "1px solid white" }}>Apri</Btn>
    </div>}

    <div className="stagger" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 32 }}>
      <StatCard kicker="Ordini produzione" value={activeOrders.length} unit="in corso" trend={`${state.workOrders.filter(o => o.status === "completato").length} completati oggi`} accent={C.terra} icon={Factory} onClick={() => setView("produzione")} />
      <StatCard kicker="Valore stoccato" value={fmt.eurShort(totalLotValue)} unit="materia prima" trend={`${state.lots.filter(l => l.status !== "esaurito").length} lotti attivi`} accent={C.teal} icon={Warehouse} onClick={() => setView("magazzino")} />
      <StatCard kicker="Richieste cucina" value={state.kitchenRequests.filter(kr => kr.status === "in_attesa").length} unit="in attesa" trend={urgentKRs.length > 0 ? `${urgentKRs.length} URGENTI` : "da evadere"} accent={urgentKRs.length > 0 ? C.terra : C.amber} icon={ChefHat} onClick={() => setView("richieste")} />
      <StatCard kicker="Da riordinare" value={lowStockItems.length} unit="articoli" trend={lowStockItems.length > 0 ? "azione richiesta" : "scorte OK"} accent={lowStockItems.length > 0 ? C.terra : C.olive} icon={ShoppingCart} onClick={() => setView("acquisti")} />
    </div>

    {state.baseProducts && state.baseProducts.length > 0 && state.dishesReal && state.dishesReal.length > 0 && <div className="fade-up" style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 18, borderRadius: 2, marginBottom: 32, display: "flex", alignItems: "center", gap: 18 }}>
      <div style={{ width: 48, height: 48, background: C.tealWash, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, flexShrink: 0 }}>
        <Activity size={22} color={C.teal} strokeWidth={1.6} />
      </div>
      <div style={{ flex: 1 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.teal, letterSpacing: "0.2em", marginBottom: 2 }}>FOOD COST REALE ATTIVO</div>
        <div className="f-display" style={{ fontSize: 16, color: C.ink, fontWeight: 500 }}>{state.dishesReal.length} piatti · {state.baseProducts.length} prodotti base tracciati con calo reale</div>
        <div className="f-body" style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>I prezzi al kg sono calcolati al netto dei cali di pulizia/lavorazione. Al variare dei prezzi fattura il food cost si aggiorna a cascata.</div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Btn size="sm" variant="ghost" icon={Activity} onClick={() => setView("foodcost")}>Costruzione</Btn>
        <Btn size="sm" variant="accent" icon={Utensils} onClick={() => setView("piatti")}>Piatti</Btn>
      </div>
    </div>}

    {/* ── Fix #6: card statistiche acquisti/magazzino ───────────────────────────
        - "A rischio scarto 14gg" mostrata solo se eccesso valorizzato >= 10€.
        - "Concentrazione fornitori" nascosta se <3 fornitori (fallback=true). */}
    {(expiryRiskTotal >= 10 || (!abcSuppliers.fallback && abcSuppliers.totalSpend > 0)) && (
      <div className="stagger" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 32 }}>
        {expiryRiskTotal >= 10 && (
          <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 18, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", textTransform: "uppercase" }}>A rischio scarto · 14gg</div>
            <div className="f-display" style={{ fontSize: 26, fontWeight: 600, color: C.terra, marginTop: 4, letterSpacing: "-0.01em" }}>{fmt.eur(expiryRiskTotal)}</div>
            <div className="f-body" style={{ fontSize: 12, color: C.inkMid, marginTop: 4 }}>{expiryRisk.length} articol{expiryRisk.length === 1 ? "o" : "i"} con eccesso vs consumo atteso</div>
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 3 }}>
              {expiryRisk.slice(0, 3).map(r => (
                <div key={r.bpId} className="f-body" style={{ fontSize: 11, color: C.inkSoft }}>
                  <span style={{ fontWeight: 500 }}>{r.name}</span>: {fmt.eur(r.totalEur)} <span style={{ color: C.muted }}>({r.lots.length} lott{r.lots.length === 1 ? "o" : "i"})</span>
                </div>
              ))}
              {expiryRisk.length > 3 && (
                <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>+ altri {expiryRisk.length - 3}</div>
              )}
            </div>
            <div className="f-body" style={{ fontSize: 10, color: C.muted, marginTop: 10, fontStyle: "italic" }}>
              Stima basata su consumo medio storico × giorni residui. Lotti pre-fix valorizzati al prezzo corrente.
            </div>
          </div>
        )}
        {!abcSuppliers.fallback && abcSuppliers.totalSpend > 0 && (
          <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 18, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", textTransform: "uppercase" }}>Concentrazione fornitori · 90gg</div>
            <div className="f-display" style={{ fontSize: 26, fontWeight: 600, color: abcSuppliers.hhi > 0.30 ? C.amberDeep : C.ink, marginTop: 4, letterSpacing: "-0.01em" }}>HHI {abcSuppliers.hhi.toFixed(2)}</div>
            <div className="f-body" style={{ fontSize: 12, color: C.inkMid, marginTop: 4 }}>
              {abcSuppliers.hhi < 0.15 ? "Spesa distribuita su più fornitori" : abcSuppliers.hhi < 0.30 ? "Concentrazione normale" : "Concentrazione alta — rischio dipendenza"}
            </div>
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 3 }}>
              {abcSuppliers.suppliers.slice(0, 3).map(s => (
                <div key={s.id} className="f-body" style={{ fontSize: 11, color: C.inkSoft }}>
                  <span style={{ fontWeight: 500 }}>{s.name}</span>: {(s.share * 100).toFixed(0)}% <span className="f-mono" style={{ color: C.muted }}>· classe {s.class}</span>
                </div>
              ))}
            </div>
            {abcSuppliers.singleSourceBpCount > 0 && (
              <div className="f-body" style={{ fontSize: 11, color: C.terra, marginTop: 8, fontWeight: 600 }}>
                {abcSuppliers.singleSourceBpCount} articol{abcSuppliers.singleSourceBpCount === 1 ? "o" : "i"} da fornitore unico
              </div>
            )}
            <div className="f-body" style={{ fontSize: 10, color: C.muted, marginTop: 10, fontStyle: "italic" }}>
              HHI = somma dei quadrati delle quote. Soglie OCSE: &lt;0.15 distribuita, 0.15–0.30 normale, &gt;0.30 concentrata.
            </div>
          </div>
        )}
      </div>
    )}

    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24, marginBottom: 32 }}>
      <div>
        <SectionHeader title="Produzione in corso" action={<Btn variant="ghost" size="sm" icon={ChevronRight} onClick={() => setView("produzione")}>Tutte</Btn>} />
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {activeOrders.length > 0 ? activeOrders.map(o => <OrderRow key={o.id} order={o} compact onClick={() => onNavigate({ view: "order", orderId: o.id })} />)
            : <EmptyState icon={Factory} title="Nessun ordine in corso" body="Avvia una nuova lavorazione dalla sezione Produzione" action={<Btn variant="accent" size="sm" icon={Plus} onClick={() => onNavigate({ view: "produzione", new: true })}>Nuovo ordine</Btn>} />}
        </div>

        {poToReceive > 0 && <div style={{ marginTop: 24 }}>
          <SectionHeader title="Da registrare" action={<Btn variant="ghost" size="sm" icon={ChevronRight} onClick={() => setView("ingresso")}>Apri</Btn>} />
          <div style={{ background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, padding: 16, display: "flex", alignItems: "center", gap: 14 }}>
            <Truck size={20} color={C.amberDeep} />
            <div style={{ flex: 1 }}>
              <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{poToReceive} ordine{poToReceive > 1 ? "i" : ""} consegnato{poToReceive > 1 ? "i" : ""}</div>
              <div className="f-body" style={{ fontSize: 12, color: C.inkSoft }}>Registra la bolla di ingresso per tracciare i lotti</div>
            </div>
            <Btn size="sm" variant="primary" icon={ArrowRight} onClick={() => setView("ingresso")}>Registra</Btn>
          </div>
        </div>}
      </div>

      <div>
        <SectionHeader title="Bacheca" action={<Btn variant="ghost" size="sm" icon={ChevronRight} onClick={() => setView("bacheca")}>Apri</Btn>} />
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[...state.messages].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)).slice(0, 3).map(m => <MessageCard key={m.id} message={m} compact />)}
        </div>
      </div>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24, marginBottom: 32 }}>
      <div>
        <SectionHeader title="Rese medie per tipologia" subtitle="kg uscita / kg entrata — ultimi 90 giorni" />
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2 }}>
          {yieldHasData ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={yieldData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke={C.rule} vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "Geist", fill: C.inkSoft }} axisLine={{ stroke: C.rule }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fontFamily: "JetBrains Mono", fill: C.muted }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
                  <Tooltip
                    contentStyle={{ background: C.paper, border: `1px solid ${C.ink}`, borderRadius: 2, fontFamily: "Geist", fontSize: 12 }}
                    formatter={(value, _name, p) => {
                      const row = p && p.payload;
                      if (row && row.fallback) return ["n insuff.", "Resa"];
                      return [`${value}%`, `Resa (n=${row?.n ?? 0})`];
                    }}
                  />
                  <Bar dataKey="resa" radius={[2, 2, 0, 0]}>
                    {yieldData.map((d, i) => (
                      <Cell key={i} fill={d.fallback ? C.rule : C.teal} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="f-mono" style={{ display: "flex", justifyContent: "space-around", marginTop: 6, fontSize: 10, color: C.muted, letterSpacing: "0.1em" }}>
                {yieldData.map(d => (
                  <span key={d.name}>{d.fallback ? "n insuff." : `n=${d.n}`}</span>
                ))}
              </div>
              <div className="f-body" style={{ fontSize: 10, color: C.muted, marginTop: 8, fontStyle: "italic" }}>
                Mediana resa effettiva (kg uscita / kg entrata) dai lotti figli completati negli ultimi 90 giorni.
              </div>
            </>
          ) : (
            <div style={{ padding: "32px 12px", textAlign: "center" }}>
              <Factory size={28} color={C.muted} style={{ marginBottom: 10 }} />
              <div className="f-body" style={{ fontSize: 13, color: C.inkSoft, fontWeight: 500 }}>Dati insufficienti</div>
              <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                Servono almeno 3 lavorazioni completate per tipologia.<br />
                Completa qualche ordine in Laboratorio per vedere le rese reali.
              </div>
            </div>
          )}
        </div>
      </div>
      <div>
        <SectionHeader title="Scadenze prossime" />
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {state.lots.filter(l => l.status !== "esaurito").sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt)).slice(0, 5).map(l => {
            const item = engine.getItems(state).find(i => i.id === l.itemId);
            const daysLeft = daysBetween(TODAY, l.expiresAt);
            const urgent = daysLeft <= 2;
            return <button key={l.id} onClick={() => onNavigate({ view: "lot", lotId: l.id })} className="tx" style={{ background: C.white, border: `1px solid ${C.rule}`, padding: "10px 14px", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = C.ink}
              onMouseLeave={e => e.currentTarget.style.borderColor = C.rule}>
              <div>
                <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{item?.name}</div>
                <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.1em" }}>{l.id}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="f-mono" style={{ fontSize: 13, color: urgent ? C.terra : C.ink, fontWeight: 600 }}>{daysLeft <= 0 ? "OGGI" : `${daysLeft}g`}</div>
                <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>{fmt.date(l.expiresAt)}</div>
              </div>
            </button>;
          })}
        </div>
      </div>
    </div>

    {lowStockItems.length > 0 && <>
      <SectionHeader title="Articoli sotto scorta minima" subtitle="Crea un ordine d'acquisto al fornitore preferito" />
      <div style={{ background: C.white, border: `1px solid ${C.terra}`, borderRadius: 2, overflow: "hidden" }}>
        {lowStockItems.map(it => {
          const stock = engine.itemStock(state, it.id);
          const pref = state.suppliers.find(s => s.id === it.preferredSupplier);
          return <div key={it.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderBottom: `1px solid ${C.ruleSoft}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <AlertTriangle size={16} color={C.terra} />
              <div>
                <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{it.name}</div>
                <div className="f-mono" style={{ fontSize: 11, color: C.muted }}>{cats[it.category]?.label}{pref && ` · Fornitore: ${pref.name}`}</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <div style={{ textAlign: "right" }}>
                <div className="f-mono" style={{ fontSize: 12, color: C.terra, fontWeight: 600 }}>{stock.toFixed(1)} {it.unit}</div>
                <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>min: {it.minStock} {it.unit}</div>
              </div>
              <Btn size="sm" variant="primary" icon={ShoppingCart} onClick={() => onNavigate({ view: "acquisti", new: true, itemId: it.id, supplierId: it.preferredSupplier })}>Ordina</Btn>
            </div>
          </div>;
        })}
      </div>
    </>}
    </>}
  </div>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      BACHECA                                               ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function Bacheca({ state, setState }) {
  const [text, setText] = useState("");
  const [author, setAuthor] = useState(state.currentUser.name);
  const [role, setRole] = useState(state.currentUser.role);
  const toast = useToast();

  const post = () => {
    if (!text.trim()) return;
    const msg = { id: uid("m_"), author, role, text: text.trim(), pinned: false, at: nowIso() };
    setState({ ...state, messages: [msg, ...state.messages] });
    setText("");
    toast({ type: "success", message: "Messaggio pubblicato in bacheca" });
  };
  const togglePin = (id) => setState({ ...state, messages: state.messages.map(m => m.id === id ? { ...m, pinned: !m.pinned } : m) });
  const del = (id) => { setState({ ...state, messages: state.messages.filter(m => m.id !== id) }); toast({ type: "info", message: "Messaggio eliminato" }); };

  const sorted = [...state.messages].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return new Date(b.at) - new Date(a.at);
  });

  return <div className="fade-up">
    <PageHeader kicker="Comunicazione interna" title="Bacheca"
      subtitle="Canale di scambio tra laboratorio, magazzino e cucina. Fissa i messaggi importanti, annota cambi di turno, avvisi scorte." />
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
      <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {sorted.length === 0 ? <EmptyState icon={MessageSquare} title="Bacheca vuota" body="Pubblica il primo messaggio per tutti" /> : sorted.map(m => <MessageCard key={m.id} message={m} onPin={() => togglePin(m.id)} onDelete={() => del(m.id)} />)}
      </div>
      <div>
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2, position: "sticky", top: 20 }}>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: C.muted, textTransform: "uppercase", marginBottom: 16 }}>Nuovo messaggio</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="Autore">
              <Select value={author} onChange={e => { const u = state.users.find(u => u.name === e.target.value); setAuthor(u.name); setRole(u.role); }}>
                {state.users.map(u => <option key={u.id} value={u.name}>{u.name} — {u.role}</option>)}
              </Select>
            </Field>
            <Field label="Messaggio"><Textarea value={text} onChange={e => setText(e.target.value)} placeholder="Scrivi un avviso per il team…" /></Field>
            <Btn variant="primary" icon={Send} onClick={post} disabled={!text.trim()}>Pubblica</Btn>
          </div>
        </div>
      </div>
    </div>
  </div>;
}

function MessageCard({ message, onPin, onDelete, compact }) {
  const roleColor = roleColors[message.role] || C.muted;
  return <div style={{ background: C.white, border: `1px solid ${message.pinned ? C.ink : C.rule}`, padding: compact ? 14 : 18, borderRadius: 2, position: "relative" }}>
    {message.pinned && <div className="f-mono" style={{ position: "absolute", top: -1, left: -1, background: C.ink, color: C.paper, padding: "2px 8px", fontSize: 9, letterSpacing: "0.2em" }}>
      <Pin size={9} style={{ display: "inline", marginRight: 4, marginBottom: -1 }} /> FISSATO
    </div>}
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginTop: message.pinned ? 8 : 0 }}>
      <Avatar name={message.author} role={message.role} size={compact ? 32 : 38} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
          <span className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{message.author}</span>
          <span className="f-mono" style={{ fontSize: 9, color: roleColor, letterSpacing: "0.2em", padding: "2px 6px", background: C.paperDeep, borderRadius: 2 }}>{message.role}</span>
          <span className="f-mono" style={{ fontSize: 10, color: C.muted, marginLeft: "auto" }}>{fmt.dateTime(message.at)}</span>
        </div>
        <div className="f-body" style={{ fontSize: compact ? 13 : 14, color: C.inkSoft, lineHeight: 1.5 }}>{message.text}</div>
        {!compact && (onPin || onDelete) && <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
          {onPin && <button onClick={onPin} className="f-mono tx" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em", display: "flex", alignItems: "center", gap: 4, border: "none", background: "transparent", cursor: "pointer", padding: 0 }}><Pin size={11} /> {message.pinned ? "RIMUOVI" : "FISSA"}</button>}
          {onDelete && <button onClick={onDelete} className="f-mono tx" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em", display: "flex", alignItems: "center", gap: 4, border: "none", background: "transparent", cursor: "pointer", padding: 0 }}><Trash2 size={11} /> ELIMINA</button>}
        </div>}
      </div>
    </div>
  </div>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      ACQUISTI (Purchase Orders)                            ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function Acquisti({ state, setState, onNavigate, initial }) {
  const [showForm, setShowForm] = useState(initial?.new || false);
  const [filter, setFilter] = useState("all");
  const [waPicker, setWaPicker] = useState(null); // po selezionato per inviare WhatsApp
  // Soft-delete state — conservazione documenti (art. 2220 c.c. analogica)
  const [confirmDelPO, setConfirmDelPO] = useState(null); // { id, fornitore, total }
  const [delReasonPO, setDelReasonPO] = useState("");
  const toast = useToast();

  // Filtro liste: di default mostra solo i NON archiviati. La tab "archiviati" mostra solo quelli.
  const visibleOrders = filter === "archiviati"
    ? state.purchaseOrders.filter(p => p.deletedAt)
    : state.purchaseOrders.filter(isActive).filter(p => filter === "all" || p.status === filter);
  const filtered = visibleOrders;

  // Soft-delete: archivia l'ordine senza rimuoverlo, traccia in auditLog
  const requestDeletePO = (po) => {
    const sup = state.suppliers.find(s => s.id === po.supplierId);
    const total = (po.lines || []).reduce((s, l) => s + (l.qty || 0) * (l.priceEst || 0), 0);
    setConfirmDelPO({ id: po.id, fornitore: sup?.name || "?", total });
    setDelReasonPO("");
  };

  const confirmDeletePO = () => {
    const po = state.purchaseOrders.find(p => p.id === confirmDelPO.id);
    if (!po) { setConfirmDelPO(null); return; }
    setState(s => ({
      ...s,
      purchaseOrders: s.purchaseOrders.map(p => p.id === po.id
        ? softDelete(p, s.currentUser?.name, delReasonPO || "Nessuna motivazione")
        : p
      ),
      auditLog: appendAudit(s, {
        action: "soft_delete",
        entityType: "purchaseOrder",
        entityId: po.id,
        before: { id: po.id, supplierId: po.supplierId, status: po.status, expectedAt: po.expectedAt, lines: po.lines?.length || 0 },
        reason: delReasonPO || "Nessuna motivazione",
      }),
    }));
    toast({ type: "success", message: "Ordine archiviato (recuperabile)" });
    setConfirmDelPO(null);
  };

  // Ripristino: rimuove i marker di soft-delete e logga restore
  const restorePO = (po) => {
    setState(s => ({
      ...s,
      purchaseOrders: s.purchaseOrders.map(p => {
        if (p.id !== po.id) return p;
        const { deletedAt, deletedBy, deletedReason, ...rest } = p;
        return rest;
      }),
      auditLog: appendAudit(s, {
        action: "restore",
        entityType: "purchaseOrder",
        entityId: po.id,
      }),
    }));
    toast({ type: "success", message: "Ordine ripristinato" });
  };

  const sendPO = (id) => {
    setState({ ...state, purchaseOrders: state.purchaseOrders.map(p => p.id === id ? { ...p, status: "inviato" } : p) });
    toast({ type: "success", message: "Ordine marcato come inviato" });
  };

  const composeWhatsAppText = (po) => {
    const sup = state.suppliers.find(s => s.id === po.supplierId);
    const lines = po.lines.map(ln => {
      const item = engine.getItems(state).find(it => it.id === ln.itemId);
      return `• ${ln.qty} ${ln.unit} — ${item?.name || "?"}`;
    }).join("\n");
    // Fix #5 (Patch C2): calcolo totali separati netto / IVA / lordo per aliquota
    // IVA del prodotto base (somministrazione 10% default, vino 22%, pane 4%...).
    const defaultRate = Number(state.settings?.vatRatePurchase) || 0.10;
    let totNetto = 0;
    let totIva = 0;
    po.lines.forEach(ln => {
      const qty = parseFloat(ln.qty) || 0;
      const price = parseFloat(ln.priceEst) || 0;
      const netto = qty * price;
      if (netto <= 0) return;
      const bp = (state.baseProducts || []).find(b => b.id === ln.itemId);
      const rate = (bp && typeof bp.vatRate === "number" && Number.isFinite(bp.vatRate))
        ? bp.vatRate
        : defaultRate;
      totNetto += netto;
      totIva += netto * rate;
    });
    const totLordo = totNetto + totIva;
    const totalLine = totIva > 0
      ? `Totale stimato lordo IVA: ${fmt.eur(totLordo)} (netto ${fmt.eur(totNetto)} + IVA ${fmt.eur(totIva)})`
      : `Totale stimato: ${fmt.eur(totNetto)}`;
    return `Buongiorno ${sup?.name || ""},\n\nordine n° ${po.id} per ${fmt.date(po.expectedAt || TODAY)}:\n\n${lines}\n\n${totalLine}\n${po.notes ? `\nNote: ${po.notes}\n` : ""}\nGrazie,\nBottega del Mare · Polverigi`;
  };

  const sendWA = (po, rep) => {
    const number = rep.whatsapp.replace(/[^+\d]/g, "");
    const text = encodeURIComponent(composeWhatsAppText(po));
    window.open(`https://wa.me/${number}?text=${text}`, "_blank");
    // Se ordine è bozza lo promuoviamo a inviato
    if (po.status === "bozza") sendPO(po.id);
    setWaPicker(null);
  };

  const receivePO = (po) => {
    onNavigate({ view: "ingresso", new: true, fromPO: po.id });
    toast({ type: "info", title: "Conferma ricevimento", message: `Compila la bolla d'ingresso con i dati reali` });
  };

  return <div className="fade-up">
    <PageHeader kicker="Ordini a fornitori" title="Acquisti"
      subtitle="Crea ordini d'acquisto, inviali via WhatsApp o email e trasformali in bolle d'ingresso quando la merce arriva."
      right={<Btn variant="accent" size="lg" icon={Plus} onClick={() => setShowForm(true)}>Nuovo ordine</Btn>} />

    {showForm && <PurchaseOrderForm state={state} setState={setState} onClose={() => setShowForm(false)} initial={initial} />}
    {waPicker && <WhatsAppPicker po={waPicker} state={state} onClose={() => setWaPicker(null)} onSend={sendWA} composeText={composeWhatsAppText} />}

    {confirmDelPO && (
      <ConfirmModal
        open
        danger
        title="Archiviare l'ordine?"
        message={`Stai per archiviare l'ordine ${confirmDelPO.id} di ${confirmDelPO.fornitore} (${fmt.eur(confirmDelPO.total)}).\n\nNon verrà cancellato: rimane conservato per audit interno e tracciabilità. Lo troverai nella tab "Archiviati" e potrai ripristinarlo in qualunque momento.`}
        confirmText="Archivia"
        cancelText="Annulla"
        onConfirm={confirmDeletePO}
        onCancel={() => setConfirmDelPO(null)}
      >
        <Field label="Motivazione (consigliata)">
          <Textarea value={delReasonPO} onChange={e => setDelReasonPO(e.target.value)} rows={2} maxLength={200} placeholder="Es: doppia registrazione, fornitore errato, ..." />
        </Field>
      </ConfirmModal>
    )}

    <Tabs value={filter} onChange={setFilter} tabs={[
      { id: "all", label: "Tutti", count: state.purchaseOrders.filter(isActive).length },
      { id: "bozza", label: "Bozze", count: state.purchaseOrders.filter(isActive).filter(p => p.status === "bozza").length },
      { id: "inviato", label: "Inviati", count: state.purchaseOrders.filter(isActive).filter(p => p.status === "inviato").length },
      { id: "ricevuto", label: "Ricevuti", count: state.purchaseOrders.filter(isActive).filter(p => p.status === "ricevuto").length },
      { id: "archiviati", label: "Archiviati", count: state.purchaseOrders.filter(p => p.deletedAt).length },
    ]} />

    <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {filtered.length === 0 ? <EmptyState icon={ShoppingCart} title="Nessun ordine in questa categoria" action={<Btn variant="accent" size="sm" icon={Plus} onClick={() => setShowForm(true)}>Crea il primo ordine</Btn>} />
      : [...filtered].reverse().map(po => {
        const sup = state.suppliers.find(s => s.id === po.supplierId);
        const total = po.lines.reduce((s, l) => s + l.qty * l.priceEst, 0);
        return <div key={po.id} className="tx" style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 16, borderBottom: `1px solid ${C.ruleSoft}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 42, height: 42, background: C.paperDeep, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2 }}>
                <ShoppingCart size={18} color={C.inkSoft} strokeWidth={1.5} />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="f-mono" style={{ fontSize: 12, color: C.ink, fontWeight: 600 }}>{po.id}</span>
                  <Badge status={po.status} />
                  {po.status === "ricevuto" && po.receiptId && <span className="f-mono" style={{ fontSize: 10, color: C.olive, letterSpacing: "0.1em", padding: "1px 6px", background: C.oliveWash, borderRadius: 2 }}>✓ DDT collegato</span>}
                </div>
                <div className="f-body" style={{ fontSize: 14, color: C.ink, fontWeight: 500, marginTop: 2 }}>{sup?.name}</div>
                <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Creato {fmt.date(po.createdAt)} · Consegna {fmt.date(po.expectedAt)}</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ textAlign: "right" }}>
                <div className="f-mono" style={{ fontSize: 17, color: C.ink, fontWeight: 600 }}>{fmt.eur(total)}</div>
                <div className="f-mono" style={{ fontSize: 11, color: C.muted }}>{po.lines.length} righe</div>
              </div>
              {!po.deletedAt && po.status === "bozza" && <>
                <Btn size="sm" variant="ghost" icon={MessageSquare} onClick={() => setWaPicker(po)} title="Invia via WhatsApp">WhatsApp</Btn>
                <Btn size="sm" variant="accent" icon={Send} onClick={() => sendPO(po.id)}>Invia</Btn>
                <IconBtn icon={Trash2} title="Archivia ordine" onClick={() => requestDeletePO(po)} color={C.muted} />
              </>}
              {!po.deletedAt && po.status === "inviato" && <>
                <Btn size="sm" variant="ghost" icon={MessageSquare} onClick={() => setWaPicker(po)} title="Re-invia via WhatsApp">WhatsApp</Btn>
                <Btn size="sm" variant="primary" icon={PackagePlus} onClick={() => receivePO(po)}>Ricevi merce</Btn>
                <IconBtn icon={Trash2} title="Archivia ordine" onClick={() => requestDeletePO(po)} color={C.muted} />
              </>}
              {!po.deletedAt && po.status === "ricevuto" && !po.receiptId && <Btn size="sm" variant="accent" icon={ArrowRight} onClick={() => onNavigate({ view: "ingresso", new: true, fromPO: po.id })}>Registra bolla</Btn>}
              {!po.deletedAt && po.receiptId && <Btn size="sm" variant="ghost" icon={FileText} onClick={() => onNavigate({ view: "ingresso" })}>Vedi DDT</Btn>}
              {po.deletedAt && <>
                <div className="f-mono" style={{ fontSize: 10, color: C.muted, textAlign: "right" }}>
                  Archiviato {fmt.date(po.deletedAt)}<br/>da {po.deletedBy || "?"}
                </div>
                <Btn size="sm" variant="ghost" icon={RefreshCw} onClick={() => restorePO(po)}>Ripristina</Btn>
              </>}
            </div>
          </div>
          <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 6 }}>
            {po.lines.map((ln, i) => {
              const item = engine.getItems(state).find(it => it.id === ln.itemId);
              return <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 13 }}>
                <div className="f-body" style={{ display: "flex", alignItems: "center", gap: 10, color: C.ink }}>
                  <span style={{ width: 6, height: 6, background: cats[item?.category]?.color || C.muted, borderRadius: 6 }} />{item?.name}
                </div>
                <div className="f-mono" style={{ display: "flex", gap: 24, color: C.inkSoft, fontSize: 12 }}>
                  <span>{ln.qty} {ln.unit}</span>
                  <span>€ {ln.priceEst.toFixed(2)}/{ln.unit}</span>
                  <span style={{ color: C.ink, fontWeight: 600, minWidth: 70, textAlign: "right" }}>{fmt.eur(ln.qty * ln.priceEst)}</span>
                </div>
              </div>;
            })}
            {po.notes && <div className="f-body" style={{ fontSize: 11, color: C.muted, fontStyle: "italic", borderTop: `1px solid ${C.ruleSoft}`, paddingTop: 8, marginTop: 8 }}>📝 {po.notes}</div>}
          </div>
        </div>;
      })}
    </div>
  </div>;
}

function WhatsAppPicker({ po, state, onClose, onSend, composeText }) {
  const sup = state.suppliers.find(s => s.id === po.supplierId);
  const reps = (sup?.representatives || []).filter(r => r.whatsapp);
  const message = composeText(po);

  return <Modal onClose={onClose} maxWidth={600} title="Invia ordine via WhatsApp" subtitle={sup?.name}
    footer={<Btn variant="ghost" onClick={onClose}>Chiudi</Btn>}>
    <div style={{ padding: 20 }}>
      {reps.length === 0 ? <div style={{ padding: 20, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <AlertTriangle size={16} color={C.amberDeep} />
          <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>Nessun contatto WhatsApp</div>
        </div>
        <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.5 }}>
          Per questo fornitore non ci sono rappresentanti con un numero WhatsApp. Vai in <strong>Anagrafiche → Fornitori</strong>, modifica <strong>{sup?.name}</strong> e aggiungi almeno un contatto con numero WhatsApp (formato internazionale, es. +39 340 1234567).
        </div>
      </div> : <>
        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, marginBottom: 10 }}>SCEGLI DESTINATARIO ({reps.length})</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
          {reps.map((r, i) => <button key={i} onClick={() => onSend(po, r)} className="tx" style={{ padding: "12px 14px", background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, cursor: "pointer", textAlign: "left", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 12 }}
            onMouseEnter={e => e.currentTarget.style.background = C.tealWash}
            onMouseLeave={e => e.currentTarget.style.background = C.white}>
            <div style={{ width: 36, height: 36, background: C.oliveWash, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, flexShrink: 0 }}>
              <MessageSquare size={16} color={C.olive} />
            </div>
            <div style={{ flex: 1 }}>
              <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{r.name}</div>
              <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{r.whatsapp}{r.role && ` · ${r.role}`}</div>
            </div>
            <ChevronRight size={14} color={C.muted} />
          </button>)}
        </div>

        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, marginBottom: 8 }}>ANTEPRIMA MESSAGGIO</div>
        <div style={{ padding: 14, background: "#E5F5D4", borderRadius: 8, fontSize: 12, color: C.ink, whiteSpace: "pre-wrap", fontFamily: "Geist, sans-serif", lineHeight: 1.5, border: `1px solid ${C.olive}` }}>{message}</div>

        <div style={{ marginTop: 14, padding: 10, background: C.paperDeep, borderRadius: 2, fontSize: 11, color: C.inkSoft, lineHeight: 1.5 }}>
          Cliccando un contatto si apre WhatsApp Web/App con il messaggio pre-compilato. Potrai modificarlo prima di inviare. Se l'ordine è in bozza, cliccando "invia" viene marcato come inviato.
        </div>
      </>}
    </div>
  </Modal>;
}

function PurchaseOrderForm({ state, setState, onClose, initial }) {
  const [supplierId, setSupplierId] = useState(initial?.supplierId || state.suppliers[0]?.id || "");
  const [expectedAt, setExpectedAt] = useState(new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState(() => {
    if (initial?.itemId) {
      const item = engine.getItems(state).find(i => i.id === initial.itemId);
      const suggested = Math.ceil((item.minStock * 2) * 2) / 2; // doppio della scorta minima, arrotondato
      return [{ itemId: initial.itemId, qty: suggested, unit: item?.unit || "kg", priceEst: engine.itemUnitCost(state, initial.itemId).toFixed(2) }];
    }
    return [{ itemId: (engine.getItems(state)[0]?.id || ""), qty: "", unit: "kg", priceEst: "" }];
  });
  const toast = useToast();

  const submit = (sendNow = false) => {
    if (lines.some(l => !l.qty || !l.priceEst)) { toast({ type: "error", message: "Compila tutti i campi delle righe" }); return; }
    const n = (state.purchaseOrders.length + 1).toString().padStart(3, "0");
    const newPO = {
      id: `PO-2026-${n}`, supplierId, createdAt: new Date().toISOString().slice(0, 10), expectedAt,
      status: sendNow ? "inviato" : "bozza", notes,
      lines: lines.map(l => ({ itemId: l.itemId, qty: parseFloat(l.qty), unit: l.unit, priceEst: parseFloat(l.priceEst) })),
      receiptId: null,
    };
    setState({ ...state, purchaseOrders: [...state.purchaseOrders, newPO] });
    toast({ type: "success", title: sendNow ? "Ordine inviato" : "Ordine salvato", message: `${newPO.id} creato correttamente` });
    onClose();
  };

  const sup = state.suppliers.find(s => s.id === supplierId);
  const total = lines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.priceEst) || 0), 0);

  // Layout responsive: su viewport < 900px collassa la riga ordine su 2 righe (articolo full-width sopra, campi numerici sotto)
  const [isCompact, setIsCompact] = useState(typeof window !== "undefined" && window.innerWidth < 900);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsCompact(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Colonne grid: articolo flessibile, numerici a larghezza fissa minima → stanno tutti dentro modal 900px
  // desktop: articolo 1.6fr · qty 88px · unit 72px · €/u 96px · totale 96px · cestino 36px = ~388 + 1.6fr → rientra in 800px utili
  // mobile (isCompact): 2 righe — riga 1: articolo (1fr) + cestino (36px); riga 2: 4 campi numerici equispaziati
  const rowGridCols = isCompact
    ? "minmax(0, 1fr) 36px"
    : "minmax(180px, 1.6fr) 88px 72px 96px 96px 36px";

  return <Modal onClose={onClose} maxWidth={900} subtitle="Crea nuovo" title="Ordine a fornitore"
    footer={<>
      <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
      <Btn variant="ghost" icon={FileText} onClick={() => submit(false)}>Salva bozza</Btn>
      <Btn variant="accent" icon={Send} onClick={() => submit(true)}>Salva e invia</Btn>
    </>}>
    <div style={{ padding: 24 }}>
      <div style={{ display: "grid", gridTemplateColumns: isCompact ? "1fr" : "2fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
        <Field label="Fornitore" required>
          <Select value={supplierId} onChange={e => setSupplierId(e.target.value)}>
            {state.suppliers.map(s => <option key={s.id} value={s.id}>{s.name} — {cats[s.category]?.label}</option>)}
          </Select>
        </Field>
        <Field label="Consegna attesa" required><Input type="date" value={expectedAt} onChange={e => setExpectedAt(e.target.value)} /></Field>
        <Field label="Pagamento">
          <div className="f-mono" style={{ padding: "9px 12px", background: C.paperDeep, fontSize: 13, color: C.ink, borderRadius: 2, border: `1px solid ${C.rule}` }}>{sup?.paymentTerms || 30} giorni</div>
        </Field>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: C.muted }}>RIGHE ORDINE</div>
        <Btn variant="ghost" size="xs" icon={Plus} onClick={() => setLines([...lines, { itemId: (engine.getItems(state)[0]?.id || ""), qty: "", unit: "kg", priceEst: "" }])}>Riga</Btn>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: isCompact ? 14 : 8, marginBottom: 16 }}>
        {lines.map((ln, i) => {
          const item = engine.getItems(state).find(it => it.id === ln.itemId);
          const stock = item ? engine.itemStock(state, item.id) : 0;
          const lowStock = item && stock < item.minStock;
          const lineTotal = (parseFloat(ln.qty) || 0) * (parseFloat(ln.priceEst) || 0);

          // Sotto-blocco numerico (qty / unit / €u / totale): in compact diventa una seconda riga 4-colonne
          const numericBlock = <>
            <Field label={i === 0 ? "Quantità" : ""}>
              <Input type="number" step="0.01" value={ln.qty} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, qty: e.target.value } : l))} style={{ minWidth: 0, width: "100%", boxSizing: "border-box" }} />
            </Field>
            <Field label={i === 0 ? "Unità" : ""}>
              <Select value={ln.unit} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, unit: e.target.value } : l))} style={{ minWidth: 0, width: "100%", boxSizing: "border-box", padding: "9px 6px" }}>
                <option>kg</option><option>pz</option><option>l</option>
              </Select>
            </Field>
            <Field label={i === 0 ? "€ stim./u" : ""} hint={i === 0 ? "ultimo prezzo" : null}>
              <Input type="number" step="0.01" value={ln.priceEst} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, priceEst: e.target.value } : l))} style={{ minWidth: 0, width: "100%", boxSizing: "border-box" }} />
            </Field>
            <Field label={i === 0 ? "Totale" : ""}>
              <div className="f-mono" style={{ padding: "9px 6px", fontSize: 13, color: C.ink, fontWeight: 600, textAlign: "right", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {fmt.eur(lineTotal)}
              </div>
            </Field>
          </>;

          return <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8, padding: lowStock ? "8px 10px" : 0, background: "transparent", borderLeft: lowStock ? `2px solid ${C.terra}` : "2px solid transparent", borderRadius: 2 }}>
            <div style={{ display: "grid", gridTemplateColumns: rowGridCols, gap: 8, alignItems: "end", minWidth: 0 }}>
              <Field label={i === 0 ? "Articolo" : ""}>
                <Select value={ln.itemId} onChange={e => { const it = engine.getItems(state).find(x => x.id === e.target.value); setLines(lines.map((l, j) => j === i ? { ...l, itemId: e.target.value, unit: it?.unit || "kg", priceEst: engine.itemUnitCost(state, e.target.value).toFixed(2) } : l)); }} style={{ minWidth: 0, width: "100%", boxSizing: "border-box" }}>
                  {(() => {
                    const all = engine.getItems(state);
                    const groups = {};
                    all.forEach(it => { (groups[it.category] = groups[it.category] || []).push(it); });
                    return Object.entries(groups).map(([cat, items]) => <optgroup key={cat} label={(cats[cat]?.label || cat).toUpperCase()}>
                      {items.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
                    </optgroup>);
                  })()}
                </Select>
                {lowStock && <div className="f-mono" style={{ fontSize: 10, color: C.terra, marginTop: 4, letterSpacing: "0.05em", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <AlertTriangle size={11} strokeWidth={1.8} /> Sotto scorta ({stock.toFixed(1)}/{item.minStock} {item.unit})
                </div>}
              </Field>
              {!isCompact && numericBlock}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 36 }}>
                <IconBtn icon={Trash2} title="Rimuovi" onClick={() => lines.length > 1 && setLines(lines.filter((_, j) => j !== i))} />
              </div>
            </div>
            {isCompact && <div style={{ display: "grid", gridTemplateColumns: "1fr 70px 1fr 1fr", gap: 8, alignItems: "end", minWidth: 0 }}>
              {numericBlock}
            </div>}
          </div>;
        })}
      </div>

      <Field label="Note interne"><Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="es. Servizio weekend, contatto telefonico prima della consegna..." /></Field>

      <div style={{ marginTop: 20, padding: 16, background: C.paperDeep, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div className="f-mono" style={{ fontSize: 11, color: C.muted, letterSpacing: "0.15em" }}>TOTALE STIMATO</div>
        <div className="f-display" style={{ fontSize: 28, color: C.ink, fontWeight: 600 }}>{fmt.eur(total)}</div>
      </div>
    </div>
  </Modal>;
}


// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      INGRESSO MERCE (DDT / Bolle)                          ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function IngressoMerce({ state, setState, onNavigate, initial }) {
  const [showForm, setShowForm] = useState(initial?.new || initial?.fromPO || false);
  const toast = useToast();

  return <div className="fade-up">
    <PageHeader kicker="Documenti di trasporto · Bolle" title="Ingresso merce"
      subtitle="Registra ogni consegna trascinando la bolla in PDF. Il sistema genera automaticamente i lotti e collega l'ordine."
      right={<Btn variant="accent" size="lg" icon={PackagePlus} onClick={() => setShowForm(true)}>Nuova bolla</Btn>} />

    {showForm && <ReceiptForm state={state} setState={setState} onClose={() => setShowForm(false)} initial={initial} />}

    {state.receipts.length === 0 ? <EmptyState icon={PackagePlus} title="Nessuna bolla registrata" body="Inizia registrando il primo ingresso merce" action={<Btn variant="accent" size="sm" icon={Plus} onClick={() => setShowForm(true)}>Prima bolla</Btn>} />
    : <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {[...state.receipts].sort((a, b) => new Date(b.date) - new Date(a.date)).map(r => {
        const sup = state.suppliers.find(s => s.id === r.supplierId);
        const total = r.lines.reduce((s, l) => s + l.qty * l.pricePerUnit, 0);
        const po = r.poId ? state.purchaseOrders.find(p => p.id === r.poId) : null;
        return <div key={r.id} className="tx" style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 16, borderBottom: `1px solid ${C.ruleSoft}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 42, height: 42, background: C.tealWash, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2 }}>
                <Truck size={18} color={C.teal} strokeWidth={1.5} />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span className="f-mono" style={{ fontSize: 12, color: C.ink, fontWeight: 600 }}>{r.ddtNumber}</span>
                  {po && <span className="f-mono" style={{ fontSize: 10, color: C.teal, letterSpacing: "0.1em", padding: "1px 6px", background: C.tealWash, borderRadius: 2 }}>← {po.id}</span>}
                  {r.attachments?.length > 0 && <span className="f-mono" style={{ fontSize: 10, color: C.muted, display: "inline-flex", alignItems: "center", gap: 3 }}><FileText size={10} /> {r.attachments.length} allegato</span>}
                  {r.temperature != null && <span className="f-mono" style={{ fontSize: 10, color: C.teal, display: "inline-flex", alignItems: "center", gap: 3 }}><Thermometer size={10} /> {r.temperature}°C</span>}
                  {r.rejected && <span className="f-mono" style={{ fontSize: 10, color: C.terra, letterSpacing: "0.1em", padding: "1px 6px", background: C.terraWash, borderRadius: 2, fontWeight: 600 }}>MERCE RIFIUTATA</span>}
                  {!r.rejected && r.ncDecision && <span className="f-mono" style={{ fontSize: 10, color: C.amberDeep, letterSpacing: "0.1em", padding: "1px 6px", background: C.amberWash, borderRadius: 2, fontWeight: 600 }}>NC: {r.ncDecision === "quarantena" ? "QUARANTENA" : "ACCETT. IN DEROGA"}</span>}
                </div>
                <div className="f-body" style={{ fontSize: 14, color: C.ink, fontWeight: 500, marginTop: 2 }}>{sup?.name}</div>
                <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{fmt.dateFull(r.date)}</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ textAlign: "right" }}>
                <div className="f-mono" style={{ fontSize: 17, color: C.ink, fontWeight: 600 }}>{fmt.eur(total)}</div>
                <div className="f-mono" style={{ fontSize: 11, color: C.muted }}>{r.lines.length} articoli</div>
              </div>
              <Btn size="sm" variant="ghost" icon={Printer} title="Stampa" onClick={() => window.print()}>Stampa</Btn>
            </div>
          </div>
          <div style={{ padding: 16 }}>
            {r.lines.map((ln, i) => {
              const item = engine.getItems(state).find(it => it.id === ln.itemId);
              const lot = state.lots.find(l => l.receiptId === r.id && l.itemId === ln.itemId);
              return <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", fontSize: 13 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 6, height: 6, background: cats[item?.category]?.color || C.muted, borderRadius: 6 }} />
                  <span className="f-body" style={{ color: C.ink }}>{item?.name}</span>
                  {lot && <button onClick={() => onNavigate({ view: "lot", lotId: lot.id })} className="f-mono tx" style={{ fontSize: 10, color: C.teal, letterSpacing: "0.1em", padding: "1px 6px", background: C.tealWash, borderRadius: 2, border: "none", cursor: "pointer" }}>{lot.id} →</button>}
                </div>
                <div className="f-mono" style={{ display: "flex", gap: 20, color: C.inkSoft, fontSize: 12 }}>
                  <span>{ln.qty} {ln.unit}{ln.pieces > 0 ? ` · ${ln.pieces} pz` : ""}</span>
                  <span>€ {ln.pricePerUnit.toFixed(2)}/{ln.unit}</span>
                  <span style={{ color: C.ink, fontWeight: 600, minWidth: 70, textAlign: "right" }}>{fmt.eur(ln.qty * ln.pricePerUnit)}</span>
                </div>
              </div>;
            })}
          </div>
        </div>;
      })}
    </div>}
  </div>;
}

function ReceiptForm({ state, setState, onClose, initial }) {
  const fromPO = initial?.fromPO ? state.purchaseOrders.find(p => p.id === initial.fromPO) : null;
  const [supplierId, setSupplierId] = useState(fromPO?.supplierId || state.suppliers[0]?.id || "");
  const [date, setDate] = useState(TODAY);
  const [ddtNumber, setDdtNumber] = useState("");
  const [temperature, setTemperature] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [cascadeUpdate, setCascadeUpdate] = useState(true);
  const [newBpForLine, setNewBpForLine] = useState(null); // se attivo, mostra editor prodotto e alla chiusura lo assegna alla riga
  // Stato di non conformita' temperatura — se compilato si apre il pannello NC.
  // Reg. CE 852/2004 Allegato II Cap. IX: temperatura fuori range = NC.
  const [ncDecision, setNcDecision] = useState(""); // "" | "accettato_in_deroga" | "rifiutato" | "quarantena"
  const [ncReason, setNcReason] = useState("");
  // Helper: scheda BP a partire da bpId, per leggere categoria/allergeni.
  const bpById = useCallback((id) => (state.baseProducts || []).find(bp => bp.id === id), [state.baseProducts]);
  const [lines, setLines] = useState(() => {
    const mkLine = (base) => {
      const bp = (state.baseProducts || []).find(b => b.id === base.itemId);
      return {
        itemId: base.itemId,
        qty: base.qty ?? "",
        unit: base.unit || "kg",
        pieces: base.pieces ?? 0,
        pricePerUnit: base.pricePerUnit ?? "",
        // NEW: campi tracciabilita' fornitore (Reg. CE 178/2002 art. 18 + Reg. UE 1169/2011 art. 24)
        supplierLotCode: "",
        supplierExpiresAt: "",            // YYYY-MM-DD, OBBLIGATORIO per cat. critiche
        expiryType: defaultExpiryTypeForCat(bp?.cat),
        // NEW: origine prodotto pesca (Reg. UE 1379/2013 art. 35)
        origin: bp?.cat === "pesce" ? { faoZone: "", productionMethod: "pescato", scientificName: "", fishingGear: "" } : null,
        // Fix #5 (Patch B1): override del calo peso per consegna.
        // null = usa bp.caloTot. Numero in [0, 0.95] = sovrascrive (es. pesce gia'
        // sfilettato dal fornitore → caloOverride=0.05 anche se bp.caloTot=0.45).
        caloOverride: null,
      };
    };
    if (fromPO) return fromPO.lines.map(l => mkLine({ itemId: l.itemId, qty: l.qty, unit: l.unit, pricePerUnit: l.priceEst }));
    return [mkLine({ itemId: engine.getItems(state)[0]?.id || "" })];
  });
  const toast = useToast();

  // Analisi live delle differenze di prezzo rispetto ai baseProducts attuali
  const priceDiffs = useMemo(() => {
    return engine.detectPriceDiffs(state, lines.filter(l => l.itemId && l.pricePerUnit), null);
  }, [lines, state.baseProducts]);

  const increases = priceDiffs.filter(d => d.diff > 0);
  const decreases = priceDiffs.filter(d => d.diff < 0);
  const bigIncreases = increases.filter(d => d.diffPct > 10);

  // Piatti impattati dalla cascata
  const dishesImpacted = useMemo(() => {
    if (priceDiffs.length === 0) return 0;
    const bpIds = new Set(priceDiffs.map(d => d.bp.id));
    return (state.dishesReal || []).filter(d => (d.ingredienti || []).some(ing => ing.bpId && bpIds.has(ing.bpId))).length;
  }, [priceDiffs, state.dishesReal]);

  // ── Validazione HACCP: temperatura + range per categoria critica ──
  // Reg. CE 853/2004 All. III Sez. VIII (pesce) + Reg. CE 852/2004 All. II Cap. IX
  const criticalLines = useMemo(() => lines.filter(l => {
    const bp = bpById(l.itemId);
    return bp && isTempCritical(bp.cat);
  }), [lines, bpById]);
  const requiresTemp = criticalLines.length > 0;
  // Risolvi il range piu' stringente fra le righe critiche.
  // Se c'e' almeno una riga pesce → range pesce (0-4). Carne 0-7, latticini 0-6, uova 5-15.
  const tempCheck = useMemo(() => {
    if (!requiresTemp || temperature === "" || temperature == null) return null;
    const t = parseFloat(temperature);
    if (Number.isNaN(t)) return null;
    // Determina la categoria piu' restrittiva fra le righe critiche.
    const order = ["pesce", "latticini", "carne", "uova"];
    let strictest = null;
    for (const cat of order) {
      if (criticalLines.some(l => bpById(l.itemId)?.cat === cat)) { strictest = cat; break; }
    }
    const range = tempRangeForCat(strictest);
    if (!range) return null;
    const inRange = t >= range.min && t <= range.max;
    return { t, range, inRange, strictest };
  }, [temperature, criticalLines, requiresTemp, bpById]);
  const tempOutOfRange = tempCheck && !tempCheck.inRange;

  const handleFiles = async (files) => {
    // Pipeline sicura: sniff MIME magic bytes + size cap singolo + cap cumulativo
    // + lettura effettiva del file come dataUrl (BUG FIX: prima i metadati erano
    // salvati ma il contenuto del PDF/immagine veniva perso al refresh).
    const result = await processAttachments(files, attachments);
    if (result.valid.length > 0) {
      setAttachments(prev => [...prev, ...result.valid]);
    }
    result.rejected.forEach(r => {
      toast({ type: "error", message: `${r.name}: ${r.reason}` });
    });
    if (result.valid.length === 0 && result.rejected.length === 0) {
      toast({ type: "warning", message: "Nessun file allegato" });
    } else if (result.valid.length > 0) {
      toast({ type: "success", message: `${result.valid.length} file allegato${result.valid.length > 1 ? "i" : ""}` });
    }
  };

  const submit = () => {
    if (!ddtNumber.trim()) { toast({ type: "error", message: "Inserisci numero DDT" }); return; }
    if (lines.some(l => !l.qty || !l.pricePerUnit)) { toast({ type: "error", message: "Compila le righe" }); return; }

    // ── Validazione tracciabilita' per categorie critiche ───────────────────
    // Reg. UE 1169/2011 art. 24 (scadenza) + Reg. CE 178/2002 art. 18 (lotto)
    for (const ln of lines) {
      const bp = bpById(ln.itemId);
      if (!bp) continue;
      if (isTempCritical(bp.cat)) {
        if (!ln.supplierExpiresAt) {
          toast({ type: "error", title: "Scadenza fornitore mancante", message: `${bp.display || bp.nome}: copia la data di scadenza dall'etichetta del fornitore` });
          return;
        }
      }
      // Reg. UE 1379/2013 art. 35 — origine prodotti pesca obbligatoria
      if (bp.cat === "pesce") {
        if (!ln.origin?.faoZone?.trim()) {
          toast({ type: "error", title: "Zona FAO mancante", message: `${bp.display || bp.nome}: indica la zona FAO (es. FAO 37.2.1)` });
          return;
        }
        if (!ln.origin?.productionMethod) {
          toast({ type: "error", title: "Metodo produzione mancante", message: `${bp.display || bp.nome}: indica pescato / allevato / raccolto` });
          return;
        }
      }
    }

    // ── Temperatura ricezione: obbligatoria per merce critica ──────────────
    if (requiresTemp) {
      if (temperature === "" || temperature == null) {
        toast({ type: "error", title: "Temperatura merce obbligatoria", message: "Per pesce/carne/latticini/uova devi rilevare la temperatura all'arrivo (Reg. CE 853/2004)" });
        return;
      }
      // Se la temperatura e' fuori range serve una decisione + motivazione.
      if (tempOutOfRange) {
        if (!ncDecision) {
          toast({ type: "error", title: "Temperatura fuori range", message: "Seleziona Rifiutato / Accettato in deroga / Quarantena e motiva la scelta" });
          return;
        }
        if (!ncReason.trim()) {
          toast({ type: "error", title: "Motivazione NC mancante", message: "Spiega per iscritto la decisione (verra' archiviata come NC)" });
          return;
        }
      }
    }

    const recId = uid("r_");
    const dateCode = date.slice(2).replace(/-/g, "");

    // ── FIX BLOCKER 1 (audit 2026-06-07): codici lotto UNIVOCI e deterministici ──
    // Reg. CE 178/2002 art. 18: ogni lotto deve avere identificazione univoca.
    // PRIMA: suffix = char(65 + (state.lots.length + idx) % 26) → dopo Z (26ª riga)
    // ripartiva da A ⇒ due lotti con lo STESSO codice L-AAMMGG-A (scarico sul lotto
    // sbagliato, giacenze e tracciabilita' inaffidabili SENZA avviso). Inoltre usare
    // state.lots.length era fragile (lotti esauriti/cancellati cambiano la length).
    // ORA: sequenza base-26 alfabetica (A..Z, poi AA, AB, ... AZ, BA, ...) ancorata
    // ai lotti GIA' esistenti dello STESSO dateCode. I primi 26 codici restano
    // identici al passato (L-AAMMGG-A..Z): i lotti gia' creati non vengono toccati
    // e display/ricerca continuano a funzionare. Dalla 27ª riga in poi: L-AAMMGG-AA.
    //
    // toAlphaSuffix: 0→"A", 25→"Z", 26→"AA", 27→"AB", 51→"AZ", 52→"BA", ...
    const toAlphaSuffix = (n) => {
      let s = "";
      let x = n;
      do {
        s = String.fromCharCode(65 + (x % 26)) + s;
        x = Math.floor(x / 26) - 1;
      } while (x >= 0);
      return s;
    };
    // Base sequenza = quanti lotti dello stesso giorno esistono gia'. Robusto contro
    // collisioni: se un codice atteso fosse comunque gia' occupato (es. import legacy),
    // avanziamo finche' troviamo un suffisso libero.
    const lotPrefix = `L-${dateCode}-`;
    const existingSameDay = new Set(
      state.lots.filter(l => typeof l.id === "string" && l.id.startsWith(lotPrefix)).map(l => l.id)
    );
    let seqCursor = state.lots.filter(l => typeof l.id === "string" && l.id.startsWith(lotPrefix)).length;
    const nextUniqueLotId = () => {
      let candidate;
      do {
        candidate = `${lotPrefix}${toAlphaSuffix(seqCursor)}`;
        seqCursor += 1;
      } while (existingSameDay.has(candidate));
      existingSameDay.add(candidate); // riserva anche per le righe successive dello stesso DDT
      return candidate;
    };

    // Se temperatura fuori range con decisione "rifiutato": NESSUN lotto generato,
    // ma il DDT viene archiviato e si crea una NC ricezione.
    const isRejected = tempOutOfRange && ncDecision === "rifiutato";
    // Se "quarantena" o temperatura ok + categoria critica → status "in_accettazione".
    // Se "accettato_in_deroga" → "in_accettazione" comunque (deve essere confermato).
    // Se non critica e temperatura ok → "disponibile" diretto.

    const newLots = [];
    const newMovements = [];
    if (!isRejected) {
      lines.forEach((ln, idx) => {
        const bp = bpById(ln.itemId);
        const item = engine.getItems(state).find(i => i.id === ln.itemId);
        // FIX BLOCKER 1: suffisso base-26 univoco (vedi nextUniqueLotId sopra).
        const lotId = nextUniqueLotId();
        // expiresAt: SE c'e' supplierExpiresAt usa quella (etichetta fornitore),
        // ALTRIMENTI fallback shelfLifeDays. Reg. UE 1169/2011 art. 24.
        const expiresAt = ln.supplierExpiresAt
          ? ln.supplierExpiresAt
          : new Date(new Date(date).getTime() + (item?.shelfLifeDays || 7) * 86400000).toISOString().slice(0, 10);
        const critical = bp && isTempCritical(bp.cat);
        // Status di partenza:
        //  - rifiutato: gia' intercettato sopra (isRejected) — non arriviamo qui
        //  - quarantena: "quarantena"
        //  - accettato_in_deroga + critica: "in_accettazione"
        //  - critica senza NC: "in_accettazione" (deve passare controllo)
        //  - non critica: "disponibile"
        let status;
        if (tempOutOfRange && ncDecision === "quarantena") status = "quarantena";
        else if (critical) status = "in_accettazione";
        else status = "disponibile";
        // Fix #5 (Patch A1+B1): snapshot CMP e calo applicato.
        // unitCost = prezzo NETTO IVA pagato per questo lotto (€/unit).
        // unitCostReal = unitCost / (1 - calo) → costo "lavorato" per food cost.
        const pricePerUnit = parseFloat(ln.pricePerUnit) || 0;
        const caloUsed = (typeof ln.caloOverride === "number" && Number.isFinite(ln.caloOverride))
          ? Math.max(0, Math.min(0.95, ln.caloOverride))
          : (Number(bp?.caloTot) || 0);
        const safeCalo = caloUsed < 0.95 ? caloUsed : 0.95;
        const unitCostReal = safeCalo > 0 ? pricePerUnit / (1 - safeCalo) : pricePerUnit;
        newLots.push({
          id: lotId,
          itemId: ln.itemId,
          receiptId: recId,
          qtyInitial: parseFloat(ln.qty),
          qtyRemaining: parseFloat(ln.qty),
          unit: ln.unit,
          createdAt: date + "T" + NOW_HOUR,
          expiresAt,
          status,
          parent: null,
          notes: null,
          // Tracciabilita' fornitore (Reg. CE 178/2002 art. 18, Reg. UE 1379/2013 art. 35)
          supplierLotCode: ln.supplierLotCode ? ln.supplierLotCode.trim() : null,
          origin: ln.origin && (ln.origin.faoZone || ln.origin.productionMethod) ? { ...ln.origin } : null,
          expiryType: ln.expiryType || defaultExpiryTypeForCat(bp?.cat),
          rootReceiptId: recId,
          // Fix #5 (Patch A1): snapshot economico immutabile del lotto.
          unitCost: pricePerUnit,           // costo NETTO fattura (CMP snapshot)
          unitCostReal: Math.round(unitCostReal * 10000) / 10000,  // costo post-calo (food cost)
          caloApplied: caloUsed,            // audit: calo effettivamente applicato
        });
        newMovements.push({ id: uid("mv_"), lotId, qty: parseFloat(ln.qty), type: "ingresso", at: date + "T" + NOW_HOUR, ref: { type: "receipt", id: recId }, by: state.currentUser.name });
      });
    }

    const receipt = {
      id: recId, date, supplierId, ddtNumber: ddtNumber.trim(), invoiceNumber: null,
      temperature: temperature ? parseFloat(temperature) : null,
      // Persistiamo i nuovi campi anche sulle righe del DDT (per audit + reimport).
      lines: lines.map(l => ({
        itemId: l.itemId, qty: parseFloat(l.qty), unit: l.unit,
        pieces: parseInt(l.pieces) || 0, pricePerUnit: parseFloat(l.pricePerUnit),
        supplierLotCode: l.supplierLotCode || null,
        supplierExpiresAt: l.supplierExpiresAt || null,
        expiryType: l.expiryType || null,
        origin: l.origin && (l.origin.faoZone || l.origin.productionMethod) ? { ...l.origin } : null,
        // Fix #5 (Patch B1): se sovrascritto, traccia il calo applicato a questa consegna.
        caloOverride: (typeof l.caloOverride === "number" && Number.isFinite(l.caloOverride)) ? l.caloOverride : null,
      })),
      attachments, poId: fromPO?.id || null,
      rejected: isRejected || undefined,
      ncDecision: tempOutOfRange ? ncDecision : undefined,
    };

    let nextState = { ...state, receipts: [...state.receipts, receipt], lots: [...state.lots, ...newLots], lotMovements: [...state.lotMovements, ...newMovements] };
    if (fromPO) nextState.purchaseOrders = state.purchaseOrders.map(p => p.id === fromPO.id ? { ...p, status: "ricevuto", receiptId: recId } : p);

    // Registrazione NC ricezione se temperatura fuori range.
    // Reg. CE 852/2004 All. II Cap. IX + D.Lgs 193/2007 (HACCP principio 5+7).
    if (tempOutOfRange) {
      const sup = state.suppliers.find(s => s.id === supplierId);
      const nc = {
        id: uid("nc_"),
        date,
        createdAt: nowIso(),
        type: "Ricezione",
        kind: "ricezione",
        description: `DDT ${ddtNumber.trim()} (${sup?.name || ""}): temperatura merce ${tempCheck.t}°C, fuori range ${tempCheck.range.min}/${tempCheck.range.max}°C (${tempCheck.range.label}). Decisione: ${ncDecision === "rifiutato" ? "MERCE RIFIUTATA" : ncDecision === "quarantena" ? "MERCE IN QUARANTENA" : "ACCETTATA IN DEROGA"}.`,
        action: ncReason.trim() || "Da definire azione correttiva",
        operator: state.currentUser.name,
        status: ncDecision === "rifiutato" ? "risolta" : "aperta",
        receiptId: recId,
        temperature: tempCheck.t,
        targetRange: `${tempCheck.range.min}/${tempCheck.range.max}`,
      };
      nextState.haccpLogs = {
        ...nextState.haccpLogs,
        nonConformities: [...(nextState.haccpLogs?.nonConformities || []), nc],
      };
      nextState.auditLog = appendAudit(nextState, {
        action: "receipt_temp_out_of_range",
        entityType: "receipt",
        entityId: recId,
        reason: `T=${tempCheck.t}°C fuori range ${tempCheck.range.min}/${tempCheck.range.max} — ${ncDecision}`,
      });
    }

    // ── CASCATA PREZZI: aggiorna baseProducts se prezzi diversi dall'attuale ──
    if (!isRejected && cascadeUpdate && priceDiffs.length > 0) {
      // Fix #5 (Patch B2): per ogni bpId trova il caloOverride della riga DDT
      // corrispondente. Se la riga non ha override, non passiamo caloUsed →
      // applyPriceUpdate fallback su bp.caloTot (comportamento storico).
      const caloByBp = new Map();
      lines.forEach(ln => {
        if (!ln.itemId) return;
        if (typeof ln.caloOverride === "number" && Number.isFinite(ln.caloOverride)) {
          caloByBp.set(ln.itemId, Math.max(0, Math.min(0.95, ln.caloOverride)));
        }
      });
      const updates = priceDiffs.map(d => ({
        bpId: d.bp.id, newPrice: d.newPrice,
        caloUsed: caloByBp.has(d.bp.id) ? caloByBp.get(d.bp.id) : undefined,
        source: "ddt", refId: recId, supplierId, date,
      }));
      nextState.baseProducts = engine.applyPriceUpdate(nextState.baseProducts, updates);
    }

    setState(nextState);
    if (isRejected) {
      toast({ type: "error", title: "Merce rifiutata", message: `DDT archiviato senza generare lotti. NC aperta per temperatura ${tempCheck.t}°C fuori range.` });
    } else {
      const cascadeMsg = cascadeUpdate && priceDiffs.length > 0 ? ` · ${priceDiffs.length} prezzi aggiornati (impatto ${dishesImpacted} piatti)` : "";
      const accMsg = newLots.some(l => l.status === "in_accettazione" || l.status === "quarantena") ? " · alcuni lotti in attesa di accettazione" : "";
      toast({ type: tempOutOfRange ? "warning" : "success", title: "Bolla registrata", message: `${newLots.length} lotti generati${fromPO ? ` · ordine ${fromPO.id} chiuso` : ""}${cascadeMsg}${accMsg}` });
    }
    onClose();
  };

  const total = lines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.pricePerUnit) || 0), 0);

  return <Modal onClose={onClose} maxWidth={820} subtitle={fromPO ? `Da ordine ${fromPO.id}` : "Registra"} title="Bolla di ingresso merce"
    footer={<>
      <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
      <Btn variant="accent" icon={CheckCircle2} onClick={submit}>Registra bolla e genera lotti</Btn>
    </>}>
    <div style={{ padding: 24 }}>
      {newBpForLine !== null && <BaseProductEditor product={{}} state={state} setState={(newState) => {
        // intercetta la creazione per assegnarla alla riga corrente
        const newBp = newState.baseProducts.find(bp => !state.baseProducts.find(b => b.id === bp.id));
        setState(newState);
        if (newBp) {
          setLines(lines.map((l, j) => j === newBpForLine ? { ...l, itemId: newBp.id, unit: newBp.unit || "kg", pricePerUnit: newBp.pFatt || "" } : l));
        }
      }} onClose={() => setNewBpForLine(null)} />}
      {fromPO && <div style={{ marginBottom: 20, padding: 12, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, display: "flex", alignItems: "center", gap: 10 }}>
        <Info size={15} color={C.teal} />
        <div className="f-body" style={{ fontSize: 12, color: C.tealDeep }}>Dati pre-compilati dall'ordine <span className="f-mono" style={{ fontWeight: 600 }}>{fromPO.id}</span>. Verifica e conferma le quantità effettivamente ricevute.</div>
      </div>}

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
        <Field label="Fornitore" required><Select value={supplierId} onChange={e => setSupplierId(e.target.value)} disabled={!!fromPO}>{state.suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
        <Field label="Data ricevimento" required><Input type="date" value={date} onChange={e => setDate(e.target.value)} /></Field>
        <Field
          label={`Temperatura merce °C${requiresTemp ? " *" : ""}`}
          hint={requiresTemp && tempCheck?.range
            ? `Range ${tempCheck.range.label}: ${tempCheck.range.min}/${tempCheck.range.max}°C`
            : (requiresTemp ? "Obbligatoria (catena del freddo)" : "Se pesce/freddo")}
          required={requiresTemp}
        >
          <Input
            type="number" step="0.1"
            value={temperature}
            onChange={e => setTemperature(e.target.value)}
            placeholder="es. 2.5"
            style={tempOutOfRange ? { borderColor: C.terra, background: C.terraWash } : undefined}
          />
        </Field>
      </div>
      <div style={{ marginBottom: 20 }}>
        <Field label="Numero DDT" required><Input value={ddtNumber} onChange={e => setDdtNumber(e.target.value)} placeholder="es. DDT/2026/0500" /></Field>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Allegati (PDF, foto)</div>
        <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          style={{ border: `2px dashed ${dragOver ? C.teal : C.rule}`, background: dragOver ? C.tealWash : C.paperSoft, padding: 20, borderRadius: 2, textAlign: "center", transition: "all 0.2s" }}>
          <Upload size={22} color={C.muted} style={{ margin: "0 auto 6px", display: "block" }} />
          <div className="f-body" style={{ fontSize: 12, color: C.inkSoft }}>Trascina il PDF della bolla qui, oppure <label style={{ color: C.teal, cursor: "pointer", textDecoration: "underline" }}>sfoglia<input type="file" multiple hidden onChange={e => handleFiles(e.target.files)} /></label></div>
        </div>
        {attachments.length > 0 && <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
          {attachments.map((a, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: C.white, border: `1px solid ${C.ruleSoft}`, borderRadius: 2 }}>
            <FileText size={13} color={C.teal} /><span className="f-body" style={{ fontSize: 12, color: C.ink, flex: 1 }}>{a.name}</span>
            <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>{fmt.bytes(a.size)}</span>
            <IconBtn icon={X} size={12} onClick={() => setAttachments(attachments.filter((_, j) => j !== i))} />
          </div>)}
        </div>}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: C.muted }}>RIGHE BOLLA (creeranno {lines.length} lotti)</div>
        <Btn variant="ghost" size="xs" icon={Plus} onClick={() => {
          const firstItem = engine.getItems(state)[0];
          const firstBp = firstItem ? (state.baseProducts || []).find(b => b.id === firstItem.id) : null;
          setLines([...lines, {
            itemId: firstItem?.id || "", qty: "", unit: "kg", pieces: 0, pricePerUnit: "",
            supplierLotCode: "", supplierExpiresAt: "",
            expiryType: defaultExpiryTypeForCat(firstBp?.cat),
            origin: firstBp?.cat === "pesce" ? { faoZone: "", productionMethod: "pescato", scientificName: "", fishingGear: "" } : null,
            caloOverride: null,
          }]);
        }}>Riga</Btn>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
        {lines.map((ln, i) => {
        const bpRow = bpById(ln.itemId);
        const critical = bpRow && isTempCritical(bpRow.cat);
        const isFish = bpRow?.cat === "pesce";
        return <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8, padding: critical ? 10 : 0, background: critical ? C.paperSoft : "transparent", border: critical ? `1px solid ${C.ruleSoft}` : "none", borderRadius: 2 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2.2fr 0.8fr 0.6fr 0.6fr 0.9fr 0.9fr auto", gap: 10, alignItems: "end" }}>
          <Field label={i === 0 ? "Articolo" : ""}>
            <div style={{ display: "flex", gap: 4 }}>
              <Select value={ln.itemId} onChange={e => {
                if (e.target.value === "__NEW__") { setNewBpForLine(i); return; }
                const it = engine.getItems(state).find(x => x.id === e.target.value);
                const newBp = bpById(e.target.value);
                setLines(lines.map((l, j) => j === i ? {
                  ...l,
                  itemId: e.target.value,
                  unit: it?.unit || "kg",
                  // Cambio categoria → reset expiryType default + origin per pesce.
                  expiryType: defaultExpiryTypeForCat(newBp?.cat),
                  origin: newBp?.cat === "pesce"
                    ? (l.origin || { faoZone: "", productionMethod: "pescato", scientificName: "", fishingGear: "" })
                    : null,
                } : l));
              }} style={{ flex: 1 }}>
                <option value="__NEW__" style={{ fontStyle: "italic", color: "#1F4D48" }}>+ Crea nuovo prodotto...</option>
                {(() => {
                    const all = engine.getItems(state);
                    const groups = {};
                    all.forEach(it => { (groups[it.category] = groups[it.category] || []).push(it); });
                    return Object.entries(groups).map(([cat, items]) => <optgroup key={cat} label={(cats[cat]?.label || cat).toUpperCase()}>
                      {items.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
                    </optgroup>);
                  })()}
              </Select>
              <IconBtn icon={Plus} size={14} title="Nuovo prodotto base" onClick={() => setNewBpForLine(i)} />
            </div>
          </Field>
          <Field label={i === 0 ? "Peso" : ""}><Input type="number" step="0.01" value={ln.qty} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, qty: e.target.value } : l))} /></Field>
          <Field label={i === 0 ? "Unità" : ""}>
            <Select value={ln.unit} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, unit: e.target.value } : l))}><option>kg</option><option>pz</option><option>l</option></Select>
          </Field>
          <Field label={i === 0 ? "N. pezzi" : ""}><Input type="number" value={ln.pieces} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, pieces: e.target.value } : l))} /></Field>
          <Field label={i === 0 ? "€/unità" : ""}><Input type="number" step="0.01" value={ln.pricePerUnit} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, pricePerUnit: e.target.value } : l))} /></Field>
          <Field label={i === 0 ? "Totale" : ""}>
            <div className="f-mono" style={{ padding: "9px 12px", background: C.paperDeep, fontSize: 13, color: C.ink, fontWeight: 600, borderRadius: 2 }}>{fmt.eur((parseFloat(ln.qty) || 0) * (parseFloat(ln.pricePerUnit) || 0))}</div>
          </Field>
          <IconBtn icon={Trash2} onClick={() => lines.length > 1 && setLines(lines.filter((_, j) => j !== i))} />
        </div>
        {/* Fix #6: detection anomalia prezzo (MAD robust z-score) */}
        {(() => {
          const price = Number(ln.pricePerUnit) || 0;
          if (price <= 0 || !ln.itemId) return null;
          const anom = engine.stats.priceAnomaly(state, ln.itemId, price);
          if (anom.fallback) {
            if (anom.deltaPct != null && anom.deltaPct > 0.10) {
              return (
                <div className="f-body" style={{ fontSize: 11, color: C.amberDeep, padding: "4px 10px", background: C.amberWash, borderRadius: 2 }}>
                  Variazione {(anom.deltaPct * 100).toFixed(0)}% vs prezzo corrente (storico insufficiente n={anom.n}, soglia ±10%)
                </div>
              );
            }
            return null;
          }
          if (anom.isAnomaly) {
            return (
              <div className="f-body" style={{ fontSize: 11, color: C.terra, fontWeight: 600, padding: "4px 10px", background: C.terraWash, borderRadius: 2 }}>
                ANOMALIA PREZZO · z={anom.z.toFixed(1)} (soglia ±3.5) · mediana storica €{anom.median.toFixed(2)} · range tipico €{anom.normalRange[0].toFixed(2)}–€{anom.normalRange[1].toFixed(2)} (n={anom.n} DDT)
              </div>
            );
          }
          return (
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.05em", padding: "2px 10px" }}>
              OK · z={anom.z.toFixed(1)} · mediana storica €{anom.median.toFixed(2)} (n={anom.n})
            </div>
          );
        })()}
        {/* ── Sotto-riga HACCP/Tracciabilita' ─────────────────────────────────
            Reg. CE 178/2002 art. 18 (lotto fornitore) +
            Reg. UE 1169/2011 art. 24 (TMC vs scadenza) */}
        {bpRow && <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1.2fr", gap: 10 }}>
          <Field label="Lotto fornitore" hint="Da etichetta">
            <Input value={ln.supplierLotCode} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, supplierLotCode: e.target.value } : l))} placeholder="es. L20250514-A" />
          </Field>
          <Field label={`Scadenza fornitore${critical ? " *" : ""}`} hint={critical ? "Obbligatorio" : "Da etichetta"}>
            <Input type="date" value={ln.supplierExpiresAt} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, supplierExpiresAt: e.target.value } : l))} />
          </Field>
          <Field label="Tipo scadenza" hint="Reg. UE 1169/2011 art. 24">
            <Select value={ln.expiryType || "consumarsi_entro"} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, expiryType: e.target.value } : l))}>
              <option value="consumarsi_entro">Da consumarsi entro (scadenza)</option>
              <option value="consumarsi_preferibilmente">Preferibilmente entro (TMC)</option>
            </Select>
          </Field>
        </div>}
        {/* ── Origine prodotti pesca (Reg. UE 1379/2013 art. 35) ───────────── */}
        {isFish && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr 1fr", gap: 10 }}>
          <Field label="Zona FAO *" hint="es. FAO 37.2.1">
            <Input value={ln.origin?.faoZone || ""} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, origin: { ...(l.origin || {}), faoZone: e.target.value } } : l))} placeholder="FAO 37.2.1" />
          </Field>
          <Field label="Metodo *">
            <Select value={ln.origin?.productionMethod || "pescato"} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, origin: { ...(l.origin || {}), productionMethod: e.target.value } } : l))}>
              <option value="pescato">Pescato</option>
              <option value="allevato">Allevato</option>
              <option value="raccolto">Raccolto</option>
            </Select>
          </Field>
          <Field label="Nome scientifico" hint="Consigliato">
            <Input value={ln.origin?.scientificName || ""} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, origin: { ...(l.origin || {}), scientificName: e.target.value } } : l))} placeholder="es. Dicentrarchus labrax" />
          </Field>
          {ln.origin?.productionMethod === "pescato" && <Field label="Attrezzo" hint="Se pescato">
            <Input value={ln.origin?.fishingGear || ""} onChange={e => setLines(lines.map((l, j) => j === i ? { ...l, origin: { ...(l.origin || {}), fishingGear: e.target.value } } : l))} placeholder="es. reti a strascico" />
          </Field>}
        </div>}
        {/* ── Fix #5 Patch B1: override calo peso per consegna ────────────────
            Se il pesce/carne/latticino arriva GIA' lavorato dal fornitore non va
            applicato il calo standard del prodotto base. Si attiva solo per
            categorie dove ha senso un calo (pesce, carne, latticini, verdura). */}
        {bpRow && ["pesce", "carne", "latticini", "verdura"].includes(bpRow.cat) && (Number(bpRow.caloTot) > 0 || ln.caloOverride !== null) && (
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 10, alignItems: "center", padding: "6px 10px", background: ln.caloOverride !== null ? C.tealWash : "transparent", borderRadius: 2, border: ln.caloOverride !== null ? `1px dashed ${C.teal}` : "1px dashed transparent" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", whiteSpace: "nowrap" }}>
              <input
                type="checkbox"
                checked={ln.caloOverride !== null}
                onChange={e => setLines(lines.map((l, j) => j === i ? {
                  ...l,
                  caloOverride: e.target.checked ? 0 : null,
                } : l))}
              />
              <span className="f-body" style={{ fontSize: 11, color: C.ink, fontWeight: 500 }}>
                Già lavorato
              </span>
              <span className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.08em" }}>
                (calo standard: {(Number(bpRow.caloTot) * 100).toFixed(0)}%)
              </span>
            </label>
            {ln.caloOverride !== null && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span className="f-mono" style={{ fontSize: 10, color: C.tealDeep, letterSpacing: "0.08em" }}>CALO DI QUESTA CONSEGNA</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="0.95"
                  value={ln.caloOverride}
                  onChange={e => {
                    const v = parseFloat(e.target.value);
                    setLines(lines.map((l, j) => j === i ? {
                      ...l,
                      caloOverride: Number.isFinite(v) ? Math.max(0, Math.min(0.95, v)) : 0,
                    } : l));
                  }}
                  placeholder="0.05"
                  style={{ maxWidth: 100 }}
                />
                <span className="f-body" style={{ fontSize: 11, color: C.muted }}>
                  ({(((ln.caloOverride || 0) * 100)).toFixed(1)}% — pReale stimato {fmt.eur(((parseFloat(ln.pricePerUnit) || 0) / (1 - Math.min(0.95, Math.max(0, ln.caloOverride || 0)))) || 0)}/{ln.unit})
                </span>
              </div>
            )}
          </div>
        )}
        </div>;
        })}
      </div>

      {/* ── Pannello Non Conformita' temperatura (Reg. CE 852/2004 All. II Cap. IX) ── */}
      {tempOutOfRange && <div style={{ marginBottom: 16, background: C.terraWash, border: `2px solid ${C.terra}`, borderRadius: 2, padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <AlertTriangle size={18} color={C.terra} />
          <div className="f-body" style={{ fontSize: 14, color: C.terraDeep, fontWeight: 700, flex: 1 }}>
            TEMPERATURA FUORI RANGE — {tempCheck.t}°C su range {tempCheck.range.label} ({tempCheck.range.min}/{tempCheck.range.max}°C)
          </div>
        </div>
        <div className="f-body" style={{ fontSize: 12, color: C.terraDeep, marginBottom: 12, lineHeight: 1.5 }}>
          Devi decidere come gestire questa merce. La scelta verra' registrata come Non Conformita' HACCP.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
          {[
            { v: "rifiutato", label: "Rifiuta merce", hint: "Non genera lotti" },
            { v: "quarantena", label: "Quarantena", hint: "Lotti bloccati, attendono analisi" },
            { v: "accettato_in_deroga", label: "Accetta in deroga", hint: "Lotti in accettazione" },
          ].map(opt => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setNcDecision(opt.v)}
              className="tx"
              style={{
                padding: "10px 12px",
                background: ncDecision === opt.v ? C.terra : C.white,
                color: ncDecision === opt.v ? C.white : C.ink,
                border: `1px solid ${ncDecision === opt.v ? C.terra : C.rule}`,
                borderRadius: 2, cursor: "pointer", textAlign: "left", fontFamily: "inherit",
              }}
            >
              <div className="f-body" style={{ fontSize: 12, fontWeight: 600 }}>{opt.label}</div>
              <div className="f-mono" style={{ fontSize: 10, marginTop: 2, opacity: 0.8, letterSpacing: "0.05em" }}>{opt.hint}</div>
            </button>
          ))}
        </div>
        <Field label="Motivazione (obbligatoria)" required>
          <Textarea
            value={ncReason}
            onChange={e => setNcReason(e.target.value)}
            placeholder="es. Camion arrivato in ritardo, temperatura non rispettata. Verificate condizioni cella alla consegna, decisione: ..."
          />
        </Field>
      </div>}

      {priceDiffs.length > 0 && <div style={{ marginBottom: 16, background: bigIncreases.length > 0 ? C.terraWash : C.amberWash, border: `1px solid ${bigIncreases.length > 0 ? C.terra : C.amber}`, borderRadius: 2, padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          {bigIncreases.length > 0 ? <AlertTriangle size={16} color={C.terra} /> : <Zap size={16} color={C.amberDeep} />}
          <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 600, flex: 1 }}>
            {bigIncreases.length > 0
              ? `⚠ ATTENZIONE: ${bigIncreases.length} prezzi in aumento > 10% rispetto all'ultimo`
              : `${priceDiffs.length} prezzi diversi rispetto ai prodotti base`}
          </div>
          {dishesImpacted > 0 && <span className="f-mono" style={{ fontSize: 10, color: bigIncreases.length > 0 ? C.terra : C.amberDeep, letterSpacing: "0.1em", padding: "2px 8px", background: C.white, borderRadius: 2, fontWeight: 600 }}>IMPATTO: {dishesImpacted} PIATTI</span>}
        </div>

        {bigIncreases.length > 0 && <div style={{ fontSize: 11, color: C.terra, marginBottom: 10, padding: 8, background: C.white, borderLeft: `3px solid ${C.terra}`, borderRadius: 2 }}>
          {bigIncreases.map((d, i) => <div key={i} style={{ marginBottom: i < bigIncreases.length - 1 ? 4 : 0, fontWeight: 500 }}>
            <strong>{(d.bp.display || d.bp.nome).slice(0, 40)}</strong>: {fmt.eur(d.oldPrice)} → <strong>{fmt.eur(d.newPrice)}</strong> (<strong>+{d.diffPct.toFixed(1)}%</strong>)
          </div>)}
        </div>}

        <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 10, maxHeight: 160, overflowY: "auto" }}>
          {priceDiffs.slice(0, 10).map((d, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 8px", background: C.white, borderRadius: 2, fontSize: 11 }}>
            <span className="f-body" style={{ flex: 1, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(d.bp.display || d.bp.nome).slice(0, 40)}</span>
            <span className="f-mono" style={{ color: C.muted, textDecoration: "line-through" }}>{fmt.eur(d.oldPrice)}</span>
            <ArrowRight size={10} color={C.muted} />
            <span className="f-mono" style={{ color: C.ink, fontWeight: 600 }}>{fmt.eur(d.newPrice)}</span>
            <span className="f-mono" style={{ color: d.diff > 0 ? C.terra : C.olive, fontWeight: 600, minWidth: 55, textAlign: "right" }}>{d.diff > 0 ? "+" : ""}{d.diffPct.toFixed(1)}%</span>
          </div>)}
          {priceDiffs.length > 10 && <div className="f-body" style={{ fontSize: 10, color: C.muted, fontStyle: "italic", textAlign: "center", padding: 2 }}>...e altri {priceDiffs.length - 10}</div>}
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: 8, background: C.white, borderRadius: 2 }}>
          <input type="checkbox" checked={cascadeUpdate} onChange={e => setCascadeUpdate(e.target.checked)} />
          <div style={{ flex: 1 }}>
            <div className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>Aggiorna prezzi prodotti base in cascata e salva storico</div>
            <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>I nuovi prezzi aggiornano pFatt e pReale (dopo calo). Il food cost dei piatti si ricalcola automaticamente. Lo storico delle variazioni viene salvato.</div>
          </div>
        </label>
      </div>}

      <div style={{ padding: 16, background: C.paperDeep, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div className="f-mono" style={{ fontSize: 11, color: C.muted, letterSpacing: "0.15em" }}>TOTALE BOLLA</div>
        <div className="f-display" style={{ fontSize: 28, color: C.ink, fontWeight: 600 }}>{fmt.eur(total)}</div>
      </div>
    </div>
  </Modal>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      FATTURE                                               ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function Fatture({ state, setState }) {
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [previewInv, setPreviewInv] = useState(null);
  // Soft-delete state — art. 2220 c.c. conservazione 10 anni, DPR 633/72 art. 39
  const [confirmDel, setConfirmDel] = useState(null); // { id, amount, fornitore, number }
  const [delReason, setDelReason] = useState("");
  const toast = useToast();

  // Filtro: la tab "archiviate" mostra solo i soft-deleted. Tutte le altre tab escludono i soft-deleted.
  const filtered = state.invoices.filter(i => {
    if (filter === "archiviate") {
      if (!i.deletedAt) return false;
    } else {
      if (i.deletedAt) return false;
      if (filter !== "all" && i.status !== filter) return false;
    }
    if (supplierFilter !== "all" && i.supplierId !== supplierFilter) return false;
    if (dateFrom && i.date < dateFrom) return false;
    if (dateTo && i.date > dateTo) return false;
    if (search) {
      const q = search.toLowerCase();
      const sup = state.suppliers.find(s => s.id === i.supplierId);
      if (!i.number.toLowerCase().includes(q) && !(sup?.name || "").toLowerCase().includes(q)) return false;
    }
    return true;
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  // KPI calcolati solo sulle fatture ATTIVE (escludendo gli archiviati per non falsare i totali)
  const activeInvoices = state.invoices.filter(isActive);
  const totDue = activeInvoices.filter(i => i.status === "da_pagare").reduce((s, i) => s + i.amount, 0);
  const totPaid30d = activeInvoices.filter(i => i.status === "pagata" && daysBetween(i.paidAt, TODAY) <= 30).reduce((s, i) => s + i.amount, 0);
  const totFiltered = filtered.reduce((s, i) => s + i.amount, 0);

  const markPaid = (id) => {
    setState({ ...state, invoices: state.invoices.map(i => i.id === id ? { ...i, status: "pagata", paidAt: TODAY } : i) });
    toast({ type: "success", message: "Fattura marcata come pagata" });
  };

  // Apre il ConfirmModal con la richiesta di archiviazione (soft-delete).
  const requestDel = (inv) => {
    const fornitore = state.suppliers.find(s => s.id === inv.supplierId)?.name || "?";
    setConfirmDel({ id: inv.id, amount: inv.amount, fornitore, number: inv.number });
    setDelReason("");
  };

  // Esegue il soft-delete + appende evento audit. Mai cancellazione fisica.
  const confirmDelOp = () => {
    const inv = state.invoices.find(i => i.id === confirmDel.id);
    if (!inv) { setConfirmDel(null); return; }
    setState(s => ({
      ...s,
      invoices: s.invoices.map(i => i.id === inv.id
        ? softDelete(i, s.currentUser?.name, delReason || "Nessuna motivazione")
        : i
      ),
      auditLog: appendAudit(s, {
        action: "soft_delete",
        entityType: "invoice",
        entityId: inv.id,
        before: { id: inv.id, supplierId: inv.supplierId, amount: inv.amount, number: inv.number, date: inv.date, status: inv.status },
        reason: delReason || "Nessuna motivazione",
      }),
    }));
    toast({ type: "success", message: "Fattura archiviata (recuperabile)" });
    setConfirmDel(null);
  };

  // Ripristino: rimuove i marker di soft-delete e logga restore in audit
  const restoreInv = (inv) => {
    setState(s => ({
      ...s,
      invoices: s.invoices.map(i => {
        if (i.id !== inv.id) return i;
        const { deletedAt, deletedBy, deletedReason, ...rest } = i;
        return rest;
      }),
      auditLog: appendAudit(s, {
        action: "restore",
        entityType: "invoice",
        entityId: inv.id,
      }),
    }));
    toast({ type: "success", message: "Fattura ripristinata" });
  };

  // Gruppo per fornitore per vista archivio (solo fatture attive)
  const bySupplier = useMemo(() => {
    const groups = {};
    activeInvoices.forEach(i => {
      const sid = i.supplierId;
      if (!groups[sid]) groups[sid] = { supplier: state.suppliers.find(s => s.id === sid), invoices: [], total: 0 };
      groups[sid].invoices.push(i);
      groups[sid].total += i.amount;
    });
    return Object.values(groups).sort((a, b) => b.total - a.total);
  }, [state.invoices, state.suppliers]);

  return <div className="fade-up">
    <PageHeader kicker="Amministrazione" title="Fatture fornitori"
      subtitle="Gestisci le fatture in entrata con allegato PDF, archivia per fornitore, filtra per data e importo."
      right={<Btn variant="accent" size="lg" icon={Plus} onClick={() => setShowForm(true)}>Nuova fattura</Btn>} />

    {showForm && <InvoiceForm state={state} setState={setState} onClose={() => setShowForm(false)} />}
    {previewInv && <InvoicePreview invoice={previewInv} state={state} onClose={() => setPreviewInv(null)} />}

    {confirmDel && (
      <ConfirmModal
        open
        danger
        title="Archiviare la fattura?"
        message={`Stai per archiviare la fattura ${confirmDel.number} di ${confirmDel.fornitore} (${fmt.eur(confirmDel.amount)}).\n\nNon verrà cancellata: rimane conservata 10 anni per obbligo civilistico-fiscale (art. 2220 c.c. e DPR 633/72). Resterà visibile nella tab "Archiviate" e potrai ripristinarla in qualunque momento.\n\nDDT e movimenti di magazzino non vengono toccati.`}
        confirmText="Archivia"
        cancelText="Annulla"
        requireType={confirmDel.amount > 1000 ? "ARCHIVIA" : null}
        onConfirm={confirmDelOp}
        onCancel={() => setConfirmDel(null)}
      >
        <Field label="Motivazione (consigliata)">
          <Textarea value={delReason} onChange={e => setDelReason(e.target.value)} rows={2} maxLength={200} placeholder="Es: doppia registrazione, fornitore errato, importo errato, ..." />
        </Field>
      </ConfirmModal>
    )}

    <div className="stagger" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
      <StatCard kicker="Da pagare" value={fmt.eurShort(totDue)} unit="totale" trend={`${activeInvoices.filter(i => i.status === "da_pagare").length} fatture aperte`} accent={C.terra} icon={Receipt} />
      <StatCard kicker="Pagate 30g" value={fmt.eurShort(totPaid30d)} unit="ultimo mese" trend={`${activeInvoices.filter(i => i.status === "pagata" && daysBetween(i.paidAt, TODAY) <= 30).length} fatture chiuse`} accent={C.olive} icon={CheckCircle2} />
      <StatCard kicker="Prossime scadenze" value={activeInvoices.filter(i => i.status === "da_pagare" && daysBetween(TODAY, i.dueDate) <= 7).length} unit="≤ 7 gg" trend="controlla" accent={C.amber} icon={Clock} />
      <StatCard kicker="Risultato filtri" value={fmt.eurShort(totFiltered)} unit={`${filtered.length} fatture`} trend="somma selezione corrente" accent={C.ink} icon={BarChart3} />
    </div>

    {bySupplier.length > 0 && <div style={{ marginBottom: 24 }}>
      <SectionHeader title="Archivio per fornitore" subtitle="Click per filtrare le fatture di quel fornitore" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
        {bySupplier.map(g => {
          const isSel = supplierFilter === g.supplier?.id;
          const pdfCount = g.invoices.filter(i => i.attachments?.length > 0).length;
          return <button key={g.supplier?.id} onClick={() => setSupplierFilter(isSel ? "all" : g.supplier?.id)} className="tx" style={{ padding: 12, background: isSel ? C.ink : C.white, color: isSel ? C.paper : C.ink, border: `1px solid ${isSel ? C.ink : C.rule}`, borderRadius: 2, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
            <div className="f-body" style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.supplier?.name || "Senza fornitore"}</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 6 }}>
              <div>
                <div className="f-mono" style={{ fontSize: 10, opacity: 0.7, letterSpacing: "0.15em" }}>{g.invoices.length} FATT · {pdfCount} PDF</div>
                <div className="f-mono" style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{fmt.eurShort(g.total)}</div>
              </div>
              {isSel && <X size={14} />}
            </div>
          </button>;
        })}
      </div>
    </div>}

    <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 180, display: "flex", alignItems: "center", gap: 8, background: C.white, border: `1px solid ${C.rule}`, padding: "6px 12px", borderRadius: 2 }}>
        <Search size={14} color={C.muted} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca n° fattura o fornitore..." className="f-body" style={{ border: "none", outline: "none", flex: 1, fontSize: 12, background: "transparent" }} />
        {search && <IconBtn icon={X} size={12} onClick={() => setSearch("")} />}
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.muted }}>
        Da <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: "6px 10px", fontSize: 11 }} />
      </label>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.muted }}>
        A <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: "6px 10px", fontSize: 11 }} />
      </label>
      {(search || dateFrom || dateTo || supplierFilter !== "all") && <Btn variant="ghost" size="sm" icon={X} onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setSupplierFilter("all"); }}>Reset filtri</Btn>}
    </div>

    <Tabs value={filter} onChange={setFilter} tabs={[
      { id: "all", label: "Tutte", count: activeInvoices.length },
      { id: "da_pagare", label: "Da pagare", count: activeInvoices.filter(i => i.status === "da_pagare").length },
      { id: "pagata", label: "Pagate", count: activeInvoices.filter(i => i.status === "pagata").length },
      { id: "archiviate", label: "Archiviate", count: state.invoices.filter(i => i.deletedAt).length },
    ]} />

    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {filtered.length === 0 ? <EmptyState icon={Receipt} title="Nessuna fattura corrisponde ai filtri" /> : filtered.map(inv => {
        const sup = state.suppliers.find(s => s.id === inv.supplierId);
        const daysLeft = daysBetween(TODAY, inv.dueDate);
        const overdue = inv.status === "da_pagare" && daysLeft < 0;
        return <div key={inv.id} style={{ background: C.white, border: `1px solid ${overdue ? C.terra : C.rule}`, borderRadius: 2, padding: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, minWidth: 0 }}>
            <div style={{ width: 42, height: 42, background: inv.status === "pagata" ? C.oliveWash : C.terraWash, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, flexShrink: 0 }}>
              <Receipt size={18} color={inv.status === "pagata" ? C.olive : C.terra} strokeWidth={1.5} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span className="f-mono" style={{ fontSize: 12, color: C.ink, fontWeight: 600 }}>{inv.number}</span>
                <Badge status={inv.status} />
                {overdue && <span className="f-mono" style={{ fontSize: 10, color: C.terra, fontWeight: 600, letterSpacing: "0.15em" }}>⚠ SCADUTA DA {-daysLeft}g</span>}
                {inv.receiptIds?.length > 0 && <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>← {inv.receiptIds.length} DDT</span>}
                {inv.attachments?.length > 0 && <span className="f-mono" style={{ fontSize: 10, color: C.teal, padding: "1px 5px", background: C.tealWash, borderRadius: 2, display: "inline-flex", alignItems: "center", gap: 3 }}><FileText size={10} /> {inv.attachments.length} PDF</span>}
              </div>
              <div className="f-body" style={{ fontSize: 14, color: C.ink, fontWeight: 500, marginTop: 2 }}>{sup?.name}</div>
              <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Data {fmt.date(inv.date)} · Scadenza {fmt.date(inv.dueDate)}{inv.paidAt && ` · Pagata ${fmt.date(inv.paidAt)}`}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <div style={{ textAlign: "right" }}>
              <div className="f-mono" style={{ fontSize: 18, color: C.ink, fontWeight: 600 }}>{fmt.eur(inv.amount)}</div>
              {inv.status === "da_pagare" && <div className="f-mono" style={{ fontSize: 11, color: daysLeft < 0 ? C.terra : (daysLeft <= 7 ? C.amber : C.muted) }}>{daysLeft < 0 ? "scaduta" : `in ${daysLeft}g`}</div>}
            </div>
            {inv.attachments?.length > 0 && <IconBtn icon={FileText} size={15} title="Apri PDF" onClick={() => setPreviewInv(inv)} />}
            {!inv.deletedAt && inv.status === "da_pagare" && <Btn size="sm" variant="olive" icon={CheckCircle2} onClick={() => markPaid(inv.id)}>Paga</Btn>}
            {!inv.deletedAt && <IconBtn icon={Trash2} size={15} title="Archivia fattura" onClick={() => requestDel(inv)} />}
            {inv.deletedAt && <>
              <div className="f-mono" style={{ fontSize: 10, color: C.muted, textAlign: "right", lineHeight: 1.3 }}>
                Archiviata {fmt.date(inv.deletedAt)}<br/>da {inv.deletedBy || "?"}
                {inv.deletedReason && <><br/><span style={{ fontStyle: "italic" }}>“{inv.deletedReason}”</span></>}
              </div>
              <Btn size="sm" variant="ghost" icon={RefreshCw} onClick={() => restoreInv(inv)}>Ripristina</Btn>
            </>}
          </div>
        </div>;
      })}
    </div>
  </div>;
}

function InvoicePreview({ invoice, state, onClose }) {
  const sup = state.suppliers.find(s => s.id === invoice.supplierId);
  const att = invoice.attachments?.[0];

  const download = (attachment) => {
    if (!attachment.dataUrl) { alert("File non disponibile per download"); return; }
    const a = document.createElement("a");
    a.href = attachment.dataUrl;
    a.download = attachment.name || "fattura.pdf";
    a.click();
  };

  return <Modal onClose={onClose} maxWidth={800} title={`Fattura ${invoice.number}`} subtitle={sup?.name}
    footer={<Btn variant="ghost" onClick={onClose}>Chiudi</Btn>}>
    <div style={{ padding: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16, padding: 12, background: C.paperDeep, borderRadius: 2 }}>
        <div><div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>NUMERO</div><div className="f-mono" style={{ fontSize: 13, fontWeight: 600 }}>{invoice.number}</div></div>
        <div><div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>DATA</div><div className="f-mono" style={{ fontSize: 13, fontWeight: 600 }}>{fmt.date(invoice.date)}</div></div>
        <div><div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>IMPORTO</div><div className="f-mono" style={{ fontSize: 13, fontWeight: 600 }}>{fmt.eur(invoice.amount)}</div></div>
        <div><div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>STATO</div><div><Badge status={invoice.status} /></div></div>
      </div>

      {!invoice.attachments || invoice.attachments.length === 0 ? <div style={{ padding: 40, textAlign: "center", background: C.paperDeep, borderRadius: 2 }}>
        <FileText size={40} color={C.muted} strokeWidth={1.2} style={{ margin: "0 auto 12px", display: "block" }} />
        <div className="f-body" style={{ fontSize: 13, color: C.muted }}>Nessun PDF allegato a questa fattura</div>
      </div>
      : <div>
        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, marginBottom: 8 }}>ALLEGATI ({invoice.attachments.length})</div>
        {invoice.attachments.map((a, i) => <div key={i} style={{ marginBottom: 10 }}>
          <div style={{ padding: 10, background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <FileText size={20} color={C.teal} />
            <div style={{ flex: 1 }}>
              <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{a.name}</div>
              <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>{(a.size / 1024).toFixed(1)} KB · aggiunto {fmt.date(a.addedAt?.slice(0, 10))}</div>
            </div>
            <Btn variant="ghost" size="sm" icon={Download} onClick={() => download(a)}>Scarica</Btn>
          </div>
          {a.dataUrl && a.type === "application/pdf" && <iframe src={a.dataUrl} sandbox="" referrerPolicy="no-referrer" style={{ width: "100%", height: 500, border: `1px solid ${C.rule}`, borderRadius: 2 }} title={a.name}></iframe>}
          {a.dataUrl && a.type?.startsWith("image/") && <img src={a.dataUrl} alt={a.name} style={{ maxWidth: "100%", border: `1px solid ${C.rule}`, borderRadius: 2 }} />}
        </div>)}
      </div>}
    </div>
  </Modal>;
}

function InvoiceForm({ state, setState, onClose }) {
  const [supplierId, setSupplierId] = useState(state.suppliers[0]?.id || "");
  const [number, setNumber] = useState("");
  const [date, setDate] = useState(TODAY);
  const [dueDate, setDueDate] = useState(new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [receiptIds, setReceiptIds] = useState([]);
  const [cascadeUpdate, setCascadeUpdate] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();
  const toast = useToast();

  const handleFiles = async (files) => {
    // Pipeline sicura: validazione MIME via magic bytes, size cap singolo
    // (10 MB), cap cumulativo per record (25 MB), lettura come dataUrl.
    // Rifiuta file mascherati (es. .html.pdf) e tipi non consentiti.
    const result = await processAttachments(files, attachments);
    if (result.valid.length > 0) {
      setAttachments(prev => [...prev, ...result.valid]);
    }
    result.rejected.forEach(r => {
      toast({ type: "error", message: `${r.name}: ${r.reason}` });
    });
    if (result.valid.length === 0 && result.rejected.length === 0) {
      toast({ type: "warning", message: "Nessun file allegato" });
    } else if (result.valid.length > 0) {
      toast({ type: "success", message: `${result.valid.length} file caricato${result.valid.length > 1 ? "i" : ""}` });
    }
  };

  const availReceipts = state.receipts.filter(r => r.supplierId === supplierId && !r.invoiceNumber);

  // Differenze prezzi rispetto ai baseProducts attuali (considerando le linee di tutti i DDT selezionati)
  const priceDiffs = useMemo(() => {
    if (receiptIds.length === 0) return [];
    const allLines = [];
    receiptIds.forEach(rid => {
      const r = state.receipts.find(x => x.id === rid);
      if (r) r.lines.forEach(ln => allLines.push({ ...ln, ddtNumber: r.ddtNumber }));
    });
    return engine.detectPriceDiffs(state, allLines);
  }, [receiptIds, state]);

  const increases = priceDiffs.filter(d => d.diff > 0);
  const bigIncreases = increases.filter(d => d.diffPct > 10);

  // Piatti impattati
  const dishesImpacted = useMemo(() => {
    if (priceDiffs.length === 0) return 0;
    const bpIdsChanged = new Set(priceDiffs.map(d => d.bp.id));
    return (state.dishesReal || []).filter(d =>
      (d.ingredienti || []).some(ing => ing.bpId && bpIdsChanged.has(ing.bpId))
    ).length;
  }, [priceDiffs, state.dishesReal]);

  // Fix #5 (Patch C1): scorporo IVA per riga DDT collegato.
  // Aggrega per aliquota IVA del baseProduct → vatBreakdown[]: [{rate, taxable, vatAmount}].
  // Default 10% (somministrazione) se bp non ha vatRate. I prezzi DDT sono NETTI IVA.
  const computeVatBreakdown = (linkedReceiptIds) => {
    const map = new Map();
    const defaultRate = Number(state.settings?.vatRatePurchase) || 0.10;
    linkedReceiptIds.forEach(rid => {
      const r = state.receipts.find(x => x.id === rid);
      if (!r) return;
      (r.lines || []).forEach(line => {
        const bp = (state.baseProducts || []).find(b => b.id === line.itemId);
        const rate = (bp && typeof bp.vatRate === "number" && Number.isFinite(bp.vatRate))
          ? bp.vatRate
          : defaultRate;
        const taxable = (Number(line.qty) || 0) * (Number(line.pricePerUnit) || 0);
        if (taxable <= 0) return;
        const vatAmount = taxable * rate;
        const key = rate.toFixed(4);  // chiavi stabili per aliquota
        const prev = map.get(key) || { rate, taxable: 0, vatAmount: 0 };
        map.set(key, { rate, taxable: prev.taxable + taxable, vatAmount: prev.vatAmount + vatAmount });
      });
    });
    const breakdown = [...map.values()].sort((a, b) => a.rate - b.rate);
    const taxable = breakdown.reduce((s, b) => s + b.taxable, 0);
    const vatTotal = breakdown.reduce((s, b) => s + b.vatAmount, 0);
    return { breakdown, taxable, vatTotal };
  };

  const vatPreview = useMemo(() => computeVatBreakdown(receiptIds), [receiptIds, state.receipts, state.baseProducts]);
  // Riconciliazione importo dichiarato vs lordo calcolato dai DDT.
  const declaredAmount = parseFloat(amount) || 0;
  const computedGross = vatPreview.taxable + vatPreview.vatTotal;
  const reconcileDiff = Math.abs(declaredAmount - computedGross);
  const reconcileMismatch = receiptIds.length > 0 && declaredAmount > 0 && reconcileDiff > Math.max(0.01 * Math.abs(declaredAmount), 0.05);

  const submit = () => {
    if (!number.trim() || !amount) { toast({ type: "error", message: "Inserisci numero fattura e importo" }); return; }
    const invId = uid("fatt_");
    // Fix #5 (Patch C1): vatBreakdown calcolato dalle righe dei DDT collegati.
    const { breakdown: vatBreakdown, taxable, vatTotal } = computeVatBreakdown(receiptIds);
    const inv = {
      id: invId, supplierId, number: number.trim(), date, dueDate,
      amount: parseFloat(amount),        // lordo dichiarato dall'utente (resta come campo storico)
      status: "da_pagare", receiptIds, attachments, paidAt: null,
      // Nuovi campi (Fix #5 Patch C1) — possono essere vuoti se nessun DDT collegato.
      vatBreakdown,
      taxable: Math.round(taxable * 100) / 100,
      vatTotal: Math.round(vatTotal * 100) / 100,
    };
    let nextState = {
      ...state,
      invoices: [...state.invoices, inv],
      receipts: state.receipts.map(r => receiptIds.includes(r.id) ? { ...r, invoiceNumber: number.trim() } : r),
    };
    // Cascade con priceHistory. Per le fatture si usa il caloOverride salvato sulle righe
    // del DDT (Fix #5 Patch B2): se la consegna era "gia' lavorata" il pReale del bp deve
    // riflettere quella scelta, non il caloTot globale.
    if (cascadeUpdate && priceDiffs.length > 0) {
      const caloByBp = new Map();
      receiptIds.forEach(rid => {
        const r = state.receipts.find(x => x.id === rid);
        if (!r) return;
        (r.lines || []).forEach(ln => {
          if (!ln.itemId) return;
          if (typeof ln.caloOverride === "number" && Number.isFinite(ln.caloOverride)) {
            caloByBp.set(ln.itemId, Math.max(0, Math.min(0.95, ln.caloOverride)));
          }
        });
      });
      const updates = priceDiffs.map(d => ({
        bpId: d.bp.id, newPrice: d.newPrice,
        caloUsed: caloByBp.has(d.bp.id) ? caloByBp.get(d.bp.id) : undefined,
        source: "fattura", refId: invId, supplierId, date,
      }));
      nextState.baseProducts = engine.applyPriceUpdate(nextState.baseProducts, updates);
    }
    setState(nextState);
    const baseMsg = cascadeUpdate && priceDiffs.length > 0
      ? `Prezzi aggiornati su ${priceDiffs.length} prodotti base · impatto su ${dishesImpacted} piatti`
      : "Fattura registrata";
    if (reconcileMismatch) {
      toast({ type: "warning", title: "Discrepanza importo fattura", message: `Dichiarato ${fmt.eur(declaredAmount)} — calcolato dai DDT ${fmt.eur(computedGross)} (diff ${fmt.eur(reconcileDiff)}). Verifica.` });
    } else {
      toast({ type: "success", title: "Fattura registrata", message: baseMsg });
    }
    onClose();
  };

  return <Modal onClose={onClose} maxWidth={720} subtitle="Registra" title="Fattura fornitore"
    footer={<><Btn variant="ghost" onClick={onClose}>Annulla</Btn><Btn variant="accent" icon={CheckCircle2} onClick={submit}>{cascadeUpdate && priceDiffs.length > 0 ? `Registra + aggiorna ${priceDiffs.length} prezzi` : "Registra"}</Btn></>}>
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <Field label="Fornitore" required><Select value={supplierId} onChange={e => { setSupplierId(e.target.value); setReceiptIds([]); }}>{state.suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Numero fattura" required><Input value={number} onChange={e => setNumber(e.target.value)} placeholder="FT/2026/..." /></Field>
        <Field label="Importo (€)" required><Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} /></Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Data fattura" required><Input type="date" value={date} onChange={e => setDate(e.target.value)} /></Field>
        <Field label="Scadenza pagamento" required><Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} /></Field>
      </div>
      {availReceipts.length > 0 && <Field label="Bolle DDT collegate" hint="Seleziona le bolle di ricevimento che questa fattura copre">
        <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: 10, background: C.paperDeep, borderRadius: 2, maxHeight: 200, overflowY: "auto" }}>
          {availReceipts.map(r => <label key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 8px", background: receiptIds.includes(r.id) ? C.white : "transparent", borderRadius: 2, cursor: "pointer" }}>
            <input type="checkbox" checked={receiptIds.includes(r.id)} onChange={e => setReceiptIds(e.target.checked ? [...receiptIds, r.id] : receiptIds.filter(x => x !== r.id))} />
            <span className="f-mono" style={{ fontSize: 12, color: C.ink }}>{r.ddtNumber}</span>
            <span className="f-body" style={{ fontSize: 12, color: C.muted }}>{fmt.date(r.date)}</span>
            <span className="f-mono" style={{ fontSize: 12, color: C.ink, marginLeft: "auto", fontWeight: 600 }}>{fmt.eur(r.lines.reduce((s, ln) => s + ln.qty * ln.pricePerUnit, 0))}</span>
          </label>)}
        </div>
      </Field>}

      {/* Fix #5 Patch C1: scorporo IVA per aliquota — calcolato dai DDT collegati */}
      {receiptIds.length > 0 && vatPreview.breakdown.length > 0 && (
        <div style={{ background: reconcileMismatch ? C.amberWash : C.paperDeep, border: `1px solid ${reconcileMismatch ? C.amber : C.ruleSoft}`, borderRadius: 2, padding: 14 }}>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", marginBottom: 10, fontWeight: 600 }}>
            SCORPORO IVA (DA RIGHE DDT) {reconcileMismatch && <span style={{ color: C.amberDeep }}>· DISCREPANZA</span>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
            {vatPreview.breakdown.map(b => (
              <div key={b.rate.toFixed(4)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 8px", background: C.white, borderRadius: 2 }}>
                <span className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>IVA {(b.rate * 100).toFixed(0)}%</span>
                <div className="f-mono" style={{ fontSize: 12, color: C.inkSoft, display: "flex", gap: 14 }}>
                  <span>imponibile <strong style={{ color: C.ink }}>{fmt.eur(b.taxable)}</strong></span>
                  <span>+ IVA <strong style={{ color: C.ink }}>{fmt.eur(b.vatAmount)}</strong></span>
                  <span>= <strong style={{ color: C.ink }}>{fmt.eur(b.taxable + b.vatAmount)}</strong></span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 8px", background: C.white, borderTop: `1px solid ${C.ruleSoft}`, marginTop: 4, borderRadius: 2 }}>
            <span className="f-mono" style={{ fontSize: 11, color: C.ink, letterSpacing: "0.15em", fontWeight: 600 }}>TOTALE</span>
            <div className="f-mono" style={{ fontSize: 12, color: C.ink, display: "flex", gap: 14, fontWeight: 600 }}>
              <span>imp. {fmt.eur(vatPreview.taxable)}</span>
              <span>IVA {fmt.eur(vatPreview.vatTotal)}</span>
              <span>lordo {fmt.eur(computedGross)}</span>
            </div>
          </div>
          {reconcileMismatch && (
            <div className="f-body" style={{ fontSize: 11, color: C.amberDeep, marginTop: 8, padding: 8, background: C.white, borderLeft: `3px solid ${C.amber}`, borderRadius: 2 }}>
              ⚠ Discrepanza fra importo dichiarato <strong>{fmt.eur(declaredAmount)}</strong> e lordo calcolato dai DDT <strong>{fmt.eur(computedGross)}</strong> (diff {fmt.eur(reconcileDiff)}). Cause comuni: aliquote IVA dei prodotti non aggiornate, righe DDT non incluse, omaggi / sconti in fattura.
            </div>
          )}
        </div>
      )}

      {priceDiffs.length > 0 && <div style={{ background: bigIncreases.length > 0 ? C.terraWash : C.amberWash, border: `1px solid ${bigIncreases.length > 0 ? C.terra : C.amber}`, borderRadius: 2, padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          {bigIncreases.length > 0 ? <AlertTriangle size={16} color={C.terra} /> : <Zap size={16} color={C.amberDeep} />}
          <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 600, flex: 1 }}>
            {bigIncreases.length > 0
              ? `⚠ ATTENZIONE: ${bigIncreases.length} aumenti > 10% rispetto al prezzo attuale`
              : `${priceDiffs.length} prezzi diversi dall'attuale`}
          </div>
          {dishesImpacted > 0 && <span className="f-mono" style={{ fontSize: 10, color: bigIncreases.length > 0 ? C.terra : C.amberDeep, letterSpacing: "0.1em", padding: "2px 8px", background: C.white, borderRadius: 2, fontWeight: 600 }}>IMPATTO: {dishesImpacted} PIATTI</span>}
        </div>
        {bigIncreases.length > 0 && <div style={{ fontSize: 11, color: C.terra, marginBottom: 10, padding: 8, background: C.white, borderLeft: `3px solid ${C.terra}`, borderRadius: 2 }}>
          {bigIncreases.slice(0, 5).map((d, i) => <div key={i} style={{ marginBottom: 2, fontWeight: 500 }}>
            <strong>{(d.bp.display || d.bp.nome).slice(0, 40)}</strong>: {fmt.eur(d.oldPrice)} → <strong>{fmt.eur(d.newPrice)}</strong> (<strong>+{d.diffPct.toFixed(1)}%</strong>)
          </div>)}
        </div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10, maxHeight: 180, overflowY: "auto" }}>
          {priceDiffs.slice(0, 8).map((d, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 8px", background: C.white, borderRadius: 2, fontSize: 12 }}>
            <span className="f-body" style={{ flex: 1, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(d.bp.display || d.bp.nome).slice(0, 40)}</span>
            <span className="f-mono" style={{ color: C.muted, textDecoration: "line-through" }}>{fmt.eur(d.oldPrice)}</span>
            <ArrowRight size={10} color={C.muted} />
            <span className="f-mono" style={{ color: C.ink, fontWeight: 600 }}>{fmt.eur(d.newPrice)}</span>
            <span className="f-mono" style={{ color: d.diff > 0 ? C.terra : C.olive, fontWeight: 600, minWidth: 50, textAlign: "right" }}>{d.diff > 0 ? "+" : ""}{d.diffPct.toFixed(1)}%</span>
          </div>)}
          {priceDiffs.length > 8 && <div className="f-body" style={{ fontSize: 11, color: C.muted, fontStyle: "italic", textAlign: "center", padding: 4 }}>...e altri {priceDiffs.length - 8}</div>}
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: 8, background: C.white, borderRadius: 2 }}>
          <input type="checkbox" checked={cascadeUpdate} onChange={e => setCascadeUpdate(e.target.checked)} />
          <div style={{ flex: 1 }}>
            <div className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>Aggiorna prezzi dei prodotti base + salva storico</div>
            <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Se le bolle avevano già aggiornato i prezzi, questa azione è idempotente e non salva nuove voci nello storico.</div>
          </div>
        </label>
      </div>}

      <Field label="Allegati (PDF fattura, scansioni)" hint="Il file viene salvato nel database del sistema, visualizzabile e scaricabile in qualsiasi momento">
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
          style={{ border: `2px dashed ${dragOver ? C.teal : C.rule}`, background: dragOver ? C.tealWash : C.paperDeep, padding: 18, borderRadius: 2, textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}>
          <Upload size={22} color={C.muted} style={{ margin: "0 auto 6px", display: "block" }} />
          <div className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>Trascina qui il PDF della fattura o clicca per sceglierlo</div>
          <div className="f-body" style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>PDF, JPG, PNG · max 10 MB per file</div>
          <input ref={fileRef} type="file" accept=".pdf,image/*" multiple style={{ display: "none" }} onChange={e => handleFiles(e.target.files)} />
        </div>
        {attachments.length > 0 && <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
          {attachments.map((a, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
            <FileText size={14} color={C.teal} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="f-body" style={{ fontSize: 12, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</div>
              <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>{(a.size / 1024).toFixed(1)} KB</div>
            </div>
            <IconBtn icon={Trash2} size={13} onClick={() => setAttachments(attachments.filter((_, j) => j !== i))} />
          </div>)}
        </div>}
      </Field>
    </div>
  </Modal>;
}


// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      MAGAZZINO & LOTTI                                     ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function Magazzino({ state, setState, onNavigate, initial }) {
  const [search, setSearch] = useState(initial?.search || "");
  const [catFilter, setCatFilter] = useState("all");
  const [expandedItem, setExpandedItem] = useState(null);
  const [labelLot, setLabelLot] = useState(null);
  const [acceptLot, setAcceptLot] = useState(null);   // lotto in_accettazione da confermare
  const [onlyInStock, setOnlyInStock] = useState(true);

  // Fix #5 (Patch A3): valore stock = CMP × qty per lotti attivi, usando
  // i prezzi a cui sono stati EFFETTIVAMENTE acquistati (lot.unitCost).
  const itemsWithStock = engine.getItems(state).map(it => {
    const lots = state.lots.filter(l => l.itemId === it.id && l.status !== "esaurito");
    const stock = lots.reduce((s, l) => s + (Number(l.qtyRemaining) || 0), 0);
    const cmpInfo = engine.weightedAvgCost(state, it.id);
    return { ...it, stock, lotsCount: lots.length, lots, cmp: cmpInfo.cmp, stockValue: cmpInfo.totalValue, cmpFallback: cmpInfo.fallback };
  });

  // Fix #6: pre-calcola coverage giorni per OGNI item in un'unica passata.
  // Un solo useMemo aggregato per evitare O(N) chiamate ricorsive nel render
  // (su iPad con 150 BP × bpConsumption costerebbe ~30k operazioni per frame).
  const coverageMap = useMemo(() => {
    const m = new Map();
    for (const it of itemsWithStock) {
      m.set(it.id, engine.stats.coverageDays(state, it.id, { leadTimeDays: 2, safetyDays: 1 }));
    }
    return m;
  }, [state.tableReceipts, state.lots, state.dishesReal, itemsWithStock.length]);

  const filtered = itemsWithStock.filter(it => {
    if (onlyInStock && it.stock <= 0) return false;
    if (catFilter !== "all" && it.category !== catFilter) return false;
    if (search && !it.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Totale magazzino aggregato sui lotti disponibili (CMP).
  const totaleMagazzino = filtered.reduce((s, it) => s + (Number(it.stockValue) || 0), 0);
  const fallbackCount = filtered.filter(it => it.cmpFallback && it.stockValue > 0).length;

  return <div className="fade-up">
    <PageHeader kicker="Giacenze · Lotti · Tracciabilità" title="Magazzino"
      subtitle="Stock per articolo con giacenza per lotto. Ogni lotto è cliccabile per vedere movimenti, tracciabilità e stampa etichetta."
      right={<Btn variant="ghost" icon={Download} onClick={() => window.print()}>Esporta lista</Btn>} />

    {labelLot && <LabelPrint lot={labelLot} state={state} onClose={() => setLabelLot(null)} />}
    {acceptLot && <AcceptLotModal lot={acceptLot} state={state} setState={setState} onClose={() => setAcceptLot(null)} />}

    <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center" }}>
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, background: C.white, border: `1px solid ${C.rule}`, padding: "8px 14px", borderRadius: 2 }}>
        <Search size={15} color={C.muted} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca articolo o lotto..." className="f-body" style={{ border: "none", outline: "none", flex: 1, fontSize: 13, background: "transparent", color: C.ink }} />
        {search && <IconBtn icon={X} size={13} onClick={() => setSearch("")} />}
      </div>
      <Select value={catFilter} onChange={e => setCatFilter(e.target.value)}>
        <option value="all">Tutte categorie</option>
        {Object.entries(cats).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
      </Select>
      <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, cursor: "pointer" }}>
        <input type="checkbox" checked={onlyInStock} onChange={e => setOnlyInStock(e.target.checked)} />
        <span className="f-body" style={{ fontSize: 12, color: C.inkSoft }}>Solo con stock</span>
      </label>
    </div>

    {/* Fix #5 Patch A3: totale valore magazzino con CMP OIC 13 */}
    {totaleMagazzino > 0 && (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", background: C.paperDeep, border: `1px solid ${C.ruleSoft}`, borderRadius: 2, marginBottom: 16 }}>
        <div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>VALORE MAGAZZINO (CMP)</div>
          <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, marginTop: 2 }}>
            Costo Medio Ponderato sui prezzi pagati per ciascun lotto attivo (OIC 13). {fallbackCount > 0 && <span style={{ color: C.amberDeep }}>· {fallbackCount} articoli con stima (lotti pre-fix)</span>}
          </div>
        </div>
        <div className="f-display" style={{ fontSize: 22, color: C.ink, fontWeight: 600 }}>{fmt.eur(totaleMagazzino)}</div>
      </div>
    )}

    <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {filtered.length === 0 ? <EmptyState icon={Warehouse} title="Nessun articolo corrisponde" body="Prova a modificare i filtri" />
      : filtered.map(it => {
        const low = it.stock < it.minStock;
        const exp = expandedItem === it.id;
        const Cat = cats[it.category];
        return <div key={it.id} style={{ background: C.white, border: `1px solid ${low ? C.terra : C.rule}`, borderRadius: 2 }}>
          <button onClick={() => setExpandedItem(exp ? null : it.id)} className="tx" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "14px 20px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 36, height: 36, background: Cat?.color + "22" || C.paperDeep, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2 }}>
                {Cat && <Cat.icon size={16} color={Cat.color} strokeWidth={1.7} />}
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="f-body" style={{ fontSize: 14, color: C.ink, fontWeight: 500 }}>{it.name}</span>
                  {low && <Badge status="urgente">SOTTO SCORTA</Badge>}
                  {it.allergens?.length > 0 && <span className="f-mono" style={{ fontSize: 9, color: C.amber, background: C.amberWash, padding: "1px 5px", borderRadius: 2, letterSpacing: "0.1em" }}>ALLERGENI</span>}
                </div>
                <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.1em", marginTop: 3 }}>{Cat?.label} · min: {it.minStock} {it.unit} · {it.lotsCount} lotti</div>
                {/* Fix #6: copertura giorni con CI 80% e alert rischio rottura */}
                {(() => {
                  const cov = coverageMap.get(it.id);
                  if (!cov || cov.n === 0) {
                    return it.stock > 0 ? <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>Nessuno storico consumi</div> : null;
                  }
                  if (!Number.isFinite(cov.days)) return null;
                  const daysLabel = cov.days >= 999 ? "∞" : cov.days.toFixed(1);
                  let ciLabel;
                  if (cov.fallback) {
                    ciLabel = "(stima preliminare · n<7gg)";
                  } else if (Number.isFinite(cov.ciLow) && Number.isFinite(cov.ciHigh)) {
                    ciLabel = `(${cov.ciLow.toFixed(1)}–${cov.ciHigh.toFixed(1)}gg · IC 80%)`;
                  } else {
                    ciLabel = `(n=${cov.n}gg storico)`;
                  }
                  return (
                    <div className="f-body" style={{ fontSize: 11, color: cov.riskOutOfStock ? C.terra : C.inkSoft, fontWeight: cov.riskOutOfStock ? 600 : 400, marginTop: 3 }}>
                      Copre <strong>{daysLabel}gg</strong> {ciLabel}
                      {cov.riskOutOfStock && <span style={{ color: C.terra, marginLeft: 6 }}>· RISCHIO ROTTURA</span>}
                    </div>
                  );
                })()}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
              {/* Fix #5 Patch A3: CMP + valore stock dai prezzi reali di acquisto */}
              {it.stockValue > 0 && (
                <div style={{ textAlign: "right" }}>
                  <div className="f-mono" style={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>{fmt.eur(it.stockValue)}</div>
                  <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.1em" }}>
                    CMP {fmt.eur(it.cmp)}/{it.unit}
                    {it.cmpFallback && <span title="Lotti pre-fix senza snapshot prezzo: stima sul prezzo attuale del prodotto base" style={{ color: C.amberDeep, marginLeft: 4 }}> · stima</span>}
                  </div>
                </div>
              )}
              <div style={{ textAlign: "right" }}>
                <div className="f-display" style={{ fontSize: 22, color: low ? C.terra : C.ink, fontWeight: 600, letterSpacing: "-0.01em" }}>{it.stock.toFixed(it.stock % 1 === 0 ? 0 : 1)}</div>
                <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em", textTransform: "uppercase" }}>{it.unit}</div>
              </div>
              <ChevronDown size={16} color={C.muted} style={{ transform: exp ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
            </div>
          </button>
          {exp && <div className="fade-in" style={{ padding: "0 20px 20px", borderTop: `1px solid ${C.ruleSoft}` }}>
            <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, padding: "12px 0 8px" }}>LOTTI ATTIVI</div>
            {it.lots.length === 0 ? <div className="f-body" style={{ fontSize: 12, color: C.muted, padding: 10, textAlign: "center" }}>Nessun lotto attivo</div>
            : <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {it.lots.sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt)).map(l => {
                const daysLeft = daysBetween(TODAY, l.expiresAt);
                const urgent = daysLeft <= 2;
                const pct = (l.qtyRemaining / l.qtyInitial) * 100;
                const pendingAcceptance = l.status === "in_accettazione" || l.status === "quarantena";
                const borderColor = pendingAcceptance ? C.amber : (urgent ? C.terra : C.ruleSoft);
                return <div key={l.id} style={{ background: pendingAcceptance ? C.amberWash : C.paperSoft, padding: "10px 12px", borderRadius: 2, border: `1px solid ${borderColor}`, display: "flex", alignItems: "center", gap: 14 }}>
                  <button onClick={() => onNavigate({ view: "lot", lotId: l.id })} className="f-mono tx" style={{ fontSize: 11, color: C.ink, fontWeight: 600, letterSpacing: "0.08em", border: "none", background: "transparent", cursor: "pointer", padding: 0 }}>{l.id} →</button>
                  <Badge status={l.status} size="sm" />
                  {l.parent && <span className="f-mono" style={{ fontSize: 9, color: C.plum, background: C.plumWash, padding: "1px 5px", borderRadius: 2 }}>← {l.parent}</span>}
                  <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1, maxWidth: 120 }}><ProgressBar value={pct} color={urgent ? C.terra : C.teal} /></div>
                    <span className="f-mono" style={{ fontSize: 11, color: C.muted }}>{l.qtyRemaining.toFixed(1)}/{l.qtyInitial.toFixed(1)}</span>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="f-mono" style={{ fontSize: 11, color: urgent ? C.terra : C.ink, fontWeight: 600 }}>{daysLeft <= 0 ? "SCADUTO" : `${daysLeft}g`}</div>
                    <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>{fmt.date(l.expiresAt)}</div>
                  </div>
                  {pendingAcceptance && <Btn size="xs" variant="amber" onClick={() => setAcceptLot(l)}>Accetta in giacenza</Btn>}
                  <IconBtn icon={Tag} title="Stampa etichetta" onClick={() => setLabelLot(l)} />
                </div>;
              })}
            </div>}
          </div>}
        </div>;
      })}
    </div>
  </div>;
}

function LotDetail({ state, setState, lotId, onNavigate }) {
  const lot = state.lots.find(l => l.id === lotId);
  const [showLabel, setShowLabel] = useState(false);
  const [showAccept, setShowAccept] = useState(false);

  if (!lot) return <div className="fade-up">
    <PageHeader title="Lotto non trovato" />
    <EmptyState title="Questo lotto non esiste più" action={<Btn onClick={() => onNavigate({ view: "magazzino" })}>Torna al magazzino</Btn>} />
  </div>;

  const item = engine.getItems(state).find(i => i.id === lot.itemId);
  const receipt = lot.receiptId ? state.receipts.find(r => r.id === lot.receiptId) : null;
  const supplier = receipt ? state.suppliers.find(s => s.id === receipt.supplierId) : null;
  const parent = lot.parent ? state.lots.find(l => l.id === lot.parent) : null;
  const children = state.lots.filter(l => l.parent === lot.id);
  const movements = state.lotMovements.filter(m => m.lotId === lot.id).sort((a, b) => new Date(b.at) - new Date(a.at));
  const daysLeft = daysBetween(TODAY, lot.expiresAt);
  const pct = (lot.qtyRemaining / lot.qtyInitial) * 100;

  const kitchenOutUsing = state.kitchenOutputs.filter(ko => (ko.consumedLots || []).some(cl => cl.lotId === lot.id));
  const Cat = cats[item?.category];

  const isPendingAcceptance = lot.status === "in_accettazione" || lot.status === "quarantena";

  return <div className="fade-up">
    {showLabel && <LabelPrint lot={lot} state={state} onClose={() => setShowLabel(false)} />}
    {showAccept && <AcceptLotModal lot={lot} state={state} setState={setState} onClose={() => setShowAccept(false)} />}
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
      <button onClick={() => onNavigate({ view: "magazzino" })} className="f-mono tx" style={{ fontSize: 10, letterSpacing: "0.15em", color: C.muted, background: "transparent", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
        <ChevronLeft size={12} /> MAGAZZINO
      </button>
    </div>
    <PageHeader kicker={`Lotto · ${Cat?.label}`} title={item?.name || "Lotto"}
      subtitle={<span className="f-mono">{lot.id}{lot.workOrderId && ` · da lavorazione ${lot.workOrderId}`}</span>}
      right={<div style={{ display: "flex", gap: 8 }}>
        {isPendingAcceptance && <Btn variant="amber" icon={CheckCircle2} onClick={() => setShowAccept(true)}>Accetta in giacenza</Btn>}
        <Btn variant="ghost" icon={Printer} onClick={() => setShowLabel(true)}>Etichetta</Btn>
        <Btn variant="ghost" icon={GitBranch} onClick={() => onNavigate({ view: "tracciabilita", lotId: lot.id })}>Tracciabilità</Btn>
      </div>} />

    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24, marginBottom: 24 }}>
      <div>
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 24, borderRadius: 2, marginBottom: 24 }}>
          <div style={{ display: "flex", gap: 40, marginBottom: 20 }}>
            <div style={{ flex: 1 }}>
              <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: C.muted, marginBottom: 6 }}>QUANTITÀ RIMANENTE</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <div className="f-display" style={{ fontSize: 40, color: daysLeft <= 2 ? C.terra : C.ink, fontWeight: 600, letterSpacing: "-0.02em" }}>{lot.qtyRemaining.toFixed(lot.qtyRemaining % 1 === 0 ? 0 : 2)}</div>
                <div className="f-body" style={{ fontSize: 13, color: C.muted }}>{lot.unit}</div>
                <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginLeft: 10 }}>di {lot.qtyInitial.toFixed(1)} iniziali</div>
              </div>
              <div style={{ marginTop: 10 }}><ProgressBar value={pct} color={daysLeft <= 2 ? C.terra : C.teal} height={6} /></div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: C.muted, marginBottom: 6 }}>SCADENZA</div>
              <div className="f-display" style={{ fontSize: 24, color: daysLeft <= 2 ? C.terra : C.ink, fontWeight: 500 }}>{daysLeft <= 0 ? "SCADUTO" : `${daysLeft} gg`}</div>
              <div className="f-mono" style={{ fontSize: 11, color: C.muted }}>{fmt.dateFull(lot.expiresAt)}</div>
            </div>
          </div>

          <Rule style={{ margin: "16px 0" }} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Metric label="Stato" value={<Badge status={lot.status} />} />
              <Metric label="Creato il" value={fmt.dateTime(lot.createdAt)} />
              {parent && <Metric label="Lotto padre" value={<button onClick={() => onNavigate({ view: "lot", lotId: parent.id })} className="f-mono" style={{ fontSize: 12, color: C.teal, background: "transparent", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}>{parent.id}</button>} />}
              {children.length > 0 && <Metric label="Lotti figli" value={`${children.length}`} />}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {supplier && <Metric label="Fornitore" value={supplier.name} />}
              {receipt && <Metric label="DDT" value={receipt.ddtNumber} />}
              {lot.supplierLotCode && <Metric label="Lotto fornitore" value={<span className="f-mono">{lot.supplierLotCode}</span>} />}
              {lot.expiryType && <Metric label="Tipo scadenza" value={lot.expiryType === "consumarsi_preferibilmente" ? "TMC (preferibilmente)" : "Scadenza (entro)"} />}
              {lot.origin?.faoZone && <Metric label="Zona FAO" value={lot.origin.faoZone} accent={C.teal} />}
              {lot.origin?.productionMethod && <Metric label="Metodo" value={lot.origin.productionMethod === "pescato" ? "Pescato" : (lot.origin.productionMethod === "allevato" ? "Allevato" : "Raccolto")} />}
              {lot.origin?.scientificName && <Metric label="Nome scientifico" value={<em>{lot.origin.scientificName}</em>} />}
              {item?.allergens?.length > 0 && <Metric label="Allergeni" value={item.allergens.join(", ")} accent={C.amber} />}
            </div>
          </div>

          {lot.notes && <div style={{ marginTop: 16, padding: 12, background: C.paperDeep, borderRadius: 2, fontSize: 12, color: C.inkSoft, fontStyle: "italic" }}>📝 {lot.notes}</div>}
        </div>

        <SectionHeader title="Storico movimenti" subtitle="Tutte le operazioni su questo lotto" />
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
          {movements.length === 0 ? <div style={{ padding: 20, textAlign: "center" }}><span className="f-body" style={{ color: C.muted, fontSize: 12 }}>Nessun movimento</span></div>
          : movements.map((m, i) => {
            const typeColors = { ingresso: C.olive, lavorazione: C.teal, prelievo_cucina: C.terra, scarico: C.terra, annullamento: C.muted };
            const typeLabels = { ingresso: "INGRESSO", lavorazione: "LAVORAZIONE", prelievo_cucina: "PRELIEVO CUCINA", scarico: "SCARICO", annullamento: "ANNULLAMENTO" };
            return <div key={m.id} style={{ padding: "12px 18px", borderBottom: i < movements.length - 1 ? `1px solid ${C.ruleSoft}` : "none", display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 28, height: 28, background: (typeColors[m.type] || C.muted) + "22", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2 }}>
                {m.qty > 0 ? <ArrowUpRight size={14} color={typeColors[m.type] || C.muted} /> : <ArrowRight size={14} color={typeColors[m.type] || C.muted} />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="f-mono" style={{ fontSize: 10, color: typeColors[m.type] || C.muted, letterSpacing: "0.15em", fontWeight: 600 }}>{typeLabels[m.type] || m.type?.toUpperCase()}</span>
                  <span className="f-body" style={{ fontSize: 11, color: C.muted }}>di {m.by}</span>
                </div>
                {m.note && <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, marginTop: 2 }}>{m.note}</div>}
                {m.ref && <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                  {m.ref.type === "receipt" && `→ DDT ${state.receipts.find(r => r.id === m.ref.id)?.ddtNumber || m.ref.id}`}
                  {m.ref.type === "workOrder" && `→ Lavorazione ${m.ref.id}`}
                  {m.ref.type === "kitchenRequest" && `→ Richiesta cucina`}
                </div>}
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="f-mono" style={{ fontSize: 14, color: m.qty > 0 ? C.olive : C.terra, fontWeight: 600 }}>{m.qty > 0 ? "+" : ""}{m.qty.toFixed(2)} {lot.unit}</div>
                <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>{fmt.dateTime(m.at)}</div>
              </div>
            </div>;
          })}
        </div>
      </div>

      <div>
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2, textAlign: "center", marginBottom: 16 }}>
          <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.3em", color: C.muted, marginBottom: 12 }}>ETICHETTA QR</div>
          <div style={{ display: "inline-block", padding: 8, background: C.white, border: `1px solid ${C.rule}` }}><QRMock size={140} data={lot.id} /></div>
          <div className="f-mono" style={{ fontSize: 11, color: C.ink, fontWeight: 600, marginTop: 10 }}>{lot.id}</div>
          <Btn variant="primary" size="sm" icon={Printer} onClick={() => setShowLabel(true)} style={{ marginTop: 12, width: "100%", justifyContent: "center" }}>Stampa etichetta</Btn>
        </div>

        {children.length > 0 && <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 16, borderRadius: 2, marginBottom: 16 }}>
          <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.3em", color: C.muted, marginBottom: 10 }}>LOTTI FIGLI ({children.length})</div>
          {children.map(c => <button key={c.id} onClick={() => onNavigate({ view: "lot", lotId: c.id })} className="tx" style={{ width: "100%", textAlign: "left", padding: "8px 10px", background: C.paperSoft, border: `1px solid ${C.ruleSoft}`, borderRadius: 2, marginBottom: 6, cursor: "pointer" }}>
            <div className="f-mono" style={{ fontSize: 11, color: C.ink, fontWeight: 600 }}>{c.id}</div>
            <div className="f-body" style={{ fontSize: 11, color: C.muted }}>{c.qtyRemaining} {c.unit} · {c.status}</div>
          </button>)}
        </div>}

        {kitchenOutUsing.length > 0 && <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 16, borderRadius: 2 }}>
          <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.3em", color: C.muted, marginBottom: 10 }}>USO IN CUCINA</div>
          {kitchenOutUsing.map(ko => {
            const dish = (state.dishesReal || []).find(d => d.id === ko.dishId); // FIX BLOCKER 2
            const used = (ko.consumedLots || []).find(cl => cl.lotId === lot.id)?.qty || 0;
            return <div key={ko.id} style={{ padding: "6px 0", borderBottom: `1px solid ${C.ruleSoft}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>{dish?.nome || dish?.name}</div>
                <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>{fmt.date(ko.date)} · {ko.servings} porz.</div>
              </div>
              <div className="f-mono" style={{ fontSize: 11, color: C.terra, fontWeight: 600 }}>-{used.toFixed(2)} {lot.unit}</div>
            </div>;
          })}
        </div>}
      </div>
    </div>
  </div>;
}

// ── Accettazione in giacenza (Reg. CE 852/2004 All. II Cap. IX, principio 4+7 HACCP)
// Conferma operatore: temperatura cella misurata + conformita' etichetta.
// Se conformita' = NO → resta in_accettazione + NC.
function AcceptLotModal({ lot, state, setState, onClose }) {
  const [cellTemp, setCellTemp] = useState("");
  const [labelOk, setLabelOk] = useState("yes");
  const [notes, setNotes] = useState("");
  const toast = useToast();
  const item = engine.getItems(state).find(i => i.id === lot.itemId);
  const bp = (state.baseProducts || []).find(b => b.id === lot.itemId);
  const range = tempRangeForCat(bp?.cat);

  const confirm = () => {
    if (cellTemp === "" || cellTemp == null) { toast({ type: "error", message: "Inserisci temperatura cella misurata" }); return; }
    const t = parseFloat(cellTemp);
    if (Number.isNaN(t)) { toast({ type: "error", message: "Temperatura non valida" }); return; }
    let next = { ...state };
    if (labelOk === "no") {
      // Conformita' etichetta non superata: NON sbloccare, registra NC.
      const nc = {
        id: uid("nc_"),
        date: TODAY,
        createdAt: nowIso(),
        type: "Etichetta",
        kind: "etichetta",
        description: `Lotto ${lot.id} (${item?.name}): etichetta NON conforme in fase di accettazione. T cella ${t}°C. ${notes ? "Note: " + notes : ""}`,
        action: "Da definire azione correttiva — non sbloccare in cucina",
        operator: state.currentUser.name,
        status: "aperta",
        lotId: lot.id,
      };
      next.haccpLogs = { ...next.haccpLogs, nonConformities: [...(next.haccpLogs?.nonConformities || []), nc] };
      next.auditLog = appendAudit(next, { action: "lot_label_nc", entityType: "lot", entityId: lot.id, reason: `Etichetta non conforme, T cella ${t}°C` });
      setState(next);
      toast({ type: "error", title: "Non conformita' etichetta", message: "Lotto resta In accettazione. NC aperta." });
      onClose();
      return;
    }
    // Conformita' ok: sblocca a "disponibile". Logga temperatura cella + audit.
    next = {
      ...next,
      lots: next.lots.map(l => l.id === lot.id ? { ...l, status: "disponibile" } : l),
    };
    // Log temperatura cella opzionale (utile in audit ASL/NAS).
    const location = bp?.cat === "pesce" ? "Frigo pesce 1" : (bp?.cat === "carne" ? "Frigo pesce 1" : "Cella verdure");
    const tempLog = {
      id: uid("t_"), date: TODAY, time: NOW_HOUR,
      location, temp: t,
      operator: state.currentUser.name,
      target: range ? `${range.min}/${range.max}` : "0/+4",
      note: `Accettazione lotto ${lot.id}${notes ? " — " + notes : ""}`,
    };
    next.haccpLogs = { ...next.haccpLogs, temperature: [...(next.haccpLogs?.temperature || []), tempLog] };
    next.auditLog = appendAudit(next, { action: "lot_accepted", entityType: "lot", entityId: lot.id, reason: `T cella ${t}°C, etichetta conforme` });
    setState(next);
    toast({ type: "success", title: "Lotto accettato", message: `${lot.id} ora disponibile in cucina` });
    onClose();
  };

  return <Modal onClose={onClose} maxWidth={520} subtitle="Magazzino · controllo accettazione" title="Accetta lotto in giacenza"
    footer={<><Btn variant="ghost" onClick={onClose}>Annulla</Btn><Btn variant="olive" icon={CheckCircle2} onClick={confirm}>Conferma accettazione</Btn></>}>
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em" }}>LOTTO</div>
        <div className="f-mono" style={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>{lot.id}</div>
        <div className="f-body" style={{ fontSize: 12, color: C.muted }}>{item?.name} · {lot.qtyRemaining} {lot.unit} · scade {fmt.date(lot.expiresAt)}</div>
      </div>
      <Field label={`Temperatura cella misurata °C${range ? ` (target ${range.min}/${range.max})` : ""}`} required>
        <Input type="number" step="0.1" value={cellTemp} onChange={e => setCellTemp(e.target.value)} placeholder="es. 2.0" />
      </Field>
      <Field label="Conformita' etichetta" required hint="Lotto fornitore + scadenza + origine leggibili e coerenti col DDT">
        <Select value={labelOk} onChange={e => setLabelOk(e.target.value)}>
          <option value="yes">Si, etichetta conforme</option>
          <option value="no">No, non conforme — apri NC</option>
        </Select>
      </Field>
      <Field label="Note (operatore)">
        <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="es. Confezione integra, etichetta leggibile, temperatura entro range" />
      </Field>
      <div className="f-body" style={{ fontSize: 11, color: C.muted, padding: 10, background: C.paperSoft, borderRadius: 2, lineHeight: 1.4 }}>
        Operatore: <strong>{state.currentUser?.name || "—"}</strong>. La conferma viene registrata in auditLog (append-only) e nei log temperature HACCP.
      </div>
    </div>
  </Modal>;
}

function LabelPrint({ lot, state, onClose }) {
  const [format, setFormat] = useState("a6"); // "a6" | "roll62" | "a4-4up"
  const [copies, setCopies] = useState(1);
  const item = engine.getItems(state).find(i => i.id === lot.itemId);
  const bp = (state.baseProducts || []).find(b => b.id === lot.itemId);
  const supplier = lot.receiptId ? state.suppliers.find(s => s.id === state.receipts.find(r => r.id === lot.receiptId)?.supplierId) : null;
  const allergeni = (item?.allergens || bp?.allergens || []);
  const denom = (item?.nomeFull || bp?.nome || item?.name || "").toUpperCase();
  // Reg. UE 1169/2011 art. 24 — etichetta deve distinguere "DA CONSUMARSI ENTRO"
  // (scadenza, deperibile) da "PREFERIBILMENTE ENTRO" (TMC).
  const isTmc = lot.expiryType === "consumarsi_preferibilmente";
  const expiryLabel = isTmc ? "DA CONSUMARSI PREFERIBILMENTE ENTRO" : "DA CONSUMARSI ENTRO";
  const isFishLot = bp?.cat === "pesce";

  // Print: usa CSS @media print
  const doPrint = () => { window.print(); };

  const labelBody = (
    <div className="label-card" style={{ border: `2px solid #000`, padding: 18, background: "#fff", width: "100%", boxSizing: "border-box", pageBreakInside: "avoid" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="f-display" style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em", color: "#000" }}>BOTTEGA DEL MARE</div>
          <div className="f-mono" style={{ fontSize: 8, letterSpacing: "0.18em", color: "#666", marginTop: 1 }}>LABORATORIO POLVERIGI · AN · IT</div>
        </div>
        <QRMock size={70} data={lot.id} />
      </div>
      <div style={{ height: 1, background: "#000", margin: "6px 0" }} />
      <div className="f-display" style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em", color: "#000", lineHeight: 1.15, wordBreak: "break-word" }}>{denom}</div>
      <div className="f-mono" style={{ fontSize: 11, color: "#000", fontWeight: 700, marginTop: 5, letterSpacing: "0.15em" }}>LOTTO: {lot.id}</div>

      <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 9 }}>
        <div><div className="f-mono" style={{ fontSize: 7, color: "#666", letterSpacing: "0.2em" }}>QUANTITÀ</div><div className="f-mono" style={{ fontWeight: 700 }}>{lot.qtyInitial} {lot.unit}</div></div>
        <div><div className="f-mono" style={{ fontSize: 7, color: "#666", letterSpacing: "0.2em" }}>CONFEZIONATO IL</div><div className="f-mono" style={{ fontWeight: 700 }}>{fmt.date(lot.createdAt)}</div></div>
        <div><div className="f-mono" style={{ fontSize: 7, color: "#000", letterSpacing: "0.2em" }}>{expiryLabel}</div><div className="f-mono" style={{ fontWeight: 700, color: "#000", textDecoration: "underline" }}>{fmt.dateFull(lot.expiresAt)}</div></div>
        <div><div className="f-mono" style={{ fontSize: 7, color: "#666", letterSpacing: "0.2em" }}>CONSERVAZIONE</div><div className="f-mono" style={{ fontWeight: 700 }}>{lot.status === "abbattuto" ? "-18°C · ABBATTUTO" : "0/+4°C"}</div></div>
      </div>

      {/* Lotto fornitore (Reg. CE 178/2002 art. 18) */}
      {lot.supplierLotCode && <div style={{ marginTop: 6, fontSize: 8, color: "#000" }} className="f-mono">
        LOTTO FORNITORE: <strong>{lot.supplierLotCode}</strong>
      </div>}

      {/* Origine prodotti pesca (Reg. UE 1379/2013 art. 35) */}
      {isFishLot && lot.origin && (lot.origin.faoZone || lot.origin.productionMethod) && <div style={{ marginTop: 4, fontSize: 8, color: "#000" }} className="f-mono">
        {lot.origin.faoZone && <span>ZONA <strong>{lot.origin.faoZone}</strong></span>}
        {lot.origin.faoZone && lot.origin.productionMethod && " · "}
        {lot.origin.productionMethod && <span>{lot.origin.productionMethod === "pescato" ? "PESCATO" : (lot.origin.productionMethod === "allevato" ? "ALLEVATO" : "RACCOLTO")}</span>}
        {lot.origin.fishingGear && <span> · {lot.origin.fishingGear}</span>}
        {lot.origin.scientificName && <div style={{ marginTop: 2, fontStyle: "italic", fontSize: 7, color: "#333" }}>{lot.origin.scientificName}</div>}
      </div>}

      {allergeni.length > 0 && <div style={{ marginTop: 8, padding: 6, background: "#FFF4E0", border: "1px solid #C48B1F" }}>
        <div className="f-mono" style={{ fontSize: 8, letterSpacing: "0.18em", color: "#8C5E0F", fontWeight: 700 }}>ALLERGENI: {allergeni.map(a => a.toUpperCase()).join(" · ")}</div>
      </div>}

      {lot.status === "abbattuto" && <div style={{ marginTop: 6, fontSize: 8, color: "#000" }} className="f-body">
        {/* Fix L3 — D.M. 17/07/2013: registrare protocollo abbattimento applicato + lotto di riferimento. */}
        {lot.coreTempProtocolUsed
          ? <>ABBATTUTO Reg. CE 853/2004 ({lot.coreTempProtocolUsed}, lotto {lot.id}). Prodotto idoneo al consumo crudo/marinato. Conservare ≤-18°C. Consumare previo scongelamento controllato in frigo.</>
          : <>Prodotto abbattuto a -20°C (Reg. CE 853/2004). Consumare previo scongelamento controllato in frigo.</>
        }
      </div>}

      {supplier && <div style={{ marginTop: 6, fontSize: 8, color: "#666" }} className="f-mono">Origine MP: {supplier.name}{supplier.address ? ` · ${supplier.address}` : ""}</div>}
      {lot.notes && <div style={{ marginTop: 4, fontSize: 8, color: "#000" }} className="f-body">Note: {lot.notes}</div>}
    </div>
  );

  return <Modal onClose={onClose} maxWidth={640} title="Etichetta lotto" subtitle="Stampa HACCP"
    footer={<><Btn variant="ghost" onClick={onClose}>Chiudi</Btn><Btn variant="primary" icon={Printer} onClick={doPrint}>Stampa {copies > 1 ? `${copies} copie` : ""}</Btn></>}>

    <style dangerouslySetInnerHTML={{ __html: `
      @media print {
        body * { visibility: hidden; }
        .printable-area, .printable-area * { visibility: visible; }
        .printable-area { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 0; }
        @page { margin: 8mm; }
      }
      .label-a6 { width: 100mm; height: 140mm; }
      .label-roll { width: 58mm; font-size: 90%; }
      .label-4up { display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; gap: 4mm; width: 190mm; }
    `}} />

    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <button onClick={() => setFormat("a6")} className="tx" style={{ padding: "8px 14px", background: format === "a6" ? C.ink : C.white, color: format === "a6" ? C.paper : C.ink, border: `1px solid ${C.ink}`, borderRadius: 2, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>A6 (10×14 cm)</button>
        <button onClick={() => setFormat("roll62")} className="tx" style={{ padding: "8px 14px", background: format === "roll62" ? C.ink : C.white, color: format === "roll62" ? C.paper : C.ink, border: `1px solid ${C.ink}`, borderRadius: 2, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>Rotolo 62mm</button>
        <button onClick={() => setFormat("a4-4up")} className="tx" style={{ padding: "8px 14px", background: format === "a4-4up" ? C.ink : C.white, color: format === "a4-4up" ? C.paper : C.ink, border: `1px solid ${C.ink}`, borderRadius: 2, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>A4 · 4 etichette</button>
        <div style={{ flex: 1 }} />
        <label className="f-body" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
          Copie: <Input type="number" min="1" max="20" value={copies} onChange={e => setCopies(parseInt(e.target.value) || 1)} style={{ width: 60, padding: "4px 8px" }} />
        </label>
      </div>

      <div style={{ background: C.paperDeep, padding: 20, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.muted, marginBottom: 10, textAlign: "center" }}>ANTEPRIMA</div>
        <div className="printable-area" style={{ margin: "0 auto", width: format === "a4-4up" ? "100%" : (format === "roll62" ? 220 : 360) }}>
          {format === "a4-4up" ? <div className="label-4up">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className={`label-a6`}>{labelBody}</div>)}
          </div>
          : format === "roll62" ? <div className="label-roll">{Array.from({ length: copies }).map((_, i) => <div key={i} style={{ pageBreakAfter: "always", marginBottom: 10 }}>{labelBody}</div>)}</div>
          : <div className="label-a6">{Array.from({ length: copies }).map((_, i) => <div key={i} style={{ pageBreakAfter: "always", marginBottom: 10 }}>{labelBody}</div>)}</div>}
        </div>
      </div>

      <div style={{ marginTop: 16, padding: 12, background: C.tealWash, borderRadius: 2, fontSize: 11, color: C.tealDeep, lineHeight: 1.5 }}>
        <strong>Conforme a:</strong> Reg. UE 1169/2011 (etichettatura alimenti), Reg. CE 853/2004 (abbattimento), D.Lgs 193/2007 (HACCP). Lotto tracciabile via QR.
      </div>
    </div>
  </Modal>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      HACCP                                                 ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function HACCP({ state, setState, initial }) {
  const [tab, setTab] = useState(initial?.new === "cleaning" ? "cleaning" : "temperature");
  const [showForm, setShowForm] = useState(initial?.new === "temperature" || initial?.new === "cleaning");
  const [showReport, setShowReport] = useState(false);
  const toast = useToast();

  return <div className="fade-up">
    <PageHeader kicker="Sicurezza alimentare · HACCP" title="Registro HACCP"
      subtitle="Temperature, sanificazioni, non conformità. Le temperature fuori range creano automaticamente una bozza di NC."
      right={<div style={{ display: "flex", gap: 8 }}>
        <Btn variant="ghost" size="lg" icon={Printer} onClick={() => setShowReport(true)}>Stampa registro mensile</Btn>
        <Btn variant="accent" size="lg" icon={Plus} onClick={() => setShowForm(true)}>Nuova registrazione</Btn>
      </div>} />

    {showForm && <HACCPForm state={state} setState={setState} tab={tab} onClose={() => setShowForm(false)} />}
    {showReport && <HACCPReport state={state} onClose={() => setShowReport(false)} />}

    <Tabs value={tab} onChange={setTab} tabs={[
      { id: "temperature", label: "Temperature", icon: Thermometer, count: state.haccpLogs.temperature.length },
      { id: "cleaning", label: "Sanificazioni", icon: Droplets, count: state.haccpLogs.cleaning.length },
      { id: "nc", label: "Non conformità", icon: AlertTriangle, count: state.haccpLogs.nonConformities.length },
    ]} />

    {tab === "temperature" && <HACCPTemperature state={state} />}
    {tab === "cleaning" && <HACCPCleaning state={state} />}
    {tab === "nc" && <HACCPNonConformity state={state} setState={setState} />}
  </div>;
}

function HACCPReport({ state, onClose }) {
  const [month, setMonth] = useState(() => TODAY.slice(0, 7)); // YYYY-MM

  const monthStart = month + "-01";
  const [y, m] = month.split("-").map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  const monthEnd = `${month}-${String(lastDay).padStart(2, "0")}`;

  const tempLogs = (state.haccpLogs?.temperature || []).filter(l => l.date >= monthStart && l.date <= monthEnd).sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`));
  const cleanLogs = (state.haccpLogs?.cleaning || []).filter(l => l.date >= monthStart && l.date <= monthEnd).sort((a, b) => new Date(a.date) - new Date(b.date));
  const ncs = (state.haccpLogs?.nonConformities || []).filter(l => l.date >= monthStart && l.date <= monthEnd).sort((a, b) => new Date(a.date) - new Date(b.date));

  const monthName = new Date(monthStart).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const outOfRange = tempLogs.filter(t => {
    if (!t.target) return false;
    const [minS, maxS] = t.target.split("/");
    const min = parseFloat((minS || "").replace("+", "")), max = parseFloat((maxS || "").replace("+", ""));
    return t.temp < min || t.temp > max;
  });

  return <Modal onClose={onClose} maxWidth={900} title="Registro HACCP mensile" subtitle={monthName}
    footer={<>
      <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ padding: "6px 10px", border: `1px solid ${C.rule}`, borderRadius: 2, fontSize: 12, marginRight: "auto" }} />
      <Btn variant="ghost" onClick={onClose}>Chiudi</Btn>
      <Btn variant="primary" icon={Printer} onClick={() => window.print()}>Stampa</Btn>
    </>}>

    <style dangerouslySetInnerHTML={{ __html: `
      @media print {
        body * { visibility: hidden; }
        .haccp-print-area, .haccp-print-area * { visibility: visible; }
        .haccp-print-area { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 10mm; }
        @page { margin: 10mm; size: A4; }
        .haccp-print-area .no-print { display: none !important; }
      }
    `}} />

    <div className="haccp-print-area" style={{ padding: 24, background: "#fff", color: "#000", fontFamily: "Geist, sans-serif" }}>
      <div style={{ borderBottom: "2px solid #000", paddingBottom: 12, marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em" }}>BOTTEGA DEL MARE</div>
          <div style={{ fontSize: 10, color: "#555", letterSpacing: "0.15em", marginTop: 2 }}>LABORATORIO ARTIGIANALE · POLVERIGI (AN) · ITALIA</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "#555" }}>REGISTRO HACCP</div>
          <div style={{ fontSize: 18, fontWeight: 600, textTransform: "capitalize" }}>{monthName}</div>
          <div style={{ fontSize: 9, color: "#888", fontFamily: "monospace" }}>Reg. CE 852/2004 · D.Lgs 193/2007</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20, padding: 12, background: "#F5F0E6", borderRadius: 2 }}>
        <div><div style={{ fontSize: 9, color: "#555", letterSpacing: "0.15em" }}>RILEVAZIONI TEMPERATURA</div><div style={{ fontSize: 20, fontWeight: 600 }}>{tempLogs.length}</div></div>
        <div><div style={{ fontSize: 9, color: "#555", letterSpacing: "0.15em" }}>FUORI RANGE</div><div style={{ fontSize: 20, fontWeight: 600, color: outOfRange.length > 0 ? "#B5472A" : "#6C7A52" }}>{outOfRange.length}</div></div>
        <div><div style={{ fontSize: 9, color: "#555", letterSpacing: "0.15em" }}>SANIFICAZIONI</div><div style={{ fontSize: 20, fontWeight: 600 }}>{cleanLogs.length}</div></div>
        <div><div style={{ fontSize: 9, color: "#555", letterSpacing: "0.15em" }}>NON CONFORMITÀ</div><div style={{ fontSize: 20, fontWeight: 600, color: ncs.length > 0 ? "#B5472A" : "#6C7A52" }}>{ncs.length}</div></div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 8, borderBottom: "1px solid #000", paddingBottom: 4 }}>§ 1 — REGISTRO TEMPERATURE</div>
        {tempLogs.length === 0 ? <div style={{ fontSize: 11, color: "#888", padding: 10, fontStyle: "italic" }}>Nessuna rilevazione nel periodo.</div>
        : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #000" }}>
              <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Data</th>
              <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Ora</th>
              <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Punto di controllo</th>
              <th style={{ textAlign: "right", padding: "4px 6px", fontWeight: 600 }}>Temp °C</th>
              <th style={{ textAlign: "center", padding: "4px 6px", fontWeight: 600 }}>Range</th>
              <th style={{ textAlign: "center", padding: "4px 6px", fontWeight: 600 }}>Esito</th>
              <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Operatore</th>
            </tr>
          </thead>
          <tbody>
            {tempLogs.map((t, i) => {
              const [minS, maxS] = (t.target || "").split("/");
              const min = parseFloat((minS || "").replace("+", ""));
              const max = parseFloat((maxS || "").replace("+", ""));
              const ok = !(t.temp < min || t.temp > max);
              return <tr key={i} style={{ borderBottom: "1px solid #ddd" }}>
                <td style={{ padding: "3px 6px" }}>{fmt.date(t.date)}</td>
                <td style={{ padding: "3px 6px", fontFamily: "monospace" }}>{t.time}</td>
                <td style={{ padding: "3px 6px" }}>{t.location}</td>
                <td style={{ padding: "3px 6px", textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>{t.temp}°</td>
                <td style={{ padding: "3px 6px", textAlign: "center", fontFamily: "monospace", color: "#555" }}>{t.target}°C</td>
                <td style={{ padding: "3px 6px", textAlign: "center", color: ok ? "#6C7A52" : "#B5472A", fontWeight: 600 }}>{ok ? "✓ OK" : "✗ NC"}</td>
                <td style={{ padding: "3px 6px" }}>{t.operator}</td>
              </tr>;
            })}
          </tbody>
        </table>}
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 8, borderBottom: "1px solid #000", paddingBottom: 4 }}>§ 2 — SANIFICAZIONI</div>
        {cleanLogs.length === 0 ? <div style={{ fontSize: 11, color: "#888", padding: 10, fontStyle: "italic" }}>Nessuna sanificazione registrata nel periodo.</div>
        : <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #000" }}>
              <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Data</th>
              <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Ora</th>
              <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Zona / attrezzatura</th>
              <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Operatore</th>
              <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Note</th>
            </tr>
          </thead>
          <tbody>
            {cleanLogs.map((c, i) => <tr key={i} style={{ borderBottom: "1px solid #ddd" }}>
              <td style={{ padding: "3px 6px" }}>{fmt.date(c.date)}</td>
              <td style={{ padding: "3px 6px", fontFamily: "monospace" }}>{c.time || "—"}</td>
              <td style={{ padding: "3px 6px", fontWeight: 600 }}>{c.zone}</td>
              <td style={{ padding: "3px 6px" }}>{c.operator}</td>
              <td style={{ padding: "3px 6px", color: "#555" }}>{c.notes || "—"}</td>
            </tr>)}
          </tbody>
        </table>}
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 8, borderBottom: "1px solid #000", paddingBottom: 4 }}>§ 3 — NON CONFORMITÀ E AZIONI CORRETTIVE</div>
        {ncs.length === 0 ? <div style={{ fontSize: 11, color: "#6C7A52", padding: 10, fontStyle: "italic" }}>✓ Nessuna non conformità nel periodo.</div>
        : ncs.map((nc, i) => <div key={i} style={{ padding: 10, border: "1px solid #B5472A", background: "#FDF5F2", marginBottom: 8, borderRadius: 2 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 600, marginBottom: 4 }}>
            <span>{fmt.date(nc.date)} · {nc.type || "generica"}</span>
            <span style={{ color: nc.status === "chiusa" ? "#6C7A52" : "#B5472A" }}>{(nc.status || "aperta").toUpperCase()}</span>
          </div>
          <div style={{ fontSize: 11, marginBottom: 4 }}>{nc.description}</div>
          {nc.correctiveAction && <div style={{ fontSize: 10, color: "#555" }}><strong>Azione correttiva:</strong> {nc.correctiveAction}</div>}
          {nc.closedBy && <div style={{ fontSize: 10, color: "#555" }}><strong>Chiusa da:</strong> {nc.closedBy} il {fmt.date(nc.closedAt)}</div>}
        </div>)}
      </div>

      {/* § 4 — REGISTRO LAVORAZIONI
          Reg. CE 852/2004 art. 5 c. 2 lett. g) — registrazione attività
          Reg. CE 178/2002 art. 18 — tracciabilità "one step back, one step forward"
          D.M. 17/07/2013 — protocollo Anisakis per pesce destinato a crudo/marinato/affumicato a freddo */}
      <div style={{ pageBreakBefore: "always", marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 4, borderBottom: "1px solid #000", paddingBottom: 4 }}>§ 4 — REGISTRO LAVORAZIONI</div>
        <div style={{ fontSize: 9, color: "#888", fontStyle: "italic", marginBottom: 10 }}>
          Reg. CE 852/2004 art. 5 c. 2 lett. g) · Reg. CE 178/2002 art. 18 (tracciabilità) · D.M. 17/07/2013 (Anisakis se applicabile)
        </div>
        {(() => {
          // Filtro append-only (sola lettura): WO completati nel mese.
          const completedWOs = (state.workOrders || []).filter(wo => {
            if (wo.status !== "completato" && wo.status !== "completed") return false;
            const ref = wo.completedAt || wo.createdAt;
            if (!ref) return false;
            const d = localDateOf(ref) || (typeof ref === "string" ? ref.slice(0, 10) : null);
            return d && d >= monthStart && d <= monthEnd;
          }).sort((a, b) => String(a.completedAt || a.createdAt || "").localeCompare(String(b.completedAt || b.createdAt || "")));

          if (completedWOs.length === 0) {
            return <div style={{ fontSize: 11, color: "#888", padding: 10, fontStyle: "italic" }}>Nessuna lavorazione completata nel periodo.</div>;
          }

          let totKgIn = 0, totKgOut = 0, totDurMin = 0;

          const ccpNCs = (state.haccpLogs?.nonConformities || []).filter(nc =>
            (nc.kind === "ccp_anisakis_violation" || nc.kind === "ccp_cottura_violation") &&
            nc.date && nc.date >= monthStart && nc.date <= monthEnd
          );

          return <>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 9 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #000" }}>
                  <th style={{ textAlign: "left", padding: "4px 4px", fontWeight: 600 }}>Data</th>
                  <th style={{ textAlign: "left", padding: "4px 4px", fontWeight: 600 }}>WO-ID</th>
                  <th style={{ textAlign: "left", padding: "4px 4px", fontWeight: 600 }}>Tipo</th>
                  <th style={{ textAlign: "left", padding: "4px 4px", fontWeight: 600 }}>Operatore</th>
                  <th style={{ textAlign: "left", padding: "4px 4px", fontWeight: 600 }}>Lotto padre</th>
                  <th style={{ textAlign: "left", padding: "4px 4px", fontWeight: 600 }}>Lotto figlio</th>
                  <th style={{ textAlign: "right", padding: "4px 4px", fontWeight: 600 }}>kg in</th>
                  <th style={{ textAlign: "right", padding: "4px 4px", fontWeight: 600 }}>kg out</th>
                  <th style={{ textAlign: "right", padding: "4px 4px", fontWeight: 600 }}>Resa</th>
                  <th style={{ textAlign: "right", padding: "4px 4px", fontWeight: 600 }}>Durata</th>
                  <th style={{ textAlign: "left", padding: "4px 4px", fontWeight: 600 }}>CCP</th>
                </tr>
              </thead>
              <tbody>
                {completedWOs.map((wo, idx) => {
                  const childLot = wo.outputLotId ? (state.lots || []).find(l => l.id === wo.outputLotId) : null;
                  const parentLot = childLot?.parent ? (state.lots || []).find(l => l.id === childLot.parent) : null;
                  const qtyIn = Number(childLot?.consumedFromParent) || 0;
                  const qtyOut = Number(childLot?.qtyInitial) || 0;
                  const yieldR = qtyIn > 0 ? qtyOut / qtyIn : null;

                  let durationMin = 0;
                  for (const ph of (wo.phases || [])) {
                    if (!ph.startedAt || !ph.endedAt) continue;
                    const d = (new Date(ph.endedAt) - new Date(ph.startedAt)) / 60000;
                    if (d > 0 && Number.isFinite(d)) durationMin += d;
                  }

                  totKgIn += qtyIn;
                  totKgOut += qtyOut;
                  totDurMin += durationMin;

                  // CCP: derivato dalle fasi marcate ccp ("anisakis" | "cottura")
                  const ccpPhases = (wo.phases || []).filter(p => p.ccp);
                  let ccpCell = <span style={{ color: "#888" }}>—</span>;
                  if (ccpPhases.length > 0) {
                    const allOk = ccpPhases.every(p => p.coreTempProtocolUsed || p.status === "completed");
                    if (allOk) {
                      const labels = ccpPhases.map(p => p.coreTempProtocolUsed || p.ccp).join(", ");
                      ccpCell = <span style={{ color: "#6C7A52", fontWeight: 600 }}>✓ {labels}</span>;
                    } else {
                      ccpCell = <span style={{ color: "#B5472A", fontWeight: 700 }}>NC</span>;
                    }
                  }

                  const dateD = localDateOf(wo.completedAt || wo.createdAt) || (wo.completedAt || wo.createdAt || "").slice(0, 10);

                  return <tr key={wo.id} style={{ borderBottom: "1px solid #ddd", background: idx % 2 ? "#FBF8F2" : "transparent" }}>
                    <td style={{ padding: "3px 4px", fontFamily: "monospace" }}>{dateD}</td>
                    <td style={{ padding: "3px 4px", fontFamily: "monospace" }}>{wo.id}</td>
                    <td style={{ padding: "3px 4px" }}>{wo.type || "—"}</td>
                    <td style={{ padding: "3px 4px" }}>{wo.operator || "—"}</td>
                    <td style={{ padding: "3px 4px", fontFamily: "monospace", color: "#555" }}>{parentLot?.id || "—"}</td>
                    <td style={{ padding: "3px 4px", fontFamily: "monospace", color: "#555" }}>{childLot?.id || "—"}</td>
                    <td style={{ padding: "3px 4px", textAlign: "right", fontFamily: "monospace" }}>{qtyIn > 0 ? qtyIn.toFixed(2) : "—"}</td>
                    <td style={{ padding: "3px 4px", textAlign: "right", fontFamily: "monospace" }}>{qtyOut > 0 ? qtyOut.toFixed(2) : "—"}</td>
                    <td style={{ padding: "3px 4px", textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>{yieldR != null ? (yieldR * 100).toFixed(0) + "%" : "—"}</td>
                    <td style={{ padding: "3px 4px", textAlign: "right", fontFamily: "monospace" }}>{durationMin > 0 ? Math.floor(durationMin / 60) + "h " + Math.round(durationMin % 60) + "min" : "—"}</td>
                    <td style={{ padding: "3px 4px" }}>{ccpCell}</td>
                  </tr>;
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: "2px solid #000", fontWeight: 700, background: "#F5F0E6" }}>
                  <td colSpan={6} style={{ padding: "5px 4px", fontSize: 9 }}>TOTALI · {completedWOs.length} lavorazioni</td>
                  <td style={{ padding: "5px 4px", textAlign: "right", fontFamily: "monospace" }}>{totKgIn.toFixed(2)}</td>
                  <td style={{ padding: "5px 4px", textAlign: "right", fontFamily: "monospace" }}>{totKgOut.toFixed(2)}</td>
                  <td style={{ padding: "5px 4px", textAlign: "right", fontFamily: "monospace" }}>{totKgIn > 0 ? ((totKgOut / totKgIn) * 100).toFixed(0) + "%" : "—"}</td>
                  <td style={{ padding: "5px 4px", textAlign: "right", fontFamily: "monospace" }}>{totDurMin > 0 ? Math.floor(totDurMin / 60) + "h" : "—"}</td>
                  <td style={{ padding: "5px 4px" }}></td>
                </tr>
                {totKgIn > 0 && <tr style={{ background: "#F5F0E6" }}>
                  <td colSpan={11} style={{ padding: "4px 4px", fontSize: 9, color: "#555", fontStyle: "italic" }}>
                    Scarto totale: <strong style={{ fontFamily: "monospace", color: "#000" }}>{Math.max(0, totKgIn - totKgOut).toFixed(2)} kg</strong> · ({totKgIn > 0 ? (((totKgIn - totKgOut) / totKgIn) * 100).toFixed(1) : "0"}% di calo peso)
                  </td>
                </tr>}
              </tfoot>
            </table>

            {ccpNCs.length > 0 && <div style={{ marginTop: 12, padding: 10, border: "1px solid #B5472A", background: "#FDF5F2", borderRadius: 2 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#7F2E18", marginBottom: 6, letterSpacing: "0.1em" }}>⚠ NON CONFORMITÀ CCP RILEVATE NEL PERIODO ({ccpNCs.length})</div>
              {ccpNCs.map((nc, i) => <div key={nc.id || i} style={{ fontSize: 10, paddingTop: 4, borderTop: i > 0 ? "1px dotted #B5472A" : "none", marginTop: i > 0 ? 4 : 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 600 }}>
                  <span>{fmt.date(nc.date)} · {nc.type || (nc.kind === "ccp_anisakis_violation" ? "CCP Anisakis" : "CCP Cottura")}{nc.ref?.id ? ` · ${nc.ref.id}` : ""}</span>
                  <span style={{ color: nc.status === "chiusa" || nc.status === "risolta" ? "#6C7A52" : "#B5472A" }}>{(nc.status || "aperta").toUpperCase()}</span>
                </div>
                <div style={{ color: "#555", marginTop: 2 }}>{nc.description}</div>
                {nc.action && <div style={{ color: "#555", marginTop: 2 }}><strong>Azione:</strong> {nc.action}</div>}
              </div>)}
            </div>}
          </>;
        })()}
      </div>

      {/* § 5 — OPERATORI E ATTESTATI HACCP
          Reg. CE 852/2004 All. II Cap. XII — formazione del personale addetto agli alimenti */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 4, borderBottom: "1px solid #000", paddingBottom: 4 }}>§ 5 — OPERATORI E ATTESTATI HACCP</div>
        <div style={{ fontSize: 9, color: "#888", fontStyle: "italic", marginBottom: 10 }}>
          Reg. CE 852/2004 All. II Cap. XII · Formazione obbligatoria personale addetto agli alimenti
        </div>
        {(() => {
          const activeUsers = (state.users || []).filter(u => u.active !== false);
          if (activeUsers.length === 0) return <div style={{ fontSize: 11, color: "#888", padding: 10, fontStyle: "italic" }}>Nessun operatore attivo registrato.</div>;
          return <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #000" }}>
                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Operatore</th>
                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Mansione</th>
                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Livello</th>
                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>N. attestato</th>
                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Ente</th>
                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Rilascio</th>
                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Scadenza</th>
                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {activeUsers.map(u => {
                const days = u.haccpCertExpiresAt ? daysBetween(TODAY, u.haccpCertExpiresAt) : null;
                let statusLabel, statusColor;
                if (days == null) { statusLabel = "Non registrato"; statusColor = "#888"; }
                else if (days < 0) { statusLabel = `SCADUTO ${Math.abs(days)}gg fa`; statusColor = "#B5472A"; }
                else if (days <= 60) { statusLabel = `In scadenza (${days}gg)`; statusColor = "#8A5A0F"; }
                else { statusLabel = `Valido (${days}gg)`; statusColor = "#6C7A52"; }
                return <tr key={u.id} style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: "3px 6px", fontWeight: 600 }}>{u.name}</td>
                  <td style={{ padding: "3px 6px" }}>{u.mansione || u.role || "—"}</td>
                  <td style={{ padding: "3px 6px" }}>{u.haccpCertLevel || "—"}</td>
                  <td style={{ padding: "3px 6px", fontFamily: "monospace", color: "#555" }}>{u.haccpCertNumber || "—"}</td>
                  <td style={{ padding: "3px 6px" }}>{u.haccpCertIssuer || "—"}</td>
                  <td style={{ padding: "3px 6px", fontFamily: "monospace" }}>{u.haccpCertIssuedAt ? fmt.date(u.haccpCertIssuedAt) : "—"}</td>
                  <td style={{ padding: "3px 6px", fontFamily: "monospace" }}>{u.haccpCertExpiresAt ? fmt.date(u.haccpCertExpiresAt) : "—"}</td>
                  <td style={{ padding: "3px 6px", color: statusColor, fontWeight: 600 }}>{statusLabel}</td>
                </tr>;
              })}
            </tbody>
          </table>;
        })()}
      </div>

      <div style={{ marginTop: 40, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, paddingTop: 20, borderTop: "1px solid #888" }}>
        <div>
          <div style={{ fontSize: 10, color: "#555", marginBottom: 30 }}>Responsabile HACCP</div>
          <div style={{ borderBottom: "1px solid #000", height: 1 }}></div>
          <div style={{ fontSize: 9, color: "#888", marginTop: 3 }}>Firma e data</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "#555", marginBottom: 30 }}>Titolare / Legale rappresentante</div>
          <div style={{ borderBottom: "1px solid #000", height: 1 }}></div>
          <div style={{ fontSize: 9, color: "#888", marginTop: 3 }}>Firma e data</div>
        </div>
      </div>

      <div style={{ marginTop: 20, fontSize: 8, color: "#888", textAlign: "center" }}>
        Documento generato il {fmt.dateFull(TODAY)} · Sistema di gestione Bottega del Mare
      </div>
    </div>
  </Modal>;
}

function HACCPTemperature({ state }) {
  const logs = [...state.haccpLogs.temperature].sort((a, b) => new Date(`${b.date}T${b.time}`) - new Date(`${a.date}T${a.time}`));
  const locations = [...new Set(logs.map(l => l.location))];

  const chartData = locations.map(loc => {
    const logsLoc = logs.filter(l => l.location === loc).slice(0, 10).reverse();
    return { location: loc, data: logsLoc.map(l => ({ time: fmt.time(l.date + "T" + l.time), temp: l.temp, date: fmt.dateShort(l.date) })) };
  });

  return <div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16, marginBottom: 20 }}>
      {chartData.map(({ location, data }) => {
        const last = data[data.length - 1];
        const logEntry = logs.find(l => l.location === location);
        const [minS, maxS] = logEntry.target.split("/");
        const min = parseFloat(minS.replace("+", "")), max = parseFloat(maxS.replace("+", ""));
        const outOfRange = last && (last.temp < min || last.temp > max);
        return <div key={location} style={{ background: C.white, border: `1px solid ${outOfRange ? C.terra : C.rule}`, padding: 18, borderRadius: 2 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div>
              <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{location}</div>
              <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.1em", marginTop: 2 }}>RANGE {logEntry.target}°C</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="f-display" style={{ fontSize: 24, color: outOfRange ? C.terra : C.ink, fontWeight: 600 }}>{last.temp}°C</div>
              {outOfRange && <Badge status="urgente" size="sm">FUORI RANGE</Badge>}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={80}>
            <LineChart data={data}>
              <Line type="monotone" dataKey="temp" stroke={outOfRange ? C.terra : C.teal} strokeWidth={2} dot={{ r: 3, fill: outOfRange ? C.terra : C.teal }} />
              <Tooltip contentStyle={{ background: C.paper, border: `1px solid ${C.ink}`, borderRadius: 2, fontSize: 11, fontFamily: "Geist" }} />
              <XAxis dataKey="time" tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: C.muted }} axisLine={false} tickLine={false} />
              <YAxis domain={["dataMin - 2", "dataMax + 2"]} tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: C.muted }} axisLine={false} tickLine={false} width={24} />
            </LineChart>
          </ResponsiveContainer>
        </div>;
      })}
    </div>

    <SectionHeader title="Storico rilevazioni" subtitle="Ultime registrazioni per ogni area" />
    <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
      {logs.map((l, i) => {
        const [minS, maxS] = l.target.split("/");
        const min = parseFloat(minS.replace("+", "")), max = parseFloat(maxS.replace("+", ""));
        const ok = l.temp >= min && l.temp <= max;
        return <div key={l.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 18px", borderBottom: i < logs.length - 1 ? `1px solid ${C.ruleSoft}` : "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <Thermometer size={15} color={ok ? C.olive : C.terra} />
            <div>
              <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{l.location}</div>
              <div className="f-mono" style={{ fontSize: 11, color: C.muted }}>{fmt.date(l.date)} · {l.time} · {l.operator}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div className="f-mono" style={{ fontSize: 11, color: C.muted }}>target {l.target}°C</div>
            <div className="f-mono" style={{ fontSize: 16, color: ok ? C.olive : C.terra, fontWeight: 600, minWidth: 60, textAlign: "right" }}>{l.temp}°C</div>
            {ok ? <CheckCircle2 size={14} color={C.olive} /> : <AlertTriangle size={14} color={C.terra} className="pulsing" />}
          </div>
        </div>;
      })}
    </div>
  </div>;
}

function HACCPCleaning({ state }) {
  const logs = [...state.haccpLogs.cleaning].sort((a, b) => new Date(b.date) - new Date(a.date));
  return <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
    {logs.map((l, i) => <div key={l.id} style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 18px", borderBottom: i < logs.length - 1 ? `1px solid ${C.ruleSoft}` : "none" }}>
      <Droplets size={16} color={C.teal} style={{ marginTop: 2 }} />
      <div style={{ flex: 1 }}>
        <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{l.zone}</div>
        <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{fmt.date(l.date)} · {l.time} · {l.operator}</div>
        {l.notes && <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, marginTop: 4, fontStyle: "italic" }}>"{l.notes}"</div>}
      </div>
    </div>)}
  </div>;
}

function HACCPNonConformity({ state, setState }) {
  const ncs = [...state.haccpLogs.nonConformities].sort((a, b) => new Date(b.date) - new Date(a.date));
  // FIX P1 (audit 2026-05-19): modale di CHIUSURA NC con azione correttiva,
  // operatore, esito di verifica e timestamp. Reg. CE 852/2004 art. 5 c. 2 lett. e/f.
  const [closingNc, setClosingNc] = useState(null);
  return <div>
    {closingNc && <HACCPCloseNcModal nc={closingNc} state={state} setState={setState} onClose={() => setClosingNc(null)} />}
    {ncs.length === 0 ? <EmptyState icon={CheckCircle2} title="Nessuna non conformità registrata" body="Le temperature fuori range generano automaticamente una bozza di NC" />
    : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {ncs.map(nc => {
        const isOpen = nc.status === "aperta";
        return <div key={nc.id} style={{ background: C.white, border: `1px solid ${isOpen ? C.terra : C.rule}`, padding: 16, borderRadius: 2 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="f-mono" style={{ fontSize: 11, color: C.ink, fontWeight: 600, letterSpacing: "0.1em" }}>{nc.type.toUpperCase()}</span>
              <Badge status={nc.status} />
              {nc.location && <span className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.1em" }}>{nc.location}</span>}
            </div>
            <div className="f-mono" style={{ fontSize: 11, color: C.muted }}>{fmt.dateFull(nc.date)} · {nc.operator}</div>
          </div>
          <div className="f-body" style={{ fontSize: 13, color: C.ink, lineHeight: 1.5, marginBottom: 6, whiteSpace: "pre-line" }}>{nc.description}</div>
          {Array.isArray(nc.evidence) && nc.evidence.length > 1 && <div style={{ marginBottom: 6, padding: 8, background: C.terraWash, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.terra, letterSpacing: "0.2em", marginBottom: 4 }}>EVIDENZE ({nc.evidence.length}) — RIPETUTE</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {nc.evidence.map((ev, i) => <div key={i} className="f-mono" style={{ fontSize: 10, color: C.terra }}>{ev.time || "—"} · {ev.temp != null ? `${ev.temp}°C` : "—"} · {ev.operator || "—"}</div>)}
            </div>
          </div>}
          <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, padding: 10, background: C.paperDeep, borderRadius: 2, whiteSpace: "pre-line" }}>
            <span className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em", marginRight: 6 }}>AZIONE CORRETTIVA:</span>{nc.action || "—"}
          </div>
          {nc.chiusura && <div style={{ marginTop: 8, padding: 10, background: C.oliveWash, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.oliveDeep, letterSpacing: "0.2em", marginBottom: 4 }}>CHIUSURA · {nc.chiusura.esito?.toUpperCase() || "RISOLTA"}</div>
            <div className="f-body" style={{ fontSize: 12, color: C.oliveDeep, lineHeight: 1.5 }}>{nc.chiusura.azione}</div>
            <div className="f-mono" style={{ fontSize: 10, color: C.oliveDeep, marginTop: 4 }}>{fmt.dateFull((nc.chiusura.timestamp || "").slice(0, 10))} · {nc.chiusura.operatore}</div>
          </div>}
          {isOpen && <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
            <Btn variant="olive" size="sm" icon={CheckCircle2} onClick={() => setClosingNc(nc)}>Chiudi NC</Btn>
          </div>}
        </div>;
      })}
    </div>}
  </div>;
}

// ── MODAL: Chiudi non-conformita' ──────────────────────────────────────────
// Reg. CE 852/2004 art. 5 c. 2 lett. e/f — azione correttiva documentata + verifica.
// Reg. CE 852/2004 art. 5 c. 2 lett. g — registrazione accessibile, append-only.
// Mai modificare retroattivamente NC gia' chiuse: una NC chiusa e' un atto firmato.
function HACCPCloseNcModal({ nc, state, setState, onClose }) {
  const [azione, setAzione] = useState("");
  const [operatore, setOperatore] = useState(state.currentUser?.name || "");
  const [esito, setEsito] = useState("risolto");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const submit = () => {
    if (submitting) return;
    if (!azione.trim()) { toast({ type: "error", message: "Descrivi l'azione correttiva applicata" }); return; }
    if (!operatore.trim()) { toast({ type: "error", message: "Indica l'operatore responsabile" }); return; }
    setSubmitting(true);
    try {
      // Append-only: la NC viene marcata "risolta"/"in_osservazione" ma i campi
      // precedenti restano leggibili (description, evidence, action). La firma di
      // chiusura va in un sotto-oggetto chiusura{} immutabile.
      const chiusura = {
        timestamp: nowIso(),
        operatore: operatore.trim(),
        azione: azione.trim(),
        esito,
        note: note.trim() || null,
      };
      const newStatus = esito === "escalation" ? "aperta" : "risolta";
      const next = {
        ...state,
        haccpLogs: {
          ...state.haccpLogs,
          nonConformities: state.haccpLogs.nonConformities.map(x => x.id === nc.id
            ? { ...x, status: newStatus, chiusura, closedBy: chiusura.operatore, closedAt: chiusura.timestamp }
            : x),
        },
        auditLog: appendAudit(state, {
          action: "nc_closed",
          entityType: "nonConformity",
          entityId: nc.id,
          reason: `NC chiusa con esito "${esito}" da ${chiusura.operatore}`,
          payload: { ncId: nc.id, esito },
        }),
      };
      setState(next);
      toast({ type: "success", title: "NC chiusa", message: `Esito: ${esito}` });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return <Modal onClose={onClose} maxWidth={560} subtitle="Chiusura non-conformita'" title={`Chiudi NC ${nc.id || ""}`}
    footer={<><Btn variant="ghost" onClick={onClose} disabled={submitting}>Annulla</Btn><Btn variant="olive" icon={CheckCircle2} onClick={submit} disabled={submitting}>{submitting ? "Registrazione..." : "Conferma chiusura"}</Btn></>}>
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ padding: 10, background: C.terraWash, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.terra, letterSpacing: "0.2em", marginBottom: 4 }}>NC IN CHIUSURA</div>
        <div className="f-body" style={{ fontSize: 12, color: C.terraDeep, whiteSpace: "pre-line" }}>{nc.description}</div>
      </div>
      <Field label="Azione correttiva applicata" required hint="Es: 'Sostituito termostato Frigo 2, verificato funzionamento per 4 ore, temperatura rientrata a +2°C'">
        <Textarea value={azione} onChange={e => setAzione(e.target.value)} rows={3} placeholder="Descrivi cosa hai fatto per risolvere..." />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Responsabile chiusura" required>
          <Input value={operatore} onChange={e => setOperatore(e.target.value)} placeholder="Nome operatore" />
        </Field>
        <Field label="Esito verifica" required>
          <Select value={esito} onChange={e => setEsito(e.target.value)}>
            <option value="risolto">Risolto — azione efficace</option>
            <option value="in_osservazione">In osservazione — da monitorare</option>
            <option value="escalation">Escalation — NC resta aperta</option>
          </Select>
        </Field>
      </div>
      <Field label="Note aggiuntive" hint="Opzionale">
        <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Eventuali considerazioni, follow-up, riferimenti..." />
      </Field>
      <div style={{ padding: 8, background: C.paperDeep, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>NORMATIVA: Reg. CE 852/2004 art. 5 c. 2 lett. e/f — azione correttiva + verifica obbligatorie. Registrazione APPEND-ONLY: dopo conferma non sara' piu' modificabile.</div>
      </div>
    </div>
  </Modal>;
}

function HACCPForm({ state, setState, tab, onClose }) {
  const [data, setData] = useState(tab === "temperature" ? { location: "Frigo pesce 1", temp: "", target: "0/+4", time: NOW_HOUR } : { zone: "Banco pesce", notes: "", time: NOW_HOUR });
  const toast = useToast();

  const submit = () => {
    if (tab === "temperature") {
      if (!data.temp) { toast({ type: "error", message: "Inserisci temperatura" }); return; }
      const log = { id: uid("t_"), date: TODAY, time: data.time, location: data.location, temp: parseFloat(data.temp), operator: state.currentUser.name, target: data.target };
      let next = { ...state, haccpLogs: { ...state.haccpLogs, temperature: [...state.haccpLogs.temperature, log] } };
      const [minS, maxS] = data.target.split("/");
      const min = parseFloat(minS.replace("+", "")), max = parseFloat(maxS.replace("+", ""));
      // FIX HACCP principio 5 — confronto INCLUSIVO sugli estremi (gli estremi sono OK).
      if (log.temp < min || log.temp > max) {
        // FIX P1 dedup NC temperatura (audit 2026-05-19): se esiste gia' una NC
        // APERTA nello stesso giorno per la stessa location e parametro temperatura,
        // NON ne creo una seconda: aggiungo una riga di evidenza alla NC esistente.
        // Reg. CE 852/2004 art. 5 principio 5: l'azione correttiva e' UNA per evento.
        const existingNc = (next.haccpLogs.nonConformities || []).find(nc =>
          nc.status === "aperta"
          && nc.type === "Temperatura"
          && nc.date === TODAY
          && nc.location === log.location
        );
        if (existingNc) {
          const evidenceLine = `${log.time} → ${log.temp}°C (fuori range ${data.target}°C) · ${state.currentUser.name}`;
          const updatedNc = {
            ...existingNc,
            evidence: [...(existingNc.evidence || []), { logId: log.id, time: log.time, temp: log.temp, operator: state.currentUser.name, at: nowIso() }],
            description: `${existingNc.description}\n${evidenceLine}`,
          };
          next.haccpLogs.nonConformities = next.haccpLogs.nonConformities.map(nc => nc.id === existingNc.id ? updatedNc : nc);
          toast({ type: "error", title: "Temperatura fuori range!", message: `Aggiunta evidenza alla NC ${existingNc.id} (gia' aperta oggi per ${log.location})` });
        } else {
          // Reg. CE 178/2002 art. 19 — campi strutturati per ritiro/richiamo:
          // location + evidence[] permettono di risalire ai lotti compromessi.
          const nc = {
            id: uid("nc_"),
            date: TODAY,
            type: "Temperatura",
            location: log.location,
            description: `${log.location} registrata a ${log.temp}°C (fuori range ${data.target}°C)`,
            action: "Da definire azione correttiva",
            operator: state.currentUser.name,
            status: "aperta",
            evidence: [{ logId: log.id, time: log.time, temp: log.temp, operator: state.currentUser.name, at: nowIso() }],
          };
          next.haccpLogs.nonConformities = [...next.haccpLogs.nonConformities, nc];
          toast({ type: "error", title: "Temperatura fuori range!", message: "Bozza di NC creata automaticamente" });
        }
      } else {
        toast({ type: "success", message: "Temperatura registrata" });
      }
      setState(next);
    } else {
      const log = { id: uid("c_"), date: TODAY, zone: data.zone, operator: state.currentUser.name, time: data.time, notes: data.notes };
      setState({ ...state, haccpLogs: { ...state.haccpLogs, cleaning: [...state.haccpLogs.cleaning, log] } });
      toast({ type: "success", message: "Sanificazione registrata" });
    }
    onClose();
  };

  return <Modal onClose={onClose} maxWidth={520} subtitle="HACCP" title={tab === "temperature" ? "Registra temperatura" : "Registra sanificazione"}
    footer={<><Btn variant="ghost" onClick={onClose}>Annulla</Btn><Btn variant="accent" icon={CheckCircle2} onClick={submit}>Registra</Btn></>}>
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      {tab === "temperature" ? <>
        <Field label="Area / frigorifero" required>
          <Select value={data.location} onChange={e => setData({ ...data, location: e.target.value })}>
            <option>Frigo pesce 1</option><option>Frigo pesce 2</option><option>Freezer -18</option><option>Cella verdure</option><option>Abbattitore</option>
          </Select>
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <Field label="Temperatura (°C)" required><Input type="number" step="0.1" value={data.temp} onChange={e => setData({ ...data, temp: e.target.value })} /></Field>
          <Field label="Target">
            <Select value={data.target} onChange={e => setData({ ...data, target: e.target.value })}>
              <option value="0/+4">0/+4 °C</option><option value="-20/-18">-20/-18 °C</option><option value="+2/+8">+2/+8 °C</option>
            </Select>
          </Field>
          <Field label="Ora"><Input type="time" value={data.time} onChange={e => setData({ ...data, time: e.target.value })} /></Field>
        </div>
      </> : <>
        <Field label="Zona sanificata" required>
          <Select value={data.zone} onChange={e => setData({ ...data, zone: e.target.value })}>
            <option>Banco pesce</option><option>Pavimenti laboratorio</option><option>Abbattitore</option><option>Banco lavorazione</option><option>Attrezzatura</option>
          </Select>
        </Field>
        <Field label="Ora"><Input type="time" value={data.time} onChange={e => setData({ ...data, time: e.target.value })} /></Field>
        <Field label="Note"><Textarea value={data.notes} onChange={e => setData({ ...data, notes: e.target.value })} placeholder="es. Prodotto utilizzato, osservazioni..." /></Field>
      </>}
    </div>
  </Modal>;
}


// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      PRODUZIONE                                            ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function Produzione({ state, setState, onNavigate, initial }) {
  const [showForm, setShowForm] = useState(initial?.new || false);
  const [typeFilter, setTypeFilter] = useState("all");

  const active = state.workOrders.filter(o => o.status === "in_corso");
  const filtered = state.workOrders.filter(o => typeFilter === "all" || o.type === typeFilter);

  return <div className="fade-up">
    <PageHeader kicker="Ordini di lavorazione" title="Produzione"
      subtitle="Monitora fasi e timer di ogni ordine. Completando l'ultima fase il sistema propone la creazione del lotto figlio."
      right={<Btn variant="accent" size="lg" icon={Plus} onClick={() => setShowForm(true)}>Nuovo ordine</Btn>} />

    {showForm && <NewOrderForm state={state} setState={setState} onClose={() => setShowForm(false)} />}

    <div className="stagger" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
      <StatCard kicker="In corso" value={active.length} unit="ordini" trend={`${active.reduce((s, o) => s + o.phases.filter(p => p.status === "in_progress").length, 0)} fasi attive`} accent={C.terra} icon={Flame} />
      <StatCard kicker="Completati oggi" value={state.workOrders.filter(o => o.status === "completato").length} unit="ordini" trend="turno corrente" accent={C.olive} icon={CheckCircle2} />
      <StatCard kicker="Pianificati" value={state.plannedOrders.length} unit="prossimi 7gg" trend="da avviare" accent={C.amber} icon={Calendar} onClick={() => onNavigate({ view: "pianificazione" })} />
    </div>

    <Tabs value={typeFilter} onChange={setTypeFilter} tabs={[
      { id: "all", label: "Tutti", count: state.workOrders.length },
      { id: "pesce", label: "Pesce", icon: Fish, count: state.workOrders.filter(o => o.type === "pesce").length },
      { id: "pane", label: "Pane", icon: Wheat, count: state.workOrders.filter(o => o.type === "pane").length },
      { id: "pasta", label: "Pasta", icon: CookingPot, count: state.workOrders.filter(o => o.type === "pasta").length },
    ]} />

    <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {filtered.length === 0 ? <EmptyState icon={Factory} title="Nessun ordine per questa categoria" action={<Btn variant="accent" icon={Plus} onClick={() => setShowForm(true)}>Nuovo ordine</Btn>} />
      : [...filtered].reverse().map(o => <OrderRow key={o.id} order={o} state={state} onClick={() => onNavigate({ view: "order", orderId: o.id })} />)}
    </div>
  </div>;
}

function OrderRow({ order, state, onClick, compact }) {
  const typeInfo = { pesce: { c: C.teal, icon: Fish }, pane: { c: C.amber, icon: Wheat }, pasta: { c: C.terra, icon: CookingPot } }[order.type] || { c: C.muted, icon: Factory };
  const Icon = typeInfo.icon;
  const doneCount = order.phases.filter(p => p.status === "completed").length;
  const totalCount = order.phases.length;
  const currentPhase = order.phases.find(p => p.status === "in_progress");
  const mainItem = state?.items.find(i => i.id === (state?.lots.find(l => l.id === order.lotIds[0])?.itemId));

  return <button onClick={onClick} className="tx hover-lift" style={{ background: C.white, border: `1px solid ${C.rule}`, padding: compact ? 14 : 18, borderRadius: 2, display: "flex", alignItems: "center", gap: 18, cursor: "pointer", textAlign: "left", width: "100%", fontFamily: "inherit" }}>
    <div style={{ width: 44, height: 44, background: typeInfo.c + "22", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, flexShrink: 0 }}>
      <Icon size={18} color={typeInfo.c} strokeWidth={1.6} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span className="f-mono" style={{ fontSize: 11, color: C.muted, letterSpacing: "0.1em" }}>{order.id}</span>
        <Badge status={order.status} />
        {currentPhase && <span className="f-mono" style={{ fontSize: 10, color: C.terra, letterSpacing: "0.1em", display: "inline-flex", alignItems: "center", gap: 4 }}><span className="pulsing" style={{ display: "inline-block", width: 6, height: 6, borderRadius: 6, background: C.terra }} />{currentPhase.name.toUpperCase()}</span>}
      </div>
      <div className="f-body" style={{ fontSize: 14, color: C.ink, fontWeight: 500, marginTop: 2 }}>{order.title}</div>
      <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{order.operator} · lotto {order.lotIds.join(", ")}</div>
    </div>
    {!compact && <div style={{ width: 180 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <span className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em" }}>AVANZAMENTO</span>
        <span className="f-mono" style={{ fontSize: 11, color: C.ink, fontWeight: 600 }}>{doneCount}/{totalCount}</span>
      </div>
      <ProgressBar value={doneCount} max={totalCount} color={order.status === "completato" ? C.olive : C.terra} />
    </div>}
    <ChevronRight size={16} color={C.muted} style={{ flexShrink: 0 }} />
  </button>;
}

function OrderDetail({ state, setState, orderId, onNavigate }) {
  const order = state.workOrders.find(o => o.id === orderId);
  const [showComplete, setShowComplete] = useState(false);
  const [tick, setTick] = useState(0);
  const toast = useToast();

  useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 15000); return () => clearInterval(t); }, []);

  if (!order) return <div className="fade-up">
    <PageHeader title="Ordine non trovato" />
    <EmptyState title="Ordine inesistente" action={<Btn onClick={() => onNavigate({ view: "produzione" })}>Torna a Produzione</Btn>} />
  </div>;

  const typeInfo = { pesce: { c: C.teal, icon: Fish }, pane: { c: C.amber, icon: Wheat }, pasta: { c: C.terra, icon: CookingPot } }[order.type];
  const mainLotId = order.lotIds[0];
  const mainLot = state.lots.find(l => l.id === mainLotId);
  const mainItem = mainLot ? engine.getItems(state).find(i => i.id === mainLot.itemId) : null;
  const doneCount = order.phases.filter(p => p.status === "completed").length;

  // Fix L2 — Reg. CE 852/2004 art. 5 + Principio HACCP 7: tracciamento operatore obbligatorio.
  // Ogni avvio/completamento fase salva nome+id dell'operatore loggato.
  const startPhase = (phaseId) => {
    const cu = state.currentUser || {};
    const updated = {
      ...order,
      phases: order.phases.map(p => p.id === phaseId
        ? { ...p, status: "in_progress", startedAt: nowIso(), startedBy: cu.name || null, startedById: cu.id || null }
        : p),
    };
    setState({
      ...state,
      workOrders: state.workOrders.map(o => o.id === orderId ? updated : o),
      auditLog: appendAudit(state, { action: "phase_start", entityType: "workOrder", entityId: orderId, reason: `Fase ${phaseId}` }),
    });
    toast({ type: "success", message: `Fase "${updated.phases.find(p => p.id === phaseId).name}" avviata` });
  };

  // Fix L3 — completePhase blocca il completamento di CCP fuori protocollo.
  //
  // CCP Anisakis (D.M. 17/07/2013 + Reg. CE 853/2004 All. III Sez. VIII):
  //   - T° centrale PRE richiesta (≤5°C, monitor catena del freddo in ingresso)
  //   - T° centrale POST + DURATA devono soddisfare ≤-20°C × 24h OPPURE ≤-35°C × 15h
  //   - violazione = blocco + Non Conformita' critica + audit log
  //
  // CCP Cottura (Reg. CE 852/2004 All. II Cap. IX):
  //   - T° centrale POST ≥75°C al cuore (abbattimento patogeni vegetativi)
  //
  // Sanzione amm.va: D.Lgs 193/2007 €1.500-€9.000.
  // Responsabilita' PENALE: art. 590 c.p. (lesioni colpose) in caso di danno al consumatore.
  const completePhase = (phaseId) => {
    const cu = state.currentUser || {};
    const phaseIdx = order.phases.findIndex(p => p.id === phaseId);
    const ph = order.phases[phaseIdx];
    if (!ph) return;

    // ── CCP Anisakis: validazione vincolante ────────────────────────────────
    if (ph.ccp === "anisakis") {
      // T° PRE: obbligatoria + ≤5°C (verifica catena del freddo in ingresso a abbattitore)
      if (ph.coreTempPre == null || isNaN(Number(ph.coreTempPre))) {
        toast({ type: "error", title: "CCP Anisakis", message: `⚠ T° centrale PRE abbattimento mancante. ${ph.normRef}` });
        return;
      }
      if (Number(ph.coreTempPre) > 5) {
        toast({ type: "error", title: "CCP Anisakis", message: `⚠ T° centrale PRE non valida (richiesta ≤5°C, registrata ${ph.coreTempPre}°C). Catena del freddo compromessa. ${ph.normRef}` });
        return;
      }
      // T° POST: obbligatoria
      if (ph.coreTempPost == null || isNaN(Number(ph.coreTempPost))) {
        toast({ type: "error", title: "CCP Anisakis", message: `⚠ T° centrale POST mancante. ${ph.normRef}` });
        return;
      }
      // Verifica protocollo: durata × temperatura
      const durationMin = ph.startedAt ? (Date.now() - new Date(ph.startedAt).getTime()) / 60000 : 0;
      const cTPost = Number(ph.coreTempPost);
      const protocolUsed = (cTPost <= ph.requiredCoreTempMaxAlt && durationMin >= ph.requiredDurationMinAt35)
        ? "-35°C × 15h"
        : (cTPost <= ph.requiredCoreTempMax && durationMin >= ph.requiredDurationMinAt20)
          ? "-20°C × 24h"
          : null;
      if (!protocolUsed) {
        const durH = Math.floor(durationMin / 60);
        const durM = Math.floor(durationMin % 60);
        toast({
          type: "error",
          title: "CCP Anisakis NON CONFORME",
          message: `⚠ Serve ≤-20°C × 24h OPPURE ≤-35°C × 15h al cuore. Registrato: ${cTPost}°C × ${durH}h ${durM}min. ${ph.normRef}`,
        });
        // Auto-NC critica append-only + audit. NON completa la fase.
        const nc = {
          id: `nc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          date: TODAY,
          type: "CCP Anisakis",
          kind: "ccp_anisakis_violation",
          severity: "critical",
          description: `Tentativo completare fase Anisakis fuori protocollo: T° ${cTPost}°C, durata ${durH}h ${durM}min. Norma: ${ph.normRef}`,
          action: "Da definire azione correttiva: prolungare abbattimento o scartare lotto",
          operator: cu.name || "Sconosciuto",
          status: "aperta",
          createdAt: nowIso(),
          ref: { type: "workOrder", id: orderId, phaseId },
        };
        setState({
          ...state,
          haccpLogs: {
            ...state.haccpLogs,
            nonConformities: [...(state.haccpLogs?.nonConformities || []), nc],
          },
          auditLog: appendAudit(state, {
            action: "ccp_violation_blocked",
            entityType: "workOrder",
            entityId: orderId,
            reason: `Anisakis non conforme: T° ${cTPost}°C × ${durH}h ${durM}min`,
            payload: { phaseId, coreTempPre: Number(ph.coreTempPre), coreTempPost: cTPost, durationMin: Math.round(durationMin) },
          }),
        });
        return;
      }
      // Conformita' OK: registra il protocollo applicato sulla fase
      // FIX BLOCKER 3 (audit 2026-06-07): timestamp REALE unico di abbattimento.
      // Lo usiamo per la fase, per l'audit E per stampare lo stato sul lotto padre,
      // cosi' che le tre registrazioni siano coerenti.
      const abbattutoAt = nowIso();
      const updatedOk = {
        ...order,
        phases: order.phases.map((p, i) => i === phaseIdx
          ? { ...p, status: "completed", endedAt: abbattutoAt, completedBy: cu.name || null, completedById: cu.id || null, coreTempProtocolUsed: protocolUsed }
          : p),
      };
      const isLastA = phaseIdx === order.phases.length - 1;
      if (isLastA) {
        updatedOk.status = "completato";
        updatedOk.completedAt = updatedOk.completedAt || abbattutoAt;
      }
      // ── FIX BLOCKER 3 — D.M. 17/07/2013 + Reg. CE 853/2004 All. III Sez. VIII ──
      // Al completamento conforme della fase Anisakis dobbiamo SCRIVERE SUL LOTTO
      // PADRE (input dell'ordine) lo stato "abbattuto" + abbattutoAt = data reale.
      // PRIMA non veniva scritto: createChildLot non vedeva parent.status="abbattuto"
      // ne' parent.abbattutoAt, quindi calcolava la scadenza del figlio dalla data
      // fresca (piu' lunga del dovuto) e il lotto padre non risultava abbattuto in
      // tracciabilita'. abbattutoAt e' la BASE corretta per la shelfLife estesa
      // del figlio (vedi createChildLot, ramo parent.status==="abbattuto").
      // Append-only sul movimento: NON cancelliamo nulla, segniamo solo lo stato +
      // protocollo + protocolN (timestamp) e lasciamo traccia in auditLog.
      const inputLotIds = new Set(order.lotIds || []);
      const lotsAbbattuti = state.lots.map(l => {
        if (!inputLotIds.has(l.id)) return l;
        // Se gia' abbattuto in passato (rilavorazione), preserviamo l'abbattutoAt
        // originale: il pesce non "ri-ringiovanisce" con un secondo passaggio.
        if (l.status === "abbattuto" && l.abbattutoAt) return l;
        return {
          ...l,
          status: "abbattuto",
          abbattutoAt,
          coreTempProtocolUsed: protocolUsed,
          abbattutoProtocol: { protocol: protocolUsed, coreTempPre: Number(ph.coreTempPre), coreTempPost: cTPost, workOrderId: orderId, normRef: ph.normRef || "Reg. CE 853/2004 — D.M. 17/07/2013" },
        };
      });
      setState({
        ...state,
        lots: lotsAbbattuti,
        workOrders: state.workOrders.map(o => o.id === orderId ? updatedOk : o),
        auditLog: appendAudit(state, {
          action: "ccp_anisakis_completed",
          entityType: "workOrder",
          entityId: orderId,
          reason: `Anisakis conforme ${protocolUsed} — lotti ${[...inputLotIds].join(", ")} marcati abbattuti @ ${abbattutoAt}`,
          payload: { phaseId, protocolUsed, abbattutoAt, lotIds: [...inputLotIds], coreTempPre: Number(ph.coreTempPre), coreTempPost: cTPost, durationMin: Math.round(durationMin) },
        }),
      });
      toast({ type: "success", title: "CCP Anisakis conforme", message: `✓ Protocollo ${protocolUsed} validato (PRE ${ph.coreTempPre}°C → POST ${cTPost}°C) · lotto marcato ABBATTUTO` });
      if (isLastA && !order.outputLotId) setShowComplete(true);
      return;
    }

    // ── CCP Cottura: validazione vincolante (Reg. CE 852/2004 All. II Cap. IX)
    if (ph.ccp === "cottura") {
      if (ph.coreTempPost == null || isNaN(Number(ph.coreTempPost))) {
        toast({ type: "error", title: "CCP Cottura", message: `⚠ T° centrale POST cottura mancante. ${ph.normRef}` });
        return;
      }
      const cTPost = Number(ph.coreTempPost);
      if (cTPost < (ph.requiredCoreTempMin || 75)) {
        toast({
          type: "error",
          title: "CCP Cottura NON CONFORME",
          message: `⚠ T° centrale ${cTPost}°C < richiesta ${ph.requiredCoreTempMin}°C. ${ph.normRef}`,
        });
        const nc = {
          id: `nc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          date: TODAY,
          type: "CCP Cottura",
          kind: "ccp_cottura_violation",
          severity: "critical",
          description: `Cottura insufficiente: T° centrale ${cTPost}°C < ${ph.requiredCoreTempMin}°C. Norma: ${ph.normRef}`,
          action: "Da definire azione correttiva: prolungare cottura o scartare",
          operator: cu.name || "Sconosciuto",
          status: "aperta",
          createdAt: nowIso(),
          ref: { type: "workOrder", id: orderId, phaseId },
        };
        setState({
          ...state,
          haccpLogs: {
            ...state.haccpLogs,
            nonConformities: [...(state.haccpLogs?.nonConformities || []), nc],
          },
          auditLog: appendAudit(state, {
            action: "ccp_violation_blocked",
            entityType: "workOrder",
            entityId: orderId,
            reason: `Cottura non conforme: T° ${cTPost}°C`,
            payload: { phaseId, coreTempPost: cTPost },
          }),
        });
        return;
      }
    }

    // ── Comportamento normale per fasi non-CCP (o cottura conforme) ─────────
    const updated = {
      ...order,
      phases: order.phases.map((p, i) => i === phaseIdx
        ? { ...p, status: "completed", endedAt: nowIso(), completedBy: cu.name || null, completedById: cu.id || null }
        : p),
    };
    const isLast = phaseIdx === order.phases.length - 1;
    if (isLast) {
      updated.status = "completato";
      // Timestamp di completamento ordine (richiesto da engine.stats.labYieldByType).
      updated.completedAt = updated.completedAt || nowIso();
    }
    setState({
      ...state,
      workOrders: state.workOrders.map(o => o.id === orderId ? updated : o),
      auditLog: appendAudit(state, { action: "phase_complete", entityType: "workOrder", entityId: orderId, reason: `Fase ${phaseId}${isLast ? " (ultima)" : ""}` }),
    });
    if (isLast && !order.outputLotId) {
      toast({ type: "success", title: "Ordine completato", message: "Proponi creazione lotto figlio?" });
      setShowComplete(true);
    } else {
      toast({ type: "success", message: "Fase completata" });
    }
  };

  // Fix L3 — update di un singolo campo della fase (T° centrale PRE/POST CCP).
  // Append-only: nessun edit di campi gia' valorizzati per fasi completate.
  const updatePhaseField = (phaseId, field, value) => {
    setState({
      ...state,
      workOrders: state.workOrders.map(o => o.id === orderId
        ? { ...o, phases: o.phases.map(p => p.id === phaseId && p.status !== "completed" ? { ...p, [field]: value } : p) }
        : o),
    });
  };

  return <div className="fade-up">
    {showComplete && <CompleteOrderModal order={order} mainLot={mainLot} mainItem={mainItem} state={state} setState={setState} onClose={() => setShowComplete(false)} onNavigate={onNavigate} />}
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
      <button onClick={() => onNavigate({ view: "produzione" })} className="f-mono tx" style={{ fontSize: 10, letterSpacing: "0.15em", color: C.muted, background: "transparent", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
        <ChevronLeft size={12} /> PRODUZIONE
      </button>
    </div>
    <PageHeader kicker={<span style={{ color: typeInfo?.c }}>{order.type.toUpperCase()} · {order.id}</span>} title={order.title}
      subtitle={<>Operatore <strong>{order.operator}</strong> · Lotto input <button onClick={() => onNavigate({ view: "lot", lotId: mainLotId })} className="f-mono" style={{ color: C.teal, background: "transparent", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", fontSize: 13 }}>{mainLotId}</button>{order.outputLotId && <> · Lotto output <button onClick={() => onNavigate({ view: "lot", lotId: order.outputLotId })} className="f-mono" style={{ color: C.teal, background: "transparent", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline", fontSize: 13 }}>{order.outputLotId}</button></>}</>}
      right={<Badge status={order.status}>{order.status.toUpperCase()}</Badge>} />

    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
      <div>
        <SectionHeader title="Fasi di lavorazione" subtitle={`${doneCount} di ${order.phases.length} completate`} />
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {order.phases.map((phase, idx) => <PhaseCard key={phase.id} phase={phase} index={idx} total={order.phases.length} onStart={() => startPhase(phase.id)} onComplete={() => completePhase(phase.id)} onUpdateField={(field, value) => updatePhaseField(phase.id, field, value)} orderStatus={order.status} tick={tick} workOrder={order} state={state} />)}
        </div>

        {order.status === "completato" && !order.outputLotId && <div style={{ marginTop: 16, padding: 16, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, display: "flex", alignItems: "center", gap: 14 }}>
          <Package size={20} color={C.teal} />
          <div style={{ flex: 1 }}>
            <div className="f-body" style={{ fontSize: 13, color: C.tealDeep, fontWeight: 500 }}>Crea il lotto figlio di questa lavorazione</div>
            <div className="f-body" style={{ fontSize: 12, color: C.tealDeep, opacity: 0.8 }}>Il nuovo lotto avrà tracciabilità automatica dal lotto padre</div>
          </div>
          <Btn size="sm" variant="accent" icon={Plus} onClick={() => setShowComplete(true)}>Crea lotto</Btn>
        </div>}
      </div>

      <div>
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2, marginBottom: 16 }}>
          <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.3em", color: C.muted, marginBottom: 12 }}>BILANCIO MASSA</div>
          {order.phases.filter(p => p.qtyIn != null).map(p => <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 0", fontSize: 12 }}>
            <span className="f-body" style={{ color: C.inkSoft }}>{p.name}</span>
            <span className="f-mono" style={{ color: C.ink, fontWeight: 600 }}>{p.qtyIn} → {p.qtyOut || "—"}</span>
          </div>)}
        </div>

        <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2 }}>
          <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.3em", color: C.muted, marginBottom: 12 }}>DETTAGLI</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Metric label="Creato" value={fmt.dateTime(order.createdAt)} />
            <Metric label="Tipo" value={<span className="f-mono" style={{ fontSize: 11, color: typeInfo?.c, letterSpacing: "0.1em" }}>{order.type.toUpperCase()}</span>} />
            <Metric label="Fasi totali" value={order.phases.length} />
          </div>
        </div>
      </div>
    </div>
  </div>;
}

function PhaseCard({ phase, index, total, onStart, onComplete, onUpdateField, orderStatus, tick, workOrder, state }) {
  const isActive = phase.status === "in_progress";
  const isDone = phase.status === "completed";
  const isCcp = !!phase.ccp;
  const color = isDone ? C.olive : (isActive ? C.terra : C.muted);
  const bg = isDone ? C.oliveWash : (isActive ? C.terraWash : C.paperDeep);

  let elapsedMin = 0;
  if (isActive && phase.startedAt) elapsedMin = Math.floor((Date.now() - new Date(phase.startedAt)) / 60000);
  else if (isDone && phase.startedAt && phase.endedAt) elapsedMin = Math.floor((new Date(phase.endedAt) - new Date(phase.startedAt)) / 60000);

  const overTarget = phase.durationTargetMin && elapsedMin > phase.durationTargetMin;

  // Fix L4 — Anomalia tempo: confronto con distribuzione storica delle stesse
  // fasi (type × phase.name) usando MAD-z robust. Mostrato solo per fasi
  // completate (servono start+end), con n >= 5 di storico.
  const phaseAnomalies = useMemo(() => {
    if (!workOrder || !state || !isDone) return [];
    return engine.stats.labPhaseAnomaly(workOrder, state);
    // dipendenze: lista workOrders + lots cambiano raramente, useMemo evita ricomputi su tick
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrder, state?.workOrders, state?.lots, isDone]);
  const phaseAnomaly = phaseAnomalies.find(a => a.phaseId === phase.id);

  // Fix L3 — calcoli runtime per CCP Anisakis (per timer "mancano Xh al protocollo")
  const minToProtocol20 = (phase.ccp === "anisakis" && phase.requiredDurationMinAt20 && elapsedMin < phase.requiredDurationMinAt20)
    ? phase.requiredDurationMinAt20 - elapsedMin : 0;

  return <div style={{ background: C.white, border: `1px solid ${isCcp ? C.terra : (isActive ? C.terra : C.rule)}`, borderLeft: isCcp ? `4px solid ${C.terra}` : undefined, borderRadius: 2, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
      <div style={{ width: 40, height: 40, borderRadius: 20, background: bg, border: `2px solid ${color}`, color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative" }} className={isActive ? "pulse-ring" : ""}>
        {isDone ? <CheckCircle2 size={18} strokeWidth={2} /> : <span className="f-mono" style={{ fontSize: 14, fontWeight: 700 }}>{index + 1}</span>}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span className="f-body" style={{ fontSize: 14, color: C.ink, fontWeight: 500 }}>{phase.name}</span>
          {isCcp && <span className="f-mono" style={{ fontSize: 9, padding: "2px 6px", background: C.terra, color: C.white, letterSpacing: "0.15em", borderRadius: 2, fontWeight: 700 }}>CCP {phase.ccp.toUpperCase()}</span>}
        </div>
        <div className="f-mono" style={{ fontSize: 11, color: C.muted, display: "flex", gap: 14, flexWrap: "wrap", marginTop: 2 }}>
          {phase.startedAt && <span>Avviata {fmt.time(phase.startedAt)}</span>}
          {phase.endedAt && <span>→ Completata {fmt.time(phase.endedAt)}</span>}
          {phase.operator && <span>· {phase.operator}</span>}
          {phase.qtyIn && <span>· {phase.qtyIn}{phase.qtyOut ? `→${phase.qtyOut}` : ""} kg</span>}
          {phase.targetTemp && <span>· target {phase.targetTemp}°C</span>}
        </div>
        {/* Fix L2 — Reg. CE 852/2004 art. 5: tracciamento operatore per fase (firma HACCP) */}
        {(phase.startedBy || phase.completedBy) && <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 4, display: "flex", gap: 12, flexWrap: "wrap" }}>
          {phase.startedBy && <span>Avviato da <strong style={{ color: C.inkSoft }}>{phase.startedBy}</strong>{phase.startedAt ? ` alle ${fmt.time(phase.startedAt)}` : ""}</span>}
          {phase.completedBy && <span>Completato da <strong style={{ color: C.inkSoft }}>{phase.completedBy}</strong>{phase.endedAt ? ` alle ${fmt.time(phase.endedAt)}` : ""}</span>}
        </div>}
        {(isActive || (isDone && elapsedMin > 0)) && <div className="f-mono" style={{ fontSize: 12, color: overTarget ? C.terra : (isActive ? C.terra : C.olive), marginTop: 4, fontWeight: 600 }}>
          <Clock size={11} style={{ display: "inline", marginRight: 4 }} />
          {Math.floor(elapsedMin / 60)}h {elapsedMin % 60}m
          {phase.durationTargetMin && ` / ${Math.floor(phase.durationTargetMin / 60)}h ${phase.durationTargetMin % 60}m target`}
          {overTarget && isActive && <span style={{ marginLeft: 6 }}>⚠ oltre target</span>}
        </div>}
        {/* Fix L4 — Badge anomalia tempo (MAD-z robust su storico stesso type × stessa fase). */}
        {phaseAnomaly && !phaseAnomaly.fallback && phaseAnomaly.isAnomaly && phaseAnomaly.medianMinPerKg != null && <div className="f-body" style={{ fontSize: 11, color: C.terra, marginTop: 4, fontWeight: 500 }}>
          ⚠ Tempo anomalo: {phaseAnomaly.observedMinPerKg.toFixed(1)} min/kg (mediana storica {phaseAnomaly.medianMinPerKg.toFixed(1)}, z={phaseAnomaly.z.toFixed(1)}, n={phaseAnomaly.n})
        </div>}
        {/* Fix L3 — Conformita' Anisakis registrata: visibile su fase completata */}
        {phase.coreTempProtocolUsed && isDone && <div className="f-body" style={{ fontSize: 11, color: C.oliveDeep, marginTop: 4, fontWeight: 600 }}>
          ✓ Conformita' Anisakis: {phase.coreTempProtocolUsed} (PRE {phase.coreTempPre}°C → POST {phase.coreTempPost}°C)
        </div>}
      </div>
      {orderStatus !== "completato" && <div>
        {phase.status === "pending" && <Btn size="sm" variant="accent" icon={Play} onClick={onStart}>Avvia</Btn>}
        {isActive && <Btn size="sm" variant="olive" icon={CheckCircle2} onClick={onComplete}>Completa</Btn>}
        {isDone && <span className="f-mono" style={{ fontSize: 10, color: C.olive, letterSpacing: "0.15em" }}>COMPLETATA</span>}
      </div>}
    </div>

    {/* Fix L3 — UI dedicata CCP Anisakis: campi T° centrale PRE/POST + timer protocollo. */}
    {/* D.M. 17/07/2013 + Reg. CE 853/2004 All. III Sez. VIII. */}
    {phase.ccp === "anisakis" && isActive && <div style={{ background: C.terraWash, padding: 12, borderRadius: 2, marginLeft: 58 }}>
      <div className="f-mono" style={{ fontSize: 10, color: C.terraDeep, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase" }}>
        ⚠ CCP Anisakis · {phase.normRef}
      </div>
      <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, marginTop: 4, marginBottom: 10 }}>
        Protocollo obbligatorio: T° ≤-20°C × 24h OPPURE T° ≤-35°C × 15h al cuore del prodotto. Misurare con sonda termometrica calibrata.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="T° centrale PRE abbattimento (°C)" required>
          <Input type="number" step="0.1" value={phase.coreTempPre ?? ""}
            onChange={e => onUpdateField("coreTempPre", e.target.value === "" ? null : parseFloat(e.target.value))}
            inputMode="decimal" lang="it" placeholder="es. 3.5" />
          <div className="f-body" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
            Richiesta ≤5°C all'ingresso in abbattitore
          </div>
        </Field>
        <Field label="T° centrale POST abbattimento (°C)" required>
          <Input type="number" step="0.1" value={phase.coreTempPost ?? ""}
            onChange={e => onUpdateField("coreTempPost", e.target.value === "" ? null : parseFloat(e.target.value))}
            inputMode="decimal" lang="it" placeholder="es. -22" />
          <div className="f-body" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
            Misurata al cuore del prodotto (sonda)
          </div>
        </Field>
      </div>
      {phase.startedAt && <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
        <Clock size={11} style={{ display: "inline", marginRight: 4 }} />
        Durata corrente: {Math.floor(elapsedMin / 60)}h {elapsedMin % 60}min
        {minToProtocol20 > 0 && <span style={{ color: C.terra, marginLeft: 8, fontWeight: 600 }}>
          → mancano {Math.floor(minToProtocol20 / 60)}h {minToProtocol20 % 60}min al protocollo -20°C × 24h
        </span>}
        {minToProtocol20 === 0 && phase.requiredDurationMinAt20 && <span style={{ color: C.oliveDeep, marginLeft: 8, fontWeight: 600 }}>
          ✓ Durata minima -20°C × 24h raggiunta
        </span>}
      </div>}
    </div>}

    {/* Fix L3 — UI dedicata CCP Cottura: T° centrale ≥75°C (Reg. CE 852/2004 All. II Cap. IX). */}
    {phase.ccp === "cottura" && isActive && <div style={{ background: C.amberWash || C.terraWash, padding: 12, borderRadius: 2, marginLeft: 58 }}>
      <div className="f-mono" style={{ fontSize: 10, color: C.terraDeep, fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase" }}>
        ⚠ CCP Cottura · {phase.normRef}
      </div>
      <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, marginTop: 4, marginBottom: 10 }}>
        Protocollo obbligatorio: T° centrale ≥{phase.requiredCoreTempMin || 75}°C al cuore (abbattimento patogeni vegetativi).
      </div>
      <Field label={`T° centrale POST cottura (°C)`} required>
        <Input type="number" step="0.1" value={phase.coreTempPost ?? ""}
          onChange={e => onUpdateField("coreTempPost", e.target.value === "" ? null : parseFloat(e.target.value))}
          inputMode="decimal" lang="it" placeholder={`es. ${(phase.requiredCoreTempMin || 75) + 2}`} />
        <div className="f-body" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
          Misurata al cuore del prodotto (sonda)
        </div>
      </Field>
    </div>}
  </div>;
}

function CompleteOrderModal({ order, mainLot, mainItem, state, setState, onClose, onNavigate }) {
  const prevQtyOut = [...order.phases].reverse().find(p => p.qtyOut)?.qtyOut || 0;
  const prevQtyIn = [...order.phases].find(p => p.qtyIn)?.qtyIn || (mainLot?.qtyRemaining ?? 0);
  // Fix L1 — Bilancio massa: qtyIn (kg prelevati dal padre) ora esplicito.
  // Default = qtyIn fase iniziale o intero qtyRemaining del padre.
  const [qtyIn, setQtyIn] = useState(prevQtyIn);
  const [qtyOut, setQtyOut] = useState(prevQtyOut || prevQtyIn || 0);
  const [extraShelfLifeDays, setExtraShelfLifeDays] = useState(order.type === "pesce" ? 60 : (mainItem?.shelfLifeDays || 7));
  const [notes, setNotes] = useState(order.type === "pesce" ? "Abbattuto a -18°C, sottovuoto" : "");
  const toast = useToast();

  // Resa attesa (benchmark indicativi per tipologia, espressi 0..1).
  // Pesce: ~0.55 (sfilettatura+pulizia); Pane: ~0.92 (calo cottura); Pasta: ~0.97.
  const targetYield = order.type === "pesce" ? 0.55 : order.type === "pane" ? 0.92 : 0.97;
  const qtyInNum = parseFloat(qtyIn) || 0;
  const qtyOutNum = parseFloat(qtyOut) || 0;
  const actualYield = qtyInNum > 0 ? qtyOutNum / qtyInNum : 0;
  const yieldDelta = actualYield - targetYield;
  const yieldWarn = qtyInNum > 0 && qtyOutNum > 0 && Math.abs(yieldDelta) > 0.10; // ±10pp
  const yieldOverdraft = qtyInNum > (mainLot?.qtyRemaining ?? 0) + 0.001;

  const create = () => {
    if (!qtyOutNum || qtyOutNum <= 0) { toast({ type: "error", message: "Inserisci peso uscita valido" }); return; }
    if (!qtyInNum || qtyInNum <= 0) { toast({ type: "error", message: "Inserisci peso entrata valido" }); return; }
    if (qtyOutNum > qtyInNum + 0.001) { toast({ type: "error", message: "Peso uscita non puo' superare peso entrata (legge conservazione massa)" }); return; }
    if (yieldOverdraft) { toast({ type: "error", message: `Peso entrata (${qtyInNum} kg) supera giacenza padre (${mainLot.qtyRemaining} kg)` }); return; }
    // Fix L3 — Propaga il protocollo Anisakis applicato (se presente) al lotto figlio.
    // L'etichetta del figlio riportera' "ABBATTUTO Reg. CE 853/2004 (-20°C × 24h, lotto X)".
    const anisakisPhase = (order.phases || []).find(p => p.ccp === "anisakis" && p.coreTempProtocolUsed);
    // FIX audit 2026-06-06: aggancia il costo reale di manodopera al lotto figlio.
    // workOrderLaborCost esisteva ma non veniva MAI invocato → il costo di
    // trasformazione (ore operatore × €/h) restava 0 e il CMP del figlio conteneva
    // solo la materia prima. Ora il childUnitCost include la manodopera reale.
    const labor = engine.workOrderLaborCost(state, order.id);
    const result = engine.createChildLot(state, {
      parentLotId: mainLot.id,
      itemId: mainLot.itemId,
      qtyOut: qtyOutNum,
      consumedFromParent: qtyInNum,
      workOrderId: order.id,
      extraShelfLifeDays: parseInt(extraShelfLifeDays),
      notes,
      unit: mainLot.unit,
      laborCost: labor.totalCost || 0,
      operatorName: order.operator || null,
      coreTempProtocolUsed: anisakisPhase?.coreTempProtocolUsed || null,
    });
    if (result.error || !result.newLot) {
      const msg = result.error === "INSUFFICIENT_PARENT_STOCK"
        ? `Stock padre insufficiente: disponibili ${result.available?.toFixed(2)} kg, richiesti ${result.requested?.toFixed(2)} kg`
        : `Errore: ${result.error || "creazione lotto fallita"}`;
      toast({ type: "error", message: msg });
      return;
    }
    const { state: newState, newLot } = result;
    const finalState = {
      ...newState,
      workOrders: newState.workOrders.map(o => o.id === order.id
        ? { ...o, outputLotId: newLot.id, completedAt: o.completedAt || nowIso(), qtyIn: qtyInNum, qtyOut: qtyOutNum, yieldRatio: actualYield }
        : o),
    };
    setState(finalState);
    toast({ type: "success", title: "Lotto figlio creato", message: `${newLot.id} · ${qtyOutNum} ${mainLot.unit} · resa ${(actualYield * 100).toFixed(1)}% · scade ${fmt.date(newLot.expiresAt)}` });
    onClose();
    setTimeout(() => onNavigate({ view: "lot", lotId: newLot.id }), 400);
  };

  return <Modal onClose={onClose} maxWidth={560} subtitle="Ordine completato" title="Crea lotto figlio"
    footer={<><Btn variant="ghost" onClick={onClose}>Salta</Btn><Btn variant="accent" icon={Plus} onClick={create}>Crea lotto figlio</Btn></>}>
    <div style={{ padding: 24 }}>
      <div style={{ padding: 14, background: C.paperDeep, borderRadius: 2, marginBottom: 20 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", marginBottom: 4 }}>LOTTO PADRE</div>
        <div className="f-mono" style={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>{mainLot.id}</div>
        <div className="f-body" style={{ fontSize: 12, color: C.muted }}>{mainItem?.name} · {mainLot.qtyRemaining} {mainLot.unit} rimanenti</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Peso entrata (kg dal padre)" required>
            <Input type="number" step="0.01" min="0" value={qtyIn} onChange={e => setQtyIn(e.target.value)} inputMode="decimal" lang="it" />
            {mainLot && (
              <div className="f-body" style={{ fontSize: 11, color: yieldOverdraft ? C.terra : C.muted, marginTop: 4 }}>
                Disponibile: {Number(mainLot.qtyRemaining).toFixed(2)} {mainLot.unit}
                {yieldOverdraft && <span style={{ marginLeft: 6, fontWeight: 600 }}>⚠ supera giacenza</span>}
              </div>
            )}
          </Field>
          <Field label="Peso uscita (kg prodotto)" required>
            <Input type="number" step="0.01" min="0" value={qtyOut} onChange={e => setQtyOut(e.target.value)} inputMode="decimal" lang="it" />
            {qtyInNum > 0 && qtyOutNum > 0 && (
              <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                Resa: <strong style={{ color: yieldWarn ? C.amberDeep : C.olive }}>{(actualYield * 100).toFixed(1)}%</strong>
                <span style={{ color: C.muted, marginLeft: 4 }}>· atteso {(targetYield * 100).toFixed(0)}%</span>
                {yieldWarn && (
                  <div style={{ color: C.amberDeep, fontSize: 10, marginTop: 2, fontWeight: 600 }}>
                    ⚠ scostamento {yieldDelta >= 0 ? "+" : ""}{(yieldDelta * 100).toFixed(1)}pp
                  </div>
                )}
              </div>
            )}
          </Field>
        </div>
        <Field label="Shelf life (giorni)" hint="Dopo abbattimento: anche 60-90gg">
          <Input type="number" value={extraShelfLifeDays} onChange={e => setExtraShelfLifeDays(e.target.value)} />
        </Field>
        <Field label="Note lotto" hint="Verranno stampate in etichetta">
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="es. Abbattuto, sottovuoto..." />
        </Field>
        {qtyInNum > 0 && qtyOutNum > 0 && (() => {
          const pUC = (Number(mainLot.unitCost) > 0 ? Number(mainLot.unitCost) : Number(mainLot.unitCostReal)) || 0;
          if (pUC <= 0) return null;
          const childUC = (pUC * qtyInNum) / qtyOutNum;
          return (
            <div className="f-mono" style={{ background: C.tealWash, border: `1px solid ${C.teal}`, padding: 10, borderRadius: 2, fontSize: 11, color: C.tealDeep, lineHeight: 1.6 }}>
              CMP figlio: <strong>{fmt.eur(childUC)}/{mainLot.unit}</strong>
              <span style={{ color: C.muted }}> ({fmt.eur(pUC)} × {qtyInNum} / {qtyOutNum})</span>
            </div>
          );
        })()}
      </div>
    </div>
  </Modal>;
}

function NewOrderForm({ state, setState, onClose }) {
  const [type, setType] = useState("pesce");
  const [title, setTitle] = useState("");
  const [lotId, setLotId] = useState("");
  const [operator, setOperator] = useState(state.users.filter(u => u.role === "Laboratorio")[0]?.name || "");
  const toast = useToast();

  // Fix L3 — D.M. 17/07/2013 + Reg. CE 853/2004 All. III Sez. VIII Cap. III lett. D:
  // abbattimento Anisakis OBBLIGATORIO per pesce destinato a crudo/marinato/affumicato a freddo.
  // Protocollo vincolante: T°≤-20°C × 24h OPPURE T°≤-35°C × 15h al CUORE del prodotto.
  // Sanzione amm.va D.Lgs 193/2007: €1.500-€9.000 + responsabilita' PENALE titolare
  // (art. 590 c.p. lesioni colpose) in caso di anisakidosi nel cliente.
  // Reg. CE 852/2004 All. II Cap. IX: cottura ≥75°C al cuore (CCP cottura).
  const phaseTemplates = {
    pesce: [
      { name: "Pulizia e sfilettatura", durationTargetMin: null, ccp: null },
      {
        name: "Abbattimento Anisakis",
        durationTargetMin: 1440,                    // 24h minimi (protocollo -20°C)
        ccp: "anisakis",                            // marker CCP
        requiresCoreTemp: true,                     // T° centrale prodotto richiesta
        requiredCoreTempMax: -20,                   // °C protocollo standard
        requiredCoreTempMaxAlt: -35,                // °C protocollo rapido
        requiredDurationMinAt20: 24 * 60,           // 1440 min @ -20°C
        requiredDurationMinAt35: 15 * 60,           // 900 min @ -35°C
        normRef: "D.M. 17/07/2013 + Reg. CE 853/2004 All. III Sez. VIII",
      },
      { name: "Confezionamento sottovuoto", durationTargetMin: 30, ccp: null },
      { name: "Etichettatura", durationTargetMin: 10, ccp: null },
    ],
    pane: [
      { name: "Impasto e lievitazione", durationTargetMin: 180, ccp: null },
      {
        name: "Cottura",
        durationTargetMin: 25,
        ccp: "cottura",
        requiresCoreTemp: true,
        requiredCoreTempMin: 75,                    // ≥75°C al cuore (abbattimento patogeni)
        normRef: "Reg. CE 852/2004 All. II Cap. IX",
      },
      { name: "Raffreddamento", durationTargetMin: 60, ccp: null },
    ],
    pasta: [
      { name: "Impasto", durationTargetMin: 30, ccp: null },
      { name: "Stesura e taglio", durationTargetMin: 45, ccp: null },
      { name: "Asciugatura", durationTargetMin: 120, ccp: null },
    ],
  };

  const availLots = state.lots.filter(l => l.status !== "esaurito" && l.qtyRemaining > 0 && ((type === "pesce" && engine.getItems(state).find(i => i.id === l.itemId)?.category === "pesce") || (type !== "pesce" && engine.getItems(state).find(i => i.id === l.itemId)?.category === "farine")));

  const submit = () => {
    if (!title.trim() || !lotId) { toast({ type: "error", message: "Compila titolo e lotto input" }); return; }
    const id = `WO-${(state.workOrders.length + 1).toString().padStart(3, "0")}`;
    const newOrder = {
      id, type, title: title.trim(), lotIds: [lotId], status: "in_corso", operator, createdAt: nowIso(), outputLotId: null,
      // Fix L3 — trasferisce TUTTI i campi CCP dal template alla fase del workOrder,
      // cosi' completePhase puo' validare i vincoli normativi al momento del completamento.
      phases: phaseTemplates[type].map((t, i) => ({
        id: `p${i + 1}`,
        name: t.name,
        status: "pending",
        durationTargetMin: t.durationTargetMin || null,
        ccp: t.ccp || null,
        requiresCoreTemp: t.requiresCoreTemp || false,
        requiredCoreTempMax: t.requiredCoreTempMax ?? null,
        requiredCoreTempMaxAlt: t.requiredCoreTempMaxAlt ?? null,
        requiredDurationMinAt20: t.requiredDurationMinAt20 ?? null,
        requiredDurationMinAt35: t.requiredDurationMinAt35 ?? null,
        requiredCoreTempMin: t.requiredCoreTempMin ?? null,
        normRef: t.normRef || null,
        startedAt: null,
        endedAt: null,
        coreTempPre: null,
        coreTempPost: null,
        coreTempProtocolUsed: null,
      })),
    };
    setState({ ...state, workOrders: [...state.workOrders, newOrder] });
    toast({ type: "success", message: `Ordine ${id} creato` });
    onClose();
  };

  return <Modal onClose={onClose} maxWidth={580} subtitle="Nuovo" title="Ordine di lavorazione"
    footer={<><Btn variant="ghost" onClick={onClose}>Annulla</Btn><Btn variant="accent" icon={Play} onClick={submit}>Crea e avvia</Btn></>}>
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
      <Field label="Tipo" required>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          {["pesce", "pane", "pasta"].map(t => {
            const isSel = type === t;
            const typeInfo = { pesce: { c: C.teal, icon: Fish }, pane: { c: C.amber, icon: Wheat }, pasta: { c: C.terra, icon: CookingPot } }[t];
            return <button key={t} onClick={() => setType(t)} className="tx" style={{ padding: "12px 10px", background: isSel ? typeInfo.c : C.white, color: isSel ? C.white : C.ink, border: `1px solid ${isSel ? typeInfo.c : C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <typeInfo.icon size={18} />
              <span className="f-body" style={{ fontSize: 12, fontWeight: 500, textTransform: "capitalize" }}>{t}</span>
            </button>;
          })}
        </div>
      </Field>
      <Field label="Titolo ordine" required><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="es. Preparazione sogliola per servizio sera" /></Field>
      <Field label="Lotto input" required hint="Lotto da cui prelevare la materia prima">
        <Select value={lotId} onChange={e => setLotId(e.target.value)}>
          <option value="">— Seleziona —</option>
          {availLots.map(l => { const it = engine.getItems(state).find(i => i.id === l.itemId); return <option key={l.id} value={l.id}>{l.id} · {it?.name} ({l.qtyRemaining} {l.unit})</option>; })}
        </Select>
      </Field>
      <Field label="Operatore assegnato" required>
        <Select value={operator} onChange={e => setOperator(e.target.value)}>
          {state.users.filter(u => u.role === "Laboratorio").map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
        </Select>
      </Field>
      <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", marginBottom: 6 }}>FASI AUTOMATICHE</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {phaseTemplates[type].map((p, i) => <span key={i} className="f-body" style={{ fontSize: 11, padding: "3px 8px", background: p.ccp ? C.terraWash : C.white, border: `1px solid ${p.ccp ? C.terra : C.rule}`, borderRadius: 2, color: p.ccp ? C.terraDeep : C.inkSoft, fontWeight: p.ccp ? 600 : 400 }}>{i + 1}. {p.name}{p.ccp && " · CCP"}</span>)}
        </div>
      </div>
    </div>
  </Modal>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      PIANIFICAZIONE                                        ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function Pianificazione({ state, setState, onNavigate }) {
  const [weekStart, setWeekStart] = useState(TODAY);
  const toast = useToast();
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d.toISOString().slice(0, 10);
  });

  const removeItem = (id) => {
    setState({ ...state, plannedOrders: state.plannedOrders.filter(p => p.id !== id) });
    toast({ type: "info", message: "Pianificazione rimossa" });
  };

  const shift = (n) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + n); setWeekStart(d.toISOString().slice(0, 10));
  };

  // Fix L4 — Forecast capacita' laboratorio prossimi 7 giorni
  // Calcolo: oreRichieste = Σ (qty / throughputMediano_type) con bootstrap CI 95%
  // Confronto con capacita' disponibile (operatoriCount × oreSettimana, default 3 × 32 = 96h)
  const capacityForecast = useMemo(
    () => engine.stats.labCapacityForecast(state, { lookAheadDays: 7 }),
    [state.plannedOrders, state.workOrders, state.lots]
  );

  return <div className="fade-up">
    <PageHeader kicker="Calendario settimanale" title="Pianificazione produzione"
      subtitle="Ordini di lavorazione pianificati. Trasforma in ordini attivi al momento giusto."
      right={<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <IconBtn icon={ChevronLeft} onClick={() => shift(-7)} title="Settimana precedente" />
        <div className="f-mono" style={{ fontSize: 11, color: C.muted, letterSpacing: "0.1em" }}>{fmt.dateShort(days[0])} — {fmt.dateShort(days[6])}</div>
        <IconBtn icon={ChevronRight} onClick={() => shift(7)} title="Settimana successiva" />
      </div>} />

    {/* Fix L4 — Capacita' laboratorio · 7gg (con CI 95% bootstrap) */}
    {!capacityForecast.fallback && (() => {
      const f = capacityForecast;
      const ratio = f.hoursAvailable > 0 ? f.hoursRequired.point / f.hoursAvailable : 0;
      const color = ratio > 0.85 ? C.terra : ratio > 0.65 ? C.amberDeep : C.olive;
      return <div style={{ background: C.white, padding: 16, borderRadius: 2, marginBottom: 16, border: `1px solid ${C.rule}`, borderLeft: `4px solid ${color}` }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.2em" }}>
          Capacita' laboratorio · prossimi 7gg
        </div>
        <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color, marginTop: 6, letterSpacing: "-0.01em" }}>
          {f.hoursRequired.point.toFixed(1)}h richieste · {f.hoursAvailable.toFixed(0)}h disponibili
        </div>
        <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
          Banda CI 95%: {f.hoursRequired.ci95Low.toFixed(1)}h–{f.hoursRequired.ci95High.toFixed(1)}h
          {f.deficit > 0 && <span style={{ color: C.terra, fontWeight: 600, marginLeft: 8 }}>· DEFICIT {f.deficit.toFixed(1)}h</span>}
          {f.deficit === 0 && f.hoursAvailable > 0 && <span style={{ color: C.olive, fontWeight: 600, marginLeft: 8 }}>· margine {(100 * (1 - ratio)).toFixed(0)}%</span>}
        </div>
        {Object.keys(f.byType).length > 0 && <div className="f-body" style={{ marginTop: 10, fontSize: 11, color: C.inkSoft, display: "flex", gap: 16, flexWrap: "wrap" }}>
          <span className="f-mono" style={{ color: C.muted, letterSpacing: "0.15em", fontSize: 10 }}>PER TIPOLOGIA:</span>
          {Object.entries(f.byType).map(([t, v]) => <span key={t}>
            <strong>{t}</strong> {v.fallback ? <span style={{ color: C.amberDeep }}>(storia insuff.)</span> : `${v.hours.toFixed(1)}h`}
            <span style={{ color: C.muted }}> · {v.qty}kg</span>
          </span>)}
        </div>}
        {f.anyTypeFallback && <div className="f-body" style={{ fontSize: 10, color: C.amberDeep, marginTop: 8 }}>
          Alcuni tipi non hanno storico sufficiente (n &lt; 3 lavorazioni 60gg) — non contribuiscono al monte ore stimato.
        </div>}
      </div>;
    })()}

    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
      {days.map(d => {
        const items = state.plannedOrders.filter(p => p.date === d);
        const isToday = d === TODAY;
        return <div key={d} style={{ background: C.white, border: `1px solid ${isToday ? C.ink : C.rule}`, borderRadius: 2, padding: 12, minHeight: 220 }}>
          <div style={{ marginBottom: 10 }}>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>{new Date(d).toLocaleDateString("it-IT", { weekday: "short" }).toUpperCase()}</div>
            <div className="f-display" style={{ fontSize: 20, color: isToday ? C.terra : C.ink, fontWeight: 500 }}>{new Date(d).getDate()}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {items.map(p => {
              const typeInfo = { pesce: { c: C.teal, icon: Fish }, pane: { c: C.amber, icon: Wheat }, pasta: { c: C.terra, icon: CookingPot } }[p.type];
              return <div key={p.id} style={{ background: typeInfo?.c + "22" || C.paperDeep, padding: 8, borderRadius: 2, position: "relative" }}>
                <div className="f-mono" style={{ fontSize: 9, color: typeInfo?.c, letterSpacing: "0.15em", marginBottom: 2 }}>{p.type.toUpperCase()}</div>
                <div className="f-body" style={{ fontSize: 11, color: C.ink, fontWeight: 500, lineHeight: 1.3 }}>{p.title}</div>
                <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 3 }}>{p.operator}</div>
                <button onClick={() => removeItem(p.id)} style={{ position: "absolute", top: 2, right: 2, background: "transparent", border: "none", cursor: "pointer", opacity: 0.3, padding: 2 }}><X size={10} /></button>
              </div>;
            })}
            {items.length === 0 && <div className="f-body" style={{ fontSize: 10, color: C.muted, fontStyle: "italic", textAlign: "center", padding: 10 }}>nessuna lavorazione</div>}
          </div>
        </div>;
      })}
    </div>
  </div>;
}


// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      RICHIESTE CUCINA (Chef → Magazzino → Conferma)       ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function RichiesteCucina({ state, setState, onNavigate, initial }) {
  const [showForm, setShowForm] = useState(initial?.new || false);
  const [pickRequest, setPickRequest] = useState(null);
  const [filter, setFilter] = useState("active");
  const toast = useToast();

  const pending = state.kitchenRequests.filter(kr => kr.status === "in_attesa");
  const completed = state.kitchenRequests.filter(kr => kr.status === "completata");

  const visible = filter === "active" ? pending : (filter === "done" ? completed : state.kitchenRequests);

  const cancel = (id) => {
    if (!confirm("Annullare questa richiesta?")) return;
    setState({ ...state, kitchenRequests: state.kitchenRequests.map(kr => kr.id === id ? { ...kr, status: "annullata" } : kr) });
    toast({ type: "info", message: "Richiesta annullata" });
  };

  return <div className="fade-up">
    <PageHeader kicker="Workflow cucina ↔ magazzino" title="Richieste cucina"
      subtitle="Lo chef richiede materie prime, il magazzino preleva dai lotti giusti (FIFO automatico) e conferma. Ogni prelievo è tracciato."
      right={<Btn variant="terra" size="lg" icon={ChefHat} onClick={() => setShowForm(true)}>Nuova richiesta</Btn>} />

    {showForm && <KitchenRequestForm state={state} setState={setState} onClose={() => setShowForm(false)} />}
    {pickRequest && <KitchenPickForm request={pickRequest} state={state} setState={setState} onClose={() => setPickRequest(null)} />}

    <div className="stagger" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
      <StatCard kicker="In attesa" value={pending.length} unit="richieste" trend={pending.filter(kr => kr.urgency === "urgente").length > 0 ? `${pending.filter(kr => kr.urgency === "urgente").length} URGENTI` : "da evadere"} accent={pending.filter(kr => kr.urgency === "urgente").length > 0 ? C.terra : C.amber} icon={Clock} />
      <StatCard kicker="Completate oggi" value={completed.filter(kr => kr.completedAt?.startsWith(TODAY)).length} unit="prelievi" trend="turno corrente" accent={C.olive} icon={CheckCircle2} />
      <StatCard kicker="Totale prelievi" value={state.kitchenRequests.length} unit="storico" trend="tutte le richieste" accent={C.ink} icon={History} />
    </div>

    <Tabs value={filter} onChange={setFilter} tabs={[
      { id: "active", label: "In attesa", icon: Clock, count: pending.length },
      { id: "done", label: "Completate", icon: CheckCircle2, count: completed.length },
      { id: "all", label: "Tutte", count: state.kitchenRequests.length },
    ]} />

    <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {visible.length === 0 ? <EmptyState icon={ChefHat} title={filter === "active" ? "Nessuna richiesta in attesa" : "Nessuna richiesta"} body={filter === "active" ? "Tutte le richieste sono evase" : "Crea la prima richiesta per la cucina"} action={filter !== "active" ? <Btn variant="terra" icon={Plus} onClick={() => setShowForm(true)}>Nuova richiesta</Btn> : null} />
      : [...visible].reverse().map(kr => <KitchenRequestCard key={kr.id} request={kr} state={state} onPick={() => setPickRequest(kr)} onCancel={() => cancel(kr.id)} onNavigate={onNavigate} />)}
    </div>
  </div>;
}

function KitchenRequestCard({ request, state, onPick, onCancel, onNavigate }) {
  const isUrgent = request.urgency === "urgente";
  const isPending = request.status === "in_attesa";
  const isComplete = request.status === "completata";
  const ageMin = Math.floor((Date.now() - new Date(request.createdAt)) / 60000);

  return <div style={{ background: C.white, border: `1px solid ${isUrgent && isPending ? C.terra : C.rule}`, borderRadius: 2, padding: 18, position: "relative" }} className={isUrgent && isPending ? "pulse-ring" : ""}>
    {isUrgent && isPending && <div style={{ position: "absolute", top: -1, right: -1, background: C.terra, color: C.white, padding: "3px 10px", fontSize: 10, letterSpacing: "0.2em" }} className="f-mono pulsing">⚠ URGENTE</div>}
    <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
      <div style={{ width: 44, height: 44, background: C.terraWash, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, flexShrink: 0 }}>
        <ChefHat size={18} color={C.terra} strokeWidth={1.6} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
          <span className="f-body" style={{ fontSize: 14, color: C.ink, fontWeight: 500 }}>{request.requestedBy}</span>
          <Badge status={request.status} />
          {isPending && <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>· creata {ageMin < 60 ? `${ageMin} min fa` : `${Math.floor(ageMin / 60)}h fa`}</span>}
          {isComplete && request.completedAt && <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>· evasa {fmt.time(request.completedAt)} da {request.pickedBy}</span>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
          {request.items.map((ri, i) => {
            const item = engine.getItems(state).find(it => it.id === ri.itemId);
            const pickedLot = request.pickedLots?.find(pl => pl.itemId === ri.itemId);
            return <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
              <span style={{ width: 6, height: 6, background: cats[item?.category]?.color || C.muted, borderRadius: 6 }} />
              <span className="f-body" style={{ color: C.ink, fontWeight: 500 }}>{item?.name}</span>
              <span className="f-mono" style={{ fontSize: 12, color: C.muted }}>{ri.qty} {ri.unit}</span>
              {pickedLot && <button onClick={() => onNavigate({ view: "lot", lotId: pickedLot.lotId })} className="f-mono tx" style={{ fontSize: 10, color: C.teal, letterSpacing: "0.1em", padding: "1px 6px", background: C.tealWash, border: "none", borderRadius: 2, cursor: "pointer" }}>da {pickedLot.lotId} →</button>}
              <span className="f-body" style={{ fontSize: 11, color: C.muted, fontStyle: "italic", marginLeft: "auto" }}>{ri.reason}</span>
            </div>;
          })}
        </div>
      </div>
      {isPending && <div style={{ display: "flex", gap: 8, flexDirection: "column" }}>
        <Btn size="sm" variant="accent" icon={Package} onClick={onPick}>Preleva</Btn>
        <Btn size="sm" variant="ghost" onClick={onCancel}>Annulla</Btn>
      </div>}
    </div>
  </div>;
}

function KitchenRequestForm({ state, setState, onClose }) {
  const [requestedBy, setRequestedBy] = useState(state.users.filter(u => u.role === "Cucina")[0]?.name || state.currentUser.name);
  const [urgency, setUrgency] = useState("normale");
  const [items, setItems] = useState([{ itemId: (engine.getItems(state)[0]?.id || ""), qty: "", unit: "kg", reason: "" }]);
  const toast = useToast();

  const submit = () => {
    if (items.some(i => !i.qty || !i.reason.trim())) { toast({ type: "error", message: "Compila quantità e motivo per ogni riga" }); return; }
    const kr = {
      id: uid("kr_"), createdAt: nowIso(), requestedBy, urgency, status: "in_attesa",
      items: items.map(i => ({ itemId: i.itemId, qty: parseFloat(i.qty), unit: i.unit, reason: i.reason.trim() })),
    };
    setState({ ...state, kitchenRequests: [...state.kitchenRequests, kr] });
    toast({ type: "success", title: "Richiesta inviata", message: `${items.length} articoli richiesti al magazzino` });
    onClose();
  };

  return <Modal onClose={onClose} maxWidth={680} subtitle="Cucina → Magazzino" title="Nuova richiesta di materiale"
    footer={<><Btn variant="ghost" onClick={onClose}>Annulla</Btn><Btn variant="terra" icon={Send} onClick={submit}>Invia richiesta</Btn></>}>
    <div style={{ padding: 24 }}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14, marginBottom: 16 }}>
        <Field label="Richiesto da" required>
          <Select value={requestedBy} onChange={e => setRequestedBy(e.target.value)}>
            {state.users.filter(u => u.role === "Cucina" || u.role === "Laboratorio").map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
          </Select>
        </Field>
        <Field label="Urgenza">
          <Select value={urgency} onChange={e => setUrgency(e.target.value)}>
            <option value="normale">Normale</option><option value="urgente">Urgente</option>
          </Select>
        </Field>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: C.muted }}>ARTICOLI RICHIESTI</div>
        <Btn variant="ghost" size="xs" icon={Plus} onClick={() => setItems([...items, { itemId: (engine.getItems(state)[0]?.id || ""), qty: "", unit: "kg", reason: "" }])}>Articolo</Btn>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((ri, i) => {
          const item = engine.getItems(state).find(it => it.id === ri.itemId);
          const stock = item ? engine.itemStock(state, item.id) : 0;
          const insufficient = ri.qty && parseFloat(ri.qty) > stock;
          return <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 0.7fr 0.5fr 2fr auto", gap: 10, alignItems: "end", padding: insufficient ? 10 : 0, background: insufficient ? C.terraWash : "transparent", borderRadius: 2 }}>
            <Field label={i === 0 ? "Articolo" : ""}>
              <Select value={ri.itemId} onChange={e => { const it = engine.getItems(state).find(x => x.id === e.target.value); setItems(items.map((l, j) => j === i ? { ...l, itemId: e.target.value, unit: it?.unit || "kg" } : l)); }}>
                {(() => {
                  const all = engine.getItems(state);
                  const groups = {};
                  all.forEach(it => { (groups[it.category] = groups[it.category] || []).push(it); });
                  return Object.entries(groups).map(([cat, items]) => <optgroup key={cat} label={(cats[cat]?.label || cat).toUpperCase()}>
                    {items.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
                  </optgroup>);
                })()}
              </Select>
              <div className="f-mono" style={{ fontSize: 9, color: insufficient ? C.terra : C.muted, marginTop: 3, letterSpacing: "0.1em" }}>DISP: {stock.toFixed(1)} {item?.unit}{insufficient && " ⚠ INSUFFICIENTE"}</div>
            </Field>
            <Field label={i === 0 ? "Quantità" : ""}><Input type="number" step="0.01" value={ri.qty} onChange={e => setItems(items.map((l, j) => j === i ? { ...l, qty: e.target.value } : l))} /></Field>
            <Field label={i === 0 ? "Un." : ""}>
              <Select value={ri.unit} onChange={e => setItems(items.map((l, j) => j === i ? { ...l, unit: e.target.value } : l))}><option>kg</option><option>pz</option><option>l</option></Select>
            </Field>
            <Field label={i === 0 ? "Motivo / uso" : ""}><Input value={ri.reason} onChange={e => setItems(items.map((l, j) => j === i ? { ...l, reason: e.target.value } : l))} placeholder="es. Servizio cena focaccia" /></Field>
            <IconBtn icon={Trash2} onClick={() => items.length > 1 && setItems(items.filter((_, j) => j !== i))} />
          </div>;
        })}
      </div>
    </div>
  </Modal>;
}

function KitchenPickForm({ request, state, setState, onClose }) {
  // Reg. CE 178/2002 art. 18 — tracciabilita' completa "one-step-back".
  // FIX P0-01 (audit 2026-05-19): in precedenza si conservava solo allocation[0]
  // anche se FIFO ne ritornava N: se il primo lotto non bastava i kg restanti
  // venivano fisicamente prelevati ma NON scaricati a sistema → inventario falsato
  // e tracciabilita' interrotta. Ora mantieniamo l'INTERO array `allocation` come
  // gia' fa ScaricoForm (linea ~10054).
  const [allocs, setAllocs] = useState(() => request.items.map(ri => {
    const { allocation, shortfall } = engine.suggestFifoAllocation(state, ri.itemId, ri.qty);
    return { itemId: ri.itemId, qty: ri.qty, unit: ri.unit, allocation, shortfall };
  }));
  // FIX P0-02 (audit 2026-05-19): idempotenza del bottone "Conferma prelievo".
  // Doppio click in <500ms ⇒ doppio scarico ⇒ inventario fasullo. Reg. CE 852/2004 art. 5.
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  // Ricalcola l'allocazione FIFO quando l'utente cambia la quantita' richiesta o
  // sovrascrive il lotto primario. Permette di evadere richieste multi-lotto.
  const reallocate = (idx, patch) => {
    setAllocs(prev => prev.map((x, j) => {
      if (j !== idx) return x;
      const next = { ...x, ...patch };
      // Cap qty al richiesto originale (no inflate accidentale)
      const targetQty = Math.max(0, Number(next.qty) || 0);
      const { allocation, shortfall } = engine.suggestFifoAllocation(state, next.itemId, targetQty);
      // Se l'utente ha forzato un lotId primario, lo portiamo in testa preservando il resto
      if (patch.primaryLotId) {
        const idxPrimary = allocation.findIndex(al => al.lotId === patch.primaryLotId);
        if (idxPrimary > 0) {
          const reordered = [allocation[idxPrimary], ...allocation.filter((_, k) => k !== idxPrimary)];
          return { ...next, allocation: reordered, shortfall };
        }
      }
      return { ...next, allocation, shortfall };
    }));
  };

  const confirm = () => {
    if (submitting) return;
    // Se uno qualsiasi degli items non ha lotti allocati o ha shortfall residuo > soglia
    // dobbiamo bloccare: scaricare meno di quanto richiesto romperebbe la tracciabilita'.
    if (allocs.some(a => !a.allocation || a.allocation.length === 0)) {
      toast({ type: "error", message: "Alcuni articoli non hanno lotto allocato" });
      return;
    }
    if (allocs.some(a => a.shortfall > 0.001)) {
      toast({ type: "error", title: "Shortfall residuo", message: "Riduci le quantita' o aggiungi lotti: non posso scaricare meno del richiesto" });
      return;
    }
    setSubmitting(true);
    // ── FIX BLOCKER 4 (audit 2026-06-07): prelievo ATOMICO — o tutto o niente ──
    // Reg. CE 178/2002 art. 18: PRIMA, su INSUFFICIENT_STOCK a meta' lista, i
    // movimenti dei lotti gia' processati erano in `nextState` e venivano PERSISTITI
    // con setState(nextState) — ma la richiesta NON veniva chiusa ne' i pickedLots
    // registrati ⇒ kg scaricati senza documento = ammanco invisibile. ORA: i
    // movimenti vengono committati SOLO se l'intera lista va a buon fine; in caso di
    // errore audit sullo STATE ORIGINALE (zero movimenti) e abort pulito.
    let nextState = { ...state };
    const pickedLots = [];
    // Cap difensivo: il totale scaricato per ogni item NON deve superare a.qty.
    try {
      for (const a of allocs) {
        let remainingForItem = a.qty;
        for (const al of a.allocation) {
          if (remainingForItem <= 0.001) break;
          const takeQty = Math.min(al.qty, remainingForItem);
          const result = engine.applyMovement(nextState, { lotId: al.lotId, qty: -takeQty, type: "prelievo_cucina", ref: { type: "kitchenRequest", id: request.id }, by: state.currentUser.name });
          if (result.error === "INSUFFICIENT_STOCK") {
            const item = engine.getItems(state).find(it => it.id === a.itemId);
            toast({ type: "error", title: "Merce insufficiente — niente prelevato", message: `${item?.name || a.itemId}: mancano ${result.shortfall.toFixed(2)} ${a.unit}. Nessun movimento applicato (operazione annullata).` });
            // Audit sullo STATE ORIGINALE: i movimenti parziali in nextState sono SCARTATI.
            setState({ ...state, auditLog: appendAudit(state, { action: "prelievo_abort_insufficiente", entityType: "kitchenRequest", entityId: request.id, reason: `Prelievo richiesta ${request.id} annullato (atomico): lotto ${al.lotId} shortfall ${result.shortfall.toFixed(3)} — 0 movimenti applicati`, payload: { itemId: a.itemId, lotId: al.lotId, shortfall: result.shortfall } }) });
            return;
          }
          if (result.error) {
            toast({ type: "error", title: "Errore — niente prelevato", message: `Lotto ${al.lotId}: ${result.error}. Operazione annullata.` });
            setState({ ...state, auditLog: appendAudit(state, { action: "prelievo_abort_errore", entityType: "kitchenRequest", entityId: request.id, reason: `Prelievo richiesta ${request.id} annullato (atomico): lotto ${al.lotId} errore ${result.error} — 0 movimenti applicati`, payload: { itemId: a.itemId, lotId: al.lotId, error: result.error } }) });
            return;
          }
          pickedLots.push({ itemId: a.itemId, lotId: al.lotId, qty: takeQty });
          nextState = result;
          remainingForItem -= takeQty;
        }
      }
      // Tutti i movimenti riusciti: commit atomico (movimenti + chiusura richiesta).
      nextState.kitchenRequests = nextState.kitchenRequests.map(kr => kr.id === request.id ? { ...kr, status: "completata", completedAt: nowIso(), pickedBy: state.currentUser.name, pickedLots } : kr);
      setState(nextState);
      toast({ type: "success", title: "Prelievo completato", message: `${allocs.length} articoli · ${pickedLots.length} movimenti di lotto` });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const anyShortfall = allocs.some(a => a.shortfall > 0.001);
  const anyEmpty = allocs.some(a => !a.allocation || a.allocation.length === 0);

  return <Modal onClose={onClose} maxWidth={720} subtitle="Magazzino · prelievo" title="Evadi richiesta cucina"
    footer={<><Btn variant="ghost" onClick={onClose} disabled={submitting}>Annulla</Btn><Btn variant="olive" icon={CheckCircle2} onClick={confirm} disabled={submitting || anyShortfall || anyEmpty}>{submitting ? "Registrazione in corso..." : "Conferma prelievo"}</Btn></>}>
    <div style={{ padding: 24 }}>
      <div style={{ padding: 12, background: C.terraWash, borderRadius: 2, marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
        <ChefHat size={16} color={C.terra} />
        <div className="f-body" style={{ fontSize: 12, color: C.terraDeep }}>Richiesta di <strong>{request.requestedBy}</strong> · {request.urgency === "urgente" && <span style={{ fontWeight: 600 }}>URGENTE · </span>}il sistema suggerisce i lotti in <strong>FIFO</strong> (scadenza prima). Multi-lotto: se un lotto non basta, vengono presi anche i successivi in ordine FIFO.</div>
      </div>

      <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: C.muted, marginBottom: 10 }}>ALLOCAZIONI</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {allocs.map((a, i) => {
          const item = engine.getItems(state).find(it => it.id === a.itemId);
          const availLots = engine.fifoLotsForItem(state, a.itemId);
          const primaryLotId = a.allocation?.[0]?.lotId || "";
          return <div key={i} style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 14, borderRadius: 2 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 8, height: 8, background: cats[item?.category]?.color, borderRadius: 8 }} />
                <span className="f-body" style={{ fontSize: 14, color: C.ink, fontWeight: 500 }}>{item?.name}</span>
                <span className="f-mono" style={{ fontSize: 12, color: C.muted }}>{a.qty} {a.unit}</span>
                <span className="f-body" style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>— {request.items[i].reason}</span>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
              <Field label="Lotto primario FIFO (gli altri seguono automaticamente)">
                <Select value={primaryLotId} onChange={e => reallocate(i, { primaryLotId: e.target.value })}>
                  {availLots.map(l => {
                    const daysLeft = daysBetween(TODAY, l.expiresAt);
                    return <option key={l.id} value={l.id}>{l.id} · {l.qtyRemaining.toFixed(1)} {l.unit} · scade {fmt.date(l.expiresAt)} ({daysLeft}g)</option>;
                  })}
                </Select>
              </Field>
              <Field label="Quantità effettiva">
                <Input type="number" step="0.01" value={a.qty} onChange={e => reallocate(i, { qty: parseFloat(e.target.value) || 0 })} />
              </Field>
            </div>
            {/* Lista completa lotti allocati (multi-lotto FIFO). Reg. CE 178/2002 art. 18. */}
            {a.allocation && a.allocation.length > 0 && <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em", alignSelf: "center" }}>SCARICO:</span>
              {a.allocation.map((al, j) => <span key={j} className="f-mono" style={{ fontSize: 10, padding: "3px 8px", background: C.tealWash, color: C.tealDeep, letterSpacing: "0.06em", borderRadius: 2, fontWeight: 600 }}>{al.lotId}: -{al.qty.toFixed(3)} {a.unit}</span>)}
            </div>}
            {a.shortfall > 0 && <div style={{ marginTop: 8, padding: 8, background: C.terraWash, borderRadius: 2 }}>
              <div className="f-mono" style={{ fontSize: 10, color: C.terra, letterSpacing: "0.1em" }}>⚠ SHORTFALL: {a.shortfall.toFixed(2)} {a.unit} non disponibili — riduci la quantita' o ricevi altra merce prima di scaricare</div>
            </div>}
          </div>;
        })}
      </div>
    </div>
  </Modal>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      PIATTI & RICETTE                                      ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function Piatti({ state, setState, onNavigate }) {
  const [expandedDish, setExpandedDish] = useState(null);
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("cat");
  const [mappingIng, setMappingIng] = useState(null);  // { dishId, ingIdx, ing }
  const [editingDish, setEditingDish] = useState(null);
  const [newDish, setNewDish] = useState(false);
  const [priceEdit, setPriceEdit] = useState(null); // { dishId, value }
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);  // id piatto in conferma elimina
  const [recentlyEditedId, setRecentlyEditedId] = useState(null);  // id piatto appena modificato (per scroll auto)
  const dishRefs = useRef({});  // mappa id -> elemento DOM card piatto
  const toast2 = useToast();

  // Scroll automatico al piatto appena modificato (con highlight visivo)
  useEffect(() => {
    if (!recentlyEditedId) return;
    // Aspetto che React completi il render del nuovo state, poi cerco l'elemento e scrollo
    // Uso requestAnimationFrame per essere sicuro che il DOM sia aggiornato
    let raf = requestAnimationFrame(() => {
      const el = dishRefs.current[recentlyEditedId];
      if (el) {
        // block: "center" → centra in viewport (non in cima!)
        // behavior: "auto" → istantaneo, evita effetto "salita prima poi discesa" dello smooth
        el.scrollIntoView({ behavior: "auto", block: "center" });
      }
    });
    // Tolgo l'highlight dopo 2 secondi
    const t = setTimeout(() => setRecentlyEditedId(null), 2000);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [recentlyEditedId]);

  // Wrapper per chiusura modal modifica con scroll-back
  const handleEditClose = (modifiedId) => {
    setEditingDish(null);
    if (modifiedId) setRecentlyEditedId(modifiedId);
  };

  const savePriceEdit = () => {
    if (!priceEdit) return;
    const newPrice = parseFloat(priceEdit.value);
    if (isNaN(newPrice) || newPrice < 0) { toast2({ type: "error", message: "Prezzo non valido" }); return; }
    const editedId = priceEdit.dishId;
    setState({ ...state, dishesReal: state.dishesReal.map(d => d.id === editedId ? { ...d, prezzo: newPrice } : d) });
    toast2({ type: "success", message: `Prezzo aggiornato: ${fmt.eur(newPrice)}` });
    setPriceEdit(null);
    // Trigger scroll automatico al piatto modificato
    setRecentlyEditedId(editedId);
  };

  const deleteDish = (id) => {
    const dish = state.dishesReal?.find(d => d.id === id);
    if (!dish) return;
    setState({ ...state, dishesReal: state.dishesReal.filter(d => d.id !== id) });
    toast2({ type: "info", title: "Piatto eliminato", message: dish.nome });
    setConfirmDeleteId(null);
    // Se era il piatto espanso, chiudi
    if (expandedDish === id) setExpandedDish(null);
  };

  const cats2 = Object.fromEntries(Object.entries(DISH_CATEGORIES).map(([k, v]) => [k, v.label]));

  const dishes = state.dishesReal || [];

  // Calcolo food cost reale per ogni piatto usando i baseProducts (prezzo reale)
  const withCosts = useMemo(() => dishes.map(d => {
    let costoReale = 0;
    let mappedCount = 0;
    const ingsDetail = d.ingredienti.map(ing => {
      const bp = ing.bpId ? state.baseProducts?.find(b => b.id === ing.bpId) : null;
      if (bp) {
        const cost = (bp.pReale || bp.pFatt || 0) * ing.qty;
        costoReale += cost;
        mappedCount++;
        return { ...ing, bp, cost };
      }
      // Fallback: costoRiga è il costo originale nel foglio, lo ripartiamo proporzionalmente
      return { ...ing, bp: null, cost: ing.prezzo || 0 };
    });
    // Se abbiamo mappato qualcosa, usiamo costo calcolato; altrimenti il costo originale del foglio
    const finalCost = mappedCount > 0 ? costoReale + d.ingredienti.filter(i => !i.bpId).reduce((s, i) => s + (i.prezzo || 0), 0) : (d.costoRiga || 0);
    const coverage = d.ingredienti.length > 0 ? (mappedCount / d.ingredienti.length) : 0;
    const costInfo = engine.computeDishCostWithVat(state, d);
    return { ...d, costoReale: finalCost, costoOriginale: d.costoRiga || 0, ingsDetail, coverage, avgVatPurchase: costInfo.avgVatPurchase, hasVatOverrides: costInfo.hasOverrides };
  }), [dishes, state.baseProducts, state.settings]);

  const filtered = withCosts.filter(d => {
    if (catFilter !== "all" && d.cat !== catFilter) return false;
    if (search && !d.nome.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    if (sortBy === "cat") {
      // Prima per ordine di categoria (antipasti -> primi -> secondi -> contorni -> dolci -> ...)
      const oa = DISH_CATEGORIES[a.cat]?.order ?? 99;
      const ob = DISH_CATEGORIES[b.cat]?.order ?? 99;
      if (oa !== ob) return oa - ob;
      // Stessa categoria: alfabetico case-insensitive con locale italiano
      return a.nome.localeCompare(b.nome, "it", { sensitivity: "base" });
    }
    if (sortBy === "fc") return (b.costoReale / b.prezzo) - (a.costoReale / a.prezzo);
    if (sortBy === "margin") return (b.prezzo - b.costoReale) - (a.prezzo - a.costoReale);
    if (sortBy === "name") return a.nome.localeCompare(b.nome, "it", { sensitivity: "base" });
    return 0;
  });

  // Raggruppa per categoria quando sortBy = "cat", altrimenti lista piatta
  const grouped = useMemo(() => {
    if (sortBy !== "cat") return null;
    const g = {};
    filtered.forEach(d => {
      if (!g[d.cat]) g[d.cat] = [];
      g[d.cat].push(d);
    });
    // Restituisco array ordinato di {cat, items}
    return Object.keys(g)
      .sort((a, b) => (DISH_CATEGORIES[a]?.order ?? 99) - (DISH_CATEGORIES[b]?.order ?? 99))
      .map(cat => ({ cat, items: g[cat] }));
  }, [filtered, sortBy]);

  // Summary stats (usa dishEconomics con IVA override coerenti)
  const summaryStats = useMemo(() => {
    if (withCosts.length === 0) return { avgFC: 0, highFC: 0, totMargine: 0 };
    let totFc = 0, highFC = 0, totM = 0;
    withCosts.forEach(d => {
      const ec = engine.dishEconomics(state, d.prezzo, d.costoReale, d.vatRate, d.avgVatPurchase);
      totFc += ec.fcPct;
      if (ec.fcPct > 35) highFC++;
      totM += ec.margin;
    });
    return { avgFC: totFc / withCosts.length, highFC, totMargine: totM };
  }, [withCosts, state.settings]);
  const { avgFC, highFC, totMargine } = summaryStats;
  const isGrossMode = engine.priceGross(state);

  const [listinoImportOpen, setListinoImportOpen] = useState(false);

  return <div className="fade-up">
    <PageHeader kicker={`${dishes.length} piatti · ${state.baseProducts?.length || 0} prodotti base · modalità: ${isGrossMode ? "LORDO (IVA incl)" : "NETTO (IVA escl)"}`} title="Piatti & ricette"
      subtitle={isGrossMode
        ? `Prezzi e costi IVA inclusa come vedi il cliente (IVA vendita ${((state.settings?.vatRateSale || 0.10) * 100).toFixed(0)}%). Usa il selettore in alto per passare al netto.`
        : `Prezzi e costi al netto IVA — visione imprenditoriale, quanto incassi davvero. Usa il selettore in alto per tornare al lordo cliente.`}
      right={<div style={{ display: "flex", gap: 8 }}>
        <Btn variant="ghost" size="sm" icon={Upload} onClick={() => setListinoImportOpen(true)} title="Importa listino dal gestionale di cassa">Importa listino</Btn>
        <Btn variant="accent" size="lg" icon={Plus} onClick={() => setNewDish(true)}>Nuovo piatto</Btn>
      </div>} />

    {listinoImportOpen && <ListinoImporter state={state} setState={setState} onClose={() => setListinoImportOpen(false)} />}
    {mappingIng && <IngredientMappingModal mappingIng={mappingIng} state={state} setState={setState} onClose={() => setMappingIng(null)} />}
    {editingDish && <DishEditorModal dish={editingDish} state={state} setState={setState} onClose={(id) => handleEditClose(id)} />}
    {newDish && <DishEditorModal dish={null} state={state} setState={setState} onClose={() => setNewDish(false)} />}

    <div className="stagger" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
      <StatCard kicker="Piatti a carta" value={dishes.length} unit="ricette" trend="importati dal tuo file" accent={C.ink} icon={Utensils} />
      <StatCard kicker="Food cost medio" value={avgFC.toFixed(0) + "%"} unit="rispetto al prezzo" trend="target < 30%" accent={avgFC > 35 ? C.terra : (avgFC > 28 ? C.amber : C.olive)} icon={Activity} />
      <StatCard kicker="Piatti FC alto" value={highFC} unit=">35%" trend="da rivedere" accent={highFC > 0 ? C.terra : C.olive} icon={AlertTriangle} onClick={() => setSortBy("fc")} />
      <StatCard kicker="Margine totale" value={fmt.eurShort(totMargine)} unit="per coperto" trend={`se tutti i piatti venduti 1x`} accent={C.teal} icon={TrendingUp} />
    </div>

    <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 240, display: "flex", alignItems: "center", gap: 10, background: C.white, border: `1px solid ${C.rule}`, padding: "8px 14px", borderRadius: 2 }}>
        <Search size={15} color={C.muted} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca piatto..." className="f-body" style={{ border: "none", outline: "none", flex: 1, fontSize: 13, background: "transparent", color: C.ink }} />
      </div>
      <Select value={catFilter} onChange={e => setCatFilter(e.target.value)}>
        <option value="all">Tutte categorie</option>
        {Object.entries(cats2).map(([k, v]) => <option key={k} value={k}>{v} ({withCosts.filter(d => d.cat === k).length})</option>)}
      </Select>
      <Select value={sortBy} onChange={e => setSortBy(e.target.value)}>
        <option value="cat">Ordine: categoria</option>
        <option value="fc">Ordine: F.C. % maggiore</option>
        <option value="margin">Ordine: margine maggiore</option>
        <option value="name">Ordine: nome</option>
      </Select>
    </div>

    {/* Funzione locale per rendere una singola card piatto */}
    {(() => {
      const renderDishCard = (d) => {
        const realNet = d.costoReale;
        const econ = engine.dishEconomics(state, d.prezzo, realNet, d.vatRate, d.avgVatPurchase);
        const price = econ.price;
        const real = econ.cost;
        const margin = econ.margin;
        const fcPct = econ.fcPct;
        const isGross = engine.priceGross(state);
        const isExp = expandedDish === d.id;
        const defaultVatSale = state.settings?.vatRateSale || 0.10;
        const hasVatOverride = d.vatRate != null && Math.abs(d.vatRate - defaultVatSale) > 0.0001;
        const hasDualPrice = d.prezzoPranzo != null && d.prezzoPranzo > 0 && d.prezzoPranzo !== d.prezzo;
        const isRecentlyEdited = recentlyEditedId === d.id;
        const isConfirmingDelete = confirmDeleteId === d.id;
        // FIX P0-04 (audit 2026-05-19) — Reg. UE 1169/2011 art. 9: aggregazione
        // allergeni del piatto come UNION degli allergens dei baseProducts mappati.
        // Visualizzazione: badge prominente PRIMA del nome piatto + lista in vista
        // espansa. Avviso "Verifica mapping ingredienti" se coverage < 100%.
        const allergeniSet = engine.dishAllergens(state, d);
        const allergeni = Array.from(allergeniSet).sort();
        return <div key={d.id}
          ref={(el) => { if (el) dishRefs.current[d.id] = el; else delete dishRefs.current[d.id]; }}
          style={{
            background: isRecentlyEdited ? C.tealWash : C.white,
            border: `${isRecentlyEdited ? 2 : 1}px solid ${isRecentlyEdited ? C.teal : (isConfirmingDelete ? C.terra : C.rule)}`,
            borderRadius: 2, overflow: "hidden", position: "relative",
            transition: "background 0.4s, border-color 0.4s",
          }}>
          {/* Quick actions in alto a destra: Modifica + Elimina sempre visibili */}
          <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 4, zIndex: 2 }}>
            <button onClick={(e) => {
                e.stopPropagation();
                // Passa il dish PULITO (dal state originale) non quello arricchito di costoReale/coverage/ingsDetail
                const pure = (state.dishesReal || []).find(x => x.id === d.id);
                setEditingDish(pure || d);
              }} className="tx" title="Modifica piatto"
              style={{ width: 30, height: 30, background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
              onMouseEnter={e => { e.currentTarget.style.background = C.tealWash; e.currentTarget.style.borderColor = C.teal; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.borderColor = C.rule; }}>
              <Settings size={13} color={C.inkSoft} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(d.id); }} className="tx" title="Elimina piatto"
              style={{ width: 30, height: 30, background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
              onMouseEnter={e => { e.currentTarget.style.background = C.terraWash; e.currentTarget.style.borderColor = C.terra; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.borderColor = C.rule; }}>
              <Trash2 size={13} color={C.terra} />
            </button>
          </div>

          {/* Conferma elimina inline (sopra alla card) */}
          {isConfirmingDelete && <div className="fade-in" style={{ padding: "12px 14px", background: C.terraWash, borderBottom: `2px solid ${C.terra}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div className="f-body" style={{ fontSize: 12, color: C.terra, fontWeight: 600 }}>Eliminare "{d.nome}"?</div>
            <div style={{ display: "flex", gap: 6 }}>
              <Btn size="xs" variant="ghost" onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}>Annulla</Btn>
              <Btn size="xs" variant="terra" icon={Trash2} onClick={(e) => { e.stopPropagation(); deleteDish(d.id); }}>Sì, elimina</Btn>
            </div>
          </div>}

          <button onClick={() => setExpandedDish(isExp ? null : d.id)} className="tx" style={{ width: "100%", padding: "18px 80px 18px 18px", background: "transparent", border: "none", textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.25em", color: C.muted, textTransform: "uppercase" }}>{cats2[d.cat] || d.cat}</span>
              {d.coverage < 0.5 && <span className="f-mono" style={{ fontSize: 9, color: C.amber, background: C.amberWash, padding: "1px 5px", borderRadius: 2, letterSpacing: "0.1em" }}>MAPPING PARZIALE</span>}
              {hasVatOverride && <span className="f-mono" style={{ fontSize: 9, color: C.teal, background: C.tealWash, padding: "1px 5px", borderRadius: 2, letterSpacing: "0.1em", fontWeight: 600 }}>IVA {(d.vatRate * 100).toFixed(0)}%</span>}
              {hasDualPrice && <span className="f-mono" style={{ fontSize: 9, color: C.amberDeep, background: C.amberWash, padding: "1px 5px", borderRadius: 2, letterSpacing: "0.1em", fontWeight: 600 }}>☀ PRANZO {fmt.eur(d.prezzoPranzo)}</span>}
            </div>
            <div className="f-display" style={{ fontSize: 18, color: C.ink, fontWeight: 500, marginBottom: 8, letterSpacing: "-0.01em" }}>{d.nome}</div>
            {/* FIX P0-04 — Reg. UE 1169/2011 art. 9: allergeni dichiarati al cliente */}
            {allergeni.length > 0 && <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
              <span className="f-mono" style={{ fontSize: 9, color: C.terra, letterSpacing: "0.2em", fontWeight: 600 }}>ALLERGENI:</span>
              {allergeni.map(a => <span key={a} className="f-mono" style={{ fontSize: 9, padding: "2px 7px", background: C.terraWash, color: C.terra, letterSpacing: "0.12em", borderRadius: 2, fontWeight: 700, textTransform: "uppercase" }}>{a.replace(/_/g, " ")}</span>)}
              {d.coverage < 1 && <span className="f-mono" style={{ fontSize: 9, color: C.amber, fontStyle: "italic" }} title="Coverage ingredienti < 100% — l'elenco allergeni potrebbe essere incompleto. Mappa tutti gli ingredienti in Anagrafiche.">⚠ verifica mapping</span>}
            </div>}
            {allergeni.length === 0 && d.coverage >= 0.5 && <div style={{ marginBottom: 8 }}>
              <span className="f-mono" style={{ fontSize: 9, color: C.olive, letterSpacing: "0.2em" }}>NESSUN ALLERGENE TRA I 14 DICHIARATI</span>
            </div>}
            <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 10 }}>
              <div style={{ flex: 1, display: "flex", gap: 14 }}>
                <div onClick={(e) => { e.stopPropagation(); setPriceEdit({ dishId: d.id, value: d.prezzo.toString() }); }} className="tx" style={{ cursor: "pointer", padding: "2px 6px", marginLeft: -6, borderRadius: 2 }}
                  onMouseEnter={e => e.currentTarget.style.background = C.paperDeep}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em", display: "flex", alignItems: "center", gap: 4 }}>
                    LISTINO <span style={{ fontSize: 8, color: isGross ? C.muted : C.teal }}>{isGross ? "IVA IN" : "NETTO"}</span> <Settings size={9} style={{ opacity: 0.6 }} />
                  </div>
                  {priceEdit?.dishId === d.id ? <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 4, alignItems: "center", marginTop: 2 }}>
                    <span className="f-mono" style={{ fontSize: 14, color: C.muted }}>€</span>
                    <input type="number" step="0.50" value={priceEdit.value} onChange={e => setPriceEdit({ ...priceEdit, value: e.target.value })}
                      onKeyDown={e => { if (e.key === "Enter") savePriceEdit(); if (e.key === "Escape") setPriceEdit(null); }}
                      onClick={e => e.stopPropagation()} autoFocus className="f-mono" style={{ width: 60, padding: "2px 4px", border: `1px solid ${C.teal}`, borderRadius: 2, fontSize: 13, fontWeight: 600, color: C.ink }} />
                    <IconBtn icon={CheckCircle2} size={12} color={C.olive} onClick={savePriceEdit} title="Salva (↵)" />
                    <IconBtn icon={X} size={12} onClick={() => setPriceEdit(null)} title="Annulla (esc)" />
                  </div> : <div className="f-mono" style={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>{fmt.eur(price)}</div>}
                </div>
                <div>
                  <div className="f-mono" style={{ fontSize: 9, color: C.teal, letterSpacing: "0.2em" }}>COSTO {isGross ? "LORDO" : "NETTO"}</div>
                  <div className="f-mono" style={{ fontSize: 14, color: C.teal, fontWeight: 600 }}>{fmt.eur(real)}</div>
                </div>
                <div>
                  <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>MARGINE</div>
                  <div className="f-mono" style={{ fontSize: 14, color: margin > 0 ? C.olive : C.terra, fontWeight: 600 }}>{fmt.eur(margin)}</div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>F.C.</div>
                <div className="f-mono" style={{ fontSize: 18, color: fcPct > 35 ? C.terra : (fcPct > 28 ? C.amber : C.olive), fontWeight: 600 }}>{fcPct.toFixed(0)}%</div>
              </div>
            </div>
          </button>
          {isExp && <div className="fade-in" style={{ padding: "0 18px 18px", borderTop: `1px solid ${C.ruleSoft}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0 6px" }}>
              <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted }}>INGREDIENTI · {d.ingredienti.length} · coverage {(d.coverage * 100).toFixed(0)}%</div>
              <div style={{ display: "flex", gap: 6 }}>
                <Btn size="xs" variant="ghost" icon={Settings} onClick={(e) => {
                  e.stopPropagation();
                  const pure = (state.dishesReal || []).find(x => x.id === d.id);
                  setEditingDish(pure || d);
                }}>Modifica piatto</Btn>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {d.ingsDetail.map((ing, i) => {
                const qtyG = ing.qty * 1000;
                const mapped = !!ing.bp;
                return <button key={i} onClick={(e) => { e.stopPropagation(); setMappingIng({ dishId: d.id, ingIdx: i, ing }); }} className="tx" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, padding: "6px 8px", background: mapped ? "transparent" : C.amberWash, borderRadius: 2, border: "none", cursor: "pointer", textAlign: "left", width: "100%", fontFamily: "inherit" }}
                  onMouseEnter={e => e.currentTarget.style.background = mapped ? C.paperDeep : C.amberWash}
                  onMouseLeave={e => e.currentTarget.style.background = mapped ? "transparent" : C.amberWash}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                    <span style={{ width: 5, height: 5, background: mapped ? C.teal : C.amber, borderRadius: 6, flexShrink: 0 }} />
                    <span className="f-body" style={{ color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ing.name}</span>
                    {mapped ? <span className="f-mono" style={{ fontSize: 10, color: C.teal, letterSpacing: "0.05em" }} title={ing.bp.nome}>✓ {fmt.eur(ing.bp.pReale || ing.bp.pFatt)}/kg reale</span>
                    : <span className="f-mono" style={{ fontSize: 9, color: C.amberDeep, letterSpacing: "0.15em", padding: "1px 5px", background: C.white, borderRadius: 2 }}>⚠ NON MAPPATO — CLICCA</span>}
                  </div>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <span className="f-mono" style={{ color: C.muted, fontSize: 11 }}>{qtyG.toFixed(qtyG < 10 ? 1 : 0)} g</span>
                    <span className="f-mono" style={{ color: C.ink, fontWeight: 600, minWidth: 60, textAlign: "right" }}>{fmt.eur(ing.cost)}</span>
                  </div>
                </button>;
              })}
            </div>
            <div style={{ marginTop: 10, padding: 10, background: C.tealWash, borderRadius: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.15em", color: C.teal }}>COSTO REALE CALCOLATO</div>
                <div className="f-body" style={{ fontSize: 11, color: C.tealDeep, marginTop: 2 }}>Su prezzi reali al kg (post-cali) dei prodotti base</div>
              </div>
              <div className="f-display" style={{ fontSize: 22, color: C.tealDeep, fontWeight: 600 }}>{fmt.eur(real)}</div>
            </div>
            {d.costoOriginale > 0 && Math.abs(d.costoOriginale - real) > 0.01 && <div className="f-body" style={{ fontSize: 11, color: C.muted, textAlign: "right", marginTop: 6, fontStyle: "italic" }}>
              Riferimento tuo foglio: {fmt.eur(d.costoOriginale)} · Δ {fmt.eur(real - d.costoOriginale)}
            </div>}
          </div>}
        </div>;
      };

      // Caso 1: nessun risultato
      if (filtered.length === 0) {
        return <EmptyState icon={Utensils} title="Nessun piatto corrisponde" />;
      }

      // Caso 2: ordinamento per categoria → mostra sezioni con header netti
      if (grouped) {
        return <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {grouped.map(({ cat, items }) => {
            const catInfo = DISH_CATEGORIES[cat] || { label: cat, color: C.muted };
            return <div key={cat}>
              {/* Header categoria — separazione netta */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, paddingBottom: 10, borderBottom: `2px solid ${catInfo.color}` }}>
                <div style={{ width: 6, height: 28, background: catInfo.color, borderRadius: 1 }}></div>
                <div style={{ flex: 1 }}>
                  <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: catInfo.color, letterSpacing: "-0.02em", textTransform: "uppercase" }}>{catInfo.label}</div>
                  <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", marginTop: 2 }}>{items.length} {items.length === 1 ? "PIATTO" : "PIATTI"} · ORDINE ALFABETICO</div>
                </div>
                <div className="f-mono" style={{ fontSize: 11, color: C.muted, padding: "4px 10px", background: C.paperDeep, borderRadius: 2 }}>
                  {items.length}
                </div>
              </div>
              {/* Griglia piatti della categoria */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
                {items.map(d => renderDishCard(d))}
              </div>
            </div>;
          })}
        </div>;
      }

      // Caso 3: altri ordinamenti → lista piatta
      return <div className="stagger" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
        {filtered.map(d => renderDishCard(d))}
      </div>;
    })()}
  </div>;
}

// ── MODAL: mappa ingrediente → baseProduct ─────────────────────────────────
function IngredientMappingModal({ mappingIng, state, setState, onClose }) {
  const [search, setSearch] = useState(mappingIng.ing.name);
  const [selected, setSelected] = useState(mappingIng.ing.bpId || null);
  const toast = useToast();

  // Suggerimenti fuzzy: prodotti base che contengono termini dell'ingrediente
  const suggestions = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return state.baseProducts || [];
    const tokens = q.split(/\s+/).filter(t => t.length >= 2);
    return (state.baseProducts || []).map(bp => {
      const bpLow = bp.nome.toLowerCase();
      let score = 0;
      tokens.forEach(t => { if (bpLow.includes(t)) score += t.length; });
      // Boost category match
      if (bp.cat === "pesce" && (q.includes("pesc") || q.includes("filet") || q.includes("spina"))) score += 3;
      return { bp, score };
    }).filter(x => x.score > 0).sort((a, b) => b.score - a.score).slice(0, 20).map(x => x.bp);
  }, [search, state.baseProducts]);

  const save = () => {
    const newDishes = state.dishesReal.map(d => {
      if (d.id !== mappingIng.dishId) return d;
      return { ...d, ingredienti: d.ingredienti.map((ing, i) => i === mappingIng.ingIdx ? { ...ing, bpId: selected } : ing) };
    });
    setState({ ...state, dishesReal: newDishes });
    toast({ type: "success", message: selected ? "Ingrediente mappato" : "Mapping rimosso" });
    onClose();
  };

  return <Modal onClose={onClose} maxWidth={640} subtitle="Collega a un prodotto base" title={`"${mappingIng.ing.name}"`}
    footer={<><Btn variant="ghost" onClick={onClose}>Annulla</Btn>
      {selected && <Btn variant="danger" onClick={() => { setSelected(null); save(); }}>Rimuovi mapping</Btn>}
      <Btn variant="accent" icon={CheckCircle2} onClick={save} disabled={!selected}>Salva</Btn></>}>
    <div style={{ padding: 24 }}>
      <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2, marginBottom: 16, display: "flex", gap: 12 }}>
        <Info size={15} color={C.muted} style={{ flexShrink: 0, marginTop: 2 }} />
        <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.5 }}>
          Nel foglio Excel questo ingrediente è scritto come <strong>"{mappingIng.ing.name}"</strong>. Mappalo al prodotto base corretto tra i {state.baseProducts?.length || 0} disponibili: così il food cost userà il prezzo reale al kg (post-cali).
        </div>
      </div>

      <Field label="Cerca prodotto base" hint="Puoi digitare parte del nome">
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: C.white, border: `1px solid ${C.rule}`, padding: "8px 14px", borderRadius: 2 }}>
          <Search size={15} color={C.muted} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="es. seppia, polpo, pomodorino..." className="f-body" style={{ border: "none", outline: "none", flex: 1, fontSize: 13, background: "transparent", color: C.ink }} autoFocus />
          {search && <IconBtn icon={X} size={13} onClick={() => setSearch("")} />}
        </div>
      </Field>

      <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, marginTop: 16, marginBottom: 8 }}>
        SUGGERIMENTI ({suggestions.length})
      </div>
      <div style={{ maxHeight: 320, overflowY: "auto", border: `1px solid ${C.rule}`, borderRadius: 2 }}>
        {suggestions.length === 0 ? <div style={{ padding: 30, textAlign: "center" }}>
          <div className="f-body" style={{ fontSize: 12, color: C.muted }}>Nessun prodotto base corrisponde. Prova un termine più generico.</div>
        </div> : suggestions.map(bp => {
          const isSel = selected === bp.id;
          return <button key={bp.id} onClick={() => setSelected(bp.id)} className="tx" style={{ width: "100%", padding: "10px 14px", background: isSel ? C.tealWash : "transparent", border: "none", borderBottom: `1px solid ${C.ruleSoft}`, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 12, fontFamily: "inherit" }}
            onMouseEnter={e => !isSel && (e.currentTarget.style.background = C.paperDeep)}
            onMouseLeave={e => !isSel && (e.currentTarget.style.background = "transparent")}>
            <div style={{ width: 20, height: 20, borderRadius: 10, border: `2px solid ${isSel ? C.teal : C.rule}`, background: isSel ? C.teal : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {isSel && <CheckCircle2 size={12} color={C.white} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{bp.nome}</div>
              <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 2, display: "flex", gap: 10 }}>
                <span style={{ textTransform: "uppercase", letterSpacing: "0.1em" }}>{bp.cat}</span>
                {bp.caloTot > 0 && <span>calo {(bp.caloTot * 100).toFixed(0)}%</span>}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="f-mono" style={{ fontSize: 13, color: C.teal, fontWeight: 600 }}>{fmt.eur(bp.pReale || bp.pFatt)}/kg</div>
              {bp.pReale > bp.pFatt && <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>fatt. {fmt.eur(bp.pFatt)}</div>}
            </div>
          </button>;
        })}
      </div>
    </div>
  </Modal>;
}

// ── MODAL: crea/modifica piatto ────────────────────────────────────────────
function DishEditorModal({ dish, state, setState, onClose }) {
  const isNew = !dish;
  const [d, setD] = useState(dish || { id: uid("d_"), nome: "", cat: "antipasti", prezzo: 0, ingredienti: [], vatRate: null });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const toast = useToast();

  // Costo netto + IVA media pesata dai baseProducts
  const costInfo = useMemo(() => engine.computeDishCostWithVat(state, d), [d, state.baseProducts]);
  const totalCost = costInfo.netCost;
  const avgVatPurchase = costInfo.avgVatPurchase;

  // IVA del piatto: override se presente, altrimenti default
  const defaultVatSale = state.settings?.vatRateSale || 0.10;
  const effectiveVatSale = d.vatRate != null ? d.vatRate : defaultVatSale;

  const econ = engine.dishEconomics(state, d.prezzo, totalCost, effectiveVatSale, avgVatPurchase);
  const fcPct = econ.fcPct;
  const margin = econ.margin;
  const isGrossMode = engine.priceGross(state);
  const vatRate = effectiveVatSale;

  const updateIng = (i, changes) => setD({ ...d, ingredienti: d.ingredienti.map((ing, j) => j === i ? { ...ing, ...changes } : ing) });
  const addIng = () => setD({ ...d, ingredienti: [...d.ingredienti, { name: "", qty: 0, unit: "kg", bpId: null }] });
  const delIng = (i) => setD({ ...d, ingredienti: d.ingredienti.filter((_, j) => j !== i) });

  const save = () => {
    if (!d.nome.trim()) { toast({ type: "error", message: "Inserisci il nome del piatto" }); return; }
    if (d.ingredienti.length === 0) { toast({ type: "error", message: "Aggiungi almeno un ingrediente" }); return; }
    const cleanDish = { ...d, nome: d.nome.trim(), costoRiga: Math.round(totalCost * 10000) / 10000 };
    const existing = (state.dishesReal || []).find(x => x.id === d.id);
    const newDishes = existing
      ? state.dishesReal.map(x => x.id === d.id ? cleanDish : x)
      : [...(state.dishesReal || []), cleanDish];
    setState({ ...state, dishesReal: newDishes });
    toast({ type: "success", title: isNew ? "Piatto creato" : "Piatto aggiornato", message: `${d.nome} · costo ${fmt.eur(totalCost)} · F.C. ${fcPct.toFixed(0)}%` });
    onClose(d.id);  // passo l'id così la pagina può fare scroll-to del piatto
  };

  const del = () => {
    setConfirmDelete(true);
  };
  const confirmDel = () => {
    const idToDel = d.id;
    setState({ ...state, dishesReal: (state.dishesReal || []).filter(x => x.id !== idToDel) });
    toast({ type: "info", message: `Piatto "${d.nome}" eliminato` });
    setConfirmDelete(false);
    onClose();
  };

  return <Modal onClose={onClose} maxWidth={820} subtitle={isNew ? "Nuovo" : "Modifica"} title={isNew ? "Crea piatto" : d.nome}
    footer={<>
      <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
      {!isNew && <Btn variant="danger" icon={Trash2} onClick={del}>Elimina</Btn>}
      <Btn variant="accent" icon={CheckCircle2} onClick={save}>Salva</Btn>
    </>}>
    {confirmDelete && <div onClick={() => setConfirmDelete(false)} style={{ position: "fixed", inset: 0, zIndex: 80, background: "rgba(28,26,23,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.paper, maxWidth: 440, width: "100%", borderRadius: 2, padding: 24, boxShadow: "0 24px 80px rgba(0,0,0,0.35)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <div style={{ width: 40, height: 40, background: C.terraWash, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Trash2 size={18} color={C.terra} />
          </div>
          <div>
            <div className="f-mono" style={{ fontSize: 10, color: C.terra, letterSpacing: "0.2em", fontWeight: 600 }}>CONFERMA ELIMINAZIONE</div>
            <div className="f-display" style={{ fontSize: 17, color: C.ink, fontWeight: 500, marginTop: 2 }}>Eliminare "{d.nome}"?</div>
          </div>
        </div>
        <div className="f-body" style={{ fontSize: 13, color: C.inkSoft, marginBottom: 18, lineHeight: 1.5 }}>
          Il piatto verrà rimosso definitivamente dall'anagrafica. Le ricette, ingredienti e impostazioni IVA andranno persi. Gli storici di scarico già fatti rimangono in magazzino.
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setConfirmDelete(false)}>Annulla</Btn>
          <Btn variant="terra" icon={Trash2} onClick={confirmDel}>Sì, elimina</Btn>
        </div>
      </div>
    </div>}
    <div style={{ padding: 24 }}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
        <Field label="Nome piatto" required><Input value={d.nome} onChange={e => setD({ ...d, nome: e.target.value })} placeholder="es. Insalata di polpo" /></Field>
        <Field label="Categoria" required>
          <Select value={d.cat} onChange={e => setD({ ...d, cat: e.target.value })}>
            {DISH_CAT_KEYS.map(k => <option key={k} value={k}>{DISH_CATEGORIES[k].label}</option>)}
          </Select>
        </Field>
        <Field label="Prezzo cena (€)" required hint="Lordo (IVA incl.)"><Input type="number" step="0.50" value={d.prezzo} onChange={e => setD({ ...d, prezzo: parseFloat(e.target.value) || 0 })} /></Field>
        <Field label="Prezzo pranzo (€)" hint="Vuoto = uguale a cena"><Input type="number" step="0.50" placeholder="opzionale" value={d.prezzoPranzo ?? ""} onChange={e => setD({ ...d, prezzoPranzo: e.target.value === "" ? null : parseFloat(e.target.value) || 0 })} /></Field>
      </div>

      <div style={{ marginBottom: 18, padding: 12, background: d.vatRate != null ? C.tealWash : C.paperDeep, border: `1px solid ${d.vatRate != null ? C.teal : C.rule}`, borderRadius: 2 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div>
            <div className="f-mono" style={{ fontSize: 10, color: d.vatRate != null ? C.tealDeep : C.muted, letterSpacing: "0.2em", fontWeight: 600 }}>
              IVA VENDITA {d.vatRate != null ? "· OVERRIDE ATTIVO" : "· USA DEFAULT"}
            </div>
            <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
              {d.vatRate != null
                ? `Questo piatto usa IVA ${(d.vatRate * 100).toFixed(0)}% al posto del default ${(defaultVatSale * 100).toFixed(0)}% — utile per alcolici, bevande, prodotti speciali.`
                : `Usa l'aliquota default del sistema (${(defaultVatSale * 100).toFixed(0)}%). Clicca "Custom" o un preset per sovrascrivere solo su questo piatto.`}
            </div>
          </div>
        </div>
        <VatSelector value={d.vatRate} onChange={v => setD({ ...d, vatRate: v })} defaultRate={defaultVatSale} />
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: C.muted }}>INGREDIENTI</div>
        <Btn variant="ghost" size="xs" icon={Plus} onClick={addIng}>Ingrediente</Btn>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
        {d.ingredienti.length === 0 ? <div style={{ padding: 24, textAlign: "center", background: C.paperDeep, borderRadius: 2 }}>
          <div className="f-body" style={{ fontSize: 12, color: C.muted }}>Nessun ingrediente ancora. Aggiungi la prima riga.</div>
        </div> : d.ingredienti.map((ing, i) => {
          const bp = ing.bpId ? state.baseProducts?.find(b => b.id === ing.bpId) : null;
          const unitPrice = bp ? (bp.pReale || bp.pFatt) : 0;
          const cost = unitPrice * (ing.qty || 0);
          return <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 3fr 0.9fr 0.7fr 0.9fr auto", gap: 8, alignItems: "center", padding: 8, background: C.white, border: `1px solid ${bp ? C.ruleSoft : C.amber}`, borderRadius: 2 }}>
            <Input value={ing.name} onChange={e => updateIng(i, { name: e.target.value })} placeholder="Nome ingrediente" style={{ fontSize: 12, padding: "6px 10px" }} />
            <Select value={ing.bpId || ""} onChange={e => updateIng(i, { bpId: e.target.value || null })} style={{ fontSize: 12, padding: "6px 10px" }}>
              <option value="">— non mappato —</option>
              {(() => {
                const bps = state.baseProducts || [];
                const groups = {};
                bps.forEach(b => { (groups[b.cat] = groups[b.cat] || []).push(b); });
                const labels = { pesce: "Pesce & molluschi", verdura: "Verdure & frutta", carne: "Carne", dispensa: "Dispensa", pasta: "Pasta & cereali", altro: "Altro" };
                return Object.entries(groups).map(([cat, items]) => <optgroup key={cat} label={(labels[cat] || cat).toUpperCase()}>
                  {items.map(b => <option key={b.id} value={b.id}>{(b.display || b.nome).slice(0, 40)} — {fmt.eur(b.pReale || b.pFatt)}/kg</option>)}
                </optgroup>);
              })()}
            </Select>
            <Input type="number" step="0.001" value={ing.qty} onChange={e => updateIng(i, { qty: parseFloat(e.target.value) || 0 })} placeholder="kg" style={{ fontSize: 12, padding: "6px 10px" }} />
            <Select value={ing.unit || "kg"} onChange={e => updateIng(i, { unit: e.target.value })} style={{ fontSize: 12, padding: "6px 10px" }}>
              <option>kg</option><option>pz</option><option>l</option>
            </Select>
            <div className="f-mono" style={{ fontSize: 12, color: bp ? C.ink : C.muted, fontWeight: 600, textAlign: "right" }}>{bp ? fmt.eur(cost) : "—"}</div>
            <IconBtn icon={Trash2} size={14} onClick={() => delIng(i)} />
          </div>;
        })}
      </div>

      <div style={{ padding: 16, background: C.tealWash, borderRadius: 2 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16 }}>
          <div>
            <div className="f-mono" style={{ fontSize: 9, color: C.teal, letterSpacing: "0.2em" }}>COSTO {isGrossMode ? "LORDO" : "NETTO"}</div>
            <div className="f-display" style={{ fontSize: 22, color: C.tealDeep, fontWeight: 600 }}>{fmt.eur(econ.cost)}</div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{isGrossMode ? `netto €${totalCost.toFixed(2)}` : `lordo €${(totalCost * (1 + avgVatPurchase)).toFixed(2)}`}</div>
          </div>
          <div>
            <div className="f-mono" style={{ fontSize: 9, color: C.teal, letterSpacing: "0.2em" }}>LISTINO {isGrossMode ? "IVA INCL" : "NETTO"}</div>
            <div className="f-display" style={{ fontSize: 22, color: C.tealDeep, fontWeight: 600 }}>{fmt.eur(econ.price)}</div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{isGrossMode ? `netto €${(d.prezzo / (1 + vatRate)).toFixed(2)}` : `lordo €${d.prezzo.toFixed(2)}`}</div>
          </div>
          <div>
            <div className="f-mono" style={{ fontSize: 9, color: C.teal, letterSpacing: "0.2em" }}>MARGINE {isGrossMode ? "LORDO" : "NETTO"}</div>
            <div className="f-display" style={{ fontSize: 22, color: margin > 0 ? C.oliveDeep : C.terra, fontWeight: 600 }}>{fmt.eur(margin)}</div>
          </div>
          <div>
            <div className="f-mono" style={{ fontSize: 9, color: C.teal, letterSpacing: "0.2em" }}>F.C. %</div>
            <div className="f-display" style={{ fontSize: 22, color: fcPct > 35 ? C.terra : (fcPct > 28 ? C.amber : C.oliveDeep), fontWeight: 600 }}>{fcPct.toFixed(0)}%</div>
          </div>
        </div>
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.teal + "66"}`, display: "flex", gap: 16, flexWrap: "wrap" }}>
          <div className="f-mono" style={{ fontSize: 10, color: C.tealDeep }}>
            IVA vendita: <strong>{(effectiveVatSale * 100).toFixed(0)}%</strong>{d.vatRate != null ? " (override)" : " (default)"}
          </div>
          <div className="f-mono" style={{ fontSize: 10, color: C.tealDeep }}>
            IVA acquisti media: <strong>{(avgVatPurchase * 100).toFixed(1)}%</strong>{costInfo.hasOverrides ? " (pesata con override)" : " (default)"}
          </div>
        </div>
        {/* FIX #5-B — margine netto degli oneri di trasformazione: riga AGGIUNTIVA, mostrata
            solo se Andi ha attivato il forfait in Impostazioni IVA (oneri > 0). Non altera il
            margine di contribuzione sopra (riga "MARGINE"). */}
        {econ.oneriUnitari > 0.005 && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${C.teal + "66"}`, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "baseline" }}>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>
              − Oneri trasformazione ({(Number(state.settings?.oneriTrasformazionePct) || 0)}%): <strong>{fmt.eur(econ.oneriUnitari)}</strong>
            </div>
            <div className="f-mono" style={{ fontSize: 11, color: econ.marginNet > 0 ? C.oliveDeep : C.terra, fontWeight: 700 }}>
              MARGINE NETTO (post-lavorazione): {fmt.eur(econ.marginNet)}
            </div>
          </div>
        )}
      </div>
    </div>
  </Modal>;
}


// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      SCARICO CUCINA (quando si servono piatti)             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// Helper modulo: applica un alias (articolo→piatto) retroattivamente su TUTTI gli scontrini,
// ricostruendo recipeSnapshot/_listinoPrice. STESSA logica di `applyAliasRetroactive` del
// pannello qualità di Vendite, copiata qui per riuso dal bridge SENZA toccare quel componente.
// Non crea movimenti: riclassifica solo le righe vendute. Ritorna {newReceipts, totalUpdated}.
function applyAliasRetroactiveBridge(aliasMap, currentReceipts, currentState) {
  const dishes = currentState.dishesReal || [];
  const baseProducts = currentState.baseProducts || [];
  let totalUpdated = 0, receiptsTouched = 0;
  const newReceipts = currentReceipts.map(r => {
    let touched = false;
    const newItems = (r.items || []).map(it => {
      if (!it.dishId || !String(it.dishId).startsWith("_unmatched_")) return it;
      const aliasKey = normStr(it._csvName || it.name);
      if (!aliasMap[aliasKey]) return it;
      const dish = dishes.find(d => d.id === aliasMap[aliasKey]);
      if (!dish) return it;
      totalUpdated++; touched = true;
      let recipeSnapshot = null;
      if (dish.ingredienti && dish.ingredienti.length > 0) {
        const snapIngs = dish.ingredienti.map(ing => {
          const bp = ing.bpId ? baseProducts.find(b => b.id === ing.bpId) : null;
          return { name: ing.name, qty: ing.qty, unit: ing.unit, bpId: ing.bpId, bpName: bp?.display || bp?.nome || null, unitCostNet: bp ? (bp.pReale || bp.pFatt || 0) : (ing.prezzo || 0), vatRate: bp?.vatRate != null ? bp.vatRate : null };
        });
        const snapCost = snapIngs.reduce((s, ing) => s + ing.qty * (ing.unitCostNet || 0), 0);
        recipeSnapshot = { cat: dish.cat, vatRate: dish.vatRate, ingredienti: snapIngs, netCost: Math.round(snapCost * 10000) / 10000 };
      }
      let newListinoPrice;
      if (isMenuConcordato(it)) newListinoPrice = 0;
      else if (it._isGift) newListinoPrice = 0;
      else if (isDiscountLine(it)) newListinoPrice = 0;
      else if (it.unitPrice === 0 && Number(dish.prezzo) > 0) newListinoPrice = Number(dish.prezzo);
      else newListinoPrice = Number(it.unitPrice) || 0;
      return { ...it, dishId: dish.id, name: dish.nome, recipeSnapshot, _listinoPrice: newListinoPrice, _rematched: true };
    });
    if (touched) {
      receiptsTouched++;
      const listinoTotal = newItems.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it._listinoPrice) || 0), 0);
      const disc = listinoTotal - r.total;
      return { ...r, items: newItems, _listinoTotal: Math.round(listinoTotal * 100) / 100, _discount: Math.round(disc * 100) / 100, _discountPct: listinoTotal > 0 ? Math.round((disc / listinoTotal) * 10000) / 10000 : 0 };
    }
    return r;
  });
  return { newReceipts, totalUpdated, receiptsTouched };
}

// ── MODAL: anteprima + classificazione + conferma scarico dal venduto ────────
// Bozza di engine.buildScaricoProposalFromSales con due KPI (Coverage Totale e Food
// Coverage) e pannello "Qualità dati venduto": ignora i non-food o mappa gli articoli ai
// piatti. Lo scarico REALE avviene solo al "Conferma scarico" (doppia conferma). Ignora/Mappa
// riclassificano i dati (no movimenti). Riusa il meccanismo esistente dishIgnoredArticles.
function ScaricoFromSalesPreview({ state, setState, onClose }) {
  // Giorni-servizio realmente importati (default = ultimo giorno)
  const importedDates = useMemo(() => {
    const set = new Set();
    (state.tableReceipts || []).forEach(r => { const d = getServiceDate(r.closedAt) || r.date; if (d) set.add(d); });
    return [...set].sort();
  }, [state.tableReceipts]);
  const lastDate = importedDates.length ? importedDates[importedDates.length - 1] : TODAY;
  const [fromDate, setFromDate] = useState(lastDate);
  const [toDate, setToDate] = useState(lastDate);
  const [proposal, setProposal] = useState(null);
  const [expanded, setExpanded] = useState({ unmatched: false, bpnull: false, suspicious: false });
  const [committing, setCommitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const toast = useToast();

  // Calcola la bozza (lettura). La generazione iniziale non tocca lo state.
  const generate = () => setProposal(engine.buildScaricoProposalFromSales(state, fromDate, toDate));

  const [mapSel, setMapSel] = useState({});
  // Dopo ignore/map: persiste la riclassificazione e rigenera la bozza sullo stato nuovo.
  // NON crea movimenti lotto (solo dishIgnoredArticles / dishAliases / tableReceipts).
  const refreshFrom = (ns) => { setState(ns); setProposal(engine.buildScaricoProposalFromSales(ns, fromDate, toDate)); };
  const ignoreArticle = (name) => {
    const key = normStr(name); const cur = state.dishIgnoredArticles || [];
    if (cur.includes(key)) return;
    refreshFrom({ ...state, dishIgnoredArticles: [...cur, key] });
    toast({ type: "info", title: "Articolo ignorato", message: `"${name}" escluso dallo scarico (non-food)` });
  };
  const restoreArticle = (key) => refreshFrom({ ...state, dishIgnoredArticles: (state.dishIgnoredArticles || []).filter(k => k !== key) });
  const mapArticle = (name, dishId) => {
    const dish = (state.dishesReal || []).find(d => d.id === dishId); if (!dish) return;
    const newAliases = { ...(state.dishAliases || {}), [normStr(name)]: dishId };
    const { newReceipts, totalUpdated } = applyAliasRetroactiveBridge(newAliases, state.tableReceipts || [], state);
    refreshFrom({ ...state, tableReceipts: newReceipts, dishAliases: newAliases });
    toast({ type: "success", title: "Articolo collegato", message: `"${name}" → "${dish.nome}" · ${totalUpdated} righe ri-mappate` });
  };

  // P1 — Suggerimento del piatto più probabile per ogni articolo da classificare.
  // Riusa levenshteinNorm (la stessa metrica di matchDishFuzzy). SOLO suggerimento:
  // non mappa nulla in automatico (Andi conferma sempre). Memoizzato per performance.
  const suggestions = useMemo(() => {
    const dishes = state.dishesReal || [];
    const dishById = new Map(dishes.map(d => [d.id, d]));
    // Pool A — nomi piatti; Pool B — alias GIÀ CONFERMATI (memoria: ogni mapping passato
    // diventa un'àncora). Pre-tokenizzati una volta.
    const poolDishes = dishes.map(d => ({ dishId: d.id, nome: d.nome, tokens: tokenize(d.nome), isAlias: false }));
    const poolAliases = Object.entries(state.dishAliases || {}).map(([key, dishId]) => {
      const d = dishById.get(dishId);
      return d ? { dishId, nome: d.nome, tokens: tokenize(key), isAlias: true, key } : null;
    }).filter(Boolean);
    const pool = poolDishes.concat(poolAliases);

    // Soglie conservative
    const TOKEN_SHOW = 0.72, TOKEN_CONFIDENT = 0.82, LEV_SHOW = 0.85, LEV_CONFIDENT = 0.90, AMB = 0.05;
    const map = {};
    for (const u of (proposal?.unmatchedAgg || [])) {
      const artTokens = tokenize(u.name);
      // 1) Token-match (E+C) sui due pool
      let best = null, second = null;
      for (const c of pool) {
        const r = tokenMatchScore(artTokens, c.tokens);
        if (!r.ok) continue;
        const cand = { dishId: c.dishId, nome: c.nome, conf: r.conf, source: c.isAlias ? "alias" : "piatto", aliasKey: c.key };
        if (!best || cand.conf > best.conf) { second = best; best = cand; }
        else if (!second || cand.conf > second.conf) { second = cand; }
      }
      if (best && best.conf >= TOKEN_SHOW) {
        const ambiguous = !!(second && second.dishId !== best.dishId && (best.conf - second.conf) < AMB);
        map[u.key] = { dishId: best.dishId, nome: best.nome, score: best.conf, source: best.source, aliasKey: best.aliasKey, confident: best.conf >= TOKEN_CONFIDENT && !ambiguous };
        continue;
      }
      // 2) Fallback Levenshtein vs nomi piatti, SOLO se ≥ 0.85 (sopprime i deboli tipo COCA COLA→CARBONARA)
      let lev = null;
      for (const d of dishes) { const sc = 1 - levenshteinNorm(u.name, d.nome); if (!lev || sc > lev.score) lev = { dishId: d.id, nome: d.nome, score: sc }; }
      if (lev && lev.score >= LEV_SHOW) {
        map[u.key] = { dishId: lev.dishId, nome: lev.nome, score: lev.score, source: "piatto", confident: lev.score >= LEV_CONFIDENT };
      }
      // altrimenti: nessun suggerimento (map[u.key] resta undefined)
    }
    return map;
  }, [proposal, state.dishesReal, state.dishAliases]);

  const sec = (t) => <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, margin: "4px 0 8px" }}>{String(t).toUpperCase()}</div>;
  const tile = (label, value, accent) => (
    <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: "10px 12px", minWidth: 0 }}>
      <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.18em", color: C.muted, textTransform: "uppercase" }}>{label}</div>
      <div className="f-display" style={{ fontSize: 18, color: accent || C.ink, fontWeight: 600, marginTop: 2 }}>{value}</div>
    </div>
  );
  const expandable = (key, title, rows, render) => rows.length === 0 ? null : (
    <div style={{ border: `1px solid ${C.rule}`, borderRadius: 2, marginBottom: 10 }}>
      <button onClick={() => setExpanded(s => ({ ...s, [key]: !s[key] }))} className="tx" style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
        <span className="f-body" style={{ fontSize: 12.5, color: C.ink }}>{title} ({rows.length})</span>
        {expanded[key] ? <ChevronDown size={15} color={C.muted} /> : <ChevronRight size={15} color={C.muted} />}
      </button>
      {expanded[key] && <div style={{ borderTop: `1px solid ${C.ruleSoft}`, maxHeight: 220, overflowY: "auto" }}>{rows.map(render)}</div>}
    </div>
  );

  // Confermabile SOLO se: canCommit, nessun blocker, NESSUNO shortfall (conservativo:
  // non si conferma uno scarico che non può essere completato), e c'è qualcosa da scaricare.
  // ── Derivazioni di SOLA PRESENTAZIONE (UX). Nessun calcolo di dominio: solo come
  //    MOSTRARE `proposal`. Non toccano canCommit/commit/motore. ──
  const ux = proposal ? (() => {
    const allocs = proposal.allocations || [];
    const reqs = proposal.requirements || [];
    const shorts = proposal.shortfalls || [];
    const allocatedTotal = allocs.reduce((s, a) => s + (Number(a.allocatedQty) || 0), 0);
    // CASO A — magazzino vuoto: ci sono prodotti richiesti ma NESSUN lotto per nessuno.
    const emptyWarehouse = reqs.length > 0 && allocs.length > 0 && allocs.every(a => (a.allocation || []).length === 0);
    // CASO B — scorte insufficienti: esiste qualche scorta ma mancano prodotti.
    const insufficient = !emptyWarehouse && shorts.length > 0;
    const costUnavailable = allocatedTotal <= 0; // costo non calcolabile (nessun lotto allocato)
    return { allocatedTotal, emptyWarehouse, insufficient, costUnavailable, shorts };
  })() : null;
  const costMP = () => (ux && ux.costUnavailable) ? "N/D" : fmt.eur(proposal.estimatedCost);
  const SEV_WARN = ["SHORTFALL", "LOW_FOOD_COVERAGE", "UNMATCHED", "BP_NULL", "SUSPICIOUS_RECIPE"];
  const actionFor = (code) => ({
    CRITICAL_SHORTFALL: "Registra un carico di magazzino (Ingresso merce) per i prodotti mancanti.",
    SHORTFALL: "Completa il carico dei prodotti insufficienti elencati in “Cosa manca e cosa fare”.",
    ALREADY_DISCHARGED: "Questi scontrini risultano già scaricati: scegli un altro giorno.",
    DUPLICATE_HASH: "Una proposta identica è già stata scaricata: scegli un altro giorno/periodo.",
    NO_RECEIPTS: "Scegli un giorno con scontrini importati.",
    INVALID_RANGE: "Correggi l'intervallo di date.",
    UNMATCHED: "Collega o ignora gli articoli non riconosciuti (pannello “Qualità dati venduto”).",
    LOW_FOOD_COVERAGE: "Collega o ignora gli articoli per alzare la copertura (pannello “Qualità dati venduto”).",
  }[code] || null);

  const canCommitNow = !!proposal && proposal.canCommit === true && (proposal.blockers || []).length === 0 && (proposal.shortfalls || []).length === 0 && (proposal.allocations || []).length > 0;
  const operatorName = state.currentUser?.name || (state.users && state.users[0]?.name) || "—";

  // Doppio click protetto da `committing`. Scrittura SOLO qui, dopo doppia conferma.
  const doCommit = () => {
    if (committing || !canCommitNow) return;
    setCommitting(true);
    try {
      const res = engine.commitScaricoProposalFromSales(state, proposal, operatorName);
      if (res.error) {
        toast({ type: "error", title: "Scarico annullato — niente scaricato", message: res.message || res.error });
        setConfirmOpen(false);
        return; // NON chiude la modale: Andi può correggere
      }
      setState(res.state);
      toast({ type: "success", title: "Scarico registrato", message: `${res.movementCount} movimenti · ${res.koCount} documenti · ${fmt.eur(proposal.estimatedCost)} materia prima` });
      setConfirmOpen(false);
      onClose();
    } finally {
      setCommitting(false);
    }
  };

  const commitLabel = committing ? "Registrazione in corso…" : !proposal ? "Conferma scarico — genera l'anteprima" : canCommitNow ? "Conferma scarico" : "Conferma scarico — non confermabile";
  const footer = <>
    <Btn variant="ghost" onClick={onClose} disabled={committing}>Chiudi</Btn>
    <Btn variant="terra" disabled={!canCommitNow || committing} onClick={() => setConfirmOpen(true)} title={canCommitNow ? "Scarica realmente il magazzino" : "Proposta non confermabile"}>{commitLabel}</Btn>
  </>;

  return <><Modal onClose={onClose} maxWidth={880} subtitle="Anteprima · conferma manuale" title="Proposta scarico dal venduto" footer={footer}>
    <div style={{ padding: 24 }}>
      {/* Banner BOZZA */}
      <div style={{ display: "flex", gap: 12, padding: 12, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, marginBottom: 16 }}>
        <Info size={16} color={C.amberDeep} style={{ flexShrink: 0, marginTop: 1 }} />
        <div className="f-body" style={{ fontSize: 12.5, color: C.amberDeep, lineHeight: 1.5 }}>
          <strong>Anteprima.</strong> Nessun movimento verrà registrato finché non premi “Conferma scarico”. La conferma chiede un'ulteriore approvazione e poi scrive in modo atomico (tutto o niente).
        </div>
      </div>

      {/* Selettore periodo */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 10 }}>
        <Field label="Dal giorno (servizio)"><Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} /></Field>
        <Field label="Al giorno (servizio)"><Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} /></Field>
        <Btn variant="accent" icon={RefreshCw} onClick={generate}>Genera anteprima</Btn>
      </div>
      {importedDates.length > 0 && <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginBottom: 18 }}>Giorni importati: {importedDates[0]} → {importedDates[importedDates.length - 1]} ({importedDates.length})</div>}

      {!proposal ? <EmptyState icon={Receipt} title="Nessuna anteprima generata" body="Scegli un giorno o un intervallo già importato e premi “Genera anteprima”." />
      : <>
        {/* Banner esplicito: distingue magazzino VUOTO da scorte INSUFFICIENTI (sola UX) */}
        {ux && ux.emptyWarehouse && <div style={{ padding: 14, background: C.terraWash, border: `1px solid ${C.terra}`, borderRadius: 2, marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}><AlertTriangle size={16} color={C.terraDeep} /><span className="f-body" style={{ fontSize: 14, fontWeight: 600, color: C.terraDeep }}>Impossibile generare lo scarico — magazzino vuoto</span></div>
          <div className="f-body" style={{ fontSize: 12.5, color: C.terraDeep, lineHeight: 1.5, marginLeft: 24 }}>Nel magazzino non risultano lotti disponibili per i prodotti richiesti. Per poter scaricare il venduto devi prima <strong>registrare un carico di magazzino</strong> (Ingresso merce).</div>
        </div>}
        {ux && ux.insufficient && <div style={{ padding: 14, background: C.terraWash, border: `1px solid ${C.terra}`, borderRadius: 2, marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}><AlertTriangle size={16} color={C.terraDeep} /><span className="f-body" style={{ fontSize: 14, fontWeight: 600, color: C.terraDeep }}>Scorte insufficienti per {ux.shorts.length} prodott{ux.shorts.length === 1 ? "o" : "i"}</span></div>
          <div className="f-body" style={{ fontSize: 12.5, color: C.terraDeep, lineHeight: 1.5, marginLeft: 24 }}>Alcuni prodotti non bastano a coprire il venduto. Registra un carico per i prodotti elencati in <strong>“Cosa manca e cosa fare”</strong>, oppure procedi quando avrai ricevuto la merce.</div>
        </div>}

        {/* Riepilogo */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 8 }}>
          {tile("Scontrini", proposal.receiptCount)}
          {tile("Coperti", proposal.covers)}
          {tile("Incasso", fmt.eur(proposal.grossRevenue))}
          {tile("Righe vendute", proposal.itemCount)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
          {tile("Coverage Totale", `${(proposal.coverageTotal * 100).toFixed(1)}%`, C.inkSoft)}
          {tile("Food Coverage", `${((proposal.foodCoverage || 0) * 100).toFixed(1)}%`, (proposal.foodCoverage || 0) < 0.95 ? C.amberDeep : C.oliveDeep)}
          {tile("Costo stimato MP", costMP(), ux && ux.costUnavailable ? C.muted : C.ink)}
          {tile("Stato commit", proposal.canCommit ? "Pronto*" : "Bloccato", proposal.canCommit ? C.oliveDeep : C.terra)}
        </div>
        {ux && ux.costUnavailable && <div className="f-mono" style={{ fontSize: 9.5, color: C.mutedSoft, marginTop: -4, marginBottom: 8 }}>Costo non disponibile: nessun lotto allocato.</div>}
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: -10, marginBottom: 4 }}>* alla conferma: movimenti lotto + documenti di scarico + voce di audit (atomico).</div>
        <div className="f-mono" style={{ fontSize: 9.5, color: C.mutedSoft, marginBottom: 14 }}>firma proposta (sourceHash): {proposal.sourceHash}</div>

        {/* 🔴 Cosa devi fare prima di poter confermare (azioni operative) */}
        {!proposal.canCommit && <div style={{ padding: 12, background: C.terraWash, border: `1px solid ${C.terra}`, borderRadius: 2, marginBottom: 14 }}>
          <div className="f-body" style={{ fontSize: 13, fontWeight: 600, color: C.terraDeep, marginBottom: 6 }}>🔴 Cosa devi fare prima di poter confermare</div>
          {(() => {
            const actions = []; const seen = new Set();
            const push = (code) => { const a = actionFor(code); if (a && !seen.has(a)) { seen.add(a); actions.push(a); } };
            (proposal.blockers || []).forEach(b => push(b.code));
            (proposal.warnings || []).forEach(w => { if (w.code === "SHORTFALL" || w.code === "UNMATCHED" || w.code === "LOW_FOOD_COVERAGE") push(w.code); });
            if (actions.length === 0) actions.push("Risolvi i punti segnalati qui sotto, poi premi “Genera anteprima”.");
            return <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {actions.map((a, i) => <div key={i} className="f-body" style={{ fontSize: 12.5, color: C.terraDeep, lineHeight: 1.5 }}>• {a}</div>)}
            </div>;
          })()}
        </div>}

        {/* Cosa verrà / NON verrà scaricato */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
          <div style={{ border: `1px solid ${C.olive}`, background: C.oliveWash, borderRadius: 2, padding: "10px 12px" }}>
            <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.oliveDeep, marginBottom: 6 }}>✓ VERRÀ SCARICATO</div>
            <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.6 }}>
              {proposal.requirements.length} prodotti base<br />
              Costo stimato MP <strong>{costMP()}</strong>
            </div>
          </div>
          <div style={{ border: `1px solid ${C.amber}`, background: C.amberWash, borderRadius: 2, padding: "10px 12px" }}>
            <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.amberDeep, marginBottom: 6 }}>✗ NON VERRÀ SCARICATO</div>
            <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.6 }}>
              {proposal.unmatchedItems.length} righe non collegate a ricetta<br />
              {proposal.bpNullIngredients.length} ingredienti senza prodotto base<br />
              {proposal.suspiciousRecipes.length} piatti senza ricetta
            </div>
          </div>
        </div>

        {/* Cosa manca e cosa fare (sempre visibile): sintesi operativa, niente ridondanza */}
        {sec("Cosa manca e cosa fare")}
        {ux && ux.emptyWarehouse
          ? <div style={{ padding: 10, background: C.terraWash, borderLeft: `3px solid ${C.terra}`, borderRadius: 2, marginBottom: 16 }}><span className="f-body" style={{ fontSize: 12.5, color: C.terraDeep }}>Magazzino vuoto: registra un carico (Ingresso merce) prima di poter scaricare.</span></div>
          : (proposal.shortfalls || []).length === 0
            ? <div style={{ padding: 10, background: C.oliveWash, borderLeft: `3px solid ${C.olive}`, borderRadius: 2, marginBottom: 16 }}><span className="f-body" style={{ fontSize: 12.5, color: C.oliveDeep }}>Tutti gli ingredienti richiesti sono disponibili a magazzino.</span></div>
            : <div style={{ border: `1px solid ${C.terra}`, borderRadius: 2, marginBottom: 16, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, padding: "6px 12px", background: C.terraWash }}>
                  <span className="f-mono" style={{ fontSize: 9, color: C.terraDeep, letterSpacing: "0.1em" }}>PRODOTTO DA CARICARE</span>
                  <span className="f-mono" style={{ fontSize: 9, color: C.terraDeep, letterSpacing: "0.1em" }}>MANCA</span>
                </div>
                {proposal.shortfalls.map((s) => <div key={s.bpId} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, padding: "6px 12px", borderTop: `1px solid ${C.ruleSoft}` }}>
                  <span className="f-body" style={{ fontSize: 12, color: C.ink }}>{s.productName}</span>
                  <span className="f-mono" style={{ fontSize: 11.5, color: C.terra, fontWeight: 600 }}>{Number(s.shortfall).toFixed(3)} {s.unit}</span>
                </div>)}
                <div style={{ padding: "6px 12px", background: C.paperDeep }}><span className="f-body" style={{ fontSize: 11.5, color: C.inkSoft }}>→ Registra un carico di magazzino per questi prodotti, poi premi “Genera anteprima”.</span></div>
              </div>}

        {/* Avvisi gerarchici: 🟡 riduce copertura/qualità · ℹ️ informazione (il 🔴 è il box azioni sopra) */}
        {(() => {
          const warn = (proposal.warnings || []).filter(w => SEV_WARN.includes(w.code));
          const info = (proposal.warnings || []).filter(w => !SEV_WARN.includes(w.code));
          const Group = (icon, title, items, color, bg) => items.length === 0 ? null : <div style={{ marginBottom: 10 }}>
            <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.1em", color, marginBottom: 4 }}>{icon} {String(title).toUpperCase()} ({items.length})</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {items.map((w, i) => <div key={i} style={{ padding: "5px 10px", background: bg, borderRadius: 2 }}>
                <span className="f-body" style={{ fontSize: 12, color: C.inkSoft }}>{w.message}</span>
              </div>)}
            </div>
          </div>;
          return (warn.length + info.length) === 0 ? null : <div style={{ marginBottom: 16 }}>
            {Group("🟡", "Riduce copertura / qualità", warn, C.amberDeep, C.amberWash)}
            {Group("ℹ️", "Informazione", info, C.muted, C.paperDeep)}
          </div>;
        })()}

        {/* Dettaglio tecnico (espandibile): ingredienti richiesti + allocazione FIFO per lotto + scorte */}
        <button onClick={() => setExpanded(s => ({ ...s, tech: !s.tech }))} className="tx" style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", marginBottom: 8, background: "transparent", border: `1px solid ${C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit" }}>
          <span className="f-body" style={{ fontSize: 12.5, color: C.ink }}>Dettaglio tecnico — ingredienti richiesti e allocazione FIFO per lotto</span>
          {expanded.tech ? <ChevronDown size={15} color={C.muted} /> : <ChevronRight size={15} color={C.muted} />}
        </button>
        {expanded.tech && <div style={{ border: `1px solid ${C.ruleSoft}`, borderRadius: 2, padding: "8px 12px", marginBottom: 16 }}>
        {/* Ingredienti richiesti */}
        {sec(`Ingredienti richiesti (${proposal.requirements.length})`)}
        {proposal.requirements.length === 0 ? <div className="f-body" style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>Nessun ingrediente scaricabile nel periodo.</div>
        : <div style={{ border: `1px solid ${C.rule}`, borderRadius: 2, marginBottom: 16, overflow: "hidden" }}>
          {proposal.requirements.map((r, i) => <div key={r.bpId} style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 10, padding: "8px 12px", borderBottom: i < proposal.requirements.length - 1 ? `1px solid ${C.ruleSoft}` : "none", alignItems: "center" }}>
            <div className="f-body" style={{ fontSize: 12.5, color: C.ink }}>{r.productName} <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>({r.bpId})</span></div>
            <div className="f-mono" style={{ fontSize: 12, color: C.teal, fontWeight: 600 }}>{r.totalQty.toFixed(3)} {r.unit}</div>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>{r.sourceRows} righe</div>
          </div>)}
        </div>}

        {/* Allocazione FIFO/FEFO */}
        {proposal.allocations.length > 0 && <>
          {sec("Allocazione FIFO/FEFO proposta")}
          <div style={{ marginBottom: 16 }}>
            {proposal.allocations.map(a => <div key={a.bpId} style={{ marginBottom: 8 }}>
              <div className="f-body" style={{ fontSize: 12, fontWeight: 600, color: C.ink, marginBottom: 4 }}>
                {a.productName} — richiesti {a.totalQty.toFixed(3)} {a.unit}, allocati {a.allocatedQty.toFixed(3)}{a.shortfall > 0 ? ` · mancano ${a.shortfall.toFixed(3)}` : ""}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {a.allocation.length === 0 ? <span className="f-mono" style={{ fontSize: 10, color: C.terra }}>nessun lotto disponibile</span>
                : a.allocation.map((al, j) => <span key={j} className="f-mono" style={{ fontSize: 10, color: C.teal, background: C.tealWash, padding: "3px 8px", borderRadius: 2 }}>{al.lotId} · {al.qty.toFixed(3)} · scad {al.expiresAt || "—"} · {fmt.eur(al.lineCost)}</span>)}
              </div>
            </div>)}
          </div>
        </>}

        {/* Shortfalls */}
        {proposal.shortfalls.length > 0 && <>
          {sec(`Scorte insufficienti (${proposal.shortfalls.length})`)}
          <div style={{ border: `1px solid ${C.terra}`, borderRadius: 2, marginBottom: 16, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 10, padding: "6px 12px", background: C.terraWash }}>
              <span className="f-mono" style={{ fontSize: 9, color: C.terraDeep, letterSpacing: "0.1em" }}>PRODOTTO</span>
              <span className="f-mono" style={{ fontSize: 9, color: C.terraDeep, letterSpacing: "0.1em" }}>RICHIESTO</span>
              <span className="f-mono" style={{ fontSize: 9, color: C.terraDeep, letterSpacing: "0.1em" }}>DISPONIB.</span>
              <span className="f-mono" style={{ fontSize: 9, color: C.terraDeep, letterSpacing: "0.1em" }}>MANCANTE</span>
            </div>
            {proposal.shortfalls.map((s, i) => <div key={s.bpId} style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 10, padding: "6px 12px", borderTop: `1px solid ${C.ruleSoft}` }}>
              <span className="f-body" style={{ fontSize: 12, color: C.ink }}>{s.productName}</span>
              <span className="f-mono" style={{ fontSize: 11, color: C.inkSoft }}>{Number(s.needed).toFixed(3)} {s.unit}</span>
              <span className="f-mono" style={{ fontSize: 11, color: C.inkSoft }}>{Number(s.allocatedQty).toFixed(3)}</span>
              <span className="f-mono" style={{ fontSize: 11, color: C.terra, fontWeight: 600 }}>{Number(s.shortfall).toFixed(3)}</span>
            </div>)}
          </div>
        </>}
        </div>}

        {/* Elenchi espandibili */}
        {/* ── QUALITÀ DATI VENDUTO: classifica gli articoli non collegati ── */}
        {sec("Qualità dati venduto")}
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginBottom: 8 }}>
          Coverage Totale {(proposal.coverageTotal * 100).toFixed(1)}% · Food Coverage {((proposal.foodCoverage || 0) * 100).toFixed(1)}% — esclude {proposal.ignoredRows || 0} righe non-food ignorate.
        </div>

        {/* P6 — Avanzamento pulizia (testuale + barra). Stima tempo dichiarata indicativa. */}
        {(() => {
          const resid = proposal.unmatchedAgg || [];
          const residRighe = resid.reduce((s, u) => s + (u.righe || 0), 0);
          const estMin = Math.max(1, Math.ceil(resid.length * 8 / 60));
          const pct = Math.round((proposal.foodCoverage || 0) * 100);
          return <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: "10px 12px", marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.muted }}>AVANZAMENTO PULIZIA</span>
              <span className="f-mono" style={{ fontSize: 12, fontWeight: 600, color: pct >= 95 ? C.oliveDeep : C.amberDeep }}>Food Coverage {pct}%</span>
            </div>
            <div style={{ height: 6, background: C.paperDeep, borderRadius: 3, overflow: "hidden", marginBottom: 6 }}>
              <div style={{ height: "100%", width: `${pct}%`, background: pct >= 95 ? C.olive : C.amber }} />
            </div>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>
              {resid.length === 0 ? "Tutti i piatti classificati 🎉" : <>Restano <strong style={{ color: C.inkSoft }}>{resid.length} articoli</strong> · {residRighe} righe food/incerte → ~{estMin} min al 100% <span style={{ color: C.mutedSoft }}>(stima indicativa)</span></>}
            </div>
          </div>;
        })()}

        {(proposal.unmatchedAgg || []).length === 0 ? <div className="f-body" style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Nessun articolo da classificare 🎉</div>
        : <div style={{ border: `1px solid ${C.rule}`, borderRadius: 2, marginBottom: 10, maxHeight: 320, overflowY: "auto" }}>
          {(proposal.unmatchedAgg || []).map((u, i) => {
            const sug = suggestions[u.key];                           // P1 + token-match
            const sugId = (sug && sug.confident) ? sug.dishId : "";    // pre-compila SOLO se confident
            const val = (u.key in mapSel) ? mapSel[u.key] : sugId;     // niente pre-compilazione su ipotesi deboli
            const gain = proposal.foodRows > 0 ? (u.righe / proposal.foodRows * 100) : 0; // P3
            return (
              <div key={u.key} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, padding: "8px 12px", borderBottom: i < proposal.unmatchedAgg.length - 1 ? `1px solid ${C.ruleSoft}` : "none", alignItems: "center" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <span className="f-body" style={{ fontSize: 12.5, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</span>
                    {gain > 0 && <span className="f-mono" title="Guadagno stimato di Food Coverage se mappato" style={{ flexShrink: 0, fontSize: 9.5, color: C.oliveDeep, background: C.oliveWash, padding: "1px 5px", borderRadius: 2 }}>+{gain.toFixed(1)}% food</span>}
                  </div>
                  <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>{u.righe} righe · {u.qta} qtà · {fmt.eur(u.fatturato)}</div>
                  {sug
                    ? <div className="f-mono" style={{ fontSize: 10, marginTop: 2, color: sug.confident ? C.oliveDeep : C.amberDeep }}>⟶ suggerito: {sug.nome} · {Math.round(sug.score * 100)}% · {sug.source === "alias" ? <>da alias «{sug.aliasKey}»</> : "da nome piatto"}</div>
                    : <div className="f-mono" style={{ fontSize: 10, marginTop: 2, color: C.mutedSoft }}>⟶ nessun suggerimento affidabile — mappa a mano o ignora</div>}
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                  <select value={val} onChange={e => setMapSel(p => ({ ...p, [u.key]: e.target.value }))} className="f-body" style={{ fontSize: 11, padding: "5px 6px", border: `1px solid ${C.rule}`, borderRadius: 2, background: C.white, color: C.ink, maxWidth: 150 }}>
                    <option value="">Mappa a piatto…</option>
                    {(state.dishesReal || []).map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
                  </select>
                  <Btn variant="ghost" size="xs" disabled={!val} onClick={() => mapArticle(u.name, val)}>Mappa</Btn>
                  <Btn variant="ghost" size="xs" onClick={() => ignoreArticle(u.name)}>Ignora</Btn>
                </div>
              </div>
            );
          })}
        </div>}
        {expandable("ignored", "Articoli ignorati (non-food, ripristinabili)", proposal.ignoredAgg || [], (g) => (
          <div key={g.key} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 12px", borderBottom: `1px solid ${C.ruleSoft}`, alignItems: "center" }}>
            <span className="f-body" style={{ fontSize: 11.5, color: C.inkSoft }}>{g.name} <span className="f-mono" style={{ color: C.muted }}>· {g.righe} righe · {fmt.eur(g.fatturato)}</span></span>
            <Btn variant="ghost" size="xs" onClick={() => restoreArticle(g.key)}>Ripristina</Btn>
          </div>
        ))}
        {expandable("bpnull", "Ingredienti senza prodotto base (ignorati)", proposal.bpNullIngredients, (b, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "5px 12px", borderBottom: `1px solid ${C.ruleSoft}` }}>
            <span className="f-body" style={{ fontSize: 11.5, color: C.inkSoft }}>{b.ingredientName} <span style={{ color: C.muted }}>· {b.dishName}</span></span>
            <span className="f-mono" style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>{(Number(b.qty) || 0).toFixed(3)}</span>
          </div>
        ))}
        {expandable("suspicious", "Piatti venduti senza ricetta", proposal.suspiciousRecipes, (s, i) => (
          <div key={i} style={{ padding: "5px 12px", borderBottom: `1px solid ${C.ruleSoft}` }}>
            <span className="f-body" style={{ fontSize: 11.5, color: C.inkSoft }}>{s.dishName} <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>({s.reason})</span></span>
          </div>
        ))}
      </>}
    </div>
  </Modal>
  <ConfirmModal open={confirmOpen} danger title="Conferma scarico magazzino"
    message="Questa azione scaricherà REALMENTE il magazzino: crea movimenti sui lotti (non reversibili automaticamente), i documenti di scarico e una voce di audit. Continuare?"
    confirmText="Sì, scarica" cancelText="Annulla" onConfirm={doCommit} onCancel={() => setConfirmOpen(false)} />
  </>;
}

function ScaricoCucina({ state, setState, onNavigate }) {
  const [showForm, setShowForm] = useState(false);
  const [showSalesPreview, setShowSalesPreview] = useState(false);
  const toast = useToast();

  return <div className="fade-up">
    <PageHeader kicker="Consumo reale dei piatti" title="Scarico cucina"
      subtitle="Registra i piatti serviti: il sistema alloca FIFO dai lotti in magazzino e aggiorna il food cost reale in tempo reale."
      right={<div style={{ display: "flex", gap: 8 }}>
        <Btn variant="ghost" size="lg" icon={Receipt} onClick={() => setShowSalesPreview(true)}>Genera proposta scarico dal venduto</Btn>
        <Btn variant="terra" size="lg" icon={Plus} onClick={() => setShowForm(true)}>Registra servizio</Btn>
      </div>} />

    {showForm && <ScaricoForm state={state} setState={setState} onClose={() => setShowForm(false)} />}
    {showSalesPreview && <ScaricoFromSalesPreview state={state} setState={setState} onClose={() => setShowSalesPreview(false)} />}

    <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {state.kitchenOutputs.length === 0 ? <EmptyState icon={Flame} title="Nessun piatto servito registrato" body="Registra il primo servizio per tracciare il food cost reale" />
      : [...state.kitchenOutputs].reverse().map(ko => {
        // FIX BLOCKER 2: i piatti sono in state.dishesReal (state.dishes e' vuoto).
        const dish = (state.dishesReal || []).find(d => d.id === ko.dishId);
        const realCost = (ko.consumedLots || []).reduce((sum, cl) => {
          const lot = state.lots.find(l => l.id === cl.lotId);
          if (!lot) return sum;
          // FIX audit 2026-06-06: valorizza al costo snapshot del lotto post-calo
          // (unitCostReal), con fallback a unitCost e, solo per lotti legacy senza
          // snapshot, al pricePerUnit del DDT. Prima usava SOLO pricePerUnit del
          // receipt → 0€ per i lotti FIGLIO (receiptId null) e ignorava il calo peso.
          let unit = Number(lot.unitCostReal);
          if (!Number.isFinite(unit) || unit <= 0) unit = Number(lot.unitCost);
          if (!Number.isFinite(unit) || unit <= 0) {
            const rec = state.receipts.find(r => r.id === lot.receiptId);
            const line = rec?.lines.find(ln => ln.itemId === lot.itemId);
            unit = line ? (Number(line.pricePerUnit) || 0) : 0;
          }
          return sum + (Number(cl.qty) || 0) * unit;
        }, 0);
        const perServ = ko.servings > 0 ? realCost / ko.servings : 0;
        return <div key={ko.id} style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 42, height: 42, background: C.terraWash, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2 }}>
                <Utensils size={18} color={C.terra} strokeWidth={1.5} />
              </div>
              <div>
                <div className="f-body" style={{ fontSize: 14, color: C.ink, fontWeight: 500 }}>{dish?.nome || dish?.name}</div>
                <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{fmt.date(ko.date)} · {ko.shift} · {ko.operator} · {ko.servings} porz.</div>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="f-mono" style={{ fontSize: 15, color: C.ink, fontWeight: 600 }}>{fmt.eur(realCost)}</div>
              <div className="f-mono" style={{ fontSize: 11, color: C.muted }}>{fmt.eur(perServ)}/porz.</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(ko.consumedLots || []).map((cl, i) => {
              const lot = state.lots.find(l => l.id === cl.lotId);
              const item = engine.getItems(state).find(it => it.id === lot?.itemId);
              return <button key={i} onClick={() => onNavigate({ view: "lot", lotId: cl.lotId })} className="f-mono tx" style={{ fontSize: 10, color: C.teal, letterSpacing: "0.1em", padding: "3px 8px", background: C.tealWash, border: "none", borderRadius: 2, cursor: "pointer" }}>{cl.lotId} → {item?.name} ({cl.qty.toFixed(2)} {lot?.unit})</button>;
            })}
          </div>
        </div>;
      })}
    </div>
  </div>;
}

function ScaricoForm({ state, setState, onClose }) {
  // FIX BLOCKER 2 (audit 2026-06-07): il listino piatti reale e' in state.dishesReal,
  // NON in state.dishes (sempre vuoto). Prima il menu' a tendina era vuoto e Andi non
  // poteva registrare nulla. Shape dishesReal: {id, nome, cat, prezzo, ingredienti:[{name,qty,unit,bpId}]}.
  // Il bpId dell'ingrediente E' l'itemId del lotto (engine.getItems espone i baseProducts
  // con id == bp.id), quindi l'allocazione FIFO usa ing.bpId come itemId.
  const dishesList = state.dishesReal || [];
  const [dishId, setDishId] = useState(dishesList[0]?.id || "");
  const [servings, setServings] = useState(1);
  const [shift, setShift] = useState("pranzo");
  const [operator, setOperator] = useState(state.users.filter(u => u.role === "Cucina")[0]?.name || state.currentUser.name);
  // FIX P0-02 (audit 2026-05-19): guardia anti doppio click.
  // Reg. CE 852/2004 art. 5 — registrazioni veridiche. Doppio submit ⇒ doppio
  // kitchenOutput + doppi movimenti FIFO ⇒ inventario fasullo, food cost gonfiato.
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const dish = dishesList.find(d => d.id === dishId);
  const allocations = useMemo(() => {
    if (!dish) return [];
    // Solo ingredienti con bpId mappato a un prodotto base tracciato: gli ingredienti
    // con bpId null (es. "succo di limone", crostini sfusi) non hanno lotto in magazzino
    // e non possono essere scaricati FIFO — vengono ignorati nello scarico MP.
    return (dish.ingredienti || [])
      .filter(ing => ing.bpId)
      .map(ing => {
        const needed = ing.qty * servings;
        const { allocation, shortfall } = engine.suggestFifoAllocation(state, ing.bpId, needed);
        const item = engine.getItems(state).find(it => it.id === ing.bpId);
        return { itemId: ing.bpId, item, needed, unit: ing.unit, allocation, shortfall };
      });
  }, [dish, servings, state]);

  const anyShortfall = allocations.some(a => a.shortfall > 0);
  const totalCost = allocations.reduce((s, a) => {
    return s + a.allocation.reduce((ss, al) => {
      const lot = state.lots.find(l => l.id === al.lotId);
      if (!lot) return ss;
      // FIX audit 2026-06-06: stesso fix del riepilogo Scarico — usa unitCostReal
      // (post-calo) con fallback unitCost / pricePerUnit legacy. Prima i lotti
      // figlio (receiptId null) valevano 0 e il calo peso era ignorato.
      let unit = Number(lot.unitCostReal);
      if (!Number.isFinite(unit) || unit <= 0) unit = Number(lot.unitCost);
      if (!Number.isFinite(unit) || unit <= 0) {
        const rec = state.receipts.find(r => r.id === lot.receiptId);
        const line = rec?.lines.find(ln => ln.itemId === lot.itemId);
        unit = line ? (Number(line.pricePerUnit) || 0) : 0;
      }
      return ss + (Number(al.qty) || 0) * unit;
    }, 0);
  }, 0);

  const submit = () => {
    // FIX P0-02: idempotenza submit. Se gia' in corso → no-op.
    if (submitting) return;
    if (anyShortfall) { toast({ type: "error", title: "Scorte insufficienti", message: "Alcuni ingredienti non sono sufficienti" }); return; }
    setSubmitting(true);
    try { doSubmit(); } finally { setSubmitting(false); }
  };

  const doSubmit = () => {
    // Fix L3 — Guardia consumo crudo: piatti destinati al consumo crudo/marinato/affumicato
    // a freddo possono essere scaricati SOLO da lotti pesce abbattuti (Anisakis-conformi).
    // D.M. 17/07/2013 + Reg. CE 853/2004 All. III Sez. VIII Cap. III lett. D.
    // Responsabilita' penale titolare art. 590 c.p. in caso di anisakidosi.
    const rawFishPatterns = /\b(crud|marinat|sashimi|tartar|carpaccio|ceviche|affumicat)/i;
    const dishName = (dish?.nome || dish?.name || "");
    const dishCat = (dish?.cat || dish?.category || "");
    const requiresAbbattimento = (dish?.requiresAbbattimento === true)
      || dishCat === "antipasti_crudi"
      || rawFishPatterns.test(dishName);
    if (requiresAbbattimento) {
      // Considera solo i lotti di categoria "pesce" (verdure/cereali non richiedono abbattimento).
      const fishIngredientsAlloc = allocations.filter(a => a.item?.category === "pesce");
      const offendingLots = [];
      for (const a of fishIngredientsAlloc) {
        for (const al of a.allocation) {
          const lot = state.lots.find(l => l.id === al.lotId);
          if (!lot) continue;
          const isAbbattuto = lot.status === "abbattuto" || !!lot.coreTempProtocolUsed;
          if (!isAbbattuto) offendingLots.push({ lotId: al.lotId, itemName: a.item?.name });
        }
      }
      if (offendingLots.length > 0) {
        const lotsStr = offendingLots.slice(0, 3).map(o => `${o.lotId} (${o.itemName})`).join(", ");
        toast({
          type: "error",
          title: "Piatto crudo: lotto NON conforme",
          message: `⚠ "${dishName}" richiede abbattimento Anisakis. Lotti non conformi: ${lotsStr}. D.M. 17/07/2013`,
        });
        const audited = {
          ...state,
          auditLog: appendAudit(state, {
            action: "raw_consumption_blocked",
            entityType: "kitchenOutput",
            entityId: null,
            reason: `Scarico piatto crudo "${dishName}" bloccato: ${offendingLots.length} lotti non abbattuti`,
            payload: { dishId, offendingLots },
          }),
        };
        setState(audited);
        return;
      }
    }

    // ── FIX BLOCKER 4 (audit 2026-06-07): scarico ATOMICO — o tutto o niente ──
    // Reg. CE 178/2002 art. 18: kg scaricati dal magazzino DEVONO avere un documento
    // giustificativo (il kitchenOutput). PRIMA: se la merce finiva a meta' lista, i
    // movimenti dei lotti gia' processati erano in `nextState` e venivano PERSISTITI
    // con setState(...nextState...) ma il kitchenOutput NON veniva creato ⇒ ammanco
    // invisibile, tracciabilita' rotta. ORA: calcoliamo nextState in locale e lo
    // committiamo SOLO se TUTTI i movimenti vanno a buon fine. In caso di errore
    // abortiamo pulito: audit applicato allo STATE ORIGINALE (zero movimenti) +
    // messaggio chiaro all'operatore (niente scaricato).
    let nextState = { ...state };
    const consumedLots = [];
    for (const a of allocations) {
      for (const al of a.allocation) {
        const result = engine.applyMovement(nextState, { lotId: al.lotId, qty: -al.qty, type: "scarico", ref: { type: "kitchenOutput", id: "pending" }, by: operator });
        if (result.error === "INSUFFICIENT_STOCK") {
          toast({ type: "error", title: "Merce insufficiente — niente scaricato", message: `Lotto ${al.lotId}: mancano ${result.shortfall.toFixed(2)}. Nessun movimento applicato (operazione annullata).` });
          // Audit sullo STATE ORIGINALE: i movimenti parziali accumulati in nextState
          // vengono SCARTATI, non committati. Nessun ammanco.
          setState({ ...state, auditLog: appendAudit(state, { action: "scarico_abort_insufficiente", entityType: "kitchenOutput", entityId: null, reason: `Scarico ${dishName || dishId} annullato (atomico): lotto ${al.lotId} shortfall ${result.shortfall.toFixed(3)} — 0 movimenti applicati`, payload: { dishId, lotId: al.lotId, shortfall: result.shortfall } }) });
          return;
        }
        if (result.error) {
          toast({ type: "error", title: "Errore — niente scaricato", message: `Lotto ${al.lotId}: ${result.error}. Operazione annullata.` });
          setState({ ...state, auditLog: appendAudit(state, { action: "scarico_abort_errore", entityType: "kitchenOutput", entityId: null, reason: `Scarico ${dishName || dishId} annullato (atomico): lotto ${al.lotId} errore ${result.error} — 0 movimenti applicati`, payload: { dishId, lotId: al.lotId, error: result.error } }) });
          return;
        }
        consumedLots.push({ lotId: al.lotId, qty: al.qty });
        nextState = result;
      }
    }
    // Tutti i movimenti riusciti: ora — e solo ora — committiamo TUTTO insieme
    // (movimenti lotti + kitchenOutput) in un singolo setState atomico.
    const ko = { id: uid("ko_"), date: TODAY, dishId, servings: parseInt(servings), operator, shift, consumedLots };
    nextState.kitchenOutputs = [...nextState.kitchenOutputs, ko];
    setState(nextState);
    toast({ type: "success", title: "Servizio registrato", message: `${servings} porz. di ${dish?.nome || dish?.name} scaricate · ${fmt.eur(totalCost)} materia prima` });
    onClose();
  };

  return <Modal onClose={onClose} maxWidth={720} subtitle="Scarico automatico FIFO" title="Registra piatti serviti"
    footer={<><Btn variant="ghost" onClick={onClose} disabled={submitting}>Annulla</Btn><Btn variant="terra" icon={Flame} onClick={submit} disabled={anyShortfall || submitting}>{submitting ? "Registrazione in corso..." : "Registra scarico"}</Btn></>}>
    <div style={{ padding: 24 }}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 0.7fr 0.8fr 1.2fr", gap: 12, marginBottom: 20 }}>
        <Field label="Piatto" required>
          {/* FIX BLOCKER 2: listino da state.dishesReal (state.dishes e' vuoto). */}
          <Select value={dishId} onChange={e => setDishId(e.target.value)}>{dishesList.map(d => <option key={d.id} value={d.id}>{d.nome || d.name}</option>)}</Select>
        </Field>
        <Field label="Porzioni" required><Input type="number" min="1" value={servings} onChange={e => setServings(e.target.value)} /></Field>
        <Field label="Turno"><Select value={shift} onChange={e => setShift(e.target.value)}><option>pranzo</option><option>cena</option></Select></Field>
        <Field label="Operatore">
          <Select value={operator} onChange={e => setOperator(e.target.value)}>{state.users.filter(u => u.role === "Cucina" || u.role === "Laboratorio").map(u => <option key={u.id} value={u.name}>{u.name}</option>)}</Select>
        </Field>
      </div>

      <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: C.muted, marginBottom: 10 }}>ALLOCAZIONE AUTOMATICA (FIFO)</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {allocations.map((a, i) => <div key={i} style={{ padding: 12, background: a.shortfall > 0 ? C.terraWash : C.paperSoft, border: `1px solid ${a.shortfall > 0 ? C.terra : C.ruleSoft}`, borderRadius: 2 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 6, height: 6, background: cats[a.item?.category]?.color, borderRadius: 6 }} />
              <span className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{a.item?.name}</span>
              <span className="f-mono" style={{ fontSize: 11, color: C.muted }}>richiesti {a.needed.toFixed(3)} {a.unit}</span>
            </div>
            {a.shortfall > 0 && <Badge status="urgente" size="sm">MANCA {a.shortfall.toFixed(2)}</Badge>}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {a.allocation.length === 0 ? <span className="f-body" style={{ fontSize: 11, color: C.terra, fontStyle: "italic" }}>Nessun lotto disponibile</span>
            : a.allocation.map((al, j) => <span key={j} className="f-mono" style={{ fontSize: 10, padding: "3px 8px", background: C.white, color: C.teal, letterSpacing: "0.08em", borderRadius: 2 }}>{al.lotId}: -{al.qty.toFixed(3)}</span>)}
          </div>
        </div>)}
      </div>

      {!anyShortfall && <div style={{ marginTop: 20, padding: 14, background: C.oliveWash, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div className="f-mono" style={{ fontSize: 10, color: C.oliveDeep, letterSpacing: "0.15em" }}>COSTO MATERIA PRIMA</div>
          <div className="f-display" style={{ fontSize: 22, color: C.oliveDeep, fontWeight: 600 }}>{fmt.eur(totalCost)}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="f-mono" style={{ fontSize: 10, color: C.oliveDeep, letterSpacing: "0.15em" }}>PER PORZIONE</div>
          <div className="f-mono" style={{ fontSize: 16, color: C.oliveDeep, fontWeight: 600 }}>{fmt.eur(totalCost / servings)}</div>
        </div>
      </div>}
    </div>
  </Modal>;
}


// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      TRACCIABILITÀ                                         ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function Tracciabilita({ state, setState, onNavigate, initial }) {
  const [mode, setMode] = useState(initial?.koId ? "backward" : "forward");
  const [selectedLotId, setSelectedLotId] = useState(initial?.lotId || state.lots[0]?.id);
  const [selectedKoId, setSelectedKoId] = useState(initial?.koId || state.kitchenOutputs[0]?.id);

  return <div className="fade-up">
    <PageHeader kicker="Dal pescato al piatto · Dal piatto al pescato" title="Tracciabilità end-to-end"
      subtitle="Segui la genealogia completa di ogni lotto. In avanti: da un lotto ai piatti serviti. All'indietro: da un piatto risali alla barca di pesca." />

    <Tabs value={mode} onChange={setMode} tabs={[
      { id: "forward", label: "Forward — dal lotto ai piatti", icon: ArrowRight },
      { id: "backward", label: "Backward — dal piatto al pescato", icon: ChevronLeft },
    ]} />

    {mode === "forward" ? <div>
      <div style={{ marginBottom: 20 }}>
        <Field label="Seleziona lotto di origine">
          <Select value={selectedLotId} onChange={e => setSelectedLotId(e.target.value)}>
            {state.lots.map(l => { const it = engine.getItems(state).find(i => i.id === l.itemId); return <option key={l.id} value={l.id}>{l.id} · {it?.name} · {l.status}</option>; })}
          </Select>
        </Field>
      </div>
      <ForwardTrace lotId={selectedLotId} state={state} onNavigate={onNavigate} />
    </div> : <div>
      <div style={{ marginBottom: 20 }}>
        <Field label="Seleziona servizio cucina">
          <Select value={selectedKoId} onChange={e => setSelectedKoId(e.target.value)}>
            {/* FIX BLOCKER 2: nomi piatti da dishesReal (state.dishes e' vuoto). */}
            {state.kitchenOutputs.map(ko => { const d = (state.dishesReal || []).find(d => d.id === ko.dishId); return <option key={ko.id} value={ko.id}>{fmt.date(ko.date)} · {d?.nome || d?.name} · {ko.servings} porz.</option>; })}
          </Select>
        </Field>
      </div>
      <BackwardTrace koId={selectedKoId} state={state} onNavigate={onNavigate} />
    </div>}
  </div>;
}

function ForwardTrace({ lotId, state, onNavigate }) {
  const lot = state.lots.find(l => l.id === lotId);
  if (!lot) return null;
  const item = engine.getItems(state).find(i => i.id === lot.itemId);
  const { children, kitchenUse } = engine.traceForward(state, lotId);

  return <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 24, borderRadius: 2 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 16, background: C.tealWash, border: `2px solid ${C.teal}`, borderRadius: 2, marginBottom: 20 }}>
      <div>
        <div className="f-mono" style={{ fontSize: 10, color: C.teal, letterSpacing: "0.2em", marginBottom: 4 }}>ORIGINE</div>
        <div className="f-display" style={{ fontSize: 20, color: C.ink, fontWeight: 500 }}>{item?.name}</div>
        <div className="f-mono" style={{ fontSize: 12, color: C.inkSoft, marginTop: 2 }}>{lot.id} · {lot.qtyInitial} {lot.unit} iniziali</div>
      </div>
      <Btn variant="ghost" size="sm" icon={ChevronRight} onClick={() => onNavigate({ view: "lot", lotId })}>Dettaglio</Btn>
    </div>

    {children.length > 0 && <div style={{ marginLeft: 30, marginBottom: 20, position: "relative" }}>
      <div style={{ position: "absolute", left: -20, top: -20, bottom: 0, width: 2, background: C.rule }} />
      <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, marginBottom: 10 }}>→ LOTTI FIGLI ({children.length})</div>
      {children.map(c => {
        const cItem = engine.getItems(state).find(i => i.id === c.itemId);
        const cUse = engine.traceForward(state, c.id).kitchenUse;
        return <div key={c.id} style={{ marginBottom: 12, position: "relative" }}>
          <div style={{ position: "absolute", left: -20, top: 20, width: 20, height: 2, background: C.rule }} />
          <button onClick={() => onNavigate({ view: "lot", lotId: c.id })} className="tx" style={{ display: "flex", alignItems: "center", gap: 14, padding: 12, background: C.paperSoft, border: `1px solid ${C.ruleSoft}`, borderRadius: 2, cursor: "pointer", width: "100%", textAlign: "left", fontFamily: "inherit" }}>
            <Package size={16} color={C.plum} />
            <div>
              <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{cItem?.name} <span className="f-mono" style={{ fontSize: 11, color: C.muted }}>({c.id})</span></div>
              <div className="f-mono" style={{ fontSize: 11, color: C.muted }}>{c.qtyInitial} {c.unit} · creato {fmt.date(c.createdAt)} · {c.status}</div>
            </div>
          </button>
          {cUse.length > 0 && <div style={{ marginLeft: 30, marginTop: 8 }}>
            {cUse.map(ko => {
              const d = (state.dishesReal || []).find(dd => dd.id === ko.dishId); // FIX BLOCKER 2
              const usedQty = (ko.consumedLots || []).find(cl => cl.lotId === c.id)?.qty || 0;
              return <div key={ko.id} style={{ padding: 10, background: C.terraWash, borderRadius: 2, marginBottom: 6, display: "flex", alignItems: "center", gap: 10 }}>
                <Utensils size={14} color={C.terra} />
                <div style={{ flex: 1 }}>
                  <div className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>{d?.nome || d?.name}</div>
                  <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>{fmt.date(ko.date)} · {ko.servings} porz. · usati {usedQty.toFixed(2)} {c.unit}</div>
                </div>
              </div>;
            })}
          </div>}
        </div>;
      })}
    </div>}

    {kitchenUse.length > 0 && <div style={{ marginLeft: 30 }}>
      <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, marginBottom: 10 }}>→ USATO DIRETTAMENTE IN CUCINA</div>
      {kitchenUse.map(ko => {
        const d = (state.dishesReal || []).find(dd => dd.id === ko.dishId); // FIX BLOCKER 2
        const usedQty = (ko.consumedLots || []).find(cl => cl.lotId === lot.id)?.qty || 0;
        return <div key={ko.id} style={{ padding: 12, background: C.terraWash, borderRadius: 2, marginBottom: 6, display: "flex", alignItems: "center", gap: 10 }}>
          <Utensils size={14} color={C.terra} />
          <div style={{ flex: 1 }}>
            <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{d?.nome || d?.name}</div>
            <div className="f-mono" style={{ fontSize: 11, color: C.muted }}>{fmt.date(ko.date)} · {ko.servings} porzioni · consumati {usedQty.toFixed(2)} {lot.unit}</div>
          </div>
        </div>;
      })}
    </div>}

    {children.length === 0 && kitchenUse.length === 0 && <EmptyState icon={GitBranch} title="Nessuna tracciabilità in avanti" body="Questo lotto non ha ancora lotti figli né è stato usato in cucina" />}
  </div>;
}

function BackwardTrace({ koId, state, onNavigate }) {
  const trace = engine.traceBackward(state, koId);
  if (!trace) return null;
  const { ko, dish, lots } = trace;

  return <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 24, borderRadius: 2 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 16, background: C.terraWash, border: `2px solid ${C.terra}`, borderRadius: 2, marginBottom: 24 }}>
      <div>
        <div className="f-mono" style={{ fontSize: 10, color: C.terra, letterSpacing: "0.2em", marginBottom: 4 }}>PIATTO SERVITO</div>
        <div className="f-display" style={{ fontSize: 20, color: C.ink, fontWeight: 500 }}>{dish?.nome || dish?.name}</div>
        <div className="f-mono" style={{ fontSize: 12, color: C.inkSoft, marginTop: 2 }}>{fmt.dateFull(ko.date)} · {ko.servings} porzioni · {ko.operator}</div>
      </div>
      <Utensils size={28} color={C.terra} />
    </div>

    {lots.map((l, idx) => <div key={idx} style={{ marginBottom: 18, padding: 14, background: C.paperSoft, border: `1px solid ${C.ruleSoft}`, borderRadius: 2 }}>
      <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", marginBottom: 10 }}>INGREDIENTE {idx + 1} · {l.consumedQty.toFixed(2)} {l.chain[l.chain.length - 1].unit}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {l.chain.map((node, i) => {
          const item = engine.getItems(state).find(it => it.id === node.itemId);
          return <React.Fragment key={node.id}>
            <button onClick={() => onNavigate({ view: "lot", lotId: node.id })} className="tx" style={{ padding: 10, background: C.white, border: `1px solid ${i === 0 ? C.teal : C.rule}`, borderRadius: 2, textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}>
              <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em" }}>{i === 0 ? "ORIGINE" : i === l.chain.length - 1 ? "USATO" : `FASE ${i}`}</div>
              <div className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>{item?.name}</div>
              <div className="f-mono" style={{ fontSize: 11, color: C.teal }}>{node.id}</div>
            </button>
            {i < l.chain.length - 1 && <ArrowRight size={14} color={C.muted} />}
          </React.Fragment>;
        })}
      </div>
      {l.supplier && <div style={{ marginTop: 12, padding: 10, background: C.tealWash, borderRadius: 2, display: "flex", alignItems: "center", gap: 10 }}>
        <Truck size={15} color={C.teal} />
        <div>
          <div className="f-body" style={{ fontSize: 12, color: C.tealDeep, fontWeight: 500 }}>Origine fornitore: {l.supplier.name}</div>
          <div className="f-mono" style={{ fontSize: 10, color: C.tealDeep, opacity: 0.8 }}>DDT {l.receipt?.ddtNumber} · {fmt.dateFull(l.receipt?.date)}</div>
        </div>
      </div>}
    </div>)}
  </div>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      ANAGRAFICHE                                           ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function Anagrafiche({ state, setState, initial }) {
  const [tab, setTab] = useState(initial?.tab || "suppliers");
  return <div className="fade-up">
    <PageHeader kicker="Dati base del sistema" title="Anagrafiche"
      subtitle="Gestisci fornitori, articoli, operatori e benchmark KPI." />
    <Tabs value={tab} onChange={setTab} tabs={[
      { id: "suppliers", label: "Fornitori", icon: Truck, count: state.suppliers.length },
      { id: "items", label: "Articoli", icon: Package, count: engine.getItems(state).length },
      { id: "users", label: "Operatori", icon: Users, count: state.users.length },
      { id: "benchmark", label: "Benchmark KPI", icon: Zap },
    ]} />
    {tab === "suppliers" && <SuppliersTab state={state} setState={setState} />}
    {tab === "items" && <ItemsTab state={state} setState={setState} />}
    {tab === "users" && <UsersTab state={state} setState={setState} />}
    {tab === "benchmark" && <BenchmarkKPITab state={state} setState={setState} />}
  </div>;
}

// Editor benchmark KPI: target/scarso/ottimo per ogni indicatore
function BenchmarkKPITab({ state, setState }) {
  const benchmarks = state.benchmarksKPI || {};
  const toast = useToast();

  const updateBench = (key, field, value) => {
    const v = parseFloat(value);
    if (isNaN(v)) return;
    setState({
      ...state,
      benchmarksKPI: {
        ...benchmarks,
        [key]: { ...benchmarks[key], [field]: v },
      },
    });
  };
  const updateMenuMin = (value) => {
    const v = parseInt(value);
    if (isNaN(v) || v < 1 || v > 4) return;
    setState({
      ...state,
      benchmarksKPI: { ...benchmarks, menuCompletoMinCat: v },
    });
  };

  const resetDefaults = () => {
    if (!window.confirm("Ripristinare i benchmark di default? I tuoi valori personalizzati andranno persi.")) return;
    // Tolgo benchmarksKPI così il SEED riempie il default
    const newState = { ...state };
    delete newState.benchmarksKPI;
    setState(newState);
    toast({ type: "info", message: "Benchmark ripristinati ai default" });
  };

  // Famiglie come nell'AnalyticsKPI
  const families = {
    "Penetrazione coperto (qty per coperto)": ["dolciPerCop", "contorniPerCop", "viniPerCop", "caffePerCop", "antipastiPerCop", "primiPerCop", "secondiPerCop"],
    "Mix di vendita (% sul ricavo totale)": ["mixVini", "mixBar", "mixDolci", "mixCaffe", "mixFoodBev"],
    "Scontrino & Cross-sell": ["ticketCop", "ticketCopPranzo", "ticketCopCena", "ticketTavolo", "portatePerCop", "articoliPerScn", "pctMenuCompleto"],
    "Performance fascia": ["coveriPranzoMedia", "coveriCenaMedia"],
  };

  return <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
    <div style={{ padding: 14, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, fontSize: 12, color: C.tealDeep, lineHeight: 1.6 }}>
      <strong>I tuoi benchmark personalizzati</strong><br />
      Per ogni KPI modifichi 3 soglie: <strong>scarso</strong> (sotto = rosso ▼▼), <strong>target</strong> (tuo obiettivo, sopra = verde ▲) e <strong>ottimo</strong> (sopra = verde scuro ▲▲).
      Il sistema usa queste soglie per i semafori. Se metti tutto a zero il KPI non viene calcolato.
    </div>

    {/* Soglia menu completo */}
    <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 16 }}>
      <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: C.muted, marginBottom: 10 }}>SOGLIA "MENU COMPLETO"</div>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ flex: 1 }}>
          <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>Quante categorie su 4 servono per considerare uno scontrino "completo"?</div>
          <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>Le 4 categorie chiave sono: antipasti, primi, secondi, dolci. Default: 3 su 4.</div>
        </div>
        <Input type="number" min="1" max="4" step="1" value={benchmarks.menuCompletoMinCat ?? 3} onChange={e => updateMenuMin(e.target.value)} style={{ width: 70, textAlign: "center", fontWeight: 600 }} />
        <span className="f-mono" style={{ color: C.muted, fontSize: 12 }}>su 4</span>
      </div>
    </div>

    {Object.keys(families).map(fam => <div key={fam}>
      <SectionHeader title={fam} />
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 0.7fr 0.7fr 0.7fr", padding: "10px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.2em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
          <span>INDICATORE</span>
          <span style={{ textAlign: "center" }}>SCARSO ▼▼</span>
          <span style={{ textAlign: "center" }}>TARGET ▲</span>
          <span style={{ textAlign: "center" }}>OTTIMO ▲▲</span>
        </div>
        {families[fam].map(key => {
          const b = benchmarks[key];
          if (!b) return null;
          const isPercent = key.startsWith("mix") || key.startsWith("pct");
          const isEur = key.startsWith("ticket");
          const unit = isPercent ? "%" : isEur ? "€" : "";
          const step = isEur ? "1" : isPercent ? "1" : "0.05";
          return <div key={key} style={{ display: "grid", gridTemplateColumns: "2fr 0.7fr 0.7fr 0.7fr", padding: "8px 14px", borderTop: `1px solid ${C.ruleSoft}`, alignItems: "center", gap: 8 }}>
            <span className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>{b.label}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}>
              <Input type="number" step={step} value={b.scarso} onChange={e => updateBench(key, "scarso", e.target.value)} style={{ width: 70, textAlign: "right", padding: "4px 8px", fontSize: 12 }} />
              <span className="f-mono" style={{ fontSize: 10, color: C.muted, width: 12 }}>{unit}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}>
              <Input type="number" step={step} value={b.target} onChange={e => updateBench(key, "target", e.target.value)} style={{ width: 70, textAlign: "right", padding: "4px 8px", fontSize: 12, fontWeight: 600 }} />
              <span className="f-mono" style={{ fontSize: 10, color: C.muted, width: 12 }}>{unit}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}>
              <Input type="number" step={step} value={b.ottimo} onChange={e => updateBench(key, "ottimo", e.target.value)} style={{ width: 70, textAlign: "right", padding: "4px 8px", fontSize: 12 }} />
              <span className="f-mono" style={{ fontSize: 10, color: C.muted, width: 12 }}>{unit}</span>
            </div>
          </div>;
        })}
      </div>
    </div>)}

    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
      <Btn variant="ghost" onClick={resetDefaults} icon={X}>Ripristina default</Btn>
    </div>
  </div>;
}

function SuppliersTab({ state, setState }) {
  const [editing, setEditing] = useState(null);
  const toast = useToast();

  const del = (id) => {
    const ordersUsing = state.purchaseOrders.filter(p => p.supplierId === id).length;
    const invoicesUsing = state.invoices.filter(i => i.supplierId === id).length;
    const warning = ordersUsing + invoicesUsing > 0
      ? `Attenzione: questo fornitore ha ${ordersUsing} ordini e ${invoicesUsing} fatture collegati.\nEliminarlo potrebbe rendere incoerenti i dati storici. Continuare?`
      : "Eliminare questo fornitore?";
    if (!confirm(warning)) return;
    setState({ ...state, suppliers: state.suppliers.filter(s => s.id !== id) });
    toast({ type: "info", message: "Fornitore eliminato" });
  };

  return <div>
    {editing && <SupplierEditor supplier={editing} state={state} setState={setState} onClose={() => setEditing(null)} />}

    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
      <Btn variant="accent" icon={Plus} onClick={() => setEditing({})}>Nuovo fornitore</Btn>
    </div>

    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {state.suppliers.length === 0 ? <EmptyState icon={Truck} title="Nessun fornitore" body="Aggiungi il primo fornitore per iniziare ad acquistare" action={<Btn variant="accent" icon={Plus} onClick={() => setEditing({})}>Nuovo fornitore</Btn>} />
      : state.suppliers.map(s => {
        const reps = s.representatives || [];
        const totalSpent = state.invoices.filter(i => i.supplierId === s.id).reduce((sum, i) => sum + i.amount, 0);
        const ordersCount = state.purchaseOrders.filter(p => p.supplierId === s.id).length;
        return <div key={s.id} style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 16, borderRadius: 2, display: "flex", alignItems: "flex-start", gap: 16 }}>
          <div style={{ width: 42, height: 42, background: cats[s.category]?.color + "22", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 2, flexShrink: 0 }}>
            <Truck size={18} color={cats[s.category]?.color} strokeWidth={1.5} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span className="f-body" style={{ fontSize: 14, color: C.ink, fontWeight: 500 }}>{s.name}</span>
              <Badge>{cats[s.category]?.label || s.category}</Badge>
              {reps.length > 0 && <span className="f-mono" style={{ fontSize: 10, color: C.teal, padding: "1px 6px", background: C.tealWash, borderRadius: 2, letterSpacing: "0.1em" }}>{reps.length} CONTATTI</span>}
            </div>
            <div className="f-body" style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{s.address}</div>
            <div style={{ display: "flex", gap: 16, marginTop: 6, flexWrap: "wrap" }}>
              {s.vat && <span className="f-mono" style={{ fontSize: 11, color: C.inkSoft }}>P.IVA {s.vat}</span>}
              {s.phone && <span className="f-mono" style={{ fontSize: 11, color: C.inkSoft }}>{s.phone}</span>}
              {s.email && <span className="f-mono" style={{ fontSize: 11, color: C.inkSoft }}>{s.email}</span>}
              <span className="f-mono" style={{ fontSize: 11, color: C.teal }}>Pag. {s.paymentTerms || 30}gg</span>
              <span className="f-mono" style={{ fontSize: 11, color: C.muted }}>{ordersCount} ordini · {fmt.eurShort(totalSpent)} fatturato</span>
            </div>
            {s.notes && <div className="f-body" style={{ fontSize: 12, color: C.muted, fontStyle: "italic", marginTop: 4 }}>📝 {s.notes}</div>}
            {reps.length > 0 && <div style={{ marginTop: 10, padding: 8, background: C.paperDeep, borderRadius: 2, display: "flex", flexDirection: "column", gap: 4 }}>
              {reps.map((r, i) => <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11 }}>
                <User size={11} color={C.muted} />
                <span className="f-body" style={{ color: C.ink, fontWeight: 500 }}>{r.name}</span>
                {r.role && <span className="f-mono" style={{ color: C.muted, fontSize: 10 }}>{r.role}</span>}
                {r.whatsapp && <a href={`https://wa.me/${r.whatsapp.replace(/[^+\d]/g, "")}`} target="_blank" rel="noopener noreferrer" className="f-mono" style={{ color: C.olive, textDecoration: "none", fontSize: 10, display: "flex", alignItems: "center", gap: 3 }}><MessageSquare size={10} /> WhatsApp</a>}
                {r.phone && <span className="f-mono" style={{ color: C.inkSoft, fontSize: 10 }}>{r.phone}</span>}
                {r.email && <span className="f-mono" style={{ color: C.inkSoft, fontSize: 10 }}>{r.email}</span>}
              </div>)}
            </div>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
            <IconBtn icon={Settings} size={14} title="Modifica" onClick={() => setEditing(s)} />
            <IconBtn icon={Trash2} size={14} title="Elimina" onClick={() => del(s.id)} />
          </div>
        </div>;
      })}
    </div>
  </div>;
}

function SupplierEditor({ supplier, state, setState, onClose }) {
  const isNew = !supplier.id;
  const [s, setS] = useState(isNew ? {
    name: "", category: "pesce", vat: "", phone: "", email: "", address: "", notes: "", paymentTerms: 30, representatives: [],
  } : { ...supplier, representatives: supplier.representatives || [] });
  const toast = useToast();

  const save = () => {
    if (!s.name.trim()) { toast({ type: "error", message: "Inserisci nome fornitore" }); return; }
    const clean = { ...s, name: s.name.trim() };
    if (isNew) clean.id = uid("s_");
    setState({
      ...state,
      suppliers: isNew ? [...state.suppliers, clean] : state.suppliers.map(x => x.id === s.id ? clean : x),
    });
    toast({ type: "success", title: isNew ? "Fornitore creato" : "Fornitore aggiornato", message: s.name.trim() });
    onClose();
  };

  const addRep = () => setS({ ...s, representatives: [...(s.representatives || []), { name: "", role: "", phone: "", whatsapp: "", email: "" }] });
  const updateRep = (i, patch) => setS({ ...s, representatives: s.representatives.map((r, j) => j === i ? { ...r, ...patch } : r) });
  const delRep = (i) => setS({ ...s, representatives: s.representatives.filter((_, j) => j !== i) });

  return <Modal onClose={onClose} maxWidth={740} subtitle={isNew ? "Nuovo" : "Modifica"} title={isNew ? "Fornitore" : s.name}
    footer={<><Btn variant="ghost" onClick={onClose}>Annulla</Btn><Btn variant="accent" icon={CheckCircle2} onClick={save}>Salva</Btn></>}>
    <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
        <Field label="Ragione sociale" required><Input value={s.name} onChange={e => setS({ ...s, name: e.target.value })} placeholder="es. Ittica Marchigiana S.r.l." autoFocus={isNew} /></Field>
        <Field label="Categoria merceologica" required>
          <Select value={s.category} onChange={e => setS({ ...s, category: e.target.value })}>
            {Object.entries(cats).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </Select>
        </Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
        <Field label="Partita IVA"><Input value={s.vat} onChange={e => setS({ ...s, vat: e.target.value })} placeholder="IT..." /></Field>
        <Field label="Telefono aziendale"><Input value={s.phone} onChange={e => setS({ ...s, phone: e.target.value })} placeholder="+39 071..." /></Field>
        <Field label="Email ordini"><Input type="email" value={s.email} onChange={e => setS({ ...s, email: e.target.value })} placeholder="ordini@..." /></Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
        <Field label="Indirizzo"><Input value={s.address} onChange={e => setS({ ...s, address: e.target.value })} placeholder="Via, città" /></Field>
        <Field label="Termini pagamento (gg)"><Input type="number" value={s.paymentTerms} onChange={e => setS({ ...s, paymentTerms: parseInt(e.target.value) || 30 })} /></Field>
      </div>

      <Field label="Note" hint="Consegne, minimo d'ordine, preferenze"><Textarea value={s.notes} onChange={e => setS({ ...s, notes: e.target.value })} rows={2} placeholder="es. Consegna mar/gio/sab mattino, min ord. 50kg" /></Field>

      <div style={{ paddingTop: 14, borderTop: `1px solid ${C.ruleSoft}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div>
            <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: C.muted }}>RAPPRESENTANTI / CONTATTI DIRETTI</div>
            <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Aggiungi i contatti delle persone con cui ordini direttamente. Il numero WhatsApp viene usato per inviare ordini con un click.</div>
          </div>
          <Btn variant="ghost" size="xs" icon={Plus} onClick={addRep}>Aggiungi contatto</Btn>
        </div>

        {(s.representatives || []).length === 0 ? <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2, textAlign: "center" }}>
          <div className="f-body" style={{ fontSize: 11, color: C.muted }}>Nessun contatto. Aggiungi il primo per poter inviare ordini via WhatsApp.</div>
        </div> : (s.representatives || []).map((r, i) => <div key={i} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1.2fr 1.3fr auto", gap: 8, alignItems: "center", marginBottom: 6, padding: 8, background: C.paperDeep, borderRadius: 2 }}>
          <Input value={r.name} onChange={e => updateRep(i, { name: e.target.value })} placeholder="Nome" style={{ fontSize: 12, padding: "6px 10px" }} />
          <Input value={r.role} onChange={e => updateRep(i, { role: e.target.value })} placeholder="Ruolo (agente...)" style={{ fontSize: 12, padding: "6px 10px" }} />
          <Input value={r.phone} onChange={e => updateRep(i, { phone: e.target.value })} placeholder="Tel fisso" style={{ fontSize: 12, padding: "6px 10px" }} />
          <Input value={r.whatsapp} onChange={e => updateRep(i, { whatsapp: e.target.value })} placeholder="WhatsApp +39..." style={{ fontSize: 12, padding: "6px 10px" }} />
          <Input value={r.email} onChange={e => updateRep(i, { email: e.target.value })} placeholder="email" style={{ fontSize: 12, padding: "6px 10px" }} />
          <IconBtn icon={Trash2} size={13} onClick={() => delRep(i)} />
        </div>)}
      </div>

    </div>
  </Modal>;
}

function ItemsTab({ state, setState }) {
  return <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 0.7fr 0.7fr 2fr 0.8fr", padding: "10px 16px", background: C.paperDeep, fontSize: 10, color: C.muted, letterSpacing: "0.2em", fontFamily: "JetBrains Mono", textTransform: "uppercase" }}>
      <span>Articolo</span><span>Categoria</span><span>Min</span><span>Shelf-life</span><span>Fornitore pref.</span><span style={{ textAlign: "right" }}>Stock</span>
    </div>
    {engine.getItems(state).map(it => {
      const stock = engine.itemStock(state, it.id);
      const pref = state.suppliers.find(s => s.id === it.preferredSupplier);
      return <div key={it.id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 0.7fr 0.7fr 2fr 0.8fr", padding: "10px 16px", borderTop: `1px solid ${C.ruleSoft}`, alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 6, height: 6, background: cats[it.category]?.color, borderRadius: 6 }} />
          <span className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>{it.name}</span>
          {it.allergens?.length > 0 && <span className="f-mono" style={{ fontSize: 8, color: C.amber, background: C.amberWash, padding: "1px 4px", borderRadius: 2, letterSpacing: "0.1em" }}>ALLERGENI</span>}
        </div>
        <span className="f-body" style={{ fontSize: 12, color: C.inkSoft }}>{cats[it.category]?.label}</span>
        <span className="f-mono" style={{ fontSize: 12, color: C.inkSoft }}>{it.minStock} {it.unit}</span>
        <span className="f-mono" style={{ fontSize: 12, color: C.inkSoft }}>{it.shelfLifeDays}g</span>
        <span className="f-body" style={{ fontSize: 12, color: C.inkSoft }}>{pref?.name || "—"}</span>
        <span className="f-mono" style={{ fontSize: 13, color: stock < it.minStock ? C.terra : C.ink, fontWeight: 600, textAlign: "right" }}>{stock.toFixed(1)}</span>
      </div>;
    })}
  </div>;
}

function UsersTab({ state, setState }) {
  const [editing, setEditing] = useState(null); // user being edited, null = none, {} = new
  const toast = useToast();

  const del = (id) => {
    if (!confirm("Eliminare questo operatore?")) return;
    setState({ ...state, users: state.users.filter(u => u.id !== id) });
    toast({ type: "info", message: "Operatore eliminato" });
  };

  const toggleActive = (id) => {
    setState({ ...state, users: state.users.map(u => u.id === id ? { ...u, active: !u.active } : u) });
  };

  const byRole = { Laboratorio: [], Magazzino: [], Cucina: [], Amministrazione: [] };
  state.users.forEach(u => { if (byRole[u.role]) byRole[u.role].push(u); });

  return <div>
    {editing && <UserEditor user={editing} state={state} setState={setState} onClose={() => setEditing(null)} />}
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
      <Btn variant="accent" icon={Plus} onClick={() => setEditing({})}>Nuovo operatore</Btn>
    </div>

    {Object.entries(byRole).map(([role, users]) => users.length > 0 && <div key={role} style={{ marginBottom: 24 }}>
      <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: roleColors[role], textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 10, height: 10, background: roleColors[role], borderRadius: 10 }} />{role} · {users.length}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        {users.map(u => <div key={u.id} className="tx" style={{ background: u.active === false ? C.paperDeep : C.white, border: `1px solid ${u.active === false ? C.ruleSoft : C.rule}`, padding: 14, borderRadius: 2, display: "flex", alignItems: "center", gap: 12, opacity: u.active === false ? 0.6 : 1 }}>
          <Avatar name={u.name} role={u.role} size={40} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</div>
            <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</div>
            {u.active === false && <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em", marginTop: 3 }}>DISATTIVO</div>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <IconBtn icon={Settings} size={14} title="Modifica" onClick={() => setEditing(u)} />
            <IconBtn icon={u.active === false ? CheckCircle2 : Circle} size={14} title={u.active === false ? "Riattiva" : "Disattiva"} onClick={() => toggleActive(u.id)} />
            <IconBtn icon={Trash2} size={14} title="Elimina" onClick={() => del(u.id)} />
          </div>
        </div>)}
      </div>
    </div>)}

    {state.users.length === 0 && <EmptyState icon={Users} title="Nessun operatore" body="Aggiungi il primo membro del team" action={<Btn variant="accent" icon={Plus} onClick={() => setEditing({})}>Nuovo operatore</Btn>} />}
  </div>;
}

function UserEditor({ user, state, setState, onClose }) {
  const isNew = !user.id;
  const [u, setU] = useState(isNew
    ? { name: "", role: "Laboratorio", email: "", active: true,
        haccpCertNumber: null, haccpCertIssuedAt: null, haccpCertExpiresAt: null, haccpCertLevel: null, haccpCertIssuer: null,
        hourlyGross: null, hourlyFullyLoaded: null, mansione: null,
        // Fase M1 — Contratto + Mensa
        contractType: null, weeklyHours: null, workDaysPerWeek: null, ccnl: null, livello: null,
        mealEntitlementHours: 6, mealsPerDayMax: 1, mealPreferenceConsume: true,
        mealDietaryRestrictions: null, mealValueDaily: 5.00 }
    : { haccpCertNumber: null, haccpCertIssuedAt: null, haccpCertExpiresAt: null, haccpCertLevel: null, haccpCertIssuer: null,
        hourlyGross: null, hourlyFullyLoaded: null, mansione: null,
        // Fase M1 — fallback se utente pre-migration nel modal
        contractType: null, weeklyHours: null, workDaysPerWeek: null, ccnl: null, livello: null,
        mealEntitlementHours: 6, mealsPerDayMax: 1, mealPreferenceConsume: true,
        mealDietaryRestrictions: null, mealValueDaily: 5.00,
        ...user });
  const toast = useToast();

  const save = () => {
    if (!u.name.trim()) { toast({ type: "error", message: "Inserisci nome e cognome" }); return; }
    if (!u.email.trim()) { toast({ type: "error", message: "Inserisci email" }); return; }
    // Reg. CE 852/2004 All. II Cap. XII: scadenza attestato obbligatoria se attestato presente.
    if (u.haccpCertNumber && !u.haccpCertExpiresAt) {
      toast({ type: "error", message: "Se inserisci un attestato HACCP devi indicarne la scadenza" }); return;
    }
    // Normalizza stringhe vuote a null per coerenza
    const normalized = {
      ...u,
      name: u.name.trim(),
      email: u.email.trim(),
      haccpCertNumber: u.haccpCertNumber?.trim?.() || null,
      haccpCertIssuedAt: u.haccpCertIssuedAt || null,
      haccpCertExpiresAt: u.haccpCertExpiresAt || null,
      haccpCertLevel: u.haccpCertLevel || null,
      haccpCertIssuer: u.haccpCertIssuer?.trim?.() || null,
      hourlyGross: (typeof u.hourlyGross === "number" && Number.isFinite(u.hourlyGross)) ? u.hourlyGross : null,
      hourlyFullyLoaded: (typeof u.hourlyFullyLoaded === "number" && Number.isFinite(u.hourlyFullyLoaded)) ? u.hourlyFullyLoaded : null,
      mansione: u.mansione?.trim?.() || null,
      // Fase M1 — Contratto
      contractType: u.contractType || null,
      weeklyHours: (typeof u.weeklyHours === "number" && Number.isFinite(u.weeklyHours)) ? u.weeklyHours : null,
      workDaysPerWeek: (typeof u.workDaysPerWeek === "number" && Number.isFinite(u.workDaysPerWeek)) ? u.workDaysPerWeek : null,
      ccnl: u.ccnl?.trim?.() || null,
      livello: u.livello?.trim?.() || null,
      // Fase M1 — Mensa (con default sensati se invalidi)
      mealEntitlementHours: (typeof u.mealEntitlementHours === "number" && Number.isFinite(u.mealEntitlementHours) && u.mealEntitlementHours >= 0) ? u.mealEntitlementHours : 6,
      mealsPerDayMax: (u.mealsPerDayMax === 1 || u.mealsPerDayMax === 2) ? u.mealsPerDayMax : 1,
      mealPreferenceConsume: u.mealPreferenceConsume !== false,
      mealDietaryRestrictions: u.mealDietaryRestrictions?.trim?.() || null,
      mealValueDaily: (typeof u.mealValueDaily === "number" && Number.isFinite(u.mealValueDaily) && u.mealValueDaily >= 0) ? u.mealValueDaily : 5.00,
    };

    // Fase M1 — Soft validation: warning non bloccanti
    if (normalized.contractType === "fullTime" && normalized.weeklyHours !== null && normalized.weeklyHours < 36) {
      toast({ type: "info", title: "Verifica contratto", message: "Tempo pieno tipicamente >=36h settimanali" });
    }
    if (normalized.weeklyHours !== null && normalized.weeklyHours > 48) {
      toast({ type: "info", title: "Soglia oraria", message: "Soglia massima settimanale è 48h (D.Lgs 66/2003)" });
    }
    if (normalized.mealValueDaily > 8) {
      toast({ type: "info", title: "Valore pasto alto", message: "Verifica con commercialista per esenzione art. 51 TUIR" });
    }
    if (normalized.mealEntitlementHours > 8) {
      toast({ type: "info", title: "Soglia ore alta", message: "CCNL Pubblici Esercizi tipico è 6h continuative" });
    }
    let nextUsers;
    if (isNew) {
      const newUser = { ...normalized, id: uid("u_") };
      nextUsers = [...state.users, newUser];
    } else {
      nextUsers = state.users.map(x => x.id === u.id ? normalized : x);
    }
    setState({
      ...state,
      users: nextUsers,
      auditLog: appendAudit(state, {
        action: isNew ? "user_create" : "user_update",
        entityType: "user",
        entityId: normalized.id || u.id,
        reason: `${normalized.name} (${normalized.role})`,
      }),
    });
    toast({ type: "success", title: isNew ? "Operatore creato" : "Operatore aggiornato", message: normalized.name });
    onClose();
  };

  const roles = ["Laboratorio", "Magazzino", "Cucina", "Amministrazione"];

  // Calcolo automatico €/h fully-loaded suggerito (lordo × 1.45) — solo come hint, non sovrascrive
  const suggestedFullyLoaded = (typeof u.hourlyGross === "number" && u.hourlyGross > 0)
    ? Math.round(u.hourlyGross * 1.45 * 100) / 100
    : null;

  // Alert visivo se attestato scaduto / in scadenza (60gg)
  const certDaysLeft = u.haccpCertExpiresAt ? daysBetween(TODAY, u.haccpCertExpiresAt) : null;
  const certExpired = certDaysLeft !== null && certDaysLeft < 0;
  const certExpiring = certDaysLeft !== null && certDaysLeft >= 0 && certDaysLeft <= 60;

  return <Modal onClose={onClose} maxWidth={620} subtitle={isNew ? "Nuovo" : "Modifica"} title={isNew ? "Operatore" : u.name}
    footer={<><Btn variant="ghost" onClick={onClose}>Annulla</Btn><Btn variant="accent" icon={CheckCircle2} onClick={save}>Salva</Btn></>}>
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, padding: 14, background: C.paperDeep, borderRadius: 2 }}>
        <Avatar name={u.name || "?"} role={u.role} size={52} />
        <div>
          <div className="f-display" style={{ fontSize: 16, color: C.ink, fontWeight: 500 }}>{u.name || "Nuovo operatore"}</div>
          <div className="f-mono" style={{ fontSize: 10, color: roleColors[u.role], letterSpacing: "0.2em", textTransform: "uppercase", marginTop: 2 }}>{u.role}</div>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Field label="Nome e cognome" required>
          <Input value={u.name} onChange={e => setU({ ...u, name: e.target.value })} placeholder="es. Marco Rossi" autoFocus={isNew} />
        </Field>
        <Field label="Ruolo" required>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
            {roles.map(r => {
              const isSel = u.role === r;
              return <button key={r} onClick={() => setU({ ...u, role: r })} className="tx" style={{ padding: "10px 12px", background: isSel ? roleColors[r] : C.white, color: isSel ? C.white : C.ink, border: `1px solid ${isSel ? roleColors[r] : C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                <div className="f-body" style={{ fontSize: 13, fontWeight: 500 }}>{r}</div>
                <div className="f-mono" style={{ fontSize: 9, opacity: 0.7, letterSpacing: "0.15em", marginTop: 2 }}>{r === "Laboratorio" ? "LAVORAZIONI" : r === "Magazzino" ? "INGRESSI E LOTTI" : r === "Cucina" ? "RICHIESTE E PIATTI" : "REPORT"}</div>
              </button>;
            })}
          </div>
        </Field>
        <Field label="Email" required>
          <Input type="email" value={u.email} onChange={e => setU({ ...u, email: e.target.value })} placeholder="nome@bottega.it" />
        </Field>

        {/* Reg. CE 852/2004 All. II Cap. XII — formazione obbligatoria personale */}
        <div style={{ marginTop: 8, paddingTop: 14, borderTop: `1px solid ${C.rule}` }}>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>Attestato HACCP</div>
          <div className="f-body" style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>Reg. CE 852/2004 All. II Cap. XII — obbligatorio per chi manipola alimenti</div>
          {certExpired && <div style={{ background: C.terraWash, border: `1px solid ${C.terra}`, padding: "8px 12px", borderRadius: 2, marginBottom: 12, fontSize: 12, color: C.terra, fontWeight: 500 }}>
            ATTENZIONE: attestato scaduto da {Math.abs(certDaysLeft)} giorni. Sanzione D.Lgs 193/2007 art. 6: €1.000–€6.000.
          </div>}
          {certExpiring && !certExpired && <div style={{ background: C.amberWash, border: `1px solid ${C.amber}`, padding: "8px 12px", borderRadius: 2, marginBottom: 12, fontSize: 12, color: C.amberDeep, fontWeight: 500 }}>
            Attestato in scadenza tra {certDaysLeft} giorni — programmare rinnovo.
          </div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Numero attestato">
              <Input value={u.haccpCertNumber || ""} onChange={e => setU({ ...u, haccpCertNumber: e.target.value })} placeholder="es. HACCP-2024-001234" />
            </Field>
            <Field label="Livello">
              <Select value={u.haccpCertLevel || ""} onChange={e => setU({ ...u, haccpCertLevel: e.target.value || null })}>
                <option value="">—</option>
                <option value="addetto">Addetto</option>
                <option value="responsabile">Responsabile</option>
              </Select>
            </Field>
            <Field label="Data rilascio">
              <Input type="date" value={u.haccpCertIssuedAt || ""} onChange={e => setU({ ...u, haccpCertIssuedAt: e.target.value })} />
            </Field>
            <Field label="Data scadenza" required={!!u.haccpCertNumber}>
              <Input type="date" value={u.haccpCertExpiresAt || ""} onChange={e => setU({ ...u, haccpCertExpiresAt: e.target.value })} />
            </Field>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Ente formatore">
                <Input value={u.haccpCertIssuer || ""} onChange={e => setU({ ...u, haccpCertIssuer: e.target.value })} placeholder="es. ASUR Marche / Confcommercio / ente accreditato" />
              </Field>
            </div>
          </div>
        </div>

        {/* Costo orario — uso interno, riservato. Base per calcolo laborCost dei piatti. */}
        <div style={{ marginTop: 8, paddingTop: 14, borderTop: `1px solid ${C.rule}` }}>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>Costo orario · uso interno</div>
          <div className="f-body" style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>Riservato. Utilizzato per calcolo costo manodopera ordini di lavorazione.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="€/ora lordo (CCNL)">
              <Input type="number" step="0.01" min="0" value={u.hourlyGross ?? ""}
                onChange={e => { const v = parseFloat(e.target.value); setU({ ...u, hourlyGross: Number.isFinite(v) ? v : null }); }}
                inputMode="decimal" lang="it" placeholder="11.50" />
            </Field>
            <Field label="€/ora costo aziendale" hint={suggestedFullyLoaded ? `Tipico: lordo × 1.45 = €${suggestedFullyLoaded.toFixed(2)} (ratei + TFR + INPS + INAIL)` : "Tipico: lordo × 1.45 (ratei + TFR + INPS + INAIL)"}>
              <Input type="number" step="0.01" min="0" value={u.hourlyFullyLoaded ?? ""}
                onChange={e => { const v = parseFloat(e.target.value); setU({ ...u, hourlyFullyLoaded: Number.isFinite(v) ? v : null }); }}
                inputMode="decimal" lang="it" placeholder={suggestedFullyLoaded ? suggestedFullyLoaded.toFixed(2) : "16.68"} />
            </Field>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Mansione">
                <Input value={u.mansione || ""} onChange={e => setU({ ...u, mansione: e.target.value })} placeholder="es. Sfilettatore / Operatore polivalente" />
              </Field>
            </div>
          </div>
        </div>

        {/* Fase M1 — Contratto di lavoro: base per anagrafica mensa/presenze. */}
        <div style={{ marginTop: 8, paddingTop: 14, borderTop: `1px solid ${C.rule}` }}>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>Contratto di lavoro</div>
          <div className="f-body" style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>Tipo contratto, orario settimanale, CCNL applicato.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Tipo contratto">
              <Select value={u.contractType || ""} onChange={e => setU({ ...u, contractType: e.target.value || null })}>
                <option value="">—</option>
                <option value="fullTime">Tempo pieno (40h)</option>
                <option value="partTime">Part-time</option>
                <option value="stagionale">Stagionale</option>
                <option value="extra">Extra/chiamata</option>
                <option value="apprendista">Apprendistato</option>
              </Select>
            </Field>
            <Field label="Ore settimanali">
              <Input type="number" min="0" max="48" step="0.5" value={u.weeklyHours ?? ""}
                onChange={e => { const v = parseFloat(e.target.value); setU({ ...u, weeklyHours: Number.isFinite(v) ? v : null }); }}
                inputMode="decimal" lang="it" placeholder="40" />
            </Field>
            <Field label="Giorni lavorativi/sett">
              <Input type="number" min="1" max="7" step="1" value={u.workDaysPerWeek ?? ""}
                onChange={e => { const v = parseInt(e.target.value, 10); setU({ ...u, workDaysPerWeek: Number.isFinite(v) ? v : null }); }}
                inputMode="numeric" placeholder="6" />
            </Field>
            <Field label="CCNL applicato">
              <Input value={u.ccnl || ""} onChange={e => setU({ ...u, ccnl: e.target.value || null })}
                placeholder="es. Pubblici Esercizi Confcommercio" />
            </Field>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Livello CCNL">
                <Input value={u.livello || ""} onChange={e => setU({ ...u, livello: e.target.value || null })}
                  placeholder="es. IV" />
              </Field>
            </div>
          </div>
        </div>

        {/* Fase M1 — Mensa aziendale art. 51 c. 2 lett. c) TUIR. */}
        {/* Regime fiscale: esenzione totale se il pasto è consumato in locali aziendali. */}
        <div style={{ marginTop: 8, paddingTop: 14, borderTop: `1px solid ${C.rule}` }}>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>Mensa aziendale</div>
          <div className="f-body" style={{ fontSize: 11, color: C.muted, marginBottom: 12 }}>Art. 51 c. 2 lett. c) TUIR — pasto in mensa aziendale esente, non trattato come buono pasto.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Soglia ore qualificante" hint="CCNL Pubblici Esercizi standard: 6 ore continuative">
              <Input type="number" min="0" max="12" step="0.5" value={u.mealEntitlementHours ?? 6}
                onChange={e => { const v = parseFloat(e.target.value); setU({ ...u, mealEntitlementHours: Number.isFinite(v) ? v : 6 }); }}
                inputMode="decimal" lang="it" placeholder="6" />
            </Field>
            <Field label="Max pasti per giorno">
              <Select value={String(u.mealsPerDayMax || 1)} onChange={e => setU({ ...u, mealsPerDayMax: parseInt(e.target.value, 10) || 1 })}>
                <option value="1">1 pasto (turno singolo)</option>
                <option value="2">2 pasti (turno spezzato pranzo+cena)</option>
              </Select>
            </Field>
            <Field label="Valore convenzionale pasto (€/giorno)" hint="Costo medio MP del pasto staff (default €5)">
              <Input type="number" min="0" max="20" step="0.01" value={u.mealValueDaily ?? 5}
                onChange={e => { const v = parseFloat(e.target.value); setU({ ...u, mealValueDaily: Number.isFinite(v) ? v : 5 }); }}
                inputMode="decimal" lang="it" placeholder="5.00" />
            </Field>
            <Field label="Preferenza consumo">
              <Select value={u.mealPreferenceConsume === false ? "no" : "yes"} onChange={e => setU({ ...u, mealPreferenceConsume: e.target.value === "yes" })}>
                <option value="yes">Consuma in mensa (default)</option>
                <option value="no">Non consuma (allergie, scelte, ecc.)</option>
              </Select>
            </Field>
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Restrizioni alimentari">
                <Textarea value={u.mealDietaryRestrictions || ""}
                  onChange={e => setU({ ...u, mealDietaryRestrictions: e.target.value || null })}
                  rows={2} maxLength={300}
                  placeholder="es. Vegetariano, allergico al pesce, intolleranza al lattosio..." />
              </Field>
            </div>
          </div>
        </div>

        {!isNew && <label style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, background: C.paperDeep, borderRadius: 2, cursor: "pointer", marginTop: 8 }}>
          <input type="checkbox" checked={u.active !== false} onChange={e => setU({ ...u, active: e.target.checked })} />
          <div style={{ flex: 1 }}>
            <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500 }}>Operatore attivo</div>
            <div className="f-body" style={{ fontSize: 11, color: C.muted }}>Gli operatori disattivi non compaiono nelle liste di selezione</div>
          </div>
        </label>}
      </div>
    </div>
  </Modal>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      MENSA — Personale, contratti, turni                   ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// Vista dedicata al modulo Mensa: tabella inline-editable dipendenti + import
// turni CSV/Excel dal commercialista. I campi anagrafici completi (HACCP, costo
// orario, ecc.) restano in Anagrafiche → Utenti — qui ottimizzato per data entry
// rapido sui 30 dipendenti.
const mensaCellStyle = { padding: 8, border: `1px solid ${C.rule}`, verticalAlign: "middle" };
const mensaInputStyle = { width: "100%", minWidth: 80, padding: "4px 6px", border: `1px solid ${C.rule}`, borderRadius: 4, fontSize: 12, fontFamily: "inherit", background: C.white, color: C.ink };
const mensaInputNumStyle = { ...mensaInputStyle, width: 64, minWidth: 56, textAlign: "right" };

function MensaView({ state, setState }) {
  const [tab, setTab] = useState("registro");
  const activeCount = useMemo(() => state.users.filter(u => u.active !== false).length, [state.users]);

  return <div>
    <PageHeader
      kicker="Personale · gestione pasti"
      title="Mensa aziendale"
      subtitle="Contratti dipendenti, turni e pasti consumati. Regime art. 51 c. 2 lett. c TUIR — esenzione totale dei pasti somministrati in mensa aziendale."
    />
    <Tabs value={tab} onChange={setTab} tabs={[
      { id: "registro", label: "Registro Mensa", icon: Calendar },
      { id: "dipendenti", label: `Dipendenti & contratti (${activeCount})`, icon: Users },
      { id: "import", label: "Importa turni CSV", icon: Upload },
      { id: "performance", label: "Performance Operatori", icon: Activity },
    ]} />

    {tab === "registro" && <MensaRegistryTab state={state} setState={setState} />}
    {tab === "dipendenti" && <MensaEmployeesTab state={state} setState={setState} />}
    {tab === "import" && <MensaImportTab state={state} setState={setState} />}
    {tab === "performance" && <MensaOperatorPerformance state={state} />}
  </div>;
}

// ── Performance operatori (S3): confronto resa/throughput/scarto per stesso
// taskType. Cabla engine.stats.labOperatorCompare. n<5 → fallback con badge.
function MensaOperatorPerformance({ state }) {
  const [taskType, setTaskType] = useState("pesce");
  const [windowDays, setWindowDays] = useState(120);

  const data = useMemo(
    () => engine.stats.labOperatorCompare(state, taskType, windowDays),
    [state.workOrders, state.lots, state.users, taskType, windowDays]
  );

  const cellStyle = { padding: "8px 10px", borderBottom: `1px solid ${C.ruleSoft}`, fontSize: 12 };
  const headStyle = { padding: "10px 10px", textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, color: C.inkMid, borderBottom: `1px solid ${C.rule}` };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 12 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Field label="Tipo lavorazione">
          <Select value={taskType} onChange={e => setTaskType(e.target.value)} style={{ minWidth: 140 }}>
            <option value="pesce">Pesce</option>
            <option value="pane">Pane</option>
            <option value="pasta">Pasta</option>
          </Select>
        </Field>
        <Field label="Finestra">
          <Select value={windowDays} onChange={e => setWindowDays(parseInt(e.target.value, 10))} style={{ minWidth: 140 }}>
            <option value={30}>30 giorni</option>
            <option value={60}>60 giorni</option>
            <option value={120}>120 giorni</option>
            <option value={365}>12 mesi</option>
          </Select>
        </Field>
        <div style={{ marginLeft: "auto", padding: "8px 12px", background: C.paperDeep, borderRadius: 2, fontSize: 11, color: C.inkMid }}>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.18em", textTransform: "uppercase" }}>Operatori analizzati</div>
          <div className="f-display" style={{ fontSize: 18, fontWeight: 600, color: C.ink }}>{data.length}</div>
        </div>
      </div>

      {data.length === 0 ? (
        <EmptyState icon={ChefHat} title="Nessun dato performance operatori"
          body={`Completa almeno 5 lavorazioni di tipo "${taskType}" negli ultimi ${windowDays} giorni per attivare il confronto.`} />
      ) : (
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 760 }}>
            <thead style={{ background: C.paperDeep }}>
              <tr>
                <th style={headStyle}>Operatore</th>
                <th style={{ ...headStyle, textAlign: "right" }} title="Numero lavorazioni completate nella finestra">N. WO</th>
                <th style={{ ...headStyle, textAlign: "right" }} title="Throughput mediano kg materia prima/ora di lavoro cronometrato">kg/h mediano</th>
                <th style={{ ...headStyle, textAlign: "right" }} title="Resa mediana qtyOut/qtyIn lotto figlio + IQR (25°-75° percentile)">Resa mediana</th>
                <th style={{ ...headStyle, textAlign: "right" }} title="% lavorazioni con resa &lt; 50% (perdita >50% materia)">Scarto &gt; 50%</th>
                <th style={{ ...headStyle, textAlign: "left" }} title="Wilson CI 95% sul tasso di scarto: robusto per n piccolo e p estremo">Wilson CI 95%</th>
              </tr>
            </thead>
            <tbody>
              {data.map((op, i) => (
                <tr key={op.operator} style={{ background: i === 0 && !op.fallback ? C.oliveWash : "transparent" }}>
                  <td style={cellStyle}>
                    <strong style={{ color: C.ink }}>{op.operator}</strong>
                    {op.fallback && <span style={{ marginLeft: 6, fontSize: 9, color: C.terra, fontWeight: 600, padding: "1px 5px", background: C.terraWash, borderRadius: 2 }} title="Servono almeno 5 lavorazioni per stima utile">n insuff. ({op.n})</span>}
                    {!op.fallback && i === 0 && <span style={{ marginLeft: 6, fontSize: 9, color: C.olive, fontWeight: 700, padding: "1px 5px", background: C.white, border: `1px solid ${C.olive}`, borderRadius: 2 }}>TOP</span>}
                  </td>
                  <td className="f-mono" style={{ ...cellStyle, textAlign: "right", color: C.inkMid }}>{op.n}</td>
                  <td className="f-mono" style={{ ...cellStyle, textAlign: "right", fontWeight: 600, color: C.ink }}>
                    {op.kgPerH ? `${op.kgPerH.median.toFixed(1)} kg/h` : "—"}
                  </td>
                  <td className="f-mono" style={{ ...cellStyle, textAlign: "right", color: C.ink }}>
                    {op.yield ? `${(op.yield.median * 100).toFixed(0)}%` : "—"}
                    {op.yield && (
                      <div style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>
                        IQR {(op.yield.p25 * 100).toFixed(0)}-{(op.yield.p75 * 100).toFixed(0)}%
                      </div>
                    )}
                  </td>
                  <td className="f-mono" style={{ ...cellStyle, textAlign: "right", color: op.scartoOver50 && op.scartoOver50.rate > 0.3 ? C.terra : C.ink, fontWeight: op.scartoOver50 && op.scartoOver50.rate > 0.3 ? 600 : 400 }}>
                    {op.scartoOver50 ? `${(op.scartoOver50.rate * 100).toFixed(0)}%` : "—"}
                  </td>
                  <td className="f-mono" style={{ ...cellStyle, color: C.muted, fontSize: 11 }}>
                    {op.scartoOver50 ? `[${(op.scartoOver50.wilsonCI[0] * 100).toFixed(0)}%—${(op.scartoOver50.wilsonCI[1] * 100).toFixed(0)}%]` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2, fontSize: 11, color: C.inkSoft, lineHeight: 1.55 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Info size={13} color={C.teal} />
          <strong className="f-display" style={{ color: C.ink, fontSize: 12 }}>Come leggere</strong>
        </div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          <li><strong>kg/h mediano</strong> = Σ kg materia in / Σ ore fasi cronometrate, per operatore prevalente del workOrder (max fasi completate).</li>
          <li><strong>Resa mediana</strong> = mediana di qtyOut/qtyIn del lotto figlio. <strong>IQR</strong> (25°-75° percentile) misura la stabilità: IQR stretto = operatore consistente.</li>
          <li><strong>Scarto &gt; 50%</strong> = % lavorazioni con resa sotto il 50%. Banda <strong>Wilson CI 95%</strong>: robusta su n piccolo e p estremo (preferibile a Wald).</li>
          <li>Operatori con <strong>n &lt; 5</strong> lavorazioni nella finestra: marcati "n insuff.", non confrontabili.</li>
          <li>Il primo operatore (per kg/h) è evidenziato come <strong>TOP</strong>: candidato a fare da riferimento per training degli altri.</li>
        </ul>
      </div>
    </div>
  );
}

function MensaEmployeesTab({ state, setState }) {
  const toast = useToast();
  const [editing, setEditing] = useState(null); // user being edited via full UserEditor modal
  const active = useMemo(() => state.users.filter(u => u.active !== false), [state.users]);

  const updateUser = useCallback((id, patch) => {
    setState(s => ({
      ...s,
      users: s.users.map(u => u.id === id ? { ...u, ...patch } : u),
    }));
  }, [setState]);

  const parseNumOrNull = (v) => {
    if (v === "" || v == null) return null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  const parseIntOrNull = (v) => {
    if (v === "" || v == null) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  };

  return <div>
    {editing && <UserEditor user={editing} state={state} setState={setState} onClose={() => { setEditing(null); toast({ type: "success", message: "Dipendente salvato" }); }} />}

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
      <div className="f-body" style={{ fontSize: 12, color: C.inkMid }}>
        Compila direttamente le celle — il salvataggio è automatico. Per anagrafica completa (HACCP, costo orario) usa il bottone <em>Modifica completa</em> o vai in <em>Anagrafiche → Utenti</em>.
      </div>
      <Btn variant="accent" icon={Plus} onClick={() => setEditing({})}>Aggiungi dipendente</Btn>
    </div>

    {active.length === 0 ? (
      <EmptyState icon={Users} title="Nessun dipendente attivo" body="Aggiungi il primo dipendente per gestire la mensa aziendale" action={<Btn variant="accent" icon={Plus} onClick={() => setEditing({})}>Aggiungi dipendente</Btn>} />
    ) : (
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 4, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1200 }}>
          <thead>
            <tr style={{ background: C.paperDeep }}>
              <th style={{ ...mensaCellStyle, textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: C.inkMid, position: "sticky", left: 0, background: C.paperDeep, zIndex: 1 }}>Dipendente</th>
              <th style={{ ...mensaCellStyle, textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: C.inkMid }}>Mansione</th>
              <th style={{ ...mensaCellStyle, textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: C.inkMid }}>Tipo contratto</th>
              <th style={{ ...mensaCellStyle, textAlign: "right", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: C.inkMid }}>Ore/sett</th>
              <th style={{ ...mensaCellStyle, textAlign: "right", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: C.inkMid }}>Gg/sett</th>
              <th style={{ ...mensaCellStyle, textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: C.inkMid }}>CCNL · Livello</th>
              <th style={{ ...mensaCellStyle, textAlign: "right", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: C.inkMid }} title="Soglia minima di ore lavorate al giorno per maturare il diritto al pasto">Soglia ore</th>
              <th style={{ ...mensaCellStyle, textAlign: "right", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: C.inkMid }}>Max pasti/g</th>
              <th style={{ ...mensaCellStyle, textAlign: "right", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: C.inkMid }}>Valore €</th>
              <th style={{ ...mensaCellStyle, textAlign: "center", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: C.inkMid }}>Consuma</th>
              <th style={{ ...mensaCellStyle, textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: C.inkMid }}>Restrizioni</th>
              <th style={{ ...mensaCellStyle, textAlign: "center", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: C.inkMid }}></th>
            </tr>
          </thead>
          <tbody>
            {active.map(u => (
              <tr key={u.id} className="tx" style={{ background: C.white }}>
                <td style={{ ...mensaCellStyle, position: "sticky", left: 0, background: C.white, zIndex: 1 }}>
                  <div className="f-body" style={{ fontWeight: 600, fontSize: 12, color: C.ink }}>{u.name}</div>
                  <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: 0.5, marginTop: 2, textTransform: "uppercase" }}>{u.role}</div>
                </td>
                <td style={mensaCellStyle}>
                  <input style={mensaInputStyle} value={u.mansione || ""} onChange={e => updateUser(u.id, { mansione: e.target.value || null })} placeholder="—" />
                </td>
                <td style={mensaCellStyle}>
                  <select style={mensaInputStyle} value={u.contractType || ""} onChange={e => updateUser(u.id, { contractType: e.target.value || null })}>
                    <option value="">—</option>
                    <option value="fullTime">Tempo pieno</option>
                    <option value="partTime">Part-time</option>
                    <option value="stagionale">Stagionale</option>
                    <option value="extra">Extra</option>
                    <option value="apprendista">Apprendista</option>
                  </select>
                </td>
                <td style={mensaCellStyle}>
                  <input type="number" min="0" max="48" step="0.5" style={mensaInputNumStyle} value={u.weeklyHours ?? ""} onChange={e => updateUser(u.id, { weeklyHours: parseNumOrNull(e.target.value) })} placeholder="—" />
                </td>
                <td style={mensaCellStyle}>
                  <input type="number" min="1" max="7" step="1" style={mensaInputNumStyle} value={u.workDaysPerWeek ?? ""} onChange={e => updateUser(u.id, { workDaysPerWeek: parseIntOrNull(e.target.value) })} placeholder="—" />
                </td>
                <td style={mensaCellStyle}>
                  <input style={mensaInputStyle} value={u.ccnl || ""} onChange={e => updateUser(u.id, { ccnl: e.target.value || null })} placeholder="CCNL" />
                  <input style={{ ...mensaInputStyle, marginTop: 3, width: 72, minWidth: 56 }} value={u.livello || ""} onChange={e => updateUser(u.id, { livello: e.target.value || null })} placeholder="Liv." />
                </td>
                <td style={mensaCellStyle}>
                  <input type="number" min="0" max="12" step="0.5" style={mensaInputNumStyle} value={u.mealEntitlementHours ?? 6} onChange={e => updateUser(u.id, { mealEntitlementHours: parseNumOrNull(e.target.value) ?? 6 })} />
                </td>
                <td style={mensaCellStyle}>
                  <select style={mensaInputNumStyle} value={u.mealsPerDayMax || 1} onChange={e => updateUser(u.id, { mealsPerDayMax: parseInt(e.target.value, 10) })}>
                    <option value="1">1</option>
                    <option value="2">2</option>
                  </select>
                </td>
                <td style={mensaCellStyle}>
                  <input type="number" min="0" max="20" step="0.01" style={mensaInputNumStyle} value={u.mealValueDaily ?? 5} onChange={e => updateUser(u.id, { mealValueDaily: parseNumOrNull(e.target.value) ?? 5 })} />
                </td>
                <td style={{ ...mensaCellStyle, textAlign: "center" }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                    <input type="checkbox" checked={u.mealPreferenceConsume !== false} onChange={e => updateUser(u.id, { mealPreferenceConsume: e.target.checked })} />
                    <span className="f-mono" style={{ fontSize: 10, color: u.mealPreferenceConsume !== false ? C.teal : C.muted }}>{u.mealPreferenceConsume !== false ? "Sì" : "No"}</span>
                  </label>
                </td>
                <td style={mensaCellStyle}>
                  <input style={mensaInputStyle} value={u.mealDietaryRestrictions || ""} onChange={e => updateUser(u.id, { mealDietaryRestrictions: e.target.value || null })} placeholder="—" />
                </td>
                <td style={{ ...mensaCellStyle, textAlign: "center" }}>
                  <IconBtn icon={Settings} size={14} title="Modifica completa (HACCP, costo orario...)" onClick={() => setEditing(u)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    <div style={{ marginTop: 16, padding: 12, background: C.paperSoft, fontSize: 11, color: C.inkMid, borderRadius: 4, border: `1px solid ${C.ruleSoft}`, display: "flex", alignItems: "flex-start", gap: 8 }}>
      <Info size={14} color={C.tealSoft} style={{ flexShrink: 0, marginTop: 1 }} />
      <div>
        <strong>Pasti attesi mensili</strong>: calcolati automaticamente da soglia ore + giorni lavorativi (vedi <code>engine.mealsExpectedFromContract</code>). Lasciando vuoti i campi contratto il dipendente è escluso dai calcoli automatici di mensa.
      </div>
    </div>
  </div>;
}

// ── FASE M2a — Import CSV SEAC paghe (mensa) ─────────────────────────────────
// UI: drop file → parseSEACPresenze → preview enrichita → conferma → auto-popola
// state.users (insert/update per CF) + auto-genera state.mealLogs giornalieri.
function MensaImportTab({ state, setState }) {
  const toast = useToast();
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (f) => {
    if (!f) return;
    setParsing(true);
    setFile(f);
    setParsed(null);
    try {
      // Encoding detection: UTF-8 default, fallback Windows-1252 (Excel italiano)
      let text;
      try {
        const buf = await f.arrayBuffer();
        // Tentativo UTF-8 strict
        try {
          text = new TextDecoder("utf-8", { fatal: true }).decode(buf);
        } catch {
          // Fallback Windows-1252 (Excel italiano legacy)
          text = new TextDecoder("windows-1252").decode(buf);
        }
      } catch {
        text = await f.text();
      }
      const result = parseSEACPresenze(text);
      setParsed(result);
      if (!result.ok) {
        toast({ type: "error", message: result.errors[0] || "Errore parsing" });
      } else {
        toast({ type: "success", message: `${result.employees.length} dipendenti rilevati per ${result.meta.monthYear || "mese sconosciuto"}` });
      }
    } catch (e) {
      toast({ type: "error", message: "Errore lettura file: " + (e.message || e) });
    } finally {
      setParsing(false);
    }
  };

  // Normalizzazione nome per matching fallback (NFD + remove diacritics + collapse spazi + UPPERCASE)
  const normalizeName = (s) => {
    if (!s) return "";
    return String(s).normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().replace(/\s+/g, " ").trim();
  };

  // Arricchimento employees con match users esistenti + suggerimenti contratto/livello.
  // Bug fix re-import: guard sulle chiavi Map per evitare collisioni "vuote" (CF null/""/"NULL").
  // Senza la guard, più users con codiceFiscale falsy collassano sulla stessa chiave Map e
  // un emp del CSV con CF malformato matcha l'ultimo user di quel gruppo (falso positivo).
  const enriched = useMemo(() => {
    if (!parsed?.ok) return [];
    const byCF = new Map();
    const byName = new Map();
    for (const u of state.users || []) {
      const cfRaw = String(u.codiceFiscale || "").trim().toUpperCase();
      // CF italiano: lunghezza 16 (persone fisiche) o 11 (partite IVA / vecchi).
      // Scartiamo stringhe vuote, "NULL", "UNDEFINED", e tutto ciò che non è plausibile.
      if (cfRaw && cfRaw.length >= 11 && cfRaw !== "NULL" && cfRaw !== "UNDEFINED") {
        byCF.set(cfRaw, u);
      }
      const nameKey = normalizeName(u.name);
      // Indicizziamo per nome SOLO se il nome è una stringa non vuota di lunghezza ≥4
      // (evita match accidentale tra SEED placeholder tipo "Titolare" e righe SEAC).
      if (nameKey && nameKey.length >= 4) {
        // Se il nome è già indicizzato (omonimia tra users), preferiamo l'user che ha
        // anche codiceFiscale valorizzato (più affidabile). Altrimenti first-wins.
        const prev = byName.get(nameKey);
        if (!prev || (!prev.codiceFiscale && u.codiceFiscale)) {
          byName.set(nameKey, u);
        }
      }
    }
    return parsed.employees.map(emp => {
      const cfKey = String(emp.cf || "").trim().toUpperCase();
      let existing = (cfKey && cfKey.length >= 11) ? byCF.get(cfKey) : null;
      let matchedBy = existing ? "cf" : null;
      if (!existing) {
        const nameKey = normalizeName(emp.name);
        if (nameKey && nameKey.length >= 4) {
          existing = byName.get(nameKey);
          if (existing) matchedBy = "name";
        }
      }
      // Backward compat: users pre-fix con `active: undefined` sono considerati attivi.
      const wasInactive = !!existing && existing.active === false;
      return {
        ...emp,
        existingUserId: existing?.id || null,
        existingUserName: existing?.name || null,
        existingUserActive: existing ? (existing.active !== false) : null,
        wasInactive,
        action: existing ? "update" : "create",
        matchedBy,
        suggestedContractType: inferContractTypeFromSEAC(emp),
        suggestedLivello: suggestLivelloFromMansione(emp.mansione),
      };
    });
  }, [parsed, state.users]);

  const stats = useMemo(() => ({
    total: enriched.length,
    toCreate: enriched.filter(e => e.action === "create").length,
    toUpdate: enriched.filter(e => e.action === "update").length,
    // Riattivazioni: utenti esistenti con active:false che ricompaiono nel CSV (senza endDate).
    toReactivate: enriched.filter(e => e.action === "update" && e.wasInactive && !e.endDate).length,
    matchedByName: enriched.filter(e => e.action === "update" && e.matchedBy === "name").length,
  }), [enriched]);

  // Stima pasti per dipendente (preview only — non scrive nello state)
  const estimateMealsForEmployee = (emp, existingUser) => {
    const soglia = Number(existingUser?.mealEntitlementHours) > 0 ? Number(existingUser.mealEntitlementHours) : 6;
    const maxPasti = Number(existingUser?.mealsPerDayMax) > 0 ? Number(existingUser.mealsPerDayMax) : 1;
    const preferConsume = existingUser ? existingUser.mealPreferenceConsume !== false : true;
    if (!preferConsume) return 0;
    let pasti = 0;
    for (const [, dh] of emp.daysHours.entries()) {
      if (dh.riposo) continue;
      const work = (dh.OR || 0) + (dh.ST || 0) + (dh.ON || 0) + (dh.SF || 0);
      if (work < soglia) continue;
      pasti += (work >= 8 && maxPasti >= 2) ? 2 : 1;
    }
    return pasti;
  };

  const confirmImport = () => {
    if (!parsed?.ok || enriched.length === 0) return;
    setImporting(true);
    try {
      setState(s => {
        let nextUsers = [...(s.users || [])];
        let mealLogs = [...(s.mealLogs || [])];
        let createdCount = 0;
        let updatedCount = 0;
        let reactivatedCount = 0;
        const reactivatedNames = [];
        let auditLogAcc = s.auditLog;

        for (const emp of enriched) {
          let userId = emp.existingUserId;

          if (emp.action === "create") {
            // Auto-compile soglie e max pasti in base a mansione + carico effettivo del mese.
            // Solo lavoro effettivo (OR+ST+ON), assenze retribuite escluse.
            const totHHwork = (emp.totHH.OR || 0) + (emp.totHH.ST || 0) + (emp.totHH.ON || 0);
            const totGGwork = Array.from(emp.daysHours.values()).filter(d =>
              !d.riposo && ((d.OR || 0) + (d.ST || 0) + (d.ON || 0)) > 0
            ).length;
            const mealConfig = inferMealConfigFromSEAC(emp.mansione, totHHwork, totGGwork);

            const newUser = {
              id: uid("u_"),
              name: emp.name,
              codiceFiscale: emp.cf,
              role: emp.reparto ? emp.reparto.charAt(0).toUpperCase() + emp.reparto.slice(1) : "Personale",
              email: null,
              active: !emp.endDate,
              // L2 — HACCP + costo orario (default null, da compilare manualmente)
              haccpCertNumber: null, haccpCertIssuedAt: null, haccpCertExpiresAt: null, haccpCertLevel: null, haccpCertIssuer: null,
              hourlyGross: null, hourlyFullyLoaded: null,
              mansione: emp.mansione || null,
              // M1 — Contratto (suggerimenti da SEAC, editabili in tab Dipendenti)
              contractType: emp.suggestedContractType,
              weeklyHours: null,
              workDaysPerWeek: null,
              ccnl: "Pubblici Esercizi Confcommercio",
              livello: emp.suggestedLivello,
              // M1 — Mensa (auto-compile da mansione + ore mese; editabili in tab Dipendenti)
              mealEntitlementHours: mealConfig.mealEntitlementHours,
              mealsPerDayMax: mealConfig.mealsPerDayMax,
              mealPreferenceConsume: true,
              mealDietaryRestrictions: null,
              mealValueDaily: 5.00,
              // M2a — Anagrafica SEAC paghe
              hireDate: emp.hireDate,
              endDate: emp.endDate,
              endTD: emp.endTD,
              centroDiCosto: emp.cdc1 || null,
            };
            nextUsers.push(newUser);
            userId = newUser.id;
            createdCount++;
          } else {
            // Update: aggiorna SOLO campi anagrafici SEAC, NON sovrascrive config mensa/contratto utente
            // (mealEntitlementHours/mealsPerDayMax potrebbero essere stati personalizzati a mano).
            // Riattivazione: se l'utente è `active: false` ma ricompare nel CSV (senza endDate),
            // è di nuovo in forza → riattivare e loggare in audit.
            let didReactivate = false;
            let reactivatedName = null;
            nextUsers = nextUsers.map(u => {
              if (u.id !== userId) return u;
              const willCessate = !!emp.endDate;
              const wasInactive = u.active === false;
              // Riattivo SOLO se: era inattivo, il CSV non porta una endDate (cessazione),
              // e il dipendente ha ore/giorni in questo mese (cioè è davvero in forza).
              const hasMonthActivity = Array.from(emp.daysHours.values()).some(d =>
                d.riposo || (d.OR || 0) + (d.ST || 0) + (d.ON || 0) + (d.SF || 0) + (d.FE || 0) + (d.PR || 0) + (d.RC || 0) > 0
              );
              if (wasInactive && !willCessate && hasMonthActivity) {
                didReactivate = true;
                reactivatedName = u.name;
              }
              return {
                ...u,
                // Aggiorna campi da SEAC (canonici)
                codiceFiscale: emp.cf || u.codiceFiscale,
                mansione: emp.mansione || u.mansione,
                hireDate: emp.hireDate || u.hireDate,
                endDate: emp.endDate || u.endDate,
                endTD: emp.endTD || u.endTD,
                centroDiCosto: emp.cdc1 || u.centroDiCosto,
                // Active flag: false se cessato, true se riattivato dal CSV, altrimenti stato precedente
                active: willCessate ? false : (didReactivate ? true : (u.active !== false)),
              };
            });
            if (didReactivate) {
              reactivatedCount++;
              if (reactivatedName) reactivatedNames.push(reactivatedName);
              // Audit log: ogni riattivazione è tracciata individualmente.
              auditLogAcc = appendAudit({ ...s, auditLog: auditLogAcc }, {
                action: "user_reactivated_by_seac",
                entityType: "user",
                entityId: userId,
                reason: `Utente riattivato: ricomparso nel CSV SEAC ${parsed.meta.monthYear} dopo essere stato inattivo`,
                payload: {
                  userName: reactivatedName,
                  monthYear: parsed.meta.monthYear,
                  matchedBy: emp.matchedBy,
                },
              });
            }
            updatedCount++;
          }

          // Genera mealLogs per il mese — usa config mensa esistente o default
          const user = nextUsers.find(u => u.id === userId);
          const newLogs = generateMealLogsFromSEAC(emp, user, parsed.meta.monthYear);

          // Idempotenza: rimuovi i mealLogs esistenti per (userId, monthYear) prima di inserire i nuovi
          const cleanedMY = parsed.meta.monthYear.replace(/\s+/g, "");
          const parts = cleanedMY.split("-");
          if (parts.length === 2) {
            const mm = parts[0].padStart(2, "0");
            const yyyy = parts[1];
            const yearMonth = `${yyyy}-${mm}`;
            mealLogs = mealLogs.filter(ml => !(ml.userId === userId && ml.date && ml.date.startsWith(yearMonth)));
          }
          mealLogs.push(...newLogs);
        }

        return {
          ...s,
          users: nextUsers,
          mealLogs,
          auditLog: appendAudit({ ...s, auditLog: auditLogAcc }, {
            action: "mensa_seac_import",
            entityType: "mealLogs",
            entityId: parsed.meta.monthYear,
            reason: `Import CSV SEAC: ${createdCount} creati + ${updatedCount} aggiornati${reactivatedCount > 0 ? ` (${reactivatedCount} riattivati)` : ""} per ${parsed.meta.monthYear}`,
            payload: {
              monthYear: parsed.meta.monthYear,
              ragione: parsed.meta.ragione,
              employeesImported: enriched.length,
              created: createdCount,
              updated: updatedCount,
              reactivated: reactivatedCount,
              reactivatedNames,
              fileName: file?.name || null,
              fileSize: file?.size || null,
            },
          }),
        };
      });

      toast({ type: "success", message: `Import completato: ${stats.toCreate} nuovi + ${stats.toUpdate} aggiornati${stats.toReactivate > 0 ? ` (${stats.toReactivate} riattivati)` : ""} per ${parsed.meta.monthYear}` });
      setFile(null);
      setParsed(null);
    } catch (e) {
      toast({ type: "error", message: "Errore import: " + (e.message || e) });
    } finally {
      setImporting(false);
    }
  };

  return <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

    {/* Drop zone */}
    <div
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files && e.dataTransfer.files[0];
        if (f) handleFile(f);
      }}
      onClick={() => inputRef.current?.click()}
      className="tx"
      style={{
        border: `2px dashed ${dragOver ? C.teal : C.rule}`,
        background: dragOver ? C.tealWash : C.white,
        padding: 40,
        textAlign: "center",
        borderRadius: 4,
        cursor: "pointer",
      }}
    >
      <Upload size={36} color={dragOver ? C.teal : C.muted} strokeWidth={1.3} style={{ margin: "0 auto 12px", display: "block" }} />
      <div className="f-display" style={{ fontSize: 16, fontWeight: 500, color: C.ink, marginBottom: 4 }}>
        Trascina qui il CSV SEAC paghe del commercialista
      </div>
      <div className="f-body" style={{ fontSize: 12, color: C.muted }}>
        oppure clicca per selezionarlo dal disco · formato atteso: <code>PRESENZE_MESE_AZIENDA.CSV</code>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.txt"
        style={{ display: "none" }}
        onChange={e => {
          const f = e.target.files && e.target.files[0];
          if (f) handleFile(f);
          e.target.value = "";
        }}
      />
    </div>

    {parsing && (
      <div style={{ padding: 16, textAlign: "center", color: C.muted, background: C.white, border: `1px solid ${C.rule}`, borderRadius: 4 }}>
        <RefreshCw size={16} style={{ marginRight: 6, verticalAlign: "middle" }} />
        Lettura e parsing in corso…
      </div>
    )}

    {/* Errore formato non riconosciuto */}
    {parsed && !parsed.ok && (
      <div style={{ padding: 16, background: C.terraWash, borderRadius: 4, color: C.terraDeep, border: `1px solid ${C.terra}` }}>
        <div className="f-display" style={{ fontWeight: 600, marginBottom: 8, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
          <AlertTriangle size={16} /> Formato non riconosciuto
        </div>
        {parsed.errors.map((e, i) => <div key={i} className="f-body" style={{ fontSize: 12, marginTop: 4 }}>{e}</div>)}
        <div className="f-body" style={{ fontSize: 11, marginTop: 12, opacity: 0.85 }}>
          Il parser si aspetta un file CSV SEAC paghe con separatore <code>;</code> e header <code>MESE-ANNO;...;CODICE FISCALE DIP;...;VPA;TOT HH;...;1-Dom;2-Lun;...</code>.
        </div>
      </div>
    )}

    {/* Preview riepilogo + tabella dipendenti */}
    {parsed?.ok && (
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 4, padding: 20 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.25em", marginBottom: 8 }}>
          Anteprima import SEAC
        </div>

        {/* Riepilogo metriche */}
        <div style={{ display: "flex", gap: 24, marginBottom: 20, flexWrap: "wrap", padding: "12px 0", borderBottom: `1px solid ${C.ruleSoft}` }}>
          <div>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Mese-Anno</div>
            <div className="f-display" style={{ fontSize: 18, fontWeight: 600, color: C.ink, marginTop: 2 }}>{parsed.meta.monthYear || "—"}</div>
          </div>
          <div>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Ragione sociale</div>
            <div className="f-display" style={{ fontSize: 14, fontWeight: 500, color: C.ink, marginTop: 2 }}>{parsed.meta.ragione || "—"}</div>
          </div>
          <div>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Giorni nel mese</div>
            <div className="f-display" style={{ fontSize: 14, fontWeight: 500, color: C.ink, marginTop: 2 }}>{parsed.meta.dayCount || "—"}</div>
          </div>
          <div style={{ borderLeft: `1px solid ${C.ruleSoft}`, paddingLeft: 24 }}>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Dipendenti totali</div>
            <div className="f-display" style={{ fontSize: 18, fontWeight: 600, color: C.ink, marginTop: 2 }}>{stats.total}</div>
          </div>
          <div>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Nuovi (verranno creati)</div>
            <div className="f-display" style={{ fontSize: 18, fontWeight: 600, color: C.olive, marginTop: 2 }}>{stats.toCreate}</div>
          </div>
          <div>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>Esistenti (aggiornati)</div>
            <div className="f-display" style={{ fontSize: 18, fontWeight: 600, color: C.tealSoft, marginTop: 2 }}>{stats.toUpdate}</div>
            {(stats.toReactivate > 0 || stats.matchedByName > 0) && (
              <div className="f-mono" style={{ fontSize: 9.5, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
                {stats.toReactivate > 0 && <div>↻ {stats.toReactivate} riattivati</div>}
                {stats.matchedByName > 0 && <div>≈ {stats.matchedByName} match per nome</div>}
              </div>
            )}
          </div>
        </div>

        {/* Tabella dipendenti */}
        <div style={{ overflow: "auto", maxHeight: 460, border: `1px solid ${C.ruleSoft}`, borderRadius: 4 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 1200 }}>
            <thead style={{ background: C.paperDeep, position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: C.inkMid, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.rule}` }}>Azione</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: C.inkMid, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.rule}` }}>Nome</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: C.inkMid, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.rule}` }}>Codice Fiscale</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: C.inkMid, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.rule}` }}>Mansione</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: C.inkMid, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.rule}` }}>Reparto</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, color: C.inkMid, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.rule}` }}>Contratto</th>
                <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600, color: C.inkMid, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.rule}` }}>Liv.</th>
                <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, color: C.inkMid, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.rule}` }} title="Lavoro effettivo (OR+ST+ON+SF) + assenze retribuite (FE+PR+RC)">Ore mese</th>
                <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, color: C.inkMid, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.rule}` }}>Gg lav.</th>
                <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, color: C.inkMid, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.rule}`, background: C.amberWash }}>Pasti stimati</th>
              </tr>
            </thead>
            <tbody>
              {enriched.map(emp => {
                const totHHwork = (emp.totHH.OR || 0) + (emp.totHH.ST || 0) + (emp.totHH.ON || 0) + (emp.totHH.SF || 0);
                const totHHleave = (emp.totHH.FE || 0) + (emp.totHH.PR || 0) + (emp.totHH.RC || 0);
                const totHH = totHHwork + totHHleave;
                const existingUser = emp.existingUserId ? state.users.find(u => u.id === emp.existingUserId) : null;
                const gg = Array.from(emp.daysHours.values()).filter(d => !d.riposo && ((d.OR || 0) + (d.ST || 0) + (d.ON || 0) + (d.SF || 0)) > 0).length;
                const pastiStimati = estimateMealsForEmployee(emp, existingUser);
                return (
                  <tr key={emp.cf} className="tx" style={{ background: C.white }}>
                    <td style={{ padding: "6px 10px", borderBottom: `1px solid ${C.ruleSoft}` }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
                        <span className="f-mono" style={{
                          padding: "2px 7px", borderRadius: 3, fontSize: 9.5, fontWeight: 600,
                          letterSpacing: 0.5, textTransform: "uppercase",
                          background: emp.action === "create" ? C.oliveWash : (emp.wasInactive && !emp.endDate ? C.amberWash : C.tealWash),
                          color: emp.action === "create" ? C.oliveDeep : (emp.wasInactive && !emp.endDate ? C.amberDeep : C.tealDeep),
                        }} title={emp.action === "create"
                          ? "Nuovo dipendente — verrà creato"
                          : `Aggiorna user esistente (match per ${emp.matchedBy === "name" ? "nome normalizzato" : "codice fiscale"})${emp.wasInactive ? " · era inattivo, verrà riattivato" : ""}`
                        }>
                          {emp.action === "create" ? "Nuovo" : (emp.wasInactive && !emp.endDate ? "Riattiva" : "Aggiorna")}
                        </span>
                        {emp.action === "update" && (
                          <span className="f-mono" style={{ fontSize: 8.5, color: C.muted, letterSpacing: 0.3 }}>
                            match: {emp.matchedBy === "name" ? "nome" : "CF"}
                            {emp.wasInactive && " · era inattivo"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: "6px 10px", borderBottom: `1px solid ${C.ruleSoft}` }}>
                      <div className="f-body" style={{ fontWeight: 600, fontSize: 12, color: C.ink }}>{emp.name}</div>
                      {emp.action === "update" && emp.existingUserName && emp.existingUserName !== emp.name && (
                        <div className="f-mono" style={{ fontSize: 9.5, color: C.muted, marginTop: 1 }}>≈ {emp.existingUserName}</div>
                      )}
                    </td>
                    <td className="f-mono" style={{ padding: "6px 10px", fontSize: 10, color: C.inkMid, borderBottom: `1px solid ${C.ruleSoft}`, letterSpacing: 0.5 }}>{emp.cf}</td>
                    <td style={{ padding: "6px 10px", borderBottom: `1px solid ${C.ruleSoft}`, fontSize: 11, color: C.inkMid }}>{emp.mansione || "—"}</td>
                    <td style={{ padding: "6px 10px", borderBottom: `1px solid ${C.ruleSoft}`, fontSize: 11, color: C.inkMid, textTransform: "capitalize" }}>{emp.reparto || "—"}</td>
                    <td style={{ padding: "6px 10px", borderBottom: `1px solid ${C.ruleSoft}`, fontSize: 11, color: C.inkMid }}>
                      {emp.suggestedContractType === "apprendista" ? "Apprendista" :
                       emp.suggestedContractType === "stagionale" ? "T. determinato" :
                       emp.suggestedContractType === "fullTime" ? "T. indeterminato" : "—"}
                    </td>
                    <td className="f-mono" style={{ padding: "6px 10px", borderBottom: `1px solid ${C.ruleSoft}`, fontSize: 11, color: C.inkMid, textAlign: "center" }}>{emp.suggestedLivello || "—"}</td>
                    <td className="f-mono" style={{ padding: "6px 10px", borderBottom: `1px solid ${C.ruleSoft}`, fontSize: 11, color: C.ink, textAlign: "right" }}>
                      {totHH.toFixed(1)}h
                      {totHHleave > 0 && (
                        <div style={{ fontSize: 9, color: C.muted, marginTop: 1 }}>{totHHwork.toFixed(0)}lav · {totHHleave.toFixed(0)}ass.</div>
                      )}
                    </td>
                    <td className="f-mono" style={{ padding: "6px 10px", borderBottom: `1px solid ${C.ruleSoft}`, fontSize: 11, color: C.inkMid, textAlign: "right" }}>{gg}</td>
                    <td className="f-mono" style={{ padding: "6px 10px", borderBottom: `1px solid ${C.ruleSoft}`, fontSize: 12, fontWeight: 600, color: C.amberDeep, textAlign: "right", background: C.amberWash }}>{pastiStimati}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot style={{ background: C.paperDeep, fontSize: 10.5, fontWeight: 600 }}>
              <tr>
                <td colSpan={9} style={{ padding: "6px 10px", textAlign: "right", color: C.inkMid, textTransform: "uppercase", letterSpacing: 0.5 }}>Totale pasti stimati nel mese</td>
                <td className="f-mono" style={{ padding: "6px 10px", textAlign: "right", color: C.amberDeep, background: C.amberWash, fontSize: 13 }}>
                  {enriched.reduce((s, e) => s + estimateMealsForEmployee(e, e.existingUserId ? state.users.find(u => u.id === e.existingUserId) : null), 0)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Warning re-import */}
        {parsed.meta.monthYear && (state.mealLogs || []).some(ml => {
          const cleanedMY = parsed.meta.monthYear.replace(/\s+/g, "");
          const parts = cleanedMY.split("-");
          if (parts.length !== 2) return false;
          const yearMonth = `${parts[1]}-${parts[0].padStart(2, "0")}`;
          return ml.date && ml.date.startsWith(yearMonth);
        }) && (
          <div style={{ marginTop: 12, padding: 10, background: C.amberWash, borderRadius: 4, fontSize: 11.5, color: C.amberDeep, border: `1px solid ${C.amber}`, display: "flex", alignItems: "flex-start", gap: 8 }}>
            <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>Esistono già <strong>mealLogs</strong> per questo mese. Confermando l'import verranno <strong>sostituiti</strong> (per dipendenti coinvolti) — eventuali rettifiche manuali precedenti andranno perse.</div>
          </div>
        )}

        {/* Errori parsing (warning, non bloccanti) */}
        {parsed.errors.length > 0 && (
          <details style={{ marginTop: 12, fontSize: 11.5 }}>
            <summary className="f-body" style={{ color: C.amberDeep, cursor: "pointer", fontWeight: 600 }}>
              {parsed.errors.length} warning durante il parsing (clicca per vedere)
            </summary>
            <ul style={{ marginTop: 8, paddingLeft: 20, color: C.inkMid }}>
              {parsed.errors.slice(0, 30).map((e, i) => <li key={i} style={{ marginBottom: 2 }}>{e}</li>)}
              {parsed.errors.length > 30 && <li style={{ color: C.muted, fontStyle: "italic" }}>… altri {parsed.errors.length - 30} warning</li>}
            </ul>
          </details>
        )}

        {/* Azioni */}
        <div style={{ marginTop: 20, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn variant="subtle" onClick={() => { setFile(null); setParsed(null); }} disabled={importing}>Annulla</Btn>
          <Btn variant="primary" disabled={importing || stats.total === 0} icon={CheckCircle2} onClick={confirmImport}>
            {importing ? "Importazione in corso…" : `Conferma import (${stats.total} dipendenti · ${parsed.meta.monthYear})`}
          </Btn>
        </div>
      </div>
    )}

    {/* Info box */}
    <div style={{ background: C.paperSoft, padding: 16, borderRadius: 4, fontSize: 12, color: C.inkMid, border: `1px solid ${C.ruleSoft}` }}>
      <div className="f-display" style={{ fontWeight: 600, marginBottom: 10, color: C.ink, display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
        <FileText size={14} color={C.tealSoft} /> Parser nativo CSV SEAC paghe
      </div>
      <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7 }}>
        <li>Riconosce automaticamente il formato esportato dal commercialista (header SEAC, separatore <code>;</code>, encoding UTF-8 o Windows-1252)</li>
        <li>Aggrega righe <strong>OR</strong> (ordinario) + <strong>ST</strong> (straordinario) + <strong>ON</strong> (notturno) + <strong>SF</strong> (straord. festivo) dello stesso dipendente per giorno</li>
        <li>Riconosce anche assenze retribuite: <strong>FE</strong> (ferie), <strong>PR</strong> (permesso retribuito), <strong>RC</strong> (riposo compensativo) — non concorrono al pasto</li>
        <li>Match utenti esistenti per <strong>codice fiscale</strong> (canonico, ≥11 caratteri) o per <em>nome normalizzato</em> (≥4 caratteri, fallback)</li>
        <li>Auto-crea nuovi utenti compilando mansione, livello CCNL suggerito, contratto, date assunzione/cessazione, centro di costo</li>
        <li><strong>Auto-compila soglia mensa + max pasti/giorno</strong> in base a mansione e ore mese (es. CHEF con media ≥7h/g → 2 pasti, BARISTA → 1, INSERVIENTE → 5h soglia)</li>
        <li><strong>Riattiva automaticamente</strong> dipendenti precedentemente <code>active: false</code> se ricompaiono nel CSV senza data di cessazione (logga in audit)</li>
        <li>Pasti dovuti: ore lavoro effettivo ≥ soglia → 1 pasto · ≥ 8h con max 2 pasti config. → 2 pasti · riposi/assenze → 0</li>
        <li>Re-import dello stesso mese sostituisce i mealLogs precedenti per i dipendenti coinvolti (no duplicati)</li>
      </ul>
    </div>
  </div>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                FASE M3 — REGISTRO MENSA (vista calendaristica)             ║
// ║                                                                            ║
// ║  Visualizza i mealLogs aggregati per mese: KPI in alto, tabella dipendente ║
// ║  con drill-down calendario 28-31 giorni. Permette rettifica manuale del    ║
// ║  numero pasti consumati (eccezioni: assenza, malattia, scelta dipendente). ║
// ║  Export CSV per commercialista e stampa PDF per archivio (art. 2220 c.c.). ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

const MENSA_MONTH_NAMES = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

// Formatta "2026-04" → "Aprile 2026"
function formatMensaMonthLabel(ym) {
  if (!ym || typeof ym !== "string") return "—";
  const [y, m] = ym.split("-");
  const mi = parseInt(m, 10);
  if (!Number.isFinite(mi) || mi < 1 || mi > 12) return ym;
  return `${MENSA_MONTH_NAMES[mi - 1]} ${y}`;
}

// Stili tabella registro (estratti come const per evitare oggetti inline su hotpath)
const MENSA_REG_CELL = { padding: "8px 10px", borderBottom: `1px solid ${C.ruleSoft}`, verticalAlign: "middle" };
const MENSA_REG_CELL_R = { ...MENSA_REG_CELL, textAlign: "right" };
const MENSA_REG_HEAD = { ...MENSA_REG_CELL, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, color: C.inkMid, textAlign: "left", borderBottom: `1px solid ${C.rule}` };
const MENSA_REG_HEAD_R = { ...MENSA_REG_HEAD, textAlign: "right" };
const MENSA_KPI_CARD = { background: C.white, border: `1px solid ${C.rule}`, borderRadius: 4, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 4 };

// KPI card locale (riusa pattern del progetto: kicker mono uppercase + valore display)
function MensaKpiCard({ label, value, hint, color }) {
  return <div style={MENSA_KPI_CARD}>
    <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.muted, textTransform: "uppercase" }}>{label}</div>
    <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: color || C.ink, lineHeight: 1.1 }}>{value}</div>
    {hint && <div className="f-body" style={{ fontSize: 10, color: C.mutedSoft }}>{hint}</div>}
  </div>;
}

function MensaRegistryTab({ state, setState }) {
  // Mesi disponibili = unione date presenti in mealLogs (YYYY-MM)
  const availableMonths = useMemo(() => {
    const set = new Set();
    for (const ml of (state.mealLogs || [])) {
      if (ml && typeof ml.date === "string" && ml.date.length >= 7) set.add(ml.date.slice(0, 7));
    }
    return Array.from(set).sort().reverse();
  }, [state.mealLogs]);

  const [yearMonth, setYearMonth] = useState(() => availableMonths[0] || todayLocalISO().slice(0, 7));
  const [includeInactive, setIncludeInactive] = useState(false);
  const [filterCdc, setFilterCdc] = useState("");
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [showPrint, setShowPrint] = useState(false);

  // Se la lista di mesi cambia (es. nuovo import) e il mese corrente non c'è più, sposta al più recente
  useEffect(() => {
    if (availableMonths.length > 0 && !availableMonths.includes(yearMonth)) {
      setYearMonth(availableMonths[0]);
    }
  }, [availableMonths, yearMonth]);

  const agg = useMemo(
    () => engine.mealsAggregateMonth(state, yearMonth, { includeInactive }),
    [state, yearMonth, includeInactive]
  );

  const filtered = useMemo(() => {
    if (!filterCdc) return agg.byUser;
    return agg.byUser.filter(u => u.cdc === filterCdc);
  }, [agg.byUser, filterCdc]);

  // Totali ri-calcolati sul filtro reparto (per coerenza visiva con la tabella filtrata)
  const filteredTotals = useMemo(() => {
    if (!filterCdc) return agg.totals;
    return {
      mealsDue: filtered.reduce((s, u) => s + u.mealsDue, 0),
      mealsConsumed: filtered.reduce((s, u) => s + u.mealsConsumed, 0),
      costEur: Math.round(filtered.reduce((s, u) => s + u.totalEur, 0) * 100) / 100,
      employeesCount: filtered.length,
      daysWorked: filtered.reduce((s, u) => s + u.daysWorked, 0),
      hoursWorked: Math.round(filtered.reduce((s, u) => s + u.hoursWorked, 0) * 100) / 100,
      rectifiedCount: filtered.reduce((s, u) => s + u.rectifiedCount, 0),
    };
  }, [filterCdc, filtered, agg.totals]);

  const rectifyMealLog = useCallback((mealLogId, patch) => {
    setState(s => {
      const before = (s.mealLogs || []).find(ml => ml.id === mealLogId);
      return {
        ...s,
        mealLogs: (s.mealLogs || []).map(ml => ml.id === mealLogId
          ? { ...ml, ...patch, source: "manual_rectification", rectifiedAt: new Date().toISOString(), rectifiedBy: s.currentUser?.name || null }
          : ml
        ),
        auditLog: appendAudit(s, {
          action: "meal_rectified",
          entityType: "mealLog",
          entityId: mealLogId,
          before: before ? { mealsConsumed: before.mealsConsumed, source: before.source } : null,
          payload: patch,
        }),
      };
    });
  }, [setState]);

  const handleExportCSV = () => exportMensaCSV(agg, yearMonth);

  return <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

    {/* ─── Filtri toolbar ────────────────────────────────────────────── */}
    <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
      <Field label="Mese di riferimento">
        <Select value={yearMonth} onChange={e => setYearMonth(e.target.value)} style={{ minWidth: 180 }}>
          {availableMonths.length === 0 && <option value={yearMonth}>{formatMensaMonthLabel(yearMonth)} (nessun dato)</option>}
          {availableMonths.map(ym => (
            <option key={ym} value={ym}>{formatMensaMonthLabel(ym)}</option>
          ))}
        </Select>
      </Field>
      <Field label="Reparto">
        <Select value={filterCdc} onChange={e => setFilterCdc(e.target.value)} style={{ minWidth: 140 }}>
          <option value="">Tutti</option>
          <option value="CUCINA">Cucina</option>
          <option value="SALA">Sala</option>
          <option value="BAR">Bar</option>
          <option value="AMMINISTRAZIONE">Amministrazione</option>
          <option value="PERSONALE">Personale</option>
        </Select>
      </Field>
      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "9px 0" }}>
        <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} />
        <span className="f-body" style={{ fontSize: 12, color: C.inkMid }}>Mostra cessati</span>
      </label>
      <div style={{ flex: 1 }} />
      <Btn variant="ghost" size="sm" icon={Download} onClick={handleExportCSV} disabled={agg.fallback}>Esporta CSV</Btn>
      <Btn variant="primary" size="sm" icon={Printer} onClick={() => setShowPrint(true)} disabled={agg.fallback}>Stampa PDF</Btn>
    </div>

    {/* ─── Empty state ──────────────────────────────────────────────── */}
    {agg.fallback && (
      <div style={{ padding: 36, textAlign: "center", color: C.muted, background: C.paperSoft, border: `1px solid ${C.ruleSoft}`, borderRadius: 4 }}>
        <Calendar size={32} color={C.mutedSoft} style={{ marginBottom: 10 }} />
        <div className="f-display" style={{ fontSize: 16, marginBottom: 6, color: C.inkMid }}>Nessun dato per {formatMensaMonthLabel(yearMonth)}</div>
        <div className="f-body" style={{ fontSize: 12 }}>Importa il CSV SEAC del mese dalla tab <strong>Importa turni CSV</strong>.</div>
      </div>
    )}

    {!agg.fallback && (
      <>
        {/* ─── KPI cards ─────────────────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
          <MensaKpiCard label="Dipendenti" value={filteredTotals.employeesCount} hint="In forza nel mese (attivi)" />
          <MensaKpiCard label="GG-uomo (somma)" value={filteredTotals.daysWorked} hint={`Somma giorni lavorati tra tutti (media ${filteredTotals.employeesCount > 0 ? (filteredTotals.daysWorked / filteredTotals.employeesCount).toFixed(1) : 0} gg/persona)`} />
          <MensaKpiCard label="Ore-uomo (somma)" value={`${filteredTotals.hoursWorked.toFixed(0)}h`} hint={`Media ${filteredTotals.daysWorked > 0 ? (filteredTotals.hoursWorked / filteredTotals.daysWorked).toFixed(1) : 0}h/giorno per persona`} />
          <MensaKpiCard label="Pasti dovuti" value={filteredTotals.mealsDue} color={C.teal} hint="Calcolati da soglia ore CCNL" />
          <MensaKpiCard label="Pasti erogati" value={filteredTotals.mealsConsumed} color={C.olive} hint="Dovuti meno rettifiche manuali" />
          <MensaKpiCard label="Costo MP totale" value={`€ ${filteredTotals.costEur.toFixed(0)}`} color={C.terra} hint="Da sottrarre al food cost vendite" />
        </div>

        {/* ─── Banner rettifiche ─────────────────────────────────────── */}
        {filteredTotals.rectifiedCount > 0 && (
          <div style={{ padding: "10px 14px", background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 4, fontSize: 12, color: C.amberDeep, display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={14} /> <strong>{filteredTotals.rectifiedCount}</strong> rettifiche manuali applicate in questo mese — sono evidenziate in ambra nel calendario.
          </div>
        )}

        {/* ─── Tabella riepilogo dipendenti ─────────────────────────── */}
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 4, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 900 }}>
            <thead style={{ background: C.paperDeep, position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                <th style={MENSA_REG_HEAD}>Dipendente</th>
                <th style={MENSA_REG_HEAD}>Mansione · Reparto</th>
                <th style={MENSA_REG_HEAD}>Contratto</th>
                <th style={MENSA_REG_HEAD_R}>Gg lav.</th>
                <th style={MENSA_REG_HEAD_R}>Ore</th>
                <th style={MENSA_REG_HEAD_R}>Pasti dovuti</th>
                <th style={MENSA_REG_HEAD_R}>Pasti erogati</th>
                <th style={MENSA_REG_HEAD_R}>Δ</th>
                <th style={MENSA_REG_HEAD_R}>Valore €</th>
                <th style={{ ...MENSA_REG_HEAD, textAlign: "center", width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ ...MENSA_REG_CELL, textAlign: "center", padding: 24, color: C.muted, fontStyle: "italic" }}>
                    Nessun dipendente con i filtri attivi.
                  </td>
                </tr>
              ) : filtered.map(u => {
                const isOpen = expandedUserId === u.userId;
                return <React.Fragment key={u.userId}>
                  <tr style={{ cursor: "pointer", background: isOpen ? C.paperSoft : C.white }}
                      onClick={() => setExpandedUserId(isOpen ? null : u.userId)}>
                    <td style={MENSA_REG_CELL}>
                      <div className="f-body" style={{ fontWeight: 600, color: C.ink }}>{u.name}</div>
                      {!u.active && <div className="f-mono" style={{ fontSize: 9, color: C.muted, fontStyle: "italic", marginTop: 1 }}>CESSATO</div>}
                    </td>
                    <td style={MENSA_REG_CELL}><span style={{ color: C.inkMid }}>{u.mansione}</span> <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>· {u.cdc}</span></td>
                    <td style={MENSA_REG_CELL}>
                      <span className="f-body" style={{ fontSize: 11, color: C.inkMid }}>
                        {u.contractType || "—"}{u.livello ? ` (${u.livello})` : ""}
                      </span>
                    </td>
                    <td style={{ ...MENSA_REG_CELL_R, fontFamily: "JetBrains Mono, monospace" }}>{u.daysWorked}</td>
                    <td style={{ ...MENSA_REG_CELL_R, fontFamily: "JetBrains Mono, monospace" }}>{u.hoursWorked.toFixed(1)}h</td>
                    <td style={{ ...MENSA_REG_CELL_R, fontFamily: "JetBrains Mono, monospace", fontWeight: 600, color: C.teal }}>{u.mealsDue}</td>
                    <td style={{ ...MENSA_REG_CELL_R, fontFamily: "JetBrains Mono, monospace", fontWeight: 600, color: C.olive }}>{u.mealsConsumed}</td>
                    <td style={{ ...MENSA_REG_CELL_R, fontFamily: "JetBrains Mono, monospace", fontWeight: 600, color: u.deltaMeals === 0 ? C.muted : u.deltaMeals > 0 ? C.amberDeep : C.terra }}>
                      {u.deltaMeals > 0 ? `+${u.deltaMeals}` : u.deltaMeals === 0 ? "—" : u.deltaMeals}
                    </td>
                    <td style={{ ...MENSA_REG_CELL_R, fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }}>€ {u.totalEur.toFixed(2)}</td>
                    <td style={{ ...MENSA_REG_CELL, textAlign: "center" }}>
                      <ChevronDown size={14} color={C.muted} style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={10} style={{ padding: 0, background: C.paperSoft, borderBottom: `1px solid ${C.rule}` }}>
                        <MealCalendarRow user={u} yearMonth={yearMonth} state={state} rectifyMealLog={rectifyMealLog} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>;
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: C.paperDeep, fontWeight: 700, fontSize: 11 }}>
                <td colSpan={3} style={{ ...MENSA_REG_CELL, fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: C.ink }}>Totale</td>
                <td style={{ ...MENSA_REG_CELL_R, fontFamily: "JetBrains Mono, monospace" }}>{filteredTotals.daysWorked}</td>
                <td style={{ ...MENSA_REG_CELL_R, fontFamily: "JetBrains Mono, monospace" }}>{filteredTotals.hoursWorked.toFixed(0)}h</td>
                <td style={{ ...MENSA_REG_CELL_R, fontFamily: "JetBrains Mono, monospace", color: C.teal }}>{filteredTotals.mealsDue}</td>
                <td style={{ ...MENSA_REG_CELL_R, fontFamily: "JetBrains Mono, monospace", color: C.olive }}>{filteredTotals.mealsConsumed}</td>
                <td style={MENSA_REG_CELL_R}></td>
                <td style={{ ...MENSA_REG_CELL_R, fontFamily: "JetBrains Mono, monospace", color: C.terra }}>€ {filteredTotals.costEur.toFixed(2)}</td>
                <td style={MENSA_REG_CELL}></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* ─── Info regime fiscale ───────────────────────────────────── */}
        <div style={{ padding: 12, background: C.paperSoft, fontSize: 11, color: C.inkMid, borderRadius: 4, border: `1px solid ${C.ruleSoft}`, display: "flex", alignItems: "flex-start", gap: 8 }}>
          <Info size={14} color={C.tealSoft} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <strong>Regime mensa aziendale</strong> ex art. 51 c. 2 lett. c TUIR: pasti consumati esenti per il dipendente, costo MP deducibile come spesa del personale. <strong>Non vanno in busta paga.</strong> Stampa il registro mensile e archivialo per eventuale controllo Agenzia delle Entrate (art. 2220 c.c. — conservazione 10 anni).
          </div>
        </div>
      </>
    )}

    {showPrint && <MensaPrintReport state={state} agg={agg} yearMonth={yearMonth} onClose={() => setShowPrint(false)} />}
  </div>;
}

// ── Drill-down calendario 28-31 giorni per dipendente ────────────────────
function MealCalendarRow({ user, yearMonth, state, rectifyMealLog }) {
  const [year, month] = yearMonth.split("-").map(Number);
  const daysInMonth = new Date(year, month, 0).getDate(); // 28-31

  const logsByDay = useMemo(() => {
    const map = new Map();
    for (const ml of (state.mealLogs || [])) {
      if (!ml || ml.userId !== user.userId) continue;
      if (typeof ml.date !== "string" || !ml.date.startsWith(yearMonth)) continue;
      const day = parseInt(ml.date.slice(8, 10), 10);
      if (Number.isFinite(day)) map.set(day, ml);
    }
    return map;
  }, [user.userId, yearMonth, state.mealLogs]);

  // Calcola colore/etichetta per cella (estratto fuori dal render hot-loop)
  const cellFor = (day) => {
    const log = logsByDay.get(day);
    const date = new Date(year, month - 1, day);
    const dow = date.getDay();
    const isWeekend = dow === 0 || dow === 6;

    let bg = "#ECE6D6"; // = C.paperDeep tone "non in forza"
    let label = "—";
    let title = `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
    let color = C.muted;

    if (!log) {
      title += " · Non in forza";
    } else if (log.isRiposo) {
      bg = C.tealWash;
      label = "R";
      color = C.tealDeep;
      title += " · Riposo";
    } else if (log.isLeave) {
      bg = C.plumWash;
      label = "A";
      color = C.plum;
      title += " · Assenza retribuita (ferie/permesso/RC)";
    } else if ((log.mealsConsumed || 0) === 0) {
      bg = C.paperDeep;
      label = `${(Number(log.hoursWorked) || 0).toFixed(1)}h`;
      color = C.inkMid;
      title += ` · ${log.hoursWorked}h lavorate · nessun pasto (sotto soglia)`;
    } else if (log.mealsConsumed === 1) {
      bg = C.oliveWash;
      label = "1";
      color = C.oliveDeep;
      title += ` · ${log.hoursWorked}h · 1 pasto`;
    } else if (log.mealsConsumed >= 2) {
      bg = C.olive;
      label = String(log.mealsConsumed);
      color = "#fff";
      title += ` · ${log.hoursWorked}h · ${log.mealsConsumed} pasti (turno spezzato)`;
    }

    if (log?.source === "manual_rectification") {
      bg = C.amberWash;
      color = C.amberDeep;
      title += " · RETTIFICATO MANUALMENTE";
    }
    return { bg, label, title, color, isWeekend, log };
  };

  const handleCellClick = (log, day) => {
    if (!log) return;
    if (log.isRiposo) return;
    const maxMeals = Math.max(2, user.mealsPerDayMax || 1);
    // eslint-disable-next-line no-alert
    const newMeals = window.prompt(
      `${user.name} — ${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}\n` +
      `Ore lavorate: ${log.hoursWorked}h\n` +
      `Pasti attuali: ${log.mealsConsumed}\n\n` +
      `Inserisci il nuovo numero di pasti consumati (0–${maxMeals}):`,
      String(log.mealsConsumed)
    );
    if (newMeals === null) return;
    const n = parseInt(String(newMeals).trim(), 10);
    if (Number.isFinite(n) && n >= 0 && n <= maxMeals) {
      rectifyMealLog(log.id, { mealsConsumed: n });
    } else {
      // eslint-disable-next-line no-alert
      window.alert(`Valore non valido. Inserisci un numero tra 0 e ${maxMeals}.`);
    }
  };

  return <div style={{ padding: "14px 16px" }}>
    <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginBottom: 6, letterSpacing: "0.1em", textTransform: "uppercase" }}>
      Calendario {formatMensaMonthLabel(yearMonth)} — {daysInMonth} giorni
    </div>
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${daysInMonth}, minmax(20px, 1fr))`, gap: 2 }}>
      {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
        const { bg, label, title, color, isWeekend, log } = cellFor(day);
        return (
          <div key={day}
            title={title}
            onClick={() => handleCellClick(log, day)}
            style={{
              background: bg,
              padding: "4px 0",
              textAlign: "center",
              fontSize: 10,
              fontWeight: 600,
              color,
              borderRadius: 3,
              cursor: log && !log.isRiposo ? "pointer" : "default",
              opacity: isWeekend ? 0.92 : 1,
              border: isWeekend ? `1px dashed ${C.ruleSoft}` : "1px solid transparent",
              minHeight: 32,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <div className="f-mono" style={{ fontSize: 8, opacity: 0.55, lineHeight: 1 }}>{day}</div>
            <div style={{ lineHeight: 1.1 }}>{label}</div>
          </div>
        );
      })}
    </div>

    {/* Legenda */}
    <div style={{ marginTop: 10, fontSize: 10, color: C.muted, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
      <MensaLegendChip color={C.oliveWash} fg={C.oliveDeep} label="1" desc="1 pasto" />
      <MensaLegendChip color={C.olive} fg="#fff" label="2" desc="2 pasti (turno spezzato)" />
      <MensaLegendChip color={C.tealWash} fg={C.tealDeep} label="R" desc="Riposo" />
      <MensaLegendChip color={C.plumWash} fg={C.plum} label="A" desc="Assenza retribuita" />
      <MensaLegendChip color={C.paperDeep} fg={C.inkMid} label="Xh" desc="Lavorato sotto soglia" />
      <MensaLegendChip color={C.amberWash} fg={C.amberDeep} label="!" desc="Rettificato" />
      <MensaLegendChip color="#ECE6D6" fg={C.muted} label="—" desc="Non in forza" />
      <span style={{ marginLeft: "auto", fontStyle: "italic" }}>Click cella per rettificare manualmente</span>
    </div>
  </div>;
}

function MensaLegendChip({ color, fg, label, desc }) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
    <span style={{ background: color, color: fg, padding: "1px 6px", borderRadius: 2, fontSize: 10, fontWeight: 600, fontFamily: "JetBrains Mono, monospace", minWidth: 16, textAlign: "center" }}>{label}</span>
    {desc}
  </span>;
}

// ── Export CSV per commercialista ────────────────────────────────────────
function exportMensaCSV(agg, yearMonth) {
  const sep = ";";
  const numIT = (n) => (Number(n) || 0).toFixed(2).replace(".", ",");
  // sanitizeCsvCell: anti-formula (CWE-1236) + quoting RFC 4180 (delimitatore ;)
  const escapeCSV = sanitizeCsvCell;

  const rows = [
    ["Dipendente", "Mansione", "Reparto", "Contratto", "Livello", "Gg lavorati", "Ore lavorate", "Pasti dovuti", "Pasti erogati", "Delta", "Valore EUR"].join(sep),
    ...agg.byUser.map(u => [
      escapeCSV(u.name),
      escapeCSV(u.mansione),
      u.cdc,
      u.contractType || "",
      u.livello || "",
      u.daysWorked,
      numIT(u.hoursWorked),
      u.mealsDue,
      u.mealsConsumed,
      u.deltaMeals,
      numIT(u.totalEur),
    ].join(sep)),
    [
      "TOTALE", "", "", "", "",
      agg.totals.daysWorked,
      numIT(agg.totals.hoursWorked),
      agg.totals.mealsDue,
      agg.totals.mealsConsumed,
      agg.totals.mealsConsumed - agg.totals.mealsDue,
      numIT(agg.totals.costEur),
    ].join(sep),
  ];

  const csv = rows.join("\r\n");
  // BOM UTF-8 per compat Excel italiano
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `REGISTRO_MENSA_${yearMonth}.csv`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
}

// ── Report stampabile A4 (window.print) ──────────────────────────────────
function MensaPrintReport({ state, agg, yearMonth, onClose }) {
  const monthName = formatMensaMonthLabel(yearMonth);
  const ragioneSociale = state?.settings?.ragioneSociale || "NIALTRI S.R.L.";
  const insegna = state?.settings?.insegna || "BOTTEGA DEL MARE";
  const sede = state?.settings?.sede || "Polverigi (AN) — Italia";

  return <Modal onClose={onClose} maxWidth={1000} title="Registro Mensa — stampa" subtitle={monthName}
    footer={<>
      <Btn variant="ghost" onClick={onClose}>Chiudi</Btn>
      <Btn variant="primary" icon={Printer} onClick={() => window.print()}>Stampa</Btn>
    </>}>

    <style dangerouslySetInnerHTML={{ __html: `
      @media print {
        body * { visibility: hidden; }
        .mensa-print-area, .mensa-print-area * { visibility: visible; }
        .mensa-print-area { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 10mm; }
        @page { margin: 10mm; size: A4 portrait; }
        .mensa-print-area .no-print { display: none !important; }
        .mensa-print-area table { page-break-inside: auto; }
        .mensa-print-area tr { page-break-inside: avoid; page-break-after: auto; }
      }
    `}} />

    <div className="mensa-print-area" style={{ padding: 24, background: "#fff", color: "#000", fontFamily: "Geist, sans-serif" }}>
      {/* Header */}
      <div style={{ borderBottom: "2px solid #000", paddingBottom: 12, marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em" }}>{insegna}</div>
          <div style={{ fontSize: 10, color: "#555", letterSpacing: "0.15em", marginTop: 2 }}>{ragioneSociale.toUpperCase()} · {sede.toUpperCase()}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "#555" }}>REGISTRO MENSA AZIENDALE</div>
          <div style={{ fontSize: 18, fontWeight: 600, textTransform: "capitalize" }}>{monthName}</div>
          <div style={{ fontSize: 9, color: "#888", fontFamily: "monospace" }}>Art. 51 c. 2 lett. c) TUIR · Art. 2220 c.c.</div>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 18, padding: 10, background: "#F5F0E6", borderRadius: 2 }}>
        <div><div style={{ fontSize: 8, color: "#555", letterSpacing: "0.15em" }}>DIPENDENTI</div><div style={{ fontSize: 16, fontWeight: 600 }}>{agg.totals.employeesCount}</div></div>
        <div><div style={{ fontSize: 8, color: "#555", letterSpacing: "0.15em" }}>GG LAVORATI</div><div style={{ fontSize: 16, fontWeight: 600 }}>{agg.totals.daysWorked}</div></div>
        <div><div style={{ fontSize: 8, color: "#555", letterSpacing: "0.15em" }}>ORE</div><div style={{ fontSize: 16, fontWeight: 600 }}>{agg.totals.hoursWorked.toFixed(0)}h</div></div>
        <div><div style={{ fontSize: 8, color: "#555", letterSpacing: "0.15em" }}>PASTI DOVUTI</div><div style={{ fontSize: 16, fontWeight: 600 }}>{agg.totals.mealsDue}</div></div>
        <div><div style={{ fontSize: 8, color: "#555", letterSpacing: "0.15em" }}>PASTI EROGATI</div><div style={{ fontSize: 16, fontWeight: 600 }}>{agg.totals.mealsConsumed}</div></div>
        <div><div style={{ fontSize: 8, color: "#555", letterSpacing: "0.15em" }}>COSTO MP</div><div style={{ fontSize: 16, fontWeight: 600 }}>€ {agg.totals.costEur.toFixed(2)}</div></div>
      </div>

      {/* Tabella riepilogo */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", marginBottom: 6, borderBottom: "1px solid #000", paddingBottom: 4 }}>§ RIEPILOGO PER DIPENDENTE</div>
        {agg.byUser.length === 0 ? (
          <div style={{ fontSize: 11, color: "#888", padding: 10, fontStyle: "italic" }}>Nessun dipendente nel periodo.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #000" }}>
                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Dipendente</th>
                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Mansione · Reparto</th>
                <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600 }}>Contratto</th>
                <th style={{ textAlign: "right", padding: "4px 6px", fontWeight: 600 }}>Gg</th>
                <th style={{ textAlign: "right", padding: "4px 6px", fontWeight: 600 }}>Ore</th>
                <th style={{ textAlign: "right", padding: "4px 6px", fontWeight: 600 }}>P. dovuti</th>
                <th style={{ textAlign: "right", padding: "4px 6px", fontWeight: 600 }}>P. erogati</th>
                <th style={{ textAlign: "right", padding: "4px 6px", fontWeight: 600 }}>Valore €</th>
              </tr>
            </thead>
            <tbody>
              {agg.byUser.map(u => <tr key={u.userId} style={{ borderBottom: "1px solid #ddd" }}>
                <td style={{ padding: "3px 6px" }}>{u.name}{!u.active && <span style={{ color: "#888", fontStyle: "italic" }}> (cessato)</span>}</td>
                <td style={{ padding: "3px 6px" }}>{u.mansione} · {u.cdc}</td>
                <td style={{ padding: "3px 6px", color: "#555" }}>{u.contractType || "—"}{u.livello ? ` (${u.livello})` : ""}</td>
                <td style={{ padding: "3px 6px", textAlign: "right", fontFamily: "monospace" }}>{u.daysWorked}</td>
                <td style={{ padding: "3px 6px", textAlign: "right", fontFamily: "monospace" }}>{u.hoursWorked.toFixed(1)}</td>
                <td style={{ padding: "3px 6px", textAlign: "right", fontFamily: "monospace" }}>{u.mealsDue}</td>
                <td style={{ padding: "3px 6px", textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>{u.mealsConsumed}</td>
                <td style={{ padding: "3px 6px", textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>€ {u.totalEur.toFixed(2)}</td>
              </tr>)}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "2px solid #000", fontWeight: 700 }}>
                <td colSpan={3} style={{ padding: "5px 6px", textAlign: "right" }}>TOTALE</td>
                <td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "monospace" }}>{agg.totals.daysWorked}</td>
                <td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "monospace" }}>{agg.totals.hoursWorked.toFixed(1)}</td>
                <td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "monospace" }}>{agg.totals.mealsDue}</td>
                <td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "monospace" }}>{agg.totals.mealsConsumed}</td>
                <td style={{ padding: "5px 6px", textAlign: "right", fontFamily: "monospace" }}>€ {agg.totals.costEur.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Note legali */}
      <div style={{ marginBottom: 24, padding: 10, background: "#F5F0E6", fontSize: 9, color: "#333", lineHeight: 1.5 }}>
        <strong>Regime fiscale:</strong> i pasti sono somministrati in mensa aziendale ai sensi dell'art. 51 c. 2 lett. c) TUIR. Tali somministrazioni non concorrono alla formazione del reddito di lavoro dipendente e il costo materia prima rappresenta spesa del personale deducibile. Documento conservato per i termini di cui all'art. 2220 c.c. (10 anni).
        {agg.totals.rectifiedCount > 0 && <><br /><strong>Rettifiche manuali nel mese:</strong> {agg.totals.rectifiedCount} record modificati rispetto al calcolo automatico SEAC (audit log interno).</>}
      </div>

      {/* Firme */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, marginTop: 40 }}>
        <div>
          <div style={{ borderTop: "1px solid #000", paddingTop: 6, fontSize: 10 }}>Responsabile di sala / cucina</div>
          <div style={{ fontSize: 9, color: "#888", marginTop: 24 }}>Data __________ Firma _____________________________</div>
        </div>
        <div>
          <div style={{ borderTop: "1px solid #000", paddingTop: 6, fontSize: 10 }}>Datore di lavoro / Titolare</div>
          <div style={{ fontSize: 9, color: "#888", marginTop: 24 }}>Data __________ Firma _____________________________</div>
        </div>
      </div>
    </div>
  </Modal>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      REPORT & COSTI                                        ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function Report({ state, setState }) {
  // ── Snapshot-first food cost (regola d'oro recipeSnapshot immutabilità) ───
  // I numeri storici NON devono cambiare quando si aggiorna oggi il prezzo di un BP.
  // Si aggrega per piatto il costo medio EFFETTIVAMENTE registrato negli scontrini
  // (recipeSnapshot.netCost), con fallback live solo per qty senza snapshot.
  // Pre-index O(1) lookup per evitare O(n²) nel doppio loop receipts × dishes.
  const bpMap = useMemo(() => {
    const m = new Map();
    (state.baseProducts || []).forEach(b => m.set(b.id, b));
    return m;
  }, [state.baseProducts]);

  const foodCostData = useMemo(() => {
    const dishes = state.dishesReal || [];
    const receipts = state.tableReceipts || [];

    // Aggrega snapshot per piatto (UN solo passaggio sui receipts → O(R·I))
    const agg = new Map(); // dishId → { snapshotCost, snapshotQty, liveQty }
    for (const r of receipts) {
      const items = r.items || [];
      for (const it of items) {
        if (!it.dishId || (typeof it.dishId === "string" && it.dishId.startsWith("_unmatched_"))) continue;
        const qty = Number(it.qty) || 0;
        if (qty <= 0) continue;
        let row = agg.get(it.dishId);
        if (!row) { row = { snapshotCost: 0, snapshotQty: 0, liveQty: 0 }; agg.set(it.dishId, row); }
        const snapCost = it.recipeSnapshot ? Number(it.recipeSnapshot.netCost) : NaN;
        if (Number.isFinite(snapCost) && snapCost > 0) {
          row.snapshotCost += snapCost * qty;
          row.snapshotQty += qty;
        } else {
          row.liveQty += qty;
        }
      }
    }

    return dishes.map(d => {
      // Costo per unità "live" (fallback, post-calo): usato per qty senza snapshot
      const liveCostPerUnit = (d.ingredienti || []).reduce((s, ing) => {
        const bp = ing.bpId ? bpMap.get(ing.bpId) : null;
        const unit = bp ? (Number(bp.pReale) || Number(bp.pFatt) || 0) : (Number(ing.prezzo) || 0);
        return s + unit * (Number(ing.qty) || 0);
      }, 0);

      const row = agg.get(d.id);
      const snapshotCost = row ? row.snapshotCost : 0;
      const snapshotQty = row ? row.snapshotQty : 0;
      const liveQty = row ? row.liveQty : 0;
      const totalQty = snapshotQty + liveQty;
      const totalCost = snapshotCost + liveCostPerUnit * liveQty;

      // Costo medio per unità: storico-snapshot dove disponibile, live solo come fallback.
      // Se piatto MAI venduto nel periodo, usiamo live (utile per nuovi piatti in carta).
      const avgCostPerUnit = totalQty > 0
        ? totalCost / totalQty
        : liveCostPerUnit;
      const fromSnapshotPct = totalQty > 0 ? snapshotQty / totalQty : 0;

      const costInfo = engine.computeDishCostWithVat(state, d);
      const econ = engine.dishEconomics(state, d.prezzo, avgCostPerUnit, d.vatRate, costInfo.avgVatPurchase);
      return {
        name: d.nome.length > 18 ? d.nome.slice(0, 18) + "…" : d.nome,
        reale: Math.round(econ.cost * 100) / 100,
        prezzo: Math.round(econ.price * 100) / 100,
        margine: Math.round(econ.margin * 100) / 100,
        fcPct: econ.fcPct,
        cat: d.cat,
        // Trasparenza statistica (per badge UI)
        fromSnapshotPct,
        snapshotQty,
        liveQty,
        totalQty,
      };
    }).sort((a, b) => b.reale - a.reale).slice(0, 12);
  }, [state.dishesReal, state.tableReceipts, bpMap, state.settings]);

  // Riepilogo provenienza dati (per badge "X% snapshot / Y% live")
  const foodCostProvenance = useMemo(() => {
    let snap = 0, live = 0;
    for (const row of foodCostData) {
      snap += row.snapshotQty || 0;
      live += row.liveQty || 0;
    }
    const total = snap + live;
    return { snap, live, total, snapPct: total > 0 ? (snap / total) * 100 : 0 };
  }, [foodCostData]);

  // Variance Food Cost teorico vs reale (14gg) — dove si nasconde la perdita strutturale
  const fcVar = useMemo(
    () => engine.stats.fcVariance(state, 14),
    [state.tableReceipts, state.kitchenOutputs, state.lots, state.dishesReal]
  );

  // Top prodotti per calo (maggior perdita materia prima)
  const topCaloData = useMemo(() => {
    return (state.baseProducts || [])
      .filter(bp => bp.caloTot > 0)
      .sort((a, b) => b.caloTot - a.caloTot)
      .slice(0, 10)
      .map(bp => ({ name: bp.nome.length > 18 ? bp.nome.slice(0, 18) + "…" : bp.nome, calo: Math.round(bp.caloTot * 1000) / 10, diff: Math.round((bp.pReale - bp.pFatt) * 100) / 100 }));
  }, [state.baseProducts]);

  const spendBySupplier = state.suppliers.map(s => {
    const total = state.invoices.filter(i => i.supplierId === s.id).reduce((sum, i) => sum + i.amount, 0);
    return { name: s.name.length > 20 ? s.name.slice(0, 20) + "…" : s.name, spend: total };
  }).filter(x => x.spend > 0).sort((a, b) => b.spend - a.spend);

  const categoryDistrib = Object.entries(cats).map(([k, v]) => {
    const total = state.receipts.reduce((sum, r) => sum + r.lines.filter(l => engine.getItems(state).find(it => it.id === l.itemId)?.category === k).reduce((s, l) => s + l.qty * l.pricePerUnit, 0), 0);
    return { name: v.label, value: total, color: v.color };
  }).filter(x => x.value > 0);

  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(TODAY); d.setDate(d.getDate() - (6 - i));
    const dStr = d.toISOString().slice(0, 10);
    const dayOutputs = state.kitchenOutputs.filter(ko => ko.date === dStr);
    return { date: fmt.dateShort(dStr), servings: dayOutputs.reduce((s, ko) => s + ko.servings, 0) };
  });

  return <div className="fade-up">
    <PageHeader kicker="Analisi costi · Rese · Food cost" title="Report & direzione"
      subtitle="Dashboard direzionale: food cost evolutivo, top fornitori, distribuzione categorie, copertura servizio." />

    <div className="stagger" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20, marginBottom: 24 }}>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2 }}>
        <SectionHeader title="Top 12 piatti per costo reale" subtitle="Costo materia prima storicizzato (snapshot al momento della vendita)" />
        {foodCostProvenance.total > 0 && <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, padding: "6px 10px", background: foodCostProvenance.snapPct >= 95 ? C.tealWash : C.amberWash, border: `1px solid ${foodCostProvenance.snapPct >= 95 ? C.teal : C.amber}`, borderRadius: 2 }} title="Da snapshot: prezzo ricetta registrato al momento della vendita (immutabile). Live: ricalcolo con prezzo BP attuale (solo per qty senza snapshot).">
          <Info size={13} color={foodCostProvenance.snapPct >= 95 ? C.tealDeep : C.amber} style={{ flexShrink: 0 }} />
          <span className="f-mono" style={{ fontSize: 10, color: C.ink, letterSpacing: "0.05em" }}>
            {foodCostProvenance.snapPct >= 99.5
              ? `100% DA SNAPSHOT · numeri storici stabili nel tempo`
              : `DA SNAPSHOT ${foodCostProvenance.snapPct.toFixed(0)}% · LIVE ${(100 - foodCostProvenance.snapPct).toFixed(0)}% (scontrini pre-snapshot, ricalcolati a prezzi attuali)`}
          </span>
        </div>}
        {foodCostData.length > 0 ? <ResponsiveContainer width="100%" height={300}>
          <BarChart data={foodCostData} margin={{ top: 10, right: 10, left: -20, bottom: 40 }}>
            <CartesianGrid strokeDasharray="2 4" stroke={C.rule} vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: "Geist", fill: C.inkSoft }} axisLine={{ stroke: C.rule }} tickLine={false} angle={-25} textAnchor="end" height={50} />
            <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: C.muted }} axisLine={false} tickLine={false} tickFormatter={v => `€${v}`} />
            <Tooltip contentStyle={{ background: C.paper, border: `1px solid ${C.ink}`, borderRadius: 2, fontFamily: "Geist", fontSize: 12 }} formatter={v => `€${v}`} />
            <Bar dataKey="prezzo" fill={C.ruleSoft} name="Prezzo listino" radius={[2, 2, 0, 0]} />
            <Bar dataKey="reale" fill={C.teal} name="Costo reale" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer> : <div style={{ padding: 40, textAlign: "center", color: C.muted }}>Carica piatti per vedere i dati</div>}
      </div>

      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2 }}>
        <SectionHeader title="Distribuzione acquisti" subtitle="Per categoria merceologica" />
        {categoryDistrib.length > 0 ? <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie data={categoryDistrib} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2}>
              {categoryDistrib.map((entry, i) => <Cell key={i} fill={entry.color} />)}
            </Pie>
            <Tooltip formatter={v => fmt.eur(v)} contentStyle={{ background: C.paper, border: `1px solid ${C.ink}`, borderRadius: 2, fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer> : <div style={{ padding: 40, textAlign: "center", color: C.muted }}>Nessun dato</div>}
      </div>
    </div>

    {/* ─── Variance Food Cost teorico vs reale (14gg) ──────────────────────── */}
    {!fcVar.fallback && fcVar.teorico > 0 && (() => {
      const v = fcVar;
      const absVar = Math.abs(v.variance);
      // Soglie operative su benchmark F&B Italia: ≤2% fisiologico, 2-5% drift, >5% anomalo
      const isAnomaly = absVar > 5;
      const isWarning = absVar > 2 && absVar <= 5;
      const accentColor = isAnomaly ? C.terra : isWarning ? C.amber : C.olive;
      const accentDeep = isAnomaly ? C.terraDeep : isWarning ? C.amberDeep : C.oliveDeep;
      const accentWash = isAnomaly ? C.terraWash : isWarning ? C.amberWash : C.oliveWash;
      // CI esclude zero? Se sì variance è statisticamente diversa da zero
      const ciExcludesZero = (v.varianceCI[0] > 0) || (v.varianceCI[1] < 0);
      // €/anno proiettati su 365gg dalla differenza assoluta osservata
      const eurAnnuale = (v.reale - v.teorico) * (365 / Math.max(1, v.nDays));
      return (
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2, marginBottom: 24 }}>
          <SectionHeader title="Variance Food Cost · teorico vs reale" subtitle="Differenza tra costo da ricetta-snapshot e costo effettivo scarico magazzino · finestra 14 giorni" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.4fr", gap: 20, alignItems: "stretch" }}>
            <div style={{ padding: 14, background: C.paper, borderRadius: 2 }}>
              <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>TEORICO (DA RICETTE)</div>
              <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink, marginTop: 4 }}>{fmt.eur(v.teorico)}</div>
              <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>Σ recipeSnapshot.netCost × qty</div>
            </div>
            <div style={{ padding: 14, background: C.paper, borderRadius: 2 }}>
              <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>REALE (DA SCARICO)</div>
              <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink, marginTop: 4 }}>{fmt.eur(v.reale)}</div>
              <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>Σ lot.qty × lot.unitCostReal</div>
            </div>
            <div style={{ padding: 14, background: accentWash, borderRadius: 2, border: `1px solid ${accentColor}` }}>
              <div className="f-mono" style={{ fontSize: 10, color: accentDeep, letterSpacing: "0.2em" }}>VARIANCE</div>
              <div className="f-display" style={{ fontSize: 26, fontWeight: 700, color: accentDeep, marginTop: 4 }}>
                {v.variance >= 0 ? "+" : ""}{v.variance.toFixed(1)}%
              </div>
              <div className="f-mono" style={{ fontSize: 10, color: accentDeep, marginTop: 2 }}>
                CI 95%: [{v.varianceCI[0].toFixed(1)}% — {v.varianceCI[1].toFixed(1)}%]
              </div>
              <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>
                n={v.n} piatti · {v.nDays}gg{v.pValue !== null && ` · p≈${v.pValue < 0.001 ? "<0.001" : v.pValue.toFixed(3)}`}
              </div>
            </div>
          </div>
          {isAnomaly && (
            <div style={{ marginTop: 12, padding: 12, background: C.terraWash, border: `1px solid ${C.terra}`, borderRadius: 2 }}>
              <div className="f-body" style={{ fontSize: 12, color: C.terraDeep, fontWeight: 600 }}>
                Scostamento &gt; 5%{ciExcludesZero ? " (statisticamente significativo)" : " (vicino al limite, conferma con altre settimane)"}
              </div>
              <div className="f-body" style={{ fontSize: 11, color: C.terraDeep, marginTop: 4 }}>
                Possibili cause: sprechi cucina (porzionatura larga, scarti), errori scarico magazzino, drift fornitore non recepito in ricetta, consumo non registrato.
                Proiezione annuale: <strong>{eurAnnuale >= 0 ? "perdita ~" : "guadagno ~"}{fmt.eurShort(Math.abs(eurAnnuale))}</strong> se la dinamica osservata persiste.
              </div>
            </div>
          )}
          {isWarning && (
            <div style={{ marginTop: 12, padding: 12, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2 }}>
              <div className="f-body" style={{ fontSize: 12, color: C.amberDeep, fontWeight: 600 }}>
                Scostamento 2-5% · da monitorare
              </div>
              <div className="f-body" style={{ fontSize: 11, color: C.amberDeep, marginTop: 4 }}>
                Rientra nel rumore di porzionatura normale, ma vicino alla soglia. Continua a tracciare per 2-3 settimane prima di concludere.
              </div>
            </div>
          )}
          {!isAnomaly && !isWarning && (
            <div style={{ marginTop: 12, padding: 12, background: C.oliveWash, border: `1px solid ${C.olive}`, borderRadius: 2 }}>
              <div className="f-body" style={{ fontSize: 12, color: C.oliveDeep, fontWeight: 600 }}>
                Variance ≤ 2% · fisiologico
              </div>
              <div className="f-body" style={{ fontSize: 11, color: C.oliveDeep, marginTop: 4 }}>
                Teorico e reale allineati. Il sistema di scarico magazzino riflette correttamente le ricette.
              </div>
            </div>
          )}
        </div>
      );
    })()}
    {fcVar.fallback && fcVar.teorico === 0 && (state.kitchenOutputs || []).length > 0 && (
      <div style={{ background: C.paper, border: `1px dashed ${C.rule}`, padding: 14, borderRadius: 2, marginBottom: 24 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>VARIANCE FOOD COST · DATI INSUFFICIENTI</div>
        <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, marginTop: 4 }}>
          Servono almeno 7 piatti venduti con recipeSnapshot e qualche giorno di scarico magazzino per calcolare la variance teorico vs reale con onestà statistica.
        </div>
      </div>
    )}

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2 }}>
        <SectionHeader title="Top 10 prodotti con maggior calo" subtitle="Dove si perde più materia prima in lavorazione" />
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {topCaloData.length > 0 ? topCaloData.map((p, i) => <div key={i}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <span className="f-body" style={{ fontSize: 12, color: C.ink }}>{p.name}</span>
              <div style={{ display: "flex", gap: 10 }}>
                <span className="f-mono" style={{ fontSize: 11, color: C.muted }}>+{fmt.eur(p.diff)}/kg</span>
                <span className="f-mono" style={{ fontSize: 12, color: p.calo > 40 ? C.terra : C.amber, fontWeight: 600, minWidth: 44, textAlign: "right" }}>{p.calo.toFixed(1)}%</span>
              </div>
            </div>
            <ProgressBar value={p.calo} max={70} color={p.calo > 40 ? C.terra : C.amber} />
          </div>) : <div className="f-body" style={{ fontSize: 12, color: C.muted, padding: 20, textAlign: "center" }}>Carica prodotti base per vedere i dati</div>}
        </div>
      </div>

      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2 }}>
        <SectionHeader title="Top fornitori" subtitle="Per spesa cumulata fatturata" />
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {spendBySupplier.length > 0 ? spendBySupplier.slice(0, 5).map((s, i) => {
            const max = spendBySupplier[0].spend;
            const pct = (s.spend / max) * 100;
            return <div key={i}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <span className="f-body" style={{ fontSize: 13, color: C.ink }}>{s.name}</span>
                <span className="f-mono" style={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>{fmt.eur(s.spend)}</span>
              </div>
              <ProgressBar value={pct} color={C.teal} />
            </div>;
          }) : <div className="f-body" style={{ fontSize: 12, color: C.muted, padding: 20, textAlign: "center" }}>Nessuna spesa registrata</div>}
        </div>
      </div>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 20, marginBottom: 24 }}>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2 }}>
        <SectionHeader title="Coperti serviti" subtitle="Ultimi 7 giorni" />
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={last7Days} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs><linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={C.terra} stopOpacity={0.3} />
              <stop offset="100%" stopColor={C.terra} stopOpacity={0.02} />
            </linearGradient></defs>
            <CartesianGrid strokeDasharray="2 4" stroke={C.rule} vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: C.muted }} axisLine={{ stroke: C.rule }} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: C.muted }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: C.paper, border: `1px solid ${C.ink}`, borderRadius: 2, fontSize: 12 }} />
            <Area type="monotone" dataKey="servings" stroke={C.terra} strokeWidth={2} fill="url(#gradient)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>

    <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2 }}>
      <SectionHeader title="Riepilogo direzionale" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20 }}>
        <div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>VAL. STOCK</div>
          <div className="f-display" style={{ fontSize: 24, color: C.ink, fontWeight: 600, marginTop: 4 }}>{fmt.eurShort(state.lots.reduce((sum, l) => { if (l.status === "esaurito") return sum; const rec = state.receipts.find(r => r.id === l.receiptId); const line = rec?.lines.find(ln => ln.itemId === l.itemId); return sum + (line ? line.pricePerUnit * l.qtyRemaining : 0); }, 0))}</div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>ACQUISTATO 30G</div>
          <div className="f-display" style={{ fontSize: 24, color: C.ink, fontWeight: 600, marginTop: 4 }}>{fmt.eurShort(state.receipts.filter(r => daysBetween(r.date, TODAY) <= 30).reduce((s, r) => s + r.lines.reduce((ss, ln) => ss + ln.qty * ln.pricePerUnit, 0), 0))}</div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>DA PAGARE</div>
          <div className="f-display" style={{ fontSize: 24, color: C.terra, fontWeight: 600, marginTop: 4 }}>{fmt.eurShort(state.invoices.filter(i => i.status === "da_pagare").reduce((s, i) => s + i.amount, 0))}</div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>COPERTI 7G</div>
          <div className="f-display" style={{ fontSize: 24, color: C.ink, fontWeight: 600, marginTop: 4 }}>{last7Days.reduce((s, d) => s + d.servings, 0)}</div>
        </div>
      </div>
    </div>
  </div>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      FOOD COST · COSTRUZIONE (dai dati reali)             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
/**
 * Modulo per tracciare il COSTO REALE al kg di ogni prodotto base.
 *
 * Il prezzo fattura NON è il costo reale: dopo pulizia, sfilettatura,
 * abbattimento, il peso che arriva in cucina è inferiore. Il COSTO REALE
 * al kg utilizzabile = prezzo_fatt / (1 - calo_totale).
 *
 * Esempio: polpo 300/350 pentola
 *   - Prezzo fattura: €9.99/kg
 *   - Calo totale: 66.1%  (pulizia + riduzione bollitura)
 *   - Prezzo reale: €29.50/kg  ← questo è quello da usare nelle ricette
 */
function FoodCost({ state, setState }) {
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [sortBy, setSortBy] = useState("cat"); // cat | calo | realPrice | diff
  const toast = useToast();

  const categories = { pesce: { label: "Pesce & molluschi", color: C.teal },
    verdura: { label: "Verdure & frutta", color: C.olive },
    carne: { label: "Carne", color: C.terra },
    dispensa: { label: "Dispensa", color: C.amber },
    pasta: { label: "Pasta & cereali", color: C.amberDeep },
    altro: { label: "Altro", color: C.muted } };

  const products = state.baseProducts || [];
  const filtered = products.filter(p => {
    if (catFilter !== "all" && p.cat !== catFilter) return false;
    if (search && !p.nome.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    if (sortBy === "cat") return (a.cat || "").localeCompare(b.cat || "");
    if (sortBy === "calo") return (b.caloTot || 0) - (a.caloTot || 0);
    if (sortBy === "realPrice") return (b.pReale || 0) - (a.pReale || 0);
    if (sortBy === "diff") return ((b.pReale - b.pFatt) || 0) - ((a.pReale - a.pFatt) || 0);
    return 0;
  });

  // Summary
  const avgCalo = products.filter(p => p.caloTot > 0).reduce((s, p, _, arr) => s + p.caloTot / arr.length, 0);
  const maxCalo = products.reduce((m, p) => (p.caloTot || 0) > m.caloTot ? p : m, { caloTot: 0 });
  const priciestReal = products.reduce((m, p) => (p.pReale || 0) > (m.pReale || 0) ? p : m, { pReale: 0 });

  return <div className="fade-up">
    <PageHeader kicker={`${products.length} prodotti base · calo reale tracciato`} title="Costruzione food cost"
      subtitle="Il costo reale al kg considera i cali di pulizia e lavorazione. Polpo a €9.99/kg diventa €29.50/kg dopo pulizia e riduzione: questo è il prezzo da usare nelle ricette."
      right={<Btn variant="accent" size="lg" icon={Plus} onClick={() => setEditing({})}>Nuovo prodotto base</Btn>} />

    {editing && <BaseProductEditor product={editing} state={state} setState={setState} onClose={() => setEditing(null)} />}

    <div className="stagger" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
      <StatCard kicker="Prodotti base" value={products.length} unit="articoli" trend="importati dal tuo file" accent={C.ink} icon={Package} />
      <StatCard kicker="Calo medio" value={(avgCalo * 100).toFixed(1) + "%"} unit="su prodotti con lavorazione" trend="materia prima che si perde" accent={C.terra} icon={TrendingDown} />
      <StatCard kicker="Max calo" value={((maxCalo.caloTot || 0) * 100).toFixed(0) + "%"} unit={maxCalo.nome?.split(" ")[0]} trend={maxCalo.nome} accent={C.terra} icon={AlertTriangle} />
      <StatCard kicker="Più costoso reale" value={fmt.eur(priciestReal.pReale || 0)} unit="al kg" trend={priciestReal.nome} accent={C.teal} icon={TrendingUp} />
    </div>

    <div style={{ background: C.tealWash, border: `1px solid ${C.teal}`, padding: 14, borderRadius: 2, marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
      <Info size={16} color={C.teal} />
      <div className="f-body" style={{ fontSize: 12.5, color: C.tealDeep, lineHeight: 1.5 }}>
        Il <strong>prezzo reale</strong> è calcolato così: <span className="f-mono" style={{ background: C.white, padding: "1px 6px", borderRadius: 2 }}>prezzo_fattura / (1 − calo_totale)</span>. Quando una fattura porta prezzi nuovi, il sistema aggiornerà in cascata i prezzi reali e il food cost di tutti i piatti.
      </div>
    </div>

    <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 240, display: "flex", alignItems: "center", gap: 10, background: C.white, border: `1px solid ${C.rule}`, padding: "8px 14px", borderRadius: 2 }}>
        <Search size={15} color={C.muted} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca prodotto..." className="f-body" style={{ border: "none", outline: "none", flex: 1, fontSize: 13, background: "transparent", color: C.ink }} />
        {search && <IconBtn icon={X} size={13} onClick={() => setSearch("")} />}
      </div>
      <Select value={catFilter} onChange={e => setCatFilter(e.target.value)}>
        <option value="all">Tutte categorie ({products.length})</option>
        {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v.label} ({products.filter(p => p.cat === k).length})</option>)}
      </Select>
      <Select value={sortBy} onChange={e => setSortBy(e.target.value)}>
        <option value="cat">Ordine: categoria</option>
        <option value="calo">Ordine: maggior calo</option>
        <option value="realPrice">Ordine: più costoso reale</option>
        <option value="diff">Ordine: maggior differenza €</option>
      </Select>
    </div>

    <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 0.9fr 0.7fr 0.7fr 0.9fr 1fr 0.7fr 0.7fr 50px", padding: "10px 16px", background: C.paperDeep, fontSize: 9, color: C.muted, letterSpacing: "0.2em", fontFamily: "JetBrains Mono", textTransform: "uppercase" }}>
        <span>Prodotto</span><span>Categoria</span><span>Calo 1°</span><span>Calo 2°</span><span style={{ textAlign: "right" }}>Prezzo fatt.</span><span style={{ textAlign: "right" }}>Prezzo REALE</span><span style={{ textAlign: "right" }}>Calo %</span><span style={{ textAlign: "center" }}>Trend</span><span></span>
      </div>
      {filtered.length === 0 ? <div style={{ padding: 40, textAlign: "center" }}>
        <div className="f-body" style={{ fontSize: 13, color: C.muted }}>Nessun prodotto corrisponde ai filtri</div>
      </div> : filtered.map(p => {
        const cat = categories[p.cat] || categories.altro;
        const diff = (p.pReale || p.pFatt) - p.pFatt;
        const diffPct = p.pFatt > 0 ? (diff / p.pFatt) * 100 : 0;
        // Trend: ultimo cambio di prezzo negli ultimi 90gg
        const recentHistory = (p.priceHistory || []).filter(h => {
          const days = (new Date(TODAY) - new Date(h.at)) / 86400000;
          return days <= 90;
        });
        const lastChange = recentHistory[recentHistory.length - 1];
        return <div key={p.id} style={{ display: "grid", gridTemplateColumns: "2fr 0.9fr 0.7fr 0.7fr 0.9fr 1fr 0.7fr 0.7fr 50px", padding: "11px 16px", borderTop: `1px solid ${C.ruleSoft}`, alignItems: "center", fontSize: 13 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span style={{ width: 6, height: 6, background: cat.color, borderRadius: 6, flexShrink: 0 }} />
            <span className="f-body" style={{ color: C.ink, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.display || p.nome}</span>
            {p.vatRate != null && <span className="f-mono" style={{ fontSize: 8, color: C.teal, background: C.tealWash, padding: "1px 4px", borderRadius: 2, letterSpacing: "0.05em", fontWeight: 600, flexShrink: 0 }} title={`IVA acquisto override: ${(p.vatRate * 100).toFixed(0)}%`}>IVA{(p.vatRate * 100).toFixed(0)}</span>}
          </div>
          <span className="f-mono" style={{ fontSize: 10, color: cat.color, letterSpacing: "0.1em", textTransform: "uppercase" }}>{cat.label}</span>
          <span className="f-mono" style={{ color: p.calo1 > 0.3 ? C.terra : (p.calo1 > 0.1 ? C.amber : C.muted), fontSize: 12 }}>{p.calo1 > 0 ? (p.calo1 * 100).toFixed(1) + "%" : "—"}</span>
          <span className="f-mono" style={{ color: p.calo2 > 0.3 ? C.terra : (p.calo2 > 0.1 ? C.amber : C.muted), fontSize: 12 }}>{p.calo2 > 0 ? (p.calo2 * 100).toFixed(1) + "%" : "—"}</span>
          <span className="f-mono" style={{ color: C.inkSoft, textAlign: "right", fontSize: 12 }}>{p.pFatt > 0 ? fmt.eur(p.pFatt) : "—"}</span>
          <span className="f-mono" style={{ color: diffPct > 20 ? C.terra : C.ink, fontWeight: 600, textAlign: "right" }}>{(p.pReale || p.pFatt) > 0 ? fmt.eur(p.pReale || p.pFatt) : "—"}</span>
          <span className="f-mono" style={{ color: diffPct > 50 ? C.terra : (diffPct > 20 ? C.amber : C.muted), textAlign: "right", fontSize: 11 }}>{diff > 0.01 ? `+${diffPct.toFixed(0)}%` : "—"}</span>
          <div style={{ textAlign: "center", fontSize: 10 }}>
            {!lastChange ? <span style={{ color: C.muted }}>—</span>
            : <span title={`${fmt.date(lastChange.at)}: ${fmt.eur(lastChange.oldPrice)} → ${fmt.eur(lastChange.newPrice)}`} className="f-mono" style={{ color: lastChange.deltaPct > 5 ? C.terra : lastChange.deltaPct < -5 ? C.olive : C.amber, fontWeight: 600 }}>
              {lastChange.deltaPct > 0 ? "↑" : "↓"} {Math.abs(lastChange.deltaPct).toFixed(0)}%
            </span>}
          </div>
          <div style={{ textAlign: "right" }}><IconBtn icon={Settings} title="Modifica" onClick={() => setEditing(p)} /></div>
        </div>;
      })}
    </div>
  </div>;
}

function BaseProductEditor({ product, state, setState, onClose }) {
  const isNew = !product.id;
  const [p, setP] = useState(isNew ? {
    id: uid("bp_"), nome: "", display: "", cat: "pesce",
    unit: "kg", minStock: 3, shelfLifeDays: 3, allergens: [], preferredSupplier: state.suppliers[0]?.id || "",
    pFatt: 0, pReale: 0, calo1: 0, calo2: 0, caloTot: 0,
    t1: 0, t2: 0, tTot: 0, desc1: "", add: "", tGas: 0, tForno: 0, vatRate: null,
  } : { ...product });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const toast = useToast();
  const defaultVatP = state.settings?.vatRatePurchase || 0.10;
  // Piatti che usano questo prodotto (per informazione delete)
  const dishesUsing = useMemo(() => {
    if (!p.id) return [];
    return (state.dishesReal || []).filter(d => (d.ingredienti || []).some(ing => ing.bpId === p.id));
  }, [state.dishesReal, p.id]);

  // Recalc real price on change
  useEffect(() => {
    const calo = (p.calo1 || 0) + (p.calo2 || 0) - (p.calo1 || 0) * (p.calo2 || 0);
    const caloTot = Math.min(0.99, Math.max(0, calo));
    const pReale = caloTot < 0.99 ? p.pFatt / (1 - caloTot) : p.pFatt;
    setP(x => ({ ...x, caloTot, pReale: Math.round(pReale * 10000) / 10000, tTot: (x.t1 || 0) + (x.t2 || 0) }));
    // eslint-disable-next-line
  }, [p.calo1, p.calo2, p.pFatt, p.t1, p.t2]);

  const save = () => {
    if (!p.nome.trim()) { toast({ type: "error", message: "Inserisci il nome del prodotto" }); return; }
    const clean = { ...p, nome: p.nome.trim(), display: (p.display || p.nome).trim().slice(0, 50) };
    const newBPs = isNew ? [...(state.baseProducts || []), clean] : state.baseProducts.map(x => x.id === p.id ? clean : x);
    setState({ ...state, baseProducts: newBPs });
    toast({ type: "success", title: isNew ? "Prodotto creato" : "Salvato", message: `${clean.display} · prezzo reale ${fmt.eur(p.pReale)}/kg` });
    onClose();
  };

  const del = () => setConfirmDelete(true);
  const confirmDel = () => {
    const idToDel = p.id;
    const cleanedDishes = (state.dishesReal || []).map(d => ({
      ...d,
      ingredienti: d.ingredienti.map(ing => ing.bpId === idToDel ? { ...ing, bpId: null } : ing),
    }));
    setState({
      ...state,
      baseProducts: (state.baseProducts || []).filter(x => x.id !== idToDel),
      dishesReal: cleanedDishes,
    });
    toast({ type: "info", message: `"${p.display || p.nome}" eliminato` });
    setConfirmDelete(false);
    onClose();
  };

  const toggleAllergen = (a) => setP({ ...p, allergens: (p.allergens || []).includes(a) ? p.allergens.filter(x => x !== a) : [...(p.allergens || []), a] });
  const ALLERGENS = ["pesce", "crostacei", "molluschi", "glutine", "uova", "latte", "arachidi", "soia", "frutta a guscio", "sedano", "senape", "sesamo", "solfiti", "lupini"];

  return <Modal onClose={onClose} maxWidth={820} subtitle={isNew ? "Nuovo prodotto base" : "Modifica"} title={p.display || p.nome || "Nuovo"}
    footer={<>
      <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
      {!isNew && <Btn variant="danger" icon={Trash2} onClick={del}>Elimina</Btn>}
      <Btn variant="accent" icon={CheckCircle2} onClick={save}>Salva</Btn>
    </>}>
    {confirmDelete && <div onClick={() => setConfirmDelete(false)} style={{ position: "fixed", inset: 0, zIndex: 80, background: "rgba(28,26,23,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.paper, maxWidth: 460, width: "100%", borderRadius: 2, padding: 24, boxShadow: "0 24px 80px rgba(0,0,0,0.35)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <div style={{ width: 40, height: 40, background: C.terraWash, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Trash2 size={18} color={C.terra} />
          </div>
          <div>
            <div className="f-mono" style={{ fontSize: 10, color: C.terra, letterSpacing: "0.2em", fontWeight: 600 }}>CONFERMA ELIMINAZIONE</div>
            <div className="f-display" style={{ fontSize: 17, color: C.ink, fontWeight: 500, marginTop: 2 }}>Eliminare "{p.display || p.nome}"?</div>
          </div>
        </div>
        <div className="f-body" style={{ fontSize: 13, color: C.inkSoft, marginBottom: dishesUsing.length > 0 ? 10 : 18, lineHeight: 1.5 }}>
          Il prodotto base verrà rimosso dall'anagrafica. Lo storico prezzi, cali e cronologia andranno persi.
        </div>
        {dishesUsing.length > 0 && <div style={{ padding: 10, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <AlertTriangle size={13} color={C.amberDeep} />
            <div className="f-mono" style={{ fontSize: 10, color: C.amberDeep, letterSpacing: "0.15em", fontWeight: 600 }}>ATTENZIONE · {dishesUsing.length} PIATTI USANO QUESTO PRODOTTO</div>
          </div>
          <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, lineHeight: 1.5 }}>
            Perderanno il mapping ingrediente → prodotto base: <strong>{dishesUsing.slice(0, 5).map(d => d.nome).join(", ")}</strong>{dishesUsing.length > 5 ? ` e altri ${dishesUsing.length - 5}` : ""}. Dovrai rimappare a mano.
          </div>
        </div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setConfirmDelete(false)}>Annulla</Btn>
          <Btn variant="terra" icon={Trash2} onClick={confirmDel}>Sì, elimina</Btn>
        </div>
      </div>
    </div>}
    <div style={{ padding: 24 }}>

      {isNew && <div style={{ padding: 12, background: C.tealWash, borderRadius: 2, marginBottom: 18, display: "flex", gap: 10, alignItems: "center" }}>
        <Info size={15} color={C.teal} />
        <div className="f-body" style={{ fontSize: 12, color: C.tealDeep }}>Stai creando un nuovo prodotto base. Comparirà subito nelle liste di Acquisti, Ingresso merce, Magazzino e Richieste cucina.</div>
      </div>}

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 14, marginBottom: 18 }}>
        <Field label="Nome completo" required hint="Come compare nel tuo listino">
          <Input value={p.nome} onChange={e => setP({ ...p, nome: e.target.value, display: p.display || e.target.value.slice(0, 50) })} placeholder="es. SEPPIA INDIA TORRY U1 (ART 087121)" autoFocus={isNew} />
        </Field>
        <Field label="Nome breve" hint="Per schede e ricevute">
          <Input value={p.display} onChange={e => setP({ ...p, display: e.target.value })} placeholder="es. Seppia India U1" />
        </Field>
        <Field label="Categoria" required>
          <Select value={p.cat} onChange={e => setP({ ...p, cat: e.target.value })}>
            <option value="pesce">Pesce & molluschi</option>
            <option value="verdura">Verdure & frutta</option>
            <option value="carne">Carne</option>
            <option value="dispensa">Dispensa</option>
            <option value="pasta">Pasta & cereali</option>
            <option value="altro">Altro</option>
          </Select>
        </Field>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, padding: 16, background: C.paperDeep, borderRadius: 2, marginBottom: 20 }}>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.25em", marginBottom: 4 }}>PREZZO FATTURA</div>
          <div className="f-display" style={{ fontSize: 26, color: C.ink, fontWeight: 500 }}>{fmt.eur(p.pFatt)}</div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>al kg acquistato</div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.25em", marginBottom: 4 }}>CALO TOTALE</div>
          <div className="f-display" style={{ fontSize: 26, color: p.caloTot > 0.3 ? C.terra : C.ink, fontWeight: 500 }}>{(p.caloTot * 100).toFixed(1)}%</div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>materia prima persa</div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.teal, letterSpacing: "0.25em", marginBottom: 4 }}>PREZZO REALE → cucina</div>
          <div className="f-display" style={{ fontSize: 26, color: C.teal, fontWeight: 600 }}>{fmt.eur(p.pReale)}</div>
          <div className="f-mono" style={{ fontSize: 10, color: C.teal }}>usato per food cost</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: C.muted, marginBottom: 10 }}>1ª LAVORAZIONE</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="Calo 1° (0.10 = 10%)" hint="Es. pulizia, scarti">
              <Input type="number" step="0.01" min="0" max="1" value={p.calo1 || 0} onChange={e => setP({ ...p, calo1: parseFloat(e.target.value) || 0 })} />
            </Field>
            <Field label="Tempo 1° (minuti per kg)"><Input type="number" step="0.1" value={p.t1 || 0} onChange={e => setP({ ...p, t1: parseFloat(e.target.value) || 0 })} /></Field>
            <Field label="Descrizione"><Input value={p.desc1 || ""} onChange={e => setP({ ...p, desc1: e.target.value })} placeholder="es. pulire togliere scarti" /></Field>
          </div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: C.muted, marginBottom: 10 }}>2ª LAVORAZIONE</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="Calo 2° (0.10 = 10%)" hint="Es. cottura, riduzione">
              <Input type="number" step="0.01" min="0" max="1" value={p.calo2 || 0} onChange={e => setP({ ...p, calo2: parseFloat(e.target.value) || 0 })} />
            </Field>
            <Field label="Tempo 2° (minuti per kg)"><Input type="number" step="0.1" value={p.t2 || 0} onChange={e => setP({ ...p, t2: parseFloat(e.target.value) || 0 })} /></Field>
            <Field label="Additivi"><Input value={p.add || ""} onChange={e => setP({ ...p, add: e.target.value })} placeholder="es. 50 gr sale" /></Field>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
        <Field label="Prezzo fattura (€/kg)" hint="Aggiornato dalle fatture">
          <Input type="number" step="0.01" value={p.pFatt} onChange={e => setP({ ...p, pFatt: parseFloat(e.target.value) || 0 })} />
        </Field>
        <Field label="Cottura GAS (min/kg)"><Input type="number" step="0.1" value={p.tGas || 0} onChange={e => setP({ ...p, tGas: parseFloat(e.target.value) || 0 })} /></Field>
        <Field label="Cottura FORNO (min/kg)"><Input type="number" step="0.1" value={p.tForno || 0} onChange={e => setP({ ...p, tForno: parseFloat(e.target.value) || 0 })} /></Field>
      </div>

      <div style={{ marginTop: 14, marginBottom: 4, padding: 12, background: p.vatRate != null ? C.tealWash : C.paperDeep, border: `1px solid ${p.vatRate != null ? C.teal : C.rule}`, borderRadius: 2 }}>
        <div style={{ marginBottom: 8 }}>
          <div className="f-mono" style={{ fontSize: 10, color: p.vatRate != null ? C.tealDeep : C.muted, letterSpacing: "0.2em", fontWeight: 600 }}>
            IVA ACQUISTO {p.vatRate != null ? "· OVERRIDE ATTIVO" : "· USA DEFAULT"}
          </div>
          <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
            {p.vatRate != null
              ? `Usa IVA ${(p.vatRate * 100).toFixed(0)}% per questo prodotto (es. vino al 22%, pane al 4%).`
              : `Usa IVA di default acquisti (${(defaultVatP * 100).toFixed(0)}%). Se questo prodotto è soggetto a IVA diversa, sovrascrivi qui.`}
          </div>
        </div>
        <VatSelector value={p.vatRate} onChange={v => setP({ ...p, vatRate: v })} defaultRate={defaultVatP} />
      </div>

      <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: C.muted, marginTop: 24, marginBottom: 10 }}>DATI MAGAZZINO</div>
      <div style={{ display: "grid", gridTemplateColumns: "0.7fr 1fr 1fr 1.4fr", gap: 14, marginBottom: 14 }}>
        <Field label="Unità">
          <Select value={p.unit} onChange={e => setP({ ...p, unit: e.target.value })}>
            <option value="kg">kg</option><option value="l">l</option><option value="pz">pz</option>
          </Select>
        </Field>
        <Field label="Scorta minima" hint={`in ${p.unit}`}>
          <Input type="number" step="0.5" value={p.minStock} onChange={e => setP({ ...p, minStock: parseFloat(e.target.value) || 0 })} />
        </Field>
        <Field label="Shelf life" hint="giorni">
          <Input type="number" value={p.shelfLifeDays} onChange={e => setP({ ...p, shelfLifeDays: parseInt(e.target.value) || 7 })} />
        </Field>
        <Field label="Fornitore preferito">
          <Select value={p.preferredSupplier || ""} onChange={e => setP({ ...p, preferredSupplier: e.target.value })}>
            <option value="">— nessuno —</option>
            {state.suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
        </Field>
      </div>

      <Field label="Allergeni" hint="Clicca per selezionare/deselezionare">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {ALLERGENS.map(a => {
            const sel = (p.allergens || []).includes(a);
            return <button key={a} onClick={() => toggleAllergen(a)} className="tx" style={{ padding: "4px 10px", background: sel ? C.amber : C.white, color: sel ? C.white : C.inkSoft, border: `1px solid ${sel ? C.amber : C.rule}`, borderRadius: 12, fontSize: 11, cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize" }}>
              {a}
            </button>;
          })}
        </div>
      </Field>

      {(p.priceHistory && p.priceHistory.length > 0) && <div style={{ marginTop: 24 }}>
        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: C.muted, marginBottom: 10 }}>CRONOLOGIA PREZZI · ULTIMI {p.priceHistory.length} CAMBI</div>
        <div style={{ border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "100px 110px 110px 80px 100px 1fr", gap: 0, background: C.paperDeep, padding: "8px 12px", fontSize: 9, letterSpacing: "0.2em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
            <div>DATA</div><div>DA</div><div>A</div><div>Δ%</div><div>FONTE</div><div>FORNITORE</div>
          </div>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {[...p.priceHistory].reverse().map((h, i) => {
              const sup = state.suppliers.find(s => s.id === h.supplierId);
              return <div key={i} style={{ display: "grid", gridTemplateColumns: "100px 110px 110px 80px 100px 1fr", gap: 0, padding: "6px 12px", fontSize: 11, borderBottom: i < p.priceHistory.length - 1 ? `1px solid ${C.ruleSoft}` : "none", fontFamily: "JetBrains Mono, monospace", alignItems: "center" }}>
                <div style={{ color: C.ink }}>{fmt.date(h.at)}</div>
                <div style={{ color: C.muted, textDecoration: "line-through" }}>{fmt.eur(h.oldPrice)}</div>
                <div style={{ color: C.ink, fontWeight: 600 }}>{fmt.eur(h.newPrice)}</div>
                <div style={{ color: h.deltaPct > 10 ? C.terra : h.deltaPct > 0 ? C.amber : C.olive, fontWeight: 600 }}>{h.deltaPct > 0 ? "+" : ""}{h.deltaPct.toFixed(1)}%</div>
                <div style={{ color: C.inkSoft, fontSize: 10 }}>{h.source === "ddt" ? "bolla" : h.source}</div>
                <div style={{ color: C.inkSoft, fontSize: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sup?.name || "—"}</div>
              </div>;
            })}
          </div>
        </div>
        {p.lastPriceUpdate && <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 8, fontStyle: "italic" }}>
          Ultimo aggiornamento: {fmt.dateFull(p.lastPriceUpdate)} · Prezzo attuale: <strong>{fmt.eur(p.pFatt)}/kg</strong> fattura → <strong>{fmt.eur(p.pReale)}/kg</strong> reale (con calo {(p.caloTot * 100).toFixed(1)}%)
        </div>}
      </div>}
    </div>
  </Modal>;
}


// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      SIMULATORE MENU: margine ponderato e what-if          ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function SimulatoreMenu({ state, setState }) {
  // ── SIMULATORE = FORWARD-LOOKING (what-if su prezzi/quote FUTURE).
  //    Il calcolo live da bp.pReale corrente è CORRETTO: simula "se vendessi
  //    da DOMANI con questi prezzi, quale sarebbe il food cost ponderato?".
  //    Etichettato in UI per distinguerlo dai Report storici (Patch A).
  //    Toggle opzionale "Usa costi storici (snapshot)" come reality-check.
  // Per ogni piatto: costo reale, prezzo attuale, e una quota di vendita % ipotizzata
  const [shares, setShares] = useState(() => {
    // Default: quote proporzionali, distribuite su antipasti più (60%) rispetto ad altri (40%)
    const dishes = state.dishesReal || [];
    const acc = {};
    dishes.forEach(d => { acc[d.id] = 0; });
    return acc;
  });
  const [priceOverrides, setPriceOverrides] = useState({});
  const [coperti, setCoperti] = useState(50); // coperti al giorno stimati
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [costMode, setCostMode] = useState("live"); // "live" | "snapshot"

  const dishes = state.dishesReal || [];

  // Pre-index O(1) lookup
  const bpMap = useMemo(() => {
    const m = new Map();
    (state.baseProducts || []).forEach(b => m.set(b.id, b));
    return m;
  }, [state.baseProducts]);

  // Aggregazione snapshot per piatto (per il toggle "Usa costi storici")
  const snapshotByDish = useMemo(() => {
    const m = new Map();
    const receipts = state.tableReceipts || [];
    for (const r of receipts) {
      for (const it of (r.items || [])) {
        if (!it.dishId) continue;
        const qty = Number(it.qty) || 0;
        if (qty <= 0) continue;
        const snapCost = it.recipeSnapshot ? Number(it.recipeSnapshot.netCost) : NaN;
        if (!Number.isFinite(snapCost) || snapCost <= 0) continue;
        let row = m.get(it.dishId);
        if (!row) { row = { cost: 0, qty: 0 }; m.set(it.dishId, row); }
        row.cost += snapCost * qty;
        row.qty += qty;
      }
    }
    return m;
  }, [state.tableReceipts]);

  // Calcolo: per ogni piatto, costo (live o snapshot a seconda di costMode), prezzo, margine, quota%
  const rows = useMemo(() => dishes.map(d => {
    // Costo live (prezzi BP attuali)
    let costoLive = 0;
    (d.ingredienti || []).forEach(ing => {
      const bp = ing.bpId ? bpMap.get(ing.bpId) : null;
      const unit = bp ? (Number(bp.pReale) || Number(bp.pFatt) || 0) : (Number(ing.prezzo) || 0);
      costoLive += unit * (Number(ing.qty) || 0);
    });
    // Costo snapshot (storico medio effettivamente speso). Fallback live se nessuna vendita storica.
    const snap = snapshotByDish.get(d.id);
    const costoSnapshot = snap && snap.qty > 0 ? snap.cost / snap.qty : costoLive;
    const costoReale = costMode === "snapshot" ? costoSnapshot : costoLive;

    const prezzoGross = priceOverrides[d.id] !== undefined ? parseFloat(priceOverrides[d.id]) || d.prezzo : d.prezzo;
    const quotaPct = parseFloat(shares[d.id]) || 0;
    // IVA piatto: override se presente
    const costInfo = engine.computeDishCostWithVat(state, d);
    const econ = engine.dishEconomics(state, prezzoGross, costoReale, d.vatRate, costInfo.avgVatPurchase);
    return {
      ...d,
      costoRealeNetto: Math.round(costoReale * 100) / 100,
      costoDisplay: Math.round(econ.cost * 100) / 100,
      prezzoAttualeGross: d.prezzo,
      prezzoGross,
      prezzoDisplay: econ.price,
      margine: econ.margin,
      fcPct: econ.fcPct,
      quotaPct,
      effectiveVatSale: econ.vatSale,
      hasVatOverride: d.vatRate != null,
      hasSnapshot: !!(snap && snap.qty > 0),
    };
  }), [dishes, bpMap, shares, priceOverrides, state.settings, costMode, snapshotByDish]);

  const filtered = rows.filter(r => {
    if (catFilter !== "all" && r.cat !== catFilter) return false;
    if (search && !r.nome.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // KPI usando i valori nella modalità corrente
  const totalQuota = rows.reduce((s, r) => s + r.quotaPct, 0);
  const weightedFC = totalQuota > 0 ? rows.reduce((s, r) => s + (r.fcPct * r.quotaPct / 100), 0) : 0;
  const weightedMargin = totalQuota > 0 ? rows.reduce((s, r) => s + (r.margine * r.quotaPct / 100), 0) : 0;
  const avgScontrino = totalQuota > 0 ? rows.reduce((s, r) => s + (r.prezzoDisplay * r.quotaPct / 100), 0) : 0;
  const ricavoStimato = coperti * avgScontrino;
  const costoStimato = coperti * (avgScontrino * weightedFC / 100);
  const margineGiornaliero = ricavoStimato - costoStimato;
  const isGrossMode = engine.priceGross(state);

  const updateShare = (id, v) => setShares({ ...shares, [id]: v });
  const updatePrice = (id, v) => setPriceOverrides({ ...priceOverrides, [id]: v });

  const applyPriceChanges = () => {
    const changedDishes = Object.entries(priceOverrides).filter(([, v]) => v !== "").map(([id, v]) => ({ id, newPrice: parseFloat(v) }));
    if (changedDishes.length === 0) return;
    if (!confirm(`Applicare ${changedDishes.length} variazioni di prezzo di listino?\n(Il sistema aggiornerà i piatti nel menu carta.)`)) return;
    setState({
      ...state,
      dishesReal: state.dishesReal.map(d => {
        const ch = changedDishes.find(c => c.id === d.id);
        return ch ? { ...d, prezzo: ch.newPrice } : d;
      }),
    });
    setPriceOverrides({});
  };

  const resetShares = () => setShares({});
  const distributeEvenly = () => {
    if (filtered.length === 0) return;
    const each = Math.round((100 / filtered.length) * 10) / 10;
    const newShares = { ...shares };
    filtered.forEach(r => { newShares[r.id] = each; });
    setShares(newShares);
  };

  const cats2 = Object.fromEntries(Object.entries(DISH_CATEGORIES).map(([k, v]) => [k, v.label]));

  return <div className="fade-up">
    <PageHeader kicker="Direzione · Menu engineering" title="Simulatore menu"
      subtitle="Inserisci la quota % di vendita di ogni piatto e simula prezzi alternativi. Il sistema calcola food cost medio ponderato, margine ponderato e ricavo stimato giornaliero." />

    <div className="stagger" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 }}>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 16, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em", marginBottom: 6 }}>COPERTI / GIORNO</div>
        <Input type="number" value={coperti} onChange={e => setCoperti(parseInt(e.target.value) || 0)} style={{ fontSize: 22, padding: "6px 10px", fontWeight: 600 }} />
      </div>
      <StatCard kicker="F.C. medio ponderato" value={weightedFC.toFixed(1) + "%"} unit={totalQuota < 100 ? `quota: ${totalQuota.toFixed(0)}%` : "quota: 100%"} trend={weightedFC < 30 ? "ottimo" : weightedFC < 35 ? "OK" : "da rivedere"} accent={weightedFC > 35 ? C.terra : weightedFC > 30 ? C.amber : C.olive} icon={Activity} />
      <StatCard kicker="Scontrino medio piatto" value={fmt.eur(avgScontrino)} unit="per piatto venduto" trend={`margine: ${fmt.eur(weightedMargin)}`} accent={C.teal} icon={TrendingUp} />
      <StatCard kicker="Margine giornaliero" value={fmt.eurShort(margineGiornaliero)} unit={`su ${coperti} coperti`} trend={`ricavo stimato: ${fmt.eurShort(ricavoStimato)}`} accent={C.ink} icon={BarChart3} />
    </div>

    <div style={{ background: C.tealWash, border: `1px solid ${C.teal}`, padding: 12, borderRadius: 2, marginBottom: 16, display: "flex", gap: 12, alignItems: "flex-start" }}>
      <Info size={15} color={C.teal} style={{ marginTop: 2, flexShrink: 0 }} />
      <div className="f-body" style={{ fontSize: 12, color: C.tealDeep, lineHeight: 1.5 }}>
        Inserisci nella colonna <strong>Quota %</strong> la percentuale di scontrini che contengono quel piatto. Se totale = 100%, il food cost ponderato è preciso. Usa "Distribuisci equamente" per dare ad ogni piatto la stessa quota. Modifica la colonna <strong>Prezzo simulato</strong> per vedere l'effetto di variazioni di listino senza toccare il menu reale, poi clicca "Applica al menu" quando sei soddisfatto.
      </div>
    </div>

    <div style={{ background: costMode === "live" ? C.amberWash : C.tealWash, border: `1px solid ${costMode === "live" ? C.amber : C.teal}`, padding: 10, borderRadius: 2, marginBottom: 16, display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 280 }}>
        <Info size={14} color={costMode === "live" ? C.amber : C.tealDeep} style={{ flexShrink: 0 }} />
        <span className="f-body" style={{ fontSize: 12, color: C.ink, lineHeight: 1.4 }}>
          {costMode === "live"
            ? <><strong>Simulazione forward-looking</strong> · costi calcolati sui <strong>prezzi BP attuali (live)</strong>. Risponde a "se vendessi da domani con questo prezzo X, quale food cost avrei?". I numeri del <strong>Report storico</strong> usano invece gli snapshot immutabili al momento della vendita.</>
            : <><strong>Modalità reality-check</strong> · costi calcolati su <strong>media storica snapshot</strong> (= identici al Report). Utile per verificare congruenza tra simulazione e storia.</>}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, background: C.white, border: `1px solid ${C.ruleSoft}`, borderRadius: 2, padding: 2 }}>
        <button onClick={() => setCostMode("live")} className="f-mono" style={{ fontSize: 10, padding: "4px 10px", letterSpacing: "0.1em", border: "none", borderRadius: 2, background: costMode === "live" ? C.ink : "transparent", color: costMode === "live" ? C.paper : C.muted, cursor: "pointer", fontWeight: 600 }}>LIVE (PREZZI OGGI)</button>
        <button onClick={() => setCostMode("snapshot")} className="f-mono" style={{ fontSize: 10, padding: "4px 10px", letterSpacing: "0.1em", border: "none", borderRadius: 2, background: costMode === "snapshot" ? C.ink : "transparent", color: costMode === "snapshot" ? C.paper : C.muted, cursor: "pointer", fontWeight: 600 }}>SNAPSHOT (STORICO)</button>
      </div>
    </div>

    <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 240, display: "flex", alignItems: "center", gap: 10, background: C.white, border: `1px solid ${C.rule}`, padding: "8px 14px", borderRadius: 2 }}>
        <Search size={15} color={C.muted} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca piatto..." className="f-body" style={{ border: "none", outline: "none", flex: 1, fontSize: 13, background: "transparent", color: C.ink }} />
      </div>
      <Select value={catFilter} onChange={e => setCatFilter(e.target.value)}>
        <option value="all">Tutte categorie ({rows.length})</option>
        {Object.entries(cats2).map(([k, v]) => <option key={k} value={k}>{v} ({rows.filter(r => r.cat === k).length})</option>)}
      </Select>
      <Btn variant="ghost" size="sm" onClick={distributeEvenly}>Distribuisci equamente</Btn>
      <Btn variant="ghost" size="sm" icon={X} onClick={resetShares}>Azzera quote</Btn>
      {Object.values(priceOverrides).some(v => v !== "") && <Btn variant="accent" size="sm" icon={CheckCircle2} onClick={applyPriceChanges}>Applica prezzi al menu</Btn>}
    </div>

    <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.8fr 0.9fr 0.9fr 0.7fr 0.8fr 1fr", padding: "10px 14px", background: C.paperDeep, fontSize: 9, color: C.muted, letterSpacing: "0.2em", fontFamily: "JetBrains Mono", textTransform: "uppercase" }}>
        <span>Piatto</span><span>Cat.</span><span style={{ textAlign: "right" }}>Costo {isGrossMode ? "lordo" : "netto"}</span><span style={{ textAlign: "right" }}>Listino {isGrossMode ? "lordo" : "netto"}</span><span style={{ textAlign: "right" }}>Simula € lordo</span><span style={{ textAlign: "right" }}>F.C. %</span><span style={{ textAlign: "right" }}>Margine {isGrossMode ? "lordo" : "netto"}</span><span style={{ textAlign: "center" }}>Quota vendita</span>
      </div>
      {filtered.map(r => {
        const override = priceOverrides[r.id];
        const isEdited = override !== undefined && override !== "";
        return <div key={r.id} style={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.8fr 0.9fr 0.9fr 0.7fr 0.8fr 1fr", padding: "8px 14px", borderTop: `1px solid ${C.ruleSoft}`, alignItems: "center", fontSize: 12, background: isEdited ? C.amberWash : "transparent" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <div className="f-body" style={{ color: C.ink, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>{r.nome}</div>
            {r.hasVatOverride && <span className="f-mono" style={{ fontSize: 9, color: C.teal, background: C.tealWash, padding: "1px 4px", borderRadius: 2, letterSpacing: "0.05em", fontWeight: 600, flexShrink: 0 }} title={`IVA ${(r.effectiveVatSale * 100).toFixed(0)}%`}>IVA{(r.effectiveVatSale * 100).toFixed(0)}</span>}
          </div>
          <span className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>{r.cat}</span>
          <span className="f-mono" style={{ color: C.inkSoft, textAlign: "right" }}>{fmt.eur(r.costoDisplay)}</span>
          <span className="f-mono" style={{ color: C.ink, textAlign: "right", fontWeight: 500 }}>{fmt.eur(r.prezzoDisplay)}</span>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <input type="number" step="0.50" value={override ?? ""} onChange={e => updatePrice(r.id, e.target.value)} placeholder={r.prezzoAttualeGross.toFixed(2)} title="Sempre LORDO (come menu cliente)" className="f-mono" style={{ width: 70, padding: "3px 6px", border: `1px solid ${isEdited ? C.amber : C.ruleSoft}`, borderRadius: 2, fontSize: 11, textAlign: "right", background: isEdited ? C.white : "transparent" }} />
          </div>
          <span className="f-mono" style={{ color: r.fcPct > 35 ? C.terra : r.fcPct > 28 ? C.amber : C.olive, textAlign: "right", fontWeight: 600 }}>{r.fcPct.toFixed(0)}%</span>
          <span className="f-mono" style={{ color: r.margine > 0 ? C.olive : C.terra, textAlign: "right", fontWeight: 500 }}>{fmt.eur(r.margine)}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
            <input type="number" step="1" min="0" max="100" value={shares[r.id] ?? ""} onChange={e => updateShare(r.id, e.target.value)} placeholder="0" className="f-mono" style={{ width: 50, padding: "3px 6px", border: `1px solid ${C.ruleSoft}`, borderRadius: 2, fontSize: 11, textAlign: "right" }} />
            <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>%</span>
          </div>
        </div>;
      })}
      {totalQuota > 0 && <div style={{ padding: "10px 14px", background: Math.abs(totalQuota - 100) > 0.5 ? C.amberWash : C.tealWash, borderTop: `2px solid ${Math.abs(totalQuota - 100) > 0.5 ? C.amber : C.teal}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>
          {Math.abs(totalQuota - 100) > 0.5 ? `⚠ Totale quote: ${totalQuota.toFixed(1)}% (dovrebbe essere 100% per simulazione precisa)` : `✓ Totale quote: 100%`}
        </span>
        <span className="f-mono" style={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>F.C. ponderato: {weightedFC.toFixed(1)}%</span>
      </div>}
    </div>
  </div>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                  IMPORT QUALITY PANEL                                      ║
// ║  Dashboard permanente sulla qualità dei dati importati.                    ║
// ║  Mostra tasso unmatched, snapshot mancante, banco, fuori turno e top      ║
// ║  articoli da mappare. Severity calibrata su pctUnmatched (impatto FC).    ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function ImportQualityPanel({ state, setState }) {
  const quality = useMemo(
    () => engine.stats.importQuality(state, 30),
    [state.tableReceipts, state.dishIgnoredArticles]
  );
  const toast = useToast();

  // Conta gli item con dishId _unmatched_ negli scontrini esistenti (esclusi gli ignorati).
  // Se > 0 e l'anagrafica piatti si è arricchita, ha senso offrire un re-match retroattivo.
  const unmatchedItemsCount = useMemo(() => {
    const ignoredSet = new Set(state.dishIgnoredArticles || []);
    let n = 0;
    (state.tableReceipts || []).forEach(r => {
      (r.items || []).forEach(it => {
        if (it.dishId && String(it.dishId).startsWith("_unmatched_")) {
          if (!ignoredSet.has(normStr(it.name || ""))) n++;
        }
      });
    });
    return n;
  }, [state.tableReceipts, state.dishIgnoredArticles]);

  // Tutti gli articoli _unmatched_ da TUTTI gli scontrini (non solo ultimi 30gg),
  // raggruppati per nome, ordinati per fatturato decrescente. Esclude gli ignorati.
  // Chiave alias = normStr(it.name) — per gli unmatched it.name === nomeArt originale.
  const allUnmatched = useMemo(() => {
    const ignoredSet = new Set(state.dishIgnoredArticles || []);
    const map = new Map(); // name → { qty, rev }
    (state.tableReceipts || []).forEach(r => {
      (r.items || []).forEach(it => {
        if (!it.dishId || !String(it.dishId).startsWith("_unmatched_")) return;
        const name = it.name || "?";
        if (ignoredSet.has(normStr(name))) return;
        const qty = Number(it.qty) || 0;
        const rev = qty * (Number(it.unitPrice) || 0);
        const m = map.get(name) || { qty: 0, rev: 0 };
        m.qty += qty;
        m.rev += rev;
        map.set(name, m);
      });
    });
    return Array.from(map.entries())
      .map(([name, v]) => ({ name, qty: v.qty, rev: v.rev }))
      .sort((a, b) => b.rev - a.rev);
  }, [state.tableReceipts, state.dishIgnoredArticles]);

  // Helper retroattivo condiviso: applica un alias manuale (articleName → dishId)
  // su TUTTI gli scontrini, ricostruisce recipeSnapshot + _listinoPrice + aggregati sconto.
  // Stessa logica di rematchUnmatched (non duplicata: estratta qui e riusata da entrambi).
  const applyAliasRetroactive = (aliasMap, currentReceipts, currentState) => {
    const dishes = currentState.dishesReal || [];
    const baseProducts = currentState.baseProducts || [];
    let totalUpdated = 0;
    let receiptsTouched = 0;
    const newReceipts = currentReceipts.map(r => {
      let touched = false;
      const newItems = (r.items || []).map(it => {
        if (!it.dishId || !String(it.dishId).startsWith("_unmatched_")) return it;
        const queryName = it._csvName || it.name;
        const aliasKey = normStr(queryName);
        if (!aliasMap[aliasKey]) return it;
        const dish = dishes.find(d => d.id === aliasMap[aliasKey]);
        if (!dish) return it;
        totalUpdated++;
        touched = true;
        let recipeSnapshot = null;
        if (dish.ingredienti && dish.ingredienti.length > 0) {
          const snapIngs = dish.ingredienti.map(ing => {
            const bp = ing.bpId ? baseProducts.find(b => b.id === ing.bpId) : null;
            return {
              name: ing.name, qty: ing.qty, unit: ing.unit, bpId: ing.bpId,
              bpName: bp?.display || bp?.nome || null,
              unitCostNet: bp ? (bp.pReale || bp.pFatt || 0) : (ing.prezzo || 0),
              vatRate: bp?.vatRate != null ? bp.vatRate : null,
            };
          });
          const snapCost = snapIngs.reduce((s, ing) => s + ing.qty * (ing.unitCostNet || 0), 0);
          recipeSnapshot = { cat: dish.cat, vatRate: dish.vatRate, ingredienti: snapIngs, netCost: Math.round(snapCost * 10000) / 10000 };
        }
        let newListinoPrice;
        if (isMenuConcordato(it)) newListinoPrice = 0;
        else if (it._isGift) newListinoPrice = 0;
        else if (isDiscountLine(it)) newListinoPrice = 0;
        else if (it.unitPrice === 0 && Number(dish.prezzo) > 0) newListinoPrice = Number(dish.prezzo);
        else newListinoPrice = Number(it.unitPrice) || 0;
        return {
          ...it,
          dishId: dish.id,
          name: dish.nome,
          recipeSnapshot,
          _listinoPrice: newListinoPrice,
          _rematched: true,
        };
      });
      if (touched) {
        receiptsTouched++;
        const listinoTotal = newItems.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it._listinoPrice) || 0), 0);
        const realTotal = r.total;
        const disc = listinoTotal - realTotal;
        const discPct = listinoTotal > 0 ? disc / listinoTotal : 0;
        return {
          ...r,
          items: newItems,
          _listinoTotal: Math.round(listinoTotal * 100) / 100,
          _discount: Math.round(disc * 100) / 100,
          _discountPct: Math.round(discPct * 10000) / 10000,
        };
      }
      return r;
    });
    return { newReceipts, totalUpdated, receiptsTouched };
  };

  // Re-match retroattivo automatico (fuzzy >= 0.85): salva nuovi alias e aggiorna scontrini.
  const rematchUnmatched = () => {
    if (!setState) return;
    const dishes = state.dishesReal || [];
    const aliases = { ...(state.dishAliases || {}) };
    if (dishes.length === 0) {
      toast({ type: "error", title: "Nessun piatto in anagrafica", message: "Crea prima i piatti che vuoi matchare" });
      return;
    }
    // Prima passa: costruisci il dizionario di alias trovati dal fuzzy
    const newAliasMap = { ...aliases };
    (state.tableReceipts || []).forEach(r => {
      (r.items || []).forEach(it => {
        if (!it.dishId || !String(it.dishId).startsWith("_unmatched_")) return;
        const queryName = it._csvName || it.name;
        const key = normStr(queryName);
        if (newAliasMap[key]) return; // già noto
        const match = matchDishFuzzy(queryName, dishes, newAliasMap);
        if (match) newAliasMap[key] = match.dish.id;
      });
    });
    const { newReceipts, totalUpdated, receiptsTouched } = applyAliasRetroactive(newAliasMap, state.tableReceipts || [], state);
    setState({ ...state, tableReceipts: newReceipts, dishAliases: newAliasMap });
    if (totalUpdated > 0) {
      toast({ type: "success", title: "Re-match completato", message: `${totalUpdated} articoli ri-mappati su ${receiptsTouched} scontrini · alias salvati` });
    } else {
      toast({ type: "info", title: "Nessun match trovato", message: `${unmatchedItemsCount} articoli ancora non riconosciuti. Verifica che i nomi in anagrafica corrispondano a quelli del CSV.` });
    }
  };

  // Collegamento manuale: salva alias + applica retroattivamente su tutti gli scontrini.
  const linkArticleToDish = (articleName, dishId) => {
    if (!setState || !articleName || !dishId) return;
    const dish = (state.dishesReal || []).find(d => d.id === dishId);
    if (!dish) return;
    const aliasKey = normStr(articleName);
    const newAliases = { ...(state.dishAliases || {}), [aliasKey]: dishId };
    const { newReceipts, totalUpdated } = applyAliasRetroactive(newAliases, state.tableReceipts || [], state);
    setState({ ...state, tableReceipts: newReceipts, dishAliases: newAliases });
    toast({
      type: "success",
      title: "Articolo collegato",
      message: `${totalUpdated} articol${totalUpdated === 1 ? "o" : "i"} "${articleName}" collegat${totalUpdated === 1 ? "o" : "i"} a "${dish.nome}" · alias salvato`,
    });
  };

  // Ignora articolo: aggiunge a dishIgnoredArticles (normStr key).
  // L'articolo scompare da topMissing e non conta come food-cost-cieco.
  const ignoreArticle = (articleName) => {
    if (!setState || !articleName) return;
    const key = normStr(articleName);
    const current = state.dishIgnoredArticles || [];
    if (current.includes(key)) return;
    setState({ ...state, dishIgnoredArticles: [...current, key] });
    toast({ type: "info", title: "Articolo ignorato", message: `"${articleName}" non comparirà più nella lista da mappare` });
  };

  // Collassabile: lo stato vive in localStorage così la scelta persiste tra reload.
  // Default: aperto (il pannello è informativo, dismissible da Andi quando ha letto).
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("bottega_quality_panel_collapsed") === "1"; }
    catch { return false; }
  });
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem("bottega_quality_panel_collapsed", next ? "1" : "0"); } catch {}
  };

  // Lista articoli espansa: true = mostra tutti; false = solo i top 5
  const [showAllUnmatched, setShowAllUnmatched] = useState(false);
  // Filtro testo per il dropdown piatti (per articolo). Map: articleName → filterText
  const [dishFilterMap, setDishFilterMap] = useState({});
  // Selezione pendente (select value) per ogni articolo. Map: articleName → dishId
  const [pendingDishMap, setPendingDishMap] = useState({});

  const setDishFilter = (name, val) => setDishFilterMap(prev => ({ ...prev, [name]: val }));
  const setPendingDish = (name, val) => setPendingDishMap(prev => ({ ...prev, [name]: val }));

  if (quality.fallback || quality.nReceipts === 0) return null;
  const pct = (v) => (v * 100).toFixed(1) + "%";
  // Severity sul tasso unmatched O sul tasso riconciliazione fallita: il peggiore vince.
  // > 15% di scontrini con discrepanza totale → food cost compromesso quanto unmatched alti.
  const pctRec = quality.pctReconcileWarnings || 0;
  const severityUnmatched = quality.pctUnmatched > 0.15 ? "high"
                          : quality.pctUnmatched > 0.05 ? "medium"
                          : "low";
  const severityReconcile = pctRec > 0.15 ? "high"
                          : pctRec > 0.05 ? "medium"
                          : "low";
  const sevRank = { low: 0, medium: 1, high: 2 };
  const severity = sevRank[severityUnmatched] >= sevRank[severityReconcile]
    ? severityUnmatched : severityReconcile;
  const bgColor = severity === "high" ? C.terraWash
                : severity === "medium" ? C.amberWash
                : C.oliveWash;
  const accentColor = severity === "high" ? C.terraDeep
                    : severity === "medium" ? C.amberDeep
                    : C.oliveDeep;
  const borderColor = severity === "high" ? C.terra
                    : severity === "medium" ? C.amber
                    : C.olive;
  const severityLabel = severity === "high" ? "AZIONE URGENTE"
                      : severity === "medium" ? "DA MONITORARE"
                      : "QUALITÀ IN LINEA";

  // Piatti disponibili per il dropdown, ordinati alfabeticamente
  const dishOptions = useMemo(
    () => [...(state.dishesReal || [])].sort((a, b) => a.nome.localeCompare(b.nome, "it")),
    [state.dishesReal]
  );

  // Lista articoli da mostrare nel pannello mappatura
  const unmatchedToShow = showAllUnmatched ? allUnmatched : allUnmatched.slice(0, 5);
  const hasMore = allUnmatched.length > 5;

  return (
    <div style={{
      background: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: 2,
      padding: 16,
      marginBottom: 18,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: collapsed ? 0 : 10, gap: 10 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.25em" }}>
          QUALITÀ DATI IMPORT · ULTIMI 30 GG · n={quality.nReceipts} SCONTRINI
          {collapsed && severity !== "low" && <span style={{ marginLeft: 10, color: accentColor, fontWeight: 600 }}>{pct(quality.pctUnmatched)} food cost cieco · {fmt.eurShort(quality.unmatchedRev)}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="f-mono" style={{
            fontSize: 10, color: accentColor, fontWeight: 600, letterSpacing: "0.15em",
            padding: "2px 8px", background: C.white, borderRadius: 2,
          }}>{severityLabel}</div>
          <button onClick={toggle} title={collapsed ? "Espandi pannello qualità" : "Comprimi pannello qualità"} style={{
            background: "transparent", border: `1px solid ${borderColor}`, color: accentColor,
            fontFamily: "JetBrains Mono, monospace", fontSize: 11, fontWeight: 600,
            padding: "2px 8px", borderRadius: 2, cursor: "pointer", lineHeight: 1.2,
          }}>{collapsed ? "▾ mostra" : "▴ nascondi"}</button>
        </div>
      </div>
      {!collapsed && <div style={{ display: "grid", gridTemplateColumns: pctRec > 0 ? "repeat(5, 1fr)" : "repeat(4, 1fr)", gap: 16 }}>
        <div>
          <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: accentColor }}>{pct(quality.pctUnmatched)}</div>
          <div className="f-body" style={{ fontSize: 11, color: C.inkMid, marginTop: 2 }}>
            Food cost <strong>cieco</strong> · {fmt.eurShort(quality.unmatchedRev)}
          </div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>
            articoli non mappati a ricetta
          </div>
        </div>
        <div>
          <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink }}>{pct(quality.pctNoSnapshot)}</div>
          <div className="f-body" style={{ fontSize: 11, color: C.inkMid, marginTop: 2 }}>
            Scontrini <strong>senza ricetta</strong>
          </div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>
            nessun recipeSnapshot.netCost
          </div>
        </div>
        <div>
          <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink }}>{pct(quality.pctBanco)}</div>
          <div className="f-body" style={{ fontSize: 11, color: C.inkMid, marginTop: 2 }}>
            Scontrini <strong>banco</strong>
          </div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>
            covers = 0 (asporto / banco)
          </div>
        </div>
        <div>
          <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink }}>{pct(quality.pctOutOfShift)}</div>
          <div className="f-body" style={{ fontSize: 11, color: C.inkMid, marginTop: 2 }}>
            <strong>Fuori turno</strong>
          </div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>
            chiusi fuori orario servizio (≈ 04-09)
          </div>
        </div>
        {pctRec > 0 && (
          <div>
            <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: pctRec > 0.05 ? accentColor : C.ink }}>{pct(pctRec)}</div>
            <div className="f-body" style={{ fontSize: 11, color: C.inkMid, marginTop: 2 }}>
              <strong>Discrepanza totale</strong> · {fmt.eurShort(quality.reconcileDiffSum || 0)}
            </div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>
              dichiarato ≠ Σ righe (storni / sconti)
            </div>
          </div>
        )}
      </div>}
      {!collapsed && allUnmatched.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${borderColor}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>
              ARTICOLI DA MAPPARE A RICETTA · {allUnmatched.length} totali (tutti i periodi)
            </div>
            {setState && unmatchedItemsCount > 0 && (
              <Btn variant="ghost" size="sm" icon={RefreshCw} onClick={rematchUnmatched} title="Ri-applica match automatico fuzzy sugli scontrini esistenti">
                Ri-applica match
              </Btn>
            )}
          </div>
          {/* Tabella articoli da mappare con azioni inline */}
          <div style={{ background: C.white, borderRadius: 2, border: `1px solid ${C.ruleSoft}`, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 60px 220px 90px", padding: "5px 10px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.15em", color: C.muted, fontFamily: "JetBrains Mono, monospace", gap: 8, alignItems: "center" }}>
              <span>ARTICOLO CSV</span>
              <span style={{ textAlign: "right" }}>QTÀ</span>
              <span style={{ textAlign: "right" }}>RICAVO</span>
              <span>COLLEGA A PIATTO</span>
              <span></span>
            </div>
            {unmatchedToShow.map((m, idx) => {
              const filterText = (dishFilterMap[m.name] || "").toLowerCase();
              const filteredDishes = filterText
                ? dishOptions.filter(d => d.nome.toLowerCase().includes(filterText))
                : dishOptions;
              const pendingId = pendingDishMap[m.name] || "";
              return (
                <div key={m.name} style={{ display: "grid", gridTemplateColumns: "1fr 80px 60px 220px 90px", padding: "6px 10px", borderTop: idx === 0 ? "none" : `1px solid ${C.ruleSoft}`, gap: 8, alignItems: "center", background: C.white }}>
                  {/* Nome articolo CSV */}
                  <span className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.name}>
                    {m.name}
                  </span>
                  {/* Quantità */}
                  <span className="f-mono" style={{ fontSize: 11, color: C.inkSoft, textAlign: "right" }}>
                    {m.qty.toFixed(0)}×
                  </span>
                  {/* Ricavo */}
                  <span className="f-mono" style={{ fontSize: 11, color: C.inkSoft, textAlign: "right" }}>
                    {fmt.eurShort(m.rev)}
                  </span>
                  {/* Dropdown collega a piatto con filtro testo */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <input
                      type="text"
                      placeholder="Filtra piatto..."
                      value={dishFilterMap[m.name] || ""}
                      onChange={e => setDishFilter(m.name, e.target.value)}
                      style={{
                        fontSize: 10, padding: "2px 6px", border: `1px solid ${C.rule}`, borderRadius: 2,
                        background: C.paperSoft, color: C.ink, fontFamily: "JetBrains Mono, monospace",
                        width: "100%", boxSizing: "border-box",
                      }}
                    />
                    <select
                      value={pendingId}
                      onChange={e => setPendingDish(m.name, e.target.value)}
                      style={{
                        fontSize: 11, padding: "3px 6px", border: `1px solid ${C.rule}`, borderRadius: 2,
                        background: C.white, color: pendingId ? C.ink : C.muted,
                        fontFamily: "Geist, system-ui, sans-serif", width: "100%", cursor: "pointer",
                      }}
                    >
                      <option value="">-- seleziona piatto --</option>
                      {filteredDishes.map(d => (
                        <option key={d.id} value={d.id}>{d.nome}</option>
                      ))}
                      {filteredDishes.length === 0 && <option disabled value="">Nessun piatto trovato</option>}
                    </select>
                  </div>
                  {/* Azioni: Collega + Ignora */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <button
                      disabled={!pendingId || !setState}
                      onClick={() => {
                        if (pendingId) {
                          linkArticleToDish(m.name, pendingId);
                          setPendingDish(m.name, "");
                          setDishFilter(m.name, "");
                        }
                      }}
                      title="Collega questo articolo al piatto selezionato e applica retroattivamente a tutti gli scontrini"
                      style={{
                        fontSize: 10, padding: "3px 8px", borderRadius: 2, cursor: pendingId ? "pointer" : "not-allowed",
                        background: pendingId ? C.teal : C.ruleSoft, color: pendingId ? C.white : C.muted,
                        border: "none", fontFamily: "JetBrains Mono, monospace", fontWeight: 600,
                        transition: "background 0.15s",
                      }}
                    >
                      Collega
                    </button>
                    <button
                      disabled={!setState}
                      onClick={() => ignoreArticle(m.name)}
                      title="Marca come non-piatto: non apparirà più nella lista da mappare e non conta come food-cost-cieco"
                      style={{
                        fontSize: 10, padding: "3px 8px", borderRadius: 2, cursor: "pointer",
                        background: "transparent", color: C.muted, border: `1px solid ${C.rule}`,
                        fontFamily: "JetBrains Mono, monospace",
                        transition: "color 0.15s, border-color 0.15s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color = C.terra; e.currentTarget.style.borderColor = C.terra; }}
                      onMouseLeave={e => { e.currentTarget.style.color = C.muted; e.currentTarget.style.borderColor = C.rule; }}
                    >
                      Ignora
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Mostra tutti / meno */}
          {hasMore && (
            <button
              onClick={() => setShowAllUnmatched(v => !v)}
              style={{
                marginTop: 6, background: "transparent", border: "none",
                color: accentColor, fontFamily: "JetBrains Mono, monospace", fontSize: 10,
                cursor: "pointer", padding: "2px 0", fontWeight: 600,
              }}
            >
              {showAllUnmatched ? `▴ Mostra solo top 5` : `▾ Mostra tutti (${allUnmatched.length})`}
            </button>
          )}
          {/* Nota sugli ignorati */}
          {(state.dishIgnoredArticles || []).length > 0 && (
            <div className="f-mono" style={{ marginTop: 6, fontSize: 9, color: C.muted }}>
              {(state.dishIgnoredArticles || []).length} articol{(state.dishIgnoredArticles || []).length === 1 ? "o ignorato" : "i ignorati"} (non piatti). Per rivedere, vai in Impostazioni → Dati.
            </div>
          )}
        </div>
      )}
      {!collapsed && allUnmatched.length === 0 && unmatchedItemsCount === 0 && quality.pctUnmatched === 0 && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${borderColor}` }}>
          <div className="f-body" style={{ fontSize: 11, color: C.olive, fontStyle: "italic" }}>
            Tutti gli articoli sono collegati a un piatto. Food cost completo.
          </div>
        </div>
      )}
    </div>
  );
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║          UNDO IMPORT — helpers delta                                        ║
// ║                                                                             ║
// ║  buildUndoDelta: costruisce il delta minimo per annullare un import.        ║
// ║  Salva solo gli ID aggiunti e gli scontrini RIMOSSI (in caso replace),      ║
// ║  invece dell'intera copia di tableReceipts. Questo riduce drasticamente     ║
// ║  la dimensione dello snapshot (da O(N) a O(delta)).                         ║
// ║                                                                             ║
// ║  applyUndoDelta: ripristina tableReceipts applicando il delta inverso.      ║
// ║  Comportamento identico al vecchio ripristino con prevTableReceipts:        ║
// ║    - rimuove gli scontrini aggiunti dall'import                             ║
// ║    - ri-inserisce gli scontrini che erano stati rimossi (replace)           ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Costruisce il delta minimo per annullare un import.
 * @param {Array} addedReceipts - scontrini che verranno aggiunti da questo import
 * @param {Array} removedReceipts - scontrini rimossi (solo in caso conflictAction=replace; [] altrimenti)
 * @returns {{ addedIds: string[], removedReceipts: Array }}
 */
function buildUndoDelta(addedReceipts, removedReceipts) {
  return {
    addedIds: addedReceipts.map(r => r.id),
    removedReceipts: removedReceipts || [],
  };
}

/**
 * Applica il delta inverso su tableReceipts per ripristinare lo stato pre-import.
 * @param {Array} currentReceipts - tableReceipts corrente (post-import)
 * @param {{ addedIds: string[], removedReceipts: Array }} delta
 * @returns {Array} - tableReceipts ripristinato
 */
function applyUndoDelta(currentReceipts, delta) {
  if (!delta || !delta.addedIds) return currentReceipts;
  const addedSet = new Set(delta.addedIds);
  // Rimuove i nuovi aggiunti dall'import
  let restored = (currentReceipts || []).filter(r => !addedSet.has(r.id));
  // Ri-aggiunge i rimossi (erano stati sostituiti con replace)
  if (Array.isArray(delta.removedReceipts) && delta.removedReceipts.length > 0) {
    restored = [...restored, ...delta.removedReceipts];
  }
  return restored;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                  VENDITE & STATISTICHE                                     ║
// ║  Importi gli scontrini dal gestionale di cassa Zucchetti TCPOS:            ║
// ║  il software li storicizza, calcola statistiche, proiezioni e fabbisogno.  ║
// ║  NESSUNA gestione operativa di tavoli — quello è già fatto dal gestionale. ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// FIX PATCH-6 (audit 2026-05-19): banner UNDO visibile per 24h dopo un import.
// Permette ad Andi di annullare un import se vede numeri sballati.
// Il snapshot _undoSnapshot viene sovrascritto al prossimo import (uno snapshot alla volta).
function UndoLastImportBanner({ state, setState }) {
  const [showConfirm, setShowConfirm] = React.useState(false);
  const snap = state._undoSnapshot;
  if (!snap) return null;
  // Visibile solo per 24h dalla data dell'import
  const snapTs = snap.ts ? new Date(snap.ts).getTime() : 0;
  const ageMs = Date.now() - snapTs;
  if (ageMs > 24 * 60 * 60 * 1000) return null;
  const snapDate = snap.ts ? new Date(snap.ts).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
  return <>
    <div style={{ background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, padding: "10px 14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
      <AlertTriangle size={16} color={C.amberDeep} style={{ flexShrink: 0 }} />
      <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, flex: 1 }}>
        <strong style={{ color: C.ink }}>Ultimo import:</strong> {snap.fileName} · {snap.receiptsImported} scontrini · {snapDate}
      </div>
      <Btn size="xs" variant="ghost" style={{ color: C.terra, borderColor: C.terra }} onClick={() => setShowConfirm(true)}>
        Annulla questo import
      </Btn>
    </div>
    {showConfirm && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: C.white, borderRadius: 4, padding: 28, maxWidth: 420, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <AlertTriangle size={20} color={C.terra} />
          <div className="f-display" style={{ fontWeight: 600, fontSize: 15, color: C.ink }}>Conferma annullamento import</div>
        </div>
        <div className="f-body" style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.5, marginBottom: 20 }}>
          Stai per rimuovere <strong>{snap.receiptsImported} scontrini</strong> importati il <strong>{snapDate}</strong> dal file <strong>{snap.fileName}</strong>.
          <br /><br />
          Questa azione non puo essere annullata. I dati torneranno allo stato precedente all'import.
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setShowConfirm(false)}>Annulla</Btn>
          <Btn variant="primary" style={{ background: C.terra, borderColor: C.terra }} onClick={() => {
            setState(s => ({
              ...s,
              // Supporta sia il vecchio formato (prevTableReceipts) sia il nuovo (delta).
              // Il vecchio formato viene mantenuto per compatibilità con snapshot già salvati.
              tableReceipts: snap.delta
                ? applyUndoDelta(s.tableReceipts, snap.delta)
                : (snap.prevTableReceipts || []),
              importHistory: snap.prevImportHistory,
              _undoSnapshot: null,
            }));
            setShowConfirm(false);
          }}>Conferma — rimuovi {snap.receiptsImported} scontrini</Btn>
        </div>
      </div>
    </div>}
  </>;
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  ConfrontoAnni — tab "Confronto anni" in VenditeStatistiche              ║
// ║  Visibile SOLO se state.historicalDaily?.length > 0                      ║
// ║  Compara 2024 e 2025 (o qualsiasi anno presente in historicalDaily):      ║
// ║  · Card riepilogo anno (incasso, coperti, media/coperto, nGiorni)         ║
// ║  · Grafico mensile affiancato (mar→ott) con toggle Incasso/Coperti        ║
// ║  · Split pranzo/cena per anno                                             ║
// ║  · Classifica piatti top con variazione                                   ║
// ║  · Mix categorie (peso % reparti)                                         ║
// ╚══════════════════════════════════════════════════════════════════════════╝
const MESI_LABELS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const YOY_YEAR_COLORS = { 2024: C.teal, 2025: C.terra, 2026: C.olive, 2023: C.plum };
const _yoyColor = (y) => YOY_YEAR_COLORS[y] || C.muted;

function ConfrontoAnni({ state }) {
  const [metricaGrafico, setMetricaGrafico] = useState("incasso"); // "incasso" | "coperti"

  // Anni presenti nello storico
  const anniStorici = useMemo(() => {
    return [...new Set((state.historicalDaily || [])
      .map(r => parseInt(String(r.date || "").slice(0, 4), 10))
      .filter(y => Number.isFinite(y) && y > 2000)
    )].sort();
  }, [state.historicalDaily]);

  // Statistiche per anno (memoizzate)
  const statsPerAnno = useMemo(() => {
    const out = {};
    anniStorici.forEach(y => { out[y] = engine.historicalYearStats(state, y); });
    return out;
  }, [state.historicalDaily, anniStorici]);

  // Prodotti per anno
  const prodPerAnno = useMemo(() => {
    const out = {};
    anniStorici.forEach(y => { out[y] = engine.historicalProductsForYear(state, y); });
    return out;
  }, [state.historicalProducts, anniStorici]);

  // Grafico mensile: unione di tutti i mesi presenti in qualsiasi anno
  const chartData = useMemo(() => {
    const mesiPresenti = new Set();
    anniStorici.forEach(y => {
      (statsPerAnno[y]?.byMonth || []).forEach(m => mesiPresenti.add(m.month));
    });
    return [...mesiPresenti].sort((a, b) => a - b).map(mIdx => {
      const row = { mese: MESI_LABELS[mIdx] };
      anniStorici.forEach(y => {
        const m = (statsPerAnno[y]?.byMonth || []).find(x => x.month === mIdx);
        row[`inc_${y}`] = m ? Math.round(m.incasso) : 0;
        row[`cop_${y}`] = m ? m.coperti : 0;
      });
      return row;
    });
  }, [statsPerAnno, anniStorici]);

  // Top piatti unione (tutti i nomi che appaiono in qualsiasi anno), ordinati per valore dell'anno più recente
  const topPiatti = useMemo(() => {
    const map = {}; // nome → { nome, byYear: {year: {qty, valore}} }
    anniStorici.forEach(y => {
      const pd = prodPerAnno[y];
      if (!pd) return;
      pd.items.forEach(it => {
        const key = it.nome;
        if (!map[key]) map[key] = { nome: key, reparto: it.reparto, byYear: {} };
        map[key].byYear[y] = { qty: Number(it.qty) || 0, valore: Number(it.valore) || 0 };
      });
    });
    const sorted = Object.values(map).sort((a, b) => {
      // Ordina per valore anno più recente disponibile
      const lastYear = anniStorici[anniStorici.length - 1];
      const vA = a.byYear[lastYear]?.valore || 0;
      const vB = b.byYear[lastYear]?.valore || 0;
      return vB - vA;
    });
    return sorted.slice(0, 30); // top 30
  }, [prodPerAnno, anniStorici]);

  // Mix categorie per anno
  const mixCategorie = useMemo(() => {
    const allCats = new Set();
    anniStorici.forEach(y => {
      const pd = prodPerAnno[y];
      if (!pd) return;
      Object.keys(pd.byCategoria).forEach(c => allCats.add(c));
    });
    return [...allCats].sort().map(cat => {
      const row = { cat };
      anniStorici.forEach(y => {
        const pd = prodPerAnno[y];
        const totAnno = pd ? Object.values(pd.byCategoria).reduce((s, x) => s + x.valore, 0) : 0;
        const valCat = pd?.byCategoria[cat]?.valore || 0;
        row[`pct_${y}`] = totAnno > 0 ? (valCat / totAnno) * 100 : 0;
        row[`val_${y}`] = valCat;
      });
      return row;
    }).sort((a, b) => {
      const lastY = anniStorici[anniStorici.length - 1];
      return (b[`val_${lastY}`] || 0) - (a[`val_${lastY}`] || 0);
    });
  }, [prodPerAnno, anniStorici]);

  const hasAnyProd = anniStorici.some(y => prodPerAnno[y] !== null);

  // Variazione % tra due anni (primo→secondo)
  const varPct = (v1, v2) => {
    if (!v1 || v1 === 0) return null;
    return ((v2 - v1) / v1) * 100;
  };
  const varTag = (pct) => {
    if (pct == null) return null;
    const color = pct >= 0 ? C.oliveDeep : C.terra;
    const arrow = pct >= 0 ? "↑" : "↓";
    return <span className="f-mono" style={{ fontSize: 11, color, fontWeight: 600 }}>{arrow} {Math.abs(pct).toFixed(1)}%</span>;
  };

  if (anniStorici.length === 0) {
    return <EmptyState icon={BarChart3} title="Nessuno storico caricato"
      body="Importa i dati aggregati degli anni precedenti nella sezione 'Dati storici' per attivare il confronto." />;
  }

  // Coppia di anni principali per confronto testuale (ultimi 2)
  const annoA = anniStorici[anniStorici.length - 2] || anniStorici[0];
  const annoB = anniStorici[anniStorici.length - 1];
  const stA = statsPerAnno[annoA];
  const stB = statsPerAnno[annoB];

  return <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

    {/* ── Card riepilogo per anno ── */}
    <div>
      <SectionHeader title="Riepilogo per anno" subtitle="Dati aggregati dagli storici importati — ristorante stagionale" />
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${anniStorici.length}, 1fr)`, gap: 12 }}>
        {anniStorici.map(y => {
          const st = statsPerAnno[y];
          if (!st) return <div key={y} style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 16 }}>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>{y} — nessun dato</div>
          </div>;
          // Variazione rispetto all'anno precedente presente
          const prevY = anniStorici[anniStorici.indexOf(y) - 1];
          const prevSt = prevY ? statsPerAnno[prevY] : null;
          const vRev = prevSt ? varPct(prevSt.incasso, st.incasso) : null;
          const vCop = prevSt ? varPct(prevSt.coperti, st.coperti) : null;
          return <div key={y} style={{ background: C.white, border: `1px solid ${C.rule}`, borderLeft: `4px solid ${_yoyColor(y)}`, borderRadius: 2, padding: 16 }}>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", marginBottom: 8 }}>{y} · {st.nGiorni} GIORNI APERTI</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
              <span className="f-display" style={{ fontSize: 26, fontWeight: 500, color: C.ink, letterSpacing: "-0.02em" }}>{fmt.eurShort(st.incasso)}</span>
              <span className="f-body" style={{ fontSize: 11, color: C.muted }}>incasso lordo</span>
              {vRev != null && varTag(vRev)}
            </div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div className="f-mono" style={{ fontSize: 11, color: C.inkSoft }}>{st.coperti.toLocaleString("it-IT")}</div>
                <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>coperti {vCop != null && varTag(vCop)}</div>
              </div>
              <div>
                <div className="f-mono" style={{ fontSize: 11, color: C.inkSoft }}>{fmt.eur(st.mediaCoperto)}</div>
                <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>media/coperto</div>
              </div>
              <div>
                <div className="f-mono" style={{ fontSize: 11, color: C.inkSoft }}>{fmt.eur(st.incasso / st.nGiorni)}</div>
                <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>media/giorno</div>
              </div>
            </div>
            {/* Pranzo vs cena */}
            {(st.pranzoInc > 0 || st.cenaInc > 0) && <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.ruleSoft}`, display: "flex", gap: 12 }}>
              <div>
                <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.1em" }}>PRANZO</div>
                <div className="f-mono" style={{ fontSize: 11, color: C.inkSoft }}>{fmt.eurShort(st.pranzoInc)} · {st.pranzoCop} cop.</div>
              </div>
              <div>
                <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.1em" }}>CENA</div>
                <div className="f-mono" style={{ fontSize: 11, color: C.inkSoft }}>{fmt.eurShort(st.cenaInc)} · {st.cenaCop} cop.</div>
              </div>
            </div>}
          </div>;
        })}
      </div>
    </div>

    {/* ── Grafico mensile affiancato ── */}
    {chartData.length > 0 && <div>
      <SectionHeader
        title="Andamento mensile per anno"
        subtitle="Mesi stagionali (solo mesi con almeno un giorno di dati)"
        action={
          <div style={{ display: "flex", border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
            {[{ id: "incasso", label: "Incasso" }, { id: "coperti", label: "Coperti" }].map(opt =>
              <button key={opt.id} onClick={() => setMetricaGrafico(opt.id)} className="f-body tx" style={{
                padding: "6px 12px", fontSize: 12, border: "none", cursor: "pointer",
                background: metricaGrafico === opt.id ? C.ink : "transparent",
                color: metricaGrafico === opt.id ? C.paper : C.inkSoft,
              }}>{opt.label}</button>
            )}
          </div>
        }
      />
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: "16px 8px" }}>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.ruleSoft} vertical={false} />
            <XAxis dataKey="mese" tick={{ fontSize: 11, fontFamily: "JetBrains Mono, monospace", fill: C.muted }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono, monospace", fill: C.muted }} axisLine={false} tickLine={false}
              tickFormatter={v => metricaGrafico === "incasso" ? (v >= 1000 ? `€${(v/1000).toFixed(0)}k` : `€${v}`) : v} />
            <Tooltip
              contentStyle={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, fontSize: 12 }}
              formatter={(value, name) => {
                const y = name.replace(/^(inc|cop)_/, "");
                const label = metricaGrafico === "incasso" ? fmt.eur(value) : value;
                return [label, y];
              }}
            />
            {anniStorici.map(y =>
              <Bar key={y} dataKey={metricaGrafico === "incasso" ? `inc_${y}` : `cop_${y}`}
                name={String(y)} fill={_yoyColor(y)} radius={[1, 1, 0, 0]} maxBarSize={32} />
            )}
          </BarChart>
        </ResponsiveContainer>
        <div style={{ display: "flex", gap: 16, paddingLeft: 16, marginTop: 6 }}>
          {anniStorici.map(y => <div key={y} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 12, height: 12, background: _yoyColor(y), borderRadius: 1 }} />
            <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>{y}</span>
          </div>)}
        </div>
      </div>
    </div>}

    {/* ── Classifica piatti (se disponibile) ── */}
    {hasAnyProd && <div>
      <SectionHeader title="Top piatti" subtitle={`Classifica per valore venduto — ${anniStorici.join(" vs ")}`} />
      {topPiatti.length === 0
        ? <EmptyState icon={Utensils} title="Nessun dato prodotti" body="Importa le statistiche piatti per anno per vedere la classifica." />
        : <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "auto" }}>
            {/* Header */}
            <div style={{ display: "grid", gridTemplateColumns: `1.8fr 0.6fr ${anniStorici.map(() => "0.7fr 0.7fr").join(" ")} 0.7fr`, padding: "8px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.15em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
              <span>PIATTO</span>
              <span>REPARTO</span>
              {anniStorici.map(y => <React.Fragment key={y}><span style={{ textAlign: "right" }}>QTÀ {y}</span><span style={{ textAlign: "right" }}>VAL {y}</span></React.Fragment>)}
              <span style={{ textAlign: "right" }}>VARIAZIONE</span>
            </div>
            {topPiatti.map((p, i) => {
              const colsTemplate = `1.8fr 0.6fr ${anniStorici.map(() => "0.7fr 0.7fr").join(" ")} 0.7fr`;
              // Variazione valore ultimi 2 anni presenti per il piatto
              const ysWithData = anniStorici.filter(y => p.byYear[y]);
              const vA2 = ysWithData.length >= 2 ? p.byYear[ysWithData[ysWithData.length - 2]]?.valore : null;
              const vB2 = ysWithData.length >= 1 ? p.byYear[ysWithData[ysWithData.length - 1]]?.valore : null;
              const vp = (vA2 != null && vB2 != null) ? varPct(vA2, vB2) : null;
              return <div key={p.nome} style={{ display: "grid", gridTemplateColumns: colsTemplate, padding: "7px 14px", borderTop: `1px solid ${C.ruleSoft}`, alignItems: "center", fontSize: 12, background: i % 2 === 1 ? C.paperSoft : "transparent" }}>
                <span className="f-body" style={{ color: C.ink, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.nome}>{p.nome}</span>
                <span className="f-mono" style={{ fontSize: 9, color: C.muted }}>{p.reparto || "—"}</span>
                {anniStorici.map(y => <React.Fragment key={y}>
                  <span className="f-mono" style={{ textAlign: "right", color: p.byYear[y] ? C.inkSoft : C.mutedSoft }}>{p.byYear[y] ? p.byYear[y].qty.toFixed(0) : "—"}</span>
                  <span className="f-mono" style={{ textAlign: "right", color: p.byYear[y] ? C.ink : C.mutedSoft }}>{p.byYear[y] ? fmt.eur(p.byYear[y].valore) : "—"}</span>
                </React.Fragment>)}
                <span style={{ textAlign: "right" }}>{vp != null ? varTag(vp) : <span style={{ color: C.mutedSoft }}>—</span>}</span>
              </div>;
            })}
          </div>
      }
    </div>}

    {/* ── Mix categorie ── */}
    {hasAnyProd && mixCategorie.length > 0 && <div>
      <SectionHeader title="Mix categorie" subtitle="Peso % del valore venduto per reparto" />
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
        <div style={{ display: "grid", gridTemplateColumns: `1.4fr ${anniStorici.map(() => "1fr 1fr").join(" ")}`, padding: "8px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.15em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
          <span>CATEGORIA</span>
          {anniStorici.map(y => <React.Fragment key={y}><span style={{ textAlign: "right" }}>% {y}</span><span style={{ textAlign: "right" }}>€ {y}</span></React.Fragment>)}
        </div>
        {mixCategorie.map((row, i) => <div key={row.cat} style={{ display: "grid", gridTemplateColumns: `1.4fr ${anniStorici.map(() => "1fr 1fr").join(" ")}`, padding: "7px 14px", borderTop: `1px solid ${C.ruleSoft}`, fontSize: 12, background: i % 2 === 1 ? C.paperSoft : "transparent" }}>
          <span className="f-body" style={{ color: C.ink, fontWeight: 500 }}>{row.cat}</span>
          {anniStorici.map(y => <React.Fragment key={y}>
            <span className="f-mono" style={{ textAlign: "right", color: C.inkSoft }}>{row[`pct_${y}`] > 0 ? row[`pct_${y}`].toFixed(1) + "%" : "—"}</span>
            <span className="f-mono" style={{ textAlign: "right", color: C.muted }}>{row[`val_${y}`] > 0 ? fmt.eurShort(row[`val_${y}`]) : "—"}</span>
          </React.Fragment>)}
        </div>)}
      </div>
    </div>}

    {!hasAnyProd && <div style={{ background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
      <Info size={14} color={C.amberDeep} />
      <span className="f-body" style={{ fontSize: 12, color: C.inkSoft }}>
        Classifica piatti non disponibile — importa le statistiche prodotti per anno dalla sezione "Dati storici".
      </span>
    </div>}
  </div>;
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  DomandaStagionalita — tab "Domanda" in VenditeStatistiche                ║
// ║  Visibile SOLO se state.voyaSeasonality presente.                         ║
// ║  Read-only. Mostra la FORMA della domanda storica su 4 anni (export       ║
// ║  Pienissimo). Sono PRENOTATI (domanda lorda), NON presenze reali: la      ║
// ║  capienza reale è ~260/servizio. Questa vista NON cambia la previsione    ║
// ║  incassi in € (forecastNDays resta invariato). Base per gli "attesi       ║
// ║  reali" della fase C futura.                                              ║
// ╚══════════════════════════════════════════════════════════════════════════╝
const DOW_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"]; // 0=lunedì..6=domenica

function DomandaStagionalita({ state }) {
  const model = useMemo(() => engine.voyaDemandModel(state),
    [state.voyaSeasonality, state.historicalDaily, state.tableReceipts, state.settings]);

  const openMonths = model.available ? (model.openMonths || []) : [];
  // Default mese: corrente se aperto, altrimenti il prossimo mese aperto (ciclico).
  const defaultMonth = useMemo(() => {
    if (!openMonths.length) return 0;
    const cur = new Date().getMonth();
    if (openMonths.includes(cur)) return cur;
    for (let step = 1; step <= 12; step++) {
      const m = (cur + step) % 12;
      if (openMonths.includes(m)) return m;
    }
    return openMonths[0];
  }, [openMonths]);
  const [selMonth, setSelMonth] = useState(defaultMonth);
  useEffect(() => { setSelMonth(defaultMonth); }, [defaultMonth]);

  if (!model.available) {
    return <EmptyState icon={Calendar} title="Nessun archivio domanda"
      body="Importa l'archivio storico Pienissimo per vedere la forma della domanda." />;
  }

  // Barre livello mensile (media~1). I mesi chiusi sono marcati e resi a 0.
  const monthChart = MESI_LABELS.map((lbl, i) => ({
    mese: lbl,
    level: Math.round((model.monthLevel[i] || 0) * 100) / 100,
    closed: (model.closedMonths || []).includes(i),
  }));
  const levelSourceLabel = model.monthLevelSource === "reale"
    ? "livello reale appreso (scontrini + storico)"
    : "livello da domanda Pienissimo (prenotato)";

  const dowShare = model.dowShareByMonth[selMonth] || null;
  const svcSplit = model.serviceSplitByMonth[selMonth] || null;
  const topDow = dowShare ? dowShare.indexOf(Math.max(...dowShare)) : -1;

  return <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

    {/* ── Nota onestà dominio ── */}
    <div style={{ background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, padding: "12px 16px", display: "flex", gap: 10 }}>
      <Info size={16} color={C.tealDeep} style={{ flexShrink: 0, marginTop: 1 }} />
      <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.5 }}>
        <strong style={{ color: C.ink }}>Forma della domanda storica su 4 anni (export Pienissimo).</strong> Sono
        {" "}<strong>prenotati (domanda), non presenze reali</strong>: includono rifiuti, cancellati e no-show.
        La capienza reale è ~260 coperti/servizio. Questa vista mostra <em>quale giorno e servizio pesano di più</em>,
        {" "}mai i livelli assoluti di presenze — e <strong>NON cambia la previsione incassi in €</strong>.
      </div>
    </div>

    {/* ── Livello per mese ── */}
    <div>
      <SectionHeader title="Livello per mese"
        subtitle={`Peso relativo dei mesi (media ~1) — fonte: ${levelSourceLabel}`} />
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: "16px 8px" }}>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={monthChart} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.ruleSoft} vertical={false} />
            <XAxis dataKey="mese" tick={{ fontSize: 11, fontFamily: "JetBrains Mono, monospace", fill: C.muted }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono, monospace", fill: C.muted }} axisLine={false} tickLine={false} domain={[0, "auto"]} />
            <Tooltip
              contentStyle={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, fontSize: 12 }}
              formatter={(value, _n, p) => [p?.payload?.closed ? "chiuso" : `${value}× media`, "Livello"]}
            />
            <Bar dataKey="level" radius={[1, 1, 0, 0]} maxBarSize={34}>
              {monthChart.map((d, i) => <Cell key={i} fill={d.closed ? C.mutedSoft : (i === new Date().getMonth() ? C.terra : C.teal)} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div style={{ display: "flex", gap: 16, paddingLeft: 16, marginTop: 6, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 12, height: 12, background: C.teal, borderRadius: 1 }} /><span className="f-mono" style={{ fontSize: 10, color: C.muted }}>mese aperto</span></div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 12, height: 12, background: C.terra, borderRadius: 1 }} /><span className="f-mono" style={{ fontSize: 10, color: C.muted }}>mese corrente</span></div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 12, height: 12, background: C.mutedSoft, borderRadius: 1 }} /><span className="f-mono" style={{ fontSize: 10, color: C.muted }}>stagione chiusa</span></div>
        </div>
      </div>
    </div>

    {/* ── Selettore mese + profilo giorno/servizio ── */}
    <div>
      <SectionHeader title="Profilo del mese"
        subtitle="Quota di domanda (prenotato) per giorno della settimana e per servizio"
        action={
          <select value={selMonth} onChange={e => setSelMonth(parseInt(e.target.value, 10))}
            className="f-body" style={{ padding: "6px 10px", fontSize: 12, border: `1px solid ${C.rule}`, borderRadius: 2, background: C.white, color: C.ink }}>
            {MESI_LABELS.map((lbl, i) => (
              <option key={i} value={i} disabled={!openMonths.includes(i)}>
                {lbl}{openMonths.includes(i) ? "" : " — chiuso"}
              </option>
            ))}
          </select>
        } />

      {!dowShare ? (
        <EmptyState icon={Calendar} title="Mese senza dati di domanda"
          body="Scegli un mese aperto per vedere il profilo giorno-settimana." />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1.7fr 1fr", gap: 12 }}>
          {/* Profilo giorno-settimana */}
          <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 16 }}>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em", marginBottom: 12 }}>QUOTA COPERTI PER GIORNO — {MESI_LABELS[selMonth].toUpperCase()}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {DOW_LABELS.map((lbl, d) => {
                const frac = dowShare[d] || 0;
                const isTop = d === topDow;
                const maxFrac = Math.max(...dowShare, 0.0001);
                return <div key={d} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="f-mono" style={{ fontSize: 11, width: 30, color: isTop ? C.ink : C.muted, fontWeight: isTop ? 600 : 400 }}>{lbl}</span>
                  <div style={{ flex: 1, height: 16, background: C.paperDeep, borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ width: `${Math.min(100, (frac / maxFrac) * 100)}%`, height: "100%", background: isTop ? C.terra : C.teal, borderRadius: 2 }} />
                  </div>
                  <span className="f-mono" style={{ fontSize: 11, width: 42, textAlign: "right", color: isTop ? C.ink : C.inkSoft, fontWeight: isTop ? 600 : 400 }}>{fmt.pct(frac)}</span>
                </div>;
              })}
            </div>
            {topDow >= 0 && <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 12 }}>Giorno di punta: <strong style={{ color: C.terra }}>{["Lunedì","Martedì","Mercoledì","Giovedì","Venerdì","Sabato","Domenica"][topDow]}</strong> ({fmt.pct(dowShare[topDow])} della domanda del mese)</div>}
          </div>

          {/* Pranzo vs Cena */}
          <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 16 }}>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em", marginBottom: 12 }}>PRANZO vs CENA</div>
            {!svcSplit ? (
              <div className="f-body" style={{ fontSize: 12, color: C.muted }}>Nessun dato servizio per questo mese.</div>
            ) : (<>
              <div style={{ display: "flex", height: 22, borderRadius: 2, overflow: "hidden", border: `1px solid ${C.rule}` }}>
                <div style={{ width: `${svcSplit.pranzoFrac * 100}%`, background: C.amber }} />
                <div style={{ width: `${svcSplit.cenaFrac * 100}%`, background: C.teal }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 10, height: 10, background: C.amber, borderRadius: 1 }} /><span className="f-mono" style={{ fontSize: 10, color: C.muted }}>PRANZO</span></div>
                  <div className="f-display" style={{ fontSize: 22, color: C.ink, fontWeight: 500 }}>{fmt.pct(svcSplit.pranzoFrac)}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, justifyContent: "flex-end" }}><span className="f-mono" style={{ fontSize: 10, color: C.muted }}>CENA</span><div style={{ width: 10, height: 10, background: C.teal, borderRadius: 1 }} /></div>
                  <div className="f-display" style={{ fontSize: 22, color: C.ink, fontWeight: 500 }}>{fmt.pct(svcSplit.cenaFrac)}</div>
                </div>
              </div>
            </>)}
          </div>
        </div>
      )}
    </div>

    {/* ── Riga riepilogo (label onesti) ── */}
    <div>
      <SectionHeader title="Riepilogo archivio" subtitle="Dati aggregati su tutto l'export Pienissimo (domanda prenotata, non presenze)" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 14 }}>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>PRENOTAZIONI UNICHE</div>
          <div className="f-display" style={{ fontSize: 24, color: C.ink, fontWeight: 500, marginTop: 4 }}>{model.reservationsUnique != null ? model.reservationsUnique.toLocaleString("it-IT") : "—"}</div>
          <div className="f-body" style={{ fontSize: 10, color: C.muted }}>{model.coversTotal != null ? `${model.coversTotal.toLocaleString("it-IT")} coperti prenotati` : ""}</div>
        </div>
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 14 }}>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>RAPPORTO CENA / PRANZO</div>
          <div className="f-display" style={{ fontSize: 24, color: C.ink, fontWeight: 500, marginTop: 4 }}>{model.cenaPranzoRatio != null ? `${model.cenaPranzoRatio.toFixed(2)}×` : "—"}</div>
          <div className="f-body" style={{ fontSize: 10, color: C.muted }}>coperti cena per ogni coperto pranzo</div>
        </div>
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 14 }}>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>ANTICIPO PRENOTAZIONE</div>
          <div className="f-display" style={{ fontSize: 24, color: C.ink, fontWeight: 500, marginTop: 4 }}>{model.leadtimeMedianDays != null ? `${model.leadtimeMedianDays} gg` : "—"}</div>
          <div className="f-body" style={{ fontSize: 10, color: C.muted }}>mediana{model.leadtimeWithin24hPct != null ? ` · ${fmt.pct(model.leadtimeWithin24hPct)} entro 24h` : ""}</div>
        </div>
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 14 }}>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>TAVOLATE PIÙ FREQUENTI</div>
          <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 3 }}>
            {(model.partyTop || []).length === 0 ? <span className="f-body" style={{ fontSize: 12, color: C.muted }}>—</span> :
              model.partyTop.map(p => <div key={p.pax} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span className="f-body" style={{ color: C.inkSoft }}>{p.pax} {p.pax === 1 ? "persona" : "persone"}</span>
                <span className="f-mono" style={{ color: C.ink, fontWeight: 500 }}>{fmt.pct(p.pct)}</span>
              </div>)}
          </div>
        </div>
      </div>
    </div>

  </div>;
}

function VenditeStatistiche({ state, setState, onNavigate }) {
  const [importerOpen, setImporterOpen] = useState(false);
  const [storiciImporterOpen, setStoriciImporterOpen] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const toast = useToast();

  const receipts = state.tableReceipts || [];

  // Receipts oggi (basato sulla data locale, non UTC) — useMemo per stabilità referenziale
  const todayReceipts = useMemo(() => receipts.filter(r => getServiceDate(r.closedAt) === TODAY), [receipts]);
  // Segmento banco (covers=0) vs tavolo (covers>0): il banco non ha coperti, includerlo
  // gonfia il "ticket medio per coperto". Andi vede solo lo scontrino-tavolo.
  const todayTableReceipts = useMemo(() => todayReceipts.filter(r => (r.covers || 0) > 0), [todayReceipts]);
  const todayBancoReceipts = useMemo(() => todayReceipts.filter(r => (r.covers || 0) === 0), [todayReceipts]);
  const todayCovers = useMemo(() => todayTableReceipts.reduce((s, r) => s + (r.covers || 0), 0), [todayTableReceipts]);
  // FIX Batch 3 — riconciliazione EC: l'EC riconciliato ha il suo totale già
  // conteggiato nello SF collegato → va escluso per evitare doppio conteggio.
  // L'EC "NERO_DEFINITIVO" invece è un incasso reale non duplicato → incluso.
  const todayRevenue = useMemo(() => todayReceipts.reduce((s, r) => {
    const isEC = r._isPreconto || r._docType === "ESTRATTO CONTO";
    if (isEC && r._regularizedTo && r._regularizedTo !== "NERO_DEFINITIVO") return s; // EC riconciliato: escludi (già nello SF)
    return s + (r.total || 0);
  }, 0), [todayReceipts]);
  // EC orfani (non ancora riconciliati né confermati): quota non fiscalizzata in sospeso
  const revenueNonFiscalOrphan = useMemo(() => todayReceipts.reduce((s, r) => {
    const isEC = r._isPreconto || r._docType === "ESTRATTO CONTO";
    if (!isEC) return s;
    if (r._regularizedTo) return s; // riconciliato o nero definitivo: fuori dalla categoria "orfani"
    return s + (r.total || 0);
  }, 0), [todayReceipts]);
  // EC confermati come nero definitivo (audit interno)
  const revenueNeroDefinitivo = useMemo(() => todayReceipts.reduce((s, r) => {
    if (r._regularizedTo === "NERO_DEFINITIVO") return s + (r.total || 0);
    return s;
  }, 0), [todayReceipts]);
  const todayTableRev = useMemo(() => todayTableReceipts.reduce((s, r) => {
    const isEC = r._isPreconto || r._docType === "ESTRATTO CONTO";
    if (isEC && r._regularizedTo && r._regularizedTo !== "NERO_DEFINITIVO") return s;
    return s + (r.total || 0);
  }, 0), [todayTableReceipts]);
  const todayBancoRev = useMemo(() => todayBancoReceipts.reduce((s, r) => {
    const isEC = r._isPreconto || r._docType === "ESTRATTO CONTO";
    if (isEC && r._regularizedTo && r._regularizedTo !== "NERO_DEFINITIVO") return s;
    return s + (r.total || 0);
  }, 0), [todayBancoReceipts]);
  const todayBancoCount = todayBancoReceipts.length;
  // Scontrino medio per coperto: SOLO scontrini-tavolo (banco escluso)
  const avgTicket = todayCovers > 0 ? todayTableRev / todayCovers : 0;

  // ── Breakdown incasso reale vs cassa ────────────────────────────────────
  // Calcola il delta tra incasso reale (piattaforma) e stampa cassa (Zucchetti)
  // dovuto a regali (SESCIALA & co.) e mezze porzioni scontate.
  // Aggiustamenti=0 → nessuna riga extra nelle card (backward compat).
  const todayBreakdown = useMemo(() => engine.incassoBreakdown(todayReceipts), [todayReceipts]);
  const avgBanco = todayBancoCount > 0 ? todayBancoRev / todayBancoCount : 0;

  // Interruttore OGGI / TOTALE — default "totale" così Andi vede sempre i dati storici
  // anche se non ha ancora importato scontrini oggi.
  const [scope, setScope] = useState("totale");
  // Orizzonte proiezione per il grafico ANDAMENTO ATTIVITÀ (default 7 giorni)
  const [fcastHorizon, setFcastHorizon] = useState(7);

  // Split pranzo / cena di oggi
  const shiftStats = useMemo(() => {
    const p = { receipts: 0, covers: 0, revenue: 0, receiptsArr: [] };
    const c = { receipts: 0, covers: 0, revenue: 0, receiptsArr: [] };
    todayReceipts.forEach(r => {
      if (!_receiptCountsForRevenue(r)) return; // AUDIT-FIX: EC riconciliato = già nello SF
      const s = getShift(r.closedAt);
      if (s === "pranzo") { p.receipts++; p.covers += r.covers || 0; p.revenue += r.total || 0; p.receiptsArr.push(r); }
      else if (s === "cena") { c.receipts++; c.covers += r.covers || 0; c.revenue += r.total || 0; c.receiptsArr.push(r); }
    });
    return { pranzo: p, cena: c };
  }, [todayReceipts]);

  // Confronto vs ieri e media 7 giorni
  const comparison = useMemo(() => {
    const yesterdayDate = daysAgoISO(1);
    // AUDIT-FIX: stessa regola EC del totale, così il delta oggi-vs-ieri non è asimmetrico
    const yesterday = receipts.filter(r => getServiceDate(r.closedAt) === yesterdayDate && _receiptCountsForRevenue(r));
    const yrev = yesterday.reduce((s, r) => s + r.total, 0);
    const ycov = yesterday.reduce((s, r) => s + (r.covers || 0), 0);
    const last7 = [];
    for (let i = 1; i <= 7; i++) {
      const d = daysAgoISO(i);
      const day = receipts.filter(r => getServiceDate(r.closedAt) === d && _receiptCountsForRevenue(r));
      last7.push({ date: d, revenue: day.reduce((s, r) => s + r.total, 0), covers: day.reduce((s, r) => s + (r.covers || 0), 0) });
    }
    const nonEmpty7 = last7.filter(x => x.revenue > 0);
    const avgRev7 = nonEmpty7.length > 0 ? nonEmpty7.reduce((s, x) => s + x.revenue, 0) / nonEmpty7.length : 0;
    const avgCov7 = nonEmpty7.length > 0 ? nonEmpty7.reduce((s, x) => s + x.covers, 0) / nonEmpty7.length : 0;
    return {
      yesterday: { revenue: yrev, covers: ycov },
      avg7: { revenue: avgRev7, covers: avgCov7, days: nonEmpty7.length },
      last7,
    };
  }, [receipts]);

  const deltaRevYest = comparison.yesterday.revenue > 0 ? ((todayRevenue - comparison.yesterday.revenue) / comparison.yesterday.revenue) * 100 : 0;
  const deltaCovAvg = comparison.avg7.covers > 0 ? ((todayCovers - comparison.avg7.covers) / comparison.avg7.covers) * 100 : 0;

  // Ultima data importata
  const lastImportedDate = useMemo(() => {
    if (receipts.length === 0) return null;
    const dates = receipts.map(r => getServiceDate(r.closedAt)).filter(Boolean).sort();
    return dates[dates.length - 1];
  }, [receipts]);

  // Split pranzo/cena sull'INTERO storico — usato dalle card sotto le 4 principali
  // quando non ci sono scontrini di OGGI (stesso pattern di historyTotals).
  const historyShiftStats = useMemo(() => {
    const p = { receipts: 0, covers: 0, revenue: 0, receiptsArr: [] };
    const c = { receipts: 0, covers: 0, revenue: 0, receiptsArr: [] };
    receipts.forEach(r => {
      if (!_receiptCountsForRevenue(r)) return; // AUDIT-FIX: EC riconciliato = già nello SF
      const s = getShift(r.closedAt);
      const cov = Number(r.covers) || 0;
      const tot = Number(r.total) || 0;
      if (s === "pranzo") { p.receipts++; p.covers += cov; p.revenue += tot; p.receiptsArr.push(r); }
      else if (s === "cena") { c.receipts++; c.covers += cov; c.revenue += tot; c.receiptsArr.push(r); }
    });
    return { pranzo: p, cena: c };
  }, [receipts]);

  // Breakdown turni (pranzo/cena) per riconciliazione cassa — scope oggi
  const shiftBreakdowns = useMemo(() => ({
    pranzo: engine.incassoBreakdown(shiftStats.pranzo.receiptsArr),
    cena:   engine.incassoBreakdown(shiftStats.cena.receiptsArr),
  }), [shiftStats]);
  // Breakdown turni per scope "totale" (storico completo)
  const historyShiftBreakdowns = useMemo(() => ({
    pranzo: engine.incassoBreakdown(historyShiftStats.pranzo.receiptsArr),
    cena:   engine.incassoBreakdown(historyShiftStats.cena.receiptsArr),
  }), [historyShiftStats]);

  // Totali "storico complessivo" — usati nelle 4 card principali quando non ci sono
  // scontrini di OGGI (così Andi vede subito 381 / €30.688 / 758 cop del dataset
  // invece di 4 card vuote "0 oggi" che non comunicano nulla).
  const historyTotals = useMemo(() => {
    const tableR = receipts.filter(r => (r.covers || 0) > 0);
    const bancoR = receipts.filter(r => (r.covers || 0) === 0);
    const dates = receipts.map(r => getServiceDate(r.closedAt)).filter(Boolean);
    const uniqDates = [...new Set(dates)].sort();
    const revenue = receipts.reduce((s, r) => s + (Number(r.total) || 0), 0);
    const tableRev = tableR.reduce((s, r) => s + (Number(r.total) || 0), 0);
    const covers = tableR.reduce((s, r) => s + (Number(r.covers) || 0), 0);
    const nDays = uniqDates.length;
    return {
      count: receipts.length,
      tableCount: tableR.length,
      bancoCount: bancoR.length,
      revenue,
      tableRev,
      covers,
      nDays,
      dateFrom: uniqDates[0] || null,
      dateTo: uniqDates[uniqDates.length - 1] || null,
      avgRevPerDay: nDays > 0 ? revenue / nDays : 0,
      avgCoversPerDay: nDays > 0 ? covers / nDays : 0,
      tableReceipts: tableR,
    };
  }, [receipts]);

  // Breakdown storico (scope="totale")
  const historyBreakdown = useMemo(() => engine.incassoBreakdown(receipts), [receipts]);

  // ── GRAFICO ANDAMENTO ATTIVITÀ ─────────────────────────────────────────────
  // Helper: aggrega una serie giornaliera in settimanale (ISO week) o mensile (YYYY-MM).
  // Restituisce array { label, revenue, covers, nDays } ordinato per data.
  // Memoizzato qui come funzione pura, non dentro useMemo, per poterla riusare.

  // dailyMetrics calcolate UNA sola volta: riusate sia dall'aggregazione storico
  // che dal forecastNDays interno al grafico.
  const dailyMetricsForChart = useMemo(() => engine.dailyMetrics(state), [state.tableReceipts]);

  // Serie COMBINATA (storico aggregato 2024-25 + scontrini 2026) — è questa la fonte
  // del grafico "Andamento attività", così i 2 anni storici appaiono. Riuso la
  // dailyMetrics già calcolata per non ricalcolarla. Se historicalDaily è vuoto,
  // questa è identica a dailyMetricsForChart (backward-compat: nulla cambia).
  const combinedSeriesForChart = useMemo(
    () => engine.combinedDailySeries(state, dailyMetricsForChart),
    [dailyMetricsForChart, state.historicalDaily]
  );

  // Aggregazione automatica: ≤60 giorni → giornaliero, ≤730 giorni → settimanale, >730 → mensile.
  const aggregatedHistoryForChart = useMemo(() => {
    const dm = combinedSeriesForChart;
    if (dm.length === 0) return { points: [], granularity: "daily" };
    const gran = dm.length <= 60 ? "daily" : dm.length <= 730 ? "weekly" : "monthly";
    if (gran === "daily") {
      return {
        granularity: "daily",
        points: dm.map(d => ({
          dateKey: d.date,
          label: new Date(d.date + "T12:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short" }),
          revenue: d.revenue,
          covers: d.covers,
          avgRev: d.revenue,   // giornaliero: media/giorno = incasso del giorno (nDays=1)
          total: d.revenue,
          nDays: 1,
        })),
      };
    }
    // Settimanale o mensile: raggruppa per chiave
    const buckets = {};
    dm.forEach(d => {
      let key;
      if (gran === "weekly") {
        // ISO week: lunedì della settimana
        const dt = new Date(d.date + "T12:00:00");
        const day = dt.getDay(); // 0=dom
        const diff = (day === 0 ? -6 : 1 - day); // offset verso lunedì
        const mon = new Date(dt);
        mon.setDate(dt.getDate() + diff);
        key = mon.toISOString().slice(0, 10); // YYYY-MM-DD del lunedì
      } else {
        key = d.date.slice(0, 7); // YYYY-MM
      }
      if (!buckets[key]) buckets[key] = { dateKey: key, revenue: 0, covers: 0, nDays: 0 };
      buckets[key].revenue += d.revenue;
      buckets[key].covers += d.covers;
      buckets[key].nDays += 1;
    });
    const sorted = Object.values(buckets).sort((a, b) => a.dateKey.localeCompare(b.dateKey));
    return {
      granularity: gran,
      points: sorted.map(b => {
        let label;
        if (gran === "weekly") {
          const dt = new Date(b.dateKey + "T12:00:00");
          label = `sett. ${dt.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}`;
        } else {
          const [y, m] = b.dateKey.split("-");
          label = new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString("it-IT", { month: "short", year: "2-digit" });
        }
        // avgRev = incasso medio per giorno del periodo → stessa scala della proiezione
        // giornaliera, così storico aggregato e forecast sono confrontabili sullo stesso asse.
        return { dateKey: b.dateKey, label, revenue: b.revenue, covers: b.covers, nDays: b.nDays, avgRev: Math.round(b.revenue / b.nDays), total: b.revenue };
      }),
    };
  }, [combinedSeriesForChart]);

  // Forecast per il grafico andamento: ricalcolato solo al cambio di horizon
  const forecastForChart = useMemo(() => engine.forecastNDays(state, fcastHorizon), [state.tableReceipts, fcastHorizon]);

  // Serie combinata storico + proiezione per recharts AreaChart.
  // Storico: campi { histRev, histCovers } valorizzati, forecast a null.
  // Proiezione: campi { forecastExpected, forecastLow, forecastDelta } valorizzati, histRev a null.
  // forecastDelta = forecastHigh - forecastLow (per stacking banda CI).
  const activityChartData = useMemo(() => {
    const hist = aggregatedHistoryForChart.points.map(p => ({
      dateKey: p.dateKey,
      label: p.label,
      histRev: Math.round(p.avgRev),   // media €/giorno: stessa scala del forecast giornaliero
      histTotal: Math.round(p.total),  // totale periodo (per tooltip)
      histNDays: p.nDays,
      histCovers: p.covers,
      forecastExpected: null,
      forecastLow: null,
      forecastDelta: null,
    }));
    const fcPoints = forecastForChart.points.map(p => ({
      dateKey: p.date,
      label: p.dayLabel,
      histRev: null,
      histCovers: null,
      forecastExpected: Math.round(p.expected),
      forecastLow: Math.round(p.low),
      forecastDelta: Math.round(p.high - p.low),
    }));
    return [...hist, ...fcPoints];
  }, [aggregatedHistoryForChart, forecastForChart]);

  // Conteggio piatti consumati oggi (per la tab consumo).
  // FIX A.1 (audit 2026-05-20): aggrego anche il costo MP snapshot (recipeSnapshot.netCost
  // dal momento della vendita) così a valle posso calcolare il margine senza retro-modifica
  // dei prezzi materia prima. costSnapshotSum e hasSnapshot tracciano la copertura: se
  // >=80% della qty ha snapshot uso quello, altrimenti fallback al calcolo da prezzi correnti.
  const todayDishes = useMemo(() => {
    const agg = {};
    todayReceipts.forEach(r => {
      (r.items || []).forEach(it => {
        if (!agg[it.dishId]) agg[it.dishId] = { dishId: it.dishId, name: it.name, qty: 0, revenue: 0, costSnapshotSum: 0, hasSnapshot: 0 };
        agg[it.dishId].qty += it.qty;
        agg[it.dishId].revenue += it.qty * it.unitPrice;
        // Snapshot del costo MP: presente solo se la vendita è stata registrata
        // con recipeSnapshot popolato (es. da kitchenOutput o import recente).
        const snapCost = it.recipeSnapshot?.netCost;
        if (snapCost != null && Number.isFinite(snapCost)) {
          agg[it.dishId].costSnapshotSum += it.qty * snapCost;
          agg[it.dishId].hasSnapshot += it.qty;
        }
      });
    });
    return Object.values(agg).sort((a, b) => b.qty - a.qty);
  }, [todayReceipts]);

  // Revenue netto IVA scorporata per riga (mai media unica).
  const todayRevenueNet = useMemo(() => {
    const defaultVat = state.settings?.vatRateSale || 0.10;
    const dishVatById = new Map();
    (state.dishesReal || []).forEach(d => {
      const v = (d.vatRate != null && Number.isFinite(d.vatRate)) ? d.vatRate : defaultVat;
      dishVatById.set(d.id, v);
    });
    let net = 0;
    todayReceipts.forEach(r => (r.items || []).forEach(it => {
      const snap = it.recipeSnapshot?.vatRate;
      const cur = dishVatById.get(it.dishId);
      const v = (snap != null && Number.isFinite(snap)) ? snap
             : (cur != null && Number.isFinite(cur)) ? cur
             : defaultVat;
      const grossLine = (Number(it.qty) || 0) * (Number(it.unitPrice) || 0);
      net += v > -1 ? grossLine / (1 + v) : grossLine;
    }));
    return net;
  }, [todayReceipts, state.dishesReal, state.settings]);

  // Consumo materia prima oggi (da snapshot ricetta o ricetta corrente)
  const todayConsumption = useMemo(() => {
    const agg = {};
    todayReceipts.forEach(r => {
      (r.items || []).forEach(it => {
        const ings = it.recipeSnapshot?.ingredienti || (() => {
          const dish = (state.dishesReal || []).find(d => d.id === it.dishId);
          return dish ? dish.ingredienti : [];
        })();
        ings.forEach(ing => {
          if (!ing.bpId) return;
          const bp = (state.baseProducts || []).find(b => b.id === ing.bpId);
          if (!bp) return;
          if (!agg[ing.bpId]) agg[ing.bpId] = { bp, qtyNet: 0, costSnapshot: 0 };
          agg[ing.bpId].qtyNet += ing.qty * it.qty;
          if (ing.unitCostNet != null) agg[ing.bpId].costSnapshot += ing.qty * it.qty * ing.unitCostNet;
        });
      });
    });
    return Object.values(agg).sort((a, b) => b.qtyNet - a.qtyNet);
  }, [todayReceipts, state.dishesReal, state.baseProducts]);

  return <div className="fade-up">
    {/* IMPORTER è il pulsante più visibile */}
    {importerOpen && <ZucchettiImporter state={state} setState={setState} onClose={() => setImporterOpen(false)} />}
    {storiciImporterOpen && <StoriciIncassiImporter state={state} setState={setState} onClose={() => setStoriciImporterOpen(false)} />}
    {viewReceipt && <TableReceiptViewer receipt={viewReceipt} state={state} setState={setState} onClose={() => setViewReceipt(null)} />}

    <PageHeader kicker={`Vendite · Statistiche · Proiezioni · ${receipts.length} scontrini storici${lastImportedDate ? ` · ultimo import ${new Date(lastImportedDate + "T12:00:00").toLocaleDateString("it-IT")}` : ""}`}
      title="Vendite & Statistiche"
      subtitle="Importa gli scontrini esportati dal gestionale di cassa Zucchetti per costruire le statistiche di vendita, le proiezioni e il fabbisogno di materia prima."
      right={<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Btn variant="ghost" size="sm" icon={History} onClick={() => setStoriciImporterOpen(true)}>Storico incassi</Btn>
        <Btn variant="accent" size="lg" icon={Upload} onClick={() => setImporterOpen(true)}>Importa scontrini</Btn>
      </div>} />

    {/* Cima pulita (richiesta Andi 2026-06-05): Annulla import, promemoria backup e Qualità
        dati spostati IN FONDO alla pagina. Rimosso il nag "non hai importato oggi"
        (ridondante col bottone "Importa scontrini" in alto). */}

    {/* Interruttore OGGI / TOTALE — default "totale" */}
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
      <span className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em" }}>MOSTRA:</span>
      {[
        { id: "totale", label: "Totale storico" },
        { id: "oggi", label: "Oggi" },
      ].map(opt => (
        <button key={opt.id} onClick={() => setScope(opt.id)} className="tx"
          style={{ padding: "5px 14px", fontSize: 12, fontFamily: "inherit", fontWeight: scope === opt.id ? 600 : 400, cursor: "pointer", borderRadius: 2, border: `1px solid ${scope === opt.id ? C.ink : C.rule}`, background: scope === opt.id ? C.ink : C.white, color: scope === opt.id ? C.paper : C.inkSoft }}>
          {opt.label}
        </button>
      ))}
      {scope === "oggi" && todayReceipts.length === 0 && (
        <span className="f-body" style={{ fontSize: 11, color: C.amberDeep, marginLeft: 4 }}>
          nessuno scontrino importato oggi — le card mostrano zeri
        </span>
      )}
    </div>

    {/* Le 4 card seguono scope: "oggi" → dati di oggi, "totale" → intero dataset importato */}
    <div className="stagger" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 18 }}>
      {scope === "oggi" ? (
        <>
          <StatCard kicker="Scontrini oggi" value={todayReceipts.length} unit="importati" trend={todayBancoCount > 0 ? `${todayTableReceipts.length} tavolo · ${todayBancoCount} banco` : `${todayCovers} coperti`} accent={C.olive} icon={Receipt} />
          <StatCard kicker="Incasso oggi" value={fmt.eurShort(todayRevenue)} unit="incasso reale" trend={comparison.yesterday.revenue > 0 ? `${deltaRevYest >= 0 ? "↑" : "↓"} ${Math.abs(deltaRevYest).toFixed(0)}% vs ieri` : "no dati ieri"} accent={C.ink} icon={TrendingUp} sub={todayBreakdown.aggiustamenti > 0 ? `≈ come cassa ${fmt.eur(todayBreakdown.comeCassa)} · regali+mezze ${fmt.eur(todayBreakdown.aggiustamenti)}` : null} />
          {(() => {
            const ts = engine.stats.ticketStats(todayTableReceipts, { perCover: true });
            return <TicketStatsCard ts={ts} kicker="Scontrino mediano (tavolo)" accent={C.teal} icon={User} note={todayBancoCount > 0 ? `banco escluso · banco ${fmt.eur(avgBanco)}/scn` : "banco escluso"} />;
          })()}
          <StatCard kicker="Coperti oggi" value={todayCovers} unit="serviti" trend={comparison.avg7.covers > 0 ? `${deltaCovAvg >= 0 ? "↑" : "↓"} ${Math.abs(deltaCovAvg).toFixed(0)}% vs media 7gg` : "no media"} accent={C.amber} icon={Users} />
        </>
      ) : (
        <>
          {(() => {
            const ht = historyTotals;
            const rangeLabel = ht.dateFrom && ht.dateTo
              ? (ht.dateFrom === ht.dateTo
                  ? new Date(ht.dateFrom + "T12:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "short" })
                  : `${new Date(ht.dateFrom + "T12:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "short" })} → ${new Date(ht.dateTo + "T12:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "short" })}`)
              : "—";
            const ts = engine.stats.ticketStats(ht.tableReceipts, { perCover: true });
            return <>
              <StatCard kicker="Scontrini totali" value={ht.count} unit="importati" trend={`${ht.tableCount} tavolo · ${ht.bancoCount} banco · ${ht.nDays} gg`} accent={C.olive} icon={Receipt} />
              <StatCard kicker={`Incasso totale · ${rangeLabel}`} value={fmt.eurShort(ht.revenue)} unit="incasso reale" trend={ht.nDays > 0 ? `media ${fmt.eur(ht.avgRevPerDay)}/gg` : "—"} accent={C.ink} icon={TrendingUp} sub={historyBreakdown.aggiustamenti > 0 ? `≈ come cassa ${fmt.eur(historyBreakdown.comeCassa)} · regali+mezze ${fmt.eur(historyBreakdown.aggiustamenti)}` : null} />
              <TicketStatsCard ts={ts} kicker="Scontrino mediano (tavolo)" accent={C.teal} icon={User} note={ht.bancoCount > 0 ? `banco escluso · ${ht.bancoCount} banco nel periodo` : "banco escluso"} />
              <StatCard kicker="Coperti totali" value={ht.covers} unit="serviti" trend={ht.nDays > 0 ? `media ${ht.avgCoversPerDay.toFixed(0)} cop/gg` : "—"} accent={C.amber} icon={Users} />
            </>;
          })()}
        </>
      )}
    </div>

    {/* ── Widget YoY "stesso giorno" ─────────────────────────────────────────
        Visibile SOLO in scope="oggi" quando ci sono dati storici (historicalDaily).
        Mostra in forma compatta il confronto dello stesso giorno di calendario
        negli anni precedenti. Il giorno di settimana può essere diverso: segnalato
        in piccolo ma NON altera il match (date-based, non weekday-based). ── */}
    {scope === "oggi" && (state.historicalDaily?.length > 0) && (() => {
      const yoy = engine.yoyForDate(state, TODAY);
      if (!yoy || yoy.years.length === 0) return null;
      const DOW_IT = ["dom", "lun", "mar", "mer", "gio", "ven", "sab"];
      return <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: "10px 14px", marginBottom: 4, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <BarChart3 size={13} color={C.muted} strokeWidth={1.5} />
          <span className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>STESSO GIORNO ANNI PRECEDENTI</span>
        </div>
        {yoy.years.map(yr => {
          const todayDow = yoy.current ? yoy.current.dayOfWeek : new Date(TODAY + "T12:00:00").getDay();
          const diffWeekday = yr.weekdayDiffers;
          const pct = yr.deltaPctVsCurrent;
          const pctColor = pct == null ? C.muted : pct >= 0 ? C.oliveDeep : C.terra;
          return <div key={yr.year} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", background: C.paperSoft, borderRadius: 2, border: `1px solid ${C.ruleSoft}` }}>
            <span className="f-mono" style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>{yr.year}</span>
            <span className="f-mono" style={{ fontSize: 12, color: C.ink, fontWeight: 600 }}>{fmt.eurShort(yr.revenue)}</span>
            {yr.covers > 0 && <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>{yr.covers} cop.</span>}
            {pct != null && <span className="f-mono" style={{ fontSize: 10, color: pctColor, fontWeight: 600 }}>
              {pct >= 0 ? "↑" : "↓"}{Math.abs(pct).toFixed(0)}%
            </span>}
            {diffWeekday && <span className="f-mono" style={{ fontSize: 9, color: C.mutedSoft }} title={`Oggi: ${DOW_IT[todayDow]} · ${yr.year}: ${DOW_IT[yr.dayOfWeek]}`}>
              ({DOW_IT[yr.dayOfWeek]})
            </span>}
          </div>;
        })}
        {yoy.years.every(yr => yr.weekdayDiffers) && <span className="f-mono" style={{ fontSize: 9, color: C.mutedSoft }}>• giorni diversi della settimana</span>}
      </div>;
    })()}

    {/* Split pranzo/cena — segue scope (stesso interruttore delle 4 card) */}
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
      {["pranzo", "cena"].map(s => {
        const useToday = scope === "oggi";
        const stat = useToday ? shiftStats[s] : historyShiftStats[s];
        const shift = SERVICE_SHIFTS[s];
        const avg = stat.covers > 0 ? stat.revenue / stat.covers : 0;
        const target = s === "pranzo" ? 40 : 46;
        const scopeLabel = useToday ? "oggi" : `totale ${historyTotals.nDays}gg`;
        const bkd = useToday ? shiftBreakdowns[s] : historyShiftBreakdowns[s];
        return <div key={s} style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 14, display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 44, height: 44, background: s === "pranzo" ? C.amberWash : C.tealWash, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>{shift.icon}</div>
          <div style={{ flex: 1 }}>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>{shift.label.toUpperCase()} · {String(shift.from).padStart(2, "0")}—{String(shift.to % 24).padStart(2, "0")} · <span style={{ color: useToday ? C.olive : C.tealDeep }}>{scopeLabel.toUpperCase()}</span></div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginTop: 2, flexWrap: "wrap" }}>
              <div><span className="f-display" style={{ fontSize: 20, fontWeight: 600, color: C.ink }}>{fmt.eurShort(stat.revenue)}</span> <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>incasso reale</span></div>
              <div><span className="f-mono" style={{ fontSize: 14, color: C.inkSoft, fontWeight: 500 }}>{stat.receipts}</span> <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>scontr.</span></div>
              <div><span className="f-mono" style={{ fontSize: 14, color: C.inkSoft, fontWeight: 500 }}>{stat.covers}</span> <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>cop.</span></div>
              {avg > 0 && <div><span className="f-mono" style={{ fontSize: 12, color: avg >= target ? C.olive : C.terra, fontWeight: 600 }}>{fmt.eur(avg)}</span> <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>/cop · target €{target}</span></div>}
            </div>
            {bkd.aggiustamenti > 0 && (
              <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 5, paddingTop: 4, borderTop: `1px solid ${C.ruleSoft}` }}>
                {`≈ come cassa ${fmt.eur(bkd.comeCassa)} · regali+mezze ${fmt.eur(bkd.aggiustamenti)}`}
              </div>
            )}
          </div>
        </div>;
      })}
    </div>

    {/* GRAFICO ANDAMENTO ATTIVITÀ — full-width, sostituisce la vecchia sparkline 7gg.
        Storico aggregato auto (giornaliero/settimanale/mensile) + proiezione N giorni.
        Distinzione visiva: storico = area olive piena; proiezione = banda CI teal + linea tratteggiata. */}
    {(() => {
      const fc = forecastForChart;
      const gran = aggregatedHistoryForChart.granularity;
      const granLabel = gran === "daily" ? "giornaliero" : gran === "weekly" ? "settimanale" : "mensile";
      const srcLabel = fc.seasonalSource === "learned"
        ? `stagionalità appresa da ${fc.seasonalYearsObserved || 2} anni · conf. ${fc.confidence}`
        : fc.seasonalSource === "manual"
        ? `stagionalità manuale · conf. ${fc.confidence}`
        : `stagionalità neutra · conf. ${fc.confidence}`;
      // Se la proiezione cade in mesi CHIUSI (stagione), segnalalo accanto all'etichetta.
      const hasClosedInForecast = (fc.points || []).some(p => p.closed);
      // Per il tooltip recharts: formatta i valori
      const fmtTick = (v) => v != null ? `€${Math.round(v).toLocaleString("it-IT")}` : "";
      return <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 16, marginBottom: 24 }}>
        {/* Intestazione con selettore orizzonte */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div>
            <div className="f-display" style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>Andamento attività</div>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
              storico {granLabel}{gran !== "daily" ? " (media €/giorno)" : ""} · proiezione {fc.horizonActual}gg · {srcLabel}
              {hasClosedInForecast && <span style={{ color: C.amberDeep }}> · include giorni in stagione chiusa (proiezione 0)</span>}
              {fc.horizonCapped && <span style={{ color: C.amberDeep }}> · troncato a {fc.horizonActual}gg (max 2× storico)</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            {[7, 15, 30].map(n => (
              <button key={n} onClick={() => setFcastHorizon(n)} className="tx"
                style={{ padding: "4px 10px", fontSize: 11, fontFamily: "inherit", fontWeight: fcastHorizon === n ? 600 : 400, cursor: "pointer", borderRadius: 2, border: `1px solid ${fcastHorizon === n ? C.teal : C.rule}`, background: fcastHorizon === n ? C.tealWash : C.white, color: fcastHorizon === n ? C.tealDeep : C.inkSoft }}>
                +{n}gg
              </button>
            ))}
          </div>
        </div>

        {activityChartData.length === 0 ? (
          <div style={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 12 }}>
            Nessun dato — importa gli scontrini per vedere l'andamento
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={activityChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gradHistRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.olive} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={C.olive} stopOpacity={0.06} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 4" stroke={C.rule} vertical={false} />
              {/* FIX 2026-06-10: minTickGap alto → con 2 anni di storico settimanale
                  (~60+ punti) le etichette date non si sovrappongono più (recharts
                  dirada i tick troppo vicini). preserveStartEnd tiene primo/ultimo. */}
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: C.muted, fontFamily: "JetBrains Mono, monospace" }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={56} />
              <YAxis tick={{ fontSize: 9, fill: C.muted, fontFamily: "JetBrains Mono, monospace" }} tickLine={false} axisLine={false} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} width={36} />
              <Tooltip
                contentStyle={{ background: C.paper, border: `1px solid ${C.rule}`, borderRadius: 2, fontSize: 11, fontFamily: "inherit" }}
                formatter={(value, name, entry) => {
                  if (name === "histRev") {
                    const nd = entry?.payload?.histNDays || 1;
                    // Se aggregato (settimana/mese): mostra media €/giorno + totale del periodo
                    if (nd > 1) return [`${fmtTick(value)}/gg · tot ${fmtTick(entry?.payload?.histTotal)}`, "storico"];
                    return [fmtTick(value), "storico"];
                  }
                  if (name === "forecastExpected") return [fmtTick(value), "atteso/gg"];
                  if (name === "forecastLow") return [fmtTick(value), "CI basso"];
                  if (name === "forecastDelta") return [null, null]; // nascosto nel tooltip
                  return [value, name];
                }}
                itemSorter={() => -1}
              />
              {/* Storico: area gradient olive */}
              <Area type="monotone" dataKey="histRev" stroke={C.olive} strokeWidth={1.5}
                fill="url(#gradHistRev)" dot={false} connectNulls={false} isAnimationActive={false} />
              {/* Banda CI proiezione: stacking. forecastLow è la base trasparente,
                  forecastDelta è lo spessore colorato sopra. */}
              <Area type="monotone" dataKey="forecastLow" stackId="fcband" stroke="none"
                fill="transparent" dot={false} connectNulls={false} isAnimationActive={false} legendType="none" />
              <Area type="monotone" dataKey="forecastDelta" stackId="fcband" stroke="none"
                fill={C.tealWash} fillOpacity={0.7} dot={false} connectNulls={false} isAnimationActive={false} legendType="none" />
              {/* Linea forecast tratteggiata sopra la banda */}
              <Area type="monotone" dataKey="forecastExpected" stroke={C.teal} strokeWidth={1.5}
                strokeDasharray="5 3" fill="none" dot={false} connectNulls={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}

        {/* Legenda */}
        <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 10, color: C.muted, fontFamily: "JetBrains Mono, monospace", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 18, height: 3, background: C.olive, borderRadius: 1 }}></div>
            storico ({granLabel}{gran !== "daily" ? ", media/gg" : ""})
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 18, height: 3, background: C.teal, borderRadius: 1, borderTop: `1px dashed ${C.teal}` }}></div>
            proiezione attesa
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 18, height: 8, background: C.tealWash, borderRadius: 1 }}></div>
            banda confidenza 95%
          </div>
        </div>
      </div>;
    })()}

    <Tabs value={tab} onChange={setTab} tabs={[
      { id: "dashboard", label: "Cruscotto", icon: Activity },
      { id: "receipts", label: "Scontrini oggi", icon: Receipt, count: todayReceipts.length },
      { id: "consumption", label: "Consumo M.P.", icon: Package },
      { id: "kpi", label: "Indicatori KPI", icon: Zap, accent: receipts.length >= 7 },
      { id: "analytics", label: "Analitica & Proiezioni", icon: TrendingUp, accent: receipts.length >= 7 },
      { id: "history", label: "Storico", icon: History, count: receipts.length },
      // Tab "Confronto anni" visibile SOLO se ci sono dati storici aggregati (historicalDaily)
      ...((state.historicalDaily?.length > 0) ? [{ id: "confronto", label: "Confronto anni", icon: BarChart3 }] : []),
      // Tab "Domanda" visibile SOLO se è stato importato l'archivio Pienissimo (voyaSeasonality)
      ...((state.voyaSeasonality) ? [{ id: "domanda", label: "Domanda", icon: Calendar }] : []),
    ]} />

    {tab === "dashboard" && <VenditeOverview state={state} setState={setState} receipts={receipts} todayReceipts={todayReceipts} todayDishes={todayDishes} todayConsumption={todayConsumption} comparison={comparison} onView={setViewReceipt} onImport={() => setImporterOpen(true)} onNavigate={onNavigate} />}
    {tab === "receipts" && <SalaReceiptsList receipts={todayReceipts} state={state} onView={setViewReceipt} emptyLabel="Nessuno scontrini oggi · clicca 'Importa scontrini' in alto" />}
    {tab === "consumption" && <SalaConsumption state={state} todayDishes={todayDishes} todayConsumption={todayConsumption} todayRevenue={todayRevenue} todayRevenueNet={todayRevenueNet} />}
    {tab === "kpi" && <AnalyticsKPI state={state} setState={setState} onNavigate={onNavigate} />}
    {tab === "analytics" && <SalaAnalytics state={state} setState={setState} />}
    {tab === "history" && <SalaHistory state={state} setState={setState} receipts={receipts} onView={setViewReceipt} />}
    {tab === "confronto" && (state.historicalDaily?.length > 0) && <ConfrontoAnni state={state} />}
    {tab === "domanda" && state.voyaSeasonality && <DomandaStagionalita state={state} />}

    {/* ── IN FONDO PAGINA (spostati 2026-06-05): Annulla import + promemoria backup + Qualità dati ── */}
    <div style={{ marginTop: 28 }}>
      <UndoLastImportBanner state={state} setState={setState} />
      {(() => {
        const lastBk = state.lastBackupAt;
        if (!lastBk) {
          return (
            <div style={{ background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, padding: "10px 14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
              <AlertTriangle size={15} color={C.amberDeep} style={{ flexShrink: 0 }} />
              <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, flex: 1 }}>
                <strong style={{ color: C.ink }}>Backup mai effettuato</strong> — esporta subito un backup JSON per non perdere i dati in caso di problemi.
              </div>
              <Btn size="xs" variant="ghost" style={{ borderColor: C.amberDeep, color: C.amberDeep }} onClick={() => onNavigate({ view: "dati" })}>Vai a Backup &amp; dati</Btn>
            </div>
          );
        }
        const ageDays = Math.floor((Date.now() - new Date(lastBk).getTime()) / (1000 * 60 * 60 * 24));
        if (ageDays >= 7) {
          return (
            <div style={{ background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, padding: "10px 14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
              <AlertTriangle size={15} color={C.amberDeep} style={{ flexShrink: 0 }} />
              <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, flex: 1 }}>
                <strong style={{ color: C.ink }}>Ultimo backup: {ageDays} giorni fa</strong> — esportane uno ora per essere al sicuro.
              </div>
              <Btn size="xs" variant="ghost" style={{ borderColor: C.amberDeep, color: C.amberDeep }} onClick={() => onNavigate({ view: "dati" })}>Vai a Backup &amp; dati</Btn>
            </div>
          );
        }
        return null;
      })()}
      <ImportQualityPanel state={state} setState={setState} />
    </div>
  </div>;
}

// ── Cruscotto vendite (overview giornaliera) ───────────────────────────────
function VenditeOverview({ state, setState, receipts, todayReceipts, todayDishes, todayConsumption, comparison, onView, onImport, onNavigate }) {
  // FIX 2026-05-20: gli EC orfani in archivio devono essere visibili SEMPRE,
  // anche quando oggi non sono ancora stati importati scontrini (caso normale: importi
  // CSV storico settimanale, poi apri il cruscotto il giorno dopo senza nuovo import).
  // Calcolo qui prima dell'early return e mostro la card sopra all'empty state.
  const _isEC = (r) => r._isPreconto || r._docType === "ESTRATTO CONTO";
  const orphanECsAlways = (receipts || []).filter(r => _isEC(r) && !r._regularizedTo);

  if (todayReceipts.length === 0) return <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
    {/* Pannello "Documenti non fiscalizzati / EC orfani" rimosso dal cruscotto su richiesta
        Andi (2026-06-05). Gli EC restano contati nell'incasso (nero); il componente
        NonFiscalSection è conservato nel codice ma non più montato qui. */}
    <EmptyState icon={Upload} title="Nessuno scontrino oggi"
      body="Importa il file scontrini esportato dal gestionale Zucchetti TCPOS per popolare le statistiche del giorno."
      action={<Btn variant="accent" icon={Upload} onClick={onImport}>Importa scontrini</Btn>} />
  </div>;

  const isGrossMode = engine.priceGross(state);
  const vatS = state.settings?.vatRateSale || 0.10;

  // Pre-index VAT per riga: il mix cibo 10% + vino 22% rende sbagliata una IVA media unica.
  const dishVatById = useMemo(() => {
    const m = new Map();
    (state.dishesReal || []).forEach(d => {
      const v = (d.vatRate != null && Number.isFinite(d.vatRate)) ? d.vatRate : vatS;
      m.set(d.id, v);
    });
    return m;
  }, [state.dishesReal, vatS]);
  const lineVat = (it) => {
    const snap = it.recipeSnapshot?.vatRate;
    if (snap != null && Number.isFinite(snap)) return snap;
    const cur = dishVatById.get(it.dishId);
    if (cur != null && Number.isFinite(cur)) return cur;
    return vatS;
  };

  // Calcoli aggregati
  // FIX F-16 (cross-check 2026-05-19): split INCASSO FISCALE vs INCASSO TOTALE.
  // FIX Batch 3 — riconciliazione EC: l'EC riconciliato va escluso dal totale (già nello SF).
  // revenueNonFiscal = solo EC orfani (non riconciliati, non confermati nero definitivo).
  // revenueFiscal = tutto meno EC orfani (SF + FT + EC riconciliati + EC nero definitivo).
  const isEC = (r) => r._isPreconto || r._docType === "ESTRATTO CONTO";
  const isECReconciled = (r) => isEC(r) && r._regularizedTo && r._regularizedTo !== "NERO_DEFINITIVO";
  const isECOrphan = (r) => isEC(r) && !r._regularizedTo;
  // Totale effettivo: escludi EC riconciliati (il loro importo è già nello SF collegato)
  const totalRevenue = todayReceipts.reduce((s, r) => isECReconciled(r) ? s : s + (r.total || 0), 0);
  // Non fiscalizzato = solo orfani (quelli senza alcun collegamento, ancora "in sospeso")
  const revenueNonFiscal = todayReceipts.reduce((s, r) => isECOrphan(r) ? s + (r.total || 0) : s, 0);
  const revenueFiscal = totalRevenue - revenueNonFiscal;
  // ecCount usato nel tile: solo EC orfani (non riconciliati)
  const ecCount = todayReceipts.filter(r => isECOrphan(r)).length;
  // Totale EC orfani su TUTTI i periodi (per NonFiscalSection che lavora sull'intero archivio)
  const allOrphanECs = useMemo(() => (receipts || []).filter(r => isEC(r) && !r._regularizedTo), [receipts]);
  // Scorporo IVA per riga (Reg. UE 1169/2011): MAI una media unica.
  const revenueNet = useMemo(() => {
    let net = 0;
    todayReceipts.forEach(r => (r.items || []).forEach(it => {
      const grossLine = (Number(it.qty) || 0) * (Number(it.unitPrice) || 0);
      const v = lineVat(it);
      net += v > -1 ? grossLine / (1 + v) : grossLine;
    }));
    return net;
  }, [todayReceipts, dishVatById]);
  const totalCostNet = (todayConsumption || []).reduce((s, c) => s + (c.costSnapshot > 0 ? c.costSnapshot : c.qtyNet * (c.bp?.pReale || c.bp?.pFatt || 0)), 0);
  const margin = revenueNet - totalCostNet;
  const fcPct = revenueNet > 0 ? (totalCostNet / revenueNet) * 100 : 0;

  // Identificazione prodotti che hanno raggiunto livelli critici post-consumo oggi
  const stockAlerts = useMemo(() => {
    return (todayConsumption || []).map(c => {
      const stockNow = engine.itemStock(state, c.bp.id);
      const minStock = c.bp.minStock || 2;
      const willRunOutInDays = c.qtyNet > 0 ? Math.floor(stockNow / c.qtyNet) : 999;
      return { bp: c.bp, stockNow, qtyToday: c.qtyNet, minStock, willRunOutInDays, critical: stockNow < minStock };
    }).filter(x => x.critical || x.willRunOutInDays < 3).sort((a, b) => a.willRunOutInDays - b.willRunOutInDays).slice(0, 8);
  }, [todayConsumption, state]);

  // Top dish con margine.
  // FIX A.1 (audit 2026-05-20): preferisci costo da snapshot ricetta (catturato al momento
  // della vendita) per evitare drift retroattivo: se Andi alza oggi il prezzo del crudo, il
  // margine di ieri NON deve cambiare. Snapshot disponibile se >=80% della qty venduta ha
  // recipeSnapshot.netCost; altrimenti fallback al calcolo da prezzi MP correnti con flag
  // `costSource: "current"` mostrato in UI.
  const dishesWithMargin = useMemo(() => {
    const dishes = state.dishesReal || [];
    const baseProducts = state.baseProducts || [];
    return todayDishes.map(d => {
      const dish = dishes.find(x => x.id === d.dishId);
      const snapshotCoverage = d.qty > 0 ? (d.hasSnapshot || 0) / d.qty : 0;
      let costPerUnit, costSource;
      if (snapshotCoverage >= 0.8 && d.costSnapshotSum > 0 && d.hasSnapshot > 0) {
        // Costo medio per unità basato su snapshot delle vendite coperte
        costPerUnit = d.costSnapshotSum / d.hasSnapshot;
        costSource = "snapshot";
      } else {
        // Fallback: calcolo dal listino corrente (logica originale)
        let cpu = 0;
        if (dish && dish.ingredienti) {
          cpu = dish.ingredienti.reduce((s, ing) => {
            if (!ing.bpId) return s;
            const bp = baseProducts.find(b => b.id === ing.bpId);
            if (!bp) return s;
            return s + (ing.qty * (bp.pReale || bp.pFatt || 0));
          }, 0);
        }
        costPerUnit = cpu;
        costSource = "current";
      }
      const totalCost = costPerUnit * d.qty;
      // IVA del piatto: override (dish.vatRate) o default. Senza fallback su lineVat
      // perché qui aggreghiamo per dishId, quindi tutti gli scontrini condividono lo stesso piatto.
      const dishVat = (dish?.vatRate != null && Number.isFinite(dish.vatRate)) ? dish.vatRate : vatS;
      const totalRevNet = d.revenue / (1 + dishVat);
      const dishMargin = totalRevNet - totalCost;
      const fc = totalRevNet > 0 ? (totalCost / totalRevNet) * 100 : 0;
      return { ...d, costPerUnit, totalCost, dishMargin, fc, costSource, snapshotCoverage };
    }).sort((a, b) => b.dishMargin - a.dishMargin);
  }, [todayDishes, state.dishesReal, state.baseProducts, vatS]);

  return <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

    {/* Pannello "Documenti non fiscalizzati / EC orfani" rimosso dal cruscotto su richiesta
        Andi (2026-06-05). Gli EC restano contati nell'incasso (nero); NonFiscalSection è
        conservato nel codice ma non più montato. Per ripristinarlo: rimontare qui. */}

    {/* Tile riepilogo giornata (incasso netto, costo MP, margine, FC%) */}
    {/* FIX F-16: tile INCASSO split fiscale / non fiscalizzato se ci sono ESTRATTO CONTO */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
      <div style={{ background: C.ink, padding: 16, borderRadius: 2, color: C.paper }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.mutedSoft, letterSpacing: "0.2em" }}>
          {ecCount > 0 ? "INCASSO FISCALE" : `INCASSO ${isGrossMode ? "LORDO" : "NETTO"}`}
        </div>
        <div className="f-display" style={{ fontSize: 26, fontWeight: 600, marginTop: 3 }}>
          {ecCount > 0 ? fmt.eur(isGrossMode ? revenueFiscal : (revenueNet - revenueNonFiscal / (1 + (state.settings?.vatRateSale || 0.10)))) : fmt.eur(isGrossMode ? totalRevenue : revenueNet)}
        </div>
        <div className="f-mono" style={{ fontSize: 10, color: C.mutedSoft, marginTop: 3 }}>
          {todayReceipts.length} scontrini
          {ecCount > 0 && <span style={{ color: C.terra, marginLeft: 6 }}>
            {" · "}tot {fmt.eur(isGrossMode ? totalRevenue : revenueNet)}
          </span>}
        </div>
        {ecCount > 0 && <div className="f-mono" style={{ fontSize: 9, color: C.terra, marginTop: 4 }}>
          di cui non fiscalizzati: {fmt.eur(revenueNonFiscal)} ({ecCount} EC)
        </div>}
      </div>
      <div style={{ background: C.terraWash, border: `1px solid ${C.terra}`, padding: 16, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.terra, letterSpacing: "0.2em" }}>COSTO M.P. NETTO</div>
        <div className="f-display" style={{ fontSize: 26, fontWeight: 600, color: C.terra, marginTop: 3 }}>{fmt.eur(totalCostNet)}</div>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>{(todayConsumption || []).length} prodotti</div>
      </div>
      <div style={{ background: C.oliveWash, border: `1px solid ${C.olive}`, padding: 16, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.olive, letterSpacing: "0.2em" }}>MARGINE LORDO</div>
        <div className="f-display" style={{ fontSize: 26, fontWeight: 600, color: C.oliveDeep, marginTop: 3 }}>{fmt.eur(margin)}</div>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>su incasso netto</div>
      </div>
      <div style={{ background: fcPct > 35 ? C.terraWash : fcPct > 28 ? C.amberWash : C.oliveWash, border: `1px solid ${fcPct > 35 ? C.terra : fcPct > 28 ? C.amber : C.olive}`, padding: 16, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: fcPct > 35 ? C.terra : fcPct > 28 ? C.amberDeep : C.olive, letterSpacing: "0.2em" }}>FOOD COST %</div>
        <div className="f-display" style={{ fontSize: 26, fontWeight: 600, color: fcPct > 35 ? C.terra : fcPct > 28 ? C.amberDeep : C.oliveDeep, marginTop: 3 }}>{fcPct.toFixed(1)}%</div>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>target &lt; 30%</div>
      </div>
    </div>

    {/* Alert stock critici */}
    {stockAlerts.length > 0 && <div style={{ background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <AlertTriangle size={18} color={C.amberDeep} />
        <div className="f-display" style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{stockAlerts.length} prodotti in attenzione dopo consumo di oggi</div>
        {onNavigate && <Btn size="xs" variant="ghost" onClick={() => onNavigate({ view: "magazzino" })} style={{ marginLeft: "auto" }}>Vai al magazzino</Btn>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
        {stockAlerts.map(a => <div key={a.bp.id} style={{ background: C.white, padding: 10, borderRadius: 2, border: `1px solid ${a.critical ? C.terra : C.ruleSoft}` }}>
          <div className="f-body" style={{ fontSize: 11, color: C.ink, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.bp.display || a.bp.nome}>{a.bp.display || a.bp.nome}</div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>Giacenza: <strong style={{ color: a.critical ? C.terra : C.ink }}>{a.stockNow.toFixed(2)} {a.bp.unit || "kg"}</strong></div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>Consumo oggi: {a.qtyToday.toFixed(3)} {a.bp.unit || "kg"}</div>
          <div className="f-mono" style={{ fontSize: 10, fontWeight: 600, color: a.willRunOutInDays < 2 ? C.terra : a.willRunOutInDays < 5 ? C.amberDeep : C.olive, marginTop: 4 }}>
            {a.willRunOutInDays < 999 ? `Finisce in ~${a.willRunOutInDays}gg` : "—"}
            {a.critical && " · SOTTO MINIMO"}
          </div>
        </div>)}
      </div>
    </div>}

    {/* Layout 2 colonne: Top piatti per margine + ultimi scontrini */}
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
      <div>
        <SectionHeader title="Top piatti per margine oggi" subtitle={`${dishesWithMargin.length} ricette · ordinato per margine assoluto generato`} />
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
          <div style={{ display: "grid", gridTemplateColumns: "30px 1.4fr 0.4fr 0.6fr 0.6fr 0.5fr", padding: "8px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.2em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
            <span>#</span><span>PIATTO</span><span style={{ textAlign: "right" }}>QTÀ</span><span style={{ textAlign: "right" }}>RICAVO</span><span style={{ textAlign: "right" }}>MARGINE</span><span style={{ textAlign: "right" }}>FC%</span>
          </div>
          {/* FIX A.1 (audit 2026-05-20): asterisco "*" su margine quando il costo MP è
              calcolato da prezzi correnti invece che da snapshot della vendita. Indica
              possibile drift retroattivo se sono cambiati i prezzi materia prima recenti. */}
          {dishesWithMargin.slice(0, 12).map((d, i) => <div key={d.dishId} style={{ display: "grid", gridTemplateColumns: "30px 1.4fr 0.4fr 0.6fr 0.6fr 0.5fr", padding: "7px 14px", borderTop: `1px solid ${C.ruleSoft}`, alignItems: "center", fontSize: 12 }}>
            <span className="f-mono" style={{ color: C.muted, fontWeight: 600 }}>{i + 1}</span>
            <span className="f-body" style={{ color: C.ink, fontWeight: 500 }}>{d.name}</span>
            <span className="f-mono" style={{ color: C.ink, fontWeight: 600, textAlign: "right" }}>× {d.qty}</span>
            <span className="f-mono" style={{ color: C.inkSoft, textAlign: "right" }}>{fmt.eur(d.revenue)}</span>
            <span className="f-mono" style={{ color: d.dishMargin > 0 ? C.olive : C.terra, fontWeight: 600, textAlign: "right" }}
              title={d.costSource === "current"
                ? `Costo MP calcolato con prezzi correnti (snapshot ricetta non disponibile per ${Math.round((1 - (d.snapshotCoverage || 0)) * 100)}% delle vendite). Possibile drift retroattivo se hai modificato i prezzi materia prima recentemente.`
                : `Costo MP da snapshot ricetta (catturato al momento della vendita). Margine stabile, immune a successive modifiche del listino.`}>
              {fmt.eur(d.dishMargin)}{d.costSource === "current" ? <span style={{ color: C.amber, marginLeft: 2, fontWeight: 700 }}>*</span> : null}
            </span>
            <span className="f-mono" style={{ color: d.fc > 35 ? C.terra : d.fc > 28 ? C.amber : C.olive, fontWeight: 600, textAlign: "right" }}>{d.costPerUnit > 0 ? `${d.fc.toFixed(0)}%` : "—"}</span>
          </div>)}
          {/* FIX A.1 (audit 2026-05-20): legenda asterisco — visibile solo se almeno un piatto è "current" */}
          {dishesWithMargin.slice(0, 12).some(d => d.costSource === "current") && <div className="f-mono" style={{ padding: "6px 14px", borderTop: `1px solid ${C.ruleSoft}`, fontSize: 9, color: C.muted, background: C.paperDeep }}>
            <span style={{ color: C.amber, fontWeight: 700 }}>*</span> Margine calcolato con prezzi materia prima correnti — non snapshot. Possibile drift retroattivo se hai aggiornato i prezzi recentemente.
          </div>}
        </div>
      </div>
      <div>
        <SectionHeader title="Ultimi scontrini" />
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, maxHeight: 420, overflowY: "auto" }}>
          {[...todayReceipts].sort((a, b) => new Date(b.closedAt) - new Date(a.closedAt)).slice(0, 15).map(r => <button key={r.id} onClick={() => onView(r)} className="tx" style={{ width: "100%", padding: "8px 12px", background: "transparent", border: "none", borderTop: `1px solid ${C.ruleSoft}`, textAlign: "left", cursor: "pointer", fontFamily: "inherit", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            onMouseEnter={e => e.currentTarget.style.background = C.paperDeep}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <div>
              <div className="f-mono" style={{ fontSize: 11, color: C.ink, fontWeight: 600 }}>Tav. {r.number} · {r.covers} cop.</div>
              <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>{new Date(r.closedAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} · {getShift(r.closedAt) === "pranzo" ? "☀" : "🌙"}</div>
            </div>
            <span className="f-mono" style={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>{fmt.eur(r.total)}</span>
          </button>)}
        </div>
      </div>
    </div>
  </div>;
}


// ── CSV PARSING & FUZZY MATCHING UTILITIES ─────────────────────────────────
// Parser CSV robusto che gestisce quote, virgole nei campi, escape
function parseCSV(text, delimiter = null) {
  // Auto-detect delimiter su PRIME 5 RIGHE NON-VUOTE (non solo la prima).
  // FIX P0-A (audit 2026-05-20): se TCPOS aggiunge una riga di commento iniziale
  // (es. "Esportato il 18/05/2026, 10:01:50 da TCPOS") il vecchio detect sulla
  // riga 0 contava le virgole del commento e sceglieva ',' come delimiter,
  // facendo collassare TUTTE le righe dati in singole stringhe → 0 righe valide
  // → import vuoto SENZA errore visibile (l'errore "Colonna Data non trovata"
  // scatta solo in certi casi; in altri header con virgole l'errore non scatta
  // e le righe vengono buttate silenziosamente). Leggere 5 righe rende il
  // detect dominante sui delimitatori reali dei dati, non sull'eventuale rumore.
  if (!delimiter) {
    const lines = text.split(/\r?\n/).filter(l => l.trim()).slice(0, 5);
    const sample = lines.join("\n");
    const commas = (sample.match(/,/g) || []).length;
    const semicolons = (sample.match(/;/g) || []).length;
    const tabs = (sample.match(/\t/g) || []).length;
    delimiter = tabs > commas && tabs > semicolons ? "\t" : (semicolons > commas ? ";" : ",");
  }
  const rows = [];
  let row = [], field = "", inQuote = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i], next = text[i + 1];
    if (inQuote) {
      if (c === '"' && next === '"') { field += '"'; i++; }
      else if (c === '"') { inQuote = false; }
      else field += c;
    } else {
      if (c === '"') inQuote = true;
      else if (c === delimiter) { row.push(field); field = ""; }
      else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
      else if (c === "\r") { /* skip */ }
      else field += c;
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return { rows: rows.filter(r => r.length > 1 || (r.length === 1 && r[0].trim())), delimiter };
}

// Normalizza stringa per matching (rimuove accenti, punteggiatura, lowercase)
function normStr(s) {
  if (!s) return "";
  return String(s).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w\s]/g, " ").replace(/\s+/g, " ").trim();
}

// Distanza di Levenshtein normalizzata (0 = identico, 1 = totalmente diverso)
function levenshteinNorm(a, b) {
  const an = normStr(a), bn = normStr(b);
  if (!an && !bn) return 0;
  if (!an || !bn) return 1;
  if (an === bn) return 0;
  const maxLen = Math.max(an.length, bn.length);
  const m = an.length, n = bn.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = an[i-1] === bn[j-1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i-1][j] + 1, dp[i][j-1] + 1, dp[i-1][j-1] + cost);
    }
  }
  return dp[m][n] / maxLen;
}

// ── Token-matching deterministico (Strategia E + nucleo C) ──────────────────
// SOLO per i suggerimenti del pannello "Qualità dati venduto" (display, no auto-map).
// Riconosce troncamenti/abbreviazioni POS ("cal"⊂"calamari") meglio di Levenshtein.
// Offline, deterministico, nessuna dipendenza. NON usato dall'importer.
const TOK_MIN = 3; // lunghezza minima di un token per poter ancorare
function tokenize(s) {
  return normStr(s).split(/\s+/).filter(Boolean); // normStr toglie già accenti/punteggiatura
}
// Quanto il token `a` àncora il token `b`: 1 se identici, prefisso comune (≥TOK_MIN) → tightness 0..1, else 0.
function tokenAnchor(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 1;
  const short = a.length <= b.length ? a : b;
  const long = a.length <= b.length ? b : a;
  if (short.length >= TOK_MIN && long.startsWith(short)) return short.length / long.length;
  return 0;
}
// Score di match articolo→candidato. GATE: OGNI token-articolo (≥TOK_MIN) deve ancorare un
// token-candidato distinto, altrimenti {ok:false} (anti falso positivo). + anti-frammento.
function tokenMatchScore(artTokens, candTokens) {
  const artC = artTokens.filter(t => t.length >= TOK_MIN);
  const candC = candTokens.filter(t => t.length >= TOK_MIN);
  if (!artC.length || !candC.length) return { ok: false };
  const used = new Array(candC.length).fill(false);
  let sumTight = 0, matched = 0;
  for (const at of artC) {
    let best = 0, bestIdx = -1;
    for (let j = 0; j < candC.length; j++) {
      if (used[j]) continue;
      const t = tokenAnchor(at, candC[j]);
      if (t > best) { best = t; bestIdx = j; }
    }
    if (best === 0) return { ok: false };               // token articolo non ancorato → scarta
    used[bestIdx] = true; sumTight += best; matched++;
  }
  if (matched < Math.ceil(candC.length / 2)) return { ok: false }; // pezzetto vs piatto lungo → scarta
  const coverageCand = matched / candC.length;
  const tightness = sumTight / artC.length;
  return { ok: true, conf: 0.6 * coverageCand + 0.4 * tightness, matched };
}

// Matching fuzzy: ritorna best match sui piatti con score
function matchDishFuzzy(nomeCsv, dishes, aliases = {}) {
  const key = normStr(nomeCsv);
  // 1) Alias esatto (priorità massima)
  if (aliases[key]) {
    const d = dishes.find(x => x.id === aliases[key]);
    if (d) return { dish: d, score: 1, source: "alias" };
  }
  // 2) Match esatto sul nome normalizzato
  const exact = dishes.find(d => normStr(d.nome) === key);
  if (exact) return { dish: exact, score: 1, source: "exact" };
  // 3) Match fuzzy Levenshtein
  let best = null;
  dishes.forEach(d => {
    const dist = levenshteinNorm(nomeCsv, d.nome);
    const score = 1 - dist;
    if (!best || score > best.score) best = { dish: d, score, source: "fuzzy" };
  });
  // Soglia 0.85: sotto-soglia → null → _unmatched_, evita falsi positivi
  // tipo "CALAMARATA"→"CARBONARA" (distanza Levenshtein bassa, semantica opposta).
  // Andi vede gli unmatched in UI e può mappare manualmente (salva alias).
  return best && best.score >= 0.85 ? best : null;
}

// ⟦RECEIPT-FACADE:START⟧ — Fase 1 ADR CRM Link-Core v1.0.2 (P8/P10/P11/P12/P13)
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                    RECEIPT READ-FACADE (CRM Fase 1)                        ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// Layer di lettura PURO sui receipt (state.tableReceipts). È l'UNICO punto di
// accesso del futuro CRM ai Receipt (ADR I10). Vincoli congelati:
//   • nessuna scrittura, nessun side effect, nessuna mutazione — Receipt read-only;
//   • NON dipende da SHIFT_CONFIG/getShift/getServiceDate — usa solo timestamp
//     grezzi (ADR P2/I13);
//   • NON referenzia Customer — dipendenza unidirezionale Link→Receipt (ADR §A.2);
//   • `source`/`registerId` sono METADATA, mai parte di namespace o chiave (P12/I11).
// Tutte le funzioni sono pure: (receipt|state) → valore, senza effetti.

// Hash puro deterministico (djb2-xor) — nessuna dipendenza, riproducibile.
function _rfHash(str) {
  let h = 5381;
  const s = String(str);
  for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

// Canonicalizzazione tavolo (ADR P10). Il prefisso "tavolo/tav/t/#" si rimuove SOLO
// se seguito da una cifra → preserva i tavoli con nome alfabetico ("Terrazza", non
// diventa "errazza"). Numerico → forma canonica senza zeri iniziali ("007" → "7").
function _rfCanonicalTable(rawNumber) {
  const raw = String(rawNumber == null ? "" : rawNumber).trim();
  if (!raw) return null;
  const stripped = raw.replace(/^(?:tavolo|tav|t|#)\s*\.?\s*(?=\d)/i, "").trim();
  if (/^\d+$/.test(stripped)) return String(parseInt(stripped, 10));
  return normStr(stripped) || null;
}

const RECEIPT_KEY_VERSION = "v1";
// v2 (sblocco autorizzato 2026-07-06): TCPOS ricomincia la numerazione fiscale
// ogni giorno → il tier T1 solo-fiscale collide tra giorni diversi (13 casi reali
// osservati sui dati 30/06–03/07). I tier v2 aggiungono il giorno GREZZO di
// closedAt (slice ISO, nessuna dipendenza da SHIFT_CONFIG — coerente con I13).
// ADDITIVO: le chiavi v1 restano emesse identiche, i link pinnati v1 continuano
// a risolvere per intersezione; i nuovi link nascono con primary v2 day-scoped.
const RECEIPT_KEY_VERSION_V2 = "v2";
// Namespace identitario FORTE = SOLO tenant/restaurant (ADR P12/I11). Tenant/restaurant
// non ancora modellati nello schema → default single-tenant "t0/r0". source/registerId
// NON entrano qui (sono metadata di disambiguazione, non identità).
function _rfNamespace(receipt, opts) {
  const tenantId = (opts && opts.tenantId) || (receipt && receipt.tenantId) || "t0";
  const restaurantId = (opts && opts.restaurantId) || (receipt && receipt.restaurantId) || "r0";
  return `${tenantId}/${restaurantId}`;
}

const receiptFacade = {
  // — Lookup diretto per id (id interno volatile: valido solo nella sessione corrente).
  getReceiptById(state, id) {
    if (!state || !Array.isArray(state.tableReceipts) || id == null) return null;
    return state.tableReceipts.find(r => r && r.id === id) || null;
  },

  // — Risolve un receipt da una stableKey provando l'intersezione con il candidateKeySet
  //   di ciascun receipt (repair multi-tier, ADR P11). Match ammesso SOLO se NON ambiguo:
  //   esattamente 1 candidato → quel receipt; 0 → null; >1 → null (mai scelta silenziosa,
  //   I12: l'ambiguità è competenza della collision/conflict queue, non di questa lettura).
  //   Implementazione O(N) a scansione: un indice persistito è ottimizzazione di Fase ≥2.
  resolveReceiptByStableKey(state, stableKey, opts) {
    if (!state || !Array.isArray(state.tableReceipts) || !stableKey) return null;
    const hits = state.tableReceipts.filter(r => {
      const set = receiptFacade.computeReceiptCandidateKeys(r, opts);
      return set.some(k => k.key === stableKey);
    });
    return hits.length === 1 ? hits[0] : null;
  },

  // — Tavolo canonico (ADR P10). Banco → null. La label originale NON viene toccata:
  //   il display continua a leggere receipt.number direttamente (comportamento invariato).
  getReceiptTable(receipt) {
    if (!receipt) return null;
    if (receipt.isBanco) return null;
    return _rfCanonicalTable(receipt.number);
  },

  // — Denaro. total lordo autoritativo del receipt (include i "regali" a prezzo 0).
  getReceiptMoney(receipt) {
    const total = receipt && Number.isFinite(Number(receipt.total)) ? Number(receipt.total) : 0;
    return { total, totalCents: Math.round(total * 100), currency: "EUR" };
  },

  // — AUDIT-FIX I1 (additivo, sblocco autorizzato 2026-07-09): true se il
  //   receipt CONTA come incasso, false se è un ESTRATTO CONTO già riconciliato
  //   a uno scontrino fiscale (in quel caso il suo importo è già dentro il SF →
  //   sommarlo raddoppia la spesa cliente). Incapsula qui l'accesso ai campi
  //   grezzi _regularizedTo/_docType/_isPreconto: i consumer (anche i blocchi
  //   congelati) restano puliti (I10) leggendo la regola SOLO via facade.
  //   Regola unica condivisa con incassi/cruscotto (_receiptCountsForRevenue).
  getReceiptCountsForRevenue(receipt) {
    return _receiptCountsForRevenue(receipt);
  },

  // — Items. Vista read-only normalizzata (nuovo array, nessuna mutazione del receipt).
  getReceiptItems(receipt) {
    const items = receipt && Array.isArray(receipt.items) ? receipt.items : [];
    return items.map(it => {
      const qty = Number(it.qty) || 0;
      const unitPrice = Number(it.unitPrice) || 0;
      return {
        dishId: it.dishId ?? null,
        name: it.name ?? null,
        qty,
        unitPrice,
        lineGross: qty * unitPrice,
        isGift: !!it._isGift,
        csvName: it._csvName ?? null,
        sourceFiscalNumber: it._sourceFiscalNumber ?? null,
      };
    });
  },

  // — Anchor temporali GREZZI (ADR P2/P13). MAI serviceDate/shift (I13: quelli sono
  //   presentazione e dipendono da SHIFT_CONFIG mutabile). closedAt è il rawTimeAnchor.
  //   NB: nell'import attuale openedAt è sintetico (== closedAt): lo segnaliamo nel flag.
  getReceiptTimeAnchors(receipt) {
    const closedAt = receipt && receipt.closedAt ? String(receipt.closedAt) : null;
    const openedAt = receipt && receipt.openedAt ? String(receipt.openedAt) : null;
    const ms = closedAt ? Date.parse(closedAt) : NaN;
    return {
      rawClosedAt: closedAt,
      rawOpenedAt: openedAt,
      rawClosedAtMs: Number.isNaN(ms) ? null : ms,
      openedAtIsSynthetic: !!(closedAt && openedAt && closedAt === openedAt),
    };
  },

  // — Identità sorgente: METADATA (ADR P12), mai identità forte. registerId NON è
  //   catturato dall'import attuale → null (documentato). source è derivato perché
  //   sul receipt non esiste un campo normalizzato (oggi: _imported/operator).
  getReceiptSourceIdentity(receipt) {
    if (!receipt) {
      return { source: null, registerId: null, sourceFile: null, docType: null, fiscalNumber: null, mergedFiscalNumbers: null, isImported: false };
    }
    const isImported = !!receipt._imported || receipt.operator === "import";
    return {
      source: isImported ? "tcpos" : "manual",
      registerId: receipt.registerId ?? null,
      sourceFile: receipt._sourceFile ?? null,
      docType: receipt._docType ?? null,
      fiscalNumber: receipt.fiscalNumber ?? null,
      mergedFiscalNumbers: Array.isArray(receipt._mergedFiscalNumbers) ? receipt._mergedFiscalNumbers.slice() : null,
      isImported,
    };
  },

  // — candidateKeySet (ADR P11/P12). Set ORDINATO forte→debole di candidate keys.
  //   Forma: `${keyVersion}:${namespace}:${tierBody}`. Namespace = SOLO tenant/restaurant.
  //   source/registerId NON entrano nella chiave (P12). Il repair (Fase ≥2) riaggancia
  //   per intersezione tra il candidateKeySnapshot del link e questo set; qui solo il
  //   calcolo PURO. Tier disponibili oggi (forte→debole):
  //     T1_FISCAL_DAY    — v2: giorno grezzo di closedAt + fiscalNumber (anti-collisione
  //                        cross-day: TCPOS riparte da 1 ogni giorno);
  //     T2_FISCALSET_DAY — v2: giorno grezzo + hash del set fiscale MENU CONCORDATO;
  //     T1_FISCAL        — v1 LEGACY: fiscalNumber singolo (collide tra giorni; resta
  //                        emesso per i link pinnati prima del 2026-07-06);
  //     T2_FISCALSET     — v1 LEGACY: merge MENU CONCORDATO → set ordinato→hash;
  //     T3_TEMPORAL      — closedAt grezzo + tavolo canonico + totale(cents);
  //     T4_SEMANTIC      — rete finale, sempre degraded (per banco senza doc è l'unico stabile).
  //   NB dati mancanti: nessun tier dipende da registerId (non catturato) — coerente con P12.
  computeReceiptCandidateKeys(receipt, opts) {
    if (!receipt) return [];
    const ns = _rfNamespace(receipt, opts);
    const pre = `${RECEIPT_KEY_VERSION}:${ns}:`;
    const pre2 = `${RECEIPT_KEY_VERSION_V2}:${ns}:`;
    const out = [];
    const merged = Array.isArray(receipt._mergedFiscalNumbers) ? receipt._mergedFiscalNumbers.filter(Boolean) : [];
    const fiscal = receipt.fiscalNumber != null ? String(receipt.fiscalNumber).trim() : "";
    const closedAt = receipt.closedAt ? String(receipt.closedAt) : "";
    // Giorno GREZZO = primi 10 char della stringa closedAt immutabile (forma ISO
    // "YYYY-MM-DD…"). Deterministico per identità; NON è il service-day (I13).
    const rawDay = closedAt.slice(0, 10);

    if (fiscal && merged.length <= 1) {
      if (rawDay) {
        out.push({ tier: "T1_FISCAL_DAY", key: `${pre2}fiscalday:${rawDay}|${normStr(fiscal) || fiscal}`, degraded: false });
      }
      out.push({ tier: "T1_FISCAL", key: `${pre}fiscal:${normStr(fiscal) || fiscal}`, degraded: false });
    }
    if (merged.length > 1) {
      const sorted = merged.map(x => String(x).trim()).sort();
      if (rawDay) {
        out.push({ tier: "T2_FISCALSET_DAY", key: `${pre2}fiscalsetday:${rawDay}|${_rfHash(sorted.join("+"))}`, degraded: false });
      }
      out.push({ tier: "T2_FISCALSET", key: `${pre}fiscalset:${_rfHash(sorted.join("+"))}`, degraded: false });
    }
    const tbl = receipt.isBanco ? "banco" : (_rfCanonicalTable(receipt.number) || "?");
    const totalCents = Math.round((Number(receipt.total) || 0) * 100);
    if (closedAt) {
      out.push({ tier: "T3_TEMPORAL", key: `${pre}temporal:${closedAt}|${tbl}|${totalCents}`, degraded: false });
    }
    const itemCount = Array.isArray(receipt.items) ? receipt.items.length : 0;
    out.push({ tier: "T4_SEMANTIC", key: `${pre}semantic:${_rfHash(`${closedAt}|${tbl}|${totalCents}|${itemCount}`)}`, degraded: true });

    const rank = { T1_FISCAL_DAY: 1, T2_FISCALSET_DAY: 2, T1_FISCAL: 3, T2_FISCALSET: 4, T3_TEMPORAL: 5, T4_SEMANTIC: 6 };
    out.sort((a, b) => rank[a.tier] - rank[b.tier]);
    return out;
  },
};

// Handle diagnostico di sola lettura per validare la facade nella PWA reale (console:
// `window.__receiptFacade.computeReceiptCandidateKeys(...)`). Non altera alcun
// comportamento esistente; è solo un riferimento all'oggetto puro qui sopra.
if (typeof window !== "undefined") { try { window.__receiptFacade = receiptFacade; } catch { /* no-op */ } }
// ⟦RECEIPT-FACADE:END⟧

// ⟦CRM-CORE:START⟧ — Fase 2 ADR CRM Link-Core v1.0.2 (§A.3/§A.5/§A.9, P3/P4/P5/P14, I5/I15)
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║              CRM CORE — Customer + Consenso (CRM Fase 2)                   ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// Layer di dominio PURO per le DUE sole entità della Fase 2: `customers` e
// `customerConsents`. Tutte le funzioni sono reducer immutabili: prendono gli
// array di stato e ne restituiscono di NUOVI, senza side effect né mutazioni.
// Il wiring UI (schermate) è Fase ≥2 e fuori scope qui (questo è il motore).
//
// Confini di Fase 2 (ADR):
//   • NIENTE CustomerReceiptLink, Visit, Reservation, Matching, Marketing (out of scope);
//   • storage additivo (SEED + array guarantee) → retrocompat totale, storage key invariata;
//   • multi-device/concorrenza CONGELATA, non implementata: ogni entità mutabile nasce
//     con i campi riservati version/updatedAt/deviceId/tombstone con default (P3);
//   • tenant/restaurant riservati da subito, default t0/r0 (P5);
//   • Customer: NESSUN delete fisico → solo tombstone (P3);
//   • Consenso: APPEND-ONLY (I5) → si "cambia" idea appendendo un nuovo evento, mai mutando;
//     stato risolto per coppia (kind, channel) con ordinamento per tupla (deviceId, seq, at)
//     e revoca dominante in ambiguità (P14/I15); default = nessun consenso (deny, I7);
//   • Oblio (GDPR): anonimizzazione del SOGGETTO (PII rimossa dal Customer) con evento
//     consenso conservato in forma minimizzata anonima come prova legale; eccezione legalHold (P4/I5).

const CRM_DEFAULT_TENANT = "t0";
const CRM_DEFAULT_RESTAURANT = "r0";
const CRM_DEFAULT_DEVICE = "local";
const CRM_CUSTOMER_TYPES = ["person", "family", "company"];
const CRM_CONSENT_KINDS = ["marketing", "profiling", "allergies"];
const CRM_CONSENT_ACTIONS = ["grant", "revoke"];

// ctx normalizzato: chi/quando/da-quale-device. `at`/`deviceId` iniettabili (test/repro),
// default runtime nowIso()/"local". L'audit (I4) NON dipende da PII conservata.
function _crmCtx(ctx) {
  const c = ctx || {};
  return {
    by: c.by != null ? String(c.by) : "system",
    at: c.at != null ? String(c.at) : nowIso(),
    deviceId: c.deviceId != null ? String(c.deviceId) : CRM_DEFAULT_DEVICE,
    tenantId: c.tenantId != null ? String(c.tenantId) : CRM_DEFAULT_TENANT,
    restaurantId: c.restaurantId != null ? String(c.restaurantId) : CRM_DEFAULT_RESTAURANT,
  };
}

// Sanitizza la lista contatti (vista difensiva: nuovo array, voci normalizzate).
function _crmContacts(contacts) {
  if (!Array.isArray(contacts)) return [];
  return contacts
    .filter(x => x && (x.value != null && String(x.value).trim() !== ""))
    .map(x => ({
      kind: x.kind != null ? String(x.kind) : "other",
      value: String(x.value).trim(),
      note: x.note != null ? String(x.note) : null,
    }));
}

// Ordinamento deterministico del consenso (ADR I15): tupla (deviceId, seq, at) ASC.
// Il MASSIMO in quest'ordine è la decisione vincente. seq è monotona PER device
// (non globale offline): per questo deviceId entra nella tupla come primo discriminante
// — congelato così nell'invariante, oggi single-writer (tutti "local") ⇒ riduce a (seq, at).
function _crmConsentCmp(a, b) {
  const da = String(a.deviceId || ""), db = String(b.deviceId || "");
  if (da < db) return -1;
  if (da > db) return 1;
  const sa = Number(a.seq) || 0, sb = Number(b.seq) || 0;
  if (sa !== sb) return sa - sb;
  const ta = String(a.at || ""), tb = String(b.at || "");
  if (ta < tb) return -1;
  if (ta > tb) return 1;
  return 0;
}

const crmCore = {
  // ── Customer — CRUD minimo (reducer puri, immutabili) ─────────────────────

  // Lookup diretto per id. Per default ESCLUDE i tombstoned (cancellati logici).
  getCustomer(customers, id, opts) {
    if (!Array.isArray(customers) || id == null) return null;
    const includeTombstoned = !!(opts && opts.includeTombstoned);
    const c = customers.find(x => x && x.id === id) || null;
    if (!c) return null;
    if (c.tombstone && !includeTombstoned) return null;
    return c;
  },

  // Elenco filtrabile. Default: solo attivi (no tombstone). Filtri opzionali per
  // tenant/restaurant (P5) e per inclusione di tombstoned/anonimizzati.
  listCustomers(customers, opts) {
    if (!Array.isArray(customers)) return [];
    const o = opts || {};
    return customers.filter(c => {
      if (!c) return false;
      if (c.tombstone && !o.includeTombstoned) return false;
      if (c.anonymized && o.excludeAnonymized) return false;
      if (o.tenantId != null && c.tenantId !== String(o.tenantId)) return false;
      if (o.restaurantId != null && c.restaurantId !== String(o.restaurantId)) return false;
      if (o.type != null && c.type !== String(o.type)) return false;
      return true;
    });
  },

  // Crea un customer. `type` validato (person|family|company), displayName obbligatorio.
  // Nasce con i campi riservati P3 a default (version 1 / deviceId / tombstone false) e
  // scope tenant/restaurant P5. Ritorna { customers, customer, error }; error=null = ok.
  createCustomer(customers, input, ctx) {
    const base = Array.isArray(customers) ? customers : [];
    const inp = input || {};
    const c = _crmCtx(ctx);
    const type = String(inp.type || "person");
    if (!CRM_CUSTOMER_TYPES.includes(type)) {
      return { customers: base, customer: null, error: `type non valido: ${type}` };
    }
    const displayName = inp.displayName != null ? String(inp.displayName).trim() : "";
    if (!displayName) {
      return { customers: base, customer: null, error: "displayName obbligatorio" };
    }
    const customer = {
      id: uid("cust_"),
      tenantId: inp.tenantId != null ? String(inp.tenantId) : c.tenantId,
      restaurantId: inp.restaurantId != null ? String(inp.restaurantId) : c.restaurantId,
      type,
      displayName,
      contacts: _crmContacts(inp.contacts),
      notes: inp.notes != null ? String(inp.notes) : null,
      // PII opzionale per auguri (marketing → richiede consenso, store separato).
      // Rientra nel perimetro oblio: anonymizeCustomer lo azzera. Default null =
      // retro-compatibile con i clienti esistenti (che restano senza campo).
      birthdate: (inp.birthdate != null && String(inp.birthdate).trim() !== "") ? String(inp.birthdate).trim() : null,
      // P3 — campi multi-device riservati (default single-writer)
      version: 1,
      updatedAt: c.at,
      deviceId: c.deviceId,
      tombstone: false,
      // I5/P4 — stato oblio
      anonymized: false,
      anonymizedAt: null,
      // I4 — audit (non dipende da PII)
      createdBy: c.by,
      createdAt: c.at,
    };
    return { customers: [...base, customer], customer, error: null };
  },

  // Aggiorna i campi anagrafici consentiti. Bump version (monotona per device) + updatedAt.
  // Rifiuta su record inesistente / tombstoned / anonimizzato (immutabile dopo l'oblio).
  updateCustomer(customers, id, patch, ctx) {
    const base = Array.isArray(customers) ? customers : [];
    const idx = base.findIndex(x => x && x.id === id);
    if (idx < 0) return { customers: base, customer: null, error: "customer non trovato" };
    const prev = base[idx];
    if (prev.tombstone) return { customers: base, customer: null, error: "customer cancellato (tombstone)" };
    if (prev.anonymized) return { customers: base, customer: null, error: "customer anonimizzato: immutabile" };
    const c = _crmCtx(ctx);
    const p = patch || {};
    const next = { ...prev };
    if (p.type !== undefined) {
      const t = String(p.type);
      if (!CRM_CUSTOMER_TYPES.includes(t)) return { customers: base, customer: null, error: `type non valido: ${t}` };
      next.type = t;
    }
    if (p.displayName !== undefined) {
      const dn = String(p.displayName).trim();
      if (!dn) return { customers: base, customer: null, error: "displayName non può essere vuoto" };
      next.displayName = dn;
    }
    if (p.contacts !== undefined) next.contacts = _crmContacts(p.contacts);
    if (p.notes !== undefined) next.notes = p.notes != null ? String(p.notes) : null;
    if (p.birthdate !== undefined) next.birthdate = (p.birthdate != null && String(p.birthdate).trim() !== "") ? String(p.birthdate).trim() : null;
    next.version = (Number(prev.version) || 1) + 1;
    next.updatedAt = c.at;
    next.deviceId = c.deviceId;
    const copy = base.slice();
    copy[idx] = next;
    return { customers: copy, customer: next, error: null };
  },

  // Cancellazione LOGICA (P3): mai delete fisico. Imposta tombstone=true, bump version.
  // Idempotente: ri-cancellare un già-tombstoned non è errore.
  softDeleteCustomer(customers, id, ctx) {
    const base = Array.isArray(customers) ? customers : [];
    const idx = base.findIndex(x => x && x.id === id);
    if (idx < 0) return { customers: base, customer: null, error: "customer non trovato" };
    const prev = base[idx];
    const c = _crmCtx(ctx);
    const next = {
      ...prev,
      tombstone: true,
      version: (Number(prev.version) || 1) + 1,
      updatedAt: c.at,
      deviceId: c.deviceId,
    };
    const copy = base.slice();
    copy[idx] = next;
    return { customers: copy, customer: next, error: null };
  },

  // ── Consenso — APPEND-ONLY (I5) + risoluzione deterministica (P14/I15) ─────

  // Prossimo seq per un device: monotono PER device (I15), parte da 1. Mai globale.
  nextSeqForDevice(customerConsents, deviceId) {
    const dev = deviceId != null ? String(deviceId) : CRM_DEFAULT_DEVICE;
    const arr = Array.isArray(customerConsents) ? customerConsents : [];
    let max = 0;
    for (const e of arr) {
      if (e && String(e.deviceId) === dev) {
        const s = Number(e.seq) || 0;
        if (s > max) max = s;
      }
    }
    return max + 1;
  },

  // Appende un evento di consenso. NON muta/elimina mai eventi esistenti (I5).
  // kind ∈ {marketing,profiling,allergies}, action ∈ {grant,revoke}, channel libero
  // (granularità stato per coppia kind+channel, P4). seq assegnato monotono per device.
  appendConsent(customerConsents, input, ctx) {
    const base = Array.isArray(customerConsents) ? customerConsents : [];
    const inp = input || {};
    const c = _crmCtx(ctx);
    const kind = String(inp.kind || "");
    if (!CRM_CONSENT_KINDS.includes(kind)) return { customerConsents: base, consent: null, error: `kind non valido: ${kind}` };
    const action = String(inp.action || "");
    if (!CRM_CONSENT_ACTIONS.includes(action)) return { customerConsents: base, consent: null, error: `action non valida: ${action}` };
    if (inp.customerId == null) return { customerConsents: base, consent: null, error: "customerId obbligatorio" };
    const channel = inp.channel != null ? String(inp.channel) : "any";
    const consent = {
      id: uid("cons_"),
      customerId: String(inp.customerId),
      tenantId: inp.tenantId != null ? String(inp.tenantId) : c.tenantId,
      restaurantId: inp.restaurantId != null ? String(inp.restaurantId) : c.restaurantId,
      kind,
      channel,
      action,
      seq: crmCore.nextSeqForDevice(base, c.deviceId),
      at: c.at,
      deviceId: c.deviceId,
      // P4 — anchorDate: data legale dell'evento (sopravvive all'oblio, senza PII).
      anchorDate: inp.anchorDate != null ? String(inp.anchorDate) : c.at,
      legalHold: !!inp.legalHold,
      createdBy: c.by,
      subjectAnonymized: false,
    };
    return { customerConsents: [...base, consent], consent, error: null };
  },

  // Tutti gli eventi (non anonimizzabili a parte) di un customer per una coppia kind+channel.
  consentEventsFor(customerConsents, customerId, kind, channel) {
    const arr = Array.isArray(customerConsents) ? customerConsents : [];
    return arr.filter(e =>
      e && String(e.customerId) === String(customerId) &&
      e.kind === kind && e.channel === channel
    );
  },

  // Risolve lo stato del consenso per (customerId, kind, channel) (P14/I15):
  //   ordina per tupla (deviceId, seq, at); la decisione vincente è il MASSIMO.
  //   In AMBIGUITÀ (più eventi con tupla identica al massimo) → REVOCA dominante.
  //   Nessun evento → revoke (deny di default, fail-safe I7: nessun marketing senza consenso).
  // Ritorna { action, consented, ambiguous, eventsConsidered, winner }.
  resolveConsent(customerConsents, customerId, kind, channel) {
    const events = crmCore.consentEventsFor(customerConsents, customerId, kind, channel);
    if (events.length === 0) {
      return { action: "revoke", consented: false, ambiguous: false, eventsConsidered: 0, winner: null };
    }
    const sorted = events.slice().sort(_crmConsentCmp);
    const top = sorted[sorted.length - 1];
    // Insieme degli eventi indistinguibili dal massimo (tupla identica).
    const tied = sorted.filter(e => _crmConsentCmp(e, top) === 0);
    const anyRevoke = tied.some(e => e.action === "revoke");
    const ambiguous = tied.length > 1 && tied.some(e => e.action !== tied[0].action);
    // Revoca dominante: se nel top-set c'è anche solo una revoke, vince revoke.
    const action = anyRevoke ? "revoke" : top.action;
    return {
      action,
      consented: action === "grant",
      ambiguous,
      eventsConsidered: events.length,
      winner: top,
    };
  },

  // Risolve TUTTE le coppie (kind, channel) presenti per un customer.
  // Ritorna mappa "kind|channel" → risultato di resolveConsent.
  resolveAllConsents(customerConsents, customerId) {
    const arr = Array.isArray(customerConsents) ? customerConsents : [];
    const pairs = new Map();
    for (const e of arr) {
      if (e && String(e.customerId) === String(customerId)) {
        pairs.set(`${e.kind}|${e.channel}`, { kind: e.kind, channel: e.channel });
      }
    }
    const out = {};
    for (const { kind, channel } of pairs.values()) {
      out[`${kind}|${channel}`] = crmCore.resolveConsent(customerConsents, customerId, kind, channel);
    }
    return out;
  },

  // ── Oblio / anonimizzazione (GDPR, P4/I5) ─────────────────────────────────
  // Anonimizza il SOGGETTO: rimuove la PII dal Customer (displayName/contatti/note)
  // e marca gli eventi consenso come subjectAnonymized, MINIMIZZANDOLI (PII rimossa,
  // restano kind/channel/action/seq/anchorDate come prova legale). Gli eventi con
  // legalHold restano INTEGRI (eccezione P4). Non elimina nulla fisicamente.
  anonymizeCustomer(customers, customerConsents, id, ctx) {
    const baseC = Array.isArray(customers) ? customers : [];
    const baseK = Array.isArray(customerConsents) ? customerConsents : [];
    const idx = baseC.findIndex(x => x && x.id === id);
    if (idx < 0) return { customers: baseC, customerConsents: baseK, customer: null, error: "customer non trovato" };
    const c = _crmCtx(ctx);
    const prev = baseC[idx];
    const anonCustomer = {
      ...prev,
      displayName: "(anonimizzato)",
      contacts: [],
      notes: null,
      birthdate: null,
      anonymized: true,
      anonymizedAt: c.at,
      version: (Number(prev.version) || 1) + 1,
      updatedAt: c.at,
      deviceId: c.deviceId,
    };
    const copyC = baseC.slice();
    copyC[idx] = anonCustomer;
    // Minimizzazione eventi consenso del soggetto (eccezione legalHold).
    const copyK = baseK.map(e => {
      if (!e || String(e.customerId) !== String(id)) return e;
      if (e.legalHold) return e; // eccezione: conservazione legale integrale
      return {
        id: e.id,
        customerId: e.customerId,
        tenantId: e.tenantId,
        restaurantId: e.restaurantId,
        kind: e.kind,
        channel: e.channel,
        action: e.action,
        seq: e.seq,
        at: e.at,
        deviceId: e.deviceId,
        anchorDate: e.anchorDate,
        legalHold: false,
        subjectAnonymized: true,
      };
    });
    return { customers: copyC, customerConsents: copyK, customer: anonCustomer, error: null };
  },

  // C4.1b — FUSIONE schede cliente: unisce l'assorbito (sourceId) nel principale
  // (targetId). Unione contatti (dedup kind+value), note concatenate, birthdate del
  // principale o, se manca, dell'assorbito. L'assorbito viene TOMBSTONATO con nota di
  // fusione (mai delete fisico). NON tocca link/consensi/prenotazioni: quelli li
  // ri-punta il chiamante (crmLinkCore.reassignCustomer + map su consensi/reservations).
  // Rifiuta se uno dei due è anonimizzato/tombstonato. Ritorna { customers, target, error }.
  mergeCustomers(customers, sourceId, targetId, ctx) {
    const base = Array.isArray(customers) ? customers : [];
    if (sourceId == null || targetId == null || String(sourceId) === String(targetId)) {
      return { customers: base, target: null, error: "sorgente e destinazione non valide" };
    }
    const si = base.findIndex(x => x && x.id === sourceId);
    const ti = base.findIndex(x => x && x.id === targetId);
    if (si < 0 || ti < 0) return { customers: base, target: null, error: "cliente non trovato" };
    const src = base[si], tgt = base[ti];
    if (src.anonymized || tgt.anonymized) return { customers: base, target: null, error: "cliente anonimizzato: fusione non consentita" };
    if (src.tombstone || tgt.tombstone) return { customers: base, target: null, error: "cliente già cancellato: fusione non consentita" };
    const c = _crmCtx(ctx);
    const seen = new Set();
    const mergedContacts = [];
    for (const ct of [...(Array.isArray(tgt.contacts) ? tgt.contacts : []), ...(Array.isArray(src.contacts) ? src.contacts : [])]) {
      if (!ct) continue;
      const k = `${ct.kind}|${String(ct.value || "").trim().toLowerCase()}`;
      if (seen.has(k)) continue;
      seen.add(k); mergedContacts.push(ct);
    }
    const mergedNotes = [tgt.notes, src.notes].filter(n => n && String(n).trim() !== "").join(" · ") || null;
    const newTarget = { ...tgt, contacts: _crmContacts(mergedContacts), notes: mergedNotes, birthdate: tgt.birthdate || src.birthdate || null, version: (Number(tgt.version) || 1) + 1, updatedAt: c.at, deviceId: c.deviceId };
    const newSource = { ...src, tombstone: true, notes: `Fuso in ${tgt.displayName} (${targetId})`, version: (Number(src.version) || 1) + 1, updatedAt: c.at, deviceId: c.deviceId };
    const copy = base.slice();
    copy[ti] = newTarget; copy[si] = newSource;
    return { customers: copy, target: newTarget, error: null };
  },
};

// Handle diagnostico di sola lettura (console: `window.__crmCore.resolveConsent(...)`).
// Non altera alcun comportamento; è solo un riferimento all'oggetto puro qui sopra.
if (typeof window !== "undefined") { try { window.__crmCore = crmCore; } catch { /* no-op */ } }
// ⟦CRM-CORE:END⟧

// ⟦CRM-LINK:START⟧ — Fase 3 ADR CRM Link-Core v1.0.2 (§A.4/§A.5/§A.8, P11, I1/I3/I8/I12)
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║         CRM LINK CORE — CustomerReceiptLink + orphan-repair (Fase 3)       ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// Layer di dominio PURO per la TERZA entità: `customerReceiptLinks`. Il
// CustomerReceiptLink è l'UNICO fatto atomico persistito del modello (ADR I1):
// l'asserzione "questo cliente è in relazione con questo receipt, in questo ruolo".
// Tutte le funzioni sono reducer immutabili / funzioni pure: nessun side effect,
// nessuna mutazione, nessun trigger automatico (l'innesco al re-import è UI/runtime,
// fuori da questa fase). Il wiring UI è Fase ≥4.
//
// Confini di Fase 3 (ADR + mandato):
//   • SOLO l'entità Link: NIENTE Visit/VisitMeta, Reservation reale, Marketing,
//     cache derivata persistita, governance-queue persistita (out of scope);
//   • storage additivo (SEED + array guarantee) → retrocompat, storage key invariata;
//   • Receipt ESTERNO e intoccato: si legge SOLO via receiptFacade (Fase 1, congelata);
//   • identità del link = chiavi stabili del receipt (P11): al confirm si PINNANO
//     `primaryStableReceiptKey` (tier più forte) + `candidateKeySnapshot` (set di allora,
//     IMMUTABILE, versionato keyVersion). Gli id receipt volatili non sono mai persistiti;
//   • payer unico per receipt (I3): un secondo payer attivo NON viene mai fuso in silenzio
//     → esito `payerConflict` esplicito restituito al chiamante (coda = governance, Fase ≥4);
//   • orphan-repair (P11/I8): si riaggancia per INTERSEZIONE tra il candidateKeySnapshot del
//     link e il candidateKeySet RICALCOLATO sul receipt re-importato; cross-tier ammesso solo
//     se NON ambiguo (un solo receipt candidato), altrimenti conflict; i tier `degraded` (T4)
//     da soli NON fondano un riaggancio automatico;
//   • mai delete fisico → solo tombstone (P3); campi multi-device riservati con default;
//   • NON modifica il blocco CRM-CORE Fase 2: ne riusa solo gli helper esterni
//     (`_crmCtx`, `CRM_DEFAULT_*`) e la facade Fase 1.

const CRM_LINK_ROLES = ["payer", "attendee", "reservation_holder"];

// Set delle sole `key` da un candidateKeySnapshot/candidateKeySet (difensivo).
function _crmKeySet(arr) {
  return new Set((Array.isArray(arr) ? arr : []).map(k => k && k.key).filter(Boolean));
}
// Come sopra, ma solo i tier NON degradati (T4 escluso): base ammessa per il
// riaggancio AUTOMATICO (ADR I8 — i degraded da soli non fondano un auto-repair).
function _crmNonDegradedKeySet(arr) {
  return new Set((Array.isArray(arr) ? arr : []).filter(k => k && !k.degraded && k.key).map(k => k.key));
}
// Calcola il fingerprint chiavi di un receipt al momento del confirm (P11).
// `candidateKeySet` ordinato forte→debole dalla facade Fase 1; primary = il più forte.
function _crmLinkKeysFromReceipt(receipt) {
  const set = receiptFacade.computeReceiptCandidateKeys(receipt);
  const primary = set.length ? set[0] : null;
  return {
    primaryStableReceiptKey: primary ? primary.key : null,
    keyTier: primary ? primary.tier : null,
    candidateKeySnapshot: set,
    keyVersion: RECEIPT_KEY_VERSION,
  };
}

const crmLinkCore = {
  // ── Letture derivate ──────────────────────────────────────────────────────

  // Lookup per id. Default ESCLUDE i tombstoned.
  getLink(links, id, opts) {
    if (!Array.isArray(links) || id == null) return null;
    const includeTombstoned = !!(opts && opts.includeTombstoned);
    const l = links.find(x => x && x.id === id) || null;
    if (!l) return null;
    if (l.tombstone && !includeTombstoned) return null;
    return l;
  },

  // Tutti i link attivi di un cliente.
  listLinksForCustomer(links, customerId, opts) {
    if (!Array.isArray(links) || customerId == null) return [];
    const includeTombstoned = !!(opts && opts.includeTombstoned);
    return links.filter(l =>
      l && String(l.customerId) === String(customerId) &&
      (includeTombstoned || !l.tombstone)
    );
  },

  // Tutti i link attivi che afferiscono a un dato receipt. Un link afferisce al
  // receipt se il suo candidateKeySnapshot INTERSECA il candidateKeySet corrente del
  // receipt (matching su chiavi stabili, mai su id volatili — ADR I6). Receipt esterno,
  // letto solo per ricalcolare le chiavi via facade Fase 1.
  listLinksForReceipt(links, receipt, opts) {
    if (!Array.isArray(links) || !receipt) return [];
    const includeTombstoned = !!(opts && opts.includeTombstoned);
    const curKeys = _crmKeySet(receiptFacade.computeReceiptCandidateKeys(receipt));
    return links.filter(l => {
      if (!l) return false;
      if (l.tombstone && !includeTombstoned) return false;
      const snap = _crmKeySet(l.candidateKeySnapshot);
      for (const k of snap) if (curKeys.has(k)) return true;
      return false;
    });
  },

  // ── CRUD minimo (reducer puri, immutabili) ───────────────────────────────

  // Crea un link cliente↔receipt PINNANDO le chiavi stabili (P11). Valida role/customerId
  // e richiede il receipt (per calcolarne le chiavi). I3 — payer unico per receipt: se
  // esiste già un payer ATTIVO afferente allo stesso receipt, il secondo payer NON viene
  // fuso → ritorna { error:"payerConflict", conflict:{...} } (mai merge silenzioso).
  // Ritorna { customerReceiptLinks, link, error, conflict }.
  createLink(links, receipt, input, ctx) {
    const base = Array.isArray(links) ? links : [];
    const inp = input || {};
    const c = _crmCtx(ctx);
    const role = String(inp.role || "");
    if (!CRM_LINK_ROLES.includes(role)) {
      return { customerReceiptLinks: base, link: null, error: `role non valido: ${role}`, conflict: null };
    }
    if (inp.customerId == null) {
      return { customerReceiptLinks: base, link: null, error: "customerId obbligatorio", conflict: null };
    }
    if (!receipt) {
      return { customerReceiptLinks: base, link: null, error: "receipt obbligatorio per pinnare le chiavi (P11)", conflict: null };
    }
    const keys = _crmLinkKeysFromReceipt(receipt);
    if (!keys.primaryStableReceiptKey) {
      return { customerReceiptLinks: base, link: null, error: "impossibile derivare una stableReceiptKey dal receipt", conflict: null };
    }
    // I3 — payer unico per receipt (intersezione su chiavi del receipt corrente).
    if (role === "payer") {
      const curKeys = _crmKeySet(keys.candidateKeySnapshot);
      const existingPayers = base.filter(l =>
        l && !l.tombstone && l.role === "payer" &&
        [...(_crmKeySet(l.candidateKeySnapshot))].some(k => curKeys.has(k))
      );
      if (existingPayers.length > 0) {
        return {
          customerReceiptLinks: base,
          link: null,
          error: "payerConflict",
          conflict: {
            type: "payerConflict",
            receiptPrimaryKey: keys.primaryStableReceiptKey,
            existingLinkIds: existingPayers.map(l => l.id),
            existingCustomerIds: existingPayers.map(l => l.customerId),
            attemptedCustomerId: String(inp.customerId),
          },
        };
      }
    }
    const link = {
      id: uid("lnk_"),
      customerId: String(inp.customerId),
      role,
      primaryStableReceiptKey: keys.primaryStableReceiptKey,
      keyTier: keys.keyTier,
      candidateKeySnapshot: keys.candidateKeySnapshot, // immutabile (P11)
      keyVersion: keys.keyVersion,
      // reservation_holder ammesso come ruolo, ma l'entità Reservation è fuori Fase 3:
      // reservationId resta opzionale/null (nessuno store prenotazioni ancora).
      reservationId: inp.reservationId != null ? String(inp.reservationId) : null,
      tenantId: inp.tenantId != null ? String(inp.tenantId) : (receipt.tenantId != null ? String(receipt.tenantId) : c.tenantId),
      restaurantId: inp.restaurantId != null ? String(inp.restaurantId) : (receipt.restaurantId != null ? String(receipt.restaurantId) : c.restaurantId),
      // P3 — campi multi-device riservati (default single-writer)
      version: 1,
      updatedAt: c.at,
      deviceId: c.deviceId,
      tombstone: false,
      // I4 — audit
      createdBy: c.by,
      createdAt: c.at,
    };
    return { customerReceiptLinks: [...base, link], link, error: null, conflict: null };
  },

  // Cancellazione LOGICA (P3): mai delete fisico. tombstone=true, bump version.
  // Idempotente. Il candidateKeySnapshot NON viene toccato (resta prova storica).
  tombstoneLink(links, id, ctx) {
    const base = Array.isArray(links) ? links : [];
    const idx = base.findIndex(x => x && x.id === id);
    if (idx < 0) return { customerReceiptLinks: base, link: null, error: "link non trovato" };
    const prev = base[idx];
    const c = _crmCtx(ctx);
    const next = {
      ...prev,
      tombstone: true,
      version: (Number(prev.version) || 1) + 1,
      updatedAt: c.at,
      deviceId: c.deviceId,
    };
    const copy = base.slice();
    copy[idx] = next;
    return { customerReceiptLinks: copy, link: next, error: null };
  },

  // C4.1b — FUSIONE: sposta i link ATTIVI dall'assorbito (fromId) al principale
  // (toId). Il candidateKeySnapshot (chiavi pinnate, P11) NON cambia — cambia SOLO
  // customerId, con bump version (customerId non fa parte delle chiavi immutabili).
  // Dedup: se il principale ha GIÀ un link attivo per lo stesso (primaryStableReceiptKey,
  // role), il link dell'assorbito viene tombstonato invece che spostato — evita doppioni
  // e preserva I3 (un solo payer per receipt: il principale non poteva già esserlo per lo
  // stesso receipt, quindi nessun conflitto nuovo). Puro. Ritorna { customerReceiptLinks, moved, tombstoned }.
  reassignCustomer(links, fromId, toId, ctx) {
    const base = Array.isArray(links) ? links : [];
    if (fromId == null || toId == null || String(fromId) === String(toId)) {
      return { customerReceiptLinks: base, moved: 0, tombstoned: 0 };
    }
    const c = _crmCtx(ctx);
    const from = String(fromId), to = String(toId);
    const targetSig = new Set(base
      .filter(l => l && !l.tombstone && String(l.customerId) === to)
      .map(l => `${l.primaryStableReceiptKey}|${l.role}`));
    let moved = 0, tombstoned = 0;
    const copy = base.map(l => {
      if (!l || l.tombstone || String(l.customerId) !== from) return l;
      const sig = `${l.primaryStableReceiptKey}|${l.role}`;
      const bump = { version: (Number(l.version) || 1) + 1, updatedAt: c.at, deviceId: c.deviceId };
      if (targetSig.has(sig)) { tombstoned++; return { ...l, ...bump, tombstone: true }; }
      targetSig.add(sig); moved++;
      return { ...l, ...bump, customerId: to };
    });
    return { customerReceiptLinks: copy, moved, tombstoned };
  },

  // ── Orphan-repair (P11/I8) — PURO, nessuna mutazione, nessun trigger ───────

  // Calcola un PIANO di riaggancio per i link attivi i cui receipt sono stati
  // re-importati (id nuovi). Per ogni link attivo:
  //   • se la primaryStableReceiptKey risolve ancora a un receipt univoco → healthy;
  //   • altrimenti (orfano) si cercano i receipt il cui candidateKeySet RICALCOLATO
  //     interseca lo snapshot del link su un tier NON degradato:
  //        – esattamente 1 receipt → repaired (nuovo primary = chiave più forte attuale);
  //        – più di 1            → conflict { reason:"ambiguous" }  (mai auto-risolto, I12);
  //        – 0 non-degradati ma ≥1 solo-degradato → conflict { reason:"degraded_only" } (I8);
  //        – 0 in assoluto       → orphan { reason:"no_match" }.
  // Funzione PURA: ritorna solo il report, non modifica nulla.
  repairOrphanLinks(links, state) {
    const arr = Array.isArray(links) ? links : [];
    const receipts = state && Array.isArray(state.tableReceipts) ? state.tableReceipts : [];
    const report = { healthy: [], repaired: [], conflicts: [], orphans: [] };
    // Pre-calcolo i candidateKeySet correnti una sola volta per receipt.
    const recKeys = receipts.map(r => ({
      receipt: r,
      set: receiptFacade.computeReceiptCandidateKeys(r),
    }));
    for (const l of arr) {
      if (!l || l.tombstone) continue;
      // Ancora sano? primary risolve a un receipt univoco (facade Fase 1).
      const resolved = receiptFacade.resolveReceiptByStableKey(state, l.primaryStableReceiptKey);
      if (resolved) { report.healthy.push(l.id); continue; }
      // Orfano → tento il riaggancio per intersezione.
      const nonDeg = _crmNonDegradedKeySet(l.candidateKeySnapshot);
      const allSnap = _crmKeySet(l.candidateKeySnapshot);
      const strongMatches = [];
      const degradedOnlyMatches = [];
      for (const rk of recKeys) {
        const hasNonDeg = rk.set.some(k => !k.degraded && nonDeg.has(k.key));
        if (hasNonDeg) { strongMatches.push(rk); continue; }
        const hasAny = rk.set.some(k => allSnap.has(k.key));
        if (hasAny) degradedOnlyMatches.push(rk);
      }
      if (strongMatches.length === 1) {
        const rk = strongMatches[0];
        const newPrimary = rk.set.length ? rk.set[0] : null;
        report.repaired.push({
          linkId: l.id,
          fromKey: l.primaryStableReceiptKey,
          toKey: newPrimary ? newPrimary.key : null,
          toTier: newPrimary ? newPrimary.tier : null,
          receiptId: rk.receipt ? rk.receipt.id : null,
        });
      } else if (strongMatches.length > 1) {
        report.conflicts.push({
          linkId: l.id,
          reason: "ambiguous",
          candidateReceiptIds: strongMatches.map(rk => rk.receipt && rk.receipt.id).filter(Boolean),
        });
      } else if (degradedOnlyMatches.length > 0) {
        report.conflicts.push({
          linkId: l.id,
          reason: "degraded_only",
          candidateReceiptIds: degradedOnlyMatches.map(rk => rk.receipt && rk.receipt.id).filter(Boolean),
        });
      } else {
        report.orphans.push({ linkId: l.id, reason: "no_match" });
      }
    }
    return report;
  },

  // Applica un piano di repair (thin, PURO): aggiorna SOLO le voci `repaired`
  // (nuovo primaryStableReceiptKey/keyTier, bump version). NON tocca conflicts/orphans
  // (governance manuale, Fase ≥4) e NON muta il candidateKeySnapshot (immutabile, P11).
  // Ritorna { customerReceiptLinks, applied }.
  applyRepairPlan(links, plan, ctx) {
    const base = Array.isArray(links) ? links : [];
    const repaired = plan && Array.isArray(plan.repaired) ? plan.repaired : [];
    if (repaired.length === 0) return { customerReceiptLinks: base, applied: 0 };
    const c = _crmCtx(ctx);
    const byId = new Map();
    for (const r of repaired) if (r && r.linkId && r.toKey) byId.set(r.linkId, r);
    let applied = 0;
    const next = base.map(l => {
      if (!l || !byId.has(l.id)) return l;
      const r = byId.get(l.id);
      applied++;
      return {
        ...l,
        primaryStableReceiptKey: r.toKey,
        keyTier: r.toTier != null ? r.toTier : l.keyTier,
        version: (Number(l.version) || 1) + 1,
        updatedAt: c.at,
        deviceId: c.deviceId,
      };
    });
    return { customerReceiptLinks: applied > 0 ? next : base, applied };
  },
};

// Handle diagnostico di sola lettura (console: `window.__crmLinkCore.repairOrphanLinks(...)`).
// Non altera alcun comportamento; è solo un riferimento all'oggetto puro qui sopra.
if (typeof window !== "undefined") { try { window.__crmLinkCore = crmLinkCore; } catch { /* no-op */ } }
// ⟦CRM-LINK:END⟧

// ⟦CRM-VISIT:START⟧ — Fase 4 ADR CRM Link-Core v1.0.2 (§A.6, P13, I2/I6/I13) + ADR CRM Foundation
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║              CRM VISIT PROJECTION — proiezione derivata (Fase 4)           ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// Strato di proiezione PURO e RUNTIME-ONLY: la Visit è SEMPRE derivata, MAI
// persistita come fatto (ADR I2). Non esiste alcuno store, alcun SEED, alcuna
// migration, alcuna cache per la Visit. Tutto si ricomputa dai fatti esistenti.
//
// Confini di Fase 4 (mandato + ADR):
//   • Receipt: letti SOLO via receiptFacade (Fase 1, congelata) — mai accesso grezzo (I10);
//   • Link: letti SOLO via crmLinkCore (Fase 3, congelata) — nessuna mutazione;
//   • niente VisitMeta, niente persistenza, niente UI, niente Reservation/Marketing;
//   • raggruppamento su forme canoniche stabili (canonicalTable, rawTimeAnchor) — mai
//     stringhe grezze o id volatili (I6); nessuna dipendenza da SHIFT_CONFIG/serviceDate (I13);
//   • la soglia di occupazione è versionata come PROJECTION (experienceProjectionVersion),
//     non come identità: un cambio futuro è v2 che RICOMPUTA, mai riscrive (P13).
//
// NB covers: il totale coperti NON è esposto dalla read-facade congelata, quindi NON
// entra nei totali della Visit (servirebbe un'estensione additiva della facade, futura).
// I totali Visit riportano solo ciò che la facade espone (denaro).

const SEATING_GAP_MINUTES_V1 = 90;
const EXPERIENCE_PROJECTION_VERSION = "v1"; // = seatingGapMinutesV1:90
const _VISIT_SEATING_GAP_MS = SEATING_GAP_MINUTES_V1 * 60 * 1000;

// Namespace di proiezione: solo tenant/restaurant (default t0/r0), coerente con P12.
function _visitNamespace(opts) {
  const t = (opts && opts.tenantId) || "t0";
  const r = (opts && opts.restaurantId) || "r0";
  return `${t}/${r}`;
}
// Bucket temporale grezzo: giorno-calendario UTC, funzione PURA del timestamp grezzo.
// NON usa SHIFT_CONFIG né serviceDate (I13). Partizione grossolana; il seatingOrdinal
// (regola 90 min) è la separazione autoritativa entro il bucket.
function _visitDayBucket(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}
// Chiave stabile primaria del receipt (tier più forte), via facade Fase 1.
function _visitPrimaryKey(receipt) {
  const s = receiptFacade.computeReceiptCandidateKeys(receipt);
  return s.length ? s[0].key : null;
}

// Costruisce un singolo oggetto Visit derivato. `healthySet` = id dei link che risolvono
// in modo pulito (da crmLinkCore.repairOrphanLinks): solo questi attribuiscono partecipanti
// (i tombstoned sono già esclusi da listLinksForReceipt; gli orfani/ambigui sono esclusi qui).
function _visitBuild(kind, visitKey, canonicalTable, seatingOrdinal, memberEntries, links, healthySet, opts) {
  const members = [];
  const participantsMap = new Map(); // customerId -> { roles:Set, receiptStableKeys:Set }
  const payerByReceiptStableKey = {};
  let grossCents = 0;
  // AUDIT-FIX I1: netFiscalCents = grossCents MENO gli EC riconciliati (già nel
  // SF). È il valore giusto per la SPESA del cliente; grossCents resta invariato
  // (lordo di tutti i member) per non cambiare la semantica di un campo esistente.
  let netFiscalCents = 0;
  let hasNonFiscal = false;
  let hasOrphanReceipts = false;
  let startMs = null, endMs = null;

  for (const m of memberEntries) {
    const r = m.receipt;
    members.push({ receiptStableKey: m.stableKey, receiptId: r.id });
    grossCents += receiptFacade.getReceiptMoney(r).totalCents;
    if (receiptFacade.getReceiptCountsForRevenue(r)) netFiscalCents += receiptFacade.getReceiptMoney(r).totalCents;
    if (receiptFacade.getReceiptSourceIdentity(r).docType === "ESTRATTO CONTO") hasNonFiscal = true;
    if (m.ms != null) {
      if (startMs === null || m.ms < startMs) startMs = m.ms;
      if (endMs === null || m.ms > endMs) endMs = m.ms;
    }
    // Attribuzione SOLO via core (listLinksForReceipt esclude i tombstoned); poi si tengono
    // solo i link "healthy" → orfani/non-risolti/ambigui esclusi dalla Visit (e segnalati a
    // parte da listUnresolvedLinks). I3 resta per-receipt: nessun payer unico per Visit.
    const attributed = crmLinkCore.listLinksForReceipt(links, r, opts).filter(l => healthySet.has(l.id));
    if (attributed.length === 0) hasOrphanReceipts = true;
    let payerForReceipt = null;
    for (const l of attributed) {
      let p = participantsMap.get(l.customerId);
      if (!p) { p = { roles: new Set(), receiptStableKeys: new Set() }; participantsMap.set(l.customerId, p); }
      p.roles.add(l.role);
      p.receiptStableKeys.add(m.stableKey);
      if (l.role === "payer" && payerForReceipt == null) payerForReceipt = l.customerId;
    }
    payerByReceiptStableKey[m.stableKey] = payerForReceipt;
  }

  const participants = [...participantsMap.entries()].map(([customerId, p]) => ({
    customerId,
    roles: [...p.roles],
    receiptStableKeys: [...p.receiptStableKeys],
  }));

  return {
    visitKey,
    kind, // "table" | "banco"
    experienceProjectionVersion: EXPERIENCE_PROJECTION_VERSION,
    canonicalTable,
    seatingOrdinal,
    rawTimeStartMs: startMs,
    rawTimeEndMs: endMs,
    members,                 // { receiptStableKey, receiptId } — receiptId volatile, runtime-only
    participants,            // [{ customerId, roles[], receiptStableKeys[] }]
    payerByReceiptStableKey, // I3 per-receipt
    totals: { grossCents, netFiscalCents },  // covers escluso (non esposto dalla facade congelata); netFiscalCents = spesa reale (EC riconciliati esclusi, AUDIT-FIX I1)
    flags: {
      hasNonFiscal,
      isAnonymous: participants.length === 0,
      hasOrphanReceipts,
    },
  };
}

const crmVisitProjection = {
  // — experienceBoundaryKey (P13): restaurant-ns | canonicalTable | dayBucket | seatingOrdinal,
  //   prefissata dalla projection version (è una projection, non un'identità di fatto).
  computeExperienceBoundaryKey(canonicalTable, dayBucket, seatingOrdinal, opts) {
    return `exp:${EXPERIENCE_PROJECTION_VERSION}:${_visitNamespace(opts)}|${canonicalTable}|${dayBucket}|${seatingOrdinal}`;
  },

  // — Proiezione di TUTTE le Visit derivate dallo stato corrente. PURA: non muta nulla.
  //   Banco / receipt senza tempo → Visit degenerata mono-receipt. Tavolo → raggruppamento
  //   per (canonicalTable, dayBucket) con seatingOrdinal dalla regola dei 90 minuti.
  projectVisits(state, opts) {
    const receipts = state && Array.isArray(state.tableReceipts) ? state.tableReceipts : [];
    const links = state && Array.isArray(state.customerReceiptLinks) ? state.customerReceiptLinks : [];
    // Un solo passaggio di risoluzione link via core: healthy = risolvono in modo pulito.
    const repair = crmLinkCore.repairOrphanLinks(links, state);
    const healthySet = new Set(repair.healthy);
    const visits = [];
    const tableRecs = [];

    for (const r of receipts) {
      if (!r) continue;
      const table = receiptFacade.getReceiptTable(r);            // banco → null (P10)
      const ms = receiptFacade.getReceiptTimeAnchors(r).rawClosedAtMs;
      const stableKey = _visitPrimaryKey(r);
      if (table === null || ms == null) {
        // Degenerata: banco (nessun tavolo, P13) o receipt privo di tempo collocabile.
        const kind = table === null ? "banco" : "table";
        const vk = `${table === null ? "banco" : "untimed"}:${_visitNamespace(opts)}|${stableKey || r.id}`;
        visits.push(_visitBuild(kind, vk, table, null, [{ receipt: r, stableKey, ms }], links, healthySet, opts));
      } else {
        tableRecs.push({ receipt: r, stableKey, ms, table, bucket: _visitDayBucket(ms) });
      }
    }

    // Raggruppa per (canonicalTable, dayBucket); entro il gruppo, separa le occupazioni
    // con la regola del gap: > 90 min → nuova occupazione (seatingOrdinal++).
    const groups = new Map();
    for (const x of tableRecs) {
      const gk = `${x.table} ${x.bucket}`;
      if (!groups.has(gk)) groups.set(gk, []);
      groups.get(gk).push(x);
    }
    for (const arr of groups.values()) {
      arr.sort((a, b) => a.ms - b.ms);
      let ordinal = 0, prevMs = null, current = [];
      const flush = (members, ord) => {
        const ebk = crmVisitProjection.computeExperienceBoundaryKey(members[0].table, members[0].bucket, ord, opts);
        visits.push(_visitBuild("table", ebk, members[0].table, ord, members, links, healthySet, opts));
      };
      for (const x of arr) {
        if (prevMs !== null && (x.ms - prevMs) > _VISIT_SEATING_GAP_MS) {
          flush(current, ordinal);
          ordinal++;
          current = [];
        }
        current.push(x);
        prevMs = x.ms;
      }
      if (current.length) flush(current, ordinal);
    }

    return visits;
  },

  // — Timeline di un cliente: le Visit in cui il cliente è partecipante. PURA.
  //   (Project-all-then-filter: accettabile in Fase 4; indici sono ottimizzazione futura.)
  projectVisitsForCustomer(state, customerId, opts) {
    const cid = String(customerId);
    return crmVisitProjection.projectVisits(state, opts)
      .filter(v => v.participants.some(p => String(p.customerId) === cid));
  },

  // — Link attivi NON collocabili in alcuna Visit (orfani/ambigui/non risolti): segnalati
  //   come flag, esclusi dalle Visit. Calcolati via core (repairOrphanLinks). PURA.
  listUnresolvedLinks(state, opts) {
    const links = state && Array.isArray(state.customerReceiptLinks) ? state.customerReceiptLinks : [];
    const repair = crmLinkCore.repairOrphanLinks(links, state);
    return [
      ...repair.repaired.map(r => r.linkId),
      ...repair.conflicts.map(c => c.linkId),
      ...repair.orphans.map(o => o.linkId),
    ];
  },
};

// Handle diagnostico di sola lettura (console: `window.__crmVisitProjection.projectVisits(...)`).
// Non altera alcun comportamento; è solo un riferimento all'oggetto puro qui sopra.
if (typeof window !== "undefined") { try { window.__crmVisitProjection = crmVisitProjection; } catch { /* no-op */ } }
// ⟦CRM-VISIT:END⟧

// ⟦RESV-CORE:START⟧ — Agenda V1 (ADR Reservation as Future Intent, PROPOSED)
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║          RESERVATION CORE — il fatto futuro dichiarato (Agenda V1)         ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// Layer di dominio PURO per l'entità `reservations`: reducer immutabili, nessun
// side effect, nessuna mutazione. La Reservation è un FATTO FUTURO persistito:
// non è Customer, non è Receipt, non è Visit (ADR §2-4). Confini duri:
//   • NESSUN Link nasce da qui: il Link pinna chiavi stabili dal receipt reale
//     (P11) — senza receipt non c'è nulla da pinnare (fulfilment → ADR-CRM-11);
//   • NESSUNA Visit nasce da qui: resta derivata dai soli scontrini (I2);
//   • NESSUNA lettura/derivazione dai receipt in questo blocco;
//   • `service` è DICHIARATO alla presa, mai derivato dall'ora — niente
//     dipendenza da SHIFT_CONFIG (stessa disciplina dell'invariante I13);
//   • `customerId` è SOLO un riferimento opzionale a un Customer esistente:
//     nessuna write su crmCore; `rawName` resta sempre lo snapshot d'agenda;
//   • `table` testo libero trimmed — canonicalizzazione rinviata al fulfilment
//     (dove esiste già lato receipt via facade);
//   • NIENTE stato `completed` in V1: semantica del fulfilment (ADR §5);
//   • mai delete fisico → solo tombstone (P3); riusa gli helper esterni
//     `_crmCtx`/`CRM_DEFAULT_*` come già fa la Fase 3.

const RESV_SERVICES = ["pranzo", "cena"];
const RESV_STATUSES = ["booked", "seated", "cancelled", "no_show"];
// R1 — provenienza operativa della prenotazione (ADR, Addendum R1). Additivo:
// assente → "phone" (il canale di gran lunga più comune); valore non valido →
// errore leggibile (stessa convenzione di service/status).
const RESV_SOURCES = ["phone", "web", "walkin", "other"];
const _resvIsServiceDate = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
// R3 — orario dichiarato "HH:MM" (Addendum R3): testo di orario di servizio,
// niente datetime/timezone; opzionale (null = "senza orario").
const _resvIsTime = (s) => typeof s === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(s);

const reservationCore = {
  // Crea una prenotazione. Stato iniziale SEMPRE "booked". Valida serviceDate
  // (YYYY-MM-DD), service (pranzo|cena), rawName non vuoto, covers intero ≥ 1.
  // Ritorna { reservations, reservation, error }; error=null = ok.
  createReservation(reservations, input, ctx) {
    const base = Array.isArray(reservations) ? reservations : [];
    const inp = input || {};
    const c = _crmCtx(ctx);
    const serviceDate = inp.serviceDate != null ? String(inp.serviceDate).trim() : "";
    if (!_resvIsServiceDate(serviceDate)) {
      return { reservations: base, reservation: null, error: "serviceDate obbligatoria (formato YYYY-MM-DD)" };
    }
    const service = String(inp.service || "");
    if (!RESV_SERVICES.includes(service)) {
      return { reservations: base, reservation: null, error: `service non valido: ${service}` };
    }
    const rawName = inp.rawName != null ? String(inp.rawName).trim() : "";
    if (!rawName) {
      return { reservations: base, reservation: null, error: "rawName obbligatorio" };
    }
    const covers = Number(inp.covers);
    if (!Number.isInteger(covers) || covers < 1) {
      return { reservations: base, reservation: null, error: "covers deve essere un intero ≥ 1" };
    }
    const table = inp.table != null && String(inp.table).trim() !== "" ? String(inp.table).trim() : null;
    const source = inp.source != null ? String(inp.source) : "phone";
    if (!RESV_SOURCES.includes(source)) {
      return { reservations: base, reservation: null, error: `source non valido: ${source}` };
    }
    const time = inp.time != null && String(inp.time).trim() !== "" ? String(inp.time).trim() : null;
    if (time !== null && !_resvIsTime(time)) {
      return { reservations: base, reservation: null, error: `time non valido: ${time} (formato HH:MM)` };
    }
    // C2.4 (sblocco autorizzato da Andi 2026-07-06) — phone: campo ADDITIVO e
    // opzionale (testo libero max 40, nessun formato imposto: internazionali,
    // spazi, interni). I record pre-esistenti restano senza campo: ogni
    // consumer deve trattare undefined/null come "nessun telefono".
    const phone = inp.phone != null && String(inp.phone).trim() !== "" ? String(inp.phone).trim().slice(0, 40) : null;
    const reservation = {
      id: uid("resv_"),
      tenantId: inp.tenantId != null ? String(inp.tenantId) : c.tenantId,
      restaurantId: inp.restaurantId != null ? String(inp.restaurantId) : c.restaurantId,
      serviceDate,
      service,
      time,
      rawName,
      covers,
      table,
      phone,
      notes: inp.notes != null ? String(inp.notes) : null,
      customerId: inp.customerId != null ? String(inp.customerId) : null,
      source,
      status: "booked",
      // P3 — campi multi-device riservati (default single-writer)
      version: 1,
      updatedAt: c.at,
      deviceId: c.deviceId,
      tombstone: false,
      // I4 — audit
      createdBy: c.by,
      createdAt: c.at,
    };
    return { reservations: [...base, reservation], reservation, error: null };
  },

  // Prenotazioni di un giorno di servizio. opts.service filtra pranzo|cena;
  // default esclude i tombstoned (includeTombstoned per includerli).
  listReservationsForDay(reservations, serviceDate, opts) {
    if (!Array.isArray(reservations)) return [];
    const day = serviceDate != null ? String(serviceDate).trim() : "";
    if (!day) return [];
    const o = opts || {};
    return reservations.filter(r => {
      if (!r) return false;
      if (r.serviceDate !== day) return false;
      if (r.tombstone && !o.includeTombstoned) return false;
      if (o.service != null && r.service !== String(o.service)) return false;
      return true;
    });
  },

  // Aggiorna SOLO i campi anagrafici della prenotazione (rawName/covers/table/
  // notes/customerId/source/time). Lo stato si cambia SOLO via changeReservationStatus.
  // Bump version + updatedAt/deviceId. Rifiuta su inesistente o tombstoned.
  updateReservation(reservations, id, patch, ctx) {
    const base = Array.isArray(reservations) ? reservations : [];
    const idx = base.findIndex(x => x && x.id === id);
    if (idx < 0) return { reservations: base, reservation: null, error: "prenotazione non trovata" };
    const prev = base[idx];
    if (prev.tombstone) return { reservations: base, reservation: null, error: "prenotazione cancellata (tombstone)" };
    const c = _crmCtx(ctx);
    const p = patch || {};
    const next = { ...prev };
    if (p.rawName !== undefined) {
      const n = String(p.rawName).trim();
      if (!n) return { reservations: base, reservation: null, error: "rawName non può essere vuoto" };
      next.rawName = n;
    }
    if (p.covers !== undefined) {
      const cov = Number(p.covers);
      if (!Number.isInteger(cov) || cov < 1) return { reservations: base, reservation: null, error: "covers deve essere un intero ≥ 1" };
      next.covers = cov;
    }
    if (p.table !== undefined) {
      next.table = p.table != null && String(p.table).trim() !== "" ? String(p.table).trim() : null;
    }
    if (p.notes !== undefined) next.notes = p.notes != null ? String(p.notes) : null;
    // C2.4 (sblocco 2026-07-06) — stesso contratto della create: additivo, opzionale.
    if (p.phone !== undefined) next.phone = p.phone != null && String(p.phone).trim() !== "" ? String(p.phone).trim().slice(0, 40) : null;
    if (p.customerId !== undefined) next.customerId = p.customerId != null ? String(p.customerId) : null;
    if (p.source !== undefined) {
      const src = String(p.source || "");
      if (!RESV_SOURCES.includes(src)) return { reservations: base, reservation: null, error: `source non valido: ${src}` };
      next.source = src;
    }
    if (p.time !== undefined) {
      const tm = p.time != null && String(p.time).trim() !== "" ? String(p.time).trim() : null;
      if (tm !== null && !_resvIsTime(tm)) return { reservations: base, reservation: null, error: `time non valido: ${tm} (formato HH:MM)` };
      next.time = tm;
    }
    next.version = (Number(prev.version) || 1) + 1;
    next.updatedAt = c.at;
    next.deviceId = c.deviceId;
    const copy = base.slice();
    copy[idx] = next;
    return { reservations: copy, reservation: next, error: null };
  },

  // Cambio di stato ESPLICITO (ADR §5): solo booked|seated|cancelled|no_show.
  // Ogni cambio è una chiamata diretta e audit-abile (version bump) — le
  // correzioni sono ammesse ma mai silenziose; stato identico → errore esplicito.
  changeReservationStatus(reservations, id, status, ctx) {
    const base = Array.isArray(reservations) ? reservations : [];
    const st = String(status || "");
    if (!RESV_STATUSES.includes(st)) {
      return { reservations: base, reservation: null, error: `status non valido: ${st}` };
    }
    const idx = base.findIndex(x => x && x.id === id);
    if (idx < 0) return { reservations: base, reservation: null, error: "prenotazione non trovata" };
    const prev = base[idx];
    if (prev.tombstone) return { reservations: base, reservation: null, error: "prenotazione cancellata (tombstone)" };
    if (prev.status === st) return { reservations: base, reservation: null, error: `stato già ${st}` };
    const c = _crmCtx(ctx);
    const next = {
      ...prev,
      status: st,
      version: (Number(prev.version) || 1) + 1,
      updatedAt: c.at,
      deviceId: c.deviceId,
    };
    const copy = base.slice();
    copy[idx] = next;
    return { reservations: copy, reservation: next, error: null };
  },

  // Cancellazione LOGICA (P3): mai delete fisico. Idempotente: ri-cancellare
  // un già-tombstoned non è errore.
  tombstoneReservation(reservations, id, ctx) {
    const base = Array.isArray(reservations) ? reservations : [];
    const idx = base.findIndex(x => x && x.id === id);
    if (idx < 0) return { reservations: base, reservation: null, error: "prenotazione non trovata" };
    const prev = base[idx];
    const c = _crmCtx(ctx);
    const next = {
      ...prev,
      tombstone: true,
      version: (Number(prev.version) || 1) + 1,
      updatedAt: c.at,
      deviceId: c.deviceId,
    };
    const copy = base.slice();
    copy[idx] = next;
    return { reservations: copy, reservation: next, error: null };
  },
};

// Handle diagnostico di sola lettura (console: `window.__reservationCore.createReservation(...)`).
// Non altera alcun comportamento; è solo un riferimento all'oggetto puro qui sopra.
if (typeof window !== "undefined") { try { window.__reservationCore = reservationCore; } catch { /* no-op */ } }
// ⟦RESV-CORE:END⟧

// ⟦FLOOR-CORE:START⟧ — R2 Floor Core (ADR Floor Core, PROPOSED)
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║           FLOOR CORE — il patrimonio fisico del ristorante (R2)            ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// Layer di dominio PURO per `floorAreas`/`floorTables`: reducer immutabili,
// nessun side effect, nessuna dipendenza React. Aree e tavoli sono ANAGRAFICA,
// mai eventi: l'occupazione è SEMPRE derivata (occupancyByArea), mai persistita.
// Confini duri (ADR Floor Core):
//   • `reservation.table` resta testo libero: il ponte è resolveTableTokens,
//     parsing soft a senso unico — MAI blocco del salvataggio, mai FK;
//   • canonicalizzazione label con le STESSE regole di _rfCanonicalTable (facade
//     congelata) ma implementata qui, appoggiandosi alla utility di modulo
//     normStr (fuori dai blocchi frozen): la facade non si tocca e non se ne
//     chiamano gli helper privati;
//   • multi-tavolo: coperti attribuiti all'area del PRIMO token riconosciuto,
//     nessuna divisione automatica dei coperti (approssimazione dichiarata, ADR §4);
//   • capienza area: esplicita > somma seats (>0) dei tavoli attivi > null —
//     mai inventata (ADR §5); overbooking = flag, mai blocco;
//   • niente coordinate/piantina/planning; mai delete fisico → tombstone (P3);
//   • riusa gli helper esterni `_crmCtx`/`uid` come già fanno CRM Fase 3 e RESV-CORE.

// Canon token tavolo — stesse regole di _rfCanonicalTable: strip prefissi
// tavolo/tav/t/#, interi senza zeri iniziali, altrimenti normStr.
const _floorCanonToken = (raw) => {
  const s = String(raw == null ? "" : raw).trim();
  if (!s) return null;
  const stripped = s.replace(/^(?:tavolo|tav|t|#)\s*\.?\s*(?=\d)/i, "").trim();
  if (/^\d+$/.test(stripped)) return String(parseInt(stripped, 10));
  return normStr(stripped) || null;
};
// Separatori ESPLICITI del multi-tavolo ("431|430", "12+13", "12,13", "12/13").
// MAI split sullo spazio: "12 bis" è un token unico (ADR §4).
const _FLOOR_TOKEN_SEPARATORS = /[|+,/]/;

const floorCore = {
  // ── AREE ────────────────────────────────────────────────────────────────────
  // Crea un'area. name obbligatorio; capacity opzionale (null = si deriva dai
  // tavoli); active default true. Ritorna { floorAreas, area, error }.
  createArea(areas, input, ctx) {
    const base = Array.isArray(areas) ? areas : [];
    const inp = input || {};
    const c = _crmCtx(ctx);
    const name = inp.name != null ? String(inp.name).trim() : "";
    if (!name) return { floorAreas: base, area: null, error: "name obbligatorio" };
    let capacity = null;
    if (inp.capacity != null && inp.capacity !== "") {
      const n = Number(inp.capacity);
      if (!Number.isInteger(n) || n < 0) return { floorAreas: base, area: null, error: "capacity deve essere un intero ≥ 0 (o assente)" };
      capacity = n;
    }
    let sortOrder = base.length + 1;
    if (inp.sortOrder != null && inp.sortOrder !== "") {
      const so = Number(inp.sortOrder);
      if (!Number.isFinite(so)) return { floorAreas: base, area: null, error: "sortOrder deve essere un numero" };
      sortOrder = so;
    }
    const area = {
      id: uid("fa_"),
      name,
      capacity,
      active: inp.active !== undefined ? !!inp.active : true,
      sortOrder,
      tenantId: inp.tenantId != null ? String(inp.tenantId) : c.tenantId,
      restaurantId: inp.restaurantId != null ? String(inp.restaurantId) : c.restaurantId,
      version: 1,
      updatedAt: c.at,
      deviceId: c.deviceId,
      tombstone: false,
      createdBy: c.by,
      createdAt: c.at,
    };
    return { floorAreas: [...base, area], area, error: null };
  },

  // Aree ordinate per sortOrder poi name. Default: esclude tombstoned E inattive
  // (i consumer operativi vedono solo il floor in uso); opts.includeInactive /
  // opts.includeTombstoned per la vista di configurazione.
  listAreas(areas, opts) {
    if (!Array.isArray(areas)) return [];
    const o = opts || {};
    return areas
      .filter(a => {
        if (!a) return false;
        if (a.tombstone && !o.includeTombstoned) return false;
        if (a.active === false && !o.includeInactive) return false;
        return true;
      })
      .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0) || String(a.name || "").localeCompare(String(b.name || "")));
  },

  // Aggiorna SOLO l'anagrafica dell'area (name/capacity/active/sortOrder).
  // Version bump; rifiuta su inesistente/tombstoned/input non valido.
  updateArea(areas, id, patch, ctx) {
    const base = Array.isArray(areas) ? areas : [];
    const idx = base.findIndex(x => x && x.id === id);
    if (idx < 0) return { floorAreas: base, area: null, error: "area non trovata" };
    const prev = base[idx];
    if (prev.tombstone) return { floorAreas: base, area: null, error: "area cancellata (tombstone)" };
    const c = _crmCtx(ctx);
    const p = patch || {};
    const next = { ...prev };
    if (p.name !== undefined) {
      const n = String(p.name).trim();
      if (!n) return { floorAreas: base, area: null, error: "name non può essere vuoto" };
      next.name = n;
    }
    if (p.capacity !== undefined) {
      if (p.capacity == null || p.capacity === "") next.capacity = null;
      else {
        const n = Number(p.capacity);
        if (!Number.isInteger(n) || n < 0) return { floorAreas: base, area: null, error: "capacity deve essere un intero ≥ 0 (o null)" };
        next.capacity = n;
      }
    }
    if (p.active !== undefined) next.active = !!p.active;
    if (p.sortOrder !== undefined) {
      const so = Number(p.sortOrder);
      if (!Number.isFinite(so)) return { floorAreas: base, area: null, error: "sortOrder deve essere un numero" };
      next.sortOrder = so;
    }
    next.version = (Number(prev.version) || 1) + 1;
    next.updatedAt = c.at;
    next.deviceId = c.deviceId;
    const copy = base.slice();
    copy[idx] = next;
    return { floorAreas: copy, area: next, error: null };
  },

  // Cancellazione LOGICA (P3): mai delete fisico; idempotente. I tavoli dell'area
  // NON vengono toccati: restano con area non risolvibile, segnalata a runtime (ADR §3).
  tombstoneArea(areas, id, ctx) {
    const base = Array.isArray(areas) ? areas : [];
    const idx = base.findIndex(x => x && x.id === id);
    if (idx < 0) return { floorAreas: base, area: null, error: "area non trovata" };
    const prev = base[idx];
    const c = _crmCtx(ctx);
    const next = { ...prev, tombstone: true, version: (Number(prev.version) || 1) + 1, updatedAt: c.at, deviceId: c.deviceId };
    const copy = base.slice();
    copy[idx] = next;
    return { floorAreas: copy, area: next, error: null };
  },

  // ── TAVOLI ──────────────────────────────────────────────────────────────────
  // Crea un tavolo. label e areaId obbligatori; seats intero ≥ 0 (default 0 =
  // non configurato: non contribuisce alla capienza derivata). NB: l'esistenza
  // dell'areaId NON è validata qui — core puro per singolo store, come gli altri
  // reducer del repo; un'area inesistente emerge a runtime come non risolvibile.
  createTable(tables, input, ctx) {
    const base = Array.isArray(tables) ? tables : [];
    const inp = input || {};
    const c = _crmCtx(ctx);
    const label = inp.label != null ? String(inp.label).trim() : "";
    if (!label) return { floorTables: base, table: null, error: "label obbligatoria" };
    const areaId = inp.areaId != null ? String(inp.areaId).trim() : "";
    if (!areaId) return { floorTables: base, table: null, error: "areaId obbligatorio" };
    let seats = 0;
    if (inp.seats != null && inp.seats !== "") {
      const n = Number(inp.seats);
      if (!Number.isInteger(n) || n < 0) return { floorTables: base, table: null, error: "seats deve essere un intero ≥ 0" };
      seats = n;
    }
    let sortOrder = base.length + 1;
    if (inp.sortOrder != null && inp.sortOrder !== "") {
      const so = Number(inp.sortOrder);
      if (!Number.isFinite(so)) return { floorTables: base, table: null, error: "sortOrder deve essere un numero" };
      sortOrder = so;
    }
    const table = {
      id: uid("ft_"),
      label,
      areaId,
      seats,
      active: inp.active !== undefined ? !!inp.active : true,
      sortOrder,
      tenantId: inp.tenantId != null ? String(inp.tenantId) : c.tenantId,
      restaurantId: inp.restaurantId != null ? String(inp.restaurantId) : c.restaurantId,
      version: 1,
      updatedAt: c.at,
      deviceId: c.deviceId,
      tombstone: false,
      createdBy: c.by,
      createdAt: c.at,
    };
    return { floorTables: [...base, table], table, error: null };
  },

  // Tavoli ordinati per sortOrder poi label canonica (ordinamento numerico:
  // "2" prima di "10"). Default: esclude tombstoned E inattivi; opts.areaId
  // filtra; opts.includeInactive / opts.includeTombstoned per la configurazione.
  listTables(tables, opts) {
    if (!Array.isArray(tables)) return [];
    const o = opts || {};
    return tables
      .filter(t => {
        if (!t) return false;
        if (t.tombstone && !o.includeTombstoned) return false;
        if (t.active === false && !o.includeInactive) return false;
        if (o.areaId != null && String(t.areaId) !== String(o.areaId)) return false;
        return true;
      })
      .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0)
        || String(_floorCanonToken(a.label) || "").localeCompare(String(_floorCanonToken(b.label) || ""), undefined, { numeric: true }));
  },

  // Aggiorna SOLO l'anagrafica del tavolo (label/areaId/seats/active/sortOrder).
  updateTable(tables, id, patch, ctx) {
    const base = Array.isArray(tables) ? tables : [];
    const idx = base.findIndex(x => x && x.id === id);
    if (idx < 0) return { floorTables: base, table: null, error: "tavolo non trovato" };
    const prev = base[idx];
    if (prev.tombstone) return { floorTables: base, table: null, error: "tavolo cancellato (tombstone)" };
    const c = _crmCtx(ctx);
    const p = patch || {};
    const next = { ...prev };
    if (p.label !== undefined) {
      const n = String(p.label).trim();
      if (!n) return { floorTables: base, table: null, error: "label non può essere vuota" };
      next.label = n;
    }
    if (p.areaId !== undefined) {
      const a = p.areaId != null ? String(p.areaId).trim() : "";
      if (!a) return { floorTables: base, table: null, error: "areaId non può essere vuoto" };
      next.areaId = a;
    }
    if (p.seats !== undefined) {
      const n = Number(p.seats);
      if (!Number.isInteger(n) || n < 0) return { floorTables: base, table: null, error: "seats deve essere un intero ≥ 0" };
      next.seats = n;
    }
    if (p.active !== undefined) next.active = !!p.active;
    if (p.sortOrder !== undefined) {
      const so = Number(p.sortOrder);
      if (!Number.isFinite(so)) return { floorTables: base, table: null, error: "sortOrder deve essere un numero" };
      next.sortOrder = so;
    }
    next.version = (Number(prev.version) || 1) + 1;
    next.updatedAt = c.at;
    next.deviceId = c.deviceId;
    const copy = base.slice();
    copy[idx] = next;
    return { floorTables: copy, table: next, error: null };
  },

  // Cancellazione LOGICA (P3): mai delete fisico; idempotente.
  tombstoneTable(tables, id, ctx) {
    const base = Array.isArray(tables) ? tables : [];
    const idx = base.findIndex(x => x && x.id === id);
    if (idx < 0) return { floorTables: base, table: null, error: "tavolo non trovato" };
    const prev = base[idx];
    const c = _crmCtx(ctx);
    const next = { ...prev, tombstone: true, version: (Number(prev.version) || 1) + 1, updatedAt: c.at, deviceId: c.deviceId };
    const copy = base.slice();
    copy[idx] = next;
    return { floorTables: copy, table: next, error: null };
  },

  // ── RISOLUZIONE TOKEN TAVOLO (parsing soft, ADR §4) ─────────────────────────
  // Da `reservation.table` (testo libero) ai tavoli d'anagrafica. Split SOLO su
  // separatori espliciti | + , / (mai sullo spazio: "12 bis" è un token unico).
  // Ogni token → matched (tavolo attivo riconosciuto) o unknown (resta nel
  // risultato con tableId null). MAI un errore, MAI un blocco: pura lettura.
  // primaryAreaId = area del PRIMO token riconosciuto (ADR §4, multi-tavolo).
  resolveTableTokens(tableText, floorTables, opts) {
    const raw = tableText != null ? String(tableText) : "";
    const trimmed = raw.trim();
    const empty = { raw, tokens: [], matchedCount: 0, unknownCount: 0, primaryAreaId: null, status: "empty" };
    if (!trimmed) return empty;
    const tables = Array.isArray(floorTables) ? floorTables : [];
    // Indice canonico dei tavoli attivi non tombstoned; a label canonica duplicata
    // vince la prima in ordine di store (il duplicato è un warning d'anagrafica, non un errore qui).
    const index = new Map();
    for (const t of tables) {
      if (!t || t.tombstone || t.active === false) continue;
      const k = _floorCanonToken(t.label);
      if (k != null && !index.has(k)) index.set(k, t);
    }
    const tokens = trimmed.split(_FLOOR_TOKEN_SEPARATORS).map(s => s.trim()).filter(s => s !== "").map(rawToken => {
      const canonical = _floorCanonToken(rawToken);
      const t = canonical != null ? index.get(canonical) : undefined;
      return t
        ? { rawToken, canonical, tableId: t.id, label: t.label, areaId: t.areaId != null ? String(t.areaId) : null, status: "matched" }
        : { rawToken, canonical, tableId: null, label: null, areaId: null, status: "unknown" };
    });
    if (tokens.length === 0) return empty;
    const matchedCount = tokens.filter(t => t.status === "matched").length;
    const unknownCount = tokens.length - matchedCount;
    const first = tokens.find(t => t.status === "matched") || null;
    return {
      raw,
      tokens,
      matchedCount,
      unknownCount,
      primaryAreaId: first ? first.areaId : null,
      status: unknownCount === 0 ? "matched" : matchedCount === 0 ? "unknown" : "partial",
    };
  },

  // ── OCCUPAZIONE / CAPIENZA PER AREA (derivata, mai persistita — ADR §5) ─────
  // Contano SOLO booked e seated (cancelled/no_show liberano i posti); tombstoned
  // esclusi; opts.serviceDate/opts.service filtrano il giorno/servizio.
  // Capienza area: esplicita > somma seats (>0) dei tavoli attivi > null (mai
  // inventata; seats 0 = non configurato, non fa base). Attribuzione coperti:
  // TUTTI i covers all'area del primo token riconosciuto; senza tavolo →
  // `unassigned`; tavolo presente ma nessun token riconosciuto (o area del match
  // non attiva/tombstoned) → `unknownTable`. remaining/overCapacity = dato puro,
  // il display decide altrove; overbooking è un flag, mai un blocco.
  occupancyByArea(reservations, floorAreas, floorTables, opts) {
    const o = opts || {};
    const resvs = (Array.isArray(reservations) ? reservations : []).filter(r => {
      if (!r || r.tombstone) return false;
      if (o.serviceDate != null && r.serviceDate !== String(o.serviceDate)) return false;
      if (o.service != null && r.service !== String(o.service)) return false;
      return r.status === "booked" || r.status === "seated";
    });
    const areas = (Array.isArray(floorAreas) ? floorAreas : []).filter(a => a && !a.tombstone && a.active !== false);
    const tables = Array.isArray(floorTables) ? floorTables : [];
    const seatsByArea = new Map();
    for (const t of tables) {
      if (!t || t.tombstone || t.active === false) continue;
      const s = Number(t.seats);
      if (Number.isInteger(s) && s > 0) {
        const aid = String(t.areaId);
        seatsByArea.set(aid, (seatsByArea.get(aid) || 0) + s);
      }
    }
    const rows = areas.map(a => ({
      areaId: a.id,
      name: a.name,
      capacity: a.capacity != null && Number.isInteger(Number(a.capacity)) ? Number(a.capacity)
        : (seatsByArea.has(String(a.id)) ? seatsByArea.get(String(a.id)) : null),
      occupied: 0,
      reservationIds: [],
    }));
    const rowById = new Map(rows.map(x => [String(x.areaId), x]));
    const unassigned = { covers: 0, reservationIds: [] };
    const unknownTable = { covers: 0, reservationIds: [] };
    for (const r of resvs) {
      const covers = Number(r.covers) || 0;
      const text = r.table != null ? String(r.table).trim() : "";
      if (!text) { unassigned.covers += covers; unassigned.reservationIds.push(r.id); continue; }
      const resolved = floorCore.resolveTableTokens(text, tables);
      const row = resolved.primaryAreaId != null ? rowById.get(String(resolved.primaryAreaId)) : null;
      if (row) { row.occupied += covers; row.reservationIds.push(r.id); }
      else { unknownTable.covers += covers; unknownTable.reservationIds.push(r.id); }
    }
    const areasOut = rows.map(x => ({
      ...x,
      remaining: x.capacity != null ? x.capacity - x.occupied : null,
      overCapacity: x.capacity != null && x.capacity - x.occupied < 0,
    }));
    const withCapacity = areasOut.filter(x => x.capacity != null);
    const totalCapacity = withCapacity.length > 0 ? withCapacity.reduce((s, x) => s + x.capacity, 0) : null;
    // totals.occupied = TUTTI i coperti che contano (aree + non assegnati + sconosciuti):
    // anche chi non ha un tavolo assegnato occupa il ristorante.
    const totalOccupied = areasOut.reduce((s, x) => s + x.occupied, 0) + unassigned.covers + unknownTable.covers;
    return {
      areas: areasOut,
      unassigned,
      unknownTable,
      totals: {
        capacity: totalCapacity,
        occupied: totalOccupied,
        remaining: totalCapacity != null ? totalCapacity - totalOccupied : null,
        overCapacity: totalCapacity != null && totalCapacity - totalOccupied < 0,
        unassignedCovers: unassigned.covers,
        unknownTableCovers: unknownTable.covers,
      },
    };
  },
};

// Handle diagnostico di sola lettura (console: `window.__floorCore.resolveTableTokens(...)`).
// Non altera alcun comportamento; è solo un riferimento all'oggetto puro qui sopra.
if (typeof window !== "undefined") { try { window.__floorCore = floorCore; } catch { /* no-op */ } }
// ⟦FLOOR-CORE:END⟧

// ⟦RESV-FULFILL:START⟧ — R5-T2 (ADR-CRM-11 Fulfilment, PROPOSED)
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║        RESERVATION FULFILMENT CORE — ReservationReceiptLink (R5)           ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// Layer di dominio PURO per l'entità `reservationReceiptLinks`: l'UNICO fatto
// persistito del fulfilment reservation→receipt (ADR-CRM-11 F1) — l'asserzione
// umana "questa prenotazione è stata realizzata da questo scontrino".
// Vincoli (F-series):
//   • receipt.id VOLATILE mai persistito (F3): si pinnano primaryStableReceiptKey
//     + candidateKeySnapshot + keyVersion via receiptFacade PUBBLICA (F4) —
//     stesso pattern P11 del CustomerReceiptLink; gli helper privati del blocco
//     ⟦CRM-LINK⟧ congelato NON si chiamano, si reimplementano localmente;
//   • fulfilled / no-show / valore / Visit MAI persistiti (F2/F10): qui solo
//     derivazioni runtime pure, ricomputate a ogni chiamata;
//   • unlink = tombstone con audit, mai delete (F7); N:M ammesso (F5);
//     receipt condiviso SEGNALATO, mai ripartito (F6);
//   • nessun auto-link (F8): questo core non decide mai da solo — esegue
//     asserzioni esplicite dell'operatore; score/confidence non entrano nel
//     record, resta solo `origin` come audit;
//   • repair PERSISTITO (ri-pinnare chiavi su receipt re-importato) fuori
//     scope T2: qui solo risoluzione NON distruttiva.

const RESV_FULFILL_ORIGINS = ["manual", "suggestion_confirmed"];
// Set delle sole `key` da uno snapshot/set (locale, nessuna dipendenza CRM-LINK).
const _rflKeySet = (arr) => new Set((Array.isArray(arr) ? arr : []).map(k => k && k.key).filter(Boolean));
// Solo chiavi NON degradate: unica base ammessa per la risoluzione di fallback
// (coerente con Foundation I8: i tier degraded da soli non fondano un match).
const _rflNonDegradedKeys = (arr) => (Array.isArray(arr) ? arr : []).filter(k => k && !k.degraded && k.key).map(k => k.key);
// Fingerprint chiavi del receipt al momento del collegamento (P11). keyVersion
// derivata dal prefisso della chiave (forma documentata `versione:namespace:tier…`).
function _rflKeysFromReceipt(receipt) {
  const set = receiptFacade.computeReceiptCandidateKeys(receipt);
  const primary = set.length ? set[0] : null;
  return {
    primaryStableReceiptKey: primary ? primary.key : null,
    keyTier: primary ? primary.tier : null,
    candidateKeySnapshot: set,
    keyVersion: primary ? String(primary.key).split(":")[0] : null,
  };
}

const reservationFulfilCore = {
  // ── Letture ────────────────────────────────────────────────────────────────
  // Default ESCLUDE i tombstoned. Filtri opzionali: reservationId; stableKey
  // (match sul primary O per appartenenza allo snapshot pinnato).
  listReservationReceiptLinks(links, opts) {
    if (!Array.isArray(links)) return [];
    const o = opts || {};
    return links.filter(l => {
      if (!l) return false;
      if (l.tombstone && !o.includeTombstoned) return false;
      if (o.reservationId != null && String(l.reservationId) !== String(o.reservationId)) return false;
      if (o.stableKey != null &&
        l.primaryStableReceiptKey !== o.stableKey && !_rflKeySet(l.candidateKeySnapshot).has(o.stableKey)) return false;
      return true;
    });
  },

  // ── Scrittura: crea il fatto (reducer puro, immutabile) ────────────────────
  // Identità receipt ammessa: (a) receipt object → chiavi calcolate via facade;
  // (b) chiavi già pinnate (primaryStableReceiptKey + candidateKeySnapshot).
  // Un input con SOLO receiptId è un ERRORE esplicito (F3). Duplicato attivo
  // sulla stessa coppia prenotazione+primary → errore; la N:M resta libera su
  // coppie diverse (F5). Ritorna { reservationReceiptLinks, link, error }.
  createReservationReceiptLink(links, reservations, receipt, input, ctx) {
    const base = Array.isArray(links) ? links : [];
    const inp = input || {};
    const c = _crmCtx(ctx);
    const origin = String(inp.origin || "manual");
    if (!RESV_FULFILL_ORIGINS.includes(origin)) {
      return { reservationReceiptLinks: base, link: null, error: `origin non valido: ${origin}` };
    }
    if (inp.reservationId == null || String(inp.reservationId).trim() === "") {
      return { reservationReceiptLinks: base, link: null, error: "reservationId obbligatorio" };
    }
    const resvArr = Array.isArray(reservations) ? reservations : [];
    const resv = resvArr.find(r => r && r.id === inp.reservationId) || null;
    if (!resv || resv.tombstone) {
      return { reservationReceiptLinks: base, link: null, error: "prenotazione non trovata (o tombstoned)" };
    }
    if (!receipt && inp.receiptId != null && inp.primaryStableReceiptKey == null) {
      return { reservationReceiptLinks: base, link: null, error: "receipt.id non è identità di dominio (F3): passare il receipt o le chiavi stabili" };
    }
    const keys = receipt ? _rflKeysFromReceipt(receipt) : {
      primaryStableReceiptKey: inp.primaryStableReceiptKey || null,
      keyTier: inp.keyTier != null ? inp.keyTier : null,
      candidateKeySnapshot: Array.isArray(inp.candidateKeySnapshot) ? inp.candidateKeySnapshot : [],
      keyVersion: inp.keyVersion != null ? String(inp.keyVersion)
        : (inp.primaryStableReceiptKey ? String(inp.primaryStableReceiptKey).split(":")[0] : null),
    };
    if (!keys.primaryStableReceiptKey) {
      return { reservationReceiptLinks: base, link: null, error: "impossibile derivare una stableReceiptKey (receipt o chiavi mancanti)" };
    }
    if (!Array.isArray(keys.candidateKeySnapshot) || keys.candidateKeySnapshot.length === 0) {
      return { reservationReceiptLinks: base, link: null, error: "candidateKeySnapshot obbligatorio (P11)" };
    }
    // Duplicato = stessa prenotazione + stesso receipt per INTERSEZIONE di chiavi
    // (non solo primary===primary: un link pinnato v1 e un tentativo v2 sullo
    // stesso scontrino fisico sono lo stesso fatto — post-sblocco 2026-07-06).
    const inpSnapKeys = _rflKeySet(keys.candidateKeySnapshot);
    const dup = base.find(l => l && !l.tombstone &&
      String(l.reservationId) === String(inp.reservationId) &&
      (l.primaryStableReceiptKey === keys.primaryStableReceiptKey ||
        inpSnapKeys.has(l.primaryStableReceiptKey) ||
        _rflKeySet(l.candidateKeySnapshot).has(keys.primaryStableReceiptKey)));
    if (dup) {
      return { reservationReceiptLinks: base, link: null, error: "link già esistente per questa prenotazione e questo scontrino" };
    }
    const link = {
      id: uid("rrl_"),
      reservationId: String(inp.reservationId),
      primaryStableReceiptKey: keys.primaryStableReceiptKey,
      keyTier: keys.keyTier,
      candidateKeySnapshot: keys.candidateKeySnapshot, // IMMUTABILE (P11)
      keyVersion: keys.keyVersion,
      origin,
      note: inp.note != null && String(inp.note).trim() !== "" ? String(inp.note) : null,
      tenantId: c.tenantId,
      restaurantId: c.restaurantId,
      version: 1,
      updatedAt: c.at,
      deviceId: c.deviceId,
      tombstone: false,
      createdBy: c.by,
      createdAt: c.at,
    };
    return { reservationReceiptLinks: [...base, link], link, error: null };
  },

  // Cancellazione LOGICA (F7): tombstone + bump version, mai delete fisico.
  // Idempotente. Lo snapshot NON si tocca: resta prova storica.
  tombstoneReservationReceiptLink(links, id, ctx) {
    const base = Array.isArray(links) ? links : [];
    const idx = base.findIndex(x => x && x.id === id);
    if (idx < 0) return { reservationReceiptLinks: base, link: null, error: "link non trovato" };
    const prev = base[idx];
    if (prev.tombstone) return { reservationReceiptLinks: base, link: prev, error: null };
    const c = _crmCtx(ctx);
    const next = { ...prev, tombstone: true, version: (Number(prev.version) || 1) + 1, updatedAt: c.at, deviceId: c.deviceId };
    const copy = base.slice();
    copy[idx] = next;
    return { reservationReceiptLinks: copy, link: next, error: null };
  },

  // ── Risoluzione NON distruttiva (mai scrive, mai ripara da sola) ───────────
  // Prova il primary pinnato, poi in fallback le altre chiavi NON degradate
  // dello snapshot. resolveReceiptByStableKey (facade) ammette solo match
  // UNIVOCI: l'ambiguo resta null → orphan (coda di lavoro, non errore).
  resolveReservationReceiptLink(state, link) {
    if (!link) return { receipt: null, orphan: true, via: null };
    let receipt = link.primaryStableReceiptKey ? receiptFacade.resolveReceiptByStableKey(state, link.primaryStableReceiptKey) : null;
    if (receipt) return { receipt, orphan: false, via: "primary" };
    for (const k of _rflNonDegradedKeys(link.candidateKeySnapshot)) {
      if (k === link.primaryStableReceiptKey) continue;
      receipt = receiptFacade.resolveReceiptByStableKey(state, k);
      if (receipt) return { receipt, orphan: false, via: "snapshot" };
    }
    return { receipt: null, orphan: true, via: null };
  },

  // ── Derivazione runtime del fulfilment (F2): MAI persistita ────────────────
  // fulfilled = ≥1 link attivo risolto; realSpendCents = somma dei receipt
  // risolti via facade (gli orfani NON contano finché non riparati);
  // sharedReceipt = lo stesso stable receipt è linkato ATTIVAMENTE anche ad
  // altre prenotazioni (F6: si segnala, mai si ripartisce l'importo).
  deriveReservationFulfilment(state, reservationId) {
    const all = Array.isArray(state && state.reservationReceiptLinks) ? state.reservationReceiptLinks : [];
    const links = reservationFulfilCore.listReservationReceiptLinks(all, { reservationId });
    const resolved = [];
    const orphanLinks = [];
    let realSpendCents = 0;
    const sharedReceiptKeys = [];
    for (const l of links) {
      const r = reservationFulfilCore.resolveReservationReceiptLink(state, l);
      if (r.orphan) { orphanLinks.push(l); continue; }
      resolved.push({ link: l, receipt: r.receipt, via: r.via });
      realSpendCents += receiptFacade.getReceiptMoney(r.receipt).totalCents;
      // Shared = un ALTRO link attivo aggancia lo stesso receipt RISOLTO. Il
      // confronto usa SOLO le chiavi FORTI correnti del receipt: v2 day-scoped
      // e T3_TEMPORAL (unica per receipt, presente in ogni snapshot vecchio e
      // nuovo). Le v1 fiscali legacy sono ESCLUSE: collidono tra giorni (TCPOS
      // riparte da 1) e generavano falsi "condiviso" cross-day. Post-sblocco
      // 2026-07-06; prima si confrontava solo il primary del link.
      const curStrong = new Set(receiptFacade.computeReceiptCandidateKeys(r.receipt)
        .filter(k => k.key.startsWith(`${RECEIPT_KEY_VERSION_V2}:`) || k.tier === "T3_TEMPORAL")
        .map(k => k.key));
      const others = all.filter(o => o && !o.tombstone && o.id !== l.id &&
        String(o.reservationId) !== String(reservationId) &&
        (curStrong.has(o.primaryStableReceiptKey) || (Array.isArray(o.candidateKeySnapshot) && o.candidateKeySnapshot.some(k => k && curStrong.has(k.key)))));
      if (others.length > 0) sharedReceiptKeys.push(l.primaryStableReceiptKey);
    }
    return {
      links,
      resolved,
      orphanLinks,
      fulfilled: resolved.length > 0,
      realSpendCents,
      sharedReceiptKeys,
      sharedReceipt: sharedReceiptKeys.length > 0,
    };
  },
};

// Handle diagnostico di sola lettura per la console (`window.__resvFulfil`).
// Non altera alcun comportamento: è solo un riferimento all'oggetto puro.
if (typeof window !== "undefined") { try { window.__resvFulfil = reservationFulfilCore; } catch { /* no-op */ } }
// ⟦RESV-FULFILL:END⟧

// CSV Importer: legge file, mostra preview, mapping colonne, matching fuzzy, crea scontrini
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║              IMPORTER ZUCCHETTI TCPOS (export scontrini cassa)             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// Importer dedicato al formato Zucchetti TCPOS (config standard ristorazione).
// Auto-detect del formato:
//  - .xls/.xlsx con foglio "Movimenti" o "Scontrini" → SheetJS
//  - .csv con delimitatore ; (italiano) → parser nativo
//
// Riconoscimento colonne supportato (varia per setup TCPOS):
//  - Data scontrino: "Data", "Data Scontrino", "Data Chiusura"
//  - Tavolo: "Tavolo", "Coperti Tavolo", "N. Tavolo"
//  - Coperti: "Coperti", "N. Coperti", "Pax"
//  - Articolo: "Articolo", "Descrizione", "Prodotto"
//  - Quantità: "Quantità", "Q.tà", "Qta"
//  - Prezzo: "Prezzo", "Importo", "Totale"
//  - Reparto: "Reparto", "Categoria"
//
// Re-import sicuro: identifica scontrini già presenti per (data + tavolo + ora ± 30min)
// e propone "sostituisci" o "salta" senza creare duplicati.

// Helper: badge tipo documento per UI scontrini importati.
// SCONTRINO FISCALE → null (default, niente badge)
// FATTURA → "FATT" olive (vendita confermata, fatturata)
// ESTRATTO CONTO → "NON FISCALIZZATO" terracotta (incasso reale fuori dalla cassa fiscale)
// Backward-compat: scontrini pre-fix _docType non hanno → no badge.
// FIX F-16 (cross-check 2026-05-19): label corretta da "PRECONTO" a "NON FISCALIZZATO".
// "Preconto" in TCPOS è la stampa di cortesia pre-conto al tavolo, NON viene esportata
// nel CSV. L'ESTRATTO CONTO è invece una chiusura tavolo senza emissione scontrino fiscale
// = incasso reale non fiscalizzato. Il termine "preconto" era semanticamente sbagliato.
// Il campo _isPreconto è mantenuto per retrocompatibilità con lo state utente esistente.
function receiptDocBadge(r) {
  if (!r) return null;
  if (r._isPreconto || r._docType === "ESTRATTO CONTO") {
    // _isPreconto kept for back-compat with user state (renaming would lose existing data)
    // Riconciliato a uno SF: badge olive tenue + riferimento
    if (r._regularizedTo && r._regularizedTo !== "NERO_DEFINITIVO") {
      return { label: "RICONCILIATO", bg: C.olive, title: `EC riconciliato — collegato a SF/FT ${r._regularizedTo}. Escluso dai totali non fiscalizzati.`, extra: r._regularizedTo };
    }
    // Confermato come nero definitivo (non verranno mai emessi documenti fiscali)
    if (r._regularizedTo === "NERO_DEFINITIVO") {
      return { label: "NERO CONFERMATO", bg: C.terraDeep, title: "EC marcato come definitivamente non fiscalizzato. Conteggiato nell'incasso totale, tracciato in auditLog." };
    }
    // EC orfano: non ancora riconciliato né confermato
    return { label: "NON FISCALIZZATO", bg: C.terra, title: "ESTRATTO CONTO — chiusura senza emissione scontrino fiscale. È un incasso reale, fuori dalla cassa fiscalizzata." };
  }
  if (r._docType === "FATTURA") {
    return { label: "FATT", bg: C.olive, title: "Fattura: vendita chiusa con documento fiscale fattura" };
  }
  return null;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║  ReconcileNFModal — collega un EC orfano a uno scontrino fiscale esistente ║
// ║  Trigger: click "Collega a scontrino fiscale" in NonFiscalSection           ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function ReconcileNFModal({ ec, state, setState, onClose }) {
  const toast = useToast();
  const [expandSearch, setExpandSearch] = useState(false);
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState(null);

  const ecDate = getServiceDate(ec.closedAt);

  // Candidati SF/FT: stesso giorno servizio, escludendo EC e già-riconciliati
  const candidates = useMemo(() => {
    const allReceipts = state.tableReceipts || [];
    return allReceipts.filter(r => {
      // Esclude l'EC stesso e gli altri EC
      if (r._isPreconto || r._docType === "ESTRATTO CONTO") return false;
      // Esclude SF già collegati a questo EC (tracciabilità inversa)
      if ((r._hasReconciledEC || []).includes(ec.id)) return true; // già collegato, mostralo uguale
      const d = getServiceDate(r.closedAt);
      if (!expandSearch) return d === ecDate;
      // Ricerca estesa: ±2 giorni
      const ecMs = new Date(ecDate + "T12:00:00").getTime();
      const dMs = new Date(d + "T12:00:00").getTime();
      return Math.abs(ecMs - dMs) <= 2 * 86400000;
    }).sort((a, b) => new Date(a.closedAt) - new Date(b.closedAt));
  }, [state.tableReceipts, ecDate, expandSearch, ec.id]);

  const ecTotal = ec.total || 0;

  const handleLink = () => {
    if (!selected) return;
    const operator = state.currentUser?.name || "Andi";
    const ts = nowIso();

    setState(s => {
      const updatedReceipts = (s.tableReceipts || []).map(r => {
        if (r.id === ec.id) {
          return {
            ...r,
            _regularizedTo: selected.id,
            _regularizedAt: ts,
            _regularizedBy: operator,
            _regularizedNote: note.trim() || null,
          };
        }
        if (r.id === selected.id) {
          return {
            ...r,
            _hasReconciledEC: [...(r._hasReconciledEC || []), ec.id],
          };
        }
        return r;
      });
      // Audit log
      const docNum = selected.receiptNumber || selected.number || selected.id;
      const auditNext = {
        ...s,
        tableReceipts: updatedReceipts,
      };
      return {
        ...auditNext,
        tableReceipts: updatedReceipts,
        auditLog: appendAudit(auditNext, {
          action: "nf_reconciled",
          entityType: "receipt",
          entityId: ec.id,
          ref: { sfId: selected.id, sfNumber: docNum },
          by: operator,
          reason: note.trim() || "Riconciliazione manuale EC→SF",
        }),
      };
    });

    const docNum = selected.receiptNumber || selected.number || selected.id;
    const ecDateFmt = new Date(ec.closedAt).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
    toast({ type: "success", title: "EC collegato", message: `EC del ${ecDateFmt} collegato a SF#${docNum}` });
    onClose();
  };

  const ecDateFmt = new Date(ec.closedAt).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
  const ecTimeFmt = new Date(ec.closedAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

  return <Modal
    onClose={onClose}
    maxWidth={760}
    title="Collega EC a scontrino fiscale"
    subtitle={`EC del ${ecDateFmt} ${ecTimeFmt} · tav. ${ec.number || "—"} · ${fmt.eur(ecTotal)}`}
    footer={<>
      <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
      <Btn variant="accent" onClick={handleLink} disabled={!selected}>Collega</Btn>
    </>}
  >
    <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.6, background: C.paperDeep, padding: "10px 14px", borderRadius: 2 }}>
        Seleziona lo scontrino fiscale (SF) o fattura (FT) a cui corrisponde questo ESTRATTO CONTO.
        Una volta collegato, l'EC uscira dai totali "non fiscalizzati" e l'incasso sara conteggiato solo nello SF per evitare doppioni.
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: C.inkSoft }}>
        <input type="checkbox" checked={expandSearch} onChange={e => setExpandSearch(e.target.checked)} />
        Allarga ricerca a ±2 giorni dalla data dell'EC (ora: {expandSearch ? "estesa" : "solo stesso giorno"})
      </label>

      {candidates.length === 0 && <div style={{ padding: 14, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, fontSize: 12, color: C.amberDeep }}>
        Nessuno scontrino fiscale trovato per il {ecDateFmt}. Prova ad allargare la ricerca a ±2 giorni.
      </div>}

      {candidates.length > 0 && <div style={{ border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "28px 0.8fr 0.5fr 0.4fr 0.4fr 0.8fr 0.8fr", padding: "8px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.2em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
          <span></span>
          <span>DATA · ORA</span>
          <span style={{ textAlign: "center" }}>TAV</span>
          <span style={{ textAlign: "center" }}>COP</span>
          <span style={{ textAlign: "right" }}>TOTALE</span>
          <span style={{ textAlign: "right" }}>DELTA vs EC</span>
          <span style={{ textAlign: "center" }}>TIPO</span>
        </div>
        <div style={{ maxHeight: 280, overflowY: "auto" }}>
          {candidates.map(r => {
            const delta = (r.total || 0) - ecTotal;
            const absDelta = Math.abs(delta);
            const deltaColor = absDelta < 1 ? C.olive : absDelta < 5 ? C.amber : C.terra;
            const isSelected = selected?.id === r.id;
            const alreadyLinked = (r._hasReconciledEC || []).includes(ec.id);
            return <button key={r.id} onClick={() => setSelected(isSelected ? null : r)} className="tx" style={{
              display: "grid", gridTemplateColumns: "28px 0.8fr 0.5fr 0.4fr 0.4fr 0.8fr 0.8fr",
              padding: "9px 14px", width: "100%", border: "none", borderTop: `1px solid ${C.ruleSoft}`,
              background: isSelected ? C.oliveWash : "transparent",
              borderLeft: isSelected ? `3px solid ${C.olive}` : "3px solid transparent",
              cursor: "pointer", textAlign: "left", fontFamily: "inherit", alignItems: "center", fontSize: 12,
            }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = C.paperDeep; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}>
              <span>{isSelected ? <span style={{ color: C.olive, fontWeight: 700, fontSize: 14 }}>✓</span> : <span style={{ width: 14, height: 14, border: `2px solid ${C.rule}`, borderRadius: 2, display: "inline-block" }}></span>}</span>
              <span className="f-mono" style={{ color: C.ink, fontSize: 11 }}>
                {new Date(r.closedAt).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                {alreadyLinked && <span style={{ marginLeft: 6, background: C.olive, color: C.paper, fontSize: 8, padding: "1px 5px", borderRadius: 2, fontWeight: 700 }}>GIA COLLEGATO</span>}
              </span>
              <span className="f-mono" style={{ textAlign: "center" }}>{r.number}</span>
              <span className="f-mono" style={{ textAlign: "center" }}>{r.covers}</span>
              <span className="f-mono" style={{ textAlign: "right", fontWeight: 600 }}>{fmt.eur(r.total)}</span>
              <span className="f-mono" style={{ textAlign: "right", color: deltaColor, fontWeight: 600 }}>
                {delta >= 0 ? "+" : ""}{fmt.eur(delta)}
              </span>
              <span style={{ textAlign: "center" }}>
                <span style={{ background: r._docType === "FATTURA" ? C.olive : C.teal, color: C.paper, fontSize: 8, padding: "2px 5px", borderRadius: 2, fontWeight: 700, fontFamily: "JetBrains Mono, monospace" }}>
                  {r._docType === "FATTURA" ? "FT" : "SF"}
                </span>
              </span>
            </button>;
          })}
        </div>
      </div>}

      <div>
        <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.muted, marginBottom: 6 }}>NOTE DI RICONCILIAZIONE (opzionale)</div>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value.slice(0, 200))}
          placeholder="Es: confermato da Andi, scontrino emesso il giorno dopo da terminale backup"
          style={{ width: "100%", padding: "8px 12px", border: `1px solid ${C.rule}`, borderRadius: 2, fontSize: 12, fontFamily: "inherit", resize: "vertical", minHeight: 60, background: C.white, boxSizing: "border-box" }}
        />
        <div style={{ textAlign: "right", fontSize: 10, color: C.muted, marginTop: 2 }}>{note.length}/200</div>
      </div>
    </div>
  </Modal>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║  NonFiscalSection — sezione EC orfani con azioni inline                   ║
// ║  Mostra i receipt _isPreconto non ancora riconciliati.                     ║
// ║  Inserita in VenditeOverview quando ci sono EC non riconciliati.           ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function NonFiscalSection({ allReceipts, state, setState }) {
  const toast = useToast();
  const [reconcileTarget, setReconcileTarget] = useState(null); // receipt EC da riconciliare
  const [confirmDefId, setConfirmDefId] = useState(null);       // id EC da confermare come nero

  // EC orfani: _isPreconto o ESTRATTO CONTO, e senza _regularizedTo
  const orphanECs = useMemo(() => {
    return (allReceipts || [])
      .filter(r => (r._isPreconto || r._docType === "ESTRATTO CONTO") && !r._regularizedTo)
      .sort((a, b) => new Date(b.closedAt) - new Date(a.closedAt));
  }, [allReceipts]);

  if (orphanECs.length === 0) return null;

  const totalOrphan = orphanECs.reduce((s, r) => s + (r.total || 0), 0);
  const totalCovers = orphanECs.reduce((s, r) => s + (r.covers || 0), 0);

  const handleConfirmNero = (ecId) => {
    const operator = state.currentUser?.name || "Andi";
    const ts = nowIso();
    setState(s => {
      const updated = (s.tableReceipts || []).map(r =>
        r.id === ecId
          ? { ...r, _regularizedTo: "NERO_DEFINITIVO", _regularizedAt: ts, _regularizedBy: operator }
          : r
      );
      const next = { ...s, tableReceipts: updated };
      return {
        ...next,
        auditLog: appendAudit(next, {
          action: "nf_confirmed_nero",
          entityType: "receipt",
          entityId: ecId,
          by: operator,
          reason: "EC confermato come nero definitivo",
        }),
      };
    });
    toast({ type: "info", title: "EC confermato", message: "Marcato come nero definitivo. Tracciato in auditLog." });
    setConfirmDefId(null);
  };

  return <>
    {reconcileTarget && <ReconcileNFModal ec={reconcileTarget} state={state} setState={setState} onClose={() => setReconcileTarget(null)} />}

    <div style={{ border: `2px solid ${C.terra}`, borderRadius: 2, background: C.terraWash + "66", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "12px 16px", background: C.terraWash, borderBottom: `1px solid ${C.terra}`, display: "flex", alignItems: "flex-start", gap: 12 }}>
        <AlertTriangle size={18} color={C.terra} style={{ marginTop: 1, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div className="f-display" style={{ fontSize: 15, fontWeight: 600, color: C.terraDeep }}>
            Documenti non fiscalizzati · {orphanECs.length} EC orfani · {fmt.eur(totalOrphan)}{totalCovers > 0 ? ` · ${totalCovers} coperti` : ""}
          </div>
          <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, marginTop: 3, lineHeight: 1.5 }}>
            ESTRATTO CONTO = chiusure senza emissione scontrino fiscale. Conteggiati nell'incasso totale ma fuori dall'incasso fiscale.
            Se uno di questi e stato regolarizzato con uno scontrino emesso separatamente, puoi collegarlo qui sotto.
          </div>
        </div>
      </div>

      {/* Lista EC orfani */}
      {orphanECs.map(ec => {
        const dateFmt = new Date(ec.closedAt).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
        const timeFmt = new Date(ec.closedAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
        const isConfirming = confirmDefId === ec.id;
        return <div key={ec.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", borderTop: `1px solid ${C.terra}44`, background: "white" }}>
          <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: "4px 20px", alignItems: "center" }}>
            <span className="f-mono" style={{ fontSize: 12, color: C.ink, fontWeight: 600 }}>{dateFmt} {timeFmt}</span>
            <span className="f-mono" style={{ fontSize: 11, color: C.muted }}>tav. {ec.number || "—"}</span>
            {(ec.covers || 0) > 0 && <span className="f-mono" style={{ fontSize: 11, color: C.muted }}>{ec.covers} cop.</span>}
            <span className="f-mono" style={{ fontSize: 13, color: C.terra, fontWeight: 700 }}>{fmt.eur(ec.total)}</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
            <Btn size="xs" variant="ghost" onClick={() => setReconcileTarget(ec)} style={{ borderColor: C.olive, color: C.oliveDeep }}>
              Collega a SF
            </Btn>
            {isConfirming
              ? <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span className="f-mono" style={{ fontSize: 10, color: C.terra }}>Confermi nero definitivo?</span>
                  <Btn size="xs" variant="danger" onClick={() => handleConfirmNero(ec.id)}>Si</Btn>
                  <Btn size="xs" variant="ghost" onClick={() => setConfirmDefId(null)}>No</Btn>
                </div>
              : <Btn size="xs" variant="ghost" onClick={() => setConfirmDefId(ec.id)} style={{ color: C.muted }}>
                  Conferma definitivo
                </Btn>
            }
          </div>
        </div>;
      })}
    </div>
  </>;
}

function ZucchettiImporter({ state, setState, onClose }) {
  const [step, setStep] = useState("upload");        // upload | review | conflict | done
  const [progress, setProgress] = useState(null);
  const [parsed, setParsed] = useState(null);         // { receipts, articlesUnmatched, dateRange, fileName }
  const [conflicts, setConflicts] = useState([]);     // scontrini già presenti (re-import)
  const [conflictAction, setConflictAction] = useState("replace");  // replace | skip
  const fileRef = useRef();
  const toast = useToast();

  // Parse data italiana (gg/mm/aaaa, dd-mm-yyyy, ISO, Excel serial)
  const parseItDate = (s) => {
    if (s == null || s === "") return null;
    if (typeof s === "number" && s > 20000 && s < 80000) {
      const d = new Date(Date.UTC(1899, 11, 30) + s * 86400000);
      return d.toISOString().slice(0, 10);
    }
    const t = String(s).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
    const m = t.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})/);
    if (m) {
      const d = m[1].padStart(2, "0");
      const mo = m[2].padStart(2, "0");
      let y = m[3];
      if (y.length === 2) y = "20" + y;
      return `${y}-${mo}-${d}`;
    }
    return null;
  };

  // FIX F-01 (audit 2026-05-19): parseHour ora accetta sia ":" che "." come separatore ore/minuti.
  // Bug originale: regex /(\d{1,2}):(\d{2})/ non matchava "13.25" (formato TCPOS alcune config),
  // causando fallback "12:30" su TUTTI gli scontrini → pranzo e cena dello stesso tavolo venivano
  // fusi in un unico scontrino errato. Aggiunta anche validazione range h 0-23 / min 0-59.
  // Business impact: perdita di granularità per-turno e food cost sballato (qty raddoppiate per piatto).
  const parseHour = (s) => {
    if (!s && s !== 0) return "12:30";
    const t = String(s).trim();
    // Formati supportati: HH:MM, HH:MM:SS, HH.MM, HH.MM.SS
    const m = t.match(/^(\d{1,2})[:\.](\d{2})/);
    if (m) {
      const h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      if (h >= 0 && h <= 23 && min >= 0 && min <= 59) {
        return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
      }
    }
    // Excel decimal time: 0.5 = 12:00, 0.5625 = 13:30
    if (/^0?\.\d+$/.test(t)) {
      const frac = parseFloat(t);
      if (Number.isFinite(frac) && frac >= 0 && frac < 1) {
        const totalMinutes = Math.round(frac * 1440);
        const h = Math.floor(totalMinutes / 60);
        const min = totalMinutes % 60;
        return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
      }
    }
    return "12:30";
  };

  // parseItNumber robusto: numerici italiani (1.234,56), americani (1,234.56),
  // negativi tra parentesi (12,50) → -12.50, valute € EUR, NBSP/spazi, BOM.
  // Regola fondamentale: l'ULTIMO separatore (. o ,) e' il decimale.
  const parsePrice = (v) => {
    if (v == null) return 0;
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;
    let s = String(v).trim();
    if (!s) return 0;
    // Strip BOM eventuale
    if (s.charCodeAt(0) === 0xFEFF) s = s.slice(1);
    // Negativi tra parentesi stile contabile: (12,50) → -12.50
    const isNegative = /^\(.*\)$/.test(s);
    s = s.replace(/[()]/g, "").replace(/\s| /g, "").replace(/€|EUR/gi, "");
    if (!s) return 0;
    const lastDot = s.lastIndexOf(".");
    const lastComma = s.lastIndexOf(",");
    if (lastDot > -1 && lastComma > -1) {
      if (lastComma > lastDot) {
        // Italiano: 1.234,56 — i punti sono migliaia
        s = s.replace(/\./g, "").replace(",", ".");
      } else {
        // US: 1,234.56 — le virgole sono migliaia
        s = s.replace(/,/g, "");
      }
    } else if (lastComma > -1) {
      // Solo virgola: italiano, decimale
      s = s.replace(/\./g, "").replace(",", ".");
    }
    // Solo punto o nessun separatore: già parseFloat-friendly (12.50 è 12.50)
    const n = parseFloat(s);
    if (!Number.isFinite(n)) return 0;
    return isNegative ? -n : n;
  };

  // Detect colonne Zucchetti TCPOS - case insensitive
  const detectColumns = (headers) => {
    const norm = headers.map(h => normStr(h));
    const find = (...patterns) => {
      for (const pat of patterns) {
        const i = norm.findIndex(h => h && new RegExp(pat, "i").test(h));
        if (i >= 0) return i;
      }
      return -1;
    };
    // Nota: i pattern usano \\s* per gestire header concatenati senza spazio.
    // normStr rimuove punteggiatura ma NON separa CamelCase, quindi
    // "NumeroDocumento" → "numerodocumento" (un'unica parola): "numero\\s*documento" matcha.
    return {
      date: find("data\\s*scontrino", "data\\s*chiusura", "^data$", "^data"),
      time: find("ora\\s*chiusura", "ora\\s*scontrino", "^ora$", "orario"),
      table: find("n[\\s\\.]*tavolo", "numero\\s*tavolo", "tavolo", "^tav"),
      covers: find("n[\\s\\.]*coperti", "^coperti$", "^pax", "persone"),
      dishName: find("articolo", "descrizione\\s*articolo", "^descrizione$", "prodotto"),
      qty: find("quantita", "q\\s*ta", "^qta$", "^qty$", "pezzi"),
      price: find("prezzo\\s*unitario", "^prezzo$", "prezzo", "importo\\s*unitario", "importo", "totale\\s*riga"),
      total: find("totale\\s*scontrino", "totale\\s*documento"),
      reparto: find("^categoria$", "reparto", "categoria"),
      // Priorità a "documento" (formato NIALTRI/TCPOS standard ristorazione 2026).
      receiptNum: find("numero\\s*documento", "n[\\s\\.]*documento", "numero\\s*scontrino", "n[\\s\\.]*scontrino", "scontrino"),
      // TipoDocumento: SCONTRINO FISCALE / FATTURA / ESTRATTO CONTO — distingue preconti dalle vendite chiuse
      docType: find("tipo\\s*documento", "tipo\\s*doc", "^tipo$"),
    };
  };

  const handleFile = async (file) => {
    // FIX F-08 (audit 2026-05-19): rifiuto esplicito file >50MB prima di allocare RAM o
    // chiamare arrayBuffer(). Un file XLSX di quella dimensione farebbe crashare la scheda
    // browser senza avviso. TCPOS non produce mai file giornalieri > qualche MB: se supera
    // 50MB significa che l'utente ha caricato il file sbagliato o un dump annuale.
    if (file.size > 50 * 1024 * 1024) {
      toast({ type: "error", title: "File troppo grande", message: "Il file supera 50 MB. Esporta dal gestionale in chunk più piccoli (es. per giornata o settimana)." });
      return;
    }
    setProgress("Caricamento file...");
    try {
      const fileName = file.name;
      const ext = fileName.toLowerCase().split('.').pop();
      let rows = [];

      if (ext === "xls" || ext === "xlsx") {
        // Carica SheetJS (CDN con fallback bundlato per l'offline)
        const XLSX = await loadXLSX();
        setProgress("Lettura foglio Excel...");
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array", cellDates: false });
        // Cerca un foglio sensato
        let sheetName = wb.SheetNames.find(n => /movimenti|scontrini|vendite/i.test(n)) || wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: true });
      } else {
        // CSV — delimitatore auto-detect (Zucchetti italiano usa ;)
        // Encoding detection: UTF-8 strict, fallback Windows-1252 (Excel italiano legacy)
        setProgress("Lettura CSV...");
        let text;
        try {
          const buf = await file.arrayBuffer();
          try {
            text = new TextDecoder("utf-8", { fatal: true }).decode(buf);
          } catch {
            text = new TextDecoder("windows-1252").decode(buf);
          }
          // Strip BOM se presente (UTF-8 BOM = U+FEFF)
          if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
        } catch {
          text = await file.text();
        }
        const p = parseCSV(text);
        rows = p.rows;
      }

      if (rows.length < 2) throw new Error("File vuoto o senza dati");

      setProgress("Analisi colonne...");
      const headers = (rows[0] || []).map(h => String(h ?? ""));
      const cols = detectColumns(headers);

      // Validazione: servono almeno data, articolo
      if (cols.date < 0) throw new Error("Colonna 'Data' non trovata. Intestazioni viste: " + headers.join(", "));
      if (cols.dishName < 0) throw new Error("Colonna 'Articolo/Descrizione' non trovata. Intestazioni viste: " + headers.join(", "));

      setProgress("Costruzione scontrini...");

      // ─── Pre-scan VARIANTE: mezze porzioni ────────────────────────────────
      // TCPOS emette righe modificatori con Data="VARIANTE" (categoria VARIANTE).
      // Queste righe non hanno data valida → parseItDate restituisce null → venivano
      // silenziosamente scartate. In realtà portano informazioni critiche:
      // "MEZZA PORZ. SEC/PRIM/ANT" = il cliente ha ordinato mezza porzione e il
      // cassiere DEVE applicare uno sconto manuale a valore sul documento precedente.
      // Se lo sconto manca, TCPOS incassa il prezzo pieno → sovrastima fatturato.
      //
      // Convenzione di dominio Andi (confermata 2026-05-20):
      //   MEZZA PORZ. SEC  → sconto atteso -€4,00
      //   MEZZA PORZ. PRIM → sconto atteso -€2,00
      //   MEZZA PORZ. ANT  → sconto atteso -€2,00
      // Valori configurabili in Impostazioni → Regole mezze porzioni.
      const mezzePorzioniRules = state.settings?.mezzePorzioniRules || { SEC: 4, PRIM: 2, ANT: 2 };
      const isMezzaPorzioneLabel = (s) => /mezza\s*porz(\.|ione)?\s*(sec|prim|ant)/i.test(String(s || ""));
      const getMezzaPorzioneType = (s) => {
        const t = String(s || "").toUpperCase();
        if (/SEC/.test(t)) return "SEC";
        if (/PRIM/.test(t)) return "PRIM";
        if (/ANT/.test(t)) return "ANT";
        return null;
      };

      // Helper chiave scontrino riusato sia nel pre-scan che nel loop principale.
      //
      // FIX P0-C (2026-06-10): TCPOS riusa i numeri documento nell'arco della stessa
      // giornata di file quando la sessione notturna (00:00–03:59 = cena del giorno
      // precedente) e il pranzo successivo sono entrambi inclusi nell'export.
      // Esempio reale 06/06: doc R49/R50/R51 appaiono alle 00:00-00:04 (cena fine 05/06)
      // e di nuovo alle 15:36-15:42 (pranzo 06/06). La chiave `fileDate|docKind|Rnum`
      // collideva → gli items del pranzo venivano sommati al receipt della cena notturna,
      // perdendo €222.20 di incasso e 5 coperti dal pranzo.
      //
      // Fix: aggiungo un tag di turno orario alla chiave per i doc numerati:
      //   N = notturno (00:00–03:59) → cena del giorno precedente
      //   D = diurno (04:00–17:59)   → pranzo (o fuori servizio mattina)
      //   E = serale (18:00–23:59)   → cena
      // I doc con stesso numero ma turno diverso ottengono chiavi distinte.
      // Le chiavi rimangono locali alla Map del parser (non persistite in localStorage).
      const buildReceiptKey = (row, rowIdx) => {
        const dateRaw = row[cols.date];
        const dateISO = parseItDate(dateRaw);
        if (!dateISO) return null;
        const time = cols.time >= 0 ? parseHour(row[cols.time]) : "12:30";
        const rawNum = cols.receiptNum >= 0 ? String(row[cols.receiptNum] ?? "").trim() : "";
        const rawTav = cols.table >= 0 ? String(row[cols.table] ?? "").trim() : "";
        const tipoDoc = cols.docType >= 0 ? String(row[cols.docType] ?? "").trim().toUpperCase() : "";
        const isPre = tipoDoc === "ESTRATTO CONTO";
        const docKind = isPre ? "EC" : (tipoDoc === "FATTURA" ? "FT" : "SF");
        const hourKey = time.slice(0, 5);
        // Tag di turno: separa doc con stesso numero ma orari di turni diversi
        const h = parseInt(time.slice(0, 2), 10);
        const turnTag = h < 4 ? "N" : h < 18 ? "D" : "E";
        if (rawNum && rawNum !== "0") return `${dateISO}|${docKind}|R${rawNum}|${turnTag}`;
        if (rawTav && rawTav !== "0") return `${dateISO}|${docKind}|T${rawTav}|${hourKey}`;
        return `${dateISO}|${docKind}|BNC|${rowIdx}`;
      };

      // Scansiona l'intero file: mantieni "ultimo doc visto" per collegare le VARIANTE
      // al documento TCPOS che le precede immediatamente.
      const mezzePorzioniByDoc = new Map();  // receiptKey → [{ label, tipo, scontoAtteso, articoloTarget, prezzoTarget }]
      let lastReceiptKey = null;
      let lastArticoloNonSconto = null;
      let lastPrezzoNonSconto = 0;
      for (let i = 1; i < rows.length; i++) {
        const r = rows[i];
        if (!r || r.length === 0) continue;
        const dateRaw = r[cols.date];
        const nomeRaw = String(r[cols.dishName] ?? "").trim();
        const tipoDocRaw = cols.docType >= 0 ? String(r[cols.docType] ?? "").trim() : "";

        if (String(dateRaw ?? "").trim() === "VARIANTE" || normStr(String(dateRaw ?? "")) === "variante") {
          // Riga modificatore TCPOS. Controlla se è una mezza porzione.
          // Il nome della mezza porzione può stare in TipoDocumento o in Articolo.
          const candidateLabel = isMezzaPorzioneLabel(tipoDocRaw) ? tipoDocRaw
                               : isMezzaPorzioneLabel(nomeRaw) ? nomeRaw : null;
          if (candidateLabel && lastReceiptKey) {
            const tipo = getMezzaPorzioneType(candidateLabel);
            const scontoAtteso = tipo ? (mezzePorzioniRules[tipo] || 0) : 0;
            if (!mezzePorzioniByDoc.has(lastReceiptKey)) mezzePorzioniByDoc.set(lastReceiptKey, []);
            mezzePorzioniByDoc.get(lastReceiptKey).push({
              label: candidateLabel,
              tipo,
              scontoAtteso,
              articoloTarget: lastArticoloNonSconto,
              prezzoTarget: lastPrezzoNonSconto,
            });
          }
          continue;  // non aggiorna lastReceiptKey (il prossimo VARIANTE rimane agganciato allo stesso doc)
        }

        // Riga normale: aggiorna l'ultimo doc visto
        const key = buildReceiptKey(r, i);
        if (key) {
          lastReceiptKey = key;
          // Teniamo l'ultimo articolo non-sconto per il contesto (debug/audit)
          if (nomeRaw && !/sconto/i.test(nomeRaw) && !/variante/i.test(tipoDocRaw)) {
            lastArticoloNonSconto = nomeRaw;
            lastPrezzoNonSconto = cols.price >= 0 ? parsePrice(r[cols.price]) : 0;
          }
        }
      }
      // ─────────────────────────────────────────────────────────────────────

      // Costruzione: ogni riga = 1 piatto venduto. Raggruppo per (data + tavolo + ora_arrotondata)
      const dishes = state.dishesReal || [];
      const aliases = state.dishAliases || {};
      const newAliases = { ...aliases };

      const receiptsMap = new Map();  // key = "data|tavolo|ora" → receipt
      const articlesUnmatched = new Map();  // dishName → count

      // FIX P0-B (audit 2026-05-20): dedup INTRA-FILE.
      // Set di fingerprint riga = (dateISO|docKey|nomeArt|qty|price|reparto).
      // Se TCPOS in configurazione anomala esporta la stessa riga 2 volte nello
      // stesso file (data entry bug documentato), il vecchio parser accumulava
      // qty/totale/coperti senza alcun avviso → fatturato e coperti raddoppiati.
      // Ora ogni riga col fingerprint già visto viene scartata + counter visibile
      // nel banner post-import. Il dedup FILE-LEVEL (importHistory hash) era già
      // presente, ma non gestiva duplicati DENTRO lo stesso file.
      const rowFingerprints = new Set();
      let duplicateRowCount = 0;

      // Map ausiliaria: per ogni scontrino raccolgo l'eventuale totale dichiarato
      // dalle colonne "Totale Scontrino/Documento" (max delle righe del gruppo: tipicamente è ripetuto).
      const declaredTotalsByKey = new Map();
      for (let i = 1; i < rows.length; i++) {
        const r = rows[i];
        if (!r || r.length === 0) continue;
        const dateRaw = r[cols.date];
        const dateISO = parseItDate(dateRaw);
        if (!dateISO) continue;
        const time = cols.time >= 0 ? parseHour(r[cols.time]) : "12:30";
        const rawReceiptNum = cols.receiptNum >= 0 ? String(r[cols.receiptNum] ?? "").trim() : "";
        const rawTable = cols.table >= 0 ? String(r[cols.table] ?? "").trim() : "";

        // covers: 0 è banco legittimo (asporto / banco), NON deve diventare 1
        const coversRaw = cols.covers >= 0 ? r[cols.covers] : null;
        const coversNum = Number(parseInt(coversRaw, 10));
        const covers = Number.isFinite(coversNum) ? coversNum : 0;

        const nomeArt = String(r[cols.dishName] ?? "").trim();
        if (!nomeArt) continue;
        const qty = cols.qty >= 0 ? (parsePrice(r[cols.qty]) || 1) : 1;
        const price = cols.price >= 0 ? parsePrice(r[cols.price]) : 0;
        // Reparto -> categoria sistema (best effort)
        const reparto = cols.reparto >= 0 ? String(r[cols.reparto] ?? "").trim() : "";
        // TipoDocumento: SCONTRINO FISCALE / FATTURA / ESTRATTO CONTO (non fiscalizzato).
        // Normalizzo upper-case per match robusto su varianti TCPOS ("Scontrino Fiscale" vs "SCONTRINO FISCALE").
        const tipoDoc = cols.docType >= 0 ? String(r[cols.docType] ?? "").trim().toUpperCase() : "";
        const isPreconto = tipoDoc === "ESTRATTO CONTO";

        // Riga COPERTO (categoria "COPERTI" + descrizione che inizia con "COPERTO"):
        // l'export NIALTRI/TCPOS non ha una colonna coperti dedicata — i coperti sono
        // articoli virtuali con qty = numero di persone, prezzo 0. Vanno usati per
        // alimentare receipt.covers, NON aggiunti come item (sporcerebbero il count piatti).
        // "MENU CONCORDATO" (categoria COPERTI ma prezzo > 0) NON è un coperto: resta item.
        const repNorm = normStr(reparto);
        const isCovertoLine = repNorm === "coperti" && /^coperto\b/i.test(nomeArt);

        // Chiave scontrino: priorità a numero scontrino fiscale (univoco per giornata),
        // fallback a tavolo+ora, ultima risorsa banco indicizzato (mai più "tav-?").
        // Includo tipoDoc nella chiave per non fondere ESTRATTO CONTO + SCONTRINO FISCALE
        // dello stesso tavolo con stesso doc (numerazione TCPOS può sovrapporsi tra tipi).
        const hourKey = time.slice(0, 5);
        const docKind = isPreconto ? "EC" : (tipoDoc === "FATTURA" ? "FT" : "SF");
        let key;
        let displayNumber;
        let isBanco;
        // FIX 2026-06-10: turn-tag anche nel LOOP PRINCIPALE (deve combaciare col
        // pre-scan buildReceiptKey, altrimenti l'annotazione mezze porzioni non trova
        // il receipt). TCPOS riusa i numeri documento tra turni diversi nello stesso
        // file (es. doc 50 alle 00:03 cena-di-ieri E alle 15:36 pranzo): senza tag
        // collidono e le righe del secondo turno vengono fuse nel primo → incasso/coperti
        // persi dal giorno giusto. N=00-03 (cena ieri), D=04-17 (pranzo), E=18-23 (cena).
        const turnTag = (() => { const h = parseInt(time.slice(0, 2), 10); return h < 4 ? "N" : h < 18 ? "D" : "E"; })();
        if (rawReceiptNum && rawReceiptNum.length > 0 && rawReceiptNum !== "0") {
          key = `${dateISO}|${docKind}|R${rawReceiptNum}|${turnTag}`;
          displayNumber = rawTable && rawTable !== "0" ? rawTable : `R${rawReceiptNum}`;
          isBanco = !rawTable || rawTable === "0" || normStr(rawTable) === "banco";
        } else if (rawTable && rawTable !== "0") {
          key = `${dateISO}|${docKind}|T${rawTable}|${hourKey}`;
          displayNumber = rawTable;
          isBanco = normStr(rawTable) === "banco";
        } else {
          // Banco senza identificatore univoco: usa indice riga → MAI collassare in "tav-?"
          // NOTA: l'indice non è riproducibile tra ri-esportazioni con ordine righe diverso.
          // Non lo rendiamo content-based per non disallineare la pre-scan buildReceiptKey
          // (aggancio mezze porzioni): la dedup re-import resta affidata all'hash file intero.
          key = `${dateISO}|${docKind}|BNC|${i}`;
          displayNumber = `bnc-${i}`;
          isBanco = true;
        }

        // FIX P0-B (audit 2026-05-20): conteggio righe duplicate intra-file.
        // ATTENZIONE: NON scarto automaticamente. Una "riga duplicata" puo' essere:
        //  (a) bug TCPOS data entry → andrebbe scartata
        //  (b) vendita legittima di articoli identici (es. 2 calici stesso vino) → va tenuta
        // Non possiamo distinguere automaticamente senza falsi positivi (NIALTRI ha ~150
        // righe legittime "duplicate" tipo 2 CAL ROSSO €5). Quindi conto le occorrenze e
        // mostro un avviso ad Andi in UI banner post-import. Se il count e' anomalo
        // (es. centinaia di duplicati su un file da 3000 righe), Andi sa che c'e' un
        // problema di esportazione TCPOS e puo' agire (es. riesportare).
        const rowFp = `${key}|${normStr(nomeArt)}|${qty}|${price}|${normStr(reparto)}`;
        if (rowFingerprints.has(rowFp)) {
          duplicateRowCount++;
          // NON continue — la riga viene comunque processata
        } else {
          rowFingerprints.add(rowFp);
        }

        if (!receiptsMap.has(key)) {
          receiptsMap.set(key, {
            id: uid("rcp_"),
            number: displayNumber,
            covers: 0,  // verrà incrementato dalle righe COPERTO Pranzo/Cena
            openedAt: `${dateISO}T${time}:00`,
            closedAt: `${dateISO}T${time}:00`,
            items: [],
            total: 0,
            notes: `Importato da Zucchetti TCPOS · ${fileName}`,
            operator: "import",
            _imported: true,
            _importBatch: nowIso(),
            _sourceFile: fileName,
            ...(rawReceiptNum ? { fiscalNumber: rawReceiptNum } : {}),
            ...(tipoDoc ? { _docType: tipoDoc } : {}),
            ...(isPreconto ? { _isPreconto: true } : {}),
            isBanco,
          });
        }
        const receipt = receiptsMap.get(key);

        // Coperti: se è una riga "COPERTO Pranzo/Cena", sommo qty (numero persone)
        // e SALTO l'aggiunta agli items (sono coperti virtuali, non piatti venduti).
        if (isCovertoLine) {
          receipt.covers += qty;
          continue;
        }
        // Fallback legacy: se l'export avesse comunque una colonna coperti popolata,
        // tieni il max (gestione retrocompatibile con altri setup TCPOS).
        if (covers > receipt.covers) receipt.covers = covers;

        // Totale dichiarato (riga "Totale Scontrino/Documento"): salvo il max per gruppo.
        // TCPOS può ripeterlo o ometterlo a seconda della config; max è robusto.
        if (cols.total >= 0) {
          const declaredRaw = parsePrice(r[cols.total]);
          if (Number.isFinite(declaredRaw) && declaredRaw > 0) {
            const prev = declaredTotalsByKey.get(key) || 0;
            if (declaredRaw > prev) declaredTotalsByKey.set(key, declaredRaw);
          }
        }

        // Match piatto
        const match = matchDishFuzzy(nomeArt, dishes, newAliases);
        let dishId = null;
        let dishNameClean = nomeArt;
        if (match) {
          dishId = match.dish.id;
          dishNameClean = match.dish.nome;
          if (match.source !== "alias") {
            newAliases[normStr(nomeArt)] = match.dish.id;
          }
        } else {
          // Articolo sconosciuto
          articlesUnmatched.set(nomeArt, (articlesUnmatched.get(nomeArt) || 0) + qty);
          // Crea riga "fantasma" usando il nome del CSV come dishId (placeholder)
          dishId = `_unmatched_${normStr(nomeArt).replace(/\s/g, "_")}`;
        }
        // Articolo "regalo" (SESCIALA & co.): unitPrice forzato a 0 sull'item.
        // Il prezzo CSV originale resta in _csvUnitPrice per audit. L'item conta
        // come consumo MP (la bottiglia è pur sempre uscita dalla cantina) ma
        // NON contribuisce all'incasso del receipt.
        const isGift = isGiftItem(nomeArt);
        const effectivePrice = isGift ? 0 : price;

        // _csvReparto: conserva il reparto CSV originale per fallback categoria.
        // Critico per articoli _unmatched_*: senza questo finivano in categoria "altro"
        // e venivano persi da portatePerCop, mixPctNet, pctMenuCompleto.
        // Vedi lineCat in engine.computeKpis (~1301): fallback su catFromReparto(_csvReparto).
        receipt.items.push({
          dishId,
          name: dishNameClean,
          qty,
          unitPrice: effectivePrice,
          note: "",
          _csvName: nomeArt !== dishNameClean ? nomeArt : undefined,
          ...(reparto ? { _csvReparto: reparto } : {}),
          ...(isGift ? { _isGift: true, _csvUnitPrice: price } : {}),
          // Doc fiscale di origine: serve nel viewer per breakdown post-merge
          // (Andi può vedere quale doc TCPOS ha portato quale item dopo la fusione MENU CONCORDATO).
          ...(rawReceiptNum ? { _sourceFiscalNumber: rawReceiptNum } : {}),
        });
        receipt.total += qty * effectivePrice;
      }

      // ─── Annota mezze porzioni sui receipt (post-loop, pre-merge) ─────────
      // Policy Andi 2026-05-20: le mezze porzioni sono un PREZZO DI VENDITA
      // differente, non uno sconto cassa. Il parser applica AUTOMATICAMENTE
      // il prezzo ridotto all'articolo target:
      //  1. Se il doc TCPOS contiene già uno sconto coerente (±€0,01) →
      //     lascia invariato (il cassiere lo ha già applicato).
      //  2. Se NON c'è sconto coerente → modifica direttamente item.unitPrice
      //     sottraendo il valore mezza porzione e ricalcola receipt.total.
      // Nessun _mezzeError. Le mezze porzioni sono sempre "risolte".
      const isDiscountItem = (it) => /sconto/i.test(it.name || "") || (Number(it.unitPrice) < 0);
      for (const [rKey, receipt] of receiptsMap.entries()) {
        const mezzeLst = mezzePorzioniByDoc.get(rKey);
        if (!mezzeLst || mezzeLst.length === 0) continue;

        // Raccoglie i valori assoluti degli sconti presenti nel documento
        const scontiPresenti = (receipt.items || [])
          .filter(isDiscountItem)
          .map(it => Math.abs((Number(it.qty) || 1) * (Number(it.unitPrice) || 0)));

        const annotated = [];
        // Ogni mezza porzione "consuma" al massimo uno sconto coerente.
        // scontiConsumati evita il doppio matching su più mezze nello stesso doc.
        const scontiConsumati = new Set();

        for (const mp of mezzeLst) {
          // Cerca uno sconto coerente NON ancora attribuito
          const idxCoerente = scontiPresenti.findIndex((sv, i) =>
            !scontiConsumati.has(i) && Math.abs(sv - mp.scontoAtteso) <= 0.01
          );

          if (idxCoerente >= 0) {
            // Sconto già applicato dal cassiere: lascia il prezzo invariato
            scontiConsumati.add(idxCoerente);
            annotated.push({ ...mp, alreadyDiscounted: true, appliedAutomatically: false });
          } else {
            // Sconto MANCANTE: applica automaticamente al prezzo dell'articolo target.
            // ATTENZIONE: articoloTarget è il nome GREZZO del CSV (es. "FRITTURA NO SPIN"),
            // mentre it.name è il nome PULITO dopo il fuzzy match (es. "FRITTURA NO SPINE").
            // Se il match ha rinominato l'articolo, il confronto su it.name fallisce → la
            // mezza porzione non veniva applicata (bug: tutte le mezze su articoli rinominati
            // dal fuzzy match restavano a prezzo intero). Confronta anche it._csvName, che
            // conserva il nome originale del CSV proprio quando differisce dal nome pulito.
            const targetMatchesName = (it) =>
              normStr(it.name) === normStr(mp.articoloTarget) ||
              (it._csvName && normStr(it._csvName) === normStr(mp.articoloTarget));
            const targetIdx = receipt.items.findIndex(it =>
              !it._mezzaPorzioneType &&
              !isDiscountItem(it) &&
              targetMatchesName(it)
            );

            if (targetIdx >= 0) {
              const item = receipt.items[targetIdx];
              const oldPrice = Number(item.unitPrice) || 0;
              const qty = Number(item.qty) || 1;

              if (qty > 1) {
                // qty > 1: separa 1 porzione (mezza) dal resto (intero)
                receipt.items[targetIdx] = { ...item, qty: qty - 1 };
                receipt.items.push({
                  ...item,
                  qty: 1,
                  unitPrice: Math.max(0, oldPrice - mp.scontoAtteso),
                  _mezzaPorzioneType: mp.tipo,
                  _originalPrice: oldPrice,
                  _mezzaSconto: mp.scontoAtteso,
                });
                receipt.total = receipt.total - oldPrice + Math.max(0, oldPrice - mp.scontoAtteso);
              } else {
                // qty === 1: modifica diretta in-place
                const newPrice = Math.max(0, oldPrice - mp.scontoAtteso);
                receipt.items[targetIdx] = {
                  ...item,
                  unitPrice: newPrice,
                  _mezzaPorzioneType: mp.tipo,
                  _originalPrice: oldPrice,
                  _mezzaSconto: mp.scontoAtteso,
                };
                receipt.total = receipt.total - (oldPrice * qty) + (newPrice * qty);
              }
              annotated.push({
                ...mp,
                alreadyDiscounted: false,
                appliedAutomatically: true,
                oldPrice,
                newPrice: Math.max(0, oldPrice - mp.scontoAtteso),
              });
            } else {
              // Target non trovato (nome articolo cambiato dal pre-scan?) — traccia ma non modifica
              annotated.push({ ...mp, alreadyDiscounted: false, appliedAutomatically: false, targetNotFound: true });
            }
          }
        }

        receipt._mezzePorzioni = annotated;
        // NB: nessun _mezzeError — le mezze porzioni non sono più un errore cassa.
      }
      // ─────────────────────────────────────────────────────────────────────

      // Fusione MENU CONCORDATO: TCPOS frammenta i menu fissi su N scontrini
      // distinti (1 per ciascun coperto del menu) + 1 scontrino "regalo" con gli
      // articoli effettivamente serviti a prezzo 0. Per analytics serve UN solo
      // documento logico per sessione tavolo. Cosi' il calcolo coperti,
      // articoli/scontrino, sconto% lavora su una vera unita' di vendita.
      //
      // Algoritmo CLUSTER GREEDY (fix audit punto 1a + 5):
      //  1. Raggruppa per (data + tavolo) — nessun bucket fisso, solo per dimezzare lo spazio di ricerca.
      //  2. Ordina i receipt del gruppo per closedAt crescente.
      //  3. Cluster greedy: due receipt adiacenti finiscono nello stesso cluster
      //     SE il loro gap è ≤ MAX_GAP_MIN (15 min, vedi fix sotto). Se gap >, si apre nuovo cluster.
      //  4. Fonde solo i cluster con ≥2 receipt E almeno una riga MENU CONCORDATO.
      //  5. Coperti: regola robusta (fix F-15): max(sum_covers, menu_qty, max_covers) a seconda di
      //     quanti doc del cluster hanno riga COPERTO esplicita.
      //
      // FIX 2026-05-20: gap aumentato da 8 a 15 min.
      // CASO REALE Andi: tav.1 cena 13/05 — 3 doc TCPOS MENU CONCORDATO (#51 23:20, #52 23:21,
      // #53 23:30). Con gap=8 i primi 2 si fondevano (cluster MENU×2 €118 / 2 cop dedotti) ma il
      // #53 a 9 min restava separato (€59 / 3 cop) → totale FITTIZIO 5 coperti per 3 persone reali.
      // Con gap=15 tutti e 3 i doc si fondono in 1 scontrino logico (€177 / 3 coperti). Verificato
      // sul CSV NIALTRI 12-17/05 che il nuovo gap NON introduce false fusioni (cluster non-menu
      // invariati). Sicurezza aggiuntiva: la fusione avviene comunque solo se hasMenu===true,
      // quindi tavoli con clienti diversi nella stessa fascia oraria senza menu NON si fondono.
      const MAX_GAP_MIN = 15;
      setProgress("Unifico scontrini MENU CONCORDATO...");
      const dayTavGroups = new Map();
      for (const [key, r] of receiptsMap.entries()) {
        const tav = String(r.number || "").trim();
        if (!tav || tav.startsWith("bnc-")) continue;  // banco senza numero: niente merge
        if (normStr(tav) === "banco") continue;  // banco stringa: niente merge (audit 1c)
        const groupKey = `${r.closedAt.slice(0,10)}|T${tav}`;
        if (!dayTavGroups.has(groupKey)) dayTavGroups.set(groupKey, []);
        dayTavGroups.get(groupKey).push({ key, receipt: r });
      }
      let menuMergedCount = 0;
      for (const [gk, all] of dayTavGroups.entries()) {
        if (all.length < 2) continue;
        // Ordina per closedAt crescente (stesso tavolo, stesso giorno)
        all.sort((a, b) => new Date(a.receipt.closedAt) - new Date(b.receipt.closedAt));
        // Costruisci cluster contigui via gap massimo
        const clusters = [];
        let current = [all[0]];
        for (let i = 1; i < all.length; i++) {
          const prev = current[current.length - 1].receipt;
          const curr = all[i].receipt;
          const gapMin = (new Date(curr.closedAt) - new Date(prev.closedAt)) / 60000;
          if (gapMin <= MAX_GAP_MIN) {
            current.push(all[i]);
          } else {
            clusters.push(current);
            current = [all[i]];
          }
        }
        clusters.push(current);
        // Fondi ogni cluster idoneo
        for (const entries of clusters) {
          if (entries.length < 2) continue;
          const hasMenu = entries.some(({ receipt }) => (receipt.items || []).some(isMenuConcordato));
          if (!hasMenu) continue;

          // FIX F-14 rev.2 (2026-06-11): cluster misti EC+SF/FT → fondi SOLO dentro il
          // sotto-gruppo dello stesso docKind. MAI fondere tra docKind diversi.
          //
          // Versione precedente: skip dell'intero cluster misto → i 5 SF MENU CONCORDATO
          // di tav.201 cena 31/05 restavano 5 righe separate da €44,30 ciascuna →
          // sembravano 5 duplicati invece di 1 scontrino unificato €221,50.
          //
          // Sicurezza: non fondendo mai SF↔EC il bug EC#5 (€84 SF assorbito dall'EC)
          // è impossibile per costruzione, non solo per guard.
          //
          // _coversInferred: calcolato a livello CLUSTER intero (se qualsiasi doc del
          // cluster ha covers>0, NESSUN sotto-gruppo riceve il flag — i coperti sono noti).
          const docKindOf = (r) => r._isPreconto ? "EC" : (r._docType === "FATTURA" ? "FT" : "SF");
          const docKinds = new Set(entries.map(({ receipt: r }) => docKindOf(r)));
          const isMixedCluster = docKinds.has("EC") && docKinds.size > 1;

          // Flag coperti a livello cluster: se almeno 1 doc nel cluster intero ha covers>0
          // i coperti sono noti → nessun sotto-gruppo deve ricevere _coversInferred.
          const clusterHasAnyCover = entries.some(({ receipt: r }) => (r.covers || 0) > 0);

          // Helper: esegui merge di un sotto-gruppo di entries (≥2, stesso docKind)
          const foodCount = (rcp) => (rcp.items || []).filter(it => !isMenuConcordato(it)).length;
          const mergeSubgroup = (subEntries) => {
            if (subEntries.length < 2) return;
            const subHasMenu = subEntries.some(({ receipt }) => (receipt.items || []).some(isMenuConcordato));
            if (!subHasMenu) return;

            subEntries.sort((a, b) => {
              const d = foodCount(b.receipt) - foodCount(a.receipt);
              if (d !== 0) return d;
              return (b.receipt.items || []).length - (a.receipt.items || []).length;
            });
            const main = subEntries[0].receipt;
            const fiscalNumbers = [main.fiscalNumber].filter(Boolean);

            // FIX F-15 rev.2 (2026-06-10): coperti SOLO da righe COPERTO reali.
            // Caso A – tutti i doc del sotto-gruppo hanno riga COPERTO: somma.
            // Caso B – solo alcuni: prendi il MAX.
            // Caso C – nessuno nel sotto-gruppo MA il cluster ha coperti su altro docKind:
            //   mergedCovers=0 e NON flaggare _coversInferred (i coperti sono contati sull'EC sibling).
            // Caso D – nessuno nel cluster intero: mergedCovers=0 + _coversInferred=true.
            const subDocsWithCovers = subEntries.filter(({ receipt: r }) => (r.covers || 0) > 0).length;
            const subSumCovers = subEntries.reduce((s, { receipt: r }) => s + (r.covers || 0), 0);
            const subMaxCovers = subEntries.reduce((m, { receipt: r }) => Math.max(m, r.covers || 0), 0);
            let mergedCovers;
            if (subDocsWithCovers === subEntries.length && subEntries.length > 1) {
              mergedCovers = subSumCovers;   // Caso A
            } else if (subDocsWithCovers >= 1) {
              mergedCovers = subMaxCovers;   // Caso B
            } else {
              mergedCovers = 0;              // Caso C o D
            }
            // _coversInferred solo se nessun doc nell'intero cluster ha coperti (Caso D)
            const coversInferred = !clusterHasAnyCover;

            for (let i = 1; i < subEntries.length; i++) {
              const { key: subKey, receipt: sub } = subEntries[i];
              main.items = (main.items || []).concat(sub.items || []);
              main.total = (main.total || 0) + (sub.total || 0);
              if (sub.fiscalNumber) fiscalNumbers.push(sub.fiscalNumber);
              receiptsMap.delete(subKey);
              menuMergedCount++;
            }
            main.covers = mergedCovers;
            if (coversInferred) main._coversInferred = true;
            main._menuConcordatoMerged = subEntries.length;
            if (fiscalNumbers.length > 1) main._mergedFiscalNumbers = fiscalNumbers;
            main.notes = `${main.notes || ""} · Unito ${subEntries.length} scontrini MENU CONCORDATO (doc ${fiscalNumbers.join(",")})`.trim();
          };

          if (isMixedCluster) {
            // Cluster misto: partiziona per docKind e fondi ogni sotto-gruppo separatamente.
            // Non si fonde mai tra docKind diversi → bug €84 impossibile per costruzione.
            const byKind = new Map();
            for (const e of entries) {
              const k = docKindOf(e.receipt);
              if (!byKind.has(k)) byKind.set(k, []);
              byKind.get(k).push(e);
            }
            for (const [, subGroup] of byKind) {
              mergeSubgroup(subGroup);
            }
          } else {
            // Cluster omogeneo (tutti SF, tutti FT, oppure tutti EC): percorso originale.
            mergeSubgroup(entries);
          }
        }
      }

      // Riconciliazione: confronto totale dichiarato (Totale Scontrino TCPOS) vs ricostruito (Σ qty*price).
      // Tolleranza: max(0.5% del ricostruito, €0.05). Sopra → flag _reconcileWarning.
      // Cause comuni di diff: storni, sconti riga non esportati, omaggi, arrotondamenti.
      for (const [key, receipt] of receiptsMap.entries()) {
        const declared = declaredTotalsByKey.get(key);
        if (declared != null && declared > 0) {
          receipt._declaredTotal = declared;
          const reconstructed = receipt.items.reduce(
            (s, it) => s + (Number(it.qty) || 0) * (Number(it.unitPrice) || 0),
            0
          );
          const diff = declared - reconstructed;
          const tolerance = Math.max(0.05, reconstructed * 0.005);
          if (Math.abs(diff) > tolerance) {
            receipt._reconcileWarning = true;
            receipt._reconcileDiff = Math.round(diff * 100) / 100;
          }
        }
      }

      // Range date — uso copia per non mutare l'originale
      const allDates = Array.from(receiptsMap.values()).map(r => r.closedAt.slice(0, 10));
      const allDatesSorted = [...allDates].sort();
      const dateRange = { from: allDatesSorted[0], to: allDatesSorted[allDatesSorted.length - 1] };

      setProgress("Verifica duplicati con storico...");
      // Identifica conflitti con scontrini già presenti (anche non importati, per sicurezza)
      const existingReceipts = state.tableReceipts || [];
      const conflictsList = [];
      for (const newR of receiptsMap.values()) {
        const newDate = newR.closedAt.slice(0, 10);
        const newTable = String(newR.number).trim();
        // Match per (data + tavolo + ora ±30min). Considera dup se totale ±€1 o ±5%
        const existing = existingReceipts.find(ex => {
          const exDate = localDateOf(ex.closedAt);
          if (exDate !== newDate) return false;
          if (String(ex.number).trim() !== newTable) return false;
          const minDiff = Math.abs(new Date(ex.closedAt) - new Date(newR.closedAt)) / 60000;
          if (minDiff > 30) return false;
          // Tolleranza totale: max(€1, 5%) - più permissiva del €0.50 originale
          const tolerance = Math.max(1, newR.total * 0.05);
          if (Math.abs(ex.total - newR.total) > tolerance) return false;
          return true;
        });
        if (existing) {
          conflictsList.push({ existing, incoming: newR });
        }
      }

      // FIX F-13 (audit 2026-05-19): calcola hash per dedup e importHistory
      let fileHashHex = null;
      try {
        const chunk = await file.arrayBuffer();
        const hashBuf = await crypto.subtle.digest("SHA-256", chunk);
        fileHashHex = Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, "0")).join("");
      } catch { /* non bloccante — importHistory funzionerà senza hash */ }

      // Calcola metriche mezze porzioni per il banner post-parse
      const receiptsList = Array.from(receiptsMap.values());
      const mezzePorzioniTotal = receiptsList.reduce(
        (s, r) => s + ((r._mezzePorzioni || []).length), 0
      );
      const mezzeAppliedAuto = receiptsList.reduce(
        (s, r) => s + ((r._mezzePorzioni || []).filter(mp => mp.appliedAutomatically).length), 0
      );
      const mezzeAlreadyDiscounted = receiptsList.reduce(
        (s, r) => s + ((r._mezzePorzioni || []).filter(mp => mp.alreadyDiscounted).length), 0
      );
      const mezzeScontoTotale = receiptsList.reduce(
        (s, r) => s + ((r._mezzePorzioni || []).filter(mp => mp.appliedAutomatically).reduce((ss, mp) => ss + mp.scontoAtteso, 0)), 0
      );
      // Legacy aliases per stats useMemo (non usati nel nuovo banner ma evitano refactor a cascata)
      const mezzeWithoutSconto = 0;
      const mezzeSovrastima = 0;

      // Metriche COPERTI DEDOTTI (MENU CONCORDATO senza riga COPERTO esplicita).
      // Quando TCPOS non esporta il documento di chiusura col conteggio coperti
      // (caso reale tav.431 12/05: gruppo da 7, esportati solo 2 menu → 2 coperti
      // dedotti, persi 5 coperti + l'incasso del pagamento non esportato), il software
      // INDOVINA i coperti dal numero di righe MENU CONCORDATO. È inaffidabile per
      // definizione: questi tavoli vanno verificati a mano sulla chiusura di cassa.
      // Segnale: receipt con MENU CONCORDATO e (_coversInferred dal merge OPPURE
      // covers === 0, cioè nessun coperto registrato del tutto).
      const coversInferredList = receiptsList
        .filter(r => (r.items || []).some(isMenuConcordato) && (r._coversInferred || (r.covers || 0) === 0))
        .map(r => ({
          id: r.id,
          date: (r.closedAt || "").slice(0, 10),
          number: r.number,
          covers: r.covers || 0,
          total: r.total || 0,
          inferred: !!r._coversInferred,
        }))
        .sort((a, b) => (a.date + String(a.number)).localeCompare(b.date + String(b.number)));
      const coversInferredCount = coversInferredList.length;

      setParsed({
        receiptsList,
        articlesUnmatched: Array.from(articlesUnmatched.entries()).sort((a, b) => b[1] - a[1]),
        dateRange,
        fileName,
        fileHash: fileHashHex,
        newAliases,
        cols,
        rowsTotal: rows.length - 1,
        mezzePorzioniTotal,
        mezzeAppliedAuto,
        mezzeAlreadyDiscounted,
        mezzeScontoTotale,
        mezzeWithoutSconto,
        mezzeSovrastima,
        duplicateRowCount,  // FIX P0-B: numero righe duplicate intra-file scartate
        coversInferredList,
        coversInferredCount,
      });
      setConflicts(conflictsList);
      setStep(conflictsList.length > 0 ? "conflict" : "review");
      setProgress(null);
    } catch (e) {
      console.error(e);
      toast({ type: "error", title: "Errore lettura file", message: e.message || String(e) });
      setProgress(null);
    }
  };

  const doImport = () => {
    if (!parsed) return;

    let receiptsToImport = parsed.receiptsList;
    let existingReceipts = state.tableReceipts || [];

    // Gestione conflitti
    if (conflicts.length > 0 && conflictAction === "skip") {
      const conflictKeys = new Set(conflicts.map(c => c.incoming.id));
      receiptsToImport = receiptsToImport.filter(r => !conflictKeys.has(r.id));
    } else if (conflicts.length > 0 && conflictAction === "replace") {
      const replaceIds = new Set(conflicts.map(c => c.existing.id));
      existingReceipts = existingReceipts.filter(ex => !replaceIds.has(ex.id));
    }

    // Snapshot ricetta per ogni item con dishId valido (non _unmatched_)
    const dishesAll = state.dishesReal || [];
    const baseProducts = state.baseProducts || [];

    const enrichedReceipts = receiptsToImport.map(r => {
      const itemsWithSnapshot = r.items.map(it => {
        // Snapshot ricetta (per food cost storico) — invariato
        let recipeSnapshot = null;
        let dish = null;
        if (!it.dishId.startsWith("_unmatched_")) {
          dish = dishesAll.find(d => d.id === it.dishId);
          if (dish && dish.ingredienti && dish.ingredienti.length > 0) {
            const snapshotIngs = dish.ingredienti.map(ing => {
              const bp = ing.bpId ? baseProducts.find(b => b.id === ing.bpId) : null;
              return {
                name: ing.name, qty: ing.qty, unit: ing.unit, bpId: ing.bpId,
                bpName: bp?.display || bp?.nome || null,
                unitCostNet: bp ? (bp.pReale || bp.pFatt || 0) : (ing.prezzo || 0),
                vatRate: bp?.vatRate != null ? bp.vatRate : null,
              };
            });
            const snapshotCost = snapshotIngs.reduce((s, ing) => s + ing.qty * (ing.unitCostNet || 0), 0);
            recipeSnapshot = { cat: dish.cat, vatRate: dish.vatRate, ingredienti: snapshotIngs, netCost: Math.round(snapshotCost * 10000) / 10000 };
          }
        }

        // Snapshot prezzo LISTINO per calcolo sconto% (vedi blocco aggregato sotto).
        // Regole (l'ORDINE CONTA — i casi sopra sovrascrivono quelli sotto):
        //   - MENU CONCORDATO: listino 0. È il PREZZO PAGATO del menu fisso, non
        //     un articolo a carta. Includerlo significherebbe contare due volte
        //     (menu pagato + articoli inclusi al listino).
        //   - regalo (SESCIALA): listino 0 (è una cortesia, non un mancato incasso)
        //   - sconto a valore / unitPrice<0: listino 0 (è già sconto puro)
        //   - matchato + unitPrice=0 + dish.prezzo>0: listino = dish.prezzo
        //     (caso tipico ARTICOLI INCLUSI in MENU CONCORDATO — listino reale)
        //   - altrimenti: listino = unitPrice (è già il prezzo carta)
        let listinoPrice;
        if (isMenuConcordato(it)) listinoPrice = 0;
        else if (it._isGift) listinoPrice = 0;
        else if (isDiscountLine(it)) listinoPrice = 0;
        else if (it.unitPrice === 0 && dish && Number(dish.prezzo) > 0) listinoPrice = Number(dish.prezzo);
        else if (it.unitPrice === 0 && !dish) listinoPrice = 0;  // unmatched a 0: indeterminato
        else listinoPrice = it.unitPrice;

        return { ...it, recipeSnapshot, _listinoPrice: listinoPrice };
      });

      // Aggregati sconto receipt: confronto valore listino vs incassato.
      // listinoTotal include MENU CONCORDATO al suo prezzo carta (= prezzo concordato)
      // E gli articoli "regalo" del menu al loro prezzo di listino reale.
      // realTotal è il pagato (incassato), già al netto di sconti negativi.
      const listinoTotal = itemsWithSnapshot.reduce(
        (s, it) => s + (Number(it.qty) || 0) * (Number(it._listinoPrice) || 0), 0
      );
      const realTotal = r.total;
      const discount = listinoTotal - realTotal;
      const discountPct = listinoTotal > 0 ? (discount / listinoTotal) : 0;
      return {
        ...r,
        items: itemsWithSnapshot,
        _listinoTotal: Math.round(listinoTotal * 100) / 100,
        _discount: Math.round(discount * 100) / 100,
        _discountPct: Math.round(discountPct * 10000) / 10000,  // 4 decimali = precisione 0.01%
      };
    });

    // Audit: scontrini con discrepanza dichiarato vs ricostruito (storni/sconti/omaggi).
    // Tracciamo per fornire pista in caso analytics food cost diventi sospetta.
    const reconcileWarnings = enrichedReceipts.filter(r => r._reconcileWarning);
    const totalReconcileDiff = reconcileWarnings.reduce(
      (s, r) => s + (Number(r._reconcileDiff) || 0), 0
    );

    // FIX F-13 (audit 2026-05-19): record importHistory per audit e dedup file-hash.
    // Teniamo solo le ultime 100 voci (slice -99 + nuova = 100 max).
    const totalRevImported = enrichedReceipts.reduce((s, r) => s + (r.total || 0), 0);
    const importRecord = {
      id: `imp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      fileHash: parsed.fileHash || null,
      fileName: parsed.fileName,
      importer: "zucchetti",
      ts: nowIso(),
      receiptsImported: enrichedReceipts.length,
      receiptsSkipped: (conflicts.length > 0 && conflictAction === "skip") ? conflicts.length : 0,
      articlesUnmatched: (parsed.articlesUnmatched || []).length,
      totalRevenue: Math.round(totalRevImported * 100) / 100,
    };
    const prevImportHistory = state.importHistory || [];
    const newImportHistory = [...prevImportHistory.slice(-99), importRecord];

    // FIX PATCH-6 (audit 2026-05-19): snapshot pre-import per UNDO a 24h.
    // FIX delta (audit 2026-06-04): usa delta minimo invece di copia completa tableReceipts.
    // Il delta contiene solo gli ID aggiunti e gli scontrini rimossi (se replace),
    // riducendo la dimensione da O(N_totale) a O(N_questo_import).
    const removedByReplace = (conflicts.length > 0 && conflictAction === "replace")
      ? conflicts.map(c => c.existing)
      : [];
    const undoSnapshot = {
      type: "import",
      ts: importRecord.ts,
      importId: importRecord.id,
      fileName: parsed.fileName,
      receiptsImported: enrichedReceipts.length,
      delta: buildUndoDelta(enrichedReceipts, removedByReplace),
      prevImportHistory: prevImportHistory,
    };

    let nextState = {
      ...state,
      tableReceipts: [...existingReceipts, ...enrichedReceipts],
      dishAliases: parsed.newAliases,
      importHistory: newImportHistory,
      _undoSnapshot: undoSnapshot,
    };
    if (reconcileWarnings.length > 0) {
      nextState.auditLog = appendAudit(nextState, {
        action: "import_reconcile_warnings",
        entityType: "import",
        entityId: parsed.fileName,
        reason: `Zucchetti import: ${reconcileWarnings.length} scontrini con discrepanza totale dichiarato vs ricostruito (diff cumulato ${totalReconcileDiff.toFixed(2)} €)`,
        payload: {
          count: reconcileWarnings.length,
          totalDiff: Math.round(totalReconcileDiff * 100) / 100,
          fileName: parsed.fileName,
        },
      });
    }
    setState(nextState);

    const skipped = conflicts.length > 0 && conflictAction === "skip" ? conflicts.length : 0;
    const replaced = conflicts.length > 0 && conflictAction === "replace" ? conflicts.length : 0;
    const recMsg = reconcileWarnings.length > 0 ? ` · ⚠ ${reconcileWarnings.length} con discrepanza totale` : "";
    toast({
      type: reconcileWarnings.length > 0 ? "warning" : "success",
      title: "Import completato",
      message: `${enrichedReceipts.length} scontrini importati · €${enrichedReceipts.reduce((s, r) => s + r.total, 0).toFixed(2)}${skipped > 0 ? ` · ${skipped} duplicati saltati` : ""}${replaced > 0 ? ` · ${replaced} sostituiti` : ""}${recMsg}`,
    });
    onClose();
  };

  const stats = useMemo(() => {
    if (!parsed) return null;
    const totalRev = parsed.receiptsList.reduce((s, r) => s + r.total, 0);
    const totalCov = parsed.receiptsList.reduce((s, r) => s + r.covers, 0);
    const matched = parsed.receiptsList.reduce((s, r) => s + r.items.filter(it => !it.dishId.startsWith("_unmatched_")).length, 0);
    const unmatched = parsed.receiptsList.reduce((s, r) => s + r.items.filter(it => it.dishId.startsWith("_unmatched_")).length, 0);
    const reconcileWarnings = parsed.receiptsList.filter(r => r._reconcileWarning).length;
    const reconcileDiffSum = parsed.receiptsList.reduce(
      (s, r) => s + (r._reconcileWarning ? Math.abs(Number(r._reconcileDiff) || 0) : 0), 0
    );
    const pctReconcileWarnings = parsed.receiptsList.length > 0
      ? reconcileWarnings / parsed.receiptsList.length : 0;
    return { totalRev, totalCov, matched, unmatched, receipts: parsed.receiptsList.length,
             reconcileWarnings, reconcileDiffSum, pctReconcileWarnings,
             mezzePorzioniTotal: parsed.mezzePorzioniTotal || 0,
             mezzeWithoutSconto: parsed.mezzeWithoutSconto || 0,
             mezzeSovrastima: parsed.mezzeSovrastima || 0 };
  }, [parsed]);

  return <Modal onClose={onClose} maxWidth={950} title="Importa scontrini Zucchetti" subtitle="Da gestionale di cassa TCPOS"
    footer={<div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
      <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
      {step === "review" && <Btn variant="accent" icon={CheckCircle2} onClick={doImport}>
        Conferma import ({stats?.receipts || 0} scontrini · €{stats?.totalRev?.toFixed(2) || 0})
      </Btn>}
      {step === "conflict" && <>
        <Btn variant="ghost" onClick={() => setStep("review")}>Vai alla preview</Btn>
        <Btn variant="accent" icon={CheckCircle2} onClick={doImport}>
          {conflictAction === "replace" ? "Sostituisci e importa" : "Importa solo nuovi"}
        </Btn>
      </>}
    </div>}>
    <div style={{ padding: 22 }}>

      {step === "upload" && <div>
        <div style={{ padding: 16, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, marginBottom: 18, fontSize: 12, color: C.tealDeep, lineHeight: 1.6 }}>
          <strong style={{ display: "block", marginBottom: 6 }}>Workflow consigliato (giornaliero)</strong>
          <ol style={{ margin: "0 0 8px", paddingLeft: 22 }}>
            <li><strong>Fine pranzo (~16:00)</strong>: dal gestionale Zucchetti esporta gli scontrini del servizio pranzo</li>
            <li><strong>Fine cena (~24:00)</strong>: stesso processo per il servizio cena</li>
            <li>Trascina qui il file (.xls, .xlsx, .csv) — il parser auto-rileva il formato</li>
            <li>Verifica preview e conferma</li>
          </ol>
          <strong>Re-import sicuro:</strong> se importi due volte lo stesso file (o stessa giornata), il sistema rileva i duplicati e ti chiede se sostituire o saltare. Non crea mai duplicati silenziosi.
        </div>

        {/* Reset import precedenti — utile dopo aggiornamenti dell'importer
            (es. cambio chiave receipt, nuovi campi tipo _csvReparto/_docType)
            quando un reimport "replace" non basta a riallineare i dati. */}
        {(() => {
          const importedReceipts = (state.tableReceipts || []).filter(r => r._imported);
          if (importedReceipts.length === 0) return null;
          const dates = [...new Set(importedReceipts.map(r => (r.closedAt || "").slice(0, 10)))].filter(Boolean).sort();
          const dateLabel = dates.length === 0 ? "—"
            : dates.length === 1 ? new Date(dates[0] + "T12:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "long" })
            : `${new Date(dates[0] + "T12:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "short" })} → ${new Date(dates[dates.length - 1] + "T12:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "short" })}`;
          const totalRev = importedReceipts.reduce((s, r) => s + (Number(r.total) || 0), 0);
          const handleClearImports = () => {
            if (!confirm(`Cancellare i ${importedReceipts.length} scontrini importati (${dateLabel}, €${totalRev.toFixed(2)})?\n\nGli scontrini creati manualmente nella Sala NON vengono toccati. Gli alias appresi e i piatti in anagrafica restano.\n\nUsa questo comando se hai aggiornato l'importer e devi riallineare i dati con un import pulito.`)) return;
            const kept = (state.tableReceipts || []).filter(r => !r._imported);
            setState({ ...state, tableReceipts: kept });
            toast({ type: "success", title: "Scontrini importati cancellati", message: `${importedReceipts.length} scontrini rimossi · ora puoi reimportare il file pulito` });
          };
          return <div style={{ padding: 14, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, marginBottom: 18, display: "flex", alignItems: "center", gap: 14 }}>
            <Trash2 size={20} color={C.amberDeep} strokeWidth={1.5} />
            <div style={{ flex: 1, fontSize: 12, color: C.inkSoft, lineHeight: 1.5 }}>
              <strong>{importedReceipts.length} scontrini</strong> già importati ({dateLabel} · €{totalRev.toFixed(2)}).
              <div style={{ marginTop: 4 }}>
                <strong>Reimport "Sostituisci esistenti" può creare duplicati</strong> se l'importer è cambiato dall'ultimo import
                (es. la fusione MENU CONCORDATO cambia il totale degli scontrini fusi, che non matchano più i singoli vecchi).
                In quel caso clicca prima "Cancella import precedenti" e poi reimporta il file.
              </div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>Gli scontrini creati a mano nella Sala e gli alias appresi NON vengono toccati.</div>
            </div>
            <Btn variant="ghost" size="sm" icon={Trash2} onClick={handleClearImports}>Cancella import precedenti</Btn>
          </div>;
        })()}

        <div onClick={() => fileRef.current?.click()} style={{ border: `2px dashed ${C.teal}`, padding: 40, borderRadius: 2, textAlign: "center", cursor: "pointer", background: C.tealWash + "40", transition: "all 0.2s" }}
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.background = C.tealWash; }}
          onDragLeave={e => e.currentTarget.style.background = C.tealWash + "40"}
          onDrop={e => { e.preventDefault(); e.currentTarget.style.background = C.tealWash + "40"; if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); }}>
          <Upload size={42} color={C.teal} strokeWidth={1.3} style={{ margin: "0 auto 14px", display: "block" }} />
          <div className="f-display" style={{ fontSize: 18, color: C.ink, fontWeight: 500 }}>Trascina il file scontrini Zucchetti</div>
          <div className="f-body" style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>oppure clicca per scegliere · supporta .xls .xlsx .csv (UTF-8)</div>
          <input ref={fileRef} type="file" accept=".xls,.xlsx,.csv,.txt" style={{ display: "none" }} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
        </div>

        {progress && <div style={{ marginTop: 14, padding: 12, background: C.amberWash, borderRadius: 2, fontSize: 13, color: C.amberDeep, textAlign: "center", fontWeight: 500 }}>
          ⏳ {progress}
        </div>}

        <div style={{ marginTop: 16, padding: 12, background: C.paperDeep, borderRadius: 2, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
          <strong>Cosa fa il parser:</strong> riconosce automaticamente le colonne tipiche di Zucchetti (Data, Tavolo, Coperti, Articolo, Quantità, Prezzo). Raggruppa le righe per scontrino (stesso tavolo + stessa ora). Fa fuzzy-matching dei nomi articolo coi tuoi piatti in anagrafica e salva gli alias per i prossimi import (la seconda volta sarà istantaneo). Crea snapshot ricetta per il calcolo costo materia prima storico.
        </div>
      </div>}

      {step === "conflict" && <div>
        <div style={{ padding: 14, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, marginBottom: 14, display: "flex", gap: 12, alignItems: "flex-start" }}>
          <AlertTriangle size={20} color={C.amberDeep} />
          <div>
            <div className="f-display" style={{ fontSize: 15, color: C.ink, fontWeight: 600 }}>{conflicts.length} scontrini già presenti</div>
            <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, marginTop: 4, lineHeight: 1.5 }}>
              Ho trovato {conflicts.length} scontrini con stessa data, tavolo, ora e totale già importati. Probabilmente stai re-importando un file già caricato in passato. Scegli cosa fare:
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <button onClick={() => setConflictAction("replace")} className="tx" style={{ flex: 1, padding: "14px 16px", background: conflictAction === "replace" ? C.ink : C.white, color: conflictAction === "replace" ? C.paper : C.ink, border: `2px solid ${conflictAction === "replace" ? C.ink : C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>🔄 Sostituisci esistenti</div>
            <div style={{ fontSize: 11, opacity: 0.8 }}>I {conflicts.length} scontrini vecchi vengono cancellati e sostituiti con quelli del nuovo file. Usa se hai re-esportato per correggere errori.</div>
          </button>
          <button onClick={() => setConflictAction("skip")} className="tx" style={{ flex: 1, padding: "14px 16px", background: conflictAction === "skip" ? C.ink : C.white, color: conflictAction === "skip" ? C.paper : C.ink, border: `2px solid ${conflictAction === "skip" ? C.ink : C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>⏭ Salta duplicati</div>
            <div style={{ fontSize: 11, opacity: 0.8 }}>Importa solo i nuovi ({stats.receipts - conflicts.length}). I {conflicts.length} esistenti restano com'erano. Usa se vuoi solo aggiungere senza toccare lo storico.</div>
          </button>
        </div>

        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, maxHeight: 280, overflowY: "auto" }}>
          <div style={{ padding: "10px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.2em", color: C.muted, fontFamily: "JetBrains Mono, monospace", display: "grid", gridTemplateColumns: "1fr 0.5fr 0.5fr 0.7fr 0.7fr", gap: 8 }}>
            <span>SCONTRINO IN CONFLITTO</span><span style={{ textAlign: "center" }}>TAVOLO</span><span style={{ textAlign: "center" }}>COPERTI</span><span style={{ textAlign: "right" }}>VECCHIO €</span><span style={{ textAlign: "right" }}>NUOVO €</span>
          </div>
          {conflicts.slice(0, 50).map((c, i) => <div key={i} style={{ padding: "6px 14px", borderTop: `1px solid ${C.ruleSoft}`, fontSize: 11, display: "grid", gridTemplateColumns: "1fr 0.5fr 0.5fr 0.7fr 0.7fr", gap: 8, alignItems: "center" }}>
            <span className="f-mono" style={{ color: C.ink }}>{new Date(c.incoming.closedAt).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
            <span className="f-mono" style={{ textAlign: "center" }}>{c.incoming.number}</span>
            <span className="f-mono" style={{ textAlign: "center" }}>{c.incoming.covers}</span>
            <span className="f-mono" style={{ textAlign: "right", color: C.muted }}>{fmt.eur(c.existing.total)}</span>
            <span className="f-mono" style={{ textAlign: "right", color: C.ink, fontWeight: 600 }}>{fmt.eur(c.incoming.total)}</span>
          </div>)}
          {conflicts.length > 50 && <div style={{ padding: 8, textAlign: "center", fontSize: 10, color: C.muted, fontStyle: "italic" }}>+ altri {conflicts.length - 50} conflitti</div>}
        </div>
      </div>}

      {step === "review" && parsed && stats && <div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 18 }}>
          <div style={{ background: C.ink, padding: 14, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.mutedSoft, letterSpacing: "0.2em" }}>SCONTRINI</div>
            <div className="f-display" style={{ fontSize: 26, fontWeight: 600, color: C.paper, marginTop: 3 }}>{stats.receipts}</div>
            <div className="f-mono" style={{ fontSize: 9, color: C.mutedSoft, marginTop: 3 }}>{parsed.dateRange.from === parsed.dateRange.to ? parsed.dateRange.from : `${parsed.dateRange.from} → ${parsed.dateRange.to}`}</div>
          </div>
          <div style={{ background: C.oliveWash, padding: 14, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.olive, letterSpacing: "0.2em" }}>INCASSO</div>
            <div className="f-display" style={{ fontSize: 26, fontWeight: 600, color: C.oliveDeep, marginTop: 3 }}>{fmt.eurShort(stats.totalRev)}</div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 3 }}>{stats.receipts > 0 ? fmt.eur(stats.totalRev / stats.receipts) : "—"}/scontrino</div>
          </div>
          <div style={{ background: C.tealWash, padding: 14, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.teal, letterSpacing: "0.2em" }}>COPERTI</div>
            <div className="f-display" style={{ fontSize: 26, fontWeight: 600, color: C.tealDeep, marginTop: 3 }}>{stats.totalCov}</div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 3 }}>{stats.totalCov > 0 ? fmt.eur(stats.totalRev / stats.totalCov) : "—"}/coperto</div>
          </div>
          <div style={{ background: parsed.articlesUnmatched.length > 0 ? C.amberWash : C.paperDeep, padding: 14, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: parsed.articlesUnmatched.length > 0 ? C.amberDeep : C.muted, letterSpacing: "0.2em" }}>ART. NUOVI</div>
            <div className="f-display" style={{ fontSize: 26, fontWeight: 600, color: parsed.articlesUnmatched.length > 0 ? C.amberDeep : C.muted, marginTop: 3 }}>{parsed.articlesUnmatched.length}</div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 3 }}>non in anagrafica</div>
          </div>
          <div style={{ background: C.paperDeep, padding: 14, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>RIGHE LETTE</div>
            <div className="f-display" style={{ fontSize: 26, fontWeight: 600, color: C.ink, marginTop: 3 }}>{parsed.rowsTotal}</div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 3 }}>{stats.matched + stats.unmatched} piatti</div>
          </div>
        </div>

        {stats.reconcileWarnings > 0 && (() => {
          const pct = stats.pctReconcileWarnings;
          // Severity: > 15% rosso (food cost compromesso), > 5% ambra (segnale), altrimenti info
          const sev = pct > 0.15 ? "high" : pct > 0.05 ? "medium" : "low";
          const bg = sev === "high" ? C.terraWash : sev === "medium" ? C.amberWash : C.paperDeep;
          const bd = sev === "high" ? C.terra : sev === "medium" ? C.amber : C.ruleSoft;
          const fg = sev === "high" ? C.terraDeep : sev === "medium" ? C.amberDeep : C.inkSoft;
          return <div style={{ padding: 12, background: bg, border: `1px solid ${bd}`, borderRadius: 2, marginBottom: 10, display: "flex", gap: 10, alignItems: "flex-start" }}>
            <AlertTriangle size={16} color={fg} style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.5 }}>
              <strong>{stats.reconcileWarnings} scontrini</strong> ({(pct * 100).toFixed(1)}%) hanno il totale dichiarato dal TCPOS diverso dalla somma delle righe (differenza cumulata <strong>{fmt.eur(stats.reconcileDiffSum)}</strong>).
              <div style={{ marginTop: 4, fontSize: 11, color: C.muted }}>Cause tipiche: storni post-chiusura, sconti riga non esportati, omaggi, arrotondamenti. Le righe ⚠ sono evidenziate qui sotto. L'evento viene registrato in auditLog.</div>
            </div>
          </div>;
        })()}

        {parsed.articlesUnmatched.length > 0 && <div style={{ padding: 12, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <AlertTriangle size={16} color={C.amberDeep} style={{ marginTop: 2 }} />
            <div style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.5 }}>
              <strong>{parsed.articlesUnmatched.length} articoli</strong> del CSV non sono nella tua anagrafica piatti. Verranno comunque importati negli scontrini per il calcolo incasso/coperti, ma <strong>il consumo materia prima e le ricette non saranno calcolabili</strong> per loro. Importa il listino aggiornato dal pulsante "Importa listino" in Piatti & ricette per averli mappati nei prossimi import.
              <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: "pointer", color: C.amberDeep, fontWeight: 600 }}>Mostra elenco articoli non riconosciuti</summary>
                <div style={{ marginTop: 6, maxHeight: 120, overflowY: "auto", padding: 6, background: C.white, borderRadius: 2 }}>
                  {parsed.articlesUnmatched.slice(0, 30).map(([name, count]) => <div key={name} style={{ fontSize: 11, color: C.inkSoft, padding: "2px 0" }}>
                    <span className="f-mono">×{count}</span> {name}
                  </div>)}
                  {parsed.articlesUnmatched.length > 30 && <div style={{ fontSize: 10, color: C.muted, fontStyle: "italic", marginTop: 4 }}>+ altri {parsed.articlesUnmatched.length - 30}</div>}
                </div>
              </details>
            </div>
          </div>
        </div>}

        {/* Banner mezze porzioni — informativo, sconto è automatico */}
        {(parsed.mezzePorzioniTotal || 0) > 0 && <div style={{ padding: 12, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Info size={16} color={C.tealDeep} />
            <div className="f-body" style={{ fontSize: 12, color: C.ink }}>
              Trovate <strong>{parsed.mezzePorzioniTotal}</strong> mezze porzioni nel file.
              Sconto automatico applicato a <strong>{parsed.mezzeAppliedAuto || 0}</strong> articoli
              (totale sconto: <strong>{fmt.eur(parsed.mezzeScontoTotale || 0)}</strong>).
              {(parsed.mezzeAlreadyDiscounted || 0) > 0 && <> {parsed.mezzeAlreadyDiscounted} avevano già sconto coerente nel doc TCPOS.</>}
            </div>
          </div>
        </div>}

        {/* Banner righe duplicate intra-file (FIX P0-B 2026-05-20) — informativo, NON scarta */}
        {/* Soglia 50: sotto, probabilmente sono vendite legittime di articoli identici
            (es. 2 calici stesso vino). Sopra, probabile bug export TCPOS. */}
        {(parsed.duplicateRowCount || 0) >= 50 && <div style={{ padding: 12, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <AlertTriangle size={16} color={C.amberDeep} />
            <div className="f-body" style={{ fontSize: 12, color: C.ink }}>
              ⚠ Rilevate <strong>{parsed.duplicateRowCount}</strong> righe duplicate intra-file
              (stesso doc TCPOS, stesso articolo, stessa qty e prezzo).
              Numero elevato — probabile <strong>bug di esportazione TCPOS</strong>: controlla il file originale
              e considera di riesportarlo prima di confermare l'import.
              <br /><span style={{ color: C.muted, fontSize: 11 }}>NB: tutte le righe sono state caricate (non scartate automaticamente per evitare di perdere vendite legittime di articoli uguali).</span>
            </div>
          </div>
        </div>}

        {/* Banner COPERTI DEDOTTI (2026-06-03) — tavoli MENU CONCORDATO senza riga
            COPERTO: i coperti sono indovinati dal software, da verificare in cassa.
            Caso reale tav.431 12/05: gruppo da 7, TCPOS ha esportato solo 2 menu →
            2 coperti dedotti, persi 5 coperti + il pagamento di chiusura non esportato. */}
        {(parsed.coversInferredCount || 0) > 0 && <div style={{ padding: 12, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <AlertTriangle size={16} color={C.amberDeep} style={{ flexShrink: 0, marginTop: 2 }} />
            <div className="f-body" style={{ fontSize: 12, color: C.ink, lineHeight: 1.5 }}>
              ⚠ <strong>{parsed.coversInferredCount}</strong> {parsed.coversInferredCount === 1 ? "tavolo" : "tavoli"} con <strong>MENU CONCORDATO</strong> senza riga COPERTO:
              i coperti sono stati <strong>dedotti automaticamente</strong> dal numero di menu e possono essere sottostimati.
              Se la chiusura di cassa di quel tavolo non è stata esportata da TCPOS, mancano coperti e incasso.
              <strong> Verifica questi tavoli sulla chiusura di cassa</strong> e, se serve, correggi i coperti nel dettaglio scontrino:
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {(parsed.coversInferredList || []).map((c, i) => {
                  const [y, m, d] = (c.date || "").split("-");
                  const dLabel = d && m ? `${d}/${m}` : c.date;
                  return <li key={c.id || i} style={{ marginBottom: 2 }}>
                    <span className="f-mono">{dLabel}</span> · tav. <strong>{c.number}</strong> · coperti dedotti: <strong>{c.covers}</strong> · incasso file: <strong>{fmt.eur(c.total)}</strong>
                  </li>;
                })}
              </ul>
              <span style={{ color: C.muted, fontSize: 11 }}>NB: gli scontrini sono importati comunque; questo è solo un promemoria di controllo.</span>
            </div>
          </div>
        </div>}

        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, marginBottom: 6 }}>ANTEPRIMA SCONTRINI</div>
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, maxHeight: 360, overflowY: "auto" }}>
          <div style={{ padding: "8px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.2em", color: C.muted, fontFamily: "JetBrains Mono, monospace", display: "grid", gridTemplateColumns: "0.5fr 1fr 0.4fr 0.4fr 0.8fr 0.6fr 0.6fr", gap: 6 }}>
            <span>DOC</span><span>DATA/ORA</span><span style={{ textAlign: "center" }}>TAV</span><span style={{ textAlign: "center" }}>COP</span><span style={{ textAlign: "center" }}>SERV</span><span style={{ textAlign: "center" }}>PIATTI</span><span style={{ textAlign: "right" }}>TOTALE</span>
          </div>
          {parsed.receiptsList.slice(0, 100).map(r => {
            const shift = getShift(r.closedAt);
            const warn = !!r._reconcileWarning;
            const docBadge = receiptDocBadge(r);
            return <div key={r.id} style={{ padding: "5px 14px", borderTop: `1px solid ${C.ruleSoft}`, fontSize: 11, display: "grid", gridTemplateColumns: "0.5fr 1fr 0.4fr 0.4fr 0.8fr 0.6fr 0.6fr", gap: 6, alignItems: "center", background: r._isPreconto ? C.terraWash + "55" : (warn ? C.amberWash + "60" : undefined), borderLeft: r._isPreconto ? `3px solid ${C.terra}` : (r._docType === "FATTURA" ? `3px solid ${C.olive}` : "3px solid transparent") }}>
              <span>{docBadge && <span title={docBadge.title} style={{ background: docBadge.bg, color: C.paper, fontSize: 8, padding: "1px 5px", borderRadius: 2, fontWeight: 700, letterSpacing: "0.08em", fontFamily: "JetBrains Mono, monospace" }}>{docBadge.label}</span>}</span>
              <span className="f-mono" style={{ color: C.ink }}>
                {warn && <span title={`Discrepanza ${fmt.eur(r._reconcileDiff)}: dichiarato ${fmt.eur(r._declaredTotal)} vs ricostruito ${fmt.eur(r.total)}`} style={{ color: C.amberDeep, marginRight: 4 }}>⚠</span>}
                {new Date(r.closedAt).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
              </span>
              <span className="f-mono" style={{ textAlign: "center" }}>{r.number}</span>
              <span className="f-mono" style={{ textAlign: "center" }}>{r.covers}</span>
              <span className="f-mono" style={{ textAlign: "center", fontSize: 10, color: shift === "pranzo" ? C.amberDeep : (shift === "cena" ? C.tealDeep : C.muted) }}>
                {shift === "pranzo" ? "☀ PRANZO" : shift === "cena" ? "🌙 CENA" : "—"}
              </span>
              <span className="f-mono" style={{ textAlign: "center" }}>{r.items.length}</span>
              <span className="f-mono" style={{ textAlign: "right", color: C.ink, fontWeight: 600 }}>{fmt.eur(r.total)}</span>
            </div>;
          })}
          {parsed.receiptsList.length > 100 && <div style={{ padding: 8, textAlign: "center", fontSize: 10, color: C.muted, fontStyle: "italic" }}>+ altri {parsed.receiptsList.length - 100} scontrini</div>}
        </div>
      </div>}

    </div>
  </Modal>;
}


function CSVImporter({ state, setState, onClose }) {
  const [step, setStep] = useState("upload");  // upload | mapping | matching | confirm
  const [rawText, setRawText] = useState("");
  const [parsed, setParsed] = useState(null);  // {rows, delimiter}
  const [mapping, setMapping] = useState({
    tableNumber: null, date: null, time: null, covers: null,
    dishName: null, qty: null, price: null, category: null,
  });
  const [hasHeader, setHasHeader] = useState(true);
  const [matches, setMatches] = useState([]);  // per riga: {row, nomeCsv, match, action, ...}
  const [categoryOverrides, setCategoryOverrides] = useState({});  // nomeCsv → categoryKey
  const [unmatchedAction, setUnmatchedAction] = useState("skip");  // skip | create | prompt
  const [dryRun, setDryRun] = useState(false);
  // FIX F-03 (audit 2026-05-19): stato per dedup file-hash (modale conferma re-import)
  const [pendingFileLoad, setPendingFileLoad] = useState(null);  // { file, hash, existingEntry } — in attesa conferma
  const fileRef = useRef();
  const toast = useToast();

  // Hash SHA-256 su TUTTO il file per dedup (prima erano solo i primi 1MB: due export
  // con header+coda diversi ma stesso 1MB iniziale collidevano, o viceversa).
  // Evita che Andi carichi lo stesso file due volte raddoppiando gli scontrini CSV.
  const computeFileHash = async (file) => {
    const chunk = await file.arrayBuffer();
    const hashBuf = await crypto.subtle.digest("SHA-256", chunk);
    return Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, "0")).join("");
  };

  // FIX F-03: funzione interna che esegue il parse effettivo del file (usata dopo conferma dedup)
  const doLoadFile = async (file) => {
    // FIX F-05 (audit 2026-05-19): encoding detection UTF-8 strict → Windows-1252 (legacy Excel italiano).
    // Bug originale: reader.readAsText(file, "UTF-8") forzava UTF-8 senza fallback, corrompendo
    // i caratteri accentati nei nomi articolo (è, à, ò) → fuzzy match falliva su nomi corrotti.
    let text;
    try {
      const buf = await file.arrayBuffer();
      try {
        text = new TextDecoder("utf-8", { fatal: true }).decode(buf);
      } catch {
        text = new TextDecoder("windows-1252").decode(buf);
      }
      if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
    } catch {
      text = await file.text();
    }
    setRawText(text);
    const p = parseCSV(text);
    setParsed(p);
    // Auto-guess delle colonne dal nome
    if (p.rows.length > 0) {
      const headers = p.rows[0].map(h => normStr(h));
      const guess = { ...mapping };
      headers.forEach((h, i) => {
        if (/tavolo|table|tav|sala/i.test(h) && guess.tableNumber == null) guess.tableNumber = i;
        else if (/data|date/i.test(h) && guess.date == null) guess.date = i;
        else if (/ora|time|hour/i.test(h) && guess.time == null) guess.time = i;
        else if (/coperti|covers|persone|pax/i.test(h) && guess.covers == null) guess.covers = i;
        else if (/descrizione|articolo|piatto|prodotto|nome|desc|item/i.test(h) && guess.dishName == null) guess.dishName = i;
        else if (/quantit|qty|qta|pezzi|pz|n\b/i.test(h) && guess.qty == null) guess.qty = i;
        else if (/prezzo|totale|importo|price|total/i.test(h) && guess.price == null) guess.price = i;
        else if (/categoria|reparto|gruppo|category/i.test(h) && guess.category == null) guess.category = i;
      });
      setMapping(guess);
    }
    setStep("mapping");
  };

  // FIX F-03: handleFile con dedup hash PRIMA di caricare.
  // FIX F-08 (audit 2026-05-19): limite 50MB — file più grandi crashano la scheda browser
  // senza alcun avviso. Rifiuta subito con messaggio chiaro.
  const handleFile = async (file) => {
    if (file.size > 50 * 1024 * 1024) {
      toast({ type: "error", title: "File troppo grande", message: "Il file supera 50 MB. Esporta dal gestionale in chunk più piccoli (es. per mese)." });
      return;
    }
    const hash = await computeFileHash(file);
    const importHistory = state.importHistory || [];
    const existingEntry = importHistory.find(h => h.fileHash === hash);
    if (existingEntry) {
      // File già caricato in precedenza — chiedi conferma prima di procedere
      setPendingFileLoad({ file, hash, existingEntry });
      return;
    }
    await doLoadFile(file);
  };

  const goToMatching = () => {
    if (mapping.dishName == null) { toast({ type: "error", message: "Mappa almeno la colonna con il nome piatto" }); return; }
    if (mapping.tableNumber == null) { toast({ type: "error", message: "Mappa la colonna tavolo" }); return; }
    // Costruisci righe dati
    const dataRows = hasHeader ? parsed.rows.slice(1) : parsed.rows;
    const dishes = state.dishesReal || [];
    const aliases = state.dishAliases || {};
    const out = [];
    dataRows.forEach((r, idx) => {
      const nomeCsv = (r[mapping.dishName] || "").trim();
      if (!nomeCsv) return;
      const match = matchDishFuzzy(nomeCsv, dishes, aliases);
      const qty = mapping.qty != null ? parseItNumber(r[mapping.qty]) || 1 : 1;
      const price = mapping.price != null ? parseItNumber(r[mapping.price]) || 0 : 0;
      const table = String(r[mapping.tableNumber] || "").trim();
      const covers = mapping.covers != null ? parseInt(r[mapping.covers]) || 0 : 0;
      const date = mapping.date != null ? (r[mapping.date] || "").trim() : "";
      const time = mapping.time != null ? (r[mapping.time] || "").trim() : "";
      const csvCat = mapping.category != null ? normStr(r[mapping.category]) : "";
      // Auto-classifica categoria se disponibile dal CSV
      let suggestedCat = null;
      if (csvCat) {
        for (const k of DISH_CAT_KEYS) {
          if (normStr(DISH_CATEGORIES[k].label).includes(csvCat) || csvCat.includes(normStr(DISH_CATEGORIES[k].label)) || csvCat.includes(k)) {
            suggestedCat = k; break;
          }
        }
      }
      out.push({
        rowIndex: idx, nomeCsv, match, qty, price, table, covers, date, time, csvCat, suggestedCat,
        action: match ? "use" : (unmatchedAction === "create" ? "create" : "skip"),
      });
    });
    setMatches(out);
    setStep("matching");
  };

  const stats = useMemo(() => {
    const s = { matched: 0, unmatched: 0, skipped: 0, willCreate: 0, tables: new Set(), total: 0 };
    matches.forEach(m => {
      if (m.match) s.matched++; else s.unmatched++;
      if (m.action === "skip") s.skipped++;
      if (m.action === "create") s.willCreate++;
      s.tables.add(m.table || "?");
      s.total += m.qty * m.price;
    });
    return s;
  }, [matches]);

  const doImport = () => {
    // Gruppo righe per tavolo + data
    const tablesMap = {};
    const newDishes = []; // piatti creati al volo
    const newAliases = { ...(state.dishAliases || {}) };
    matches.forEach(m => {
      if (m.action === "skip") return;
      if (m.action === "create" && !m.match) {
        // crea piatto placeholder
        const cat = m.suggestedCat || categoryOverrides[m.nomeCsv] || "bar";
        const nd = {
          id: uid("d_"),
          nome: m.nomeCsv,
          cat,
          prezzo: m.price || 0,
          ingredienti: [],
          vatRate: null,
          _imported: true,
        };
        newDishes.push(nd);
        newAliases[normStr(m.nomeCsv)] = nd.id;
        m.match = { dish: nd, score: 1, source: "created" };
      } else if (m.match && m.source !== "alias") {
        // Salva alias per future importazioni
        newAliases[normStr(m.nomeCsv)] = m.match.dish.id;
      }
      if (!m.match) return;
      const tableKey = `${m.table}__${m.date || TODAY}`;
      if (!tablesMap[tableKey]) {
        tablesMap[tableKey] = {
          number: m.table, date: m.date || TODAY, covers: m.covers || 0,
          items: [],
        };
      }
      // Se covers non c'è prendi il max trovato per quel tavolo
      if (m.covers && m.covers > tablesMap[tableKey].covers) tablesMap[tableKey].covers = m.covers;
      tablesMap[tableKey].items.push({
        dishId: m.match.dish.id,
        name: m.match.dish.nome,
        qty: m.qty,
        unitPrice: m.price || m.match.dish.prezzo,
        note: "",
        addedAt: `${m.date || TODAY}T${m.time || "12:00"}:00`,
      });
    });
    // Crea scontrini (tavoli già chiusi, come storico)
    const newReceipts = Object.values(tablesMap).map(t => {
      const total = t.items.reduce((s, it) => s + it.qty * it.unitPrice, 0);
      // Snapshot ricette (stessa logica di confirmCloseTable)
      const itemsWithSnapshot = t.items.map(it => {
        const dish = [...newDishes, ...(state.dishesReal || [])].find(d => d.id === it.dishId);
        if (!dish || !dish.ingredienti || dish.ingredienti.length === 0) return { ...it, recipeSnapshot: null };
        const snapshotIngs = dish.ingredienti.map(ing => {
          const bp = ing.bpId ? (state.baseProducts || []).find(b => b.id === ing.bpId) : null;
          return {
            name: ing.name, qty: ing.qty, unit: ing.unit, bpId: ing.bpId,
            bpName: bp?.display || bp?.nome || null,
            unitCostNet: bp ? (bp.pReale || bp.pFatt || 0) : (ing.prezzo || 0),
            vatRate: bp?.vatRate != null ? bp.vatRate : null,
          };
        });
        const snapshotCost = snapshotIngs.reduce((s, ing) => s + ing.qty * (ing.unitCostNet || 0), 0);
        return {
          ...it,
          recipeSnapshot: { cat: dish.cat, vatRate: dish.vatRate, ingredienti: snapshotIngs, netCost: Math.round(snapshotCost * 10000) / 10000 },
        };
      });
      return {
        id: uid("rcp_"),
        number: t.number,
        // covers: 0 è banco legittimo (asporto). Non forzare a 1 → falserebbe il ticket per coperto.
        covers: Number.isFinite(Number(t.covers)) ? Number(t.covers) : 0,
        openedAt: `${t.date}T12:00:00`,
        closedAt: `${t.date}T13:30:00`,
        items: itemsWithSnapshot,
        total,
        notes: "Importato da CSV cassa",
        operator: state.currentUser?.name || "import",
        _imported: true,
      };
    });

    if (dryRun) {
      toast({ type: "info", title: "Simulazione completata", message: `${newReceipts.length} scontrini · ${newDishes.length} piatti creati · ${fmt.eur(newReceipts.reduce((s, r) => s + r.total, 0))}` });
      return;
    }

    // C3 — Avviso anti-duplicato a livello scontrino. A differenza dell'importer
    // Zucchetti, il CSV manuale non ha lo step "conflitto": l'hash file blocca solo il
    // RI-caricamento dello stesso identico file. Se riesporti lo stesso periodo con un
    // file diverso, i dati si raddoppierebbero in silenzio. Avvisiamo se importi giorni
    // dove esistono già scontrini importati.
    {
      const importDates = new Set(newReceipts.map(r => (r.closedAt || r.openedAt || "").slice(0, 10)).filter(Boolean));
      const existingDates = new Set((state.tableReceipts || [])
        .filter(r => r._imported)
        .map(r => (r.closedAt || r.openedAt || "").slice(0, 10)));
      const overlap = [...importDates].filter(d => existingDates.has(d));
      if (overlap.length > 0) {
        const sample = overlap.slice(0, 5).join(", ");
        if (!confirm(`Attenzione: esistono già scontrini importati in ${overlap.length} giorno/i che stai per ricaricare (${sample}${overlap.length > 5 ? "…" : ""}).\n\nProcedendo i dati di quei giorni verrebbero RADDOPPIATI. Continuare comunque?`)) {
          return;
        }
      }
    }

    // FIX F-13 (audit 2026-05-19): importHistory + UNDO snapshot per CSVImporter
    const csvTotalRev = newReceipts.reduce((s, r) => s + (r.total || 0), 0);
    const csvImportRecord = {
      id: `imp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      fileHash: null,  // hash non disponibile qui (FileReader sincrono storico); sarà null
      fileName: "csv_import",
      importer: "csv",
      ts: nowIso(),
      receiptsImported: newReceipts.length,
      receiptsSkipped: 0,
      articlesUnmatched: matches.filter(m => !m.match && m.action !== "create").length,
      totalRevenue: Math.round(csvTotalRev * 100) / 100,
    };
    const csvPrevHistory = state.importHistory || [];
    const csvNewHistory = [...csvPrevHistory.slice(-99), csvImportRecord];
    // FIX delta (audit 2026-06-04): delta minimo, nessuna copia completa di tableReceipts.
    // Import CSV non ha gestione conflitti/replace, quindi removedReceipts è sempre [].
    const csvUndoSnapshot = {
      type: "import",
      ts: csvImportRecord.ts,
      importId: csvImportRecord.id,
      fileName: "csv_import",
      receiptsImported: newReceipts.length,
      delta: buildUndoDelta(newReceipts, []),
      prevImportHistory: csvPrevHistory,
    };

    setState({
      ...state,
      tableReceipts: [...(state.tableReceipts || []), ...newReceipts],
      dishesReal: [...(state.dishesReal || []), ...newDishes],
      dishAliases: newAliases,
      importHistory: csvNewHistory,
      _undoSnapshot: csvUndoSnapshot,
    });
    toast({ type: "success", title: "Import completato", message: `${newReceipts.length} scontrini · ${newDishes.length} piatti creati · ${fmt.eur(newReceipts.reduce((s, r) => s + r.total, 0))} incasso` });
    onClose();
  };

  // UI
  return <Modal onClose={onClose} maxWidth={1000} subtitle="Import da gestionale" title="Importa scontrini CSV"
    footer={<div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
      <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
      {step === "mapping" && <Btn variant="primary" icon={ArrowRight} onClick={goToMatching}>Analizza matching</Btn>}
      {step === "matching" && <>
        <Btn variant="ghost" onClick={() => setStep("mapping")}>Indietro</Btn>
        <Btn variant="ghost" icon={Play} onClick={() => { setDryRun(true); setTimeout(() => { doImport(); setDryRun(false); }, 100); }}>Simula</Btn>
        <Btn variant="accent" icon={CheckCircle2} onClick={doImport}>Importa {stats.matched + stats.willCreate} righe</Btn>
      </>}
    </div>}>
    {/* FIX F-03: modal conferma re-import stesso file (dedup hash) */}
    {pendingFileLoad && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: C.white, borderRadius: 4, padding: 28, maxWidth: 420, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <AlertTriangle size={20} color={C.amberDeep} />
          <div className="f-display" style={{ fontWeight: 600, fontSize: 15, color: C.ink }}>File gia caricato</div>
        </div>
        <div className="f-body" style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.5, marginBottom: 20 }}>
          Questo file sembra gia stato importato il <strong>{pendingFileLoad.existingEntry?.ts ? new Date(pendingFileLoad.existingEntry.ts).toLocaleString("it-IT") : "data sconosciuta"}</strong> ({pendingFileLoad.existingEntry?.receiptsImported ?? "?"} scontrini).
          <br /><br />
          Continuare comunque potrebbe raddoppiare gli scontrini gia presenti.
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setPendingFileLoad(null)}>Annulla</Btn>
          <Btn variant="primary" onClick={async () => { const f = pendingFileLoad.file; setPendingFileLoad(null); await doLoadFile(f); }}>Carica comunque</Btn>
        </div>
      </div>
    </div>}
    <div style={{ padding: 20 }}>

      {step === "upload" && <div>
        <div style={{ padding: 16, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <Info size={16} color={C.teal} style={{ marginTop: 2 }} />
            <div className="f-body" style={{ fontSize: 12, color: C.tealDeep, lineHeight: 1.5 }}>
              <strong>Formato CSV atteso:</strong> una riga per ogni piatto venduto. Colonne minime: <strong>tavolo, nome piatto, quantità, prezzo</strong>. Optional: data, ora, coperti, categoria. Delimitatore virgola, punto-e-virgola o tab (rilevato automaticamente). Codifica UTF-8.
              <br /><br />
              <strong>Come funziona:</strong>
              <br />1️⃣ Carichi il CSV · 2️⃣ Mappi le colonne · 3️⃣ Il sistema fa matching fuzzy con i tuoi {(state.dishesReal || []).length} piatti · 4️⃣ Confermi e genera scontrini storicizzati.
              <br /><br />
              <strong>Memoria alias:</strong> dopo il primo import il sistema salva la corrispondenza "nome_csv → piatto" così le prossime importazioni sono istantanee per nomi già visti ({Object.keys(state.dishAliases || {}).length} alias memorizzati).
            </div>
          </div>
        </div>

        <div onClick={() => fileRef.current?.click()} style={{ border: `2px dashed ${C.rule}`, padding: 32, borderRadius: 2, textAlign: "center", cursor: "pointer", background: C.paperDeep }}
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.teal; }}
          onDragLeave={e => e.currentTarget.style.borderColor = C.rule}
          onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.rule; if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); }}>
          <Upload size={36} color={C.muted} strokeWidth={1.3} style={{ margin: "0 auto 12px", display: "block" }} />
          <div className="f-display" style={{ fontSize: 16, color: C.ink, fontWeight: 500 }}>Trascina qui il file CSV o clicca per sceglierlo</div>
          <div className="f-body" style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>CSV, TSV · UTF-8 · max 10 MB</div>
          <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" style={{ display: "none" }} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
        </div>
      </div>}

      {step === "mapping" && parsed && <div>
        <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2, marginBottom: 14, fontSize: 12, color: C.inkSoft }}>
          <strong>File caricato:</strong> {parsed.rows.length} righe · delimitatore <code style={{ background: C.white, padding: "1px 5px", borderRadius: 2 }}>{parsed.delimiter === "\t" ? "TAB" : parsed.delimiter}</code> · <label style={{ cursor: "pointer" }}><input type="checkbox" checked={hasHeader} onChange={e => setHasHeader(e.target.checked)} /> La prima riga è l'intestazione</label>
        </div>

        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", marginBottom: 8 }}>MAPPA LE COLONNE</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
          {[
            ["tableNumber", "Tavolo (numero)", true],
            ["dishName", "Nome piatto", true],
            ["qty", "Quantità", false],
            ["price", "Prezzo unitario", false],
            ["covers", "Coperti", false],
            ["date", "Data", false],
            ["time", "Ora", false],
            ["category", "Categoria (opzionale)", false],
          ].map(([key, label, req]) => <Field key={key} label={label} required={req}>
            <Select value={mapping[key] ?? ""} onChange={e => setMapping({ ...mapping, [key]: e.target.value === "" ? null : parseInt(e.target.value) })}>
              <option value="">-- non mappata --</option>
              {(hasHeader ? parsed.rows[0] : parsed.rows[0].map((_, i) => `Colonna ${i + 1}`)).map((h, i) => <option key={i} value={i}>{h || `Colonna ${i + 1}`}</option>)}
            </Select>
          </Field>)}
        </div>

        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", marginBottom: 8 }}>ANTEPRIMA PRIME 5 RIGHE</div>
        <div style={{ maxHeight: 220, overflow: "auto", border: `1px solid ${C.rule}`, borderRadius: 2 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, fontFamily: "JetBrains Mono, monospace" }}>
            <thead><tr style={{ background: C.paperDeep }}>
              {(hasHeader ? parsed.rows[0] : parsed.rows[0].map((_, i) => `Col ${i + 1}`)).map((h, i) => <th key={i} style={{ textAlign: "left", padding: "6px 10px", borderBottom: `1px solid ${C.rule}`, fontWeight: 600 }}>{h || `Col ${i + 1}`}</th>)}
            </tr></thead>
            <tbody>
              {(hasHeader ? parsed.rows.slice(1, 6) : parsed.rows.slice(0, 5)).map((r, i) => <tr key={i}>
                {r.map((cell, j) => <td key={j} style={{ padding: "4px 10px", borderBottom: `1px solid ${C.ruleSoft}`, color: C.inkSoft }}>{cell}</td>)}
              </tr>)}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 14, padding: 10, background: C.paperDeep, borderRadius: 2 }}>
          <Field label="Come gestire piatti non trovati in anagrafica">
            <Select value={unmatchedAction} onChange={e => setUnmatchedAction(e.target.value)}>
              <option value="skip">Salta (non importare quelle righe)</option>
              <option value="create">Crea piatto placeholder con prezzo del CSV (categoria bar default, senza ricetta)</option>
            </Select>
          </Field>
        </div>
      </div>}

      {step === "matching" && <div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 16 }}>
          <div style={{ background: C.oliveWash, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.olive, letterSpacing: "0.2em" }}>MATCH OK</div>
            <div className="f-display" style={{ fontSize: 22, color: C.oliveDeep, fontWeight: 600 }}>{stats.matched}</div>
          </div>
          <div style={{ background: C.terraWash, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.terra, letterSpacing: "0.2em" }}>SCONOSCIUTI</div>
            <div className="f-display" style={{ fontSize: 22, color: C.terra, fontWeight: 600 }}>{stats.unmatched}</div>
          </div>
          <div style={{ background: C.amberWash, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.amberDeep, letterSpacing: "0.2em" }}>SALTATI</div>
            <div className="f-display" style={{ fontSize: 22, color: C.amberDeep, fontWeight: 600 }}>{stats.skipped}</div>
          </div>
          <div style={{ background: C.tealWash, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.teal, letterSpacing: "0.2em" }}>TAVOLI</div>
            <div className="f-display" style={{ fontSize: 22, color: C.tealDeep, fontWeight: 600 }}>{stats.tables.size}</div>
          </div>
          <div style={{ background: C.ink, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.mutedSoft, letterSpacing: "0.2em" }}>INCASSO</div>
            <div className="f-display" style={{ fontSize: 18, color: C.paper, fontWeight: 600 }}>{fmt.eur(stats.total)}</div>
          </div>
        </div>

        <div style={{ maxHeight: 420, overflowY: "auto", border: `1px solid ${C.rule}`, borderRadius: 2 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead><tr style={{ background: C.paperDeep, position: "sticky", top: 0, zIndex: 1 }}>
              <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Tavolo</th>
              <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Nome CSV</th>
              <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Piatto matchato</th>
              <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Score</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Qtà</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>€</th>
              <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Azione</th>
            </tr></thead>
            <tbody>
              {matches.map((m, i) => {
                const scoreColor = !m.match ? C.terra : m.match.score >= 0.95 ? C.olive : m.match.score >= 0.75 ? C.amber : C.terra;
                return <tr key={i} style={{ borderBottom: `1px solid ${C.ruleSoft}`, background: m.action === "skip" ? C.paperDeep : "transparent" }}>
                  <td style={{ padding: "5px 10px" }} className="f-mono">{m.table}</td>
                  <td style={{ padding: "5px 10px", color: C.ink }}>{m.nomeCsv}</td>
                  <td style={{ padding: "5px 10px" }}>
                    {m.match ? <span style={{ color: C.ink, fontWeight: 500 }}>{m.match.dish.nome}</span>
                    : <select value={m.action === "create" ? "__CREATE__" : ""} onChange={e => {
                        if (e.target.value === "__CREATE__") setMatches(matches.map((x, j) => j === i ? { ...x, action: "create" } : x));
                        else if (e.target.value === "__SKIP__") setMatches(matches.map((x, j) => j === i ? { ...x, action: "skip" } : x));
                        else {
                          const d = (state.dishesReal || []).find(dd => dd.id === e.target.value);
                          if (d) setMatches(matches.map((x, j) => j === i ? { ...x, match: { dish: d, score: 1, source: "manual" }, action: "use" } : x));
                        }
                      }} style={{ fontSize: 11, padding: "3px 6px", border: `1px solid ${C.ruleSoft}` }}>
                        <option value="">-- scegli --</option>
                        <option value="__CREATE__">🆕 Crea piatto nuovo</option>
                        <option value="__SKIP__">❌ Salta questa riga</option>
                        <optgroup label="Piatti esistenti">
                          {(state.dishesReal || []).map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
                        </optgroup>
                      </select>}
                  </td>
                  <td style={{ padding: "5px 10px", textAlign: "center" }}>
                    {m.match && <span className="f-mono" style={{ color: scoreColor, fontWeight: 600, fontSize: 10 }}>
                      {(m.match.score * 100).toFixed(0)}% {m.match.source === "alias" ? "★" : m.match.source === "exact" ? "✓" : m.match.source === "manual" ? "⚙" : "~"}
                    </span>}
                  </td>
                  <td style={{ padding: "5px 10px", textAlign: "right" }} className="f-mono">{m.qty}</td>
                  <td style={{ padding: "5px 10px", textAlign: "right" }} className="f-mono">{fmt.eur(m.price)}</td>
                  <td style={{ padding: "5px 10px", textAlign: "center" }}>
                    <select value={m.action} onChange={e => setMatches(matches.map((x, j) => j === i ? { ...x, action: e.target.value } : x))}
                      style={{ fontSize: 11, padding: "2px 4px", border: `1px solid ${C.ruleSoft}`, borderRadius: 2 }}>
                      <option value="use" disabled={!m.match}>Importa</option>
                      <option value="create" disabled={!!m.match}>Crea</option>
                      <option value="skip">Salta</option>
                    </select>
                  </td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 14, padding: 10, background: C.paperDeep, borderRadius: 2, fontSize: 11, color: C.inkSoft, lineHeight: 1.5 }}>
          <strong>Legenda score:</strong> ★ alias memorizzato · ✓ match esatto · ~ fuzzy. <strong>Azioni:</strong> Importa (usa il piatto matchato) · Crea (aggiunge piatto nuovo all'anagrafica) · Salta (ignora riga). I nuovi alias verranno salvati automaticamente: la prossima volta il match sarà 100%.
        </div>
      </div>}

    </div>
  </Modal>;
}


// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                  SALA · ANALITICA & PROIEZIONI                             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function SalaAnalytics({ state, setState }) {
  const [horizon, setHorizon] = useState(7);  // 7 | 14 | 30
  const [subTab, setSubTab] = useState("dashboard");
  const [importOpen, setImportOpen] = useState(false);
  const toast = useToast();

  const receipts = state.tableReceipts || [];
  const metrics = useMemo(() => engine.dailyMetrics(state), [state.tableReceipts]);
  const forecast = useMemo(() => engine.forecastNDays(state, horizon), [state.tableReceipts, horizon]);
  const ingForecast = useMemo(() => engine.forecastIngredients(state, horizon), [state.tableReceipts, state.baseProducts, state.dishesReal, horizon]);
  const menuEng = useMemo(() => engine.menuEngineering(state), [state.tableReceipts, state.dishesReal, state.baseProducts]);

  if (receipts.length === 0) return <EmptyState icon={TrendingUp} title="Nessun dato storico"
    body="Le statistiche e le proiezioni si popolano con gli scontrini chiusi. Puoi iniziare a chiudere tavoli dalla tab 'Tavoli aperti' oppure importare il tuo storico da Excel del gestionale cassa."
    action={<Btn variant="accent" icon={Upload} onClick={() => setImportOpen(true)}>Importa storico Excel/CSV</Btn>} />;

  return <div>
    {importOpen && <HistoricalImporter state={state} setState={setState} onClose={() => setImportOpen(false)} />}

    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
      <div>
        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted }}>AFFIDABILITÀ MODELLO</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
          <ConfidenceBadge level={forecast.confidence} />
          <span className="f-body" style={{ fontSize: 12, color: C.inkSoft }}>basato su {forecast.dataPoints} giorni di storico</span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <Btn variant="ghost" size="sm" icon={Upload} onClick={() => setImportOpen(true)}>Importa storico</Btn>
      </div>
    </div>

    <Tabs value={subTab} onChange={setSubTab} tabs={[
      { id: "dashboard", label: "Cruscotto", icon: Activity },
      { id: "history", label: "Analisi storica", icon: BarChart3 },
      { id: "dishMatrix", label: "Piatti per servizio", icon: Utensils },
      { id: "avgTicket", label: "Scontrino/coperto", icon: Receipt },
      { id: "anatomy", label: "Anatomia scontrino", icon: Zap },
      { id: "menu", label: "Menu Engineering", icon: TrendingUp },
      { id: "crossSell", label: "Cross-sell", icon: Link2 },
      { id: "forecast", label: "Proiezioni", icon: Calendar },
      { id: "ingredients", label: "Fabbisogno M.P.", icon: Package },
    ]} />

    {subTab === "dashboard" && <AnalyticsDashboard state={state} metrics={metrics} forecast={forecast} />}
    {subTab === "history" && <AnalyticsHistory state={state} metrics={metrics} forecast={forecast} />}
    {subTab === "dishMatrix" && <AnalyticsDishMatrix state={state} />}
    {subTab === "avgTicket" && <AnalyticsAvgTicket state={state} />}
    {subTab === "anatomy" && <AnalyticsAnatomy state={state} setState={setState} />}
    {subTab === "menu" && <AnalyticsMenuEngineering state={state} menuEng={menuEng} />}
    {subTab === "crossSell" && <AnalyticsCrossSell state={state} />}
    {subTab === "forecast" && <AnalyticsForecast state={state} forecast={forecast} horizon={horizon} setHorizon={setHorizon} metrics={metrics} />}
    {subTab === "ingredients" && <AnalyticsIngredients state={state} setState={setState} ingForecast={ingForecast} horizon={horizon} setHorizon={setHorizon} />}
  </div>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                    ANALYTICS KPI — INDICATORI DERIVATI                     ║
// ║   Penetrazione, mix, cross-sell, performance, anomalie con benchmark       ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function AnalyticsKPI({ state, setState, onNavigate }) {
  const [period, setPeriod] = useState("30d");
  const [compareWith, setCompareWith] = useState("prev_period");
  const [expandedKpi, setExpandedKpi] = useState(null);

  const kpiData = useMemo(() => engine.kpiDerived(state, period, compareWith), [state.tableReceipts, state.benchmarksKPI, state.dishesReal, period, compareWith]);

  // Mappa colore string → C.color
  const colorMap = {
    oliveDeep: C.oliveDeep,
    olive: C.olive,
    amber: C.amber,
    terra: C.terra,
    muted: C.muted,
  };
  const bgMap = {
    oliveDeep: C.oliveWash,
    olive: C.oliveWash,
    amber: C.amberWash,
    terra: C.terraWash,
    muted: C.paperDeep,
  };

  // Raggruppa KPI per famiglia
  const families = {
    "Penetrazione coperto": ["dolciPerCop", "contorniPerCop", "viniPerCop", "caffePerCop", "antipastiPerCop", "primiPerCop", "secondiPerCop"],
    "Mix di vendita (% ricavo)": ["mixVini", "mixBar", "mixDolci", "mixCaffe", "mixFoodBev"],
    "Scontrino & Cross-sell": ["ticketCop", "ticketCopPranzo", "ticketCopCena", "ticketTavolo", "portatePerCop", "articoliPerScn", "pctMenuCompleto"],
    "Performance fascia": ["coveriPranzoMedia", "coveriCenaMedia"],
  };

  if (!kpiData.current) return <EmptyState icon={Activity} title="Nessuno scontrino nel periodo selezionato"
    body={`Il periodo ${period} non contiene scontrini importati. Importa scontrini o cambia periodo.`} />;

  const formatValue = (k) => {
    if (k.key.startsWith("ticket")) return fmt.eur(k.value);
    if (k.key.startsWith("mix") || k.key.startsWith("pct")) return `${k.value.toFixed(1)}%`;
    if (k.key.includes("PerCop") || k.key.includes("Per") && !k.key.includes("Pranzo")) return k.value.toFixed(2);
    return k.value.toFixed(1);
  };

  // Mappa key KPI → categoria DISH_CATEGORIES per recuperare il Wilson CI.
  // Il Wilson CI ha senso statistico per n>=30 (sotto e' molto largo); sotto i 10
  // mostriamo solo "dati insufficienti".
  const KPI_TO_CAT = {
    dolciPerCop: "dolci",
    contorniPerCop: "contorni",
    viniPerCop: "vini",
    caffePerCop: "caffetteria",
    antipastiPerCop: "antipasti",
    primiPerCop: "primi",
    secondiPerCop: "secondi",
  };
  const ciByCat = kpiData.ciByCat || {};

  return <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

    {/* Toolbar periodo + confronto */}
    <div style={{ background: C.paperDeep, padding: 14, borderRadius: 2, display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
      <div>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em", marginBottom: 4 }}>PERIODO</div>
        <Select value={period} onChange={e => setPeriod(e.target.value)} style={{ padding: "6px 10px", fontSize: 12 }}>
          <option value="today">Oggi</option>
          <option value="7d">Ultimi 7 giorni</option>
          <option value="30d">Ultimi 30 giorni</option>
          <option value="90d">Ultimi 90 giorni</option>
          <option value="year">Ultimo anno</option>
        </Select>
      </div>
      <div>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em", marginBottom: 4 }}>CONFRONTA CON</div>
        <Select value={compareWith} onChange={e => setCompareWith(e.target.value)} style={{ padding: "6px 10px", fontSize: 12 }}>
          <option value="prev_period">Periodo precedente di pari durata</option>
          <option value="year_ago">Stesso periodo anno scorso</option>
        </Select>
      </div>
      <div style={{ marginLeft: "auto", display: "flex", gap: 18 }}>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>SCONTRINI {kpiData.nBancoReceipts > 0 ? "(TAVOLO+BANCO)" : ""}</div>
          <div className="f-mono" style={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>{kpiData.nReceipts}{kpiData.nBancoReceipts > 0 ? <span style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}> ({kpiData.nTableReceipts} T · {kpiData.nBancoReceipts} B)</span> : null}</div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>COPERTI</div>
          <div className="f-mono" style={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>{kpiData.nCovers}</div>
        </div>
        <div title={kpiData.totalRevNet > 0 ? `Netto IVA scorporato per riga: ${fmt.eur(kpiData.totalRevNet)}` : ""}>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>INCASSO LORDO</div>
          <div className="f-mono" style={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>{fmt.eurShort(kpiData.totalRev)}{kpiData.bancoMixPct > 0 ? <span style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}> ({kpiData.bancoMixPct.toFixed(0)}% banco)</span> : null}</div>
        </div>
        <Btn size="sm" variant="ghost" icon={Settings} onClick={() => onNavigate && onNavigate({ view: "anagrafiche", tab: "benchmark" })}>Imposta benchmark</Btn>
      </div>
    </div>

    {/* Riepilogo critici / eccellenti */}
    {(kpiData.critical.length > 0 || kpiData.warning.length > 0 || kpiData.excellent.length > 0) && <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
      <div style={{ padding: 14, background: kpiData.critical.length > 0 ? C.terraWash : C.paperDeep, border: `1px solid ${kpiData.critical.length > 0 ? C.terra : C.rule}`, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: kpiData.critical.length > 0 ? C.terra : C.muted, letterSpacing: "0.2em" }}>🔴 SOTTO TARGET (CRITICO)</div>
        <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color: kpiData.critical.length > 0 ? C.terra : C.muted, marginTop: 3 }}>{kpiData.critical.length}</div>
        <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, marginTop: 3 }}>{kpiData.critical.slice(0, 2).map(k => k.label).join(", ") || "—"}</div>
      </div>
      <div style={{ padding: 14, background: kpiData.warning.length > 0 ? C.amberWash : C.paperDeep, border: `1px solid ${kpiData.warning.length > 0 ? C.amber : C.rule}`, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: kpiData.warning.length > 0 ? C.amberDeep : C.muted, letterSpacing: "0.2em" }}>🟡 IN CALO</div>
        <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color: kpiData.warning.length > 0 ? C.amberDeep : C.muted, marginTop: 3 }}>{kpiData.warning.length}</div>
        <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, marginTop: 3 }}>{kpiData.warning.slice(0, 2).map(k => k.label).join(", ") || "—"}</div>
      </div>
      <div style={{ padding: 14, background: kpiData.excellent.length > 0 ? C.oliveWash : C.paperDeep, border: `1px solid ${kpiData.excellent.length > 0 ? C.olive : C.rule}`, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: kpiData.excellent.length > 0 ? C.oliveDeep : C.muted, letterSpacing: "0.2em" }}>🟢 ECCELLENTI</div>
        <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color: kpiData.excellent.length > 0 ? C.oliveDeep : C.muted, marginTop: 3 }}>{kpiData.excellent.length}</div>
        <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, marginTop: 3 }}>{kpiData.excellent.slice(0, 2).map(k => k.label).join(", ") || "—"}</div>
      </div>
    </div>}

    {/* KPI per famiglia */}
    {Object.keys(families).map(fam => {
      const famKpis = kpiData.kpis.filter(k => families[fam].includes(k.key));
      if (famKpis.length === 0) return null;
      return <div key={fam}>
        <SectionHeader title={fam} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
          {famKpis.map(k => {
            const isExp = expandedKpi === k.key;
            const c = colorMap[k.color] || C.muted;
            const bg = bgMap[k.color] || C.paperDeep;
            return <button key={k.key} onClick={() => setExpandedKpi(isExp ? null : k.key)} className="tx" style={{
              background: C.white, border: `1px solid ${C.rule}`, borderLeft: `4px solid ${c}`,
              padding: 14, borderRadius: 2, cursor: "pointer", textAlign: "left", fontFamily: "inherit",
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{k.label.toUpperCase()}</div>
                </div>
                <span style={{ fontSize: 14, color: c, fontWeight: 700, marginLeft: 6 }} title={k.level.replace("_", " ")}>{k.symbol}</span>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span className="f-display" style={{ fontSize: 24, fontWeight: 600, color: C.ink, letterSpacing: "-0.01em" }}>{formatValue(k)}</span>
                <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>target {k.key.startsWith("ticket") ? fmt.eur(k.target) : k.key.startsWith("mix") || k.key.startsWith("pct") ? `${k.target}%` : k.target.toFixed(2)}</span>
              </div>
              {/* Wilson CI 95% per penetrazione coperto — onesta' statistica su n piccolo */}
              {(() => {
                const catKey = KPI_TO_CAT[k.key];
                if (!catKey) return null;
                const ci = ciByCat[catKey];
                if (!ci || !ci.n) return null;
                if (ci.n < 10) {
                  return <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>
                    n={ci.n} · dati insufficienti per CI
                  </div>;
                }
                return <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 4 }} title="Intervallo di confidenza 95% (Wilson score). Indica il range plausibile della 'vera' penetrazione, dati i coperti osservati.">
                  CI95 [{ci.ciLow.toFixed(2)}—{ci.ciHigh.toFixed(2)}] · n={ci.n}
                </div>;
              })()}
              {k.delta != null && <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 10, color: k.delta >= 0 ? C.olive : C.terra, fontWeight: 600 }}>{k.delta >= 0 ? "↑" : "↓"} {Math.abs(k.delta).toFixed(1)}%</span>
                <span className="f-mono" style={{ fontSize: 9, color: C.muted }}>vs periodo precedente</span>
              </div>}
              {isExp && <div className="fade-in" style={{ marginTop: 10, padding: 10, background: bg, borderRadius: 2 }}>
                <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, lineHeight: 1.5 }}>
                  <div><strong>Soglia scarsa:</strong> {k.key.startsWith("ticket") ? fmt.eur(k.scarso) : k.scarso.toFixed(2)}{k.key.startsWith("mix") || k.key.startsWith("pct") ? "%" : ""}</div>
                  <div><strong>Soglia target:</strong> {k.key.startsWith("ticket") ? fmt.eur(k.target) : k.target.toFixed(2)}{k.key.startsWith("mix") || k.key.startsWith("pct") ? "%" : ""}</div>
                  <div><strong>Soglia ottima:</strong> {k.key.startsWith("ticket") ? fmt.eur(k.ottimo) : k.ottimo.toFixed(2)}{k.key.startsWith("mix") || k.key.startsWith("pct") ? "%" : ""}</div>
                  {k.prevValue != null && <div style={{ marginTop: 4 }}><strong>Periodo precedente:</strong> {k.key.startsWith("ticket") ? fmt.eur(k.prevValue) : k.prevValue.toFixed(2)}{k.key.startsWith("mix") || k.key.startsWith("pct") ? "%" : ""}</div>}
                  {k.level === "molto_sotto" && <div style={{ marginTop: 6, color: C.terra, fontWeight: 600 }}>⚠ Sotto soglia critica. Azione consigliata.</div>}
                  {k.level === "molto_sopra" && <div style={{ marginTop: 6, color: C.olive, fontWeight: 600 }}>✓ Performance eccellente.</div>}
                </div>
              </div>}
            </button>;
          })}
        </div>
      </div>;
    })}

    {/* Legenda */}
    <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2, fontSize: 11, color: C.inkSoft, lineHeight: 1.7 }}>
      <strong>Legenda semaforo:</strong>{" "}
      <span style={{ color: C.oliveDeep, fontWeight: 700 }}>▲▲</span> molto sopra · 
      <span style={{ color: C.olive, fontWeight: 700 }}> ▲</span> sopra · 
      <span style={{ color: C.muted, fontWeight: 700 }}> =</span> in linea · 
      <span style={{ color: C.amber, fontWeight: 700 }}> ▼</span> sotto · 
      <span style={{ color: C.terra, fontWeight: 700 }}> ▼▼</span> molto sotto.
      Click su una card per vedere soglie e dettagli. I benchmark sono modificabili da Anagrafiche → Benchmark KPI.
    </div>
  </div>;
}


function ConfidenceBadge({ level }) {
  const conf = {
    alta:           { label: "ALTA", color: C.olive, bg: C.oliveWash },
    media:          { label: "MEDIA", color: C.amberDeep, bg: C.amberWash },
    bassa:          { label: "BASSA", color: C.terra, bg: C.terraWash },
    insufficienti:  { label: "DATI INSUFFICIENTI", color: C.terra, bg: C.terraWash },
    nessuno:        { label: "N/D", color: C.muted, bg: C.paperDeep },
  }[level] || { label: level.toUpperCase(), color: C.muted, bg: C.paperDeep };
  return <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.15em", padding: "3px 8px", background: conf.bg, color: conf.color, borderRadius: 2, fontWeight: 600 }}>
    {conf.label}
  </span>;
}

// ── Cruscotto operativo giornata in corso ──────────────────────────────────
function AnalyticsDashboard({ state, metrics, forecast }) {
  const todayData = metrics.find(m => m.date === TODAY);
  const todayRev = todayData?.revenue || 0;
  const todayCov = todayData?.covers || 0;
  const todayPoint = forecast.points[0] || null;
  const targetRev = todayPoint?.expected || 0;
  const targetRange = todayPoint ? { low: todayPoint.low, high: todayPoint.high } : null;
  const pctOfTarget = targetRev > 0 ? (todayRev / targetRev) * 100 : 0;
  const isGrossMode = engine.priceGross(state);

  // Distribuzione per ora oggi
  const hourDist = useMemo(() => {
    const today = (state.tableReceipts || []).filter(r => getServiceDate(r.closedAt) === TODAY);
    const byHour = {};
    today.forEach(r => {
      const h = new Date(r.closedAt).getHours();
      byHour[h] = (byHour[h] || 0) + r.total;
    });
    return byHour;
  }, [state.tableReceipts]);

  // Picco di ore (80% dell'incasso)
  const peakHours = useMemo(() => {
    const total = Object.values(hourDist).reduce((s, v) => s + v, 0);
    if (total === 0) return [];
    const sorted = Object.entries(hourDist).sort((a, b) => b[1] - a[1]);
    const peak = [];
    let acc = 0;
    for (const [h, v] of sorted) {
      peak.push(parseInt(h));
      acc += v;
      if (acc >= total * 0.8) break;
    }
    return peak.sort((a, b) => a - b);
  }, [hourDist]);

  // ── CUSUM drift detection sul food cost % giornaliero ────────────────────
  // Aggrega per data, calcola food cost % = snapCost/rev usando recipeSnapshot
  // quando disponibile (storicamente fedele). Soglia ≥14gg con fc>0 per essere
  // statisticamente affidabile (sotto: serie troppo corta).
  const fcDriftSeries = useMemo(() => {
    const map = new Map();
    for (const r of (state.tableReceipts || [])) {
      const date = getServiceDate(r.closedAt) || r.date;
      if (!date) continue;
      let snapCost = 0, rev = 0;
      for (const it of (r.items || [])) {
        const qty = Number(it.qty) || 0;
        const unitPrice = Number(it.unitPrice) || 0;
        const snapNet = Number(it.recipeSnapshot?.netCost);
        rev += unitPrice * qty;
        if (Number.isFinite(snapNet)) snapCost += snapNet * qty;
      }
      const e = map.get(date) || { cost: 0, rev: 0 };
      e.cost += snapCost;
      e.rev += rev;
      map.set(date, e);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-60)
      .map(([date, v]) => ({ date, fc: v.rev > 0 ? v.cost / v.rev : 0, rev: v.rev }))
      .filter(x => x.fc > 0);
  }, [state.tableReceipts]);
  const fcDrift = useMemo(() => {
    const values = fcDriftSeries.map(s => s.fc);
    return engine.stats.cusumChangepoint(values);
  }, [fcDriftSeries]);

  // CUSUM anche su daily revenue: indipendente dal food cost ed estremamente
  // sensibile a eventi (chiusura per ristrutturazione, nuovo concept, ecc.).
  const revDriftSeries = useMemo(() => {
    const map = new Map();
    for (const r of (state.tableReceipts || [])) {
      const date = getServiceDate(r.closedAt) || r.date;
      if (!date) continue;
      map.set(date, (map.get(date) || 0) + (Number(r.total) || 0));
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-60)
      .map(([date, rev]) => ({ date, rev }))
      .filter(x => x.rev > 0);
  }, [state.tableReceipts]);
  const revDrift = useMemo(() => {
    const values = revDriftSeries.map(s => s.rev);
    return engine.stats.cusumChangepoint(values);
  }, [revDriftSeries]);

  return <div>
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 14, marginBottom: 18 }}>
      {/* Card target oggi */}
      <div style={{ background: C.white, border: `1px solid ${pctOfTarget >= 100 ? C.olive : pctOfTarget >= 70 ? C.amber : C.rule}`, padding: 20, borderRadius: 2, position: "relative", overflow: "hidden" }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", marginBottom: 6 }}>TARGET INCASSO OGGI</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 4 }}>
          <div className="f-display" style={{ fontSize: 34, fontWeight: 600, color: C.ink }}>{fmt.eur(todayRev)}</div>
          {targetRev > 0 && <div className="f-mono" style={{ fontSize: 14, color: C.muted }}>/ {fmt.eur(targetRev)}</div>}
        </div>
        {targetRev > 0 && <>
          <div style={{ position: "relative", height: 8, background: C.paperDeep, borderRadius: 2, overflow: "hidden", marginBottom: 6 }}>
            <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${Math.min(pctOfTarget, 150)}%`, background: pctOfTarget >= 100 ? C.olive : pctOfTarget >= 70 ? C.amber : C.terra, transition: "width 0.5s" }}></div>
          </div>
          <div className="f-mono" style={{ fontSize: 11, color: C.inkSoft }}>
            <strong>{pctOfTarget.toFixed(0)}%</strong> del target · range atteso: {fmt.eur(targetRange?.low || 0)} — {fmt.eur(targetRange?.high || 0)}
          </div>
        </>}
      </div>

      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>COPERTI OGGI</div>
        <div className="f-display" style={{ fontSize: 28, fontWeight: 600, color: C.ink, marginTop: 4 }}>{todayCov}</div>
        <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>atteso: {todayPoint?.covers || 0}</div>
      </div>

      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 20, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>ORE DI PUNTA</div>
        <div className="f-display" style={{ fontSize: 20, fontWeight: 600, color: C.ink, marginTop: 4 }}>{peakHours.length > 0 ? peakHours.map(h => `${h}:00`).join(" · ") : "—"}</div>
        <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>80% dell'incasso</div>
      </div>
    </div>

    {/* Distribuzione oraria */}
    {Object.keys(hourDist).length > 0 && <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 18, borderRadius: 2, marginBottom: 18 }}>
      <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", marginBottom: 10 }}>DISTRIBUZIONE ORARIA OGGI</div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 80 }}>
        {Array.from({ length: 16 }, (_, i) => i + 8).map(h => {
          const v = hourDist[h] || 0;
          const max = Math.max(...Object.values(hourDist), 1);
          const hh = (v / max) * 70;
          const isPeak = peakHours.includes(h);
          return <div key={h} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }} title={`${h}:00 · ${fmt.eur(v)}`}>
            <div style={{ width: "100%", height: Math.max(hh, 1), background: isPeak ? C.ink : v > 0 ? C.teal : C.ruleSoft, borderRadius: "1px 1px 0 0" }}></div>
            <div className="f-mono" style={{ fontSize: 8, color: isPeak ? C.ink : C.muted, fontWeight: isPeak ? 600 : 400 }}>{h}</div>
          </div>;
        })}
      </div>
    </div>}

    {/* DRIFT DETECTION (CUSUM): cambi di regime food cost % e ricavo giornaliero */}
    {(fcDrift.found || revDrift.found) && <div style={{ marginBottom: 18, display: "grid", gap: 10 }}>
      {fcDrift.found && (() => {
        const cpDate = fcDriftSeries[fcDrift.changePointAt]?.date || "?";
        const up = fcDrift.afterMean > fcDrift.beforeMean;
        const direction = up ? "salito" : "sceso";
        const deltaPp = Math.abs(fcDrift.afterMean - fcDrift.beforeMean) * 100;
        // Impatto mensile stimato: Δfc% × ricavo medio giornaliero × 30
        const meanRev = revDriftSeries.length > 0
          ? revDriftSeries.reduce((a, b) => a + b.rev, 0) / revDriftSeries.length
          : 200;
        const monthlyImpact = (fcDrift.afterMean - fcDrift.beforeMean) * meanRev * 30;
        const cpFmt = (() => { try { return new Date(cpDate + "T12:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return cpDate; } })();
        return <div style={{ background: C.amberWash, border: `1px solid ${C.amber}`, padding: 14, borderRadius: 2 }}>
          <div className="f-mono" style={{ fontSize: 10, color: C.amberDeep, letterSpacing: "0.2em", fontWeight: 600 }}>⚠ CAMBIO DI REGIME · FOOD COST %</div>
          <div className="f-display" style={{ fontSize: 14, color: C.ink, marginTop: 6, fontWeight: 600, lineHeight: 1.4 }}>
            Food cost {direction} da <strong>{(fcDrift.beforeMean * 100).toFixed(1)}%</strong> a <strong>{(fcDrift.afterMean * 100).toFixed(1)}%</strong> (Δ {up ? "+" : "−"}{deltaPp.toFixed(1)}pp) intorno al <strong>{cpFmt}</strong>
          </div>
          <div className="f-body" style={{ fontSize: 11, color: C.inkMid, marginTop: 6, lineHeight: 1.5 }}>
            Cause probabili: cambio fornitore (controlla priceHistory delle materie prime), nuovo cuoco/turno, modifica ricette, evento speciale, errore nello scarico magazzino. Impatto mensile stimato: <strong>{monthlyImpact >= 0 ? "+" : "−"}{fmt.eur(Math.abs(monthlyImpact))}/mese</strong>. Indaga prima di considerarlo strutturale.
          </div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 6, letterSpacing: "0.1em" }}>Metodo: CUSUM (Page 1954) su {fcDriftSeries.length} giorni · soglia 3σ</div>
        </div>;
      })()}
      {revDrift.found && (() => {
        const cpDate = revDriftSeries[revDrift.changePointAt]?.date || "?";
        const up = revDrift.afterMean > revDrift.beforeMean;
        const direction = up ? "salito" : "sceso";
        const deltaPct = revDrift.beforeMean > 0 ? ((revDrift.afterMean - revDrift.beforeMean) / revDrift.beforeMean) * 100 : 0;
        const cpFmt = (() => { try { return new Date(cpDate + "T12:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return cpDate; } })();
        return <div style={{ background: up ? C.oliveWash : C.amberWash, border: `1px solid ${up ? C.olive : C.amber}`, padding: 14, borderRadius: 2 }}>
          <div className="f-mono" style={{ fontSize: 10, color: up ? C.oliveDeep : C.amberDeep, letterSpacing: "0.2em", fontWeight: 600 }}>
            {up ? "✓" : "⚠"} CAMBIO DI REGIME · RICAVO GIORNALIERO
          </div>
          <div className="f-display" style={{ fontSize: 14, color: C.ink, marginTop: 6, fontWeight: 600, lineHeight: 1.4 }}>
            Ricavo medio {direction} da <strong>{fmt.eur(revDrift.beforeMean)}/gg</strong> a <strong>{fmt.eur(revDrift.afterMean)}/gg</strong> ({up ? "+" : ""}{deltaPct.toFixed(1)}%) intorno al <strong>{cpFmt}</strong>
          </div>
          <div className="f-body" style={{ fontSize: 11, color: C.inkMid, marginTop: 6, lineHeight: 1.5 }}>
            Cause probabili: inizio/fine stagione, campagna marketing, evento locale, chiusure forzate, cambio orari. {up ? "Se la causa è stagionale, attesa naturale. Se interna (es. nuovo menu), conferma e consolida." : "Verifica se è temporaneo o strutturale. Cerca lo specchio nei costi prima di scalare."}
          </div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 6, letterSpacing: "0.1em" }}>Metodo: CUSUM su {revDriftSeries.length} giorni</div>
        </div>;
      })()}
    </div>}

    <div style={{ padding: 12, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, fontSize: 12, color: C.tealDeep, lineHeight: 1.5 }}>
      💡 Il modello di proiezione sta usando {forecast.dataPoints} giorni di dati storici con affidabilità <strong>{forecast.confidence}</strong>.
      {/* FIX A.3: chiarisco che il trend è weekday-adjusted (al netto della stagionalità settimanale) */}
      {forecast.trendReliable && <> Rilevato trend <strong>{forecast.trend.pct >= 0 ? "+" : ""}{(forecast.trend.pct * 100).toFixed(1)}%/sett</strong> al netto della stagionalità weekday (applicato compound).</>}
      {forecast.horizonCapped && <> <strong>Attenzione</strong>: la proiezione a {forecast.horizonRequested}gg è stata troncata a {forecast.horizonActual}gg per onestà statistica (servono ≥{Math.ceil(forecast.horizonRequested / 2)}gg di storia).</>}
    </div>
  </div>;
}

// ── Analisi storica: heatmap giorno/ora + trend mensile + scontrino medio ──
function AnalyticsHistory({ state, metrics, forecast }) {
  const receipts = state.tableReceipts || [];
  const baseline = forecast.baseline;
  const dowNames = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
  const dowFullNames = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];

  // Heatmap giorno x ora
  const heatmap = useMemo(() => {
    const grid = Array(7).fill(null).map(() => Array(24).fill(0));
    receipts.forEach(r => {
      if (!r.closedAt) return;
      const d = new Date(r.closedAt);
      const dow = d.getDay();
      const hr = d.getHours();
      grid[dow][hr] += r.total || 0;
    });
    // Conta giorni distinti per normalizzare (media per giorno)
    const daysByDow = {};
    receipts.forEach(r => {
      const day = getServiceDate(r.closedAt);
      if (!day) return;
      const dow = new Date(day + "T12:00:00").getDay();
      if (!daysByDow[dow]) daysByDow[dow] = new Set();
      daysByDow[dow].add(day);
    });
    // Normalizza
    const normalized = grid.map((row, dow) => {
      const n = daysByDow[dow]?.size || 1;
      return row.map(v => v / n);
    });
    const maxVal = Math.max(...normalized.flat(), 1);
    return { grid: normalized, maxVal };
  }, [receipts]);

  // Trend mensile (area chart)
  const monthTrend = useMemo(() => {
    if (metrics.length === 0) return [];
    const sorted = [...metrics].sort((a, b) => a.date.localeCompare(b.date));
    const withMovingAvg = sorted.map((d, i) => {
      const window = sorted.slice(Math.max(0, i - 6), i + 1);
      const ma = window.reduce((s, x) => s + x.revenue, 0) / window.length;
      return { ...d, ma7: ma };
    });
    return withMovingAvg.slice(-60);  // ultimi 60 giorni max
  }, [metrics]);

  // Scontrino medio (manteniamo per backward compat aggregati)
  const avgTicket = useMemo(() => {
    const totRev = receipts.reduce((s, r) => s + r.total, 0);
    const totCov = receipts.reduce((s, r) => s + (r.covers || 0), 0);
    return { perReceipt: receipts.length > 0 ? totRev / receipts.length : 0, perCover: totCov > 0 ? totRev / totCov : 0 };
  }, [receipts]);
  // Mediana ticket €/coperto su tutto lo storico (esclusi banco) — la card
  // di sintesi mostra mediana + CI 95%, non la media (gonfiata da outlier).
  const tsAllPerCover = useMemo(() => engine.stats.ticketStats(receipts, { perCover: true }), [receipts]);

  const maxRev = Math.max(...monthTrend.map(d => d.revenue), 1);

  return <div>
    {/* Stat row */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 18 }}>
      <StatCard kicker="Giorni dati" value={metrics.length} unit="totali" trend={`dal ${metrics[0]?.date || "—"}`} accent={C.ink} icon={Calendar} />
      <StatCard kicker="Scontrini totali" value={receipts.length} unit="chiusi" trend={`${receipts.reduce((s, r) => s + (r.covers || 0), 0)} coperti`} accent={C.olive} icon={Receipt} />
      {/* Ticket €/coperto: mediana + CI 95% bootstrap (la media e' gonfiata da outlier in distribuzione log-normale). */}
      <TicketStatsCard ts={tsAllPerCover} kicker="Scontrino mediano (€/cop)" accent={C.teal} icon={TrendingUp} note={`media (aggregata) ${fmt.eur(avgTicket.perCover)}/cop`} />
      <StatCard kicker="Giorno migliore" value={(() => {
        let best = null;
        for (let d = 0; d < 7; d++) {
          if (!best || baseline[d].revenue > baseline[best].revenue) best = d;
        }
        return dowNames[best] || "—";
      })()} unit={`media ${fmt.eurShort(Math.max(...Object.values(baseline).map(b => b.revenue)))}`} trend="per giorno settimana" accent={C.amber} icon={BarChart3} />
    </div>

    {/* Baseline per giorno della settimana */}
    <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 18, borderRadius: 2, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <div className="f-display" style={{ fontSize: 16, fontWeight: 500, color: C.ink }}>Media incasso per giorno della settimana</div>
          <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Calcolata con media robusta (trim outlier top/bottom 10%)</div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
        {[1, 2, 3, 4, 5, 6, 0].map(dow => {
          const b = baseline[dow];
          const maxB = Math.max(...Object.values(baseline).map(x => x.revenue), 1);
          const pct = (b.revenue / maxB) * 100;
          return <div key={dow} style={{ background: C.paperDeep, padding: 10, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.15em" }}>{dowFullNames[dow].toUpperCase()}</div>
            <div className="f-mono" style={{ fontSize: 16, fontWeight: 600, color: C.ink, marginTop: 4 }}>{fmt.eurShort(b.revenue)}</div>
            <div style={{ height: 4, background: C.ruleSoft, borderRadius: 2, marginTop: 6, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: b.revenue === maxB ? C.olive : C.teal }}></div>
            </div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>{b.covers.toFixed(0)} cop · n={b.n} · <ConfidenceBadge level={b.confidence} /></div>
          </div>;
        })}
      </div>
    </div>

    {/* Heatmap ora x giorno */}
    <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 18, borderRadius: 2, marginBottom: 16 }}>
      <div className="f-display" style={{ fontSize: 16, fontWeight: 500, color: C.ink, marginBottom: 10 }}>Heatmap incasso: giorno × ora</div>
      <div style={{ display: "flex", fontFamily: "JetBrains Mono, monospace", fontSize: 9 }}>
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-around", paddingRight: 6 }}>
          {[1, 2, 3, 4, 5, 6, 0].map(dow => <div key={dow} style={{ height: 22, display: "flex", alignItems: "center", color: C.muted, fontWeight: 600 }}>{dowNames[dow]}</div>)}
        </div>
        <div style={{ flex: 1 }}>
          {[1, 2, 3, 4, 5, 6, 0].map(dow => <div key={dow} style={{ display: "flex", gap: 1, height: 22, marginBottom: 1 }}>
            {Array.from({ length: 16 }, (_, i) => i + 8).map(h => {
              const v = heatmap.grid[dow][h];
              const intensity = heatmap.maxVal > 0 ? v / heatmap.maxVal : 0;
              const bg = intensity < 0.01 ? C.paperDeep : `rgba(31,77,72,${Math.min(0.95, 0.15 + intensity * 0.85)})`;
              return <div key={h} style={{ flex: 1, background: bg, borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: intensity > 0.5 ? C.white : C.inkSoft }} title={`${dowNames[dow]} ${h}:00 · media ${fmt.eur(v)}`}>
                {intensity > 0.25 ? fmt.eurShort(v) : ""}
              </div>;
            })}
          </div>)}
          <div style={{ display: "flex", gap: 1, marginTop: 4 }}>
            {Array.from({ length: 16 }, (_, i) => i + 8).map(h => <div key={h} style={{ flex: 1, textAlign: "center", fontSize: 8, color: C.muted }}>{h}</div>)}
          </div>
        </div>
      </div>
    </div>

    {/* Trend mensile lineare */}
    {monthTrend.length > 2 && <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 18, borderRadius: 2 }}>
      <div className="f-display" style={{ fontSize: 16, fontWeight: 500, color: C.ink, marginBottom: 10 }}>Trend incasso giornaliero con media mobile 7gg</div>
      <div style={{ height: 140, position: "relative", display: "flex", alignItems: "flex-end", gap: 1 }}>
        {monthTrend.map((d, i) => {
          const h1 = (d.revenue / maxRev) * 120;
          const h2 = (d.ma7 / maxRev) * 120;
          return <div key={i} style={{ flex: 1, position: "relative", height: 120 }} title={`${d.date} · ${fmt.eur(d.revenue)} · MA7: ${fmt.eur(d.ma7)}`}>
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: h1, background: C.tealWash, borderRadius: "1px 1px 0 0" }}></div>
            <div style={{ position: "absolute", bottom: h2 - 2, left: 0, right: 0, height: 2, background: C.ink }}></div>
          </div>;
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 9, color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
        <span>{monthTrend[0]?.date}</span>
        <span>—</span>
        <span>{monthTrend[monthTrend.length - 1]?.date}</span>
      </div>
    </div>}
  </div>;
}

// ── Menu Engineering (Boston Matrix) ───────────────────────────────────────
// ── Piatti per servizio (matrice + classifica) ─────────────────────────────
function AnalyticsDishMatrix({ state }) {
  const [period, setPeriod] = useState("30");  // 30, 90, all, custom
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [viewMode, setViewMode] = useState("matrix");  // matrix | ranking
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [topN, setTopN] = useState(15);

  const receipts = state.tableReceipts || [];
  const dishes = state.dishesReal || [];

  // Range date
  const dateRange = useMemo(() => {
    if (period === "custom" && fromDate && toDate) return { from: fromDate, to: toDate };
    if (period === "all") return null;
    const days = parseInt(period) || 30;
    const d = new Date(TODAY + "T12:00:00");
    d.setDate(d.getDate() - days);
    return { from: d.toISOString().slice(0, 10), to: TODAY };
  }, [period, fromDate, toDate]);

  const filteredReceipts = useMemo(() => {
    if (!dateRange) return receipts;
    return receipts.filter(r => {
      const day = getServiceDate(r.closedAt);
      return day && day >= dateRange.from && day <= dateRange.to;
    });
  }, [receipts, dateRange]);

  // Aggrega: dishId -> { name, cat, pranzo: { dow: { qty, rev } }, cena: { dow: { qty, rev } }, total }
  const aggregated = useMemo(() => {
    const map = {};
    filteredReceipts.forEach(r => {
      const shift = getShift(r.closedAt);
      if (!shift) return;
      const dow = new Date(r.closedAt).getDay();
      (r.items || []).forEach(it => {
        if (!map[it.dishId]) {
          const dish = dishes.find(d => d.id === it.dishId);
          map[it.dishId] = {
            dishId: it.dishId,
            name: it.name,
            cat: dish?.cat || "altro",
            pranzo: { qty: 0, revenue: 0, byDow: Array(7).fill(0) },
            cena: { qty: 0, revenue: 0, byDow: Array(7).fill(0) },
            totalQty: 0, totalRev: 0,
          };
        }
        const entry = map[it.dishId];
        entry[shift].qty += it.qty;
        entry[shift].revenue += it.qty * it.unitPrice;
        entry[shift].byDow[dow] += it.qty;
        entry.totalQty += it.qty;
        entry.totalRev += it.qty * it.unitPrice;
      });
    });
    return Object.values(map);
  }, [filteredReceipts, dishes]);

  const filteredDishes = categoryFilter === "all" ? aggregated : aggregated.filter(d => d.cat === categoryFilter);
  const sorted = [...filteredDishes].sort((a, b) => b.totalQty - a.totalQty);
  const topDishes = sorted.slice(0, topN);

  // Totali per categoria
  const catTotals = useMemo(() => {
    const tot = {};
    aggregated.forEach(d => {
      if (!tot[d.cat]) tot[d.cat] = { qty: 0, revenue: 0 };
      tot[d.cat].qty += d.totalQty;
      tot[d.cat].revenue += d.totalRev;
    });
    return tot;
  }, [aggregated]);

  const dowLabels = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
  const dowOrder = [1, 2, 3, 4, 5, 6, 0];

  if (filteredReceipts.length === 0) return <EmptyState icon={Utensils} title="Nessuno scontrino nel periodo"
    body={`Non ci sono scontrini tra ${dateRange?.from || "—"} e ${dateRange?.to || "—"}.`} />;

  return <div>
    {/* Toolbar */}
    <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
      <div style={{ display: "flex", gap: 6 }}>
        {[["30", "30gg"], ["90", "90gg"], ["all", "Tutto"], ["custom", "Custom"]].map(([v, l]) => <button key={v} onClick={() => setPeriod(v)} className="tx"
          style={{ padding: "7px 12px", background: period === v ? C.ink : C.white, color: period === v ? C.paper : C.ink, border: `1px solid ${period === v ? C.ink : C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 500 }}>
          {l}
        </button>)}
      </div>
      {period === "custom" && <>
        <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ padding: "6px 10px", fontSize: 11 }} />
        <span style={{ color: C.muted }}>→</span>
        <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ padding: "6px 10px", fontSize: 11 }} />
      </>}
      <div style={{ height: 24, width: 1, background: C.rule }} />
      <Select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} style={{ padding: "6px 10px", fontSize: 11 }}>
        <option value="all">Tutte le categorie ({aggregated.length})</option>
        {DISH_CAT_KEYS.map(k => <option key={k} value={k}>{DISH_CATEGORIES[k].label} ({aggregated.filter(d => d.cat === k).length})</option>)}
      </Select>
      <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
        <button onClick={() => setViewMode("matrix")} className="tx"
          style={{ padding: "7px 12px", background: viewMode === "matrix" ? C.teal : C.white, color: viewMode === "matrix" ? C.white : C.ink, border: `1px solid ${viewMode === "matrix" ? C.teal : C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 500 }}>Matrice</button>
        <button onClick={() => setViewMode("ranking")} className="tx"
          style={{ padding: "7px 12px", background: viewMode === "ranking" ? C.teal : C.white, color: viewMode === "ranking" ? C.white : C.ink, border: `1px solid ${viewMode === "ranking" ? C.teal : C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 500 }}>Classifica</button>
      </div>
    </div>

    {/* KPI sintesi */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 12, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>PIATTI DIVERSI VENDUTI</div>
        <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink, marginTop: 3 }}>{aggregated.length}</div>
      </div>
      <div style={{ background: C.amberWash, border: `1px solid ${C.amber}`, padding: 12, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.amberDeep, letterSpacing: "0.2em" }}>☀ PRANZO</div>
        <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.amberDeep, marginTop: 3 }}>{aggregated.reduce((s, d) => s + d.pranzo.qty, 0)}</div>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>{fmt.eur(aggregated.reduce((s, d) => s + d.pranzo.revenue, 0))}</div>
      </div>
      <div style={{ background: C.tealWash, border: `1px solid ${C.teal}`, padding: 12, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.teal, letterSpacing: "0.2em" }}>🌙 CENA</div>
        <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.tealDeep, marginTop: 3 }}>{aggregated.reduce((s, d) => s + d.cena.qty, 0)}</div>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>{fmt.eur(aggregated.reduce((s, d) => s + d.cena.revenue, 0))}</div>
      </div>
      <div style={{ background: C.ink, padding: 12, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.mutedSoft, letterSpacing: "0.2em" }}>TOT PIATTI</div>
        <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.paper, marginTop: 3 }}>{aggregated.reduce((s, d) => s + d.totalQty, 0)}</div>
        <div className="f-mono" style={{ fontSize: 9, color: C.mutedSoft }}>{fmt.eur(aggregated.reduce((s, d) => s + d.totalRev, 0))}</div>
      </div>
    </div>

    {/* Top-N selector */}
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      <span className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>MOSTRA TOP</span>
      {[10, 15, 25, 50, "all"].map(n => <button key={n} onClick={() => setTopN(n === "all" ? 9999 : n)} className="tx"
        style={{ padding: "4px 10px", background: (topN === n || (n === "all" && topN === 9999)) ? C.ink : C.white, color: (topN === n || (n === "all" && topN === 9999)) ? C.paper : C.ink, border: `1px solid ${C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", fontSize: 10 }}>
        {n === "all" ? "Tutti" : n}
      </button>)}
      <span className="f-mono" style={{ fontSize: 10, color: C.muted, marginLeft: "auto" }}>{topDishes.length} di {sorted.length}</span>
    </div>

    {viewMode === "matrix" ? (
      /* MATRICE giorno × servizio */
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr style={{ background: C.paperDeep, borderBottom: `2px solid ${C.rule}` }}>
              <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, position: "sticky", left: 0, background: C.paperDeep, minWidth: 200 }}>Piatto</th>
              <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 600, background: C.amberWash, borderLeft: `1px solid ${C.amber}` }} colSpan={7}>☀ PRANZO</th>
              <th style={{ padding: "10px 8px", textAlign: "center", fontWeight: 600, background: C.tealWash, borderLeft: `1px solid ${C.teal}` }} colSpan={7}>🌙 CENA</th>
              <th style={{ padding: "10px 10px", textAlign: "right", fontWeight: 600, borderLeft: `1px solid ${C.rule}` }}>TOT</th>
            </tr>
            <tr style={{ background: C.paperDeep, borderBottom: `1px solid ${C.rule}`, fontSize: 9, color: C.muted, letterSpacing: "0.1em" }}>
              <th style={{ padding: "4px 12px", position: "sticky", left: 0, background: C.paperDeep }}></th>
              {dowOrder.map(d => <th key={`p${d}`} style={{ padding: "4px 6px", textAlign: "center", background: C.amberWash + "40", borderLeft: d === 1 ? `1px solid ${C.amber}` : "none", fontWeight: 500 }}>{dowLabels[d]}</th>)}
              {dowOrder.map(d => <th key={`c${d}`} style={{ padding: "4px 6px", textAlign: "center", background: C.tealWash + "40", borderLeft: d === 1 ? `1px solid ${C.teal}` : "none", fontWeight: 500 }}>{dowLabels[d]}</th>)}
              <th style={{ padding: "4px 6px", borderLeft: `1px solid ${C.rule}` }}></th>
            </tr>
          </thead>
          <tbody>
            {topDishes.map((d, i) => {
              const maxDow = Math.max(...d.pranzo.byDow, ...d.cena.byDow, 1);
              return <tr key={d.dishId} style={{ borderBottom: `1px solid ${C.ruleSoft}`, background: i % 2 === 0 ? "transparent" : C.paperDeep + "30" }}>
                <td style={{ padding: "6px 12px", position: "sticky", left: 0, background: i % 2 === 0 ? C.white : C.paperDeep }}>
                  <div className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500 }}>{d.name}</div>
                  <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 1 }}>{dishCatLabel(d.cat)}</div>
                </td>
                {dowOrder.map(dow => {
                  const v = d.pranzo.byDow[dow];
                  const alpha = maxDow > 0 ? v / maxDow : 0;
                  return <td key={`p${dow}`} style={{ padding: "6px 4px", textAlign: "center", background: v > 0 ? `rgba(196,123,59,${Math.max(0.08, alpha * 0.5)})` : "transparent", borderLeft: dow === 1 ? `1px solid ${C.amber}` : "none" }} className="f-mono">
                    {v > 0 ? v : <span style={{ color: C.muted }}>·</span>}
                  </td>;
                })}
                {dowOrder.map(dow => {
                  const v = d.cena.byDow[dow];
                  const alpha = maxDow > 0 ? v / maxDow : 0;
                  return <td key={`c${dow}`} style={{ padding: "6px 4px", textAlign: "center", background: v > 0 ? `rgba(31,77,72,${Math.max(0.08, alpha * 0.5)})` : "transparent", borderLeft: dow === 1 ? `1px solid ${C.teal}` : "none" }} className="f-mono">
                    {v > 0 ? v : <span style={{ color: C.muted }}>·</span>}
                  </td>;
                })}
                <td style={{ padding: "6px 10px", textAlign: "right", borderLeft: `1px solid ${C.rule}` }}>
                  <div className="f-mono" style={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>{d.totalQty}</div>
                  <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>{fmt.eurShort(d.totalRev)}</div>
                </td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
    ) : (
      /* CLASSIFICA pranzo vs cena */
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {[["pranzo", "☀ PRANZO", C.amberWash, C.amber, C.amberDeep], ["cena", "🌙 CENA", C.tealWash, C.teal, C.tealDeep]].map(([shift, label, bg, border, deep]) => {
          const sortedShift = [...filteredDishes].filter(d => d[shift].qty > 0).sort((a, b) => b[shift].qty - a[shift].qty).slice(0, topN);
          const shiftTot = sortedShift.reduce((s, d) => s + d[shift].qty, 0);
          const maxQty = Math.max(...sortedShift.map(d => d[shift].qty), 1);
          return <div key={shift} style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", background: bg, borderBottom: `1px solid ${border}` }}>
              <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: deep, fontWeight: 600 }}>{label}</div>
              <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sortedShift.length} piatti · {shiftTot} vendute · {fmt.eur(sortedShift.reduce((s, d) => s + d[shift].revenue, 0))}</div>
            </div>
            <div style={{ maxHeight: 500, overflowY: "auto" }}>
              {sortedShift.length === 0 ? <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 11 }}>Nessun piatto in questo servizio</div>
              : sortedShift.map((d, i) => {
                const pct = shiftTot > 0 ? (d[shift].qty / shiftTot) * 100 : 0;
                const barPct = (d[shift].qty / maxQty) * 100;
                return <div key={d.dishId} style={{ padding: "8px 16px", borderBottom: `1px solid ${C.ruleSoft}`, position: "relative" }}>
                  <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${barPct}%`, background: bg, opacity: 0.4 }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 10, position: "relative" }}>
                    <span className="f-mono" style={{ fontSize: 11, color: C.muted, minWidth: 22, fontWeight: 600 }}>#{i + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}</div>
                      <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>{dishCatLabel(d.cat)} · {pct.toFixed(1)}% del servizio</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div className="f-mono" style={{ fontSize: 13, fontWeight: 600, color: deep }}>× {d[shift].qty}</div>
                      <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>{fmt.eurShort(d[shift].revenue)}</div>
                    </div>
                  </div>
                </div>;
              })}
            </div>
          </div>;
        })}
      </div>
    )}

    {/* Footer con categorie summary */}
    <div style={{ marginTop: 16, padding: 14, background: C.paperDeep, borderRadius: 2 }}>
      <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em", marginBottom: 8 }}>DISTRIBUZIONE PER CATEGORIA</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {DISH_CAT_KEYS.filter(k => catTotals[k]?.qty > 0).map(k => {
          const t = catTotals[k];
          return <div key={k} style={{ background: C.white, padding: "6px 10px", borderRadius: 2, border: `1px solid ${C.rule}`, fontSize: 11 }}>
            <span className="f-mono" style={{ color: DISH_CATEGORIES[k].color, fontWeight: 600 }}>{DISH_CATEGORIES[k].label}</span>
            <span className="f-mono" style={{ color: C.ink, marginLeft: 6, fontWeight: 600 }}>× {t.qty}</span>
            <span className="f-mono" style={{ color: C.muted, marginLeft: 4 }}>· {fmt.eurShort(t.revenue)}</span>
          </div>;
        })}
      </div>
    </div>
  </div>;
}


// ── Scontrino/coperto trend pranzo vs cena ─────────────────────────────────
function AnalyticsAvgTicket({ state }) {
  const [period, setPeriod] = useState("30");

  const receipts = state.tableReceipts || [];
  const dateRange = useMemo(() => {
    if (period === "all") return null;
    const days = parseInt(period) || 30;
    const d = new Date(TODAY + "T12:00:00");
    d.setDate(d.getDate() - days);
    return { from: d.toISOString().slice(0, 10), to: TODAY };
  }, [period]);

  const filtered = useMemo(() => {
    if (!dateRange) return receipts;
    return receipts.filter(r => { const day = getServiceDate(r.closedAt); return day && day >= dateRange.from && day <= dateRange.to; });
  }, [receipts, dateRange]);

  // Aggrega per giorno × servizio — SOLO scontrini-tavolo (covers>0).
  // Gli scontrini banco (covers=0) inquinano i ricavi senza coperti e
  // farebbero crollare il €/coperto.
  const byDayShift = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      if ((r.covers || 0) === 0) return;  // scarta banco
      const day = getServiceDate(r.closedAt);
      const shift = getShift(r.closedAt);
      if (!day || !shift) return;
      if (!map[day]) map[day] = { date: day, pranzo: { rev: 0, cov: 0, n: 0 }, cena: { rev: 0, cov: 0, n: 0 } };
      map[day][shift].rev += r.total || 0;
      map[day][shift].cov += r.covers || 0;
      map[day][shift].n += 1;
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
  }, [filtered]);

  // Medie (mantenute per backward compat con i grafici di trend).
  // Per le card di sintesi usiamo invece engine.stats.ticketStats (mediana + CI).
  const avgPranzo = useMemo(() => {
    const sum = byDayShift.reduce((acc, d) => ({ rev: acc.rev + d.pranzo.rev, cov: acc.cov + d.pranzo.cov }), { rev: 0, cov: 0 });
    return sum.cov > 0 ? sum.rev / sum.cov : 0;
  }, [byDayShift]);
  const avgCena = useMemo(() => {
    const sum = byDayShift.reduce((acc, d) => ({ rev: acc.rev + d.cena.rev, cov: acc.cov + d.cena.cov }), { rev: 0, cov: 0 });
    return sum.cov > 0 ? sum.rev / sum.cov : 0;
  }, [byDayShift]);
  const avgTot = useMemo(() => {
    const sum = byDayShift.reduce((acc, d) => ({ rev: acc.rev + d.pranzo.rev + d.cena.rev, cov: acc.cov + d.pranzo.cov + d.cena.cov }), { rev: 0, cov: 0 });
    return sum.cov > 0 ? sum.rev / sum.cov : 0;
  }, [byDayShift]);

  // Statistiche ticket per servizio: mediana + IQR + P90 + CI95 bootstrap.
  // Esclude scontrini banco (covers=0) — il "per coperto" sarebbe indefinito.
  const tsPranzo = useMemo(() => {
    const rs = filtered.filter(r => (r.covers || 0) > 0 && getShift(r.closedAt) === "pranzo");
    return engine.stats.ticketStats(rs, { perCover: true });
  }, [filtered]);
  const tsCena = useMemo(() => {
    const rs = filtered.filter(r => (r.covers || 0) > 0 && getShift(r.closedAt) === "cena");
    return engine.stats.ticketStats(rs, { perCover: true });
  }, [filtered]);
  const tsTot = useMemo(() => engine.stats.ticketStats(filtered, { perCover: true }), [filtered]);

  // Per giorno della settimana — SOLO scontrini-tavolo
  const byDow = useMemo(() => {
    const map = Array.from({ length: 7 }, (_, i) => ({ dow: i, pranzo: { rev: 0, cov: 0 }, cena: { rev: 0, cov: 0 } }));
    filtered.forEach(r => {
      if ((r.covers || 0) === 0) return;  // scarta banco
      const shift = getShift(r.closedAt);
      if (!shift) return;
      const dow = new Date(r.closedAt).getDay();
      map[dow][shift].rev += r.total || 0;
      map[dow][shift].cov += r.covers || 0;
    });
    return map;
  }, [filtered]);

  const dowLabels = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
  const dowOrder = [1, 2, 3, 4, 5, 6, 0];

  // Target di riferimento (media attuale dichiarata dall'utente)
  const TARGET_PRANZO = 40;
  const TARGET_CENA = 46;

  // Max scala per il grafico
  const maxY = useMemo(() => {
    let m = 0;
    byDayShift.forEach(d => {
      const ap = d.pranzo.cov > 0 ? d.pranzo.rev / d.pranzo.cov : 0;
      const ac = d.cena.cov > 0 ? d.cena.rev / d.cena.cov : 0;
      m = Math.max(m, ap, ac);
    });
    return Math.max(m, 60);
  }, [byDayShift]);

  if (filtered.length === 0) return <EmptyState icon={Receipt} title="Nessuno scontrino nel periodo" />;

  return <div>
    {/* Period selector */}
    <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
      {[["30", "30gg"], ["90", "90gg"], ["all", "Tutto"]].map(([v, l]) => <button key={v} onClick={() => setPeriod(v)} className="tx"
        style={{ padding: "7px 12px", background: period === v ? C.ink : C.white, color: period === v ? C.paper : C.ink, border: `1px solid ${period === v ? C.ink : C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 500 }}>{l}</button>)}
    </div>

    {/* KPI ticket — mediana + CI 95% bootstrap su distribuzione log-normale.
        La media e' deenfatizzata: in F&B il 5-10% di tavolate top gonfia la
        media aritmetica e ne fa una bugia educata. La mediana e' lo stimatore
        corretto del "ticket tipico". */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 18 }}>
      {/* PRANZO */}
      <div style={{ background: C.amberWash, border: `1px solid ${C.amber}`, padding: 18, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.amberDeep, letterSpacing: "0.2em" }}>☀ PRANZO · €/COPERTO</div>
        {tsPranzo.fallback ? <>
          <div className="f-display" style={{ fontSize: 26, fontWeight: 600, color: C.muted, marginTop: 4 }}>—</div>
          <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>dati insufficienti (n={tsPranzo.n})</div>
        </> : <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
            <div className="f-display" style={{ fontSize: 32, fontWeight: 600, color: C.amberDeep }}>{fmt.eur(tsPranzo.median)}</div>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>mediana</div>
          </div>
          <div className="f-mono" style={{ fontSize: 10, color: C.inkSoft, marginTop: 3 }} title="Intervallo di confidenza 95% sulla mediana (bootstrap, 1000 resample)">
            CI95 {fmt.eur(tsPranzo.medianCI[0])}—{fmt.eur(tsPranzo.medianCI[1])} · n={tsPranzo.n}
          </div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
            media {fmt.eur(tsPranzo.mean)} · P25-75 €{tsPranzo.p25.toFixed(0)}-{tsPranzo.p75.toFixed(0)} · P90 €{tsPranzo.p90.toFixed(0)}
          </div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
            riferimento: <strong>{fmt.eur(TARGET_PRANZO)}</strong> {tsPranzo.median >= TARGET_PRANZO ? "✓" : "•"}
            <span style={{ color: tsPranzo.median >= TARGET_PRANZO ? C.olive : C.terra, marginLeft: 6 }}>
              {tsPranzo.median >= TARGET_PRANZO ? "+" : ""}{(((tsPranzo.median - TARGET_PRANZO) / TARGET_PRANZO) * 100).toFixed(1)}%
            </span>
          </div>
        </>}
      </div>
      {/* CENA */}
      <div style={{ background: C.tealWash, border: `1px solid ${C.teal}`, padding: 18, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.teal, letterSpacing: "0.2em" }}>🌙 CENA · €/COPERTO</div>
        {tsCena.fallback ? <>
          <div className="f-display" style={{ fontSize: 26, fontWeight: 600, color: C.muted, marginTop: 4 }}>—</div>
          <div className="f-mono" style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>dati insufficienti (n={tsCena.n})</div>
        </> : <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
            <div className="f-display" style={{ fontSize: 32, fontWeight: 600, color: C.tealDeep }}>{fmt.eur(tsCena.median)}</div>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted }}>mediana</div>
          </div>
          <div className="f-mono" style={{ fontSize: 10, color: C.inkSoft, marginTop: 3 }} title="Intervallo di confidenza 95% sulla mediana (bootstrap, 1000 resample)">
            CI95 {fmt.eur(tsCena.medianCI[0])}—{fmt.eur(tsCena.medianCI[1])} · n={tsCena.n}
          </div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
            media {fmt.eur(tsCena.mean)} · P25-75 €{tsCena.p25.toFixed(0)}-{tsCena.p75.toFixed(0)} · P90 €{tsCena.p90.toFixed(0)}
          </div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
            riferimento: <strong>{fmt.eur(TARGET_CENA)}</strong> {tsCena.median >= TARGET_CENA ? "✓" : "•"}
            <span style={{ color: tsCena.median >= TARGET_CENA ? C.olive : C.terra, marginLeft: 6 }}>
              {tsCena.median >= TARGET_CENA ? "+" : ""}{(((tsCena.median - TARGET_CENA) / TARGET_CENA) * 100).toFixed(1)}%
            </span>
          </div>
        </>}
      </div>
      {/* GENERALE */}
      <div style={{ background: C.ink, padding: 18, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.mutedSoft, letterSpacing: "0.2em" }}>GENERALE · €/COPERTO</div>
        {tsTot.fallback ? <>
          <div className="f-display" style={{ fontSize: 26, fontWeight: 600, color: C.mutedSoft, marginTop: 4 }}>—</div>
          <div className="f-mono" style={{ fontSize: 11, color: C.mutedSoft, marginTop: 4 }}>dati insufficienti (n={tsTot.n})</div>
        </> : <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
            <div className="f-display" style={{ fontSize: 32, fontWeight: 600, color: C.paper }}>{fmt.eur(tsTot.median)}</div>
            <div className="f-mono" style={{ fontSize: 10, color: C.mutedSoft }}>mediana</div>
          </div>
          <div className="f-mono" style={{ fontSize: 10, color: C.paper, marginTop: 3 }} title="Intervallo di confidenza 95% sulla mediana (bootstrap, 1000 resample)">
            CI95 {fmt.eur(tsTot.medianCI[0])}—{fmt.eur(tsTot.medianCI[1])} · n={tsTot.n}
          </div>
          <div className="f-mono" style={{ fontSize: 10, color: C.mutedSoft, marginTop: 2 }}>
            media {fmt.eur(tsTot.mean)} · P25-75 €{tsTot.p25.toFixed(0)}-{tsTot.p75.toFixed(0)} · P90 €{tsTot.p90.toFixed(0)}
          </div>
          <div className="f-mono" style={{ fontSize: 10, color: C.mutedSoft, marginTop: 4 }}>riferimento: <strong>€43</strong></div>
        </>}
      </div>
    </div>

    {/* Grafico cronologico */}
    {byDayShift.length > 1 && <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 18, borderRadius: 2, marginBottom: 16 }}>
      <div className="f-display" style={{ fontSize: 15, fontWeight: 500, color: C.ink, marginBottom: 12 }}>Scontrino medio per coperto nel tempo</div>
      <div style={{ position: "relative", height: 200, display: "flex", alignItems: "flex-end", gap: 2, borderLeft: `1px solid ${C.rule}`, borderBottom: `1px solid ${C.rule}`, padding: "4px 0 0 4px" }}>
        {/* Linee target */}
        <div style={{ position: "absolute", left: 4, right: 0, bottom: `${(TARGET_PRANZO / maxY) * 100}%`, borderTop: `1px dashed ${C.amber}`, zIndex: 1 }}>
          <span style={{ position: "absolute", right: 0, top: -14, fontSize: 9, color: C.amberDeep, background: C.white, padding: "1px 4px", fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }}>PRANZO €{TARGET_PRANZO}</span>
        </div>
        <div style={{ position: "absolute", left: 4, right: 0, bottom: `${(TARGET_CENA / maxY) * 100}%`, borderTop: `1px dashed ${C.teal}`, zIndex: 1 }}>
          <span style={{ position: "absolute", right: 0, top: -14, fontSize: 9, color: C.tealDeep, background: C.white, padding: "1px 4px", fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }}>CENA €{TARGET_CENA}</span>
        </div>
        {byDayShift.map((d, i) => {
          const ap = d.pranzo.cov > 0 ? d.pranzo.rev / d.pranzo.cov : null;
          const ac = d.cena.cov > 0 ? d.cena.rev / d.cena.cov : null;
          return <div key={i} style={{ flex: 1, height: "100%", position: "relative", display: "flex", flexDirection: "column-reverse", gap: 1, minWidth: 4 }} title={`${d.date}\nPranzo: ${ap ? fmt.eur(ap) : "—"}\nCena: ${ac ? fmt.eur(ac) : "—"}`}>
            {ap != null && <div style={{ position: "absolute", bottom: `${(ap / maxY) * 100}%`, left: 0, width: "50%", height: 6, background: C.amberDeep, borderRadius: 2 }}></div>}
            {ac != null && <div style={{ position: "absolute", bottom: `${(ac / maxY) * 100}%`, right: 0, width: "50%", height: 6, background: C.tealDeep, borderRadius: 2 }}></div>}
          </div>;
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 10, color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
        <span>{byDayShift[0]?.date}</span>
        <span>{byDayShift.length} giorni</span>
        <span>{byDayShift[byDayShift.length - 1]?.date}</span>
      </div>
      <div style={{ display: "flex", gap: 14, marginTop: 6, fontSize: 10, color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 12, height: 6, background: C.amberDeep, borderRadius: 2 }}></div> Pranzo €/cop</div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 12, height: 6, background: C.tealDeep, borderRadius: 2 }}></div> Cena €/cop</div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 12, border: `1px dashed ${C.amber}`, marginTop: 2 }}></div> Riferimento pranzo €40</div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 12, border: `1px dashed ${C.teal}`, marginTop: 2 }}></div> Riferimento cena €46</div>
      </div>
    </div>}

    {/* Tabella per giorno della settimana */}
    <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 18, borderRadius: 2 }}>
      <div className="f-display" style={{ fontSize: 15, fontWeight: 500, color: C.ink, marginBottom: 12 }}>Scontrino medio/coperto per giorno della settimana</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
        {dowOrder.map(dow => {
          const d = byDow[dow];
          const ap = d.pranzo.cov > 0 ? d.pranzo.rev / d.pranzo.cov : 0;
          const ac = d.cena.cov > 0 ? d.cena.rev / d.cena.cov : 0;
          return <div key={dow} style={{ background: C.paperDeep, padding: 10, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 11, color: C.ink, fontWeight: 600, letterSpacing: "0.1em", marginBottom: 6 }}>{dowLabels[dow].toUpperCase()}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
              <span className="f-mono" style={{ fontSize: 10, color: C.amberDeep, minWidth: 18 }}>☀</span>
              <span className="f-mono" style={{ fontSize: 13, color: ap >= TARGET_PRANZO ? C.olive : ap > 0 ? C.inkSoft : C.muted, fontWeight: 600 }}>{ap > 0 ? fmt.eur(ap) : "—"}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span className="f-mono" style={{ fontSize: 10, color: C.tealDeep, minWidth: 18 }}>🌙</span>
              <span className="f-mono" style={{ fontSize: 13, color: ac >= TARGET_CENA ? C.olive : ac > 0 ? C.inkSoft : C.muted, fontWeight: 600 }}>{ac > 0 ? fmt.eur(ac) : "—"}</span>
            </div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 4 }}>{d.pranzo.cov + d.cena.cov} cop</div>
          </div>;
        })}
      </div>
    </div>
  </div>;
}


// ── Anatomia scontrino (A. leve ricavo / B. leve margine / C. completezza) ─
function AnalyticsAnatomy({ state, setState }) {
  const [period, setPeriod] = useState("all");
  const [thresholdPranzo, setThresholdPranzo] = useState(40);
  const [thresholdCena, setThresholdCena] = useState(46);
  const [section, setSection] = useState("A");  // A | B | C

  const receipts = state.tableReceipts || [];
  const dishes = state.dishesReal || [];

  const filtered = useMemo(() => {
    if (period === "all") return receipts;
    const days = parseInt(period) || 30;
    const d = new Date(TODAY + "T12:00:00");
    d.setDate(d.getDate() - days);
    const from = d.toISOString().slice(0, 10);
    return receipts.filter(r => getServiceDate(r.closedAt) >= from);
  }, [receipts, period]);

  // Enrich: per ogni scontrino calcola €/cop, margine/cop, categorie toccate.
  // Scarta scontrini-banco (covers=0): senza coperti, perCover/marginPerCover esplodono.
  const enriched = useMemo(() => {
    const vatS = state.settings?.vatRateSale || 0.10;
    // Pre-index dish per O(n) lookup
    const dishById = new Map();
    dishes.forEach(d => dishById.set(d.id, d));
    const bpById = new Map();
    (state.baseProducts || []).forEach(b => bpById.set(b.id, b));
    const lineVat = (it, dish) => {
      const snap = it.recipeSnapshot?.vatRate;
      if (snap != null && Number.isFinite(snap)) return snap;
      if (dish?.vatRate != null && Number.isFinite(dish.vatRate)) return dish.vatRate;
      return vatS;
    };
    return filtered
      .filter(r => (r.covers || 0) > 0)  // SOLO scontrini-tavolo
      .map(r => {
        const shift = getShift(r.closedAt);
        const covers = Number(r.covers) || 1;
        const perCover = (Number(r.total) || 0) / covers;
        // Categorie toccate + costo netto + revenue netto IVA per riga
        const cats = new Set();
        let netCost = 0;
        let revenueNet = 0;
        (r.items || []).forEach(it => {
          const dish = dishById.get(it.dishId);
          if (dish) cats.add(dish.cat);
          const qty = Number(it.qty) || 0;
          const unitPrice = Number(it.unitPrice) || 0;
          const grossLine = qty * unitPrice;
          const v = lineVat(it, dish);
          revenueNet += v > -1 ? grossLine / (1 + v) : grossLine;
          // Costo netto: snapshot se c'è, altrimenti ricetta corrente
          if (it.recipeSnapshot?.netCost != null) {
            netCost += it.recipeSnapshot.netCost * qty;
          } else if (dish) {
            (dish.ingredienti || []).forEach(ing => {
              const bp = ing.bpId ? bpById.get(ing.bpId) : null;
              const c = bp ? (bp.pReale || bp.pFatt || 0) * ing.qty : (ing.prezzo || 0);
              netCost += c * qty;
            });
          }
        });
        const margin = revenueNet - netCost;
        const marginPerCover = margin / covers;
        // Quanti piatti per categoria
        const catCounts = {};
        DISH_CAT_KEYS.forEach(k => catCounts[k] = 0);
        (r.items || []).forEach(it => {
          const dish = dishById.get(it.dishId);
          if (dish && catCounts[dish.cat] != null) catCounts[dish.cat] += Number(it.qty) || 0;
        });
        return { receipt: r, shift, covers, perCover, perCoverNet: revenueNet / covers, margin, marginPerCover, netCost, cats, catCount: cats.size, catCounts, revenueNet };
      }).filter(x => x.shift);  // escludi fuori servizio
  }, [filtered, dishes, state.baseProducts, state.settings]);

  // Suggerimento soglie automatiche (75° percentile)
  const suggestedThresholds = useMemo(() => {
    const p = enriched.filter(x => x.shift === "pranzo").map(x => x.perCover).sort((a, b) => a - b);
    const c = enriched.filter(x => x.shift === "cena").map(x => x.perCover).sort((a, b) => a - b);
    const percentile = (arr, p) => arr.length === 0 ? 0 : arr[Math.floor(arr.length * p)] || 0;
    return { pranzo: Math.round(percentile(p, 0.75)), cena: Math.round(percentile(c, 0.75)) };
  }, [enriched]);

  // Splitta ottimizzati/non-ottimizzati per le sezioni A e B
  const groupedA = useMemo(() => {
    const opt = [], nopt = [];
    enriched.forEach(x => {
      const threshold = x.shift === "pranzo" ? thresholdPranzo : thresholdCena;
      if (x.perCover >= threshold) opt.push(x); else nopt.push(x);
    });
    return { opt, nopt };
  }, [enriched, thresholdPranzo, thresholdCena]);

  const groupedB = useMemo(() => {
    // Per margine usiamo percentile 50 del margine/cop come soglia (mediana)
    const sorted = [...enriched].map(x => x.marginPerCover).sort((a, b) => a - b);
    const median = sorted.length > 0 ? sorted[Math.floor(sorted.length / 2)] : 0;
    const opt = [], nopt = [];
    enriched.forEach(x => {
      if (x.marginPerCover >= median) opt.push(x); else nopt.push(x);
    });
    return { opt, nopt, median };
  }, [enriched]);

  // Helper inline per calcolo % presenza/quantità categoria nel gruppo
  // (definiti qui per essere disponibili nei useMemo successivi, prima del possibile return early)
  const presencePct = (group, cat) => group.length === 0 ? 0 : (group.filter(x => x.catCounts[cat] > 0).length / group.length) * 100;
  const avgQty = (group, cat) => group.length === 0 ? 0 : group.reduce((s, x) => s + x.catCounts[cat], 0) / group.length;
  const avgCovers = (group) => group.length === 0 ? 0 : group.reduce((s, x) => s + x.covers, 0) / group.length;
  const avgPerCover = (group) => group.length === 0 ? 0 : group.reduce((s, x) => s + x.perCover, 0) / group.length;

  // Conta presenza (numero scontrini in cui la categoria compare) — necessario per χ².
  const presenceCount = (group, cat) => group.filter(x => x.catCounts[cat] > 0).length;

  // I 3 useMemo seguenti DEVONO stare PRIMA del return early per rispettare le Rules of Hooks
  // χ² test 2×2 per ogni categoria: significativita' della differenza di presenza
  // tra "ottimizzati" e "non-ottimizzati". p<0.05 → leva reale, p>0.05 → rumore.
  const categoryDeltasA = useMemo(() => {
    const optN = groupedA.opt.length;
    const noptN = groupedA.nopt.length;
    return DISH_CAT_KEYS.map(cat => {
      const optP = presencePct(groupedA.opt, cat);
      const noptP = presencePct(groupedA.nopt, cat);
      const optQ = avgQty(groupedA.opt, cat);
      const noptQ = avgQty(groupedA.nopt, cat);
      const aK = presenceCount(groupedA.opt, cat);
      const bK = presenceCount(groupedA.nopt, cat);
      const chi = engine.stats.chiSquare2x2(aK, bK, optN - aK, noptN - bK);
      return { cat, optP, noptP, deltaP: optP - noptP, optQ, noptQ, deltaQ: optQ - noptQ, chi };
    }).sort((a, b) => Math.abs(b.deltaP) - Math.abs(a.deltaP));
  }, [groupedA]);

  const categoryDeltasB = useMemo(() => {
    const optN = groupedB.opt.length;
    const noptN = groupedB.nopt.length;
    return DISH_CAT_KEYS.map(cat => {
      const optP = presencePct(groupedB.opt, cat);
      const noptP = presencePct(groupedB.nopt, cat);
      const optQ = avgQty(groupedB.opt, cat);
      const noptQ = avgQty(groupedB.nopt, cat);
      const aK = presenceCount(groupedB.opt, cat);
      const bK = presenceCount(groupedB.nopt, cat);
      const chi = engine.stats.chiSquare2x2(aK, bK, optN - aK, noptN - bK);
      return { cat, optP, noptP, deltaP: optP - noptP, optQ, noptQ, deltaQ: optQ - noptQ, chi };
    }).sort((a, b) => Math.abs(b.deltaP) - Math.abs(a.deltaP));
  }, [groupedB]);

  const completezzaBuckets = useMemo(() => {
    const buckets = { "1 categoria": [], "2 categorie": [], "3 categorie": [], "4+ categorie": [] };
    enriched.forEach(x => {
      if (x.catCount === 1) buckets["1 categoria"].push(x);
      else if (x.catCount === 2) buckets["2 categorie"].push(x);
      else if (x.catCount === 3) buckets["3 categorie"].push(x);
      else buckets["4+ categorie"].push(x);
    });
    return buckets;
  }, [enriched]);

  if (enriched.length === 0) return <EmptyState icon={Zap} title="Nessuno scontrino con servizio riconoscibile"
    body="Servono scontrini chiusi nelle fasce pranzo (09-18) o cena (18-04)." />;

  const MIN_N = 20;  // sotto questa soglia non do insight affidabili
  const hasConfidenceA = groupedA.opt.length >= MIN_N && groupedA.nopt.length >= MIN_N;
  const hasConfidenceB = groupedB.opt.length >= MIN_N && groupedB.nopt.length >= MIN_N;

  return <div>
    {/* Toolbar con soglie */}
    <div style={{ background: C.paperDeep, padding: 14, borderRadius: 2, marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em", marginBottom: 4 }}>PERIODO</div>
          <div style={{ display: "flex", gap: 4 }}>
            {[["30", "30gg"], ["90", "90gg"], ["all", "Tutto"]].map(([v, l]) => <button key={v} onClick={() => setPeriod(v)} className="tx"
              style={{ padding: "5px 10px", background: period === v ? C.ink : C.white, color: period === v ? C.paper : C.ink, border: `1px solid ${C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", fontSize: 10 }}>{l}</button>)}
          </div>
        </div>
        <div style={{ height: 40, width: 1, background: C.rule }} />
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.amberDeep, letterSpacing: "0.2em", marginBottom: 4 }}>☀ SOGLIA PRANZO €/COP</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input type="number" value={thresholdPranzo} onChange={e => setThresholdPranzo(parseFloat(e.target.value) || 0)} style={{ width: 70, padding: "5px 8px", border: `1px solid ${C.rule}`, borderRadius: 2, fontSize: 12, fontFamily: "JetBrains Mono, monospace", textAlign: "right" }} />
            <button onClick={() => setThresholdPranzo(suggestedThresholds.pranzo)} className="tx"
              style={{ padding: "5px 8px", background: C.amberWash, color: C.amberDeep, border: `1px solid ${C.amber}`, borderRadius: 2, cursor: "pointer", fontFamily: "JetBrains Mono, monospace", fontSize: 10 }} title="Usa il 75° percentile">
              P75: €{suggestedThresholds.pranzo}
            </button>
          </div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.tealDeep, letterSpacing: "0.2em", marginBottom: 4 }}>🌙 SOGLIA CENA €/COP</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input type="number" value={thresholdCena} onChange={e => setThresholdCena(parseFloat(e.target.value) || 0)} style={{ width: 70, padding: "5px 8px", border: `1px solid ${C.rule}`, borderRadius: 2, fontSize: 12, fontFamily: "JetBrains Mono, monospace", textAlign: "right" }} />
            <button onClick={() => setThresholdCena(suggestedThresholds.cena)} className="tx"
              style={{ padding: "5px 8px", background: C.tealWash, color: C.tealDeep, border: `1px solid ${C.teal}`, borderRadius: 2, cursor: "pointer", fontFamily: "JetBrains Mono, monospace", fontSize: 10 }} title="Usa il 75° percentile">
              P75: €{suggestedThresholds.cena}
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Section selector */}
    <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
      {[
        { id: "A", label: "A · Leve di ricavo", color: C.ink },
        { id: "B", label: "B · Leve di margine", color: C.olive },
        { id: "C", label: "C · Completezza scontrino", color: C.teal },
      ].map(s => <button key={s.id} onClick={() => setSection(s.id)} className="tx"
        style={{ flex: 1, padding: "10px 14px", background: section === s.id ? s.color : C.white, color: section === s.id ? C.paper : C.ink, border: `1px solid ${section === s.id ? s.color : C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 500, textAlign: "left" }}>
        {s.label}
      </button>)}
    </div>

    {/* SECTION A — Leve di ricavo */}
    {section === "A" && <div>
      <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2, marginBottom: 14, fontSize: 12, color: C.inkSoft, lineHeight: 1.5 }}>
        Divido i tuoi scontrini in due gruppi: <strong>ottimizzati</strong> (sopra la soglia per servizio) e <strong>non-ottimizzati</strong> (sotto). Poi confronto cosa contengono di diverso. Le categorie con il delta più alto sono <strong>le leve per alzare il ricavo</strong>.
      </div>
      {!hasConfidenceA && <div style={{ padding: 12, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, marginBottom: 14, fontSize: 12, color: C.inkSoft, display: "flex", gap: 10, alignItems: "center" }}>
        <AlertTriangle size={14} color={C.amberDeep} />
        <span>Campione limitato ({groupedA.opt.length} ottimizzati, {groupedA.nopt.length} non ottimizzati). Servono almeno {MIN_N} per gruppo per insight affidabili.</span>
      </div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <div style={{ background: C.oliveWash, border: `1px solid ${C.olive}`, padding: 16, borderRadius: 2 }}>
          <div className="f-mono" style={{ fontSize: 10, color: C.olive, letterSpacing: "0.2em" }}>✓ SCONTRINI OTTIMIZZATI</div>
          <div className="f-display" style={{ fontSize: 28, fontWeight: 600, color: C.oliveDeep, marginTop: 3 }}>{groupedA.opt.length}</div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>
            {((groupedA.opt.length / enriched.length) * 100).toFixed(1)}% del totale · media <strong>{fmt.eur(avgPerCover(groupedA.opt))}/cop</strong> · {avgCovers(groupedA.opt).toFixed(1)} coperti medi
          </div>
        </div>
        <div style={{ background: C.terraWash, border: `1px solid ${C.terra}`, padding: 16, borderRadius: 2 }}>
          <div className="f-mono" style={{ fontSize: 10, color: C.terra, letterSpacing: "0.2em" }}>• SCONTRINI NON OTTIMIZZATI</div>
          <div className="f-display" style={{ fontSize: 28, fontWeight: 600, color: C.terra, marginTop: 3 }}>{groupedA.nopt.length}</div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>
            {((groupedA.nopt.length / enriched.length) * 100).toFixed(1)}% del totale · media <strong>{fmt.eur(avgPerCover(groupedA.nopt))}/cop</strong> · {avgCovers(groupedA.nopt).toFixed(1)} coperti medi
          </div>
        </div>
      </div>

      <div className="f-display" style={{ fontSize: 15, fontWeight: 500, color: C.ink, marginBottom: 4 }}>Cosa hanno di diverso</div>
      <div className="f-body" style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.4 }}>
        Test χ² (chi-quadro) con correzione di Yates: <strong>p&lt;0.05</strong> = differenza reale (leva); <strong>p≥0.05</strong> = scarto compatibile col caso → riga deenfatizzata, NON usare come leva.
      </div>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.7fr 0.7fr 0.6fr 0.8fr 1.6fr", padding: "10px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.2em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
          <span>CATEGORIA</span><span style={{ textAlign: "right" }}>% OTTIM.</span><span style={{ textAlign: "right" }}>% NON-OTT.</span><span style={{ textAlign: "right" }}>DELTA</span><span style={{ textAlign: "right" }}>p-VALUE</span><span>FRASE</span>
        </div>
        {categoryDeltasA.map(d => {
          const sign = d.deltaP >= 0 ? "+" : "";
          const isSig = d.chi.isSignificant;
          const hasWarning = !!d.chi.warning;
          const color = !isSig ? C.muted : (d.deltaP > 10 ? C.olive : d.deltaP < -10 ? C.terra : C.muted);
          const pLabel = hasWarning
            ? "n/a"
            : (d.chi.pValue < 0.001 ? "p<0.001" : `p=${d.chi.pValue.toFixed(3)}`);
          const pColor = isSig ? C.oliveDeep : C.muted;
          const sentence = !isSig
            ? `Scarto di ${sign}${d.deltaP.toFixed(0)}pp non significativo${hasWarning ? ` (${d.chi.warning})` : ` (${pLabel})`} — compatibile con fluttuazione casuale, NON una leva.`
            : Math.abs(d.deltaP) < 5
              ? `Differenza piccola ma significativa (${pLabel}). Effetto reale ma ridotto.`
              : d.deltaP > 0
                ? `Gli ottimizzati includono ${DISH_CATEGORIES[d.cat].label.toLowerCase()} nel ${d.optP.toFixed(0)}% dei casi vs ${d.noptP.toFixed(0)}% dei non-ottimizzati. Differenza ${Math.abs(d.deltaP).toFixed(0)}pp, ${pLabel} → leva confermata.`
                : `I non-ottimizzati includono ${DISH_CATEGORIES[d.cat].label.toLowerCase()} più frequentemente (${d.noptP.toFixed(0)}% vs ${d.optP.toFixed(0)}%, ${pLabel}). Non è una leva per alzare il ricavo.`;
          return <div key={d.cat} style={{ display: "grid", gridTemplateColumns: "1.1fr 0.7fr 0.7fr 0.6fr 0.8fr 1.6fr", padding: "8px 14px", borderTop: `1px solid ${C.ruleSoft}`, alignItems: "center", fontSize: 11, opacity: isSig ? 1 : 0.55 }}>
            <span className="f-body" style={{ color: DISH_CATEGORIES[d.cat].color, fontWeight: 600 }}>{DISH_CATEGORIES[d.cat].label}</span>
            <span className="f-mono" style={{ textAlign: "right", color: C.oliveDeep, fontWeight: 600 }}>{d.optP.toFixed(0)}%</span>
            <span className="f-mono" style={{ textAlign: "right", color: C.terra, fontWeight: 600 }}>{d.noptP.toFixed(0)}%</span>
            <span className="f-mono" style={{ textAlign: "right", color, fontWeight: 700, fontSize: 13 }}>{sign}{d.deltaP.toFixed(0)}pp</span>
            <span className="f-mono" style={{ textAlign: "right", color: pColor, fontWeight: isSig ? 700 : 400, fontSize: 11 }} title={hasWarning ? d.chi.warning : `χ²=${d.chi.chi.toFixed(2)}, df=1`}>
              {pLabel}{isSig ? " ✓" : ""}
            </span>
            <span className="f-body" style={{ color: C.inkSoft, fontSize: 11, lineHeight: 1.4 }}>{sentence}</span>
          </div>;
        })}
      </div>
    </div>}

    {/* SECTION B — Leve di margine */}
    {section === "B" && <div>
      <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2, marginBottom: 14, fontSize: 12, color: C.inkSoft, lineHeight: 1.5 }}>
        Stesso ragionamento della sezione A, ma la soglia è sul <strong>margine netto per coperto</strong> (ricavo netto − costo materia prima). Uno scontrino può avere ricavo alto ma margine basso se è pieno di pesce crudo premium. Qui vedi cosa fa aumentare il <strong>margine vero</strong>, non il ricavo. La soglia è la mediana del tuo storico ({fmt.eur(groupedB.median)}).
      </div>
      {!hasConfidenceB && <div style={{ padding: 12, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, marginBottom: 14, fontSize: 12, color: C.inkSoft, display: "flex", gap: 10, alignItems: "center" }}>
        <AlertTriangle size={14} color={C.amberDeep} />
        <span>Campione limitato. Servono almeno {MIN_N} scontrini per gruppo.</span>
      </div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <div style={{ background: C.oliveWash, border: `1px solid ${C.olive}`, padding: 16, borderRadius: 2 }}>
          <div className="f-mono" style={{ fontSize: 10, color: C.olive, letterSpacing: "0.2em" }}>✓ ALTO MARGINE</div>
          <div className="f-display" style={{ fontSize: 28, fontWeight: 600, color: C.oliveDeep, marginTop: 3 }}>{groupedB.opt.length}</div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>
            margine medio <strong>{fmt.eur(groupedB.opt.reduce((s, x) => s + x.marginPerCover, 0) / Math.max(groupedB.opt.length, 1))}/cop</strong>
          </div>
        </div>
        <div style={{ background: C.terraWash, border: `1px solid ${C.terra}`, padding: 16, borderRadius: 2 }}>
          <div className="f-mono" style={{ fontSize: 10, color: C.terra, letterSpacing: "0.2em" }}>• BASSO MARGINE</div>
          <div className="f-display" style={{ fontSize: 28, fontWeight: 600, color: C.terra, marginTop: 3 }}>{groupedB.nopt.length}</div>
          <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>
            margine medio <strong>{fmt.eur(groupedB.nopt.reduce((s, x) => s + x.marginPerCover, 0) / Math.max(groupedB.nopt.length, 1))}/cop</strong>
          </div>
        </div>
      </div>

      <div className="f-display" style={{ fontSize: 15, fontWeight: 500, color: C.ink, marginBottom: 4 }}>Categorie che aumentano o riducono il margine</div>
      <div className="f-body" style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.4 }}>
        Test χ²: <strong>p&lt;0.05</strong> = effetto reale; <strong>p≥0.05</strong> = scarto casuale → riga deenfatizzata.
      </div>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.7fr 0.7fr 0.6fr 0.8fr 1.6fr", padding: "10px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.2em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
          <span>CATEGORIA</span><span style={{ textAlign: "right" }}>% ALTO M.</span><span style={{ textAlign: "right" }}>% BASSO M.</span><span style={{ textAlign: "right" }}>DELTA</span><span style={{ textAlign: "right" }}>p-VALUE</span><span>FRASE</span>
        </div>
        {categoryDeltasB.map(d => {
          const sign = d.deltaP >= 0 ? "+" : "";
          const isSig = d.chi.isSignificant;
          const hasWarning = !!d.chi.warning;
          const color = !isSig ? C.muted : (d.deltaP > 10 ? C.olive : d.deltaP < -10 ? C.terra : C.muted);
          const pLabel = hasWarning
            ? "n/a"
            : (d.chi.pValue < 0.001 ? "p<0.001" : `p=${d.chi.pValue.toFixed(3)}`);
          const pColor = isSig ? C.oliveDeep : C.muted;
          const sentence = !isSig
            ? `Scarto di ${sign}${d.deltaP.toFixed(0)}pp non significativo${hasWarning ? ` (${d.chi.warning})` : ` (${pLabel})`} — compatibile col caso, NON una leva di margine.`
            : Math.abs(d.deltaP) < 5
              ? `Effetto piccolo ma significativo (${pLabel}).`
              : d.deltaP > 0
                ? `Gli scontrini ad alto margine includono ${DISH_CATEGORIES[d.cat].label.toLowerCase()} nel ${d.optP.toFixed(0)}% vs ${d.noptP.toFixed(0)}% (${pLabel}) — spingi questa categoria per migliorare la redditività.`
                : `Categoria che abbassa il margine: ${d.noptP.toFixed(0)}% nei bassi vs ${d.optP.toFixed(0)}% negli alti (${pLabel}). Valuta food cost.`;
          return <div key={d.cat} style={{ display: "grid", gridTemplateColumns: "1.1fr 0.7fr 0.7fr 0.6fr 0.8fr 1.6fr", padding: "8px 14px", borderTop: `1px solid ${C.ruleSoft}`, alignItems: "center", fontSize: 11, opacity: isSig ? 1 : 0.55 }}>
            <span className="f-body" style={{ color: DISH_CATEGORIES[d.cat].color, fontWeight: 600 }}>{DISH_CATEGORIES[d.cat].label}</span>
            <span className="f-mono" style={{ textAlign: "right", color: C.oliveDeep, fontWeight: 600 }}>{d.optP.toFixed(0)}%</span>
            <span className="f-mono" style={{ textAlign: "right", color: C.terra, fontWeight: 600 }}>{d.noptP.toFixed(0)}%</span>
            <span className="f-mono" style={{ textAlign: "right", color, fontWeight: 700, fontSize: 13 }}>{sign}{d.deltaP.toFixed(0)}pp</span>
            <span className="f-mono" style={{ textAlign: "right", color: pColor, fontWeight: isSig ? 700 : 400, fontSize: 11 }} title={hasWarning ? d.chi.warning : `χ²=${d.chi.chi.toFixed(2)}, df=1`}>
              {pLabel}{isSig ? " ✓" : ""}
            </span>
            <span className="f-body" style={{ color: C.inkSoft, fontSize: 11, lineHeight: 1.4 }}>{sentence}</span>
          </div>;
        })}
      </div>
    </div>}

    {/* SECTION C — Completezza scontrino */}
    {section === "C" && <div>
      <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2, marginBottom: 14, fontSize: 12, color: C.inkSoft, lineHeight: 1.5 }}>
        Uno scontrino è <strong>completo</strong> se tocca più categorie del menu (antipasto + primo/secondo + contorno + dolce + vino/caffè). Qui vedi la distribuzione della completezza nei tuoi scontrini e l'impatto sul fatturato per coperto. Ti dice se lo staff in sala propone il <strong>percorso completo</strong> o si ferma prima.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
        {Object.entries(completezzaBuckets).map(([label, group]) => {
          const pct = enriched.length > 0 ? (group.length / enriched.length) * 100 : 0;
          const avgPC = avgPerCover(group);
          const color = label === "4+ categorie" ? C.olive : label === "3 categorie" ? C.teal : label === "2 categorie" ? C.amberDeep : C.terra;
          const bg = label === "4+ categorie" ? C.oliveWash : label === "3 categorie" ? C.tealWash : label === "2 categorie" ? C.amberWash : C.terraWash;
          return <div key={label} style={{ background: bg, border: `1px solid ${color}`, padding: 14, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 10, color, letterSpacing: "0.2em", fontWeight: 600 }}>{label.toUpperCase()}</div>
            <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color, marginTop: 3 }}>{group.length}</div>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{pct.toFixed(1)}% scontrini</div>
            <div style={{ height: 1, background: color, opacity: 0.3, margin: "8px 0" }} />
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>€/COP MEDIO</div>
            <div className="f-mono" style={{ fontSize: 16, color: C.ink, fontWeight: 600 }}>{fmt.eur(avgPC)}</div>
          </div>;
        })}
      </div>

      {/* Heatmap categoria × n° categorie */}
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 16, borderRadius: 2, marginBottom: 16 }}>
        <div className="f-display" style={{ fontSize: 14, fontWeight: 500, color: C.ink, marginBottom: 10 }}>Quale categoria compare in base alla completezza</div>
        <div style={{ overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: C.paperDeep, fontSize: 9, letterSpacing: "0.15em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
                <th style={{ padding: "8px 10px", textAlign: "left" }}>Categoria</th>
                {Object.keys(completezzaBuckets).map(k => <th key={k} style={{ padding: "8px 10px", textAlign: "center" }}>{k}</th>)}
              </tr>
            </thead>
            <tbody>
              {DISH_CAT_KEYS.map(cat => <tr key={cat} style={{ borderTop: `1px solid ${C.ruleSoft}` }}>
                <td style={{ padding: "6px 10px" }}>
                  <span className="f-body" style={{ color: DISH_CATEGORIES[cat].color, fontWeight: 600 }}>{DISH_CATEGORIES[cat].label}</span>
                </td>
                {Object.entries(completezzaBuckets).map(([label, group]) => {
                  const pct = group.length > 0 ? (group.filter(x => x.catCounts[cat] > 0).length / group.length) * 100 : 0;
                  const alpha = pct / 100;
                  return <td key={label} style={{ padding: "6px 10px", textAlign: "center", background: pct > 0 ? `rgba(31,77,72,${Math.max(0.08, alpha * 0.6)})` : "transparent" }}>
                    <span className="f-mono" style={{ fontSize: 12, color: pct > 40 ? C.white : C.inkSoft, fontWeight: 600 }}>{pct.toFixed(0)}%</span>
                  </td>;
                })}
              </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Frase chiave di sintesi */}
      {(() => {
        const b4 = completezzaBuckets["4+ categorie"];
        const b1 = completezzaBuckets["1 categoria"];
        if (enriched.length < MIN_N || b4.length < 5) return null;
        const pct4 = (b4.length / enriched.length) * 100;
        const avg4 = avgPerCover(b4);
        const avg1 = avgPerCover(b1);
        const gap = avg1 > 0 ? ((avg4 - avg1) / avg1) * 100 : 0;
        return <div style={{ padding: 16, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, fontSize: 13, color: C.tealDeep, lineHeight: 1.6 }}>
          💡 <strong>Leva completezza:</strong> il {pct4.toFixed(0)}% dei tuoi scontrini tocca 4+ categorie con €/cop medio di <strong>{fmt.eur(avg4)}</strong>. Il {((b1.length / enriched.length) * 100).toFixed(0)}% si ferma a 1 sola categoria con <strong>{fmt.eur(avg1)}</strong>. Scontrini completi rendono <strong>+{gap.toFixed(0)}%</strong> per coperto.
          {pct4 < 30 && <> Margine di crescita significativo: se passi dal {pct4.toFixed(0)}% al 50% di scontrini completi, incasso stimato +{((0.5 - pct4 / 100) * gap / 100 * 100).toFixed(0)}% per coperto.</>}
        </div>;
      })()}
    </div>}
  </div>;
}


// ── Card concentrazione menu (HHI + Gini + Pareto 80) ─────────────────────
function MenuConcentrationCard({ concentration }) {
  if (!concentration || concentration.fallback) {
    return (
      <div style={{ padding: 12, background: C.paperDeep, border: `1px solid ${C.rule}`, borderRadius: 2, marginBottom: 12, fontSize: 12, color: C.inkMid }}>
        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>Concentrazione menu · {concentration?.windowDays || 90}gg</div>
        <div>{concentration?.reason || "Dati insufficienti per stimare concentrazione."}</div>
      </div>
    );
  }

  // Soglie HHI
  const hhi = concentration.hhi;
  let hhiLabel, hhiColor, hhiBg;
  if (hhi < 0.05)      { hhiLabel = "Distribuito";                hhiColor = C.olive;  hhiBg = C.oliveWash; }
  else if (hhi < 0.15) { hhiLabel = "Concentrazione normale";     hhiColor = C.teal;   hhiBg = C.tealWash; }
  else if (hhi < 0.25) { hhiLabel = "Concentrato · rischio dipendenza"; hhiColor = C.amberDeep; hhiBg = C.amberWash; }
  else                 { hhiLabel = "Fragile · over-dependence";  hhiColor = C.terraDeep; hhiBg = C.terraWash; }

  const top = concentration.items[0];
  const top5 = concentration.items.slice(0, 5);
  const giniLabel = concentration.gini < 0.55 ? "equilibrato" : concentration.gini < 0.70 ? "moderatamente sbilanciato" : "Pareto attivo";

  return (
    <div style={{ background: C.white, border: `1px solid ${hhiColor}`, borderRadius: 2, marginBottom: 14, overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", background: hhiBg, borderBottom: `1px solid ${hhiColor}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: hhiColor, textTransform: "uppercase" }}>Concentrazione menu · {concentration.windowDays}gg · {concentration.nScontrini} scontrini</div>
          <div className="f-display" style={{ fontSize: 16, fontWeight: 600, color: hhiColor, marginTop: 2 }}>{hhiLabel}</div>
        </div>
        {top && (
          <div style={{ textAlign: "right" }}>
            <div className="f-mono" style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em" }}>Se il top piatto va out-of-stock</div>
            <div className="f-display" style={{ fontSize: 18, fontWeight: 600, color: C.terra }}>−{(top.share * 100).toFixed(1)}% fatturato</div>
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0 }}>
        <div style={{ padding: 12, borderRight: `1px solid ${C.ruleSoft}` }} title="HHI = Σ(quota_i)². Più alto = più concentrato. <0.05 distribuito, >0.25 fragile.">
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.18em" }}>HHI</div>
          <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink, marginTop: 4 }}>{hhi.toFixed(3)}</div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>Herfindahl-Hirschman</div>
        </div>
        <div style={{ padding: 12, borderRight: `1px solid ${C.ruleSoft}` }} title="Gini 0 = perfetta equità tra piatti; Gini → 1 = massima concentrazione. >0.65 = Pareto attivo.">
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.18em" }}>GINI</div>
          <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink, marginTop: 4 }}>{concentration.gini.toFixed(2)}</div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{giniLabel}</div>
        </div>
        <div style={{ padding: 12, borderRight: `1px solid ${C.ruleSoft}` }} title="Quanti piatti servono per fare l'80% del fatturato.">
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.18em" }}>PARETO 80</div>
          <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink, marginTop: 4 }}>
            {concentration.pareto80} <span style={{ fontSize: 12, color: C.muted }}>/ {concentration.totalDishes}</span>
          </div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{(concentration.pareto80Pct * 100).toFixed(0)}% dei piatti = 80% fatturato</div>
        </div>
        <div style={{ padding: 12 }} title="Quota di fatturato del piatto più venduto.">
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.18em" }}>TOP CONCENTR.</div>
          <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink, marginTop: 4 }}>{(concentration.topConcentration * 100).toFixed(1)}%</div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={top?.name || ""}>{top?.name || "—"}</div>
        </div>
      </div>

      {top5.length > 0 && (
        <div style={{ padding: 12, borderTop: `1px solid ${C.ruleSoft}` }}>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.18em", color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>TOP 5 PIATTI · fatturato e cumulato</div>
          {top5.map((it) => (
            <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: `1px solid ${C.ruleSoft}`, fontSize: 12 }}>
              <div className="f-mono" style={{ fontSize: 10, color: C.muted, width: 22, textAlign: "right" }}>{it.rank}.</div>
              <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: C.ink }} title={it.name}>{it.name}</div>
              <div className="f-mono" style={{ width: 80, textAlign: "right", color: C.ink, fontWeight: 600 }}>{fmt.eur(it.revenue)}</div>
              <div className="f-mono" style={{ width: 60, textAlign: "right", color: C.inkMid }}>{(it.share * 100).toFixed(1)}%</div>
              <div className="f-mono" style={{ width: 70, textAlign: "right", color: C.muted, fontSize: 11 }} title="Quota cumulata">cum {(it.cumShare * 100).toFixed(1)}%</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AnalyticsMenuEngineering({ state, menuEng }) {
  const concentration = useMemo(() => engine.stats.menuConcentration(state, { windowDays: 90 }), [state.tableReceipts, state.dishesReal]);

  if (!menuEng.items || menuEng.items.length === 0) return <EmptyState icon={Utensils} title="Dati insufficienti" body={menuEng.fallback ? `Nessuno scontrino negli ultimi ${menuEng.periodDays || 30} giorni. Importa storico o chiudi nuovi scontrini per popolare il modello.` : "Servono almeno alcuni scontrini chiusi per analizzare il menu"} />;

  const quadrants = {
    star:      { label: "⭐ Stelle",       desc: "Alta popolarità + alto margine · spingi queste", color: C.olive, bg: C.oliveWash, action: "Mantieni la posizione. Promuovi visivamente nel menu.", tooltip: "Star: combinazione vincente. Non toccare il prezzo, dai visibilità nel menu, garantisci disponibilità ingredienti." },
    plowhorse: { label: "🐎 Cavalli da soma", desc: "Alta popolarità + basso margine · da ottimizzare", color: C.amberDeep, bg: C.amberWash, action: "Alza il prezzo di €1-2 o riduci costo ingredienti. Il cliente non noterà.", tooltip: "Plowhorse: piace ma rende poco. Opzioni: alzare prezzo (+€1-2 su 100 piatti vale), riqualificare ingrediente costoso, ridurre porzione, abbinare a contorno alto margine." },
    puzzle:    { label: "🔍 Enigmi",       desc: "Bassa popolarità + alto margine · valuta promozione", color: C.teal, bg: C.tealWash, action: "Promuovi: foto, descrizione, suggerimento cameriere. Oppure riposiziona nel menu.", tooltip: "Puzzle: rende bene ma non vende. Strategia: foto, copy, posizione nel menu, suggerimento attivo dei camerieri, abbassare leggermente il prezzo." },
    dog:       { label: "🐕 Cani",         desc: "Bassa popolarità + basso margine · rimuovi dal menu", color: C.terra, bg: C.terraWash, action: "Candidato alla rimozione. Occupa spazio senza rendere.", tooltip: "Dog: occupa spazio senza rendere. Candidato alla rimozione, salvo motivi strategici (es. unico vegano del menu). Libera spazio per nuovi piatti." },
  };

  const byQuadrant = { star: [], plowhorse: [], puzzle: [], dog: [] };
  menuEng.items.forEach(item => byQuadrant[item.quadrant].push(item));

  const popThresholdPct = (menuEng.popThreshold || 0) * 100;
  const popularityRule = menuEng.popularityRule || 0.70;

  return <div>
    <MenuConcentrationCard concentration={concentration} />

    <div style={{ padding: 12, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, marginBottom: 12, fontSize: 12, color: C.tealDeep, lineHeight: 1.5 }}>
      Metodologia <strong>Kasavana-Smith</strong> (Cornell HRA, 1982): ogni piatto è classificato su <strong>popolarità</strong> (quota di vendite vs media) e <strong>margine di contribuzione</strong> (prezzo netto − costo netto, ponderato per ciò che si vende davvero).
    </div>

    {/* Pannello soglie + meta statistica */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 14 }}>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 12, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.18em" }}>SOGLIA MARGINE</div>
        <div className="f-display" style={{ fontSize: 18, fontWeight: 600, color: C.ink, marginTop: 4 }}>{fmt.eur(menuEng.cmThreshold || 0)}</div>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>media ponderata per qty</div>
      </div>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 12, borderRadius: 2 }} title={`Regola Kasavana-Smith: ${popularityRule.toFixed(2)} × (1/N piatti). Sotto questa quota = impopolare.`}>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.18em" }}>SOGLIA POPOLARITÀ</div>
        <div className="f-display" style={{ fontSize: 18, fontWeight: 600, color: C.ink, marginTop: 4 }}>{popThresholdPct.toFixed(2)}%</div>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{popularityRule.toFixed(2)} × (1/{menuEng.items.length})</div>
      </div>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 12, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.18em" }}>PIATTI ANALIZZATI</div>
        <div className="f-display" style={{ fontSize: 18, fontWeight: 600, color: C.ink, marginTop: 4 }}>{menuEng.items.length}</div>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>su {menuEng.totalQty} vendite · {menuEng.periodDays}gg</div>
      </div>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 12, borderRadius: 2 }} title="Borderline = entro ±15% da una soglia. Classificazione fragile, basta una manciata di scontrini per flippare il quadrante.">
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.18em" }}>BORDERLINE / RARI</div>
        <div className="f-display" style={{ fontSize: 18, fontWeight: 600, color: C.amberDeep, marginTop: 4 }}>{menuEng.borderlineCount || 0} <span style={{ fontSize: 12, color: C.muted }}>/ {menuEng.rareCount || 0}</span></div>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>confidenza incerta / n&lt;5</div>
      </div>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
      {Object.entries(quadrants).map(([key, q]) => {
        const items = byQuadrant[key];
        return <div key={key} style={{ background: C.white, border: `1px solid ${q.color}`, borderRadius: 2, overflow: "hidden" }}>
          <div style={{ padding: "14px 16px", background: q.bg, borderBottom: `1px solid ${q.color}` }} title={q.tooltip}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div className="f-display" style={{ fontSize: 16, fontWeight: 600, color: q.color }}>{q.label}</div>
              <div className="f-mono" style={{ fontSize: 11, color: q.color, fontWeight: 600 }}>{items.length}</div>
            </div>
            <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, marginTop: 2 }}>{q.desc}</div>
            <div className="f-body" style={{ fontSize: 10, color: q.color, marginTop: 6, fontStyle: "italic" }}>→ {q.action}</div>
          </div>
          <div style={{ padding: 8, maxHeight: 280, overflowY: "auto" }}>
            {items.length === 0 ? <div style={{ padding: 12, textAlign: "center", color: C.muted, fontSize: 11 }}>Nessun piatto</div>
            : items.slice(0, 12).map(item => {
              const sharePct = (item.shareQty * 100).toFixed(2);
              const shareCITxt = item.shareCI ? ` (CI ${(item.shareCI[0]*100).toFixed(1)}–${(item.shareCI[1]*100).toFixed(1)}%)` : "";
              const rareTooltip = item.isRare ? `DATI INSUFFICIENTI: solo ${item.qty} vendite in ${menuEng.periodDays}gg. Stima Bayesiana (prior Gamma α=2,β=1) → λ atteso ≈ ${(item.qtyExpected || 0).toFixed(1)}/periodo. Servono almeno altri 14gg di osservazioni.` : "";
              const borderlineTooltip = item.borderline ? "BORDERLINE: classificazione incerta — il piatto è entro ±15% da una soglia. Servono ~2 settimane di vendite in più per stabilizzare il quadrante." : "";
              return <div key={item.dishId} style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, borderBottom: `1px solid ${C.ruleSoft}`, fontSize: 11 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <div className="f-body" style={{ color: C.ink, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>{item.name}</div>
                    {item.isRare && <span title={rareTooltip} style={{ fontSize: 8, fontWeight: 700, padding: "1px 5px", background: C.terra, color: C.white, borderRadius: 1, letterSpacing: "0.05em", cursor: "help" }}>DATI INSUFF.</span>}
                    {item.borderline && !item.isRare && <span title={borderlineTooltip} style={{ fontSize: 8, fontWeight: 700, padding: "1px 5px", background: C.amber, color: C.ink, borderRadius: 1, letterSpacing: "0.05em", cursor: "help" }}>BORDERLINE</span>}
                  </div>
                  <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }} title={`Quota vendita: ${sharePct}%${shareCITxt}. Margine unitario: ${fmt.eur(item.margin)}. Food cost: ${item.fcPct.toFixed(0)}%.`}>
                    × {item.qty} · {sharePct}%{item.shareCI ? "*" : ""} · F.C. {item.fcPct.toFixed(0)}%
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="f-mono" style={{ fontSize: 12, color: q.color, fontWeight: 600 }}>{fmt.eur(item.marginTotal)}</div>
                  <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>margine tot</div>
                </div>
              </div>;
            })}
            {items.length > 12 && <div style={{ padding: 8, textAlign: "center", fontSize: 10, color: C.muted }}>+ altri {items.length - 12}</div>}
          </div>
        </div>;
      })}
    </div>

    {/* Footer metodologia */}
    <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2, marginTop: 14, fontSize: 11, color: C.inkSoft, lineHeight: 1.55 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Info size={13} color={C.teal} />
        <strong className="f-display" style={{ color: C.ink, fontSize: 12 }}>Note metodologiche</strong>
      </div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        <li>Soglia margine = <strong>media ponderata per qty venduta</strong> (Σ margin × qty / Σ qty), canone Kasavana-Smith 1982.</li>
        <li>Soglia popolarità = <strong>{popularityRule.toFixed(2)} × (1/N)</strong>, non 1/N: evita il bias "in media è popolare".</li>
        <li>Piatti con &lt;5 vendite nel periodo: classificazione con <strong>prior Bayesian Gamma-Poisson</strong> (α=2, β=1). Badge "DATI INSUFF.".</li>
        <li>Piatti entro <strong>±15%</strong> da una soglia: badge "BORDERLINE" — classificazione fragile, ricontrollare a +14gg.</li>
        <li>* = Wilson CI 95% sulla quota di vendita (mostrato in tooltip su quote). Calcolato solo se N totale ≥ 10.</li>
      </ul>
    </div>
  </div>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║              ANALYTICS CROSS-SELL — MARKET BASKET ANALYSIS                 ║
// ║       P(B|A) + lift + χ² (Yates) + correzione Bonferroni                   ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function AnalyticsCrossSell({ state }) {
  const [windowDays, setWindowDays] = useState(90);
  const [minLift, setMinLift] = useState(1.5);
  const [minSupport, setMinSupport] = useState(30);

  const cross = useMemo(
    () => engine.stats.crossSellMatrix(state, { windowDays, minLift, minSupport, topN: 30, alpha: 0.01 }),
    [state.tableReceipts, state.dishesReal, windowDays, minLift, minSupport]
  );

  return <div>
    {/* Banner spiegazione metodologica */}
    <div style={{ padding: 12, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, marginBottom: 12, fontSize: 12, color: C.tealDeep, lineHeight: 1.5 }}>
      <strong>Market basket analysis</strong>: per i top-30 piatti più venduti, identifica le coppie che compaiono insieme più spesso del caso. <strong>Lift &gt; 1.5</strong> indica abbinamento più frequente del caso; significatività testata con <strong>χ²</strong> 2×2 (Yates) + <strong>correzione Bonferroni</strong> per evitare falsi positivi su test multipli.
    </div>

    {/* Filtri */}
    <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 14 }}>
      <Field label="Finestra">
        <Select value={windowDays} onChange={e => setWindowDays(parseInt(e.target.value, 10))} style={{ minWidth: 130 }}>
          <option value={30}>30 giorni</option>
          <option value={60}>60 giorni</option>
          <option value={90}>90 giorni</option>
          <option value={180}>6 mesi</option>
          <option value={365}>12 mesi</option>
        </Select>
      </Field>
      <Field label="Lift minimo">
        <Select value={minLift} onChange={e => setMinLift(parseFloat(e.target.value))} style={{ minWidth: 110 }}>
          <option value={1.2}>1.2× (debole)</option>
          <option value={1.5}>1.5× (consigliato)</option>
          <option value={2.0}>2.0× (forte)</option>
          <option value={3.0}>3.0× (fortissimo)</option>
        </Select>
      </Field>
      <Field label="Support minimo">
        <Select value={minSupport} onChange={e => setMinSupport(parseInt(e.target.value, 10))} style={{ minWidth: 110 }}>
          <option value={10}>10 scontrini</option>
          <option value={20}>20 scontrini</option>
          <option value={30}>30 (consigliato)</option>
          <option value={50}>50 scontrini</option>
        </Select>
      </Field>
      <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-end" }}>
        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.18em", color: C.muted, textTransform: "uppercase" }}>Scontrini analizzati</div>
        <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink }}>{cross.nScontrini}</div>
      </div>
    </div>

    {cross.fallback ? (
      <EmptyState icon={Link2} title="Dati insufficienti per cross-sell"
        body={cross.reason || "Servono almeno 50 scontrini nella finestra selezionata. Importa più storico o aspetta che si chiudano nuovi scontrini."} />
    ) : cross.associations.length === 0 ? (
      <EmptyState icon={Link2} title="Nessuna associazione significativa trovata"
        body={`Con i filtri attuali (lift ≥ ${minLift}, support ≥ ${minSupport}) non emergono coppie. Prova ad abbassare le soglie o ampliare la finestra.`} />
    ) : (
      <>
        {/* Card riepilogo */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 14 }}>
          <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.18em" }}>ASSOCIAZIONI</div>
            <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink, marginTop: 4 }}>{cross.associations.length}</div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>coppie con lift ≥ {minLift}</div>
          </div>
          <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.18em" }}>SIGNIFICATIVE</div>
            <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.olive, marginTop: 4 }}>{cross.associations.filter(a => a.isSignificant).length}</div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>p &lt; {cross.adjAlpha.toExponential(1)} (Bonferroni)</div>
          </div>
          <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.18em" }}>TOP PIATTI</div>
            <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink, marginTop: 4 }}>{cross.nTopDishes}</div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>candidati per coppie</div>
          </div>
          <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.18em" }}>LIFT MEDIANO</div>
            <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.ink, marginTop: 4 }}>
              {(() => {
                const arr = cross.associations.map(a => a.lift).sort((a, b) => a - b);
                if (!arr.length) return "—";
                const m = arr[Math.floor(arr.length / 2)];
                return m.toFixed(2) + "×";
              })()}
            </div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>su {cross.associations.length} coppie</div>
          </div>
        </div>

        {/* Tabella associazioni */}
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 800 }}>
            <thead>
              <tr style={{ background: C.paperDeep }}>
                <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, color: C.inkMid, borderBottom: `1px solid ${C.rule}` }}>Chi prende…</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, color: C.inkMid, borderBottom: `1px solid ${C.rule}` }}>…prende anche</th>
                <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, color: C.inkMid, borderBottom: `1px solid ${C.rule}` }} title="Numero scontrini con entrambi i piatti">Support</th>
                <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, color: C.inkMid, borderBottom: `1px solid ${C.rule}` }} title="P(B|A) = % degli scontrini con A che hanno anche B">P(B|A)</th>
                <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, color: C.inkMid, borderBottom: `1px solid ${C.rule}` }} title="Lift = P(B|A)/P(B). >1 abbinamento più frequente del caso">Lift</th>
                <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, color: C.inkMid, borderBottom: `1px solid ${C.rule}` }}>p-value</th>
                <th style={{ padding: "10px 12px", textAlign: "center", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, color: C.inkMid, borderBottom: `1px solid ${C.rule}` }}>Significatività</th>
              </tr>
            </thead>
            <tbody>
              {cross.associations.slice(0, 50).map((row, i) => {
                const pBApct = (row.pBA * 100).toFixed(0);
                const pValueText = row.chiWarning ? "—" : (row.pValue < 0.001 ? "<0.001" : row.pValue.toFixed(3));
                return (
                  <tr key={`${row.a}|${row.b}`} style={{ background: i % 2 === 0 ? C.white : C.paperSoft, borderBottom: `1px solid ${C.ruleSoft}` }}>
                    <td style={{ padding: "8px 12px", color: C.ink, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.aName}>{row.aName}</td>
                    <td style={{ padding: "8px 12px", color: C.ink, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }} title={row.bName}>{row.bName}</td>
                    <td className="f-mono" style={{ padding: "8px 12px", textAlign: "right", color: C.inkMid }}>{row.support}</td>
                    <td className="f-mono" style={{ padding: "8px 12px", textAlign: "right", color: C.ink, fontWeight: 600 }}>{pBApct}%</td>
                    <td className="f-mono" style={{ padding: "8px 12px", textAlign: "right", color: row.lift >= 2 ? C.olive : C.ink, fontWeight: 600 }}>{row.lift.toFixed(2)}×</td>
                    <td className="f-mono" style={{ padding: "8px 12px", textAlign: "right", color: C.inkMid }}>{pValueText}</td>
                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                      {row.chiWarning ? (
                        <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", background: C.paperDeep, color: C.muted, borderRadius: 2, letterSpacing: "0.04em" }} title={row.chiWarning}>n insuff.</span>
                      ) : row.isSignificant ? (
                        <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", background: C.olive, color: C.white, borderRadius: 2, letterSpacing: "0.05em" }} title={`p < ${cross.adjAlpha.toExponential(1)} (Bonferroni corretto)`}>SIGNIFICATIVO</span>
                      ) : (
                        <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", background: C.paperDeep, color: C.muted, borderRadius: 2, letterSpacing: "0.05em" }} title="non passa Bonferroni: potrebbe essere rumore">rumore</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {cross.associations.length > 50 && (
            <div style={{ padding: 10, textAlign: "center", fontSize: 11, color: C.muted, borderTop: `1px solid ${C.rule}` }}>
              + altre {cross.associations.length - 50} associazioni (mostrate le prime 50 per lift)
            </div>
          )}
        </div>

        {/* Footer metodologico */}
        <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2, marginTop: 14, fontSize: 11, color: C.inkSoft, lineHeight: 1.55 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <Info size={13} color={C.teal} />
            <strong className="f-display" style={{ color: C.ink, fontSize: 12 }}>Come leggere</strong>
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li><strong>P(B|A)</strong>: chi prende A, in quale % prende anche B. Es. 78% = "su 100 clienti che hanno preso A, 78 hanno preso anche B".</li>
            <li><strong>Lift</strong>: rapporto P(B|A)/P(B). Lift 1.0 = indipendenti, lift 2.0× = preso il doppio del caso. Soglia operativa F&B: ≥1.5.</li>
            <li><strong>Support</strong>: numero di scontrini con entrambi. Sotto 30 il segnale è dominato dal rumore (Agresti 2013).</li>
            <li><strong>Significativo</strong>: passa il test χ² con correzione Bonferroni (α/{cross.nTopDishes * (cross.nTopDishes - 1)} ≈ {cross.adjAlpha.toExponential(1)}). "Rumore" = la statistica regge a un test singolo ma non al multiplo.</li>
            <li>Le associazioni con badge <strong>SIGNIFICATIVO</strong> sono candidate ideali per training cameriere e suggerimenti up-selling.</li>
          </ul>
        </div>
      </>
    )}
  </div>;
}

// ── Proiezioni 7/14/30 giorni ──────────────────────────────────────────────
function AnalyticsForecast({ state, forecast, horizon, setHorizon, metrics }) {
  if (forecast.dataPoints < 7) return <div style={{ padding: 20, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2 }}>
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
      <AlertTriangle size={18} color={C.amberDeep} />
      <div>
        <div className="f-display" style={{ fontSize: 15, color: C.ink, fontWeight: 600 }}>Dati insufficienti per proiezioni affidabili</div>
        <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, marginTop: 6, lineHeight: 1.6 }}>
          Hai <strong>{forecast.dataPoints} giorni</strong> di storico, ne servono almeno 7 per iniziare a proiettare. Continua a chiudere scontrini o importa il tuo storico Excel per popolare il modello.
        </div>
      </div>
    </div>
  </div>;

  const maxY = Math.max(...forecast.points.map(p => p.high), 1);
  // Orizzonte effettivamente coperto (può essere < richiesto se cappato)
  const horizonEff = forecast.horizonActual || forecast.points.length || horizon;

  return <div>
    {/* Selettore orizzonte */}
    <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
      {[7, 14, 30].map(n => <button key={n} onClick={() => setHorizon(n)} className="tx"
        style={{ padding: "8px 14px", background: horizon === n ? C.ink : C.white, color: horizon === n ? C.paper : C.ink, border: `1px solid ${horizon === n ? C.ink : C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", fontWeight: 500, fontSize: 12 }}>
        {n} giorni
      </button>)}
    </div>

    {/* AVVERTIMENTO: orizzonte troncato perché supera 2× lo storico */}
    {forecast.horizonCapped && <div style={{ padding: 14, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, marginBottom: 16, display: "flex", gap: 12, alignItems: "flex-start" }}>
      <AlertTriangle size={18} color={C.amberDeep} style={{ flexShrink: 0, marginTop: 2 }} />
      <div>
        <div className="f-display" style={{ fontSize: 13, color: C.amberDeep, fontWeight: 600 }}>
          Forecast troncato a {forecast.horizonActual}gg (richiesti {forecast.horizonRequested}gg)
        </div>
        <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, marginTop: 6, lineHeight: 1.55 }}>
          Con solo <strong>{forecast.dataPoints}gg</strong> di storia, estrapolare oltre <strong>{Math.floor(forecast.dataPoints * 2)}gg</strong> non è statisticamente affidabile (regola: mai più di 2× lo storico).
          Per un forecast pulito a {forecast.horizonRequested}gg ti servono almeno <strong>{Math.ceil(forecast.horizonRequested / 2)}gg</strong> di storia.
        </div>
      </div>
    </div>}

    {/* AVVERTIMENTO: trend stimato ma non significativo */}
    {/* FIX A.3: il nuovo metodo weekday-adjusted ritorna pct=0 quando reliable=false
        (non si "inventa" un trend su rumore stagionale), quindi questo banner ora si
        attiva solo se il vecchio percorso espone un pct≠0. Mantenuto per backward-compat. */}
    {forecast.trend.pct !== 0 && !forecast.trendReliable && <div style={{ padding: 10, background: C.paperDeep, border: `1px dashed ${C.rule}`, borderRadius: 2, marginBottom: 16, fontSize: 11, color: C.inkSoft, fontStyle: "italic", lineHeight: 1.5 }}>
      Trend stimato <strong>{forecast.trend.pct >= 0 ? "+" : ""}{(forecast.trend.pct * 100).toFixed(1)}%/sett</strong> ma non statisticamente significativo (servono ≥28gg per separare trend reale da stagionalità weekday). Il forecast assume <strong>zero trend</strong> per non amplificare il rumore.
    </div>}

    {/* STAGIONALITÀ MENSILE: etichetta onesta sulla fonte (FIX #6 audit 2026-06-04) */}
    {(() => {
      const src = forecast.seasonalSource;
      if (src === "learned") {
        return <div style={{ padding: 12, background: C.oliveWash, border: `1px solid ${C.olive}`, borderRadius: 2, marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <Calendar size={16} color={C.oliveDeep} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.55 }}>
            <strong className="f-display" style={{ color: C.oliveDeep }}>Stagionalità: appresa dai dati</strong> {forecast.seasonalYearsObserved ? `(${forecast.seasonalYearsObserved} anni di storico)` : `(almeno ${forecast.seasonalMinCycles} anni per i mesi coinvolti)`}.
            Il forecast corregge i mesi di alta/bassa stagione usando il tuo storico reale. Banda di confidenza normale.
            {(forecast.seasonalClosedMonths || []).length > 0 && <> Mesi rilevati come <strong>chiusi</strong> (locale stagionale): <strong>{forecast.seasonalClosedMonths.map(mo => MONTH_LABELS_IT[mo]).join(", ")}</strong> → proiezione 0 in quei mesi.</>}
          </div>
        </div>;
      }
      if (src === "manual") {
        return <div style={{ padding: 12, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <Calendar size={16} color={C.amberDeep} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.55 }}>
            <strong className="f-display" style={{ color: C.amberDeep }}>Stagionalità: profilo manuale (stima tua)</strong> — impostato in Impostazioni.
            Non hai ancora 2 anni di storico per "imparare" la stagionalità dai dati, quindi stiamo usando i coefficienti che hai inserito tu.
            Poiché è un'assunzione, la <strong>banda di confidenza è allargata del 25%</strong>: meglio onesti che falsamente precisi.
          </div>
        </div>;
      }
      // neutral
      return <div style={{ padding: 12, background: C.paperDeep, border: `1px dashed ${C.rule}`, borderRadius: 2, marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
        <Calendar size={16} color={C.muted} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.55 }}>
          <strong className="f-display" style={{ color: C.ink }}>Stagionalità: nessuna (profilo neutro)</strong>.
          Il forecast non applica correzioni mensili. Per un ristorante di costa (estate molto più piena dell'inverno) puoi impostare il
          tuo profilo stagionale in <strong>Impostazioni → Stagionalità mensile</strong>, oppure attendere ~2 anni di storico perché il sistema la apprenda da solo.
        </div>
      </div>;
    })()}

    {/* KPI totali */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 18 }}>
      <div style={{ background: C.white, border: `1px solid ${C.ink}`, padding: 16, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>INCASSO ATTESO {horizonEff}GG</div>
        <div className="f-display" style={{ fontSize: 28, fontWeight: 600, color: C.ink, marginTop: 4 }}>{fmt.eur(forecast.totalExpected)}</div>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>CI 95%: {fmt.eurShort(forecast.totalLow)} — {fmt.eurShort(forecast.totalHigh)}</div>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>banda ±{fmt.eurShort(forecast.totalCiHalf || 0)}</div>
      </div>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 16, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>COPERTI ATTESI</div>
        <div className="f-display" style={{ fontSize: 28, fontWeight: 600, color: C.ink, marginTop: 4 }}>{forecast.totalCovers}</div>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>~{Math.round(forecast.totalCovers / Math.max(1, horizonEff))} al giorno</div>
      </div>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 16, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>INCASSO MEDIO/GG</div>
        <div className="f-display" style={{ fontSize: 28, fontWeight: 600, color: C.ink, marginTop: 4 }}>{fmt.eur(forecast.totalExpected / Math.max(1, horizonEff))}</div>
      </div>
      {/* FIX A.3 (audit 2026-05-20): tooltip esplicito sul metodo + "n/d" se non affidabile */}
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 16, borderRadius: 2 }}
        title={forecast.trendReliable
          ? `Trend al netto della stagionalità weekday (regressione OLS su ${forecast.trend?.nUsed || forecast.dataPoints} giorni · R²=${(forecast.trend?.rSquared || 0).toFixed(2)}). Cattura crescita strutturale, non l'effetto "sabato vs lunedì".`
          : `Trend non affidabile: servono ≥28 giorni di storia per separare il trend reale dalla stagionalità weekday. Attualmente ${forecast.dataPoints}gg.`}>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>TREND</div>
        <div className="f-display" style={{ fontSize: 28, fontWeight: 600, color: (forecast.trendApplied || 0) >= 0 ? C.olive : C.terra, marginTop: 4 }}>
          {forecast.trendReliable
            ? <>{forecast.trend.pct >= 0 ? "+" : ""}{(forecast.trend.pct * 100).toFixed(1)}%</>
            : <span style={{ color: C.muted, fontSize: 22 }}>n/d</span>}
        </div>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
          {forecast.trendReliable ? "settimanale · weekday-adjusted · applicato compound" : "in attesa di più dati (≥28gg)"}
        </div>
      </div>
    </div>

    {/* Grafico proiezione con bande di confidenza */}
    <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 18, borderRadius: 2, marginBottom: 16 }}>
      <div className="f-display" style={{ fontSize: 16, fontWeight: 500, color: C.ink, marginBottom: 14 }}>Proiezione incasso giornaliero</div>
      <div style={{ position: "relative", height: 220, display: "flex", alignItems: "flex-end", gap: horizon <= 14 ? 6 : 2 }}>
        {forecast.points.map((p, i) => {
          const expH = (p.expected / maxY) * 200;
          const lowH = (p.low / maxY) * 200;
          const highH = (p.high / maxY) * 200;
          return <div key={i} style={{ flex: 1, position: "relative", height: 200, display: "flex", flexDirection: "column", justifyContent: "flex-end" }} title={`${p.dayLabel} · atteso ${fmt.eur(p.expected)} · range ${fmt.eur(p.low)}-${fmt.eur(p.high)}`}>
            {/* Banda confidenza */}
            <div style={{ position: "absolute", bottom: lowH, height: highH - lowH, left: "15%", right: "15%", background: C.tealWash, borderRadius: 1 }}></div>
            {/* Barra attesa */}
            <div style={{ height: expH, background: C.teal, position: "relative", zIndex: 1 }}></div>
            {/* Label data */}
            {horizon <= 14 && <div className="f-mono" style={{ position: "absolute", bottom: -22, left: 0, right: 0, fontSize: 8, color: C.muted, textAlign: "center", transform: "rotate(-30deg)", transformOrigin: "center" }}>{p.dayLabel}</div>}
          </div>;
        })}
      </div>
      <div style={{ display: "flex", gap: 14, marginTop: horizon <= 14 ? 28 : 12, fontSize: 10, color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 14, height: 8, background: C.teal }}></div> Incasso atteso</div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}><div style={{ width: 14, height: 8, background: C.tealWash }}></div> Banda confidenza 95% (cresce con √h)</div>
      </div>
    </div>

    {/* Tabella dettaglio giorni */}
    <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 0.7fr 0.9fr 0.9fr 0.9fr 0.6fr", padding: "10px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.2em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
        <span>GIORNO</span><span style={{ textAlign: "right" }}>COPERTI</span><span style={{ textAlign: "right" }}>PESSIM.</span><span style={{ textAlign: "right" }}>ATTESO</span><span style={{ textAlign: "right" }}>OTTIM.</span><span style={{ textAlign: "center" }}>CONF.</span>
      </div>
      <div style={{ maxHeight: 320, overflowY: "auto" }}>
        {forecast.points.map((p, i) => {
          const isWeekend = p.dayOfWeek === 0 || p.dayOfWeek === 6;
          return <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 0.7fr 0.9fr 0.9fr 0.9fr 0.6fr", padding: "8px 14px", borderTop: `1px solid ${C.ruleSoft}`, fontSize: 12, alignItems: "center", background: isWeekend ? C.amberWash + "40" : "transparent" }}>
            <span className="f-body" style={{ color: C.ink, fontWeight: 500 }}>{p.dayLabel}</span>
            <span className="f-mono" style={{ color: C.inkSoft, textAlign: "right" }}>{p.covers}</span>
            <span className="f-mono" style={{ color: C.muted, textAlign: "right" }}>{fmt.eur(p.low)}</span>
            <span className="f-mono" style={{ color: C.ink, fontWeight: 600, textAlign: "right" }}>{fmt.eur(p.expected)}</span>
            <span className="f-mono" style={{ color: C.olive, textAlign: "right" }}>{fmt.eur(p.high)}</span>
            <div style={{ textAlign: "center" }}><ConfidenceBadge level={p.confidence} /></div>
          </div>;
        })}
      </div>
    </div>

    {/* Spiega come funziona */}
    <div style={{ padding: 14, background: C.paperDeep, borderRadius: 2, marginTop: 16, fontSize: 12, color: C.inkSoft, lineHeight: 1.6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Info size={14} color={C.teal} />
        <strong className="f-display" style={{ color: C.ink }}>Cosa guida la proiezione</strong>
      </div>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        <li>Il modello usa <strong>{forecast.dataPoints} giorni</strong> di dati storici con affidabilità <strong>{forecast.confidence}</strong></li>
        <li>Baseline calcolata per ogni giorno della settimana con media trimmed (esclude outlier ±10%)</li>
        {/* FIX A.3 (audit 2026-05-20): trend = regressione weekday-adjusted, non confronto naïve sum(7)/sum(7) */}
        <li>{forecast.trendReliable ? `Trend settimanale ${forecast.trend.pct >= 0 ? "+" : ""}${(forecast.trend.pct * 100).toFixed(1)}% (al netto della stagionalità weekday, OLS su ${forecast.trend?.nUsed || forecast.dataPoints} giorni) applicato in modo compound (geometrico): a 30gg pesa come (1+trend)^(30/7), non linearmente` : "Trend non ancora affidabile (servono almeno 28gg per separare trend reale dalla stagionalità weekday) — il forecast assume zero trend"}</li>
        <li>{forecast.seasonalSource === "learned"
          ? `Stagionalità mensile appresa dal tuo storico (≥${forecast.seasonalMinCycles} anni per i mesi coinvolti): corregge alta/bassa stagione`
          : forecast.seasonalSource === "manual"
          ? "Stagionalità mensile dal tuo profilo manuale (stima): applicata ai mesi, con banda CI allargata del 25% perché è un'assunzione, non un dato appreso"
          : "Stagionalità mensile non applicata (profilo neutro): impostala in Impostazioni se l'estate è molto più piena dell'inverno"}</li>
        <li>Banda CI 95% (z=1.96) che cresce con √h: la incertezza al gg+14 è più larga del gg+1, come dev'essere</li>
        <li>Hard stop a 2× lo storico: non estrapoliamo mai oltre — è disonestà statistica</li>
        <li>Banda totale calcolata in quadratura (assume indipendenza giornaliera) — più onesta della somma low/high</li>
        {forecast.cappedRate > 0.30 && <li style={{ color: C.amberDeep }}>⚠ Banda inferiore cappata a 0 sul <strong>{(forecast.cappedRate * 100).toFixed(0)}%</strong> dei giorni: la banda è "naïve normal-symmetric". Upgrade in roadmap: quantile log-normal asimmetrico.</li>}
      </ul>
    </div>
  </div>;
}

// ── Fabbisogno materia prima proiettato ────────────────────────────────────
function AnalyticsIngredients({ state, setState, ingForecast, horizon, setHorizon }) {
  const toast = useToast();

  // Raggruppa per fornitore preferito per suggerire ordini (DEVE stare prima di qualunque return early)
  const bySupplier = useMemo(() => {
    const g = {};
    (ingForecast.items || []).forEach(x => {
      if (x.toOrder <= 0) return;
      const sid = x.bp.preferredSupplier || "_nessuno";
      if (!g[sid]) g[sid] = { supplier: (state.suppliers || []).find(s => s.id === sid), items: [], total: 0 };
      g[sid].items.push(x);
      g[sid].total += (x.toOrder * (x.bp.pFatt || 0));
    });
    return Object.values(g).sort((a, b) => b.total - a.total);
  }, [ingForecast, state.suppliers]);

  if (ingForecast.confidence === "insufficienti") return <div style={{ padding: 20, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2 }}>
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
      <AlertTriangle size={18} color={C.amberDeep} />
      <div>
        <div className="f-display" style={{ fontSize: 15, color: C.ink, fontWeight: 600 }}>Dati insufficienti per fabbisogno materia prima</div>
        <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, marginTop: 6, lineHeight: 1.6 }}>Servono almeno 7 giorni di scontrini con piatti mappati a ricette per calcolare il consumo previsto.</div>
      </div>
    </div>
  </div>;

  const totalCost = ingForecast.items.reduce((s, x) => s + x.cost, 0);
  const totalToOrder = ingForecast.items.reduce((s, x) => s + x.toOrder, 0);
  const shortageItems = ingForecast.items.filter(x => x.shortage);

  const createOrderFromSuggestion = (group) => {
    if (!group.supplier) { toast({ type: "error", message: "Nessun fornitore associato" }); return; }
    const newPO = {
      id: uid("po_"),
      supplierId: group.supplier.id,
      createdAt: nowIso(),
      expectedAt: new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10),
      status: "bozza",
      notes: `Suggerito da proiezione ${horizon} giorni`,
      lines: group.items.map(x => ({
        itemId: x.bp.id,
        qty: Math.ceil(x.toOrder * 2) / 2,  // arrotonda a 0.5 kg
        unit: x.bp.unit || "kg",
        priceEst: x.bp.pFatt || 0,
      })),
    };
    setState({ ...state, purchaseOrders: [...(state.purchaseOrders || []), newPO] });
    toast({ type: "success", title: "Ordine bozza creato", message: `${group.supplier.name} · ${newPO.lines.length} articoli · ${fmt.eur(group.total)}` });
  };

  return <div>
    <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
      {[7, 14, 30].map(n => <button key={n} onClick={() => setHorizon(n)} className="tx"
        style={{ padding: "8px 14px", background: horizon === n ? C.ink : C.white, color: horizon === n ? C.paper : C.ink, border: `1px solid ${horizon === n ? C.ink : C.rule}`, borderRadius: 2, cursor: "pointer", fontFamily: "inherit", fontWeight: 500, fontSize: 12 }}>
        {n} giorni
      </button>)}
      <span className="f-body" style={{ fontSize: 11, color: C.muted, marginLeft: 10, display: "flex", alignItems: "center" }}>basato su {ingForecast.daysData} giorni di storico · affidabilità <ConfidenceBadge level={ingForecast.confidence} /></span>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 18 }}>
      <StatCard kicker={`Costo materia prima ${horizon}gg`} value={fmt.eurShort(totalCost)} unit="stimato" trend={`${ingForecast.items.length} prodotti`} accent={C.terra} icon={Package} />
      <StatCard kicker="Da ordinare" value={totalToOrder.toFixed(1)} unit="kg stimati" trend="oltre la giacenza corrente" accent={C.amber} icon={ShoppingCart} />
      <StatCard kicker="Prodotti in shortage" value={shortageItems.length} unit="sotto fabbisogno" trend="urgenti" accent={C.terra} icon={AlertTriangle} />
    </div>

    {/* Ordini suggeriti per fornitore */}
    {bySupplier.length > 0 && <div style={{ marginBottom: 18 }}>
      <div className="f-display" style={{ fontSize: 16, fontWeight: 500, color: C.ink, marginBottom: 10 }}>Ordini suggeriti per fornitore</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
        {bySupplier.filter(g => g.supplier).map((g, i) => <div key={i} style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 14 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
            <div>
              <div className="f-display" style={{ fontSize: 14, color: C.ink, fontWeight: 500 }}>{g.supplier.name}</div>
              <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{g.items.length} articoli · {fmt.eur(g.total)}</div>
            </div>
            <Btn variant="accent" size="sm" icon={Plus} onClick={() => createOrderFromSuggestion(g)}>Crea bozza</Btn>
          </div>
          <div style={{ maxHeight: 160, overflowY: "auto" }}>
            {g.items.slice(0, 6).map(x => <div key={x.bp.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "3px 0", color: C.inkSoft }}>
              <span>{x.bp.display || x.bp.nome}</span>
              <span className="f-mono">{x.toOrder.toFixed(1)} {x.bp.unit || "kg"}</span>
            </div>)}
            {g.items.length > 6 && <div style={{ fontSize: 10, color: C.muted, marginTop: 4, fontStyle: "italic" }}>+ altri {g.items.length - 6}</div>}
          </div>
        </div>)}
        {bySupplier.find(g => !g.supplier) && <div style={{ background: C.terraWash, border: `1px solid ${C.terra}`, borderRadius: 2, padding: 14 }}>
          <div className="f-display" style={{ fontSize: 14, color: C.terra, fontWeight: 500 }}>⚠ Prodotti senza fornitore</div>
          <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, marginTop: 4 }}>{bySupplier.find(g => !g.supplier).items.length} prodotti non hanno un fornitore preferito. Impostalo in Costruzione food cost.</div>
        </div>}
      </div>
    </div>}

    {/* Tabella dettaglio completa */}
    <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 0.9fr 0.9fr 0.9fr 0.7fr", padding: "10px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.2em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
        <span>PRODOTTO</span><span style={{ textAlign: "right" }}>FABBISOGNO</span><span style={{ textAlign: "right" }}>GIACENZA</span><span style={{ textAlign: "right" }}>DA ORDINARE</span><span style={{ textAlign: "right" }}>COSTO</span>
      </div>
      <div style={{ maxHeight: 420, overflowY: "auto" }}>
        {ingForecast.items.map(x => <div key={x.bp.id} style={{ display: "grid", gridTemplateColumns: "2fr 0.9fr 0.9fr 0.9fr 0.7fr", padding: "8px 14px", borderTop: `1px solid ${C.ruleSoft}`, alignItems: "center", fontSize: 12, background: x.shortage ? C.terraWash + "60" : "transparent" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {x.shortage && <AlertTriangle size={12} color={C.terra} />}
            <span className="f-body" style={{ color: C.ink, fontWeight: 500 }}>{x.bp.display || x.bp.nome}</span>
          </div>
          <span className="f-mono" style={{ color: C.ink, fontWeight: 600, textAlign: "right" }}>{x.qtyNeeded.toFixed(2)} {x.bp.unit || "kg"}</span>
          <span className="f-mono" style={{ color: x.shortage ? C.terra : C.inkSoft, textAlign: "right" }}>{x.stockNow.toFixed(2)}</span>
          <span className="f-mono" style={{ color: x.toOrder > 0 ? C.amberDeep : C.muted, fontWeight: 600, textAlign: "right" }}>{x.toOrder > 0 ? x.toOrder.toFixed(2) : "—"}</span>
          <span className="f-mono" style={{ color: C.inkSoft, textAlign: "right" }}>{fmt.eur(x.cost)}</span>
        </div>)}
      </div>
    </div>
  </div>;
}

// ── Import storico Excel/CSV ───────────────────────────────────────────────
// Riusa la stessa logica del CSVImporter ma con parser flessibile anche per Excel CSV e date italiane
// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                  IMPORT LISTINO ARTICOLI DAL GESTIONALE                    ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// Importa il listino .xls/.xlsx esportato dal gestionale di cassa.
// Riconosce automaticamente le colonne: Codice, Descrizione, Reparto, Prezzo1 (cena), Prezzo2 (pranzo).
// Usa fuzzy matching con i piatti esistenti per:
//   - aggiornare prezzi (cena + pranzo) sui piatti che hai già con ricetta
//   - creare nuovi articoli per quelli mancanti
//   - mantenere intatte le ricette e le grammature
function ListinoImporter({ state, setState, onClose }) {
  const [step, setStep] = useState("upload");      // upload | analysis | confirm
  const [parsed, setParsed] = useState(null);       // { articles, departments }
  const [matches, setMatches] = useState([]);       // [{action, listArt, dishMatch?, score, ...}]
  const [updatePrices, setUpdatePrices] = useState(true);
  const [importNew, setImportNew] = useState(true);
  const [includeMenuSpeciali, setIncludeMenuSpeciali] = useState(true);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef();
  const toast = useToast();

  // Mappa reparti: usa la single source of truth top-level REPARTO_TO_CAT.
  // Vedi commenti riga ~152 — estesa per coprire varianti TCPOS (VINI ROSSI/BIANCHI,
  // BIBITE, AMARI, BIRRE, DISTILLATI, ecc.).
  const repartoToCat = REPARTO_TO_CAT;
  const SKIP_REP = new Set(["COPERTI", "MSG", "VENDITA", "MATERIALE", "BANCO LINDA", "VARIANTE", "SCONTO"]);

  const handleFile = async (file) => {
    setProgress("Caricamento file...");
    try {
      // Carico SheetJS (CDN con fallback bundlato per l'offline)
      const XLSX = await loadXLSX();

      setProgress("Lettura foglio Excel...");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: false, raw: true });

      // Cerco il foglio "Articoli" (preferito) o il primo
      let sheetName = wb.SheetNames.find(n => /articoli|articol/i.test(n)) || wb.SheetNames[0];
      const ws = wb.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });

      if (rows.length < 2) { throw new Error("Foglio vuoto o senza dati"); }

      // Identifico le colonne dall'intestazione
      const headers = (rows[0] || []).map(h => normStr(h));
      const colIdx = {
        cod: headers.findIndex(h => /codice|cod/i.test(h)),
        name: headers.findIndex(h => /descrizione breve|descrizione|nome/i.test(h)),
        rep: headers.findIndex(h => /reparto/i.test(h)),
        p1: headers.findIndex(h => /prezzo1|prezzo 1|cena/i.test(h)),
        p2: headers.findIndex(h => /prezzo2|prezzo 2|pranzo/i.test(h)),
      };
      if (colIdx.name < 0) throw new Error("Non riesco a trovare la colonna Descrizione");
      if (colIdx.p1 < 0) throw new Error("Non riesco a trovare la colonna Prezzo1");

      // Provo a leggere mappa reparti dal foglio Reparti se c'è
      const repartiMap = {};
      const sheetReparti = wb.SheetNames.find(n => /reparti/i.test(n));
      if (sheetReparti) {
        const wsR = wb.Sheets[sheetReparti];
        const rRows = XLSX.utils.sheet_to_json(wsR, { header: 1, defval: null });
        for (let i = 1; i < rRows.length; i++) {
          const row = rRows[i];
          if (row && row[0] != null && row[1]) {
            repartiMap[String(row[0])] = String(row[1]).trim();
          }
        }
      }

      const parsePrice = parseItNumber;

      const articles = [];
      // Tracciamento reparti per diagnostica: l'utente DEVE poter vedere
      // perché articoli "spariscono" durante l'import (causa #1 dei "Vini 0").
      const unrecognizedReparti = new Map();  // repName → [{cod, name}]
      const skippedZeroPrice = [];            // articoli a prezzo 0 saltati
      const skippedSystem = new Map();        // reparti "tecnici" saltati (COPERTI, ecc.)

      for (let i = 1; i < rows.length; i++) {
        const r = rows[i];
        if (!r || r[colIdx.name] == null) continue;
        const cod = colIdx.cod >= 0 ? String(r[colIdx.cod] || "").trim() : "";
        const name = String(r[colIdx.name] || "").trim();
        if (!name) continue;
        const repCode = colIdx.rep >= 0 ? String(r[colIdx.rep] || "").trim() : "";
        const repName = repartiMap[repCode] || repCode;
        const repUpper = repName.toUpperCase();
        if (SKIP_REP.has(repUpper)) {
          skippedSystem.set(repUpper, (skippedSystem.get(repUpper) || 0) + 1);
          continue;
        }
        const p1 = parsePrice(r[colIdx.p1]);
        const p2 = colIdx.p2 >= 0 ? parsePrice(r[colIdx.p2]) : 0;
        let cat = repartoToCat[repUpper];
        if (!cat) {
          // Reparto sconosciuto: PRIMA traccialo per UI, POI fallback a "menu_speciali".
          // Così l'articolo viene importato (visibile in lista) e Andi può
          // decidere se ricategorizzarlo o ignorarlo.
          if (!unrecognizedReparti.has(repUpper)) unrecognizedReparti.set(repUpper, []);
          unrecognizedReparti.get(repUpper).push({ cod, name });
          cat = "menu_speciali";
        }
        if (p1 === 0 && p2 === 0) {
          // Prezzo 0: scarto (spesso sono articoli "informativi" o non in vendita)
          skippedZeroPrice.push({ cod, name, rep: repName });
          continue;
        }
        articles.push({
          cod, name, rep: repName, cat,
          priceCena: p1,
          priceP: p2 > 0 ? p2 : p1,
          hasDualPrice: p2 > 0 && p2 !== p1,
          _unknownReparto: !REPARTO_TO_CAT[repUpper],
        });
      }

      setParsed({
        articles,
        departments: repartiMap,
        sheetName,
        unrecognizedReparti: Array.from(unrecognizedReparti.entries())
          .map(([rep, items]) => ({ rep, count: items.length, samples: items.slice(0, 5) }))
          .sort((a, b) => b.count - a.count),
        skippedZeroPrice,
        skippedSystem: Array.from(skippedSystem.entries()).map(([rep, count]) => ({ rep, count })),
      });
      // Calcolo matching subito
      runMatching(articles);
      setProgress(null);
    } catch (e) {
      console.error(e);
      toast({ type: "error", title: "Errore lettura file", message: e.message || String(e) });
      setProgress(null);
    }
  };

  const runMatching = (articles) => {
    const dishes = state.dishesReal || [];
    const aliases = state.dishAliases || {};
    const out = articles.map(art => {
      // Cerco match con i piatti esistenti
      let best = null;
      // Prima alias diretto
      const aliasKey = normStr(art.name);
      if (aliases[aliasKey]) {
        const d = dishes.find(x => x.id === aliases[aliasKey]);
        if (d) best = { dish: d, score: 1, source: "alias" };
      }
      if (!best) {
        // Match esatto
        const exact = dishes.find(d => normStr(d.nome) === aliasKey);
        if (exact) best = { dish: exact, score: 1, source: "exact" };
      }
      if (!best) {
        // Fuzzy
        for (const d of dishes) {
          const score = 1 - levenshteinNorm(art.name, d.nome);
          if (!best || score > best.score) best = { dish: d, score, source: "fuzzy" };
        }
        if (best && best.score < 0.6) best = null;
      }
      // Action di default:
      // - match score >= 0.85 → update (auto)
      // - match score 0.6-0.85 → review (richiede conferma manuale, defaulto a skip update)
      // - no match → create
      let action = "create";
      if (best && best.score >= 0.85) action = "update";
      else if (best && best.score >= 0.6) action = "review";
      return { listArt: art, match: best, action };
    });
    setMatches(out);
    setStep("analysis");
  };

  const stats = useMemo(() => {
    const s = { total: matches.length, willUpdate: 0, willCreate: 0, willSkip: 0, review: 0 };
    matches.forEach(m => {
      if (!includeMenuSpeciali && m.listArt.cat === "menu_speciali") { s.willSkip++; return; }
      if (m.action === "update") s.willUpdate++;
      else if (m.action === "create") s.willCreate++;
      else if (m.action === "review") s.review++;
      else s.willSkip++;
    });
    return s;
  }, [matches, includeMenuSpeciali]);

  const doImport = () => {
    const dishes = state.dishesReal || [];
    const aliases = { ...(state.dishAliases || {}) };
    let updatedDishes = [...dishes];
    const newDishes = [];

    matches.forEach(m => {
      // Filtri
      if (!includeMenuSpeciali && m.listArt.cat === "menu_speciali") return;
      if (m.action === "skip") return;
      const art = m.listArt;

      if (m.action === "update" || (m.action === "review" && m.match)) {
        // Aggiorna prezzo del piatto esistente, mantieni ricetta intatta
        if (!updatePrices) return;
        const idx = updatedDishes.findIndex(d => d.id === m.match.dish.id);
        if (idx >= 0) {
          const dish = updatedDishes[idx];
          updatedDishes[idx] = {
            ...dish,
            prezzo: art.priceCena,
            prezzoPranzo: art.priceP > 0 && art.priceP !== art.priceCena ? art.priceP : null,
            // Aggiorno categoria se la corrente è di fallback (antipasti / menu_speciali)
            // e il listino propone una categoria più specifica. Casi tipici:
            //  - Vino importato come "menu_speciali" perché reparto non era mappato
            //    al primo import → reimport con mappa estesa → diventa "vini" ✓
            //  - Piatto auto-classificato "antipasti" → listino propone "secondi" → si aggiorna
            // Se invece l'utente ha già scelto una cat specifica (es. ha cambiato a mano
            // da menu_speciali a primi), il reimport NON la sovrascrive — sua scelta.
            cat: (dish.cat === "antipasti" || dish.cat === "menu_speciali") && art.cat && art.cat !== dish.cat ? art.cat : dish.cat,
            _listinoSync: { cod: art.cod, name: art.name, syncAt: nowIso() },
          };
          // Salva alias per future importazioni
          aliases[normStr(art.name)] = dish.id;
        }
      } else if (m.action === "create" || (m.action === "review" && !m.match)) {
        if (!importNew) return;
        const nd = {
          id: uid("d_"),
          nome: art.name,
          cat: art.cat,
          prezzo: art.priceCena,
          prezzoPranzo: art.priceP > 0 && art.priceP !== art.priceCena ? art.priceP : null,
          ingredienti: [],  // ricetta vuota, articolo "rivendita"
          vatRate: null,
          _fromListino: true,
          _listinoSync: { cod: art.cod, name: art.name, syncAt: nowIso() },
        };
        newDishes.push(nd);
        aliases[normStr(art.name)] = nd.id;
      }
    });

    setState({
      ...state,
      dishesReal: [...updatedDishes, ...newDishes],
      dishAliases: aliases,
    });
    toast({
      type: "success", title: "Listino importato",
      message: `${stats.willUpdate} piatti aggiornati · ${newDishes.length} nuovi articoli · ${stats.willSkip} saltati`
    });
    onClose();
  };

  // Update manuale dell'azione di una riga
  const setRowAction = (i, action) => setMatches(matches.map((m, j) => j === i ? { ...m, action } : m));
  const setRowMatch = (i, dishId) => {
    const dish = (state.dishesReal || []).find(d => d.id === dishId);
    if (!dish) return;
    setMatches(matches.map((m, j) => j === i ? { ...m, match: { dish, score: 1, source: "manual" }, action: "update" } : m));
  };

  return <Modal onClose={onClose} maxWidth={1100} title="Importa listino articoli" subtitle="Da gestionale cassa (.xls/.xlsx)"
    footer={<div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
      <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
      {step === "analysis" && <>
        <Btn variant="accent" icon={CheckCircle2} onClick={doImport}>
          Conferma import ({stats.willUpdate + stats.willCreate})
        </Btn>
      </>}
    </div>}>
    <div style={{ padding: 22 }}>

      {step === "upload" && <div>
        <div style={{ padding: 14, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, marginBottom: 16, fontSize: 12, color: C.tealDeep, lineHeight: 1.6 }}>
          <strong>Cosa fa questo importatore</strong><br />
          Legge il listino articoli del tuo gestionale di cassa (esportato in .xls o .xlsx) e:
          <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
            <li>Aggiorna i <strong>prezzi cena/pranzo</strong> dei piatti che hai già</li>
            <li>Mantiene intatte le <strong>ricette e grammature</strong> del food cost</li>
            <li>Crea nuovi articoli (vini, caffetteria, bar) come "rivendita" senza ricetta</li>
            <li>Salva gli <strong>alias automatici</strong> per i futuri import scontrini istantanei</li>
            <li>Riconosce le <strong>8 categorie</strong> automaticamente dai reparti del gestionale</li>
          </ul>
          <strong>Funziona direttamente con il file .xls — non serve convertire prima in CSV.</strong>
        </div>

        <div onClick={() => fileRef.current?.click()} style={{ border: `2px dashed ${C.rule}`, padding: 32, borderRadius: 2, textAlign: "center", cursor: "pointer", background: C.paperDeep }}
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.teal; }}
          onDragLeave={e => e.currentTarget.style.borderColor = C.rule}
          onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.rule; if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); }}>
          <Upload size={36} color={C.muted} strokeWidth={1.3} style={{ margin: "0 auto 12px", display: "block" }} />
          <div className="f-display" style={{ fontSize: 16, color: C.ink, fontWeight: 500 }}>Trascina qui il file listino</div>
          <div className="f-body" style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>.xls / .xlsx — qualsiasi gestionale che esporta articoli con codice/descrizione/reparto/prezzo</div>
          <input ref={fileRef} type="file" accept=".xls,.xlsx" style={{ display: "none" }} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
        </div>

        {progress && <div style={{ marginTop: 14, padding: 10, background: C.amberWash, borderRadius: 2, fontSize: 12, color: C.amberDeep, textAlign: "center" }}>
          ⏳ {progress}
        </div>}
      </div>}

      {step === "analysis" && parsed && <div>
        {/* DIAGNOSTICA REPARTI: se ci sono reparti non riconosciuti (es. "VINI ROSSI"
            non in mappa) li elenco PRIMA di tutto, così Andi capisce subito perché
            "Vini=0" o "Bar=0". Cliccare risolve = aggiungerlo a REPARTO_TO_CAT. */}
        {parsed.unrecognizedReparti && parsed.unrecognizedReparti.length > 0 && (
          <div style={{ marginBottom: 14, padding: 14, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <AlertTriangle size={18} color={C.amberDeep} style={{ marginTop: 2, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div className="f-display" style={{ fontSize: 13, fontWeight: 600, color: C.ink, marginBottom: 4 }}>
                  {parsed.unrecognizedReparti.length} reparti non riconosciuti — articoli salvati come <em>menu_speciali</em>
                </div>
                <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, lineHeight: 1.5, marginBottom: 8 }}>
                  Questi nomi reparto del tuo file listino non sono nella mappa standard. Gli articoli vengono comunque importati (categoria fallback <strong>menu_speciali</strong>) ma per averli nella categoria corretta (Vini, Bar, Caffetteria, ecc.) servono due cose: o rinomini il reparto nel gestionale Zucchetti, oppure puoi cambiare la categoria di ogni piatto manualmente in Piatti & ricette dopo l'import.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 6 }}>
                  {parsed.unrecognizedReparti.map(u => (
                    <div key={u.rep} style={{ background: C.white, padding: "6px 10px", borderRadius: 2, fontSize: 11 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 6, marginBottom: 2 }}>
                        <span className="f-mono" style={{ color: C.terra, fontWeight: 600 }}>{u.rep}</span>
                        <span className="f-mono" style={{ color: C.muted }}>{u.count}× articoli</span>
                      </div>
                      <div style={{ fontSize: 10, color: C.muted, overflow: "hidden", textOverflow: "ellipsis" }}>
                        es. {u.samples.map(s => s.name).join(", ")}{u.count > u.samples.length ? "…" : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Anche reparti "tecnici" saltati (COPERTI, MSG, ecc.) — solo informativo */}
        {parsed.skippedSystem && parsed.skippedSystem.length > 0 && (
          <div style={{ marginBottom: 14, padding: 10, background: C.paperDeep, borderRadius: 2, fontSize: 11, color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
            <strong>Reparti tecnici saltati di proposito:</strong> {parsed.skippedSystem.map(s => `${s.rep} (${s.count})`).join(" · ")}
          </div>
        )}

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 14 }}>
          <div style={{ background: C.ink, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.mutedSoft, letterSpacing: "0.2em" }}>ARTICOLI LETTI</div>
            <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color: C.paper, marginTop: 3 }}>{stats.total}</div>
          </div>
          <div style={{ background: C.oliveWash, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.olive, letterSpacing: "0.2em" }}>AGGIORNAMENTI</div>
            <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color: C.oliveDeep, marginTop: 3 }}>{stats.willUpdate}</div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>match certi</div>
          </div>
          <div style={{ background: C.amberWash, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.amberDeep, letterSpacing: "0.2em" }}>DA REVISIONARE</div>
            <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color: C.amberDeep, marginTop: 3 }}>{stats.review}</div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>match incerti</div>
          </div>
          <div style={{ background: C.tealWash, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.teal, letterSpacing: "0.2em" }}>NUOVI</div>
            <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color: C.tealDeep, marginTop: 3 }}>{stats.willCreate}</div>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>da creare</div>
          </div>
          <div style={{ background: C.paperDeep, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>SALTATI</div>
            <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color: C.muted, marginTop: 3 }}>{stats.willSkip}</div>
          </div>
        </div>

        {/* Opzioni */}
        <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2, marginBottom: 14, display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={updatePrices} onChange={e => setUpdatePrices(e.target.checked)} />
            <span>Aggiorna prezzi sui piatti esistenti</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={importNew} onChange={e => setImportNew(e.target.checked)} />
            <span>Crea i nuovi articoli</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={includeMenuSpeciali} onChange={e => setIncludeMenuSpeciali(e.target.checked)} />
            <span>Includi menu speciali (APE, MENU, EVENTI)</span>
          </label>
        </div>

        {/* Tabella matching */}
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "auto", maxHeight: 480 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: C.paperDeep, position: "sticky", top: 0, zIndex: 1 }}>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Articolo listino</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Cat</th>
                <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>€ cena</th>
                <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>€ pranzo</th>
                <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Match con piatto</th>
                <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Score</th>
                <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Azione</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m, i) => {
                const skipped = !includeMenuSpeciali && m.listArt.cat === "menu_speciali";
                const rowBg = skipped ? C.paperDeep + "80" : m.action === "update" ? "transparent" : m.action === "create" ? C.tealWash + "30" : m.action === "review" ? C.amberWash + "40" : C.paperDeep + "60";
                const scoreColor = !m.match ? C.muted : m.match.score >= 0.95 ? C.olive : m.match.score >= 0.75 ? C.amber : C.terra;
                return <tr key={i} style={{ borderBottom: `1px solid ${C.ruleSoft}`, background: rowBg, opacity: skipped ? 0.5 : 1 }}>
                  <td style={{ padding: "5px 10px" }}>
                    <div className="f-body" style={{ color: C.ink, fontWeight: 500 }}>{m.listArt.name}</div>
                    <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>cod {m.listArt.cod} · {m.listArt.rep}</div>
                  </td>
                  <td style={{ padding: "5px 10px" }}>
                    <span className="f-mono" style={{ fontSize: 10, color: DISH_CATEGORIES[m.listArt.cat]?.color, fontWeight: 600 }}>{DISH_CATEGORIES[m.listArt.cat]?.label || m.listArt.cat}</span>
                  </td>
                  <td style={{ padding: "5px 10px", textAlign: "right" }} className="f-mono">{fmt.eur(m.listArt.priceCena)}</td>
                  <td style={{ padding: "5px 10px", textAlign: "right" }} className="f-mono">{m.listArt.hasDualPrice ? fmt.eur(m.listArt.priceP) : <span style={{ color: C.muted }}>=</span>}</td>
                  <td style={{ padding: "5px 10px" }}>
                    {m.match ? <div>
                      <div className="f-body" style={{ color: C.ink, fontWeight: 500 }}>{m.match.dish.nome}</div>
                      <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>
                        {fmt.eur(m.match.dish.prezzo)} → {fmt.eur(m.listArt.priceCena)}
                        {m.match.dish.ingredienti && m.match.dish.ingredienti.length > 0 && <span> · {m.match.dish.ingredienti.length} ingr.</span>}
                      </div>
                    </div> : <select value="" onChange={e => { if (e.target.value) setRowMatch(i, e.target.value); }} style={{ fontSize: 11, padding: "3px 6px" }}>
                      <option value="">-- collega manualmente --</option>
                      {(state.dishesReal || []).map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
                    </select>}
                  </td>
                  <td style={{ padding: "5px 10px", textAlign: "center" }}>
                    {m.match && <span className="f-mono" style={{ color: scoreColor, fontWeight: 600, fontSize: 10 }}>{(m.match.score * 100).toFixed(0)}% {m.match.source === "alias" ? "★" : m.match.source === "exact" ? "✓" : m.match.source === "manual" ? "⚙" : "~"}</span>}
                  </td>
                  <td style={{ padding: "5px 10px", textAlign: "center" }}>
                    <select value={m.action} onChange={e => setRowAction(i, e.target.value)} style={{ fontSize: 11, padding: "3px 6px", border: `1px solid ${C.ruleSoft}`, borderRadius: 2 }}>
                      <option value="update" disabled={!m.match}>Aggiorna prezzo</option>
                      <option value="create">Crea nuovo</option>
                      <option value="review" disabled={!m.match}>Revisione</option>
                      <option value="skip">Salta</option>
                    </select>
                  </td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 12, padding: 10, background: C.paperDeep, borderRadius: 2, fontSize: 11, color: C.inkSoft, lineHeight: 1.5 }}>
          <strong>Legenda:</strong> ★ alias salvato · ✓ match esatto · ~ fuzzy · ⚙ manuale. Le righe in <span style={{ color: C.amberDeep, fontWeight: 600 }}>arancione</span> hanno match incerto, controllale prima di confermare. Le ricette dei piatti aggiornati <strong>non vengono toccate</strong> — solo i prezzi.
        </div>
      </div>}

    </div>
  </Modal>;
}


function HistoricalImporter({ state, setState, onClose }) {
  const [step, setStep] = useState("upload");
  const [parsed, setParsed] = useState(null);
  const [hasHeader, setHasHeader] = useState(true);
  const [mapping, setMapping] = useState({
    date: null, time: null, tableNumber: null, covers: null,
    dishName: null, qty: null, price: null, category: null,
  });
  const [matches, setMatches] = useState([]);
  const [unmatchedAction, setUnmatchedAction] = useState("create");
  // FIX F-03 (audit 2026-05-19): stato per dedup file-hash (modale conferma re-import)
  const [pendingFileLoad, setPendingFileLoad] = useState(null);
  const fileRef = useRef();
  const toast = useToast();

  // Parser data italiana (dd/mm/yyyy) o ISO
  const parseItDate = (s) => {
    if (!s) return null;
    const t = String(s).trim();
    // ISO yyyy-mm-dd
    if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
    // Italiano dd/mm/yyyy o dd-mm-yyyy
    const m = t.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})/);
    if (m) {
      const d = m[1].padStart(2, "0");
      const mo = m[2].padStart(2, "0");
      let y = m[3];
      if (y.length === 2) y = "20" + y;
      return `${y}-${mo}-${d}`;
    }
    // Excel serial number (giorni da 1900-01-01)
    if (/^\d+$/.test(t) && parseInt(t) > 20000 && parseInt(t) < 80000) {
      const d = new Date(Date.UTC(1899, 11, 30) + parseInt(t) * 86400000);
      return d.toISOString().slice(0, 10);
    }
    return null;
  };

  // Hash SHA-256 su TUTTO il file per dedup (prima solo i primi 1MB).
  const computeFileHash = async (file) => {
    const chunk = await file.arrayBuffer();
    const hashBuf = await crypto.subtle.digest("SHA-256", chunk);
    return Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, "0")).join("");
  };

  // FIX F-03 + F-05: parsing effettivo con encoding detection (usato dopo conferma dedup)
  const doLoadFile = async (file) => {
    // FIX F-05 (audit 2026-05-19): encoding auto-detection UTF-8 → Windows-1252.
    // Bug originale: reader.readAsText(file, "UTF-8") corrompeva accenti da Excel italiano legacy.
    let text;
    try {
      const buf = await file.arrayBuffer();
      try {
        text = new TextDecoder("utf-8", { fatal: true }).decode(buf);
      } catch {
        text = new TextDecoder("windows-1252").decode(buf);
      }
      if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
    } catch {
      text = await file.text();
    }
    const p = parseCSV(text);
    setParsed(p);
    if (p.rows.length > 0) {
      const headers = p.rows[0].map(h => normStr(h));
      const guess = { ...mapping };
      headers.forEach((h, i) => {
        if (/data|date/i.test(h) && guess.date == null) guess.date = i;
        else if (/ora|time/i.test(h) && guess.time == null) guess.time = i;
        else if (/tavolo|table|tav/i.test(h) && guess.tableNumber == null) guess.tableNumber = i;
        else if (/coperti|covers|persone|pax/i.test(h) && guess.covers == null) guess.covers = i;
        else if (/descrizione|articolo|piatto|prodotto|item/i.test(h) && guess.dishName == null) guess.dishName = i;
        else if (/quantit|qty|qta|pezzi/i.test(h) && guess.qty == null) guess.qty = i;
        else if (/prezzo|totale|importo/i.test(h) && guess.price == null) guess.price = i;
        else if (/categoria|reparto|gruppo/i.test(h) && guess.category == null) guess.category = i;
      });
      setMapping(guess);
    }
    setStep("mapping");
  };

  // FIX F-03 + F-08: handleFile con dedup hash e limite 50MB
  const handleFile = async (file) => {
    // FIX F-08 (audit 2026-05-19): rifiuto esplicito file >50MB prima di allocare RAM.
    // Un file di questa dimensione farebbe crashare la scheda browser senza alcun avviso.
    if (file.size > 50 * 1024 * 1024) {
      toast({ type: "error", title: "File troppo grande", message: "Il file supera 50 MB. Esporta dal gestionale in chunk più piccoli (es. per mese)." });
      return;
    }
    const hash = await computeFileHash(file);
    const importHistory = state.importHistory || [];
    const existingEntry = importHistory.find(h => h.fileHash === hash);
    if (existingEntry) {
      setPendingFileLoad({ file, hash, existingEntry });
      return;
    }
    await doLoadFile(file);
  };

  const analyze = () => {
    if (mapping.dishName == null || mapping.date == null) {
      toast({ type: "error", message: "Mappa almeno 'Nome piatto' e 'Data'" });
      return;
    }
    const dataRows = hasHeader ? parsed.rows.slice(1) : parsed.rows;
    const dishes = state.dishesReal || [];
    const aliases = state.dishAliases || {};
    const out = [];
    dataRows.forEach((r, idx) => {
      const nomeCsv = (r[mapping.dishName] || "").trim();
      if (!nomeCsv) return;
      const date = parseItDate(r[mapping.date]);
      if (!date) return;
      const qty = mapping.qty != null ? parseItNumber(r[mapping.qty]) || 1 : 1;
      const price = mapping.price != null ? parseItNumber(r[mapping.price]) || 0 : 0;
      const match = matchDishFuzzy(nomeCsv, dishes, aliases);
      out.push({
        rowIndex: idx, nomeCsv, match, qty, price, date,
        time: mapping.time != null ? String(r[mapping.time] || "").trim() : "12:30",
        table: mapping.tableNumber != null ? String(r[mapping.tableNumber] || "").trim() : String(idx + 1),
        covers: mapping.covers != null ? parseInt(r[mapping.covers]) || 2 : 2,
        action: match ? "use" : (unmatchedAction === "create" ? "create" : "skip"),
      });
    });
    setMatches(out);
    setStep("preview");
  };

  const doImport = () => {
    // Raggruppa per (data, tavolo) → scontrino
    const byKey = {};
    const newDishes = [];
    const newAliases = { ...(state.dishAliases || {}) };
    matches.forEach(m => {
      if (m.action === "skip") return;
      if (m.action === "create" && !m.match) {
        const nd = { id: uid("d_"), nome: m.nomeCsv, cat: "bar", prezzo: m.price || 0, ingredienti: [], vatRate: null, _imported: true };
        newDishes.push(nd);
        newAliases[normStr(m.nomeCsv)] = nd.id;
        m.match = { dish: nd, score: 1, source: "created" };
      } else if (m.match && m.match.source !== "alias") {
        newAliases[normStr(m.nomeCsv)] = m.match.dish.id;
      }
      if (!m.match) return;
      const key = `${m.date}__${m.table}`;
      if (!byKey[key]) byKey[key] = { date: m.date, table: m.table, covers: m.covers, time: m.time, items: [] };
      if (m.covers > byKey[key].covers) byKey[key].covers = m.covers;
      byKey[key].items.push({
        dishId: m.match.dish.id,
        name: m.match.dish.nome,
        qty: m.qty,
        unitPrice: m.price || m.match.dish.prezzo,
        note: "",
      });
    });
    const newReceipts = Object.values(byKey).map(t => {
      const total = t.items.reduce((s, it) => s + it.qty * it.unitPrice, 0);
      return {
        id: uid("rcp_"),
        number: t.table,
        covers: t.covers || 2,
        openedAt: `${t.date}T${t.time || "12:00"}:00`,
        closedAt: `${t.date}T${t.time || "13:30"}:00`,
        items: t.items,
        total,
        notes: "Importato da storico Excel",
        operator: state.currentUser?.name || "import",
        _imported: true,
      };
    });
    // C3 — Avviso anti-duplicato a livello scontrino (lo storico non ha lo step
    // "conflitto": l'hash file blocca solo lo stesso identico file).
    {
      const importDates = new Set(newReceipts.map(r => (r.closedAt || r.openedAt || "").slice(0, 10)).filter(Boolean));
      const existingDates = new Set((state.tableReceipts || [])
        .filter(r => r._imported)
        .map(r => (r.closedAt || r.openedAt || "").slice(0, 10)));
      const overlap = [...importDates].filter(d => existingDates.has(d));
      if (overlap.length > 0) {
        const sample = overlap.slice(0, 5).join(", ");
        if (!confirm(`Attenzione: esistono già scontrini importati in ${overlap.length} giorno/i che stai per ricaricare (${sample}${overlap.length > 5 ? "…" : ""}).\n\nProcedendo i dati di quei giorni verrebbero RADDOPPIATI. Continuare comunque?`)) {
          return;
        }
      }
    }

    // FIX F-13 (audit 2026-05-19): importHistory + UNDO snapshot per HistoricalImporter
    const histTotalRev = newReceipts.reduce((s, r) => s + (r.total || 0), 0);
    const histImportRecord = {
      id: `imp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      fileHash: null,
      fileName: "storico_import",
      importer: "historical",
      ts: nowIso(),
      receiptsImported: newReceipts.length,
      receiptsSkipped: 0,
      articlesUnmatched: matches.filter(m => !m.match && m.action !== "create").length,
      totalRevenue: Math.round(histTotalRev * 100) / 100,
    };
    const histPrevHistory = state.importHistory || [];
    const histNewHistory = [...histPrevHistory.slice(-99), histImportRecord];
    // FIX delta (audit 2026-06-04): delta minimo, nessuna copia completa di tableReceipts.
    // Import storico non ha gestione conflitti/replace, quindi removedReceipts è sempre [].
    const histUndoSnapshot = {
      type: "import",
      ts: histImportRecord.ts,
      importId: histImportRecord.id,
      fileName: "storico_import",
      receiptsImported: newReceipts.length,
      delta: buildUndoDelta(newReceipts, []),
      prevImportHistory: histPrevHistory,
    };

    setState({
      ...state,
      tableReceipts: [...(state.tableReceipts || []), ...newReceipts],
      dishesReal: [...(state.dishesReal || []), ...newDishes],
      dishAliases: newAliases,
      importHistory: histNewHistory,
      _undoSnapshot: histUndoSnapshot,
    });
    toast({ type: "success", title: "Storico importato", message: `${newReceipts.length} scontrini · ${newDishes.length} piatti creati · totale ${fmt.eur(newReceipts.reduce((s, r) => s + r.total, 0))}` });
    onClose();
  };

  const stats = useMemo(() => {
    const dateSet = new Set();
    let matched = 0, unmatched = 0, total = 0;
    matches.forEach(m => {
      if (m.action === "skip") return;
      dateSet.add(m.date);
      if (m.match) matched++; else unmatched++;
      total += m.qty * m.price;
    });
    return { days: dateSet.size, matched, unmatched, total };
  }, [matches]);

  return <Modal onClose={onClose} maxWidth={1000} title="Importa storico scontrini" subtitle="Excel/CSV dal gestionale cassa"
    footer={<div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
      <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
      {step === "mapping" && <Btn variant="primary" icon={ArrowRight} onClick={analyze}>Analizza</Btn>}
      {step === "preview" && <>
        <Btn variant="ghost" onClick={() => setStep("mapping")}>Indietro</Btn>
        <Btn variant="accent" icon={CheckCircle2} onClick={doImport}>Importa {stats.matched + matches.filter(m => m.action === "create").length} righe</Btn>
      </>}
    </div>}>

    {/* FIX F-03: modal conferma re-import stesso file (dedup hash) */}
    {pendingFileLoad && <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: C.white, borderRadius: 4, padding: 28, maxWidth: 420, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <AlertTriangle size={20} color={C.amberDeep} />
          <div className="f-display" style={{ fontWeight: 600, fontSize: 15, color: C.ink }}>File gia caricato</div>
        </div>
        <div className="f-body" style={{ fontSize: 13, color: C.inkSoft, lineHeight: 1.5, marginBottom: 20 }}>
          Questo file sembra gia stato importato il <strong>{pendingFileLoad.existingEntry?.ts ? new Date(pendingFileLoad.existingEntry.ts).toLocaleString("it-IT") : "data sconosciuta"}</strong> ({pendingFileLoad.existingEntry?.receiptsImported ?? "?"} scontrini).
          <br /><br />
          Continuare comunque potrebbe raddoppiare gli scontrini gia presenti.
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setPendingFileLoad(null)}>Annulla</Btn>
          <Btn variant="primary" onClick={async () => { const f = pendingFileLoad.file; setPendingFileLoad(null); await doLoadFile(f); }}>Carica comunque</Btn>
        </div>
      </div>
    </div>}
    <div style={{ padding: 22 }}>
      {step === "upload" && <div>
        <div style={{ padding: 14, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, marginBottom: 16, fontSize: 12, color: C.tealDeep, lineHeight: 1.6 }}>
          <strong>Questo importatore è tarato per storico del gestionale cassa</strong><br />
          Legge Excel/CSV con date italiane (gg/mm/aaaa), numeri con virgola, delimitatori auto-detect. Se il tuo Excel ha formato strano, convertilo in CSV UTF-8 prima di caricarlo (da Excel: File → Salva con nome → CSV UTF-8).<br /><br />
          <strong>Dopo l'import le proiezioni statistiche si attivano immediatamente.</strong>
        </div>
        <div onClick={() => fileRef.current?.click()} style={{ border: `2px dashed ${C.rule}`, padding: 32, borderRadius: 2, textAlign: "center", cursor: "pointer", background: C.paperDeep }}
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.teal; }}
          onDragLeave={e => e.currentTarget.style.borderColor = C.rule}
          onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.rule; if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); }}>
          <Upload size={36} color={C.muted} strokeWidth={1.3} style={{ margin: "0 auto 12px", display: "block" }} />
          <div className="f-display" style={{ fontSize: 16, color: C.ink, fontWeight: 500 }}>Carica il file storico</div>
          <div className="f-body" style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>CSV / TSV · UTF-8 · max 10 MB</div>
          <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" style={{ display: "none" }} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
        </div>
      </div>}

      {step === "mapping" && parsed && <div>
        <div style={{ padding: 10, background: C.paperDeep, borderRadius: 2, marginBottom: 12, fontSize: 11, color: C.inkSoft }}>
          {parsed.rows.length} righe · delimitatore <code>{parsed.delimiter === "\t" ? "TAB" : parsed.delimiter}</code> · <label><input type="checkbox" checked={hasHeader} onChange={e => setHasHeader(e.target.checked)} /> Prima riga è intestazione</label>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          {[
            ["date", "Data (richiesto)", true],
            ["dishName", "Nome piatto (richiesto)", true],
            ["qty", "Quantità", false],
            ["price", "Prezzo", false],
            ["tableNumber", "Tavolo", false],
            ["covers", "Coperti", false],
            ["time", "Ora", false],
            ["category", "Categoria", false],
          ].map(([key, label, req]) => <Field key={key} label={label} required={req}>
            <Select value={mapping[key] ?? ""} onChange={e => setMapping({ ...mapping, [key]: e.target.value === "" ? null : parseInt(e.target.value) })}>
              <option value="">-- non mappata --</option>
              {(hasHeader ? parsed.rows[0] : parsed.rows[0].map((_, i) => `Col ${i + 1}`)).map((h, i) => <option key={i} value={i}>{h || `Col ${i + 1}`}</option>)}
            </Select>
          </Field>)}
        </div>
        <Field label="Piatti sconosciuti">
          <Select value={unmatchedAction} onChange={e => setUnmatchedAction(e.target.value)}>
            <option value="create">Crea placeholder (consigliato per storico)</option>
            <option value="skip">Salta</option>
          </Select>
        </Field>
      </div>}

      {step === "preview" && <div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 14 }}>
          <div style={{ background: C.tealWash, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.teal, letterSpacing: "0.2em" }}>GIORNI</div>
            <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.tealDeep }}>{stats.days}</div>
          </div>
          <div style={{ background: C.oliveWash, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.olive, letterSpacing: "0.2em" }}>MATCHATI</div>
            <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.oliveDeep }}>{stats.matched}</div>
          </div>
          <div style={{ background: C.amberWash, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.amberDeep, letterSpacing: "0.2em" }}>DA CREARE</div>
            <div className="f-display" style={{ fontSize: 22, fontWeight: 600, color: C.amberDeep }}>{stats.unmatched}</div>
          </div>
          <div style={{ background: C.ink, padding: 12, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 9, color: C.mutedSoft, letterSpacing: "0.2em" }}>INCASSO TOTALE</div>
            <div className="f-display" style={{ fontSize: 18, fontWeight: 600, color: C.paper }}>{fmt.eur(stats.total)}</div>
          </div>
        </div>
        <div style={{ maxHeight: 380, overflowY: "auto", border: `1px solid ${C.rule}`, borderRadius: 2 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead><tr style={{ background: C.paperDeep, position: "sticky", top: 0 }}>
              <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Data</th>
              <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Tavolo</th>
              <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Nome CSV</th>
              <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Piatto</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Qtà</th>
              <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>€</th>
              <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>Azione</th>
            </tr></thead>
            <tbody>
              {matches.slice(0, 200).map((m, i) => <tr key={i} style={{ borderBottom: `1px solid ${C.ruleSoft}` }}>
                <td style={{ padding: "4px 10px" }} className="f-mono">{m.date}</td>
                <td style={{ padding: "4px 10px" }} className="f-mono">{m.table}</td>
                <td style={{ padding: "4px 10px", color: C.inkSoft }}>{m.nomeCsv}</td>
                <td style={{ padding: "4px 10px", color: m.match ? C.ink : C.terra }}>{m.match ? m.match.dish.nome : "— nuovo —"}</td>
                <td style={{ padding: "4px 10px", textAlign: "right" }} className="f-mono">{m.qty}</td>
                <td style={{ padding: "4px 10px", textAlign: "right" }} className="f-mono">{fmt.eur(m.price)}</td>
                <td style={{ padding: "4px 10px", textAlign: "center" }}>
                  <select value={m.action} onChange={e => setMatches(matches.map((x, j) => j === i ? { ...x, action: e.target.value } : x))} style={{ fontSize: 10, padding: "2px 4px" }}>
                    <option value="use" disabled={!m.match}>Importa</option>
                    <option value="create" disabled={!!m.match}>Crea</option>
                    <option value="skip">Salta</option>
                  </select>
                </td>
              </tr>)}
            </tbody>
          </table>
          {matches.length > 200 && <div style={{ padding: 8, textAlign: "center", fontSize: 10, color: C.muted, fontStyle: "italic" }}>...mostrate prime 200 di {matches.length}. Premi "Importa" per procedere con tutte.</div>}
        </div>
      </div>}
    </div>
  </Modal>;
}


// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║           STORICO INCASSI — importer file "conta" e "prodotti venduti"    ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

// Converte un seriale Excel (intero) in stringa YYYY-MM-DD.
// L'asse temporale Excel parte dal 1900-01-00 fittizio (bug Excel del 1900): la
// formula corretta è base = 1899-12-30 UTC, poi +N giorni.
function excelSerialToIso(n) {
  const d = new Date(Date.UTC(1899, 11, 30) + Math.round(n) * 86400000);
  if (isNaN(d.getTime())) return null;
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

// Parser robusto numeri italiani per i file "conta" (prezzi con punto migliaia e virgola decimale).
// Mirror di parsePrice presente nel parser TCPOS — riutilizziamo la stessa logica.
function parseContaNumber(v) {
  if (v == null) return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  let s = String(v).trim().replace(/[€\s]/g, "");
  if (!s) return 0;
  const neg = /^\(.*\)$/.test(s);
  s = s.replace(/[()]/g, "");
  const lastDot = s.lastIndexOf(".");
  const lastComma = s.lastIndexOf(",");
  if (lastDot > -1 && lastComma > -1) {
    if (lastComma > lastDot) { s = s.replace(/\./g, "").replace(",", "."); }
    else { s = s.replace(/,/g, ""); }
  } else if (lastComma > -1) {
    s = s.replace(",", ".");
  }
  const n = parseFloat(s);
  if (!Number.isFinite(n)) return 0;
  return neg ? -n : n;
}

// Auto-detect layout per i fogli "conta": individua la colonna svcCol cercando
// la prima colonna dove ricorrono le stringhe "sera" o "giorno" (case-insensitive).
// Da svcCol deriva: incassoCol=svcCol+1, copertiCol=svcCol-2, dateCol=colonna col seriale.
// Restituisce null se il foglio non sembra un file "conta".
function detectContaLayout(rows) {
  // Cerca svcCol: prima colonna con ≥2 occorrenze di "sera" o "giorno"
  const maxCols = rows.reduce((m, r) => Math.max(m, r.length), 0);
  let svcCol = -1;
  for (let c = 0; c < maxCols; c++) {
    let count = 0;
    for (const row of rows) {
      const v = String(row[c] || "").trim().toLowerCase();
      if (v === "sera" || v === "giorno") count++;
    }
    if (count >= 2) { svcCol = c; break; }
  }
  if (svcCol < 0) return null;

  const incassoCol = svcCol + 1;
  const copertiCol = svcCol - 2;
  if (copertiCol < 0) return null;

  // Cerca dateCol: colonna che contiene almeno un seriale Excel nel range 40000-50000
  let dateCol = -1;
  for (let c = 0; c < maxCols; c++) {
    for (const row of rows) {
      const v = row[c];
      if (typeof v === "number" && v > 40000 && v < 50000) { dateCol = c; break; }
    }
    if (dateCol >= 0) break;
  }
  if (dateCol < 0) return null;

  return { svcCol, incassoCol, copertiCol, dateCol };
}

// Parser principale per file "conta" (incassi + coperti giornalieri).
// Restituisce { days: Map<YYYY-MM-DD, {pranzo,cena}>, controlCovers, controlRevenue }
//
// LOGICA DATE — problema strutturale del foglio "conta":
// Le coppie per ogni giorno sono disposte in ordine variabile: può essere (sera, giorno)
// oppure (giorno, sera). Solo la riga "giorno" porta il seriale Excel in dateCol; la riga
// "sera" ha il nome-giorno-settimana (testo). Non possiamo quindi assegnare la data
// in un singolo pass, perché quando incontriamo "sera" la data non è ancora nota.
//
// Soluzione: PRE-PASS in due fasi.
// Fase 1 — costruisce la mappa rowIndex → dateISO per tutte le righe "giorno" (seriale
//           diretto) e, per ogni riga "sera", cerca la riga "giorno" più vicina
//           (lookahead fino a 3 righe) che porta il seriale.
// Fase 2 — itera tutte le righe dati assegnando la data dal precomputed map.
function parseContaSheet(rows) {
  const layout = detectContaLayout(rows);
  if (!layout) return null;
  const { svcCol, incassoCol, copertiCol, dateCol } = layout;

  // Totali di controllo
  let controlCovers = null;
  let controlRevenue = null;

  // Pass 0 — raccoglie totali di controllo e prepara array indici righe dati
  const dataRowIndices = []; // [{ri, svcRaw, dateRaw}]
  for (let ri = 0; ri < rows.length; ri++) {
    const row = rows[ri];
    if (!row) continue;
    // Totali di controllo: cerca label "coperti"
    for (let c = 0; c < row.length; c++) {
      const cellStr = String(row[c] || "").trim().toLowerCase()
        .normalize("NFD").replace(/[̀-ͯ]/g, "");
      if (cellStr === "coperti") {
        for (let delta = 1; delta <= 3; delta++) {
          const candidate = row[c + delta];
          if (typeof candidate === "number" && candidate > 1000 && candidate < 200000) {
            controlCovers = candidate;
            break;
          }
        }
      }
    }
    // Incasso annuale: valore grande nell'intorno di incassoCol
    for (let delta = -2; delta <= 2; delta++) {
      const ci = incassoCol + delta;
      if (ci < 0 || ci >= row.length) continue;
      const v = row[ci];
      if (typeof v === "number" && v > 100000 && v < 10000000) {
        if (controlRevenue === null || v > controlRevenue) controlRevenue = v;
      }
    }
    const svcRaw = String(row[svcCol] || "").trim().toLowerCase();
    if (svcRaw === "giorno" || svcRaw === "sera") {
      dataRowIndices.push({ ri, svcRaw });
    }
  }

  // Fase 1 — PRE-PASS date: costruisce mappa ri → dateISO
  // Per ogni riga "giorno": data = seriale in dateCol.
  // Per ogni riga "sera": cerca lookahead (max 3 righe avanti nell'elenco dataRowIndices)
  //   la prima riga "giorno" — quella porta la data del giorno.
  // Se non trovata: fallback a lookbehind (la riga "giorno" precedente + 1 giorno).
  const rowDateMap = new Map(); // ri → dateISO

  for (let di = 0; di < dataRowIndices.length; di++) {
    const { ri, svcRaw } = dataRowIndices[di];
    const row = rows[ri];

    if (svcRaw === "giorno") {
      const dateRaw = row[dateCol];
      if (typeof dateRaw === "number" && dateRaw > 40000 && dateRaw < 50000) {
        const iso = excelSerialToIso(dateRaw);
        if (iso) rowDateMap.set(ri, iso);
      }
    }
  }

  // Seconda passata: accoppiamento deterministico sera → giorno.
  // Scansiona le righe dati a coppie consecutive:
  //   - se la coppia è (giorno[i], sera[i+1]) → sera prende la data di giorno[i]
  //   - se la coppia è (sera[i], giorno[i+1]) → sera prende la data di giorno[i+1]
  // Processa in passi di 2 per non fare cross-contamination tra giorni.
  for (let di = 0; di < dataRowIndices.length; di += 2) {
    const a = dataRowIndices[di];
    const b = di + 1 < dataRowIndices.length ? dataRowIndices[di + 1] : null;
    if (!b) {
      // Riga singola senza coppia: se è "sera" senza data, usa il "giorno" più vicino già risolto
      if (a.svcRaw === "sera" && !rowDateMap.has(a.ri)) {
        for (let bwd = di - 1; bwd >= 0; bwd--) {
          const prev = dataRowIndices[bwd];
          if (prev.svcRaw === "giorno" && rowDateMap.has(prev.ri)) {
            rowDateMap.set(a.ri, rowDateMap.get(prev.ri));
            break;
          }
        }
      }
      continue;
    }
    // Coppia (giorno, sera) o (sera, giorno)
    if (a.svcRaw === "giorno" && b.svcRaw === "sera") {
      // ordine giorno-poi-sera: sera prende la data di giorno
      if (rowDateMap.has(a.ri) && !rowDateMap.has(b.ri)) {
        rowDateMap.set(b.ri, rowDateMap.get(a.ri));
      }
    } else if (a.svcRaw === "sera" && b.svcRaw === "giorno") {
      // ordine sera-poi-giorno: sera prende la data di giorno
      if (rowDateMap.has(b.ri) && !rowDateMap.has(a.ri)) {
        rowDateMap.set(a.ri, rowDateMap.get(b.ri));
      }
    } else {
      // Coppia omogenea (giorno,giorno) o (sera,sera) — non dovrebbe succedere;
      // se accade, torna indietro di un passo e rielabora come coppie sfasate.
      // Estrategia: gestisci ogni elemento singolarmente come fallback.
      di -= 1; // step indietro: la prossima iterazione riprova da di+1
    }
  }
  // Fallback finale per eventuali "sera" ancora non risolte (riepilogo o righe isolate)
  for (let di = 0; di < dataRowIndices.length; di++) {
    const { ri, svcRaw } = dataRowIndices[di];
    if (svcRaw !== "sera" || rowDateMap.has(ri)) continue;
    // Cerca il "giorno" più vicino in qualsiasi direzione
    for (let dist = 1; dist <= 5; dist++) {
      const fwd = di + dist < dataRowIndices.length ? dataRowIndices[di + dist] : null;
      const bwd = di - dist >= 0 ? dataRowIndices[di - dist] : null;
      if (bwd && bwd.svcRaw === "giorno" && rowDateMap.has(bwd.ri)) { rowDateMap.set(ri, rowDateMap.get(bwd.ri)); break; }
      if (fwd && fwd.svcRaw === "giorno" && rowDateMap.has(fwd.ri)) { rowDateMap.set(ri, rowDateMap.get(fwd.ri)); break; }
    }
  }

  // Fase 2 — accumulo per giorno
  const days = new Map(); // date ISO → {pranzoRevenue, pranzoCovers, cenaRevenue, cenaCovers}

  for (const { ri, svcRaw } of dataRowIndices) {
    const dateISO = rowDateMap.get(ri);
    if (!dateISO) continue;

    const row = rows[ri];
    const incasso = parseContaNumber(row[incassoCol]);
    const copertiRaw = row[copertiCol];
    const coperti = (typeof copertiRaw === "number" && Number.isFinite(copertiRaw)) ? Math.round(copertiRaw) : 0;

    if (!days.has(dateISO)) {
      days.set(dateISO, { pranzoRevenue: 0, pranzoCovers: 0, cenaRevenue: 0, cenaCovers: 0 });
    }
    const day = days.get(dateISO);

    if (svcRaw === "giorno") {
      day.pranzoRevenue += incasso;
      day.pranzoCovers += coperti;
    } else {
      day.cenaRevenue += incasso;
      day.cenaCovers += coperti;
    }
  }

  // Nota "ridati cliente" (conta 24): righe con svcCol="ridati cliente" hanno
  // incasso negativo e vengono già skip dal filtro "giorno"/"sera" — non serve
  // gestirle: il totale di controllo del foglio è già netto di quei valori.

  return { days, controlCovers, controlRevenue };
}

// Auto-detect layout per i fogli "prodotti venduti": individua la riga di intestazione
// cercando una riga che contenga "reparto", "descrizione"/"nome"/"articolo", "quantit", "valore"/"importo".
function detectProdottiLayout(rows) {
  for (let ri = 0; ri < Math.min(rows.length, 10); ri++) {
    const row = rows[ri];
    const norm = row.map(c => String(c || "").trim().toLowerCase()
      .normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^\w]/g, " ").trim());
    const repCol = norm.findIndex(h => /^reparto/.test(h));
    const descCol = norm.findIndex(h => /descrizione|articolo|^nome/.test(h));
    const qtyCol = norm.findIndex(h => /quantit|^qty|^qta/.test(h));
    const valCol = norm.findIndex(h => /^valore|^importo|^totale/.test(h));
    if (repCol >= 0 && descCol >= 0 && qtyCol >= 0) {
      return { headerRow: ri, repCol, descCol, qtyCol, valCol: valCol >= 0 ? valCol : -1 };
    }
  }
  // Fallback prodotti 25: no header "Reparto" — layout fisso [reparto, qty, nome, valore]
  // Riconosce dalla riga 1 che ha: stringa breve (reparto), numero (qty), stringa lunga (nome), numero (valore)
  if (rows.length > 1) {
    const r = rows[1]; // skip riga 0 che è spesso il totale
    if (r && r.length >= 4) {
      const col0isStr = typeof r[0] === "string" && r[0].trim().length > 0 && r[0].trim().length < 40;
      const col1isNum = typeof r[1] === "number" || /^\d/.test(String(r[1] || ""));
      const col2isStr = typeof r[2] === "string" && r[2].trim().length > 1;
      if (col0isStr && col1isNum && col2isStr) {
        return { headerRow: 0, repCol: 0, descCol: 2, qtyCol: 1, valCol: 3, isFixedLayout: true };
      }
    }
  }
  return null;
}

// Funzione di riconoscimento tipo file: restituisce "conta" | "prodotti" | "unknown"
function detectStoriciFileType(rows) {
  const layout = detectContaLayout(rows);
  if (layout) return "conta";
  const prodLayout = detectProdottiLayout(rows);
  if (prodLayout) return "prodotti";
  return "unknown";
}

// Calcola hash SHA-256 su tutti i byte del file (stesso pattern di ZucchettiImporter).
// Async, restituisce stringa hex o null su errore.
async function computeStoriciFileHash(file) {
  try {
    const buf = await file.arrayBuffer();
    const hashBuf = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, "0")).join("");
  } catch { return null; }
}

// ═══════════════════════════════════════════════════════════════════════════
// ARCHIVIO CLIENTI STORICO PIENISSIMO — distiller + loader pigro (ADDITIVO).
// L'export Pienissimo (contacts.history-2.csv, ~102k righe) è DATO DI RIFERIMENTO
// storico enorme: NON entra mai in state (ucciderebbe i memo prenotazioni che
// iterano state.customers O(n) per riga). Vive in una chiave IndexedDB SEPARATA
// (VOYA_ARCHIVE_KEY), caricato pigramente solo quando la vista Clienti lo cerca.
// In state finiscono SOLO due artefatti piccoli: voyaArchiveMeta (~200B) e
// voyaSeasonality (~9KB). Porta la logica del distiller Python validato
// (scratchpad/distill.py). Riusa gli helper esistenti normStr / _blkPhoneKey.
// Nota dominio: i coperti Pienissimo sono DOMANDA LORDA (includono rifiutati /
// no-show), NON presenze reali → nella UI si etichettano "coperti prenotati".
// ═══════════════════════════════════════════════════════════════════════════
const VOYA_ARCHIVE_KEY = "voya_archive_v1";

// Parser CSV robusto per campi quotati (virgole dentro le virgolette, "" escape,
// newline dentro i campi, \r\n / \n). Nessuna dipendenza (no papaparse). La prima
// riga è l'header; ritorna un array di oggetti { header: valore }.
function _voyaParseCSV(text) {
  const s = String(text || "");
  const rows = [];
  let field = "";
  let row = [];
  let inQuotes = false;
  let started = false; // true appena la riga contiene almeno un carattere/campo
  const pushField = () => { row.push(field); field = ""; };
  const pushRow = () => { rows.push(row); row = []; };
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; }
        else { inQuotes = false; }
      } else { field += ch; }
      continue;
    }
    if (ch === '"') { inQuotes = true; started = true; continue; }
    if (ch === ",") { pushField(); started = true; continue; }
    if (ch === "\r") { continue; } // gestito insieme a \n
    if (ch === "\n") {
      pushField(); pushRow(); started = false; continue;
    }
    field += ch; started = true;
  }
  // Ultima riga (senza newline finale)
  if (started || field.length > 0 || row.length > 0) { pushField(); pushRow(); }
  if (rows.length === 0) return [];
  // Rimuove BOM eventuale dal primo header
  const header = rows[0].map((h, idx) => idx === 0 ? String(h).replace(/^﻿/, "") : h);
  const out = [];
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r];
    // Salta righe totalmente vuote
    if (cells.length === 1 && cells[0] === "") continue;
    const obj = {};
    for (let c = 0; c < header.length; c++) obj[header[c]] = cells[c] != null ? cells[c] : "";
    out.push(obj);
  }
  return out;
}

// Parse data "YYYY-MM-DD" → { y, m, d, ord } (ord = giorni assoluti per diff/confronto)
// oppure null. ord usa Date.UTC per evitare problemi di fuso.
function _voyaParseDate(str) {
  const t = String(str || "").trim();
  if (!t) return null;
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const y = +m[1], mo = +m[2], d = +m[3];
  if (!mo || !d) return null;
  return { y, m: mo, d, ord: Math.floor(Date.UTC(y, mo - 1, d) / 86400000) };
}

// Servizio dall'ora "HH:MM": pranzo se ora < 16, altrimenti cena. null se illeggibile.
function _voyaServiceOf(ora) {
  const t = String(ora || "").trim();
  const m = t.match(/^(\d{1,2})/);
  if (!m) return null;
  const h = +m[1];
  return h < 16 ? "pranzo" : "cena";
}

// Npax → intero clampato (0 se fuori (0,60]). Come distill.py.
function _voyaNpaxCell(v) {
  const n = Math.trunc(Number(v) || 0);
  return (n > 0 && n <= 60) ? n : 0;
}

// Porta distill.py: dedup, stagionalità, archivio clienti. Ritorna { archive, seasonality, meta }.
function _voyaDistillPienissimo(text) {
  const rows = _voyaParseCSV(text);
  // ── Dedup: la stessa prenotazione appare come "Prenotazioni" e "Prenotazioni Web".
  //    chiave = data + ora + nome + cognome + (phoneKey||telefono) + npax
  const seen = new Set();
  const uniq = [];
  for (const r of rows) {
    const phoneKey = _blkPhoneKey(r["Telefono"]) || (r["Telefono"] || "");
    const k = [r["Data prenotazione"] || "", r["Ora prenotazione"] || "", r["Nome"] || "",
               r["Cognome"] || "", phoneKey, r["Npax"] || ""].join("");
    if (seen.has(k)) continue;
    seen.add(k);
    uniq.push(r);
  }

  // ── Stagionalità: celle mese|dow|servizio → {cov,res}
  const cells = {};
  const monthCovers = {};
  for (let m = 1; m <= 12; m++) monthCovers[m] = 0;
  const partySize = {};
  const leadtimes = [];
  const svcCov = { pranzo: 0, cena: 0 };
  let coversTotal = 0;
  let rangeFirst = null, rangeLast = null; // stringhe "YYYY-MM-DD"

  for (const r of uniq) {
    const dp = _voyaParseDate(r["Data prenotazione"]);
    if (!dp) continue;
    const svc = _voyaServiceOf(r["Ora prenotazione"]);
    const npax = _voyaNpaxCell(r["Npax"]);
    const dow = (((new Date(Date.UTC(dp.y, dp.m - 1, dp.d)).getUTCDay()) + 6) % 7); // 0=lun..6=dom
    const key = `${dp.m}|${dow}|${svc || "na"}`;
    if (!cells[key]) cells[key] = { cov: 0, res: 0 };
    cells[key].cov += npax;
    cells[key].res += 1;
    monthCovers[dp.m] += npax;
    coversTotal += npax;
    if (npax) partySize[npax] = (partySize[npax] || 0) + 1;
    if (svc) svcCov[svc] += npax;
    // range date (confronto lessicografico su "YYYY-MM-DD" = confronto cronologico)
    const ds = String(r["Data prenotazione"]).slice(0, 10);
    if (ds) {
      if (rangeFirst === null || ds < rangeFirst) rangeFirst = ds;
      if (rangeLast === null || ds > rangeLast) rangeLast = ds;
    }
    const dc = _voyaParseDate(r["Data creazione"]);
    if (dc) leadtimes.push(dp.ord - dc.ord);
  }

  // Statistiche lead-time
  const leadSorted = leadtimes.filter(x => x >= 0).sort((a, b) => a - b);
  let leadMedian = null;
  if (leadSorted.length) {
    const mid = Math.floor(leadSorted.length / 2);
    leadMedian = leadSorted.length % 2 ? leadSorted[mid] : (leadSorted[mid - 1] + leadSorted[mid]) / 2;
  }
  const leadWithin24h = leadSorted.length
    ? leadSorted.filter(x => x <= 1).length / leadSorted.length : null;
  const cenaPranzoRatio = svcCov.pranzo ? svcCov.cena / svcCov.pranzo : null;

  const seasonality = {
    source: "pienissimo_export",
    reservationsUnique: uniq.length,
    coversTotal,
    monthCovers,
    cells,
    partySize,
    cenaPranzoRatio: cenaPranzoRatio != null ? Math.round(cenaPranzoRatio * 1000) / 1000 : null,
    leadtimeMedianDays: leadMedian,
    leadtimeWithin24hPct: leadWithin24h != null ? Math.round(leadWithin24h * 10000) / 10000 : null,
  };

  // ── Archivio clienti (unici per telefono, fallback nome normalizzato)
  const custMap = new Map();
  for (const r of uniq) {
    const pk = _blkPhoneKey(r["Telefono"]);
    const nk = normStr(`${r["Nome"] || ""} ${r["Cognome"] || ""}`);
    const key = pk || (nk ? "name:" + nk : null);
    if (!key) continue;
    const npax = Math.max(0, Math.trunc(Number(r["Npax"]) || 0));
    let rec = custMap.get(key);
    if (!rec) {
      rec = {
        key,
        nome: String(r["Nome"] || "").trim(),
        cognome: String(r["Cognome"] || "").trim(),
        phone: String(r["Telefono"] || "").trim(),
        email: String(r["Email"] || "").trim(),
        visite: 0, coperti: 0, first: null, last: null,
      };
      custMap.set(key, rec);
    }
    rec.visite += 1;
    rec.coperti += npax;
    if (!rec.email && r["Email"]) rec.email = String(r["Email"]).trim();
    const ds = String(r["Data prenotazione"] || "").slice(0, 10);
    if (ds) {
      if (rec.first === null || ds < rec.first) rec.first = ds;
      if (rec.last === null || ds > rec.last) rec.last = ds;
    }
  }
  const customers = Array.from(custMap.values());
  const regulars = customers.reduce((n, c) => n + (c.visite >= 2 ? 1 : 0), 0);
  const builtAt = new Date().toISOString();

  const archive = {
    version: 1,
    builtAt,
    count: customers.length,
    regulars,
    customers,
  };
  const meta = {
    count: customers.length,
    regulars,
    coversTotal,
    reservationsUnique: uniq.length,
    rangeFirst,
    rangeLast,
    builtAt,
  };
  return { archive, seasonality, meta };
}

// ── Loader pigro a livello modulo (NON in React state) ──────────────────────
// Carica l'archivio da IndexedDB una sola volta, memoizza, e precomputa i campi
// di ricerca (_searchName, _phoneKey) su ogni cliente. Ritorna { customers, loadedAt }
// o null se l'archivio non è ancora stato importato.
let _voyaArchiveCache = null;
let _voyaArchivePromise = null;
async function voyaArchiveLoad() {
  if (_voyaArchiveCache) return _voyaArchiveCache;
  if (_voyaArchivePromise) return _voyaArchivePromise;
  _voyaArchivePromise = (async () => {
    let archive = null;
    try { archive = await idbGet(VOYA_ARCHIVE_KEY); } catch { archive = null; }
    if (!archive || !Array.isArray(archive.customers)) { _voyaArchivePromise = null; return null; }
    // Precompute per la ricerca (una volta sola, non a ogni keystroke)
    for (const c of archive.customers) {
      c._searchName = normStr(`${c.nome || ""} ${c.cognome || ""}`);
      c._phoneKey = _blkPhoneKey(c.phone) || "";
    }
    _voyaArchiveCache = { customers: archive.customers, loadedAt: Date.now() };
    _voyaArchivePromise = null;
    return _voyaArchiveCache;
  })();
  return _voyaArchivePromise;
}
// Da chiamare dopo un (re)import per forzare il ricaricamento pigro.
function _voyaArchiveInvalidate() { _voyaArchiveCache = null; _voyaArchivePromise = null; }

// Ricerca sull'archivio precomputato. Query con ≥2 cifre consecutive → match su
// telefono; altrimenti substring sul nome normalizzato. Ordina per visite desc,
// ritorna i primi ~50.
function voyaArchiveSearch(customers, query) {
  const list = Array.isArray(customers) ? customers : [];
  const raw = String(query || "").trim();
  if (!raw) return [];
  const digits = raw.replace(/\D/g, "");
  let out;
  if (digits.length >= 2) {
    out = list.filter(c => (c._phoneKey || "").includes(digits));
  } else {
    const nq = normStr(raw);
    if (!nq) return [];
    out = list.filter(c => (c._searchName || "").includes(nq));
  }
  out.sort((a, b) => (b.visite || 0) - (a.visite || 0));
  return out.slice(0, 50);
}

// Componente importer storico incassi + prodotti venduti.
// Aperto da VenditeStatistiche tramite stato locale storiciImporterOpen.
function StoriciIncassiImporter({ state, setState, onClose }) {
  const toast = useToast();
  const fileRef = useRef(null);

  // step: "upload" | "preview_conta" | "preview_prodotti" | "done"
  const [step, setStep] = useState("upload");
  const [loading, setLoading] = useState(false);
  const [parsed, setParsed] = useState(null);
  // parsed per "conta": { year, days: Map, controlCovers, controlRevenue, parsedCovers, parsedRevenue }
  // parsed per "prodotti": { year, items:[{reparto,nome,qty,valore}] }

  const handleFile = async (file) => {
    if (file.size > 50 * 1024 * 1024) {
      toast({ type: "error", title: "File troppo grande", message: "Massimo 50 MB. Esporta per anno." });
      return;
    }
    setLoading(true);
    try {
      // Carica SheetJS (CDN con fallback bundlato per l'offline).
      // La CSP (public/_headers script-src) whitelista cdnjs.cloudflare.com.
      const XLSX = await loadXLSX();
      if (!XLSX) throw new Error("Libreria XLSX non caricata.");

      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: false, cellNF: false, raw: true });

      // Sceglie il foglio: preferisce "Foglio1", altrimenti il primo
      const sheetName = wb.SheetNames.includes("Foglio1") ? "Foglio1" : wb.SheetNames[0];
      const ws = wb.Sheets[sheetName];
      // Converte in array di array con valori grezzi
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: null });

      const fileType = detectStoriciFileType(rows);
      if (fileType === "unknown") {
        toast({ type: "error", title: "Formato non riconosciuto", message: "Il file non sembra un file 'conta' (incassi) né 'prodotti venduti'. Verifica che il foglio si chiami 'Foglio1' e che contenga le colonne giuste." });
        setLoading(false);
        return;
      }

      // Tenta di inferire l'anno dal nome file (es. "conta 25.xlsx" → 2025)
      const yearMatch = file.name.match(/(\d{2,4})/);
      let year = yearMatch ? parseInt(yearMatch[1]) : new Date().getFullYear();
      if (year < 100) year = year >= 50 ? 1900 + year : 2000 + year;

      if (fileType === "conta") {
        const result = parseContaSheet(rows);
        if (!result || result.days.size === 0) {
          toast({ type: "error", title: "Nessun dato trovato", message: "Il parser non ha trovato righe di servizio (sera/giorno) nel file. Verifica il formato." });
          setLoading(false);
          return;
        }
        // Calcola totali dal parser
        let parsedRevenue = 0, parsedCovers = 0;
        for (const day of result.days.values()) {
          parsedRevenue += day.pranzoRevenue + day.cenaRevenue;
          parsedCovers += day.pranzoCovers + day.cenaCovers;
        }
        setParsed({ type: "conta", year, days: result.days, controlCovers: result.controlCovers, controlRevenue: result.controlRevenue, parsedRevenue, parsedCovers, fileName: file.name });
        setStep("preview_conta");
      } else {
        // "prodotti"
        const layout = detectProdottiLayout(rows);
        if (!layout) { toast({ type: "error", message: "Layout prodotti non riconosciuto." }); setLoading(false); return; }
        const items = [];
        const startRow = layout.isFixedLayout ? 1 : layout.headerRow + 1;
        for (let ri = startRow; ri < rows.length; ri++) {
          const row = rows[ri];
          if (!row || row.every(c => c == null || String(c || "").trim() === "")) continue;
          const nome = String(row[layout.descCol] || "").trim();
          if (!nome) continue;
          const reparto = String(row[layout.repCol] || "").trim();
          const qty = parseContaNumber(row[layout.qtyCol]);
          const valore = layout.valCol >= 0 ? parseContaNumber(row[layout.valCol]) : 0;
          // Salta righe di totale (reparto vuoto e nome contiene "totale")
          if (!reparto && /totale|^tot\.?$/i.test(nome)) continue;
          items.push({ reparto, nome, qty, valore });
        }
        if (items.length === 0) { toast({ type: "error", message: "Nessun prodotto trovato nel file." }); setLoading(false); return; }
        setParsed({ type: "prodotti", year, items, fileName: file.name });
        setStep("preview_prodotti");
      }
    } catch (err) {
      toast({ type: "error", title: "Errore lettura file", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const doImportConta = async () => {
    if (!parsed || parsed.type !== "conta") return;
    setLoading(true);
    try {
      const importedAt = new Date().toISOString();
      const newRecords = [];
      for (const [date, day] of parsed.days.entries()) {
        newRecords.push({
          date,
          revenue: day.pranzoRevenue + day.cenaRevenue,
          covers: day.pranzoCovers + day.cenaCovers,
          pranzoRevenue: day.pranzoRevenue,
          pranzoCovers: day.pranzoCovers,
          cenaRevenue: day.cenaRevenue,
          cenaCovers: day.cenaCovers,
          source: "import_conta",
          importedAt,
        });
      }
      // Dedup/merge incrementale: rimuovi record esistenti con la stessa data che hanno source="import_conta"
      // e appartengono allo stesso anno — poi inserisci i nuovi.
      const yearStr = String(parsed.year);
      const existing = (state.historicalDaily || []).filter(r => !r.date.startsWith(yearStr) || r.source !== "import_conta");
      const merged = [...existing, ...newRecords].sort((a, b) => a.date < b.date ? -1 : a.date > b.date ? 1 : 0);
      const nextState = { ...state, historicalDaily: merged };
      // Rigenera il modello ATTESI REALI (fonte primaria = historicalDaily appena
      // importato) e sincronizzalo al tablet via store demandModel.
      const dm = engine.buildExpectedCoversModel(nextState);
      nextState.demandModel = [{ id: "current", model: dm, builtAt: dm.builtAt, updatedAt: dm.builtAt, version: 1 }];
      setState(nextState);
      toast({ type: "success", title: "Storico incassi importato", message: `${newRecords.length} giorni anno ${parsed.year} · totale ${fmt.eur(parsed.parsedRevenue)} · ${parsed.parsedCovers} coperti` });
      onClose();
    } catch (err) {
      toast({ type: "error", title: "Errore import", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const doImportProdotti = async () => {
    if (!parsed || parsed.type !== "prodotti") return;
    setLoading(true);
    try {
      const importedAt = new Date().toISOString();
      const newRecord = { year: parsed.year, importedAt, items: parsed.items, fileName: parsed.fileName };
      // Dedup: un solo record per anno
      const existing = (state.historicalProducts || []).filter(r => r.year !== parsed.year);
      setState({ ...state, historicalProducts: [...existing, newRecord] });
      const totalQty = parsed.items.reduce((s, it) => s + (it.qty || 0), 0);
      const totalValore = parsed.items.reduce((s, it) => s + (it.valore || 0), 0);
      toast({ type: "success", title: "Prodotti venduti importati", message: `${parsed.items.length} prodotti anno ${parsed.year} · ${Math.round(totalQty)} pezzi · ${fmt.eur(totalValore)}` });
      onClose();
    } catch (err) {
      toast({ type: "error", title: "Errore import", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  // Calcola delta % tra valore parsato e valore di controllo
  const deltaPercent = (parsed_, valore_) => {
    if (!parsed_ || parsed_ === 0) return null;
    return ((valore_ - parsed_) / parsed_ * 100).toFixed(2);
  };

  return <Modal onClose={onClose} maxWidth={820} title="Importa storico incassi" subtitle="File conta (giornaliero) o prodotti venduti (annuale)"
    footer={<div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
      <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
      {step === "preview_conta" && <Btn variant="accent" icon={CheckCircle2} onClick={doImportConta} disabled={loading}>
        Importa {parsed?.days?.size || 0} giorni
      </Btn>}
      {step === "preview_prodotti" && <Btn variant="accent" icon={CheckCircle2} onClick={doImportProdotti} disabled={loading}>
        Importa {parsed?.items?.length || 0} prodotti
      </Btn>}
    </div>}>

    <div style={{ padding: 22 }}>
      {/* STEP UPLOAD */}
      {step === "upload" && <div>
        <div style={{ padding: 12, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, marginBottom: 16, fontSize: 12, color: C.tealDeep, lineHeight: 1.7 }}>
          <strong>Importa i tuoi file storici Excel</strong><br />
          Carica i file <code>conta 25.xlsx</code> / <code>conta 24.xlsx</code> (incassi+coperti giornalieri) oppure <code>prodotti venduti 25.xlsx</code> / <code>24.xlsx</code> (mix vendite per prodotto).<br />
          Il sistema riconosce automaticamente il tipo di file e l&apos;anno. Puoi importarli uno alla volta — i dati si accumulano senza sovrascriversi.
        </div>
        {loading && <div style={{ textAlign: "center", padding: 24, color: C.muted }}>Analisi in corso...</div>}
        {!loading && <div onClick={() => fileRef.current?.click()} style={{ border: `2px dashed ${C.rule}`, padding: 36, borderRadius: 2, textAlign: "center", cursor: "pointer", background: C.paperDeep }}
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.teal; }}
          onDragLeave={e => { e.currentTarget.style.borderColor = C.rule; }}
          onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.rule; if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); }}>
          <Upload size={34} color={C.muted} strokeWidth={1.3} style={{ margin: "0 auto 10px", display: "block" }} />
          <div className="f-display" style={{ fontSize: 15, color: C.ink, fontWeight: 500 }}>Trascina qui il file Excel</div>
          <div className="f-body" style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>.xlsx · max 50 MB</div>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.ods" style={{ display: "none" }} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
        </div>}

        {/* Riepilogo dati storici gia presenti */}
        {((state.historicalDaily || []).length > 0 || (state.historicalProducts || []).length > 0) && (
          <div style={{ marginTop: 18, padding: 12, background: C.oliveWash, borderRadius: 2, fontSize: 12, color: C.oliveDeep }}>
            <strong>Dati gia presenti:</strong>
            {(state.historicalDaily || []).length > 0 && (() => {
              const years = [...new Set((state.historicalDaily).map(r => r.date.slice(0, 4)))].sort();
              const totalRev = (state.historicalDaily).reduce((s, r) => s + (r.revenue || 0), 0);
              const totalCov = (state.historicalDaily).reduce((s, r) => s + (r.covers || 0), 0);
              return <div>{(state.historicalDaily).length} giorni · anni {years.join(", ")} · {fmt.eur(totalRev)} · {totalCov} coperti</div>;
            })()}
            {(state.historicalProducts || []).length > 0 && (
              <div>{(state.historicalProducts).length} import prodotti · anni {(state.historicalProducts).map(r => r.year).sort().join(", ")}</div>
            )}
          </div>
        )}
      </div>}

      {/* STEP PREVIEW CONTA */}
      {step === "preview_conta" && parsed && parsed.type === "conta" && (() => {
        const deltaC = parsed.controlCovers != null ? deltaPercent(parsed.parsedCovers, parsed.controlCovers) : null;
        const deltaR = parsed.controlRevenue != null ? deltaPercent(parsed.parsedRevenue, parsed.controlRevenue) : null;
        const deltaCAbs = deltaC != null ? Math.abs(parseFloat(deltaC)) : null;
        const deltaRAbs = deltaR != null ? Math.abs(parseFloat(deltaR)) : null;
        const okC = deltaCAbs == null || deltaCAbs <= 1.0;
        const okR = deltaRAbs == null || deltaRAbs <= 1.0;

        // Sample prime 10 date
        const sampleDays = Array.from(parsed.days.entries()).slice(0, 10);

        return <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            {/* Card coperti */}
            <div style={{ background: okC ? C.oliveWash : C.amberWash, border: `1px solid ${okC ? C.olive : C.amber}`, borderRadius: 2, padding: 14 }}>
              <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.muted, marginBottom: 4 }}>COPERTI — VERIFICA</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.ink, fontFamily: "Fraunces, serif" }}>{parsed.parsedCovers.toLocaleString("it-IT")}</div>
              <div style={{ fontSize: 11, color: C.inkSoft, marginTop: 4 }}>
                {parsed.controlCovers != null ? <>Controllo foglio: <strong>{parsed.controlCovers.toLocaleString("it-IT")}</strong> · delta <strong style={{ color: okC ? C.oliveDeep : C.terraDeep }}>{deltaC}%</strong></> : "Totale di controllo non trovato nel foglio"}
              </div>
              {!okC && <div style={{ fontSize: 11, color: C.terraDeep, marginTop: 4, fontWeight: 600 }}>Delta &gt; 1% — verifica il file prima di confermare.</div>}
            </div>
            {/* Card incasso */}
            <div style={{ background: okR ? C.oliveWash : C.amberWash, border: `1px solid ${okR ? C.olive : C.amber}`, borderRadius: 2, padding: 14 }}>
              <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.muted, marginBottom: 4 }}>INCASSO — VERIFICA</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.ink, fontFamily: "Fraunces, serif" }}>{fmt.eur(parsed.parsedRevenue)}</div>
              <div style={{ fontSize: 11, color: C.inkSoft, marginTop: 4 }}>
                {parsed.controlRevenue != null ? <>Controllo foglio: <strong>{fmt.eur(parsed.controlRevenue)}</strong> · delta <strong style={{ color: okR ? C.oliveDeep : C.terraDeep }}>{deltaR}%</strong></> : "Totale di controllo non trovato nel foglio"}
              </div>
              {!okR && <div style={{ fontSize: 11, color: C.terraDeep, marginTop: 4, fontWeight: 600 }}>Delta &gt; 1% — verifica il file prima di confermare.</div>}
            </div>
          </div>

          <div style={{ marginBottom: 14, padding: 10, background: C.paperDeep, borderRadius: 2, fontSize: 12 }}>
            <strong>File:</strong> {parsed.fileName} &nbsp;·&nbsp; <strong>Anno inferito:</strong> {parsed.year} &nbsp;·&nbsp; <strong>Giorni trovati:</strong> {parsed.days.size}
          </div>

          {/* Tabella anteprima prime 10 date */}
          <div style={{ overflow: "auto", maxHeight: 280, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ background: C.paperDeep }}>
                  {["Data", "Pranzo €", "Pranzo cop.", "Cena €", "Cena cop.", "Totale €", "Tot. cop."].map(h => (
                    <th key={h} className="f-mono" style={{ padding: "6px 10px", textAlign: "right", fontSize: 9, letterSpacing: "0.1em", color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sampleDays.map(([date, day]) => (
                  <tr key={date} style={{ borderBottom: `1px solid ${C.ruleSoft}` }}>
                    <td className="f-mono" style={{ padding: "5px 10px", fontSize: 12 }}>{new Date(date + "T12:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</td>
                    <td className="f-mono" style={{ padding: "5px 10px", textAlign: "right" }}>{fmt.eur(day.pranzoRevenue)}</td>
                    <td className="f-mono" style={{ padding: "5px 10px", textAlign: "right" }}>{day.pranzoCovers}</td>
                    <td className="f-mono" style={{ padding: "5px 10px", textAlign: "right" }}>{fmt.eur(day.cenaRevenue)}</td>
                    <td className="f-mono" style={{ padding: "5px 10px", textAlign: "right" }}>{day.cenaCovers}</td>
                    <td className="f-mono" style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600 }}>{fmt.eur(day.pranzoRevenue + day.cenaRevenue)}</td>
                    <td className="f-mono" style={{ padding: "5px 10px", textAlign: "right", fontWeight: 600 }}>{day.pranzoCovers + day.cenaCovers}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {parsed.days.size > 10 && <div style={{ padding: 8, textAlign: "center", fontSize: 10, color: C.muted, fontStyle: "italic" }}>...prime 10 di {parsed.days.size} date. Premi "Importa" per procedere con tutte.</div>}
          </div>
        </div>;
      })()}

      {/* STEP PREVIEW PRODOTTI */}
      {step === "preview_prodotti" && parsed && parsed.type === "prodotti" && (() => {
        const totalQty = parsed.items.reduce((s, it) => s + (it.qty || 0), 0);
        const totalValore = parsed.items.reduce((s, it) => s + (it.valore || 0), 0);
        const reparti = [...new Set(parsed.items.map(it => it.reparto).filter(Boolean))];
        const sampleItems = parsed.items.slice(0, 15);
        return <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
            <div style={{ background: C.tealWash, padding: 12, borderRadius: 2 }}>
              <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.12em", marginBottom: 4 }}>PRODOTTI</div>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "Fraunces, serif" }}>{parsed.items.length}</div>
            </div>
            <div style={{ background: C.tealWash, padding: 12, borderRadius: 2 }}>
              <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.12em", marginBottom: 4 }}>QTA TOTALE</div>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "Fraunces, serif" }}>{Math.round(totalQty).toLocaleString("it-IT")}</div>
            </div>
            <div style={{ background: C.tealWash, padding: 12, borderRadius: 2 }}>
              <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.12em", marginBottom: 4 }}>VALORE TOTALE</div>
              <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "Fraunces, serif" }}>{fmt.eur(totalValore)}</div>
            </div>
          </div>
          <div style={{ marginBottom: 12, fontSize: 12, color: C.inkSoft }}>
            <strong>Anno:</strong> {parsed.year} &nbsp;·&nbsp; <strong>Reparti:</strong> {reparti.slice(0, 8).join(", ")}{reparti.length > 8 ? " ..." : ""}
          </div>
          <div style={{ overflow: "auto", maxHeight: 280, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ background: C.paperDeep }}>
                  {["Reparto", "Prodotto", "Qty", "Valore €"].map(h => (
                    <th key={h} className="f-mono" style={{ padding: "6px 10px", textAlign: h === "Prodotto" || h === "Reparto" ? "left" : "right", fontSize: 9, letterSpacing: "0.1em", color: C.muted, fontWeight: 600, borderBottom: `1px solid ${C.rule}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sampleItems.map((it, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.ruleSoft}` }}>
                    <td style={{ padding: "4px 10px", color: C.muted, fontSize: 11 }}>{it.reparto || "—"}</td>
                    <td style={{ padding: "4px 10px" }}>{it.nome}</td>
                    <td className="f-mono" style={{ padding: "4px 10px", textAlign: "right" }}>{it.qty}</td>
                    <td className="f-mono" style={{ padding: "4px 10px", textAlign: "right" }}>{fmt.eur(it.valore)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {parsed.items.length > 15 && <div style={{ padding: 8, textAlign: "center", fontSize: 10, color: C.muted, fontStyle: "italic" }}>...prime 15 di {parsed.items.length}. Premi "Importa" per procedere con tutte.</div>}
          </div>
        </div>;
      })()}
    </div>
  </Modal>;
}

// ═══════════════════════════════════════════════════════════════════════════
// ArchivioPienissimoImporter — import dell'export storico Pienissimo.
// Legge il CSV via file.text(), distilla con _voyaDistillPienissimo, salva
// l'archivio (grosso) in una CHIAVE IndexedDB SEPARATA (VOYA_ARCHIVE_KEY) e
// mette in state SOLO i due artefatti piccoli (voyaArchiveMeta + voyaSeasonality).
// La promozione dei clienti al CRM è SEMPRE manuale (vista Clienti), MAI qui.
// ═══════════════════════════════════════════════════════════════════════════
function ArchivioPienissimoImporter({ state, setState, onClose }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [step, setStep] = useState("upload"); // "upload" | "preview"
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { archive, seasonality, meta }

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast({ type: "error", title: "Formato non valido", message: "Serve un file .csv esportato da Pienissimo." });
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      toast({ type: "error", title: "File troppo grande", message: "Massimo 30 MB." });
      return;
    }
    setLoading(true);
    try {
      const text = await file.text();
      const res = _voyaDistillPienissimo(text);
      if (!res || !res.archive || res.archive.count === 0) {
        toast({ type: "error", title: "Nessun cliente trovato", message: "Il file non sembra un export clienti Pienissimo valido." });
        setLoading(false);
        return;
      }
      setResult(res);
      setStep("preview");
    } catch (err) {
      toast({ type: "error", title: "Errore lettura file", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const doImport = async () => {
    if (!result) return;
    setLoading(true);
    try {
      // L'archivio (grosso) va SOLO in IndexedDB, MAI in state.
      await idbSet(VOYA_ARCHIVE_KEY, result.archive);
      _voyaArchiveInvalidate();
      // In state solo i due artefatti piccoli.
      const nextState = { ...state, voyaArchiveMeta: result.meta, voyaSeasonality: result.seasonality };
      // Rigenera il modello ATTESI REALI: con l'archivio Pienissimo importato le celle
      // senza storico reale acquisiscono un fallback stimato. Sincronizzato al tablet.
      const dm = engine.buildExpectedCoversModel(nextState);
      nextState.demandModel = [{ id: "current", model: dm, builtAt: dm.builtAt, updatedAt: dm.builtAt, version: 1 }];
      setState(nextState);
      toast({ type: "success", title: "Archivio storico importato",
        message: `${result.meta.count.toLocaleString("it-IT")} clienti · ${result.meta.regulars.toLocaleString("it-IT")} abituali` });
      onClose();
    } catch (err) {
      toast({ type: "error", title: "Errore salvataggio", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const m = result?.meta;
  const yr = (d) => (d ? String(d).slice(0, 4) : "?");

  return <Modal onClose={onClose} maxWidth={720} title="Archivio storico Pienissimo" subtitle="Import export clienti (CSV)"
    footer={<div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
      <Btn variant="ghost" onClick={onClose}>Annulla</Btn>
      {step === "preview" && <Btn variant="accent" icon={CheckCircle2} onClick={doImport} disabled={loading}>
        Importa archivio
      </Btn>}
    </div>}>
    <div style={{ padding: 22 }}>
      {step === "upload" && <div>
        <div style={{ padding: 12, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, marginBottom: 16, fontSize: 12, color: C.tealDeep, lineHeight: 1.7 }}>
          <strong>Carica l&apos;export storico da Pienissimo</strong><br />
          Il file <code>contacts.history-*.csv</code> con lo storico prenotazioni. Il sistema deduplica le righe doppie e costruisce l&apos;archivio clienti (visite, coperti, prima/ultima prenotazione).<br />
          L&apos;archivio resta SEPARATO dai clienti operativi del CRM: non rallenta l&apos;app. I clienti si promuovono al CRM uno alla volta, a mano, dalla ricerca.
        </div>
        {loading && <div style={{ textAlign: "center", padding: 24, color: C.muted }}>Analisi in corso... (può richiedere qualche secondo)</div>}
        {!loading && <div onClick={() => fileRef.current?.click()} style={{ border: `2px dashed ${C.rule}`, padding: 36, borderRadius: 2, textAlign: "center", cursor: "pointer", background: C.paperDeep }}
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.teal; }}
          onDragLeave={e => { e.currentTarget.style.borderColor = C.rule; }}
          onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = C.rule; if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); }}>
          <Database size={34} color={C.muted} strokeWidth={1.3} style={{ margin: "0 auto 10px", display: "block" }} />
          <div className="f-display" style={{ fontSize: 15, color: C.ink, fontWeight: 500 }}>Trascina qui il file CSV</div>
          <div className="f-body" style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>.csv · max 30 MB</div>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }} onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
        </div>}
        {state.voyaArchiveMeta && <div style={{ marginTop: 18, padding: 12, background: C.oliveWash, borderRadius: 2, fontSize: 12, color: C.oliveDeep }}>
          <strong>Archivio già presente:</strong> {Number(state.voyaArchiveMeta.count || 0).toLocaleString("it-IT")} clienti · {Number(state.voyaArchiveMeta.regulars || 0).toLocaleString("it-IT")} abituali · dal {yr(state.voyaArchiveMeta.rangeFirst)} al {yr(state.voyaArchiveMeta.rangeLast)}. Un nuovo import lo sostituisce.
        </div>}
      </div>}

      {step === "preview" && m && <div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div style={{ padding: 14, background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase" }}>Clienti unici</div>
            <div className="f-display" style={{ fontSize: 28, color: C.ink, fontWeight: 500 }}>{m.count.toLocaleString("it-IT")}</div>
          </div>
          <div style={{ padding: 14, background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase" }}>Abituali (≥2 visite)</div>
            <div className="f-display" style={{ fontSize: 28, color: C.ink, fontWeight: 500 }}>{m.regulars.toLocaleString("it-IT")}</div>
          </div>
          <div style={{ padding: 14, background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase" }}>Prenotazioni uniche</div>
            <div className="f-display" style={{ fontSize: 28, color: C.ink, fontWeight: 500 }}>{m.reservationsUnique.toLocaleString("it-IT")}</div>
          </div>
          <div style={{ padding: 14, background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
            <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase" }}>Coperti prenotati storici</div>
            <div className="f-display" style={{ fontSize: 28, color: C.ink, fontWeight: 500 }}>{m.coversTotal.toLocaleString("it-IT")}</div>
          </div>
        </div>
        <div style={{ padding: 12, background: C.paperDeep, borderRadius: 2, fontSize: 12, color: C.inkSoft, lineHeight: 1.7 }}>
          Periodo: dal <strong>{m.rangeFirst || "?"}</strong> al <strong>{m.rangeLast || "?"}</strong>.<br />
          I coperti sono <strong>prenotati</strong> (domanda storica, includono eventuali rifiuti/no-show), non presenze reali.<br />
          Premi <strong>Importa archivio</strong> per salvarlo. I clienti restano fuori dal CRM finché non li aggiungi a mano dalla ricerca.
        </div>
      </div>}
    </div>
  </Modal>;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║              VENDITE — SOTTOCOMPONENTI (lista, consumo, storico)           ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

// Lista scontrini (oggi o filtrati) — riga compatta cliccabile
function SalaReceiptsList({ receipts, state, onView, emptyLabel, outliersSet }) {
  if (!receipts || receipts.length === 0) {
    return <EmptyState icon={Receipt} title={emptyLabel || "Nessuno scontrino"} />;
  }
  // Ordina dal più recente
  const sorted = [...receipts].sort((a, b) => new Date(b.closedAt) - new Date(a.closedAt));
  const outliers = outliersSet instanceof Set ? outliersSet : null;
  return <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, overflow: "hidden" }}>
    <div style={{ display: "grid", gridTemplateColumns: "0.5fr 0.6fr 0.5fr 0.4fr 0.4fr 0.6fr 1fr 0.6fr 0.6fr", padding: "10px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.2em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
      <span>DOC</span>
      <span>DATA · ORA</span>
      <span style={{ textAlign: "center" }}>SERV</span>
      <span style={{ textAlign: "center" }}>TAV</span>
      <span style={{ textAlign: "center" }}>COP</span>
      <span style={{ textAlign: "center" }}>PIATTI</span>
      <span style={{ textAlign: "right" }}>TOTALE</span>
      <span style={{ textAlign: "right" }}>€/COP</span>
      <span></span>
    </div>
    {sorted.map(r => {
      const shift = getShift(r.closedAt);
      const perCover = r.covers > 0 ? r.total / r.covers : 0;
      const isOutlier = outliers ? outliers.has(r.id) : false;
      const docBadge = receiptDocBadge(r);
      const isPreconto = !!r._isPreconto || r._docType === "ESTRATTO CONTO";
      const isFattura = r._docType === "FATTURA";
      // Batch 3 — riconciliazione EC: varianti stato EC per bordo e sfondo
      const isECReconciled = isPreconto && r._regularizedTo && r._regularizedTo !== "NERO_DEFINITIVO";
      const isECNeroDefinitivo = isPreconto && r._regularizedTo === "NERO_DEFINITIVO";
      const isECOrphan = isPreconto && !r._regularizedTo;
      // Sfondo e bordo sinistro per tipo documento (priorità: EC > outlier > default)
      let bgRest, bgHover, leftBorder;
      if (isECReconciled)      { bgRest = C.oliveWash + "55"; bgHover = C.oliveWash + "AA"; leftBorder = `3px solid ${C.olive}`; }
      else if (isECNeroDefinitivo) { bgRest = C.terraWash + "33"; bgHover = C.terraWash + "66"; leftBorder = `3px solid ${C.terraDeep}`; }
      else if (isECOrphan)     { bgRest = C.terraWash + "55"; bgHover = C.terraWash + "AA"; leftBorder = `3px solid ${C.terra}`; }
      else if (isOutlier)      { bgRest = C.amberWash; bgHover = C.amberWash; leftBorder = "3px solid transparent"; }
      else if (isFattura)      { bgRest = "transparent"; bgHover = C.paperDeep; leftBorder = `3px solid ${C.olive}`; }
      else                     { bgRest = "transparent"; bgHover = C.paperDeep; leftBorder = "3px solid transparent"; }

      return <button key={r.id} onClick={() => onView(r)} className="tx" style={{
        display: "grid", gridTemplateColumns: "0.5fr 0.6fr 0.5fr 0.4fr 0.4fr 0.6fr 1fr 0.6fr 0.6fr",
        padding: "8px 14px", alignItems: "center", fontSize: 12,
        background: bgRest, border: "none", borderTop: `1px solid ${C.ruleSoft}`, borderLeft: leftBorder, cursor: "pointer",
        textAlign: "left", fontFamily: "inherit", width: "100%", opacity: isOutlier && !isPreconto ? 0.92 : 1,
      }}
        onMouseEnter={e => e.currentTarget.style.background = bgHover}
        onMouseLeave={e => e.currentTarget.style.background = bgRest}>
        <span>{docBadge && <span title={docBadge.title} style={{ background: docBadge.bg, color: C.paper, fontSize: 8, padding: "2px 5px", borderRadius: 2, fontWeight: 700, letterSpacing: "0.08em", fontFamily: "JetBrains Mono, monospace" }}>{docBadge.label}</span>}</span>
        <span className="f-mono" style={{ color: C.ink, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
          {new Date(r.closedAt).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
          {isOutlier && <span title="Scontrino outlier (MAD-z > 3.5 vs distribuzione storica)" style={{ background: C.amber, color: C.paper, fontSize: 8, padding: "1px 5px", borderRadius: 2, fontWeight: 700, letterSpacing: "0.1em" }}>OUTLIER</span>}
          {r._menuConcordatoMerged && <span title={`Unito da ${r._menuConcordatoMerged} scontrini TCPOS (menu concordato)`} style={{ background: C.teal, color: C.paper, fontSize: 8, padding: "1px 5px", borderRadius: 2, fontWeight: 700, letterSpacing: "0.1em" }}>MENU×{r._menuConcordatoMerged}</span>}
          {(r.items || []).some(it => it._isGift) && <span title="Contiene articoli regalo (es. Sesciala) non incassati" style={{ background: C.olive, color: C.paper, fontSize: 8, padding: "1px 5px", borderRadius: 2, fontWeight: 700, letterSpacing: "0.1em" }}>GIFT</span>}
          {(r._mezzePorzioni && r._mezzePorzioni.length > 0) && <span
            title={`${r._mezzePorzioni.length} mezza/e porzione/i · sconto automatico applicato €${((r._mezzePorzioni || []).filter(mp => mp.appliedAutomatically).reduce((s, mp) => s + mp.scontoAtteso, 0)).toFixed(2)}`}
            style={{ background: C.teal, color: C.paper, fontSize: 8, padding: "1px 5px", borderRadius: 2, fontWeight: 700, letterSpacing: "0.1em" }}>MEZZA×{r._mezzePorzioni.length}</span>}
          {isECReconciled && r._regularizedTo && <span title={`EC riconciliato — collegato a SF ${r._regularizedTo}`} style={{ background: C.olive, color: C.paper, fontSize: 8, padding: "1px 5px", borderRadius: 2, fontWeight: 700, letterSpacing: "0.1em" }}>RIC. SF</span>}
        </span>
        <span className="f-mono" style={{ textAlign: "center", fontSize: 10, color: shift === "pranzo" ? C.amberDeep : (shift === "cena" ? C.tealDeep : C.muted), fontWeight: 600 }}>
          {shift === "pranzo" ? "☀" : shift === "cena" ? "🌙" : "—"}
        </span>
        <span className="f-mono" style={{ textAlign: "center", fontWeight: 600 }}>{r.number}</span>
        <span className="f-mono" style={{ textAlign: "center" }}>{r.covers}</span>
        <span className="f-mono" style={{ textAlign: "center", color: C.muted }}>{(r.items || []).reduce((s, it) => s + it.qty, 0)}</span>
        <span className="f-mono" style={{ textAlign: "right", color: C.ink, fontWeight: 600 }}>{fmt.eur(r.total)}</span>
        <span className="f-mono" style={{ textAlign: "right", color: C.muted, fontSize: 11 }}>{perCover > 0 ? fmt.eur(perCover) : "—"}</span>
        <ChevronRight size={14} color={C.muted} style={{ justifySelf: "end" }} />
      </button>;
    })}
  </div>;
}

// Consumo materia prima del giorno + top piatti + KPI margine
function SalaConsumption({ state, todayDishes, todayConsumption, todayRevenue, todayRevenueNet }) {
  const isGrossMode = engine.priceGross(state);
  const vatS = state.settings?.vatRateSale || 0.10;
  const vatP = state.settings?.vatRatePurchase || 0.10;

  // Costo totale giornaliero (somma snapshot oppure ricetta corrente — già aggregato in todayConsumption)
  const totalCostNet = useMemo(() => {
    if (!todayConsumption || todayConsumption.length === 0) return 0;
    return todayConsumption.reduce((s, c) => {
      // Se c'è snapshot, usa quello (storicamente preciso)
      if (c.costSnapshot > 0) return s + c.costSnapshot;
      // Altrimenti ricetta corrente
      const unitCost = c.bp?.pReale || c.bp?.pFatt || 0;
      return s + (c.qtyNet * unitCost);
    }, 0);
  }, [todayConsumption]);

  // Se il chiamante passa todayRevenueNet (scorporato per riga) usalo, altrimenti fallback
  // alla media unica (peggior caso, ma evita di rompere chiamate legacy).
  const revenueNet = (todayRevenueNet != null && Number.isFinite(todayRevenueNet))
    ? todayRevenueNet
    : (todayRevenue / (1 + vatS));
  const costDisplay = isGrossMode ? totalCostNet * (1 + vatP) : totalCostNet;
  const revenueDisplay = isGrossMode ? todayRevenue : revenueNet;
  const margin = revenueDisplay - costDisplay;
  const marginPct = revenueDisplay > 0 ? (margin / revenueDisplay) * 100 : 0;
  const fcPct = revenueNet > 0 ? (totalCostNet / revenueNet) * 100 : 0;

  if (!todayDishes || todayDishes.length === 0) {
    return <EmptyState icon={Package} title="Nessun consumo oggi"
      body="I dati di consumo materia prima si popolano automaticamente quando importi gli scontrini." />;
  }

  return <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
    {/* KPI fila */}
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 16, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>INCASSO {isGrossMode ? "LORDO" : "NETTO"}</div>
        <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color: C.ink, marginTop: 3 }}>{fmt.eur(revenueDisplay)}</div>
      </div>
      <div style={{ background: C.terraWash, border: `1px solid ${C.terra}`, padding: 16, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.terra, letterSpacing: "0.2em" }}>COSTO M.P. {isGrossMode ? "LORDO" : "NETTO"}</div>
        <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color: C.terra, marginTop: 3 }}>{fmt.eur(costDisplay)}</div>
      </div>
      <div style={{ background: C.oliveWash, border: `1px solid ${C.olive}`, padding: 16, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.olive, letterSpacing: "0.2em" }}>MARGINE LORDO</div>
        <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color: C.oliveDeep, marginTop: 3 }}>{fmt.eur(margin)}</div>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>{marginPct.toFixed(1)}% del ricavo</div>
      </div>
      <div style={{ background: C.paperDeep, border: `1px solid ${C.rule}`, padding: 16, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>FOOD COST %</div>
        <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color: fcPct > 35 ? C.terra : fcPct > 28 ? C.amber : C.olive, marginTop: 3 }}>{fcPct.toFixed(1)}%</div>
        <div className="f-mono" style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>target &lt; 30%</div>
      </div>
    </div>

    {/* Top piatti del giorno */}
    <div>
      <SectionHeader title="Piatti più venduti oggi" subtitle={`${todayDishes.length} piatti diversi · ${todayDishes.reduce((s, d) => s + d.qty, 0)} portate totali`} />
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
        <div style={{ display: "grid", gridTemplateColumns: "30px 1fr 0.4fr 0.6fr", padding: "8px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.2em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
          <span>#</span>
          <span>PIATTO</span>
          <span style={{ textAlign: "right" }}>QTÀ</span>
          <span style={{ textAlign: "right" }}>INCASSO</span>
        </div>
        {todayDishes.slice(0, 25).map((d, i) => {
          // IVA del piatto: override o default. Necessario per piatti vino/distillati al 22%.
          const dish = (state.dishesReal || []).find(x => x.id === d.dishId);
          const dishVat = (dish?.vatRate != null && Number.isFinite(dish.vatRate)) ? dish.vatRate : vatS;
          const revDisplay = isGrossMode ? d.revenue : d.revenue / (1 + dishVat);
          return <div key={d.dishId} style={{ display: "grid", gridTemplateColumns: "30px 1fr 0.4fr 0.6fr", padding: "7px 14px", borderTop: `1px solid ${C.ruleSoft}`, alignItems: "center", fontSize: 12 }}>
            <span className="f-mono" style={{ color: C.muted, fontWeight: 600 }}>{i + 1}</span>
            <span className="f-body" style={{ color: C.ink, fontWeight: 500 }}>{d.name}</span>
            <span className="f-mono" style={{ color: C.ink, fontWeight: 600, textAlign: "right" }}>× {d.qty}</span>
            <span className="f-mono" style={{ color: C.inkSoft, textAlign: "right" }}>{fmt.eur(revDisplay)}</span>
          </div>;
        })}
        {todayDishes.length > 25 && <div style={{ padding: 8, textAlign: "center", fontSize: 10, color: C.muted, fontStyle: "italic" }}>+ altri {todayDishes.length - 25} piatti</div>}
      </div>
    </div>

    {/* Tabella consumo materia prima */}
    {todayConsumption && todayConsumption.length > 0 && <div>
      <SectionHeader title="Consumo materia prima oggi" subtitle={`${todayConsumption.length} prodotti base consumati · valorizzati ai prezzi reali`} />
      <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 0.5fr 0.5fr 0.6fr 0.6fr", padding: "8px 14px", background: C.paperDeep, fontSize: 9, letterSpacing: "0.2em", color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
          <span>PRODOTTO</span>
          <span style={{ textAlign: "right" }}>CONSUMATO</span>
          <span style={{ textAlign: "right" }}>GIACENZA</span>
          <span style={{ textAlign: "right" }}>COSTO</span>
          <span style={{ textAlign: "right" }}>STATO</span>
        </div>
        {todayConsumption.slice(0, 30).map(c => {
          const stock = engine.itemStock(state, c.bp.id);
          const unitCost = c.bp.pReale || c.bp.pFatt || 0;
          const cost = c.costSnapshot > 0 ? c.costSnapshot : c.qtyNet * unitCost;
          const lowAfter = stock < (c.bp.minStock || 2);
          return <div key={c.bp.id} style={{ display: "grid", gridTemplateColumns: "1fr 0.5fr 0.5fr 0.6fr 0.6fr", padding: "7px 14px", borderTop: `1px solid ${C.ruleSoft}`, alignItems: "center", fontSize: 12 }}>
            <span className="f-body" style={{ color: C.ink, fontWeight: 500 }}>{c.bp.display || c.bp.nome}</span>
            <span className="f-mono" style={{ color: C.ink, fontWeight: 600, textAlign: "right" }}>{c.qtyNet.toFixed(3)} {c.bp.unit || "kg"}</span>
            <span className="f-mono" style={{ color: lowAfter ? C.terra : C.muted, textAlign: "right" }}>{stock.toFixed(2)} {c.bp.unit || "kg"}</span>
            <span className="f-mono" style={{ color: C.terra, fontWeight: 500, textAlign: "right" }}>{fmt.eur(cost)}</span>
            <span className="f-mono" style={{ color: lowAfter ? C.terra : C.olive, fontWeight: 600, textAlign: "right" }}>{lowAfter ? "⚠ BASSO" : "OK"}</span>
          </div>;
        })}
      </div>
    </div>}
  </div>;
}

// Storico completo scontrini con filtri data/tavolo
function SalaHistory({ state, setState, receipts, onView }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [tableFilter, setTableFilter] = useState("");
  const [shiftFilter, setShiftFilter] = useState("all");  // all | pranzo | cena
  const [articleFilter, setArticleFilter] = useState("");  // ricerca per nome articolo nello scontrino
  const [excludeOutliers, setExcludeOutliers] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;
  // Normalizzo la query articolo una volta (NFD + lowercase + senza accenti) per
  // match robusto su "caffè"/"caffe", "spaghètti"/"spaghetti", ecc.
  const articleQueryNorm = articleFilter.trim() ? normStr(articleFilter) : "";

  // Detect outlier scontrini su TUTTO lo storico (MAD-z robust > 3.5).
  // La detection e' globale: gli outlier sono outlier rispetto alla distribuzione
  // intera, non rispetto al sotto-insieme filtrato (altrimenti il flag cambia
  // a seconda di cosa stai guardando — non robusto).
  const outliersSet = useMemo(
    () => engine.stats.detectOutlierReceipts(receipts),
    [receipts]
  );

  const filtered = useMemo(() => {
    return receipts.filter(r => {
      const day = getServiceDate(r.closedAt);
      if (!day) return false;
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      if (tableFilter && !String(r.number).toLowerCase().includes(tableFilter.toLowerCase())) return false;
      if (shiftFilter !== "all") {
        // getShift() ritorna null per gli scontrini chiusi fuori dalle fasce
        // pranzo (09–18) e cena (18–04): tipicamente banco del mattino 04:00–08:59.
        // Li mappiamo sul valore "fuori" così che Pranzo+Cena+Fuori turno = Tutti.
        const sh = getShift(r.closedAt) || "fuori";
        if (sh !== shiftFilter) return false;
      }
      if (excludeOutliers && outliersSet.has(r.id)) return false;
      // Filtro articolo: scontrino passa se almeno un item ha nome o _csvName
      // che contiene la query (normalizzata: case-insensitive, senza accenti).
      // Esempi: "cozze" match "COZZE PORTONOVO"; "spaghet" match "SPAGHETTO SARDE".
      if (articleQueryNorm) {
        const hit = (r.items || []).some(it => {
          const n = normStr(it.name);
          if (n.includes(articleQueryNorm)) return true;
          if (it._csvName && normStr(it._csvName).includes(articleQueryNorm)) return true;
          return false;
        });
        if (!hit) return false;
      }
      return true;
    });
  }, [receipts, dateFrom, dateTo, tableFilter, shiftFilter, excludeOutliers, outliersSet, articleQueryNorm]);

  const total = filtered.reduce((s, r) => s + r.total, 0);
  const covers = filtered.reduce((s, r) => s + (r.covers || 0), 0);
  const avgPerCover = covers > 0 ? total / covers : 0;
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const sortedFiltered = useMemo(() => [...filtered].sort((a, b) => new Date(b.closedAt) - new Date(a.closedAt)), [filtered]);
  const pageItems = sortedFiltered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Range disponibile (per placeholder filtri) — uso copia per non mutare
  const allDates = useMemo(() => [...receipts.map(r => getServiceDate(r.closedAt)).filter(Boolean)].sort(), [receipts]);
  const minDate = allDates[0];
  const maxDate = allDates[allDates.length - 1];

  // Heatmap incassi per giorno (ultimi 60 giorni)
  const heatmapData = useMemo(() => {
    const byDate = {};
    receipts.forEach(r => {
      const d = getServiceDate(r.closedAt);
      if (!d) return;
      if (!byDate[d]) byDate[d] = { revenue: 0, covers: 0, receipts: 0 };
      byDate[d].revenue += r.total;
      byDate[d].covers += r.covers || 0;
      byDate[d].receipts += 1;
    });
    // Genera array continuo degli ultimi 60 gg (per vedere anche i buchi)
    const days = [];
    for (let i = 59; i >= 0; i--) {
      const d = daysAgoISO(i);
      days.push({ date: d, ...(byDate[d] || { revenue: 0, covers: 0, receipts: 0 }) });
    }
    const maxRev = Math.max(...days.map(d => d.revenue), 1);
    return { days, maxRev };
  }, [receipts]);

  return <div>
    {/* Qualità import permanente — anche nello storico, per chi naviga indietro */}
    <ImportQualityPanel state={state} setState={setState} />

    {/* Calendario heatmap incassi 60gg */}
    {receipts.length > 0 && <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: C.muted }}>CALENDARIO IMPORT · ULTIMI 60 GIORNI</div>
          <div className="f-body" style={{ fontSize: 11, color: C.inkSoft, marginTop: 3 }}>Click su un giorno per filtrare gli scontrini di quella data. Celle vuote = nessun import.</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 10, color: C.muted, fontFamily: "JetBrains Mono, monospace" }}>
          <span>POCO</span>
          <div style={{ display: "flex", gap: 1 }}>
            {[0.1, 0.3, 0.5, 0.7, 1].map((v, i) => <div key={i} style={{ width: 10, height: 10, background: v === 0.1 ? C.ruleSoft : `rgba(74,124,89,${v})`, borderRadius: 1 }} />)}
          </div>
          <span>TANTO</span>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(60, 1fr)", gap: 2 }}>
        {heatmapData.days.map((d, i) => {
          const intensity = d.revenue > 0 ? Math.max(0.15, d.revenue / heatmapData.maxRev) : 0;
          const dt = new Date(d.date + "T12:00:00");
          const dow = dt.getDay();
          const isToday = d.date === TODAY;
          const isSelected = (d.date === dateFrom || d.date === dateTo);
          const isWeekend = dow === 0 || dow === 6;
          return <button key={d.date} onClick={() => { setDateFrom(d.date); setDateTo(d.date); setPage(0); }} className="tx" title={`${dt.toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })} · ${d.revenue > 0 ? `${fmt.eur(d.revenue)} · ${d.receipts} scontrini · ${d.covers} cop.` : "nessun import"}`}
            style={{
              aspectRatio: "1", border: isSelected ? `2px solid ${C.ink}` : isToday ? `1.5px solid ${C.amberDeep}` : `1px solid ${isWeekend ? C.rule : C.ruleSoft}`,
              background: d.revenue > 0 ? `rgba(74,124,89,${intensity})` : C.paperDeep,
              borderRadius: 2, cursor: "pointer", padding: 0,
              fontFamily: "JetBrains Mono, monospace", fontSize: 7, color: intensity > 0.5 ? C.paper : C.muted,
              display: "flex", alignItems: "center", justifyContent: "center", fontWeight: isToday ? 700 : 500,
            }}>
            {dt.getDate()}
          </button>;
        })}
      </div>
    </div>}

    {/* Toolbar filtri */}
    <div style={{ background: C.paperDeep, padding: 14, borderRadius: 2, marginBottom: 14, display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
      <div>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em", marginBottom: 4 }}>DA</div>
        <Input type="date" value={dateFrom} min={minDate} max={maxDate} onChange={e => { setDateFrom(e.target.value); setPage(0); }} style={{ padding: "6px 10px", fontSize: 11 }} />
      </div>
      <div>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em", marginBottom: 4 }}>A</div>
        <Input type="date" value={dateTo} min={minDate} max={maxDate} onChange={e => { setDateTo(e.target.value); setPage(0); }} style={{ padding: "6px 10px", fontSize: 11 }} />
      </div>
      <div>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em", marginBottom: 4 }}>TAVOLO</div>
        <Input value={tableFilter} onChange={e => { setTableFilter(e.target.value); setPage(0); }} placeholder="es. 5" style={{ padding: "6px 10px", fontSize: 11, width: 100 }} />
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em", marginBottom: 4 }}>ARTICOLO / PAROLA</div>
        <Input value={articleFilter} onChange={e => { setArticleFilter(e.target.value); setPage(0); }} placeholder='es. "cozze", "spaghetto", "tartare"' style={{ padding: "6px 10px", fontSize: 11, width: "100%" }} />
      </div>
      <div>
        <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em", marginBottom: 4 }}>SERVIZIO</div>
        <Select value={shiftFilter} onChange={e => { setShiftFilter(e.target.value); setPage(0); }} style={{ padding: "6px 10px", fontSize: 11 }}>
          <option value="all">Tutti</option>
          <option value="pranzo">☀ Pranzo</option>
          <option value="cena">🌙 Cena</option>
          <option value="fuori">⊘ Fuori turno</option>
        </Select>
      </div>
      {/* Toggle escludi outlier: ha senso solo se almeno 1 outlier rilevato */}
      {outliersSet.size > 0 && <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "6px 8px", background: excludeOutliers ? C.amberWash : "transparent", border: `1px solid ${excludeOutliers ? C.amber : C.rule}`, borderRadius: 2 }} title="Outlier rilevati con MAD-z robust > 3.5 sulla distribuzione del totale scontrino. Tipicamente: tavolate fuori scala (€500+) che gonfiano le medie giornaliere.">
        <input type="checkbox" checked={excludeOutliers} onChange={e => { setExcludeOutliers(e.target.checked); setPage(0); }} style={{ cursor: "pointer", margin: 0 }} />
        <span className="f-mono" style={{ fontSize: 10, color: excludeOutliers ? C.amberDeep : C.inkSoft, fontWeight: excludeOutliers ? 700 : 500, letterSpacing: "0.05em" }}>
          ESCLUDI {outliersSet.size} OUTLIER (|z|&gt;3.5)
        </span>
      </label>}
      {(dateFrom || dateTo || tableFilter || articleFilter || shiftFilter !== "all" || excludeOutliers) && <Btn variant="ghost" size="sm" icon={X} onClick={() => { setDateFrom(""); setDateTo(""); setTableFilter(""); setArticleFilter(""); setShiftFilter("all"); setExcludeOutliers(false); setPage(0); }}>Reset</Btn>}
      {/* Export CSV degli scontrini filtrati */}
      <Btn variant="ghost" size="sm" icon={Download} onClick={() => {
        if (filtered.length === 0) return;
        const headers = ["Data", "Ora", "Servizio", "Tavolo", "Coperti", "N.Piatti", "Totale"];
        const rows = sortedFiltered.map(r => {
          const dt = new Date(r.closedAt);
          return [
            dt.toLocaleDateString("it-IT"),
            dt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }),
            getShift(r.closedAt) || "—",
            r.number,
            r.covers,
            (r.items || []).reduce((s, it) => s + it.qty, 0),
            r.total.toFixed(2).replace(".", ","),
          ];
        });
        // sanitizeCsvCell: disinnesca formule (CWE-1236) + quoting RFC 4180
        const csv = [headers.map(sanitizeCsvCell).join(";"), ...rows.map(r => r.map(sanitizeCsvCell).join(";"))].join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `scontrini_${dateFrom || "tutto"}_${dateTo || "oggi"}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }}>Export CSV</Btn>
      <div style={{ marginLeft: "auto", display: "flex", gap: 18, alignItems: "center" }}>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>SCONTRINI</div>
          <div className="f-mono" style={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>{filtered.length}</div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>COPERTI</div>
          <div className="f-mono" style={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>{covers}</div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>INCASSO</div>
          <div className="f-mono" style={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>{fmt.eur(total)}</div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>€/COPERTO</div>
          <div className="f-mono" style={{ fontSize: 14, color: avgPerCover >= 43 ? C.olive : C.ink, fontWeight: 600 }}>{fmt.eur(avgPerCover)}</div>
        </div>
      </div>
    </div>

    {/* Banner outlier rilevati (info) */}
    {outliersSet.size > 0 && !excludeOutliers && <div style={{ background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, padding: 12, marginBottom: 12, fontSize: 12, color: C.amberDeep, lineHeight: 1.5, display: "flex", gap: 10, alignItems: "flex-start" }}>
      <AlertTriangle size={14} color={C.amberDeep} style={{ flexShrink: 0, marginTop: 2 }} />
      <div>
        <strong>{outliersSet.size} scontrini outlier rilevati</strong> (MAD-z robust &gt; 3.5 sulla distribuzione del totale). Tipicamente tavolate fuori scala che inquinano le medie giornaliere. Sono marcati con badge <span style={{ background: C.amber, color: C.paper, fontSize: 9, padding: "1px 4px", borderRadius: 2, fontWeight: 700 }}>OUTLIER</span> in lista. Usa il toggle "Escludi outlier" per medie più rappresentative.
      </div>
    </div>}

    {/* Lista scontrini */}
    <SalaReceiptsList receipts={pageItems} state={state} onView={onView} emptyLabel="Nessuno scontrino corrisponde ai filtri" outliersSet={outliersSet} />

    {/* Paginazione */}
    {totalPages > 1 && <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 14 }}>
      <Btn variant="ghost" size="sm" icon={ChevronLeft} disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Precedente</Btn>
      <span className="f-mono" style={{ fontSize: 11, color: C.muted, padding: "0 10px" }}>
        Pagina {page + 1} di {totalPages} · {filtered.length} totali
      </span>
      <Btn variant="ghost" size="sm" icon={ChevronRight} disabled={page >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>Successiva</Btn>
    </div>}
  </div>;
}


function TableReceiptViewer({ receipt, state, setState, onClose }) {
  const toast = useToast();
  const dishes = state.dishesReal || [];
  const defaultVat = state.settings?.vatRateSale || 0.10;
  const isEC = !!(receipt._isPreconto || receipt._docType === "ESTRATTO CONTO");
  const isECReconciled = isEC && receipt._regularizedTo && receipt._regularizedTo !== "NERO_DEFINITIVO";

  // FIX 2026-05-20: editing manuale coperti.
  // Caso reale: TCPOS frammenta MENU CONCORDATO su N scontrini SENZA emettere alcuna riga
  // "COPERTO Cena" (es. tav.431 12/05 22:35: 2 MENU €100+€72, zero coperti dichiarati).
  // Il fix F-15 deduce automaticamente N coperti = N item MENU CONCORDATO (1 menu = 1 persona),
  // ma il dato originale e' incompleto: se al tavolo erano 4 persone con solo 2 menu pagati
  // (o menu condivisi), il valore dedotto sottostima. Andi deve poter correggere a mano.
  // Strategia: salva _coversOriginal alla prima modifica per back-out reversibile,
  // poi sovrascrive receipt.covers (così tutte le aggregazioni downstream funzionano senza modifiche).
  const [editingCovers, setEditingCovers] = useState(false);
  const [editValue, setEditValue] = useState(String(receipt.covers || 0));
  const hasManualCovers = receipt._coversOriginal != null;
  const isInferredCovers = !!receipt._coversInferred;

  const handleSaveCovers = () => {
    const n = parseInt(editValue, 10);
    if (!Number.isFinite(n) || n < 0 || n > 200) {
      toast({ type: "error", title: "Valore non valido", message: "Inserisci un numero di coperti tra 0 e 200." });
      return;
    }
    if (n === receipt.covers) { setEditingCovers(false); return; }
    if (!setState) return;
    const operator = state.currentUser?.name || "Andi";
    const oldVal = receipt.covers;
    const originalVal = hasManualCovers ? receipt._coversOriginal : oldVal;
    setState(s => {
      const updated = (s.tableReceipts || []).map(r => {
        if (r.id !== receipt.id) return r;
        const next = { ...r, covers: n, _coversOriginal: originalVal, _coversManualAt: nowIso(), _coversManualBy: operator };
        // Il count NON e' piu' dedotto automaticamente — l'utente l'ha confermato/corretto.
        delete next._coversInferred;
        return next;
      });
      return {
        ...s,
        tableReceipts: updated,
        auditLog: appendAudit(s, {
          action: "covers_manual_edit",
          entityType: "receipt",
          entityId: receipt.id,
          ref: { from: oldVal, to: n, originalImport: originalVal },
          by: operator,
          reason: `Coperti modificati manualmente da ${oldVal} a ${n} (originale import: ${originalVal})`,
        }),
      };
    });
    setEditingCovers(false);
    toast({ type: "success", title: "Coperti aggiornati", message: `Da ${oldVal} a ${n} coperti.` });
  };

  const handleResetCovers = () => {
    if (!hasManualCovers || !setState) return;
    const operator = state.currentUser?.name || "Andi";
    const oldVal = receipt.covers;
    const originalVal = receipt._coversOriginal;
    setState(s => {
      const updated = (s.tableReceipts || []).map(r => {
        if (r.id !== receipt.id) return r;
        const { _coversOriginal, _coversManualAt, _coversManualBy, ...rest } = r;
        return { ...rest, covers: originalVal };
      });
      return {
        ...s,
        tableReceipts: updated,
        auditLog: appendAudit(s, {
          action: "covers_manual_reverted",
          entityType: "receipt",
          entityId: receipt.id,
          ref: { from: oldVal, to: originalVal },
          by: operator,
          reason: `Coperti ripristinati al valore importato (${originalVal})`,
        }),
      };
    });
    setEditValue(String(originalVal));
    toast({ type: "info", title: "Coperti ripristinati", message: `Riportati al valore importato (${originalVal}).` });
  };
  // Calcolo consumo e costo
  const consumption = useMemo(() => {
    const agg = {};
    (receipt.items || []).forEach(it => {
      // Prima cerchiamo ingredienti nello snapshot (più affidabile per lo storico)
      const ings = it.recipeSnapshot?.ingredienti || (() => {
        const dish = dishes.find(d => d.id === it.dishId);
        return dish ? dish.ingredienti : [];
      })();
      ings.forEach(ing => {
        if (!ing.bpId) return;
        const bp = (state.baseProducts || []).find(b => b.id === ing.bpId);
        if (!bp) return;
        if (!agg[ing.bpId]) agg[ing.bpId] = { bp, qty: 0, cost: 0 };
        agg[ing.bpId].qty += ing.qty * it.qty;
        // Snapshot cost se presente, altrimenti corrente
        const unitCost = ing.unitCostNet != null ? ing.unitCostNet : (bp.pReale || bp.pFatt || 0);
        agg[ing.bpId].cost += ing.qty * it.qty * unitCost;
      });
    });
    return Object.values(agg);
  }, [receipt, dishes, state.baseProducts]);

  // Revenue netto IVA scorporata per riga (mai media unica). Uno scontrino con cibo 10% + vino 22%
  // produce un netto reale ≠ total/(1+0.10).
  const revenueNet = useMemo(() => {
    let net = 0;
    (receipt.items || []).forEach(it => {
      const dish = dishes.find(d => d.id === it.dishId);
      const snap = it.recipeSnapshot?.vatRate;
      const dishV = (dish?.vatRate != null && Number.isFinite(dish.vatRate)) ? dish.vatRate : null;
      const v = (snap != null && Number.isFinite(snap)) ? snap
             : (dishV != null) ? dishV
             : defaultVat;
      const grossLine = (Number(it.qty) || 0) * (Number(it.unitPrice) || 0);
      net += v > -1 ? grossLine / (1 + v) : grossLine;
    });
    return net;
  }, [receipt, dishes, defaultVat]);

  const totalCost = consumption.reduce((s, c) => s + c.cost, 0);
  const isGrossMode = engine.priceGross(state);
  const revenueDisplay = isGrossMode ? receipt.total : revenueNet;
  const costDisplay = isGrossMode ? totalCost * (1 + (state.settings?.vatRatePurchase || 0.10)) : totalCost;
  const margin = revenueDisplay - costDisplay;
  const fcPct = revenueDisplay > 0 ? (costDisplay / revenueDisplay) * 100 : 0;
  // Sconto globale dello scontrino (listino - pagato).
  // Vedi engine.stats.receiptDiscount per la logica completa (regali, menu concordato).
  const disc = useMemo(() => engine.stats.receiptDiscount(receipt, dishes), [receipt, dishes]);

  const handleUndoReconcile = () => {
    if (!setState) return;
    const operator = state.currentUser?.name || "Andi";
    setState(s => {
      // Recupera l'SF collegato per rimuovere la traccia inversa
      const sfId = receipt._regularizedTo;
      const updated = (s.tableReceipts || []).map(r => {
        if (r.id === receipt.id) {
          // Rimuove tutti i campi di riconciliazione
          const { _regularizedTo, _regularizedAt, _regularizedBy, _regularizedNote, ...rest } = r;
          return rest;
        }
        if (sfId && r.id === sfId) {
          return { ...r, _hasReconciledEC: (r._hasReconciledEC || []).filter(id => id !== receipt.id) };
        }
        return r;
      });
      const next = { ...s, tableReceipts: updated };
      return {
        ...next,
        auditLog: appendAudit(next, {
          action: "nf_reconcile_undone",
          entityType: "receipt",
          entityId: receipt.id,
          ref: { sfId },
          by: operator,
          reason: "Annullamento riconciliazione EC",
        }),
      };
    });
    toast({ type: "info", title: "Riconciliazione annullata", message: "L'EC torna nella lista non fiscalizzati." });
    onClose();
  };

  return <Modal onClose={onClose} maxWidth={800} title={`Scontrino tavolo ${receipt.number}`} subtitle={`${receipt.covers} coperti · ${new Date(receipt.closedAt).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}`}
    footer={<>
      <Btn variant="ghost" onClick={onClose}>Chiudi</Btn>
      {isECReconciled && setState && <Btn variant="ghost" style={{ color: C.terra, borderColor: C.terra }} onClick={handleUndoReconcile}>Annulla riconciliazione</Btn>}
      <Btn variant="ghost" icon={Printer} onClick={() => window.print()}>Stampa A4</Btn>
      <Btn variant="primary" icon={Receipt} onClick={() => printThermal(receipt, state)}>Stampa 80mm</Btn>
    </>}>
    <div style={{ padding: 22 }}>

      {/* Banner EC riconciliato */}
      {isECReconciled && <div style={{ marginBottom: 14, padding: 12, background: C.oliveWash, border: `1px solid ${C.olive}`, borderRadius: 2, display: "flex", alignItems: "flex-start", gap: 10 }}>
        <CheckCircle2 size={16} color={C.oliveDeep} style={{ marginTop: 2, flexShrink: 0 }} />
        <div>
          <div className="f-mono" style={{ fontSize: 10, color: C.oliveDeep, letterSpacing: "0.2em", fontWeight: 600, marginBottom: 3 }}>EC RICONCILIATO</div>
          <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.5 }}>
            Questo ESTRATTO CONTO e stato collegato allo scontrino fiscale <strong>{receipt._regularizedTo}</strong>
            {receipt._regularizedAt && <> il {new Date(receipt._regularizedAt).toLocaleDateString("it-IT")} da <strong>{receipt._regularizedBy || "—"}</strong></>}.
            {receipt._regularizedNote && <><br />Nota: {receipt._regularizedNote}</>}
            <br /><span style={{ color: C.muted, fontSize: 11 }}>Il totale di questo EC ({fmt.eur(receipt.total)}) e escluso dall'incasso per evitare doppione con lo SF.</span>
          </div>
        </div>
      </div>}

      {/* Note tavolo evidenti in cima (allergie/preferenze) */}
      {receipt.notes && <div style={{ marginBottom: 14, padding: 12, background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, display: "flex", alignItems: "flex-start", gap: 10 }}>
        <AlertTriangle size={16} color={C.amberDeep} style={{ marginTop: 2, flexShrink: 0 }} />
        <div>
          <div className="f-mono" style={{ fontSize: 10, color: C.amberDeep, letterSpacing: "0.2em", fontWeight: 600, marginBottom: 3 }}>NOTE TAVOLO</div>
          <div className="f-body" style={{ fontSize: 13, color: C.ink, fontWeight: 500, lineHeight: 1.5 }}>{receipt.notes}</div>
        </div>
      </div>}

      {/* Widget COPERTI con editing manuale (fix 2026-05-20). Visibile sempre, evidenziato
          se il valore e' dedotto automaticamente (_coversInferred) o modificato manualmente. */}
      {setState && <div style={{ marginBottom: 14, padding: "10px 14px", background: hasManualCovers ? C.tealWash : (isInferredCovers ? C.amberWash : C.paperDeep), border: `1px solid ${hasManualCovers ? C.teal : (isInferredCovers ? C.amber : C.rule)}`, borderRadius: 2, display: "flex", alignItems: "center", gap: 12 }}>
        <Users size={16} color={hasManualCovers ? C.tealDeep : (isInferredCovers ? C.amberDeep : C.muted)} style={{ flexShrink: 0 }} />
        {editingCovers ? <>
          <span className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>COPERTI</span>
          <input type="number" min="0" max="200" value={editValue} onChange={e => setEditValue(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") handleSaveCovers(); if (e.key === "Escape") { setEditingCovers(false); setEditValue(String(receipt.covers || 0)); } }}
            autoFocus style={{ width: 70, padding: "4px 8px", border: `1px solid ${C.ink}`, borderRadius: 2, fontFamily: "JetBrains Mono, monospace", fontSize: 14, fontWeight: 600 }} />
          <Btn size="sm" variant="primary" onClick={handleSaveCovers}>Salva</Btn>
          <Btn size="sm" variant="ghost" onClick={() => { setEditingCovers(false); setEditValue(String(receipt.covers || 0)); }}>Annulla</Btn>
        </> : <>
          <span className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>COPERTI</span>
          <span className="f-display" style={{ fontSize: 20, fontWeight: 600, color: C.ink }}>{receipt.covers}</span>
          {hasManualCovers && <span className="f-mono" style={{ fontSize: 9, padding: "2px 6px", background: C.teal, color: C.paper, letterSpacing: "0.1em", borderRadius: 2, fontWeight: 700 }} title={`Modificato manualmente da ${receipt._coversManualBy || "—"} il ${receipt._coversManualAt ? new Date(receipt._coversManualAt).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—"} · Valore importato: ${receipt._coversOriginal}`}>MODIFICATO MANUALMENTE</span>}
          {!hasManualCovers && isInferredCovers && <span className="f-mono" style={{ fontSize: 9, padding: "2px 6px", background: C.amber, color: C.paper, letterSpacing: "0.1em", borderRadius: 2, fontWeight: 700 }} title="TCPOS non ha emesso la riga COPERTO Cena per questo scontrino. Il numero e' stato dedotto dagli articoli MENU CONCORDATO. Se sono più persone, modificalo manualmente.">DEDOTTO</span>}
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="ghost" icon={Edit2} onClick={() => { setEditValue(String(receipt.covers || 0)); setEditingCovers(true); }}>Modifica</Btn>
          {hasManualCovers && <Btn size="sm" variant="ghost" onClick={handleResetCovers} title={`Ripristina al valore originale dell'import: ${receipt._coversOriginal}`}>↺ Ripristina importato ({receipt._coversOriginal})</Btn>}
        </>}
      </div>}

      {/* Widget CLIENTE (slice "Riconosci Cliente" T1+T2) — definito in ⟦CRM-UI-P0⟧ */}
      <ReceiptCustomerWidget receipt={receipt} state={state} setState={setState} />

      {/* Riquadro MEZZE PORZIONI — visibile solo se il receipt ha dati dal pre-scan VARIANTE */}
      {receipt._mezzePorzioni && receipt._mezzePorzioni.length > 0 && (() => {
        const autoCount = receipt._mezzePorzioni.filter(mp => mp.appliedAutomatically).length;
        return <div style={{ marginBottom: 14, padding: 12, background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2 }}>
          <div className="f-mono" style={{ fontSize: 10, color: C.tealDeep, letterSpacing: "0.2em", fontWeight: 600, marginBottom: 8 }}>
            MEZZE PORZIONI · {receipt._mezzePorzioni.length} rilevate · {autoCount} applicate automaticamente
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {receipt._mezzePorzioni.map((mp, idx) => (
              <div key={idx} style={{ padding: "6px 8px", background: C.white, border: `1px solid ${C.teal}`, borderRadius: 2, fontSize: 11 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: mp.targetNotFound ? 4 : 0 }}>
                  <span className="f-mono" style={{ fontWeight: 600, color: C.tealDeep, minWidth: 36 }}>{mp.tipo || "?"}</span>
                  {mp.articoloTarget
                    ? <span className="f-body" style={{ flex: 1, color: C.inkSoft }}>{mp.articoloTarget}</span>
                    : <span style={{ flex: 1, color: C.muted, fontStyle: "italic" }}>articolo non identificato</span>}
                </div>
                {mp.alreadyDiscounted && (
                  <div style={{ fontSize: 10, color: C.tealDeep, marginTop: 2 }}>
                    Sconto già presente nel doc TCPOS — nessuna modifica al prezzo.
                  </div>
                )}
                {mp.appliedAutomatically && (
                  <div style={{ fontSize: 10, color: C.inkSoft, marginTop: 2 }}>
                    Prezzo intero: <span className="f-mono">{fmt.eur(mp.oldPrice)}</span>
                    {" "}&rarr;{" "}
                    Prezzo mezza: <span className="f-mono" style={{ fontWeight: 600, color: C.tealDeep }}>{fmt.eur(mp.newPrice)}</span>
                    <span style={{ color: C.muted }}> (sconto -{fmt.eur(mp.scontoAtteso)} applicato in automatico)</span>
                  </div>
                )}
                {mp.targetNotFound && (
                  <div style={{ fontSize: 10, color: C.amberDeep, marginTop: 2 }}>
                    Articolo target non trovato — prezzo non modificato. Verifica il nome dell'articolo nel CSV.
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>;
      })()}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20, padding: 14, background: C.paperDeep, borderRadius: 2 }}>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>INCASSO</div>
          <div className="f-mono" style={{ fontSize: 16, fontWeight: 600, color: C.ink }}>{fmt.eur(revenueDisplay)}</div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.terra, letterSpacing: "0.2em" }}>COSTO M.P.</div>
          <div className="f-mono" style={{ fontSize: 16, fontWeight: 600, color: C.terra }}>{fmt.eur(costDisplay)}</div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.olive, letterSpacing: "0.2em" }}>MARGINE</div>
          <div className="f-mono" style={{ fontSize: 16, fontWeight: 600, color: C.oliveDeep }}>{fmt.eur(margin)}</div>
        </div>
        <div>
          <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.2em" }}>F.C.</div>
          <div className="f-mono" style={{ fontSize: 16, fontWeight: 600, color: fcPct > 35 ? C.terra : fcPct > 28 ? C.amber : C.oliveDeep }}>{fcPct.toFixed(1)}%</div>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, marginBottom: 8 }}>PIATTI ORDINATI</div>
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
          {receipt.items.map((it, i) => <div key={i} style={{ display: "grid", gridTemplateColumns: "3fr 0.3fr 0.8fr 0.8fr", padding: "8px 14px", borderTop: i > 0 ? `1px solid ${C.ruleSoft}` : "none", alignItems: "center", fontSize: 12 }}>
            <div>
              <div className="f-body" style={{ color: C.ink, fontWeight: 500, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                {it.name}
                {it._mezzaPorzioneType && <span style={{ background: C.teal, color: C.paper, fontSize: 8, padding: "1px 5px", borderRadius: 2, fontWeight: 700, letterSpacing: "0.08em" }}>mezza porz.</span>}
              </div>
              {it._mezzaPorzioneType && it._originalPrice != null && (
                <div className="f-mono" style={{ fontSize: 10, color: C.muted, fontStyle: "italic", marginTop: 2 }}>
                  prezzo intero: {fmt.eur(it._originalPrice)}
                </div>
              )}
              {it.note && <div className="f-body" style={{ fontSize: 10, color: C.muted, fontStyle: "italic", marginTop: 2 }}>📝 {it.note}</div>}
            </div>
            <span className="f-mono" style={{ color: C.ink, fontWeight: 600, textAlign: "right" }}>× {it.qty}</span>
            <span className="f-mono" style={{ color: C.muted, textAlign: "right" }}>{fmt.eur(it.unitPrice)}</span>
            <span className="f-mono" style={{ color: C.ink, fontWeight: 600, textAlign: "right" }}>{fmt.eur(it.qty * it.unitPrice)}</span>
          </div>)}
          <div style={{ padding: "10px 14px", background: C.paperDeep, borderTop: `1px solid ${C.rule}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted }}>TOTALE</span>
            <span className="f-mono" style={{ fontSize: 16, fontWeight: 600, color: C.ink }}>{fmt.eur(receipt.total)}</span>
          </div>

          {/* Breakdown per documento fiscale — visibile solo se lo scontrino è
              il risultato di una fusione MENU CONCORDATO. Mostra ogni doc TCPOS
              originale con n articoli e subtotale. Permette ad Andi di verificare
              che la somma torni esattamente al CSV TCPOS. */}
          {receipt._menuConcordatoMerged && receipt._menuConcordatoMerged > 1 && (() => {
            const byDoc = new Map();
            (receipt.items || []).forEach(it => {
              const k = it._sourceFiscalNumber || receipt.fiscalNumber || "?";
              if (!byDoc.has(k)) byDoc.set(k, { items: 0, subtotal: 0 });
              const g = byDoc.get(k);
              g.items += Number(it.qty) || 0;
              g.subtotal += (Number(it.qty) || 0) * (Number(it.unitPrice) || 0);
            });
            const entries = Array.from(byDoc.entries()).sort((a, b) => Number(a[0]) - Number(b[0]));
            const sum = entries.reduce((s, [, v]) => s + v.subtotal, 0);
            return <div style={{ padding: "10px 14px", background: C.tealWash + "55", borderTop: `1px solid ${C.teal}`, fontSize: 11 }}>
              <div className="f-mono" style={{ fontSize: 9, color: C.tealDeep, letterSpacing: "0.15em", marginBottom: 6, fontWeight: 600 }}>
                ⛓ AUDIT FUSIONE — {entries.length} DOCUMENTI TCPOS UNITI
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "0.7fr 1fr 1fr", gap: 6 }}>
                {entries.map(([fn, v]) => (
                  <React.Fragment key={fn}>
                    <span className="f-mono" style={{ color: C.tealDeep, fontWeight: 600 }}>doc #{fn}</span>
                    <span className="f-mono" style={{ color: C.muted }}>{v.items} articoli</span>
                    <span className="f-mono" style={{ color: C.ink, textAlign: "right", fontWeight: 600 }}>{fmt.eur(v.subtotal)}</span>
                  </React.Fragment>
                ))}
                <span className="f-mono" style={{ color: C.tealDeep, fontWeight: 700, gridColumn: "1 / 3", borderTop: `1px solid ${C.teal}`, paddingTop: 4, marginTop: 2 }}>Σ doc</span>
                <span className="f-mono" style={{ color: C.tealDeep, textAlign: "right", fontWeight: 700, borderTop: `1px solid ${C.teal}`, paddingTop: 4, marginTop: 2 }}>{fmt.eur(sum)}</span>
              </div>
              {Math.abs(sum - receipt.total) > 0.01 && (
                <div style={{ marginTop: 6, padding: 6, background: C.terraWash, color: C.terraDeep, fontSize: 10, borderRadius: 2 }}>
                  ⚠ Somma doc ({fmt.eur(sum)}) ≠ totale receipt ({fmt.eur(receipt.total)}) — discrepanza {fmt.eur(sum - receipt.total)}. Segnala a sviluppo.
                </div>
              )}
            </div>;
          })()}

          {/* Box sconto globale: visibile solo se c'è uno sconto significativo (>1%)
              o se ci sono articoli regalo / menu concordato sull'ordine */}
          {disc.hasData && (disc.discount > 0.5 || receipt._menuConcordatoMerged || receipt.items.some(it => it._isGift)) && (
            <div style={{ padding: "10px 14px", background: C.terraWash + "55", borderTop: `1px solid ${C.terra}`, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, fontSize: 11 }}>
              <div>
                <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>VAL. LISTINO</div>
                <div className="f-mono" style={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>{fmt.eur(disc.listinoTotal)}</div>
              </div>
              <div>
                <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>SCONTO APPLICATO</div>
                <div className="f-mono" style={{ fontSize: 13, color: C.terraDeep, fontWeight: 600 }}>{fmt.eur(disc.discount)}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="f-mono" style={{ fontSize: 9, color: C.muted, letterSpacing: "0.15em" }}>SCONTO %</div>
                <div className="f-mono" style={{ fontSize: 13, color: C.terraDeep, fontWeight: 700 }}>{(disc.discountPct * 100).toFixed(1)}%</div>
              </div>
              {receipt._menuConcordatoMerged && (
                <div style={{ gridColumn: "1 / -1", fontSize: 10, color: C.muted, marginTop: 2, fontStyle: "italic" }}>
                  ⛓ Scontrino unito da {receipt._menuConcordatoMerged} documenti TCPOS (menu concordato)
                </div>
              )}
              {receipt.items.some(it => it._isGift) && (
                <div style={{ gridColumn: "1 / -1", fontSize: 10, color: C.muted, marginTop: 2, fontStyle: "italic" }}>
                  🎁 Contiene articoli regalo (non incassati)
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {consumption.length > 0 && <div>
        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, marginBottom: 8 }}>CONSUMO MATERIA PRIMA</div>
        <div style={{ background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
          {consumption.sort((a, b) => b.cost - a.cost).map((c, i) => <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.8fr", padding: "6px 14px", borderTop: i > 0 ? `1px solid ${C.ruleSoft}` : "none", fontSize: 11, alignItems: "center" }}>
            <span className="f-body" style={{ color: C.ink }}>{c.bp.display || c.bp.nome}</span>
            <span className="f-mono" style={{ color: C.inkSoft, textAlign: "right" }}>{c.qty.toFixed(3)} {c.bp.unit || "kg"}</span>
            <span className="f-mono" style={{ color: C.terra, fontWeight: 500, textAlign: "right" }}>{fmt.eur(c.cost)}</span>
          </div>)}
        </div>
      </div>}

      <div className="f-body" style={{ fontSize: 10, color: C.muted, marginTop: 16, textAlign: "center" }}>Operatore: {receipt.operator || "—"}</div>
    </div>
  </Modal>;
}

// Stampa scontrino formato 80mm (stampanti termiche)
function printThermal(receipt, state) {
  const company = state.settings?.companyName || "Bottega del Mare";
  const address = state.settings?.companyAddress || "Polverigi (AN)";
  const dateF = new Date(receipt.closedAt).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
  const w = window.open("", "_blank", "width=320,height=600");
  if (!w) { alert("Consenti i popup per stampare"); return; }
  const htmlItems = receipt.items.map(it => `
    <div class="item">
      <div class="item-row"><span class="qty">${it.qty}x</span> <span class="name">${it.name}</span></div>
      ${it.note ? `<div class="note">  → ${it.note}</div>` : ""}
      <div class="price-row"><span>${(it.qty).toFixed(0)} × ${it.unitPrice.toFixed(2)}</span> <span>€ ${(it.qty * it.unitPrice).toFixed(2)}</span></div>
    </div>`).join("");
  const notes = receipt.notes ? `<div class="notes"><b>NOTE TAVOLO:</b><br/>${receipt.notes}</div>` : "";
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Scontrino T${receipt.number}</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; }
  body { width: 80mm; margin: 0; padding: 3mm; font-family: 'Courier New', monospace; font-size: 11px; color: #000; }
  .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 4px; margin-bottom: 6px; }
  .title { font-size: 14px; font-weight: bold; letter-spacing: 1px; }
  .subtitle { font-size: 9px; margin-top: 2px; }
  .meta { margin: 6px 0; font-size: 10px; }
  .meta > div { display: flex; justify-content: space-between; }
  .notes { margin: 6px 0; padding: 4px; border: 1px dashed #000; font-size: 10px; }
  .divider { border-top: 1px dashed #000; margin: 6px 0; }
  .item { margin-bottom: 4px; }
  .item-row { display: flex; gap: 4px; }
  .qty { font-weight: bold; min-width: 20px; }
  .name { flex: 1; }
  .note { font-size: 9px; font-style: italic; color: #444; padding-left: 12px; }
  .price-row { display: flex; justify-content: space-between; font-size: 9px; color: #444; padding-left: 20px; }
  .total-block { margin-top: 8px; padding-top: 4px; border-top: 2px solid #000; }
  .total-row { display: flex; justify-content: space-between; font-weight: bold; font-size: 14px; }
  .footer { margin-top: 12px; text-align: center; font-size: 9px; }
  @media print { body { width: 80mm; } }
</style>
</head><body onload="window.print(); setTimeout(function(){window.close();}, 300);">
<div class="header">
  <div class="title">${company}</div>
  <div class="subtitle">${address}</div>
</div>
<div class="meta">
  <div><span>Tavolo:</span><b>${receipt.number}</b></div>
  <div><span>Coperti:</span><b>${receipt.covers}</b></div>
  <div><span>Data:</span><span>${dateF}</span></div>
  <div><span>Operatore:</span><span>${receipt.operator || "-"}</span></div>
</div>
${notes}
<div class="divider"></div>
${htmlItems}
<div class="total-block">
  <div class="total-row"><span>TOTALE</span><span>€ ${receipt.total.toFixed(2)}</span></div>
  ${receipt.covers > 0 ? `<div style="display:flex;justify-content:space-between;font-size:9px;margin-top:3px"><span>per coperto</span><span>€ ${(receipt.total / receipt.covers).toFixed(2)}</span></div>` : ""}
</div>
<div class="footer">
  Grazie per la visita!<br/>
  ${new Date().toLocaleString("it-IT")}
</div>
</body></html>`);
  w.document.close();
}


// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      BACKUP & GESTIONE DATI                                ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
// ═══════════════════════════════════════════════════════════════════════════
// W2 — SYNC CLIENT multi-dispositivo (ADR_SYNC_ONLINE_CHANNEL_PROPOSAL, ACCEPTED
// 2026-07-06). La PWA resta offline-first: il sync è un POSTINO in background,
// mai un prerequisito. Regole:
//   • si sincronizzano SOLO gli store dell'Agenda (D2: mai tableReceipts);
//   • conflitti: STESSA regola LWW del server (version, poi updatedAt) — il
//     locale vince a parità: un pull non riscrive mai ciò che è già identico;
//   • config PER DISPOSITIVO in localStorage (token, cursore) — mai nello state
//     di dominio, mai nei backup, mai sincronizzata;
//   • inbound difensivo: solo store noti, payload con id coerente, sostituzione
//     record-intero — mai merge parziale, mai delete fisico (i tombstone restano
//     record come da dominio);
//   • nessun segreto nel codice: il token si incolla in Backup & dati.
const SYNC_CLIENT_STORES = ["reservations", "reservationReceiptLinks", "floorAreas", "floorTables", "customers", "customerReceiptLinks", "blocklist", "demandModel"];
const SYNC_LS_KEY = "voya_sync_config_v1";
const _syncGetConfig = () => {
  try { return JSON.parse(localStorage.getItem(SYNC_LS_KEY) || "{}") || {}; } catch { return {}; }
};
const _syncSetConfig = (patch) => {
  const next = { ..._syncGetConfig(), ...patch };
  try { localStorage.setItem(SYNC_LS_KEY, JSON.stringify(next)); } catch { /* no-op */ }
  return next;
};
// Outbound: record degli store sincronizzabili modificati dopo lastPushAt
// (updatedAt ISO, confronto lessicografico). lastPushAt assente = prima
// sincronizzazione → si spedisce tutto. Un record senza updatedAt si rispedisce
// sempre: innocuo, il server lo skippa come replay (stessa version).
const _syncCollectOutbound = (state, lastPushAt) => {
  const out = [];
  for (const store of SYNC_CLIENT_STORES) {
    const arr = Array.isArray(state && state[store]) ? state[store] : [];
    for (const r of arr) {
      if (!r || r.id == null) continue;
      const u = String(r.updatedAt || "");
      if (lastPushAt && u && u <= lastPushAt) continue;
      out.push({ store, id: String(r.id), version: Number(r.version) || 1, updatedAt: u, deviceId: String(r.deviceId || ""), tombstone: !!r.tombstone, payload: r });
    }
  }
  return out;
};
// AUDIT-FIX (sync [SY1]): rango dello STATO di una prenotazione per la regola
// "lo stato operativo non regredisce". "booked" (atteso) è il meno avanzato;
// seated/no_show/cancelled sono decisioni ESPLICITE dell'operatore e non devono
// tornare "atteso" per colpa di una modifica concorrente di note/tavolo su un
// altro dispositivo. Solo per lo store reservations.
const _resvStatusRank = (s) => (s == null || s === "booked") ? 0 : 1;

// Inbound: applica i record del pull sullo state con LWW. Il payload sostituisce
// il record per intero (stessa unità di verità del dominio); id sconosciuto =
// insert. ECCEZIONE (SY1): per le prenotazioni, a pari version, se l'inbound
// REGREDIREBBE lo stato del tavolo, si FONDE (campi nuovi dell'altro dispositivo
// + stato locale preservato) e si ri-emette con version+1 → converge ovunque.
const _syncApplyInbound = (state, records) => {
  const byStore = new Map();
  for (const rec of (Array.isArray(records) ? records : [])) {
    if (!rec || !SYNC_CLIENT_STORES.includes(rec.store)) continue;
    const p = rec.payload;
    if (!p || typeof p !== "object" || String(p.id) !== String(rec.id)) continue;
    if (!byStore.has(rec.store)) byStore.set(rec.store, []);
    byStore.get(rec.store).push(rec);
  }
  let next = state;
  let applied = 0;
  for (const [store, recs] of byStore) {
    const arr = Array.isArray(state[store]) ? state[store] : [];
    const idx = new Map(arr.map((r, i) => [String(r && r.id), i]));
    let copy = null;
    for (const rec of recs) {
      const i = idx.has(String(rec.id)) ? idx.get(String(rec.id)) : null;
      const local = i != null ? (copy || arr)[i] : null;
      const lv = Number(local && local.version) || 0;
      const rv = Number(rec.version) || 0;
      const lu = String((local && local.updatedAt) || "");
      const ru = String(rec.updatedAt || "");
      // AUDIT-FIX (sync [SY6]): LWW con TOMBSTONE ASSORBENTE. A pari version una
      // cancellazione non si perde né si annulla per orologio sballato: vince il
      // tombstone. [SY1]: e lo STATO del tavolo non regredisce (vedi sotto).
      if (local) {
        if (lv > rv) continue;
        if (lv === rv) {
          const lt = !!local.tombstone, rt = !!(rec.payload && rec.payload.tombstone);
          if (lt !== rt) { if (lt) continue; /* local cancellato vince: non riapplicare il vivo */ }
          else {
            // Stessa tombstone-ness. SY1: solo le prenotazioni hanno uno stato
            // operativo che non deve regredire.
            if (store === "reservations") {
              const lsr = _resvStatusRank(local.status);
              const rsr = _resvStatusRank(rec.payload && rec.payload.status);
              if (rsr < lsr) {
                // L'inbound riporterebbe indietro lo stato (es. "seduto" → "atteso").
                if (ru > lu) {
                  // …ma è più recente sugli altri campi → FONDI: prendi i suoi
                  // campi, tieni lo stato locale, ri-emetti (version+1) così il
                  // record corretto propaga e converge su tutti i dispositivi.
                  if (!copy) copy = arr.slice();
                  copy[i] = { ...rec.payload, status: local.status, version: lv + 1, updatedAt: nowIso(), deviceId: (local.deviceId || "local") };
                  applied++;
                }
                continue; // local più recente o già fuso: lo stato locale resta
              }
              // rsr >= lsr: nessuna regressione → LWW normale per updatedAt
            }
            if (lu >= ru) continue; // pareggio tiene il locale (idempotenza replay)
          }
        }
      }
      if (!copy) copy = arr.slice();
      if (i != null) copy[i] = rec.payload;
      else { idx.set(String(rec.id), copy.length); copy.push(rec.payload); }
      applied++;
    }
    if (copy) next = { ...(next === state ? state : next), [store]: copy };
  }
  return { state: next, applied };
};
// Runner: push (chunk da 400) poi pull (pagine da 500, max 50 giri di guardia).
// getState/applyInbound sono callback del chiamante (AppInner) — il runner non
// tocca React. Ogni esito finisce in config.lastResult per la UI.
async function _syncRun(getState, applyInbound, opts) {
  const manual = !!(opts && opts.manual);
  const cfg = _syncGetConfig();
  if (!cfg.token) return { skipped: "token non configurato" };
  // Default-ON: chi ha un token sincronizza, salvo spegnimento ESPLICITO dal
  // pannello (enabled === false). Cura anche il caso reale del 07/07: scrittura
  // localStorage del claim persa nel reload immediato su iOS → tablet che
  // contattava il cloud (coda online) ma non scaricava mai.
  if (cfg.enabled === false && !manual) return { skipped: "sync disattivato" };
  const headers = { authorization: `Bearer ${cfg.token}`, "content-type": "application/json" };
  const summary = { at: new Date().toISOString(), pushed: 0, pulled: 0, error: null };
  try {
    const outbound = _syncCollectOutbound(getState(), cfg.lastPushAt || null);
    let maxU = cfg.lastPushAt || "";
    for (let i = 0; i < outbound.length; i += 400) {
      const chunk = outbound.slice(i, i + 400);
      const res = await fetch("/api/sync/push", { method: "POST", headers, body: JSON.stringify({ records: chunk }) });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || !out.ok) throw new Error(out.error || `push HTTP ${res.status}`);
      summary.pushed += Number(out.applied) || 0;
      for (const r of chunk) { if (r.updatedAt && r.updatedAt > maxU) maxU = r.updatedAt; }
    }
    if (outbound.length > 0 && maxU) _syncSetConfig({ lastPushAt: maxU });
    let cursor = Number(cfg.cursor) || 0;
    for (let guard = 0; guard < 50; guard++) {
      const res = await fetch(`/api/sync/pull?since=${cursor}&limit=500`, { headers });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || !out.ok) throw new Error(out.error || `pull HTTP ${res.status}`);
      if (Array.isArray(out.records) && out.records.length > 0) summary.pulled += applyInbound(out.records);
      cursor = Number(out.cursor) || cursor;
      if (!out.hasMore) break;
    }
    _syncSetConfig({ cursor });
  } catch (e) {
    summary.error = e && e.message ? e.message : String(e);
  }
  _syncSetConfig({ lastResult: summary });
  return summary;
}

// ═══════════════════════════════════════════════════════════════════════════
// W2.5 — MODALITÀ ACCOGLIENZA (richiesta Andi 2026-07-06): il dispositivo di
// sala mostra SOLO l'Agenda — niente menu, niente CRM/magazzino/incassi/personale.
// Config PER DISPOSITIVO in localStorage (mai dominio, mai sincronizzata).
// Uscita protetta da PIN (si salva solo l'hash). Difesa da curiosità del
// personale, non da attacchi: i dati davvero sensibili (scontrini/€) sul
// dispositivo di sala NON arrivano proprio (D2 — esclusi dal sync).
const DEVICE_MODE_LS_KEY = "voya_device_mode_v1";
const _dmHash = (s) => {
  let h = 5381;
  const x = String(s);
  for (let i = 0; i < x.length; i++) h = ((h * 33) ^ x.charCodeAt(i)) >>> 0;
  return h.toString(36);
};
const _deviceModeGet = () => {
  try { return JSON.parse(localStorage.getItem(DEVICE_MODE_LS_KEY) || "null"); } catch { return null; }
};
const _deviceModeSet = (mode) => {
  try {
    if (mode) localStorage.setItem(DEVICE_MODE_LS_KEY, JSON.stringify(mode));
    else localStorage.removeItem(DEVICE_MODE_LS_KEY);
  } catch { /* no-op */ }
};

// C1.1-EASY — accoglienza del dispositivo NUOVO: se questo dispositivo non ha
// un token sync, questa card scambia il codice di collegamento (generato dal
// Mac in Backup & dati) con la configurazione completa: sync attiva + eventuale
// Modalità Accoglienza col PIN scelto sul Mac. Un campo, un tocco — la
// "manfrina" resta solo sul Mac di Andi (richiesta 07/07). Sul Mac già
// configurato la card non esiste (c'è il token).
function PairClaimBanner() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [claimErr, setClaimErr] = useState(null);
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem("voya_pair_hide") === "1"; } catch { return false; }
  });
  if (hidden || ((_syncGetConfig() || {}).token || "").length > 0) return null;
  const claim = async () => {
    const c = code.trim().toUpperCase().replace(/[^A-Z2-9]/g, "");
    if (c.length < 6) { setClaimErr("Scrivi il codice di 8 lettere che vedi sul Mac."); return; }
    setBusy(true); setClaimErr(null);
    try {
      const res = await fetch("/api/pair/claim", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ code: c }) });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || !out.ok) throw new Error(out.error || `HTTP ${res.status}`);
      _syncSetConfig({ token: out.token, enabled: true });
      if (out.mode === "accoglienza" && out.pinHash) {
        _deviceModeSet({ mode: "accoglienza", pinHash: out.pinHash, activatedAt: new Date().toISOString() });
      }
      // Reload DIFFERITO: su iOS un reload immediato può perdere le scritture
      // localStorage appena fatte (successo il 07/07 col primo tablet).
      setTimeout(() => window.location.reload(), 400);
    } catch (e) { setClaimErr(String(e && e.message || e)); setBusy(false); }
  };
  return <div className="fade-in" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "14px 18px", marginBottom: 18, background: C.amberWash, border: `2px solid ${C.amber}`, borderRadius: 3 }}>
    <div style={{ flex: 1, minWidth: 220 }}>
      <div className="f-mono" style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: C.amberDeep, fontWeight: 700 }}>Dispositivo nuovo?</div>
      <div className="f-body" style={{ fontSize: 12.5, color: C.inkMid, marginTop: 2 }}>
        Genera un codice dal Mac (Backup &amp; dati → Prepara un dispositivo) e scrivilo qui: sincronizzazione e Agenda si configurano da sole.
      </div>
      {claimErr && <div className="f-body" style={{ fontSize: 12, color: C.terraDeep, marginTop: 4 }}>{claimErr}</div>}
    </div>
    <input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="CODICE" maxLength={12}
      autoCapitalize="characters" autoCorrect="off" spellCheck={false}
      className="f-mono" style={{ fontSize: 18, letterSpacing: "0.2em", padding: "12px 12px", width: 180, textTransform: "uppercase", border: `1px solid ${C.rule}`, borderRadius: 3, background: C.white, color: C.ink }} />
    <Btn variant="accent" onClick={claim} disabled={busy}>{busy ? "Collego…" : "Collega"}</Btn>
    <Btn size="xs" variant="ghost" onClick={() => { setHidden(true); try { sessionStorage.setItem("voya_pair_hide", "1"); } catch { /* no-op */ } }}>Nascondi</Btn>
  </div>;
}

// Guscio a schermo pieno per la sala: barra minima (titolo + lucchetto) e SOLO
// AgendaView. Il sync resta vivo (gira in AppInner, sopra questo guscio).
// AUDIT-FIX (visibilità sync, ispettore #4 [C3]): in Accoglienza un sync fermo
// era invisibile (il diagnostico stava solo nel pannello del Mac). Pallino di
// stato nella barra: verde = sincronizzato di recente, giallo = in ritardo,
// rosso = errore/fermo da troppo. Solo lettura di lastResult, tick leggero.
function SyncHealthDot() {
  const [, force] = useState(0);
  useEffect(() => { const t = setInterval(() => force(n => n + 1), 20000); return () => clearInterval(t); }, []);
  const cfg = _syncGetConfig() || {};
  const lr = cfg.lastResult || null;
  const ageMin = lr && lr.at ? (Date.now() - Date.parse(lr.at)) / 60000 : null;
  let color = C.muted, label = "sync in attesa";
  if (lr && lr.error) { color = C.terra; label = "sync bloccato"; }
  else if (ageMin != null) {
    if (ageMin <= 3) { color = C.olive; label = "sincronizzato"; }
    else if (ageMin <= 15) { color = C.amber; label = `sync ${Math.round(ageMin)} min fa`; }
    else { color = C.terra; label = `sync fermo da ${Math.round(ageMin)} min`; }
  }
  return <span title={label} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <span style={{ width: 9, height: 9, borderRadius: "50%", background: color, flexShrink: 0 }} />
    <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.12em", color: C.muted, textTransform: "uppercase" }}>{label}</span>
  </span>;
}

function AccoglienzaShell({ state, setState, onlineReqs, refreshOnlineReqs }) {
  const [askPin, setAskPin] = useState(false);
  const [pin, setPin] = useState("");
  const [pinErr, setPinErr] = useState(false);
  const tryExit = () => {
    const cfg = _deviceModeGet() || {};
    if (pin.trim() && _dmHash(pin.trim()) === cfg.pinHash) { _deviceModeSet(null); window.location.reload(); }
    else { setPinErr(true); setPin(""); }
  };
  return <div className="f-body" style={{ minHeight: "100vh", background: C.paper, color: C.ink }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 18px", borderBottom: `1px solid ${C.rule}`, background: C.white, position: "sticky", top: 0, zIndex: 20 }}>
      <span className="f-display" style={{ fontSize: 15, fontWeight: 600 }}>Da Nialtri · Agenda</span>
      <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase" }}>Accoglienza</span>
      <div style={{ flex: 1 }} />
      <SyncHealthDot />
      <Btn size="xs" variant="ghost" onClick={() => { setAskPin(v => !v); setPinErr(false); setPin(""); }}>🔒</Btn>
    </div>
    {askPin && <div className="fade-in" style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 18px", background: C.paperDeep, borderBottom: `1px solid ${C.rule}` }}>
      <span className="f-body" style={{ fontSize: 12, color: C.inkMid }}>PIN per uscire dalla modalità:</span>
      <input type="password" inputMode="numeric" autoFocus value={pin}
        onChange={e => { setPin(e.target.value); setPinErr(false); }}
        onKeyDown={e => { if (e.key === "Enter") tryExit(); }}
        style={{ width: 110, padding: "6px 10px", fontSize: 14, border: `1px solid ${pinErr ? C.terra : C.rule}`, borderRadius: 2, background: C.white, color: C.ink }} />
      <Btn size="xs" variant="accent" onClick={tryExit}>Sblocca</Btn>
      {pinErr && <span className="f-body" style={{ fontSize: 11, color: C.terraDeep }}>PIN errato</span>}
    </div>}
    <div style={{ padding: 18, maxWidth: 1100, margin: "0 auto" }}>
      <AgendaView state={state} setState={setState} onlineReqs={onlineReqs} refreshOnlineReqs={refreshOnlineReqs} />
    </div>
  </div>;
}

function BackupDati({ state, setState }) {
  const toast = useToast();
  const fileRef = useRef();
  const [preview, setPreview] = useState(null);
  // W2 — configurazione sync di QUESTO dispositivo (localStorage, mai dominio).
  const [syncCfg, setSyncCfgState] = useState(_syncGetConfig);
  const [syncBusy, setSyncBusy] = useState(false);
  const saveSyncCfg = (patch) => setSyncCfgState(_syncSetConfig(patch));
  const handleSyncNow = async () => {
    if (!window.__voyaSync) return;
    setSyncBusy(true);
    const r = await window.__voyaSync.run(true);
    setSyncBusy(false);
    setSyncCfgState(_syncGetConfig());
    if (r.skipped) toast({ type: "info", title: "Sync", message: r.skipped });
    else if (r.error) toast({ type: "error", title: "Sync fallito", message: r.error });
    else toast({ type: "success", title: "Sync completato", message: `${r.pushed} inviati · ${r.pulled} ricevuti` });
  };
  // Salute sync — stato dispositivi dal server (timbri last_seen/pull/push,
  // mai token). Caricato a richiesta col bottone, nessun polling.
  const [devStatus, setDevStatus] = useState(null);
  const [devBusy, setDevBusy] = useState(false);
  const handleDevStatus = async () => {
    setDevBusy(true);
    try {
      const res = await fetch("/api/sync/status", { headers: { authorization: `Bearer ${(syncCfg.token || "").trim()}` } });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || !out.ok) throw new Error(out.error || `HTTP ${res.status}`);
      setDevStatus(out.devices);
    } catch (e) {
      toast({ type: "error", title: "Stato non disponibile", message: String(e && e.message || e) });
    }
    setDevBusy(false);
  };
  // C1.1-EASY — generatore codice di collegamento (dal Mac): il server mette
  // il token del dispositivo target DENTRO il codice; qui viaggia solo il nome
  // del dispositivo e il PIN già hashato. Stato solo runtime.
  const [pairName, setPairName] = useState("tablet-sala");
  const [pairPin, setPairPin] = useState("");
  const [pairOut, setPairOut] = useState(null);
  const [pairBusy, setPairBusy] = useState(false);
  const handlePairCreate = async () => {
    const pin = pairPin.trim();
    if (pin && pin.length < 4) { toast({ type: "error", title: "PIN troppo corto", message: "Scegli un PIN di almeno 4 caratteri (o lascialo vuoto per un dispositivo senza blindatura)." }); return; }
    setPairBusy(true); setPairOut(null);
    try {
      const res = await fetch("/api/pair/create", {
        method: "POST",
        headers: { authorization: `Bearer ${(syncCfg.token || "").trim()}`, "content-type": "application/json" },
        body: JSON.stringify({ deviceName: pairName.trim(), mode: pin ? "accoglienza" : null, pinHash: pin ? _dmHash(pin) : null }),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || !out.ok) throw new Error(out.error || `HTTP ${res.status}`);
      setPairOut(out);
    } catch (e) {
      toast({ type: "error", title: "Codice non generato", message: String(e && e.message || e) });
    }
    setPairBusy(false);
  };
  // W2.5 — attivazione Modalità Accoglienza per QUESTO dispositivo (PIN → hash).
  const [accPin, setAccPin] = useState("");
  const activateAccoglienza = () => {
    const p = accPin.trim();
    if (p.length < 4) { toast({ type: "error", title: "PIN troppo corto", message: "Scegli un PIN di almeno 4 caratteri." }); return; }
    _deviceModeSet({ mode: "accoglienza", pinHash: _dmHash(p), activatedAt: new Date().toISOString() });
    window.location.reload();
  };
  // R6 — interruttore pannelli bootstrap Agenda: la PWA installata non ha barra
  // dell'indirizzo per ?bootstrap=1. sessionStorage (mai stato di dominio): si
  // spegne da solo alla chiusura dell'app. L'Agenda lo legge a ogni render.
  const [bootPanels, setBootPanels] = useState(() => {
    try { return sessionStorage.getItem("voya_bootstrap_panels") === "1"; } catch { return false; }
  });
  const toggleBootPanels = () => {
    const next = !bootPanels;
    try {
      if (next) sessionStorage.setItem("voya_bootstrap_panels", "1");
      else sessionStorage.removeItem("voya_bootstrap_panels");
    } catch { /* no-op */ }
    setBootPanels(next);
    toast({ type: "info", title: "Pannelli bootstrap", message: next
      ? "Attivi — vai in Agenda e scorri in fondo alla pagina."
      : "Nascosti. Si spengono comunque da soli alla chiusura dell'app." });
  };

  const stats = {
    fornitori: state.suppliers?.length || 0,
    prodottiBase: state.baseProducts?.length || 0,
    piatti: state.dishesReal?.length || 0,
    lotti: state.lots?.length || 0,
    ordiniAcquisto: state.purchaseOrders?.length || 0,
    fatture: state.invoices?.length || 0,
    operatori: state.users?.length || 0,
    tempLogs: state.haccpLogs?.temperature?.length || 0,
    cleanLogs: state.haccpLogs?.cleaning?.length || 0,
    messaggi: state.messages?.length || 0,
  };

  const exportJson = () => {
    const backupTs = nowIso();
    // Aggiorna lastBackupAt nello state prima di serializzare il dump,
    // così il campo è già presente nel backup e persiste nello state locale.
    const stateWithBackup = { ...state, lastBackupAt: backupTs };
    setState(stateWithBackup);
    const dump = {
      exportedAt: backupTs,
      appVersion: "bottega_v6",
      state: stateWithBackup,
    };
    const blob = new Blob([JSON.stringify(dump, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const d = new Date();
    const fname = `bottega-backup-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,"0")}${String(d.getDate()).padStart(2,"0")}-${String(d.getHours()).padStart(2,"0")}${String(d.getMinutes()).padStart(2,"0")}.json`;
    a.download = fname;
    a.click();
    URL.revokeObjectURL(url);
    toast({ type: "success", title: "Backup scaricato", message: fname });
  };

  const exportCsvFornitori = () => {
    const rows = [["Fornitore","P.IVA","Telefono","Email","Termini pagamento","Totale fatturato €"]];
    state.suppliers.forEach(s => {
      const total = state.invoices.filter(i => i.supplierId === s.id).reduce((sum, i) => sum + i.amount, 0);
      rows.push([s.name, s.vat || "", s.phone || "", s.email || "", `${s.paymentTerms || 30}gg`, total.toFixed(2)]);
    });
    downloadCsv(rows, "fornitori");
  };

  const exportCsvFoodcost = () => {
    const rows = [["ID","Nome","Categoria","Prezzo fattura €/kg","Calo 1°","Calo 2°","Calo totale %","Prezzo reale €/kg","Shelf life gg","Min stock","Allergeni"]];
    (state.baseProducts || []).forEach(p => {
      rows.push([p.id, p.nome, p.cat, p.pFatt, p.calo1, p.calo2, (p.caloTot * 100).toFixed(1) + "%", p.pReale.toFixed(4), p.shelfLifeDays, p.minStock, (p.allergens || []).join(";")]);
    });
    downloadCsv(rows, "prodotti-base");
  };

  const exportCsvPiatti = () => {
    const rows = [["ID","Nome","Categoria","Prezzo listino €","Costo reale €","Food cost %","Margine €","N. ingredienti","Mapping %"]];
    (state.dishesReal || []).forEach(d => {
      let cost = 0, mapped = 0;
      (d.ingredienti || []).forEach(ing => {
        const bp = ing.bpId ? state.baseProducts.find(b => b.id === ing.bpId) : null;
        if (bp) { cost += (bp.pReale || bp.pFatt) * ing.qty; mapped++; }
        else cost += ing.prezzo || 0;
      });
      const fcPct = d.prezzo > 0 ? (cost / d.prezzo * 100) : 0;
      const cov = d.ingredienti.length > 0 ? (mapped / d.ingredienti.length * 100) : 0;
      rows.push([d.id, d.nome, d.cat, d.prezzo.toFixed(2), cost.toFixed(2), fcPct.toFixed(1) + "%", (d.prezzo - cost).toFixed(2), d.ingredienti.length, cov.toFixed(0) + "%"]);
    });
    downloadCsv(rows, "piatti");
  };

  // Export Excel riepilogo scontrini per GIORNO + SERVIZIO (pranzo/cena).
  // Legge i dati LIVE da state.tableReceipts: si aggiorna automaticamente a ogni
  // import. Usa la stessa logica della piattaforma (getServiceDate + getShift +
  // esclusione EC riconciliati) così i numeri combaciano con il cruscotto Vendite.
  const exportXlsxRiepilogo = async () => {
    try {
      const XLSX = await loadXLSX();
      const r2 = (x) => Math.round((Number(x) || 0) * 100) / 100;
      const GIORNI = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
      const shiftLabel = (iso) => { const s = getShift(iso); return s === "pranzo" ? "Pranzo" : s === "cena" ? "Cena" : "Fuori servizio"; };
      const agg = {};
      (state.tableReceipts || []).forEach(r => {
        const isEC = !!(r._isPreconto || r._docType === "ESTRATTO CONTO");
        const isECReconciled = isEC && r._regularizedTo && r._regularizedTo !== "NERO_DEFINITIVO";
        if (isECReconciled) return; // coerente con engine.dailyMetrics
        const day = getServiceDate(r.closedAt);
        if (!day) return;
        const sh = shiftLabel(r.closedAt);
        const k = `${day}|${sh}`;
        if (!agg[k]) agg[k] = { day, sh, cov: 0, tot: 0, fisc: 0, ec: 0, n: 0 };
        const tot = Number(r.total) || 0;
        agg[k].cov += Number(r.covers) || 0;
        agg[k].tot += tot;
        agg[k].n += 1;
        if (isEC) agg[k].ec += tot; else agg[k].fisc += tot;
      });
      const order = { "Pranzo": 0, "Cena": 1, "Fuori servizio": 2 };
      const rows = Object.values(agg).sort((a, b) => a.day.localeCompare(b.day) || (order[a.sh] - order[b.sh]));
      // Foglio 1 — Giorno/Servizio. Colonne: ...E=Incasso piattaforma, F=Incasso cassa
      // (vuota, da compilare a mano col totale del report Z), G=Differenza (formula E-F).
      const aoa = [["Data", "Giorno", "Servizio", "Coperti", "Incasso piattaforma €", "Incasso cassa €", "Differenza €", "di cui fiscale €", "di cui preconti €", "N° scontrini", "Ticket medio/coperto €"]];
      rows.forEach(r => {
        const g = GIORNI[new Date(r.day + "T12:00:00").getDay()];
        aoa.push([r.day, g, r.sh, r.cov, r2(r.tot), "", "", r2(r.fisc), r2(r.ec), r.n, r.cov > 0 ? r2(r.tot / r.cov) : 0]);
      });
      const byDay = {};
      rows.forEach(r => { if (!byDay[r.day]) byDay[r.day] = { cov: 0, tot: 0, n: 0 }; byDay[r.day].cov += r.cov; byDay[r.day].tot += r.tot; byDay[r.day].n += r.n; });
      const aoa2 = [["Data", "Giorno", "Coperti", "Incasso piattaforma €", "Incasso cassa €", "Differenza €", "N° scontrini", "Ticket medio/coperto €"]];
      Object.keys(byDay).sort().forEach(d => { const g = GIORNI[new Date(d + "T12:00:00").getDay()]; const b = byDay[d]; aoa2.push([d, g, b.cov, r2(b.tot), "", "", b.n, b.cov > 0 ? r2(b.tot / b.cov) : 0]); });
      const wb = XLSX.utils.book_new();
      const ws1 = XLSX.utils.aoa_to_sheet(aoa);
      // Differenza = Incasso piattaforma − Incasso cassa (vuota finché non compili la cassa)
      for (let i = 2; i <= aoa.length; i++) ws1["G" + i] = { t: "n", f: `IF(F${i}="","",E${i}-F${i})` };
      ws1["!cols"] = [{ wch: 12 }, { wch: 11 }, { wch: 14 }, { wch: 9 }, { wch: 18 }, { wch: 15 }, { wch: 13 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws1, "Giorno-Servizio");
      const ws2 = XLSX.utils.aoa_to_sheet(aoa2);
      for (let i = 2; i <= aoa2.length; i++) ws2["F" + i] = { t: "n", f: `IF(E${i}="","",D${i}-E${i})` };
      ws2["!cols"] = [{ wch: 12 }, { wch: 11 }, { wch: 9 }, { wch: 18 }, { wch: 15 }, { wch: 13 }, { wch: 12 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws2, "Per giorno");
      const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([out], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const d = new Date();
      a.href = url;
      a.download = `bottega-riepilogo-scontrini-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ type: "success", title: "Excel scaricato", message: `${rows.length} righe giorno/servizio · ${a.download}` });
    } catch (e) {
      toast({ type: "error", title: "Export Excel fallito", message: e.message || String(e) });
    }
  };

  function downloadCsv(rows, filename) {
    // sanitizeCsvCell: disinnesca le formule (CWE-1236) + quoting RFC 4180
    const csv = rows.map(r => r.map(sanitizeCsvCell).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const d = new Date();
    a.download = `bottega-${filename}-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,"0")}${String(d.getDate()).padStart(2,"0")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ type: "success", title: "CSV scaricato", message: a.download });
  }

  const handleImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // 1) Validazione file (size + estensione) PRIMA di leggerlo
    const fileCheck = validateBackupFile(file);
    if (!fileCheck.ok) {
      toast({ type: "error", title: "Import fallito", message: fileCheck.error });
      e.target.value = null;
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        // 2) Validazione schema: blocca backup troncati / con array mancanti
        const shapeCheck = validateBackupShape(parsed);
        if (!shapeCheck.ok) {
          toast({ type: "error", title: "Backup non valido", message: shapeCheck.error });
          return;
        }
        setPreview({ fileName: file.name, data: parsed, stats: {
          fornitori: parsed.state.suppliers?.length || 0,
          prodottiBase: parsed.state.baseProducts?.length || 0,
          piatti: parsed.state.dishesReal?.length || 0,
          lotti: parsed.state.lots?.length || 0,
          operatori: parsed.state.users?.length || 0,
        }});
      } catch (err) {
        toast({ type: "error", title: "Import fallito", message: err.message });
      }
    };
    reader.readAsText(file);
    e.target.value = null; // così se lo stesso file viene ri-selezionato triggera di nuovo
  };

  const confirmImport = async () => {
    if (!preview) return;
    // Difesa in profondità: ri-valida lo schema immediatamente prima della
    // sovrascrittura distruttiva (azzera TUTTO lo storico reale).
    const shapeCheck = validateBackupShape(preview.data);
    if (!shapeCheck.ok) {
      toast({ type: "error", title: "Import annullato", message: shapeCheck.error });
      setPreview(null);
      return;
    }
    if (!confirm("Sostituire TUTTI i dati attuali con quelli del backup?\nLo stato attuale viene salvato come copia di sicurezza prima di procedere.")) return;
    try {
      // C4 — Snapshot di sicurezza: salva lo stato corrente prima di sovrascriverlo,
      // così un import sbagliato (file giusto come forma, ma dati sbagliati) è recuperabile.
      if (isIdbAvailable()) {
        await idbSet(`bottega_prod_v6_pre_restore_${Date.now()}`, state);
      }
      // Scrive il backup nello storage e RICARICA: al load gira applyMigration, che
      // normalizza i backup vecchi allo schema corrente (prima veniva saltata).
      if (isIdbAvailable()) {
        await idbSet("bottega_prod_v6", preview.data.state);
      } else {
        localStorage.setItem("bottega_prod_v6", JSON.stringify(preview.data.state));
      }
    } catch (e) {
      toast({ type: "error", title: "Import fallito", message: e.message || String(e) });
      return;
    }
    setPreview(null);
    window.location.reload();
  };

  const resetAll = async () => {
    if (!confirm("Cancellare TUTTO e ripartire dal seed iniziale?\nPerderai tutti i dati operativi (lotti, fatture, ordini).")) return;
    if (!confirm("Sei proprio sicuro? Dopo questo passo non c'è ritorno.")) return;
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        localStorage.removeItem("bottega_prod_v6");
      }
    } catch (e) {}
    // Lo storage attivo è IndexedDB: senza questo, dopo il reload i dati tornavano.
    try {
      if (isIdbAvailable()) await idbDel("bottega_prod_v6");
    } catch (e) { console.warn("Reset IndexedDB fallito:", e); }
    window.location.reload();
  };

  // Banner promemoria backup — calcola età ultimo backup
  const backupReminderBanner = (() => {
    if (!state.lastBackupAt) {
      return { show: true, msg: "Non hai mai esportato un backup — fallo ora per non perdere i dati." };
    }
    const ageMs = Date.now() - new Date(state.lastBackupAt).getTime();
    const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24));
    if (ageDays >= 7) {
      return { show: true, msg: `Ultimo backup: ${ageDays} giorni fa — esportane uno ora per essere al sicuro.` };
    }
    return { show: false, msg: "" };
  })();

  return <div className="fade-up">
    <PageHeader kicker="Impostazioni" title="Backup & dati"
      subtitle="Esporta un backup completo del sistema, importa una versione precedente, o esporta singoli CSV per il commercialista." />

    {/* Promemoria backup — visibile se non è mai stato fatto o se > 7 giorni fa */}
    {backupReminderBanner.show && (
      <div style={{ background: C.amberWash, border: `1px solid ${C.amber}`, borderRadius: 2, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
        <AlertTriangle size={16} color={C.amberDeep} style={{ flexShrink: 0 }} />
        <div className="f-body" style={{ fontSize: 13, color: C.ink, flex: 1 }}>
          <strong>Attenzione backup:</strong> {backupReminderBanner.msg}
        </div>
        <Btn size="xs" variant="accent" icon={Download} onClick={exportJson}>Esporta ora</Btn>
      </div>
    )}

    <div className="stagger" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 28 }}>
      <Metric label="Fornitori" value={stats.fornitori} />
      <Metric label="Prodotti base" value={stats.prodottiBase} />
      <Metric label="Piatti a carta" value={stats.piatti} />
      <Metric label="Lotti" value={stats.lotti} />
      <Metric label="Operatori" value={stats.operatori} />
      <Metric label="Ordini acquisto" value={stats.ordiniAcquisto} />
      <Metric label="Fatture" value={stats.fatture} />
      <Metric label="Temp. HACCP" value={stats.tempLogs} />
      <Metric label="Sanific. HACCP" value={stats.cleanLogs} />
      <Metric label="Messaggi" value={stats.messaggi} />
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>

      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 22, borderRadius: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <Download size={18} color={C.teal} />
          <div className="f-display" style={{ fontSize: 16, color: C.ink, fontWeight: 500 }}>Esporta backup completo</div>
        </div>
        <div className="f-body" style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>Scarica un file JSON con l'intero stato del sistema. Conservalo in un posto sicuro (Drive, chiavetta, email a te stesso): è la tua polizza assicurativa se il browser perde i dati.</div>
        <Btn variant="accent" icon={Download} onClick={exportJson}>Scarica backup JSON</Btn>

        <div style={{ margin: "20px 0 8px", paddingTop: 16, borderTop: `1px solid ${C.ruleSoft}` }}>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, marginBottom: 10 }}>EXPORT CSV SPECIFICI</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Btn variant="ghost" size="sm" icon={FileText} onClick={exportCsvFornitori}>Fornitori & fatturato</Btn>
            <Btn variant="ghost" size="sm" icon={FileText} onClick={exportCsvFoodcost}>Prodotti base & cali</Btn>
            <Btn variant="ghost" size="sm" icon={FileText} onClick={exportCsvPiatti}>Piatti & food cost</Btn>
          </div>
        </div>

        <div style={{ margin: "20px 0 8px", paddingTop: 16, borderTop: `1px solid ${C.ruleSoft}` }}>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, marginBottom: 6 }}>RIEPILOGO SCONTRINI (EXCEL)</div>
          <div className="f-body" style={{ fontSize: 12, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>Tutti gli scontrini caricati, giorno per giorno e servizio per servizio (pranzo/cena): coperti e incasso. Si aggiorna da solo: ogni volta che importi un nuovo CSV scontrini, riscarica il file ed è già aggiornato.</div>
          <Btn variant="ghost" size="sm" icon={Download} onClick={exportXlsxRiepilogo}>Esporta riepilogo giorno/servizio (Excel)</Btn>
        </div>
      </div>

      <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 22, borderRadius: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <Upload size={18} color={C.terra} />
          <div className="f-display" style={{ fontSize: 16, color: C.ink, fontWeight: 500 }}>Importa backup</div>
        </div>
        <div className="f-body" style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>Carica un file JSON di backup esportato in precedenza. Vedrai un'anteprima dei dati prima di confermare.</div>

        <input ref={fileRef} type="file" accept=".json,application/json" onChange={handleImportFile} style={{ display: "none" }} />
        <Btn variant="primary" icon={Upload} onClick={() => fileRef.current?.click()}>Seleziona file JSON</Btn>

        {preview && <div style={{ marginTop: 16, padding: 14, background: C.tealWash, borderRadius: 2, border: `1px solid ${C.teal}` }}>
          <div className="f-mono" style={{ fontSize: 10, color: C.teal, letterSpacing: "0.2em", marginBottom: 8 }}>ANTEPRIMA · {preview.fileName}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, fontSize: 11, marginBottom: 12 }}>
            <div><strong>{preview.stats.fornitori}</strong> fornitori</div>
            <div><strong>{preview.stats.prodottiBase}</strong> prodotti base</div>
            <div><strong>{preview.stats.piatti}</strong> piatti</div>
            <div><strong>{preview.stats.lotti}</strong> lotti</div>
            <div><strong>{preview.stats.operatori}</strong> operatori</div>
            <div>Esportato {fmt.date(preview.data.exportedAt?.slice(0, 10))}</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="ghost" size="sm" onClick={() => setPreview(null)}>Annulla</Btn>
            <Btn variant="accent" size="sm" icon={CheckCircle2} onClick={confirmImport}>Sostituisci dati attuali</Btn>
          </div>
        </div>}

        <div style={{ margin: "20px 0 8px", paddingTop: 16, borderTop: `1px solid ${C.ruleSoft}` }}>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, marginBottom: 10 }}>SINCRONIZZAZIONE DISPOSITIVI · BETA</div>
          <div className="f-body" style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
            Collega questo dispositivo al postino cloud (Agenda, tavoli, clienti — mai gli scontrini).
            Il lasciapassare del dispositivo te lo dà Claude; resta solo su questo dispositivo.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10, marginBottom: 8, maxWidth: 560 }}>
            <Field label="Lasciapassare dispositivo (token)">
              <Input type="password" value={syncCfg.token || ""} placeholder="incolla qui il token…"
                onChange={e => saveSyncCfg({ token: e.target.value.trim() })} />
            </Field>
            <Field label="Sync automatico">
              {/* default-ON col token: "spento" solo se scelto esplicitamente */}
              <Btn size="sm" variant={syncCfg.enabled !== false ? "accent" : "ghost"} onClick={() => saveSyncCfg({ enabled: syncCfg.enabled === false })}>
                {syncCfg.enabled !== false ? "Attivo (ogni minuto)" : "Spento"}
              </Btn>
            </Field>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Btn size="sm" variant="ghost" onClick={handleSyncNow} disabled={syncBusy || !(syncCfg.token || "").length}>
              {syncBusy ? "Sincronizzo…" : "Sincronizza ora"}
            </Btn>
            {syncCfg.lastResult && <span className="f-mono" style={{ fontSize: 10, color: syncCfg.lastResult.error ? C.terraDeep : C.oliveDeep }}>
              {syncCfg.lastResult.error
                ? `ultimo sync fallito: ${syncCfg.lastResult.error}`
                : `ultimo sync ${fmt.time(syncCfg.lastResult.at)} · ${syncCfg.lastResult.pushed} inviati · ${syncCfg.lastResult.pulled} ricevuti`}
            </span>}
          </div>
          {/* Salute sync — "il tablet ha scaricato?": timbri dal server, a richiesta */}
          {(syncCfg.token || "").length > 0 && <div style={{ marginTop: 14 }}>
            <Btn size="sm" variant="ghost" onClick={handleDevStatus} disabled={devBusy}>
              {devBusy ? "Controllo…" : "Stato dispositivi"}
            </Btn>
            {devStatus && <div className="fade-in" style={{ marginTop: 8, maxWidth: 560 }}>
              {devStatus.map(d => {
                const fmtTs = (ts) => ts ? new Date(ts).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "mai";
                return <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "8px 12px", background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, marginBottom: 4, opacity: d.revoked ? 0.5 : 1 }}>
                  <span className="f-body" style={{ fontSize: 12.5, fontWeight: 600, color: C.ink, width: 110 }}>{d.name}{d.revoked ? " (revocato)" : ""}</span>
                  <span className="f-mono" style={{ fontSize: 10.5, color: d.lastPullAt ? C.oliveDeep : C.terraDeep }}>scaricato: {fmtTs(d.lastPullAt)}</span>
                  <span className="f-mono" style={{ fontSize: 10.5, color: C.inkMid }}>inviato: {fmtTs(d.lastPushAt)}</span>
                  <span className="f-mono" style={{ fontSize: 10.5, color: C.muted }}>contatto: {fmtTs(d.lastSeen)}</span>
                </div>;
              })}
              <div className="f-body" style={{ fontSize: 10.5, color: C.muted, fontStyle: "italic" }}>I timbri partono da oggi: "mai" su un dispositivo attivo significa solo che non si è ancora collegato da quando esiste il registro.</div>
            </div>}
          </div>}
          {/* C1.1-EASY — prepara un dispositivo con UN codice (zero manfrina):
              sul tablet basta scriverlo — sync + solo-Agenda + PIN da soli. */}
          {(syncCfg.token || "").length > 0 && <div style={{ marginTop: 16, padding: "12px 14px", background: C.paperDeep, border: `1px dashed ${C.rule}`, borderRadius: 2, maxWidth: 560 }}>
            <div className="f-body" style={{ fontSize: 12, color: C.ink, fontWeight: 500, marginBottom: 8 }}>Prepara un dispositivo nuovo (tablet di sala)</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input value={pairName} onChange={e => setPairName(e.target.value)} placeholder="nome dispositivo"
                style={{ width: 150, padding: "7px 10px", fontSize: 13, border: `1px solid ${C.rule}`, borderRadius: 2, background: C.white, color: C.ink }} />
              <input type="password" inputMode="numeric" value={pairPin} onChange={e => setPairPin(e.target.value)} placeholder="PIN Accoglienza (min 4)"
                style={{ width: 180, padding: "7px 10px", fontSize: 13, border: `1px solid ${C.rule}`, borderRadius: 2, background: C.white, color: C.ink }} />
              <Btn size="sm" variant="accent" onClick={handlePairCreate} disabled={pairBusy}>{pairBusy ? "Genero…" : "Genera codice"}</Btn>
            </div>
            <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
              Col PIN il dispositivo nasce già blindato alla sola Agenda; senza PIN nasce come dispositivo completo. Il codice vale 15 minuti, una volta sola.
            </div>
            {pairOut && <div className="fade-in" style={{ marginTop: 10, padding: "12px 16px", background: C.white, border: `2px solid ${C.olive}`, borderRadius: 3 }}>
              <div className="f-mono" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "0.3em", color: C.ink }}>
                {String(pairOut.code).slice(0, 4)} {String(pairOut.code).slice(4)}
              </div>
              <div className="f-body" style={{ fontSize: 12, color: C.inkMid, marginTop: 6 }}>
                Sul {pairOut.deviceName}: apri l'app → scrivi questo codice nella card "Dispositivo nuovo?" in alto → Collega.
                {pairOut.mode === "accoglienza" ? " Si riavvierà già in Modalità Accoglienza (solo Agenda)." : ""} Scade tra 15 minuti.
              </div>
            </div>}
          </div>}
        </div>

        <div style={{ margin: "20px 0 8px", paddingTop: 16, borderTop: `1px solid ${C.ruleSoft}` }}>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, marginBottom: 10 }}>STRUMENTI AVANZATI</div>
          <Btn variant="ghost" size="sm" icon={Layers} onClick={toggleBootPanels}>
            {bootPanels ? "Nascondi pannelli bootstrap (Agenda)" : "Mostra pannelli bootstrap (Agenda)"}
          </Btn>
          <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
            Pannelli di manutenzione (import Pienissimo, tavoli, posti, audit): compaiono in fondo all'Agenda. Si nascondono da soli alla chiusura dell'app.
          </div>
          {/* W2.5 — Modalità Accoglienza: blinda QUESTO dispositivo alla sola Agenda.
              Pensata per il tablet di sala; per uscire serve il PIN scelto qui. */}
          <div style={{ marginTop: 16 }}>
            <div className="f-body" style={{ fontSize: 12, color: C.ink, marginBottom: 6, fontWeight: 500 }}>Modalità Accoglienza — questo dispositivo mostrerà SOLO l'Agenda</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input type="password" inputMode="numeric" placeholder="scegli un PIN (min 4)" value={accPin}
                onChange={e => setAccPin(e.target.value)}
                style={{ width: 170, padding: "7px 10px", fontSize: 13, border: `1px solid ${C.rule}`, borderRadius: 2, background: C.white, color: C.ink }} />
              <Btn size="sm" variant="accent" onClick={activateAccoglienza}>Attiva su questo dispositivo</Btn>
            </div>
            <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
              Per il tablet di sala: niente menu, niente CRM/incassi/magazzino — solo Agenda. Si esce col PIN (lucchetto in alto a destra). Il sync continua a funzionare.
            </div>
          </div>
        </div>

        <div style={{ margin: "20px 0 8px", paddingTop: 16, borderTop: `1px solid ${C.ruleSoft}` }}>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.terra, marginBottom: 10 }}>ZONA PERICOLOSA</div>
          <Btn variant="danger" size="sm" icon={Trash2} onClick={resetAll}>Cancella tutto e ricarica dal seed</Btn>
        </div>
      </div>

    </div>

    <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 22, borderRadius: 2, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <Settings size={18} color={C.ink} />
        <div className="f-display" style={{ fontSize: 16, color: C.ink, fontWeight: 500 }}>Impostazioni IVA</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
        <Field label="IVA vendita piatti (%)" hint="Default 10% ristorazione">
          <Input type="number" step="1" min="0" max="30" value={(state.settings?.vatRateSale || 0.10) * 100}
            onChange={e => {
              const v = parseFloat(e.target.value) || 0;
              setState({ ...state, settings: { ...(state.settings || {}), vatRateSale: v / 100 } });
              toast({ type: "success", message: `IVA vendita aggiornata a ${v}%` });
            }} />
        </Field>
        <Field label="IVA acquisti (%)" hint="Default 10% prodotti alimentari">
          <Input type="number" step="1" min="0" max="30" value={(state.settings?.vatRatePurchase || 0.10) * 100}
            onChange={e => {
              const v = parseFloat(e.target.value) || 0;
              setState({ ...state, settings: { ...(state.settings || {}), vatRatePurchase: v / 100 } });
              toast({ type: "success", message: `IVA acquisti aggiornata a ${v}%` });
            }} />
        </Field>
        <Field label="Modalità default visualizzazione">
          <Select value={state.settings?.priceDisplay || "gross"}
            onChange={e => setState({ ...state, settings: { ...(state.settings || {}), priceDisplay: e.target.value } })}>
            <option value="gross">Lordo (IVA inclusa)</option>
            <option value="net">Netto (IVA esclusa)</option>
          </Select>
        </Field>
        <Field label="Oneri di trasformazione (manodopera+energia) % sul prezzo" hint="0 = disattivato. Es. 8 = scala l'8% del prezzo netto come costo di lavorazione (cottura, marinatura). Alimenta solo il MARGINE NETTO, non il margine di contribuzione.">
          <Input type="number" step="0.5" min="0" max="60"
            value={(state.settings?.oneriTrasformazionePct ?? 0)}
            onChange={e => {
              const v = Math.max(0, Math.min(60, parseFloat(e.target.value) || 0));
              setState({ ...state, settings: { ...(state.settings || {}), oneriTrasformazionePct: v } });
              toast({ type: "success", message: v > 0 ? `Oneri di trasformazione: ${v}% del prezzo netto` : "Oneri di trasformazione disattivati" });
            }} />
        </Field>
        <Field label="Orario taglio pranzo → cena" hint="Ora (12–23) in cui finisce il pranzo e inizia la cena. Default 18. Abbassalo (es. 17) se i tavoli di gruppo che chiudono nel pomeriggio devono contare come cena. Cambia solo COME vengono divisi i turni, non l'incasso totale.">
          <Input type="number" step="1" min="12" max="23"
            value={(state.settings?.serviceShift?.cenaFrom ?? 18)}
            onChange={e => {
              const v = Math.max(12, Math.min(23, parseInt(e.target.value, 10) || 18));
              const prev = state.settings?.serviceShift || {};
              setState({ ...state, settings: { ...(state.settings || {}), serviceShift: { pranzoFrom: prev.pranzoFrom ?? 9, cenaFrom: v, nightEnd: prev.nightEnd ?? 4 } } });
              toast({ type: "success", message: `Taglio pranzo/cena impostato alle ${v}:00` });
            }} />
        </Field>
      </div>
      <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 12, lineHeight: 1.5, padding: 10, background: C.paperDeep, borderRadius: 2 }}>
        <strong>Come funziona:</strong> il prezzo listino dei piatti è memorizzato <strong>sempre lordo</strong> (come il cliente lo vede). I costi dei prodotti base sono <strong>sempre netti</strong> (dalle fatture B2B). Il sistema converte automaticamente in base alla modalità scelta nell'header. In modalità <strong>Netto</strong> vedi il margine reale (quello che incassi davvero, tolte le imposte). Food cost netto è circa 9% più alto del lordo (con IVA 10%).
      </div>
    </div>

    {/* ── Stagionalità mensile (FIX #6 audit 2026-06-04) ─────────────────────── */}
    {(() => {
      const sm = (state.settings?.seasonalityMonthly && state.settings.seasonalityMonthly.length === 12)
        ? state.settings.seasonalityMonthly : [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1];
      const isNeutral = sm.every(v => Math.abs(Number(v) - 1) < 1e-9);
      const setMonthly = (next) => setState({ ...state, settings: { ...(state.settings || {}), seasonalityMonthly: next } });
      const setOne = (idx, val) => {
        const v = Math.max(0.05, Math.min(10, parseFloat(val) || 0));
        const next = sm.map((x, i) => (i === idx ? v : Number(x)));
        setMonthly(next);
      };
      return <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 22, borderRadius: 2, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <Calendar size={18} color={C.teal} />
          <div className="f-display" style={{ fontSize: 16, color: C.ink, fontWeight: 500 }}>Stagionalità mensile (forecast)</div>
        </div>
        <div style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.55, marginBottom: 14 }}>
          Per un ristorante di costa l'estate vale molto più dell'inverno. Questi 12 coefficienti dicono al forecast
          quanto pesa ogni mese rispetto alla media. <strong>Lascia tutti a 1</strong> per disattivare (forecast invariato).
          Esempio: se luglio è circa il triplo di gennaio, metti gennaio ≈ 0,5 e luglio ≈ 2,5. Non serve siano precisi né "sommare a 12":
          il sistema li normalizza automaticamente. <strong>Quando li imposti tu</strong>, il forecast li applica ma allarga la banda di
          incertezza del 25% (è una tua stima, non un dato). Appena avrai ~2 anni di storico, il sistema imparerà la stagionalità da solo e userà quella.
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          <Btn variant="ghost" size="sm" icon={TrendingUp} onClick={() => {
            setMonthly([0.55, 0.55, 0.70, 0.95, 1.25, 1.70, 2.30, 2.50, 1.55, 0.95, 0.60, 0.90]);
            toast({ type: "success", message: "Applicato preset 'ristorante di costa' — ritoccalo coi tuoi numeri" });
          }}>Applica preset costa adriatica</Btn>
          <Btn variant="ghost" size="sm" icon={Trash2} onClick={() => {
            setMonthly([1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]);
            toast({ type: "success", message: "Stagionalità azzerata (profilo neutro) — forecast senza correzioni mensili" });
          }}>Azzera (neutro)</Btn>
          <span className="f-mono" style={{ fontSize: 10, color: isNeutral ? C.muted : C.tealDeep || C.teal, alignSelf: "center", letterSpacing: "0.1em" }}>
            {isNeutral ? "PROFILO NEUTRO — NESSUNA CORREZIONE" : "PROFILO ATTIVO — BANDA CI +25%"}
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10 }}>
          {["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"].map((lbl, i) => (
            <Field key={lbl} label={lbl}>
              <Input type="number" step="0.05" min="0.05" max="10"
                value={Number(sm[i])}
                onChange={e => setOne(i, e.target.value)} />
            </Field>
          ))}
        </div>
      </div>;
    })()}

    <div style={{ background: C.white, border: `1px solid ${C.rule}`, padding: 22, borderRadius: 2, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <AlertTriangle size={18} color={C.terra} />
        <div className="f-display" style={{ fontSize: 16, color: C.ink, fontWeight: 500 }}>Regole mezze porzioni (Zucchetti TCPOS)</div>
      </div>
      <div style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.5, marginBottom: 14 }}>
        Quando TCPOS emette una riga <strong>MEZZA PORZ.</strong> nel file esportato, il parser verifica che il cassiere abbia applicato lo sconto corrispondente.
        Se lo sconto manca, lo scontrino viene flaggato con il badge <span style={{ background: C.terra, color: C.paper, fontSize: 9, padding: "1px 5px", borderRadius: 2, fontWeight: 700 }}>MEZZA SENZA SCONTO</span> e puoi correggerlo manualmente dallo storico scontrini.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
        <Field label="Sconto MEZZA PORZ. SEC (€)" hint="Mezza porzione secondo (es. frittura) — default €4">
          <Input type="number" step="0.5" min="0" max="50"
            value={(state.settings?.mezzePorzioniRules?.SEC ?? 4)}
            onChange={e => {
              const v = parseFloat(e.target.value) || 0;
              setState({ ...state, settings: { ...(state.settings || {}), mezzePorzioniRules: { ...(state.settings?.mezzePorzioniRules || { SEC: 4, PRIM: 2, ANT: 2 }), SEC: v } } });
              toast({ type: "success", message: `Sconto mezza porzione SEC aggiornato a €${v.toFixed(2)}` });
            }} />
        </Field>
        <Field label="Sconto MEZZA PORZ. PRIM (€)" hint="Mezza porzione primo — default €2">
          <Input type="number" step="0.5" min="0" max="50"
            value={(state.settings?.mezzePorzioniRules?.PRIM ?? 2)}
            onChange={e => {
              const v = parseFloat(e.target.value) || 0;
              setState({ ...state, settings: { ...(state.settings || {}), mezzePorzioniRules: { ...(state.settings?.mezzePorzioniRules || { SEC: 4, PRIM: 2, ANT: 2 }), PRIM: v } } });
              toast({ type: "success", message: `Sconto mezza porzione PRIM aggiornato a €${v.toFixed(2)}` });
            }} />
        </Field>
        <Field label="Sconto MEZZA PORZ. ANT (€)" hint="Mezza porzione antipasto — default €2">
          <Input type="number" step="0.5" min="0" max="50"
            value={(state.settings?.mezzePorzioniRules?.ANT ?? 2)}
            onChange={e => {
              const v = parseFloat(e.target.value) || 0;
              setState({ ...state, settings: { ...(state.settings || {}), mezzePorzioniRules: { ...(state.settings?.mezzePorzioniRules || { SEC: 4, PRIM: 2, ANT: 2 }), ANT: v } } });
              toast({ type: "success", message: `Sconto mezza porzione ANT aggiornato a €${v.toFixed(2)}` });
            }} />
        </Field>
      </div>
      <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 12, lineHeight: 1.5, padding: 10, background: C.paperDeep, borderRadius: 2 }}>
        <strong>Nota:</strong> i valori qui configurati vengono usati dal parser Zucchetti al prossimo import. Se cambi i valori dopo un import, ri-importa il file per vedere i flag aggiornati. I cassieri devono applicare manualmente questi sconti a valore sul terminale TCPOS — il parser non li crea automaticamente, li verifica solo.
      </div>
    </div>

    <div style={{ background: C.paperDeep, padding: 16, borderRadius: 2, fontSize: 12, color: C.inkSoft, lineHeight: 1.6 }}>
      <strong style={{ color: C.ink }}>Consiglio:</strong> fai un backup JSON una volta a settimana (o dopo inserimenti importanti come nuove ricette o molte fatture). I dati sono nello storage del browser: se cancelli la cache del sito, o se cambi dispositivo, li perdi. Un backup JSON da 300-500 KB risolve il problema.
    </div>
  </div>;
}


// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                      MAIN APP                                              ║
// ╚═══════════════════════════════════════════════════════════════════════════╝
function AppInner() {
  const [state, setState] = usePersistedState("bottega_prod_v6", SEED);
  // Sincronizza gli orari di servizio configurabili (settings → SHIFT_CONFIG) PRIMA
  // che engine/getShift/getServiceDate vengano usati in questo render. I default
  // riproducono il comportamento storico, quindi senza impostazioni nulla cambia.
  setShiftConfig(state.settings?.serviceShift);
  const [nav, setNav] = useState({ view: "dashboard" });
  const [cmdOpen, setCmdOpen] = useState(false);
  const toast = useToast();

  // ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setCmdOpen(v => !v); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // W2 — sync multi-dispositivo in background: parte solo se configurato E
  // attivato (Backup & dati). stateRef evita closure stantie nel runner;
  // busyRef impedisce sovrapposizioni. Il pannello richiama la stessa identica
  // strada via window.__voyaSync.run(true) — un solo canale di sync.
  const stateRef = useRef(state);
  stateRef.current = state;
  const syncBusyRef = useRef(false);
  const runSync = useCallback(async (manual) => {
    if (syncBusyRef.current) return { skipped: "sync già in corso" };
    syncBusyRef.current = true;
    try {
      return await _syncRun(
        () => stateRef.current,
        (records) => { let n = 0; setState(s => { const r = _syncApplyInbound(s, records); n = r.applied; return r.state; }); return n; },
        { manual });
    } finally { syncBusyRef.current = false; }
  }, [setState]);
  useEffect(() => {
    try { window.__voyaSync = { run: runSync }; } catch { /* no-op */ }
    const kick = setTimeout(() => { runSync(false); }, 4000);
    const t = setInterval(() => { runSync(false); }, 60000);
    return () => { clearTimeout(kick); clearInterval(t); };
  }, [runSync]);

  // W3.1 — coda richieste online a livello APP (feedback Andi 06/07: serve un
  // badge di avviso ovunque, non solo dentro l'Agenda). Polling 60s + refresh
  // al focus della finestra; il pannello in Agenda usa QUESTA lista (un solo
  // canale). Silenzioso offline: il badge semplicemente non cresce.
  const [onlineReqs, setOnlineReqs] = useState([]);
  const refreshOnlineReqs = useCallback(async () => {
    const cfg = _syncGetConfig();
    if (!cfg.token) return;
    try {
      const res = await fetch("/api/booking?status=pending", { headers: { authorization: `Bearer ${cfg.token}` } });
      const out = await res.json().catch(() => ({}));
      if (res.ok && out.ok) setOnlineReqs(Array.isArray(out.requests) ? out.requests : []);
    } catch { /* offline: si riprova al giro dopo */ }
  }, []);
  useEffect(() => {
    refreshOnlineReqs();
    const t = setInterval(refreshOnlineReqs, 60000);
    const onFocus = () => refreshOnlineReqs();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [refreshOnlineReqs]);

  const onNavigate = useCallback((params) => {
    setNav({ view: params.view || "dashboard", ...params });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const setView = useCallback((v) => setNav({ view: v }), []);

  const notifications = useMemo(() => engine.computeNotifications(state), [state]);

  const counts = useMemo(() => ({
    unread: 0,
    poOpen: state.purchaseOrders.filter(p => p.status === "inviato" || p.status === "bozza").length,
    poToReceive: state.purchaseOrders.filter(p => p.status === "ricevuto" && !p.receiptId).length,
    invoicesDue: state.invoices.filter(i => i.status === "da_pagare").length,
    lotsActive: state.lots.filter(l => l.status !== "esaurito").length,
    haccpAlerts: notifications.filter(n => n.type === "haccp").length,
    ordersActive: state.workOrders.filter(o => o.status === "in_corso").length,
    kitchenRequests: state.kitchenRequests.filter(kr => kr.status === "in_attesa").length,
    kitchenUrgent: state.kitchenRequests.filter(kr => kr.status === "in_attesa" && kr.urgency === "urgente").length,
    baseProducts: state.baseProducts?.length || 0,
    todayReceipts: (state.tableReceipts || []).filter(r => getServiceDate(r.closedAt) === TODAY).length,
    onlineRequests: onlineReqs.length,
  }), [state, notifications, onlineReqs]);

  const view = nav.view;

  // W2.5 — dispositivo in Modalità Accoglienza (tablet di sala): SOLO Agenda,
  // niente sidebar/menu/CRM. Dopo tutti gli hook (ordine hook invariato); il
  // sync in background sopra resta attivo. Si esce col PIN nel guscio.
  if (_deviceModeGet()?.mode === "accoglienza") {
    return <AccoglienzaShell state={state} setState={setState} onlineReqs={onlineReqs} refreshOnlineReqs={refreshOnlineReqs} />;
  }

  return <div className="f-body" style={{ display: "flex", minHeight: "100vh", background: C.paper, color: C.ink }}>
    <Sidebar view={view} setView={setView} counts={counts} currentUser={state.currentUser} onOpenCommand={() => setCmdOpen(true)} />
    <main style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
      <TopBar notifications={notifications} onNavigate={onNavigate} state={state} setState={setState} />
      <div style={{ padding: "16px 40px 80px", flex: 1 }}>
        {/* C1.1-EASY — visibile SOLO senza token sync: dispositivo appena installato */}
        <PairClaimBanner />
        <ViewErrorBoundary viewName={view} onReset={() => { setNav({ view: "dashboard" }); }}>
          {view === "dashboard" && <Dashboard state={state} setView={setView} notifications={notifications} onNavigate={onNavigate} onlineCount={counts.onlineRequests} />}
          {view === "bacheca" && <Bacheca state={state} setState={setState} />}
          {view === "acquisti" && <Acquisti state={state} setState={setState} onNavigate={onNavigate} initial={nav} />}
          {view === "ingresso" && <IngressoMerce state={state} setState={setState} onNavigate={onNavigate} initial={nav} />}
          {view === "fatture" && <Fatture state={state} setState={setState} />}
          {view === "magazzino" && <Magazzino state={state} setState={setState} onNavigate={onNavigate} initial={nav} />}
          {view === "lot" && <LotDetail state={state} setState={setState} lotId={nav.lotId} onNavigate={onNavigate} />}
          {view === "haccp" && <HACCP state={state} setState={setState} initial={nav} />}
          {view === "produzione" && <Produzione state={state} setState={setState} onNavigate={onNavigate} initial={nav} />}
          {view === "order" && <OrderDetail state={state} setState={setState} orderId={nav.orderId} onNavigate={onNavigate} />}
          {view === "pianificazione" && <Pianificazione state={state} setState={setState} onNavigate={onNavigate} />}
          {view === "richieste" && <RichiesteCucina state={state} setState={setState} onNavigate={onNavigate} initial={nav} />}
          {view === "piatti" && <Piatti state={state} setState={setState} onNavigate={onNavigate} />}
          {view === "scarico" && <ScaricoCucina state={state} setState={setState} onNavigate={onNavigate} />}
          {view === "foodcost" && <FoodCost state={state} setState={setState} />}
          {view === "mensa" && <MensaView state={state} setState={setState} />}
          {view === "tracciabilita" && <Tracciabilita state={state} setState={setState} onNavigate={onNavigate} initial={nav} />}
          {view === "anagrafiche" && <Anagrafiche state={state} setState={setState} initial={nav} />}
          {view === "clienti" && <Clienti state={state} setState={setState} />}
          {view === "agenda" && <AgendaView state={state} setState={setState} onlineReqs={onlineReqs} refreshOnlineReqs={refreshOnlineReqs} />}
          {view === "sala-tavoli" && <FloorSettingsView state={state} setState={setState} />}
          {view === "report" && <Report state={state} setState={setState} />}
          {view === "simulatore" && <SimulatoreMenu state={state} setState={setState} />}
          {view === "vendite" && <VenditeStatistiche state={state} setState={setState} onNavigate={onNavigate} />}
          {view === "dati" && <BackupDati state={state} setState={setState} />}
        </ViewErrorBoundary>
      </div>
    </main>
    <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} state={state} onNavigate={onNavigate} />
  </div>;
}

// ⟦CRM-UI-P0:START⟧ — P0 Customer Card · T1 (lista) + T2 (detail card) + T3 (visite + scontrini).
// La UI è un CONSUMER puro della Foundation congelata: lettura SOLO via
// crmCore.listCustomers / crmCore.getCustomer (⟦CRM-CORE⟧), crmVisitProjection.
// projectVisitsForCustomer (⟦CRM-VISIT⟧, derivata runtime-only), crmLinkCore.
// listLinksForCustomer (⟦CRM-LINK⟧) e i selector della receiptFacade (⟦RECEIPT-FACADE⟧)
// per risolvere i link ai receipt. Nessuna mutazione, nessuna metrica nuova, nessuna
// persistenza: la card rende visibili SOLO dati già prodotti dalla Foundation.
const CRM_UI_TYPE_LABELS = { person: "Persona", family: "Famiglia", company: "Azienda" };
const CRM_UI_CONTACT_KIND_LABELS = { phone: "Telefono", email: "Email", other: "Altro" };
const CRM_UI_ROLE_LABELS = { payer: "Pagatore", attendee: "Partecipante", reservation_holder: "Titolare prenotazione" };

// T2 — Customer Detail Card: pannello read-only del cliente selezionato.
// Consuma il record così com'è: per gli anonimizzati la PII è GIÀ rimossa dal core
// (displayName="(anonimizzato)", contacts=[], notes=null) — qui solo presentazione.
function CustomerDetailCard({ customer, state, onClose, onAnonymize, onDelete }) {
  const c = customer;
  const contacts = Array.isArray(c.contacts) ? c.contacts : [];

  // Oblio azionabile — conferma a due passi (pattern "armato": 1° click arma, 2°
  // esegue), come il reject in OnlineRequestsPanel. Reset quando cambia cliente
  // (stessa istanza montata al variare della selezione: niente azione ereditata).
  const [armed, setArmed] = useState(null); // null | "anon" | "del"
  useEffect(() => { setArmed(null); }, [c.id]);

  // T3 — storico visite: SOLO la projection frozen (derivata runtime-only, mai persistita).
  // Ordinamento discendente per anchor temporale grezzo = pura presentazione.
  const visits = useMemo(() =>
    [...crmVisitProjection.projectVisitsForCustomer(state, c.id)]
      .sort((a, b) => (b.rawTimeStartMs || 0) - (a.rawTimeStartMs || 0)),
    [state, c.id]);

  // T3 — scontrini collegati: link attivi via core (Fase 3), ognuno risolto al suo receipt
  // via facade (Fase 1, resolveReceiptByStableKey: ambiguo → null, mai scelta silenziosa).
  const linkedReceipts = useMemo(() =>
    crmLinkCore.listLinksForCustomer(state.customerReceiptLinks, c.id)
      .map(l => ({ link: l, receipt: l.primaryStableReceiptKey ? receiptFacade.resolveReceiptByStableKey(state, l.primaryStableReceiptKey) : null })),
    [state, c.id]);
  return <div className="slide-left" style={{ width: 380, flexShrink: 0, background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2, padding: 22, alignSelf: "flex-start", position: "sticky", top: 20 }}>
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.25em", color: C.muted, textTransform: "uppercase", marginBottom: 6 }}>Scheda cliente</div>
        <div className="f-display" style={{ fontSize: 22, fontWeight: 500, color: C.ink, lineHeight: 1.15, overflowWrap: "break-word" }}>{c.displayName}</div>
        <span className="f-mono" style={{ display: "inline-block", marginTop: 8, fontSize: 9, letterSpacing: "0.15em", color: C.inkMid, textTransform: "uppercase", background: C.paperDeep, border: `1px solid ${C.ruleSoft}`, padding: "3px 8px", borderRadius: 2 }}>
          {CRM_UI_TYPE_LABELS[c.type] || c.type}
        </span>
      </div>
      <IconBtn icon={X} size={15} onClick={onClose} title="Chiudi scheda" />
    </div>

    {c.anonymized && <div style={{ marginTop: 16, padding: "10px 14px", background: C.paperDeep, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
      <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.inkMid, textTransform: "uppercase" }}>Cliente anonimizzato</div>
      <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
        Dati personali rimossi su richiesta (oblio GDPR){c.anonymizedAt ? ` il ${fmt.dateFull(c.anonymizedAt)}` : ""}. Il record è immutabile.
      </div>
    </div>}

    <div style={{ marginTop: 20 }}>
      <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.25em", color: C.muted, textTransform: "uppercase", marginBottom: 10 }}>Contatti</div>
      {contacts.length === 0
        ? <div className="f-body" style={{ fontSize: 12, color: C.mutedSoft, fontStyle: "italic" }}>Nessun contatto registrato</div>
        : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {contacts.map((k, i) => <div key={i} style={{ display: "flex", alignItems: "baseline", gap: 10, paddingBottom: 8, borderBottom: i < contacts.length - 1 ? `1px solid ${C.ruleSoft}` : "none" }}>
            <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.1em", color: C.muted, textTransform: "uppercase", width: 68, flexShrink: 0 }}>{CRM_UI_CONTACT_KIND_LABELS[k.kind] || k.kind}</span>
            <span style={{ minWidth: 0 }}>
              <span className="f-body" style={{ fontSize: 13, color: C.ink, overflowWrap: "break-word" }}>{k.value}</span>
              {k.note && <span className="f-body" style={{ display: "block", fontSize: 11, color: C.muted, marginTop: 2 }}>{k.note}</span>}
            </span>
          </div>)}
        </div>}
      {c.birthdate && !c.anonymized && <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: contacts.length ? 10 : 8, paddingTop: contacts.length ? 8 : 0, borderTop: contacts.length ? `1px solid ${C.ruleSoft}` : "none" }}>
        <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.1em", color: C.muted, textTransform: "uppercase", width: 68, flexShrink: 0 }}>Compleanno</span>
        <span className="f-body" style={{ fontSize: 13, color: C.ink }}>{fmt.dateFull(c.birthdate)}</span>
      </div>}
    </div>

    {c.notes && <div style={{ marginTop: 20 }}>
      <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.25em", color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>Note</div>
      <div className="f-body" style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "break-word" }}>{c.notes}</div>
    </div>}

    {/* T3 — Storico visite (projection derivata: si ricomputa dai fatti, mai persistita) */}
    <div style={{ marginTop: 20 }}>
      <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.25em", color: C.muted, textTransform: "uppercase", marginBottom: 10 }}>Visite · {visits.length}</div>
      {visits.length === 0
        ? <div className="f-body" style={{ fontSize: 12, color: C.mutedSoft, fontStyle: "italic" }}>Nessuna visita collegata a questo cliente</div>
        : <div className="hide-scroll" style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 240, overflowY: "auto" }}>
          {visits.map(v => <div key={v.visitKey} style={{ display: "flex", alignItems: "baseline", gap: 10, paddingBottom: 8, borderBottom: `1px solid ${C.ruleSoft}` }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="f-body" style={{ fontSize: 12, color: C.ink }}>{v.rawTimeStartMs != null ? fmt.dateTime(v.rawTimeStartMs) : "Data non disponibile"}</div>
              <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.1em", color: C.muted, textTransform: "uppercase", marginTop: 2 }}>
                {v.kind === "banco" ? "Banco" : (v.canonicalTable != null ? `Tavolo ${v.canonicalTable}` : "Tavolo —")}
                {v.members.length > 1 ? ` · ${v.members.length} scontrini` : ""}
                {v.flags.hasNonFiscal ? " · non fiscale" : ""}
                {v.flags.hasOrphanReceipts ? " · attribuzione parziale" : ""}
              </div>
            </div>
            <div className="f-mono" style={{ fontSize: 12, color: C.ink, flexShrink: 0 }}>{fmt.eur((v.totals.netFiscalCents ?? v.totals.grossCents) / 100)}</div>
          </div>)}
        </div>}
    </div>

    {/* T3 — Scontrini collegati (fatti atomici Link, Fase 3; receipt risolto via facade) */}
    <div style={{ marginTop: 20 }}>
      <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.25em", color: C.muted, textTransform: "uppercase", marginBottom: 10 }}>Scontrini collegati · {linkedReceipts.length}</div>
      {linkedReceipts.length === 0
        ? <div className="f-body" style={{ fontSize: 12, color: C.mutedSoft, fontStyle: "italic" }}>Nessuno scontrino collegato</div>
        : <div className="hide-scroll" style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 240, overflowY: "auto" }}>
          {linkedReceipts.map(({ link: l, receipt: r }) => {
            const src = r ? receiptFacade.getReceiptSourceIdentity(r) : null;
            const anchors = r ? receiptFacade.getReceiptTimeAnchors(r) : null;
            return <div key={l.id} style={{ display: "flex", alignItems: "baseline", gap: 10, paddingBottom: 8, borderBottom: `1px solid ${C.ruleSoft}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="f-body" style={{ fontSize: 12, color: r ? C.ink : C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r
                    ? (src.fiscalNumber ? `Doc. ${src.fiscalNumber}` : (src.docType || "Documento senza numero"))
                    : (l.primaryStableReceiptKey || "Chiave assente")}
                </div>
                <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.1em", color: C.muted, textTransform: "uppercase", marginTop: 2 }}>
                  {CRM_UI_ROLE_LABELS[l.role] || l.role}
                  {anchors && anchors.rawClosedAt ? ` · ${fmt.dateTime(anchors.rawClosedAt)}` : ""}
                  {!r ? " · non risolvibile ora" : ""}
                </div>
              </div>
              {r && <div className="f-mono" style={{ fontSize: 12, color: C.ink, flexShrink: 0 }}>{fmt.eur(receiptFacade.getReceiptMoney(r).total)}</div>}
            </div>;
          })}
        </div>}
    </div>

    {/* Privacy & oblio — esercizio del diritto di cancellazione (azione volontaria
        del titolare). Nascosto se il record è già anonimizzato/tombstoned. */}
    {!c.anonymized && !c.tombstone && (onAnonymize || onDelete) && <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.rule}` }}>
      <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.25em", color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>Privacy &amp; oblio</div>
      <div className="f-body" style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
        Diritto di cancellazione (GDPR). L'anonimizzazione rimuove nome, contatti, note e compleanno: il record resta come prova storica ma diventa immutabile.
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {onAnonymize && <Btn size="sm" variant={armed === "anon" ? "danger" : "ghost"}
          onClick={() => { if (armed !== "anon") { setArmed("anon"); return; } setArmed(null); onAnonymize(c.id); }}>
          {armed === "anon" ? "Confermi anonimizzazione?" : "Anonimizza (oblio GDPR)"}
        </Btn>}
        {onDelete && <Btn size="sm" variant={armed === "del" ? "danger" : "ghost"}
          onClick={() => { if (armed !== "del") { setArmed("del"); return; } setArmed(null); onDelete(c.id); }}>
          {armed === "del" ? "Confermi eliminazione?" : "Elimina scheda"}
        </Btn>}
      </div>
    </div>}

    <div className="f-body" style={{ marginTop: 22, paddingTop: 12, borderTop: `1px solid ${C.ruleSoft}`, fontSize: 10, color: C.mutedSoft }}>
      Consensi e statistiche arriveranno nelle prossime fasi del CRM.
    </div>
  </div>;
}

// Slice "Riconosci Cliente" · Task 1+2+3 — widget CLIENTE nel viewer scontrino.
// LETTURA via crmLinkCore.listLinksForReceipt (⟦CRM-LINK⟧, chiavi stabili, tombstoned
// esclusi) + crmCore.getCustomer/listCustomers (⟦CRM-CORE⟧). SCRITTURE: il picker inline
// (pattern editing-coperti, niente modale annidato) collega un cliente ESISTENTE come
// pagatore via crmLinkCore.createLink; a ricerca senza risultati CREA il cliente col solo
// nome digitato (type=person, crmCore.createCustomer) e lo collega nello stesso gesto.
// Atomicità: entrambi i reducer puri calcolati prima, UNICO setState solo se entrambi ok
// — un createLink fallito non lascia mai un cliente orfano. payerConflict mai fuso in silenzio.
const CRM_UI_PICKER_MAX = 8;

function ReceiptCustomerWidget({ receipt, state, setState }) {
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState("");

  const linked = useMemo(() =>
    crmLinkCore.listLinksForReceipt(state.customerReceiptLinks, receipt)
      .map(l => ({ link: l, customer: crmCore.getCustomer(state.customers, l.customerId) })),
    [state.customerReceiptLinks, state.customers, receipt]);

  // Candidati: clienti attivi non anonimizzati (opzione già nel core), filtro substring.
  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    const all = crmCore.listCustomers(state.customers, { excludeAnonymized: true });
    return q ? all.filter(c => (c.displayName || "").toLowerCase().includes(q)) : all;
  }, [state.customers, q]);
  const shown = matches.slice(0, CRM_UI_PICKER_MAX);

  const closePicker = () => { setExpanded(false); setQuery(""); };

  const handleLink = (cu) => {
    if (!setState) return;
    const operator = state.currentUser?.name || "Andi";
    const res = crmLinkCore.createLink(state.customerReceiptLinks, receipt, { customerId: cu.id, role: "payer" }, { by: operator });
    if (res.error === "payerConflict") {
      const names = (res.conflict?.existingCustomerIds || [])
        .map(id => crmCore.getCustomer(state.customers, id)).filter(Boolean).map(x => x.displayName).join(", ");
      toast({ type: "error", title: "Pagatore già presente", message: names ? `Questo scontrino ha già un pagatore: ${names}.` : "Questo scontrino ha già un pagatore." });
      return;
    }
    if (res.error) {
      toast({ type: "error", title: "Collegamento non riuscito", message: res.error });
      return;
    }
    setState(s => ({ ...s, customerReceiptLinks: res.customerReceiptLinks }));
    closePicker();
    toast({ type: "success", title: "Cliente collegato", message: `${cu.displayName} · Pagatore` });
  };

  // Task 3 — crea il cliente col testo digitato E lo collega, in un solo gesto atomico:
  // entrambi i reducer calcolati prima, un UNICO setState solo se entrambi riescono.
  // Se createLink fallisce, l'array customers di createCustomer NON viene mai committato.
  const handleCreateAndLink = () => {
    if (!setState) return;
    const name = query.trim();
    if (!name) return;
    const operator = state.currentUser?.name || "Andi";
    const ctx = { by: operator };
    const resC = crmCore.createCustomer(state.customers, { displayName: name, type: "person" }, ctx);
    if (resC.error) {
      toast({ type: "error", title: "Creazione non riuscita", message: resC.error });
      return;
    }
    const resL = crmLinkCore.createLink(state.customerReceiptLinks, receipt, { customerId: resC.customer.id, role: "payer" }, ctx);
    if (resL.error === "payerConflict") {
      const names = (resL.conflict?.existingCustomerIds || [])
        .map(id => crmCore.getCustomer(state.customers, id)).filter(Boolean).map(x => x.displayName).join(", ");
      toast({ type: "error", title: "Pagatore già presente", message: names ? `Questo scontrino ha già un pagatore: ${names}.` : "Questo scontrino ha già un pagatore." });
      return;
    }
    if (resL.error) {
      toast({ type: "error", title: "Collegamento non riuscito", message: resL.error });
      return;
    }
    setState(s => ({ ...s, customers: resC.customers, customerReceiptLinks: resL.customerReceiptLinks }));
    closePicker();
    toast({ type: "success", title: "Cliente creato e collegato", message: `${name} · Pagatore` });
  };

  return <div style={{ marginBottom: 14, padding: "10px 14px", background: linked.length > 0 ? C.tealWash : C.paperDeep, border: `1px solid ${linked.length > 0 ? C.teal : C.rule}`, borderRadius: 2 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <User size={16} color={linked.length > 0 ? C.tealDeep : C.muted} style={{ flexShrink: 0 }} />
      <span className="f-mono" style={{ fontSize: 10, color: C.muted, letterSpacing: "0.2em" }}>CLIENTE</span>
      {linked.length === 0
        ? <>
          <span className="f-body" style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>Nessun cliente collegato</span>
          <div style={{ flex: 1 }} />
          {setState && (expanded
            ? <Btn size="sm" variant="ghost" onClick={closePicker}>Annulla</Btn>
            : <Btn size="sm" variant="ghost" onClick={() => setExpanded(true)}>Collega</Btn>)}
        </>
        : <div style={{ flex: 1, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
          {linked.map(({ link: l, customer: cu }) => <span key={l.id} style={{ display: "inline-flex", alignItems: "baseline", gap: 8 }}>
            <span className="f-display" style={{ fontSize: 15, fontWeight: 500, color: cu ? C.ink : C.muted }}>{cu ? cu.displayName : "Cliente non disponibile"}</span>
            <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.tealDeep, textTransform: "uppercase" }}>
              {cu ? `${CRM_UI_TYPE_LABELS[cu.type] || cu.type} · ` : ""}{CRM_UI_ROLE_LABELS[l.role] || l.role}
            </span>
          </span>)}
        </div>}
    </div>

    {/* Picker inline (Task 2): ricerca + selezione tra clienti ESISTENTI. */}
    {expanded && linked.length === 0 && <div className="fade-in" style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.rule}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, background: C.white, border: `1px solid ${C.rule}`, padding: "6px 12px", borderRadius: 2, marginBottom: 8 }}>
        <Search size={14} color={C.muted} />
        <input value={query} onChange={e => setQuery(e.target.value)} autoFocus placeholder="Cerca cliente..."
          onKeyDown={e => { if (e.key === "Escape") closePicker(); }}
          className="f-body" style={{ border: "none", outline: "none", flex: 1, fontSize: 13, background: "transparent", color: C.ink }} />
        {query && <IconBtn icon={X} size={12} onClick={() => setQuery("")} />}
      </div>
      {shown.length === 0
        ? (q
          ? <div className="fade-in" style={{ padding: "2px 0" }}>
            <div className="f-body" style={{ fontSize: 12, color: C.muted, fontStyle: "italic", marginBottom: 8 }}>Nessun cliente trovato.</div>
            <Btn size="sm" variant="accent" icon={Plus} onClick={handleCreateAndLink}>Crea e collega "{query.trim()}"</Btn>
          </div>
          : <div className="f-body" style={{ fontSize: 12, color: C.muted, fontStyle: "italic", padding: "6px 2px" }}>
            Nessun cliente in anagrafica. Digita un nome per crearlo e collegarlo.
          </div>)
        : <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {shown.map(cu => <div key={cu.id} className="tx" onClick={() => handleLink(cu)}
            style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "7px 12px", background: C.white, border: `1px solid ${C.ruleSoft}`, borderRadius: 2, cursor: "pointer" }}>
            <span className="f-body" style={{ flex: 1, fontSize: 13, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cu.displayName}</span>
            <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.inkMid, textTransform: "uppercase" }}>{CRM_UI_TYPE_LABELS[cu.type] || cu.type}</span>
          </div>)}
          {matches.length > shown.length && <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.muted, textTransform: "uppercase", padding: "4px 2px" }}>
            +{matches.length - shown.length} altri — affina la ricerca
          </div>}
        </div>}
    </div>}
  </div>;
}

// ═══════════════════════════════════════════════════════════════════════════
// R8-B — LISTA NERA (richiesta Andi 2026-07-07): clienti segnalati con MOTIVO,
// visibili a colpo d'occhio quando riprenotano (richieste online + righe Agenda).
// Store additivo `blocklist` (sincronizzato: il tablet VEDE i bollini; la
// GESTIONE sta nella vista Clienti, che il tablet blindato non raggiunge).
// Aggancio: telefono (certo, suffisso a 10 cifre: +39 333… ≡ 333…) o nome
// normalizzato (segnalato come "possibile" — mai certezza da un nome).
// GDPR: motivi FATTUALI ("3 no-show", "assegno scoperto"), mai giudizi.
// Mini-core puro locale (nessun blocco congelato coinvolto).
const _blkPhoneKey = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  return d.length >= 6 ? d.slice(-10) : null;
};
const _blkMatch = (blocklist, subject) => {
  const list = (Array.isArray(blocklist) ? blocklist : []).filter(b => b && !b.tombstone);
  if (list.length === 0) return null;
  const pk = _blkPhoneKey(subject && subject.phone);
  if (pk) {
    const hit = list.find(b => b.phoneKey === pk);
    if (hit) return { entry: hit, via: "telefono" };
  }
  const nn = normStr((subject && subject.name) || "");
  if (nn) {
    const hit = list.find(b => b.nameNorm && b.nameNorm === nn);
    if (hit) return { entry: hit, via: "nome" };
  }
  return null;
};
const _blkCreate = (blocklist, input, by) => {
  const base = Array.isArray(blocklist) ? blocklist : [];
  const name = String((input && input.name) || "").trim();
  const phone = String((input && input.phone) || "").trim();
  const reason = String((input && input.reason) || "").trim();
  if (!reason) return { blocklist: base, entry: null, error: "il motivo è obbligatorio" };
  if (!name && !phone) return { blocklist: base, entry: null, error: "serve almeno nome o telefono" };
  const phoneKey = _blkPhoneKey(phone);
  if (phone && !phoneKey) return { blocklist: base, entry: null, error: "telefono troppo corto" };
  const now = new Date().toISOString();
  const entry = {
    id: uid("blk_"), name: name || null, phone: phone || null,
    phoneKey, nameNorm: name ? normStr(name) : null, reason,
    version: 1, updatedAt: now, deviceId: "local", tombstone: false,
    createdBy: by || "Andi", createdAt: now,
  };
  return { blocklist: [...base, entry], entry, error: null };
};
const _blkTombstone = (blocklist, id) => {
  const base = Array.isArray(blocklist) ? blocklist : [];
  const idx = base.findIndex(b => b && b.id === id);
  if (idx < 0) return { blocklist: base, error: "voce non trovata" };
  const copy = base.slice();
  copy[idx] = { ...copy[idx], tombstone: true, version: (Number(copy[idx].version) || 1) + 1, updatedAt: new Date().toISOString() };
  return { blocklist: copy, error: null };
};

// M1 — RICONOSCIMENTO cliente da un contatto (richiesta online / prenotazione).
// PURO, sola lettura: PROPONE, non scrive mai (F8, come il matching scontrini).
// Chiave telefono = identica a quella della blacklist (_blkPhoneKey: ultime 10
// cifre, ignora +39/spazi) → dedup coerente in TUTTO il sistema e nessuna nuova
// normalizzazione da mantenere. Livelli, dal più forte:
//   • "match"     → un solo cliente con quel telefono: identità forte, a monte
//                   è ammesso l'auto-collegamento (il telefono non è omonimabile);
//   • "ambiguous" → più clienti con lo stesso telefono O lo stesso nome
//                   (famiglia, numero condiviso): MAI auto, si mostrano i candidati;
//   • "suggest"   → nessun telefono in comune ma UN solo nome uguale: proposta
//                   debole (i nomi collidono), conferma umana in Agenda;
//   • "none"      → nessuna corrispondenza: cliente nuovo.
// Esclude anonimizzati (I5/oblio) e tombstoned. Il telefono è la spina anti-
// doppione: cercando PRIMA di creare, la 2ª prenotazione dello stesso numero si
// aggancia al cliente esistente invece di duplicarlo.
const _crmMatchContact = (customers, subject) => {
  const list = (Array.isArray(customers) ? customers : []).filter(c => c && !c.tombstone && !c.anonymized);
  const pk = _blkPhoneKey(subject && subject.phone);
  if (pk) {
    const byPhone = list.filter(c => Array.isArray(c.contacts) &&
      c.contacts.some(ct => ct && ct.kind === "phone" && _blkPhoneKey(ct.value) === pk));
    if (byPhone.length === 1) return { status: "match", customer: byPhone[0], candidates: byPhone, via: "telefono" };
    if (byPhone.length > 1) return { status: "ambiguous", customer: null, candidates: byPhone, via: "telefono" };
  }
  const nn = subject && subject.name ? normStr(subject.name) : null;
  if (nn) {
    const byName = list.filter(c => normStr(c.displayName || "") === nn);
    if (byName.length === 1) return { status: "suggest", customer: byName[0], candidates: byName, via: "nome" };
    if (byName.length > 1) return { status: "ambiguous", customer: null, candidates: byName, via: "nome" };
  }
  return { status: "none", customer: null, candidates: [], via: null };
};

// C4.3 — Richiamo alla presa: sommario storico COMPATTO di un cliente, derivato
// SOLO dai fatti (Visite dai Link scontrino→cliente; prenotazioni dallo store).
// Puro, runtime-only, mai persistito. Stesso criterio conservativo di relMetrics
// (tavolo ricorrente = dominante netto con ≥2 occorrenze): niente certezze inventate.
const _resvCustomerRecall = (state, customerId) => {
  const cid = String(customerId);
  const visits = crmVisitProjection.projectVisitsForCustomer(state, cid);
  const totalCents = visits.reduce((s, v) => s + ((v.totals && (v.totals.netFiscalCents ?? v.totals.grossCents)) || 0), 0);
  const lastMs = visits.reduce((mx, v) => {
    const ms = v.rawTimeEndMs != null ? v.rawTimeEndMs : v.rawTimeStartMs;
    return ms != null && (mx === null || ms > mx) ? ms : mx;
  }, null);
  const resvs = (Array.isArray(state.reservations) ? state.reservations : []).filter(x =>
    x && !x.tombstone && x.customerId != null && String(x.customerId) === cid);
  const noShows = resvs.filter(x => x.status === "no_show").length;
  const tCounts = new Map();
  for (const x of resvs) {
    const t = x.table != null ? String(x.table).trim() : "";
    if (!t) continue;
    const k = t.toLowerCase();
    const e = tCounts.get(k) || { n: 0, label: t };
    e.n++; tCounts.set(k, e);
  }
  const tRanked = [...tCounts.values()].sort((a, b) => b.n - a.n);
  const recurringTable = tRanked.length > 0 && tRanked[0].n >= 2 && (tRanked.length === 1 || tRanked[0].n > tRanked[1].n)
    ? tRanked[0].label : null;
  return { visitCount: visits.length, totalCents, lastMs, noShows, recurringTable };
};

// C4.1 — Rilevatore doppioni (READ-ONLY, propone mai fonde — F8/roadmap). Scansiona
// i clienti attivi e raggruppa quelli che condividono telefono, email o nome
// normalizzato. Deduplica i gruppi con gli stessi membri tenendo la ragione più
// forte (telefono > email > nome). Puro: nessuna scrittura, nessuna fusione.
const _crmFindDuplicates = (customers) => {
  const list = (Array.isArray(customers) ? customers : []).filter(c => c && !c.tombstone && !c.anonymized);
  const byKey = (reason, keyFn) => {
    const m = new Map();
    for (const c of list) for (const k of keyFn(c)) {
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(c);
    }
    const out = [];
    for (const [k, members] of m) {
      const uniq = [...new Map(members.map(c => [c.id, c])).values()];
      if (uniq.length >= 2) out.push({ reason, key: k, members: uniq });
    }
    return out;
  };
  const all = [
    ...byKey("telefono", c => (c.contacts || []).filter(ct => ct && ct.kind === "phone").map(ct => _blkPhoneKey(ct.value))),
    ...byKey("email", c => (c.contacts || []).filter(ct => ct && ct.kind === "email" && ct.value).map(ct => String(ct.value).trim().toLowerCase())),
    ...byKey("nome", c => [normStr(c.displayName || "")]),
  ];
  const prio = { telefono: 3, email: 2, nome: 1 };
  const bySig = new Map();
  for (const g of all) {
    const sig = g.members.map(c => c.id).sort().join("|");
    const prev = bySig.get(sig);
    if (!prev || prio[g.reason] > prio[prev.reason]) bySig.set(sig, g);
  }
  return [...bySig.values()].sort((a, b) => prio[b.reason] - prio[a.reason]);
};

function BlacklistPanel({ state, setState, prefill }) {
  const toast = useToast();
  const [bName, setBName] = useState("");
  const [bPhone, setBPhone] = useState("");
  const [bReason, setBReason] = useState("");
  // R8-B.1 — scorciatoia dalla riga prenotazione: nome/telefono già compilati,
  // resta solo il motivo da scrivere.
  useEffect(() => {
    if (prefill) { setBName(prefill.name || ""); setBPhone(prefill.phone || ""); setBReason(""); }
  }, [prefill]);
  const entries = (Array.isArray(state.blocklist) ? state.blocklist : []).filter(b => b && !b.tombstone);
  const handleAdd = () => {
    const res = _blkCreate(state.blocklist, { name: bName, phone: bPhone, reason: bReason }, state.currentUser?.name || "Andi");
    if (res.error) { toast({ type: "error", title: "Voce non aggiunta", message: res.error }); return; }
    setState(s => ({ ...s, blocklist: res.blocklist }));
    setBName(""); setBPhone(""); setBReason("");
    toast({ type: "success", title: "Aggiunto alla lista nera", message: `${res.entry.name || res.entry.phone} — ${res.entry.reason}` });
  };
  const handleRemove = (id) => {
    const res = _blkTombstone(state.blocklist, id);
    if (res.error) { toast({ type: "error", title: "Voce non rimossa", message: res.error }); return; }
    setState(s => ({ ...s, blocklist: res.blocklist }));
    toast({ type: "info", title: "Rimosso dalla lista nera", message: "La voce resta nello storico (disattivata)." });
  };
  const inputStyle = { padding: "8px 10px", fontSize: 13, border: `1px solid ${C.rule}`, borderRadius: 2, background: C.white, color: C.ink };
  return <div style={{ background: C.white, border: `1px solid ${C.terra}`, padding: 22, borderRadius: 2, marginBottom: 20 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
      <span style={{ fontSize: 16 }}>⛔</span>
      <span className="f-display" style={{ fontSize: 16, color: C.terraDeep, fontWeight: 600 }}>Lista nera</span>
      <span className="f-mono" style={{ fontSize: 10, color: C.muted }}>{entries.length} {entries.length === 1 ? "voce" : "voci"}</span>
    </div>
    <div className="f-body" style={{ fontSize: 11.5, color: C.inkMid, marginBottom: 14 }}>
      Chi è in lista appare con il bollino ⛔ e il motivo su richieste online e prenotazioni (anche sul tablet).
      Aggancio per telefono (certo) o nome (possibile omonimo). Scrivi motivi <strong>fattuali</strong> ("3 no-show"), mai giudizi.
    </div>
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
      <input value={bName} onChange={e => setBName(e.target.value)} placeholder="Nome" className="f-body" style={{ ...inputStyle, flex: 1, minWidth: 140 }} />
      <input value={bPhone} onChange={e => setBPhone(e.target.value)} placeholder="Telefono" type="tel" className="f-body" style={{ ...inputStyle, width: 150 }} />
      <input value={bReason} onChange={e => setBReason(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleAdd(); }} placeholder="Motivo * (es. 3 no-show)" className="f-body" style={{ ...inputStyle, flex: 2, minWidth: 200 }} />
      <Btn size="sm" variant="danger" onClick={handleAdd}>Aggiungi</Btn>
    </div>
    {entries.length > 0 && <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {entries.map(b => <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "8px 12px", background: C.terraWash, border: `1px solid ${C.terra}`, borderRadius: 2 }}>
        <span className="f-display" style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{b.name || "—"}</span>
        {b.phone && <span className="f-mono" style={{ fontSize: 11, color: C.inkMid }}>{b.phone}</span>}
        <span className="f-body" style={{ fontSize: 12, color: C.terraDeep, fontStyle: "italic" }}>“{b.reason}”</span>
        <div style={{ flex: 1 }} />
        <span className="f-mono" style={{ fontSize: 9, color: C.muted }}>{String(b.createdAt).slice(0, 10)}</span>
        <Btn size="xs" variant="ghost" onClick={() => handleRemove(b.id)}>Rimuovi</Btn>
      </div>)}
    </div>}
  </div>;
}

function Clienti({ state, setState }) {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const toast = useToast();

  // Lettura via API frozen: default del core = solo attivi (tombstoned esclusi).
  const customers = useMemo(() => crmCore.listCustomers(state.customers), [state.customers]);

  // C4.1 — doppioni proposti (read-only): pannello ripiegabile sopra la lista.
  const [dupOpen, setDupOpen] = useState(false);
  const [mergeArm, setMergeArm] = useState(null); // "gi:memberId" del principale armato
  const duplicates = useMemo(() => _crmFindDuplicates(customers), [customers]);

  // Ricerca semplice per substring sul displayName (case-insensitive, come le altre viste).
  const q = search.trim().toLowerCase();
  const filtered = q ? customers.filter(c => (c.displayName || "").toLowerCase().includes(q)) : customers;

  // T2 — dettaglio via API frozen: getCustomer risolve per id (tombstoned → null → card assente).
  const selected = selectedId ? crmCore.getCustomer(state.customers, selectedId) : null;

  // ── Privacy & oblio — scritture legittime (diritto di cancellazione GDPR) ────
  // I reducer sono puri; qui il componente fa l'unico setState (+ toast).
  const handleAnonymize = (id) => {
    const by = state.currentUser?.name || "Andi";
    const res = crmCore.anonymizeCustomer(state.customers, state.customerConsents, id, { by });
    if (res.error) { toast({ type: "error", title: "Anonimizzazione non riuscita", message: res.error }); return; }
    // La scheda resta selezionata: getCustomer la ritorna ancora (non è tombstoned)
    // e il banner "Cliente anonimizzato" compare da sé.
    setState(s => ({ ...s, customers: res.customers, customerConsents: res.customerConsents }));
    toast({ type: "success", title: "Cliente anonimizzato", message: "Dati personali rimossi (oblio GDPR). Il record è ora immutabile." });
  };
  const handleDelete = (id) => {
    const by = state.currentUser?.name || "Andi";
    const res = crmCore.softDeleteCustomer(state.customers, id, { by });
    if (res.error) { toast({ type: "error", title: "Eliminazione non riuscita", message: res.error }); return; }
    setState(s => ({ ...s, customers: res.customers }));
    setSelectedId(null); // tombstoned → la card si chiude (getCustomer → null)
    toast({ type: "info", title: "Scheda eliminata", message: "La scheda cliente è stata rimossa (cancellazione logica)." });
  };
  // C4.1b — Fusione: fonde tutti i `sourceIds` nel principale `targetId`, spostando
  // link scontrino (reassignCustomer, dedup I3), prenotazioni (updateReservation,
  // bump version) e consensi (map customerId), poi tombstonando gli assorbiti.
  const handleMerge = (targetId, sourceIds) => {
    const by = state.currentUser?.name || "Andi";
    const sources = (sourceIds || []).filter(id => id && id !== targetId);
    if (sources.length === 0) return;
    let customers = state.customers, links = state.customerReceiptLinks;
    let consents = state.customerConsents, reservations = state.reservations;
    let movedLinks = 0, movedResv = 0;
    for (const sid of sources) {
      const cm = crmCore.mergeCustomers(customers, sid, targetId, { by });
      if (cm.error) { toast({ type: "error", title: "Fusione non riuscita", message: cm.error }); return; }
      customers = cm.customers;
      const rl = crmLinkCore.reassignCustomer(links, sid, targetId, { by });
      links = rl.customerReceiptLinks; movedLinks += rl.moved;
      for (const r of (Array.isArray(reservations) ? reservations : []).filter(x => x && String(x.customerId) === String(sid))) {
        const up = reservationCore.updateReservation(reservations, r.id, { customerId: String(targetId) }, { by });
        if (!up.error) { reservations = up.reservations; movedResv++; }
      }
      consents = (Array.isArray(consents) ? consents : []).map(e => e && String(e.customerId) === String(sid) ? { ...e, customerId: String(targetId) } : e);
    }
    setState(s => ({ ...s, customers, customerReceiptLinks: links, customerConsents: consents, reservations }));
    setSelectedId(targetId); setMergeArm(null);
    toast({ type: "success", title: "Clienti fusi", message: `${sources.length} scheda/e unite · ${movedLinks} scontrini · ${movedResv} prenotazioni spostate` });
  };

  // ── Archivio storico Pienissimo (ADDITIVO, separato dal CRM operativo) ──────
  // L'archivio NON è in state: si carica pigramente da IndexedDB solo qui.
  const [archivioOpen, setArchivioOpen] = useState(false);
  const [arcQuery, setArcQuery] = useState("");
  const [arcResults, setArcResults] = useState(null); // null = mai cercato
  const [arcLoading, setArcLoading] = useState(false);
  const [promotedKeys, setPromotedKeys] = useState(() => new Set());
  const vMeta = state.voyaArchiveMeta;

  // Set dei phoneKey già presenti nel CRM operativo (anti-doppione promozione).
  const crmPhoneKeys = useMemo(() => {
    const s = new Set();
    for (const c of (state.customers || [])) {
      if (!c || c.tombstone) continue;
      for (const ct of (c.contacts || [])) {
        if (ct && ct.kind === "phone") { const k = _blkPhoneKey(ct.value); if (k) s.add(k); }
      }
    }
    return s;
  }, [state.customers]);

  const runArcSearch = async () => {
    const query = arcQuery.trim();
    if (!query) { setArcResults([]); return; }
    setArcLoading(true);
    try {
      const loaded = await voyaArchiveLoad();
      if (!loaded) { setArcResults([]); toast({ type: "info", title: "Archivio non presente", message: "Importa prima l'archivio storico Pienissimo." }); return; }
      setArcResults(voyaArchiveSearch(loaded.customers, query));
    } catch (err) {
      toast({ type: "error", title: "Errore ricerca archivio", message: err.message });
    } finally {
      setArcLoading(false);
    }
  };

  // Promozione MANUALE di un cliente dall'archivio al CRM (un tocco di Andi).
  const promoteToCrm = (arc) => {
    const pk = _blkPhoneKey(arc.phone);
    if (pk && crmPhoneKeys.has(pk)) {
      toast({ type: "info", title: "Già nel CRM", message: `${arc.nome} ${arc.cognome} è già tra i clienti.` });
      setPromotedKeys(prev => new Set(prev).add(arc.key));
      return;
    }
    const displayName = `${arc.nome || ""} ${arc.cognome || ""}`.trim() || (arc.phone || "Cliente");
    const contacts = [
      arc.phone ? { kind: "phone", value: String(arc.phone) } : null,
      arc.email ? { kind: "email", value: String(arc.email) } : null,
    ].filter(Boolean);
    const by = state.currentUser?.name || "Andi";
    const res = crmCore.createCustomer(state.customers, { type: "person", displayName, contacts, notes: "Da archivio storico Pienissimo" }, { by });
    if (res.error) { toast({ type: "error", title: "Creazione fallita", message: res.error }); return; }
    setState({ ...state, customers: res.customers });
    setPromotedKeys(prev => new Set(prev).add(arc.key));
    toast({ type: "success", title: "Cliente aggiunto al CRM", message: displayName });
  };

  const arcYear = (d) => (d ? String(d).slice(0, 4) : "?");

  const archivioSection = <div style={{ marginBottom: 24, border: `1px solid ${C.rule}`, borderRadius: 2, background: C.white }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderBottom: `1px solid ${C.rule}` }}>
      <Database size={18} color={C.tealDeep} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="f-display" style={{ fontSize: 15, fontWeight: 500, color: C.ink }}>Archivio storico Pienissimo</div>
        <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
          {vMeta
            ? `${Number(vMeta.count || 0).toLocaleString("it-IT")} clienti · ${Number(vMeta.regulars || 0).toLocaleString("it-IT")} abituali · dal ${arcYear(vMeta.rangeFirst)} al ${arcYear(vMeta.rangeLast)}`
            : "Nessun archivio importato. Carica l'export clienti da Pienissimo."}
        </div>
      </div>
      <Btn variant="ghost" size="sm" icon={Upload} onClick={() => setArchivioOpen(true)}>
        {vMeta ? "Aggiorna archivio" : "Importa archivio"}
      </Btn>
    </div>
    {vMeta && <div style={{ padding: "14px 18px" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, background: C.paperDeep, border: `1px solid ${C.rule}`, padding: "8px 14px", borderRadius: 2 }}>
          <Search size={15} color={C.muted} />
          <input value={arcQuery} onChange={e => setArcQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") runArcSearch(); }}
            placeholder="Cerca nell'archivio storico: nome o telefono..." className="f-body"
            style={{ border: "none", outline: "none", flex: 1, fontSize: 13, background: "transparent", color: C.ink }} />
          {arcQuery && <IconBtn icon={X} size={13} onClick={() => { setArcQuery(""); setArcResults(null); }} />}
        </div>
        <Btn variant="accent" size="sm" onClick={runArcSearch} disabled={arcLoading}>{arcLoading ? "Cerco..." : "Cerca"}</Btn>
      </div>
      {arcResults != null && <div style={{ marginTop: 14 }}>
        {arcResults.length === 0
          ? <div className="f-body" style={{ fontSize: 12, color: C.muted, padding: "8px 2px" }}>Nessun cliente nell'archivio corrisponde alla ricerca.</div>
          : <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.15em", color: C.muted, textTransform: "uppercase", marginBottom: 2 }}>
              {arcResults.length} risultat{arcResults.length === 1 ? "o" : "i"}{arcResults.length >= 50 ? " (primi 50)" : ""}
            </div>
            {arcResults.map(c => {
              const pk = _blkPhoneKey(c.phone);
              const already = promotedKeys.has(c.key) || (pk && crmPhoneKeys.has(pk));
              return <div key={c.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: C.paperDeep, border: `1px solid ${C.ruleSoft}`, borderRadius: 2 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="f-display" style={{ fontSize: 14, fontWeight: 500, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {`${c.nome || ""} ${c.cognome || ""}`.trim() || "(senza nome)"}
                  </div>
                  <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    {c.phone ? c.phone + " · " : ""}{c.visite} visit{c.visite === 1 ? "a" : "e"} · {c.coperti} coperti · dal {c.first || "?"} al {c.last || "?"}
                  </div>
                </div>
                {already
                  ? <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.oliveDeep, textTransform: "uppercase", border: `1px solid ${C.olive}`, padding: "4px 10px", borderRadius: 2 }}>Nel CRM</span>
                  : <Btn variant="ghost" size="sm" icon={Plus} onClick={() => promoteToCrm(c)}>Aggiungi al CRM</Btn>}
              </div>;
            })}
          </div>}
      </div>}
    </div>}
    {archivioOpen && <ArchivioPienissimoImporter state={state} setState={setState} onClose={() => setArchivioOpen(false)} />}
  </div>;

  return <div className="fade-up">
    <PageHeader kicker="CRM · Scheda cliente P0" title="Clienti"
      subtitle="Anagrafica clienti della Foundation CRM. Seleziona un cliente dalla lista per aprire la scheda di dettaglio." />

    {archivioSection}

    {customers.length === 0
      ? <EmptyState icon={Users} title="Nessun cliente registrato"
          body="L'anagrafica clienti è vuota. La creazione dei clienti verrà abilitata nelle prossime fasi del CRM." />
      : <>
        <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, background: C.white, border: `1px solid ${C.rule}`, padding: "8px 14px", borderRadius: 2 }}>
            <Search size={15} color={C.muted} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca cliente..." className="f-body" style={{ border: "none", outline: "none", flex: 1, fontSize: 13, background: "transparent", color: C.ink }} />
            {search && <IconBtn icon={X} size={13} onClick={() => setSearch("")} />}
          </div>
          <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase" }}>
            {filtered.length} {filtered.length === 1 ? "cliente" : "clienti"}
          </div>
        </div>

        {/* C4.1 — Possibili doppioni: solo SEGNALAZIONE (F8). Clic su un cliente ne
            apre la scheda; nessuna fusione automatica. */}
        {duplicates.length > 0 && <div style={{ marginBottom: 20, border: `1px solid ${C.amber}`, borderRadius: 2, overflow: "hidden" }}>
          <div onClick={() => setDupOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: C.amberWash, cursor: "pointer" }}>
            <span aria-hidden="true">⚠</span>
            <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: C.amberDeep, fontWeight: 700 }}>
              {duplicates.length} possibil{duplicates.length === 1 ? "e doppione" : "i doppioni"}
            </span>
            <span className="f-body" style={{ fontSize: 11, color: C.inkMid }}>clienti che condividono telefono, email o nome</span>
            <div style={{ flex: 1 }} />
            <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: C.amberDeep }}>{dupOpen ? "nascondi" : "mostra"}</span>
          </div>
          {dupOpen && <div style={{ padding: "6px 14px 12px", background: C.white }}>
            {duplicates.map((g, gi) => <div key={gi} style={{ padding: "8px 0", borderBottom: gi < duplicates.length - 1 ? `1px solid ${C.ruleSoft}` : "none" }}>
              <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: C.muted, marginBottom: 6 }}>
                Stesso {g.reason}{(g.reason === "telefono" || g.reason === "email") ? `: ${g.key}` : ""}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {g.members.map(m => {
                  const armKey = `${gi}:${m.id}`;
                  const armed = mergeArm === armKey;
                  return <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, background: C.paperDeep, border: `1px solid ${armed ? C.terra : C.rule}`, borderRadius: 2, padding: "5px 7px 5px 10px" }}>
                    <button className="tx" onClick={() => setSelectedId(m.id)} title="Apri la scheda"
                      style={{ textAlign: "left", cursor: "pointer", background: "transparent", border: "none", padding: 0 }}>
                      <span className="f-body" style={{ fontSize: 13, color: C.ink }}>{m.displayName}</span>
                      <span className="f-mono" style={{ fontSize: 10, color: C.muted, marginLeft: 8 }}>{(m.contacts || []).length} contatt{(m.contacts || []).length === 1 ? "o" : "i"}</span>
                    </button>
                    <button className="tx f-mono" title={armed ? "Conferma: unisci gli altri in questa scheda" : "Tieni questa scheda e unisci qui gli altri del gruppo"}
                      onClick={() => { if (!armed) { setMergeArm(armKey); return; } handleMerge(m.id, g.members.filter(x => x.id !== m.id).map(x => x.id)); }}
                      style={{ fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer", padding: "3px 8px", borderRadius: 2, flexShrink: 0,
                        border: `1px solid ${armed ? C.terra : C.ruleSoft}`, background: armed ? C.terra : C.white, color: armed ? "#fff" : C.inkMid }}>
                      {armed ? "Unisci gli altri qui ✓" : "★ Tieni questo"}
                    </button>
                  </div>;
                })}
              </div>
            </div>)}
            <div className="f-body" style={{ fontSize: 10, color: C.mutedSoft, marginTop: 8, fontStyle: "italic" }}>
              Solo segnalazione: clicca un cliente per aprirne la scheda. La fusione non è automatica.
            </div>
          </div>}
        </div>}

        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
        {filtered.length === 0
          ? <EmptyState icon={Users} title="Nessun cliente corrisponde" body="Prova a modificare la ricerca" />
          : <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filtered.map(c => {
              const sel = c.id === selectedId;
              return <div key={c.id} className="tx" onClick={() => setSelectedId(sel ? null : c.id)}
                style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", background: sel ? C.tealWash : C.white, border: `1px solid ${sel ? C.teal : C.rule}`, borderRadius: 2, cursor: "pointer" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="f-display" style={{ fontSize: 16, fontWeight: 500, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.displayName}</div>
                  {(c.contacts || []).length > 0 && <div className="f-body" style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                    {(c.contacts || []).length} {(c.contacts || []).length === 1 ? "contatto" : "contatti"}
                  </div>}
                </div>
                {c.anonymized && <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.muted, textTransform: "uppercase", border: `1px solid ${C.rule}`, padding: "3px 8px", borderRadius: 2 }}>Anonimizzato</span>}
                <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: sel ? C.tealDeep : C.inkMid, textTransform: "uppercase", background: sel ? "transparent" : C.paperDeep, border: sel ? `1px solid ${C.teal}` : `1px solid ${C.ruleSoft}`, padding: "3px 8px", borderRadius: 2 }}>
                  {CRM_UI_TYPE_LABELS[c.type] || c.type}
                </span>
              </div>;
            })}
          </div>}
          </div>
          {selected && <CustomerDetailCard customer={selected} state={state} onClose={() => setSelectedId(null)} onAnonymize={handleAnonymize} onDelete={handleDelete} />}
        </div>
      </>}
  </div>;
}
// ⟦CRM-UI-P0:END⟧

// ⟦RESV-UI:START⟧ — Agenda V1 · T3 (read + add) + T4 (stati + edit inline).
// CONSUMER puro di reservationCore (⟦RESV-CORE⟧): lettura via listReservationsForDay,
// scritture SOLO via createReservation / changeReservationStatus / updateReservation.
// R1-T2 aggiunge la SOLA lettura CRM (crmCore.listCustomers/getCustomer, ⟦CRM-CORE⟧)
// per collegare un cliente ESISTENTE alla prenotazione: Reservation.customerId =
// Customer.id — MAI un CustomerReceiptLink (quello pinna chiavi da un receipt reale,
// che qui non esiste ancora, ADR §4). R1-T3 aggiunge badge visite (una sola
// crmVisitProjection.projectVisits memoizzata, solo se il giorno ha linked) e la
// suggestion "probable customer": DERIVATA a runtime, mai persistita — propone,
// non decide; diventa fatto solo con la conferma esplicita. serviceDate e service
// NON si chiedono mai: ereditati dal giorno selezionato e dalla tab attiva. Contatori
// derivati in UI, mai persistiti. Cambio stato = bottoni contestuali per riga (niente
// modali, adatto al telefono durante il servizio); correzione esplicita "↺ Prenotato"
// per gli stati chiusi (il reducer la ammette come transizione esplicita, ADR §5).
// Edit inline coperti/tavolo/note = pattern del widget COPERTI (entra/salva/annulla).
// Ordinamento dichiarato (R3-T2): per time crescente, senza orario in coda, fallback
// createdAt crescente; raggruppamento in fasce orarie di presentazione. Tutto derivato
// in UI dal campo time (Addendum R3): il dato persistito non cambia mai.
// Visibilità dichiarata (R3-T3): ricerca substring (rawName + displayName collegato)
// e filtri chip nel solo giorno/servizio corrente; il default "Tutte" ESCLUDE le
// chiuse (cancelled/no_show), che restano dietro il chip "Chiuse". Solo visibilità
// derivata: nessuna prenotazione viene mai modificata o nascosta nel dato.
// Modalità (R3-T4): "Lista" (tutto quanto sopra, intatto) o "Arrivi" — board di
// servizio con sole booked+seated ordinate T2, contatori in COPERTI
// (attesi/arrivati/mancanti), bottone unico "Arrivato" (changeReservationStatus →
// seated) e ritardo derivato al render SOLO per oggi (time < now − 20', nessun
// timer/polling). Stato UI runtime, mai persistito.
// Walk-in (R3-T5): quick-add minimale in Arrivi — createReservation (source
// "walkin", time = adesso) + changeReservationStatus → seated calcolati in
// sequenza sulle riduzioni pure, UN SOLO setState se entrambe vanno a buon fine.
// Fulfilment (R5-T3, ADR-CRM-11): visibilità READ-ONLY — badge per riga in Lista
// (collegato con importo / da collegare / link orfano), sezione Scontrino
// nell'espansione, contatore "da collegare" sul giorno. Tutto derivato via
// reservationFulfilCore.deriveReservationFulfilment (F2).
// Fulfilment (R5-T4): workflow MANUALE puro — picker degli scontrini reali del
// giorno/servizio (perimetro via getServiceDate/getShift esistenti, dati riga
// via facade; nel link finiscono SOLO chiavi stabili, F3), Collega =
// createReservationReceiptLink origin "manual", Scollega = tombstone (F7).
// L'operatore sceglie, il sistema non decide (F8): niente scoring persistito,
// N:M ammesso col receipt condiviso segnalato (F5/F6).
// Fulfilment (R5-T5): suggerimenti CONSERVATIVI (ADR-CRM-11 §7) — euristiche
// runtime pure (gate giorno/servizio; tavolo canonico, coperti, orario
// plausibile, seated; banco e già-collegato penalizzati, mai nascosti), score
// SOLO variabile interna di ordinamento (mai persistito, mai nel link, mai
// "verità" in UI), motivazioni leggibili, classificazione Suggerito/Da
// verificare/Ambiguo. MAI auto-link: il gesto resta il Collega manuale T4,
// con origin "suggestion_confirmed" come solo audit.
// Tavoli (R4-T2, Addendum R4 ADR Floor Core): terza modalità — griglia per area
// derivata a runtime (tavoli attivi × prenotazioni attive via resolveTokens già
// memoizzato), stati Libero/Prenotato/Al tavolo/Turni/Conflitto (≥2 seated =
// conflitto; booked multipli = turni, informativo MAI errore; overflow solo se
// seats > 0). R4-T3/T4 aggiungono l'assegnazione operativa: strip "Da assegnare"
// e "Tavolo sconosciuto", selezione runtime, tap-assegna con multi-tavolo
// (join canonico " + "), conferma a due tocchi sui non liberi, bonifica dei
// token unknown. UNICA scrittura: updateReservation({ table: testo|vuoto }).
// Selezione/conferma mai persistite; label con separatori → rifiuto, mai
// normalizzazioni inventate.
const RESV_UI_STATUS_LABELS = { booked: "Prenotato", seated: "Al tavolo", cancelled: "Annullata", no_show: "No-show" };
const RESV_UI_STATUS_COLORS = {
  booked:    { fg: C.tealDeep,  border: C.teal },
  seated:    { fg: C.oliveDeep, border: C.olive },
  cancelled: { fg: C.muted,     border: C.rule },
  no_show:   { fg: C.terraDeep, border: C.terra },
};
// R1 — provenienza (Addendum R1). Default "phone": se la segretaria non tocca
// nulla, il comportamento è identico a prima. In riga il badge appare SOLO per
// le provenienze non-telefono (il telefono è la norma: mostrarlo ovunque è rumore).
const RESV_UI_SOURCE_LABELS = { phone: "Telefono", web: "Web", walkin: "Walk-in", other: "Altro" };
// C2.5 — link WhatsApp con conferma precompilata. Normalizzazione prudente:
// solo cifre; un mobile italiano scritto senza prefisso (10 cifre, inizia per 3)
// riceve il 39; il resto passa com'è (numeri già internazionali). Nessun invio
// automatico: si apre WhatsApp col testo pronto, il gesto resta umano.
const _resvWaLink = (r) => {
  const digits = String(r.phone || "").replace(/\D/g, "");
  if (digits.length < 6) return null;
  const intl = digits.length === 10 && digits.startsWith("3") ? "39" + digits : digits;
  const giorno = r.serviceDate ? `${r.serviceDate.slice(8)}/${r.serviceDate.slice(5, 7)}` : "";
  const testo = `Ciao! Confermiamo la prenotazione da Nialtri${giorno ? ` per il ${giorno}` : ""}${r.time ? ` alle ${r.time}` : ""}, ${r.covers} ${Number(r.covers) === 1 ? "persona" : "persone"} a nome ${r.rawName}. A presto!`;
  return `https://wa.me/${intl}?text=${encodeURIComponent(testo)}`;
};
// R1-T3 — normalizzazione nome per la suggestion (solo confronto UI, mai persistita).
const _resvNormName = (s) => String(s || "").toLowerCase().trim().replace(/\s+/g, " ");
// R1-T3 — matching CONSERVATIVO rawName→Customer: la suggestion propone, non decide.
// Tier 1: uguaglianza esatta normalizzata col displayName. Tier 2 (solo se il rawName
// è una singola parola, com'è tipico in agenda): uguaglianza esatta con l'ULTIMA parola
// del displayName (cognome intero). Niente fuzzy. Candidato unico → suggestion;
// 0 o ≥2 candidati → NULLA: meglio il silenzio di un falso positivo.
const _resvSuggestCustomer = (rawName, customers) => {
  const raw = _resvNormName(rawName);
  if (!raw) return null;
  const exact = customers.filter(c => _resvNormName(c.displayName) === raw);
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) return null;
  if (raw.includes(" ") || raw.length < 2) return null;
  const bySurname = customers.filter(c => {
    const parts = _resvNormName(c.displayName).split(" ");
    return parts.length > 1 && parts[parts.length - 1] === raw;
  });
  return bySurname.length === 1 ? bySurname[0] : null;
};
// R1-T4 — "quanto tempo fa" leggibile per l'ultima visita (solo presentazione).
const _resvDaysAgo = (ms) => {
  const d = Math.floor((Date.now() - ms) / 86400000);
  return d <= 0 ? "oggi" : d === 1 ? "ieri" : `${d} giorni fa`;
};
// R1-T5 — segnali ricorrenti dalle note passate: conteggio per-nota (document
// frequency) su parole (≥4 lettere) e coppie di parole adiacenti ("lato mare",
// "senza glutine"). RICORRENTE = compare in ALMENO 2 note distinte: niente NLP,
// niente fuzzy, niente categorie inventate — se nulla ricorre, si mostra al più
// l'ultima nota com'è. Indicazione operativa, mai dato autoritativo.
const _RESV_NOTE_STOP = new Set(["alla", "allo", "alle", "agli", "con", "per", "che", "del", "della", "delle", "dei", "degli", "una", "uno", "non", "sono", "come", "anche", "tavolo", "persone", "coperti", "prenotazione"]);
const _resvRecurringNoteSignals = (notes) => {
  const perNote = notes.map(n => {
    const toks = String(n).toLowerCase().replace(/[^\p{L}\p{N} ]/gu, " ").split(/\s+/).filter(t => t.length >= 2);
    const grams = new Set();
    toks.forEach((t, i) => {
      if (t.length >= 4 && !_RESV_NOTE_STOP.has(t)) grams.add(t);
      if (i > 0 && !_RESV_NOTE_STOP.has(t) && !_RESV_NOTE_STOP.has(toks[i - 1])) grams.add(`${toks[i - 1]} ${t}`);
    });
    return grams;
  });
  const df = new Map();
  for (const g of perNote) for (const t of g) df.set(t, (df.get(t) || 0) + 1);
  const rec = [...df.entries()].filter(([, n]) => n >= 2).sort((a, b) => b[1] - a[1]);
  // Preferenza al bigramma: le singole parole già coperte da una coppia ricorrente si tacciono.
  const bigrams = rec.filter(([t]) => t.includes(" "));
  const covered = new Set(bigrams.flatMap(([t]) => t.split(" ")));
  const singles = rec.filter(([t]) => !t.includes(" ") && !covered.has(t));
  return [...bigrams, ...singles].slice(0, 3).map(([t]) => t);
};
// Giorno ± delta in ISO locale (pattern "T12:00:00" già usato dal codebase per
// evitare slittamenti di timezone sul boundary di mezzanotte).
const _resvShiftDay = (day, delta) => {
  const d = new Date(day + "T12:00:00");
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
};
// R3-T2 — ordinamento professionale (solo presentazione, mai persistito): time
// crescente ("HH:MM" confronta bene come stringa), senza orario in coda; a parità
// di time — o nel bucket senza orario — fallback createdAt crescente, cioè il
// vecchio ordine di inserimento resta il tie-break.
const _resvCompareByTime = (a, b) => {
  const ta = a.time || null, tb = b.time || null;
  if (ta !== tb) {
    if (ta === null) return 1;
    if (tb === null) return -1;
    if (ta < tb) return -1;
    if (ta > tb) return 1;
  }
  return String(a.createdAt || "").localeCompare(String(b.createdAt || ""));
};
// R3-T2 — fascia oraria di presentazione: "20:15" → "20:00–21:00"; senza time →
// "Senza orario". Derivata dall'ora della prenotazione al volo, mai persistita.
const _resvHourBand = (time) => {
  if (!time) return "Senza orario";
  const h = String(time).slice(0, 2);
  const next = String((parseInt(h, 10) + 1) % 24).padStart(2, "0");
  return `${h}:00–${next}:00`;
};
// R3-T3 — filtri chip a selezione singola (derivazione UI pura, mai persistiti).
// Semantica chiave: "Tutte" NON include le chiuse (cancelled/no_show) — le chiuse
// escono dalla lista operativa e restano raggiungibili solo dal chip "Chiuse".
const RESV_UI_FILTERS = [
  { key: "all", label: "Tutte" },
  { key: "unassigned", label: "Da assegnare" },
  { key: "seated", label: "Arrivati" },
  { key: "unknown_table", label: "Tavolo sconosciuto" },
  { key: "to_link", label: "Da collegare" },
  { key: "closed", label: "Chiuse" },
];
const _resvIsActiveStatus = (r) => r.status === "booked" || r.status === "seated";
// tres = entry di tableResolution (R2-T5) per la prenotazione: la logica di parsing
// tavolo NON si duplica qui, si legge solo il suo esito (unknownCount).
const _resvFilterPass = (r, key, tres, fulfil) => {
  switch (key) {
    case "all": return _resvIsActiveStatus(r);
    case "unassigned": return _resvIsActiveStatus(r) && (r.table == null || String(r.table).trim() === "");
    case "seated": return r.status === "seated";
    case "unknown_table": return _resvIsActiveStatus(r) && !!tres && tres.unknownCount > 0;
    // R5.9 — coda fulfilment: legge SOLO l'esito già derivato da fulfilmentByResv
    // (R5-T3: giorno passato, attiva, nessun link risolto/orfano). Mai ricalcolato qui.
    case "to_link": return !!fulfil && fulfil.needsLinking === true;
    case "closed": return r.status === "cancelled" || r.status === "no_show";
    default: return true;
  }
};
// R4-T2 — stati derivati della card tavolo (Addendum R4): resa per stato.
// "turns" (booked multipli) è volutamente NEUTRO come "booked": un secondo turno
// sullo stesso tavolo è normale amministrazione, non un problema. Solo il
// conflitto (≥2 seated contemporanee) usa il registro d'allerta terra.
const RESV_UI_TABLE_STATES = {
  free:     { label: "Libero",    fg: C.muted,     border: C.rule,  bg: C.white },
  booked:   { label: "Prenotato", fg: C.tealDeep,  border: C.teal,  bg: C.tealWash },
  turns:    { label: "Turni",     fg: C.tealDeep,  border: C.teal,  bg: C.tealWash },
  seated:   { label: "Al tavolo", fg: C.oliveDeep, border: C.olive, bg: C.oliveWash },
  conflict: { label: "Conflitto", fg: C.terraDeep, border: C.terra, bg: C.terraWash },
};
// R4-T4 — round-trip delle label (Addendum R4): una label che contiene un
// separatore canonico del parser (| + , /) non risolverebbe più su se stessa
// dopo il join. In quel caso l'operazione si RIFIUTA, senza inventare
// normalizzazioni. Stessi caratteri di _FLOOR_TOKEN_SEPARATORS del core.
const _resvLabelCleanForJoin = (label) => !/[|+,/]/.test(String(label));
// R6-T2 — TAVOLO CONSIGLIATO (docs/AGENDA_PIENISSIMO_REPLACEMENT.md, F1).
// Euristica di PRESENTAZIONE pura sul board già derivato: mai persistita, mai
// auto-assegnazione — accende i candidati, l'operatore tocca. Regole dichiarate:
//   • solo tavoli con seats > 0 (capienza non dichiarata = mai inventata) e
//     seats ≥ coperti (mai proporre sotto-capienza: lo strizzo lo decide l'umano);
//   • rank: liberi prima, poi spreco minimo (seats − coperti), poi seats, poi label;
//   • DOPPIO TURNO: un tavolo booked/turns è candidato (rank dopo i liberi) se
//     TUTTE le sue prenotazioni distano ≥ 2h dall'orario della selezionata —
//     nessuna durata dedotta, solo distanza dichiarata tra orari; senza orario
//     niente candidatura da turno;
//   • AREE DI RISERVA (regola Andi 06/07): la Pedana si usa solo nei giorni di
//     grande afflusso → i suoi tavoli vanno in FONDO al rank, mai esclusi:
//     nei giorni normali non emergono mai (vince il resto), quando tutto il
//     resto è pieno riemergono da soli — senza interruttori da ricordare;
//   • COMBINAZIONI (C2.2): per i gruppi che nessun tavolo singolo regge, si
//     propongono UNIONI di tavoli — MAI inventate: solo combinazioni già usate
//     ≥2 volte nello storico prenotazioni (il "431|430" scritto dalla
//     segretaria è la prova che quei tavoli si uniscono davvero). Candidabile
//     solo se TUTTI i membri sono liberi; rank: dopo i singoli liberi, prima
//     dei secondi turni.
const RESV_RECO_RESERVE_AREAS = new Set(["pedana"]);
const RESV_RECO_TURN_GAP_MIN = 120;
const RESV_RECO_COMBO_MIN_SEEN = 2;
// Canone token locale (stessa semantica del core, reimplementata: mai chiamare
// gli helper privati dei blocchi congelati).
const _resvComboCanonTok = (raw) => {
  const s = String(raw == null ? "" : raw).trim();
  if (!s) return null;
  const stripped = s.replace(/^(?:tavolo|tav|t|#)\s*\.?\s*(?=\d)/i, "").trim();
  if (/^\d+$/.test(stripped)) return String(parseInt(stripped, 10));
  return normStr(stripped) || null;
};
// Combinazioni CONOSCIUTE derivate dallo storico (derivazione pura, mai
// persistita): gruppi di 2-3 token canonici visti insieme ≥2 volte, mappati
// sui tavoli ATTIVI del board (tutti i membri devono esistere e avere posti).
const _resvKnownCombos = (reservations, board) => {
  if (!Array.isArray(reservations) || !Array.isArray(board)) return [];
  const byCanon = new Map();
  for (const g of board) {
    for (const tc of g.tables) {
      const k = _resvComboCanonTok(tc.table.label);
      if (k != null && !byCanon.has(k)) byCanon.set(k, { tc, areaName: g.area.name });
    }
  }
  const seen = new Map();
  for (const r of reservations) {
    if (!r || r.tombstone || r.table == null) continue;
    const toks = String(r.table).split(_FLOOR_TOKEN_SEPARATORS)
      .map(_resvComboCanonTok).filter(Boolean);
    if (toks.length < 2 || toks.length > 3) continue;
    const uniq = [...new Set(toks)].sort();
    if (uniq.length < 2) continue;
    const key = uniq.join("+");
    seen.set(key, (seen.get(key) || 0) + 1);
  }
  const out = [];
  for (const [key, count] of seen) {
    if (count < RESV_RECO_COMBO_MIN_SEEN) continue;
    const members = key.split("+").map(k => byCanon.get(k));
    if (members.some(m => !m || m.tc.seats <= 0)) continue;
    out.push({
      key,
      members: members.map(m => m.tc),
      labels: members.map(m => m.tc.table.label),
      seats: members.reduce((s, m) => s + m.tc.seats, 0),
      reserve: members.some(m => RESV_RECO_RESERVE_AREAS.has(normStr(m.areaName) || "")),
      count,
    });
  }
  return out;
};
const _resvTimeToMin = (t) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || "").trim());
  if (!m) return null;
  let min = Number(m[1]) * 60 + Number(m[2]);
  if (Number(m[1]) < 4) min += 1440; // post-mezzanotte = coda del servizio (stessa convenzione getShift)
  return min;
};
// ═══ SALA CHE IMPARA (feedback ragazze 08/07: "il sistema deve imparare da
// come predisponiamo i tavoli, mai sprecare posti") ═══
// (A) AFFINITÀ tavolo↔coperti dallo storico prenotazioni: ogni prenotazione
// passata con tavolo SINGOLO e coperti è un voto ("il 431 lo usiamo per i 4").
// Fonte: SOLO store sincronizzati — il tablet non ha gli scontrini (D2); i
// coperti reali degli scontrini entrano comunque via seats (R6-T1, dal Mac).
// Escluse solo le annullate: un no-show era comunque una scelta di
// predisposizione. Le combo hanno già il loro motore (C2.2). PURO, testabile.
const _resvTableAffinity = (reservations) => {
  const m = new Map();
  for (const r of (Array.isArray(reservations) ? reservations : [])) {
    if (!r || r.tombstone || r.status === "cancelled") continue;
    const cov = Number(r.covers);
    if (!Number.isInteger(cov) || cov < 1) continue;
    const t = r.table != null ? String(r.table).trim() : "";
    if (!t || /[|+,/]/.test(t)) continue;
    const k = _resvComboCanonTok(t);
    if (!k) continue;
    if (!m.has(k)) m.set(k, new Map());
    const h = m.get(k);
    h.set(cov, (h.get(cov) || 0) + 1);
  }
  return m;
};
const _resvAffinityScore = (affinity, label, covers) => {
  if (!affinity) return 0;
  const h = affinity.get(_resvComboCanonTok(label));
  return h ? (h.get(Number(covers)) || 0) : 0;
};
// (A-real) AFFINITÀ dai COPERTI REALI degli scontrini (feedback Andi 09/07:
// "imparare dagli scontrini dove vedono i coperti", non solo dal prenotato).
// Ogni scontrino di un tavolo servito è un voto REALE ("il 431 stasera ha
// servito 4"). Stesso spazio-chiavi dell'affinità da prenotazioni (tutto passa
// per _resvComboCanonTok). Esclude banco (P10) e EC riconciliato (stessa regola
// incassi: evita di contare due volte lo stesso tavolo EC+SF). PURO, testabile.
const _resvTableAffinityReal = (tableReceipts) => {
  const m = new Map();
  for (const rc of (Array.isArray(tableReceipts) ? tableReceipts : [])) {
    if (!rc || rc.isBanco) continue;
    if (!_receiptCountsForRevenue(rc)) continue;
    const cov = Number(rc.covers);
    if (!Number.isInteger(cov) || cov < 1) continue;
    const k = _resvComboCanonTok(receiptFacade.getReceiptTable(rc));
    if (!k) continue;
    if (!m.has(k)) m.set(k, new Map());
    const h = m.get(k);
    h.set(cov, (h.get(cov) || 0) + 1);
  }
  return m;
};
// Sorgente PREFERITA = scontrini reali quando ci sono (Mac). Il tablet non ha
// gli scontrini (D2) → fallback trasparente al prenotato. Dove esistono voti
// reali si usano quelli; i tavoli MAI visti su scontrino ereditano dal prenotato
// (nessun doppio conteggio: per-tavolo una sola sorgente). Ogni device impara dal
// meglio che ha in casa; nessuna divergenza persistita (derivazione runtime).
const _resvTableAffinityMerged = (reservations, tableReceipts) => {
  const real = _resvTableAffinityReal(tableReceipts);
  if (real.size === 0) return _resvTableAffinity(reservations);
  const booked = _resvTableAffinity(reservations);
  for (const [tok, h] of booked) if (!real.has(tok)) real.set(tok, h);
  return real;
};
// SCARTO COPERTI prenotato→reale (feedback Andi 09/07: "captare se c'è
// differenza tra n° prenotazioni e coperti tavolo… sono venuti in meno? quanti
// soldi persi?"). Derivazione PURA per giorno/servizio, sola lettura. ONESTA e
// conservativa — non inventa perdite:
//   • shortfall = "venuti in meno": SOLO da prenotazioni con scontrino collegato
//     dove prenotati > coperti reali (perdita CERTA);
//   • noShow = "non venuti": SOLO status esplicito no_show (perdita CERTA);
//   • unclear = passato senza scontrino collegato: NON è perdita (può essere solo
//     non collegato) → conteggiato a parte, invita a collegare (retroazione punto 1);
//   • over = arrivati in PIÙ del prenotato (walk-in accorpati / sotto-prenotato);
//   • €/coperto = spesa REALE degli scontrini / coperti reali del servizio (mai
//     una media inventata); soldi persi = (shortfall + noShow) × €/coperto reale.
const _resvCoversVariance = (state, day, service) => {
  const s = state || {};
  const resvs = (Array.isArray(s.reservations) ? s.reservations : [])
    .filter(r => r && !r.tombstone && r.serviceDate === day && r.service === service);
  const dayPast = day < _resvServiceToday();
  let bookedFulfilled = 0, actualFulfilled = 0, shortfallCovers = 0, overCovers = 0;
  let noShowCovers = 0, unclearCovers = 0, realSpendCents = 0;
  let nFulfilled = 0, nNoShow = 0, nUnclear = 0;
  for (const r of resvs) {
    const booked = Number(r.covers) || 0;
    if (r.status === "cancelled") continue; // annullata = scelta legittima, non perdita
    if (r.status === "no_show") { noShowCovers += booked; nNoShow++; continue; }
    const f = reservationFulfilCore.deriveReservationFulfilment(s, r.id);
    if (f.fulfilled) {
      const actual = f.resolved.reduce((a, x) => a + (Number(x.receipt && x.receipt.covers) || 0), 0);
      bookedFulfilled += booked; actualFulfilled += actual; nFulfilled++;
      realSpendCents += Number(f.realSpendCents) || 0;
      if (booked > actual) shortfallCovers += (booked - actual);
      else if (actual > booked) overCovers += (actual - booked);
    } else if (dayPast) {
      unclearCovers += booked; nUnclear++;
    }
  }
  const avgPerCoverCents = actualFulfilled > 0 ? Math.round(realSpendCents / actualFulfilled) : 0;
  const lostCovers = shortfallCovers + noShowCovers;
  const lostRevenueCents = avgPerCoverCents > 0 ? lostCovers * avgPerCoverCents : 0;
  return {
    bookedFulfilled, actualFulfilled, shortfallCovers, overCovers, noShowCovers, unclearCovers,
    lostCovers, avgPerCoverCents, lostRevenueCents, realSpendCents,
    nFulfilled, nNoShow, nUnclear,
    hasData: nFulfilled > 0 || nNoShow > 0,
  };
};
// (B) FINESTRA WALK-IN: un tavolo PRENOTATO PIÙ TARDI non è una zona morta —
// può ospitare un walk-in ADESSO se TUTTE le prenotazioni non ancora sedute
// distano ≥ 2h (stessa soglia del 2° turno) dall'ora corrente. Ritorna anche
// "fino a quando" (la prima prenotazione futura) da dire alla ragazza.
// nowMin nella convenzione di _resvTimeToMin (<4:00 → +1440).
const _resvWalkinWindow = (tc, nowMin) => {
  if (!tc || tc.status === "seated" || tc.status === "conflict") return { ok: false, until: null };
  const rs = (tc.reservations || []).filter(r => r && r.status !== "seated");
  if (rs.length === 0) return { ok: tc.status === "free", until: null };
  let first = null;
  for (const r of rs) {
    const m = _resvTimeToMin(r.time);
    if (m == null || m - nowMin < RESV_RECO_TURN_GAP_MIN) return { ok: false, until: null };
    if (first == null || m < first) first = m;
  }
  return { ok: true, until: `${String(Math.floor(first / 60) % 24).padStart(2, "0")}:${String(first % 60).padStart(2, "0")}` };
};
// Minuto corrente nella convenzione del motore (post-mezzanotte = coda servizio).
const _resvNowMin = () => {
  const d = new Date();
  const n = d.getHours() * 60 + d.getMinutes();
  return n < 240 ? n + 1440 : n;
};
// AUDIT-FIX (date, ispettore #5): il "giorno di servizio" corrente per l'AGENDA
// deve seguire la STESSA convenzione degli scontrini (getServiceDate: la coda
// dopo mezzanotte, <nightEnd, appartiene alla cena del giorno prima). Usare
// todayLocalISO() faceva "saltare" la cena in corso al nuovo giorno dopo la
// mezzanotte e faceva nascere i walk-in con serviceDate sbagliato → mai
// riconciliati con lo scontrino. Un solo punto di verità, riletto a ogni uso.
const _resvServiceToday = () => getServiceDate(new Date().toISOString());
// Servizio corrente dedotto dall'ora (per il default della vista Agenda).
const _resvServiceNow = () => { const s = getShift(new Date().toISOString()); return s === "pranzo" ? "pranzo" : "cena"; };
// C2.1 — TIMELINE TURNI per tavolo: presentazione PURA di tableBoard sul filo
// orario del servizio. La larghezza dei blocchi è una CONVENZIONE VISIVA di
// 120 min (stessa soglia del 2° turno del consigliato) — MAI una durata dedotta
// (ADR R4: niente turnover dedotto dagli orari). Solo tavoli con prenotazioni:
// i liberi si leggono già in griglia. Post-mezzanotte: stessa convenzione
// _resvTimeToMin (<4:00 = coda del servizio).
const RESV_TIMELINE_WINDOWS = { pranzo: [11 * 60 + 30, 16 * 60], cena: [18 * 60 + 30, 25 * 60] };
const RESV_TIMELINE_BLOCK_MIN = 120;
const _resvTimelineLayout = (board, service) => {
  const win = RESV_TIMELINE_WINDOWS[service] || RESV_TIMELINE_WINDOWS.cena;
  const [start, end] = win;
  const span = end - start;
  const areas = [];
  for (const g of (Array.isArray(board) ? board : [])) {
    const rows = [];
    for (const tc of g.tables) {
      if (!tc.reservations || tc.reservations.length === 0) continue;
      const blocks = tc.reservations.map(r => {
        const m = _resvTimeToMin(r.time);
        if (m == null) return { r, min: null, left: 0, width: 6, lane: 0 };
        const clamped = Math.max(start, Math.min(m, end - 15));
        return {
          r, min: m,
          left: ((clamped - start) / span) * 100,
          width: (Math.min(RESV_TIMELINE_BLOCK_MIN, end - clamped) / span) * 100,
          lane: 0,
        };
      }).sort((a, b) => (a.min == null ? -1 : a.min) - (b.min == null ? -1 : b.min));
      for (let i = 1; i < blocks.length; i++) {
        const prev = blocks[i - 1];
        if (blocks[i].min != null && prev.min != null && blocks[i].min < prev.min + RESV_TIMELINE_BLOCK_MIN) {
          blocks[i].lane = prev.lane === 0 ? 1 : 0;
        }
      }
      rows.push({ tc, blocks, lanes: blocks.some(b => b.lane === 1) ? 2 : 1 });
    }
    if (rows.length > 0) areas.push({ area: g.area, rows });
  }
  const ticks = [];
  for (let m = Math.ceil(start / 60) * 60; m <= end; m += 60) {
    ticks.push({ left: ((m - start) / span) * 100, label: `${String(Math.floor(m / 60) % 24).padStart(2, "0")}` });
  }
  return { areas, ticks };
};
function ResvTimelineBoard({ board, service }) {
  const layout = useMemo(() => _resvTimelineLayout(board, service), [board, service]);
  if (layout.areas.length === 0) {
    return <div className="f-body" style={{ fontSize: 12, color: C.muted, fontStyle: "italic", padding: "16px 0" }}>
      Nessuna prenotazione con tavolo in questo servizio: la timeline è vuota.
    </div>;
  }
  const blockStyle = (r) => r.status === "seated"
    ? { bg: C.oliveWash, bd: C.olive, fg: C.oliveDeep }
    : { bg: C.tealWash, bd: C.teal, fg: C.tealDeep };
  return <div className="fade-in">
    <div className="f-body" style={{ fontSize: 10.5, color: C.muted, fontStyle: "italic", marginBottom: 8 }}>
      La lunghezza dei blocchi è indicativa (2 ore): mostra il ritmo dei turni, non la durata reale.
    </div>
    {layout.areas.map(a => <div key={a.area.id} style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
        <span className="f-display" style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{a.area.name}</span>
        <div style={{ flex: 1, height: 1, background: C.ruleSoft }} />
      </div>
      {/* righello ore */}
      <div style={{ display: "flex" }}>
        <div style={{ width: 56, flexShrink: 0 }} />
        <div style={{ position: "relative", flex: 1, height: 14 }}>
          {layout.ticks.map((t, i) => <span key={i} className="f-mono" style={{ position: "absolute", left: `${t.left}%`, transform: "translateX(-50%)", fontSize: 8.5, color: C.muted }}>{t.label}</span>)}
        </div>
      </div>
      {a.rows.map(row => <div key={row.tc.table.id} style={{ display: "flex", alignItems: "stretch", marginBottom: 3 }}>
        <div className="f-display" style={{ width: 56, flexShrink: 0, fontSize: 13, fontWeight: 600, color: C.ink, display: "flex", alignItems: "center" }}>
          {row.tc.table.label}
          {row.tc.seats > 0 && <span className="f-mono" style={{ fontSize: 8, color: C.muted, marginLeft: 4 }}>{row.tc.seats}p</span>}
        </div>
        <div style={{ position: "relative", flex: 1, height: row.lanes === 2 ? 46 : 26, background: C.white, border: `1px solid ${C.ruleSoft}`, borderRadius: 2, overflow: "hidden" }}>
          {layout.ticks.map((t, i) => <div key={i} style={{ position: "absolute", left: `${t.left}%`, top: 0, bottom: 0, width: 1, background: C.ruleSoft }} />)}
          {row.blocks.map((b, i) => {
            const st = blockStyle(b.r);
            return <div key={b.r.id || i} title={`${b.r.time || "senza orario"} · ${b.r.rawName} · ${b.r.covers}`}
              style={{ position: "absolute", left: `${b.left}%`, width: `${Math.max(b.width, 6)}%`, top: b.lane === 1 ? 23 : 2, height: 20,
                background: st.bg, borderLeft: `3px solid ${st.bd}`, borderRadius: 2, display: "flex", alignItems: "center", gap: 4, padding: "0 5px", overflow: "hidden", whiteSpace: "nowrap" }}>
              <span className="f-mono" style={{ fontSize: 9, fontWeight: 700, color: st.fg }}>{b.r.time || "—"}</span>
              <span className="f-body" style={{ fontSize: 10.5, color: C.ink, overflow: "hidden", textOverflow: "ellipsis" }}>{b.r.rawName}</span>
              <span className="f-mono" style={{ fontSize: 9, color: st.fg, fontWeight: 600 }}>·{b.r.covers}</span>
            </div>;
          })}
        </div>
      </div>)}
    </div>)}
  </div>;
}
// ═══ REGIA (redesign approvato da Andi 07/07: impianto B "Regia" + innesto C
// "Flusso", spec del visual-design-director in voya-regia-spec.html) ═══
// Doppio tema selezionabile: token SEMANTICI (un colore = un significato:
// ambra = "vivo/adesso" — online, linea ADESSO, ★; acqua/oliva/corallo = stati
// tavolo; rosso profondo SOLO lista nera). Il toggle è uno swap di oggetto.
const RG_THEMES = {
  chiaro: {
    appBg: "#F1ECE0", surface: "#FBF7ED", surfaceRaised: "#FFFFFF", surfaceSunken: "#EDE7D8",
    line: "#DED6C4", lineStrong: "#C4B99F",
    textPrimary: "#1C2226", textSecondary: "#4A5157", textMuted: "#8A857A", textOnAccent: "#241900",
    stateFreeBg: "#FBF7ED", stateFreeBd: "#D8CFBB",
    stateBookedBg: "#DCE9E6", stateBookedBd: "#2E6D65",
    stateSeatedBg: "#E0E9CC", stateSeatedBd: "#5E7D3E",
    stateConflictBg: "#F4DBD1", stateConflictBd: "#B5472A",
    stateSuggestBg: "#FBF1DC", stateSuggestBd: "#C8841E",
    accentOnline: "#C8841E", dangerBlacklist: "#9E3320", nowLine: "#C8841E", focusRing: "#1F6D63",
  },
  scuro: {
    appBg: "#12171B", surface: "#1B232A", surfaceRaised: "#232E36", surfaceSunken: "#161C22",
    line: "#2E3A43", lineStrong: "#435059",
    textPrimary: "#F0EAD9", textSecondary: "#AEB9BF", textMuted: "#78858C", textOnAccent: "#1A1206",
    stateFreeBg: "#1B232A", stateFreeBd: "#3A464F",
    stateBookedBg: "#143430", stateBookedBd: "#2E7D6F",
    stateSeatedBg: "#24331A", stateSeatedBd: "#6F9142",
    stateConflictBg: "#3A211A", stateConflictBd: "#C0553A",
    stateSuggestBg: "#241C0C", stateSuggestBd: "#E6A93C",
    accentOnline: "#E6A93C", dangerBlacklist: "#C0392B", nowLine: "#E6A93C", focusRing: "#84D6C7",
  },
};
// Tema per DISPOSITIVO (mai sincronizzato, come la Modalità Accoglienza):
// il tablet di sala può stare scuro la sera mentre il Mac resta chiaro.
const RG_THEME_LS_KEY = "voya_regia_theme_v1";
const _rgThemeGet = () => {
  try { const v = localStorage.getItem(RG_THEME_LS_KEY); return RG_THEMES[v] ? v : "scuro"; }
  catch { return "scuro"; }
};
const _rgThemeSet = (v) => { try { localStorage.setItem(RG_THEME_LS_KEY, v); } catch { /* private mode */ } };

// Layout timeline Regia: PURO, in PIXEL (spec: 4 px/minuto, riga 64px, blocco
// ≥88px). Diverso da _resvTimelineLayout (percentuale, solo tavoli occupati):
// qui entrano TUTTI i tavoli attivi — la riga libera è il bersaglio del
// walk-in/assegnazione. Stessa finestra e stessa regola corsie del C2.1.
const RG_PX_PER_MIN = 4;
const RG_ROW_H = 64;
const _rgLayout = (board, service) => {
  const win = RESV_TIMELINE_WINDOWS[service] || RESV_TIMELINE_WINDOWS.cena;
  const [start, end] = win;
  const widthPx = (end - start) * RG_PX_PER_MIN;
  const areas = [];
  for (const g of (Array.isArray(board) ? board : [])) {
    const rows = [];
    for (const tc of g.tables) {
      const blocks = (tc.reservations || []).map(r => {
        const m = _resvTimeToMin(r.time);
        if (m == null) return { r, min: null, left: 0, width: 88, lane: 0 };
        const clamped = Math.max(start, Math.min(m, end - 15));
        return {
          r, min: m,
          left: (clamped - start) * RG_PX_PER_MIN,
          width: Math.max(88, Math.min(RESV_TIMELINE_BLOCK_MIN, end - clamped) * RG_PX_PER_MIN),
          lane: 0,
        };
      }).sort((a, b) => (a.min == null ? -1 : a.min) - (b.min == null ? -1 : b.min));
      for (let i = 1; i < blocks.length; i++) {
        const prev = blocks[i - 1];
        if (blocks[i].min != null && prev.min != null && blocks[i].min < prev.min + RESV_TIMELINE_BLOCK_MIN) {
          blocks[i].lane = prev.lane === 0 ? 1 : 0;
        }
      }
      rows.push({ tc, blocks, lanes: blocks.some(b => b.lane === 1) ? 2 : 1 });
    }
    if (rows.length > 0) areas.push({ area: g.area, rows });
  }
  const ticks = [];
  for (let m = Math.ceil(start / 30) * 30; m <= end; m += 30) {
    ticks.push({ px: (m - start) * RG_PX_PER_MIN, label: m % 60 === 0 ? `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:00` : null });
  }
  return { areas, ticks, start, end, widthPx };
};

// REGIA FASE 2 — motore dati PURO del cruscotto Mac (proposta B: numero
// dominante + curva + racconto). Stesse regole di VenditeStatistiche:
// · EC riconciliato (_regularizedTo ≠ NERO_DEFINITIVO) ESCLUSO dall'incasso
//   (già contato nello SF collegato — FIX Batch 3);
// · tavolo = covers>0, banco = covers=0; ticket medio SOLO su scontrini-tavolo;
// · EC orfani (senza _regularizedTo) = "in sospeso", mai dentro l'incasso.
// Differenza voluta: qui la regola EC vale ANCHE nello split pranzo/cena e
// nella serie 14gg (coerenza col numero dominante).
// AUDIT-FIX: un solo motore di verità sulla regola EC (definito in alto come
// _receiptCountsForRevenue) — il cruscotto Regia ci si allinea via alias.
const _rgRcCounts = _receiptCountsForRevenue;
const _rgDashData = (receipts, todayISO) => {
  const rs = Array.isArray(receipts) ? receipts.filter(r => r && r.closedAt) : [];
  // finestra 14 giorni che finisce oggi (chiavi service-date)
  const dayKeys = [];
  const anchor = new Date(`${todayISO}T12:00:00`);
  for (let i = 13; i >= 0; i--) {
    const d = new Date(anchor);
    d.setDate(d.getDate() - i);
    dayKeys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
  }
  const byDay = new Map(dayKeys.map(k => [k, { day: k, revenue: 0, covers: 0 }]));
  const today = {
    revenue: 0, covers: 0, tableRevenue: 0, bancoRevenue: 0, bancoCount: 0, receipts: 0,
    orphanEC: 0,
    pranzo: { revenue: 0, covers: 0, receipts: 0 },
    cena: { revenue: 0, covers: 0, receipts: 0 },
  };
  const feed = [];
  for (const r of rs) {
    const sd = getServiceDate(r.closedAt);
    const counts = _rgRcCounts(r);
    const total = Number(r.total) || 0;
    const covers = Number(r.covers) || 0;
    const slot = byDay.get(sd);
    if (slot && counts) { slot.revenue += total; slot.covers += covers; }
    if (sd !== todayISO) continue;
    const isEC = !!(r._isPreconto || r._docType === "ESTRATTO CONTO");
    if (isEC && !r._regularizedTo) today.orphanEC += total;
    if (counts) {
      today.revenue += total;
      today.receipts++;
      if (covers > 0) { today.covers += covers; today.tableRevenue += total; }
      else { today.bancoRevenue += total; today.bancoCount++; }
      const sh = getShift(r.closedAt);
      if (sh === "pranzo" || sh === "cena") {
        today[sh].revenue += total; today[sh].covers += covers; today[sh].receipts++;
      }
    }
    const ms = Date.parse(r.closedAt);
    feed.push({
      ms: Number.isFinite(ms) ? ms : 0,
      closedAt: String(r.closedAt), // l'ora si formatta in locale nel componente (come fa la UI receipts)
      table: r.isBanco || covers === 0 ? "Banco" : String(r.number || "?"),
      total, covers,
      shift: getShift(r.closedAt),
      excluded: !counts,
    });
  }
  feed.sort((a, b) => b.ms - a.ms);
  const days = dayKeys.map(k => byDay.get(k));
  const nonEmpty = days.filter(d => d.revenue > 0 && d.day !== todayISO);
  return {
    today,
    avgTicket: today.covers > 0 ? today.tableRevenue / today.covers : 0,
    days,
    avg14: nonEmpty.length > 0 ? nonEmpty.reduce((s, d) => s + d.revenue, 0) / nonEmpty.length : 0,
    feed,
  };
};

const _resvRecommendTables = (resv, board, combos, affinity) => {
  const empty = { best: null, starIds: new Set(), count: 0 };
  if (!resv || !Array.isArray(board)) return empty;
  const cov = Number(resv.covers) || 0;
  if (cov <= 0) return empty;
  const resMin = _resvTimeToMin(resv.time);
  const cands = [];
  for (const g of board) {
    for (const tc of g.tables) {
      if (tc.seats <= 0 || tc.seats < cov) continue;
      let mode = null;
      if (tc.status === "free") mode = "free";
      else if ((tc.status === "booked" || tc.status === "turns") && resMin != null && tc.reservations.length > 0 &&
        tc.reservations.every(r => { const m = _resvTimeToMin(r.time); return m != null && Math.abs(m - resMin) >= RESV_RECO_TURN_GAP_MIN; })) mode = "turn";
      if (!mode) continue;
      const reserve = RESV_RECO_RESERVE_AREAS.has(normStr(g.area.name) || "");
      cands.push({ tc, areaName: g.area.name, mode, waste: tc.seats - cov, reserve,
        aff: _resvAffinityScore(affinity, tc.table.label, cov) });
    }
  }
  // C2.2 — combinazioni note: candidabili solo con TUTTI i membri liberi e
  // capienza somma sufficiente. Tier tra i singoli liberi e i secondi turni.
  for (const cb of (Array.isArray(combos) ? combos : [])) {
    if (cb.seats < cov) continue;
    if (!cb.members.every(tc => tc.status === "free")) continue;
    cands.push({ combo: cb, labels: cb.labels, mode: "combo", waste: cb.seats - cov, reserve: cb.reserve, aff: 0 });
  }
  const tier = (c) => c.mode === "free" ? 0 : c.mode === "combo" ? 1 : 2;
  // SALA CHE IMPARA (A): a parità di spreco vince l'ABITUDINE (quante volte
  // quel tavolo ha ospitato proprio questi coperti). Lo spreco resta sopra:
  // "mai sprecare posti" (Andi) batte l'abitudine; l'abitudine spiega la scelta.
  cands.sort((a, b) =>
    (a.reserve ? 1 : 0) - (b.reserve ? 1 : 0) ||
    tier(a) - tier(b) ||
    a.waste - b.waste ||
    b.aff - a.aff ||
    (a.combo ? a.combo.seats : a.tc.seats) - (b.combo ? b.combo.seats : b.tc.seats) ||
    String(a.combo ? a.labels.join("+") : a.tc.table.label).localeCompare(String(b.combo ? b.labels.join("+") : b.tc.table.label), undefined, { numeric: true }));
  return {
    best: cands[0] || null,
    starIds: new Set(cands.slice(0, 3).flatMap(c => c.combo ? c.combo.members.map(m => String(m.table.id)) : [String(c.tc.table.id)])),
    count: cands.length,
  };
};
// R5-T4/T5 — riga-candidato receipt condivisa tra picker manuale e suggerimenti:
// dati di presentazione via facade (tavolo canonico, importo, anchors, fiscale);
// covers letto dal campo (non esposto dalla facade); linkedTo = prenotazioni con
// link ATTIVO che interseca una chiave FORTE corrente del receipt (v2 day-scoped
// o T3_TEMPORAL, unica per receipt e presente in ogni snapshot): i link pinnati
// v1 pre-sblocco 2026-07-06 restano riconosciuti via T3; le v1 fiscali legacy
// sono escluse dal confronto perché collidono tra giorni. Solo lettura.
const _resvReceiptRow = (rc, links) => {
  const keys = receiptFacade.computeReceiptCandidateKeys(rc);
  const keySet = new Set(keys
    .filter(k => k.key.startsWith(`${RECEIPT_KEY_VERSION_V2}:`) || k.tier === "T3_TEMPORAL")
    .map(k => k.key));
  const linkedTo = reservationFulfilCore.listReservationReceiptLinks(links)
    .filter(l => keySet.has(l.primaryStableReceiptKey) ||
      (Array.isArray(l.candidateKeySnapshot) && l.candidateKeySnapshot.some(k => k && keySet.has(k.key))))
    .map(l => String(l.reservationId));
  return {
    receipt: rc,
    table: receiptFacade.getReceiptTable(rc),
    isBanco: !!rc.isBanco,
    totalCents: receiptFacade.getReceiptMoney(rc).totalCents,
    closedAtMs: receiptFacade.getReceiptTimeAnchors(rc).rawClosedAtMs,
    covers: Number(rc.covers) || 0,
    fiscalNumber: receiptFacade.getReceiptSourceIdentity(rc).fiscalNumber,
    linkedTo,
  };
};
// R5-T5 — matching CONSERVATIVO (ADR-CRM-11 §7): euristiche di presentazione,
// score SOLO variabile runtime interna per ordinare — mai persistito, mai nel
// link, mai spacciato per certezza. Segnali e pesi dichiarati: tavolo canonico
// compatibile +3 (dominante), coperti uguali +2 / ±1 +1, orario plausibile +1
// (chiusura ≥ orario dichiarato, entro 3h, wrap post-mezzanotte fino alle 4),
// prenotazione al tavolo +1; malus: banco −2 (mai favorito), già collegato ad
// altra prenotazione −2 (visibile, non spinto). VIETATI e assenti: fuzzy nomi,
// customer inference, storico/valore cliente, AI.
const _resvScoreReceiptCandidate = (resv, row, resvCanonSet) => {
  const reasons = [];
  let score = 0;
  let tableMatch = false;
  if (row.table && resvCanonSet.has(String(row.table))) { score += 3; tableMatch = true; reasons.push("stesso tavolo"); }
  const rCov = Number(resv.covers) || 0;
  if (row.covers > 0 && rCov > 0) {
    if (row.covers === rCov) { score += 2; reasons.push(`${row.covers} coperti`); }
    else if (Math.abs(row.covers - rCov) === 1) { score += 1; reasons.push(`coperti vicini (${row.covers})`); }
  }
  if (resv.time && row.receipt.closedAt) {
    const d = new Date(row.receipt.closedAt);
    let closedMin = d.getHours() * 60 + d.getMinutes();
    if (d.getHours() < 4) closedMin += 1440;
    const parts = String(resv.time).split(":");
    const resMin = (Number(parts[0]) || 0) * 60 + (Number(parts[1]) || 0);
    if (closedMin >= resMin && closedMin - resMin <= 180) { score += 1; reasons.push(`chiuso ${fmt.time(row.receipt.closedAt)}`); }
  }
  if (resv.status === "seated") { score += 1; reasons.push("prenotazione al tavolo"); }
  if (row.isBanco) { score -= 2; reasons.push("banco"); }
  const linkedOther = row.linkedTo.some(id => id !== String(resv.id));
  if (linkedOther) { score -= 2; reasons.push("già collegato ad altra prenotazione"); }
  return { ...row, score, reasons, tableMatch, linkedOther };
};
// R5-T5 — classificazione conservativa: Suggerito SOLO con candidato forte
// (score ≥ 4, tavolo compatibile) e distacco netto dal secondo (≥ 2); più
// candidati forti vicini → tutti Ambiguo, nessuno spinto come sicuro; il resto
// plausibile → Da verificare; sotto soglia (score < 2) → nessun suggerimento.
// Max 4 candidati per non appesantire. MAI auto-link, a qualunque punteggio.
// R5.9 — regola dura: candidato già collegato ad ALTRA prenotazione = MAI
// "Suggerito" (il solo malus −2 non bastava con base score 6: nei doppi turni
// lo stesso scontrino risultava Suggerito anche per la seconda seduta).
// Resta visibile come Da verificare/Ambiguo con la reason "già collegato".
const _resvClassifySuggestions = (cands) => {
  const elig = cands
    .filter(c => c.score >= 2)
    .sort((a, b) => b.score - a.score || (a.closedAtMs || 0) - (b.closedAtMs || 0))
    .slice(0, 4);
  if (elig.length === 0) return [];
  const top = elig[0];
  const second = elig[1] || null;
  if (!top.linkedOther && top.score >= 4 && top.tableMatch && (!second || top.score - second.score >= 2)) {
    return elig.map((c, i) => ({ ...c, status: i === 0 ? "suggested" : "review" }));
  }
  const strongClose = elig.filter(c => c.score >= 4 && top.score - c.score < 2);
  if (strongClose.length >= 2) {
    return elig.map(c => ({ ...c, status: (c.score >= 4 && top.score - c.score < 2) ? "ambiguous" : "review" }));
  }
  return elig.map(c => ({ ...c, status: "review" }));
};
const RESV_UI_SUGG_STATUS = {
  suggested: { label: "Suggerito",     fg: C.oliveDeep, border: C.olive, bg: C.oliveWash },
  review:    { label: "Da verificare", fg: C.inkMid,    border: C.rule,  bg: C.paperDeep },
  ambiguous: { label: "Ambiguo",       fg: C.amberDeep, border: C.amber, bg: C.amberWash },
};
// R2-T5 (estratto come componente in R3-T4 per riuso Lista/Arrivi) — badge tavolo:
// presentazione PURA dell'esito di floorCore.resolveTableTokens già memoizzato dal
// chiamante. Warning visivo, mai blocco; nessuna entry → "Tavolo non assegnato".
function ResvTableBadge({ tres, areaNameById }) {
  const chipBase = { fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", padding: "3px 8px", borderRadius: 2, flexShrink: 0 };
  if (!tres) return <span className="f-body" style={{ fontSize: 11, color: C.mutedSoft, fontStyle: "italic", flexShrink: 0 }}>Tavolo non assegnato</span>;
  const matched = tres.tokens.filter(t => t.status === "matched");
  const unknown = tres.tokens.filter(t => t.status === "unknown");
  const areaName = tres.primaryAreaId != null ? areaNameById.get(String(tres.primaryAreaId)) : null;
  return <>
    {matched.length > 0 && <span className="f-mono" style={{ ...chipBase, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.ruleSoft}` }}>
      Tav {matched.map(t => t.label).join(" + ")}{areaName ? ` · ${areaName}` : ""}
    </span>}
    {unknown.length > 0 && <span className="f-mono" title="Tavolo non riconosciuto in Sala & Tavoli — il salvataggio non è mai bloccato"
      style={{ ...chipBase, color: C.terraDeep, background: C.paperDeep, border: `1px solid ${C.terra}` }}>
      {matched.length > 0 ? "Sconosciuto" : "Tavolo sconosciuto"}: {unknown.map(t => t.rawToken).join(", ")}
    </span>}
  </>;
}

// W3 — CODA RICHIESTE ONLINE (ADR sync/online §4): le richieste dal form
// pubblico /prenota restano sul server finché un OPERATORE decide — mai
// conferma automatica (F8). Accetta = createReservation (source "web",
// contatti nelle note) + decide sul server; Rifiuta = due tocchi + decide
// (la richiesta resta nello storico server, D4 — mai cancellata). Il pannello
// esiste solo se il dispositivo ha il token sync; polling leggero 60s.
function OnlineRequestsPanel({ state, setState, reqs, onRefresh }) {
  const toast = useToast();
  const [armedReject, setArmedReject] = useState(null);
  const token = (_syncGetConfig() || {}).token || null;
  const decide = async (id, decision) => {
    const res = await fetch("/api/booking/decide", {
      method: "POST",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify({ id, decision }),
    });
    const out = await res.json().catch(() => ({}));
    if (!res.ok || !out.ok) throw new Error(out.error || `HTTP ${res.status}`);
  };
  const handleAccept = async (q) => {
    const service = String(q.time) < "17:00" ? "pranzo" : "cena";
    const by = state.currentUser?.name || "Andi";
    // GDPR — minimizzazione PII: le note della prenotazione (store SINCRONIZZATO su
    // D1 e FUORI dal perimetro oblio: anonymizeCustomer non le tocca) contengono SOLO
    // il messaggio libero del cliente + marcatore [WEB]. Email e compleanno NON vanno
    // qui: vivono già in booking_requests (D1, record legittimo) e, per i clienti
    // creati/collegati, nella scheda cliente (birthdate strutturato, cancellabile).
    const freeNote = q.notes ? String(q.notes).trim() : "";
    const res = reservationCore.createReservation(state.reservations, {
      serviceDate: q.service_date, service, time: q.time, rawName: q.name,
      covers: Number(q.covers), source: "web", phone: q.phone,
      notes: `${freeNote}${freeNote ? " " : ""}[WEB ${q.id}]`,
    }, { by });
    if (res.error) { toast({ type: "error", title: "Richiesta non accettabile", message: res.error }); return; }
    // M1 — PONTE online→CRM (scelta operatore: creazione all'accettazione). Il
    // motore riconosce; qui si SCRIVE solo per esiti sicuri: "match" (telefono
    // univoco) → collega al cliente esistente; "none" → crea la scheda (nome +
    // contatti, NIENTE consenso marketing: store separato) e collega. "ambiguous"
    // e "suggest" NON si auto-collegano (nomi/numeri condivisi) → la prenotazione
    // entra e il suggerimento a un tocco già in Agenda fa il resto. Cliente in
    // lista nera → nessuna creazione automatica (già segnalato nel pannello).
    const match = _crmMatchContact(state.customers, { phone: q.phone, name: q.name });
    const blk = _blkMatch(state.blocklist, { phone: q.phone, name: q.name });
    let nextReservations = res.reservations;
    let nextCustomers = state.customers;
    let linkNote = "";
    if (match.status === "match") {
      const up = reservationCore.updateReservation(nextReservations, res.reservation.id, { customerId: match.customer.id }, { by });
      if (!up.error) { nextReservations = up.reservations; linkNote = ` · cliente riconosciuto: ${match.customer.displayName}`; }
      // Backfill compleanno sulla scheda esistente SOLO se non ne ha già uno: mai
      // sovrascrivere un valore presente (il dato in scheda è più affidabile).
      if (q.birthdate && !match.customer.birthdate) {
        const bf = crmCore.updateCustomer(nextCustomers, match.customer.id, { birthdate: q.birthdate }, { by });
        if (!bf.error) nextCustomers = bf.customers;
      }
    } else if (match.status === "none" && !blk) {
      const contacts = [
        q.phone ? { kind: "phone", value: String(q.phone) } : null,
        q.email ? { kind: "email", value: String(q.email) } : null,
      ].filter(Boolean);
      const cc = crmCore.createCustomer(state.customers, { displayName: q.name, type: "person", contacts, notes: "Creato da richiesta online", birthdate: q.birthdate }, { by });
      if (!cc.error) {
        nextCustomers = cc.customers;
        const up = reservationCore.updateReservation(nextReservations, res.reservation.id, { customerId: cc.customer.id }, { by });
        if (!up.error) { nextReservations = up.reservations; linkNote = " · nuovo cliente creato"; }
      }
    }
    try { await decide(q.id, "accepted"); }
    catch (e) { toast({ type: "error", title: "Server non aggiornato", message: e.message }); return; }
    setState(s => ({ ...s, reservations: nextReservations, customers: nextCustomers }));
    if (onRefresh) onRefresh();
    toast({ type: "success", title: "Prenotazione creata", message: `${q.name} · ${q.covers} · ${q.service_date} ${q.time}${linkNote}` });
  };
  const handleReject = async (q) => {
    if (armedReject !== q.id) { setArmedReject(q.id); return; }
    try { await decide(q.id, "rejected"); }
    catch (e) { toast({ type: "error", title: "Server non aggiornato", message: e.message }); return; }
    setArmedReject(null);
    if (onRefresh) onRefresh();
    toast({ type: "info", title: "Richiesta rifiutata", message: `${q.name} — resta nello storico. Avvisa il cliente!` });
  };
  if (!token || !Array.isArray(reqs) || reqs.length === 0) return null;
  // W3.1 — resa VISTOSA (feedback Andi): la richiesta online non deve sembrare
  // una riga qualsiasi. Testata scura, card con bordo acceso, gerarchia grande.
  return <div className="fade-in" style={{ marginBottom: 16, border: `2px solid ${C.amber}`, borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 10px rgba(181,71,42,0.10)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", background: C.amberDeep }}>
      <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: "50%", background: "#fff", boxShadow: "0 0 0 3px rgba(255,255,255,0.3)", flexShrink: 0 }} />
      <span className="f-mono" style={{ fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase", color: "#fff", fontWeight: 700 }}>
        {reqs.length === 1 ? "1 nuova richiesta online" : `${reqs.length} nuove richieste online`}
      </span>
      <span className="f-body" style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", fontStyle: "italic" }}>il cliente aspetta la vostra conferma</span>
      <div style={{ flex: 1 }} />
      <button className="tx f-mono" onClick={onRefresh}
        style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 2, cursor: "pointer", background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.4)" }}>
        Aggiorna
      </button>
    </div>
    <div style={{ background: C.amberWash, padding: "10px 12px" }}>
      {reqs.map(q => <div key={q.id} style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "12px 14px", background: C.white, borderLeft: `5px solid ${C.amberDeep}`, border: `1px solid ${C.rule}`, borderLeftWidth: 5, borderLeftColor: C.amberDeep, borderRadius: 2, marginBottom: 6 }}>
        <div style={{ minWidth: 92 }}>
          <div className="f-mono" style={{ fontSize: 16, fontWeight: 700, color: C.amberDeep }}>{q.time}</div>
          <div className="f-mono" style={{ fontSize: 11, color: C.inkMid }}>{String(q.service_date).slice(8)}/{String(q.service_date).slice(5, 7)}</div>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span className="f-display" style={{ fontSize: 17, fontWeight: 600, color: C.ink }}>{q.name}</span>
            <span className="f-mono" style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>· {q.covers} {Number(q.covers) === 1 ? "coperto" : "coperti"}</span>
          </div>
          <div className="f-body" style={{ fontSize: 12, color: C.inkMid, marginTop: 2 }}>{q.phone}{q.email ? ` · ${q.email}` : ""}</div>
          {q.notes && <div className="f-body" style={{ fontSize: 12.5, color: C.terraDeep, fontStyle: "italic", marginTop: 2 }}>“{q.notes}”</div>}
          {(() => { const m = _blkMatch(state.blocklist, { phone: q.phone, name: q.name }); return m && <div className="f-mono" style={{ marginTop: 5, padding: "5px 9px", background: C.terraDeep, color: "#fff", borderRadius: 2, fontSize: 11, fontWeight: 700, display: "inline-block" }}>
            ⛔ LISTA NERA · {m.entry.reason} {m.via === "nome" ? "· (aggancio per nome: possibile omonimo)" : ""}
          </div>; })()}
          {/* M1 — riconoscimento cliente PRIMA di accettare: decidi con lo storico
              in mano. Sola lettura; l'aggancio/creazione avviene solo su "Accetta". */}
          {(() => {
            const cm = _crmMatchContact(state.customers, { phone: q.phone, name: q.name });
            const chip = { marginTop: 5, marginLeft: 6, padding: "4px 9px", borderRadius: 2, fontSize: 11, fontWeight: 600, display: "inline-block" };
            if (cm.status === "match") return <span className="f-mono" style={{ ...chip, background: C.oliveWash, color: C.oliveDeep, border: `1px solid ${C.olive}` }}>👤 Cliente conosciuto: {cm.customer.displayName}</span>;
            if (cm.status === "suggest") return <span className="f-mono" style={{ ...chip, background: C.paperDeep, color: C.inkMid, border: `1px solid ${C.rule}` }}>Forse {cm.customer.displayName}? (per nome — da confermare)</span>;
            if (cm.status === "ambiguous") return <span className="f-mono" style={{ ...chip, background: C.amberWash, color: C.amberDeep, border: `1px solid ${C.amber}` }}>Più clienti con questo {cm.via} — collega a mano dopo</span>;
            return <span className="f-mono" style={{ ...chip, background: C.paperDeep, color: C.muted, border: `1px solid ${C.ruleSoft}` }}>Cliente nuovo → scheda creata all'accettazione</span>;
          })()}
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <Btn size="sm" variant="accent" onClick={() => handleAccept(q)}>Accetta</Btn>
          <Btn size="sm" variant={armedReject === q.id ? "danger" : "ghost"} onClick={() => handleReject(q)}>
            {armedReject === q.id ? "Confermi rifiuto?" : "Rifiuta"}
          </Btn>
        </div>
      </div>)}
    </div>
  </div>;
}

// C3.2 — statistiche del canale online per il doppio binario (W4). PURO e senza
// dipendenze: rows = booking_requests dal server (qualsiasi status), reservations
// = store locale, todayISO passato dal chiamante (mai Date.now qui: testabile).
// I giorni delle richieste usano created_at troncato (UTC): per un cruscotto di
// tendenza lo scarto di fuso non sposta le decisioni. Mix canali: prenotazioni
// con serviceDate negli ultimi 14 giorni COMPRESO oggi, tombstone esclusi.
const _bkMonStats = (rows, reservations, todayISO) => {
  const rs = Array.isArray(rows) ? rows.filter(r => r && r.created_at) : [];
  const counts = { pending: 0, accepted: 0, rejected: 0 };
  const respMins = [];
  const byDay = new Map();
  for (const r of rs) {
    if (counts[r.status] != null) counts[r.status]++;
    const day = String(r.created_at).slice(0, 10);
    byDay.set(day, (byDay.get(day) || 0) + 1);
    if (r.decided_at && (r.status === "accepted" || r.status === "rejected")) {
      const ms = Date.parse(r.decided_at) - Date.parse(r.created_at);
      if (Number.isFinite(ms) && ms >= 0) respMins.push(ms / 60000);
    }
  }
  respMins.sort((a, b) => a - b);
  const pick = (q) => respMins.length === 0 ? null : respMins[Math.min(respMins.length - 1, Math.floor(q * respMins.length))];
  const decided = counts.accepted + counts.rejected;
  // Finestra: i 14 giorni che finiscono oggi (mezzogiorno per stare lontani da DST).
  const days = [];
  const windowSet = new Set();
  const anchor = new Date(`${todayISO}T12:00:00`);
  for (let i = 13; i >= 0; i--) {
    const d = new Date(anchor);
    d.setDate(d.getDate() - i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    windowSet.add(key);
    days.push({ day: key, count: byDay.get(key) || 0 });
  }
  const mix = { web: 0, phone: 0, walkin: 0, other: 0, total: 0 };
  for (const r of (Array.isArray(reservations) ? reservations : [])) {
    if (!r || r.tombstone || !windowSet.has(r.serviceDate)) continue;
    const src = mix[r.source] != null && r.source !== "total" ? r.source : "other";
    mix[src]++;
    mix.total++;
  }
  return {
    total: rs.length,
    counts,
    decided,
    acceptRatePct: decided > 0 ? Math.round((counts.accepted / decided) * 100) : null,
    respMedianMin: pick(0.5),
    respP90Min: pick(0.9),
    days,
    mix,
  };
};

// C3.2 — cruscotto del canale online (roadmap C3 "monitor"): quante richieste
// arrivano, quante ne accettiamo, quanto ci mettiamo a rispondere, quota del
// web sulle prenotazioni. Read-only: legge la coda COMPLETA dal server
// (status=all, device-auth) all'apertura e su richiesta — niente polling: il
// pannello vive solo dietro il toggle bootstrap (mai sul tablet in Accoglienza).
// Questi numeri decidono il gate C3: switch del link e disdetta Pienissimo.
function OnlineMonitorPanel({ state }) {
  const token = (_syncGetConfig() || {}).token || null;
  const [rows, setRows] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    if (!token) return;
    setBusy(true); setLoadErr(null);
    try {
      const res = await fetch("/api/booking?status=all", { headers: { authorization: `Bearer ${token}` } });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || !out.ok) throw new Error(out.error || `HTTP ${res.status}`);
      setRows(Array.isArray(out.requests) ? out.requests : []);
    } catch (e) { setLoadErr(String(e && e.message || e)); }
    setBusy(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const stats = useMemo(() => rows ? _bkMonStats(rows, state.reservations, todayLocalISO()) : null,
    [rows, state.reservations]);
  if (!token) return null;
  const chip = { fontSize: 10, letterSpacing: "0.08em", padding: "3px 8px", borderRadius: 2, flexShrink: 0 };
  const fmtMin = (m) => m == null ? "—" : m < 1 ? "<1 min" : m < 90 ? `${Math.round(m)} min` : `${(m / 60).toFixed(1)} h`;
  const maxDay = stats ? Math.max(1, ...stats.days.map(d => d.count)) : 1;
  const pct = (n) => stats && stats.mix.total > 0 ? Math.round((n / stats.mix.total) * 100) : 0;
  return <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${C.amber}` }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
      <span className="f-mono" style={{ fontSize: 11, letterSpacing: "0.2em", color: C.amberDeep, textTransform: "uppercase", fontWeight: 600 }}>Monitor canale online</span>
      <span className="f-body" style={{ fontSize: 11, color: C.inkMid }}>
        richieste dal form /prenota (storico server completo, D4) · i numeri per decidere switch e disdetta Pienissimo
      </span>
      <div style={{ flex: 1 }} />
      <Btn size="sm" variant="ghost" onClick={load} disabled={busy}>{busy ? "Carico…" : "Aggiorna"}</Btn>
    </div>
    {loadErr && <span className="f-mono" style={{ ...chip, color: C.terraDeep, background: C.terraWash, border: `1px solid ${C.terra}` }}>
      server non raggiungibile: {loadErr}
    </span>}
    {stats && <>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        <span className="f-mono" style={{ ...chip, color: C.ink, background: C.paperDeep, border: `1px solid ${C.rule}` }}>
          {stats.total} richieste totali
        </span>
        <span className="f-mono" style={{ ...chip, color: C.amberDeep, background: C.amberWash, border: `1px solid ${C.amber}`, fontWeight: 700 }}>
          {stats.counts.pending} in attesa
        </span>
        <span className="f-mono" style={{ ...chip, color: C.oliveDeep, background: C.oliveWash, border: `1px solid ${C.olive}` }}>
          {stats.counts.accepted} accettate · {stats.counts.rejected} rifiutate
          {stats.acceptRatePct != null ? ` · tasso ${stats.acceptRatePct}%` : ""}
        </span>
        <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>
          risposta: mediana {fmtMin(stats.respMedianMin)}{stats.respP90Min != null ? ` · p90 ${fmtMin(stats.respP90Min)}` : ""}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3, marginBottom: 10 }}>
        {stats.days.map(d => <div key={d.day} title={`${d.day} · ${d.count} richieste`} style={{ width: 26, textAlign: "center" }}>
          <div className="f-mono" style={{ fontSize: 9, color: d.count > 0 ? C.amberDeep : C.muted, fontWeight: d.count > 0 ? 700 : 400 }}>{d.count > 0 ? d.count : ""}</div>
          <div style={{ height: Math.max(2, Math.round((d.count / maxDay) * 34)), background: d.count > 0 ? C.amberDeep : C.rule, borderRadius: 1 }} />
          <div className="f-mono" style={{ fontSize: 8.5, color: C.muted, marginTop: 2 }}>{d.day.slice(8)}</div>
        </div>)}
        <span className="f-body" style={{ fontSize: 10, color: C.muted, marginLeft: 6, alignSelf: "center" }}>richieste/giorno · ultimi 14 gg</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: C.inkMid, textTransform: "uppercase" }}>Mix canali (prenotazioni 14 gg)</span>
        {stats.mix.total > 0
          ? <>
              <div style={{ display: "flex", width: 220, height: 10, borderRadius: 2, overflow: "hidden", border: `1px solid ${C.rule}` }}>
                <div title={`web ${pct(stats.mix.web)}%`} style={{ width: `${pct(stats.mix.web)}%`, background: C.amberDeep }} />
                <div title={`telefono ${pct(stats.mix.phone)}%`} style={{ width: `${pct(stats.mix.phone)}%`, background: C.oliveDeep }} />
                <div title={`walk-in ${pct(stats.mix.walkin)}%`} style={{ width: `${pct(stats.mix.walkin)}%`, background: C.terra }} />
                <div style={{ flex: 1, background: C.paperDeep }} />
              </div>
              <span className="f-mono" style={{ fontSize: 10.5, color: C.inkMid }}>
                web {stats.mix.web} ({pct(stats.mix.web)}%) · telefono {stats.mix.phone} ({pct(stats.mix.phone)}%) · walk-in {stats.mix.walkin} ({pct(stats.mix.walkin)}%){stats.mix.other > 0 ? ` · altro ${stats.mix.other} (${pct(stats.mix.other)}%)` : ""}
              </span>
            </>
          : <span className="f-body" style={{ fontSize: 11, color: C.muted }}>nessuna prenotazione nella finestra</span>}
      </div>
    </>}
    {!stats && !loadErr && <span className="f-body" style={{ fontSize: 11, color: C.muted }}>Carico lo storico richieste dal server…</span>}
  </div>;
}

// R5.9-OP — Riconciliazione OPERATIVA in Agenda: porta il motore di match
// (già collaudato in BootstrapAuditPanel: _bootReconAudit → _resvClassify-
// Suggestions) dentro il flusso reale della sala, per il giorno/servizio
// mostrato. Nessun motore nuovo, nessuna scrittura silenziosa: il batch
// "Conferma i sicuri" è UN gesto esplicito dell'operatore, origin
// "suggestion_confirmed", stessa guardia anti-doppione del bootstrap (F8:
// mai auto-link, il sistema propone, l'umano conferma). Appare SOLO quando
// c'è lavoro reale: giorno ≤ oggi, prenotazioni da collegare, scontrini in
// istanza. Memo su fette precise (lezione perf ispettore #4): mai ricalcolo
// O(prenotazioni × scontrini) a ogni setState.
function AgendaReconStrip({ state, setState, day, service }) {
  const toast = useToast();
  const recon = useMemo(() => {
    const all = _bootReconAudit(state, day);
    return (all && all[service]) || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.reservations, state.tableReceipts, state.reservationReceiptLinks, state.floorTables, day, service]);
  if (!recon) return null;
  const { counts, receipts } = recon;
  // Solo giorno corrente o passato (nel futuro non esistono scontrini) e solo
  // se c'è davvero da collegare con almeno uno scontrino in istanza.
  if (day > _resvServiceToday() || counts.daCollegare === 0 || receipts.total === 0) return null;

  // Copia FEDELE di handleLinkFacili (BootstrapAuditPanel): collega in blocco i
  // soli "facile" (top Suggerito dal motore R5, già hard-block linkedOther),
  // guardia anti-doppione dentro il giro (lo stesso scontrino non serve due
  // prenotazioni del batch), origin puro-audit, resoconto a toast.
  const handleLinkFacili = () => {
    const plan = recon.plan || [];
    const facili = plan.filter(p => p.klass === "facile" && p.top && p.top.receipt);
    if (facili.length === 0) return;
    let links = Array.isArray(state.reservationReceiptLinks) ? state.reservationReceiptLinks : [];
    const usedReceipts = new Set();
    let ok = 0, skipped = 0, firstError = null;
    for (const p of facili) {
      const rcId = p.top.receipt.id;
      if (rcId != null && usedReceipts.has(rcId)) { skipped++; continue; }
      const res = reservationFulfilCore.createReservationReceiptLink(
        links, state.reservations, p.top.receipt,
        { reservationId: p.r.id, origin: "suggestion_confirmed" },
        { by: state.currentUser?.name || "Andi" });
      if (res.error) { skipped++; if (!firstError) firstError = `${p.r.rawName}: ${res.error}`; continue; }
      links = res.reservationReceiptLinks;
      if (rcId != null) usedReceipts.add(rcId);
      ok++;
    }
    if (ok > 0) setState(s => ({ ...s, reservationReceiptLinks: links }));
    toast({
      type: skipped > 0 ? "info" : "success",
      title: `Collegati ${ok} ${ok === 1 ? "abbinamento sicuro" : "abbinamenti sicuri"}`,
      message: `${skipped > 0 ? `${skipped} lasciati alla verifica${firstError ? ` — ${firstError}` : ""}. ` : ""}Se ne emergono altri, ripremi il bottone.`,
    });
  };

  const chip = { fontSize: 10, letterSpacing: "0.08em", padding: "3px 8px", borderRadius: 2, flexShrink: 0 };
  return <div style={{ marginBottom: 14, padding: "11px 14px", background: C.oliveWash, border: `1px solid ${C.olive}`, borderRadius: 2 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <span className="f-mono" style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: C.oliveDeep, fontWeight: 600, flexShrink: 0 }}>
        Abbina scontrini
      </span>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
        {counts.facili > 0 && <span className="f-mono" style={{ ...chip, color: C.oliveDeep, background: C.white, border: `1px solid ${C.olive}` }}>{counts.facili} sicuri</span>}
        {counts.ambigue > 0 && <span className="f-mono" style={{ ...chip, color: C.amberDeep, background: C.amberWash, border: `1px solid ${C.amber}` }}>{counts.ambigue} ambigui</span>}
        {counts.medie > 0 && <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>{counts.medie} da verificare</span>}
        {counts.senzaCandidati > 0 && <span className="f-mono" style={{ ...chip, color: C.terraDeep, background: C.paperDeep, border: `1px solid ${C.terra}` }}>{counts.senzaCandidati} senza scontrino</span>}
      </div>
      {counts.facili > 0
        ? <Btn size="sm" variant="accent" onClick={handleLinkFacili}>Conferma {counts.facili} {counts.facili === 1 ? "sicuro" : "sicuri"}</Btn>
        : <span className="f-body" style={{ fontSize: 11, color: C.muted, fontStyle: "italic", flexShrink: 0 }}>Apri le righe qui sotto per collegarle a mano</span>}
    </div>
    <div className="f-body" style={{ fontSize: 10.5, color: C.oliveDeep, marginTop: 6, opacity: 0.9 }}>
      {counts.daCollegare} {counts.daCollegare === 1 ? "prenotazione da collegare" : "prenotazioni da collegare"} · i "sicuri" hanno tavolo, coperti e orario coerenti — restano tuoi da confermare, mai automatici
    </div>
  </div>;
}

// FASE C — ATTESI REALI in cima al servizio (in ⟦RESV-UI⟧). Sola lettura,
// derivazione pura da engine.expectedCoversFor (che legge il modello compatto
// SINCRONIZZATO al tablet, o lo calcola live sul Mac). Mostra i coperti-presenze
// ATTESI come range, i posti ancora dabili sotto il tetto fisico 260 e i
// prenotati attivi finora. Onesta: è una STIMA delle presenze, non una certezza.
// Se non ci sono dati (source "none" / cella assente) NON compare.
function AgendaAttesiStrip({ state, day, service }) {
  const exp = useMemo(() => engine.expectedCoversFor(state, day, service),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.demandModel, state.historicalDaily, state.voyaSeasonality, day, service]);
  // Prenotati ATTIVI finora (booked/seated; escludi cancelled/no_show/tombstone).
  const bookedNow = useMemo(() => {
    const rows = reservationCore.listReservationsForDay(state.reservations, day, { service });
    return rows.reduce((s, r) => {
      if (!r || r.tombstone) return s;
      if (r.status === "cancelled" || r.status === "no_show") return s;
      return s + (Number(r.covers) || 0);
    }, 0);
  }, [state.reservations, day, service]);

  if (!exp || !exp.available) return null;

  const cap = Number(exp.capacity) || 260;
  const low = Math.max(0, Number(exp.low) || 0);
  const high = Math.max(0, Number(exp.high) || 0);
  const svcLabel = service === "pranzo" ? "Pranzo" : "Cena";
  const dataLbl = (() => {
    try { return new Date(day + "T12:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" }); }
    catch { return day; }
  })();
  const rangeTxt = (a, b) => (a === b ? `${a}` : `${a}–${b}`);
  const dabiliLow = Math.max(0, cap - high);
  const dabiliHigh = Math.max(0, cap - low);
  const isReal = exp.from === "real" || (exp.from == null && exp.source === "historical");
  const chip = { fontSize: 10, letterSpacing: "0.08em", padding: "3px 8px", borderRadius: 2, flexShrink: 0 };

  return <div style={{ marginBottom: 14, padding: "11px 14px", background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <span className="f-mono" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: C.tealDeep, fontWeight: 600, flexShrink: 0 }}>
        🔮 {svcLabel} — {dataLbl}
      </span>
      <span className="f-mono" style={{ fontSize: 13, color: C.ink }}>
        Attesi <b style={{ color: C.tealDeep }}>{rangeTxt(low, high)}</b> coperti
      </span>
      <div style={{ flex: 1 }} />
      <span className="f-mono" style={{ ...chip, color: isReal ? C.tealDeep : C.amberDeep, background: C.white, border: `1px solid ${isReal ? C.teal : C.amber}` }}
        title={isReal ? "Stima costruita sulle presenze reali degli anni scorsi" : "Stima dalla forma della domanda storica (archivio), calibrata a presenze"}>
        {isReal ? "da storico reale" : "stima"}
      </span>
    </div>
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 7, alignItems: "center" }}>
      <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>
        prenotati finora <b style={{ color: C.ink }}>{bookedNow}</b>
      </span>
      <span className="f-mono" style={{ ...chip, color: C.tealDeep, background: C.white, border: `1px solid ${C.teal}` }}>
        posti ancora dabili ~{rangeTxt(dabiliLow, dabiliHigh)}
      </span>
      <span className="f-mono" style={{ ...chip, color: C.muted, background: C.paperDeep, border: `1px solid ${C.rule}` }}>
        capienza {cap}
      </span>
    </div>
    <div className="f-body" style={{ fontSize: 10.5, color: C.tealDeep, marginTop: 6, opacity: 0.9 }}>
      Gli "attesi" sono una stima delle presenze (non prenotazioni certe): servono a leggere il servizio in anticipo, non sostituiscono il tuo occhio.
    </div>
  </div>;
}

// R6-VAR — CONSUNTIVO COPERTI in Agenda (feedback Andi 09/07): per il servizio
// GIÀ CHIUSO (giorno passato) mostra prenotato→reale e stima i soldi persi dai
// coperti mancati. Sola lettura, derivazione pura (_resvCoversVariance). Appare
// solo con dati reali; se non c'è scarto, lo dice (rinforzo positivo).
function AgendaCoversVarianceStrip({ state, day, service }) {
  const v = useMemo(() => _resvCoversVariance(state, day, service),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.reservations, state.tableReceipts, state.reservationReceiptLinks, day, service]);
  if (day >= _resvServiceToday() || !v.hasData) return null; // solo servizi CHIUSI (passati)
  const chip = { fontSize: 10, letterSpacing: "0.08em", padding: "3px 8px", borderRadius: 2, flexShrink: 0 };
  const hasLoss = v.lostCovers > 0;
  return <div style={{ marginBottom: 14, padding: "11px 14px", background: hasLoss ? C.paperDeep : C.oliveWash, border: `1px solid ${hasLoss ? C.terra : C.olive}`, borderRadius: 2 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <span className="f-mono" style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: hasLoss ? C.terraDeep : C.oliveDeep, fontWeight: 600, flexShrink: 0 }}>
        Consuntivo coperti
      </span>
      <span className="f-mono" style={{ fontSize: 12, color: C.inkMid }}>
        prenotati <b style={{ color: C.ink }}>{v.bookedFulfilled}</b> · arrivati <b style={{ color: C.ink }}>{v.actualFulfilled}</b>
      </span>
      <div style={{ flex: 1 }} />
      {hasLoss
        ? <span className="f-mono" style={{ fontSize: 13, fontWeight: 700, color: C.terraDeep, flexShrink: 0 }}>
            {v.lostCovers} {v.lostCovers === 1 ? "coperto perso" : "coperti persi"}{v.lostRevenueCents > 0 ? ` ≈ ${fmt.eur(v.lostRevenueCents / 100)}` : ""}
          </span>
        : <span className="f-mono" style={{ fontSize: 12, fontWeight: 600, color: C.oliveDeep, flexShrink: 0 }}>pieno rispettato ✓</span>}
    </div>
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 7 }}>
      {v.shortfallCovers > 0 && <span className="f-mono" style={{ ...chip, color: C.terraDeep, background: C.white, border: `1px solid ${C.terra}` }}>{v.shortfallCovers} venuti in meno</span>}
      {v.noShowCovers > 0 && <span className="f-mono" style={{ ...chip, color: C.terraDeep, background: C.white, border: `1px solid ${C.terra}` }}>{v.noShowCovers} no-show ({v.nNoShow})</span>}
      {v.overCovers > 0 && <span className="f-mono" style={{ ...chip, color: C.oliveDeep, background: C.oliveWash, border: `1px solid ${C.olive}` }}>{v.overCovers} arrivati in più</span>}
      {v.avgPerCoverCents > 0 && <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>€/coperto reale {fmt.eur(v.avgPerCoverCents / 100)}</span>}
      {v.unclearCovers > 0 && <span className="f-mono" style={{ ...chip, color: C.amberDeep, background: C.amberWash, border: `1px solid ${C.amber}` }} title="Prenotazioni passate senza scontrino collegato: non contano come perdita finché non le colleghi (usa 'Abbina scontrini')">{v.unclearCovers} coperti da collegare</span>}
    </div>
    {hasLoss && v.lostRevenueCents > 0 && <div className="f-body" style={{ fontSize: 10.5, color: C.terraDeep, marginTop: 6, opacity: 0.9 }}>
      Stima prudente: coperti mancati × spesa media reale del servizio. I "da collegare" NON sono conteggiati come perdita.
    </div>}
  </div>;
}

// C5-S1 — Chat operativa, strato 1: parser DETERMINISTICO (no AI, offline, gratis).
// Traduce l'italiano leggero nei reducer esistenti (R7_CHAT_OPERATIVA_DESIGN). Puro,
// nessuna scrittura: ritorna solo l'intento. Grammatica:
//   "4 20:30 rossi [tav 25] [nota …]" → create (coperti+orario+nome, clausole opz.)
//   "arrivato rossi" / "no-show rossi" → cambio stato · "sposta rossi 25" → assegna tavolo
const _resvParseCommand = (input) => {
  const raw = String(input || "").trim();
  if (!raw) return { kind: "empty" };
  let m;
  if ((m = /^arrivat[oa]\s+(.+)$/i.exec(raw))) return { kind: "seat", name: m[1].trim() };
  if ((m = /^no[\s-]?show\s+(.+)$/i.exec(raw))) return { kind: "noshow", name: m[1].trim() };
  if ((m = /^sposta\s+(.+)$/i.exec(raw))) {
    const parts = m[1].trim().split(/\s+/);
    if (parts.length < 2) return { kind: "unknown", error: "Formato: sposta <nome> <tavolo>" };
    const table = parts.pop();
    return { kind: "move", name: parts.join(" "), table };
  }
  // create: estrai prima "nota …" e "tav X", poi coperti/orario/nome (ordine libero)
  let s = raw, note = null, table = null;
  const noteMatch = /\bnota\s+(.+)$/i.exec(s);
  if (noteMatch) { note = noteMatch[1].trim(); s = s.slice(0, noteMatch.index).trim(); }
  const tavMatch = /\btav(?:olo)?\s+(\S+)/i.exec(s);
  if (tavMatch) { table = tavMatch[1].trim(); s = (s.slice(0, tavMatch.index) + s.slice(tavMatch.index + tavMatch[0].length)).trim(); }
  let covers = null, time = null; const nameToks = [];
  for (const t of s.split(/\s+/).filter(Boolean)) {
    const tm = /^(\d{1,2})[:.](\d{2})$/.exec(t);
    if (tm && time == null && +tm[1] < 24 && +tm[2] < 60) { time = `${String(+tm[1]).padStart(2, "0")}:${tm[2]}`; continue; }
    if (/^\d{1,2}$/.test(t) && covers == null && +t >= 1 && +t <= 99) { covers = +t; continue; }
    nameToks.push(t);
  }
  const name = nameToks.join(" ").trim();
  if (covers == null && !name) return { kind: "unknown", error: null };
  if (covers == null) return { kind: "unknown", error: "Manca il numero di coperti (es. \"4 20:30 rossi\")" };
  if (!name) return { kind: "unknown", error: "Manca il nome (es. \"4 20:30 rossi\")" };
  return { kind: "create", covers, time, name, table, note };
};

function AgendaView({ state, setState, onlineReqs, refreshOnlineReqs }) {
  const toast = useToast();
  // AUDIT-FIX (date): default = giorno di servizio corrente (coda notturna
  // inclusa) e servizio dedotto dall'ora → alla riapertura dopo mezzanotte
  // l'Agenda resta sulla cena in corso, e di pranzo si apre su pranzo.
  const [day, setDay] = useState(() => _resvServiceToday());
  const [service, setService] = useState(() => _resvServiceNow());
  // Form di aggiunta rapida (riga inline in cima alla lista)
  const [fName, setFName] = useState("");
  const [fCovers, setFCovers] = useState("");
  const [fTable, setFTable] = useState("");
  const [fNotes, setFNotes] = useState("");
  const [fPhone, setFPhone] = useState("");
  const [fSource, setFSource] = useState("phone");
  const [fTime, setFTime] = useState("");
  // T4 — edit inline (una riga alla volta, pattern widget COPERTI)
  const [editingId, setEditingId] = useState(null);
  const [eCovers, setECovers] = useState("");
  const [eTable, setETable] = useState("");
  const [eNotes, setENotes] = useState("");
  // R1-T2 — picker cliente inline (una riga alla volta, pattern widget CLIENTE)
  const [pickerId, setPickerId] = useState(null);
  const [pQuery, setPQuery] = useState("");
  // R1-T4 — card Relazione espansa (una alla volta; solo prenotazioni collegate)
  const [relId, setRelId] = useState(null);
  // R3-T2 — riga espansa (una alla volta): la parte cliente completa di R1
  // (collega/scollega, picker, Relazione, Esperienza) vive SOLO qui dentro.
  const [expandedId, setExpandedId] = useState(null);
  // R3-T3 — ricerca nel giorno/servizio + filtro chip a selezione singola.
  // Default "all": la lista operativa NON mostra le chiuse.
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  // R3-T4 — modalità di presentazione: "lista" (default, comportamento attuale)
  // o "arrivi" (board di servizio). Solo stato UI runtime, mai persistito.
  const [mode, setMode] = useState("lista");
  // C5-S1 — barra comando rapido (solo stato UI runtime, mai persistito).
  const [cmd, setCmd] = useState("");
  // R3-T5 — form walk-in (solo modalità Arrivi): nome opzionale, coperti
  // obbligatori, tavolo opzionale.
  const [wName, setWName] = useState("");
  const [wCovers, setWCovers] = useState("");
  const [wTable, setWTable] = useState("");
  // R3-T4b — board Arrivi: i "già al tavolo" sono chiusi di default (durante il
  // servizio conta chi DEVE ancora arrivare). Solo stato UI runtime.
  const [showArrived, setShowArrived] = useState(false);
  // R4-T3 — prenotazione selezionata per l'assegnazione da griglia (una alla
  // volta). Solo stato UI runtime, mai persistito (Addendum R4).
  const [assignId, setAssignId] = useState(null);
  // R4-T4 — conferma a due tocchi su tavolo non libero: { tableId, resvId },
  // solo runtime, niente modali. Decade su cambio selezione/giorno/servizio/
  // modalità (effect sotto), su tap altrove e a operazione completata.
  const [confirmTap, setConfirmTap] = useState(null);
  useEffect(() => { setConfirmTap(null); }, [day, service, mode, assignId]);
  // R5-T4 — picker manuale scontrino (uno alla volta, id prenotazione).
  // Solo stato UI runtime, mai persistito.
  const [fulfilPickerId, setFulfilPickerId] = useState(null);
  // R5.5-T3 — gate esplicito del pannello bootstrap Pienissimo: ?bootstrap=1
  // nell'URL OPPURE interruttore "Strumenti avanzati" in Backup & dati
  // (sessionStorage, R6: la PWA installata non ha barra dell'indirizzo).
  // Letto a ogni render (costo trascurabile): il toggle da Backup & dati deve
  // valere alla prossima visita dell'Agenda senza ricaricare. Si spegne da solo
  // alla chiusura dell'app (sessionStorage): mai visibile per sbaglio l'indomani.
  const bootstrapOn = (() => {
    try {
      return new URLSearchParams(window.location.search).get("bootstrap") === "1"
        || sessionStorage.getItem("voya_bootstrap_panels") === "1";
    } catch { return false; }
  })();

  // Lettura via motore T2; ordinamento professionale R3-T2: time crescente,
  // senza orario in coda, fallback createdAt crescente (derivazione UI pura).
  const reservations = useMemo(() =>
    [...reservationCore.listReservationsForDay(state.reservations, day, { service })].sort(_resvCompareByTime),
    [state.reservations, day, service]);
  // Contatori derivati in UI (mai persistiti).
  const totalCovers = reservations.reduce((s, r) => s + (Number(r.covers) || 0), 0);

  // C5-S1 — parsing del comando + proposta risolta contro le prenotazioni del
  // giorno/servizio. Pura lettura: nessuna scrittura finché l'operatore non conferma.
  const parsedCmd = useMemo(() => _resvParseCommand(cmd), [cmd]);
  const cmdProposal = useMemo(() => {
    const p = parsedCmd;
    if (p.kind === "empty") return null;
    if (p.kind === "unknown") return { ok: false, hint: true, msg: p.error || "Comando non riconosciuto" };
    if (p.kind === "create") {
      const bits = [`${p.covers} coperti`, p.time || "orario —", `« ${p.name} »`, p.table ? `tav ${p.table}` : null, p.note ? `nota: ${p.note}` : null].filter(Boolean);
      return { ok: true, kind: "create", label: `Nuova prenotazione — ${bits.join(" · ")}`, data: p };
    }
    // seat / noshow / move: risolvi il nome tra le prenotazioni ATTIVE del giorno
    const matches = reservations.filter(r => _resvIsActiveStatus(r) && (r.rawName || "").toLowerCase().includes(p.name.toLowerCase()));
    if (matches.length === 0) return { ok: false, msg: `Nessuna prenotazione attiva "${p.name}" oggi (${service})` };
    if (matches.length > 1) return { ok: false, msg: `${matches.length} prenotazioni "${p.name}" — sii più preciso` };
    const r = matches[0];
    if (p.kind === "seat") return { ok: true, kind: "seat", label: `Segna ARRIVATO — ${r.rawName} · ${r.time || "—"} · ${r.covers}p`, target: r };
    if (p.kind === "noshow") return { ok: true, kind: "noshow", label: `Segna NO-SHOW — ${r.rawName} · ${r.time || "—"}`, target: r };
    if (p.kind === "move") return { ok: true, kind: "move", label: `Sposta ${r.rawName} → tavolo ${p.table}`, target: r, table: p.table };
    return null;
  }, [parsedCmd, reservations, service]);
  const handleRunCommand = () => {
    const pr = cmdProposal;
    if (!pr || !pr.ok) return;
    const by = state.currentUser?.name || "Andi";
    let res;
    if (pr.kind === "create") {
      const p = pr.data;
      res = reservationCore.createReservation(state.reservations, { serviceDate: day, service, time: p.time || null, rawName: p.name, covers: p.covers, table: p.table || "", notes: p.note || null, source: "phone" }, { by });
    } else if (pr.kind === "seat" || pr.kind === "noshow") {
      res = reservationCore.changeReservationStatus(state.reservations, pr.target.id, pr.kind === "seat" ? "seated" : "no_show", { by });
    } else if (pr.kind === "move") {
      res = reservationCore.updateReservation(state.reservations, pr.target.id, { table: pr.table }, { by });
    }
    if (!res || res.error) { toast({ type: "error", title: "Comando non eseguito", message: (res && res.error) || "errore" }); return; }
    setState(s => ({ ...s, reservations: res.reservations }));
    setCmd("");
    toast({ type: "success", title: "Fatto", message: pr.label });
  };

  // R2-T4 — riepilogo coperti per area: UNICA fonte di semantica è
  // floorCore.occupancyByArea (booked+seated contano, cancelled/no_show liberano,
  // bucket non assegnati/sconosciuti, capienza esplicita > tavoli > null, primo
  // token riconosciuto). Derivato e memoizzato, mai persistito; il core filtra
  // da sé giorno/servizio via opts.
  const occupancy = useMemo(() =>
    floorCore.occupancyByArea(state.reservations, state.floorAreas, state.floorTables, { serviceDate: day, service }),
    [state.reservations, state.floorAreas, state.floorTables, day, service]);

  // R2-T5 — validazione SOFT del tavolo per riga: parsing via
  // floorCore.resolveTableTokens, una mappa memoizzata sul giorno mostrato.
  // Overlay derivato: MAI blocca il salvataggio, MAI modifica table, MAI crea
  // tavoli, nessuna autocorrezione. Senza tavolo → nessuna entry (riga "non assegnato").
  const tableResolution = useMemo(() => {
    const m = new Map();
    for (const r of reservations) {
      if (r.table != null && String(r.table).trim() !== "") m.set(r.id, floorCore.resolveTableTokens(r.table, state.floorTables));
    }
    return m;
  }, [reservations, state.floorTables]);
  // Nomi area per il badge: solo aree attive non tombstoned (coerente con occupancy);
  // areaId che non risolve → parte area semplicemente omessa.
  const floorAreaNameById = useMemo(() =>
    new Map(floorCore.listAreas(state.floorAreas).map(a => [String(a.id), a.name])), [state.floorAreas]);

  // R5-T3 — fulfilment READ-ONLY per il giorno (ADR-CRM-11 F2): una sola
  // derivazione memoizzata, via reservationFulfilCore.deriveReservationFulfilment,
  // saltata in blocco se non esiste alcun link attivo (fast path). Stati con
  // precedenza: fulfilled (≥1 link risolto) > orphan (link presenti, nessuno
  // risolve) > needsLinking. Regola "Da collegare" (conservativa, dichiarata):
  // giorno mostrato NEL PASSATO (< oggi locale — il giorno corrente non matura
  // mai il warning: il servizio può essere in corso), stato né cancelled né
  // no_show, nessun link. Niente è mai persistito; nessuna scrittura da qui.
  // R5.9 — dichiarato PRIMA dei filtri: alimenta anche il chip "Da collegare".
  const fulfilmentByResv = useMemo(() => {
    const m = new Map();
    const anyLinks = Array.isArray(state.reservationReceiptLinks) && state.reservationReceiptLinks.some(l => l && !l.tombstone);
    const dayPast = day < _resvServiceToday();
    const EMPTY = { links: [], resolved: [], orphanLinks: [], fulfilled: false, realSpendCents: 0, sharedReceiptKeys: [], sharedReceipt: false };
    for (const r of reservations) {
      const f = anyLinks ? reservationFulfilCore.deriveReservationFulfilment(state, r.id) : EMPTY;
      const orphan = !f.fulfilled && f.orphanLinks.length > 0;
      const needsLinking = dayPast && r.status !== "cancelled" && r.status !== "no_show" && !f.fulfilled && !orphan;
      m.set(r.id, { ...f, orphan, needsLinking });
    }
    return m;
    // AUDIT-FIX (performance, ispettore #4 [C2]): dipendere da `state` INTERO
    // rifaceva questa derivazione O(prenotazioni × scontrini) a OGNI setState,
    // anche per un toggle UI o un keystroke su magazzino → freeze sul tablet in
    // servizio. deriveReservationFulfilment consuma SOLO reservationReceiptLinks
    // e tableReceipts (via facade): dipendo esattamente da quelli.
  }, [state.reservationReceiptLinks, state.tableReceipts, reservations, day]);
  const needsLinkingCount = [...fulfilmentByResv.values()].filter(f => f.needsLinking).length;

  // R3-T3 — conteggi chip: ogni filtro contato sulla lista completa del giorno/servizio
  // (la ricerca NON influenza i conteggi: dicono cosa c'è dietro ogni chip).
  const filterCounts = useMemo(() => {
    const m = {};
    for (const f of RESV_UI_FILTERS) m[f.key] = reservations.filter(r => _resvFilterPass(r, f.key, tableResolution.get(r.id), fulfilmentByResv.get(r.id))).length;
    return m;
  }, [reservations, tableResolution, fulfilmentByResv]);

  // R3-T3 — pipeline di visibilità: giorno/servizio (già ordinata T2) → filtro chip →
  // ricerca substring case-insensitive su rawName e displayName del cliente collegato
  // (customer non risolvibile → si cerca solo il rawName, mai crash). Niente fuzzy,
  // niente note, niente tavolo, niente cross-day. Derivazione pura, mai persistita.
  const sq = _resvNormName(search);
  const visibleReservations = useMemo(() =>
    reservations.filter(r => {
      if (!_resvFilterPass(r, filter, tableResolution.get(r.id), fulfilmentByResv.get(r.id))) return false;
      if (!sq) return true;
      if (_resvNormName(r.rawName).includes(sq)) return true;
      const cu = r.customerId ? crmCore.getCustomer(state.customers, r.customerId) : null;
      return !!cu && _resvNormName(cu.displayName || "").includes(sq);
    }),
    [reservations, filter, sq, tableResolution, fulfilmentByResv, state.customers]);

  // R3-T2 — fasce orarie: la lista visibile è già ordinata per time, quindi ogni fascia
  // è un run consecutivo; "Senza orario" chiude sempre la coda (i null ordinano per
  // ultimi). Coperti di fascia = somma derivata per il colpo d'occhio, mai persistita.
  // R3-T3: i gruppi si costruiscono DOPO filtro + ricerca, l'ordine resta quello di T2.
  const resvGroups = useMemo(() => {
    const groups = [];
    let cur = null;
    for (const r of visibleReservations) {
      const band = _resvHourBand(r.time);
      if (!cur || cur.band !== band) { cur = { band, rows: [], covers: 0 }; groups.push(cur); }
      cur.rows.push(r);
      cur.covers += Number(r.covers) || 0;
    }
    return groups;
  }, [visibleReservations]);

  // R5-T4 — candidati del picker MANUALE: gli scontrini reali del giorno/servizio
  // mostrato, perimetrati con le convenzioni esistenti del repo (getServiceDate/
  // getShift — presentazione e filtro, non identità: nel link persistito entrano
  // solo chiavi stabili via facade, F3). Dati di riga via facade (tavolo canonico,
  // importo, anchors, fiscale); covers letto dal campo (non esposto dalla facade,
  // limite noto e dichiarato). "Già collegato" = un link attivo interseca la
  // chiave primaria del receipt. Ordinamento semplice dichiarato: closedAt
  // crescente. NIENTE scoring, NIENTE ranking intelligente (F8).
  const fulfilPickerCandidates = useMemo(() => {
    if (!fulfilPickerId) return [];
    const links = Array.isArray(state.reservationReceiptLinks) ? state.reservationReceiptLinks : [];
    const rows = (state.tableReceipts || [])
      .filter(rc => rc && getServiceDate(rc.closedAt) === day && getShift(rc.closedAt) === service)
      .map(rc => _resvReceiptRow(rc, links));
    rows.sort((a, b) => (a.closedAtMs || 0) - (b.closedAtMs || 0));
    return rows;
  }, [fulfilPickerId, state.tableReceipts, state.reservationReceiptLinks, day, service]);

  // R5-T5 — suggerimenti CONSERVATIVI per la SOLA riga espansa: stessi receipt
  // del giorno/servizio del picker, esclusi quelli già collegati a QUESTA
  // prenotazione, punteggiati con le euristiche dichiarate (_resvScoreReceipt-
  // Candidate) e classificati (_resvClassifySuggestions). Il tavolo della
  // prenotazione entra come set di token canonici (resolveTokens già memoizzato
  // R2-T5). Derivazione runtime pura: niente persistenza, niente auto-link.
  const fulfilSuggestions = useMemo(() => {
    if (!expandedId) return [];
    const resv = reservations.find(x => x.id === expandedId);
    if (!resv) return [];
    const links = Array.isArray(state.reservationReceiptLinks) ? state.reservationReceiptLinks : [];
    const tres = tableResolution.get(resv.id);
    const canon = new Set((tres ? tres.tokens : []).map(t => t.canonical != null ? String(t.canonical) : null).filter(Boolean));
    const cands = (state.tableReceipts || [])
      .filter(rc => rc && getServiceDate(rc.closedAt) === day && getShift(rc.closedAt) === service)
      .map(rc => _resvReceiptRow(rc, links))
      .filter(row => !row.linkedTo.includes(String(resv.id)))
      .map(row => _resvScoreReceiptCandidate(resv, row, canon));
    return _resvClassifySuggestions(cands);
  }, [expandedId, reservations, state.tableReceipts, state.reservationReceiptLinks, tableResolution, day, service]);

  // R3-T4 — board Arrivi: sole booked+seated del giorno/servizio (tombstoned già
  // esclusi dal core, chiuse fuori), stesso ordinamento T2 della base — nessuna
  // semantica divergente. Contatori in COPERTI: attesi = booked+seated,
  // arrivati = seated, mancanti = attesi − arrivati. Tutto derivato, mai persistito.
  const arrivals = useMemo(() => reservations.filter(_resvIsActiveStatus), [reservations]);
  const arrivalTotals = useMemo(() => {
    let expected = 0, arrived = 0;
    for (const r of arrivals) {
      const c = Number(r.covers) || 0;
      expected += c;
      if (r.status === "seated") arrived += c;
    }
    return { expected, arrived, missing: expected - arrived };
  }, [arrivals]);

  // R4-T2 — GRIGLIA TAVOLI read-only (Addendum R4): derivazione UNICA e memoizzata
  // O(prenotazioni + tavoli), mai persistita, ZERO scritture. Riusa gli esiti già
  // memoizzati (arrivals = attive del giorno/servizio; tableResolution = R2-T5):
  // ogni token MATCHED aggancia la prenotazione al suo tavolo; gli unknown NON
  // entrano in griglia (restano coda di lavoro, ADR §5). Stato per tavolo:
  // ≥2 seated = conflitto; booked multipli = turni (informativo, MAI errore —
  // niente durate, niente turnover dedotto dagli orari); overflow SOLO se
  // seats > 0 (seats 0 = capienza non dichiarata, mai inventata). Le card a valle
  // sono presentazione pura di questa struttura.
  const tableBoard = useMemo(() => {
    if (mode !== "tavoli" && mode !== "regia" && mode !== "arrivi") return null;
    const byTable = new Map();
    for (const r of arrivals) {
      const tres = tableResolution.get(r.id);
      if (!tres) continue;
      for (const t of tres.tokens) {
        if (t.status !== "matched" || t.tableId == null) continue;
        const k = String(t.tableId);
        const list = byTable.get(k) || [];
        if (!list.some(x => x.id === r.id)) { list.push(r); byTable.set(k, list); }
      }
    }
    const tablesByArea = new Map();
    for (const t of floorCore.listTables(state.floorTables)) {
      const k = String(t.areaId);
      const list = tablesByArea.get(k) || [];
      list.push(t); tablesByArea.set(k, list);
    }
    return floorCore.listAreas(state.floorAreas).map(a => ({
      area: a,
      tables: (tablesByArea.get(String(a.id)) || []).map(t => {
        const rs = byTable.get(String(t.id)) || [];
        const seatedN = rs.filter(x => x.status === "seated").length;
        const bookedN = rs.length - seatedN;
        const covers = rs.reduce((s, x) => s + (Number(x.covers) || 0), 0);
        const seats = Number(t.seats) || 0;
        const status = seatedN >= 2 ? "conflict" : seatedN === 1 ? "seated"
          : bookedN >= 2 ? "turns" : bookedN === 1 ? "booked" : "free";
        return { table: t, reservations: rs, covers, seats, overflow: seats > 0 && covers > seats, status };
      }),
    }));
  }, [mode, arrivals, tableResolution, state.floorAreas, state.floorTables]);

  // R4-T3 — coda "Da assegnare" per la griglia: STESSA semantica del filtro T3
  // (attive senza tavolo, via _resvFilterPass), ordinata per orario poi nome.
  // Derivata, mai persistita.
  const unassignedResvs = useMemo(() => {
    if (mode !== "tavoli") return [];
    return arrivals
      .filter(r => _resvFilterPass(r, "unassigned"))
      .sort((a, b) => {
        const ta = a.time || null, tb = b.time || null;
        if (ta !== tb) {
          if (ta === null) return 1;
          if (tb === null) return -1;
          if (ta < tb) return -1;
          if (ta > tb) return 1;
        }
        return String(a.rawName || "").localeCompare(String(b.rawName || ""));
      });
  }, [mode, arrivals]);
  // R4-T4 — coda "Tavolo sconosciuto": attive con table valorizzato ma almeno
  // un token non risolto (esito R2-T5 riusato). Stesso ordinamento della coda
  // "Da assegnare". Derivata, mai persistita.
  const unknownResvs = useMemo(() => {
    if (mode !== "tavoli") return [];
    return arrivals
      .filter(r => { const t = tableResolution.get(r.id); return !!t && t.unknownCount > 0; })
      .sort((a, b) => {
        const ta = a.time || null, tb = b.time || null;
        if (ta !== tb) {
          if (ta === null) return 1;
          if (tb === null) return -1;
          if (ta < tb) return -1;
          if (ta > tb) return 1;
        }
        return String(a.rawName || "").localeCompare(String(b.rawName || ""));
      });
  }, [mode, arrivals, tableResolution]);
  // Selezione risolta contro le attive del giorno: giorno/servizio cambiato o
  // prenotazione non più attiva → selezione decade da sola, tap inerti.
  const assignResv = assignId ? arrivals.find(r => r.id === assignId) || null : null;
  // R6-T2 — raccomandazione tavolo per la selezione attiva: derivazione pura dal
  // board memoizzato, si ricalcola a ogni assegnazione (il board cambia). Mai scrive.
  // C2.2 — combinazioni note derivate dallo storico (pure, mai persistite)
  const knownCombos = useMemo(
    () => ((mode === "tavoli" || mode === "regia" || mode === "arrivi") && tableBoard) ? _resvKnownCombos(state.reservations, tableBoard) : [],
    [mode, state.reservations, tableBoard]);
  // SALA CHE IMPARA (A) — affinità tavolo↔coperti dall'INTERO storico (tutti i
  // giorni, non solo quello attivo): derivazione pura, si aggiorna da sola a
  // ogni prenotazione nuova. È il "come predisponiamo davvero".
  const tableAffinity = useMemo(
    () => (mode === "tavoli" || mode === "regia" || mode === "arrivi") ? _resvTableAffinityMerged(state.reservations, state.tableReceipts) : null,
    [mode, state.reservations, state.tableReceipts]);
  const tableReco = useMemo(
    () => ((mode === "tavoli" || mode === "regia") && assignResv && tableBoard) ? _resvRecommendTables(assignResv, tableBoard, knownCombos, tableAffinity) : null,
    [mode, assignResv, tableBoard, knownCombos, tableAffinity]);
  // R8-B.1 — pannello lista nera IN AGENDA (le ragazze la gestiscono dal
  // tablet, il CRM registra soltanto — richiesta Andi 07/07). prefill dalla
  // scorciatoia ⛔ sulla riga.
  const [blkOpen, setBlkOpen] = useState(false);
  const [blkPrefill, setBlkPrefill] = useState(null);
  const openBlacklistFor = (r) => { setBlkPrefill({ name: r.rawName || "", phone: r.phone || "" }); setBlkOpen(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
  // R8-B — match lista nera per le righe del giorno (derivazione pura, memo)
  const blkByResv = useMemo(() => {
    const m = new Map();
    const bl = Array.isArray(state.blocklist) ? state.blocklist.filter(b => b && !b.tombstone) : [];
    if (bl.length === 0) return m;
    for (const r of reservations) {
      const hit = _blkMatch(bl, { phone: r.phone, name: r.rawName });
      if (hit) m.set(r.id, hit);
    }
    return m;
  }, [reservations, state.blocklist]);
  // Set/label dei tavoli già assegnati alla selezionata (solo token matched):
  // servono a tap handler E banner, derivati dall'esito già memoizzato.
  const assignOwned = useMemo(() => {
    if (!assignResv) return { ids: new Set(), matched: [], labels: [], hadUnknown: false };
    const own = tableResolution.get(assignResv.id);
    // Coppie {id,label} dedupate per id: un'eventuale ripetizione dello stesso
    // tavolo nel testo si riscrive una volta sola.
    const seen = new Set();
    const matched = [];
    for (const t of (own ? own.tokens : [])) {
      if (t.status !== "matched" || t.tableId == null) continue;
      const k = String(t.tableId);
      if (seen.has(k)) continue;
      seen.add(k);
      matched.push({ id: k, label: t.label });
    }
    return { ids: seen, matched, labels: matched.map(m => m.label), hadUnknown: !!own && own.unknownCount > 0 };
  }, [assignResv, tableResolution]);

  // R4-T3/T4 — tap su card tavolo. UNICA scrittura ammessa dalla griglia:
  // updateReservation({ table: <testo canonico o vuoto> }) — label, mai id/FK
  // (Addendum R4). Semantica T4:
  //   · tavolo GIÀ della selezionata → lo rimuove dal set (ultimo → table vuoto);
  //   · tavolo libero → append al set, join canonico " + ";
  //   · tavolo NON libero → conferma a due tocchi (primo arma, secondo scrive);
  //   · label con separatori canonici → RIFIUTO, nessuna normalizzazione;
  //   · ogni riscrittura preserva SOLO i matched: i token unknown si bonificano
  //     (l'azione dalla griglia è esplicita, ADR Addendum R4).
  // La selezione RESTA attiva dopo la scrittura (serve per il multi-tavolo);
  // si chiude con Fine/Annulla dal banner. Senza selezione il tap è inerte.
  const handleTableTap = (tc) => {
    if (!assignResv) {
      // C2.3 + SALA CHE IMPARA (B) — nessuna selezione: arma il walk-in su
      // tavolo libero OPPURE prenotato solo più tardi (tutte le prenotazioni
      // ≥2h da adesso): un tavolo con la cena alle 21:30 alle 19 è VIVO.
      if (day === _resvServiceToday() && _resvWalkinWindow(tc, _resvNowMin()).ok) {
        setWalkinTap(prev => prev === String(tc.table.id) ? null : String(tc.table.id));
      } else {
        setWalkinTap(null);
      }
      return;
    }
    const by = state.currentUser?.name || "Andi";
    const tappedId = String(tc.table.id);
    const write = (labels, title, message) => {
      const res = reservationCore.updateReservation(state.reservations, assignResv.id, { table: labels.length === 0 ? "" : labels.join(" + ") }, { by });
      if (res.error) { toast({ type: "error", title: "Tavolo non aggiornato", message: res.error }); return; }
      setState(s => ({ ...s, reservations: res.reservations }));
      setConfirmTap(null);
      toast({ type: "success", title, message });
    };
    if (assignOwned.ids.has(tappedId)) {
      const kept = assignOwned.matched.filter(m => m.id !== tappedId).map(m => m.label);
      write(kept, "Tavolo rimosso", `${assignResv.rawName} → ${kept.length > 0 ? `Tav ${kept.join(" + ")}` : "senza tavolo"}`);
      return;
    }
    if (!_resvLabelCleanForJoin(tc.table.label)) {
      toast({ type: "error", title: "Etichetta non compatibile", message: `"${tc.table.label}" contiene un separatore (| + , /) e non sopravviverebbe alla riscrittura automatica. Rinominala in Sala & Tavoli o scrivi il tavolo a mano.` });
      return;
    }
    if (tc.status !== "free") {
      const armed = confirmTap && confirmTap.tableId === tappedId && confirmTap.resvId === assignResv.id;
      if (!armed) { setConfirmTap({ tableId: tappedId, resvId: assignResv.id }); return; }
    }
    const next = [...assignOwned.labels, tc.table.label];
    write(next,
      assignOwned.labels.length > 0 ? "Tavolo aggiunto" : "Tavolo assegnato",
      `${assignResv.rawName} → Tav ${next.join(" + ")}${assignOwned.hadUnknown ? " · testo bonificato" : ""}`);
  };

  // C2.1 — sotto-vista della modalità Tavoli: griglia (default, operativa) o
  // timeline (lettura del ritmo turni). Solo stato UI runtime.
  const [tavoliViz, setTavoliViz] = useState("griglia");
  // C2.3 — walk-in un tocco: tap su tavolo LIBERO senza selezione attiva arma
  // la scelta rapida coperti sul tile (solo per OGGI: un walk-in di ieri o di
  // domani non esiste). Solo stato UI runtime.
  const [walkinTap, setWalkinTap] = useState(null);
  // REGIA — tema per dispositivo (chiaro/scuro, richiesta Andi) + minuto
  // corrente per la linea ADESSO (ricalcolo 30s SOLO in modalità regia).
  const [rgTheme, setRgThemeState] = useState(_rgThemeGet);
  const toggleRgTheme = () => setRgThemeState(t => {
    const next = t === "scuro" ? "chiaro" : "scuro";
    _rgThemeSet(next);
    return next;
  });
  const [nowMin, setNowMin] = useState(() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); });
  useEffect(() => {
    if (mode !== "regia") return undefined;
    const tick = () => { const d = new Date(); setNowMin(d.getHours() * 60 + d.getMinutes()); };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [mode]);
  // R4-T5 — shortcut "Assegna" da Lista/Arrivi: SOLO stato UI runtime —
  // seleziona la prenotazione, azzera l'eventuale conferma armata e passa alla
  // modalità Tavoli; il flusso T3/T4 esistente fa il resto (se ha già tavolo,
  // il banner mostra il set e permette append/rimozione). ZERO scritture.
  const handleGoAssign = (r) => {
    setAssignId(r.id);
    setConfirmTap(null);
    setMode("tavoli");
  };
  // SALA CHE IMPARA (C) — assegnazione a UN TOCCO dalla scheda Regia: scrive
  // subito il consigliato migliore (stessa scrittura del tap manuale). Il
  // gesto fine resta: tocchi la scheda (selezione) e scegli tu in timeline.
  const handleQuickAssign = (r, best) => {
    if (!best) return;
    const labels = best.tc ? [best.tc.table.label] : best.labels;
    if (labels.some(l => !_resvLabelCleanForJoin(l))) {
      toast({ type: "error", title: "Etichetta non compatibile", message: "Contiene un separatore (| + , /): rinominala in Sala & Tavoli." });
      return;
    }
    const res = reservationCore.updateReservation(state.reservations, r.id, { table: labels.join(" + ") }, { by: state.currentUser?.name || "Andi" });
    if (res.error) { toast({ type: "error", title: "Tavolo non assegnato", message: res.error }); return; }
    setState(s => ({ ...s, reservations: res.reservations }));
    setAssignId(null);
    setConfirmTap(null);
    toast({ type: "success", title: "Tavolo assegnato", message: `${r.rawName} → Tav ${labels.join(" + ")}${best.aff > 0 ? ` · scelta abituale per ${r.covers} coperti (${best.aff}×)` : ""}` });
  };
  // C2.2 — assegnazione COMBINAZIONE dal banner: stessa scrittura del tap
  // (updateReservation, label unite con " + "), un solo gesto, mai silenziosa
  // (parte dal bottone col consigliato).
  const handleAssignCombo = (labels) => {
    if (!assignResv || !Array.isArray(labels) || labels.length === 0) return;
    if (labels.some(l => !_resvLabelCleanForJoin(l))) {
      toast({ type: "error", title: "Etichette non compatibili", message: "Una delle etichette contiene un separatore (| + , /): rinominala in Sala & Tavoli." });
      return;
    }
    const res = reservationCore.updateReservation(state.reservations, assignResv.id, { table: labels.join(" + ") }, { by: state.currentUser?.name || "Andi" });
    if (res.error) { toast({ type: "error", title: "Tavoli non assegnati", message: res.error }); return; }
    setState(s => ({ ...s, reservations: res.reservations }));
    setConfirmTap(null);
    toast({ type: "success", title: "Tavoli uniti assegnati", message: `${assignResv.rawName} → Tav ${labels.join(" + ")}` });
  };
  // C2.3 — creazione walk-in: due reducer puri in catena (create → seated),
  // UN solo setState. Orario = adesso; il gruppo è GIÀ al tavolo.
  const handleWalkinCreate = (tc, covers) => {
    const by = state.currentUser?.name || "Andi";
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    const created = reservationCore.createReservation(state.reservations, {
      serviceDate: day, service, rawName: "Walk-in", covers,
      table: tc.table.label, source: "walkin", time: hhmm,
    }, { by });
    if (created.error) { toast({ type: "error", title: "Walk-in non creato", message: created.error }); return; }
    const seated = reservationCore.changeReservationStatus(created.reservations, created.reservation.id, "seated", { by });
    setState(s => ({ ...s, reservations: seated.error ? created.reservations : seated.reservations }));
    setWalkinTap(null);
    toast({ type: "success", title: "Walk-in al tavolo", message: `Tav ${tc.table.label} · ${covers} ${covers === 1 ? "coperto" : "coperti"} · ${hhmm}` });
  };

  // R5-T4 — collega MANUALE: l'unica scrittura è il reducer puro del core T2
  // (chiavi stabili pinnate dal receipt object via facade — receipt.id mai
  // persistito, F3). Errore → toast, nessun fallback. Dopo il successo
  // badge/contatore/sezione si ri-derivano da soli (T3).
  // R5-T5: stesso identico handler anche dal click su un suggerimento — cambia
  // SOLO origin ("suggestion_confirmed", puro audit): mai score/motivazioni
  // nel link, mai link senza gesto esplicito dell'operatore (F8).
  const handleLinkReceipt = (r, receipt, origin = "manual") => {
    const res = reservationFulfilCore.createReservationReceiptLink(
      state.reservationReceiptLinks, state.reservations, receipt,
      { reservationId: r.id, origin, note: null },
      { by: state.currentUser?.name || "Andi" });
    if (res.error) {
      toast({ type: "error", title: "Scontrino non collegato", message: res.error });
      return;
    }
    setState(s => ({ ...s, reservationReceiptLinks: res.reservationReceiptLinks }));
    setFulfilPickerId(null);
    toast({ type: "success", title: "Scontrino collegato", message: `${r.rawName} → ${fmt.eur(receiptFacade.getReceiptMoney(receipt).totalCents / 100)}` });
  };
  // R5-T4 — scollega = tombstone (F7): mai delete, receipt e reservation intatti.
  const handleUnlinkReceipt = (r, linkId) => {
    const res = reservationFulfilCore.tombstoneReservationReceiptLink(state.reservationReceiptLinks, linkId, { by: state.currentUser?.name || "Andi" });
    if (res.error) {
      toast({ type: "error", title: "Scontrino non scollegato", message: res.error });
      return;
    }
    setState(s => ({ ...s, reservationReceiptLinks: res.reservationReceiptLinks }));
    toast({ type: "success", title: "Scontrino scollegato", message: r.rawName });
  };

  // R1-T2 — candidati picker: SOLO clienti esistenti non anonimizzati (opzione già
  // nel core frozen), filtro substring case-insensitive. Un solo picker aperto alla volta.
  const pq = pQuery.trim().toLowerCase();
  const pickerMatches = useMemo(() => {
    if (!pickerId) return [];
    const all = crmCore.listCustomers(state.customers, { excludeAnonymized: true });
    return pq ? all.filter(c => (c.displayName || "").toLowerCase().includes(pq)) : all;
  }, [state.customers, pq, pickerId]);
  const pickerShown = pickerMatches.slice(0, CRM_UI_PICKER_MAX);

  // R1-T3 — badge visite: UNA SOLA proiezione (projectVisits è O(receipts) e
  // projectVisitsForCustomer la rifarebbe da capo per ogni cliente), memoizzata e
  // calcolata SOLO se il giorno ha almeno una prenotazione collegata. Dalla
  // proiezione si estrae una mappa customerId → n. visite. Runtime-only, mai persistita.
  const linkedIds = useMemo(() => new Set(reservations.filter(r => r.customerId).map(r => String(r.customerId))), [reservations]);
  const visitCountByCustomer = useMemo(() => {
    const m = new Map();
    if (linkedIds.size === 0) return m;
    for (const v of crmVisitProjection.projectVisits(state)) {
      for (const p of v.participants) {
        const cid = String(p.customerId);
        if (linkedIds.has(cid)) m.set(cid, (m.get(cid) || 0) + 1);
      }
    }
    return m;
  }, [state, linkedIds]);

  // R1-T3 — suggestion "probable customer" per le prenotazioni NON collegate:
  // derivata a runtime (mai persistita), matching conservativo _resvSuggestCustomer,
  // al massimo UN candidato per prenotazione. Diventa fatto solo alla conferma.
  const suggestionByResv = useMemo(() => {
    const unlinked = reservations.filter(r => !r.customerId);
    if (unlinked.length === 0) return new Map();
    const all = crmCore.listCustomers(state.customers, { excludeAnonymized: true });
    const m = new Map();
    for (const r of unlinked) {
      const cu = _resvSuggestCustomer(r.rawName, all);
      if (cu) m.set(r.id, cu);
    }
    return m;
  }, [reservations, state.customers]);

  // R1-T4 — metriche relazionali: calcolate SOLO per la card espansa (mai per riga),
  // memoizzate finché non cambia lo stato. UNA proiezione visite on-demand
  // (projectVisitsForCustomer) per la sola prenotazione aperta; no-show e canale
  // abituale letti dallo store reservations stesso (dominio proprio, nessuna facade).
  // Tutto derivato a runtime, mai persistito.
  const relResv = relId ? reservations.find(x => x.id === relId) : null;
  const relMetrics = useMemo(() => {
    if (!relResv || !relResv.customerId) return null;
    const cid = String(relResv.customerId);
    // Visite/spesa: SOLO dallo storico collegato (Visit derivate dai Link) — parziale
    // per costruzione se il cliente ha scontrini non ancora linkati; la card lo dichiara.
    const visits = crmVisitProjection.projectVisitsForCustomer(state, cid);
    // AUDIT-FIX I1: spesa cliente = netFiscalCents (EC riconciliati esclusi, mai
    // doppio conteggio). Fallback a grossCents per robustezza (sempre presente runtime).
    const totalCents = visits.reduce((s, v) => s + ((v.totals && (v.totals.netFiscalCents ?? v.totals.grossCents)) || 0), 0);
    const lastMs = visits.reduce((mx, v) => {
      const ms = v.rawTimeEndMs != null ? v.rawTimeEndMs : v.rawTimeStartMs;
      return ms != null && (mx === null || ms > mx) ? ms : mx;
    }, null);
    // Storico prenotazioni del cliente: SOLO passate (serviceDate < quella aperta),
    // esclusa la prenotazione corrente, tombstoned esclusi.
    const past = (Array.isArray(state.reservations) ? state.reservations : []).filter(x =>
      x && !x.tombstone && x.id !== relResv.id && x.customerId != null &&
      String(x.customerId) === cid && x.serviceDate < relResv.serviceDate);
    const noShows = past.filter(x => x.status === "no_show").length;
    // Canale abituale: dominante NETTO tra le source storiche valorizzate; base
    // minima 2 prenotazioni e nessun pareggio in testa, altrimenti "—" (mai certezze inventate).
    const counts = {};
    for (const x of past) if (x.source) counts[x.source] = (counts[x.source] || 0) + 1;
    const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const sourcedTotal = ranked.reduce((s, e) => s + e[1], 0);
    const usualSource = sourcedTotal >= 2 && ranked.length > 0 && (ranked.length === 1 || ranked[0][1] > ranked[1][1])
      ? ranked[0][0] : null;
    // R1-T5 — experience insights, stesso perimetro del memo (solo card espansa, derivato).
    // Tavolo ricorrente: valorizzato, normalizzato trim/case, ≥2 occorrenze e dominante
    // netto — altrimenti nulla.
    const tCounts = new Map();
    for (const x of past) {
      const t = x.table != null ? String(x.table).trim() : "";
      if (!t) continue;
      const k = t.toLowerCase();
      const e = tCounts.get(k) || { n: 0, label: t };
      e.n++; tCounts.set(k, e);
    }
    const tRanked = [...tCounts.values()].sort((a, b) => b.n - a.n);
    const recurringTable = tRanked.length > 0 && tRanked[0].n >= 2 && (tRanked.length === 1 || tRanked[0].n > tRanked[1].n)
      ? tRanked[0].label : null;
    // Note: segnali ricorrenti (≥2 note distinte); se nulla ricorre ma esistono note
    // passate, si mostra l'ultima nota testuale com'è — mai categorie inventate.
    const pastNoted = past.filter(x => x.notes && String(x.notes).trim() !== "")
      .sort((a, b) => String(b.serviceDate).localeCompare(String(a.serviceDate)));
    const recurringNotes = _resvRecurringNoteSignals(pastNoted.map(x => x.notes));
    const lastNote = recurringNotes.length === 0 && pastNoted.length > 0
      ? { date: pastNoted[0].serviceDate, text: String(pastNoted[0].notes).trim() } : null;
    return { visitCount: visits.length, totalCents, lastMs, noShows, usualSource, pastCount: past.length, recurringTable, recurringNotes, lastNote };
  }, [relResv, state]);

  // C4.3 — Richiamo ALLA PRESA: mentre l'operatore digita nome/telefono nel form
  // di aggiunta, riconosce il cliente e ne mostra lo storico. Telefono univoco =
  // "match" (identità forte); nome univoco = "suggest" (proposta); più clienti =
  // "ambiguous" (nessuno storico, solo avviso). Pura lettura, nessuna scrittura.
  const takeRecall = useMemo(() => {
    const name = fName.trim(), phone = fPhone.trim();
    // soglia minima per non cercare su input insignificante (1 lettera / 3 cifre)
    if (name.length < 2 && phone.replace(/\D/g, "").length < 4) return null;
    const m = _crmMatchContact(state.customers, { phone, name });
    if (m.status === "none") return null;
    if (m.status === "ambiguous") return { level: "ambiguous", via: m.via, count: m.candidates.length };
    return { level: m.status, via: m.via, customer: m.customer, recall: _resvCustomerRecall(state, m.customer.id) };
  }, [fName, fPhone, state]);

  const dayLabel = new Date(day + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  const isToday = day === _resvServiceToday();
  // R3-T4 — soglia ritardo derivata AL RENDER (nessun timer, nessun polling):
  // una booked è "in ritardo" se time < now − 20 minuti, SOLO se il giorno mostrato
  // è oggi. "HH:MM" confronta bene come stringa. Se la sottrazione di 20' scavalca
  // la mezzanotte (locale), nessun ritardo: meglio nessun alert di uno sbagliato.
  const lateBefore = (() => {
    if (!isToday) return null;
    const d = new Date(Date.now() - 20 * 60000);
    if (d.getDate() !== new Date().getDate()) return null;
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  })();
  // R3-T4b — soglia "a breve": prenotazioni entro i prossimi 30 min (solo oggi).
  const soonAfter = (() => {
    if (!isToday) return null;
    const d = new Date(Date.now() + 30 * 60000);
    if (d.getDate() !== new Date().getDate()) return null; // a cavallo di mezzanotte: nessun highlight (semplice, mai sbagliato)
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  })();
  // R3-T4b — board Arrivi per URGENZA (feedback ragazze 08/07: migliorare la
  // vista "prenotati / ancora da arrivare"). Tre gruppi dall'ordine T2 già
  // ordinato per orario: in ritardo (non seduti, oltre soglia) → in arrivo (il
  // resto dei non seduti) → già al tavolo (seduti). Derivazione pura, mai persistita.
  const arrivalGroups = useMemo(() => {
    const late = [], incoming = [], seated = [];
    for (const r of arrivals) {
      if (r.status === "seated") { seated.push(r); continue; }
      if (lateBefore != null && r.time != null && r.time < lateBefore) late.push(r);
      else incoming.push(r);
    }
    return { late, incoming, seated };
  }, [arrivals, lateBefore]);

  const handleAdd = () => {
    if (!fName.trim()) { toast({ type: "error", title: "Nome mancante", message: "Inserisci il nome della prenotazione." }); return; }
    if (!fCovers.trim() || !Number.isInteger(Number(fCovers)) || Number(fCovers) < 1) { toast({ type: "error", title: "Coperti mancanti", message: "Inserisci un numero di coperti valido (≥ 1)." }); return; }
    const res = reservationCore.createReservation(state.reservations, {
      serviceDate: day, service, rawName: fName, covers: Number(fCovers),
      table: fTable, notes: fNotes.trim() !== "" ? fNotes : null, source: fSource,
      phone: fPhone.trim() !== "" ? fPhone : null,
      time: fTime.trim() !== "" ? fTime : null,
    }, { by: state.currentUser?.name || "Andi" });
    if (res.error) {
      toast({ type: "error", title: "Prenotazione non salvata", message: res.error });
      return;
    }
    // C4.3 — collega alla scheda cliente SOLO su match telefono univoco (sicuro,
    // come il ponte online); nessun auto-link su nome/ambiguo (F8).
    let nextReservations = res.reservations;
    const m = _crmMatchContact(state.customers, { phone: fPhone.trim(), name: fName.trim() });
    if (m.status === "match") {
      const up = reservationCore.updateReservation(nextReservations, res.reservation.id, { customerId: m.customer.id }, { by: state.currentUser?.name || "Andi" });
      if (!up.error) nextReservations = up.reservations;
    }
    setState(s => ({ ...s, reservations: nextReservations }));
    setFName(""); setFCovers(""); setFTable(""); setFNotes(""); setFPhone(""); setFSource("phone"); setFTime("");
    toast({ type: "success", title: "Prenotazione aggiunta", message: `${res.reservation.rawName} · ${res.reservation.covers} coperti · ${service === "pranzo" ? "Pranzo" : "Cena"}` });
  };
  const addOnEnter = (e) => { if (e.key === "Enter") handleAdd(); };

  // T4 — cambio stato rapido: un tocco per riga, esito via toast, mutazione solo se ok.
  const handleStatus = (r, status) => {
    const res = reservationCore.changeReservationStatus(state.reservations, r.id, status, { by: state.currentUser?.name || "Andi" });
    if (res.error) {
      toast({ type: "error", title: "Stato non cambiato", message: res.error });
      return;
    }
    setState(s => ({ ...s, reservations: res.reservations }));
    toast({ type: "success", title: RESV_UI_STATUS_LABELS[status] || status, message: r.rawName });
  };

  // R3-T4b — testata di sezione della board Arrivi: etichetta + conteggio + filetto.
  const arrivalSectionHead = (label, count, color) => <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.18em", color, textTransform: "uppercase", fontWeight: 700, flexShrink: 0 }}>{label} · {count}</span>
    <div style={{ flex: 1, height: 1, background: C.ruleSoft }} />
  </div>;
  // R3-T4b — scheda ARRIVO (non seduto): orario grande, badge ritardo/a-breve,
  // blacklist e n° visite in riga (feedback ragazze: li vogliono a colpo d'occhio),
  // coperti, tavolo, e le azioni rapide — Arrivato sempre, No-show solo se in
  // ritardo (chi non è arrivato all'orario), Assegna se senza tavolo.
  const arrivalCard = (r) => {
    const late = lateBefore != null && r.time != null && r.time < lateBefore;
    const soon = !late && soonAfter != null && r.time != null && r.time <= soonAfter;
    const blk = blkByResv.get(r.id);
    const visits = r.customerId ? (visitCountByCustomer.get(String(r.customerId)) || 0) : 0;
    const noTable = !r.table || String(r.table).trim() === "";
    return <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "13px 15px", borderRadius: 2,
      background: C.white, border: `1px solid ${late ? C.terra : soon ? C.amber : C.rule}`, borderLeft: `5px solid ${late ? C.terraDeep : soon ? C.amberDeep : C.ruleSoft}` }}>
      <span className="f-mono" style={{ fontSize: 18, fontWeight: 700, color: r.time ? (late ? C.terraDeep : C.ink) : C.mutedSoft, width: 56, flexShrink: 0 }}>{r.time || "—"}</span>
      {late && <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.12em", color: C.terraDeep, background: C.terraWash, border: `1px solid ${C.terra}`, padding: "3px 8px", borderRadius: 2, textTransform: "uppercase", flexShrink: 0 }}>In ritardo</span>}
      {soon && <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.12em", color: C.amberDeep, background: C.amberWash, border: `1px solid ${C.amber}`, padding: "3px 8px", borderRadius: 2, textTransform: "uppercase", flexShrink: 0 }}>A breve</span>}
      <div style={{ flex: 1, minWidth: 130 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <span className="f-display" style={{ fontSize: 16, fontWeight: 500, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.rawName}</span>
          {visits > 0 && <span className="f-mono" title={`${visits} ${visits === 1 ? "visita" : "visite"}`} style={{ fontSize: 10, color: C.oliveDeep, flexShrink: 0 }}>★ {visits}</span>}
        </div>
        {blk && <div className="f-mono" title={blk.via === "nome" ? "aggancio per nome: possibile omonimo" : "aggancio per telefono"} style={{ marginTop: 3, padding: "3px 8px", background: C.terraDeep, color: "#fff", borderRadius: 2, fontSize: 10.5, fontWeight: 700, display: "inline-block" }}>⛔ {blk.entry.reason}{blk.via === "nome" ? " (omonimo?)" : ""}</div>}
        {r.notes && <div className="f-body" style={{ fontSize: 11, color: C.amberDeep, marginTop: 2, overflowWrap: "break-word" }}>{r.notes}</div>}
      </div>
      <span className="f-mono" style={{ fontSize: 13, fontWeight: 600, color: C.inkMid, flexShrink: 0 }}>{r.covers} <span style={{ fontWeight: 400, fontSize: 11 }}>{r.covers === 1 ? "coperto" : "coperti"}</span></span>
      <ResvTableBadge tres={tableResolution.get(r.id)} areaNameById={floorAreaNameById} />
      {/* SALA CHE IMPARA (C) — assegnazione a UN TOCCO senza uscire dagli Arrivi:
          il consigliato è un chip ★, "Scegli" apre la griglia per la scelta fine. */}
      {noTable && (() => {
        const qBest = tableBoard ? (_resvRecommendTables(r, tableBoard, knownCombos, tableAffinity).best || null) : null;
        const bestLabels = qBest ? (qBest.tc ? [qBest.tc.table.label] : qBest.labels) : null;
        return <>
          {qBest && <Btn size="sm" variant="accent"
            title={`Consigliato: spreco ${qBest.waste} ${qBest.waste === 1 ? "posto" : "posti"}${qBest.aff > 0 ? ` · usato ${qBest.aff}× per ${r.covers} coperti` : ""} — un tocco assegna`}
            onClick={() => handleQuickAssign(r, qBest)}>★ Tav {bestLabels.join(" + ")}</Btn>}
          <Btn size="xs" variant="ghost" onClick={() => handleGoAssign(r)}>{qBest ? "Scegli" : "Assegna"}</Btn>
        </>;
      })()}
      {/* C2.5 — conferma/promemoria WhatsApp anche in board Arrivi: il nudge
          anti-no-show è più utile qui che in Lista (mai invio automatico). */}
      {_resvWaLink(r) && <a href={_resvWaLink(r)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
        title={`WhatsApp a ${r.phone}: conferma precompilata`}
        className="f-mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: C.oliveDeep, background: C.oliveWash, border: `1px solid ${C.olive}`, padding: "3px 8px", borderRadius: 2, flexShrink: 0, textDecoration: "none", fontWeight: 600 }}>
        WA ✓
      </a>}
      <Btn variant="olive" onClick={() => handleStatus(r, "seated")}>Arrivato</Btn>
      {late && <Btn size="sm" variant="danger" onClick={() => handleStatus(r, "no_show")}>No-show</Btn>}
    </div>;
  };
  // R3-T4b — scheda GIÀ AL TAVOLO: compatta e attenuata (durante il servizio è
  // "fatto", non deve rubare spazio ai da-arrivare). Un tocco la riporta indietro.
  const seatedCardCompact = (r) => <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", borderRadius: 2, background: C.oliveWash, border: `1px solid ${C.olive}` }}>
    <span className="f-mono" style={{ fontSize: 13, color: C.oliveDeep, width: 46, flexShrink: 0 }}>{r.time || "—"}</span>
    <span className="f-display" style={{ fontSize: 14, color: C.ink, flex: 1, minWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.rawName}</span>
    <span className="f-mono" style={{ fontSize: 12, color: C.inkMid, flexShrink: 0 }}>{r.covers}p</span>
    <ResvTableBadge tres={tableResolution.get(r.id)} areaNameById={floorAreaNameById} />
    <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.12em", color: C.oliveDeep, textTransform: "uppercase", flexShrink: 0 }}>✓ Al tavolo</span>
    <button className="tx f-mono" onClick={() => handleStatus(r, "booked")} title="Riporta tra gli attesi" style={{ fontSize: 10, color: C.muted, cursor: "pointer", background: "none", border: "none", flexShrink: 0 }}>↺</button>
  </div>;

  // R3-T5 — walk-in: cliente entrato senza prenotare. Le DUE riduzioni pure
  // (createReservation → changeReservationStatus "seated") si calcolano in
  // sequenza sull'array ritornato dalla prima; lo stato si tocca con UN SOLO
  // setState e SOLO se entrambe vanno a buon fine — mai stati a metà.
  // time = orario corrente HH:MM (il walk-in "arriva adesso": fatto dichiarato),
  // source = "walkin", nome vuoto → "Walk-in".
  const handleWalkIn = () => {
    if (!wCovers.trim() || !Number.isInteger(Number(wCovers)) || Number(wCovers) < 1) {
      toast({ type: "error", title: "Coperti mancanti", message: "Inserisci un numero di coperti valido (≥ 1)." });
      return;
    }
    const nowD = new Date();
    const nowTime = `${String(nowD.getHours()).padStart(2, "0")}:${String(nowD.getMinutes()).padStart(2, "0")}`;
    const by = state.currentUser?.name || "Andi";
    const created = reservationCore.createReservation(state.reservations, {
      serviceDate: day, service, rawName: wName.trim() !== "" ? wName : "Walk-in",
      covers: Number(wCovers), table: wTable, notes: null, source: "walkin", time: nowTime,
    }, { by });
    if (created.error) {
      toast({ type: "error", title: "Walk-in non salvato", message: created.error });
      return;
    }
    const seated = reservationCore.changeReservationStatus(created.reservations, created.reservation.id, "seated", { by });
    if (seated.error) {
      toast({ type: "error", title: "Walk-in non salvato", message: seated.error });
      return;
    }
    setState(s => ({ ...s, reservations: seated.reservations }));
    setWName(""); setWCovers(""); setWTable("");
    toast({ type: "success", title: "Walk-in al tavolo", message: `${created.reservation.rawName} · ${created.reservation.covers} ${created.reservation.covers === 1 ? "coperto" : "coperti"} · ${nowTime}` });
  };
  const walkInOnEnter = (e) => { if (e.key === "Enter") handleWalkIn(); };

  // T4 — edit inline coperti/tavolo/note (mai rawName/serviceDate/service/customerId qui).
  const startEdit = (r) => { closePicker(); setEditingId(r.id); setECovers(String(r.covers)); setETable(r.table || ""); setENotes(r.notes || ""); };
  const cancelEdit = () => { setEditingId(null); setECovers(""); setETable(""); setENotes(""); };
  const handleSaveEdit = (r) => {
    if (!eCovers.trim() || !Number.isInteger(Number(eCovers)) || Number(eCovers) < 1) {
      toast({ type: "error", title: "Coperti non validi", message: "Inserisci un numero di coperti valido (≥ 1)." });
      return;
    }
    const res = reservationCore.updateReservation(state.reservations, r.id, {
      covers: Number(eCovers), table: eTable, notes: eNotes.trim() !== "" ? eNotes : null,
    }, { by: state.currentUser?.name || "Andi" });
    if (res.error) {
      toast({ type: "error", title: "Modifica non salvata", message: res.error });
      return;
    }
    setState(s => ({ ...s, reservations: res.reservations }));
    cancelEdit();
    toast({ type: "success", title: "Prenotazione aggiornata", message: r.rawName });
  };

  // R1-T2 — collega/scollega cliente ESISTENTE: SOLO Reservation.customerId via
  // updateReservation (mai crmLinkCore: nessun receipt esiste ancora). Il Customer
  // non viene mai creato, modificato o cancellato da qui.
  const openPicker = (r) => { setPickerId(r.id); setPQuery(""); };
  const closePicker = () => { setPickerId(null); setPQuery(""); };
  const handleLinkCustomer = (r, cu) => {
    const res = reservationCore.updateReservation(state.reservations, r.id, { customerId: cu.id }, { by: state.currentUser?.name || "Andi" });
    if (res.error) {
      toast({ type: "error", title: "Cliente non collegato", message: res.error });
      return;
    }
    setState(s => ({ ...s, reservations: res.reservations }));
    closePicker();
    toast({ type: "success", title: "Cliente collegato", message: `${r.rawName} → ${cu.displayName}` });
  };
  const handleUnlinkCustomer = (r) => {
    const res = reservationCore.updateReservation(state.reservations, r.id, { customerId: null }, { by: state.currentUser?.name || "Andi" });
    if (res.error) {
      toast({ type: "error", title: "Cliente non scollegato", message: res.error });
      return;
    }
    setState(s => ({ ...s, reservations: res.reservations }));
    toast({ type: "success", title: "Cliente scollegato", message: r.rawName });
  };

  const inputStyle = { padding: "8px 10px", border: `1px solid ${C.rule}`, borderRadius: 2, fontSize: 13, background: C.white, color: C.ink, outline: "none" };

  return <div className="fade-up">
    <PageHeader kicker="Sala · Prenotazioni" title="Agenda"
      subtitle="L'agenda prenotazioni del ristorante. Scegli il giorno e il servizio, aggiungi le prenotazioni con nome e coperti." />

    {/* A. Controllo giorno */}
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
      <IconBtn icon={ChevronLeft} onClick={() => setDay(d => _resvShiftDay(d, -1))} title="Giorno precedente" />
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <span className="f-display" style={{ fontSize: 20, fontWeight: 600, color: C.ink, textTransform: "capitalize" }}>{dayLabel}</span>
        {isToday && <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.tealDeep, background: C.tealWash, border: `1px solid ${C.teal}`, padding: "2px 8px", borderRadius: 2 }}>OGGI</span>}
      </div>
      <IconBtn icon={ChevronRight} onClick={() => setDay(d => _resvShiftDay(d, 1))} title="Giorno successivo" />
      <div style={{ flex: 1 }} />
      <input type="date" value={day} onChange={e => { if (e.target.value) setDay(e.target.value); }} className="f-body" style={inputStyle} />
      {!isToday && <Btn size="sm" variant="ghost" onClick={() => setDay(todayLocalISO())}>Oggi</Btn>}
    </div>

    {/* W3 — coda richieste online (lista globale da AppInner, badge in sidebar) */}
    <OnlineRequestsPanel state={state} setState={setState} reqs={onlineReqs} onRefresh={refreshOnlineReqs} />

    {/* R8-B.1 — lista nera gestita QUI (anche dal tablet in Accoglienza) */}
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: blkOpen ? 8 : 12 }}>
      <button className="tx f-mono" onClick={() => { setBlkOpen(v => !v); if (blkOpen) setBlkPrefill(null); }}
        style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", padding: "5px 12px", borderRadius: 2, cursor: "pointer",
          background: blkOpen ? C.terraDeep : C.white, color: blkOpen ? "#fff" : C.terraDeep, border: `1px solid ${C.terra}` }}>
        ⛔ Lista nera{(state.blocklist || []).filter(b => b && !b.tombstone).length > 0 ? ` (${(state.blocklist || []).filter(b => b && !b.tombstone).length})` : ""}
      </button>
    </div>
    {blkOpen && <BlacklistPanel state={state} setState={setState} prefill={blkPrefill} />}

    {/* B. Switch servizio + toggle modalità (R3-T4) + C. Contatori */}
    <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
      <Tabs value={service} onChange={setService} tabs={[
        { id: "pranzo", label: "Pranzo" },
        { id: "cena", label: "Cena" },
      ]} />
      <Tabs value={mode} onChange={setMode} tabs={[
        { id: "lista", label: "Lista" },
        { id: "arrivi", label: "Arrivi" },
        { id: "tavoli", label: "Tavoli" },
        { id: "regia", label: "Regia" },
      ]} />
      <div className="f-mono" style={{ fontSize: 11, letterSpacing: "0.15em", color: C.inkMid, textTransform: "uppercase", marginBottom: 20 }}>
        {reservations.length} {reservations.length === 1 ? "prenotazione" : "prenotazioni"} · {totalCovers} coperti
        {/* R5-T3 — coda di lavoro, non un errore: appare solo se > 0 */}
        {needsLinkingCount > 0 && <span style={{ color: C.amberDeep }}> · {needsLinkingCount} da collegare</span>}
      </div>
    </div>

    {/* R2-T4 — contatori per area: fascia chip compatta, solo lettura. Capienza
        ignota → solo occupati, mai residui inventati; overCapacity = warning sobrio,
        mai blocco. Bucket visibili solo se > 0. */}
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
      {occupancy.areas.length === 0
        ? <span className="f-body" style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>
          Nessuna area attiva — configura sale e tavoli in Sala &amp; Tavoli.
        </span>
        : occupancy.areas.map(a => <span key={a.areaId} className="f-mono"
          style={{ fontSize: 10, letterSpacing: "0.08em", padding: "4px 10px", borderRadius: 2, flexShrink: 0,
            background: a.overCapacity ? C.terraWash : C.paperDeep,
            border: `1px solid ${a.overCapacity ? C.terra : C.rule}`,
            color: a.overCapacity ? C.terraDeep : C.inkMid }}>
          {a.name} <span style={{ fontWeight: 600, color: a.overCapacity ? C.terraDeep : C.ink }}>{a.occupied}{a.capacity != null ? `/${a.capacity}` : ""}</span>
          {a.capacity != null && !a.overCapacity && ` · ${a.remaining} liberi`}
          {a.overCapacity && ` · ${-a.remaining} oltre`}
        </span>)}
      {occupancy.unassigned.covers > 0 && <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.08em", padding: "4px 10px", borderRadius: 2, flexShrink: 0, background: C.paperDeep, border: `1px solid ${C.amber}`, color: C.amberDeep }}>
        Non assegnati <span style={{ fontWeight: 600 }}>{occupancy.unassigned.covers}</span>
      </span>}
      {occupancy.unknownTable.covers > 0 && <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.08em", padding: "4px 10px", borderRadius: 2, flexShrink: 0, background: C.paperDeep, border: `1px solid ${C.terra}`, color: C.terraDeep }}>
        Tavolo sconosciuto <span style={{ fontWeight: 600 }}>{occupancy.unknownTable.covers}</span>
      </span>}
    </div>

    {/* FASE C — ATTESI REALI in cima al servizio selezionato. Sola lettura; il
        servizio è quello scelto dai Tabs Pranzo/Cena (la vista mostra un solo
        servizio per volta). Si auto-nasconde se non ci sono dati per gli attesi. */}
    {mode === "lista" && <AgendaAttesiStrip state={state} day={day} service={service} />}

    {/* R5.9-OP — riconciliazione operativa: batch "conferma i sicuri" nel flusso
        reale della sala. Solo in Lista; si auto-nasconde se non c'è lavoro. */}
    {mode === "lista" && <AgendaReconStrip state={state} setState={setState} day={day} service={service} />}

    {/* R6-VAR — consuntivo coperti prenotato→reale + soldi persi. Anche in Arrivi
        (QA): durante il servizio l'operatore vede lì i no-show/mancati in tempo reale. */}
    {(mode === "lista" || mode === "arrivi") && <AgendaCoversVarianceStrip state={state} day={day} service={service} />}

    {/* R5.5-T3 — pannello bootstrap Pienissimo: nascosto dietro ?bootstrap=1 */}
    {bootstrapOn && <BootstrapImportPanel state={state} setState={setState} />}

    {/* C5-S1 — Barra comando rapido: scrivi in italiano leggero, vedi la proposta,
        conferma con Invio o "Esegui" (F8: nessuna scrittura senza conferma esplicita). */}
    {mode === "lista" && <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, background: C.ink, borderRadius: 2, padding: "8px 12px" }}>
        <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.15em", color: C.paper, textTransform: "uppercase", flexShrink: 0 }}>⌘ Comando</span>
        <input value={cmd} onChange={e => setCmd(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") handleRunCommand(); else if (e.key === "Escape") setCmd(""); }}
          placeholder='4 20:30 rossi tav 25 · arrivato rossi · sposta rossi 25 · no-show rossi'
          className="f-body" style={{ flex: 1, border: "none", outline: "none", background: "transparent", color: C.paper, fontSize: 13, minWidth: 0 }} />
        {cmd && <IconBtn icon={X} size={13} onClick={() => setCmd("")} />}
      </div>
      {cmdProposal && <div className="fade-in" style={{ marginTop: 6, padding: "8px 12px", borderRadius: 2, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        background: cmdProposal.ok ? C.tealWash : C.amberWash, border: `1px solid ${cmdProposal.ok ? C.teal : C.amber}` }}>
        {cmdProposal.ok
          ? <>
            <span className="f-body" style={{ fontSize: 13, color: C.ink, flex: 1, minWidth: 140 }}>{cmdProposal.label}</span>
            <Btn size="sm" variant="accent" onClick={handleRunCommand}>Esegui ⏎</Btn>
          </>
          : <span className="f-body" style={{ fontSize: 12, color: C.amberDeep }}>
            {cmdProposal.msg}{cmdProposal.hint ? ' — es. "4 20:30 rossi", "arrivato rossi", "sposta rossi 25"' : ""}
          </span>}
      </div>}
    </div>}

    {/* Add inline rapido — serviceDate/service ereditati dalla vista, mai chiesti.
        R3-T4: solo in modalità Lista (l'inserimento walk-in dedicato è T5). */}
    {mode === "lista" && <div style={{ display: "flex", gap: 8, marginBottom: 16, padding: "12px 14px", background: C.paperDeep, border: `1px solid ${C.rule}`, borderRadius: 2, alignItems: "center", flexWrap: "wrap" }}>
      <input value={fName} onChange={e => setFName(e.target.value)} onKeyDown={addOnEnter} placeholder="Nome *" className="f-body" style={{ ...inputStyle, flex: 2, minWidth: 140 }} />
      <input value={fTime} onChange={e => setFTime(e.target.value)} onKeyDown={addOnEnter} title="Orario (opzionale)" type="time" className="f-body" style={{ ...inputStyle, width: 105 }} />
      <input value={fCovers} onChange={e => setFCovers(e.target.value)} onKeyDown={addOnEnter} placeholder="Coperti *" type="number" min="1" className="f-body" style={{ ...inputStyle, width: 90 }} />
      <input value={fTable} onChange={e => setFTable(e.target.value)} onKeyDown={addOnEnter} placeholder="Tavolo" className="f-body" style={{ ...inputStyle, width: 90 }} />
      <input value={fNotes} onChange={e => setFNotes(e.target.value)} onKeyDown={addOnEnter} placeholder="Note (allergie, occasioni...)" className="f-body" style={{ ...inputStyle, flex: 3, minWidth: 160 }} />
      <input value={fPhone} onChange={e => setFPhone(e.target.value)} onKeyDown={addOnEnter} placeholder="Telefono" type="tel" className="f-body" style={{ ...inputStyle, width: 120 }} />
      <select value={fSource} onChange={e => setFSource(e.target.value)} title="Provenienza della prenotazione" className="f-body" style={{ ...inputStyle, width: 110 }}>
        {RESV_SOURCES.map(s => <option key={s} value={s}>{RESV_UI_SOURCE_LABELS[s]}</option>)}
      </select>
      <Btn size="sm" variant="accent" icon={Plus} onClick={handleAdd}>Aggiungi</Btn>
    </div>}

    {/* C4.3 — Richiamo alla presa: chi è questo cliente, live mentre digiti. Solo
        lettura; il collegamento vero avviene su match telefono univoco all'Aggiungi. */}
    {mode === "lista" && takeRecall && (() => {
      const t = takeRecall;
      if (t.level === "ambiguous") {
        return <div className="fade-in" style={{ marginTop: -8, marginBottom: 16, padding: "8px 14px", background: C.paperDeep, border: `1px solid ${C.rule}`, borderRadius: 2, fontSize: 12, color: C.inkMid, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span aria-hidden="true">⚠</span>
          <span>Più clienti con questo {t.via === "telefono" ? "numero" : "nome"} — controlla in <strong>Clienti</strong> prima di collegare.</span>
        </div>;
      }
      const strong = t.level === "match";
      const r = t.recall;
      const bits = [
        `${r.visitCount} visit${r.visitCount === 1 ? "a" : "e"}`,
        r.recurringTable ? `ama il tavolo ${r.recurringTable}` : null,
        r.lastMs != null ? `ultima ${fmt.dateFull(r.lastMs)}` : null,
        r.totalCents > 0 ? `${fmt.eur(r.totalCents / 100)} storico` : null,
      ].filter(Boolean);
      return <div className="fade-in" style={{ marginTop: -8, marginBottom: 16, padding: "9px 14px", background: strong ? C.tealWash : C.amberWash, border: `1px solid ${strong ? C.teal : C.amber}`, borderRadius: 2, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: strong ? C.tealDeep : C.amberDeep, fontWeight: 700, flexShrink: 0 }}>
          {strong ? "Cliente riconosciuto" : "Forse conosciuto"}
        </span>
        <span className="f-display" style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>{t.customer.displayName}</span>
        {bits.length > 0 && <span className="f-body" style={{ fontSize: 12, color: C.inkSoft }}>{bits.join(" · ")}</span>}
        {r.noShows > 0 && <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: C.amberDeep, background: C.white, border: `1px solid ${C.amber}`, padding: "2px 7px", borderRadius: 2 }}>⚠ {r.noShows} no-show</span>}
        <div style={{ flex: 1 }} />
        <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: C.muted, flexShrink: 0 }}>{strong ? "dal telefono" : "stesso nome"}</span>
      </div>;
    })()}

    {/* R3-T3 — ricerca nel giorno/servizio + filtri chip a selezione singola con
        conteggi. Solo visibilità: il dato non cambia mai; le chiuse escono dal
        default "Tutte" e vivono dietro il chip "Chiuse". R3-T4: solo in Lista. */}
    {mode === "lista" && <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.white, border: `1px solid ${C.rule}`, padding: "6px 12px", borderRadius: 2, flex: 1, minWidth: 200 }}>
        <Search size={14} color={C.muted} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca cliente o prenotazione"
          onKeyDown={e => { if (e.key === "Escape") setSearch(""); }}
          className="f-body" style={{ border: "none", outline: "none", flex: 1, fontSize: 13, background: "transparent", color: C.ink }} />
        {search && <IconBtn icon={X} size={12} onClick={() => setSearch("")} />}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {RESV_UI_FILTERS.map(f => {
          const on = filter === f.key;
          return <button key={f.key} className="tx f-mono" onClick={() => setFilter(f.key)}
            style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", padding: "5px 10px", borderRadius: 2, cursor: "pointer", flexShrink: 0,
              background: on ? C.ink : C.white, color: on ? C.paper : C.inkMid, border: `1px solid ${on ? C.ink : C.rule}` }}>
            {f.label} <span style={{ fontWeight: 600 }}>{filterCounts[f.key]}</span>
          </button>;
        })}
      </div>
    </div>}

    {/* D. Lista prenotazioni del giorno/servizio (modalità Lista) */}
    {mode === "lista" && (reservations.length === 0
      ? <EmptyState icon={Calendar} title={`Nessuna prenotazione a ${service} ${isToday ? "oggi" : `il ${dayLabel}`}`}
          body="Aggiungi la prima con la riga qui sopra: nome e coperti bastano." />
      : visibleReservations.length === 0
      ? <EmptyState icon={Search} title="Nessuna prenotazione corrisponde"
          body={sq
            ? "Nessun risultato per questa ricerca con il filtro attivo. Svuota la ricerca o cambia filtro."
            : `Nessuna prenotazione nel filtro "${(RESV_UI_FILTERS.find(f => f.key === filter) || {}).label || filter}" — cambia filtro per vedere le altre.`} />
      : <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {resvGroups.map(g => <div key={g.band} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {/* R3-T2 — header di fascia sottile: etichetta derivata + coperti di fascia */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase", flexShrink: 0 }}>
            {g.band} · {g.covers} {g.covers === 1 ? "coperto" : "coperti"}
          </span>
          <div style={{ flex: 1, height: 1, background: C.ruleSoft }} />
        </div>
        {g.rows.map(r => {
          const sc = RESV_UI_STATUS_COLORS[r.status] || RESV_UI_STATUS_COLORS.booked;
          const closed = r.status === "cancelled" || r.status === "no_show";
          const editing = editingId === r.id;
          const picking = pickerId === r.id;
          const expanded = expandedId === r.id;
          // R1-T2 — risoluzione read-only: tombstoned/inesistente → null → "non disponibile".
          const cust = r.customerId ? crmCore.getCustomer(state.customers, r.customerId) : null;
          // R1-T3 — badge visite (solo linked risolti) e suggestion (solo non collegate).
          const visitCount = cust ? (visitCountByCustomer.get(String(r.customerId)) || 0) : null;
          const sugg = !r.customerId ? suggestionByResv.get(r.id) : null;
          return <div key={r.id} style={{ padding: "10px 14px", background: C.white, border: `1px solid ${editing ? C.ink : C.rule}`, borderRadius: 2, opacity: closed && !editing ? 0.55 : 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {/* R3-T1/T2 — l'orario apre la riga: colonna mono fissa, primo colpo d'occhio.
                  Senza time → "—" discreto (la fascia "Senza orario" lo dice già). */}
              <span className="f-mono" style={{ fontSize: 13, fontWeight: 600, color: r.time ? C.ink : C.mutedSoft, width: 44, flexShrink: 0 }}>{r.time || "—"}</span>
              <div style={{ flex: 1, minWidth: 120 }}>
                <div className="f-display" style={{ fontSize: 15, fontWeight: 500, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: r.status === "cancelled" ? "line-through" : "none" }}>{r.rawName}</div>
                {!editing && r.notes && <div className="f-body" style={{ fontSize: 11, color: C.amberDeep, marginTop: 3, overflowWrap: "break-word" }}>{r.notes}</div>}
              </div>
              {!editing && <>
                {/* R3-T2 — segnale cliente COMPATTO a riga chiusa: chip linked (1 tocco →
                    espansione con Relazione già aperta) o suggestion con conferma diretta
                    (resta a 1 tocco). Tutto il resto della parte cliente vive nell'espansione. */}
                {r.customerId && (cust
                  ? <button className="tx f-mono" onClick={() => { if (expanded) { setExpandedId(null); setRelId(null); closePicker(); } else { setExpandedId(r.id); setRelId(r.id); } }}
                    title={expanded ? "Chiudi dettagli cliente" : "Apri relazione cliente"}
                    style={{ fontSize: 10, letterSpacing: "0.1em", color: C.tealDeep, background: C.tealWash, border: `1px solid ${C.teal}`, padding: "3px 8px", borderRadius: 2, flexShrink: 0, cursor: "pointer", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    ★ {visitCount} · {cust.displayName}
                  </button>
                  : <span className="f-body" style={{ fontSize: 10, color: C.muted, fontStyle: "italic", flexShrink: 0 }}>Cliente n/d</span>)}
                {!r.customerId && sugg && !expanded && <span className="fade-in" style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                  <span className="f-body" style={{ fontSize: 11, color: C.tealDeep }}>È <span className="f-display" style={{ fontWeight: 500 }}>{sugg.displayName}</span>?</span>
                  <Btn size="xs" variant="accent" onClick={() => handleLinkCustomer(r, sugg)}>Collega</Btn>
                </span>}
                <span className="f-mono" style={{ fontSize: 11, color: C.inkMid, flexShrink: 0 }}>{r.covers} {r.covers === 1 ? "coperto" : "coperti"}</span>
                {/* R2-T5 — badge tavolo con validazione soft: riconosciuto (+area),
                    parziale, sconosciuto, non assegnato. Warning visivo, mai blocco.
                    R3-T4: stesso componente condiviso con la board Arrivi. */}
                <ResvTableBadge tres={tableResolution.get(r.id)} areaNameById={floorAreaNameById} />
                {/* R4-T5 — shortcut alla griglia: solo attive, solo stato UI */}
                {!closed && <Btn size="xs" variant="ghost" onClick={() => handleGoAssign(r)}>Assegna</Btn>}
                {r.source && r.source !== "phone" && <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.1em", color: C.inkMid, textTransform: "uppercase", background: C.paperDeep, border: `1px solid ${C.ruleSoft}`, padding: "3px 8px", borderRadius: 2, flexShrink: 0 }}>{RESV_UI_SOURCE_LABELS[r.source] || r.source}</span>}
                {/* C2.5 — conferma WhatsApp: apre wa.me col testo pronto, mai invio automatico */}
                {_resvWaLink(r) && <a href={_resvWaLink(r)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                  title={`WhatsApp a ${r.phone}: conferma precompilata`}
                  className="f-mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: C.oliveDeep, background: C.oliveWash, border: `1px solid ${C.olive}`, padding: "3px 8px", borderRadius: 2, flexShrink: 0, textDecoration: "none", fontWeight: 600 }}>
                  WA ✓
                </a>}
                {/* R8-B — bollino lista nera COL MOTIVO, impossibile non vederlo */}
                {blkByResv.has(r.id) && <span className="f-mono"
                  title={`Lista nera: ${blkByResv.get(r.id).entry.reason}${blkByResv.get(r.id).via === "nome" ? " — aggancio per nome: possibile omonimo" : ""}`}
                  style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: C.terraDeep, padding: "3px 9px", borderRadius: 2, flexShrink: 0 }}>
                  ⛔ {String(blkByResv.get(r.id).entry.reason).slice(0, 34)}{blkByResv.get(r.id).via === "nome" ? " ?" : ""}
                </span>}
                {/* R5-T3 — badge fulfilment READ-ONLY (tutto derivato, ADR-CRM-11):
                    collegato con importo reale / link orfano / da collegare;
                    futuro senza link → nessun badge, nessun rumore. */}
                {(() => {
                  const f = fulfilmentByResv.get(r.id);
                  if (!f) return null;
                  const chip = { fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", padding: "3px 8px", borderRadius: 2, flexShrink: 0 };
                  if (f.fulfilled) return <span className="f-mono" title={`Scontrino collegato · ${f.resolved.length} ${f.resolved.length === 1 ? "scontrino" : "scontrini"}`}
                    style={{ ...chip, color: C.oliveDeep, background: C.oliveWash, border: `1px solid ${C.olive}` }}>
                    {f.realSpendCents > 0 ? fmt.eur(f.realSpendCents / 100) : "Scontrino collegato"}
                  </span>;
                  if (f.orphan) return <span className="f-mono" title="Link presente ma nessuno scontrino si risolve (probabile re-import): da riconciliare"
                    style={{ ...chip, color: C.terraDeep, background: C.paperDeep, border: `1px solid ${C.terra}` }}>Link orfano</span>;
                  if (f.needsLinking) return <span className="f-mono"
                    style={{ ...chip, color: C.amberDeep, background: C.amberWash, border: `1px solid ${C.amber}` }}>Da collegare</span>;
                  return null;
                })()}
              </>}
              <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: sc.fg, textTransform: "uppercase", border: `1px solid ${sc.border}`, padding: "3px 8px", borderRadius: 2, flexShrink: 0 }}>
                {RESV_UI_STATUS_LABELS[r.status] || r.status}
              </span>
              {!editing && <IconBtn icon={Edit2} size={14} title="Modifica coperti / tavolo / note" onClick={() => startEdit(r)} />}
              {/* R3-T2 — toggle espansione: apre la parte cliente completa (R1 intatta) */}
              {!editing && <button className="tx" title={expanded ? "Chiudi dettagli" : "Cliente / relazione"}
                onClick={() => { if (expanded) { setExpandedId(null); setRelId(null); closePicker(); } else setExpandedId(r.id); }}
                style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6, display: "inline-flex", alignItems: "center" }}>
                <ChevronDown size={15} color={C.muted} style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
              </button>}
            </div>

            {/* T4 — azioni di stato contestuali: un tocco, niente modali */}
            {!editing && <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
              {r.status === "booked" ? <>
                <Btn size="xs" variant="olive" onClick={() => handleStatus(r, "seated")}>Arrivato</Btn>
                <Btn size="xs" variant="ghost" onClick={() => handleStatus(r, "cancelled")}>Annullata</Btn>
                <Btn size="xs" variant="danger" onClick={() => handleStatus(r, "no_show")}>No-show</Btn>
                {!blkByResv.has(r.id) && <Btn size="xs" variant="ghost" onClick={() => openBlacklistFor(r)} title="Metti in lista nera (nome e telefono già compilati: scrivi solo il motivo)">⛔</Btn>}
              </> : <Btn size="xs" variant="ghost" onClick={() => handleStatus(r, "booked")} title="Correzione esplicita: riporta a Prenotato">↺ Prenotato</Btn>}
            </div>}

            {/* R1-T2 — riga cliente completa: collegato / non collegato / riferimento non
                risolvibile. R3-T2: vive SOLO nell'espansione — a riga chiusa resta il
                segnale compatto nella riga principale. */}
            {!editing && expanded && <div className="fade-in" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.ruleSoft}` }}>
              <User size={13} color={cust ? C.tealDeep : C.mutedSoft} style={{ flexShrink: 0 }} />
              {r.customerId
                ? (cust
                  ? <>
                    <span className="f-body" style={{ fontSize: 12, color: C.ink }}>Cliente: <span className="f-display" style={{ fontWeight: 500 }}>{cust.displayName}</span></span>
                    <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.1em", color: C.tealDeep, background: C.tealWash, border: `1px solid ${C.teal}`, padding: "2px 8px", borderRadius: 2, flexShrink: 0 }}>★ {visitCount} {visitCount === 1 ? "visita" : "visite"}</span>
                    <div style={{ flex: 1 }} />
                    {relId === r.id
                      ? <Btn size="xs" variant="ghost" onClick={() => setRelId(null)}>Chiudi</Btn>
                      : <Btn size="xs" variant="ghost" onClick={() => setRelId(r.id)}>Relazione</Btn>}
                    <Btn size="xs" variant="ghost" onClick={() => handleUnlinkCustomer(r)}>Scollega</Btn>
                  </>
                  : <>
                    <span className="f-body" style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>Cliente non disponibile</span>
                    <div style={{ flex: 1 }} />
                    <Btn size="xs" variant="ghost" onClick={() => handleUnlinkCustomer(r)}>Scollega riferimento</Btn>
                  </>)
                : <>
                  <span className="f-body" style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>Cliente: non collegato</span>
                  {/* R1-T3 — proposta, non certezza: si collega solo alla conferma esplicita */}
                  {sugg && !picking && <span className="fade-in" style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <span className="f-body" style={{ fontSize: 12, color: C.tealDeep }}>È <span className="f-display" style={{ fontWeight: 500 }}>{sugg.displayName}</span>?</span>
                    <Btn size="xs" variant="accent" onClick={() => handleLinkCustomer(r, sugg)}>Collega</Btn>
                  </span>}
                  <div style={{ flex: 1 }} />
                  {picking
                    ? <Btn size="xs" variant="ghost" onClick={closePicker}>Annulla</Btn>
                    : <Btn size="xs" variant="ghost" onClick={() => openPicker(r)}>Collega cliente</Btn>}
                </>}
            </div>}

            {/* R1-T4 — pannello Relazione: solo card espansa + cliente risolto, tutto derivato */}
            {!editing && expanded && relId === r.id && cust && relMetrics && (() => {
              const m = relMetrics;
              const relRow = (label, value) => <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 190 }}>
                <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.muted, textTransform: "uppercase" }}>{label}</span>
                <span className="f-body" style={{ fontSize: 12.5, color: C.ink }}>{value}</span>
              </div>;
              return <div className="fade-in" style={{ marginTop: 8, padding: "10px 14px", background: C.paperDeep, border: `1px solid ${C.ruleSoft}`, borderRadius: 2 }}>
                <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.tealDeep, marginBottom: 8 }}>RELAZIONE</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "7px 28px" }}>
                  {relRow("Visite collegate", m.visitCount)}
                  {relRow("Ultima visita", m.lastMs != null ? `${fmt.dateFull(m.lastMs)} · ${_resvDaysAgo(m.lastMs)}` : "—")}
                  {relRow("Spesa totale", m.visitCount > 0 ? fmt.eur(m.totalCents / 100) : "—")}
                  {relRow("Spesa media", m.visitCount > 0 ? fmt.eur(m.totalCents / 100 / m.visitCount) : "—")}
                  {relRow("No-show storici", m.noShows)}
                  {relRow("Canale abituale", m.usualSource ? RESV_UI_SOURCE_LABELS[m.usualSource] : "—")}
                </div>
                {/* R1-T5 — Esperienza: segnali derivati dalle prenotazioni passate, mai persistiti */}
                <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.tealDeep, margin: "12px 0 6px" }}>ESPERIENZA</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div className="f-body" style={{ fontSize: 12, color: C.ink }}>
                    {m.pastCount === 0 ? "Prima prenotazione collegata in agenda" : "Cliente di ritorno in agenda"}
                  </div>
                  {m.recurringTable && <div className="f-body" style={{ fontSize: 12, color: C.ink }}>
                    Tavolo ricorrente: <span className="f-mono" style={{ fontSize: 11 }}>{m.recurringTable}</span>
                  </div>}
                  {m.recurringNotes.length > 0 && <div className="f-body" style={{ fontSize: 12, color: C.ink }}>
                    Note ricorrenti: <span style={{ color: C.amberDeep }}>{m.recurringNotes.join(" · ")}</span>
                  </div>}
                  {m.lastNote && <div className="f-body" style={{ fontSize: 12, color: C.ink }}>
                    Ultima nota ({fmt.date(m.lastNote.date + "T12:00:00")}): <span style={{ color: C.amberDeep }}>{m.lastNote.text.length > 70 ? m.lastNote.text.slice(0, 70) + "…" : m.lastNote.text}</span>
                  </div>}
                </div>
                <div className="f-body" style={{ marginTop: 8, fontSize: 10, color: C.mutedSoft }}>
                  Dati basati sullo storico collegato del cliente: {m.visitCount === 1 ? "1 visita collegata" : `${m.visitCount} visite collegate`} e {m.pastCount === 1 ? "1 prenotazione passata" : `${m.pastCount} prenotazioni passate`}.
                  {" "}Note e segnali esperienza sono indicazioni operative, non dati autoritativi. Verificare sempre allergie/intolleranze con il cliente.
                </div>
              </div>;
            })()}

            {/* R5-T3 — sezione SCONTRINO nell'espansione: espone il derivato del
                core (ADR-CRM-11 F2). R5-T4 aggiunge il workflow MANUALE puro:
                Collega scontrino (picker dei receipt reali del giorno/servizio)
                e Scollega (tombstone). L'operatore sceglie, il sistema non decide. */}
            {!editing && expanded && (() => {
              const f = fulfilmentByResv.get(r.id);
              if (!f) return null;
              const fPicking = fulfilPickerId === r.id;
              const stato = f.fulfilled ? "Collegato" : f.orphan ? "Link orfano" : f.needsLinking ? "Da collegare" : "Nessuno scontrino collegato";
              const statoColor = f.fulfilled ? C.oliveDeep : f.orphan ? C.terraDeep : f.needsLinking ? C.amberDeep : C.muted;
              return <div className="fade-in" style={{ marginTop: 8, padding: "8px 14px", background: C.paperDeep, border: `1px solid ${C.ruleSoft}`, borderRadius: 2 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.tealDeep }}>SCONTRINO</div>
                  <div style={{ flex: 1 }} />
                  {fPicking
                    ? <Btn size="xs" variant="ghost" onClick={() => setFulfilPickerId(null)}>Annulla</Btn>
                    : <Btn size="xs" variant="ghost" onClick={() => setFulfilPickerId(r.id)}>Collega scontrino</Btn>}
                </div>
                <div className="f-body" style={{ fontSize: 12, color: C.ink, display: "flex", gap: "4px 18px", flexWrap: "wrap", marginTop: 4 }}>
                  <span>Stato: <span style={{ fontWeight: 600, color: statoColor }}>{stato}</span></span>
                  {f.links.length > 0 && <span>{f.links.length} {f.links.length === 1 ? "link attivo" : "link attivi"}</span>}
                  {f.fulfilled && <span>Importo reale: <span className="f-mono" style={{ fontWeight: 600 }}>{fmt.eur(f.realSpendCents / 100)}</span></span>}
                </div>
                {/* R5-T4 — link attivi: riga per riga con Scollega (tombstone, F7) */}
                {(f.resolved.length > 0 || f.orphanLinks.length > 0) && <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
                  {f.resolved.map(({ link, receipt }) => <div key={link.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="f-mono" style={{ fontSize: 11, color: C.ink }}>
                      {fmt.eur(receiptFacade.getReceiptMoney(receipt).totalCents / 100)}
                      {receiptFacade.getReceiptTable(receipt) ? ` · Tav ${receiptFacade.getReceiptTable(receipt)}` : receipt.isBanco ? " · Banco" : ""}
                      {receipt.closedAt ? ` · ${fmt.time(receipt.closedAt)}` : ""}
                    </span>
                    <div style={{ flex: 1 }} />
                    <Btn size="xs" variant="ghost" onClick={() => handleUnlinkReceipt(r, link.id)}>Scollega</Btn>
                  </div>)}
                  {f.orphanLinks.map(l => <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="f-body" style={{ fontSize: 11, color: C.terraDeep, fontStyle: "italic" }}>Link non risolto (probabile re-import): da riconciliare.</span>
                    <div style={{ flex: 1 }} />
                    <Btn size="xs" variant="ghost" onClick={() => handleUnlinkReceipt(r, l.id)}>Scollega</Btn>
                  </div>)}
                </div>}
                {f.sharedReceipt && <div className="f-body" style={{ fontSize: 10.5, color: C.amberDeep, marginTop: 3 }}>
                  Scontrino condiviso con altre prenotazioni: l'importo è mostrato per intero, mai ripartito.
                </div>}
                {/* R5-T5 — SUGGERITI: scorciatoie visive conservative con motivazioni
                    leggibili; l'azione resta il Collega manuale (origin
                    suggestion_confirmed). Se nulla è buono, c'è il picker T4. */}
                {fulfilSuggestions.length > 0 && <div style={{ marginTop: 8 }}>
                  <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.muted, marginBottom: 4 }}>SUGGERITI</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {fulfilSuggestions.map(cand => {
                      const ss = RESV_UI_SUGG_STATUS[cand.status] || RESV_UI_SUGG_STATUS.review;
                      return <div key={cand.receipt.id} style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "6px 12px", background: C.white, border: `1px solid ${C.ruleSoft}`, borderRadius: 2, flexWrap: "wrap" }}>
                        <span className="f-mono" style={{ fontSize: 11, fontWeight: 600, color: C.ink, width: 40, flexShrink: 0 }}>{cand.receipt.closedAt ? fmt.time(cand.receipt.closedAt) : "—"}</span>
                        <span className="f-body" style={{ fontSize: 11.5, color: C.inkMid, flexShrink: 0 }}>{cand.isBanco ? "Banco" : cand.table ? `Tav ${cand.table}` : "—"}</span>
                        <span className="f-mono" style={{ fontSize: 10.5, color: C.inkMid, flexShrink: 0 }}>{cand.covers > 0 ? `${cand.covers} cop.` : ""}</span>
                        <span className="f-mono" style={{ fontSize: 11.5, fontWeight: 600, color: C.ink, flexShrink: 0 }}>{fmt.eur(cand.totalCents / 100)}</span>
                        {cand.fiscalNumber && <span className="f-mono" style={{ fontSize: 9.5, color: C.mutedSoft, flexShrink: 0 }}>#{cand.fiscalNumber}</span>}
                        <span className="f-body" style={{ fontSize: 10.5, color: C.muted, fontStyle: "italic", flex: 1, minWidth: 120 }}>{cand.reasons.join(" · ")}</span>
                        <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 2, flexShrink: 0, color: ss.fg, background: ss.bg, border: `1px solid ${ss.border}` }}>{ss.label}</span>
                        <Btn size="xs" variant="accent" onClick={() => handleLinkReceipt(r, cand.receipt, "suggestion_confirmed")}>Collega</Btn>
                      </div>;
                    })}
                  </div>
                </div>}
                {/* R5-T4 — picker MANUALE: receipt reali del giorno/servizio, ordine
                    closedAt crescente, banco visibile ma mai favorito. Nessuno score. */}
                {fPicking && <div className="fade-in" style={{ marginTop: 8 }}>
                  {fulfilPickerCandidates.length === 0
                    ? <div className="f-body" style={{ fontSize: 11.5, color: C.muted, fontStyle: "italic" }}>
                      Nessuno scontrino trovato per questo giorno/servizio.
                    </div>
                    : <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 240, overflowY: "auto" }}>
                      {fulfilPickerCandidates.map(cand => {
                        const linkedHere = cand.linkedTo.includes(String(r.id));
                        const linkedOther = cand.linkedTo.some(id => id !== String(r.id));
                        return <div key={cand.receipt.id} className="tx" onClick={() => { if (!linkedHere) handleLinkReceipt(r, cand.receipt); }}
                          style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "6px 12px", background: C.white, border: `1px solid ${C.ruleSoft}`, borderRadius: 2, cursor: linkedHere ? "default" : "pointer", opacity: linkedHere ? 0.55 : 1, flexWrap: "wrap" }}>
                          <span className="f-mono" style={{ fontSize: 11, fontWeight: 600, color: C.ink, width: 40, flexShrink: 0 }}>{cand.receipt.closedAt ? fmt.time(cand.receipt.closedAt) : "—"}</span>
                          <span className="f-body" style={{ fontSize: 11.5, color: C.inkMid, width: 70, flexShrink: 0 }}>{cand.isBanco ? "Banco" : cand.table ? `Tav ${cand.table}` : "—"}</span>
                          <span className="f-mono" style={{ fontSize: 10.5, color: C.inkMid, width: 46, flexShrink: 0 }}>{cand.covers > 0 ? `${cand.covers} cop.` : ""}</span>
                          <span className="f-mono" style={{ fontSize: 11.5, fontWeight: 600, color: C.ink }}>{fmt.eur(cand.totalCents / 100)}</span>
                          {cand.fiscalNumber && <span className="f-mono" style={{ fontSize: 9.5, color: C.mutedSoft }}>#{cand.fiscalNumber}</span>}
                          <div style={{ flex: 1 }} />
                          {linkedHere && <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.1em", color: C.oliveDeep, textTransform: "uppercase" }}>Collegato a questa</span>}
                          {!linkedHere && linkedOther && <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.1em", color: C.amberDeep, textTransform: "uppercase" }}>Già collegato</span>}
                        </div>;
                      })}
                    </div>}
                </div>}
              </div>;
            })()}

            {/* R1-T2 — picker inline: SOLO clienti esistenti, nessuna creazione da Agenda */}
            {!editing && expanded && picking && !r.customerId && <div className="fade-in" style={{ marginTop: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: C.paperDeep, border: `1px solid ${C.rule}`, padding: "6px 12px", borderRadius: 2, marginBottom: 6 }}>
                <Search size={14} color={C.muted} />
                <input value={pQuery} onChange={e => setPQuery(e.target.value)} autoFocus placeholder="Cerca cliente..."
                  onKeyDown={e => { if (e.key === "Escape") closePicker(); }}
                  className="f-body" style={{ border: "none", outline: "none", flex: 1, fontSize: 13, background: "transparent", color: C.ink }} />
                {pQuery && <IconBtn icon={X} size={12} onClick={() => setPQuery("")} />}
              </div>
              {pickerShown.length === 0
                ? <div className="f-body" style={{ fontSize: 12, color: C.muted, fontStyle: "italic", padding: "4px 2px" }}>
                  {pq ? "Nessun cliente trovato con questo nome." : "Nessun cliente in anagrafica."} I clienti si creano dalla vista Clienti.
                </div>
                : <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {pickerShown.map(cu => <div key={cu.id} className="tx" onClick={() => handleLinkCustomer(r, cu)}
                    style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "7px 12px", background: C.paperDeep, border: `1px solid ${C.ruleSoft}`, borderRadius: 2, cursor: "pointer" }}>
                    <span className="f-body" style={{ flex: 1, fontSize: 13, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cu.displayName}</span>
                    <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.inkMid, textTransform: "uppercase" }}>{CRM_UI_TYPE_LABELS[cu.type] || cu.type}</span>
                  </div>)}
                  {pickerMatches.length > pickerShown.length && <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", color: C.muted, textTransform: "uppercase", padding: "4px 2px" }}>
                    +{pickerMatches.length - pickerShown.length} altri — affina la ricerca
                  </div>}
                </div>}
            </div>}

            {/* T4 — edit inline coperti/tavolo/note (pattern widget COPERTI) */}
            {editing && <div className="fade-in" style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input value={eCovers} onChange={e => setECovers(e.target.value)} type="number" min="1" placeholder="Coperti *" autoFocus
                onKeyDown={e => { if (e.key === "Enter") handleSaveEdit(r); if (e.key === "Escape") cancelEdit(); }}
                className="f-body" style={{ ...inputStyle, width: 90 }} />
              <input value={eTable} onChange={e => setETable(e.target.value)} placeholder="Tavolo"
                onKeyDown={e => { if (e.key === "Enter") handleSaveEdit(r); if (e.key === "Escape") cancelEdit(); }}
                className="f-body" style={{ ...inputStyle, width: 90 }} />
              <input value={eNotes} onChange={e => setENotes(e.target.value)} placeholder="Note"
                onKeyDown={e => { if (e.key === "Enter") handleSaveEdit(r); if (e.key === "Escape") cancelEdit(); }}
                className="f-body" style={{ ...inputStyle, flex: 1, minWidth: 140 }} />
              <Btn size="sm" variant="primary" onClick={() => handleSaveEdit(r)}>Salva</Btn>
              <Btn size="sm" variant="ghost" onClick={cancelEdit}>Annulla</Btn>
            </div>}
          </div>;
        })}
        </div>)}
      </div>)}

    {/* R3-T4 — BOARD ARRIVI: seconda presentazione dello stesso giorno/servizio,
        non una riscrittura della Lista. Solo booked+seated, ordinamento T2,
        contatori in coperti, un solo bottone grande "Arrivato" per le booked. */}
    {mode === "arrivi" && <>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        {[
          { label: "Attesi", value: arrivalTotals.expected, color: C.ink },
          { label: "Arrivati", value: arrivalTotals.arrived, color: C.oliveDeep },
          { label: "Mancanti", value: arrivalTotals.missing, color: arrivalTotals.missing > 0 ? C.amberDeep : C.muted },
        ].map(s => <div key={s.label} style={{ flex: 1, minWidth: 110, padding: "10px 14px", background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
          <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase", marginBottom: 4 }}>{s.label}</div>
          <div className="f-display" style={{ fontSize: 24, fontWeight: 600, color: s.color }}>{s.value} <span className="f-body" style={{ fontSize: 11, fontWeight: 400, color: C.muted }}>coperti</span></div>
        </div>)}
      </div>
      {/* R3-T5 — quick-add walk-in: minimale, solo qui in Arrivi. Nome opzionale
          (vuoto → "Walk-in"), coperti obbligatori, tavolo opzionale; entra
          direttamente come seated con l'orario di adesso. */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, padding: "12px 14px", background: C.paperDeep, border: `1px solid ${C.rule}`, borderRadius: 2, alignItems: "center", flexWrap: "wrap" }}>
        <input value={wName} onChange={e => setWName(e.target.value)} onKeyDown={walkInOnEnter} placeholder="Nome (vuoto = Walk-in)" className="f-body" style={{ ...inputStyle, flex: 2, minWidth: 140 }} />
        <input value={wCovers} onChange={e => setWCovers(e.target.value)} onKeyDown={walkInOnEnter} placeholder="Coperti *" type="number" min="1" className="f-body" style={{ ...inputStyle, width: 90 }} />
        <input value={wTable} onChange={e => setWTable(e.target.value)} onKeyDown={walkInOnEnter} placeholder="Tavolo" className="f-body" style={{ ...inputStyle, width: 90 }} />
        <Btn size="sm" variant="accent" icon={Plus} onClick={handleWalkIn}>Walk-in</Btn>
      </div>
      {arrivals.length === 0
        ? <EmptyState icon={Calendar} title={`Nessun arrivo atteso a ${service} ${isToday ? "oggi" : `il ${dayLabel}`}`}
            body="Le prenotazioni attive del giorno compariranno qui. Le nuove si aggiungono dalla modalità Lista." />
        : <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Riga situazione: cosa guardare ADESSO */}
          {isToday && (() => {
            if (arrivalGroups.late.length > 0) return <div className="f-body" style={{ fontSize: 12.5, color: C.terraDeep, fontWeight: 600 }}>
              {arrivalGroups.late.length} {arrivalGroups.late.length === 1 ? "tavolo in ritardo" : "tavoli in ritardo"} — un colpo di telefono o segna il no-show.
            </div>;
            const next = arrivalGroups.incoming.find(r => r.time);
            if (next) return <div className="f-body" style={{ fontSize: 12.5, color: C.inkMid }}>
              Prossimo arrivo: <b style={{ color: C.ink }}>{next.time}</b> · {next.rawName} ({next.covers})
            </div>;
            return null;
          })()}
          {/* IN RITARDO — la priorità assoluta della board */}
          {arrivalGroups.late.length > 0 && <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {arrivalSectionHead("In ritardo", arrivalGroups.late.length, C.terraDeep)}
            {arrivalGroups.late.map(arrivalCard)}
          </div>}
          {/* IN ARRIVO — il resto degli attesi, in ordine di orario */}
          {arrivalGroups.incoming.length > 0 && <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {arrivalSectionHead("In arrivo", arrivalGroups.incoming.length, C.inkMid)}
            {arrivalGroups.incoming.map(arrivalCard)}
          </div>}
          {/* GIÀ AL TAVOLO — richiuso di default: durante il servizio è "fatto" */}
          {arrivalGroups.seated.length > 0 && <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button className="tx" onClick={() => setShowArrived(v => !v)}
              style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", background: "none", border: "none", padding: 0, textAlign: "left" }}>
              <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.18em", color: C.oliveDeep, textTransform: "uppercase", fontWeight: 700, flexShrink: 0 }}>
                Già al tavolo · {arrivalGroups.seated.length} {showArrived ? "▲" : "▼"}
              </span>
              <div style={{ flex: 1, height: 1, background: C.ruleSoft }} />
            </button>
            {showArrived && arrivalGroups.seated.map(seatedCardCompact)}
          </div>}
        </div>}
    </>}

    {/* ═══ REGIA (impianto B + innesto C, approvato 07/07) — board di servizio:
        coda arrivi a schede grandi sopra, timeline per tavolo con linea ADESSO
        sotto. AFFIANCATA alle modalità esistenti (mai sostitutiva finché non
        vince in sala). Riusa i gestori della griglia: handleTableTap (assegna/
        walk-in, conferma a due tocchi), handleStatus, handleAssignCombo,
        tableReco (★). La card online resta il pannello vistoso sopra la board:
        un solo punto di decisione Accetta/Rifiuta, mai duplicato. */}
    {mode === "regia" && tableBoard && (() => {
      const T = RG_THEMES[rgTheme] || RG_THEMES.scuro;
      const layout = _rgLayout(tableBoard, service);
      const queue = arrivals.filter(r => r.status !== "seated");
      const nm = nowMin < 360 ? nowMin + 1440 : nowMin; // cena oltre mezzanotte
      const nowPx = (isToday && nm >= layout.start && nm <= layout.end) ? (nm - layout.start) * RG_PX_PER_MIN : null;
      const stateTok = (tc) => tc.status === "seated" ? [T.stateSeatedBg, T.stateSeatedBd]
        : tc.status === "conflict" ? [T.stateConflictBg, T.stateConflictBd]
        : (tc.status === "booked" || tc.status === "turns") ? [T.stateBookedBg, T.stateBookedBd]
        : [T.stateFreeBg, T.stateFreeBd];
      const starIds = tableReco ? tableReco.starIds : null;
      const LABEL_W = 84;
      return <div className="fade-in" style={{ background: T.appBg, border: `1px solid ${T.line}`, borderRadius: 6, padding: "14px 14px 18px" }}>
        {/* testata: contesto + orologio + toggle tema (richiesta Andi: selezionabile) */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <span className="f-mono" style={{ fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: T.accentOnline, fontWeight: 700 }}>◉ Regia</span>
          <span className="f-body" style={{ fontSize: 12, color: T.textSecondary }}>{isToday ? "oggi" : dayLabel} · {service} · {queue.length} in arrivo</span>
          <div style={{ flex: 1 }} />
          {isToday && <span className="f-mono" style={{ fontSize: 14, color: T.textPrimary, fontWeight: 700 }}>
            {String(Math.floor((nm % 1440) / 60)).padStart(2, "0")}:{String(nm % 60).padStart(2, "0")}
          </span>}
          <button className="tx" onClick={toggleRgTheme} aria-label="Cambia tema chiaro/scuro" title={rgTheme === "scuro" ? "Passa al tema chiaro" : "Passa al tema scuro"}
            style={{ minWidth: 44, minHeight: 44, borderRadius: 8, cursor: "pointer", fontSize: 17, background: T.surfaceRaised, color: T.textPrimary, border: `1px solid ${T.lineStrong}` }}>
            {rgTheme === "scuro" ? "☀" : "☾"}
          </button>
        </div>
        {/* coda arrivi a schede (innesto C: stato = lo vedi, non lo decifri) */}
        <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 8 }}>
          {queue.length === 0
            ? <div className="f-body" style={{ fontSize: 13, color: T.textMuted, fontStyle: "italic", padding: "8px 2px" }}>
                Nessuno in arrivo{onlineReqs.length > 0 ? " — c'è una richiesta online qui sopra da decidere" : ": coda vuota"}.
              </div>
            : queue.map(r => {
              const late = lateBefore != null && r.time != null && r.time < lateBefore;
              const sel = assignId === r.id;
              const blk = blkByResv.get(r.id);
              // SALA CHE IMPARA (C): consigliato calcolato PER SCHEDA (solo senza
              // tavolo) → assegnazione a un tocco senza entrare in selezione.
              const qBest = (!r.table || String(r.table).trim() === "")
                ? (_resvRecommendTables(r, tableBoard, knownCombos, tableAffinity).best || null) : null;
              return <div key={r.id} onClick={() => { setAssignId(prev => prev === r.id ? null : r.id); setConfirmTap(null); setWalkinTap(null); }}
                style={{ width: 236, flexShrink: 0, minHeight: 96, borderRadius: 12, cursor: "pointer", padding: "10px 12px",
                  background: T.surfaceRaised,
                  border: `1px solid ${sel ? T.accentOnline : T.line}`,
                  borderLeft: `6px solid ${late ? T.stateConflictBd : sel ? T.accentOnline : T.stateBookedBd}`,
                  boxShadow: sel ? `0 0 0 3px ${T.accentOnline}55` : "none" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span className="f-mono" style={{ fontSize: 16, fontWeight: 700, color: late ? T.stateConflictBd : T.textPrimary }}>{r.time || "—"}</span>
                  <span className="f-mono" style={{ fontSize: 12, color: T.textSecondary }}>{r.covers}p</span>
                  {late && <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: T.stateConflictBd, fontWeight: 700 }}>ritardo</span>}
                  {r.table && <span className="f-mono" style={{ fontSize: 10, marginLeft: "auto", color: T.textOnAccent, background: T.stateSuggestBd, padding: "2px 7px", borderRadius: 8, fontWeight: 700, whiteSpace: "nowrap" }}>T {r.table}</span>}
                </div>
                <div className="f-display" style={{ fontSize: 17, fontWeight: 600, color: T.textPrimary, margin: "3px 0 2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.rawName}</div>
                {blk && <div className="f-mono" title={blk.via === "nome" ? "aggancio per nome: possibile omonimo" : "aggancio per telefono"}
                  style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: T.dangerBlacklist, padding: "3px 7px", borderRadius: 3, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  ⛔ {blk.entry.reason}{blk.via === "nome" ? " (omonimo?)" : ""}
                </div>}
                <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                  <button className="tx f-mono" onClick={e => { e.stopPropagation(); handleStatus(r, "seated"); }}
                    style={{ flex: 1, minHeight: 44, borderRadius: 8, cursor: "pointer", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, background: T.stateSeatedBg, color: T.textPrimary, border: `1px solid ${T.stateSeatedBd}` }}>
                    Arrivato
                  </button>
                  {qBest
                    ? <button className="tx f-mono" title={`Consigliato: spreco ${qBest.waste} posti${qBest.aff > 0 ? ` · usato ${qBest.aff} volte per ${r.covers} coperti` : ""} — un tocco assegna, per scegliere a mano tocca la scheda`}
                        onClick={e => { e.stopPropagation(); handleQuickAssign(r, qBest); }}
                        style={{ minWidth: 70, minHeight: 44, borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700, padding: "0 10px", background: T.stateSuggestBd, color: T.textOnAccent, border: 0, whiteSpace: "nowrap" }}>
                        ★ {qBest.tc ? qBest.tc.table.label : qBest.labels.join("+")}
                      </button>
                    : <button className="tx f-mono" onClick={e => { e.stopPropagation(); setAssignId(r.id); setConfirmTap(null); setWalkinTap(null); }}
                        style={{ minWidth: 70, minHeight: 44, borderRadius: 8, cursor: "pointer", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, background: "transparent", color: T.textSecondary, border: `1px solid ${T.lineStrong}` }}>
                        {r.table ? "Sposta" : "Tavolo"}
                      </button>}
                </div>
              </div>;
            })}
        </div>
        {/* banner selezione: stesso motore ★ della griglia (tableReco) */}
        {assignResv && <div className="fade-in" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 12px", borderRadius: 8, background: T.surface, border: `1px dashed ${T.accentOnline}`, margin: "4px 0 10px" }}>
          <span className="f-body" style={{ fontSize: 13, color: T.textPrimary }}>
            <b>{assignResv.rawName}</b> · {assignResv.covers} {assignResv.covers === 1 ? "coperto" : "coperti"} — tocca un tavolo nella timeline
            {tableReco && tableReco.best && <span style={{ color: T.textSecondary }}>
              {" "}· il ★ è scelto così: spreco {tableReco.best.waste} {tableReco.best.waste === 1 ? "posto" : "posti"}
              {tableReco.best.aff > 0 ? ` · usato ${tableReco.best.aff} volte per ${assignResv.covers} coperti` : ""}
              {tableReco.best.mode === "turn" ? " · secondo turno" : tableReco.best.mode === "combo" ? " · unione già vista nello storico" : ""}
            </span>}
          </span>
          {tableReco && tableReco.best && (tableReco.best.tc
            ? <button className="tx f-mono" onClick={() => handleTableTap(tableReco.best.tc)}
                style={{ minHeight: 44, padding: "0 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700, background: T.stateSuggestBd, color: T.textOnAccent, border: 0 }}>
                ★ Tav {tableReco.best.tc.table.label}
              </button>
            : <button className="tx f-mono" onClick={() => handleAssignCombo(tableReco.best.labels)}
                style={{ minHeight: 44, padding: "0 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700, background: T.stateSuggestBd, color: T.textOnAccent, border: 0 }}>
                ★ Unisci Tav {tableReco.best.labels.join(" + ")}
              </button>)}
          <div style={{ flex: 1 }} />
          <button className="tx f-mono" onClick={() => { setAssignId(null); setConfirmTap(null); }}
            style={{ minHeight: 44, padding: "0 16px", borderRadius: 8, cursor: "pointer", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", background: "transparent", color: T.textSecondary, border: `1px solid ${T.lineStrong}` }}>
            Fine
          </button>
        </div>}
        {/* timeline per area: la riga È il tavolo, il tempo scorre, ADESSO avanza */}
        {layout.areas.length === 0
          ? <div className="f-body" style={{ fontSize: 12, color: T.textMuted, fontStyle: "italic", padding: "14px 0" }}>Nessun tavolo attivo: configura Sala &amp; Tavoli.</div>
          : layout.areas.map(a => <div key={a.area.id} style={{ marginTop: 14 }}>
            <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: T.textMuted, marginBottom: 6 }}>{a.area.name}</div>
            <div style={{ overflowX: "auto" }}>
              <div style={{ position: "relative", width: LABEL_W + layout.widthPx }}>
                <div style={{ display: "flex" }}>
                  <div style={{ width: LABEL_W, flexShrink: 0, position: "sticky", left: 0, zIndex: 3, background: T.appBg }} />
                  <div style={{ position: "relative", width: layout.widthPx, height: 20 }}>
                    {layout.ticks.map((t, i) => t.label && <span key={i} className="f-mono" style={{ position: "absolute", left: t.px, transform: "translateX(-50%)", fontSize: 10, color: T.textMuted }}>{t.label}</span>)}
                  </div>
                </div>
                {nowPx != null && <div style={{ position: "absolute", left: LABEL_W + nowPx, top: 20, bottom: 0, width: 2, background: T.nowLine, zIndex: 2, pointerEvents: "none" }}>
                  <span className="f-mono" style={{ position: "absolute", top: -17, left: -21, fontSize: 8.5, fontWeight: 700, letterSpacing: "0.08em", color: T.textOnAccent, background: T.nowLine, padding: "1px 5px", borderRadius: 3 }}>ADESSO</span>
                </div>}
                {a.rows.map(row => {
                  const tc = row.tc;
                  const id = String(tc.table.id);
                  const starred = !!(starIds && starIds.has(id));
                  const armed = !!(confirmTap && assignResv && confirmTap.tableId === id && confirmTap.resvId === assignResv.id);
                  const rowH = row.lanes === 2 ? 92 : RG_ROW_H;
                  const [, bd] = stateTok(tc);
                  return <div key={id} style={{ display: "flex", marginBottom: 4 }}>
                    <div style={{ width: LABEL_W, flexShrink: 0, position: "sticky", left: 0, zIndex: 3, background: T.appBg, display: "flex", flexDirection: "column", justifyContent: "center", paddingRight: 8 }}>
                      <span className="f-display" style={{ fontSize: 15, fontWeight: 600, color: starred ? T.stateSuggestBd : T.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{starred ? "★ " : ""}{tc.table.label}</span>
                      {(() => {
                        // SALA CHE IMPARA (B): il prenotato-più-tardi si dichiara
                        // ("libero fino alle 21:30") — il posto non muore in attesa.
                        const wk = isToday ? _resvWalkinWindow(tc, nm) : { ok: false, until: null };
                        return <span className="f-mono" style={{ fontSize: 9, color: tc.status === "free" || wk.ok ? bd : T.textMuted }}>
                          {tc.seats > 0 ? `${tc.seats} posti` : "posti ?"}
                          {tc.status === "free" ? " · libero" : wk.ok && wk.until ? ` · libero fino ${wk.until}` : ""}
                        </span>;
                      })()}
                    </div>
                    <div onClick={() => handleTableTap(tc)}
                      style={{ position: "relative", width: layout.widthPx, height: rowH, cursor: "pointer", borderRadius: 6, overflow: "hidden",
                        background: starred ? T.stateSuggestBg : T.surfaceSunken,
                        border: `1px solid ${starred ? T.stateSuggestBd : T.line}`,
                        boxShadow: armed ? `inset 0 0 0 2px ${T.stateConflictBd}` : "none" }}>
                      {layout.ticks.map((t, i) => <div key={i} style={{ position: "absolute", left: t.px, top: 0, bottom: 0, width: 1, background: t.label ? T.line : "transparent", pointerEvents: "none" }} />)}
                      {row.blocks.map((b, i) => {
                        const st = b.r.status === "seated" ? [T.stateSeatedBg, T.stateSeatedBd] : [T.stateBookedBg, T.stateBookedBd];
                        const bblk = blkByResv.get(b.r.id);
                        return <div key={b.r.id || i} title={`${b.r.time || "senza orario"} · ${b.r.rawName} · ${b.r.covers} coperti`}
                          style={{ position: "absolute", left: b.left, width: b.width, top: row.lanes === 2 ? (b.lane === 1 ? 48 : 6) : 8, height: row.lanes === 2 ? 38 : 48,
                            background: st[0], border: `1px solid ${st[1]}`, borderLeft: `4px solid ${st[1]}`, borderRadius: 6,
                            display: "flex", alignItems: "center", gap: 6, padding: "0 8px", overflow: "hidden", whiteSpace: "nowrap", pointerEvents: "none" }}>
                          <span className="f-mono" style={{ fontSize: 12, fontWeight: 700, color: st[1] }}>{b.r.time || "—"}</span>
                          <span className="f-body" style={{ fontSize: 13, color: T.textPrimary, overflow: "hidden", textOverflow: "ellipsis" }}>{bblk ? "⛔ " : ""}{b.r.rawName}</span>
                          <span className="f-mono" style={{ fontSize: 11, color: T.textSecondary, fontWeight: 600 }}>{b.r.covers}p</span>
                        </div>;
                      })}
                      {armed && <div className="f-mono" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.stateConflictBd, background: `${T.appBg}CC`, zIndex: 2 }}>
                        Tavolo occupato — tocca ancora per confermare
                      </div>}
                      {walkinTap === id && !assignResv && <div onClick={e => e.stopPropagation()} style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", gap: 6, padding: "0 10px", background: `${T.surfaceRaised}F2`, zIndex: 2, overflowX: "auto" }}>
                        <span className="f-mono" style={{ fontSize: 10, color: T.textSecondary, textTransform: "uppercase", letterSpacing: "0.1em", flexShrink: 0 }}>
                          Walk-in{(() => { const wk = _resvWalkinWindow(tc, nm); return wk.until ? ` · fino ${wk.until}` : ""; })()}
                        </span>
                        {[1, 2, 3, 4, 5, 6, 8].map(n => <button key={n} className="tx f-mono" onClick={() => handleWalkinCreate(tc, n)}
                          style={{ minWidth: 44, minHeight: 44, borderRadius: 8, cursor: "pointer", fontSize: 15, fontWeight: 700, flexShrink: 0, background: T.stateSeatedBg, color: T.textPrimary, border: `1px solid ${T.stateSeatedBd}` }}>{n}</button>)}
                      </div>}
                    </div>
                  </div>;
                })}
              </div>
            </div>
          </div>)}
        <div className="f-body" style={{ fontSize: 10.5, color: T.textMuted, fontStyle: "italic", marginTop: 10 }}>
          Blocchi lunghi 2 ore (indicativi) · ★ sulla scheda = assegna in un tocco (tocca la scheda per scegliere a mano) · walk-in su tavoli liberi o "liberi fino alle…"
        </div>
      </div>;
    })()}

    {/* R4-T2 — MODALITÀ TAVOLI (read-only, Addendum R4): la "piantina utile" —
        griglia di card per area, stato derivato, ZERO scritture in T2: nessun
        tap-assegna, nessuna selezione, nessuna strip. Solo colpo d'occhio. */}
    {mode === "tavoli" && tableBoard && (() => {
      const occByArea = new Map(occupancy.areas.map(a => [String(a.areaId), a]));
      const totalTables = tableBoard.reduce((s, g) => s + g.tables.length, 0);
      if (tableBoard.length === 0) return <EmptyState icon={Layers} title="Nessuna area attiva"
        body="Configura sale e tavoli nella vista Sala & Tavoli: la griglia si popola da sola." />;
      if (totalTables === 0) return <EmptyState icon={Layers} title="Nessun tavolo configurato"
        body="Le aree ci sono ma non hanno tavoli. Aggiungili nella vista Sala & Tavoli: la griglia si popola da sola." />;
      // C2.1 — toggle griglia/timeline: la timeline è vista di LETTURA (il
      // ritmo dei turni); l'assegnazione resta gesto della griglia.
      const vizToggle = <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {[["griglia", "Griglia"], ["timeline", "Timeline turni"]].map(([k, lbl]) =>
          <button key={k} className="tx f-mono" onClick={() => setTavoliViz(k)}
            style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", padding: "5px 12px", borderRadius: 2, cursor: "pointer",
              background: tavoliViz === k ? C.ink : C.white, color: tavoliViz === k ? C.paper : C.inkMid, border: `1px solid ${tavoliViz === k ? C.ink : C.rule}` }}>
            {lbl}
          </button>)}
      </div>;
      if (tavoliViz === "timeline") {
        return <div className="fade-in">
          {vizToggle}
          <ResvTimelineBoard board={tableBoard} service={service} />
        </div>;
      }
      return <div className="fade-in">
        {vizToggle}
        {/* R4-T3 — strip "Da assegnare": stessa semantica del filtro T3, tap sul
            chip = seleziona (solo stato UI). Sparisce quando la coda è vuota. */}
        {unassignedResvs.length > 0 && <div style={{ marginBottom: 12 }}>
          <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase", marginBottom: 6 }}>
            Da assegnare · {unassignedResvs.length}
          </div>
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
            {unassignedResvs.map(r => {
              const on = assignId === r.id;
              return <button key={r.id} className="tx f-body" onClick={() => setAssignId(on ? null : r.id)}
                style={{ display: "inline-flex", alignItems: "baseline", gap: 6, padding: "6px 12px", borderRadius: 2, cursor: "pointer", flexShrink: 0, fontSize: 12,
                  background: on ? C.ink : C.white, color: on ? C.paper : C.ink, border: `1px solid ${on ? C.ink : C.rule}` }}>
                <span className="f-mono" style={{ fontSize: 11, fontWeight: 600 }}>{r.time || "—"}</span>
                {r.rawName}
                <span className="f-mono" style={{ fontSize: 10, opacity: 0.75 }}>· {r.covers}</span>
              </button>;
            })}
          </div>
        </div>}
        {/* R4-T4 — strip "Tavolo sconosciuto": attive con token non risolti.
            Tap = seleziona; l'assegnazione da griglia riscrive il testo con
            label valide (bonifica). I token unknown NON entrano in griglia. */}
        {unknownResvs.length > 0 && <div style={{ marginBottom: 12 }}>
          <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.2em", color: C.terraDeep, textTransform: "uppercase", marginBottom: 6 }}>
            Tavolo sconosciuto · {unknownResvs.length}
          </div>
          <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
            {unknownResvs.map(r => {
              const on = assignId === r.id;
              return <button key={r.id} className="tx f-body" onClick={() => setAssignId(on ? null : r.id)}
                style={{ display: "inline-flex", alignItems: "baseline", gap: 6, padding: "6px 12px", borderRadius: 2, cursor: "pointer", flexShrink: 0, fontSize: 12,
                  background: on ? C.ink : C.terraWash, color: on ? C.paper : C.ink, border: `1px solid ${on ? C.ink : C.terra}` }}>
                <span className="f-mono" style={{ fontSize: 11, fontWeight: 600 }}>{r.time || "—"}</span>
                {r.rawName}
                <span className="f-mono" style={{ fontSize: 10, opacity: 0.75 }}>· {r.covers}</span>
                <span className="f-mono" style={{ fontSize: 10, color: on ? C.paper : C.terraDeep }}>“{r.table}”</span>
              </button>;
            })}
          </div>
        </div>}
        {/* R4-T3/T4 — banner selezione: sticky sopra la griglia. Resta attivo
            dopo ogni scrittura (multi-tavolo); Fine/Annulla chiude solo la UI. */}
        {assignResv && <div className="fade-in" style={{ position: "sticky", top: 8, zIndex: 5, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 14px", background: C.tealWash, border: `1px solid ${C.teal}`, borderRadius: 2, marginBottom: 12 }}>
          <span className="f-body" style={{ fontSize: 12.5, color: C.tealDeep }}>
            Assegna: <span className="f-display" style={{ fontWeight: 600 }}>{assignResv.rawName}</span>
            {" "}· {assignResv.covers} {assignResv.covers === 1 ? "coperto" : "coperti"}{assignResv.time ? ` · ${assignResv.time}` : ""}
            {assignOwned.labels.length > 0 && <span className="f-mono" style={{ fontSize: 11, fontWeight: 600 }}> → Tav {assignOwned.labels.join(" + ")}</span>}
          </span>
          <span className="f-body" style={{ fontSize: 11, color: C.tealDeep, fontStyle: "italic", flex: 1, minWidth: 140 }}>
            {assignOwned.hadUnknown
              ? "Tocca un tavolo per riscrivere con etichette valide."
              : assignOwned.labels.length > 0
                ? "Tocca un tavolo libero per aggiungere, uno assegnato per rimuovere."
                : "Tocca un tavolo libero per assegnare."}
          </span>
          {/* R6-T2 — un tocco sul consigliato: stesso identico handler del tap in
              griglia (nessun canale nuovo). Solo per best LIBERO: il doppio turno
              resta gesto consapevole in griglia (stellina + conferma a due tocchi). */}
          {tableReco && tableReco.best && tableReco.best.mode === "free" && assignOwned.labels.length === 0 &&
            <Btn size="xs" variant="accent" onClick={() => handleTableTap(tableReco.best.tc)}>
              ★ Tav {tableReco.best.tc.table.label} · {tableReco.best.tc.seats} posti — assegna
            </Btn>}
          {/* C2.2 — il consigliato è una COMBINAZIONE: un tocco assegna tutte le label */}
          {tableReco && tableReco.best && tableReco.best.mode === "combo" && assignOwned.labels.length === 0 &&
            <Btn size="xs" variant="accent" onClick={() => handleAssignCombo(tableReco.best.labels)}>
              ★ Unisci Tav {tableReco.best.labels.join(" + ")} · {tableReco.best.combo.seats} posti — assegna
            </Btn>}
          <Btn size="xs" variant="ghost" onClick={() => setAssignId(null)}>{assignOwned.labels.length > 0 ? "Fine" : "Annulla"}</Btn>
        </div>}
        {tableBoard.map(g => {
          const occ = occByArea.get(String(g.area.id));
          return <div key={g.area.id} style={{ marginBottom: 22 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
              <span className="f-display" style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>{g.area.name}</span>
              {occ && <span className="f-mono" style={{ fontSize: 10, color: occ.overCapacity ? C.terraDeep : C.inkMid }}>
                {occ.occupied}{occ.capacity != null ? `/${occ.capacity}` : ""} coperti
                {occ.capacity != null && !occ.overCapacity && ` · ${occ.remaining} liberi`}
                {occ.overCapacity && ` · ${-occ.remaining} oltre`}
              </span>}
              <div style={{ flex: 1, height: 1, background: C.ruleSoft }} />
            </div>
            {g.tables.length === 0
              ? <div className="f-body" style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>Nessun tavolo in quest'area.</div>
              : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 8 }}>
                {g.tables.map(tc => {
                  const st = RESV_UI_TABLE_STATES[tc.status];
                  // R4-T3/T4 — tap: scrive SOLO con selezione attiva (handleTableTap);
                  // senza selezione resta inerte. Con selezione: liberi invitano
                  // (teal), i già assegnati alla selezionata si marcano (ink),
                  // il non-libero armato mostra la conferma a due tocchi (ambra).
                  const invite = assignResv && tc.status === "free";
                  const ownedBysel = assignResv && assignOwned.ids.has(String(tc.table.id));
                  const armed = assignResv && confirmTap && confirmTap.tableId === String(tc.table.id) && confirmTap.resvId === assignResv.id;
                  // R6-T2 — stellina sui migliori candidati (max 3) per la selezione
                  // attiva; il migliore in assoluto ha bordo teal pieno. Solo resa
                  // visiva della raccomandazione: il tap resta l'unico gesto che scrive.
                  const starred = tableReco && !ownedBysel && tableReco.starIds.has(String(tc.table.id));
                  const isBest = starred && tableReco.best && String(tableReco.best.tc.table.id) === String(tc.table.id);
                  const borderC = armed ? C.amber : ownedBysel ? C.ink : isBest ? C.tealDeep : invite ? C.teal : st.border;
                  return <div key={tc.table.id} onClick={() => handleTableTap(tc)}
                    style={{ padding: "10px 12px", background: st.bg, border: `1px solid ${borderC}`, borderRadius: 2, minHeight: 76, cursor: (assignResv || (tc.status === "free" && isToday)) ? "pointer" : "default", boxShadow: (invite || ownedBysel || armed || isBest) ? `inset 0 0 0 1px ${borderC}` : "none" }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
                      <span className="f-display" style={{ fontSize: 17, fontWeight: 600, color: C.ink }}>{tc.table.label}</span>
                      {starred && <span className="f-mono" title={tc.status === "free" ? "Consigliato per la prenotazione selezionata" : "Possibile doppio turno: tutte le prenotazioni distano almeno 2 ore"}
                        style={{ fontSize: 9, fontWeight: 600, color: C.tealDeep }}>★{isBest ? " consigliato" : ""}{tc.status !== "free" ? " 2° turno" : ""}</span>}
                      {tc.seats > 0 && <span className="f-mono" style={{ fontSize: 9, color: C.muted }}>{tc.seats} posti</span>}
                      {tc.overflow && <span className="f-mono" title={`${tc.covers} coperti su ${tc.seats} posti`}
                        style={{ fontSize: 9, fontWeight: 600, color: C.amberDeep }}>+{tc.covers - tc.seats}</span>}
                    </div>
                    <div className="f-mono" style={{ fontSize: 8.5, letterSpacing: "0.12em", textTransform: "uppercase", color: st.fg, margin: "3px 0 4px" }}>
                      {tc.status === "turns" ? `${tc.reservations.length} turni` : st.label}{tc.status === "conflict" ? " ⚠" : ""}
                    </div>
                    {/* R4-T4 — conferma a due tocchi (solo runtime, niente modali) */}
                    {armed && <div className="f-body fade-in" style={{ fontSize: 10, color: C.amberDeep, margin: "0 0 4px" }}>
                      Occupato da {tc.reservations[0] ? tc.reservations[0].rawName : "altri"} — tocca ancora per assegnare comunque
                    </div>}
                    {/* C2.3 — walk-in armato: scelta rapida coperti, un tocco e sono al tavolo */}
                    {!assignResv && walkinTap === String(tc.table.id) && <div className="fade-in" onClick={e => e.stopPropagation()} style={{ margin: "2px 0 4px" }}>
                      <div className="f-mono" style={{ fontSize: 8.5, letterSpacing: "0.12em", textTransform: "uppercase", color: C.tealDeep, marginBottom: 3 }}>
                        Walk-in{(() => { const wk = _resvWalkinWindow(tc, _resvNowMin()); return wk.until ? ` · fino ${wk.until}` : ""; })()} · coperti?
                      </div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {[1, 2, 3, 4, 5, 6, 8].map(n =>
                          <button key={n} className="tx f-mono" onClick={() => handleWalkinCreate(tc, n)}
                            style={{ fontSize: 12, fontWeight: 700, width: 26, height: 24, borderRadius: 2, cursor: "pointer", background: C.tealWash, color: C.tealDeep, border: `1px solid ${C.teal}` }}>
                            {n}
                          </button>)}
                        <button className="tx f-mono" onClick={() => setWalkinTap(null)}
                          style={{ fontSize: 12, width: 26, height: 24, borderRadius: 2, cursor: "pointer", background: C.white, color: C.muted, border: `1px solid ${C.rule}` }}>✕</button>
                      </div>
                    </div>}
                    {tc.reservations.slice(0, 3).map(r => <div key={r.id} className="f-body"
                      style={{ fontSize: 10.5, color: r.status === "seated" ? C.ink : C.inkSoft, fontWeight: r.status === "seated" ? 500 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.time && <span className="f-mono" style={{ fontWeight: 600 }}>{r.time} </span>}{r.rawName} · {r.covers}
                    </div>)}
                    {tc.reservations.length > 3 && <div className="f-mono" style={{ fontSize: 9, color: C.muted }}>+{tc.reservations.length - 3} altre</div>}
                  </div>;
                })}
              </div>}
          </div>;
        })}
      </div>;
    })()}
  </div>;
}
// ⟦RESV-UI:END⟧

// ⟦RESV-BOOTSTRAP-UI:START⟧ — R5.5-T3 · Pannello import bootstrap Pienissimo
// (docs/BOOTSTRAP_PIENISSIMO.md). Pannello TEMPORANEO e nascosto, raggiungibile
// SOLO con ?bootstrap=1: incolla un JSON di staging già reviewato → preview con
// esito riga per riga → import batch dei soli `confirmed` importabili.
// UNICO canale di scrittura: reservationCore.createReservation (source "other",
// status deciso dal core, customerId mai valorizzato). Append-only: mai update,
// mai merge, mai fulfilment. Un SOLO setState a fine batch. Nessun nuovo store,
// nessuno staging persistito nel dominio, blocchi congelati intatti.
const RESV_BOOT_SCHEMA = "pienissimo-staging-v1";
const RESV_BOOT_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const RESV_BOOT_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
// Identificatore foto compatto e STABILE: hash djb2→base36 del nome file.
// Deviazione dichiarata dal formato fX del documento (indice 1-based nel
// payload): un indice dipende dalla composizione del payload, e un futuro
// import incrementale di una foto singola (es. la prenotazione mancante della
// cena 30/06) genererebbe f1 collidendo con i tag già importati. L'hash del
// nome file non dipende dal payload: stesso file → stesso tag, sempre.
const _bootPhotoId = (name) => {
  const s = String(name || "");
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return h.toString(36);
};
const _bootTag = (sr) => `[PNSM v1 ${sr.day}/${sr.service}/p${_bootPhotoId(sr.photo)}/r${sr.row}]`;
// Chiave contenuto per il warning duplicate-like (mai bloccante, mai persistita).
const _bootContentKey = (serviceDate, service, rawName, covers, time) =>
  [serviceDate, service, normStr(rawName), String(covers), time == null ? "" : String(time)].join("|");

// Analisi PURA del payload contro le prenotazioni esistenti: nessuna scrittura.
// Esiti per riga: "import" | "import_warn" (simile già in agenda) |
// "skip_tag" (tag PNSM già presente → idempotenza) | "blocked" (review-first:
// solo confirmed non barrati con campi validi passano).
const _bootAnalyze = (payload, reservations) => {
  if (payload == null || typeof payload !== "object" || Array.isArray(payload)) return { error: "il payload non è un oggetto JSON" };
  if (payload.schemaVersion !== RESV_BOOT_SCHEMA) return { error: `schemaVersion non supportata: ${payload.schemaVersion || "assente"} (attesa: ${RESV_BOOT_SCHEMA})` };
  const src = payload.source;
  if (!src || typeof src !== "object") return { error: "source assente nel payload" };
  const pDate = String(src.serviceDate || "");
  const pService = String(src.service || "");
  if (!RESV_BOOT_DATE_RE.test(pDate)) return { error: `source.serviceDate non valida: ${src.serviceDate}` };
  if (pService !== "pranzo" && pService !== "cena") return { error: `source.service non valido: ${src.service}` };
  if (!Array.isArray(payload.items)) return { error: "items assente o non è un array" };
  const existing = Array.isArray(reservations) ? reservations.filter(r => r && !r.tombstone) : [];
  // Dedup forte (idempotenza): il tag si cerca nelle notes di TUTTE le
  // prenotazioni vive, non solo del giorno — un tag è unico per costruzione.
  const notesBlob = existing.map(r => String(r.notes || ""));
  const hasTag = (tag) => notesBlob.some(n => n.includes(tag));
  const keySet = new Set(existing
    .filter(r => r.serviceDate === pDate && r.service === pService)
    .map(r => _bootContentKey(r.serviceDate, r.service, r.rawName, r.covers, r.time)));
  const batchTags = new Set();
  const batchKeys = new Set();
  const rows = payload.items.map((raw, idx) => {
    const item = raw != null && typeof raw === "object" ? raw : {};
    const rv = item.review != null && typeof item.review === "object" ? item.review : null;
    const sr = item.src != null && typeof item.src === "object" ? item.src : null;
    const reasons = [];
    if (!sr || !sr.photo || sr.row == null || !sr.day || !sr.service) reasons.push("src incompleto (day/service/photo/row)");
    if (!rv || !rv.status) reasons.push("review assente");
    if (String(item.serviceDate || "") !== pDate) reasons.push("serviceDate diversa dal payload");
    if (String(item.service || "") !== pService) reasons.push("service diverso dal payload");
    if (String(item.rawName == null ? "" : item.rawName).trim() === "") reasons.push("rawName vuoto");
    if (!Number.isInteger(Number(item.covers)) || Number(item.covers) < 1) reasons.push("covers non valido (intero ≥ 1)");
    if (item.time != null && !RESV_BOOT_TIME_RE.test(String(item.time))) reasons.push("time non valido (HH:MM o null)");
    if (rv && rv.crossedOut === true) reasons.push("riga barrata/cancellata nella sorgente");
    if (rv && rv.status && rv.status !== "confirmed") reasons.push(`review.status "${rv.status}": solo confirmed è importabile (review-first)`);
    if (reasons.length > 0) return { idx, item, tag: null, outcome: "blocked", reasons };
    const tag = _bootTag(sr);
    if (hasTag(tag) || batchTags.has(tag)) return { idx, item, tag, outcome: "skip_tag", reasons: ["tag PNSM già presente: riga già importata"] };
    batchTags.add(tag);
    const key = _bootContentKey(pDate, pService, item.rawName, Number(item.covers), item.time == null ? null : item.time);
    const warn = keySet.has(key) || batchKeys.has(key);
    batchKeys.add(key);
    if (warn) return { idx, item, tag, outcome: "import_warn", reasons: ["esiste già una prenotazione simile (stesso giorno/servizio/nome/coperti/orario)"] };
    return { idx, item, tag, outcome: "import", reasons: [] };
  });
  const counts = { total: rows.length, importable: 0, warned: 0, skipTag: 0, blocked: 0 };
  for (const r of rows) {
    if (r.outcome === "import") counts.importable++;
    else if (r.outcome === "import_warn") counts.warned++;
    else if (r.outcome === "skip_tag") counts.skipTag++;
    else counts.blocked++;
  }
  return { error: null, meta: { serviceDate: pDate, service: pService, generatedAt: src.generatedAt || null }, rows, counts };
};

// Import batch PURO: solo righe import/import_warn, ogni riga passa da
// reservationCore.createReservation sull'array accumulato (append-only, il
// core decide status booked e valida da capo: resta l'unico gate del dominio).
// notes finale = nota originale + tag PNSM in coda (solo tag se nota vuota).
// Un errore ferma la riga, mai il batch. Il chiamante fa l'UNICO setState.
const _bootImport = (reservations, rows, ctx) => {
  let cur = Array.isArray(reservations) ? reservations : [];
  const created = [];
  const errors = [];
  for (const row of rows) {
    if (row.outcome !== "import" && row.outcome !== "import_warn") continue;
    const it = row.item;
    const baseNotes = it.notes != null && String(it.notes).trim() !== "" ? String(it.notes) : "";
    const res = reservationCore.createReservation(cur, {
      serviceDate: it.serviceDate, service: it.service, rawName: it.rawName,
      covers: Number(it.covers), time: it.time == null ? null : it.time,
      table: it.table != null && String(it.table).trim() !== "" ? String(it.table) : null,
      notes: baseNotes !== "" ? `${baseNotes}\n${row.tag}` : row.tag,
      source: "other",
    }, ctx);
    if (res.error) { errors.push({ idx: row.idx, rawName: it.rawName, error: res.error }); continue; }
    cur = res.reservations;
    created.push(res.reservation);
  }
  return { reservations: cur, created, errors };
};

const RESV_BOOT_OUTCOME_STYLE = {
  import: { label: "IMPORTA", fg: C.oliveDeep, bg: C.oliveWash, bd: C.olive },
  import_warn: { label: "IMPORTA CON WARNING", fg: C.amberDeep, bg: C.amberWash, bd: C.amber },
  skip_tag: { label: "SKIP TAG", fg: C.inkMid, bg: C.paperDeep, bd: C.rule },
  blocked: { label: "BLOCCATO", fg: C.terraDeep, bg: C.paperDeep, bd: C.terra },
};

function BootstrapImportPanel({ state, setState }) {
  const toast = useToast();
  const [rawText, setRawText] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [onlyImportable, setOnlyImportable] = useState(false);

  const parseText = () => {
    try { return { parsed: JSON.parse(rawText), parseError: null }; }
    catch (e) { return { parsed: null, parseError: `JSON non valido: ${e.message}` }; }
  };
  const handleAnalyze = () => {
    const { parsed, parseError } = parseText();
    if (parseError) { setAnalysis({ error: parseError }); return; }
    setAnalysis(_bootAnalyze(parsed, state.reservations));
  };
  const handleImport = () => {
    if (!analysis || analysis.error) return;
    const { parsed, parseError } = parseText();
    if (parseError) { setAnalysis({ error: parseError }); return; }
    // Ricalcolo sull'agenda CORRENTE: la preview può essere stale se lo stato
    // è cambiato dopo "Analizza"; il dedup per tag resta così idempotente.
    const fresh = _bootAnalyze(parsed, state.reservations);
    if (fresh.error) { setAnalysis(fresh); return; }
    const out = _bootImport(state.reservations, fresh.rows, { by: state.currentUser?.name || "Andi" });
    if (out.created.length > 0) setState(s => ({ ...s, reservations: out.reservations }));
    toast({
      type: out.errors.length > 0 ? "error" : out.created.length > 0 ? "success" : "info",
      title: "Import bootstrap",
      message: `${out.created.length} create · ${fresh.counts.skipTag} skip tag · ${fresh.counts.blocked} bloccate · ${out.errors.length} errori${out.errors.length > 0 ? ` — prima: ${out.errors[0].error}` : ""}`,
    });
    // Ri-analisi post-import: le righe appena create ora risultano SKIP TAG
    // (evidenza visiva dell'idempotenza), le bloccate restano bloccate.
    setAnalysis(_bootAnalyze(parsed, out.reservations));
  };

  const importableN = analysis && !analysis.error ? analysis.counts.importable + analysis.counts.warned : 0;
  const visibleRows = analysis && !analysis.error
    ? (onlyImportable ? analysis.rows.filter(r => r.outcome === "import" || r.outcome === "import_warn") : analysis.rows)
    : [];
  return <div style={{ marginBottom: 18, padding: "14px 16px", background: C.amberWash, border: `2px dashed ${C.amber}`, borderRadius: 2 }}>
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
      <span className="f-mono" style={{ fontSize: 11, letterSpacing: "0.2em", color: C.amberDeep, textTransform: "uppercase", fontWeight: 600 }}>Bootstrap Pienissimo</span>
      <span className="f-body" style={{ fontSize: 11, color: C.inkMid }}>
        pannello temporaneo (?bootstrap=1) · review-first: importa SOLO righe confirmed · append-only · docs/BOOTSTRAP_PIENISSIMO.md
      </span>
    </div>
    <textarea value={rawText} onChange={e => { setRawText(e.target.value); setAnalysis(null); }}
      placeholder='Incolla qui il JSON di staging ("pienissimo-staging-v1")…' spellCheck={false}
      className="f-mono" style={{ width: "100%", minHeight: 140, fontSize: 11, padding: 10, background: C.white, color: C.ink, border: `1px solid ${C.rule}`, borderRadius: 2, resize: "vertical", boxSizing: "border-box" }} />
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
      <Btn size="sm" onClick={handleAnalyze} disabled={rawText.trim() === ""}>Analizza</Btn>
      {analysis && !analysis.error && <>
        <Btn size="sm" variant="accent" onClick={handleImport} disabled={importableN === 0}>
          Importa confermati ({importableN})
        </Btn>
        <button className="tx f-mono" onClick={() => setOnlyImportable(v => !v)}
          style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 2, cursor: "pointer", background: onlyImportable ? C.ink : C.white, color: onlyImportable ? C.white : C.inkMid, border: `1px solid ${onlyImportable ? C.ink : C.rule}` }}>
          Solo importabili
        </button>
      </>}
    </div>
    {analysis && analysis.error && <div className="f-body" style={{ marginTop: 10, padding: "8px 12px", fontSize: 12, color: C.terraDeep, background: C.paperDeep, border: `1px solid ${C.terra}`, borderRadius: 2 }}>
      Payload non importabile: {analysis.error}
    </div>}
    {analysis && !analysis.error && <>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
        <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.1em", color: C.ink, background: C.white, border: `1px solid ${C.rule}`, padding: "3px 8px", borderRadius: 2 }}>
          {analysis.meta.serviceDate} · {analysis.meta.service} · {analysis.counts.total} righe
        </span>
        <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.1em", color: C.oliveDeep, background: C.oliveWash, border: `1px solid ${C.olive}`, padding: "3px 8px", borderRadius: 2 }}>importabili {analysis.counts.importable}</span>
        <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.1em", color: C.amberDeep, background: C.white, border: `1px solid ${C.amber}`, padding: "3px 8px", borderRadius: 2 }}>con warning {analysis.counts.warned}</span>
        <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.1em", color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}`, padding: "3px 8px", borderRadius: 2 }}>skip tag {analysis.counts.skipTag}</span>
        <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.1em", color: C.terraDeep, background: C.paperDeep, border: `1px solid ${C.terra}`, padding: "3px 8px", borderRadius: 2 }}>bloccate {analysis.counts.blocked}</span>
      </div>
      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
        {visibleRows.map(row => {
          const st = RESV_BOOT_OUTCOME_STYLE[row.outcome];
          const it = row.item;
          return <div key={row.idx} style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", padding: "6px 10px", background: C.white, border: `1px solid ${C.ruleSoft}`, borderRadius: 2 }}>
            <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.1em", color: st.fg, background: st.bg, border: `1px solid ${st.bd}`, padding: "2px 7px", borderRadius: 2, flexShrink: 0 }}>{st.label}</span>
            <span className="f-display" style={{ fontSize: 13, fontWeight: 500, color: C.ink }}>{String(it.rawName || "(senza nome)")}</span>
            <span className="f-mono" style={{ fontSize: 11, color: C.inkMid }}>{it.covers != null ? `${it.covers} cop.` : "cop. ?"}</span>
            <span className="f-mono" style={{ fontSize: 11, color: C.inkMid }}>{it.time || "senza orario"}</span>
            <span className="f-mono" style={{ fontSize: 11, color: C.inkMid }}>{it.table != null && String(it.table).trim() !== "" ? `tav ${it.table}` : "senza tavolo"}</span>
            {it.notes != null && String(it.notes).trim() !== "" && <span className="f-body" style={{ fontSize: 11, color: C.muted, fontStyle: "italic", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{String(it.notes)}</span>}
            <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.08em", color: C.muted }}>
              {it.review && it.review.status ? it.review.status : "?"} · {it.src && it.src.photo ? `p${_bootPhotoId(it.src.photo)}/r${it.src.row}` : "src ?"}
            </span>
            {row.reasons.length > 0 && <span className="f-body" style={{ fontSize: 10, color: row.outcome === "blocked" ? C.terraDeep : C.amberDeep }}>{row.reasons.join(" · ")}</span>}
          </div>;
        })}
      </div>
    </>}
    {/* R5.6 — foundation aree/tavoli reale da Pienissimo (gesto esplicito) */}
    <BootstrapFloorPanel state={state} setState={setState} />
    {/* R6-T1 — posti per tavolo derivati dai coperti reali (review-first) */}
    <BootstrapSeatsPanel state={state} setState={setState} />
    {/* R5.5-T4.6 — audit read-only del giorno: stessa fonte runtime della UI */}
    <BootstrapAuditPanel state={state} setState={setState} />
    {/* C3.2 — monitor del canale online: i numeri del gate C3 (switch/disdetta) */}
    <OnlineMonitorPanel state={state} />
  </div>;
}

// R5.6 — Bootstrap foundation tavoli/aree da Pienissimo. Ground truth FISSATA
// dal mandato (schermate Pienissimo): solo questi tavoli, mai inventarne.
// Scritture SOLO via floorCore.createArea/createTable (un solo setState),
// idempotente per label canonica (mai duplicare, nemmeno tavoli inattivi o
// in altra area), additive e reversibile da Sala & Tavoli. seats resta il
// default 0 del core ("mai overflow", R4): nessuna semantica nuova inventata.
const RESV_BOOT_FLOOR_PLAN = [
  { area: "Sala", tables: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "21", "22", "23", "24", "25", "26", "27", "28", "29", "30", "31", "32", "33", "34", "35", "37", "39", "401", "402", "403", "420", "421", "422", "423", "430", "431", "432", "433"] },
  { area: "300", tables: ["300", "301", "302", "303", "304", "310", "311", "312", "313", "314", "315", "316", "441", "442", "451", "452", "453"] },
  { area: "Pedana", tables: ["900", "901", "903", "905", "909", "921", "923", "925", "927", "929", "931", "933", "935", "937", "939"] },
  { area: "Saletta", tables: ["201", "202", "211", "212", "221", "222", "231", "241", "242", "245", "246", "247"] },
  { area: "Strada", tables: ["500", "501", "502", "503", "504", "505", "600", "601", "602"] },
];
// Canone label locale (stessa semantica del canone FLOOR-CORE, reimplementato
// per non chiamare l'helper privato del blocco congelato): prefissi tav/t/#
// rimossi, interi normalizzati, altrimenti normStr.
const _bootFloorCanon = (raw) => {
  const s = String(raw == null ? "" : raw).trim();
  if (!s) return null;
  const stripped = s.replace(/^(?:tavolo|tav|t|#)\s*\.?\s*(?=\d)/i, "").trim();
  if (/^\d+$/.test(stripped)) return String(parseInt(stripped, 10));
  return normStr(stripped) || null;
};
// Analisi PURA del piano contro lo state runtime: cosa esiste già, cosa manca,
// eventuali tavoli già presenti ma in un'area diversa (informativo, mai "fix").
const _bootFloorAnalyze = (state) => {
  const s = state || {};
  const areas = (Array.isArray(s.floorAreas) ? s.floorAreas : []).filter(a => a && !a.tombstone);
  const tables = (Array.isArray(s.floorTables) ? s.floorTables : []).filter(t => t && !t.tombstone);
  const byCanon = new Map();
  for (const t of tables) {
    const k = _bootFloorCanon(t.label);
    if (k != null && !byCanon.has(k)) byCanon.set(k, t);
  }
  const plan = RESV_BOOT_FLOOR_PLAN.map(p => {
    const existingArea = areas.find(a => normStr(a.name) === normStr(p.area)) || null;
    const rows = p.tables.map(label => {
      const hit = byCanon.get(_bootFloorCanon(label)) || null;
      return {
        label,
        exists: !!hit,
        inactive: !!(hit && hit.active === false),
        // già esistente ma fuori dall'area del piano (o area del piano ancora
        // assente): informativo, mai spostato né toccato
        areaMismatch: !!(hit && (existingArea ? String(hit.areaId) !== String(existingArea.id) : true)),
      };
    });
    return {
      areaName: p.area, existingArea, rows,
      present: rows.filter(r => r.exists).length,
      missing: rows.filter(r => !r.exists).length,
      mismatch: rows.filter(r => r.areaMismatch).length,
    };
  });
  return {
    plan,
    missingAreas: plan.filter(p => !p.existingArea).length,
    missingTables: plan.reduce((t, p) => t + p.missing, 0),
    presentTables: plan.reduce((t, p) => t + p.present, 0),
    mismatchTables: plan.reduce((t, p) => t + p.mismatch, 0),
    totalPlanned: plan.reduce((t, p) => t + p.rows.length, 0),
  };
};
// Applicazione PURA: crea SOLO ciò che manca, accumulando via i reducer del
// core (unico canale di scrittura). Aree esistenti riusate (anche se
// inattive: mai riattivate né duplicate); tavoli esistenti mai toccati.
// Un errore ferma la riga, mai il batch. Il chiamante fa l'UNICO setState.
const _bootFloorApply = (state, ctx) => {
  const s = state || {};
  let areasArr = Array.isArray(s.floorAreas) ? s.floorAreas : [];
  let tablesArr = Array.isArray(s.floorTables) ? s.floorTables : [];
  const canon = new Set(tablesArr.filter(t => t && !t.tombstone).map(t => _bootFloorCanon(t.label)).filter(k => k != null));
  const createdAreas = [];
  const createdTables = [];
  const errors = [];
  for (const p of RESV_BOOT_FLOOR_PLAN) {
    let area = areasArr.filter(a => a && !a.tombstone).find(a => normStr(a.name) === normStr(p.area)) || null;
    if (!area) {
      const res = floorCore.createArea(areasArr, { name: p.area }, ctx);
      if (res.error) { errors.push({ scope: `area ${p.area}`, error: res.error }); continue; }
      areasArr = res.floorAreas;
      area = res.area;
      createdAreas.push(res.area);
    }
    for (const label of p.tables) {
      const k = _bootFloorCanon(label);
      if (k == null || canon.has(k)) continue;
      const res = floorCore.createTable(tablesArr, { label, areaId: area.id }, ctx);
      if (res.error) { errors.push({ scope: `tavolo ${label}`, error: res.error }); continue; }
      tablesArr = res.floorTables;
      canon.add(k);
      createdTables.push(res.table);
    }
  }
  return { floorAreas: areasArr, floorTables: tablesArr, createdAreas, createdTables, errors };
};

function BootstrapFloorPanel({ state, setState }) {
  const toast = useToast();
  const analysis = useMemo(() => _bootFloorAnalyze(state), [state]);
  const toCreate = analysis.missingTables + analysis.missingAreas;
  const chip = { fontSize: 10, letterSpacing: "0.08em", padding: "3px 8px", borderRadius: 2, flexShrink: 0 };
  const handleApply = () => {
    const out = _bootFloorApply(state, { by: state.currentUser?.name || "Andi" });
    if (out.createdAreas.length > 0 || out.createdTables.length > 0) {
      setState(st => ({ ...st, floorAreas: out.floorAreas, floorTables: out.floorTables }));
    }
    toast({
      type: out.errors.length > 0 ? "error" : out.createdTables.length > 0 || out.createdAreas.length > 0 ? "success" : "info",
      title: "Bootstrap tavoli",
      message: `${out.createdAreas.length} aree · ${out.createdTables.length} tavoli creati · ${out.errors.length} errori${out.errors.length > 0 ? ` — primo: ${out.errors[0].scope}: ${out.errors[0].error}` : ""}`,
    });
  };
  return <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${C.amber}` }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
      <span className="f-mono" style={{ fontSize: 11, letterSpacing: "0.2em", color: C.amberDeep, textTransform: "uppercase", fontWeight: 600 }}>Bootstrap Tavoli</span>
      <span className="f-body" style={{ fontSize: 11, color: C.inkMid }}>
        foundation aree/tavoli dalla mappa Pienissimo (R5.6) · additiva e idempotente · reversibile da Sala &amp; Tavoli
      </span>
      <div style={{ flex: 1 }} />
      {toCreate > 0
        ? <Btn size="sm" variant="accent" onClick={handleApply}>Crea aree e tavoli mancanti ({toCreate})</Btn>
        : <span className="f-mono" style={{ ...chip, color: C.oliveDeep, background: C.oliveWash, border: `1px solid ${C.olive}` }}>Foundation completa ✓ ({analysis.presentTables}/{analysis.totalPlanned})</span>}
    </div>
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {analysis.plan.map(p => <span key={p.areaName} className="f-mono"
        style={{ ...chip, color: p.missing > 0 ? C.amberDeep : C.oliveDeep, background: p.missing > 0 ? C.amberWash : C.oliveWash, border: `1px solid ${p.missing > 0 ? C.amber : C.olive}` }}>
        {p.areaName}{!p.existingArea ? " (area da creare)" : ""} · {p.present}/{p.rows.length} presenti{p.missing > 0 ? ` · ${p.missing} mancanti` : ""}
      </span>)}
      {analysis.mismatchTables > 0 && <span className="f-mono" style={{ ...chip, color: C.terraDeep, background: C.paperDeep, border: `1px solid ${C.terra}` }}
        title="Tavoli già esistenti ma assegnati a un'area diversa dal piano: non vengono toccati né spostati">
        {analysis.mismatchTables} in area diversa dal piano (non toccati)
      </span>}
    </div>
  </div>;
}

// R6-T1 — Bootstrap POSTI per tavolo dai coperti REALI degli scontrini.
// Fondamento della proposta-tavolo intelligente (docs/AGENDA_PIENISSIMO_REPLACEMENT.md,
// F1): seats oggi è 0 su tutta l'anagrafica R5.6 e senza posti nessun ranking di
// capienza è possibile. Invece di chiedere 90 numeri a mano, si DERIVANO dallo
// storico: per ogni tavolo attivo i coperti dei suoi scontrini (banco escluso,
// covers>0, stessa label canonica). Proposta = p90 dei coperti osservati (robusto
// contro fusioni/outlier, più onesto del max). Regole: solo tavoli con ≥3
// scontrini; i tavoli con seats già >0 NON si toccano mai (rispetto del dato
// inserito a mano); scrittura SOLO via floorCore.updateTable, un solo setState,
// reversibile da Sala & Tavoli. Analisi PURA, nessun side effect.
const _bootSeatsAnalyze = (state) => {
  const s = state || {};
  const tables = (Array.isArray(s.floorTables) ? s.floorTables : []).filter(t => t && !t.tombstone && t.active !== false);
  const areas = (Array.isArray(s.floorAreas) ? s.floorAreas : []).filter(a => a && !a.tombstone);
  const areaName = new Map(areas.map(a => [String(a.id), a.name]));
  const hist = new Map();
  for (const rc of (Array.isArray(s.tableReceipts) ? s.tableReceipts : [])) {
    if (!rc || rc.isBanco) continue;
    const k = _bootFloorCanon(rc.number);
    const cov = Number(rc.covers) || 0;
    if (k == null || cov <= 0) continue;
    if (!hist.has(k)) hist.set(k, []);
    hist.get(k).push(cov);
  }
  const rows = [];
  let alreadySet = 0, noData = 0;
  for (const t of tables) {
    if (Number(t.seats) > 0) { alreadySet++; continue; }
    const h = hist.get(_bootFloorCanon(t.label)) || [];
    if (h.length < 3) { noData++; continue; }
    const sorted = [...h].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    const p90 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.9))];
    rows.push({
      table: t,
      area: areaName.get(String(t.areaId)) || "?",
      n: sorted.length,
      median,
      p90,
      max: sorted[sorted.length - 1],
      proposed: Math.max(2, p90),
    });
  }
  rows.sort((a, b) => a.area.localeCompare(b.area) || String(a.table.label).localeCompare(String(b.table.label), undefined, { numeric: true }));
  return { rows, alreadySet, noData, totalActive: tables.length };
};

function BootstrapSeatsPanel({ state, setState }) {
  const toast = useToast();
  const analysis = useMemo(() => _bootSeatsAnalyze(state), [state]);
  const [excluded, setExcluded] = useState(() => new Set());
  const [open, setOpen] = useState(false);
  const selected = analysis.rows.filter(r => !excluded.has(String(r.table.id)));
  const chip = { fontSize: 10, letterSpacing: "0.08em", padding: "3px 8px", borderRadius: 2, flexShrink: 0 };
  const toggle = (id) => setExcluded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const handleApply = () => {
    let tablesArr = Array.isArray(state.floorTables) ? state.floorTables : [];
    const ctx = { by: state.currentUser?.name || "Andi" };
    let applied = 0;
    const errors = [];
    for (const r of selected) {
      const res = floorCore.updateTable(tablesArr, r.table.id, { seats: r.proposed }, ctx);
      if (res.error) { errors.push({ scope: `tavolo ${r.table.label}`, error: res.error }); continue; }
      tablesArr = res.floorTables;
      applied++;
    }
    if (applied > 0) setState(st => ({ ...st, floorTables: tablesArr }));
    toast({
      type: errors.length > 0 ? "error" : applied > 0 ? "success" : "info",
      title: "Posti per tavolo",
      message: `${applied} tavoli aggiornati dai dati storici · ${errors.length} errori${errors.length > 0 ? ` — primo: ${errors[0].scope}: ${errors[0].error}` : ""}`,
    });
  };
  return <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${C.amber}` }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
      <span className="f-mono" style={{ fontSize: 11, letterSpacing: "0.2em", color: C.amberDeep, textTransform: "uppercase", fontWeight: 600 }}>Bootstrap Posti</span>
      <span className="f-body" style={{ fontSize: 11, color: C.inkMid }}>
        posti per tavolo derivati dai coperti reali degli scontrini (p90) · solo tavoli con ≥3 scontrini e posti non ancora impostati · reversibile da Sala &amp; Tavoli
      </span>
      <div style={{ flex: 1 }} />
      {analysis.rows.length > 0 && <Btn size="sm" variant="ghost" onClick={() => setOpen(o => !o)}>{open ? "Nascondi proposte" : `Rivedi proposte (${analysis.rows.length})`}</Btn>}
      {analysis.rows.length > 0
        ? <Btn size="sm" variant="accent" onClick={handleApply}>Applica selezionati ({selected.length})</Btn>
        : <span className="f-mono" style={{ ...chip, color: C.oliveDeep, background: C.oliveWash, border: `1px solid ${C.olive}` }}>
            Niente da proporre {analysis.alreadySet > 0 ? `· ${analysis.alreadySet} già impostati` : ""}{analysis.noData > 0 ? ` · ${analysis.noData} senza dati sufficienti` : ""}
          </span>}
    </div>
    {analysis.rows.length > 0 && <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: open ? 8 : 0 }}>
      <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>
        {analysis.rows.length} proposte · {analysis.alreadySet} già impostati (intoccati) · {analysis.noData} senza dati sufficienti
      </span>
    </div>}
    {open && analysis.rows.length > 0 && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 6 }}>
      {analysis.rows.map(r => {
        const off = excluded.has(String(r.table.id));
        return <button key={r.table.id} className="tx f-mono" onClick={() => toggle(String(r.table.id))}
          title={`${r.n} scontrini · mediana ${r.median} · p90 ${r.p90} · max ${r.max} — tocca per ${off ? "includere" : "escludere"}`}
          style={{ textAlign: "left", fontSize: 10.5, padding: "6px 10px", borderRadius: 2, cursor: "pointer",
            background: off ? C.paperDeep : C.oliveWash, color: off ? C.muted : C.ink,
            border: `1px solid ${off ? C.rule : C.olive}`, textDecoration: off ? "line-through" : "none" }}>
          <span style={{ fontWeight: 600 }}>{r.area} · {r.table.label}</span> → {r.proposed} posti
          <span style={{ opacity: 0.7 }}> · {r.n} sc. · med {r.median} · max {r.max}</span>
        </button>;
      })}
    </div>}
  </div>;
}

// R5.5-T4.6 — Audit READ-ONLY del bootstrap: deriva tutto dallo STESSO state
// runtime che alimenta l'Agenda (prop state, mai localStorage diretto) e dagli
// stessi helper della UI: deriveReservationFulfilment (badge R5),
// resolveTableTokens (griglia R4), getServiceDate/getShift (convenzioni
// scontrini). Nessuna scrittura di dominio, nessun setState di store.
const _bootAudit = (state, day) => {
  const s = state || {};
  const dayResv = (Array.isArray(s.reservations) ? s.reservations : [])
    .filter(r => r && !r.tombstone && r.serviceDate === day);
  const tables = Array.isArray(s.floorTables) ? s.floorTables : [];
  const services = {};
  for (const svc of ["pranzo", "cena"]) {
    const rows = dayResv.filter(r => r.service === svc).sort(_resvCompareByTime).map(r => {
      const tres = floorCore.resolveTableTokens(r.table, tables);
      const f = reservationFulfilCore.deriveReservationFulfilment(s, r.id);
      const hasTag = String(r.notes || "").includes("[PNSM");
      const tableOutcome = r.table == null || String(r.table).trim() === "" ? "senza tavolo"
        : tres && tres.matchedCount > 0 && tres.unknownCount === 0 ? "riconosciuto"
        : tres && tres.matchedCount > 0 && tres.unknownCount > 0 ? "misto"
        : "sconosciuto";
      const fulfil = f.fulfilled ? (f.sharedReceipt ? "shared" : "collegata")
        : f.orphanLinks.length > 0 ? "orphan" : "da collegare";
      return { r, tres, f, hasTag, tableOutcome, fulfil };
    });
    const active = rows.filter(x => x.r.status === "booked" || x.r.status === "seated");
    services[svc] = {
      rows,
      total: rows.length,
      active: active.length,
      covers: active.reduce((t, x) => t + (Number(x.r.covers) || 0), 0),
      seated: rows.filter(x => x.r.status === "seated").length,
      cancelled: rows.filter(x => x.r.status === "cancelled").length,
      noShow: rows.filter(x => x.r.status === "no_show").length,
      linked: active.filter(x => x.fulfil === "collegata" || x.fulfil === "shared").length,
      toLink: active.filter(x => x.fulfil === "da collegare").length,
      orphan: rows.filter(x => x.fulfil === "orphan").length,
      shared: rows.filter(x => x.f.sharedReceipt).length,
      unknownTables: rows.filter(x => x.tableOutcome === "sconosciuto" || x.tableOutcome === "misto").length,
      withTag: rows.filter(x => x.hasTag).length,
      withoutTag: rows.filter(x => !x.hasTag).length,
    };
  }
  // Scontrini del giorno di servizio: stesse convenzioni del resto del repo
  // (post-mezzanotte fino a nightEnd → cena del giorno prima).
  const receipts = (Array.isArray(s.tableReceipts) ? s.tableReceipts : [])
    .filter(rc => rc && getServiceDate(rc.closedAt) === day);
  const linkedReceiptIds = new Set();
  for (const svc of ["pranzo", "cena"]) {
    for (const x of services[svc].rows) {
      for (const res of x.f.resolved) if (res.receipt) linkedReceiptIds.add(res.receipt.id);
    }
  }
  const lastClosed = receipts.map(rc => rc.closedAt).filter(Boolean).sort().slice(-1)[0] || null;
  return {
    day,
    services,
    receipts: {
      total: receipts.length,
      pranzo: receipts.filter(rc => getShift(rc.closedAt) === "pranzo").length,
      cena: receipts.filter(rc => getShift(rc.closedAt) === "cena").length,
      linked: receipts.filter(rc => linkedReceiptIds.has(rc.id)).length,
      unlinked: receipts.filter(rc => !linkedReceiptIds.has(rc.id)).length,
      lastClosed,
    },
  };
};

// Baseline INFORMATIVA (read-only, mai logica di business): i valori attesi
// dallo staging 30/06 approvato in review T2.5, mostrati accanto al runtime.
const RESV_BOOT_BASELINE = {
  "2026-06-30": {
    pranzo: { attive: 13, coperti: 44, nota: "1 riga cancellata (ignored) resta fuori dominio; tutte con tag PNSM" },
    cena: { attive: 30, coperti: 101, nota: "gap dichiarato vs header Pienissimo 31/103: manca 1 prenotazione / 2 coperti (sala 300, tav ~305–309); tutte con tag PNSM" },
  },
};
const RESV_BOOT_DAYS = ["2026-06-30", "2026-07-01", "2026-07-02", "2026-07-03", "2026-07-04", "2026-07-05"];
const RESV_BOOT_FULFIL_STYLE = {
  "collegata": { fg: C.oliveDeep, bg: C.oliveWash, bd: C.olive },
  "shared": { fg: C.amberDeep, bg: C.amberWash, bd: C.amber },
  "da collegare": { fg: C.amberDeep, bg: C.amberWash, bd: C.amber },
  "orphan": { fg: C.terraDeep, bg: C.paperDeep, bd: C.terra },
};

// R5.7-T2 — Diagnostica READ-ONLY dello store scontrini: fotografa TUTTO
// state.tableReceipts (non filtrato per giorno) per distinguere "store senza
// receipt" da "receipt presenti ma fuori giorno/formato". Stessa fonte runtime
// della UI, stessi helper canonici (getServiceDate/getShift, facade per il
// tavolo). Deriva e basta: zero scritture.
const _bootReceiptDiag = (state) => {
  const all = (Array.isArray(state?.tableReceipts) ? state.tableReceipts : []).filter(rc => rc);
  const hasClosed = (rc) => rc.closedAt != null && String(rc.closedAt).trim() !== "";
  const parseable = (rc) => hasClosed(rc) && !isNaN(new Date(rc.closedAt).getTime());
  const noClosed = all.filter(rc => !hasClosed(rc));
  const unparsable = all.filter(rc => hasClosed(rc) && !parseable(rc));
  const okList = all.filter(parseable);
  const perDay = RESV_BOOT_DAYS.map(d => {
    const list = okList.filter(rc => getServiceDate(rc.closedAt) === d);
    return {
      day: d,
      total: list.length,
      pranzo: list.filter(rc => getShift(rc.closedAt) === "pranzo").length,
      cena: list.filter(rc => getShift(rc.closedAt) === "cena").length,
    };
  });
  const inRange = perDay.reduce((t, d) => t + d.total, 0);
  const sorted = [...okList].sort((a, b) => String(b.closedAt).localeCompare(String(a.closedAt)));
  return {
    total: all.length,
    withClosed: all.length - noClosed.length,
    noClosed: noClosed.length,
    unparsable: unparsable.length,
    lastClosed: sorted.length > 0 ? sorted[0].closedAt : null,
    perDay,
    outOfRange: okList.length - inRange,
    sample: sorted.slice(0, 5).map(rc => ({
      number: rc.number != null ? rc.number : "—",
      fiscalNumber: rc.fiscalNumber || null,
      closedAt: rc.closedAt,
      serviceDate: getServiceDate(rc.closedAt),
      shift: getShift(rc.closedAt),
      total: Number(rc.total) || 0,
      table: receiptFacade.getReceiptTable(rc),
      source: rc._sourceFile || rc._importBatch || null,
    })),
  };
};

// R5.9-T1 — Audit RICONCILIAZIONE read-only: per ogni prenotazione attiva del
// giorno classifica la linkabilità usando il VERO motore R5-T5 (stessi helper,
// stessi pesi e soglie: _resvReceiptRow → _resvScoreReceiptCandidate →
// _resvClassifySuggestions) e fotografa i receipt del turno. SOLO derivazione:
// zero scritture, zero bottoni di collegamento — il gesto resta in Agenda (F8).
// Classi prenotazione: "facile" = top Suggerito; "ambigua" = candidati forti
// vicini; "media" = solo Da verificare; "senza candidati" = niente score ≥ 2.
const _bootReconAudit = (state, day) => {
  const s = state || {};
  const links = Array.isArray(s.reservationReceiptLinks) ? s.reservationReceiptLinks : [];
  const tables = Array.isArray(s.floorTables) ? s.floorTables : [];
  const dayResv = (Array.isArray(s.reservations) ? s.reservations : [])
    .filter(r => r && !r.tombstone && r.serviceDate === day);
  const out = {};
  for (const svc of ["pranzo", "cena"]) {
    const receipts = (Array.isArray(s.tableReceipts) ? s.tableReceipts : [])
      .filter(rc => rc && getServiceDate(rc.closedAt) === day && getShift(rc.closedAt) === svc);
    const rows = receipts.map(rc => _resvReceiptRow(rc, links));
    const svcResv = dayResv.filter(r => r.service === svc);
    const linkedReceiptIds = new Set();
    const counts = { daCollegare: 0, facili: 0, medie: 0, ambigue: 0, senzaCandidati: 0, collegate: 0, orphan: 0, shared: 0 };
    const plan = [];
    for (const r of svcResv) {
      const f = reservationFulfilCore.deriveReservationFulfilment(s, r.id);
      for (const res of f.resolved) if (res.receipt) linkedReceiptIds.add(res.receipt.id);
      if (r.status !== "booked" && r.status !== "seated") continue;
      if (f.sharedReceipt) counts.shared++;
      if (f.fulfilled) { counts.collegate++; continue; }
      if (f.orphanLinks.length > 0) { counts.orphan++; continue; }
      counts.daCollegare++;
      const tres = floorCore.resolveTableTokens(r.table, tables);
      const canon = new Set((tres ? tres.tokens : [])
        .map(t => t.canonical != null ? String(t.canonical) : null).filter(Boolean));
      const sugg = _resvClassifySuggestions(rows
        .filter(row => !row.linkedTo.includes(String(r.id)))
        .map(row => _resvScoreReceiptCandidate(r, row, canon)));
      const top = sugg[0] || null;
      const klass = top && top.status === "suggested" ? "facile"
        : sugg.some(c => c.status === "ambiguous") ? "ambigua"
        : sugg.length > 0 ? "media" : "senza candidati";
      if (klass === "facile") counts.facili++;
      else if (klass === "media") counts.medie++;
      else if (klass === "ambigua") counts.ambigue++;
      else counts.senzaCandidati++;
      plan.push({ r, klass, top, nCand: sugg.length });
    }
    const order = { "facile": 0, "media": 1, "ambigua": 2, "senza candidati": 3 };
    plan.sort((a, b) => (order[a.klass] - order[b.klass]) || _resvCompareByTime(a.r, b.r));
    out[svc] = {
      counts, plan,
      receipts: {
        total: receipts.length,
        linked: receipts.filter(rc => linkedReceiptIds.has(rc.id)).length,
        unlinked: receipts.filter(rc => !linkedReceiptIds.has(rc.id)).length,
        banco: rows.filter(x => x.isBanco).length,
        senzaTavolo: rows.filter(x => !x.isBanco && x.table == null).length,
        preconti: receipts.filter(rc => rc._isPreconto === true).length,
      },
    };
  }
  return out;
};
const RESV_BOOT_KLASS_STYLE = {
  "facile": { fg: C.oliveDeep, bg: C.oliveWash, bd: C.olive },
  "media": { fg: C.inkMid, bg: C.paperDeep, bd: C.rule },
  "ambigua": { fg: C.amberDeep, bg: C.amberWash, bd: C.amber },
  "senza candidati": { fg: C.terraDeep, bg: C.paperDeep, bd: C.terra },
};

function BootstrapAuditPanel({ state, setState }) {
  const toast = useToast();
  const [auditDay, setAuditDay] = useState("2026-06-30");
  const [showRows, setShowRows] = useState(false);
  const [showRecon, setShowRecon] = useState(false);
  const audit = useMemo(() => _bootAudit(state, auditDay), [state, auditDay]);
  const rdiag = useMemo(() => _bootReceiptDiag(state), [state]);
  const recon = useMemo(() => _bootReconAudit(state, auditDay), [state, auditDay]);
  // R5.9-T2 — batch "Collega i facili": UN gesto esplicito dell'operatore collega
  // in blocco i soli casi classificati "facile" (top Suggerito dal motore R5,
  // già con hard-block linkedOther). MAI silenzioso: parte solo dal bottone,
  // origin "suggestion_confirmed", resoconto a toast. Guardia anti-doppione
  // DENTRO il giro: lo stesso receipt non può servire due prenotazioni del
  // batch (il secondo caso resta in coda alla review umana). La convergenza
  // (nuovi facili emersi dopo i collegamenti) si vede subito nei contatori:
  // si ripreme finché la coda non si asciuga — sempre l'umano a decidere.
  const handleLinkFacili = (svc) => {
    if (!setState) return;
    const plan = (recon[svc] && recon[svc].plan) || [];
    const facili = plan.filter(p => p.klass === "facile" && p.top && p.top.receipt);
    if (facili.length === 0) return;
    let links = Array.isArray(state.reservationReceiptLinks) ? state.reservationReceiptLinks : [];
    const usedReceipts = new Set();
    let ok = 0, skipped = 0;
    let firstError = null;
    for (const p of facili) {
      const rcId = p.top.receipt.id;
      if (rcId != null && usedReceipts.has(rcId)) { skipped++; continue; }
      const res = reservationFulfilCore.createReservationReceiptLink(
        links, state.reservations, p.top.receipt,
        { reservationId: p.r.id, origin: "suggestion_confirmed" },
        { by: state.currentUser?.name || "Andi" });
      if (res.error) { skipped++; if (!firstError) firstError = `${p.r.rawName}: ${res.error}`; continue; }
      links = res.reservationReceiptLinks;
      if (rcId != null) usedReceipts.add(rcId);
      ok++;
    }
    if (ok > 0) setState(s => ({ ...s, reservationReceiptLinks: links }));
    toast({
      type: skipped > 0 ? "info" : "success",
      title: `Collegati ${ok} facili (${svc})`,
      message: `${skipped > 0 ? `${skipped} lasciati alla review${firstError ? ` — ${firstError}` : ""}. ` : ""}Se compaiono nuovi facili, ripremi il bottone.`,
    });
  };
  const baseline = RESV_BOOT_BASELINE[auditDay] || null;
  const chip = { fontSize: 10, letterSpacing: "0.08em", padding: "3px 8px", borderRadius: 2, flexShrink: 0 };
  const svcCard = (svc) => {
    const d = audit.services[svc];
    const b = baseline ? baseline[svc] : null;
    return <div key={svc} style={{ flex: 1, minWidth: 250, padding: "10px 12px", background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
        <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: C.ink, fontWeight: 600 }}>{svc}</span>
        <span className="f-mono" style={{ fontSize: 11, color: C.inkMid }}>{d.active} attive · {d.covers} coperti</span>
        {b && <span className="f-mono" style={{ fontSize: 10, color: d.active === b.attive && d.covers === b.coperti ? C.oliveDeep : C.terraDeep }}>
          (attesi {b.attive} / {b.coperti}) {d.active === b.attive && d.covers === b.coperti ? "✓" : "✗"}
        </span>}
      </div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>tot {d.total}</span>
        <span className="f-mono" style={{ ...chip, color: C.oliveDeep, background: C.oliveWash, border: `1px solid ${C.olive}` }}>arrivate {d.seated}</span>
        <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>annullate {d.cancelled} · no-show {d.noShow}</span>
        <span className="f-mono" style={{ ...chip, color: C.amberDeep, background: C.amberWash, border: `1px solid ${C.amber}` }}>da collegare {d.toLink}</span>
        <span className="f-mono" style={{ ...chip, color: C.oliveDeep, background: C.oliveWash, border: `1px solid ${C.olive}` }}>collegate {d.linked}</span>
        {d.orphan > 0 && <span className="f-mono" style={{ ...chip, color: C.terraDeep, background: C.paperDeep, border: `1px solid ${C.terra}` }}>orfani {d.orphan}</span>}
        {d.shared > 0 && <span className="f-mono" style={{ ...chip, color: C.amberDeep, background: C.paperDeep, border: `1px solid ${C.amber}` }}>shared {d.shared}</span>}
        <span className="f-mono" style={{ ...chip, color: d.unknownTables > 0 ? C.terraDeep : C.inkMid, background: C.paperDeep, border: `1px solid ${d.unknownTables > 0 ? C.terra : C.rule}` }}>tav. sconosciuti {d.unknownTables}</span>
        <span className="f-mono" style={{ ...chip, color: C.tealDeep, background: C.tealWash, border: `1px solid ${C.teal}` }}>PNSM {d.withTag}</span>
        {d.withoutTag > 0 && <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>senza tag {d.withoutTag}</span>}
      </div>
      {b && <div className="f-body" style={{ fontSize: 10, color: C.muted, fontStyle: "italic", marginTop: 6 }}>{b.nota}</div>}
    </div>;
  };
  return <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${C.amber}` }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
      <span className="f-mono" style={{ fontSize: 11, letterSpacing: "0.2em", color: C.amberDeep, textTransform: "uppercase", fontWeight: 600 }}>Bootstrap Audit</span>
      <span className="f-body" style={{ fontSize: 11, color: C.inkMid }}>sola lettura · stessa fonte runtime dell'Agenda</span>
      <div style={{ flex: 1 }} />
      {RESV_BOOT_DAYS.map(d => <button key={d} className="tx f-mono" onClick={() => setAuditDay(d)}
        style={{ fontSize: 10, letterSpacing: "0.05em", padding: "4px 8px", borderRadius: 2, cursor: "pointer",
          background: auditDay === d ? C.ink : C.white, color: auditDay === d ? C.white : C.inkMid, border: `1px solid ${auditDay === d ? C.ink : C.rule}` }}>
        {d.slice(8)}/{d.slice(5, 7)}
      </button>)}
      <input type="date" value={auditDay} onChange={e => { if (e.target.value) setAuditDay(e.target.value); }}
        className="f-body" style={{ fontSize: 11, padding: "4px 6px", border: `1px solid ${C.rule}`, borderRadius: 2, background: C.white, color: C.ink }} />
    </div>
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
      {svcCard("pranzo")}
      {svcCard("cena")}
      <div style={{ flex: 1, minWidth: 220, padding: "10px 12px", background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
        <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: C.ink, fontWeight: 600, marginBottom: 6 }}>Scontrini del giorno</div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          <span className="f-mono" style={{ ...chip, color: C.ink, background: C.paperDeep, border: `1px solid ${C.rule}` }}>totale {audit.receipts.total}</span>
          <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>pranzo {audit.receipts.pranzo} · cena {audit.receipts.cena}</span>
          <span className="f-mono" style={{ ...chip, color: C.oliveDeep, background: C.oliveWash, border: `1px solid ${C.olive}` }}>collegati {audit.receipts.linked}</span>
          <span className="f-mono" style={{ ...chip, color: audit.receipts.unlinked > 0 ? C.amberDeep : C.inkMid, background: audit.receipts.unlinked > 0 ? C.amberWash : C.paperDeep, border: `1px solid ${audit.receipts.unlinked > 0 ? C.amber : C.rule}` }}>senza prenotazione {audit.receipts.unlinked}</span>
          {audit.receipts.lastClosed && <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>ultimo {fmt.time(audit.receipts.lastClosed)}</span>}
        </div>
      </div>
    </div>
    {/* R5.7-T2 — diagnostica store scontrini: vista GLOBALE non filtrata per
        giorno, per distinguere "store vuoto" da "receipt fuori giorno/formato" */}
    <div style={{ marginBottom: 8, padding: "10px 12px", background: C.white, border: `1px solid ${rdiag.total === 0 ? C.terra : C.rule}`, borderRadius: 2 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: C.ink, fontWeight: 600 }}>Store scontrini (diagnostica)</span>
        <span className="f-body" style={{ fontSize: 10, color: C.muted, fontStyle: "italic" }}>campo data: closedAt · attribuzione giorno/servizio: getServiceDate + getShift · sola lettura</span>
      </div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 6 }}>
        <span className="f-mono" style={{ ...chip, color: rdiag.total === 0 ? C.terraDeep : C.ink, background: rdiag.total === 0 ? C.paperDeep : C.paperDeep, border: `1px solid ${rdiag.total === 0 ? C.terra : C.rule}`, fontWeight: 600 }}>
          store: {rdiag.total} receipt{rdiag.total === 0 ? " — VUOTO: gli scontrini non sono in questa istanza del browser" : ""}
        </span>
        {rdiag.total > 0 && <>
          <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>con closedAt {rdiag.withClosed}</span>
          <span className="f-mono" style={{ ...chip, color: rdiag.noClosed > 0 ? C.terraDeep : C.inkMid, background: C.paperDeep, border: `1px solid ${rdiag.noClosed > 0 ? C.terra : C.rule}` }}>senza closedAt {rdiag.noClosed}</span>
          <span className="f-mono" style={{ ...chip, color: rdiag.unparsable > 0 ? C.terraDeep : C.inkMid, background: C.paperDeep, border: `1px solid ${rdiag.unparsable > 0 ? C.terra : C.rule}` }}>non parsabili {rdiag.unparsable}</span>
          <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>fuori range 30/06–03/07: {rdiag.outOfRange}</span>
          {rdiag.lastClosed && <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>ultimo closedAt: {String(rdiag.lastClosed)}</span>}
        </>}
      </div>
      {rdiag.total > 0 && <>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 6 }}>
          {rdiag.perDay.map(d => <span key={d.day} className="f-mono"
            style={{ ...chip, color: d.total > 0 ? C.oliveDeep : C.inkMid, background: d.total > 0 ? C.oliveWash : C.paperDeep, border: `1px solid ${d.total > 0 ? C.olive : C.rule}` }}>
            {d.day.slice(8)}/{d.day.slice(5, 7)}: {d.total}{d.total > 0 ? ` (P ${d.pranzo} · C ${d.cena})` : ""}
          </span>)}
        </div>
        {rdiag.sample.length > 0 && <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted }}>ultimi {rdiag.sample.length} per closedAt</span>
          {rdiag.sample.map((r, i) => <span key={i} className="f-mono" style={{ fontSize: 10, color: C.inkMid }}>
            #{String(r.number)}{r.fiscalNumber ? ` (${r.fiscalNumber})` : ""} · {String(r.closedAt)} → {r.serviceDate || "?"} {r.shift || "?"} · {fmt.eur(r.total)} · {r.table != null ? `tav ${r.table}` : "banco/n.d."}{r.source ? ` · ${String(r.source).slice(0, 40)}` : ""}
          </span>)}
        </div>}
      </>}
    </div>
    {/* R5.9-T1 — riconciliazione read-only: classi calcolate col VERO motore
        suggerimenti R5; i collegamenti si fanno SOLO in Agenda (F8) */}
    <div style={{ marginBottom: 8, padding: "10px 12px", background: C.white, border: `1px solid ${C.rule}`, borderRadius: 2 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: C.ink, fontWeight: 600 }}>Riconciliazione (read-only)</span>
        <span className="f-body" style={{ fontSize: 10, color: C.muted, fontStyle: "italic" }}>classi dal motore suggerimenti R5 · i collegamenti si fanno in Agenda</span>
        <div style={{ flex: 1 }} />
        <button className="tx f-mono" onClick={() => setShowRecon(v => !v)}
          style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 2, cursor: "pointer",
            background: showRecon ? C.ink : C.white, color: showRecon ? C.white : C.inkMid, border: `1px solid ${showRecon ? C.ink : C.rule}` }}>
          {showRecon ? "Nascondi piano" : "Mostra piano"}
        </button>
      </div>
      {["pranzo", "cena"].map(svc => {
        const d = recon[svc];
        if (!d) return null;
        return <div key={svc} style={{ marginBottom: 6 }}>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
            <span className="f-mono" style={{ fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: C.ink, fontWeight: 600, width: 52 }}>{svc}</span>
            <span className="f-mono" style={{ ...chip, color: C.amberDeep, background: C.amberWash, border: `1px solid ${C.amber}` }}>da collegare {d.counts.daCollegare}</span>
            <span className="f-mono" style={{ ...chip, color: C.oliveDeep, background: C.oliveWash, border: `1px solid ${C.olive}` }}>facili {d.counts.facili}</span>
            {setState && d.counts.facili > 0 && <Btn size="xs" variant="accent" onClick={() => handleLinkFacili(svc)}>
              Collega i facili ({d.counts.facili})
            </Btn>}
            <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>medie {d.counts.medie}</span>
            <span className="f-mono" style={{ ...chip, color: C.amberDeep, background: C.paperDeep, border: `1px solid ${C.amber}` }}>ambigue {d.counts.ambigue}</span>
            <span className="f-mono" style={{ ...chip, color: d.counts.senzaCandidati > 0 ? C.terraDeep : C.inkMid, background: C.paperDeep, border: `1px solid ${d.counts.senzaCandidati > 0 ? C.terra : C.rule}` }}>senza candidati {d.counts.senzaCandidati}</span>
            <span className="f-mono" style={{ ...chip, color: C.oliveDeep, background: C.paperDeep, border: `1px solid ${C.rule}` }}>collegate {d.counts.collegate}</span>
            {d.counts.orphan > 0 && <span className="f-mono" style={{ ...chip, color: C.terraDeep, background: C.paperDeep, border: `1px solid ${C.terra}` }}>orfane {d.counts.orphan}</span>}
            {d.counts.shared > 0 && <span className="f-mono" style={{ ...chip, color: C.amberDeep, background: C.paperDeep, border: `1px solid ${C.amber}` }}>shared {d.counts.shared}</span>}
            <span className="f-mono" style={{ ...chip, color: C.inkMid, background: C.paperDeep, border: `1px solid ${C.rule}` }}>
              receipt {d.receipts.total} · senza pren. {d.receipts.unlinked} · banco {d.receipts.banco}{d.receipts.senzaTavolo > 0 ? ` · senza tavolo ${d.receipts.senzaTavolo}` : ""}{d.receipts.preconti > 0 ? ` · preconti ${d.receipts.preconti}` : ""}
            </span>
          </div>
          {showRecon && d.plan.length > 0 && <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 5 }}>
            {d.plan.map(p => {
              const ks = RESV_BOOT_KLASS_STYLE[p.klass];
              return <div key={p.r.id} style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", padding: "4px 10px", background: C.paperDeep, border: `1px solid ${C.ruleSoft}`, borderRadius: 2 }}>
                <span className="f-mono" style={{ ...chip, fontSize: 9, color: ks.fg, background: ks.bg, border: `1px solid ${ks.bd}` }}>{p.klass.toUpperCase()}</span>
                <span className="f-mono" style={{ fontSize: 11, color: C.ink, width: 40, flexShrink: 0 }}>{p.r.time || "—"}</span>
                <span className="f-display" style={{ fontSize: 12, fontWeight: 500, color: C.ink }}>{p.r.rawName}</span>
                <span className="f-mono" style={{ fontSize: 10, color: C.inkMid }}>{p.r.covers} cop. · {p.r.table != null && String(p.r.table).trim() !== "" ? `tav ${p.r.table}` : "senza tavolo"}</span>
                {p.top
                  ? <span className="f-body" style={{ fontSize: 10, color: C.inkMid }}>
                    → #{String(p.top.receipt.number)}{p.top.fiscalNumber ? ` (${p.top.fiscalNumber})` : ""} · {p.top.covers} cop. · {fmt.eur((p.top.totalCents || 0) / 100)} · score {p.top.score}{p.nCand > 1 ? ` · +${p.nCand - 1} alt.` : ""} · <span style={{ fontStyle: "italic" }}>{p.top.reasons.slice(0, 3).join(", ")}</span>
                  </span>
                  : <span className="f-body" style={{ fontSize: 10, color: C.terraDeep, fontStyle: "italic" }}>nessun candidato con score ≥ 2 nel turno</span>}
              </div>;
            })}
          </div>}
        </div>;
      })}
    </div>
    <button className="tx f-mono" onClick={() => setShowRows(v => !v)}
      style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 2, cursor: "pointer",
        background: showRows ? C.ink : C.white, color: showRows ? C.white : C.inkMid, border: `1px solid ${showRows ? C.ink : C.rule}` }}>
      {showRows ? "Nascondi righe" : "Mostra righe"}
    </button>
    {showRows && ["pranzo", "cena"].map(svc => audit.services[svc].rows.length > 0 && <div key={svc} style={{ marginTop: 8 }}>
      <div className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 4 }}>{svc} — {audit.services[svc].rows.length} righe</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {audit.services[svc].rows.map(x => {
          const fs = RESV_BOOT_FULFIL_STYLE[x.fulfil] || RESV_BOOT_FULFIL_STYLE["da collegare"];
          return <div key={x.r.id} style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", padding: "5px 10px", background: C.white, border: `1px solid ${C.ruleSoft}`, borderRadius: 2, opacity: x.r.status === "cancelled" || x.r.status === "no_show" ? 0.55 : 1 }}>
            <span className="f-mono" style={{ fontSize: 11, color: C.ink, width: 40, flexShrink: 0 }}>{x.r.time || "—"}</span>
            <span className="f-display" style={{ fontSize: 13, fontWeight: 500, color: C.ink }}>{x.r.rawName}</span>
            <span className="f-mono" style={{ fontSize: 11, color: C.inkMid }}>{x.r.covers} cop.</span>
            <span className="f-mono" style={{ fontSize: 11, color: x.tableOutcome === "riconosciuto" ? C.inkMid : x.tableOutcome === "senza tavolo" ? C.muted : C.terraDeep }}>
              {x.r.table != null && String(x.r.table).trim() !== "" ? `tav ${x.r.table}` : "senza tavolo"}{x.tableOutcome !== "riconosciuto" && x.tableOutcome !== "senza tavolo" ? ` (${x.tableOutcome})` : ""}
            </span>
            <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase", color: C.inkMid }}>{x.r.status}</span>
            <span className="f-mono" style={{ ...chip, fontSize: 9, color: fs.fg, background: fs.bg, border: `1px solid ${fs.bd}` }}>{x.fulfil}{x.f.resolved.length > 0 ? ` (${x.f.resolved.length})` : ""}</span>
            <span className="f-mono" style={{ fontSize: 9, color: x.hasTag ? C.tealDeep : C.muted }}>{x.hasTag ? "PNSM" : "no tag"}</span>
          </div>;
        })}
      </div>
    </div>)}
  </div>;
}
// ⟦RESV-BOOTSTRAP-UI:END⟧

// ⟦FLOOR-UI:START⟧ — R2-T3 · Vista configurazione "Sala & Tavoli".
// CONSUMER puro di floorCore (⟦FLOOR-CORE⟧): letture via listAreas/listTables
// (includeInactive: la configurazione vede tutto), scritture SOLO via
// createArea/updateArea/createTable/updateTable — pattern reducer → guard error →
// singolo setState → toast. NIENTE tombstone da UI in V1 (si disattiva, mai si
// cancella: la disattivazione è reversibile e i derivati la rispettano già).
// Nessun contatore agenda, nessuna validazione reservation, nessuna piantina.
function FloorSettingsView({ state, setState }) {
  const toast = useToast();
  const operator = () => ({ by: state.currentUser?.name || "Andi" });
  // Form crea area
  const [aName, setAName] = useState("");
  const [aCapacity, setACapacity] = useState("");
  // Edit inline area (una alla volta)
  const [aEditId, setAEditId] = useState(null);
  const [aeName, setAeName] = useState("");
  const [aeCapacity, setAeCapacity] = useState("");
  // Form crea tavolo
  const [tLabel, setTLabel] = useState("");
  const [tAreaId, setTAreaId] = useState("");
  const [tSeats, setTSeats] = useState("");
  // Edit inline tavolo (uno alla volta)
  const [tEditId, setTEditId] = useState(null);
  const [teLabel, setTeLabel] = useState("");
  const [teAreaId, setTeAreaId] = useState("");
  const [teSeats, setTeSeats] = useState("");

  // La configurazione vede TUTTO (anche il disattivato); i derivati operativi no.
  const areas = useMemo(() => floorCore.listAreas(state.floorAreas, { includeInactive: true }), [state.floorAreas]);
  const activeAreas = useMemo(() => floorCore.listAreas(state.floorAreas), [state.floorAreas]);
  const tables = useMemo(() => floorCore.listTables(state.floorTables, { includeInactive: true }), [state.floorTables]);
  const areaNameById = useMemo(() => new Map(areas.map(a => [String(a.id), a.name])), [areas]);
  // Per riga area: tavoli attivi associati + somma posti (solo seats > 0, come la capienza derivata).
  const tableStatsByArea = useMemo(() => {
    const m = new Map();
    for (const t of floorCore.listTables(state.floorTables)) {
      const k = String(t.areaId);
      const e = m.get(k) || { count: 0, seats: 0 };
      e.count++;
      if (Number.isInteger(Number(t.seats)) && Number(t.seats) > 0) e.seats += Number(t.seats);
      m.set(k, e);
    }
    return m;
  }, [state.floorTables]);

  // ── Handler aree ──
  const handleCreateArea = () => {
    if (!aName.trim()) { toast({ type: "error", title: "Nome mancante", message: "Inserisci il nome dell'area." }); return; }
    const res = floorCore.createArea(state.floorAreas, { name: aName, capacity: aCapacity.trim() !== "" ? aCapacity : null }, operator());
    if (res.error) { toast({ type: "error", title: "Area non creata", message: res.error }); return; }
    setState(s => ({ ...s, floorAreas: res.floorAreas }));
    setAName(""); setACapacity("");
    toast({ type: "success", title: "Area creata", message: res.area.name });
  };
  const startEditArea = (a) => { setAEditId(a.id); setAeName(a.name); setAeCapacity(a.capacity != null ? String(a.capacity) : ""); };
  const cancelEditArea = () => { setAEditId(null); setAeName(""); setAeCapacity(""); };
  const handleSaveArea = (a) => {
    const res = floorCore.updateArea(state.floorAreas, a.id, { name: aeName, capacity: aeCapacity.trim() !== "" ? aeCapacity : null }, operator());
    if (res.error) { toast({ type: "error", title: "Area non salvata", message: res.error }); return; }
    setState(s => ({ ...s, floorAreas: res.floorAreas }));
    cancelEditArea();
    toast({ type: "success", title: "Area aggiornata", message: res.area.name });
  };
  const handleToggleArea = (a) => {
    const res = floorCore.updateArea(state.floorAreas, a.id, { active: a.active === false }, operator());
    if (res.error) { toast({ type: "error", title: "Stato non cambiato", message: res.error }); return; }
    setState(s => ({ ...s, floorAreas: res.floorAreas }));
    toast({ type: "success", title: res.area.active ? "Area riattivata" : "Area disattivata", message: a.name });
  };

  // ── Handler tavoli ──
  const handleCreateTable = () => {
    if (!tLabel.trim()) { toast({ type: "error", title: "Numero mancante", message: "Inserisci il numero/nome del tavolo." }); return; }
    if (!tAreaId) { toast({ type: "error", title: "Area mancante", message: "Scegli l'area del tavolo." }); return; }
    const res = floorCore.createTable(state.floorTables, { label: tLabel, areaId: tAreaId, seats: tSeats.trim() !== "" ? tSeats : 0 }, operator());
    if (res.error) { toast({ type: "error", title: "Tavolo non creato", message: res.error }); return; }
    setState(s => ({ ...s, floorTables: res.floorTables }));
    setTLabel(""); setTSeats("");
    toast({ type: "success", title: "Tavolo creato", message: `${res.table.label} · ${areaNameById.get(String(res.table.areaId)) || "?"}` });
  };
  const startEditTable = (t) => { setTEditId(t.id); setTeLabel(t.label); setTeAreaId(String(t.areaId)); setTeSeats(t.seats != null ? String(t.seats) : "0"); };
  const cancelEditTable = () => { setTEditId(null); setTeLabel(""); setTeAreaId(""); setTeSeats(""); };
  const handleSaveTable = (t) => {
    const res = floorCore.updateTable(state.floorTables, t.id, { label: teLabel, areaId: teAreaId, seats: teSeats.trim() !== "" ? teSeats : 0 }, operator());
    if (res.error) { toast({ type: "error", title: "Tavolo non salvato", message: res.error }); return; }
    setState(s => ({ ...s, floorTables: res.floorTables }));
    cancelEditTable();
    toast({ type: "success", title: "Tavolo aggiornato", message: res.table.label });
  };
  const handleToggleTable = (t) => {
    const res = floorCore.updateTable(state.floorTables, t.id, { active: t.active === false }, operator());
    if (res.error) { toast({ type: "error", title: "Stato non cambiato", message: res.error }); return; }
    setState(s => ({ ...s, floorTables: res.floorTables }));
    toast({ type: "success", title: res.table.active ? "Tavolo riattivato" : "Tavolo disattivato", message: t.label });
  };

  const inputStyle = { padding: "8px 10px", border: `1px solid ${C.rule}`, borderRadius: 2, fontSize: 13, background: C.white, color: C.ink, outline: "none" };
  const statusChip = (active) => <span className="f-mono" style={{ fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", padding: "3px 8px", borderRadius: 2, flexShrink: 0, color: active ? C.oliveDeep : C.muted, border: `1px solid ${active ? C.olive : C.rule}` }}>{active ? "Attiva" : "Non attiva"}</span>;

  return <div className="fade-up">
    <PageHeader kicker="Floor Core · Configurazione" title="Sala & Tavoli"
      subtitle="Le aree e i tavoli del ristorante. Qui si configura il patrimonio fisico: l'Agenda lo userà per contare i coperti per area e riconoscere i tavoli." />

    {/* ── AREE ── */}
    <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase", marginBottom: 10 }}>Aree / Sale</div>
    <div style={{ display: "flex", gap: 8, marginBottom: 12, padding: "12px 14px", background: C.paperDeep, border: `1px solid ${C.rule}`, borderRadius: 2, alignItems: "center", flexWrap: "wrap" }}>
      <input value={aName} onChange={e => setAName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleCreateArea(); }} placeholder="Nome area *" className="f-body" style={{ ...inputStyle, flex: 2, minWidth: 140 }} />
      <input value={aCapacity} onChange={e => setACapacity(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleCreateArea(); }} placeholder="Capienza (opz.)" type="number" min="0" className="f-body" style={{ ...inputStyle, width: 130 }} />
      <Btn size="sm" variant="accent" icon={Plus} onClick={handleCreateArea}>Crea area</Btn>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 28 }}>
      {areas.map(a => {
        const active = a.active !== false;
        const stats = tableStatsByArea.get(String(a.id)) || { count: 0, seats: 0 };
        const editing = aEditId === a.id;
        return <div key={a.id} style={{ padding: "10px 14px", background: C.white, border: `1px solid ${editing ? C.ink : C.rule}`, borderRadius: 2, opacity: active || editing ? 1 : 0.55 }}>
          {!editing
            ? <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span className="f-display" style={{ fontSize: 15, fontWeight: 500, color: C.ink, minWidth: 90 }}>{a.name}</span>
              <span className="f-mono" style={{ fontSize: 11, color: C.inkMid }}>
                Capienza {a.capacity != null ? a.capacity : (stats.seats > 0 ? `${stats.seats} (da tavoli)` : "—")}
              </span>
              <span className="f-mono" style={{ fontSize: 11, color: C.inkMid }}>{stats.count} {stats.count === 1 ? "tavolo attivo" : "tavoli attivi"}{stats.seats > 0 ? ` · ${stats.seats} posti` : ""}</span>
              {statusChip(active)}
              <div style={{ flex: 1 }} />
              <IconBtn icon={Edit2} size={14} title="Modifica nome / capienza" onClick={() => startEditArea(a)} />
              <Btn size="xs" variant="ghost" onClick={() => handleToggleArea(a)}>{active ? "Disattiva" : "Riattiva"}</Btn>
            </div>
            : <div className="fade-in" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input value={aeName} onChange={e => setAeName(e.target.value)} autoFocus placeholder="Nome *"
                onKeyDown={e => { if (e.key === "Enter") handleSaveArea(a); if (e.key === "Escape") cancelEditArea(); }}
                className="f-body" style={{ ...inputStyle, flex: 2, minWidth: 140 }} />
              <input value={aeCapacity} onChange={e => setAeCapacity(e.target.value)} placeholder="Capienza (vuota = da tavoli)" type="number" min="0"
                onKeyDown={e => { if (e.key === "Enter") handleSaveArea(a); if (e.key === "Escape") cancelEditArea(); }}
                className="f-body" style={{ ...inputStyle, width: 200 }} />
              <Btn size="sm" variant="primary" onClick={() => handleSaveArea(a)}>Salva</Btn>
              <Btn size="sm" variant="ghost" onClick={cancelEditArea}>Annulla</Btn>
            </div>}
        </div>;
      })}
    </div>

    {/* ── TAVOLI ── */}
    <div className="f-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: C.muted, textTransform: "uppercase", marginBottom: 10 }}>Tavoli</div>
    <div style={{ display: "flex", gap: 8, marginBottom: 12, padding: "12px 14px", background: C.paperDeep, border: `1px solid ${C.rule}`, borderRadius: 2, alignItems: "center", flexWrap: "wrap" }}>
      <input value={tLabel} onChange={e => setTLabel(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleCreateTable(); }} placeholder="Numero / nome *" className="f-body" style={{ ...inputStyle, width: 140 }} />
      <select value={tAreaId} onChange={e => setTAreaId(e.target.value)} className="f-body" style={{ ...inputStyle, width: 150 }}>
        <option value="">Area *</option>
        {activeAreas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      <input value={tSeats} onChange={e => setTSeats(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleCreateTable(); }} placeholder="Posti" type="number" min="0" className="f-body" style={{ ...inputStyle, width: 90 }} />
      <Btn size="sm" variant="accent" icon={Plus} onClick={handleCreateTable}>Crea tavolo</Btn>
    </div>
    {tables.length === 0
      ? <EmptyState icon={Layers} title="Nessun tavolo configurato"
          body="Crea i tavoli veri del ristorante con la riga qui sopra: numero, area e posti. L'Agenda li riconoscerà nel campo tavolo delle prenotazioni." />
      : <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {tables.map(t => {
          const active = t.active !== false;
          const areaName = areaNameById.get(String(t.areaId));
          const editing = tEditId === t.id;
          return <div key={t.id} style={{ padding: "10px 14px", background: C.white, border: `1px solid ${editing ? C.ink : C.rule}`, borderRadius: 2, opacity: active || editing ? 1 : 0.55 }}>
            {!editing
              ? <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span className="f-mono" style={{ fontSize: 13, fontWeight: 600, color: C.ink, minWidth: 70 }}>{t.label}</span>
                {areaName
                  ? <span className="f-body" style={{ fontSize: 12, color: C.inkMid }}>{areaName}</span>
                  : <span className="f-body" style={{ fontSize: 12, color: C.terraDeep, fontStyle: "italic" }}>Area non trovata</span>}
                <span className="f-mono" style={{ fontSize: 11, color: C.inkMid }}>{Number(t.seats) > 0 ? `${t.seats} posti` : "posti —"}</span>
                {statusChip(active)}
                <div style={{ flex: 1 }} />
                <IconBtn icon={Edit2} size={14} title="Modifica numero / area / posti" onClick={() => startEditTable(t)} />
                <Btn size="xs" variant="ghost" onClick={() => handleToggleTable(t)}>{active ? "Disattiva" : "Riattiva"}</Btn>
              </div>
              : <div className="fade-in" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input value={teLabel} onChange={e => setTeLabel(e.target.value)} autoFocus placeholder="Numero / nome *"
                  onKeyDown={e => { if (e.key === "Enter") handleSaveTable(t); if (e.key === "Escape") cancelEditTable(); }}
                  className="f-body" style={{ ...inputStyle, width: 140 }} />
                <select value={teAreaId} onChange={e => setTeAreaId(e.target.value)} className="f-body" style={{ ...inputStyle, width: 150 }}>
                  {activeAreas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  {!activeAreas.some(a => String(a.id) === teAreaId) && teAreaId && <option value={teAreaId}>{areaNameById.get(teAreaId) || "Area attuale (non attiva)"}</option>}
                </select>
                <input value={teSeats} onChange={e => setTeSeats(e.target.value)} placeholder="Posti" type="number" min="0"
                  onKeyDown={e => { if (e.key === "Enter") handleSaveTable(t); if (e.key === "Escape") cancelEditTable(); }}
                  className="f-body" style={{ ...inputStyle, width: 90 }} />
                <Btn size="sm" variant="primary" onClick={() => handleSaveTable(t)}>Salva</Btn>
                <Btn size="sm" variant="ghost" onClick={cancelEditTable}>Annulla</Btn>
              </div>}
          </div>;
        })}
      </div>}
  </div>;
}
// ⟦FLOOR-UI:END⟧

// ErrorBoundary per catturare errori runtime
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null, errorInfo: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info);
    this.setState({ errorInfo: info });
  }
  resetStorage = async () => {
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        localStorage.removeItem("bottega_prod_v6");
        localStorage.removeItem("bottega_prod_v5");
        localStorage.removeItem("bottega_prod_v4");
        localStorage.removeItem("bottega_prod_v3");
      }
    } catch (e) {}
    // Lo storage attivo è IndexedDB: serve cancellare anche lì, altrimenti
    // "Pulisci dati e ricarica" non puliva nulla e i dati corrotti tornavano.
    try {
      if (isIdbAvailable()) await idbDel("bottega_prod_v6");
    } catch (e) { console.warn("Reset IndexedDB fallito:", e); }
    window.location.reload();
  };
  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      const msg = err?.message || err?.toString() || "Errore sconosciuto";
      const name = err?.name || "Error";
      const stack = err?.stack || "";
      const componentStack = this.state.errorInfo?.componentStack || "";
      const fullDetail = `${name}: ${msg}\n\n--- STACK ---\n${stack}\n\n--- COMPONENT STACK ---\n${componentStack}`;
      return <div style={{ padding: 40, fontFamily: "Geist, sans-serif", maxWidth: 720, margin: "40px auto", background: "#F5F0E6" }}>
        <div style={{ background: "#fff", padding: 32, border: "1px solid #B5472A", borderRadius: 2 }}>
          <h2 style={{ color: "#B5472A", margin: "0 0 16px 0" }}>Si è verificato un errore</h2>
          <p style={{ color: "#3A3630", marginBottom: 16, fontSize: 14 }}>Il sistema ha incontrato un problema.</p>
          <div style={{ background: "#1C1A17", color: "#F5F0E6", padding: 14, fontSize: 12, fontFamily: "monospace", marginBottom: 16, borderRadius: 2, overflowX: "auto" }}>
            <strong style={{ color: "#FFB84D" }}>{name}:</strong> {msg}
          </div>
          <details style={{ background: "#F5F0E6", padding: 12, fontSize: 11, fontFamily: "monospace", marginBottom: 20, borderRadius: 2 }} open>
            <summary style={{ cursor: "pointer", color: "#5A5349", marginBottom: 8 }}>Stack trace (clicca per copiare)</summary>
            <pre onClick={() => { navigator.clipboard?.writeText(fullDetail); alert("Copiato negli appunti"); }} style={{ whiteSpace: "pre-wrap", marginTop: 8, cursor: "pointer", maxHeight: 300, overflowY: "auto" }}>{fullDetail}</pre>
          </details>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={this.resetStorage} style={{ padding: "10px 20px", background: "#1F4D48", color: "#fff", border: "none", borderRadius: 2, cursor: "pointer", fontSize: 13, fontWeight: 500 }}>Pulisci dati e ricarica</button>
            <button onClick={() => window.location.reload()} style={{ padding: "10px 20px", background: "transparent", color: "#1C1A17", border: "1px solid #D9D0BE", borderRadius: 2, cursor: "pointer", fontSize: 13 }}>Ricarica soltanto</button>
          </div>
        </div>
      </div>;
    }
    return this.props.children;
  }
}

export default function App() {
  // NOTA: FONT_STYLE è ora in src/styles.css (importato da src/main.jsx).
  return <>
    {/* Banner errore storage: fuori da ErrorBoundary per restare visibile anche in caso di crash di AppInner */}
    <StorageErrorBanner />
    <ErrorBoundary>
      <ToastProvider>
        <AppInner />
      </ToastProvider>
    </ErrorBoundary>
  </>;
}

// ErrorBoundary per singole view (non blocca tutta l'app)
class ViewErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null, info: null, viewName: props.viewName }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { this.setState({ info }); console.error("ViewErrorBoundary:", this.props.viewName, error, info); }
  componentDidUpdate(prev) {
    if (prev.viewName !== this.props.viewName && this.state.hasError) {
      this.setState({ hasError: false, error: null, info: null, viewName: this.props.viewName });
    }
  }
  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      const msg = err?.message || String(err);
      const stack = err?.stack || "";
      const compStack = this.state.info?.componentStack || "";
      const detail = `VIEW: ${this.props.viewName}\nERROR: ${msg}\n\n--- STACK ---\n${stack}\n\n--- COMPONENT STACK ---\n${compStack}`;
      return <div style={{ padding: 32, background: "#fff", border: "1px solid #B5472A", borderRadius: 2, margin: 20 }}>
        <div style={{ color: "#B5472A", fontSize: 14, fontWeight: 600, marginBottom: 8, fontFamily: "Geist" }}>Errore nella pagina "{this.props.viewName}"</div>
        <div style={{ background: "#1C1A17", color: "#FFB84D", padding: 12, fontFamily: "monospace", fontSize: 11, borderRadius: 2, marginBottom: 12, wordBreak: "break-word" }}>{msg}</div>
        <details open style={{ fontFamily: "monospace", fontSize: 10, background: "#F5F0E6", padding: 10, borderRadius: 2, marginBottom: 12 }}>
          <summary style={{ cursor: "pointer", marginBottom: 6 }}>Stack (click per copiare)</summary>
          <pre onClick={() => { navigator.clipboard?.writeText(detail); alert("Copiato"); }} style={{ whiteSpace: "pre-wrap", cursor: "pointer", maxHeight: 260, overflowY: "auto" }}>{detail}</pre>
        </details>
        <button onClick={() => { this.setState({ hasError: false, error: null }); this.props.onReset?.(); }} style={{ padding: "8px 16px", background: "#1F4D48", color: "#fff", border: "none", borderRadius: 2, cursor: "pointer", fontSize: 12, fontFamily: "Geist" }}>Torna alla dashboard</button>
      </div>;
    }
    return this.props.children;
  }
}
